var markedExtensions = (function (exports) {
  'use strict';

  /**
   * Assigns the keys from source into target, but only those that are not already assigned. Useful
   * in place of Object.assign which would require a polyfill
   * @param target
   * @param source
   * @returns {*}
   */
  function assignMissing(target, source) {
    Object.keys(source || {}).forEach(function (key) {
      if (!(key in target)) {
        target[key] = source[key];
      }
    });
    return target;
  }

  function upperCaseWords(str) {
    if (!str) return str;
    return str.replace(/(?:^|\s)\S/g, function (a) {
      return a.toUpperCase();
    });
  }

  function camelCase(str, pascal) {
    var cased = upperCaseWords(str.replace(/_/g, ' ')).replace(/ /g, '');
    return pascal ? cased : lowerCaseFirst(cased);
  }

  function lowerCaseFirst(str) {
    return '' + str[0].toLowerCase() + str.substr(1);
  }

  function escapeHtml(text) {
    return text ? text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') : text;
  }

  function unescapeHtml(text) {
    return text ? text.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>') : text;
  }

  /**
   * Tracks some basic types that we convert for different languages. We don't need to try to
   * maintain a list for every type for every language we support, however we do want to track some
   * common types, such as hash, void, and primary types to minimize confusion.
   *
   * We will want to expand or trim this list as needed to find a good balance between useful and
   * too much language specialization.
   *
   */
  var TYPES = {
    void: {
      'undefined': ['javascript', 'coffeescript'],
      nil: ['ruby'],
      None: ['python']
    },
    'null': {
      nil: ['ruby'],
      NSNull: ['objc'],
      None: ['python']
    },
    object: {
      NSObject: ['objc'],
      _alias: 'hash'
    },
    hash: {
      Hash: ['ruby'],
      dict: ['python'],
      Dictionary: ['csharp'],
      HashMap: ['java'],
      Object: ['javascript', 'typescript', 'coffeescript']
    },
    collection: {
      List: ['java'],
      Collection: ['csharp'],
      _alias: 'list'
    },
    enumerable: {
      IEnumerable: ['csharp'],
      vector: ['cpp'],
      _alias: 'array'
    },
    array: {
      List: ['java'],
      'NSArray*': ['objc'],
      'std::list': ['cpp'],
      default: 'Array'
    },
    list: {
      List: ['java', 'csharp', 'scala', 'groovy', 'kotlin'],
      'std::list': ['cpp'],
      default: 'Array'
    },
    string: {
      string: ['csharp', 'typescript'],
      'std::string': ['cpp'],
      'char*': ['c'],
      'NSString *': ['objc'],
      default: 'String'
    },
    integer: {
      int: ['csharp', 'cpp', 'c', 'go'],
      Int: ['swift', 'haskell', 'kotlin'],
      'NSNumber *': ['objc'],
      Number: ['javascript'],
      number: ['typescript'],
      default: 'Integer'
    },
    long: {
      long: ['csharp', 'java', 'c', 'cpp'],
      _alias: 'integer'
    },
    boolean: {
      bool: ['csharp', 'c', 'cpp'],
      Bool: ['swift'],
      BOOL: ['objc'],
      boolean: ['java', 'typescript'],
      default: 'Boolean'
    },
    float: {
      float: ['csharp'],
      Number: ['javascript', 'coffeescript', 'typescript'],
      default: 'Float'
    }
  };

  var NULLABLE = {
    csharp: {
      default: 'Nullable<@@>'
    }

    /**
     * Long story short this handles these types of cases
     * <code>@@docType:Array</code>
     * <code>@@docType:Array<String></code>
     * `@@docType:Array`
     * `@@docType:Array<String>`
     * `@@docType:Array<String, Int>`
     * `@@docType:Array<String,Int>`
     * @@docType:Array<String, Int>
     * @@docType: Promise<Array<Int>>
     * @@docType:Array
     * @type {RegExp}
     */
  };var regex = /(`|<code>|<pre>)?@@docType: ?([\w_?]*(?:(?:<|&lt;)[\w?]*(?:[,<]?\s?\w*>?){0,3}(?:>|&gt;))?)(`|<\/code>|<\/pre>)?/g;
  var nestedRegex = /(?:<|&lt;)([\w?]*(?:[,<]?\s?\w*>?){0,3})(?:>|&gt;)/g;

  /**
   * process all @@docType: tokens and replaces them with the correct display value, and possibly wraps them in a dfn tag
   * @param language
   * @param pre Boolean that determines if we are displaying this content within a pre element, so don't wrap with dfn tags
   * @param content The content to be replaced. Can handle replacing multiple tags at once
   */
  function replaceDocTypes(language, pre, content) {
    return content.replace(regex, function (shell, codeStart, value, codeEnd) {
      var nullable = !!value.match(/\?$/);
      value = value.replace('?', '').trim();
      value = unescapeHtml(value);

      value = maybeMapGeneric(language, value);

      if (nullable) {
        value = mapNullable(language, value);
      }

      return wrap(value, shell, pre, codeStart, codeEnd);
    });
  }

  function mapNullable(language, value) {
    if (NULLABLE[language]) {
      var config = NULLABLE[language][value] || NULLABLE[language].default;
      if (config) {
        return config.replace('@@', value);
      }
    }

    return value;
  }

  function maybeMapGeneric(language, value) {
    var parts = value.split(nestedRegex).filter(function (p) {
      return !!p;
    });

    if (parts.length < 2) {
      return mapType(language, value);
    } else if (parts.length === 2) {
      return collectionType(language, parts[0], parts[1]);
    } else {
      var root = parts.shift();
      var mapped = parts.map(function (p) {
        return collectionType(language, p);
      });
      return root + '<' + mapped.join(', ') + '>';
    }
  }

  function mapType(language, type, _default) {
    var map = TYPES[type.toLowerCase()];
    var mapped = null;
    if (map) {
      Object.keys(map).forEach(function (key) {
        if (!mapped) {
          if (key === 'default') {
            mapped = map.default;
          }
          // if a string, then the value is an alias to another type
          else if (key === '_alias') {
              mapped = mapType(language, map._alias, type);
            } else if (map[key].indexOf(language) >= 0) {
              mapped = key;
            }
        }
      });
      return mapped || _default || type;
    }

    return type;
  }

  function isTransformedType(type) {
    return Object.keys(TYPES).indexOf(type.toLowerCase()) > -1;
  }

  function collectionType(language, type, nestedType) {
    var nestedTypes = nestedType.split(/, ?/);
    if (isTransformedType(type)) {
      switch (language) {
        case 'ruby':
        case 'objc':
        case 'python':
          var plurals = mapTypes(language, nestedTypes).map(function (s) {
            return s.replace(' *', '');
          }).join('s/');

          var addS = plurals.indexOf(')') > 0 ? '' : 's';
          return mapType(language, type) + ' (of ' + plurals + addS + ')';

        case 'csharp':
        case 'java':
          // if an array and not wrapping another array
          if (type === 'Array' && nestedTypes.length == 1 && nestedTypes[0].indexOf('<') === -1) {
            return mapType(language, nestedType) + '[]';
          }
          break;
        case 'c':
          if (type === 'List' && nestedTypes.length == 1 && nestedTypes[0].indexOf('<') === -1) {
            return mapType(language, nestedType) + '[]';
          }
          break;
        case 'haskell':
          if ((type === 'List' || type === 'Array') && nestedTypes.length == 1) {
            return '[' + mapType(language, nestedType) + ']';
          }
          break;
        case 'go':
          if ((type === 'List' || type === 'Array') && nestedTypes.length == 1) {
            return '[]' + mapType(language, nestedType);
          }
          break;
      }
    }
    var result = collectionGeneric(language, type, nestedTypes);
    return result;
  }

  function mapTypes(language, types) {
    return types.map(function (t) {
      return maybeMapGeneric(language, t);
    });
  }

  function collectionGeneric(language, type, nestedTypes) {
    var mapped = mapTypes(language, nestedTypes).join(', ');
    return mapType(language, type) + '<' + mapped + '>';
  }

  /**
   * Wraps the value within the necessary html to ensure that it gets rendered correctly
   * @param value
   * @param shell the shell of the @@docType syntax. Used to determine if a ` is present
   * @returns {string}
   */
  function wrap(value, shell, pre, start, end) {
    value = escapeHtml(value.trim());

    if (pre) {
      return value;
    } else if (start) {
      return start + value + (end || '');
    } else {
      return '<dfn class="doc-type">' + value + '</dfn>';
    }
  }

  var STYLES = {
    Const: {
      upper: ['default']
    },
    Method: {
      upperCamel: ['csharp']
    },
    Prop: {
      upperCamel: ['csharp']
    },
    Class: {
      upperCamel: ['default']
    },
    // name acts as default
    Name: {
      camel: ['javascript', 'java', 'coffeescript', 'typescript', 'go', 'kotlin', 'scala', 'objc', 'php', 'swift', 'csharp', 'solidity', 'haskell'],
      kabob: ['clojure']
    }
  };

  function replaceDocNames(language, pre, content) {

    return content.replace(/`?@@doc(Name|Method|Const|Prop|Class|Param): ?([a-zA-Z0-9?_]*)`?/g, function (shell, type, value) {
      return wrap$1(shell, type, pre, function () {
        if (value) {
          var style = findStyle(type, language);

          switch (style) {
            case 'upper':
              return value.toUpperCase();

            case 'kabob':
              return value.replace(/_/g, '-');

            case 'camel':
              return camelCase(value);

            case 'upperCamel':
              return camelCase(value, true);

            default:
              return value;
          }
        } else {
          console.warn('replaceDocNames value is missing:', content); // eslint-disable-line no-console
          return '';
        }
      });
    });
  }

  function findStyle(type, language) {
    var style = Object.keys(STYLES[type] || []).find(function (style) {
      var _style = STYLES[type][style];
      return _style ? _style.indexOf(language) >= 0 : false;
    });

    // try to find a default style for the type
    if (!style && language !== 'default') {
      style = findStyle(type, 'default');
    }

    // default to name if no specific style is set
    if (!style && type !== 'Name') {
      style = findStyle('Name', language);
    }

    return style;
  }

  function wrap$1(shell, type, pre, value) {
    value = value();
    if (shell.indexOf('`') === 0 || pre) {
      return shell.replace(/@@doc(Name|Method|Const|Prop|Class|Param): ?([a-zA-Z0-9?_]*)/, value);
    } else {
      return '<dfn class="doc-name doc-name--' + type.toLowerCase() + '">' + value + '</dfn>';
    }
  }

  function replaceDocGlobals(language, pre, content) {
    return content.replace(/@@docGlobal: ?.*`?/g, function (value) {
      switch (language) {
        // languages which should keep the global class
        case 'java':
        case 'csharp':
        case 'kotlin':
        case 'scala':
          return wrap$2(value.replace(/@@docGlobal: ?/, ''), pre);

        // all other languages remove the global class
        default:
          return wrap$2(value.replace(/@@docGlobal: ?[a-zA-Z\d]*\./, ''), pre);
      }
    });
  }

  function wrap$2(value, pre) {
    return pre ? value : '<dfn class="doc-class">' + value + '</dfn>';
  }

  /**
   * Preprocesses the markdown before sending it through marked. This is used to process
   * doc tokens and any other future extensions that we support that don't require being handled
   * during the marked rendering.
   * @param result
   */
  function processDocTokens(result, html, pre) {
    var language = result.originalLanguage || result.language;
    var globals = replaceDocGlobals(language, pre, html);
    var types = replaceDocTypes(language, pre, globals);
    var names = replaceDocNames(language, pre, types);

    return names;
  }

  function methodDoc(code, language) {
    try {
      var json = JSON.parse(code);

      // support language specific overrides
      if (json.languages && json.languages[language]) {
        Object.assign(json, json.languages[language]);
      }

      var md = [];

      if (!json.examplesOnly) {
        if (json.method) {
          md.push(methodHeader(json));
        }
        if (json.desc) {
          md.push(json.desc);
        }

        md.push('```%doc');

        if (json.args) {
          md.push('Parameters:');
          md.push(parameters(json));
        }
        md.push('Return Value:');
        md.push(returnType(json));
        if (json.constraints && json.constraints.length) {
          md.push('Constraints:');
          md.push(json.constraints.join('\n'));
        }
        if (json.errors && json.errors.length) {
          md.push('Errors:');
          md.push(json.errors.join('\n'));
        }
        md.push('```');
      }

      if (json.examples && json.examples.length) {
        md.push('```%doc-block');
        md.push('#### Examples');
        md.push(exampleHeader(json));
        md.push(exampleRows(json));
        md.push('```');
      }

      return md.join('\n');
    } catch (ex) {
      return '`Failed to render %jsonblock: ' + ex.message + '`';
    }
  }

  function hasExampleNames(json) {
    return json.examples && json.examples.filter(function (e) {
      return !!e.name;
    }).length > 0;
  }
  function exampleRows(json) {
    var hasExamples = hasExampleNames(json);
    return json.examples.map(function (v) {
      return exampleRow(json, v, hasExamples);
    }).join('\n');
  }

  function exampleRow(json, example, hasExamples) {
    var md = '';
    if (hasExamples) {
      var name = example.name;
      md = '*' + (name || 'Example') + '*|';
    }

    if (example.args) {
      md += example.args.map(function (arg) {
        return formatExampleValue(arg);
      }).join('|');
    }
    md += '|' + (formatExampleValue(example.returns) || '');
    return md;
  }

  function formatExampleValue(value) {
    return '<code>' + JSON.stringify(value) + '</code>';
  }

  function exampleHeader(json) {
    var line1 = [];
    var line2 = [];
    if (hasExampleNames(json)) {
      line1.push('');
      line2.push('');
    }

    Object.keys(getArgs(json)).forEach(function (key) {
      line1.push(key);
      line2.push('');
    });
    line1.push('Return Value');
    return line1.join('|') + '\n-' + line2.join('|-') + '|-';
  }

  function getArgs(json) {
    return json.args || json.params || json.parameters || {};
  }

  function methodName(json) {
    var globalName = json.global !== false ? '@@docGlobal:' + (json.global || 'Challenge') + '.' : '';
    // if a class is provided, it will always be shown and overrides global
    if (json.class) {
      globalName = '@@docClass:' + json.class + '.';
    }

    return globalName + '@@docMethod:' + json.method;
  }

  function methodHeader(json) {
    var args = Object.keys(getArgs(json)).map(function (key) {
      return '`@@docName:' + key + '`';
    });
    return '### `' + methodName(json) + '`(' + args.join(', ') + ')';
  }

  function parameters(json) {
    var args = getArgs(json);
    var params = Object.keys(args).map(function (key) {
      var arg = args[key];
      var type = typeof arg === 'string' ? arg : arg.type;
      var md = '@@docName:' + key + ': ' + formatDocType(json, type, 'String');
      if (arg.desc) {
        md += ' - ' + arg.desc;
      }

      return md;
    });

    return params.join('\n');
  }

  function returnType(json) {
    if (json.returns) {
      var type = typeof json.returns === 'string' ? json.returns : json.returns.type;
      var md = formatDocType(json, type, 'void');
      if (json.returns.desc) {
        md += ' - ' + json.returns.desc;
      }

      return md;
    }
    return '@@docType:void';
  }

  function formatDocType(json, type, defaultValue) {
    if (json.formatTypes === false) {
      return '<dfn class="doc-type">' + escapeHtml(type) + '</dfn>';
    } else {
      return '@@docType:' + (type || defaultValue || 'null');
    }
  }

  function tableDoc(code) {
    try {
      var json = JSON.parse(code);
      var html = [];

      if (json.table) {
        html.push('<h3>' + json.table + '</h3>');
      }

      if (json.desc) {
        html.push('<p>' + json.desc + '</p>');
      }

      html.push('<table>');
      html.push('<thead><tr><th>Name</th><th>Type</th></tr></thead>');
      if (json.columns) {
        html.push('<tbody>');
        html.push(tableHeaders(json.columns));
        html.push('</tbody>');
      }

      html.push('</table>');
      return html.join('\n');
    } catch (ex) {
      return '`failed to render %jsonblock: `' + ex.message + '`';
    }
  }

  var tableHeaders = function tableHeaders(json) {
    return Object.keys(json).map(function (k) {
      return '<tr><td>' + k + '</td><td>' + json[k] + '</td></tr>';
    }).join('\n');
  };

  function buildRenderer(marked, options, result) {
    var renderer = result.renderer = new marked.Renderer();

    var markedOptions = { renderer: renderer };
    assignMissing(markedOptions, options.marked || {});

    // provide the render method, this will also be used later to render nested blocks
    result.render = function (md) {
      return marked(md, markedOptions);
    };

    setupHeader(renderer, options, result);
    setupCode(options, result);
  }

  /**
   *
   * @param renderer
   */
  function setupHeader(renderer, options, result) {
    // heading extensions
    renderer.heading = function (text, level) {
      // you can set icons via icon::name
      var icon = text.match(/icon::([a-z-]*)/);
      var attributes = '';
      if (icon) {
        // indicate that this icon has been used
        result.icons[icon[1]] = true;

        attributes = ' class="' + options.iconClassPrefix + icon[1] + '"';
        text = text.replace(/icon::[a-z-]*/, '');
      }

      // we track headers 1 - 4 and add ids to them so that we can link to them later if we want to
      if (level < 5) {
        var header = result.headers['h' + level];
        var index = header.length;
        attributes += ' id="h' + level + '_' + index + '"';
        header.push(text);
      }

      return '<h' + level + attributes + '>' + text + '</h' + level + '>';
    };
  }

  /**
   * Handles code blocks in a variety of ways
   * @param options
   * @param result
   */
  function setupCode(options, result) {
    var _code = result.renderer.code;

    // special version of render that will process doc tokens. Needed at times when
    // tokens are nested inside of pre and should be rendered without tags (but their labels should still be processed)
    var render = function render(code, preTokens) {
      if (preTokens && options.docTokens) {
        code = processDocTokens(result, code, true);
      }

      return result.render(code);
    };

    result.renderer.code = function (code, language) {
      if (language) {
        if (language.match(/^if:/)) {
          return matchIfBlockLanguage(result, language) ? render(code) : '';
        } else if (language.match(/^if-not:/)) {
          return matchIfBlockLanguage(result, language) ? '' : render(code);
        } else if (language.match(/^tab:/)) {
          return handleTab(result, code, language);
        } else if (result.extensions.indexOf(language) >= 0) {
          return handleExtension(options, result, code, language);
        } else if (language === '%definitions' || language === '%doc') {
          return wrapInBlockDiv(language, renderDefinitions(result, code, render));
        } else if (language === '%method-doc') {
          return wrapInBlockDiv('docs method-doc', render(methodDoc(code, result.originalLanguage)));
        } else if (language === '%table-doc') {
          return wrapInBlockDiv('docs table-doc', tableDoc(code));
        } else if (language[0] === '%') {
          return wrapInBlockDiv(language, result.render(code));
        }

        // make sure this is a language and not some random tag
        var foundLanguage = options.findLanguage(language.split(':')[0]);

        if (foundLanguage) {
          // if filtering is enabled and this is not the active language then filter it out
          if (options.filterLanguages && foundLanguage !== result.language && result.language) {
            return '';
          }

          // if CodeMirror is provided then highlight using that instead
          if (options.cm) {
            return highlightCM(options, code, foundLanguage, language);
          }
        }
        // process line numbers, if they are set (i.e. ruby:10) in a naive simple way
        else if (options.lineNumbers) {
            code = lineNumbers(code, language);
          }
      }

      return wrapLanguage(options, _code.call(result.renderer, code, language), language);
    };
  }

  function lineNumbers(code, language) {
    var wrapper = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '@@ ';

    var lineNumber = getLineNumber(language);

    // if there are line numbers, then add them now starting at the start index
    if (lineNumber > 0) {
      code = code.split('\n').map(function (line) {
        return '' + wrapper.replace('@@', lineNumber++) + line;
      }).join('\n');
    }

    return code;
  }

  function wrapLanguage(options, code, language) {
    // if we have reached this point then CM isn't enabled and we need to
    if (language && options.languageWrapper) {
      if (typeof options.languageWrapper === 'function') {
        code = options.languageWrapper(code, language);
      } else {
        code = options.languageWrapper.replace('{slot}', code);
      }
    }

    return code;
  }

  function highlightCM(options, code, language, raw) {
    var el = window.document.createElement('div');
    options.cm.runMode(code, options.findMode(language), el);

    var codeHtml = el.innerHTML;
    if (options.lineNumbers) {
      codeHtml = lineNumbers(codeHtml, raw, '<span class="cm-line-number">@@</span>');
    }

    var result = '<pre class="cm-runmode cm-s-' + options.theme + '"><code>' + codeHtml + '</code></pre>';
    return wrapLanguage(options, result, language);
  }

  function getLineNumber(language) {
    var parts = language.split(':');
    return parts.length > 1 ? parseInt(parts[1], 10) : null;
  }

  function wrapInBlockDiv(type, contents) {
    return '<div class="block block--' + type.replace(/^%/, '') + '">' + contents + '</div>';
  }

  function matchIfBlockLanguage(result, language) {
    return language.replace(/^if(-not)?: ?/, '').split(',').indexOf(result.originalLanguage) >= 0;
  }

  function handleTab(result, code, language) {
    // parts should be up to tab:LABEL with language being optional
    var parts = language.split(':');
    var label = parts[1].replace(/\+/g, ' ');
    result.tabs[label] = '' + result.render(code);
    return '';
  }

  /**
   * If the extension value is a function, it will treat it as a render function, otherwise it will
   * assume the extension value is a string and treat it as a template with {code} as the code placeholder.
   * @param options
   * @param code
   * @param language
   */
  function handleExtension(options, result, code, language) {
    var ext = options.extensions[language];

    if (typeof ext.code === 'function') {
      return ext.code(code, options);
    } else {
      return ext.code.replace('{code}', code);
    }
  }

  /**
   *
   * @param result
   * @param code
   * @returns {string}
   */
  function renderDefinitions(result, code, render) {
    var html = '<dl>';
    if (code) {
      code.split('\n').forEach(function (line) {
        if (line.match(/^#/)) {
          html += render(line);
        } else if (line.match(/: *$/)) {
          html += '<dt>' + line.replace(/:$/, '') + '</dt>';
        } else {
          // if line starts with 4 white spaces, a tab or a ` block, then consider it a pre tag and don't render dfn
          html += '<dd>' + render(line, !!line.match(/^(\t|\s{4}|`)/)) + '</dd>';
        }
      });
    }

    return html + '</dl>';
  }

  var defaultOptions = {
    // these are the options passed to marked directly
    marked: {},
    // languages that should be treated as extensions. You can configure how each gets handled
    extensions: {
      mermaid: {
        // code handler
        code: function code(_code) {
          return '<div class="mermaid">' + _code + '</div>';
        },
        // will lazy load script automatically
        src: 'https://cdn.rawgit.com/knsv/mermaid/6.0.0/dist/mermaid.min.js',
        afterRender: function afterRender() {
          return window.mermaid && window.mermaid.init();
        }
      }
    },
    // set to a method that that will receive a language and return the mapped
    // name. if no name is returned, it is assumed that the language is an extension.
    // The default method only handles a default set of languages.
    findLanguage: function findLanguage(language) {
      return defaultLanguages.indexOf(language) >= 0 ? language : null;
    },

    // used with CodeMirror.runMode, used to find the mode of the language. By default will just return the language
    findMode: function findMode(language) {
      return language;
    },

    // The highlight theme to add, only used with cm runMode
    theme: 'neo',

    // you can set icons within headers using icon::ICONNAME, this setting determines the icon class prefix used
    iconClassPrefix: 'icon-',

    // set to a value that should wrap standard languages. Use "{slot}" to indicate where the code should be inserted: i.e.
    // '<div class="tab">{code}</div>'. Can also be a function which takes (code, language) as its parameters.
    languageWrapper: null,

    // the language that should be used for filtering out all other languages
    language: null,

    // true if languages should be filtered
    filterLanguages: true,

    // if the language value is not set, or is set to a value not found within the markdown, this
    // setting will default the language to the first one found within the markdown.
    defaultLanguageToFirst: true,

    // true if line numbers should be added to highlighted code (if a starting line number is configured for the block)
    lineNumbers: true,

    // css class used to display a line number gutter, which can be used to try to create an opacity layer
    // which will style the cm-number values differently. Set to null to disable adding a gutter.
    lineNumbersGutter: 'cm-runmode-linenumbers-gutter',

    // true if doc tokens should be parsed
    docTokens: true,

    // if set to an instance of js-yaml, it will process meta data at the top of the markdown.
    jsYaml: null,

    // If set to CodeMirror class, it will use CodeMirror.runMode to process syntax highlighting
    // Note: make sure to import codemirror/addon/runmode/runmode.js first.
    cm: null,

    // set to automatically load a CM language. This is provided by default but you can override if you wish
    // to override. This option is only used if both cm and loadScript are set.
    // You can set this to null if you do not wish or need to load languages dynamically
    loadCMLanguage: function loadCMLanguage(language, options) {
      return options.loadScript('//cdnjs.cloudflare.com/ajax/libs/codemirror/' + options.cm.version + '/mode/' + language + '/' + language + '.min.js');
    },

    // If you wish to support loading external extension scripts, you should set this to a
    // function that takes a url and returns a promise. Note that this function will need to be responsible
    // for not reloading the same scripts if requested more than once, this library does not take care of caching.
    loadScript: null,

    // if set to a function, will be called back after all external scripts have loaded
    onLoaded: null
  };
  var defaultLanguages = ['c', 'clojure', 'coffeescript', 'cpp', 'csharp', 'elixir', 'erlang', 'fsharp', 'go', 'groovy', 'haskell', 'java', 'javascript', 'kotlin', 'objc', 'ocaml', 'php', 'python', 'r', 'ruby', 'scala', 'shell', 'solidity', 'sql', 'swift', 'typescript'];

  /**
   * Processes the markdown using marked along with the many extensions this library provides
   * @param marked The marked library, must be passed in since it is not included within this library as a dependency
   * @param markdown The markdown to process
   * @param options The extended set of options, as well as marked options. See defaultOptions for more details.
   * @returns {{originalLanguage, language, languages: [], extensions: [], tabs: {}, headers: {h1: Array, h2: Array, h3: Array, h4: Array}, icons: [], raw: *, preprocessed: *}}
   */
  function process(marked, markdown) {
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

    assignMissing(options, defaultOptions);

    var result = {
      originalLanguage: options.language,
      language: options.language,
      languages: {},
      extensions: {},
      tabs: {},
      headers: { h1: [], h2: [], h3: [], h4: [] },
      icons: {},
      raw: markdown,
      preprocessed: markdown
    };

    if (options.jsYaml) {
      processMeta(options, result);
    }

    processBlocks(options, result);
    if (options.defaultLanguageToFirst) {
      processLanguage(result);
    }

    buildRenderer(marked, options, result);

    var html = null;
    result.html = function () {
      return html || (html = render(options, result));
    };
    result.afterRender = afterRenderFn(options, result);

    // convert objects which have been acting as basic sets to an array
    ['languages', 'extensions', 'icons'].forEach(function (key) {
      return result[key] = Object.keys(result[key]);
    });

    if (options.loadScript) {
      processExternalScripts(options, result);
    }

    return result;
  }

  function render(options, result) {
    var html = result.render(result.preprocessed);

    if (options.docTokens) {
      html = processDocTokens(result, html);
    }

    return html;
  }

  /**
   * Creates the afterRender function that is added to the result, which can be called once the
   * processed html has been added to the DOM to initialize any extensions that may have been loaded.
   * @param options
   * @param result
   * @returns {Function}
   */
  function afterRenderFn(options, result) {
    return function () {
      var _arguments = arguments;

      result.extensions.forEach(function (ext) {
        var config = options.extensions[ext];
        if (config && config.afterRender) {
          config.afterRender.apply(result, _arguments);
        }
      });
    };
  }

  /**
   * Will loop through extensions and languages and try to dynamically load scripts.
   * @param options
   * @param result
   */
  function processExternalScripts(options, result) {
    var promises = [];
    result.extensions.forEach(function (ext) {
      var config = options.extensions[ext];
      if (config && config.src) {
        promises.push(options.loadScript(config.src));
      }
    });

    // automatically load the CM language that is shown
    if (options.cm && options.loadCMLanguage) {
      // if we filter languages then we only need to load the one being shown
      if (options.filterLanguages && result.language) {
        promises.push(options.loadCMLanguage(result.language, options));
      }
      // otherwise we need to load them all
      else if (!options.filterLanguages && result.languages.length) {
          result.languages.forEach(function (language) {
            promises.push(options.loadCMLanguage(language, options));
          });
        }
    }

    Promise.all(promises).then(function () {
      if (options.onLoaded) {
        options.onLoaded(result);
      }
    });
  }

  /**
   * Processes yaml content at the top of the markdown, marked starting by a --- and ending with a ...
   * @param options
   * @param result
   */
  function processMeta(options, result) {
    result.preprocessed = result.preprocessed.replace(/^---\r?\n(.*\r?\n)*\.{3}\s*(\r?\n){2}?/, function (meta) {
      var yaml = meta.replace(/^---\r?\n/, '').replace(/\r?\n\.\.\. *\r?\n?/, '');
      result.meta = options.jsYaml.safeLoad(yaml);
      return '';
    });
  }

  /**
   * if no language was provided, or the one provided is not in the list of supported languages,
   * then switch to the first language found
   * @param result
   */
  function processLanguage(result) {
    if (!result.language || !result.languages[result.language]) {
      result.language = Object.keys(result.languages)[0];
    }
  }

  /**
   * Loops through all ``` style blocks and figures out which are languages and which are
   * possibly extensions
   * @param markdown
   * @param options
   * @param result
   */
  function processBlocks(options, result) {
    var blocks = result.preprocessed.match(/^(```|~~~) ?(.*) *$/gm) || [];
    blocks = blocks.map(function (m) {
      return m.replace(/(```|~~~) ?/g, '');
    });

    // loop through each block and track which are languages and which are extensions
    blocks.forEach(function (text) {
      if (text) {
        text = text.replace(/^if(-not)?: ?/, '').split(':')[0];

        text.split(',').forEach(function (name) {
          // % is a special token, we know these aren't either languages or extensions
          if (name.indexOf('%') === -1) {
            // if an extension has been defined for the language, track it now
            if (options.extensions[name]) {
              result.extensions[name] = true;
            } else {
              var language = options.findLanguage(name);
              if (language) {
                result.languages[language] = true;
              }
            }
          }
        });
      }
    });
  }

  exports.defaultOptions = defaultOptions;
  exports.defaultLanguages = defaultLanguages;
  exports.process = process;

  return exports;

}({}));
//# sourceMappingURL=marked-extensions.js.map
;
/*
 * angular-loading-bar
 *
 * intercepts XHR requests and creates a loading bar.
 * Based on the excellent nprogress work by rstacruz (more info in readme)
 *
 * (c) 2013 Wes Cruver
 * License: MIT
 */


(function() {

'use strict';

// Alias the loading bar for various backwards compatibilities since the project has matured:
angular.module('angular-loading-bar', ['cfp.loadingBarInterceptor']);
angular.module('chieffancypants.loadingBar', ['cfp.loadingBarInterceptor']);


/**
 * loadingBarInterceptor service
 *
 * Registers itself as an Angular interceptor and listens for XHR requests.
 */
angular.module('cfp.loadingBarInterceptor', ['cfp.loadingBar'])
  .config(['$httpProvider', function ($httpProvider) {

    var interceptor = ['$q', '$cacheFactory', '$timeout', '$rootScope', '$log', 'cfpLoadingBar', function ($q, $cacheFactory, $timeout, $rootScope, $log, cfpLoadingBar) {

      /**
       * The total number of requests made
       */
      var reqsTotal = 0;

      /**
       * The number of requests completed (either successfully or not)
       */
      var reqsCompleted = 0;

      /**
       * The amount of time spent fetching before showing the loading bar
       */
      var latencyThreshold = cfpLoadingBar.latencyThreshold;

      /**
       * $timeout handle for latencyThreshold
       */
      var startTimeout;


      /**
       * calls cfpLoadingBar.complete() which removes the
       * loading bar from the DOM.
       */
      function setComplete() {
        $timeout.cancel(startTimeout);
        cfpLoadingBar.complete();
        reqsCompleted = 0;
        reqsTotal = 0;
      }

      /**
       * Determine if the response has already been cached
       * @param  {Object}  config the config option from the request
       * @return {Boolean} retrns true if cached, otherwise false
       */
      function isCached(config) {
        var cache;
        var defaultCache = $cacheFactory.get('$http');
        var defaults = $httpProvider.defaults;

        // Choose the proper cache source. Borrowed from angular: $http service
        if ((config.cache || defaults.cache) && config.cache !== false &&
          (config.method === 'GET' || config.method === 'JSONP')) {
            cache = angular.isObject(config.cache) ? config.cache
              : angular.isObject(defaults.cache) ? defaults.cache
              : defaultCache;
        }

        var cached = cache !== undefined ?
          cache.get(config.url) !== undefined : false;

        if (config.cached !== undefined && cached !== config.cached) {
          return config.cached;
        }
        config.cached = cached;
        return cached;
      }


      return {
        'request': function(config) {
          // Check to make sure this request hasn't already been cached and that
          // the requester didn't explicitly ask us to ignore this request:
          if (!config.ignoreLoadingBar && !isCached(config)) {
            $rootScope.$broadcast('cfpLoadingBar:loading', {url: config.url});
            if (reqsTotal === 0) {
              startTimeout = $timeout(function() {
                cfpLoadingBar.start();
              }, latencyThreshold);
            }
            reqsTotal++;
            cfpLoadingBar.set(reqsCompleted / reqsTotal);
          }
          return config;
        },

        'response': function(response) {
          if (!response || !response.config) {
            $log.error('Broken interceptor detected: Config object not supplied in response:\n https://github.com/chieffancypants/angular-loading-bar/pull/50');
            return response;
          }

          if (!response.config.ignoreLoadingBar && !isCached(response.config)) {
            reqsCompleted++;
            $rootScope.$broadcast('cfpLoadingBar:loaded', {url: response.config.url, result: response});
            if (reqsCompleted >= reqsTotal) {
              setComplete();
            } else {
              cfpLoadingBar.set(reqsCompleted / reqsTotal);
            }
          }
          return response;
        },

        'responseError': function(rejection) {
          if (!rejection || !rejection.config) {
            $log.error('Broken interceptor detected: Config object not supplied in rejection:\n https://github.com/chieffancypants/angular-loading-bar/pull/50');
            return $q.reject(rejection);
          }

          if (!rejection.config.ignoreLoadingBar && !isCached(rejection.config)) {
            reqsCompleted++;
            $rootScope.$broadcast('cfpLoadingBar:loaded', {url: rejection.config.url, result: rejection});
            if (reqsCompleted >= reqsTotal) {
              setComplete();
            } else {
              cfpLoadingBar.set(reqsCompleted / reqsTotal);
            }
          }
          return $q.reject(rejection);
        }
      };
    }];

    $httpProvider.interceptors.push(interceptor);
  }]);


/**
 * Loading Bar
 *
 * This service handles adding and removing the actual element in the DOM.
 * Generally, best practices for DOM manipulation is to take place in a
 * directive, but because the element itself is injected in the DOM only upon
 * XHR requests, and it's likely needed on every view, the best option is to
 * use a service.
 */
angular.module('cfp.loadingBar', [])
  .provider('cfpLoadingBar', function() {

    this.autoIncrement = true;
    this.includeSpinner = true;
    this.includeBar = true;
    this.latencyThreshold = 100;
    this.startSize = 0.02;
    this.parentSelector = 'body';
    this.spinnerTemplate = '<div id="loading-bar-spinner"><div class="spinner-icon"></div></div>';
    this.loadingBarTemplate = '<div id="loading-bar"><div class="bar"><div class="peg"></div></div></div>';

    this.$get = ['$injector', '$document', '$timeout', '$rootScope', function ($injector, $document, $timeout, $rootScope) {
      var $animate;
      var $parentSelector = this.parentSelector,
        loadingBarContainer = angular.element(this.loadingBarTemplate),
        loadingBar = loadingBarContainer.find('div').eq(0),
        spinner = angular.element(this.spinnerTemplate);

      var incTimeout,
        completeTimeout,
        started = false,
        status = 0;

      var autoIncrement = this.autoIncrement;
      var includeSpinner = this.includeSpinner;
      var includeBar = this.includeBar;
      var startSize = this.startSize;

      /**
       * Inserts the loading bar element into the dom, and sets it to 2%
       */
      function _start() {
        if (!$animate) {
          $animate = $injector.get('$animate');
        }

        $timeout.cancel(completeTimeout);

        // do not continually broadcast the started event:
        if (started) {
          return;
        }

        var document = $document[0];
        var parent = document.querySelector ?
          document.querySelector($parentSelector)
          : $document.find($parentSelector)[0]
        ;

        if (! parent) {
          parent = document.getElementsByTagName('body')[0];
        }

        var $parent = angular.element(parent);
        var $after = parent.lastChild && angular.element(parent.lastChild);

        $rootScope.$broadcast('cfpLoadingBar:started');
        started = true;

        if (includeBar) {
          $animate.enter(loadingBarContainer, $parent, $after);
        }

        if (includeSpinner) {
          $animate.enter(spinner, $parent, loadingBarContainer);
        }

        _set(startSize);
      }

      /**
       * Set the loading bar's width to a certain percent.
       *
       * @param n any value between 0 and 1
       */
      function _set(n) {
        if (!started) {
          return;
        }
        var pct = (n * 100) + '%';
        loadingBar.css('width', pct);
        status = n;

        // increment loadingbar to give the illusion that there is always
        // progress but make sure to cancel the previous timeouts so we don't
        // have multiple incs running at the same time.
        if (autoIncrement) {
          $timeout.cancel(incTimeout);
          incTimeout = $timeout(function() {
            _inc();
          }, 250);
        }
      }

      /**
       * Increments the loading bar by a random amount
       * but slows down as it progresses
       */
      function _inc() {
        if (_status() >= 1) {
          return;
        }

        var rnd = 0;

        // TODO: do this mathmatically instead of through conditions

        var stat = _status();
        if (stat >= 0 && stat < 0.25) {
          // Start out between 3 - 6% increments
          rnd = (Math.random() * (5 - 3 + 1) + 3) / 100;
        } else if (stat >= 0.25 && stat < 0.65) {
          // increment between 0 - 3%
          rnd = (Math.random() * 3) / 100;
        } else if (stat >= 0.65 && stat < 0.9) {
          // increment between 0 - 2%
          rnd = (Math.random() * 2) / 100;
        } else if (stat >= 0.9 && stat < 0.99) {
          // finally, increment it .5 %
          rnd = 0.005;
        } else {
          // after 99%, don't increment:
          rnd = 0;
        }

        var pct = _status() + rnd;
        _set(pct);
      }

      function _status() {
        return status;
      }

      function _completeAnimation() {
        status = 0;
        started = false;
      }

      function _complete() {
        if (!$animate) {
          $animate = $injector.get('$animate');
        }

        $rootScope.$broadcast('cfpLoadingBar:completed');
        _set(1);

        $timeout.cancel(completeTimeout);

        // Attempt to aggregate any start/complete calls within 500ms:
        completeTimeout = $timeout(function() {
          var promise = $animate.leave(loadingBarContainer, _completeAnimation);
          if (promise && promise.then) {
            promise.then(_completeAnimation);
          }
          $animate.leave(spinner);
        }, 500);
      }

      return {
        start            : _start,
        set              : _set,
        status           : _status,
        inc              : _inc,
        complete         : _complete,
        autoIncrement    : this.autoIncrement,
        includeSpinner   : this.includeSpinner,
        latencyThreshold : this.latencyThreshold,
        parentSelector   : this.parentSelector,
        startSize        : this.startSize
      };


    }];     //
  });       // wtf javascript. srsly
})();       //
;
/*!
 * js-data
 * @version 2.8.2 - Homepage <http://www.js-data.io/>
 * @author Jason Dobry <jason.dobry@gmail.com>
 * @copyright (c) 2014-2015 Jason Dobry 
 * @license MIT <https://github.com/js-data/js-data/blob/master/LICENSE>
 * 
 * @overview Robust framework-agnostic data store.
 */
(function webpackUniversalModuleDefinition(root, factory) {
	if(typeof exports === 'object' && typeof module === 'object')
		module.exports = factory();
	else if(typeof define === 'function' && define.amd)
		define([], factory);
	else if(typeof exports === 'object')
		exports["JSData"] = factory();
	else
		root["JSData"] = factory();
})(this, function() {
return /******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};

/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {

/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId])
/******/ 			return installedModules[moduleId].exports;

/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			exports: {},
/******/ 			id: moduleId,
/******/ 			loaded: false
/******/ 		};

/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);

/******/ 		// Flag the module as loaded
/******/ 		module.loaded = true;

/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}


/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;

/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;

/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";

/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(0);
/******/ })
/************************************************************************/
/******/ ([
/* 0 */
/***/ function(module, exports, __webpack_require__) {

	var _datastoreIndex = __webpack_require__(1);

	var _utils = __webpack_require__(2);

	var _errors = __webpack_require__(3);

	/**
	 * The library export.
	 *   - window.JSData
	 *   - require('js-data')
	 *   - define(['js-data', function (JSData) { ... }]);
	 *   - import JSData from 'js-data'
	 */
	module.exports = {
	  DS: _datastoreIndex['default'],
	  DSUtils: _utils['default'],
	  DSErrors: _errors['default'],
	  createStore: function createStore(options) {
	    return new _datastoreIndex['default'](options);
	  },
	  version: {
	    full: '2.8.2',
	    major: parseInt('2', 10),
	    minor: parseInt('8', 10),
	    patch: parseInt('2', 10),
	    alpha:  true ? 'false' : false,
	    beta:  true ? 'false' : false
	  }
	};

/***/ },
/* 1 */
/***/ function(module, exports, __webpack_require__) {

	var _createClass = (function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ('value' in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; })();

	function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError('Cannot call a class as a function'); } }

	/* jshint eqeqeq:false */

	var _utils = __webpack_require__(2);

	var _errors = __webpack_require__(3);

	var _sync_methodsIndex = __webpack_require__(30);

	var _async_methodsIndex = __webpack_require__(36);

	function lifecycleNoopCb(resource, attrs, cb) {
	  cb(null, attrs);
	}

	function lifecycleNoop(resource, attrs) {
	  return attrs;
	}

	function compare(_x, _x2, _x3, _x4) {
	  var _again = true;

	  _function: while (_again) {
	    var orderBy = _x,
	        index = _x2,
	        a = _x3,
	        b = _x4;
	    def = cA = cB = undefined;
	    _again = false;

	    var def = orderBy[index];
	    var cA = _utils['default'].get(a, def[0]);
	    var cB = _utils['default'].get(b, def[0]);
	    if (_utils['default']._s(cA)) {
	      cA = _utils['default'].upperCase(cA);
	    }
	    if (_utils['default']._s(cB)) {
	      cB = _utils['default'].upperCase(cB);
	    }
	    if (def[1] === 'DESC') {
	      if (cB < cA) {
	        return -1;
	      } else if (cB > cA) {
	        return 1;
	      } else {
	        if (index < orderBy.length - 1) {
	          _x = orderBy;
	          _x2 = index + 1;
	          _x3 = a;
	          _x4 = b;
	          _again = true;
	          continue _function;
	        } else {
	          return 0;
	        }
	      }
	    } else {
	      if (cA < cB) {
	        return -1;
	      } else if (cA > cB) {
	        return 1;
	      } else {
	        if (index < orderBy.length - 1) {
	          _x = orderBy;
	          _x2 = index + 1;
	          _x3 = a;
	          _x4 = b;
	          _again = true;
	          continue _function;
	        } else {
	          return 0;
	        }
	      }
	    }
	  }
	}

	var Defaults = (function () {
	  function Defaults() {
	    _classCallCheck(this, Defaults);
	  }

	  _createClass(Defaults, [{
	    key: 'errorFn',
	    value: function errorFn(a, b) {
	      if (this.error && typeof this.error === 'function') {
	        try {
	          if (typeof a === 'string') {
	            throw new Error(a);
	          } else {
	            throw a;
	          }
	        } catch (err) {
	          a = err;
	        }
	        this.error(this.name || null, a || null, b || null);
	      }
	    }
	  }]);

	  return Defaults;
	})();

	var defaultsPrototype = Defaults.prototype;

	defaultsPrototype.actions = {};
	defaultsPrototype.afterCreate = lifecycleNoopCb;
	defaultsPrototype.afterCreateCollection = lifecycleNoop;
	defaultsPrototype.afterCreateInstance = lifecycleNoop;
	defaultsPrototype.afterDestroy = lifecycleNoopCb;
	defaultsPrototype.afterEject = lifecycleNoop;
	defaultsPrototype.afterFind = lifecycleNoopCb;
	defaultsPrototype.afterFindAll = lifecycleNoopCb;
	defaultsPrototype.afterInject = lifecycleNoop;
	defaultsPrototype.afterLoadRelations = lifecycleNoopCb;
	defaultsPrototype.afterReap = lifecycleNoop;
	defaultsPrototype.afterUpdate = lifecycleNoopCb;
	defaultsPrototype.afterValidate = lifecycleNoopCb;
	defaultsPrototype.allowSimpleWhere = true;
	defaultsPrototype.basePath = '';
	defaultsPrototype.beforeCreate = lifecycleNoopCb;
	defaultsPrototype.beforeCreateCollection = lifecycleNoop;
	defaultsPrototype.beforeCreateInstance = lifecycleNoop;
	defaultsPrototype.beforeDestroy = lifecycleNoopCb;
	defaultsPrototype.beforeEject = lifecycleNoop;
	defaultsPrototype.beforeInject = lifecycleNoop;
	defaultsPrototype.beforeReap = lifecycleNoop;
	defaultsPrototype.beforeUpdate = lifecycleNoopCb;
	defaultsPrototype.beforeValidate = lifecycleNoopCb;
	defaultsPrototype.bypassCache = false;
	defaultsPrototype.cacheResponse = !!_utils['default'].w;
	defaultsPrototype.csp = false;
	defaultsPrototype.clearEmptyQueries = true;
	defaultsPrototype.computed = {};
	defaultsPrototype.defaultAdapter = 'http';
	defaultsPrototype.debug = false;
	defaultsPrototype.defaultValues = {};
	defaultsPrototype.eagerEject = false;
	// TODO: Implement eagerInject in DS#create
	defaultsPrototype.eagerInject = false;
	defaultsPrototype.endpoint = '';
	defaultsPrototype.error = console ? function (a, b, c) {
	  return console[typeof console.error === 'function' ? 'error' : 'log'](a, b, c);
	} : false;
	defaultsPrototype.errorHandler = function () {
	  return _utils['default'].Promise.reject(arguments[0]);
	};
	defaultsPrototype.fallbackAdapters = ['http'];
	defaultsPrototype.findStrictCache = false;
	defaultsPrototype.idAttribute = 'id';
	defaultsPrototype.ignoredChanges = [/\$/];
	defaultsPrototype.instanceEvents = !!_utils['default'].w;
	defaultsPrototype.keepChangeHistory = false;
	defaultsPrototype.linkRelations = !!_utils['default'].w;
	defaultsPrototype.log = console ? function (a, b, c, d, e) {
	  return console[typeof console.info === 'function' ? 'info' : 'log'](a, b, c, d, e);
	} : false;

	defaultsPrototype.logFn = function (a, b, c, d) {
	  var _this = this;
	  if (_this.debug && _this.log && typeof _this.log === 'function') {
	    _this.log(_this.name || null, a || null, b || null, c || null, d || null);
	  }
	};

	defaultsPrototype.maxAge = false;
	defaultsPrototype.methods = {};
	defaultsPrototype.notify = !!_utils['default'].w;
	defaultsPrototype.omit = [];
	defaultsPrototype.onConflict = 'merge';
	defaultsPrototype.reapAction = _utils['default'].w ? 'inject' : 'none';
	defaultsPrototype.reapInterval = _utils['default'].w ? 30000 : false;
	defaultsPrototype.relationsEnumerable = false;
	defaultsPrototype.resetHistoryOnInject = true;
	defaultsPrototype.returnMeta = false;
	defaultsPrototype.scopes = {};
	defaultsPrototype.strategy = 'single';
	defaultsPrototype.upsert = !!_utils['default'].w;
	defaultsPrototype.useClass = true;
	defaultsPrototype.useFilter = false;
	defaultsPrototype.validate = lifecycleNoopCb;
	defaultsPrototype.watchChanges = !!_utils['default'].w;

	var escapeRegExp = /([.*+?^=!:${}()|[\]\/\\])/g;
	var percentRegExp = /%/g;
	var underscoreRegExp = /_/g;

	function escape(pattern) {
	  return pattern.replace(escapeRegExp, '\\$1');
	}

	function like(pattern, flags) {
	  return new RegExp('^' + escape(pattern).replace(percentRegExp, '.*').replace(underscoreRegExp, '.') + '$', flags);
	}

	defaultsPrototype.defaultFilter = function (collection, resourceName, params, options) {
	  var filtered = collection;
	  var where = null;
	  var reserved = {
	    skip: '',
	    offset: '',
	    where: '',
	    limit: '',
	    orderBy: '',
	    sort: ''
	  };

	  params = params || {};
	  options = options || {};

	  if (_utils['default']._o(params.where)) {
	    where = params.where;
	  } else {
	    where = {};
	  }

	  if (options.allowSimpleWhere) {
	    _utils['default'].forOwn(params, function (value, key) {
	      if (!(key in reserved) && !(key in where)) {
	        where[key] = {
	          '==': value
	        };
	      }
	    });
	  }

	  if (_utils['default'].isEmpty(where)) {
	    where = null;
	  }

	  if (where) {
	    filtered = _utils['default'].filter(filtered, function (attrs) {
	      var first = true;
	      var keep = true;
	      _utils['default'].forOwn(where, function (clause, field) {
	        if (!_utils['default']._o(clause)) {
	          clause = {
	            '==': clause
	          };
	        }
	        _utils['default'].forOwn(clause, function (term, op) {
	          var expr = undefined;
	          var isOr = op[0] === '|';
	          var val = _utils['default'].get(attrs, field);
	          op = isOr ? op.substr(1) : op;
	          if (op === '==') {
	            expr = val == term; // eslint-disable-line
	          } else if (op === '===') {
	              expr = val === term;
	            } else if (op === '!=') {
	              expr = val != term; // eslint-disable-line
	            } else if (op === '!==') {
	                expr = val !== term;
	              } else if (op === '>') {
	                expr = val > term;
	              } else if (op === '>=') {
	                expr = val >= term;
	              } else if (op === '<') {
	                expr = val < term;
	              } else if (op === '<=') {
	                expr = val <= term;
	              } else if (op === 'isectEmpty') {
	                expr = !_utils['default'].intersection(val || [], term || []).length;
	              } else if (op === 'isectNotEmpty') {
	                expr = _utils['default'].intersection(val || [], term || []).length;
	              } else if (op === 'in') {
	                if (_utils['default']._s(term)) {
	                  expr = term.indexOf(val) !== -1;
	                } else {
	                  expr = _utils['default'].contains(term, val);
	                }
	              } else if (op === 'notIn') {
	                if (_utils['default']._s(term)) {
	                  expr = term.indexOf(val) === -1;
	                } else {
	                  expr = !_utils['default'].contains(term, val);
	                }
	              } else if (op.indexOf('like') === 0) {
	                expr = like(term, op.substr(4)).exec(val) !== null;
	              } else if (op.indexOf('notLike') === 0) {
	                expr = like(term, op.substr(7)).exec(val) === null;
	              } else if (op === 'contains') {
	                if (_utils['default']._s(val)) {
	                  expr = val.indexOf(term) !== -1;
	                } else {
	                  expr = _utils['default'].contains(val, term);
	                }
	              } else if (op === 'notContains') {
	                if (_utils['default']._s(val)) {
	                  expr = val.indexOf(term) === -1;
	                } else {
	                  expr = !_utils['default'].contains(val, term);
	                }
	              }
	          if (expr !== undefined) {
	            keep = first ? expr : isOr ? keep || expr : keep && expr;
	          }
	          first = false;
	        });
	      });
	      return keep;
	    });
	  }

	  var orderBy = null;

	  if (_utils['default']._s(params.orderBy)) {
	    orderBy = [[params.orderBy, 'ASC']];
	  } else if (_utils['default']._a(params.orderBy)) {
	    orderBy = params.orderBy;
	  }

	  if (!orderBy && _utils['default']._s(params.sort)) {
	    orderBy = [[params.sort, 'ASC']];
	  } else if (!orderBy && _utils['default']._a(params.sort)) {
	    orderBy = params.sort;
	  }

	  // Apply 'orderBy'
	  if (orderBy) {
	    (function () {
	      var index = 0;
	      _utils['default'].forEach(orderBy, function (def, i) {
	        if (_utils['default']._s(def)) {
	          orderBy[i] = [def, 'ASC'];
	        } else if (!_utils['default']._a(def)) {
	          throw new _errors['default'].IA('DS.filter("' + resourceName + '"[, params][, options]): ' + _utils['default'].toJson(def) + ': Must be a string or an array!', {
	            params: {
	              'orderBy[i]': {
	                actual: typeof def,
	                expected: 'string|array'
	              }
	            }
	          });
	        }
	      });
	      filtered = _utils['default'].sort(filtered, function (a, b) {
	        return compare(orderBy, index, a, b);
	      });
	    })();
	  }

	  var limit = _utils['default']._n(params.limit) ? params.limit : null;
	  var skip = null;

	  if (_utils['default']._n(params.skip)) {
	    skip = params.skip;
	  } else if (_utils['default']._n(params.offset)) {
	    skip = params.offset;
	  }

	  // Apply 'limit' and 'skip'
	  if (limit && skip) {
	    filtered = _utils['default'].slice(filtered, skip, Math.min(filtered.length, skip + limit));
	  } else if (_utils['default']._n(limit)) {
	    filtered = _utils['default'].slice(filtered, 0, Math.min(filtered.length, limit));
	  } else if (_utils['default']._n(skip)) {
	    if (skip < filtered.length) {
	      filtered = _utils['default'].slice(filtered, skip);
	    } else {
	      filtered = [];
	    }
	  }

	  return filtered === collection ? filtered.slice() : filtered;
	};

	var DS = (function () {
	  function DS(options) {
	    _classCallCheck(this, DS);

	    var _this = this;
	    options = options || {};

	    _this.store = {};
	    _this.definitions = {};
	    _this.adapters = {};
	    _this.defaults = new Defaults();
	    _this.observe = _utils['default'].observe;
	    _utils['default'].forOwn(options, function (v, k) {
	      if (k === 'omit') {
	        _this.defaults.omit = v.concat(Defaults.prototype.omit);
	      } else {
	        _this.defaults[k] = v;
	      }
	    });

	    var P = _utils['default'].Promise;

	    if (P && !P.prototype.spread) {
	      P.prototype.spread = function (cb) {
	        return this.then(function (arr) {
	          return cb.apply(this, arr);
	        });
	      };
	    }

	    _utils['default'].Events(_this);
	  }

	  _createClass(DS, [{
	    key: 'getAdapterName',
	    value: function getAdapterName(options) {
	      var errorIfNotExist = false;
	      options = options || {};
	      if (_utils['default']._s(options)) {
	        errorIfNotExist = true;
	        options = {
	          adapter: options
	        };
	      }
	      if (this.adapters[options.adapter]) {
	        return options.adapter;
	      } else if (errorIfNotExist) {
	        throw new Error(options.adapter + ' is not a registered adapter!');
	      } else {
	        return options.defaultAdapter;
	      }
	    }
	  }, {
	    key: 'getAdapter',
	    value: function getAdapter(options) {
	      options = options || {};
	      return this.adapters[this.getAdapterName(options)];
	    }
	  }, {
	    key: 'registerAdapter',
	    value: function registerAdapter(name, Adapter, options) {
	      var _this = this;
	      options = options || {};
	      if (_utils['default'].isFunction(Adapter)) {
	        _this.adapters[name] = new Adapter(options);
	      } else {
	        _this.adapters[name] = Adapter;
	      }
	      if (options['default']) {
	        _this.defaults.defaultAdapter = name;
	      }
	    }
	  }, {
	    key: 'is',
	    value: function is(resourceName, instance) {
	      var definition = this.definitions[resourceName];
	      if (!definition) {
	        throw new _errors['default'].NER(resourceName);
	      }
	      return instance instanceof definition[definition['class']];
	    }
	  }, {
	    key: 'clear',
	    value: function clear() {
	      var _this2 = this;

	      var ejected = {};
	      _utils['default'].forOwn(this.definitions, function (definition) {
	        var name = definition.name;
	        ejected[name] = definition.ejectAll();
	        _this2.store[name].completedQueries = {};
	        _this2.store[name].queryData = {};
	      });
	      return ejected;
	    }
	  }, {
	    key: 'errorFn',
	    value: function errorFn() {
	      for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
	        args[_key] = arguments[_key];
	      }

	      var options = args[args.length - 1];
	      var defaultHandler = this.defaults.errorHandler;
	      var errorHandler = options ? options.errorHandler : defaultHandler;
	      errorHandler = errorHandler || defaultHandler;
	      return function (err) {
	        return errorHandler.apply(undefined, [err].concat(args));
	      };
	    }
	  }]);

	  return DS;
	})();

	var dsPrototype = DS.prototype;

	dsPrototype.getAdapterName.shorthand = false;
	dsPrototype.getAdapter.shorthand = false;
	dsPrototype.registerAdapter.shorthand = false;
	dsPrototype.errors = _errors['default'];
	dsPrototype.utils = _utils['default'];

	function addMethods(target, obj) {
	  _utils['default'].forOwn(obj, function (v, k) {
	    target[k] = v;
	    target[k].before = function (fn) {
	      var orig = target[k];
	      target[k] = function () {
	        for (var _len2 = arguments.length, args = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
	          args[_key2] = arguments[_key2];
	        }

	        return orig.apply(this, fn.apply(this, args) || args);
	      };
	    };
	  });
	}

	addMethods(dsPrototype, _sync_methodsIndex['default']);
	addMethods(dsPrototype, _async_methodsIndex['default']);

	exports['default'] = DS;

/***/ },
/* 2 */
/***/ function(module, exports, __webpack_require__) {

	/* jshint eqeqeq:false */

	/**
	 * Mix of ES6 and CommonJS module imports because the interop of Babel + Webpack + ES6 modules + CommonJS isn't very good.
	 */

	var _errors = __webpack_require__(3);

	var BinaryHeap = __webpack_require__(4);
	var forEach = __webpack_require__(5);
	var slice = __webpack_require__(6);
	var forOwn = __webpack_require__(7);
	var contains = __webpack_require__(10);
	var deepMixIn = __webpack_require__(12);
	var pascalCase = __webpack_require__(14);
	var remove = __webpack_require__(21);
	var pick = __webpack_require__(22);
	var _keys = __webpack_require__(23);
	var sort = __webpack_require__(24);
	var upperCase = __webpack_require__(19);
	var get = __webpack_require__(25);
	var set = __webpack_require__(27);
	var observe = __webpack_require__(29);
	var w = undefined,
	    P = undefined,
	    File = undefined;
	var objectProto = Object.prototype;
	var toString = objectProto.toString;

	/**
	 * Attempt to detect the global Promise constructor.
	 * JSData will still work without one, as long you do something like this:
	 *
	 * var JSData = require('js-data');
	 * JSData.DSUtils.Promise = MyPromiseLib;
	 */
	try {
	  P = Promise;
	} catch (e) {
	  console.error('js-data requires a global Promise constructor!');
	}

	try {
	  File = window.File;
	} catch (e) {
	  File = function () {};
	}

	function _isArray(value) {
	  return toString.call(value) === '[object Array]' || false;
	}

	var isArray = Array.isArray || _isArray;

	function isRegExp(value) {
	  return toString.call(value) === '[object RegExp]' || false;
	}

	// adapted from lodash.isString
	function isString(value) {
	  return typeof value === 'string' || value && typeof value === 'object' && toString.call(value) === '[object String]' || false;
	}

	function isObject(value) {
	  return toString.call(value) === '[object Object]' || false;
	}

	// adapted from lodash.isDate
	function isDate(value) {
	  return value && typeof value === 'object' && toString.call(value) === '[object Date]' || false;
	}

	// adapted from lodash.isNumber
	function isNumber(value) {
	  var type = typeof value;
	  return type === 'number' || value && type === 'object' && toString.call(value) === '[object Number]' || false;
	}

	// adapted from lodash.isFunction
	function isFunction(value) {
	  return typeof value === 'function' || value && toString.call(value) === '[object Function]' || false;
	}

	// shorthand argument checking functions, using these shaves 1.18 kb off of the minified build
	function isStringOrNumber(value) {
	  return isString(value) || isNumber(value);
	}

	function isStringOrNumberErr(field) {
	  return new _errors['default'].IA('"' + field + '" must be a string or a number!');
	}

	function isObjectErr(field) {
	  return new _errors['default'].IA('"' + field + '" must be an object!');
	}

	function isArrayErr(field) {
	  return new _errors['default'].IA('"' + field + '" must be an array!');
	}

	// adapted from mout.isEmpty
	function isEmpty(val) {
	  if (val == null) {
	    // jshint ignore:line
	    // typeof null == 'object' so we check it first
	    return true;
	  } else if (typeof val === 'string' || isArray(val)) {
	    return !val.length;
	  } else if (typeof val === 'object') {
	    var result = true;
	    forOwn(val, function () {
	      result = false;
	      return false; // break loop
	    });
	    return result;
	  } else {
	    return true;
	  }
	}

	// Find the intersection between two arrays
	function intersection(array1, array2) {
	  if (!array1 || !array2) {
	    return [];
	  }
	  var result = [];
	  var item = undefined;
	  for (var i = 0, _length = array1.length; i < _length; i++) {
	    item = array1[i];
	    if (contains(result, item)) {
	      continue;
	    }
	    if (contains(array2, item)) {
	      result.push(item);
	    }
	  }
	  return result;
	}

	function filter(array, cb, thisObj) {
	  var results = [];
	  forEach(array, function (value, key, arr) {
	    if (cb(value, key, arr)) {
	      results.push(value);
	    }
	  }, thisObj);
	  return results;
	}

	/**
	 * Attempt to detect whether we are in the browser.
	 */
	try {
	  w = window;
	  w = {};
	} catch (e) {
	  w = null;
	}

	/**
	 * Event mixin. Usage:
	 *
	 * function handler() { ... }
	 * Events(myObject);
	 * myObject.on('foo', handler);
	 * myObject.emit('foo', 'some', 'data');
	 * myObject.off('foo', handler);
	 */
	function Events(target) {
	  var events = {};
	  target = target || this;
	  Object.defineProperties(target, {
	    on: {
	      value: function value(type, func, ctx) {
	        events[type] = events[type] || [];
	        events[type].push({
	          f: func,
	          c: ctx
	        });
	      }
	    },
	    off: {
	      value: function value(type, func) {
	        var listeners = events[type];
	        if (!listeners) {
	          events = {};
	        } else if (func) {
	          for (var i = 0; i < listeners.length; i++) {
	            if (listeners[i].f === func) {
	              listeners.splice(i, 1);
	              break;
	            }
	          }
	        } else {
	          listeners.splice(0, listeners.length);
	        }
	      }
	    },
	    emit: {
	      value: function value() {
	        for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
	          args[_key] = arguments[_key];
	        }

	        var listeners = events[args.shift()] || [];
	        if (listeners) {
	          for (var i = 0; i < listeners.length; i++) {
	            listeners[i].f.apply(listeners[i].c, args);
	          }
	        }
	      }
	    }
	  });
	}

	/**
	 * Lifecycle hooks that should support promises.
	 */
	var toPromisify = ['beforeValidate', 'validate', 'afterValidate', 'beforeCreate', 'afterCreate', 'beforeUpdate', 'afterUpdate', 'beforeDestroy', 'afterDestroy'];

	/**
	 * Return whether "prop" is in the blacklist.
	 */
	var isBlacklisted = observe.isBlacklisted;

	// adapted from angular.copy
	function copy(source, destination, stackSource, stackDest, blacklist) {
	  if (!destination) {
	    destination = source;
	    if (source) {
	      if (isArray(source)) {
	        destination = copy(source, [], stackSource, stackDest, blacklist);
	      } else if (isDate(source)) {
	        destination = new Date(source.getTime());
	      } else if (isRegExp(source)) {
	        destination = new RegExp(source.source, source.toString().match(/[^\/]*$/)[0]);
	        destination.lastIndex = source.lastIndex;
	      } else if (isObject(source)) {
	        destination = copy(source, Object.create(Object.getPrototypeOf(source)), stackSource, stackDest, blacklist);
	      }
	    }
	  } else {
	    if (source === destination) {
	      throw new Error('Cannot copy! Source and destination are identical.');
	    }

	    stackSource = stackSource || [];
	    stackDest = stackDest || [];

	    if (isObject(source)) {
	      var index = stackSource.indexOf(source);
	      if (index !== -1) {
	        return stackDest[index];
	      }

	      stackSource.push(source);
	      stackDest.push(destination);
	    }

	    var result = undefined;
	    if (isArray(source)) {
	      var i = undefined;
	      destination.length = 0;
	      for (i = 0; i < source.length; i++) {
	        result = copy(source[i], null, stackSource, stackDest, blacklist);
	        if (isObject(source[i])) {
	          stackSource.push(source[i]);
	          stackDest.push(result);
	        }
	        destination.push(result);
	      }
	    } else {
	      if (isArray(destination)) {
	        destination.length = 0;
	      } else {
	        forEach(destination, function (value, key) {
	          delete destination[key];
	        });
	      }
	      for (var key in source) {
	        if (source.hasOwnProperty(key)) {
	          if (isBlacklisted(key, blacklist)) {
	            continue;
	          }
	          result = copy(source[key], null, stackSource, stackDest, blacklist);
	          if (isObject(source[key])) {
	            stackSource.push(source[key]);
	            stackDest.push(result);
	          }
	          destination[key] = result;
	        }
	      }
	    }
	  }
	  return destination;
	}

	// adapted from angular.equals
	function equals(_x, _x2) {
	  var _again = true;

	  _function: while (_again) {
	    var o1 = _x,
	        o2 = _x2;
	    t1 = t2 = length = key = keySet = undefined;
	    _again = false;

	    if (o1 === o2) {
	      return true;
	    }
	    if (o1 === null || o2 === null) {
	      return false;
	    }
	    if (o1 !== o1 && o2 !== o2) {
	      // eslint-disable-line
	      return true;
	    } // NaN === NaN
	    var t1 = typeof o1;
	    var t2 = typeof o2;
	    var length, key, keySet;
	    if (t1 == t2) {
	      // eslint-disable-line
	      if (t1 == 'object') {
	        // eslint-disable-line
	        if (isArray(o1)) {
	          if (!isArray(o2)) {
	            return false;
	          }
	          if ((length = o1.length) === o2.length) {
	            // jshint ignore:line
	            for (key = 0; key < length; key++) {
	              if (!equals(o1[key], o2[key])) {
	                return false;
	              }
	            }
	            return true;
	          }
	        } else if (isDate(o1)) {
	          if (!isDate(o2)) {
	            return false;
	          }
	          _x = o1.getTime();
	          _x2 = o2.getTime();
	          _again = true;
	          continue _function;
	        } else if (isRegExp(o1) && isRegExp(o2)) {
	          return o1.toString() == o2.toString(); // eslint-disable-line
	        } else {
	            if (isArray(o2)) {
	              return false;
	            }
	            keySet = {};
	            for (key in o1) {
	              if (key.charAt(0) === '$' || isFunction(o1[key])) {
	                continue;
	              }
	              if (!equals(o1[key], o2[key])) {
	                return false;
	              }
	              keySet[key] = true;
	            }
	            for (key in o2) {
	              if (!keySet.hasOwnProperty(key) && key.charAt(0) !== '$' && o2[key] !== undefined && !isFunction(o2[key])) {
	                return false;
	              }
	            }
	            return true;
	          }
	      }
	    }
	    return false;
	  }
	}

	/**
	 * Given either an instance or the primary key of an instance, return the primary key.
	 */
	function resolveId(definition, idOrInstance) {
	  if (isString(idOrInstance) || isNumber(idOrInstance)) {
	    return idOrInstance;
	  } else if (idOrInstance && definition) {
	    return idOrInstance[definition.idAttribute] || idOrInstance;
	  } else {
	    return idOrInstance;
	  }
	}

	/**
	 * Given either an instance or the primary key of an instance, return the instance.
	 */
	function resolveItem(resource, idOrInstance) {
	  if (resource && (isString(idOrInstance) || isNumber(idOrInstance))) {
	    return resource.index[idOrInstance] || idOrInstance;
	  } else {
	    return idOrInstance;
	  }
	}

	function isValidString(val) {
	  return val != null && val !== ''; // jshint ignore:line
	}

	function join(items, separator) {
	  separator = separator || '';
	  return filter(items, isValidString).join(separator);
	}

	function makePath() {
	  for (var _len2 = arguments.length, args = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
	    args[_key2] = arguments[_key2];
	  }

	  var result = join(args, '/');
	  return result.replace(/([^:\/]|^)\/{2,}/g, '$1/');
	}

	exports['default'] = {
	  Promise: P,
	  /**
	   * Method to wrap an "options" object so that it will inherit from
	   * some parent object, such as a resource definition.
	   */
	  _: function _(parent, options) {
	    var _this = this;
	    parent = parent || {};
	    options = options || {};
	    if (options && options.constructor === parent.constructor) {
	      return options;
	    } else if (!isObject(options)) {
	      throw new _errors['default'].IA('"options" must be an object!');
	    }
	    forEach(toPromisify, function (name) {
	      if (typeof options[name] === 'function' && options[name].toString().indexOf('for (var _len = arg') === -1) {
	        options[name] = _this.promisify(options[name]);
	      }
	    });
	    // Dynamic constructor function
	    var O = function Options(attrs) {
	      var self = this;
	      forOwn(attrs, function (value, key) {
	        self[key] = value;
	      });
	    };
	    // Inherit from some parent object
	    O.prototype = parent;
	    // Give us a way to get the original options back.
	    O.prototype.orig = function () {
	      var orig = {};
	      forOwn(this, function (value, key) {
	        orig[key] = value;
	      });
	      return orig;
	    };
	    return new O(options);
	  },
	  _n: isNumber,
	  _s: isString,
	  _sn: isStringOrNumber,
	  _snErr: isStringOrNumberErr,
	  _o: isObject,
	  _oErr: isObjectErr,
	  _a: isArray,
	  _aErr: isArrayErr,
	  applyScope: function applyScope(definition, params, options) {
	    var scope = options.scope;
	    var _params = copy(params);
	    if (scope) {
	      if (isString(scope)) {
	        scope = [scope];
	      }
	    } else {
	      scope = [];
	    }
	    scope.unshift('defaultScope');
	    forEach(scope, function (_scope) {
	      var scopeDef = options.scopes[_scope];
	      if (typeof scopeDef === 'function') {
	        deepMixIn(params, scopeDef(definition));
	      } else if (scopeDef) {
	        deepMixIn(params, scopeDef);
	      }
	    });
	    deepMixIn(params, _params);
	  },
	  compute: function compute(fn, field) {
	    var _this = this;
	    var args = [];
	    if (!isObject(fn)) {
	      forEach(fn.deps, function (dep) {
	        args.push(get(_this, dep));
	      });
	      // compute property
	      set(_this, field, fn[fn.length - 1].apply(_this, args));
	    }
	  },
	  contains: contains,
	  copy: copy,
	  deepMixIn: deepMixIn,
	  diffObjectFromOldObject: observe.diffObjectFromOldObject,
	  BinaryHeap: BinaryHeap,
	  equals: equals,
	  Events: Events,
	  filter: filter,
	  fillIn: function fillIn(target, obj) {
	    forOwn(obj, function (v, k) {
	      if (!(k in target)) {
	        target[k] = v;
	      }
	    });
	    return target;
	  },
	  forEach: forEach,
	  forOwn: forOwn,
	  fromJson: function fromJson(json) {
	    return isString(json) ? JSON.parse(json) : json;
	  },
	  get: get,
	  intersection: intersection,
	  isArray: isArray,
	  isBlacklisted: isBlacklisted,
	  isEmpty: isEmpty,
	  isFunction: isFunction,
	  isObject: isObject,
	  isNumber: isNumber,
	  isString: isString,
	  keys: _keys,
	  makePath: makePath,
	  observe: observe,
	  omit: function omit(obj, bl) {
	    var toRemove = [];
	    forOwn(obj, function (v, k) {
	      if (isBlacklisted(k, bl)) {
	        toRemove.push(k);
	      }
	    });
	    forEach(toRemove, function (k) {
	      delete obj[k];
	    });
	    return obj;
	  },
	  pascalCase: pascalCase,
	  pick: pick,
	  // Turn the given node-style callback function into one that can return a promise.
	  promisify: function promisify(fn, target) {
	    var _this = this;
	    if (!fn) {
	      return;
	    } else if (typeof fn !== 'function') {
	      throw new Error('Can only promisify functions!');
	    }
	    return function () {
	      for (var _len3 = arguments.length, args = Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
	        args[_key3] = arguments[_key3];
	      }

	      return new _this.Promise(function (resolve, reject) {
	        args.push(function (err, result) {
	          if (err) {
	            reject(err);
	          } else {
	            resolve(result);
	          }
	        });

	        try {
	          var promise = fn.apply(target || this, args);
	          if (promise && promise.then) {
	            promise.then(resolve, reject);
	          }
	        } catch (err) {
	          reject(err);
	        }
	      });
	    };
	  },
	  remove: remove,
	  set: set,
	  slice: slice,
	  sort: sort,
	  toJson: JSON.stringify,
	  updateTimestamp: function updateTimestamp(timestamp) {
	    var newTimestamp = typeof Date.now === 'function' ? Date.now() : new Date().getTime();
	    if (timestamp && newTimestamp <= timestamp) {
	      return timestamp + 1;
	    } else {
	      return newTimestamp;
	    }
	  },
	  upperCase: upperCase,
	  // Return a copy of "object" with cycles removed.
	  removeCircular: function removeCircular(object) {
	    return (function rmCirc(value, ctx) {
	      var i = undefined;
	      var nu = undefined;

	      if (typeof value === 'object' && value !== null && !(value instanceof Boolean) && !(value instanceof Date) && !(value instanceof Number) && !(value instanceof RegExp) && !(value instanceof String) && (!File || !(value instanceof File))) {
	        // check if current object points back to itself
	        var cur = ctx.cur;
	        var parent = ctx.ctx;
	        while (parent) {
	          if (parent.cur === cur) {
	            return undefined;
	          }
	          parent = parent.ctx;
	        }

	        if (isArray(value)) {
	          nu = [];
	          for (i = 0; i < value.length; i += 1) {
	            nu[i] = rmCirc(value[i], { ctx: ctx, cur: value[i] });
	          }
	        } else {
	          nu = {};
	          forOwn(value, function (v, k) {
	            nu[k] = rmCirc(value[k], { ctx: ctx, cur: value[k] });
	          });
	        }
	        return nu;
	      }
	      return value;
	    })(object, { ctx: null, cur: object });
	  },
	  resolveItem: resolveItem,
	  resolveId: resolveId,
	  respond: function respond(response, meta, options) {
	    if (options.returnMeta === 'array') {
	      return [response, meta];
	    } else if (options.returnMeta === 'object') {
	      return { response: response, meta: meta };
	    } else {
	      return response;
	    }
	  },
	  w: w,
	  // This is where the magic of relations happens.
	  applyRelationGettersToTarget: function applyRelationGettersToTarget(store, definition, target) {
	    this.forEach(definition.relationList, function (def) {
	      var relationName = def.relation;
	      var localField = def.localField;
	      var localKey = def.localKey;
	      var foreignKey = def.foreignKey;
	      var localKeys = def.localKeys;
	      var foreignKeys = def.foreignKeys;
	      var enumerable = typeof def.enumerable === 'boolean' ? def.enumerable : !!definition.relationsEnumerable;
	      if (typeof def.link === 'boolean' ? def.link : !!definition.linkRelations) {
	        delete target[localField];
	        var prop = {
	          enumerable: enumerable
	        };
	        if (def.type === 'belongsTo') {
	          prop.get = function () {
	            var key = get(this, localKey);
	            var hasKey = !!(key || key === 0);
	            return hasKey ? definition.getResource(relationName).get(key) : undefined;
	          };
	          prop.set = function (parent) {
	            if (parent) {
	              set(this, localKey, get(parent, definition.getResource(relationName).idAttribute));
	            }
	            return get(this, localField);
	          };
	        } else if (def.type === 'hasMany') {
	          prop.get = function () {
	            var params = {};
	            if (foreignKey) {
	              params[foreignKey] = this[definition.idAttribute];
	              return definition.getResource(relationName).defaultFilter.call(store, store.store[relationName].collection, relationName, params, { allowSimpleWhere: true });
	            } else if (localKeys) {
	              var keys = get(this, localKeys) || [];
	              return definition.getResource(relationName).getAll(isArray(keys) ? keys : _keys(keys));
	            } else if (foreignKeys) {
	              set(params, 'where.' + foreignKeys + '.contains', this[definition.idAttribute]);
	              return definition.getResource(relationName).defaultFilter.call(store, store.store[relationName].collection, relationName, params);
	            }
	            return undefined;
	          };
	          prop.set = function (children) {
	            var _this2 = this;

	            if (children && children.length) {
	              (function () {
	                var id = get(_this2, definition.idAttribute);
	                if (foreignKey) {
	                  forEach(children, function (child) {
	                    set(child, foreignKey, id);
	                  });
	                } else if (localKeys) {
	                  (function () {
	                    var keys = [];
	                    forEach(children, function (child) {
	                      keys.push(get(child, definition.getResource(relationName).idAttribute));
	                    });
	                    set(_this2, localKeys, keys);
	                  })();
	                } else if (foreignKeys) {
	                  forEach(children, function (child) {
	                    var keys = get(child, foreignKeys);
	                    if (keys) {
	                      if (!contains(keys, id)) {
	                        keys.push(id);
	                      }
	                    } else {
	                      set(child, foreignKeys, [id]);
	                    }
	                  });
	                }
	              })();
	            }
	            return get(this, localField);
	          };
	        } else if (def.type === 'hasOne') {
	          if (localKey) {
	            prop.get = function () {
	              var key = get(this, localKey);
	              var hasKey = !!(key || key === 0);
	              return hasKey ? definition.getResource(relationName).get(key) : undefined;
	            };
	            prop.set = function (sibling) {
	              if (sibling) {
	                set(this, localKey, get(sibling, definition.getResource(relationName).idAttribute));
	              }
	              return get(this, localField);
	            };
	          } else {
	            prop.get = function () {
	              var params = {};
	              params[foreignKey] = this[definition.idAttribute];
	              var items = params[foreignKey] ? definition.getResource(relationName).defaultFilter.call(store, store.store[relationName].collection, relationName, params, { allowSimpleWhere: true }) : [];
	              if (items.length) {
	                return items[0];
	              }
	              return undefined;
	            };
	            prop.set = function (sibling) {
	              if (sibling) {
	                set(sibling, foreignKey, get(this, definition.idAttribute));
	              }
	              return get(this, localField);
	            };
	          }
	        }
	        if (def.get) {
	          (function () {
	            var orig = prop.get;
	            prop.get = function () {
	              var _this3 = this;

	              return def.get(definition, def, this, function () {
	                for (var _len4 = arguments.length, args = Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
	                  args[_key4] = arguments[_key4];
	                }

	                return orig.apply(_this3, args);
	              });
	            };
	          })();
	        }
	        Object.defineProperty(target, localField, prop);
	      }
	    });
	  }
	};

/***/ },
/* 3 */
/***/ function(module, exports) {

	var _get = function get(_x, _x2, _x3) { var _again = true; _function: while (_again) { var object = _x, property = _x2, receiver = _x3; desc = parent = getter = undefined; _again = false; if (object === null) object = Function.prototype; var desc = Object.getOwnPropertyDescriptor(object, property); if (desc === undefined) { var parent = Object.getPrototypeOf(object); if (parent === null) { return undefined; } else { _x = parent; _x2 = property; _x3 = receiver; _again = true; continue _function; } } else if ('value' in desc) { return desc.value; } else { var getter = desc.get; if (getter === undefined) { return undefined; } return getter.call(receiver); } } };

	function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError('Cannot call a class as a function'); } }

	function _inherits(subClass, superClass) { if (typeof superClass !== 'function' && superClass !== null) { throw new TypeError('Super expression must either be null or a function, not ' + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

	/**
	 * Thrown during a method call when an argument passed into the method is invalid.
	 */

	var IllegalArgumentError = (function (_Error) {
	  _inherits(IllegalArgumentError, _Error);

	  function IllegalArgumentError(message) {
	    _classCallCheck(this, IllegalArgumentError);

	    _get(Object.getPrototypeOf(IllegalArgumentError.prototype), 'constructor', this).call(this);
	    if (typeof Error.captureStackTrace === 'function') {
	      Error.captureStackTrace(this, this.constructor);
	    }
	    this.type = this.constructor.name;
	    this.message = message;
	  }

	  /**
	   * Thrown when an invariant is violated or unrecoverable error is encountered during execution.
	   */
	  return IllegalArgumentError;
	})(Error);

	var RuntimeError = (function (_Error2) {
	  _inherits(RuntimeError, _Error2);

	  function RuntimeError(message) {
	    _classCallCheck(this, RuntimeError);

	    _get(Object.getPrototypeOf(RuntimeError.prototype), 'constructor', this).call(this);
	    if (typeof Error.captureStackTrace === 'function') {
	      Error.captureStackTrace(this, this.constructor);
	    }
	    this.type = this.constructor.name;
	    this.message = message;
	  }

	  /**
	   * Thrown when attempting to access or work with a non-existent resource.
	   */
	  return RuntimeError;
	})(Error);

	var NonexistentResourceError = (function (_Error3) {
	  _inherits(NonexistentResourceError, _Error3);

	  function NonexistentResourceError(resourceName) {
	    _classCallCheck(this, NonexistentResourceError);

	    _get(Object.getPrototypeOf(NonexistentResourceError.prototype), 'constructor', this).call(this);
	    if (typeof Error.captureStackTrace === 'function') {
	      Error.captureStackTrace(this, this.constructor);
	    }
	    this.type = this.constructor.name;
	    this.message = resourceName + ' is not a registered resource!';
	  }

	  return NonexistentResourceError;
	})(Error);

	exports['default'] = {
	  IllegalArgumentError: IllegalArgumentError,
	  IA: IllegalArgumentError,
	  RuntimeError: RuntimeError,
	  R: RuntimeError,
	  NonexistentResourceError: NonexistentResourceError,
	  NER: NonexistentResourceError
	};

/***/ },
/* 4 */
/***/ function(module, exports, __webpack_require__) {

	/*!
	 * yabh
	 * @version 1.1.0 - Homepage <http://jmdobry.github.io/yabh/>
	 * @author Jason Dobry <jason.dobry@gmail.com>
	 * @copyright (c) 2015 Jason Dobry 
	 * @license MIT <https://github.com/jmdobry/yabh/blob/master/LICENSE>
	 * 
	 * @overview Yet another Binary Heap.
	 */
	(function webpackUniversalModuleDefinition(root, factory) {
		if(true)
			module.exports = factory();
		else if(typeof define === 'function' && define.amd)
			define("yabh", factory);
		else if(typeof exports === 'object')
			exports["BinaryHeap"] = factory();
		else
			root["BinaryHeap"] = factory();
	})(this, function() {
	return /******/ (function(modules) { // webpackBootstrap
	/******/ 	// The module cache
	/******/ 	var installedModules = {};

	/******/ 	// The require function
	/******/ 	function __webpack_require__(moduleId) {

	/******/ 		// Check if module is in cache
	/******/ 		if(installedModules[moduleId])
	/******/ 			return installedModules[moduleId].exports;

	/******/ 		// Create a new module (and put it into the cache)
	/******/ 		var module = installedModules[moduleId] = {
	/******/ 			exports: {},
	/******/ 			id: moduleId,
	/******/ 			loaded: false
	/******/ 		};

	/******/ 		// Execute the module function
	/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);

	/******/ 		// Flag the module as loaded
	/******/ 		module.loaded = true;

	/******/ 		// Return the exports of the module
	/******/ 		return module.exports;
	/******/ 	}


	/******/ 	// expose the modules object (__webpack_modules__)
	/******/ 	__webpack_require__.m = modules;

	/******/ 	// expose the module cache
	/******/ 	__webpack_require__.c = installedModules;

	/******/ 	// __webpack_public_path__
	/******/ 	__webpack_require__.p = "";

	/******/ 	// Load entry module and return exports
	/******/ 	return __webpack_require__(0);
	/******/ })
	/************************************************************************/
	/******/ ([
	/* 0 */
	/***/ function(module, exports, __webpack_require__) {

		/**
		 * @method bubbleUp
		 * @param {array} heap The heap.
		 * @param {function} weightFunc The weight function.
		 * @param {number} n The index of the element to bubble up.
		 */
		function bubbleUp(heap, weightFunc, n) {
		  var element = heap[n];
		  var weight = weightFunc(element);
		  // When at 0, an element can not go up any further.
		  while (n > 0) {
		    // Compute the parent element's index, and fetch it.
		    var parentN = Math.floor((n + 1) / 2) - 1;
		    var _parent = heap[parentN];
		    // If the parent has a lesser weight, things are in order and we
		    // are done.
		    if (weight >= weightFunc(_parent)) {
		      break;
		    } else {
		      heap[parentN] = element;
		      heap[n] = _parent;
		      n = parentN;
		    }
		  }
		}

		/**
		 * @method bubbleDown
		 * @param {array} heap The heap.
		 * @param {function} weightFunc The weight function.
		 * @param {number} n The index of the element to sink down.
		 */
		var bubbleDown = function bubbleDown(heap, weightFunc, n) {
		  var length = heap.length;
		  var node = heap[n];
		  var nodeWeight = weightFunc(node);

		  while (true) {
		    var child2N = (n + 1) * 2,
		        child1N = child2N - 1;
		    var swap = null;
		    if (child1N < length) {
		      var child1 = heap[child1N],
		          child1Weight = weightFunc(child1);
		      // If the score is less than our node's, we need to swap.
		      if (child1Weight < nodeWeight) {
		        swap = child1N;
		      }
		    }
		    // Do the same checks for the other child.
		    if (child2N < length) {
		      var child2 = heap[child2N],
		          child2Weight = weightFunc(child2);
		      if (child2Weight < (swap === null ? nodeWeight : weightFunc(heap[child1N]))) {
		        swap = child2N;
		      }
		    }

		    if (swap === null) {
		      break;
		    } else {
		      heap[n] = heap[swap];
		      heap[swap] = node;
		      n = swap;
		    }
		  }
		};

		function BinaryHeap(weightFunc, compareFunc) {
		  if (!weightFunc) {
		    weightFunc = function (x) {
		      return x;
		    };
		  }
		  if (!compareFunc) {
		    compareFunc = function (x, y) {
		      return x === y;
		    };
		  }
		  if (typeof weightFunc !== 'function') {
		    throw new Error('BinaryHeap([weightFunc][, compareFunc]): "weightFunc" must be a function!');
		  }
		  if (typeof compareFunc !== 'function') {
		    throw new Error('BinaryHeap([weightFunc][, compareFunc]): "compareFunc" must be a function!');
		  }
		  this.weightFunc = weightFunc;
		  this.compareFunc = compareFunc;
		  this.heap = [];
		}

		var BHProto = BinaryHeap.prototype;

		BHProto.push = function (node) {
		  this.heap.push(node);
		  bubbleUp(this.heap, this.weightFunc, this.heap.length - 1);
		};

		BHProto.peek = function () {
		  return this.heap[0];
		};

		BHProto.pop = function () {
		  var front = this.heap[0];
		  var end = this.heap.pop();
		  if (this.heap.length > 0) {
		    this.heap[0] = end;
		    bubbleDown(this.heap, this.weightFunc, 0);
		  }
		  return front;
		};

		BHProto.remove = function (node) {
		  var length = this.heap.length;
		  for (var i = 0; i < length; i++) {
		    if (this.compareFunc(this.heap[i], node)) {
		      var removed = this.heap[i];
		      var end = this.heap.pop();
		      if (i !== length - 1) {
		        this.heap[i] = end;
		        bubbleUp(this.heap, this.weightFunc, i);
		        bubbleDown(this.heap, this.weightFunc, i);
		      }
		      return removed;
		    }
		  }
		  return null;
		};

		BHProto.removeAll = function () {
		  this.heap = [];
		};

		BHProto.size = function () {
		  return this.heap.length;
		};

		module.exports = BinaryHeap;

	/***/ }
	/******/ ])
	});
	;

/***/ },
/* 5 */
/***/ function(module, exports) {

	

	    /**
	     * Array forEach
	     */
	    function forEach(arr, callback, thisObj) {
	        if (arr == null) {
	            return;
	        }
	        var i = -1,
	            len = arr.length;
	        while (++i < len) {
	            // we iterate over sparse items since there is no way to make it
	            // work properly on IE 7-8. see #64
	            if ( callback.call(thisObj, arr[i], i, arr) === false ) {
	                break;
	            }
	        }
	    }

	    module.exports = forEach;




/***/ },
/* 6 */
/***/ function(module, exports) {

	

	    /**
	     * Create slice of source array or array-like object
	     */
	    function slice(arr, start, end){
	        var len = arr.length;

	        if (start == null) {
	            start = 0;
	        } else if (start < 0) {
	            start = Math.max(len + start, 0);
	        } else {
	            start = Math.min(start, len);
	        }

	        if (end == null) {
	            end = len;
	        } else if (end < 0) {
	            end = Math.max(len + end, 0);
	        } else {
	            end = Math.min(end, len);
	        }

	        var result = [];
	        while (start < end) {
	            result.push(arr[start++]);
	        }

	        return result;
	    }

	    module.exports = slice;




/***/ },
/* 7 */
/***/ function(module, exports, __webpack_require__) {

	var hasOwn = __webpack_require__(8);
	var forIn = __webpack_require__(9);

	    /**
	     * Similar to Array/forEach but works over object properties and fixes Don't
	     * Enum bug on IE.
	     * based on: http://whattheheadsaid.com/2010/10/a-safer-object-keys-compatibility-implementation
	     */
	    function forOwn(obj, fn, thisObj){
	        forIn(obj, function(val, key){
	            if (hasOwn(obj, key)) {
	                return fn.call(thisObj, obj[key], key, obj);
	            }
	        });
	    }

	    module.exports = forOwn;




/***/ },
/* 8 */
/***/ function(module, exports) {

	

	    /**
	     * Safer Object.hasOwnProperty
	     */
	     function hasOwn(obj, prop){
	         return Object.prototype.hasOwnProperty.call(obj, prop);
	     }

	     module.exports = hasOwn;




/***/ },
/* 9 */
/***/ function(module, exports, __webpack_require__) {

	var hasOwn = __webpack_require__(8);

	    var _hasDontEnumBug,
	        _dontEnums;

	    function checkDontEnum(){
	        _dontEnums = [
	                'toString',
	                'toLocaleString',
	                'valueOf',
	                'hasOwnProperty',
	                'isPrototypeOf',
	                'propertyIsEnumerable',
	                'constructor'
	            ];

	        _hasDontEnumBug = true;

	        for (var key in {'toString': null}) {
	            _hasDontEnumBug = false;
	        }
	    }

	    /**
	     * Similar to Array/forEach but works over object properties and fixes Don't
	     * Enum bug on IE.
	     * based on: http://whattheheadsaid.com/2010/10/a-safer-object-keys-compatibility-implementation
	     */
	    function forIn(obj, fn, thisObj){
	        var key, i = 0;
	        // no need to check if argument is a real object that way we can use
	        // it for arrays, functions, date, etc.

	        //post-pone check till needed
	        if (_hasDontEnumBug == null) checkDontEnum();

	        for (key in obj) {
	            if (exec(fn, obj, key, thisObj) === false) {
	                break;
	            }
	        }


	        if (_hasDontEnumBug) {
	            var ctor = obj.constructor,
	                isProto = !!ctor && obj === ctor.prototype;

	            while (key = _dontEnums[i++]) {
	                // For constructor, if it is a prototype object the constructor
	                // is always non-enumerable unless defined otherwise (and
	                // enumerated above).  For non-prototype objects, it will have
	                // to be defined on this object, since it cannot be defined on
	                // any prototype objects.
	                //
	                // For other [[DontEnum]] properties, check if the value is
	                // different than Object prototype value.
	                if (
	                    (key !== 'constructor' ||
	                        (!isProto && hasOwn(obj, key))) &&
	                    obj[key] !== Object.prototype[key]
	                ) {
	                    if (exec(fn, obj, key, thisObj) === false) {
	                        break;
	                    }
	                }
	            }
	        }
	    }

	    function exec(fn, obj, key, thisObj){
	        return fn.call(thisObj, obj[key], key, obj);
	    }

	    module.exports = forIn;




/***/ },
/* 10 */
/***/ function(module, exports, __webpack_require__) {

	var indexOf = __webpack_require__(11);

	    /**
	     * If array contains values.
	     */
	    function contains(arr, val) {
	        return indexOf(arr, val) !== -1;
	    }
	    module.exports = contains;



/***/ },
/* 11 */
/***/ function(module, exports) {

	

	    /**
	     * Array.indexOf
	     */
	    function indexOf(arr, item, fromIndex) {
	        fromIndex = fromIndex || 0;
	        if (arr == null) {
	            return -1;
	        }

	        var len = arr.length,
	            i = fromIndex < 0 ? len + fromIndex : fromIndex;
	        while (i < len) {
	            // we iterate over sparse items since there is no way to make it
	            // work properly on IE 7-8. see #64
	            if (arr[i] === item) {
	                return i;
	            }

	            i++;
	        }

	        return -1;
	    }

	    module.exports = indexOf;



/***/ },
/* 12 */
/***/ function(module, exports, __webpack_require__) {

	var forOwn = __webpack_require__(7);
	var isPlainObject = __webpack_require__(13);

	    /**
	     * Mixes objects into the target object, recursively mixing existing child
	     * objects.
	     */
	    function deepMixIn(target, objects) {
	        var i = 0,
	            n = arguments.length,
	            obj;

	        while(++i < n){
	            obj = arguments[i];
	            if (obj) {
	                forOwn(obj, copyProp, target);
	            }
	        }

	        return target;
	    }

	    function copyProp(val, key) {
	        var existing = this[key];
	        if (isPlainObject(val) && isPlainObject(existing)) {
	            deepMixIn(existing, val);
	        } else {
	            this[key] = val;
	        }
	    }

	    module.exports = deepMixIn;




/***/ },
/* 13 */
/***/ function(module, exports) {

	

	    /**
	     * Checks if the value is created by the `Object` constructor.
	     */
	    function isPlainObject(value) {
	        return (!!value && typeof value === 'object' &&
	            value.constructor === Object);
	    }

	    module.exports = isPlainObject;




/***/ },
/* 14 */
/***/ function(module, exports, __webpack_require__) {

	var toString = __webpack_require__(15);
	var camelCase = __webpack_require__(16);
	var upperCase = __webpack_require__(19);
	    /**
	     * camelCase + UPPERCASE first char
	     */
	    function pascalCase(str){
	        str = toString(str);
	        return camelCase(str).replace(/^[a-z]/, upperCase);
	    }

	    module.exports = pascalCase;



/***/ },
/* 15 */
/***/ function(module, exports) {

	

	    /**
	     * Typecast a value to a String, using an empty string value for null or
	     * undefined.
	     */
	    function toString(val){
	        return val == null ? '' : val.toString();
	    }

	    module.exports = toString;




/***/ },
/* 16 */
/***/ function(module, exports, __webpack_require__) {

	var toString = __webpack_require__(15);
	var replaceAccents = __webpack_require__(17);
	var removeNonWord = __webpack_require__(18);
	var upperCase = __webpack_require__(19);
	var lowerCase = __webpack_require__(20);
	    /**
	    * Convert string to camelCase text.
	    */
	    function camelCase(str){
	        str = toString(str);
	        str = replaceAccents(str);
	        str = removeNonWord(str)
	            .replace(/[\-_]/g, ' ') //convert all hyphens and underscores to spaces
	            .replace(/\s[a-z]/g, upperCase) //convert first char of each word to UPPERCASE
	            .replace(/\s+/g, '') //remove spaces
	            .replace(/^[A-Z]/g, lowerCase); //convert first char to lowercase
	        return str;
	    }
	    module.exports = camelCase;



/***/ },
/* 17 */
/***/ function(module, exports, __webpack_require__) {

	var toString = __webpack_require__(15);
	    /**
	    * Replaces all accented chars with regular ones
	    */
	    function replaceAccents(str){
	        str = toString(str);

	        // verifies if the String has accents and replace them
	        if (str.search(/[\xC0-\xFF]/g) > -1) {
	            str = str
	                    .replace(/[\xC0-\xC5]/g, "A")
	                    .replace(/[\xC6]/g, "AE")
	                    .replace(/[\xC7]/g, "C")
	                    .replace(/[\xC8-\xCB]/g, "E")
	                    .replace(/[\xCC-\xCF]/g, "I")
	                    .replace(/[\xD0]/g, "D")
	                    .replace(/[\xD1]/g, "N")
	                    .replace(/[\xD2-\xD6\xD8]/g, "O")
	                    .replace(/[\xD9-\xDC]/g, "U")
	                    .replace(/[\xDD]/g, "Y")
	                    .replace(/[\xDE]/g, "P")
	                    .replace(/[\xE0-\xE5]/g, "a")
	                    .replace(/[\xE6]/g, "ae")
	                    .replace(/[\xE7]/g, "c")
	                    .replace(/[\xE8-\xEB]/g, "e")
	                    .replace(/[\xEC-\xEF]/g, "i")
	                    .replace(/[\xF1]/g, "n")
	                    .replace(/[\xF2-\xF6\xF8]/g, "o")
	                    .replace(/[\xF9-\xFC]/g, "u")
	                    .replace(/[\xFE]/g, "p")
	                    .replace(/[\xFD\xFF]/g, "y");
	        }
	        return str;
	    }
	    module.exports = replaceAccents;



/***/ },
/* 18 */
/***/ function(module, exports, __webpack_require__) {

	var toString = __webpack_require__(15);
	    // This pattern is generated by the _build/pattern-removeNonWord.js script
	    var PATTERN = /[^\x20\x2D0-9A-Z\x5Fa-z\xC0-\xD6\xD8-\xF6\xF8-\xFF]/g;

	    /**
	     * Remove non-word chars.
	     */
	    function removeNonWord(str){
	        str = toString(str);
	        return str.replace(PATTERN, '');
	    }

	    module.exports = removeNonWord;



/***/ },
/* 19 */
/***/ function(module, exports, __webpack_require__) {

	var toString = __webpack_require__(15);
	    /**
	     * "Safer" String.toUpperCase()
	     */
	    function upperCase(str){
	        str = toString(str);
	        return str.toUpperCase();
	    }
	    module.exports = upperCase;



/***/ },
/* 20 */
/***/ function(module, exports, __webpack_require__) {

	var toString = __webpack_require__(15);
	    /**
	     * "Safer" String.toLowerCase()
	     */
	    function lowerCase(str){
	        str = toString(str);
	        return str.toLowerCase();
	    }

	    module.exports = lowerCase;



/***/ },
/* 21 */
/***/ function(module, exports, __webpack_require__) {

	var indexOf = __webpack_require__(11);

	    /**
	     * Remove a single item from the array.
	     * (it won't remove duplicates, just a single item)
	     */
	    function remove(arr, item){
	        var idx = indexOf(arr, item);
	        if (idx !== -1) arr.splice(idx, 1);
	    }

	    module.exports = remove;



/***/ },
/* 22 */
/***/ function(module, exports, __webpack_require__) {

	var slice = __webpack_require__(6);

	    /**
	     * Return a copy of the object, filtered to only have values for the whitelisted keys.
	     */
	    function pick(obj, var_keys){
	        var keys = typeof arguments[1] !== 'string'? arguments[1] : slice(arguments, 1),
	            out = {},
	            i = 0, key;
	        while (key = keys[i++]) {
	            out[key] = obj[key];
	        }
	        return out;
	    }

	    module.exports = pick;




/***/ },
/* 23 */
/***/ function(module, exports, __webpack_require__) {

	var forOwn = __webpack_require__(7);

	    /**
	     * Get object keys
	     */
	     var keys = Object.keys || function (obj) {
	            var keys = [];
	            forOwn(obj, function(val, key){
	                keys.push(key);
	            });
	            return keys;
	        };

	    module.exports = keys;




/***/ },
/* 24 */
/***/ function(module, exports) {

	

	    /**
	     * Merge sort (http://en.wikipedia.org/wiki/Merge_sort)
	     */
	    function mergeSort(arr, compareFn) {
	        if (arr == null) {
	            return [];
	        } else if (arr.length < 2) {
	            return arr;
	        }

	        if (compareFn == null) {
	            compareFn = defaultCompare;
	        }

	        var mid, left, right;

	        mid   = ~~(arr.length / 2);
	        left  = mergeSort( arr.slice(0, mid), compareFn );
	        right = mergeSort( arr.slice(mid, arr.length), compareFn );

	        return merge(left, right, compareFn);
	    }

	    function defaultCompare(a, b) {
	        return a < b ? -1 : (a > b? 1 : 0);
	    }

	    function merge(left, right, compareFn) {
	        var result = [];

	        while (left.length && right.length) {
	            if (compareFn(left[0], right[0]) <= 0) {
	                // if 0 it should preserve same order (stable)
	                result.push(left.shift());
	            } else {
	                result.push(right.shift());
	            }
	        }

	        if (left.length) {
	            result.push.apply(result, left);
	        }

	        if (right.length) {
	            result.push.apply(result, right);
	        }

	        return result;
	    }

	    module.exports = mergeSort;




/***/ },
/* 25 */
/***/ function(module, exports, __webpack_require__) {

	var isPrimitive = __webpack_require__(26);

	    /**
	     * get "nested" object property
	     */
	    function get(obj, prop){
	        var parts = prop.split('.'),
	            last = parts.pop();

	        while (prop = parts.shift()) {
	            obj = obj[prop];
	            if (obj == null) return;
	        }

	        return obj[last];
	    }

	    module.exports = get;




/***/ },
/* 26 */
/***/ function(module, exports) {

	

	    /**
	     * Checks if the object is a primitive
	     */
	    function isPrimitive(value) {
	        // Using switch fallthrough because it's simple to read and is
	        // generally fast: http://jsperf.com/testing-value-is-primitive/5
	        switch (typeof value) {
	            case "string":
	            case "number":
	            case "boolean":
	                return true;
	        }

	        return value == null;
	    }

	    module.exports = isPrimitive;




/***/ },
/* 27 */
/***/ function(module, exports, __webpack_require__) {

	var namespace = __webpack_require__(28);

	    /**
	     * set "nested" object property
	     */
	    function set(obj, prop, val){
	        var parts = (/^(.+)\.(.+)$/).exec(prop);
	        if (parts){
	            namespace(obj, parts[1])[parts[2]] = val;
	        } else {
	            obj[prop] = val;
	        }
	    }

	    module.exports = set;




/***/ },
/* 28 */
/***/ function(module, exports, __webpack_require__) {

	var forEach = __webpack_require__(5);

	    /**
	     * Create nested object if non-existent
	     */
	    function namespace(obj, path){
	        if (!path) return obj;
	        forEach(path.split('.'), function(key){
	            if (!obj[key]) {
	                obj[key] = {};
	            }
	            obj = obj[key];
	        });
	        return obj;
	    }

	    module.exports = namespace;




/***/ },
/* 29 */
/***/ function(module, exports) {

	/*
	 * Copyright (c) 2014 The Polymer Project Authors. All rights reserved.
	 * This code may only be used under the BSD style license found at http://polymer.github.io/LICENSE.txt
	 * The complete set of authors may be found at http://polymer.github.io/AUTHORS.txt
	 * The complete set of contributors may be found at http://polymer.github.io/CONTRIBUTORS.txt
	 * Code distributed by Google as part of the polymer project is also
	 * subject to an additional IP rights grant found at http://polymer.github.io/PATENTS.txt
	 */

	// Modifications
	// Copyright 2014-2015 Jason Dobry
	//
	// Summary of modifications:
	// Fixed use of "delete" keyword for IE8 compatibility
	// Exposed diffObjectFromOldObject on the exported object
	// Added the "equals" argument to diffObjectFromOldObject to be used to check equality
	// Added a way in diffObjectFromOldObject to ignore changes to certain properties
	// Removed all code related to:
	// - ArrayObserver
	// - ArraySplice
	// - PathObserver
	// - CompoundObserver
	// - Path
	// - ObserverTransform

	(function(global) {
	  var testingExposeCycleCount = global.testingExposeCycleCount;

	  // Detect and do basic sanity checking on Object/Array.observe.
	  function detectObjectObserve() {
	    if (typeof Object.observe !== 'function' ||
	        typeof Array.observe !== 'function') {
	      return false;
	    }

	    var records = [];

	    function callback(recs) {
	      records = recs;
	    }

	    var test = {};
	    var arr = [];
	    Object.observe(test, callback);
	    Array.observe(arr, callback);
	    test.id = 1;
	    test.id = 2;
	    delete test.id;
	    arr.push(1, 2);
	    arr.length = 0;

	    Object.deliverChangeRecords(callback);
	    if (records.length !== 5)
	      return false;

	    if (records[0].type != 'add' ||
	        records[1].type != 'update' ||
	        records[2].type != 'delete' ||
	        records[3].type != 'splice' ||
	        records[4].type != 'splice') {
	      return false;
	    }

	    Object.unobserve(test, callback);
	    Array.unobserve(arr, callback);

	    return true;
	  }

	  var hasObserve = detectObjectObserve();

	  var createObject = ('__proto__' in {}) ?
	    function(obj) { return obj; } :
	    function(obj) {
	      var proto = obj.__proto__;
	      if (!proto)
	        return obj;
	      var newObject = Object.create(proto);
	      Object.getOwnPropertyNames(obj).forEach(function(name) {
	        Object.defineProperty(newObject, name,
	                             Object.getOwnPropertyDescriptor(obj, name));
	      });
	      return newObject;
	    };

	  var MAX_DIRTY_CHECK_CYCLES = 1000;

	  function dirtyCheck(observer) {
	    var cycles = 0;
	    while (cycles < MAX_DIRTY_CHECK_CYCLES && observer.check_()) {
	      cycles++;
	    }
	    if (testingExposeCycleCount)
	      global.dirtyCheckCycleCount = cycles;

	    return cycles > 0;
	  }

	  function objectIsEmpty(object) {
	    for (var prop in object)
	      return false;
	    return true;
	  }

	  function diffIsEmpty(diff) {
	    return objectIsEmpty(diff.added) &&
	           objectIsEmpty(diff.removed) &&
	           objectIsEmpty(diff.changed);
	  }

	  function isBlacklisted(prop, bl) {
	    if (!bl || !bl.length) {
	      return false;
	    }
	    var matches;
	    for (var i = 0; i < bl.length; i++) {
	      if ((Object.prototype.toString.call(bl[i]) === '[object RegExp]' && bl[i].test(prop)) || bl[i] === prop) {
	        return matches = prop;
	      }
	    }
	    return !!matches;
	  }

	  function diffObjectFromOldObject(object, oldObject, equals, bl) {
	    var added = {};
	    var removed = {};
	    var changed = {};

	    for (var prop in oldObject) {
	      var newValue = object[prop];

	      if (isBlacklisted(prop, bl))
	        continue;

	      if (newValue !== undefined && (equals ? equals(newValue, oldObject[prop]) : newValue === oldObject[prop]))
	        continue;

	      if (!(prop in object)) {
	        removed[prop] = undefined;
	        continue;
	      }

	      if (equals ? !equals(newValue, oldObject[prop]) : newValue !== oldObject[prop])
	        changed[prop] = newValue;
	    }

	    for (var prop in object) {
	      if (prop in oldObject)
	        continue;

	      if (isBlacklisted(prop, bl))
	        continue;

	      added[prop] = object[prop];
	    }

	    if (Array.isArray(object) && object.length !== oldObject.length)
	      changed.length = object.length;

	    return {
	      added: added,
	      removed: removed,
	      changed: changed
	    };
	  }

	  var eomTasks = [];
	  function runEOMTasks() {
	    if (!eomTasks.length)
	      return false;

	    for (var i = 0; i < eomTasks.length; i++) {
	      eomTasks[i]();
	    }
	    eomTasks.length = 0;
	    return true;
	  }

	  var runEOM = hasObserve ? (function(){
	    return function(fn) {
	      return Promise.resolve().then(fn);
	    }
	  })() :
	  (function() {
	    return function(fn) {
	      eomTasks.push(fn);
	    };
	  })();

	  var observedObjectCache = [];

	  function newObservedObject() {
	    var observer;
	    var object;
	    var discardRecords = false;
	    var first = true;

	    function callback(records) {
	      if (observer && observer.state_ === OPENED && !discardRecords)
	        observer.check_(records);
	    }

	    return {
	      open: function(obs) {
	        if (observer)
	          throw Error('ObservedObject in use');

	        if (!first)
	          Object.deliverChangeRecords(callback);

	        observer = obs;
	        first = false;
	      },
	      observe: function(obj, arrayObserve) {
	        object = obj;
	        if (arrayObserve)
	          Array.observe(object, callback);
	        else
	          Object.observe(object, callback);
	      },
	      deliver: function(discard) {
	        discardRecords = discard;
	        Object.deliverChangeRecords(callback);
	        discardRecords = false;
	      },
	      close: function() {
	        observer = undefined;
	        Object.unobserve(object, callback);
	        observedObjectCache.push(this);
	      }
	    };
	  }

	  function getObservedObject(observer, object, arrayObserve) {
	    var dir = observedObjectCache.pop() || newObservedObject();
	    dir.open(observer);
	    dir.observe(object, arrayObserve);
	    return dir;
	  }

	  var UNOPENED = 0;
	  var OPENED = 1;
	  var CLOSED = 2;

	  var nextObserverId = 1;

	  function Observer() {
	    this.state_ = UNOPENED;
	    this.callback_ = undefined;
	    this.target_ = undefined; // TODO(rafaelw): Should be WeakRef
	    this.directObserver_ = undefined;
	    this.value_ = undefined;
	    this.id_ = nextObserverId++;
	  }

	  Observer.prototype = {
	    open: function(callback, target) {
	      if (this.state_ != UNOPENED)
	        throw Error('Observer has already been opened.');

	      addToAll(this);
	      this.callback_ = callback;
	      this.target_ = target;
	      this.connect_();
	      this.state_ = OPENED;
	      return this.value_;
	    },

	    close: function() {
	      if (this.state_ != OPENED)
	        return;

	      removeFromAll(this);
	      this.disconnect_();
	      this.value_ = undefined;
	      this.callback_ = undefined;
	      this.target_ = undefined;
	      this.state_ = CLOSED;
	    },

	    deliver: function() {
	      if (this.state_ != OPENED)
	        return;

	      dirtyCheck(this);
	    },

	    report_: function(changes) {
	      try {
	        this.callback_.apply(this.target_, changes);
	      } catch (ex) {
	        Observer._errorThrownDuringCallback = true;
	        console.error('Exception caught during observer callback: ' +
	                       (ex.stack || ex));
	      }
	    },

	    discardChanges: function() {
	      this.check_(undefined, true);
	      return this.value_;
	    }
	  }

	  var collectObservers = !hasObserve;
	  var allObservers;
	  Observer._allObserversCount = 0;

	  if (collectObservers) {
	    allObservers = [];
	  }

	  function addToAll(observer) {
	    Observer._allObserversCount++;
	    if (!collectObservers)
	      return;

	    allObservers.push(observer);
	  }

	  function removeFromAll(observer) {
	    Observer._allObserversCount--;
	  }

	  var runningMicrotaskCheckpoint = false;

	  global.Platform = global.Platform || {};

	  global.Platform.performMicrotaskCheckpoint = function() {
	    if (runningMicrotaskCheckpoint)
	      return;

	    if (!collectObservers)
	      return;

	    runningMicrotaskCheckpoint = true;

	    var cycles = 0;
	    var anyChanged, toCheck;

	    do {
	      cycles++;
	      toCheck = allObservers;
	      allObservers = [];
	      anyChanged = false;

	      for (var i = 0; i < toCheck.length; i++) {
	        var observer = toCheck[i];
	        if (observer.state_ != OPENED)
	          continue;

	        if (observer.check_())
	          anyChanged = true;

	        allObservers.push(observer);
	      }
	      if (runEOMTasks())
	        anyChanged = true;
	    } while (cycles < MAX_DIRTY_CHECK_CYCLES && anyChanged);

	    if (testingExposeCycleCount)
	      global.dirtyCheckCycleCount = cycles;

	    runningMicrotaskCheckpoint = false;
	  };

	  if (collectObservers) {
	    global.Platform.clearObservers = function() {
	      allObservers = [];
	    };
	  }

	  function ObjectObserver(object) {
	    Observer.call(this);
	    this.value_ = object;
	    this.oldObject_ = undefined;
	  }

	  ObjectObserver.prototype = createObject({
	    __proto__: Observer.prototype,

	    arrayObserve: false,

	    connect_: function(callback, target) {
	      if (hasObserve) {
	        this.directObserver_ = getObservedObject(this, this.value_,
	                                                 this.arrayObserve);
	      } else {
	        this.oldObject_ = this.copyObject(this.value_);
	      }

	    },

	    copyObject: function(object) {
	      var copy = Array.isArray(object) ? [] : {};
	      for (var prop in object) {
	        copy[prop] = object[prop];
	      };
	      if (Array.isArray(object))
	        copy.length = object.length;
	      return copy;
	    },

	    check_: function(changeRecords, skipChanges) {
	      var diff;
	      var oldValues;
	      if (hasObserve) {
	        if (!changeRecords)
	          return false;

	        oldValues = {};
	        diff = diffObjectFromChangeRecords(this.value_, changeRecords,
	                                           oldValues);
	      } else {
	        oldValues = this.oldObject_;
	        diff = diffObjectFromOldObject(this.value_, this.oldObject_);
	      }

	      if (diffIsEmpty(diff))
	        return false;

	      if (!hasObserve)
	        this.oldObject_ = this.copyObject(this.value_);

	      this.report_([
	        diff.added || {},
	        diff.removed || {},
	        diff.changed || {},
	        function(property) {
	          return oldValues[property];
	        }
	      ]);

	      return true;
	    },

	    disconnect_: function() {
	      if (hasObserve) {
	        this.directObserver_.close();
	        this.directObserver_ = undefined;
	      } else {
	        this.oldObject_ = undefined;
	      }
	    },

	    deliver: function() {
	      if (this.state_ != OPENED)
	        return;

	      if (hasObserve)
	        this.directObserver_.deliver(false);
	      else
	        dirtyCheck(this);
	    },

	    discardChanges: function() {
	      if (this.directObserver_)
	        this.directObserver_.deliver(true);
	      else
	        this.oldObject_ = this.copyObject(this.value_);

	      return this.value_;
	    }
	  });

	  var observerSentinel = {};

	  var expectedRecordTypes = {
	    add: true,
	    update: true,
	    'delete': true
	  };

	  function diffObjectFromChangeRecords(object, changeRecords, oldValues) {
	    var added = {};
	    var removed = {};

	    for (var i = 0; i < changeRecords.length; i++) {
	      var record = changeRecords[i];
	      if (!expectedRecordTypes[record.type]) {
	        console.error('Unknown changeRecord type: ' + record.type);
	        console.error(record);
	        continue;
	      }

	      if (!(record.name in oldValues))
	        oldValues[record.name] = record.oldValue;

	      if (record.type == 'update')
	        continue;

	      if (record.type == 'add') {
	        if (record.name in removed)
	          delete removed[record.name];
	        else
	          added[record.name] = true;

	        continue;
	      }

	      // type = 'delete'
	      if (record.name in added) {
	        delete added[record.name];
	        delete oldValues[record.name];
	      } else {
	        removed[record.name] = true;
	      }
	    }

	    for (var prop in added)
	      added[prop] = object[prop];

	    for (var prop in removed)
	      removed[prop] = undefined;

	    var changed = {};
	    for (var prop in oldValues) {
	      if (prop in added || prop in removed)
	        continue;

	      var newValue = object[prop];
	      if (oldValues[prop] !== newValue)
	        changed[prop] = newValue;
	    }

	    return {
	      added: added,
	      removed: removed,
	      changed: changed
	    };
	  }

	  // Export the observe-js object for **Node.js**, with backwards-compatibility
	  // for the old `require()` API. Also ensure `exports` is not a DOM Element.
	  // If we're in the browser, export as a global object.

	  global.Observer = Observer;
	  global.isBlacklisted = isBlacklisted;
	  global.Observer.runEOM_ = runEOM;
	  global.Observer.observerSentinel_ = observerSentinel; // for testing.
	  global.Observer.hasObjectObserve = hasObserve;
	  global.diffObjectFromOldObject = diffObjectFromOldObject;
	  global.ObjectObserver = ObjectObserver;

	})(exports);


/***/ },
/* 30 */
/***/ function(module, exports, __webpack_require__) {

	var _utils = __webpack_require__(2);

	var _errors = __webpack_require__(3);

	var NER = _errors['default'].NER;
	var IA = _errors['default'].IA;
	var R = _errors['default'].R;

	var fakeId = 'DS_' + new Date().getTime();

	function diffIsEmpty(diff) {
	  return !(_utils['default'].isEmpty(diff.added) && _utils['default'].isEmpty(diff.removed) && _utils['default'].isEmpty(diff.changed));
	}

	function check(fnName, resourceName, id, options) {
	  var _this = this;
	  var definition = _this.definitions[resourceName];
	  options = options || {};

	  id = _utils['default'].resolveId(definition, id);
	  if (!definition) {
	    throw new NER(resourceName);
	  } else if (!_utils['default']._sn(id)) {
	    throw _utils['default']._snErr('id');
	  }
	  id = id === fakeId ? undefined : id;

	  options = _utils['default']._(definition, options);


	  return { _this: _this, definition: definition, _resourceName: resourceName, _id: id, _options: options };
	}

	exports['default'] = {
	  // Return the changes for the given item, if any.
	  //
	  // @param resourceName The name of the type of resource of the item whose changes are to be returned.
	  // @param id The primary key of the item whose changes are to be returned.
	  // @param options Optional configuration.
	  // @param options.ignoredChanges Array of strings or regular expressions of fields, the changes of which are to be ignored.
	  // @returns The changes of the given item, if any.
	  changes: function changes(resourceName, id, options) {
	    var _check$call = check.call(this, 'changes', resourceName, id, options);

	    var _this = _check$call._this;
	    var definition = _check$call.definition;
	    var _resourceName = _check$call._resourceName;
	    var _id = _check$call._id;
	    var _options = _check$call._options;

	    var item = definition.get(_id);
	    if (item) {
	      var _ret = (function () {
	        var observer = _this.store[_resourceName].observers[_id];
	        if (observer && typeof observer === 'function') {
	          // force observation handler to be fired for item if there are changes and `Object.observe` is not available
	          observer.deliver();
	        }

	        var ignoredChanges = _options.ignoredChanges || [];
	        // add linked relations to list of ignored changes
	        _utils['default'].forEach(definition.relationFields, function (field) {
	          if (!_utils['default'].contains(ignoredChanges, field)) {
	            ignoredChanges.push(field);
	          }
	        });
	        // calculate changes
	        var diff = _utils['default'].diffObjectFromOldObject(item, _this.store[_resourceName].previousAttributes[_id], _utils['default'].equals, ignoredChanges);
	        // remove functions from diff
	        _utils['default'].forOwn(diff, function (changeset, name) {
	          var toKeep = [];
	          _utils['default'].forOwn(changeset, function (value, field) {
	            if (!_utils['default'].isFunction(value)) {
	              toKeep.push(field);
	            }
	          });
	          diff[name] = _utils['default'].pick(diff[name], toKeep);
	        });
	        // definitely ignore changes to linked relations
	        _utils['default'].forEach(definition.relationFields, function (field) {
	          delete diff.added[field];
	          delete diff.removed[field];
	          delete diff.changed[field];
	        });
	        return {
	          v: diff
	        };
	      })();

	      if (typeof _ret === 'object') return _ret.v;
	    }
	  },

	  // Return the change history of the given item, if any.
	  //
	  // @param resourceName The name of the type of resource of the item whose change history is to be returned.
	  // @param id The primary key of the item whose change history is to be returned.
	  // @returns The change history of the given item, if any.
	  changeHistory: function changeHistory(resourceName, id) {
	    var _check$call2 = check.call(this, 'changeHistory', resourceName, id || fakeId);

	    var _this = _check$call2._this;
	    var definition = _check$call2.definition;
	    var _resourceName = _check$call2._resourceName;
	    var _id = _check$call2._id;

	    var resource = _this.store[_resourceName];

	    if (!definition.keepChangeHistory) {
	      definition.errorFn('changeHistory is disabled for this resource!');
	    } else {
	      if (_resourceName) {
	        var item = definition.get(_id);
	        if (item) {
	          return resource.changeHistories[_id];
	        }
	      } else {
	        return resource.changeHistory;
	      }
	    }
	  },

	  commit: function commit(resourceName, id) {
	    var _check$call3 = check.call(this, 'commit', resourceName, id);

	    var _this = _check$call3._this;
	    var definition = _check$call3.definition;
	    var _resourceName = _check$call3._resourceName;
	    var _id = _check$call3._id;

	    var resource = _this.store[_resourceName];
	    var item = _this.store[_resourceName].index[_id];
	    if (item) {
	      resource.previousAttributes[_id] = _utils['default'].copy(item, null, null, null, definition.relationFields);
	    }

	    if (resource.changeHistories[_id].length) {
	      _utils['default'].forEach(resource.changeHistories[_id], function (changeRecord) {
	        _utils['default'].remove(resource.changeHistory, changeRecord);
	      });
	      resource.changeHistories[_id].splice(0, resource.changeHistories[_id].length);
	    }
	    return item;
	  },

	  // Re-compute the computed properties of the given item.
	  //
	  // @param resourceName The name of the type of resource of the item whose computed properties are to be re-computed.
	  // @param instance The instance whose computed properties are to be re-computed.
	  // @returns The item whose computed properties were re-computed.
	  compute: function compute(resourceName, instance) {
	    var _this = this;
	    var definition = _this.definitions[resourceName];

	    instance = _utils['default'].resolveItem(_this.store[resourceName], instance);
	    if (!definition) {
	      throw new NER(resourceName);
	    } else if (!instance) {
	      throw new R('Item not in the store!');
	    } else if (!_utils['default']._o(instance) && !_utils['default']._sn(instance)) {
	      throw new IA('"instance" must be an object, string or number!');
	    }


	    // re-compute all computed properties
	    _utils['default'].forOwn(definition.computed, function (fn, field) {
	      _utils['default'].compute.call(instance, fn, field);
	    });
	    return instance;
	  },

	  // Factory function to create an instance of the specified Resource.
	  //
	  // @param resourceName The name of the type of resource of which to create an instance.
	  // @param attrs Hash of properties with which to initialize the instance.
	  // @param options Optional configuration.
	  // @param options.defaults Default values with which to initialize the instance.
	  // @returns The new instance.
	  createInstance: function createInstance(resourceName, attrs, options) {
	    var definition = this.definitions[resourceName];
	    var item = undefined;

	    attrs = attrs || {};

	    if (!definition) {
	      throw new NER(resourceName);
	    } else if (attrs && !_utils['default'].isObject(attrs)) {
	      throw new IA('"attrs" must be an object!');
	    }

	    options = _utils['default']._(definition, options);

	    // lifecycle
	    options.beforeCreateInstance(options, attrs);

	    // grab instance constructor function from Resource definition
	    var Constructor = definition[definition['class']];

	    // create instance
	    item = new Constructor();

	    if (definition.instanceEvents) {
	      _utils['default'].Events(item);
	    }

	    // add default values
	    if (options.defaultValues) {
	      _utils['default'].deepMixIn(item, options.defaultValues);
	    }
	    _utils['default'].deepMixIn(item, attrs);

	    // compute computed properties
	    if (definition.computed) {
	      definition.compute(item);
	    }
	    // lifecycle
	    options.afterCreateInstance(options, item);
	    return item;
	  },

	  // Create a new collection of the specified Resource.
	  //
	  // @param resourceName The name of the type of resource of which to create a collection
	  // @param arr Possibly empty array of data from which to create the collection.
	  // @param params The criteria by which to filter items. Will be passed to `DS#findAll` if `fetch` is called. See http://www.js-data.io/docs/query-syntax
	  // @param options Optional configuration.
	  // @param options.notify Whether to call the beforeCreateCollection and afterCreateCollection lifecycle hooks..
	  // @returns The new collection.
	  createCollection: function createCollection(resourceName, arr, params, options) {
	    var _this = this;
	    var definition = _this.definitions[resourceName];

	    arr = arr || [];
	    params = params || {};

	    if (!definition) {
	      throw new NER(resourceName);
	    } else if (arr && !_utils['default'].isArray(arr)) {
	      throw new IA('"arr" must be an array!');
	    }

	    options = _utils['default']._(definition, options);


	    // lifecycle
	    options.beforeCreateCollection(options, arr);

	    // define the API for this collection
	    Object.defineProperties(arr, {
	      //  Call DS#findAll with the params of this collection, filling the collection with the results.
	      fetch: {
	        value: function value(params, options) {
	          var __this = this;
	          __this.params = params || __this.params;
	          return definition.findAll(__this.params, options).then(function (data) {
	            if (data === __this) {
	              return __this;
	            }
	            data.unshift(__this.length);
	            data.unshift(0);
	            __this.splice.apply(__this, data);
	            data.shift();
	            data.shift();
	            if (data.$$injected) {
	              _this.store[resourceName].queryData[_utils['default'].toJson(__this.params)] = __this;
	              __this.$$injected = true;
	            }
	            return __this;
	          });
	        }
	      },
	      // params for this collection. See http://www.js-data.io/docs/query-syntax
	      params: {
	        value: params,
	        writable: true
	      },
	      // name of the resource type of this collection
	      resourceName: {
	        value: resourceName
	      }
	    });

	    // lifecycle
	    options.afterCreateCollection(options, arr);
	    return arr;
	  },
	  defineResource: __webpack_require__(31),
	  digest: function digest() {
	    this.observe.Platform.performMicrotaskCheckpoint();
	  },
	  eject: __webpack_require__(32),
	  ejectAll: __webpack_require__(33),
	  filter: __webpack_require__(34),

	  // Return the item with the given primary key if its in the store.
	  //
	  // @param resourceName The name of the type of resource of the item to retrieve.
	  // @param id The primary key of the item to retrieve.
	  // @returns The item with the given primary key if it's in the store.
	  // /
	  get: function get(resourceName, id) {
	    var _check$call4 = check.call(this, 'get', resourceName, id);

	    var _this = _check$call4._this;
	    var _resourceName = _check$call4._resourceName;
	    var _id = _check$call4._id;

	    // return the item if it exists
	    return _this.store[_resourceName].index[_id];
	  },

	  // Return the items in the store that have the given primary keys.
	  //
	  // @param resourceName The name of the type of resource of the items to retrieve.
	  // @param ids The primary keys of the items to retrieve.
	  // @returns The items with the given primary keys if they're in the store.
	  getAll: function getAll(resourceName, ids) {
	    var _this = this;
	    var definition = _this.definitions[resourceName];
	    var resource = _this.store[resourceName];
	    var collection = [];

	    if (!definition) {
	      throw new NER(resourceName);
	    } else if (ids && !_utils['default']._a(ids)) {
	      throw _utils['default']._aErr('ids');
	    }


	    if (_utils['default']._a(ids)) {
	      // return just the items with the given primary keys
	      var _length = ids.length;
	      for (var i = 0; i < _length; i++) {
	        if (resource.index[ids[i]]) {
	          collection.push(resource.index[ids[i]]);
	        }
	      }
	    } else {
	      // most efficient of retrieving ALL items from the store
	      collection = resource.collection.slice();
	    }

	    return collection;
	  },

	  // Return the whether the item with the given primary key has any changes.
	  //
	  // @param resourceName The name of the type of resource of the item.
	  // @param id The primary key of the item.
	  // @returns Whether the item with the given primary key has any changes.
	  hasChanges: function hasChanges(resourceName, id) {
	    var _check$call5 = check.call(this, 'hasChanges', resourceName, id);

	    var definition = _check$call5.definition;
	    var _id = _check$call5._id;

	    return definition.get(_id) ? diffIsEmpty(definition.changes(_id)) : false;
	  },
	  inject: __webpack_require__(35),

	  // Return the timestamp from the last time the item with the given primary key was changed.
	  //
	  // @param resourceName The name of the type of resource of the item.
	  // @param id The primary key of the item.
	  // @returns Timestamp from the last time the item was changed.
	  lastModified: function lastModified(resourceName, id) {
	    var _check$call6 = check.call(this, 'lastModified', resourceName, id || fakeId);

	    var _this = _check$call6._this;
	    var _resourceName = _check$call6._resourceName;
	    var _id = _check$call6._id;

	    var resource = _this.store[_resourceName];

	    if (_id) {
	      if (!(_id in resource.modified)) {
	        resource.modified[_id] = 0;
	      }
	      return resource.modified[_id];
	    }
	    return resource.collectionModified;
	  },

	  // Return the timestamp from the last time the item with the given primary key was saved via an adapter.
	  //
	  // @param resourceName The name of the type of resource of the item.
	  // @param id The primary key of the item.
	  // @returns Timestamp from the last time the item was saved.
	  lastSaved: function lastSaved(resourceName, id) {
	    var _check$call7 = check.call(this, 'lastSaved', resourceName, id || fakeId);

	    var _this = _check$call7._this;
	    var _resourceName = _check$call7._resourceName;
	    var _id = _check$call7._id;

	    var resource = _this.store[_resourceName];

	    if (!(_id in resource.saved)) {
	      resource.saved[_id] = 0;
	    }
	    return resource.saved[_id];
	  },

	  // Return the previous attributes of the item with the given primary key before it was changed.
	  //
	  // @param resourceName The name of the type of resource of the item.
	  // @param id The primary key of the item.
	  // @returns The previous attributes of the item
	  previous: function previous(resourceName, id) {
	    var _check$call8 = check.call(this, 'previous', resourceName, id);

	    var _this = _check$call8._this;
	    var _resourceName = _check$call8._resourceName;
	    var _id = _check$call8._id;

	    var resource = _this.store[_resourceName];

	    // return resource from cache
	    return resource.previousAttributes[_id] ? _utils['default'].copy(resource.previousAttributes[_id]) : undefined;
	  },

	  // Revert all attributes of the item with the given primary key to their previous values.
	  //
	  // @param resourceName The name of the type of resource of the item.
	  // @param id The primary key of the item.
	  // @param options Optional configuration.
	  // @returns The reverted item
	  revert: function revert(resourceName, id, options) {
	    var _check$call9 = check.call(this, 'revert', resourceName, id, options);

	    var _this = _check$call9._this;
	    var definition = _check$call9.definition;
	    var _resourceName = _check$call9._resourceName;
	    var _id = _check$call9._id;
	    var _options = _check$call9._options;

	    var preserve = _options.preserve || [];
	    var injectObj = {};

	    if (preserve.length === 0) {
	      injectObj = _this.previous(_resourceName, _id);
	    } else {
	      var _ret2 = (function () {
	        var instance = definition.get(id);
	        var previousInstance = _this.previous(_resourceName, _id);

	        if (!instance) {
	          return {
	            v: undefined
	          };
	        }

	        _utils['default'].forOwn(instance, function (value, key) {
	          if (_utils['default'].contains(preserve, key)) {
	            injectObj[key] = instance[key];
	          } else {
	            injectObj[key] = previousInstance[key];
	          }
	        });
	      })();

	      if (typeof _ret2 === 'object') return _ret2.v;
	    }

	    return definition.inject(injectObj, {
	      onConflict: 'replace'
	    });
	  }
	};

/***/ },
/* 31 */
/***/ function(module, exports, __webpack_require__) {

	/* jshint evil:true, loopfunc:true */

	var _utils = __webpack_require__(2);

	var _errors = __webpack_require__(3);

	/**
	 * These are DS methods that will be proxied by instances. e.g.
	 *
	 * var store = new JSData.DS()
	 * var User = store.defineResource('user')
	 * var user = User.createInstance({ id: 1 })
	 *
	 * store.update(resourceName, id, attrs[, options]) // DS method
	 * User.update(id, attrs[, options]) // DS method proxied on a Resource
	 * user.DSUpdate(attrs[, options]) // DS method proxied on an Instance
	 */
	var instanceMethods = ['compute', 'eject', 'refresh', 'save', 'update', 'destroy', 'loadRelations', 'changeHistory', 'changes', 'commit', 'hasChanges', 'lastModified', 'lastSaved', 'previous', 'revert'];

	module.exports = function defineResource(definition) {
	  var _this = this;
	  var definitions = _this.definitions;

	  /**
	   * This allows the name-only definition shorthand.
	   * store.defineResource('user') is the same as store.defineResource({ name: 'user'})
	   */
	  if (_utils['default']._s(definition)) {
	    definition = {
	      name: definition.replace(/\s/gi, '')
	    };
	  }
	  if (!_utils['default']._o(definition)) {
	    throw _utils['default']._oErr('definition');
	  } else if (!_utils['default']._s(definition.name)) {
	    throw new _errors['default'].IA('"name" must be a string!');
	  } else if (definitions[definition.name]) {
	    throw new _errors['default'].R(definition.name + ' is already registered!');
	  }

	  /**
	   * Dynamic Resource constructor function.
	   *
	   * A Resource inherits from the defaults of the data store that created it.
	   */
	  function Resource(options) {
	    this.defaultValues = {};
	    this.methods = {};
	    this.computed = {};
	    this.scopes = {};
	    this.actions = {};
	    _utils['default'].deepMixIn(this, options);
	    var parent = _this.defaults;
	    if (definition['extends'] && definitions[definition['extends']]) {
	      parent = definitions[definition['extends']];
	    }
	    _utils['default'].fillIn(this.defaultValues, parent.defaultValues);
	    _utils['default'].fillIn(this.methods, parent.methods);
	    _utils['default'].fillIn(this.computed, parent.computed);
	    _utils['default'].fillIn(this.scopes, parent.scopes);
	    _utils['default'].fillIn(this.actions, parent.actions);
	    this.endpoint = 'endpoint' in options ? options.endpoint : this.name;
	  }

	  try {
	    var def;

	    var _class;

	    var _ret = (function () {
	      // Resources can inherit from another resource instead of inheriting directly from the data store defaults.
	      if (definition['extends'] && definitions[definition['extends']]) {
	        // Inherit from another resource
	        Resource.prototype = definitions[definition['extends']];
	      } else {
	        // Inherit from global defaults
	        Resource.prototype = _this.defaults;
	      }
	      definitions[definition.name] = new Resource(definition);

	      def = definitions[definition.name];

	      def.getResource = function (resourceName) {
	        return _this.definitions[resourceName];
	      };


	      if (!_utils['default']._s(def.idAttribute)) {
	        throw new _errors['default'].IA('"idAttribute" must be a string!');
	      }

	      // Setup nested parent configuration
	      if (def.relations) {
	        def.relationList = [];
	        def.relationFields = [];
	        _utils['default'].forOwn(def.relations, function (relatedModels, type) {
	          _utils['default'].forOwn(relatedModels, function (defs, relationName) {
	            if (!_utils['default']._a(defs)) {
	              relatedModels[relationName] = [defs];
	            }
	            _utils['default'].forEach(relatedModels[relationName], function (d) {
	              d.type = type;
	              d.relation = relationName;
	              d.name = def.name;
	              def.relationList.push(d);
	              if (d.localField) {
	                def.relationFields.push(d.localField);
	              }
	            });
	          });
	        });
	        if (def.relations.belongsTo) {
	          _utils['default'].forOwn(def.relations.belongsTo, function (relatedModel, modelName) {
	            _utils['default'].forEach(relatedModel, function (relation) {
	              if (relation.parent) {
	                def.parent = modelName;
	                def.parentKey = relation.localKey;
	                def.parentField = relation.localField;
	              }
	            });
	          });
	        }
	        if (typeof Object.freeze === 'function') {
	          Object.freeze(def.relations);
	          Object.freeze(def.relationList);
	        }
	      }

	      // Create the wrapper class for the new resource
	      _class = def['class'] = _utils['default'].pascalCase(def.name);

	      try {
	        if (typeof def.useClass === 'function') {
	          if (def.csp) {
	            def[_class] = function () {
	              def.useClass.call(this);
	            };
	          } else {
	            def[_class] = new Function('def', 'return function ' + _class + '() { def.useClass.call(this); }')(def); // eslint-disable-line
	          }
	          def[_class].prototype = (function (proto) {
	            function Ctor() {}

	            Ctor.prototype = proto;
	            return new Ctor();
	          })(def.useClass.prototype);
	        } else if (def.csp) {
	          def[_class] = function () {};
	        } else {
	          def[_class] = new Function('return function ' + _class + '() {}')(); // eslint-disable-line
	        }
	      } catch (e) {
	        def[_class] = function () {};
	      }

	      // Apply developer-defined instance methods
	      _utils['default'].forOwn(def.methods, function (fn, m) {
	        def[_class].prototype[m] = fn;
	      });

	      /**
	       * var user = User.createInstance({ id: 1 })
	       * user.set('foo', 'bar')
	       */
	      def[_class].prototype.set = function (key, value) {
	        var _this2 = this;

	        _utils['default'].set(this, key, value);
	        def.compute(this);
	        if (def.instanceEvents) {
	          setTimeout(function () {
	            _this2.emit('DS.change', def, _this2);
	          }, 0);
	        }
	        def.handleChange(this);
	        return this;
	      };

	      /**
	       * var user = User.createInstance({ id: 1 })
	       * user.get('id') // 1
	       */
	      def[_class].prototype.get = function (key) {
	        return _utils['default'].get(this, key);
	      };

	      // Setup the relation links
	      _utils['default'].applyRelationGettersToTarget(_this, def, def[_class].prototype);

	      var parentOmit = null;
	      if (!def.hasOwnProperty('omit')) {
	        parentOmit = def.omit;
	        def.omit = [];
	      } else {
	        parentOmit = _this.defaults.omit;
	      }
	      def.omit = def.omit.concat(parentOmit || []);

	      // Prepare for computed properties
	      _utils['default'].forOwn(def.computed, function (fn, field) {
	        if (_utils['default'].isFunction(fn)) {
	          def.computed[field] = [fn];
	          fn = def.computed[field];
	        }
	        if (def.methods && field in def.methods) {
	          def.errorFn('Computed property "' + field + '" conflicts with previously defined prototype method!');
	        }
	        def.omit.push(field);
	        if (_utils['default'].isArray(fn)) {
	          var deps;
	          if (fn.length === 1) {
	            var match = fn[0].toString().match(/function.*?\(([\s\S]*?)\)/);
	            deps = match[1].split(',');
	            deps = _utils['default'].filter(deps, function (x) {
	              return x;
	            });
	            def.computed[field] = deps.concat(fn);
	            fn = def.computed[field];
	            if (deps.length) {
	              def.errorFn('Use the computed property array syntax for compatibility with minified code!');
	            }
	          }
	          deps = fn.slice(0, fn.length - 1);
	          _utils['default'].forEach(deps, function (val, index) {
	            deps[index] = val.trim();
	          });
	          fn.deps = _utils['default'].filter(deps, function (dep) {
	            return !!dep;
	          });
	        } else if (_utils['default'].isObject(fn)) {
	          Object.defineProperty(def[_class].prototype, field, fn);
	        }
	      });

	      // add instance proxies of DS methods
	      _utils['default'].forEach(instanceMethods, function (name) {
	        def[_class].prototype['DS' + _utils['default'].pascalCase(name)] = function () {
	          for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
	            args[_key] = arguments[_key];
	          }

	          args.unshift(this[def.idAttribute] || this);
	          args.unshift(def.name);
	          return _this[name].apply(_this, args);
	        };
	      });

	      // manually add instance proxy for DS#create
	      def[_class].prototype.DSCreate = function () {
	        for (var _len2 = arguments.length, args = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
	          args[_key2] = arguments[_key2];
	        }

	        args.unshift(this);
	        args.unshift(def.name);
	        return _this.create.apply(_this, args);
	      };

	      // Initialize store data for the new resource
	      _this.store[def.name] = {
	        collection: [],
	        expiresHeap: new _utils['default'].BinaryHeap(function (x) {
	          return x.expires;
	        }, function (x, y) {
	          return x.item === y;
	        }),
	        completedQueries: {},
	        queryData: {},
	        pendingQueries: {},
	        index: {},
	        modified: {},
	        saved: {},
	        previousAttributes: {},
	        observers: {},
	        changeHistories: {},
	        changeHistory: [],
	        collectionModified: 0
	      };

	      var resource = _this.store[def.name];

	      // start the reaping
	      if (def.reapInterval) {
	        setInterval(function () {
	          return def.reap();
	        }, def.reapInterval);
	      }

	      // proxy DS methods with shorthand ones
	      var fns = ['registerAdapter', 'getAdapterName', 'getAdapter', 'is', '!clear'];
	      for (var key in _this) {
	        if (typeof _this[key] === 'function') {
	          fns.push(key);
	        }
	      }

	      /**
	       * Create the Resource shorthands that proxy DS methods. e.g.
	       *
	       * var store = new JSData.DS()
	       * var User = store.defineResource('user')
	       *
	       * store.update(resourceName, id, attrs[, options]) // DS method
	       * User.update(id, attrs[, options]) // DS method proxied on a Resource
	       */
	      _utils['default'].forEach(fns, function (key) {
	        var k = key;
	        if (k[0] === '!') {
	          return;
	        }
	        if (_this[k].shorthand !== false) {
	          def[k] = function () {
	            for (var _len3 = arguments.length, args = Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
	              args[_key3] = arguments[_key3];
	            }

	            args.unshift(def.name);
	            return _this[k].apply(_this, args);
	          };
	          def[k].before = function (fn) {
	            var orig = def[k];
	            def[k] = function () {
	              for (var _len4 = arguments.length, args = Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
	                args[_key4] = arguments[_key4];
	              }

	              return orig.apply(def, fn.apply(def, args) || args);
	            };
	          };
	        } else {
	          def[k] = function () {
	            for (var _len5 = arguments.length, args = Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
	              args[_key5] = arguments[_key5];
	            }

	            return _this[k].apply(_this, args);
	          };
	        }
	      });

	      def.beforeValidate = _utils['default'].promisify(def.beforeValidate);
	      def.validate = _utils['default'].promisify(def.validate);
	      def.afterValidate = _utils['default'].promisify(def.afterValidate);
	      def.beforeCreate = _utils['default'].promisify(def.beforeCreate);
	      def.afterCreate = _utils['default'].promisify(def.afterCreate);
	      def.afterFind = _utils['default'].promisify(def.afterFind);
	      def.afterFindAll = _utils['default'].promisify(def.afterFindAll);
	      def.afterLoadRelations = _utils['default'].promisify(def.afterLoadRelations);
	      def.beforeUpdate = _utils['default'].promisify(def.beforeUpdate);
	      def.afterUpdate = _utils['default'].promisify(def.afterUpdate);
	      def.beforeDestroy = _utils['default'].promisify(def.beforeDestroy);
	      def.afterDestroy = _utils['default'].promisify(def.afterDestroy);

	      var defaultAdapter = undefined;
	      if (def.hasOwnProperty('defaultAdapter')) {
	        defaultAdapter = def.defaultAdapter;
	      }

	      // setup "actions"
	      _utils['default'].forOwn(def.actions, function (action, name) {
	        if (def[name] && !def.actions[name]) {
	          throw new Error('Cannot override existing method "' + name + '"!');
	        }
	        action.request = action.request || function (config) {
	          return config;
	        };
	        action.response = action.response || function (response) {
	          return response;
	        };
	        action.responseError = action.responseError || function (err) {
	          return _utils['default'].Promise.reject(err);
	        };
	        def[name] = function (id, options) {
	          if (_utils['default']._o(id)) {
	            options = id;
	          }
	          options = options || {};
	          var adapter = def.getAdapter(action.adapter || defaultAdapter || 'http');
	          var config = _utils['default'].deepMixIn({}, action);
	          if (!options.hasOwnProperty('endpoint') && config.endpoint) {
	            options.endpoint = config.endpoint;
	          }
	          if (typeof options.getEndpoint === 'function') {
	            config.url = options.getEndpoint(def, options);
	          } else {
	            var args = [options.basePath || def.basePath || adapter.defaults.basePath, adapter.getEndpoint(def, _utils['default']._sn(id) ? id : null, options)];
	            if (_utils['default']._sn(id)) {
	              args.push(id);
	            }
	            args.push(action.pathname || name);
	            config.url = _utils['default'].makePath.apply(null, args);
	          }
	          config.method = config.method || 'GET';
	          config.resourceName = def.name;
	          _utils['default'].deepMixIn(config, options);
	          return new _utils['default'].Promise(function (resolve) {
	            return resolve(config);
	          }).then(options.request || action.request).then(function (config) {
	            return adapter.HTTP(config);
	          }).then(function (data) {
	            if (data && data.config) {
	              data.config.resourceName = def.name;
	            }
	            return data;
	          }).then(options.response || action.response, options.responseError || action.responseError);
	        };
	      });

	      // mix in events
	      _utils['default'].Events(def);

	      def.handleChange = function (data) {
	        resource.collectionModified = _utils['default'].updateTimestamp(resource.collectionModified);
	        if (def.notify) {
	          setTimeout(function () {
	            def.emit('DS.change', def, data);
	          }, 0);
	        }
	      };


	      return {
	        v: def
	      };
	    })();

	    if (typeof _ret === 'object') return _ret.v;
	  } catch (err) {
	    _this.defaults.errorFn(err);
	    delete definitions[definition.name];
	    delete _this.store[definition.name];
	    throw err;
	  }
	};

/***/ },
/* 32 */
/***/ function(module, exports) {

	/* jshint eqeqeq:false */
	/**
	 * Eject an item from the store, if it is currently in the store.
	 *
	 * @param resourceName The name of the resource type of the item eject.
	 * @param id The primary key of the item to eject.
	 * @param options Optional configuration.
	 * @param options.notify Whether to emit the "DS.beforeEject" and "DS.afterEject" events
	 * @param options.clearEmptyQueries Whether to remove cached findAll queries that become empty as a result of this method call.
	 * @returns The ejected item if one was ejected.
	 */
	module.exports = function eject(resourceName, id, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var definition = _this.definitions[resourceName];
	  var resource = _this.store[resourceName];
	  var item = undefined;
	  var found = false;

	  id = DSUtils.resolveId(definition, id);

	  if (!definition) {
	    throw new _this.errors.NER(resourceName);
	  } else if (!DSUtils._sn(id)) {
	    throw DSUtils._snErr('id');
	  }

	  options = DSUtils._(definition, options);


	  // find the item to eject
	  for (var i = 0; i < resource.collection.length; i++) {
	    if (resource.collection[i][definition.idAttribute] == id) {
	      // eslint-disable-line
	      item = resource.collection[i];
	      // remove its expiration timestamp
	      resource.expiresHeap.remove(item);
	      found = true;
	      break;
	    }
	  }
	  if (found) {
	    var _ret = (function () {
	      // lifecycle
	      definition.beforeEject(options, item);
	      if (options.notify) {
	        definition.emit('DS.beforeEject', definition, item);
	      }

	      // find the item in any ($$injected) cached queries
	      var toRemove = [];
	      DSUtils.forOwn(resource.queryData, function (items, queryHash) {
	        if (items.$$injected) {
	          DSUtils.remove(items, item);
	        }
	        // optionally remove any empty queries
	        if (!items.length && options.clearEmptyQueries) {
	          toRemove.push(queryHash);
	        }
	      });

	      // clean up
	      DSUtils.forEach(resource.changeHistories[id], function (changeRecord) {
	        DSUtils.remove(resource.changeHistory, changeRecord);
	      });
	      DSUtils.forEach(toRemove, function (queryHash) {
	        delete resource.completedQueries[queryHash];
	        delete resource.queryData[queryHash];
	      });
	      if (resource.observers[id] && typeof resource.observers[id].close === 'function') {
	        // stop observation
	        resource.observers[id].close();
	      }
	      delete resource.observers[id];
	      delete resource.index[id];
	      delete resource.previousAttributes[id];
	      delete resource.completedQueries[id];
	      delete resource.pendingQueries[id];
	      delete resource.changeHistories[id];
	      delete resource.modified[id];
	      delete resource.saved[id];
	      if (definition.instanceEvents && item.off) {
	        item.off();
	      }

	      // remove it from the store
	      resource.collection.splice(i, 1);
	      // collection has been modified
	      definition.handleChange(item);

	      // lifecycle
	      definition.afterEject(options, item);
	      if (options.notify) {
	        definition.emit('DS.afterEject', definition, item);
	      }

	      return {
	        v: item
	      };
	    })();

	    if (typeof _ret === 'object') return _ret.v;
	  }
	};

/***/ },
/* 33 */
/***/ function(module, exports) {

	/**
	 * Eject a collection of items from the store, if any items currently in the store match the given criteria.
	 *
	 * @param resourceName The name of the resource type of the items eject.
	 * @param params The criteria by which to match items to eject. See http://www.js-data.io/docs/query-syntax
	 * @param options Optional configuration.
	 * @returns The collection of items that were ejected, if any.
	 */
	module.exports = function ejectAll(resourceName, params, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var definition = _this.definitions[resourceName];
	  params = params || {};

	  if (!definition) {
	    throw new _this.errors.NER(resourceName);
	  } else if (!DSUtils._o(params)) {
	    throw DSUtils._oErr('params');
	  }

	  options = DSUtils._(definition, options);

	  DSUtils.applyScope(definition, params, options);

	  var resource = _this.store[resourceName];
	  var queryHash = DSUtils.toJson(params);

	  // get items that match the criteria
	  var items = definition.filter(params);

	  if (DSUtils.isEmpty(params)) {
	    // remove all completed queries if ejecting all items
	    resource.completedQueries = {};
	  } else {
	    // remove matching completed query, if any
	    delete resource.completedQueries[queryHash];
	  }
	  // prepare to remove matching items
	  DSUtils.forEach(items, function (item) {
	    if (item && item[definition.idAttribute]) {
	      definition.eject(item[definition.idAttribute], options);
	    }
	  });
	  // collection has been modified
	  definition.handleChange(items);
	  return items;
	};

/***/ },
/* 34 */
/***/ function(module, exports) {

	/**
	 * Return the subset of items currently in the store that match the given criteria.
	 *
	 * The actual filtering is delegated to DS#defaults.defaultFilter, which can be overridden by developers.
	 *
	 * @param resourceName The name of the resource type of the items to filter.
	 * @param params The criteria by which to filter items. See http://www.js-data.io/docs/query-syntax
	 * @param options Optional configuration.
	 * @returns Matching items.
	 */
	module.exports = function filter(resourceName, params, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var definition = _this.definitions[resourceName];

	  if (!definition) {
	    throw new _this.errors.NER(resourceName);
	  } else if (params && !DSUtils._o(params)) {
	    throw DSUtils._oErr('params');
	  }

	  // Protect against null
	  params = params || {};
	  options = DSUtils._(definition, options);

	  DSUtils.applyScope(definition, params, options);

	  // delegate filtering to DS#defaults.defaultFilter, which can be overridden by developers.
	  return definition.defaultFilter.call(_this, _this.store[resourceName].collection, resourceName, params, options);
	};

/***/ },
/* 35 */
/***/ function(module, exports, __webpack_require__) {

	var _utils = __webpack_require__(2);

	var _errors = __webpack_require__(3);

	/**
	 * This is a beast of a file, but it's where a significant portion of the magic happens.
	 *
	 * DS#inject makes up the core of how data gets into the store.
	 */

	/**
	 * This factory function produces an observer handler function tailor-made for the current item being injected.
	 *
	 * The observer handler is what allows computed properties and change tracking to function.
	 *
	 * @param definition Resource definition produced by DS#defineResource
	 * @param resource Resource data as internally stored by the data store
	 * @returns {Function} Observer handler function
	 * @private
	 */
	function makeObserverHandler(definition, resource) {
	  var DS = this;

	  // using "var" avoids a JSHint error
	  var name = definition.name;

	  /**
	   * This will be called by observe-js when a new change record is available for the observed object
	   *
	   * @param added Change record for added properties
	   * @param removed Change record for removed properties
	   * @param changed Change record for changed properties
	   * @param oldValueFn Function that can be used to get the previous value of a changed property
	   * @param firstTime Whether this is the first time this function is being called for the given item. Will only be true once.
	   */
	  return function _react(added, removed, changed, oldValueFn, firstTime) {
	    var target = this;
	    var item = undefined;

	    // Get the previous primary key of the observed item, in-case some knucklehead changed it
	    var innerId = oldValueFn && oldValueFn(definition.idAttribute) ? oldValueFn(definition.idAttribute) : target[definition.idAttribute];

	    // Ignore changes to relation links
	    _utils['default'].forEach(definition.relationFields, function (field) {
	      delete added[field];
	      delete removed[field];
	      delete changed[field];
	    });

	    // Detect whether there are actually any changes
	    if (!_utils['default'].isEmpty(added) || !_utils['default'].isEmpty(removed) || !_utils['default'].isEmpty(changed) || firstTime) {
	      item = DS.get(name, innerId);

	      // update item and collection "modified" timestamps
	      resource.modified[innerId] = _utils['default'].updateTimestamp(resource.modified[innerId]);

	      if (item && definition.instanceEvents) {
	        setTimeout(function () {
	          item.emit('DS.change', definition, item);
	        }, 0);
	      }

	      definition.handleChange(item);

	      // Save a change record for the item
	      if (definition.keepChangeHistory) {
	        var changeRecord = {
	          resourceName: name,
	          target: item,
	          added: added,
	          removed: removed,
	          changed: changed,
	          timestamp: resource.modified[innerId]
	        };
	        resource.changeHistories[innerId].push(changeRecord);
	        resource.changeHistory.push(changeRecord);
	      }
	    }

	    // Recompute computed properties if any computed properties depend on changed properties
	    if (definition.computed) {
	      item = item || DS.get(name, innerId);
	      _utils['default'].forOwn(definition.computed, function (fn, field) {
	        if (_utils['default']._o(fn)) {
	          return;
	        }
	        var compute = false;
	        // check if required fields changed
	        _utils['default'].forEach(fn.deps, function (dep) {
	          if (dep in added || dep in removed || dep in changed || !(field in item)) {
	            compute = true;
	          }
	        });
	        compute = compute || !fn.deps.length;
	        if (compute) {
	          _utils['default'].compute.call(item, fn, field);
	        }
	      });
	    }

	    if (definition.idAttribute in changed) {
	      definition.errorFn('Doh! You just changed the primary key of an object! Your data for the "' + name + '" resource is now in an undefined (probably broken) state.');
	    }
	  };
	}

	/**
	 * A recursive function for injecting data into the store.
	 *
	 * @param definition Resource definition produced by DS#defineResource
	 * @param resource Resource data as internally stored by the data store
	 * @param attrs The data to be injected. Will be an object or an array of objects.
	 * @param options Optional configuration.
	 * @returns The injected data
	 * @private
	 */
	function _inject(definition, resource, attrs, options) {
	  var _this = this;
	  var injected = undefined;

	  if (_utils['default']._a(attrs)) {
	    // have an array of objects, go ahead and inject each one individually and return the resulting array
	    injected = [];
	    for (var i = 0; i < attrs.length; i++) {
	      injected.push(_inject.call(_this, definition, resource, attrs[i], options));
	    }
	  } else {
	    // check if "idAttribute" is a computed property
	    var c = definition.computed;
	    var idA = definition.idAttribute;
	    // compute the primary key if necessary
	    if (c && c[idA]) {
	      (function () {
	        var args = [];
	        _utils['default'].forEach(c[idA].deps, function (dep) {
	          args.push(attrs[dep]);
	        });
	        attrs[idA] = c[idA][c[idA].length - 1].apply(attrs, args);
	      })();
	    }

	    if (!(idA in attrs)) {
	      var error = new _errors['default'].R(definition.name + '.inject: "attrs" must contain the property specified by "idAttribute"!');
	      options.errorFn(error);
	      throw error;
	    } else {
	      try {
	        (function () {
	          // when injecting object that contain their nested relations, this code
	          // will recursively inject them into their proper places in the data store.
	          // Magic!
	          _utils['default'].forEach(definition.relationList, function (def) {
	            var relationName = def.relation;
	            var relationDef = _this.definitions[relationName];
	            var toInject = attrs[def.localField];
	            if (typeof def.inject === 'function') {
	              def.inject(definition, def, attrs);
	            } else if (toInject && def.inject !== false) {
	              if (!relationDef) {
	                throw new _errors['default'].R(definition.name + ' relation is defined but the resource is not!');
	              }
	              // handle injecting hasMany relations
	              if (_utils['default']._a(toInject)) {
	                (function () {
	                  var items = [];
	                  _utils['default'].forEach(toInject, function (toInjectItem) {
	                    if (toInjectItem !== _this.store[relationName].index[toInjectItem[relationDef.idAttribute]]) {
	                      try {
	                        var injectedItem = relationDef.inject(toInjectItem, options.orig());
	                        if (def.foreignKey) {
	                          _utils['default'].set(injectedItem, def.foreignKey, attrs[definition.idAttribute]);
	                        }
	                        items.push(injectedItem);
	                      } catch (err) {
	                        options.errorFn(err, 'Failed to inject ' + def.type + ' relation: "' + relationName + '"!');
	                      }
	                    }
	                  });
	                })();
	              } else {
	                // handle injecting belongsTo and hasOne relations
	                if (toInject !== _this.store[relationName].index[toInject[relationDef.idAttribute]]) {
	                  try {
	                    var _injected = relationDef.inject(attrs[def.localField], options.orig());
	                    if (def.foreignKey) {
	                      _utils['default'].set(_injected, def.foreignKey, attrs[definition.idAttribute]);
	                    }
	                  } catch (err) {
	                    options.errorFn(err, 'Failed to inject ' + def.type + ' relation: "' + relationName + '"!');
	                  }
	                }
	              }
	            }
	          });

	          // primary key of item being injected
	          var id = attrs[idA];
	          // item being injected
	          var item = definition.get(id);
	          // 0 if the item is new, otherwise the previous last modified timestamp of the item
	          var initialLastModified = item ? resource.modified[id] : 0;

	          // item is new
	          if (!item) {
	            if (attrs instanceof definition[definition['class']]) {
	              item = attrs;
	            } else {
	              item = new definition[definition['class']]();
	            }

	            if (definition.instanceEvents && typeof item.emit !== 'function') {
	              _utils['default'].Events(item);
	            }
	            // remove relation properties from the item, since those relations have been injected by now
	            _utils['default'].forEach(definition.relationList, function (def) {
	              if (typeof def.link === 'boolean' ? def.link : !!definition.linkRelations) {
	                delete attrs[def.localField];
	              }
	            });

	            // copy remaining properties to the injected item
	            _utils['default'].deepMixIn(item, attrs);

	            // add item to collection
	            resource.collection.push(item);
	            resource.changeHistories[id] = [];

	            // create the observer handler for the data to be injected
	            var _react = makeObserverHandler.call(_this, definition, resource);

	            // If we're in the browser, start observation
	            if (definition.watchChanges) {
	              resource.observers[id] = new _this.observe.ObjectObserver(item);
	              resource.observers[id].open(_react, item);
	            }

	            // index item
	            resource.index[id] = item;
	            // fire observation handler for the first time
	            _react.call(item, {}, {}, {}, null, true);
	            // save "previous" attributes of the injected item, for change diffs later
	            resource.previousAttributes[id] = _utils['default'].copy(item, null, null, null, definition.relationFields);
	          } else {
	            // item is being re-injected
	            // new properties take precedence
	            if (options.onConflict === 'merge') {
	              _utils['default'].deepMixIn(item, attrs);
	            } else if (options.onConflict === 'replace') {
	              _utils['default'].forOwn(item, function (v, k) {
	                if (k !== definition.idAttribute) {
	                  if (!attrs.hasOwnProperty(k)) {
	                    delete item[k];
	                  }
	                }
	              });
	              _utils['default'].forOwn(attrs, function (v, k) {
	                if (k !== definition.idAttribute) {
	                  item[k] = v;
	                }
	              });
	            }

	            if (definition.resetHistoryOnInject) {
	              // clear change history for item
	              _this.commit(definition.name, id);
	            }
	            if (resource.observers[id] && typeof resource.observers[id] === 'function') {
	              // force observation callback to be fired if there are any changes to the item and `Object.observe` is not available
	              resource.observers[id].deliver();
	            }
	          }
	          // update modified timestamp of item
	          resource.modified[id] = initialLastModified && resource.modified[id] === initialLastModified ? _utils['default'].updateTimestamp(resource.modified[id]) : resource.modified[id];

	          // reset expiry tracking for item
	          resource.expiresHeap.remove(item);
	          var timestamp = new Date().getTime();
	          resource.expiresHeap.push({
	            item: item,
	            timestamp: timestamp,
	            expires: definition.maxAge ? timestamp + definition.maxAge : Number.MAX_VALUE
	          });

	          // final injected item
	          injected = item;
	        })();
	      } catch (err) {
	        options.errorFn(err, attrs);
	      }
	    }
	  }
	  return injected;
	}

	/**
	 * Inject the given object or array of objects into the data store.
	 *
	 * @param resourceName The name of the type of resource of the data to be injected.
	 * @param attrs Object or array of objects. Objects must contain a primary key.
	 * @param options Optional configuration.
	 * @param options.notify Whether to emit the "DS.beforeInject" and "DS.afterInject" events.
	 * @returns The injected data.
	 */
	module.exports = function inject(resourceName, attrs, options) {
	  var _this = this;
	  var definition = _this.definitions[resourceName];
	  var resource = _this.store[resourceName];
	  var injected = undefined;

	  if (!definition) {
	    throw new _errors['default'].NER(resourceName);
	  } else if (!_utils['default']._o(attrs) && !_utils['default']._a(attrs)) {
	    throw new _errors['default'].IA(resourceName + '.inject: "attrs" must be an object or an array!');
	  }

	  options = _utils['default']._(definition, options);

	  // lifecycle
	  options.beforeInject(options, attrs);
	  if (options.notify) {
	    definition.emit('DS.beforeInject', definition, attrs);
	  }

	  // start the recursive injection of data
	  injected = _inject.call(_this, definition, resource, attrs, options);

	  // collection was modified
	  definition.handleChange(injected);

	  // lifecycle
	  options.afterInject(options, injected);
	  if (options.notify) {
	    definition.emit('DS.afterInject', definition, injected);
	  }

	  return injected;
	};

/***/ },
/* 36 */
/***/ function(module, exports, __webpack_require__) {

	exports['default'] = {
	  create: __webpack_require__(37),
	  destroy: __webpack_require__(38),
	  destroyAll: __webpack_require__(39),
	  find: __webpack_require__(40),
	  findAll: __webpack_require__(41),
	  loadRelations: __webpack_require__(42),
	  reap: __webpack_require__(43),
	  refresh: function refresh(resourceName, id, options) {
	    var _this = this;
	    var DSUtils = _this.utils;

	    return new DSUtils.Promise(function (resolve, reject) {
	      var definition = _this.definitions[resourceName];
	      id = DSUtils.resolveId(_this.definitions[resourceName], id);
	      if (!definition) {
	        reject(new _this.errors.NER(resourceName));
	      } else if (!DSUtils._sn(id)) {
	        reject(DSUtils._snErr('id'));
	      } else {
	        options = DSUtils._(definition, options);
	        options.bypassCache = true;
	        resolve(_this.get(resourceName, id));
	      }
	    }).then(function (item) {
	      return item ? _this.find(resourceName, id, options) : item;
	    })['catch'](_this.errorFn('refresh', resourceName, id, options));
	  },
	  refreshAll: function refreshAll(resourceName, params, options) {
	    var _this = this;
	    var DSUtils = _this.utils;
	    var definition = _this.definitions[resourceName];
	    params = params || {};

	    return new DSUtils.Promise(function (resolve, reject) {
	      if (!definition) {
	        reject(new _this.errors.NER(resourceName));
	      } else if (!DSUtils._o(params)) {
	        reject(DSUtils._oErr('params'));
	      } else {
	        options = DSUtils._(definition, options);
	        options.bypassCache = true;
	        resolve(_this.filter(resourceName, params, options));
	      }
	    }).then(function (existing) {
	      options.bypassCache = true;
	      return _this.findAll(resourceName, params, options).then(function (found) {
	        DSUtils.forEach(existing, function (item) {
	          if (found.indexOf(item) === -1) {
	            definition.eject(item);
	          }
	        });
	        return found;
	      });
	    })['catch'](_this.errorFn('refreshAll', resourceName, params, options));
	  },
	  save: __webpack_require__(44),
	  update: __webpack_require__(45),
	  updateAll: __webpack_require__(46)
	};

/***/ },
/* 37 */
/***/ function(module, exports) {

	/**
	 * Using an adapter, create a new item.
	 *
	 * Generally a primary key will NOT be provided in the properties hash,
	 * because the adapter's persistence layer should be generating one.
	 *
	 * @param resourceName The name of the type of resource of the new item.
	 * @param attrs Hash of properties with which to create the new item.
	 * @param options Optional configuration.
	 * @param options.cacheResponse Whether the newly created item as returned by the adapter should be injected into the data store.
	 * @param options.upsert If the properties hash contains a primary key, attempt to call DS#update instead.
	 * @param options.notify Whether to emit the "DS.beforeCreate" and "DS.afterCreate" events.
	 * @param options.beforeValidate Lifecycle hook.
	 * @param options.validate Lifecycle hook.
	 * @param options.afterValidate Lifecycle hook.
	 * @param options.beforeCreate Lifecycle hook.
	 * @param options.afterCreate Lifecycle hook.
	 */
	module.exports = function create(resourceName, attrs, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var definition = _this.definitions[resourceName];
	  var adapter = undefined;

	  options = options || {};
	  attrs = attrs || {};

	  var rejectionError = undefined;
	  if (!definition) {
	    rejectionError = new _this.errors.NER(resourceName);
	  } else if (!DSUtils._o(attrs)) {
	    rejectionError = DSUtils._oErr('attrs');
	  } else {
	    options = DSUtils._(definition, options);
	    if (options.upsert && DSUtils._sn(attrs[definition.idAttribute])) {
	      return _this.update(resourceName, attrs[definition.idAttribute], attrs, options);
	    }
	  }

	  return new DSUtils.Promise(function (resolve, reject) {
	    if (rejectionError) {
	      reject(rejectionError);
	    } else {
	      resolve(attrs);
	    }
	  })
	  // start lifecycle
	  .then(function (attrs) {
	    return options.beforeValidate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    return options.validate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    return options.afterValidate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    return options.beforeCreate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    if (options.notify) {
	      definition.emit('DS.beforeCreate', definition, attrs);
	    }
	    adapter = _this.getAdapterName(options);
	    return _this.adapters[adapter].create(definition, DSUtils.omit(attrs, options.omit), options);
	  }).then(function (attrs) {
	    return options.afterCreate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    if (options.notify) {
	      definition.emit('DS.afterCreate', definition, attrs);
	    }
	    if (options.cacheResponse) {
	      // injected created intem into the store
	      var created = _this.inject(definition.name, attrs, options.orig());
	      var id = created[definition.idAttribute];
	      // mark item's `find` query as completed, so a subsequent `find` call for this item will resolve immediately
	      var resource = _this.store[resourceName];
	      resource.completedQueries[id] = new Date().getTime();
	      resource.saved[id] = DSUtils.updateTimestamp(resource.saved[id]);
	      return created;
	    } else {
	      // just return an un-injected instance
	      return _this.createInstance(resourceName, attrs, options);
	    }
	  }).then(function (item) {
	    return DSUtils.respond(item, { adapter: adapter }, options);
	  })['catch'](_this.errorFn('create', resourceName, attrs, options));
	};

/***/ },
/* 38 */
/***/ function(module, exports) {

	/**
	 * Using an adapter, destroy an item.
	 *
	 * @param resourceName The name of the type of resource of the item to destroy.
	 * @param id The primary key of the item to destroy.
	 * @param options Optional configuration.
	 * @param options.eagerEject Whether to eject the item from the store before the adapter operation completes, re-injecting if the adapter operation fails.
	 * @param options.notify Whether to emit the "DS.beforeDestroy" and "DS.afterDestroy" events.
	 * @param options.beforeDestroy Lifecycle hook.
	 * @param options.afterDestroy Lifecycle hook.
	 * @returns The primary key of the destroyed item.
	 */
	module.exports = function destroy(resourceName, id, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var definition = _this.definitions[resourceName];
	  var item = undefined,
	      adapter = undefined;

	  return new DSUtils.Promise(function (resolve, reject) {
	    id = DSUtils.resolveId(definition, id);
	    if (!definition) {
	      reject(new _this.errors.NER(resourceName));
	    } else if (!DSUtils._sn(id)) {
	      reject(DSUtils._snErr('id'));
	    } else {
	      // check if the item is in the store
	      item = definition.get(id) || { id: id };
	      options = DSUtils._(definition, options);
	      resolve(item);
	    }
	  })
	  // start lifecycle
	  .then(function (attrs) {
	    return options.beforeDestroy.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    if (options.notify) {
	      definition.emit('DS.beforeDestroy', definition, attrs);
	    }
	    // don't wait for the adapter, remove the item from the store
	    if (options.eagerEject) {
	      definition.eject(id);
	    }
	    adapter = definition.getAdapter(options);
	    return adapter.destroy(definition, id, options);
	  }).then(function () {
	    return options.afterDestroy.call(item, options, item);
	  }).then(function (item) {
	    if (options.notify) {
	      definition.emit('DS.afterDestroy', definition, item);
	    }
	    // make sure the item is removed from the store
	    definition.eject(id);
	    return DSUtils.respond(id, { adapter: adapter }, options);
	  })['catch'](function (err) {
	    // rollback by re-injecting the item into the store
	    if (options && options.eagerEject && item) {
	      definition.inject(item, { notify: false });
	    }
	    return _this.errorFn('destroy', resourceName, id, options)(err);
	  });
	};

/***/ },
/* 39 */
/***/ function(module, exports) {

	/**
	 * Using an adapter, destroy an item.
	 *
	 * @param resourceName The name of the type of resource of the item to destroy.
	 * @param params The criteria by which to filter items to destroy. See http://www.js-data.io/docs/query-syntax
	 * @param options Optional configuration.
	 * @param options.eagerEject Whether to eject the items from the store before the adapter operation completes, re-injecting if the adapter operation fails.
	 * @param options.notify Whether to emit the "DS.beforeDestroy" and "DS.afterDestroy" events.
	 * @param options.beforeDestroy Lifecycle hook.
	 * @param options.afterDestroy Lifecycle hook.
	 * @returns The ejected items, if any.
	 */
	module.exports = function destroyAll(resourceName, params, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var definition = _this.definitions[resourceName];
	  var ejected = undefined,
	      toEject = undefined,
	      adapter = undefined;

	  params = params || {};

	  return new DSUtils.Promise(function (resolve, reject) {
	    if (!definition) {
	      reject(new _this.errors.NER(resourceName));
	    } else if (!DSUtils._o(params)) {
	      reject(DSUtils._oErr('attrs'));
	    } else {
	      options = DSUtils._(definition, options);
	      DSUtils.applyScope(definition, params, options);
	      resolve();
	    }
	  }).then(function () {
	    // find items that are to be ejected from the store
	    toEject = definition.defaultFilter.call(_this, resourceName, params);
	    return options.beforeDestroy(options, toEject);
	  }).then(function () {
	    if (options.notify) {
	      definition.emit('DS.beforeDestroy', definition, toEject);
	    }
	    // don't wait for the adapter, remove the items from the store
	    if (options.eagerEject) {
	      ejected = definition.ejectAll(params);
	    }
	    adapter = definition.getAdapterName(options);
	    return _this.adapters[adapter].destroyAll(definition, params, options);
	  }).then(function () {
	    return options.afterDestroy(options, toEject);
	  }).then(function () {
	    if (options.notify) {
	      definition.emit('DS.afterDestroy', definition, toEject);
	    }
	    // make sure items are removed from the store
	    return ejected || definition.ejectAll(params);
	  }).then(function (items) {
	    return DSUtils.respond(items, { adapter: adapter }, options);
	  })['catch'](function (err) {
	    // rollback by re-injecting the items into the store
	    if (options && options.eagerEject && ejected) {
	      definition.inject(ejected, { notify: false });
	    }
	    return _this.errorFn('destroyAll', resourceName, params, options)(err);
	  });
	};

/***/ },
/* 40 */
/***/ function(module, exports) {

	/* jshint -W082 */

	/**
	 * Using an adapter, retrieve a single item.
	 *
	 * @param resourceName The of the type of resource of the item to retrieve.
	 * @param id The primary key of the item to retrieve.
	 * @param options Optional configuration.
	 * @param options.bypassCache Whether to ignore any cached item and force the retrieval through the adapter.
	 * @param options.cacheResponse Whether to inject the found item into the data store.
	 * @param options.strictCache Whether to only consider items to be "cached" if they were injected into the store as the result of `find` or `findAll`.
	 * @param options.strategy The retrieval strategy to use.
	 * @param options.findStrategy The retrieval strategy to use. Overrides "strategy".
	 * @param options.fallbackAdapters Array of names of adapters to use if using "fallback" strategy.
	 * @param options.findFallbackAdapters Array of names of adapters to use if using "fallback" strategy. Overrides "fallbackAdapters".
	 * @returns The item.
	 */
	module.exports = function find(resourceName, id, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var definition = _this.definitions[resourceName];
	  var resource = _this.store[resourceName];
	  var adapter = undefined;

	  return new DSUtils.Promise(function (resolve, reject) {
	    if (!definition) {
	      reject(new _this.errors.NER(resourceName));
	    } else if (!DSUtils._sn(id)) {
	      reject(DSUtils._snErr('id'));
	    } else {
	      options = DSUtils._(definition, options);

	      if (options.params) {
	        options.params = DSUtils.copy(options.params);
	      }

	      if (options.bypassCache || !options.cacheResponse) {
	        delete resource.completedQueries[id];
	      }

	      var expired = options.maxAge && id in resource.completedQueries && resource.completedQueries[id] + options.maxAge < new Date().getTime();

	      if ((!options.findStrictCache || id in resource.completedQueries) && definition.get(id) && !options.bypassCache && !expired) {
	        // resolve immediately with the cached item
	        resolve(definition.get(id));
	      } else {
	        // we're going to delegate to the adapter next
	        delete resource.completedQueries[id];
	        resolve();
	      }
	    }
	  }).then(function (item) {
	    if (!item) {
	      if (!(id in resource.pendingQueries)) {
	        var promise = undefined;
	        var strategy = options.findStrategy || options.strategy;

	        // try subsequent adapters if the preceeding one fails
	        if (strategy === 'fallback') {
	          var makeFallbackCall = function makeFallbackCall(index) {
	            adapter = definition.getAdapterName((options.findFallbackAdapters || options.fallbackAdapters)[index]);
	            return _this.adapters[adapter].find(definition, id, options)['catch'](function (err) {
	              index++;
	              if (index < options.fallbackAdapters.length) {
	                return makeFallbackCall(index);
	              } else {
	                return DSUtils.Promise.reject(err);
	              }
	            });
	          };

	          promise = makeFallbackCall(0);
	        } else {
	          adapter = definition.getAdapterName(options);
	          // just make a single attempt
	          promise = _this.adapters[adapter].find(definition, id, options);
	        }

	        resource.pendingQueries[id] = promise.then(function (data) {
	          return options.afterFind.call(data, options, data);
	        }).then(function (data) {
	          // Query is no longer pending
	          delete resource.pendingQueries[id];
	          if (options.cacheResponse) {
	            // inject the item into the data store
	            var injected = definition.inject(data, options.orig());
	            // mark the item as "cached"
	            resource.completedQueries[id] = new Date().getTime();
	            resource.saved[id] = DSUtils.updateTimestamp(resource.saved[id]);
	            return injected;
	          } else {
	            // just return an un-injected instance
	            return definition.createInstance(data, options.orig());
	          }
	        });
	      }
	      return resource.pendingQueries[id];
	    } else {
	      // resolve immediately with the item
	      return item;
	    }
	  }).then(function (item) {
	    return DSUtils.respond(item, { adapter: adapter }, options);
	  })['catch'](function (err) {
	    if (resource) {
	      delete resource.pendingQueries[id];
	    }
	    return _this.errorFn('find', resourceName, id, options)(err);
	  });
	};

/***/ },
/* 41 */
/***/ function(module, exports) {

	/* jshint -W082 */
	function processResults(data, resourceName, queryHash, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var definition = _this.definitions[resourceName];
	  var resource = _this.store[resourceName];
	  var idAttribute = _this.definitions[resourceName].idAttribute;
	  var date = new Date().getTime();

	  data = data || [];

	  // Query is no longer pending
	  delete resource.pendingQueries[queryHash];
	  resource.completedQueries[queryHash] = date;

	  // Merge the new values into the cache
	  var injected = definition.inject(data, options.orig());

	  // Make sure each object is added to completedQueries
	  if (DSUtils._a(injected)) {
	    DSUtils.forEach(injected, function (item) {
	      if (item) {
	        var id = item[idAttribute];
	        if (id) {
	          resource.completedQueries[id] = date;
	          resource.saved[id] = DSUtils.updateTimestamp(resource.saved[id]);
	        }
	      }
	    });
	  } else {
	    options.errorFn('response is expected to be an array!');
	    resource.completedQueries[injected[idAttribute]] = date;
	  }

	  return injected;
	}

	/**
	 * Using an adapter, retrieve a collection of items.
	 *
	 * @param resourceName The name of the type of resource of the items to retrieve.
	 * @param params The criteria by which to filter items to retrieve. See http://www.js-data.io/docs/query-syntax
	 * @param options Optional configuration.
	 * @param options.bypassCache Whether to ignore any cached query for these items and force the retrieval through the adapter.
	 * @param options.cacheResponse Whether to inject the found items into the data store.
	 * @returns The items.
	 */
	module.exports = function findAll(resourceName, params, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var definition = _this.definitions[resourceName];
	  var resource = _this.store[resourceName];
	  var queryHash = undefined,
	      adapter = undefined;

	  return new DSUtils.Promise(function (resolve, reject) {
	    params = params || {};

	    if (!_this.definitions[resourceName]) {
	      reject(new _this.errors.NER(resourceName));
	    } else if (!DSUtils._o(params)) {
	      reject(DSUtils._oErr('params'));
	    } else {
	      options = DSUtils._(definition, options);
	      queryHash = DSUtils.toJson(params);

	      if (options.params) {
	        options.params = DSUtils.copy(options.params);
	      }

	      DSUtils.applyScope(definition, params, options);

	      // force a new request
	      if (options.bypassCache || !options.cacheResponse) {
	        delete resource.completedQueries[queryHash];
	        delete resource.queryData[queryHash];
	      }

	      var expired = options.maxAge && queryHash in resource.completedQueries && resource.completedQueries[queryHash] + options.maxAge < new Date().getTime();

	      if (queryHash in resource.completedQueries && !expired) {
	        if (options.useFilter) {
	          if (options.localKeys) {
	            resolve(definition.getAll(options.localKeys, options.orig()));
	          } else {
	            // resolve immediately by filtering data from the data store
	            resolve(definition.filter(params, options.orig()));
	          }
	        } else {
	          // resolve immediately by returning the cached array from the previously made query
	          resolve(resource.queryData[queryHash]);
	        }
	      } else {
	        resolve();
	      }
	    }
	  }).then(function (items) {
	    if (!items) {
	      if (!(queryHash in resource.pendingQueries)) {
	        var promise = undefined;
	        var strategy = options.findAllStrategy || options.strategy;

	        // try subsequent adapters if the preceeding one fails
	        if (strategy === 'fallback') {
	          var makeFallbackCall = function makeFallbackCall(index) {
	            adapter = definition.getAdapterName((options.findAllFallbackAdapters || options.fallbackAdapters)[index]);
	            return _this.adapters[adapter].findAll(definition, params, options)['catch'](function (err) {
	              index++;
	              if (index < options.fallbackAdapters.length) {
	                return makeFallbackCall(index);
	              } else {
	                return DSUtils.Promise.reject(err);
	              }
	            });
	          };

	          promise = makeFallbackCall(0);
	        } else {
	          adapter = definition.getAdapterName(options);
	          // just make a single attempt
	          promise = _this.adapters[adapter].findAll(definition, params, options);
	        }

	        resource.pendingQueries[queryHash] = promise.then(function (data) {
	          return options.afterFindAll.call(data, options, data);
	        }).then(function (data) {
	          // Query is no longer pending
	          delete resource.pendingQueries[queryHash];
	          if (options.cacheResponse) {
	            // inject the items into the data store
	            resource.queryData[queryHash] = processResults.call(_this, data, resourceName, queryHash, options);
	            resource.queryData[queryHash].$$injected = true;
	            return resource.queryData[queryHash];
	          } else {
	            DSUtils.forEach(data, function (item, i) {
	              data[i] = definition.createInstance(item, options.orig());
	            });
	            return data;
	          }
	        });
	      }

	      return resource.pendingQueries[queryHash];
	    } else {
	      // resolve immediately with the items
	      return items;
	    }
	  }).then(function (items) {
	    return DSUtils.respond(items, { adapter: adapter }, options);
	  })['catch'](function (err) {
	    if (resource) {
	      delete resource.pendingQueries[queryHash];
	    }
	    return _this.errorFn('findAll', resourceName, params, options)(err);
	  });
	};

/***/ },
/* 42 */
/***/ function(module, exports) {

	function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

	/**
	 * Load the specified relations for the given instance.
	 *
	 * @param resourceName The name of the type of resource of the instance for which to load relations.
	 * @param instance The instance or the primary key of the instance.
	 * @param relations An array of the relations to load.
	 * @param options Optional configuration.
	 * @returns The instance, now with its relations loaded.
	 */
	module.exports = function loadRelations(resourceName, instance, relations, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var DSErrors = _this.errors;

	  var definition = _this.definitions[resourceName];
	  var _options = undefined;

	  return new DSUtils.Promise(function (resolve, reject) {
	    if (DSUtils._sn(instance)) {
	      instance = definition.get(instance);
	    }

	    if (DSUtils._s(relations)) {
	      relations = [relations];
	    }

	    relations = relations || [];

	    if (!definition) {
	      reject(new DSErrors.NER(resourceName));
	    } else if (!DSUtils._o(instance)) {
	      reject(new DSErrors.IA('"instance(id)" must be a string, number or object!'));
	    } else if (!DSUtils._a(relations)) {
	      reject(new DSErrors.IA('"relations" must be a string or an array!'));
	    } else {
	      (function () {
	        _options = DSUtils._(definition, options);

	        var tasks = [];

	        DSUtils.forEach(definition.relationList, function (def) {
	          var relationName = def.relation;
	          var relationDef = definition.getResource(relationName);
	          var __options = DSUtils._(relationDef, options);

	          // relations can be loaded based on resource name or field name
	          if (!relations.length || DSUtils.contains(relations, relationName) || DSUtils.contains(relations, def.localField)) {
	            var task = undefined;
	            var params = {};
	            if (__options.allowSimpleWhere) {
	              params[def.foreignKey] = instance[definition.idAttribute];
	            } else {
	              params.where = {};
	              params.where[def.foreignKey] = {
	                '==': instance[definition.idAttribute]
	              };
	            }

	            var orig = __options.orig();
	            var defKey = def.localKey ? DSUtils.get(instance, def.localKey) : null;
	            var hasDefKey = !!(defKey || defKey === 0);

	            if (typeof def.load === 'function') {
	              task = def.load(definition, def, instance, orig);
	            } else {
	              if (def.type === 'hasMany') {
	                if (def.localKeys) {
	                  delete params[def.foreignKey];
	                  var keys = DSUtils.get(instance, def.localKeys) || [];
	                  keys = DSUtils._a(keys) ? keys : DSUtils.keys(keys);
	                  params.where = _defineProperty({}, relationDef.idAttribute, {
	                    'in': keys
	                  });
	                  orig.localKeys = keys;
	                } else if (def.foreignKeys) {
	                  delete params[def.foreignKey];
	                  params.where = _defineProperty({}, def.foreignKeys, {
	                    contains: instance[definition.idAttribute]
	                  });
	                }
	                task = relationDef.findAll(params, orig);
	              } else if (def.type === 'hasOne') {
	                if (def.localKey && hasDefKey) {
	                  task = relationDef.find(defKey, orig);
	                } else if (def.foreignKey) {
	                  task = relationDef.findAll(params, orig).then(function (hasOnes) {
	                    return hasOnes.length ? hasOnes[0] : null;
	                  });
	                }
	              } else if (hasDefKey) {
	                task = relationDef.find(defKey, orig);
	              }
	            }

	            if (task) {
	              if (!_options.linkRelations) {
	                task = task.then(function (data) {
	                  instance[def.localField] = data;
	                });
	              }
	              tasks.push(task);
	            }
	          }
	        });

	        resolve(tasks);
	      })();
	    }
	  }).then(function (tasks) {
	    return DSUtils.Promise.all(tasks);
	  }).then(function () {
	    return _options.afterLoadRelations.call(instance, _options, instance);
	  })['catch'](_this.errorFn('loadRelations', resourceName, instance, relations, options));
	};

/***/ },
/* 43 */
/***/ function(module, exports) {

	/**
	 * Find expired items of the specified resource type and perform the configured action.
	 *
	 * @param resourceName The name of the type of resource of the items to reap.
	 * @param options Optional configuration.
	 * @returns The reaped items.
	 */
	module.exports = function reap(resourceName, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var definition = _this.definitions[resourceName];
	  var resource = _this.store[resourceName];

	  return new DSUtils.Promise(function (resolve, reject) {
	    if (!definition) {
	      reject(new _this.errors.NER(resourceName));
	    } else {
	      options = DSUtils._(definition, options);
	      if (!options.hasOwnProperty('notify')) {
	        options.notify = false;
	      }
	      var items = [];
	      var now = new Date().getTime();
	      var expiredItem = undefined;

	      // find the expired items
	      while ((expiredItem = resource.expiresHeap.peek()) && expiredItem.expires < now) {
	        items.push(expiredItem.item);
	        delete expiredItem.item;
	        resource.expiresHeap.pop();
	      }
	      resolve(items);
	    }
	  }).then(function (items) {
	    // only hit lifecycle if there are items
	    if (items.length) {
	      definition.beforeReap(options, items);
	      if (options.notify) {
	        definition.emit('DS.beforeReap', definition, items);
	      }
	    }

	    if (options.reapAction === 'inject') {
	      (function () {
	        var timestamp = new Date().getTime();
	        DSUtils.forEach(items, function (item) {
	          resource.expiresHeap.push({
	            item: item,
	            timestamp: timestamp,
	            expires: definition.maxAge ? timestamp + definition.maxAge : Number.MAX_VALUE
	          });
	        });
	      })();
	    } else if (options.reapAction === 'eject') {
	      DSUtils.forEach(items, function (item) {
	        definition.eject(item[definition.idAttribute]);
	      });
	    } else if (options.reapAction === 'refresh') {
	      var _ret2 = (function () {
	        var tasks = [];
	        DSUtils.forEach(items, function (item) {
	          tasks.push(definition.refresh(item[definition.idAttribute]));
	        });
	        return {
	          v: DSUtils.Promise.all(tasks)
	        };
	      })();

	      if (typeof _ret2 === 'object') return _ret2.v;
	    }
	    return items;
	  }).then(function (items) {
	    // only hit lifecycle if there are items
	    if (items.length) {
	      definition.afterReap(options, items);
	      if (options.notify) {
	        definition.emit('DS.afterReap', definition, items);
	      }
	    }
	    return items;
	  })['catch'](_this.errorFn('reap', resourceName, options));
	};

/***/ },
/* 44 */
/***/ function(module, exports) {

	/**
	 * Save a single item in its present state.
	 *
	 * @param resourceName The name of the type of resource of the item.
	 * @param id The primary key of the item.
	 * @param options Optional congifuration.
	 * @returns The item, now saved.
	 */
	module.exports = function save(resourceName, id, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var DSErrors = _this.errors;

	  var definition = _this.definitions[resourceName];
	  var resource = _this.store[resourceName];
	  var item = undefined,
	      noChanges = undefined,
	      adapter = undefined;

	  return new DSUtils.Promise(function (resolve, reject) {
	    id = DSUtils.resolveId(definition, id);
	    if (!definition) {
	      reject(new DSErrors.NER(resourceName));
	    } else if (!DSUtils._sn(id)) {
	      reject(DSUtils._snErr('id'));
	    } else if (!definition.get(id)) {
	      reject(new DSErrors.R('id "' + id + '" not found in cache!'));
	    } else {
	      item = definition.get(id);
	      options = DSUtils._(definition, options);
	      resolve(item);
	    }
	  })
	  // start lifecycle
	  .then(function (attrs) {
	    return options.beforeValidate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    return options.validate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    return options.afterValidate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    return options.beforeUpdate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    if (options.notify) {
	      definition.emit('DS.beforeUpdate', definition, attrs);
	    }
	    // only send changed properties to the adapter
	    if (options.changesOnly) {
	      if (resource.observers[id] && typeof resource.observers[id] === 'function') {
	        resource.observers[id].deliver();
	      }
	      var toKeep = [];
	      var changes = definition.changes(id);

	      for (var key in changes.added) {
	        toKeep.push(key);
	      }
	      for (key in changes.changed) {
	        toKeep.push(key);
	      }
	      changes = DSUtils.pick(attrs, toKeep);
	      // no changes? no save
	      if (DSUtils.isEmpty(changes)) {
	        // no changes, return
	        noChanges = true;
	        return attrs;
	      } else {
	        attrs = changes;
	      }
	    }
	    adapter = definition.getAdapterName(options);
	    return _this.adapters[adapter].update(definition, id, DSUtils.omit(attrs, options.omit), options);
	  }).then(function (data) {
	    return options.afterUpdate.call(data, options, data);
	  }).then(function (attrs) {
	    if (options.notify) {
	      definition.emit('DS.afterUpdate', definition, attrs);
	    }
	    if (noChanges) {
	      // no changes, just return
	      return attrs;
	    } else if (options.cacheResponse) {
	      // inject the reponse into the store, updating the item
	      var injected = definition.inject(attrs, options.orig());
	      var _id = injected[definition.idAttribute];
	      // mark the item as "saved"
	      resource.saved[_id] = DSUtils.updateTimestamp(resource.saved[_id]);
	      if (!definition.resetHistoryOnInject) {
	        resource.previousAttributes[_id] = DSUtils.copy(injected, null, null, null, definition.relationFields);
	      }
	      return injected;
	    } else {
	      // just return an instance
	      return definition.createInstance(attrs, options.orig());
	    }
	  }).then(function (item) {
	    return DSUtils.respond(item, { adapter: adapter }, options);
	  })['catch'](_this.errorFn('save', resourceName, id, options));
	};

/***/ },
/* 45 */
/***/ function(module, exports) {

	/**
	 * Update a single item using the supplied properties hash.
	 *
	 * @param resourceName The name of the type of resource of the item to update.
	 * @param id The primary key of the item to update.
	 * @param attrs The attributes with which to update the item.
	 * @param options Optional configuration.
	 * @returns The item, now updated.
	 */
	module.exports = function update(resourceName, id, attrs, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var DSErrors = _this.errors;

	  var definition = _this.definitions[resourceName];
	  var adapter = undefined;

	  return new DSUtils.Promise(function (resolve, reject) {
	    id = DSUtils.resolveId(definition, id);
	    if (!definition) {
	      reject(new DSErrors.NER(resourceName));
	    } else if (!DSUtils._sn(id)) {
	      reject(DSUtils._snErr('id'));
	    } else {
	      options = DSUtils._(definition, options);
	      resolve(attrs);
	    }
	  })
	  // start lifecycle
	  .then(function (attrs) {
	    return options.beforeValidate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    return options.validate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    return options.afterValidate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    return options.beforeUpdate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    if (options.notify) {
	      definition.emit('DS.beforeUpdate', definition, attrs);
	    }
	    adapter = definition.getAdapterName(options);
	    return _this.adapters[adapter].update(definition, id, DSUtils.omit(attrs, options.omit), options);
	  }).then(function (data) {
	    return options.afterUpdate.call(data, options, data);
	  }).then(function (attrs) {
	    if (options.notify) {
	      definition.emit('DS.afterUpdate', definition, attrs);
	    }
	    if (options.cacheResponse) {
	      // inject the updated item into the store
	      var injected = definition.inject(attrs, options.orig());
	      var resource = _this.store[resourceName];
	      var _id = injected[definition.idAttribute];
	      // mark the item as "saved"
	      resource.saved[_id] = DSUtils.updateTimestamp(resource.saved[_id]);
	      if (!definition.resetHistoryOnInject) {
	        resource.previousAttributes[_id] = DSUtils.copy(injected, null, null, null, definition.relationFields);
	      }
	      return injected;
	    } else {
	      // just return an instance
	      return definition.createInstance(attrs, options.orig());
	    }
	  }).then(function (item) {
	    return DSUtils.respond(item, { adapter: adapter }, options);
	  })['catch'](_this.errorFn('update', resourceName, id, attrs, options));
	};

/***/ },
/* 46 */
/***/ function(module, exports) {

	/**
	 * Update a collection of items using the supplied properties hash.
	 *
	 * @param resourceName The name of the type of resource of the items to update.
	 * @param attrs  The attributes with which to update the item.
	 * @param params The criteria by which to select items to update. See http://www.js-data.io/docs/query-syntax
	 * @param options Optional configuration.
	 * @returns The updated items.
	 */
	module.exports = function updateAll(resourceName, attrs, params, options) {
	  var _this = this;
	  var DSUtils = _this.utils;
	  var DSErrors = _this.errors;

	  var definition = _this.definitions[resourceName];
	  var adapter = undefined;

	  return new DSUtils.Promise(function (resolve, reject) {
	    if (!definition) {
	      reject(new DSErrors.NER(resourceName));
	    } else {
	      options = DSUtils._(definition, options);
	      DSUtils.applyScope(definition, params, options);
	      resolve(attrs);
	    }
	  })
	  // start lifecycle
	  .then(function (attrs) {
	    return options.beforeValidate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    return options.validate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    return options.afterValidate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    return options.beforeUpdate.call(attrs, options, attrs);
	  }).then(function (attrs) {
	    if (options.notify) {
	      definition.emit('DS.beforeUpdate', definition, attrs);
	    }
	    adapter = definition.getAdapterName(options);
	    return _this.adapters[adapter].updateAll(definition, DSUtils.omit(attrs, options.omit), params, options);
	  }).then(function (data) {
	    return options.afterUpdate.call(data, options, data);
	  }).then(function (data) {
	    if (options.notify) {
	      definition.emit('DS.afterUpdate', definition, attrs);
	    }
	    var origOptions = options.orig();
	    if (options.cacheResponse) {
	      var _ret = (function () {
	        // inject the updated items into the store
	        var injected = definition.inject(data, origOptions);
	        var resource = _this.store[resourceName];
	        // mark the items as "saved"
	        DSUtils.forEach(injected, function (i) {
	          var id = i[definition.idAttribute];
	          resource.saved[id] = DSUtils.updateTimestamp(resource.saved[id]);
	          if (!definition.resetHistoryOnInject) {
	            resource.previousAttributes[id] = DSUtils.copy(i, null, null, null, definition.relationFields);
	          }
	        });
	        return {
	          v: injected
	        };
	      })();

	      if (typeof _ret === 'object') return _ret.v;
	    } else {
	      var _ret2 = (function () {
	        // just return instances
	        var instances = [];
	        DSUtils.forEach(data, function (item) {
	          instances.push(definition.createInstance(item, origOptions));
	        });
	        return {
	          v: instances
	        };
	      })();

	      if (typeof _ret2 === 'object') return _ret2.v;
	    }
	  }).then(function (items) {
	    return DSUtils.respond(items, { adapter: adapter }, options);
	  })['catch'](_this.errorFn('updateAll', resourceName, attrs, params, options));
	};

/***/ }
/******/ ])
});
;
;
/*!
 * js-data-angular
 * @version 3.1.0 - Homepage <https://www.js-data.io/docs/js-data-angular/>
 * @author Jason Dobry <jason.dobry@gmail.com>
 * @copyright (c) 2014-2015 Jason Dobry 
 * @license MIT <https://github.com/js-data/js-data-angular/blob/master/LICENSE>
 * 
 * @overview Angular wrapper for js-data.
 */
(function webpackUniversalModuleDefinition(root, factory) {
	if(typeof exports === 'object' && typeof module === 'object')
		module.exports = factory(require("js-data"), require("angular"), (function webpackLoadOptionalExternalModule() { try { return require("axios"); } catch(e) {} }()));
	else if(typeof define === 'function' && define.amd)
		define(["js-data", "angular"], function webpackLoadOptionalExternalModuleAmd(__WEBPACK_EXTERNAL_MODULE_1__, __WEBPACK_EXTERNAL_MODULE_4__) {
			return factory(__WEBPACK_EXTERNAL_MODULE_1__, __WEBPACK_EXTERNAL_MODULE_4__, root["axios"]);
		});
	else if(typeof exports === 'object')
		exports["jsDataAngularModuleName"] = factory(require("js-data"), require("angular"), (function webpackLoadOptionalExternalModule() { try { return require("axios"); } catch(e) {} }()));
	else
		root["jsDataAngularModuleName"] = factory(root["JSData"], root["angular"], root["axios"]);
})(this, function(__WEBPACK_EXTERNAL_MODULE_1__, __WEBPACK_EXTERNAL_MODULE_4__, __WEBPACK_EXTERNAL_MODULE_3__) {
return /******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};

/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {

/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId])
/******/ 			return installedModules[moduleId].exports;

/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			exports: {},
/******/ 			id: moduleId,
/******/ 			loaded: false
/******/ 		};

/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);

/******/ 		// Flag the module as loaded
/******/ 		module.loaded = true;

/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}


/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;

/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;

/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";

/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(0);
/******/ })
/************************************************************************/
/******/ ([
/* 0 */
/***/ function(module, exports, __webpack_require__) {

	function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError('Cannot call a class as a function'); } }

	/* jshint loopfunc:true */
	var JSData = __webpack_require__(1);
	var DSHttpAdapter = __webpack_require__(2);
	var angular = __webpack_require__(4);

	var DSUtils = JSData.DSUtils;
	var DSErrors = JSData.DSErrors;
	var isString = DSUtils.isString;
	var isNumber = DSUtils.isNumber;
	var isObject = DSUtils.isObject;
	var set = DSUtils.set;
	var resolveId = DSUtils.resolveId;

	var adapters = [{
	  project: 'js-data-localstorage',
	  name: 'localstorage',
	  'class': 'DSLocalStorageAdapter'
	}, {
	  project: 'js-data-localforage',
	  name: 'localforage',
	  'class': 'DSLocalForageAdapter'
	}, {
	  project: 'js-data-firebase',
	  name: 'firebase',
	  'class': 'DSFirebaseAdapter'
	}, {
	  project: 'js-data-sql',
	  name: 'sql',
	  'class': 'DSSqlAdapter'
	}];

	var functionsToWrap = ['compute', 'digest', 'eject', 'inject'];

	function registerAdapter(adapter) {
	  var Adapter = undefined;

	  try {
	    Adapter = __webpack_require__(5)(adapter.project);
	  } catch (e) {}

	  if (!Adapter) {
	    Adapter = window[adapter['class']];
	  }

	  if (Adapter) {
	    adapter.loaded = true;
	    angular.module('js-data').provider(adapter['class'], function () {
	      var _this = this;
	      _this.defaults = {};
	      _this.$get = [function () {
	        return new Adapter(_this.defaults);
	      }];
	    });
	  }
	}

	var DSHttpAdapterProvider = function DSHttpAdapterProvider() {
	  _classCallCheck(this, DSHttpAdapterProvider);

	  var defaults = {};
	  this.defaults = defaults;

	  this.$get = ['$http', 'DS', function ($http, DS) {
	    defaults.http = defaults.http || $http;
	    var adapter = new DSHttpAdapter(defaults);
	    DS.registerAdapter('http', adapter, { 'default': true });
	    return adapter;
	  }];
	};

	var DSProvider = function DSProvider() {
	  _classCallCheck(this, DSProvider);

	  var _this = this;
	  var deps = [];

	  for (var i = 0; i < adapters.length; i++) {
	    if (adapters[i].loaded) {
	      deps.push(adapters[i]['class']);
	    }
	  }

	  _this.defaults = {};

	  JSData.DS.prototype.bindAll = function (resourceName, params, scope, expr, cb) {
	    var _this = this;

	    params = params || {};

	    if (!_this.definitions[resourceName]) {
	      throw new DSErrors.NER(resourceName);
	    } else if (!isObject(params)) {
	      throw new DSErrors.IA('"params" must be an object!');
	    } else if (!isObject(scope)) {
	      throw new DSErrors.IA('"scope" must be an object!');
	    } else if (!isString(expr)) {
	      throw new DSErrors.IA('"expr" must be a string!');
	    }

	    try {
	      return scope.$watch(function () {
	        return _this.lastModified(resourceName);
	      }, function () {
	        var items = _this.filter(resourceName, params);
	        set(scope, expr, items);
	        if (cb) {
	          cb(null, items);
	        }
	      });
	    } catch (err) {
	      if (cb) {
	        cb(err);
	      } else {
	        throw err;
	      }
	    }
	  };

	  JSData.DS.prototype.bindOne = function (resourceName, id, scope, expr, cb) {
	    var _this = this;

	    id = resolveId(_this.definitions[resourceName], id);
	    if (!_this.definitions[resourceName]) {
	      throw new DSErrors.NER(resourceName);
	    } else if (!isString(id) && !isNumber(id)) {
	      throw new DSErrors.IA('"id" must be a string or a number!');
	    } else if (!isObject(scope)) {
	      throw new DSErrors.IA('"scope" must be an object!');
	    } else if (!isString(expr)) {
	      throw new DSErrors.IA('"expr" must be a string!');
	    }

	    try {
	      return scope.$watch(function () {
	        return _this.lastModified(resourceName, id);
	      }, function () {
	        var item = _this.get(resourceName, id);
	        if (item) {
	          _this.compute(resourceName, id);
	        }
	        set(scope, expr, item);
	        if (cb) {
	          cb(null, item);
	        }
	      });
	    } catch (err) {
	      if (cb) {
	        cb(err);
	      } else {
	        throw err;
	      }
	    }
	  };

	  function load() {
	    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
	      args[_key] = arguments[_key];
	    }

	    var $rootScope = args[args.length - 2];
	    var $q = args[args.length - 1];
	    var store = new JSData.DS(_this.defaults);
	    var originals = {};

	    function QPromise(executor) {
	      var deferred = $q.defer();

	      try {
	        executor(angular.bind(deferred, deferred.resolve), angular.bind(deferred, deferred.reject));
	      } catch (err) {
	        deferred.reject(err);
	      }

	      return deferred.promise;
	    }

	    QPromise.all = $q.all;
	    QPromise.when = $q.when;
	    QPromise.reject = $q.reject;

	    DSUtils.Promise = QPromise;

	    // Register any adapters that have been loaded
	    if (args.length) {
	      for (var i = 0; i < args.length; i++) {
	        for (var j = 0; j < adapters.length; j++) {
	          if (adapters[j].loaded && !adapters[j].registered) {
	            adapters[j].registered = true;
	            store.registerAdapter(adapters[j].name, args[i]);
	            break;
	          }
	        }
	      }
	    }

	    // Wrap certain sync functions with $apply

	    var _loop = function () {
	      var name = functionsToWrap[k];
	      originals[name] = store[name];
	      store[name] = function () {
	        for (var _len2 = arguments.length, args = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
	          args[_key2] = arguments[_key2];
	        }

	        if (!$rootScope.$$phase) {
	          return $rootScope.$apply(function () {
	            return originals[name].apply(store, args);
	          });
	        }
	        return originals[name].apply(store, args);
	      };
	    };

	    for (var k = 0; k < functionsToWrap.length; k++) {
	      _loop();
	    }

	    // Hook into the digest loop
	    if (typeof Object.observe !== 'function' || typeof Array.observe !== 'function') {
	      $rootScope.$watch(function () {
	        return store.observe.Platform.performMicrotaskCheckpoint();
	      });
	    }

	    return store;
	  }

	  deps.push('$rootScope');
	  deps.push('$q');
	  deps.push(load);

	  _this.$get = deps;
	};

	angular.module('js-data', ['ng']).value('DSUtils', DSUtils).value('DSErrors', DSErrors).value('DSVersion', JSData.version).provider('DS', DSProvider).provider('DSHttpAdapter', DSHttpAdapterProvider).run(['DS', 'DSHttpAdapter', function (DS, DSHttpAdapter) {
	  DS.registerAdapter('http', DSHttpAdapter, { 'default': true });
	}]);

	for (var i = 0; i < adapters.length; i++) {
	  registerAdapter(adapters[i]);
	}

	// return the module name
	module.exports = 'js-data';
	module.exports.name = 'js-data';

/***/ },
/* 1 */
/***/ function(module, exports) {

	module.exports = __WEBPACK_EXTERNAL_MODULE_1__;

/***/ },
/* 2 */
/***/ function(module, exports, __webpack_require__) {

	var _createClass = (function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ('value' in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; })();

	function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError('Cannot call a class as a function'); } }

	var JSData = __webpack_require__(1);
	var axios = null;

	try {
	  axios = __webpack_require__(3);
	} catch (e) {}

	var DSUtils = JSData.DSUtils;
	var deepMixIn = DSUtils.deepMixIn;
	var removeCircular = DSUtils.removeCircular;
	var copy = DSUtils.copy;
	var makePath = DSUtils.makePath;
	var isString = DSUtils.isString;
	var isNumber = DSUtils.isNumber;

	var Defaults = (function () {
	  function Defaults() {
	    _classCallCheck(this, Defaults);
	  }

	  _createClass(Defaults, [{
	    key: 'queryTransform',
	    value: function queryTransform(resourceConfig, params) {
	      return params;
	    }
	  }, {
	    key: 'deserialize',
	    value: function deserialize(resourceConfig, data) {
	      return data ? 'data' in data ? data.data : data : data;
	    }
	  }, {
	    key: 'serialize',
	    value: function serialize(resourceConfig, data) {
	      return data;
	    }
	  }, {
	    key: 'log',
	    value: function log() {}
	  }, {
	    key: 'error',
	    value: function error() {}
	  }]);

	  return Defaults;
	})();

	var defaultsPrototype = Defaults.prototype;

	defaultsPrototype.basePath = '';

	defaultsPrototype.forceTrailingSlash = '';

	defaultsPrototype.httpConfig = {};

	defaultsPrototype.verbsUseBasePath = false;

	var DSHttpAdapter = (function () {
	  function DSHttpAdapter(options) {
	    _classCallCheck(this, DSHttpAdapter);

	    options = options || {};
	    this.defaults = new Defaults();
	    if (console) {
	      this.defaults.log = function (a, b) {
	        return console[typeof console.info === 'function' ? 'info' : 'log'](a, b);
	      };
	    }
	    if (console) {
	      this.defaults.error = function (a, b) {
	        return console[typeof console.error === 'function' ? 'error' : 'log'](a, b);
	      };
	    }
	    deepMixIn(this.defaults, options);
	    this.http = options.http || axios;
	  }

	  _createClass(DSHttpAdapter, [{
	    key: 'getEndpoint',
	    value: function getEndpoint(resourceConfig, id, options) {
	      var _this2 = this;

	      options = options || {};
	      options.params = options.params || {};

	      var item = undefined;
	      var parentKey = resourceConfig.parentKey;
	      var endpoint = options.hasOwnProperty('endpoint') ? options.endpoint : resourceConfig.endpoint;
	      var parentField = resourceConfig.parentField;
	      var parentDef = resourceConfig.getResource(resourceConfig.parent);
	      var parentId = options.params[parentKey];

	      if (parentId === false || !parentKey || !parentDef) {
	        if (parentId === false) {
	          delete options.params[parentKey];
	        }
	        return endpoint;
	      } else {
	        delete options.params[parentKey];

	        if (DSUtils._sn(id)) {
	          item = resourceConfig.get(id);
	        } else if (DSUtils._o(id)) {
	          item = id;
	        }

	        if (item) {
	          parentId = parentId || item[parentKey] || (item[parentField] ? item[parentField][parentDef.idAttribute] : null);
	        }

	        if (parentId) {
	          var _ret = (function () {
	            delete options.endpoint;
	            var _options = {};
	            DSUtils.forOwn(options, function (value, key) {
	              _options[key] = value;
	            });
	            return {
	              v: DSUtils.makePath(_this2.getEndpoint(parentDef, parentId, DSUtils._(parentDef, _options)), parentId, endpoint)
	            };
	          })();

	          if (typeof _ret === 'object') return _ret.v;
	        } else {
	          return endpoint;
	        }
	      }
	    }
	  }, {
	    key: 'getPath',
	    value: function getPath(method, resourceConfig, id, options) {
	      var _this = this;
	      options = options || {};
	      if (isString(options.urlPath)) {
	        return makePath.apply(DSUtils, [options.basePath || _this.defaults.basePath || resourceConfig.basePath, options.urlPath]);
	      } else {
	        var args = [options.basePath || _this.defaults.basePath || resourceConfig.basePath, this.getEndpoint(resourceConfig, isString(id) || isNumber(id) || method === 'create' ? id : null, options)];
	        if (method === 'find' || method === 'update' || method === 'destroy') {
	          args.push(id);
	        }
	        return makePath.apply(DSUtils, args);
	      }
	    }
	  }, {
	    key: 'HTTP',
	    value: function HTTP(config) {
	      var _this = this;
	      var start = new Date();
	      config = copy(config);
	      config = deepMixIn(config, _this.defaults.httpConfig);
	      if (!('verbsUseBasePath' in config)) {
	        config.verbsUseBasePath = _this.defaults.verbsUseBasePath;
	      }
	      if (!config.urlOverride && config.verbsUseBasePath) {
	        config.url = makePath(config.basePath || _this.defaults.basePath, config.url);
	      }
	      if (_this.defaults.forceTrailingSlash && config.url[config.url.length - 1] !== '/' && !config.urlOverride) {
	        config.url += '/';
	      }
	      if (typeof config.data === 'object') {
	        config.data = removeCircular(config.data);
	      }
	      config.method = config.method.toUpperCase();
	      var suffix = config.suffix || _this.defaults.suffix;
	      if (suffix && config.url.substr(config.url.length - suffix.length) !== suffix && !config.urlOverride) {
	        config.url += suffix;
	      }

	      // logs the HTTP response
	      function logResponse(data, isRejection) {
	        data = data || {};
	        // examine the data object
	        if (data instanceof Error) {
	          // log the Error object
	          _this.defaults.error('FAILED: ' + (data.message || 'Unknown Error'), data);
	          return DSUtils.Promise.reject(data);
	        } else if (typeof data === 'object') {
	          var str = start.toUTCString() + ' - ' + config.method + ' ' + config.url + ' - ' + data.status + ' ' + (new Date().getTime() - start.getTime()) + 'ms';

	          if (data.status >= 200 && data.status < 300 && !isRejection) {
	            if (_this.defaults.log) {
	              _this.defaults.log(str, data);
	            }
	            return data;
	          } else {
	            if (_this.defaults.error) {
	              _this.defaults.error('FAILED: ' + str, data);
	            }
	            return DSUtils.Promise.reject(data);
	          }
	        } else {
	          // unknown type for 'data' that is not an Object or Error
	          _this.defaults.error('FAILED', data);
	          return DSUtils.Promise.reject(data);
	        }
	      }

	      if (!this.http) {
	        throw new Error('You have not configured this adapter with an http library!');
	      }

	      return this.http(config).then(logResponse, function (data) {
	        return logResponse(data, true);
	      });
	    }
	  }, {
	    key: 'GET',
	    value: function GET(url, config) {
	      config = config || {};
	      config.method = config.method || 'get';
	      config.urlOverride = !!config.url;
	      config.url = config.url || url;
	      return this.HTTP(config);
	    }
	  }, {
	    key: 'POST',
	    value: function POST(url, attrs, config) {
	      config = config || {};
	      config.method = config.method || 'post';
	      config.urlOverride = !!config.url;
	      config.url = config.url || url;
	      config.data = config.data || attrs;
	      return this.HTTP(config);
	    }
	  }, {
	    key: 'PUT',
	    value: function PUT(url, attrs, config) {
	      config = config || {};
	      config.method = config.method || 'put';
	      config.urlOverride = !!config.url;
	      config.url = config.url || url;
	      config.data = config.data || attrs;
	      return this.HTTP(config);
	    }
	  }, {
	    key: 'DEL',
	    value: function DEL(url, config) {
	      config = config || {};
	      config.method = config.method || 'delete';
	      config.urlOverride = !!config.url;
	      config.url = config.url || url;
	      return this.HTTP(config);
	    }
	  }, {
	    key: 'find',
	    value: function find(resourceConfig, id, options) {
	      var _this = this;
	      options = options ? copy(options) : {};
	      options.suffix = options.suffix || resourceConfig.suffix;
	      options.params = options.params || {};
	      options.params = _this.defaults.queryTransform(resourceConfig, options.params);
	      return _this.GET(_this.getPath('find', resourceConfig, id, options), options).then(function (data) {
	        var item = (options.deserialize ? options.deserialize : _this.defaults.deserialize)(resourceConfig, data);
	        return !item ? DSUtils.Promise.reject(new Error('Not Found!')) : item;
	      });
	    }
	  }, {
	    key: 'findAll',
	    value: function findAll(resourceConfig, params, options) {
	      var _this = this;
	      options = options ? copy(options) : {};
	      options.suffix = options.suffix || resourceConfig.suffix;
	      options.params = options.params || {};
	      if (params) {
	        params = _this.defaults.queryTransform(resourceConfig, params);
	        deepMixIn(options.params, params);
	      }
	      return _this.GET(_this.getPath('findAll', resourceConfig, params, options), options).then(function (data) {
	        return (options.deserialize ? options.deserialize : _this.defaults.deserialize)(resourceConfig, data);
	      });
	    }
	  }, {
	    key: 'create',
	    value: function create(resourceConfig, attrs, options) {
	      var _this = this;
	      options = options ? copy(options) : {};
	      options.suffix = options.suffix || resourceConfig.suffix;
	      options.params = options.params || {};
	      options.params = _this.defaults.queryTransform(resourceConfig, options.params);
	      return _this.POST(_this.getPath('create', resourceConfig, attrs, options), options.serialize ? options.serialize(resourceConfig, attrs) : _this.defaults.serialize(resourceConfig, attrs), options).then(function (data) {
	        return (options.deserialize ? options.deserialize : _this.defaults.deserialize)(resourceConfig, data);
	      });
	    }
	  }, {
	    key: 'update',
	    value: function update(resourceConfig, id, attrs, options) {
	      var _this = this;
	      options = options ? copy(options) : {};
	      options.suffix = options.suffix || resourceConfig.suffix;
	      options.params = options.params || {};
	      options.params = _this.defaults.queryTransform(resourceConfig, options.params);
	      return _this.PUT(_this.getPath('update', resourceConfig, id, options), options.serialize ? options.serialize(resourceConfig, attrs) : _this.defaults.serialize(resourceConfig, attrs), options).then(function (data) {
	        return (options.deserialize ? options.deserialize : _this.defaults.deserialize)(resourceConfig, data);
	      });
	    }
	  }, {
	    key: 'updateAll',
	    value: function updateAll(resourceConfig, attrs, params, options) {
	      var _this = this;
	      options = options ? copy(options) : {};
	      options.suffix = options.suffix || resourceConfig.suffix;
	      options.params = options.params || {};
	      if (params) {
	        params = _this.defaults.queryTransform(resourceConfig, params);
	        deepMixIn(options.params, params);
	      }
	      return this.PUT(_this.getPath('updateAll', resourceConfig, attrs, options), options.serialize ? options.serialize(resourceConfig, attrs) : _this.defaults.serialize(resourceConfig, attrs), options).then(function (data) {
	        return (options.deserialize ? options.deserialize : _this.defaults.deserialize)(resourceConfig, data);
	      });
	    }
	  }, {
	    key: 'destroy',
	    value: function destroy(resourceConfig, id, options) {
	      var _this = this;
	      options = options ? copy(options) : {};
	      options.suffix = options.suffix || resourceConfig.suffix;
	      options.params = options.params || {};
	      options.params = _this.defaults.queryTransform(resourceConfig, options.params);
	      return _this.DEL(_this.getPath('destroy', resourceConfig, id, options), options).then(function (data) {
	        return (options.deserialize ? options.deserialize : _this.defaults.deserialize)(resourceConfig, data);
	      });
	    }
	  }, {
	    key: 'destroyAll',
	    value: function destroyAll(resourceConfig, params, options) {
	      var _this = this;
	      options = options ? copy(options) : {};
	      options.suffix = options.suffix || resourceConfig.suffix;
	      options.params = options.params || {};
	      if (params) {
	        params = _this.defaults.queryTransform(resourceConfig, params);
	        deepMixIn(options.params, params);
	      }
	      return this.DEL(_this.getPath('destroyAll', resourceConfig, params, options), options).then(function (data) {
	        return (options.deserialize ? options.deserialize : _this.defaults.deserialize)(resourceConfig, data);
	      });
	    }
	  }]);

	  return DSHttpAdapter;
	})();

	module.exports = DSHttpAdapter;

/***/ },
/* 3 */
/***/ function(module, exports) {

	if(typeof __WEBPACK_EXTERNAL_MODULE_3__ === 'undefined') {var e = new Error("Cannot find module \"axios\""); e.code = 'MODULE_NOT_FOUND'; throw e;}
	module.exports = __WEBPACK_EXTERNAL_MODULE_3__;

/***/ },
/* 4 */
/***/ function(module, exports) {

	module.exports = __WEBPACK_EXTERNAL_MODULE_4__;

/***/ },
/* 5 */
/***/ function(module, exports, __webpack_require__) {

	var map = {};
	function webpackContext(req) {
		return __webpack_require__(webpackContextResolve(req));
	};
	function webpackContextResolve(req) {
		return map[req] || (function() { throw new Error("Cannot find module '" + req + "'.") }());
	};
	webpackContext.keys = function webpackContextKeys() {
		return Object.keys(map);
	};
	webpackContext.resolve = webpackContextResolve;
	module.exports = webpackContext;
	webpackContext.id = 5;


/***/ }
/******/ ])
});
;;
/**
 * State-based routing for AngularJS
 * @version v0.4.3
 * @link http://angular-ui.github.com/
 * @license MIT License, http://www.opensource.org/licenses/MIT
 */

/* commonjs package manager support (eg componentjs) */
if (typeof module !== "undefined" && typeof exports !== "undefined" && module.exports === exports){
  module.exports = 'ui.router';
}

(function (window, angular, undefined) {
/*jshint globalstrict:true*/
/*global angular:false*/
'use strict';

var isDefined = angular.isDefined,
    isFunction = angular.isFunction,
    isString = angular.isString,
    isObject = angular.isObject,
    isArray = angular.isArray,
    forEach = angular.forEach,
    extend = angular.extend,
    copy = angular.copy,
    toJson = angular.toJson;

function inherit(parent, extra) {
  return extend(new (extend(function() {}, { prototype: parent }))(), extra);
}

function merge(dst) {
  forEach(arguments, function(obj) {
    if (obj !== dst) {
      forEach(obj, function(value, key) {
        if (!dst.hasOwnProperty(key)) dst[key] = value;
      });
    }
  });
  return dst;
}

/**
 * Finds the common ancestor path between two states.
 *
 * @param {Object} first The first state.
 * @param {Object} second The second state.
 * @return {Array} Returns an array of state names in descending order, not including the root.
 */
function ancestors(first, second) {
  var path = [];

  for (var n in first.path) {
    if (first.path[n] !== second.path[n]) break;
    path.push(first.path[n]);
  }
  return path;
}

/**
 * IE8-safe wrapper for `Object.keys()`.
 *
 * @param {Object} object A JavaScript object.
 * @return {Array} Returns the keys of the object as an array.
 */
function objectKeys(object) {
  if (Object.keys) {
    return Object.keys(object);
  }
  var result = [];

  forEach(object, function(val, key) {
    result.push(key);
  });
  return result;
}

/**
 * IE8-safe wrapper for `Array.prototype.indexOf()`.
 *
 * @param {Array} array A JavaScript array.
 * @param {*} value A value to search the array for.
 * @return {Number} Returns the array index value of `value`, or `-1` if not present.
 */
function indexOf(array, value) {
  if (Array.prototype.indexOf) {
    return array.indexOf(value, Number(arguments[2]) || 0);
  }
  var len = array.length >>> 0, from = Number(arguments[2]) || 0;
  from = (from < 0) ? Math.ceil(from) : Math.floor(from);

  if (from < 0) from += len;

  for (; from < len; from++) {
    if (from in array && array[from] === value) return from;
  }
  return -1;
}

/**
 * Merges a set of parameters with all parameters inherited between the common parents of the
 * current state and a given destination state.
 *
 * @param {Object} currentParams The value of the current state parameters ($stateParams).
 * @param {Object} newParams The set of parameters which will be composited with inherited params.
 * @param {Object} $current Internal definition of object representing the current state.
 * @param {Object} $to Internal definition of object representing state to transition to.
 */
function inheritParams(currentParams, newParams, $current, $to) {
  var parents = ancestors($current, $to), parentParams, inherited = {}, inheritList = [];

  for (var i in parents) {
    if (!parents[i] || !parents[i].params) continue;
    parentParams = objectKeys(parents[i].params);
    if (!parentParams.length) continue;

    for (var j in parentParams) {
      if (indexOf(inheritList, parentParams[j]) >= 0) continue;
      inheritList.push(parentParams[j]);
      inherited[parentParams[j]] = currentParams[parentParams[j]];
    }
  }
  return extend({}, inherited, newParams);
}

/**
 * Performs a non-strict comparison of the subset of two objects, defined by a list of keys.
 *
 * @param {Object} a The first object.
 * @param {Object} b The second object.
 * @param {Array} keys The list of keys within each object to compare. If the list is empty or not specified,
 *                     it defaults to the list of keys in `a`.
 * @return {Boolean} Returns `true` if the keys match, otherwise `false`.
 */
function equalForKeys(a, b, keys) {
  if (!keys) {
    keys = [];
    for (var n in a) keys.push(n); // Used instead of Object.keys() for IE8 compatibility
  }

  for (var i=0; i<keys.length; i++) {
    var k = keys[i];
    if (a[k] != b[k]) return false; // Not '===', values aren't necessarily normalized
  }
  return true;
}

/**
 * Returns the subset of an object, based on a list of keys.
 *
 * @param {Array} keys
 * @param {Object} values
 * @return {Boolean} Returns a subset of `values`.
 */
function filterByKeys(keys, values) {
  var filtered = {};

  forEach(keys, function (name) {
    filtered[name] = values[name];
  });
  return filtered;
}

// like _.indexBy
// when you know that your index values will be unique, or you want last-one-in to win
function indexBy(array, propName) {
  var result = {};
  forEach(array, function(item) {
    result[item[propName]] = item;
  });
  return result;
}

// extracted from underscore.js
// Return a copy of the object only containing the whitelisted properties.
function pick(obj) {
  var copy = {};
  var keys = Array.prototype.concat.apply(Array.prototype, Array.prototype.slice.call(arguments, 1));
  forEach(keys, function(key) {
    if (key in obj) copy[key] = obj[key];
  });
  return copy;
}

// extracted from underscore.js
// Return a copy of the object omitting the blacklisted properties.
function omit(obj) {
  var copy = {};
  var keys = Array.prototype.concat.apply(Array.prototype, Array.prototype.slice.call(arguments, 1));
  for (var key in obj) {
    if (indexOf(keys, key) == -1) copy[key] = obj[key];
  }
  return copy;
}

function pluck(collection, key) {
  var result = isArray(collection) ? [] : {};

  forEach(collection, function(val, i) {
    result[i] = isFunction(key) ? key(val) : val[key];
  });
  return result;
}

function filter(collection, callback) {
  var array = isArray(collection);
  var result = array ? [] : {};
  forEach(collection, function(val, i) {
    if (callback(val, i)) {
      result[array ? result.length : i] = val;
    }
  });
  return result;
}

function map(collection, callback) {
  var result = isArray(collection) ? [] : {};

  forEach(collection, function(val, i) {
    result[i] = callback(val, i);
  });
  return result;
}

// issue #2676 #2889
function silenceUncaughtInPromise (promise) {
  return promise.then(undefined, function() {}) && promise;
}

/**
 * @ngdoc overview
 * @name ui.router.util
 *
 * @description
 * # ui.router.util sub-module
 *
 * This module is a dependency of other sub-modules. Do not include this module as a dependency
 * in your angular app (use {@link ui.router} module instead).
 *
 */
angular.module('ui.router.util', ['ng']);

/**
 * @ngdoc overview
 * @name ui.router.router
 * 
 * @requires ui.router.util
 *
 * @description
 * # ui.router.router sub-module
 *
 * This module is a dependency of other sub-modules. Do not include this module as a dependency
 * in your angular app (use {@link ui.router} module instead).
 */
angular.module('ui.router.router', ['ui.router.util']);

/**
 * @ngdoc overview
 * @name ui.router.state
 * 
 * @requires ui.router.router
 * @requires ui.router.util
 *
 * @description
 * # ui.router.state sub-module
 *
 * This module is a dependency of the main ui.router module. Do not include this module as a dependency
 * in your angular app (use {@link ui.router} module instead).
 * 
 */
angular.module('ui.router.state', ['ui.router.router', 'ui.router.util']);

/**
 * @ngdoc overview
 * @name ui.router
 *
 * @requires ui.router.state
 *
 * @description
 * # ui.router
 * 
 * ## The main module for ui.router 
 * There are several sub-modules included with the ui.router module, however only this module is needed
 * as a dependency within your angular app. The other modules are for organization purposes. 
 *
 * The modules are:
 * * ui.router - the main "umbrella" module
 * * ui.router.router - 
 * 
 * *You'll need to include **only** this module as the dependency within your angular app.*
 * 
 * <pre>
 * <!doctype html>
 * <html ng-app="myApp">
 * <head>
 *   <script src="js/angular.js"></script>
 *   <!-- Include the ui-router script -->
 *   <script src="js/angular-ui-router.min.js"></script>
 *   <script>
 *     // ...and add 'ui.router' as a dependency
 *     var myApp = angular.module('myApp', ['ui.router']);
 *   </script>
 * </head>
 * <body>
 * </body>
 * </html>
 * </pre>
 */
angular.module('ui.router', ['ui.router.state']);

angular.module('ui.router.compat', ['ui.router']);

/**
 * @ngdoc object
 * @name ui.router.util.$resolve
 *
 * @requires $q
 * @requires $injector
 *
 * @description
 * Manages resolution of (acyclic) graphs of promises.
 */
$Resolve.$inject = ['$q', '$injector'];
function $Resolve(  $q,    $injector) {
  
  var VISIT_IN_PROGRESS = 1,
      VISIT_DONE = 2,
      NOTHING = {},
      NO_DEPENDENCIES = [],
      NO_LOCALS = NOTHING,
      NO_PARENT = extend($q.when(NOTHING), { $$promises: NOTHING, $$values: NOTHING });
  

  /**
   * @ngdoc function
   * @name ui.router.util.$resolve#study
   * @methodOf ui.router.util.$resolve
   *
   * @description
   * Studies a set of invocables that are likely to be used multiple times.
   * <pre>
   * $resolve.study(invocables)(locals, parent, self)
   * </pre>
   * is equivalent to
   * <pre>
   * $resolve.resolve(invocables, locals, parent, self)
   * </pre>
   * but the former is more efficient (in fact `resolve` just calls `study` 
   * internally).
   *
   * @param {object} invocables Invocable objects
   * @return {function} a function to pass in locals, parent and self
   */
  this.study = function (invocables) {
    if (!isObject(invocables)) throw new Error("'invocables' must be an object");
    var invocableKeys = objectKeys(invocables || {});
    
    // Perform a topological sort of invocables to build an ordered plan
    var plan = [], cycle = [], visited = {};
    function visit(value, key) {
      if (visited[key] === VISIT_DONE) return;
      
      cycle.push(key);
      if (visited[key] === VISIT_IN_PROGRESS) {
        cycle.splice(0, indexOf(cycle, key));
        throw new Error("Cyclic dependency: " + cycle.join(" -> "));
      }
      visited[key] = VISIT_IN_PROGRESS;
      
      if (isString(value)) {
        plan.push(key, [ function() { return $injector.get(value); }], NO_DEPENDENCIES);
      } else {
        var params = $injector.annotate(value);
        forEach(params, function (param) {
          if (param !== key && invocables.hasOwnProperty(param)) visit(invocables[param], param);
        });
        plan.push(key, value, params);
      }
      
      cycle.pop();
      visited[key] = VISIT_DONE;
    }
    forEach(invocables, visit);
    invocables = cycle = visited = null; // plan is all that's required
    
    function isResolve(value) {
      return isObject(value) && value.then && value.$$promises;
    }
    
    return function (locals, parent, self) {
      if (isResolve(locals) && self === undefined) {
        self = parent; parent = locals; locals = null;
      }
      if (!locals) locals = NO_LOCALS;
      else if (!isObject(locals)) {
        throw new Error("'locals' must be an object");
      }       
      if (!parent) parent = NO_PARENT;
      else if (!isResolve(parent)) {
        throw new Error("'parent' must be a promise returned by $resolve.resolve()");
      }
      
      // To complete the overall resolution, we have to wait for the parent
      // promise and for the promise for each invokable in our plan.
      var resolution = $q.defer(),
          result = silenceUncaughtInPromise(resolution.promise),
          promises = result.$$promises = {},
          values = extend({}, locals),
          wait = 1 + plan.length/3,
          merged = false;

      silenceUncaughtInPromise(result);
          
      function done() {
        // Merge parent values we haven't got yet and publish our own $$values
        if (!--wait) {
          if (!merged) merge(values, parent.$$values); 
          result.$$values = values;
          result.$$promises = result.$$promises || true; // keep for isResolve()
          delete result.$$inheritedValues;
          resolution.resolve(values);
        }
      }
      
      function fail(reason) {
        result.$$failure = reason;
        resolution.reject(reason);
      }

      // Short-circuit if parent has already failed
      if (isDefined(parent.$$failure)) {
        fail(parent.$$failure);
        return result;
      }
      
      if (parent.$$inheritedValues) {
        merge(values, omit(parent.$$inheritedValues, invocableKeys));
      }

      // Merge parent values if the parent has already resolved, or merge
      // parent promises and wait if the parent resolve is still in progress.
      extend(promises, parent.$$promises);
      if (parent.$$values) {
        merged = merge(values, omit(parent.$$values, invocableKeys));
        result.$$inheritedValues = omit(parent.$$values, invocableKeys);
        done();
      } else {
        if (parent.$$inheritedValues) {
          result.$$inheritedValues = omit(parent.$$inheritedValues, invocableKeys);
        }        
        parent.then(done, fail);
      }
      
      // Process each invocable in the plan, but ignore any where a local of the same name exists.
      for (var i=0, ii=plan.length; i<ii; i+=3) {
        if (locals.hasOwnProperty(plan[i])) done();
        else invoke(plan[i], plan[i+1], plan[i+2]);
      }
      
      function invoke(key, invocable, params) {
        // Create a deferred for this invocation. Failures will propagate to the resolution as well.
        var invocation = $q.defer(), waitParams = 0;
        function onfailure(reason) {
          invocation.reject(reason);
          fail(reason);
        }
        // Wait for any parameter that we have a promise for (either from parent or from this
        // resolve; in that case study() will have made sure it's ordered before us in the plan).
        forEach(params, function (dep) {
          if (promises.hasOwnProperty(dep) && !locals.hasOwnProperty(dep)) {
            waitParams++;
            promises[dep].then(function (result) {
              values[dep] = result;
              if (!(--waitParams)) proceed();
            }, onfailure);
          }
        });
        if (!waitParams) proceed();
        function proceed() {
          if (isDefined(result.$$failure)) return;
          try {
            invocation.resolve($injector.invoke(invocable, self, values));
            invocation.promise.then(function (result) {
              values[key] = result;
              done();
            }, onfailure);
          } catch (e) {
            onfailure(e);
          }
        }
        // Publish promise synchronously; invocations further down in the plan may depend on it.
        promises[key] = silenceUncaughtInPromise(invocation.promise);
      }
      
      return result;
    };
  };
  
  /**
   * @ngdoc function
   * @name ui.router.util.$resolve#resolve
   * @methodOf ui.router.util.$resolve
   *
   * @description
   * Resolves a set of invocables. An invocable is a function to be invoked via 
   * `$injector.invoke()`, and can have an arbitrary number of dependencies. 
   * An invocable can either return a value directly,
   * or a `$q` promise. If a promise is returned it will be resolved and the 
   * resulting value will be used instead. Dependencies of invocables are resolved 
   * (in this order of precedence)
   *
   * - from the specified `locals`
   * - from another invocable that is part of this `$resolve` call
   * - from an invocable that is inherited from a `parent` call to `$resolve` 
   *   (or recursively
   * - from any ancestor `$resolve` of that parent).
   *
   * The return value of `$resolve` is a promise for an object that contains 
   * (in this order of precedence)
   *
   * - any `locals` (if specified)
   * - the resolved return values of all injectables
   * - any values inherited from a `parent` call to `$resolve` (if specified)
   *
   * The promise will resolve after the `parent` promise (if any) and all promises 
   * returned by injectables have been resolved. If any invocable 
   * (or `$injector.invoke`) throws an exception, or if a promise returned by an 
   * invocable is rejected, the `$resolve` promise is immediately rejected with the 
   * same error. A rejection of a `parent` promise (if specified) will likewise be 
   * propagated immediately. Once the `$resolve` promise has been rejected, no 
   * further invocables will be called.
   * 
   * Cyclic dependencies between invocables are not permitted and will cause `$resolve`
   * to throw an error. As a special case, an injectable can depend on a parameter 
   * with the same name as the injectable, which will be fulfilled from the `parent` 
   * injectable of the same name. This allows inherited values to be decorated. 
   * Note that in this case any other injectable in the same `$resolve` with the same
   * dependency would see the decorated value, not the inherited value.
   *
   * Note that missing dependencies -- unlike cyclic dependencies -- will cause an 
   * (asynchronous) rejection of the `$resolve` promise rather than a (synchronous) 
   * exception.
   *
   * Invocables are invoked eagerly as soon as all dependencies are available. 
   * This is true even for dependencies inherited from a `parent` call to `$resolve`.
   *
   * As a special case, an invocable can be a string, in which case it is taken to 
   * be a service name to be passed to `$injector.get()`. This is supported primarily 
   * for backwards-compatibility with the `resolve` property of `$routeProvider` 
   * routes.
   *
   * @param {object} invocables functions to invoke or 
   * `$injector` services to fetch.
   * @param {object} locals  values to make available to the injectables
   * @param {object} parent  a promise returned by another call to `$resolve`.
   * @param {object} self  the `this` for the invoked methods
   * @return {object} Promise for an object that contains the resolved return value
   * of all invocables, as well as any inherited and local values.
   */
  this.resolve = function (invocables, locals, parent, self) {
    return this.study(invocables)(locals, parent, self);
  };
}

angular.module('ui.router.util').service('$resolve', $Resolve);



/**
 * @ngdoc object
 * @name ui.router.util.$templateFactoryProvider
 *
 * @description
 * Provider for $templateFactory. Manages which template-loading mechanism to
 * use, and will default to the most recent one ($templateRequest on Angular
 * versions starting from 1.3, $http otherwise).
 */
function TemplateFactoryProvider() {
  var shouldUnsafelyUseHttp = angular.version.minor < 3;

  /**
   * @ngdoc function
   * @name ui.router.util.$templateFactoryProvider#shouldUnsafelyUseHttp
   * @methodOf ui.router.util.$templateFactoryProvider
   *
   * @description
   * Forces $templateFactory to use $http instead of $templateRequest. This
   * might cause XSS, as $http doesn't enforce the regular security checks for
   * templates that have been introduced in Angular 1.3. Note that setting this
   * to false on Angular older than 1.3.x will crash, as the $templateRequest
   * service (and the security checks) are not implemented on these versions.
   *
   * See the $sce documentation, section
   * <a href="https://docs.angularjs.org/api/ng/service/$sce#impact-on-loading-templates">
   * Impact on loading templates</a> for more details about this mechanism.
   *
   * @param {boolean} value
   */
  this.shouldUnsafelyUseHttp = function(value) {
    shouldUnsafelyUseHttp = !!value;
  };

  /**
   * @ngdoc object
   * @name ui.router.util.$templateFactory
   *
   * @requires $http
   * @requires $templateCache
   * @requires $injector
   *
   * @description
   * Service. Manages loading of templates.
   */
  this.$get = ['$http', '$templateCache', '$injector', function($http, $templateCache, $injector){
    return new TemplateFactory($http, $templateCache, $injector, shouldUnsafelyUseHttp);}];
}


/**
 * @ngdoc object
 * @name ui.router.util.$templateFactory
 *
 * @requires $http
 * @requires $templateCache
 * @requires $injector
 *
 * @description
 * Service. Manages loading of templates.
 */
function TemplateFactory($http, $templateCache, $injector, shouldUnsafelyUseHttp) {

  /**
   * @ngdoc function
   * @name ui.router.util.$templateFactory#fromConfig
   * @methodOf ui.router.util.$templateFactory
   *
   * @description
   * Creates a template from a configuration object. 
   *
   * @param {object} config Configuration object for which to load a template. 
   * The following properties are search in the specified order, and the first one 
   * that is defined is used to create the template:
   *
   * @param {string|object} config.template html string template or function to 
   * load via {@link ui.router.util.$templateFactory#fromString fromString}.
   * @param {string|object} config.templateUrl url to load or a function returning 
   * the url to load via {@link ui.router.util.$templateFactory#fromUrl fromUrl}.
   * @param {Function} config.templateProvider function to invoke via 
   * {@link ui.router.util.$templateFactory#fromProvider fromProvider}.
   * @param {object} params  Parameters to pass to the template function.
   * @param {object} locals Locals to pass to `invoke` if the template is loaded 
   * via a `templateProvider`. Defaults to `{ params: params }`.
   *
   * @return {string|object}  The template html as a string, or a promise for 
   * that string,or `null` if no template is configured.
   */
  this.fromConfig = function (config, params, locals) {
    return (
      isDefined(config.template) ? this.fromString(config.template, params) :
      isDefined(config.templateUrl) ? this.fromUrl(config.templateUrl, params) :
      isDefined(config.templateProvider) ? this.fromProvider(config.templateProvider, params, locals) :
      null
    );
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$templateFactory#fromString
   * @methodOf ui.router.util.$templateFactory
   *
   * @description
   * Creates a template from a string or a function returning a string.
   *
   * @param {string|object} template html template as a string or function that 
   * returns an html template as a string.
   * @param {object} params Parameters to pass to the template function.
   *
   * @return {string|object} The template html as a string, or a promise for that 
   * string.
   */
  this.fromString = function (template, params) {
    return isFunction(template) ? template(params) : template;
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$templateFactory#fromUrl
   * @methodOf ui.router.util.$templateFactory
   * 
   * @description
   * Loads a template from the a URL via `$http` and `$templateCache`.
   *
   * @param {string|Function} url url of the template to load, or a function 
   * that returns a url.
   * @param {Object} params Parameters to pass to the url function.
   * @return {string|Promise.<string>} The template html as a string, or a promise 
   * for that string.
   */
  this.fromUrl = function (url, params) {
    if (isFunction(url)) url = url(params);
    if (url == null) return null;
    else {
      if(!shouldUnsafelyUseHttp) {
        return $injector.get('$templateRequest')(url);
      } else {
        return $http
          .get(url, { cache: $templateCache, headers: { Accept: 'text/html' }})
          .then(function(response) { return response.data; });
      }
    }
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$templateFactory#fromProvider
   * @methodOf ui.router.util.$templateFactory
   *
   * @description
   * Creates a template by invoking an injectable provider function.
   *
   * @param {Function} provider Function to invoke via `$injector.invoke`
   * @param {Object} params Parameters for the template.
   * @param {Object} locals Locals to pass to `invoke`. Defaults to 
   * `{ params: params }`.
   * @return {string|Promise.<string>} The template html as a string, or a promise 
   * for that string.
   */
  this.fromProvider = function (provider, params, locals) {
    return $injector.invoke(provider, null, locals || { params: params });
  };
}

angular.module('ui.router.util').provider('$templateFactory', TemplateFactoryProvider);

var $$UMFP; // reference to $UrlMatcherFactoryProvider

/**
 * @ngdoc object
 * @name ui.router.util.type:UrlMatcher
 *
 * @description
 * Matches URLs against patterns and extracts named parameters from the path or the search
 * part of the URL. A URL pattern consists of a path pattern, optionally followed by '?' and a list
 * of search parameters. Multiple search parameter names are separated by '&'. Search parameters
 * do not influence whether or not a URL is matched, but their values are passed through into
 * the matched parameters returned by {@link ui.router.util.type:UrlMatcher#methods_exec exec}.
 *
 * Path parameter placeholders can be specified using simple colon/catch-all syntax or curly brace
 * syntax, which optionally allows a regular expression for the parameter to be specified:
 *
 * * `':'` name - colon placeholder
 * * `'*'` name - catch-all placeholder
 * * `'{' name '}'` - curly placeholder
 * * `'{' name ':' regexp|type '}'` - curly placeholder with regexp or type name. Should the
 *   regexp itself contain curly braces, they must be in matched pairs or escaped with a backslash.
 *
 * Parameter names may contain only word characters (latin letters, digits, and underscore) and
 * must be unique within the pattern (across both path and search parameters). For colon
 * placeholders or curly placeholders without an explicit regexp, a path parameter matches any
 * number of characters other than '/'. For catch-all placeholders the path parameter matches
 * any number of characters.
 *
 * Examples:
 *
 * * `'/hello/'` - Matches only if the path is exactly '/hello/'. There is no special treatment for
 *   trailing slashes, and patterns have to match the entire path, not just a prefix.
 * * `'/user/:id'` - Matches '/user/bob' or '/user/1234!!!' or even '/user/' but not '/user' or
 *   '/user/bob/details'. The second path segment will be captured as the parameter 'id'.
 * * `'/user/{id}'` - Same as the previous example, but using curly brace syntax.
 * * `'/user/{id:[^/]*}'` - Same as the previous example.
 * * `'/user/{id:[0-9a-fA-F]{1,8}}'` - Similar to the previous example, but only matches if the id
 *   parameter consists of 1 to 8 hex digits.
 * * `'/files/{path:.*}'` - Matches any URL starting with '/files/' and captures the rest of the
 *   path into the parameter 'path'.
 * * `'/files/*path'` - ditto.
 * * `'/calendar/{start:date}'` - Matches "/calendar/2014-11-12" (because the pattern defined
 *   in the built-in  `date` Type matches `2014-11-12`) and provides a Date object in $stateParams.start
 *
 * @param {string} pattern  The pattern to compile into a matcher.
 * @param {Object} config  A configuration object hash:
 * @param {Object=} parentMatcher Used to concatenate the pattern/config onto
 *   an existing UrlMatcher
 *
 * * `caseInsensitive` - `true` if URL matching should be case insensitive, otherwise `false`, the default value (for backward compatibility) is `false`.
 * * `strict` - `false` if matching against a URL with a trailing slash should be treated as equivalent to a URL without a trailing slash, the default value is `true`.
 *
 * @property {string} prefix  A static prefix of this pattern. The matcher guarantees that any
 *   URL matching this matcher (i.e. any string for which {@link ui.router.util.type:UrlMatcher#methods_exec exec()} returns
 *   non-null) will start with this prefix.
 *
 * @property {string} source  The pattern that was passed into the constructor
 *
 * @property {string} sourcePath  The path portion of the source property
 *
 * @property {string} sourceSearch  The search portion of the source property
 *
 * @property {string} regex  The constructed regex that will be used to match against the url when
 *   it is time to determine which url will match.
 *
 * @returns {Object}  New `UrlMatcher` object
 */
function UrlMatcher(pattern, config, parentMatcher) {
  config = extend({ params: {} }, isObject(config) ? config : {});

  // Find all placeholders and create a compiled pattern, using either classic or curly syntax:
  //   '*' name
  //   ':' name
  //   '{' name '}'
  //   '{' name ':' regexp '}'
  // The regular expression is somewhat complicated due to the need to allow curly braces
  // inside the regular expression. The placeholder regexp breaks down as follows:
  //    ([:*])([\w\[\]]+)              - classic placeholder ($1 / $2) (search version has - for snake-case)
  //    \{([\w\[\]]+)(?:\:\s*( ... ))?\}  - curly brace placeholder ($3) with optional regexp/type ... ($4) (search version has - for snake-case
  //    (?: ... | ... | ... )+         - the regexp consists of any number of atoms, an atom being either
  //    [^{}\\]+                       - anything other than curly braces or backslash
  //    \\.                            - a backslash escape
  //    \{(?:[^{}\\]+|\\.)*\}          - a matched set of curly braces containing other atoms
  var placeholder       = /([:*])([\w\[\]]+)|\{([\w\[\]]+)(?:\:\s*((?:[^{}\\]+|\\.|\{(?:[^{}\\]+|\\.)*\})+))?\}/g,
      searchPlaceholder = /([:]?)([\w\[\].-]+)|\{([\w\[\].-]+)(?:\:\s*((?:[^{}\\]+|\\.|\{(?:[^{}\\]+|\\.)*\})+))?\}/g,
      compiled = '^', last = 0, m,
      segments = this.segments = [],
      parentParams = parentMatcher ? parentMatcher.params : {},
      params = this.params = parentMatcher ? parentMatcher.params.$$new() : new $$UMFP.ParamSet(),
      paramNames = [];

  function addParameter(id, type, config, location) {
    paramNames.push(id);
    if (parentParams[id]) return parentParams[id];
    if (!/^\w+([-.]+\w+)*(?:\[\])?$/.test(id)) throw new Error("Invalid parameter name '" + id + "' in pattern '" + pattern + "'");
    if (params[id]) throw new Error("Duplicate parameter name '" + id + "' in pattern '" + pattern + "'");
    params[id] = new $$UMFP.Param(id, type, config, location);
    return params[id];
  }

  function quoteRegExp(string, pattern, squash, optional) {
    var surroundPattern = ['',''], result = string.replace(/[\\\[\]\^$*+?.()|{}]/g, "\\$&");
    if (!pattern) return result;
    switch(squash) {
      case false: surroundPattern = ['(', ')' + (optional ? "?" : "")]; break;
      case true:
        result = result.replace(/\/$/, '');
        surroundPattern = ['(?:\/(', ')|\/)?'];
      break;
      default:    surroundPattern = ['(' + squash + "|", ')?']; break;
    }
    return result + surroundPattern[0] + pattern + surroundPattern[1];
  }

  this.source = pattern;

  // Split into static segments separated by path parameter placeholders.
  // The number of segments is always 1 more than the number of parameters.
  function matchDetails(m, isSearch) {
    var id, regexp, segment, type, cfg, arrayMode;
    id          = m[2] || m[3]; // IE[78] returns '' for unmatched groups instead of null
    cfg         = config.params[id];
    segment     = pattern.substring(last, m.index);
    regexp      = isSearch ? m[4] : m[4] || (m[1] == '*' ? '.*' : null);

    if (regexp) {
      type      = $$UMFP.type(regexp) || inherit($$UMFP.type("string"), { pattern: new RegExp(regexp, config.caseInsensitive ? 'i' : undefined) });
    }

    return {
      id: id, regexp: regexp, segment: segment, type: type, cfg: cfg
    };
  }

  var p, param, segment;
  while ((m = placeholder.exec(pattern))) {
    p = matchDetails(m, false);
    if (p.segment.indexOf('?') >= 0) break; // we're into the search part

    param = addParameter(p.id, p.type, p.cfg, "path");
    compiled += quoteRegExp(p.segment, param.type.pattern.source, param.squash, param.isOptional);
    segments.push(p.segment);
    last = placeholder.lastIndex;
  }
  segment = pattern.substring(last);

  // Find any search parameter names and remove them from the last segment
  var i = segment.indexOf('?');

  if (i >= 0) {
    var search = this.sourceSearch = segment.substring(i);
    segment = segment.substring(0, i);
    this.sourcePath = pattern.substring(0, last + i);

    if (search.length > 0) {
      last = 0;
      while ((m = searchPlaceholder.exec(search))) {
        p = matchDetails(m, true);
        param = addParameter(p.id, p.type, p.cfg, "search");
        last = placeholder.lastIndex;
        // check if ?&
      }
    }
  } else {
    this.sourcePath = pattern;
    this.sourceSearch = '';
  }

  compiled += quoteRegExp(segment) + (config.strict === false ? '\/?' : '') + '$';
  segments.push(segment);

  this.regexp = new RegExp(compiled, config.caseInsensitive ? 'i' : undefined);
  this.prefix = segments[0];
  this.$$paramNames = paramNames;
}

/**
 * @ngdoc function
 * @name ui.router.util.type:UrlMatcher#concat
 * @methodOf ui.router.util.type:UrlMatcher
 *
 * @description
 * Returns a new matcher for a pattern constructed by appending the path part and adding the
 * search parameters of the specified pattern to this pattern. The current pattern is not
 * modified. This can be understood as creating a pattern for URLs that are relative to (or
 * suffixes of) the current pattern.
 *
 * @example
 * The following two matchers are equivalent:
 * <pre>
 * new UrlMatcher('/user/{id}?q').concat('/details?date');
 * new UrlMatcher('/user/{id}/details?q&date');
 * </pre>
 *
 * @param {string} pattern  The pattern to append.
 * @param {Object} config  An object hash of the configuration for the matcher.
 * @returns {UrlMatcher}  A matcher for the concatenated pattern.
 */
UrlMatcher.prototype.concat = function (pattern, config) {
  // Because order of search parameters is irrelevant, we can add our own search
  // parameters to the end of the new pattern. Parse the new pattern by itself
  // and then join the bits together, but it's much easier to do this on a string level.
  var defaultConfig = {
    caseInsensitive: $$UMFP.caseInsensitive(),
    strict: $$UMFP.strictMode(),
    squash: $$UMFP.defaultSquashPolicy()
  };
  return new UrlMatcher(this.sourcePath + pattern + this.sourceSearch, extend(defaultConfig, config), this);
};

UrlMatcher.prototype.toString = function () {
  return this.source;
};

/**
 * @ngdoc function
 * @name ui.router.util.type:UrlMatcher#exec
 * @methodOf ui.router.util.type:UrlMatcher
 *
 * @description
 * Tests the specified path against this matcher, and returns an object containing the captured
 * parameter values, or null if the path does not match. The returned object contains the values
 * of any search parameters that are mentioned in the pattern, but their value may be null if
 * they are not present in `searchParams`. This means that search parameters are always treated
 * as optional.
 *
 * @example
 * <pre>
 * new UrlMatcher('/user/{id}?q&r').exec('/user/bob', {
 *   x: '1', q: 'hello'
 * });
 * // returns { id: 'bob', q: 'hello', r: null }
 * </pre>
 *
 * @param {string} path  The URL path to match, e.g. `$location.path()`.
 * @param {Object} searchParams  URL search parameters, e.g. `$location.search()`.
 * @returns {Object}  The captured parameter values.
 */
UrlMatcher.prototype.exec = function (path, searchParams) {
  var m = this.regexp.exec(path);
  if (!m) return null;
  searchParams = searchParams || {};

  var paramNames = this.parameters(), nTotal = paramNames.length,
    nPath = this.segments.length - 1,
    values = {}, i, j, cfg, paramName;

  if (nPath !== m.length - 1) throw new Error("Unbalanced capture group in route '" + this.source + "'");

  function decodePathArray(string) {
    function reverseString(str) { return str.split("").reverse().join(""); }
    function unquoteDashes(str) { return str.replace(/\\-/g, "-"); }

    var split = reverseString(string).split(/-(?!\\)/);
    var allReversed = map(split, reverseString);
    return map(allReversed, unquoteDashes).reverse();
  }

  var param, paramVal;
  for (i = 0; i < nPath; i++) {
    paramName = paramNames[i];
    param = this.params[paramName];
    paramVal = m[i+1];
    // if the param value matches a pre-replace pair, replace the value before decoding.
    for (j = 0; j < param.replace.length; j++) {
      if (param.replace[j].from === paramVal) paramVal = param.replace[j].to;
    }
    if (paramVal && param.array === true) paramVal = decodePathArray(paramVal);
    if (isDefined(paramVal)) paramVal = param.type.decode(paramVal);
    values[paramName] = param.value(paramVal);
  }
  for (/**/; i < nTotal; i++) {
    paramName = paramNames[i];
    values[paramName] = this.params[paramName].value(searchParams[paramName]);
    param = this.params[paramName];
    paramVal = searchParams[paramName];
    for (j = 0; j < param.replace.length; j++) {
      if (param.replace[j].from === paramVal) paramVal = param.replace[j].to;
    }
    if (isDefined(paramVal)) paramVal = param.type.decode(paramVal);
    values[paramName] = param.value(paramVal);
  }

  return values;
};

/**
 * @ngdoc function
 * @name ui.router.util.type:UrlMatcher#parameters
 * @methodOf ui.router.util.type:UrlMatcher
 *
 * @description
 * Returns the names of all path and search parameters of this pattern in an unspecified order.
 *
 * @returns {Array.<string>}  An array of parameter names. Must be treated as read-only. If the
 *    pattern has no parameters, an empty array is returned.
 */
UrlMatcher.prototype.parameters = function (param) {
  if (!isDefined(param)) return this.$$paramNames;
  return this.params[param] || null;
};

/**
 * @ngdoc function
 * @name ui.router.util.type:UrlMatcher#validates
 * @methodOf ui.router.util.type:UrlMatcher
 *
 * @description
 * Checks an object hash of parameters to validate their correctness according to the parameter
 * types of this `UrlMatcher`.
 *
 * @param {Object} params The object hash of parameters to validate.
 * @returns {boolean} Returns `true` if `params` validates, otherwise `false`.
 */
UrlMatcher.prototype.validates = function (params) {
  return this.params.$$validates(params);
};

/**
 * @ngdoc function
 * @name ui.router.util.type:UrlMatcher#format
 * @methodOf ui.router.util.type:UrlMatcher
 *
 * @description
 * Creates a URL that matches this pattern by substituting the specified values
 * for the path and search parameters. Null values for path parameters are
 * treated as empty strings.
 *
 * @example
 * <pre>
 * new UrlMatcher('/user/{id}?q').format({ id:'bob', q:'yes' });
 * // returns '/user/bob?q=yes'
 * </pre>
 *
 * @param {Object} values  the values to substitute for the parameters in this pattern.
 * @returns {string}  the formatted URL (path and optionally search part).
 */
UrlMatcher.prototype.format = function (values) {
  values = values || {};
  var segments = this.segments, params = this.parameters(), paramset = this.params;
  if (!this.validates(values)) return null;

  var i, search = false, nPath = segments.length - 1, nTotal = params.length, result = segments[0];

  function encodeDashes(str) { // Replace dashes with encoded "\-"
    return encodeURIComponent(str).replace(/-/g, function(c) { return '%5C%' + c.charCodeAt(0).toString(16).toUpperCase(); });
  }

  for (i = 0; i < nTotal; i++) {
    var isPathParam = i < nPath;
    var name = params[i], param = paramset[name], value = param.value(values[name]);
    var isDefaultValue = param.isOptional && param.type.equals(param.value(), value);
    var squash = isDefaultValue ? param.squash : false;
    var encoded = param.type.encode(value);

    if (isPathParam) {
      var nextSegment = segments[i + 1];
      var isFinalPathParam = i + 1 === nPath;

      if (squash === false) {
        if (encoded != null) {
          if (isArray(encoded)) {
            result += map(encoded, encodeDashes).join("-");
          } else {
            result += encodeURIComponent(encoded);
          }
        }
        result += nextSegment;
      } else if (squash === true) {
        var capture = result.match(/\/$/) ? /\/?(.*)/ : /(.*)/;
        result += nextSegment.match(capture)[1];
      } else if (isString(squash)) {
        result += squash + nextSegment;
      }

      if (isFinalPathParam && param.squash === true && result.slice(-1) === '/') result = result.slice(0, -1);
    } else {
      if (encoded == null || (isDefaultValue && squash !== false)) continue;
      if (!isArray(encoded)) encoded = [ encoded ];
      if (encoded.length === 0) continue;
      encoded = map(encoded, encodeURIComponent).join('&' + name + '=');
      result += (search ? '&' : '?') + (name + '=' + encoded);
      search = true;
    }
  }

  return result;
};

/**
 * @ngdoc object
 * @name ui.router.util.type:Type
 *
 * @description
 * Implements an interface to define custom parameter types that can be decoded from and encoded to
 * string parameters matched in a URL. Used by {@link ui.router.util.type:UrlMatcher `UrlMatcher`}
 * objects when matching or formatting URLs, or comparing or validating parameter values.
 *
 * See {@link ui.router.util.$urlMatcherFactory#methods_type `$urlMatcherFactory#type()`} for more
 * information on registering custom types.
 *
 * @param {Object} config  A configuration object which contains the custom type definition.  The object's
 *        properties will override the default methods and/or pattern in `Type`'s public interface.
 * @example
 * <pre>
 * {
 *   decode: function(val) { return parseInt(val, 10); },
 *   encode: function(val) { return val && val.toString(); },
 *   equals: function(a, b) { return this.is(a) && a === b; },
 *   is: function(val) { return angular.isNumber(val) isFinite(val) && val % 1 === 0; },
 *   pattern: /\d+/
 * }
 * </pre>
 *
 * @property {RegExp} pattern The regular expression pattern used to match values of this type when
 *           coming from a substring of a URL.
 *
 * @returns {Object}  Returns a new `Type` object.
 */
function Type(config) {
  extend(this, config);
}

/**
 * @ngdoc function
 * @name ui.router.util.type:Type#is
 * @methodOf ui.router.util.type:Type
 *
 * @description
 * Detects whether a value is of a particular type. Accepts a native (decoded) value
 * and determines whether it matches the current `Type` object.
 *
 * @param {*} val  The value to check.
 * @param {string} key  Optional. If the type check is happening in the context of a specific
 *        {@link ui.router.util.type:UrlMatcher `UrlMatcher`} object, this is the name of the
 *        parameter in which `val` is stored. Can be used for meta-programming of `Type` objects.
 * @returns {Boolean}  Returns `true` if the value matches the type, otherwise `false`.
 */
Type.prototype.is = function(val, key) {
  return true;
};

/**
 * @ngdoc function
 * @name ui.router.util.type:Type#encode
 * @methodOf ui.router.util.type:Type
 *
 * @description
 * Encodes a custom/native type value to a string that can be embedded in a URL. Note that the
 * return value does *not* need to be URL-safe (i.e. passed through `encodeURIComponent()`), it
 * only needs to be a representation of `val` that has been coerced to a string.
 *
 * @param {*} val  The value to encode.
 * @param {string} key  The name of the parameter in which `val` is stored. Can be used for
 *        meta-programming of `Type` objects.
 * @returns {string}  Returns a string representation of `val` that can be encoded in a URL.
 */
Type.prototype.encode = function(val, key) {
  return val;
};

/**
 * @ngdoc function
 * @name ui.router.util.type:Type#decode
 * @methodOf ui.router.util.type:Type
 *
 * @description
 * Converts a parameter value (from URL string or transition param) to a custom/native value.
 *
 * @param {string} val  The URL parameter value to decode.
 * @param {string} key  The name of the parameter in which `val` is stored. Can be used for
 *        meta-programming of `Type` objects.
 * @returns {*}  Returns a custom representation of the URL parameter value.
 */
Type.prototype.decode = function(val, key) {
  return val;
};

/**
 * @ngdoc function
 * @name ui.router.util.type:Type#equals
 * @methodOf ui.router.util.type:Type
 *
 * @description
 * Determines whether two decoded values are equivalent.
 *
 * @param {*} a  A value to compare against.
 * @param {*} b  A value to compare against.
 * @returns {Boolean}  Returns `true` if the values are equivalent/equal, otherwise `false`.
 */
Type.prototype.equals = function(a, b) {
  return a == b;
};

Type.prototype.$subPattern = function() {
  var sub = this.pattern.toString();
  return sub.substr(1, sub.length - 2);
};

Type.prototype.pattern = /.*/;

Type.prototype.toString = function() { return "{Type:" + this.name + "}"; };

/** Given an encoded string, or a decoded object, returns a decoded object */
Type.prototype.$normalize = function(val) {
  return this.is(val) ? val : this.decode(val);
};

/*
 * Wraps an existing custom Type as an array of Type, depending on 'mode'.
 * e.g.:
 * - urlmatcher pattern "/path?{queryParam[]:int}"
 * - url: "/path?queryParam=1&queryParam=2
 * - $stateParams.queryParam will be [1, 2]
 * if `mode` is "auto", then
 * - url: "/path?queryParam=1 will create $stateParams.queryParam: 1
 * - url: "/path?queryParam=1&queryParam=2 will create $stateParams.queryParam: [1, 2]
 */
Type.prototype.$asArray = function(mode, isSearch) {
  if (!mode) return this;
  if (mode === "auto" && !isSearch) throw new Error("'auto' array mode is for query parameters only");

  function ArrayType(type, mode) {
    function bindTo(type, callbackName) {
      return function() {
        return type[callbackName].apply(type, arguments);
      };
    }

    // Wrap non-array value as array
    function arrayWrap(val) { return isArray(val) ? val : (isDefined(val) ? [ val ] : []); }
    // Unwrap array value for "auto" mode. Return undefined for empty array.
    function arrayUnwrap(val) {
      switch(val.length) {
        case 0: return undefined;
        case 1: return mode === "auto" ? val[0] : val;
        default: return val;
      }
    }
    function falsey(val) { return !val; }

    // Wraps type (.is/.encode/.decode) functions to operate on each value of an array
    function arrayHandler(callback, allTruthyMode) {
      return function handleArray(val) {
        if (isArray(val) && val.length === 0) return val;
        val = arrayWrap(val);
        var result = map(val, callback);
        if (allTruthyMode === true)
          return filter(result, falsey).length === 0;
        return arrayUnwrap(result);
      };
    }

    // Wraps type (.equals) functions to operate on each value of an array
    function arrayEqualsHandler(callback) {
      return function handleArray(val1, val2) {
        var left = arrayWrap(val1), right = arrayWrap(val2);
        if (left.length !== right.length) return false;
        for (var i = 0; i < left.length; i++) {
          if (!callback(left[i], right[i])) return false;
        }
        return true;
      };
    }

    this.encode = arrayHandler(bindTo(type, 'encode'));
    this.decode = arrayHandler(bindTo(type, 'decode'));
    this.is     = arrayHandler(bindTo(type, 'is'), true);
    this.equals = arrayEqualsHandler(bindTo(type, 'equals'));
    this.pattern = type.pattern;
    this.$normalize = arrayHandler(bindTo(type, '$normalize'));
    this.name = type.name;
    this.$arrayMode = mode;
  }

  return new ArrayType(this, mode);
};



/**
 * @ngdoc object
 * @name ui.router.util.$urlMatcherFactory
 *
 * @description
 * Factory for {@link ui.router.util.type:UrlMatcher `UrlMatcher`} instances. The factory
 * is also available to providers under the name `$urlMatcherFactoryProvider`.
 */
function $UrlMatcherFactory() {
  $$UMFP = this;

  var isCaseInsensitive = false, isStrictMode = true, defaultSquashPolicy = false;

  // Use tildes to pre-encode slashes.
  // If the slashes are simply URLEncoded, the browser can choose to pre-decode them,
  // and bidirectional encoding/decoding fails.
  // Tilde was chosen because it's not a RFC 3986 section 2.2 Reserved Character
  function valToString(val) { return val != null ? val.toString().replace(/(~|\/)/g, function (m) { return {'~':'~~', '/':'~2F'}[m]; }) : val; }
  function valFromString(val) { return val != null ? val.toString().replace(/(~~|~2F)/g, function (m) { return {'~~':'~', '~2F':'/'}[m]; }) : val; }

  var $types = {}, enqueue = true, typeQueue = [], injector, defaultTypes = {
    "string": {
      encode: valToString,
      decode: valFromString,
      // TODO: in 1.0, make string .is() return false if value is undefined/null by default.
      // In 0.2.x, string params are optional by default for backwards compat
      is: function(val) { return val == null || !isDefined(val) || typeof val === "string"; },
      pattern: /[^/]*/
    },
    "int": {
      encode: valToString,
      decode: function(val) { return parseInt(val, 10); },
      is: function(val) { return val !== undefined && val !== null && this.decode(val.toString()) === val; },
      pattern: /-?\d+/
    },
    "bool": {
      encode: function(val) { return val ? 1 : 0; },
      decode: function(val) { return parseInt(val, 10) !== 0; },
      is: function(val) { return val === true || val === false; },
      pattern: /0|1/
    },
    "date": {
      encode: function (val) {
        if (!this.is(val))
          return undefined;
        return [ val.getFullYear(),
          ('0' + (val.getMonth() + 1)).slice(-2),
          ('0' + val.getDate()).slice(-2)
        ].join("-");
      },
      decode: function (val) {
        if (this.is(val)) return val;
        var match = this.capture.exec(val);
        return match ? new Date(match[1], match[2] - 1, match[3]) : undefined;
      },
      is: function(val) { return val instanceof Date && !isNaN(val.valueOf()); },
      equals: function (a, b) { return this.is(a) && this.is(b) && a.toISOString() === b.toISOString(); },
      pattern: /[0-9]{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[1-2][0-9]|3[0-1])/,
      capture: /([0-9]{4})-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])/
    },
    "json": {
      encode: angular.toJson,
      decode: angular.fromJson,
      is: angular.isObject,
      equals: angular.equals,
      pattern: /[^/]*/
    },
    "any": { // does not encode/decode
      encode: angular.identity,
      decode: angular.identity,
      equals: angular.equals,
      pattern: /.*/
    }
  };

  function getDefaultConfig() {
    return {
      strict: isStrictMode,
      caseInsensitive: isCaseInsensitive
    };
  }

  function isInjectable(value) {
    return (isFunction(value) || (isArray(value) && isFunction(value[value.length - 1])));
  }

  /**
   * [Internal] Get the default value of a parameter, which may be an injectable function.
   */
  $UrlMatcherFactory.$$getDefaultValue = function(config) {
    if (!isInjectable(config.value)) return config.value;
    if (!injector) throw new Error("Injectable functions cannot be called at configuration time");
    return injector.invoke(config.value);
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$urlMatcherFactory#caseInsensitive
   * @methodOf ui.router.util.$urlMatcherFactory
   *
   * @description
   * Defines whether URL matching should be case sensitive (the default behavior), or not.
   *
   * @param {boolean} value `false` to match URL in a case sensitive manner; otherwise `true`;
   * @returns {boolean} the current value of caseInsensitive
   */
  this.caseInsensitive = function(value) {
    if (isDefined(value))
      isCaseInsensitive = value;
    return isCaseInsensitive;
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$urlMatcherFactory#strictMode
   * @methodOf ui.router.util.$urlMatcherFactory
   *
   * @description
   * Defines whether URLs should match trailing slashes, or not (the default behavior).
   *
   * @param {boolean=} value `false` to match trailing slashes in URLs, otherwise `true`.
   * @returns {boolean} the current value of strictMode
   */
  this.strictMode = function(value) {
    if (isDefined(value))
      isStrictMode = value;
    return isStrictMode;
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$urlMatcherFactory#defaultSquashPolicy
   * @methodOf ui.router.util.$urlMatcherFactory
   *
   * @description
   * Sets the default behavior when generating or matching URLs with default parameter values.
   *
   * @param {string} value A string that defines the default parameter URL squashing behavior.
   *    `nosquash`: When generating an href with a default parameter value, do not squash the parameter value from the URL
   *    `slash`: When generating an href with a default parameter value, squash (remove) the parameter value, and, if the
   *             parameter is surrounded by slashes, squash (remove) one slash from the URL
   *    any other string, e.g. "~": When generating an href with a default parameter value, squash (remove)
   *             the parameter value from the URL and replace it with this string.
   */
  this.defaultSquashPolicy = function(value) {
    if (!isDefined(value)) return defaultSquashPolicy;
    if (value !== true && value !== false && !isString(value))
      throw new Error("Invalid squash policy: " + value + ". Valid policies: false, true, arbitrary-string");
    defaultSquashPolicy = value;
    return value;
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$urlMatcherFactory#compile
   * @methodOf ui.router.util.$urlMatcherFactory
   *
   * @description
   * Creates a {@link ui.router.util.type:UrlMatcher `UrlMatcher`} for the specified pattern.
   *
   * @param {string} pattern  The URL pattern.
   * @param {Object} config  The config object hash.
   * @returns {UrlMatcher}  The UrlMatcher.
   */
  this.compile = function (pattern, config) {
    return new UrlMatcher(pattern, extend(getDefaultConfig(), config));
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$urlMatcherFactory#isMatcher
   * @methodOf ui.router.util.$urlMatcherFactory
   *
   * @description
   * Returns true if the specified object is a `UrlMatcher`, or false otherwise.
   *
   * @param {Object} object  The object to perform the type check against.
   * @returns {Boolean}  Returns `true` if the object matches the `UrlMatcher` interface, by
   *          implementing all the same methods.
   */
  this.isMatcher = function (o) {
    if (!isObject(o)) return false;
    var result = true;

    forEach(UrlMatcher.prototype, function(val, name) {
      if (isFunction(val)) {
        result = result && (isDefined(o[name]) && isFunction(o[name]));
      }
    });
    return result;
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$urlMatcherFactory#type
   * @methodOf ui.router.util.$urlMatcherFactory
   *
   * @description
   * Registers a custom {@link ui.router.util.type:Type `Type`} object that can be used to
   * generate URLs with typed parameters.
   *
   * @param {string} name  The type name.
   * @param {Object|Function} definition   The type definition. See
   *        {@link ui.router.util.type:Type `Type`} for information on the values accepted.
   * @param {Object|Function} definitionFn (optional) A function that is injected before the app
   *        runtime starts.  The result of this function is merged into the existing `definition`.
   *        See {@link ui.router.util.type:Type `Type`} for information on the values accepted.
   *
   * @returns {Object}  Returns `$urlMatcherFactoryProvider`.
   *
   * @example
   * This is a simple example of a custom type that encodes and decodes items from an
   * array, using the array index as the URL-encoded value:
   *
   * <pre>
   * var list = ['John', 'Paul', 'George', 'Ringo'];
   *
   * $urlMatcherFactoryProvider.type('listItem', {
   *   encode: function(item) {
   *     // Represent the list item in the URL using its corresponding index
   *     return list.indexOf(item);
   *   },
   *   decode: function(item) {
   *     // Look up the list item by index
   *     return list[parseInt(item, 10)];
   *   },
   *   is: function(item) {
   *     // Ensure the item is valid by checking to see that it appears
   *     // in the list
   *     return list.indexOf(item) > -1;
   *   }
   * });
   *
   * $stateProvider.state('list', {
   *   url: "/list/{item:listItem}",
   *   controller: function($scope, $stateParams) {
   *     console.log($stateParams.item);
   *   }
   * });
   *
   * // ...
   *
   * // Changes URL to '/list/3', logs "Ringo" to the console
   * $state.go('list', { item: "Ringo" });
   * </pre>
   *
   * This is a more complex example of a type that relies on dependency injection to
   * interact with services, and uses the parameter name from the URL to infer how to
   * handle encoding and decoding parameter values:
   *
   * <pre>
   * // Defines a custom type that gets a value from a service,
   * // where each service gets different types of values from
   * // a backend API:
   * $urlMatcherFactoryProvider.type('dbObject', {}, function(Users, Posts) {
   *
   *   // Matches up services to URL parameter names
   *   var services = {
   *     user: Users,
   *     post: Posts
   *   };
   *
   *   return {
   *     encode: function(object) {
   *       // Represent the object in the URL using its unique ID
   *       return object.id;
   *     },
   *     decode: function(value, key) {
   *       // Look up the object by ID, using the parameter
   *       // name (key) to call the correct service
   *       return services[key].findById(value);
   *     },
   *     is: function(object, key) {
   *       // Check that object is a valid dbObject
   *       return angular.isObject(object) && object.id && services[key];
   *     }
   *     equals: function(a, b) {
   *       // Check the equality of decoded objects by comparing
   *       // their unique IDs
   *       return a.id === b.id;
   *     }
   *   };
   * });
   *
   * // In a config() block, you can then attach URLs with
   * // type-annotated parameters:
   * $stateProvider.state('users', {
   *   url: "/users",
   *   // ...
   * }).state('users.item', {
   *   url: "/{user:dbObject}",
   *   controller: function($scope, $stateParams) {
   *     // $stateParams.user will now be an object returned from
   *     // the Users service
   *   },
   *   // ...
   * });
   * </pre>
   */
  this.type = function (name, definition, definitionFn) {
    if (!isDefined(definition)) return $types[name];
    if ($types.hasOwnProperty(name)) throw new Error("A type named '" + name + "' has already been defined.");

    $types[name] = new Type(extend({ name: name }, definition));
    if (definitionFn) {
      typeQueue.push({ name: name, def: definitionFn });
      if (!enqueue) flushTypeQueue();
    }
    return this;
  };

  // `flushTypeQueue()` waits until `$urlMatcherFactory` is injected before invoking the queued `definitionFn`s
  function flushTypeQueue() {
    while(typeQueue.length) {
      var type = typeQueue.shift();
      if (type.pattern) throw new Error("You cannot override a type's .pattern at runtime.");
      angular.extend($types[type.name], injector.invoke(type.def));
    }
  }

  // Register default types. Store them in the prototype of $types.
  forEach(defaultTypes, function(type, name) { $types[name] = new Type(extend({name: name}, type)); });
  $types = inherit($types, {});

  /* No need to document $get, since it returns this */
  this.$get = ['$injector', function ($injector) {
    injector = $injector;
    enqueue = false;
    flushTypeQueue();

    forEach(defaultTypes, function(type, name) {
      if (!$types[name]) $types[name] = new Type(type);
    });
    return this;
  }];

  this.Param = function Param(id, type, config, location) {
    var self = this;
    config = unwrapShorthand(config);
    type = getType(config, type, location);
    var arrayMode = getArrayMode();
    type = arrayMode ? type.$asArray(arrayMode, location === "search") : type;
    if (type.name === "string" && !arrayMode && location === "path" && config.value === undefined)
      config.value = ""; // for 0.2.x; in 0.3.0+ do not automatically default to ""
    var isOptional = config.value !== undefined;
    var squash = getSquashPolicy(config, isOptional);
    var replace = getReplace(config, arrayMode, isOptional, squash);

    function unwrapShorthand(config) {
      var keys = isObject(config) ? objectKeys(config) : [];
      var isShorthand = indexOf(keys, "value") === -1 && indexOf(keys, "type") === -1 &&
                        indexOf(keys, "squash") === -1 && indexOf(keys, "array") === -1;
      if (isShorthand) config = { value: config };
      config.$$fn = isInjectable(config.value) ? config.value : function () { return config.value; };
      return config;
    }

    function getType(config, urlType, location) {
      if (config.type && urlType) throw new Error("Param '"+id+"' has two type configurations.");
      if (urlType) return urlType;
      if (!config.type) return (location === "config" ? $types.any : $types.string);

      if (angular.isString(config.type))
        return $types[config.type];
      if (config.type instanceof Type)
        return config.type;
      return new Type(config.type);
    }

    // array config: param name (param[]) overrides default settings.  explicit config overrides param name.
    function getArrayMode() {
      var arrayDefaults = { array: (location === "search" ? "auto" : false) };
      var arrayParamNomenclature = id.match(/\[\]$/) ? { array: true } : {};
      return extend(arrayDefaults, arrayParamNomenclature, config).array;
    }

    /**
     * returns false, true, or the squash value to indicate the "default parameter url squash policy".
     */
    function getSquashPolicy(config, isOptional) {
      var squash = config.squash;
      if (!isOptional || squash === false) return false;
      if (!isDefined(squash) || squash == null) return defaultSquashPolicy;
      if (squash === true || isString(squash)) return squash;
      throw new Error("Invalid squash policy: '" + squash + "'. Valid policies: false, true, or arbitrary string");
    }

    function getReplace(config, arrayMode, isOptional, squash) {
      var replace, configuredKeys, defaultPolicy = [
        { from: "",   to: (isOptional || arrayMode ? undefined : "") },
        { from: null, to: (isOptional || arrayMode ? undefined : "") }
      ];
      replace = isArray(config.replace) ? config.replace : [];
      if (isString(squash))
        replace.push({ from: squash, to: undefined });
      configuredKeys = map(replace, function(item) { return item.from; } );
      return filter(defaultPolicy, function(item) { return indexOf(configuredKeys, item.from) === -1; }).concat(replace);
    }

    /**
     * [Internal] Get the default value of a parameter, which may be an injectable function.
     */
    function $$getDefaultValue() {
      if (!injector) throw new Error("Injectable functions cannot be called at configuration time");
      var defaultValue = injector.invoke(config.$$fn);
      if (defaultValue !== null && defaultValue !== undefined && !self.type.is(defaultValue))
        throw new Error("Default value (" + defaultValue + ") for parameter '" + self.id + "' is not an instance of Type (" + self.type.name + ")");
      return defaultValue;
    }

    /**
     * [Internal] Gets the decoded representation of a value if the value is defined, otherwise, returns the
     * default value, which may be the result of an injectable function.
     */
    function $value(value) {
      function hasReplaceVal(val) { return function(obj) { return obj.from === val; }; }
      function $replace(value) {
        var replacement = map(filter(self.replace, hasReplaceVal(value)), function(obj) { return obj.to; });
        return replacement.length ? replacement[0] : value;
      }
      value = $replace(value);
      return !isDefined(value) ? $$getDefaultValue() : self.type.$normalize(value);
    }

    function toString() { return "{Param:" + id + " " + type + " squash: '" + squash + "' optional: " + isOptional + "}"; }

    extend(this, {
      id: id,
      type: type,
      location: location,
      array: arrayMode,
      squash: squash,
      replace: replace,
      isOptional: isOptional,
      value: $value,
      dynamic: undefined,
      config: config,
      toString: toString
    });
  };

  function ParamSet(params) {
    extend(this, params || {});
  }

  ParamSet.prototype = {
    $$new: function() {
      return inherit(this, extend(new ParamSet(), { $$parent: this}));
    },
    $$keys: function () {
      var keys = [], chain = [], parent = this,
        ignore = objectKeys(ParamSet.prototype);
      while (parent) { chain.push(parent); parent = parent.$$parent; }
      chain.reverse();
      forEach(chain, function(paramset) {
        forEach(objectKeys(paramset), function(key) {
            if (indexOf(keys, key) === -1 && indexOf(ignore, key) === -1) keys.push(key);
        });
      });
      return keys;
    },
    $$values: function(paramValues) {
      var values = {}, self = this;
      forEach(self.$$keys(), function(key) {
        values[key] = self[key].value(paramValues && paramValues[key]);
      });
      return values;
    },
    $$equals: function(paramValues1, paramValues2) {
      var equal = true, self = this;
      forEach(self.$$keys(), function(key) {
        var left = paramValues1 && paramValues1[key], right = paramValues2 && paramValues2[key];
        if (!self[key].type.equals(left, right)) equal = false;
      });
      return equal;
    },
    $$validates: function $$validate(paramValues) {
      var keys = this.$$keys(), i, param, rawVal, normalized, encoded;
      for (i = 0; i < keys.length; i++) {
        param = this[keys[i]];
        rawVal = paramValues[keys[i]];
        if ((rawVal === undefined || rawVal === null) && param.isOptional)
          break; // There was no parameter value, but the param is optional
        normalized = param.type.$normalize(rawVal);
        if (!param.type.is(normalized))
          return false; // The value was not of the correct Type, and could not be decoded to the correct Type
        encoded = param.type.encode(normalized);
        if (angular.isString(encoded) && !param.type.pattern.exec(encoded))
          return false; // The value was of the correct type, but when encoded, did not match the Type's regexp
      }
      return true;
    },
    $$parent: undefined
  };

  this.ParamSet = ParamSet;
}

// Register as a provider so it's available to other providers
angular.module('ui.router.util').provider('$urlMatcherFactory', $UrlMatcherFactory);
angular.module('ui.router.util').run(['$urlMatcherFactory', function($urlMatcherFactory) { }]);

/**
 * @ngdoc object
 * @name ui.router.router.$urlRouterProvider
 *
 * @requires ui.router.util.$urlMatcherFactoryProvider
 * @requires $locationProvider
 *
 * @description
 * `$urlRouterProvider` has the responsibility of watching `$location`. 
 * When `$location` changes it runs through a list of rules one by one until a 
 * match is found. `$urlRouterProvider` is used behind the scenes anytime you specify 
 * a url in a state configuration. All urls are compiled into a UrlMatcher object.
 *
 * There are several methods on `$urlRouterProvider` that make it useful to use directly
 * in your module config.
 */
$UrlRouterProvider.$inject = ['$locationProvider', '$urlMatcherFactoryProvider'];
function $UrlRouterProvider(   $locationProvider,   $urlMatcherFactory) {
  var rules = [], otherwise = null, interceptDeferred = false, listener;

  // Returns a string that is a prefix of all strings matching the RegExp
  function regExpPrefix(re) {
    var prefix = /^\^((?:\\[^a-zA-Z0-9]|[^\\\[\]\^$*+?.()|{}]+)*)/.exec(re.source);
    return (prefix != null) ? prefix[1].replace(/\\(.)/g, "$1") : '';
  }

  // Interpolates matched values into a String.replace()-style pattern
  function interpolate(pattern, match) {
    return pattern.replace(/\$(\$|\d{1,2})/, function (m, what) {
      return match[what === '$' ? 0 : Number(what)];
    });
  }

  /**
   * @ngdoc function
   * @name ui.router.router.$urlRouterProvider#rule
   * @methodOf ui.router.router.$urlRouterProvider
   *
   * @description
   * Defines rules that are used by `$urlRouterProvider` to find matches for
   * specific URLs.
   *
   * @example
   * <pre>
   * var app = angular.module('app', ['ui.router.router']);
   *
   * app.config(function ($urlRouterProvider) {
   *   // Here's an example of how you might allow case insensitive urls
   *   $urlRouterProvider.rule(function ($injector, $location) {
   *     var path = $location.path(),
   *         normalized = path.toLowerCase();
   *
   *     if (path !== normalized) {
   *       return normalized;
   *     }
   *   });
   * });
   * </pre>
   *
   * @param {function} rule Handler function that takes `$injector` and `$location`
   * services as arguments. You can use them to return a valid path as a string.
   *
   * @return {object} `$urlRouterProvider` - `$urlRouterProvider` instance
   */
  this.rule = function (rule) {
    if (!isFunction(rule)) throw new Error("'rule' must be a function");
    rules.push(rule);
    return this;
  };

  /**
   * @ngdoc object
   * @name ui.router.router.$urlRouterProvider#otherwise
   * @methodOf ui.router.router.$urlRouterProvider
   *
   * @description
   * Defines a path that is used when an invalid route is requested.
   *
   * @example
   * <pre>
   * var app = angular.module('app', ['ui.router.router']);
   *
   * app.config(function ($urlRouterProvider) {
   *   // if the path doesn't match any of the urls you configured
   *   // otherwise will take care of routing the user to the
   *   // specified url
   *   $urlRouterProvider.otherwise('/index');
   *
   *   // Example of using function rule as param
   *   $urlRouterProvider.otherwise(function ($injector, $location) {
   *     return '/a/valid/url';
   *   });
   * });
   * </pre>
   *
   * @param {string|function} rule The url path you want to redirect to or a function 
   * rule that returns the url path. The function version is passed two params: 
   * `$injector` and `$location` services, and must return a url string.
   *
   * @return {object} `$urlRouterProvider` - `$urlRouterProvider` instance
   */
  this.otherwise = function (rule) {
    if (isString(rule)) {
      var redirect = rule;
      rule = function () { return redirect; };
    }
    else if (!isFunction(rule)) throw new Error("'rule' must be a function");
    otherwise = rule;
    return this;
  };


  function handleIfMatch($injector, handler, match) {
    if (!match) return false;
    var result = $injector.invoke(handler, handler, { $match: match });
    return isDefined(result) ? result : true;
  }

  /**
   * @ngdoc function
   * @name ui.router.router.$urlRouterProvider#when
   * @methodOf ui.router.router.$urlRouterProvider
   *
   * @description
   * Registers a handler for a given url matching. 
   * 
   * If the handler is a string, it is
   * treated as a redirect, and is interpolated according to the syntax of match
   * (i.e. like `String.replace()` for `RegExp`, or like a `UrlMatcher` pattern otherwise).
   *
   * If the handler is a function, it is injectable. It gets invoked if `$location`
   * matches. You have the option of inject the match object as `$match`.
   *
   * The handler can return
   *
   * - **falsy** to indicate that the rule didn't match after all, then `$urlRouter`
   *   will continue trying to find another one that matches.
   * - **string** which is treated as a redirect and passed to `$location.url()`
   * - **void** or any **truthy** value tells `$urlRouter` that the url was handled.
   *
   * @example
   * <pre>
   * var app = angular.module('app', ['ui.router.router']);
   *
   * app.config(function ($urlRouterProvider) {
   *   $urlRouterProvider.when($state.url, function ($match, $stateParams) {
   *     if ($state.$current.navigable !== state ||
   *         !equalForKeys($match, $stateParams) {
   *      $state.transitionTo(state, $match, false);
   *     }
   *   });
   * });
   * </pre>
   *
   * @param {string|object} what The incoming path that you want to redirect.
   * @param {string|function} handler The path you want to redirect your user to.
   */
  this.when = function (what, handler) {
    var redirect, handlerIsString = isString(handler);
    if (isString(what)) what = $urlMatcherFactory.compile(what);

    if (!handlerIsString && !isFunction(handler) && !isArray(handler))
      throw new Error("invalid 'handler' in when()");

    var strategies = {
      matcher: function (what, handler) {
        if (handlerIsString) {
          redirect = $urlMatcherFactory.compile(handler);
          handler = ['$match', function ($match) { return redirect.format($match); }];
        }
        return extend(function ($injector, $location) {
          return handleIfMatch($injector, handler, what.exec($location.path(), $location.search()));
        }, {
          prefix: isString(what.prefix) ? what.prefix : ''
        });
      },
      regex: function (what, handler) {
        if (what.global || what.sticky) throw new Error("when() RegExp must not be global or sticky");

        if (handlerIsString) {
          redirect = handler;
          handler = ['$match', function ($match) { return interpolate(redirect, $match); }];
        }
        return extend(function ($injector, $location) {
          return handleIfMatch($injector, handler, what.exec($location.path()));
        }, {
          prefix: regExpPrefix(what)
        });
      }
    };

    var check = { matcher: $urlMatcherFactory.isMatcher(what), regex: what instanceof RegExp };

    for (var n in check) {
      if (check[n]) return this.rule(strategies[n](what, handler));
    }

    throw new Error("invalid 'what' in when()");
  };

  /**
   * @ngdoc function
   * @name ui.router.router.$urlRouterProvider#deferIntercept
   * @methodOf ui.router.router.$urlRouterProvider
   *
   * @description
   * Disables (or enables) deferring location change interception.
   *
   * If you wish to customize the behavior of syncing the URL (for example, if you wish to
   * defer a transition but maintain the current URL), call this method at configuration time.
   * Then, at run time, call `$urlRouter.listen()` after you have configured your own
   * `$locationChangeSuccess` event handler.
   *
   * @example
   * <pre>
   * var app = angular.module('app', ['ui.router.router']);
   *
   * app.config(function ($urlRouterProvider) {
   *
   *   // Prevent $urlRouter from automatically intercepting URL changes;
   *   // this allows you to configure custom behavior in between
   *   // location changes and route synchronization:
   *   $urlRouterProvider.deferIntercept();
   *
   * }).run(function ($rootScope, $urlRouter, UserService) {
   *
   *   $rootScope.$on('$locationChangeSuccess', function(e) {
   *     // UserService is an example service for managing user state
   *     if (UserService.isLoggedIn()) return;
   *
   *     // Prevent $urlRouter's default handler from firing
   *     e.preventDefault();
   *
   *     UserService.handleLogin().then(function() {
   *       // Once the user has logged in, sync the current URL
   *       // to the router:
   *       $urlRouter.sync();
   *     });
   *   });
   *
   *   // Configures $urlRouter's listener *after* your custom listener
   *   $urlRouter.listen();
   * });
   * </pre>
   *
   * @param {boolean} defer Indicates whether to defer location change interception. Passing
            no parameter is equivalent to `true`.
   */
  this.deferIntercept = function (defer) {
    if (defer === undefined) defer = true;
    interceptDeferred = defer;
  };

  /**
   * @ngdoc object
   * @name ui.router.router.$urlRouter
   *
   * @requires $location
   * @requires $rootScope
   * @requires $injector
   * @requires $browser
   *
   * @description
   *
   */
  this.$get = $get;
  $get.$inject = ['$location', '$rootScope', '$injector', '$browser', '$sniffer'];
  function $get(   $location,   $rootScope,   $injector,   $browser,   $sniffer) {

    var baseHref = $browser.baseHref(), location = $location.url(), lastPushedUrl;

    function appendBasePath(url, isHtml5, absolute) {
      if (baseHref === '/') return url;
      if (isHtml5) return baseHref.slice(0, -1) + url;
      if (absolute) return baseHref.slice(1) + url;
      return url;
    }

    // TODO: Optimize groups of rules with non-empty prefix into some sort of decision tree
    function update(evt) {
      if (evt && evt.defaultPrevented) return;
      var ignoreUpdate = lastPushedUrl && $location.url() === lastPushedUrl;
      lastPushedUrl = undefined;
      // TODO: Re-implement this in 1.0 for https://github.com/angular-ui/ui-router/issues/1573
      //if (ignoreUpdate) return true;

      function check(rule) {
        var handled = rule($injector, $location);

        if (!handled) return false;
        if (isString(handled)) $location.replace().url(handled);
        return true;
      }
      var n = rules.length, i;

      for (i = 0; i < n; i++) {
        if (check(rules[i])) return;
      }
      // always check otherwise last to allow dynamic updates to the set of rules
      if (otherwise) check(otherwise);
    }

    function listen() {
      listener = listener || $rootScope.$on('$locationChangeSuccess', update);
      return listener;
    }

    if (!interceptDeferred) listen();

    return {
      /**
       * @ngdoc function
       * @name ui.router.router.$urlRouter#sync
       * @methodOf ui.router.router.$urlRouter
       *
       * @description
       * Triggers an update; the same update that happens when the address bar url changes, aka `$locationChangeSuccess`.
       * This method is useful when you need to use `preventDefault()` on the `$locationChangeSuccess` event,
       * perform some custom logic (route protection, auth, config, redirection, etc) and then finally proceed
       * with the transition by calling `$urlRouter.sync()`.
       *
       * @example
       * <pre>
       * angular.module('app', ['ui.router'])
       *   .run(function($rootScope, $urlRouter) {
       *     $rootScope.$on('$locationChangeSuccess', function(evt) {
       *       // Halt state change from even starting
       *       evt.preventDefault();
       *       // Perform custom logic
       *       var meetsRequirement = ...
       *       // Continue with the update and state transition if logic allows
       *       if (meetsRequirement) $urlRouter.sync();
       *     });
       * });
       * </pre>
       */
      sync: function() {
        update();
      },

      listen: function() {
        return listen();
      },

      update: function(read) {
        if (read) {
          location = $location.url();
          return;
        }
        if ($location.url() === location) return;

        $location.url(location);
        $location.replace();
      },

      push: function(urlMatcher, params, options) {
         var url = urlMatcher.format(params || {});

        // Handle the special hash param, if needed
        if (url !== null && params && params['#']) {
            url += '#' + params['#'];
        }

        $location.url(url);
        lastPushedUrl = options && options.$$avoidResync ? $location.url() : undefined;
        if (options && options.replace) $location.replace();
      },

      /**
       * @ngdoc function
       * @name ui.router.router.$urlRouter#href
       * @methodOf ui.router.router.$urlRouter
       *
       * @description
       * A URL generation method that returns the compiled URL for a given
       * {@link ui.router.util.type:UrlMatcher `UrlMatcher`}, populated with the provided parameters.
       *
       * @example
       * <pre>
       * $bob = $urlRouter.href(new UrlMatcher("/about/:person"), {
       *   person: "bob"
       * });
       * // $bob == "/about/bob";
       * </pre>
       *
       * @param {UrlMatcher} urlMatcher The `UrlMatcher` object which is used as the template of the URL to generate.
       * @param {object=} params An object of parameter values to fill the matcher's required parameters.
       * @param {object=} options Options object. The options are:
       *
       * - **`absolute`** - {boolean=false},  If true will generate an absolute url, e.g. "http://www.example.com/fullurl".
       *
       * @returns {string} Returns the fully compiled URL, or `null` if `params` fail validation against `urlMatcher`
       */
      href: function(urlMatcher, params, options) {
        if (!urlMatcher.validates(params)) return null;

        var isHtml5 = $locationProvider.html5Mode();
        if (angular.isObject(isHtml5)) {
          isHtml5 = isHtml5.enabled;
        }

        isHtml5 = isHtml5 && $sniffer.history;
        
        var url = urlMatcher.format(params);
        options = options || {};

        if (!isHtml5 && url !== null) {
          url = "#" + $locationProvider.hashPrefix() + url;
        }

        // Handle special hash param, if needed
        if (url !== null && params && params['#']) {
          url += '#' + params['#'];
        }

        url = appendBasePath(url, isHtml5, options.absolute);

        if (!options.absolute || !url) {
          return url;
        }

        var slash = (!isHtml5 && url ? '/' : ''), port = $location.port();
        port = (port === 80 || port === 443 ? '' : ':' + port);

        return [$location.protocol(), '://', $location.host(), port, slash, url].join('');
      }
    };
  }
}

angular.module('ui.router.router').provider('$urlRouter', $UrlRouterProvider);

/**
 * @ngdoc object
 * @name ui.router.state.$stateProvider
 *
 * @requires ui.router.router.$urlRouterProvider
 * @requires ui.router.util.$urlMatcherFactoryProvider
 *
 * @description
 * The new `$stateProvider` works similar to Angular's v1 router, but it focuses purely
 * on state.
 *
 * A state corresponds to a "place" in the application in terms of the overall UI and
 * navigation. A state describes (via the controller / template / view properties) what
 * the UI looks like and does at that place.
 *
 * States often have things in common, and the primary way of factoring out these
 * commonalities in this model is via the state hierarchy, i.e. parent/child states aka
 * nested states.
 *
 * The `$stateProvider` provides interfaces to declare these states for your app.
 */
$StateProvider.$inject = ['$urlRouterProvider', '$urlMatcherFactoryProvider'];
function $StateProvider(   $urlRouterProvider,   $urlMatcherFactory) {

  var root, states = {}, $state, queue = {}, abstractKey = 'abstract';

  // Builds state properties from definition passed to registerState()
  var stateBuilder = {

    // Derive parent state from a hierarchical name only if 'parent' is not explicitly defined.
    // state.children = [];
    // if (parent) parent.children.push(state);
    parent: function(state) {
      if (isDefined(state.parent) && state.parent) return findState(state.parent);
      // regex matches any valid composite state name
      // would match "contact.list" but not "contacts"
      var compositeName = /^(.+)\.[^.]+$/.exec(state.name);
      return compositeName ? findState(compositeName[1]) : root;
    },

    // inherit 'data' from parent and override by own values (if any)
    data: function(state) {
      if (state.parent && state.parent.data) {
        state.data = state.self.data = inherit(state.parent.data, state.data);
      }
      return state.data;
    },

    // Build a URLMatcher if necessary, either via a relative or absolute URL
    url: function(state) {
      var url = state.url, config = { params: state.params || {} };

      if (isString(url)) {
        if (url.charAt(0) == '^') return $urlMatcherFactory.compile(url.substring(1), config);
        return (state.parent.navigable || root).url.concat(url, config);
      }

      if (!url || $urlMatcherFactory.isMatcher(url)) return url;
      throw new Error("Invalid url '" + url + "' in state '" + state + "'");
    },

    // Keep track of the closest ancestor state that has a URL (i.e. is navigable)
    navigable: function(state) {
      return state.url ? state : (state.parent ? state.parent.navigable : null);
    },

    // Own parameters for this state. state.url.params is already built at this point. Create and add non-url params
    ownParams: function(state) {
      var params = state.url && state.url.params || new $$UMFP.ParamSet();
      forEach(state.params || {}, function(config, id) {
        if (!params[id]) params[id] = new $$UMFP.Param(id, null, config, "config");
      });
      return params;
    },

    // Derive parameters for this state and ensure they're a super-set of parent's parameters
    params: function(state) {
      var ownParams = pick(state.ownParams, state.ownParams.$$keys());
      return state.parent && state.parent.params ? extend(state.parent.params.$$new(), ownParams) : new $$UMFP.ParamSet();
    },

    // If there is no explicit multi-view configuration, make one up so we don't have
    // to handle both cases in the view directive later. Note that having an explicit
    // 'views' property will mean the default unnamed view properties are ignored. This
    // is also a good time to resolve view names to absolute names, so everything is a
    // straight lookup at link time.
    views: function(state) {
      var views = {};

      forEach(isDefined(state.views) ? state.views : { '': state }, function (view, name) {
        if (name.indexOf('@') < 0) name += '@' + state.parent.name;
        view.resolveAs = view.resolveAs || state.resolveAs || '$resolve';
        views[name] = view;
      });
      return views;
    },

    // Keep a full path from the root down to this state as this is needed for state activation.
    path: function(state) {
      return state.parent ? state.parent.path.concat(state) : []; // exclude root from path
    },

    // Speed up $state.contains() as it's used a lot
    includes: function(state) {
      var includes = state.parent ? extend({}, state.parent.includes) : {};
      includes[state.name] = true;
      return includes;
    },

    $delegates: {}
  };

  function isRelative(stateName) {
    return stateName.indexOf(".") === 0 || stateName.indexOf("^") === 0;
  }

  function findState(stateOrName, base) {
    if (!stateOrName) return undefined;

    var isStr = isString(stateOrName),
        name  = isStr ? stateOrName : stateOrName.name,
        path  = isRelative(name);

    if (path) {
      if (!base) throw new Error("No reference point given for path '"  + name + "'");
      base = findState(base);
      
      var rel = name.split("."), i = 0, pathLength = rel.length, current = base;

      for (; i < pathLength; i++) {
        if (rel[i] === "" && i === 0) {
          current = base;
          continue;
        }
        if (rel[i] === "^") {
          if (!current.parent) throw new Error("Path '" + name + "' not valid for state '" + base.name + "'");
          current = current.parent;
          continue;
        }
        break;
      }
      rel = rel.slice(i).join(".");
      name = current.name + (current.name && rel ? "." : "") + rel;
    }
    var state = states[name];

    if (state && (isStr || (!isStr && (state === stateOrName || state.self === stateOrName)))) {
      return state;
    }
    return undefined;
  }

  function queueState(parentName, state) {
    if (!queue[parentName]) {
      queue[parentName] = [];
    }
    queue[parentName].push(state);
  }

  function flushQueuedChildren(parentName) {
    var queued = queue[parentName] || [];
    while(queued.length) {
      registerState(queued.shift());
    }
  }

  function registerState(state) {
    // Wrap a new object around the state so we can store our private details easily.
    state = inherit(state, {
      self: state,
      resolve: state.resolve || {},
      toString: function() { return this.name; }
    });

    var name = state.name;
    if (!isString(name) || name.indexOf('@') >= 0) throw new Error("State must have a valid name");
    if (states.hasOwnProperty(name)) throw new Error("State '" + name + "' is already defined");

    // Get parent name
    var parentName = (name.indexOf('.') !== -1) ? name.substring(0, name.lastIndexOf('.'))
        : (isString(state.parent)) ? state.parent
        : (isObject(state.parent) && isString(state.parent.name)) ? state.parent.name
        : '';

    // If parent is not registered yet, add state to queue and register later
    if (parentName && !states[parentName]) {
      return queueState(parentName, state.self);
    }

    for (var key in stateBuilder) {
      if (isFunction(stateBuilder[key])) state[key] = stateBuilder[key](state, stateBuilder.$delegates[key]);
    }
    states[name] = state;

    // Register the state in the global state list and with $urlRouter if necessary.
    if (!state[abstractKey] && state.url) {
      $urlRouterProvider.when(state.url, ['$match', '$stateParams', function ($match, $stateParams) {
        if ($state.$current.navigable != state || !equalForKeys($match, $stateParams)) {
          $state.transitionTo(state, $match, { inherit: true, location: false });
        }
      }]);
    }

    // Register any queued children
    flushQueuedChildren(name);

    return state;
  }

  // Checks text to see if it looks like a glob.
  function isGlob (text) {
    return text.indexOf('*') > -1;
  }

  // Returns true if glob matches current $state name.
  function doesStateMatchGlob (glob) {
    var globSegments = glob.split('.'),
        segments = $state.$current.name.split('.');

    //match single stars
    for (var i = 0, l = globSegments.length; i < l; i++) {
      if (globSegments[i] === '*') {
        segments[i] = '*';
      }
    }

    //match greedy starts
    if (globSegments[0] === '**') {
       segments = segments.slice(indexOf(segments, globSegments[1]));
       segments.unshift('**');
    }
    //match greedy ends
    if (globSegments[globSegments.length - 1] === '**') {
       segments.splice(indexOf(segments, globSegments[globSegments.length - 2]) + 1, Number.MAX_VALUE);
       segments.push('**');
    }

    if (globSegments.length != segments.length) {
      return false;
    }

    return segments.join('') === globSegments.join('');
  }


  // Implicit root state that is always active
  root = registerState({
    name: '',
    url: '^',
    views: null,
    'abstract': true
  });
  root.navigable = null;


  /**
   * @ngdoc function
   * @name ui.router.state.$stateProvider#decorator
   * @methodOf ui.router.state.$stateProvider
   *
   * @description
   * Allows you to extend (carefully) or override (at your own peril) the 
   * `stateBuilder` object used internally by `$stateProvider`. This can be used 
   * to add custom functionality to ui-router, for example inferring templateUrl 
   * based on the state name.
   *
   * When passing only a name, it returns the current (original or decorated) builder
   * function that matches `name`.
   *
   * The builder functions that can be decorated are listed below. Though not all
   * necessarily have a good use case for decoration, that is up to you to decide.
   *
   * In addition, users can attach custom decorators, which will generate new 
   * properties within the state's internal definition. There is currently no clear 
   * use-case for this beyond accessing internal states (i.e. $state.$current), 
   * however, expect this to become increasingly relevant as we introduce additional 
   * meta-programming features.
   *
   * **Warning**: Decorators should not be interdependent because the order of 
   * execution of the builder functions in non-deterministic. Builder functions 
   * should only be dependent on the state definition object and super function.
   *
   *
   * Existing builder functions and current return values:
   *
   * - **parent** `{object}` - returns the parent state object.
   * - **data** `{object}` - returns state data, including any inherited data that is not
   *   overridden by own values (if any).
   * - **url** `{object}` - returns a {@link ui.router.util.type:UrlMatcher UrlMatcher}
   *   or `null`.
   * - **navigable** `{object}` - returns closest ancestor state that has a URL (aka is 
   *   navigable).
   * - **params** `{object}` - returns an array of state params that are ensured to 
   *   be a super-set of parent's params.
   * - **views** `{object}` - returns a views object where each key is an absolute view 
   *   name (i.e. "viewName@stateName") and each value is the config object 
   *   (template, controller) for the view. Even when you don't use the views object 
   *   explicitly on a state config, one is still created for you internally.
   *   So by decorating this builder function you have access to decorating template 
   *   and controller properties.
   * - **ownParams** `{object}` - returns an array of params that belong to the state, 
   *   not including any params defined by ancestor states.
   * - **path** `{string}` - returns the full path from the root down to this state. 
   *   Needed for state activation.
   * - **includes** `{object}` - returns an object that includes every state that 
   *   would pass a `$state.includes()` test.
   *
   * @example
   * <pre>
   * // Override the internal 'views' builder with a function that takes the state
   * // definition, and a reference to the internal function being overridden:
   * $stateProvider.decorator('views', function (state, parent) {
   *   var result = {},
   *       views = parent(state);
   *
   *   angular.forEach(views, function (config, name) {
   *     var autoName = (state.name + '.' + name).replace('.', '/');
   *     config.templateUrl = config.templateUrl || '/partials/' + autoName + '.html';
   *     result[name] = config;
   *   });
   *   return result;
   * });
   *
   * $stateProvider.state('home', {
   *   views: {
   *     'contact.list': { controller: 'ListController' },
   *     'contact.item': { controller: 'ItemController' }
   *   }
   * });
   *
   * // ...
   *
   * $state.go('home');
   * // Auto-populates list and item views with /partials/home/contact/list.html,
   * // and /partials/home/contact/item.html, respectively.
   * </pre>
   *
   * @param {string} name The name of the builder function to decorate. 
   * @param {object} func A function that is responsible for decorating the original 
   * builder function. The function receives two parameters:
   *
   *   - `{object}` - state - The state config object.
   *   - `{object}` - super - The original builder function.
   *
   * @return {object} $stateProvider - $stateProvider instance
   */
  this.decorator = decorator;
  function decorator(name, func) {
    /*jshint validthis: true */
    if (isString(name) && !isDefined(func)) {
      return stateBuilder[name];
    }
    if (!isFunction(func) || !isString(name)) {
      return this;
    }
    if (stateBuilder[name] && !stateBuilder.$delegates[name]) {
      stateBuilder.$delegates[name] = stateBuilder[name];
    }
    stateBuilder[name] = func;
    return this;
  }

  /**
   * @ngdoc function
   * @name ui.router.state.$stateProvider#state
   * @methodOf ui.router.state.$stateProvider
   *
   * @description
   * Registers a state configuration under a given state name. The stateConfig object
   * has the following acceptable properties.
   *
   * @param {string} name A unique state name, e.g. "home", "about", "contacts".
   * To create a parent/child state use a dot, e.g. "about.sales", "home.newest".
   * @param {object} stateConfig State configuration object.
   * @param {string|function=} stateConfig.template
   * <a id='template'></a>
   *   html template as a string or a function that returns
   *   an html template as a string which should be used by the uiView directives. This property 
   *   takes precedence over templateUrl.
   *   
   *   If `template` is a function, it will be called with the following parameters:
   *
   *   - {array.&lt;object&gt;} - state parameters extracted from the current $location.path() by
   *     applying the current state
   *
   * <pre>template:
   *   "<h1>inline template definition</h1>" +
   *   "<div ui-view></div>"</pre>
   * <pre>template: function(params) {
   *       return "<h1>generated template</h1>"; }</pre>
   * </div>
   *
   * @param {string|function=} stateConfig.templateUrl
   * <a id='templateUrl'></a>
   *
   *   path or function that returns a path to an html
   *   template that should be used by uiView.
   *   
   *   If `templateUrl` is a function, it will be called with the following parameters:
   *
   *   - {array.&lt;object&gt;} - state parameters extracted from the current $location.path() by 
   *     applying the current state
   *
   * <pre>templateUrl: "home.html"</pre>
   * <pre>templateUrl: function(params) {
   *     return myTemplates[params.pageId]; }</pre>
   *
   * @param {function=} stateConfig.templateProvider
   * <a id='templateProvider'></a>
   *    Provider function that returns HTML content string.
   * <pre> templateProvider:
   *       function(MyTemplateService, params) {
   *         return MyTemplateService.getTemplate(params.pageId);
   *       }</pre>
   *
   * @param {string|function=} stateConfig.controller
   * <a id='controller'></a>
   *
   *  Controller fn that should be associated with newly
   *   related scope or the name of a registered controller if passed as a string.
   *   Optionally, the ControllerAs may be declared here.
   * <pre>controller: "MyRegisteredController"</pre>
   * <pre>controller:
   *     "MyRegisteredController as fooCtrl"}</pre>
   * <pre>controller: function($scope, MyService) {
   *     $scope.data = MyService.getData(); }</pre>
   *
   * @param {function=} stateConfig.controllerProvider
   * <a id='controllerProvider'></a>
   *
   * Injectable provider function that returns the actual controller or string.
   * <pre>controllerProvider:
   *   function(MyResolveData) {
   *     if (MyResolveData.foo)
   *       return "FooCtrl"
   *     else if (MyResolveData.bar)
   *       return "BarCtrl";
   *     else return function($scope) {
   *       $scope.baz = "Qux";
   *     }
   *   }</pre>
   *
   * @param {string=} stateConfig.controllerAs
   * <a id='controllerAs'></a>
   * 
   * A controller alias name. If present the controller will be
   *   published to scope under the controllerAs name.
   * <pre>controllerAs: "myCtrl"</pre>
   *
   * @param {string|object=} stateConfig.parent
   * <a id='parent'></a>
   * Optionally specifies the parent state of this state.
   *
   * <pre>parent: 'parentState'</pre>
   * <pre>parent: parentState // JS variable</pre>
   *
   * @param {object=} stateConfig.resolve
   * <a id='resolve'></a>
   *
   * An optional map&lt;string, function&gt; of dependencies which
   *   should be injected into the controller. If any of these dependencies are promises, 
   *   the router will wait for them all to be resolved before the controller is instantiated.
   *   If all the promises are resolved successfully, the $stateChangeSuccess event is fired
   *   and the values of the resolved promises are injected into any controllers that reference them.
   *   If any  of the promises are rejected the $stateChangeError event is fired.
   *
   *   The map object is:
   *   
   *   - key - {string}: name of dependency to be injected into controller
   *   - factory - {string|function}: If string then it is alias for service. Otherwise if function, 
   *     it is injected and return value it treated as dependency. If result is a promise, it is 
   *     resolved before its value is injected into controller.
   *
   * <pre>resolve: {
   *     myResolve1:
   *       function($http, $stateParams) {
   *         return $http.get("/api/foos/"+stateParams.fooID);
   *       }
   *     }</pre>
   *
   * @param {string=} stateConfig.url
   * <a id='url'></a>
   *
   *   A url fragment with optional parameters. When a state is navigated or
   *   transitioned to, the `$stateParams` service will be populated with any 
   *   parameters that were passed.
   *
   *   (See {@link ui.router.util.type:UrlMatcher UrlMatcher} `UrlMatcher`} for
   *   more details on acceptable patterns )
   *
   * examples:
   * <pre>url: "/home"
   * url: "/users/:userid"
   * url: "/books/{bookid:[a-zA-Z_-]}"
   * url: "/books/{categoryid:int}"
   * url: "/books/{publishername:string}/{categoryid:int}"
   * url: "/messages?before&after"
   * url: "/messages?{before:date}&{after:date}"
   * url: "/messages/:mailboxid?{before:date}&{after:date}"
   * </pre>
   *
   * @param {object=} stateConfig.views
   * <a id='views'></a>
   * an optional map&lt;string, object&gt; which defined multiple views, or targets views
   * manually/explicitly.
   *
   * Examples:
   *
   * Targets three named `ui-view`s in the parent state's template
   * <pre>views: {
   *     header: {
   *       controller: "headerCtrl",
   *       templateUrl: "header.html"
   *     }, body: {
   *       controller: "bodyCtrl",
   *       templateUrl: "body.html"
   *     }, footer: {
   *       controller: "footCtrl",
   *       templateUrl: "footer.html"
   *     }
   *   }</pre>
   *
   * Targets named `ui-view="header"` from grandparent state 'top''s template, and named `ui-view="body" from parent state's template.
   * <pre>views: {
   *     'header@top': {
   *       controller: "msgHeaderCtrl",
   *       templateUrl: "msgHeader.html"
   *     }, 'body': {
   *       controller: "messagesCtrl",
   *       templateUrl: "messages.html"
   *     }
   *   }</pre>
   *
   * @param {boolean=} [stateConfig.abstract=false]
   * <a id='abstract'></a>
   * An abstract state will never be directly activated,
   *   but can provide inherited properties to its common children states.
   * <pre>abstract: true</pre>
   *
   * @param {function=} stateConfig.onEnter
   * <a id='onEnter'></a>
   *
   * Callback function for when a state is entered. Good way
   *   to trigger an action or dispatch an event, such as opening a dialog.
   * If minifying your scripts, make sure to explicitly annotate this function,
   * because it won't be automatically annotated by your build tools.
   *
   * <pre>onEnter: function(MyService, $stateParams) {
   *     MyService.foo($stateParams.myParam);
   * }</pre>
   *
   * @param {function=} stateConfig.onExit
   * <a id='onExit'></a>
   *
   * Callback function for when a state is exited. Good way to
   *   trigger an action or dispatch an event, such as opening a dialog.
   * If minifying your scripts, make sure to explicitly annotate this function,
   * because it won't be automatically annotated by your build tools.
   *
   * <pre>onExit: function(MyService, $stateParams) {
   *     MyService.cleanup($stateParams.myParam);
   * }</pre>
   *
   * @param {boolean=} [stateConfig.reloadOnSearch=true]
   * <a id='reloadOnSearch'></a>
   *
   * If `false`, will not retrigger the same state
   *   just because a search/query parameter has changed (via $location.search() or $location.hash()). 
   *   Useful for when you'd like to modify $location.search() without triggering a reload.
   * <pre>reloadOnSearch: false</pre>
   *
   * @param {object=} stateConfig.data
   * <a id='data'></a>
   *
   * Arbitrary data object, useful for custom configuration.  The parent state's `data` is
   *   prototypally inherited.  In other words, adding a data property to a state adds it to
   *   the entire subtree via prototypal inheritance.
   *
   * <pre>data: {
   *     requiredRole: 'foo'
   * } </pre>
   *
   * @param {object=} stateConfig.params
   * <a id='params'></a>
   *
   * A map which optionally configures parameters declared in the `url`, or
   *   defines additional non-url parameters.  For each parameter being
   *   configured, add a configuration object keyed to the name of the parameter.
   *
   *   Each parameter configuration object may contain the following properties:
   *
   *   - ** value ** - {object|function=}: specifies the default value for this
   *     parameter.  This implicitly sets this parameter as optional.
   *
   *     When UI-Router routes to a state and no value is
   *     specified for this parameter in the URL or transition, the
   *     default value will be used instead.  If `value` is a function,
   *     it will be injected and invoked, and the return value used.
   *
   *     *Note*: `undefined` is treated as "no default value" while `null`
   *     is treated as "the default value is `null`".
   *
   *     *Shorthand*: If you only need to configure the default value of the
   *     parameter, you may use a shorthand syntax.   In the **`params`**
   *     map, instead mapping the param name to a full parameter configuration
   *     object, simply set map it to the default parameter value, e.g.:
   *
   * <pre>// define a parameter's default value
   * params: {
   *     param1: { value: "defaultValue" }
   * }
   * // shorthand default values
   * params: {
   *     param1: "defaultValue",
   *     param2: "param2Default"
   * }</pre>
   *
   *   - ** array ** - {boolean=}: *(default: false)* If true, the param value will be
   *     treated as an array of values.  If you specified a Type, the value will be
   *     treated as an array of the specified Type.  Note: query parameter values
   *     default to a special `"auto"` mode.
   *
   *     For query parameters in `"auto"` mode, if multiple  values for a single parameter
   *     are present in the URL (e.g.: `/foo?bar=1&bar=2&bar=3`) then the values
   *     are mapped to an array (e.g.: `{ foo: [ '1', '2', '3' ] }`).  However, if
   *     only one value is present (e.g.: `/foo?bar=1`) then the value is treated as single
   *     value (e.g.: `{ foo: '1' }`).
   *
   * <pre>params: {
   *     param1: { array: true }
   * }</pre>
   *
   *   - ** squash ** - {bool|string=}: `squash` configures how a default parameter value is represented in the URL when
   *     the current parameter value is the same as the default value. If `squash` is not set, it uses the
   *     configured default squash policy.
   *     (See {@link ui.router.util.$urlMatcherFactory#methods_defaultSquashPolicy `defaultSquashPolicy()`})
   *
   *   There are three squash settings:
   *
   *     - false: The parameter's default value is not squashed.  It is encoded and included in the URL
   *     - true: The parameter's default value is omitted from the URL.  If the parameter is preceeded and followed
   *       by slashes in the state's `url` declaration, then one of those slashes are omitted.
   *       This can allow for cleaner looking URLs.
   *     - `"<arbitrary string>"`: The parameter's default value is replaced with an arbitrary placeholder of  your choice.
   *
   * <pre>params: {
   *     param1: {
   *       value: "defaultId",
   *       squash: true
   * } }
   * // squash "defaultValue" to "~"
   * params: {
   *     param1: {
   *       value: "defaultValue",
   *       squash: "~"
   * } }
   * </pre>
   *
   *
   * @example
   * <pre>
   * // Some state name examples
   *
   * // stateName can be a single top-level name (must be unique).
   * $stateProvider.state("home", {});
   *
   * // Or it can be a nested state name. This state is a child of the
   * // above "home" state.
   * $stateProvider.state("home.newest", {});
   *
   * // Nest states as deeply as needed.
   * $stateProvider.state("home.newest.abc.xyz.inception", {});
   *
   * // state() returns $stateProvider, so you can chain state declarations.
   * $stateProvider
   *   .state("home", {})
   *   .state("about", {})
   *   .state("contacts", {});
   * </pre>
   *
   */
  this.state = state;
  function state(name, definition) {
    /*jshint validthis: true */
    if (isObject(name)) definition = name;
    else definition.name = name;
    registerState(definition);
    return this;
  }

  /**
   * @ngdoc object
   * @name ui.router.state.$state
   *
   * @requires $rootScope
   * @requires $q
   * @requires ui.router.state.$view
   * @requires $injector
   * @requires ui.router.util.$resolve
   * @requires ui.router.state.$stateParams
   * @requires ui.router.router.$urlRouter
   *
   * @property {object} params A param object, e.g. {sectionId: section.id)}, that 
   * you'd like to test against the current active state.
   * @property {object} current A reference to the state's config object. However 
   * you passed it in. Useful for accessing custom data.
   * @property {object} transition Currently pending transition. A promise that'll 
   * resolve or reject.
   *
   * @description
   * `$state` service is responsible for representing states as well as transitioning
   * between them. It also provides interfaces to ask for current state or even states
   * you're coming from.
   */
  this.$get = $get;
  $get.$inject = ['$rootScope', '$q', '$view', '$injector', '$resolve', '$stateParams', '$urlRouter', '$location', '$urlMatcherFactory'];
  function $get(   $rootScope,   $q,   $view,   $injector,   $resolve,   $stateParams,   $urlRouter,   $location,   $urlMatcherFactory) {

    var TransitionSupersededError = new Error('transition superseded');

    var TransitionSuperseded = silenceUncaughtInPromise($q.reject(TransitionSupersededError));
    var TransitionPrevented = silenceUncaughtInPromise($q.reject(new Error('transition prevented')));
    var TransitionAborted = silenceUncaughtInPromise($q.reject(new Error('transition aborted')));
    var TransitionFailed = silenceUncaughtInPromise($q.reject(new Error('transition failed')));

    // Handles the case where a state which is the target of a transition is not found, and the user
    // can optionally retry or defer the transition
    function handleRedirect(redirect, state, params, options) {
      /**
       * @ngdoc event
       * @name ui.router.state.$state#$stateNotFound
       * @eventOf ui.router.state.$state
       * @eventType broadcast on root scope
       * @description
       * Fired when a requested state **cannot be found** using the provided state name during transition.
       * The event is broadcast allowing any handlers a single chance to deal with the error (usually by
       * lazy-loading the unfound state). A special `unfoundState` object is passed to the listener handler,
       * you can see its three properties in the example. You can use `event.preventDefault()` to abort the
       * transition and the promise returned from `go` will be rejected with a `'transition aborted'` value.
       *
       * @param {Object} event Event object.
       * @param {Object} unfoundState Unfound State information. Contains: `to, toParams, options` properties.
       * @param {State} fromState Current state object.
       * @param {Object} fromParams Current state params.
       *
       * @example
       *
       * <pre>
       * // somewhere, assume lazy.state has not been defined
       * $state.go("lazy.state", {a:1, b:2}, {inherit:false});
       *
       * // somewhere else
       * $scope.$on('$stateNotFound',
       * function(event, unfoundState, fromState, fromParams){
       *     console.log(unfoundState.to); // "lazy.state"
       *     console.log(unfoundState.toParams); // {a:1, b:2}
       *     console.log(unfoundState.options); // {inherit:false} + default options
       * })
       * </pre>
       */
      var evt = $rootScope.$broadcast('$stateNotFound', redirect, state, params);

      if (evt.defaultPrevented) {
        $urlRouter.update();
        return TransitionAborted;
      }

      if (!evt.retry) {
        return null;
      }

      // Allow the handler to return a promise to defer state lookup retry
      if (options.$retry) {
        $urlRouter.update();
        return TransitionFailed;
      }
      var retryTransition = $state.transition = $q.when(evt.retry);

      retryTransition.then(function() {
        if (retryTransition !== $state.transition) {
          $rootScope.$broadcast('$stateChangeCancel', redirect.to, redirect.toParams, state, params);
          return TransitionSuperseded;
        }
        redirect.options.$retry = true;
        return $state.transitionTo(redirect.to, redirect.toParams, redirect.options);
      }, function() {
        return TransitionAborted;
      });
      $urlRouter.update();

      return retryTransition;
    }

    root.locals = { resolve: null, globals: { $stateParams: {} } };

    $state = {
      params: {},
      current: root.self,
      $current: root,
      transition: null
    };

    /**
     * @ngdoc function
     * @name ui.router.state.$state#reload
     * @methodOf ui.router.state.$state
     *
     * @description
     * A method that force reloads the current state. All resolves are re-resolved,
     * controllers reinstantiated, and events re-fired.
     *
     * @example
     * <pre>
     * var app angular.module('app', ['ui.router']);
     *
     * app.controller('ctrl', function ($scope, $state) {
     *   $scope.reload = function(){
     *     $state.reload();
     *   }
     * });
     * </pre>
     *
     * `reload()` is just an alias for:
     * <pre>
     * $state.transitionTo($state.current, $stateParams, { 
     *   reload: true, inherit: false, notify: true
     * });
     * </pre>
     *
     * @param {string=|object=} state - A state name or a state object, which is the root of the resolves to be re-resolved.
     * @example
     * <pre>
     * //assuming app application consists of 3 states: 'contacts', 'contacts.detail', 'contacts.detail.item' 
     * //and current state is 'contacts.detail.item'
     * var app angular.module('app', ['ui.router']);
     *
     * app.controller('ctrl', function ($scope, $state) {
     *   $scope.reload = function(){
     *     //will reload 'contact.detail' and 'contact.detail.item' states
     *     $state.reload('contact.detail');
     *   }
     * });
     * </pre>
     *
     * `reload()` is just an alias for:
     * <pre>
     * $state.transitionTo($state.current, $stateParams, { 
     *   reload: true, inherit: false, notify: true
     * });
     * </pre>

     * @returns {promise} A promise representing the state of the new transition. See
     * {@link ui.router.state.$state#methods_go $state.go}.
     */
    $state.reload = function reload(state) {
      return $state.transitionTo($state.current, $stateParams, { reload: state || true, inherit: false, notify: true});
    };

    /**
     * @ngdoc function
     * @name ui.router.state.$state#go
     * @methodOf ui.router.state.$state
     *
     * @description
     * Convenience method for transitioning to a new state. `$state.go` calls 
     * `$state.transitionTo` internally but automatically sets options to 
     * `{ location: true, inherit: true, relative: $state.$current, notify: true }`. 
     * This allows you to easily use an absolute or relative to path and specify 
     * only the parameters you'd like to update (while letting unspecified parameters 
     * inherit from the currently active ancestor states).
     *
     * @example
     * <pre>
     * var app = angular.module('app', ['ui.router']);
     *
     * app.controller('ctrl', function ($scope, $state) {
     *   $scope.changeState = function () {
     *     $state.go('contact.detail');
     *   };
     * });
     * </pre>
     * <img src='../ngdoc_assets/StateGoExamples.png'/>
     *
     * @param {string} to Absolute state name or relative state path. Some examples:
     *
     * - `$state.go('contact.detail')` - will go to the `contact.detail` state
     * - `$state.go('^')` - will go to a parent state
     * - `$state.go('^.sibling')` - will go to a sibling state
     * - `$state.go('.child.grandchild')` - will go to grandchild state
     *
     * @param {object=} params A map of the parameters that will be sent to the state, 
     * will populate $stateParams. Any parameters that are not specified will be inherited from currently 
     * defined parameters. Only parameters specified in the state definition can be overridden, new 
     * parameters will be ignored. This allows, for example, going to a sibling state that shares parameters
     * specified in a parent state. Parameter inheritance only works between common ancestor states, I.e.
     * transitioning to a sibling will get you the parameters for all parents, transitioning to a child
     * will get you all current parameters, etc.
     * @param {object=} options Options object. The options are:
     *
     * - **`location`** - {boolean=true|string=} - If `true` will update the url in the location bar, if `false`
     *    will not. If string, must be `"replace"`, which will update url and also replace last history record.
     * - **`inherit`** - {boolean=true}, If `true` will inherit url parameters from current url.
     * - **`relative`** - {object=$state.$current}, When transitioning with relative path (e.g '^'), 
     *    defines which state to be relative from.
     * - **`notify`** - {boolean=true}, If `true` will broadcast $stateChangeStart and $stateChangeSuccess events.
     * - **`reload`** (v0.2.5) - {boolean=false|string|object}, If `true` will force transition even if no state or params
     *    have changed.  It will reload the resolves and views of the current state and parent states.
     *    If `reload` is a string (or state object), the state object is fetched (by name, or object reference); and \
     *    the transition reloads the resolves and views for that matched state, and all its children states.
     *
     * @returns {promise} A promise representing the state of the new transition.
     *
     * Possible success values:
     *
     * - $state.current
     *
     * <br/>Possible rejection values:
     *
     * - 'transition superseded' - when a newer transition has been started after this one
     * - 'transition prevented' - when `event.preventDefault()` has been called in a `$stateChangeStart` listener
     * - 'transition aborted' - when `event.preventDefault()` has been called in a `$stateNotFound` listener or
     *   when a `$stateNotFound` `event.retry` promise errors.
     * - 'transition failed' - when a state has been unsuccessfully found after 2 tries.
     * - *resolve error* - when an error has occurred with a `resolve`
     *
     */
    $state.go = function go(to, params, options) {
      return $state.transitionTo(to, params, extend({ inherit: true, relative: $state.$current }, options));
    };

    /**
     * @ngdoc function
     * @name ui.router.state.$state#transitionTo
     * @methodOf ui.router.state.$state
     *
     * @description
     * Low-level method for transitioning to a new state. {@link ui.router.state.$state#methods_go $state.go}
     * uses `transitionTo` internally. `$state.go` is recommended in most situations.
     *
     * @example
     * <pre>
     * var app = angular.module('app', ['ui.router']);
     *
     * app.controller('ctrl', function ($scope, $state) {
     *   $scope.changeState = function () {
     *     $state.transitionTo('contact.detail');
     *   };
     * });
     * </pre>
     *
     * @param {string} to State name.
     * @param {object=} toParams A map of the parameters that will be sent to the state,
     * will populate $stateParams.
     * @param {object=} options Options object. The options are:
     *
     * - **`location`** - {boolean=true|string=} - If `true` will update the url in the location bar, if `false`
     *    will not. If string, must be `"replace"`, which will update url and also replace last history record.
     * - **`inherit`** - {boolean=false}, If `true` will inherit url parameters from current url.
     * - **`relative`** - {object=}, When transitioning with relative path (e.g '^'), 
     *    defines which state to be relative from.
     * - **`notify`** - {boolean=true}, If `true` will broadcast $stateChangeStart and $stateChangeSuccess events.
     * - **`reload`** (v0.2.5) - {boolean=false|string=|object=}, If `true` will force transition even if the state or params 
     *    have not changed, aka a reload of the same state. It differs from reloadOnSearch because you'd
     *    use this when you want to force a reload when *everything* is the same, including search params.
     *    if String, then will reload the state with the name given in reload, and any children.
     *    if Object, then a stateObj is expected, will reload the state found in stateObj, and any children.
     *
     * @returns {promise} A promise representing the state of the new transition. See
     * {@link ui.router.state.$state#methods_go $state.go}.
     */
    $state.transitionTo = function transitionTo(to, toParams, options) {
      toParams = toParams || {};
      options = extend({
        location: true, inherit: false, relative: null, notify: true, reload: false, $retry: false
      }, options || {});

      var from = $state.$current, fromParams = $state.params, fromPath = from.path;
      var evt, toState = findState(to, options.relative);

      // Store the hash param for later (since it will be stripped out by various methods)
      var hash = toParams['#'];

      if (!isDefined(toState)) {
        var redirect = { to: to, toParams: toParams, options: options };
        var redirectResult = handleRedirect(redirect, from.self, fromParams, options);

        if (redirectResult) {
          return redirectResult;
        }

        // Always retry once if the $stateNotFound was not prevented
        // (handles either redirect changed or state lazy-definition)
        to = redirect.to;
        toParams = redirect.toParams;
        options = redirect.options;
        toState = findState(to, options.relative);

        if (!isDefined(toState)) {
          if (!options.relative) throw new Error("No such state '" + to + "'");
          throw new Error("Could not resolve '" + to + "' from state '" + options.relative + "'");
        }
      }
      if (toState[abstractKey]) throw new Error("Cannot transition to abstract state '" + to + "'");
      if (options.inherit) toParams = inheritParams($stateParams, toParams || {}, $state.$current, toState);
      if (!toState.params.$$validates(toParams)) return TransitionFailed;

      toParams = toState.params.$$values(toParams);
      to = toState;

      var toPath = to.path;

      // Starting from the root of the path, keep all levels that haven't changed
      var keep = 0, state = toPath[keep], locals = root.locals, toLocals = [];

      if (!options.reload) {
        while (state && state === fromPath[keep] && state.ownParams.$$equals(toParams, fromParams)) {
          locals = toLocals[keep] = state.locals;
          keep++;
          state = toPath[keep];
        }
      } else if (isString(options.reload) || isObject(options.reload)) {
        if (isObject(options.reload) && !options.reload.name) {
          throw new Error('Invalid reload state object');
        }
        
        var reloadState = options.reload === true ? fromPath[0] : findState(options.reload);
        if (options.reload && !reloadState) {
          throw new Error("No such reload state '" + (isString(options.reload) ? options.reload : options.reload.name) + "'");
        }

        while (state && state === fromPath[keep] && state !== reloadState) {
          locals = toLocals[keep] = state.locals;
          keep++;
          state = toPath[keep];
        }
      }

      // If we're going to the same state and all locals are kept, we've got nothing to do.
      // But clear 'transition', as we still want to cancel any other pending transitions.
      // TODO: We may not want to bump 'transition' if we're called from a location change
      // that we've initiated ourselves, because we might accidentally abort a legitimate
      // transition initiated from code?
      if (shouldSkipReload(to, toParams, from, fromParams, locals, options)) {
        if (hash) toParams['#'] = hash;
        $state.params = toParams;
        copy($state.params, $stateParams);
        copy(filterByKeys(to.params.$$keys(), $stateParams), to.locals.globals.$stateParams);
        if (options.location && to.navigable && to.navigable.url) {
          $urlRouter.push(to.navigable.url, toParams, {
            $$avoidResync: true, replace: options.location === 'replace'
          });
          $urlRouter.update(true);
        }
        $state.transition = null;
        return $q.when($state.current);
      }

      // Filter parameters before we pass them to event handlers etc.
      toParams = filterByKeys(to.params.$$keys(), toParams || {});
      
      // Re-add the saved hash before we start returning things or broadcasting $stateChangeStart
      if (hash) toParams['#'] = hash;
      
      // Broadcast start event and cancel the transition if requested
      if (options.notify) {
        /**
         * @ngdoc event
         * @name ui.router.state.$state#$stateChangeStart
         * @eventOf ui.router.state.$state
         * @eventType broadcast on root scope
         * @description
         * Fired when the state transition **begins**. You can use `event.preventDefault()`
         * to prevent the transition from happening and then the transition promise will be
         * rejected with a `'transition prevented'` value.
         *
         * @param {Object} event Event object.
         * @param {State} toState The state being transitioned to.
         * @param {Object} toParams The params supplied to the `toState`.
         * @param {State} fromState The current state, pre-transition.
         * @param {Object} fromParams The params supplied to the `fromState`.
         *
         * @example
         *
         * <pre>
         * $rootScope.$on('$stateChangeStart',
         * function(event, toState, toParams, fromState, fromParams){
         *     event.preventDefault();
         *     // transitionTo() promise will be rejected with
         *     // a 'transition prevented' error
         * })
         * </pre>
         */
        if ($rootScope.$broadcast('$stateChangeStart', to.self, toParams, from.self, fromParams, options).defaultPrevented) {
          $rootScope.$broadcast('$stateChangeCancel', to.self, toParams, from.self, fromParams);
          //Don't update and resync url if there's been a new transition started. see issue #2238, #600
          if ($state.transition == null) $urlRouter.update();
          return TransitionPrevented;
        }
      }

      // Resolve locals for the remaining states, but don't update any global state just
      // yet -- if anything fails to resolve the current state needs to remain untouched.
      // We also set up an inheritance chain for the locals here. This allows the view directive
      // to quickly look up the correct definition for each view in the current state. Even
      // though we create the locals object itself outside resolveState(), it is initially
      // empty and gets filled asynchronously. We need to keep track of the promise for the
      // (fully resolved) current locals, and pass this down the chain.
      var resolved = $q.when(locals);

      for (var l = keep; l < toPath.length; l++, state = toPath[l]) {
        locals = toLocals[l] = inherit(locals);
        resolved = resolveState(state, toParams, state === to, resolved, locals, options);
      }

      // Once everything is resolved, we are ready to perform the actual transition
      // and return a promise for the new state. We also keep track of what the
      // current promise is, so that we can detect overlapping transitions and
      // keep only the outcome of the last transition.
      var transition = $state.transition = resolved.then(function () {
        var l, entering, exiting;

        if ($state.transition !== transition) {
          $rootScope.$broadcast('$stateChangeCancel', to.self, toParams, from.self, fromParams);
          return TransitionSuperseded;
        }

        // Exit 'from' states not kept
        for (l = fromPath.length - 1; l >= keep; l--) {
          exiting = fromPath[l];
          if (exiting.self.onExit) {
            $injector.invoke(exiting.self.onExit, exiting.self, exiting.locals.globals);
          }
          exiting.locals = null;
        }

        // Enter 'to' states not kept
        for (l = keep; l < toPath.length; l++) {
          entering = toPath[l];
          entering.locals = toLocals[l];
          if (entering.self.onEnter) {
            $injector.invoke(entering.self.onEnter, entering.self, entering.locals.globals);
          }
        }

        // Run it again, to catch any transitions in callbacks
        if ($state.transition !== transition) {
          $rootScope.$broadcast('$stateChangeCancel', to.self, toParams, from.self, fromParams);
          return TransitionSuperseded;
        }

        // Update globals in $state
        $state.$current = to;
        $state.current = to.self;
        $state.params = toParams;
        copy($state.params, $stateParams);
        $state.transition = null;

        if (options.location && to.navigable) {
          $urlRouter.push(to.navigable.url, to.navigable.locals.globals.$stateParams, {
            $$avoidResync: true, replace: options.location === 'replace'
          });
        }

        if (options.notify) {
        /**
         * @ngdoc event
         * @name ui.router.state.$state#$stateChangeSuccess
         * @eventOf ui.router.state.$state
         * @eventType broadcast on root scope
         * @description
         * Fired once the state transition is **complete**.
         *
         * @param {Object} event Event object.
         * @param {State} toState The state being transitioned to.
         * @param {Object} toParams The params supplied to the `toState`.
         * @param {State} fromState The current state, pre-transition.
         * @param {Object} fromParams The params supplied to the `fromState`.
         */
          $rootScope.$broadcast('$stateChangeSuccess', to.self, toParams, from.self, fromParams);
        }
        $urlRouter.update(true);

        return $state.current;
      }).then(null, function (error) {
        // propagate TransitionSuperseded error without emitting $stateChangeCancel
        // as it was already emitted in the success handler above
        if (error === TransitionSupersededError) return TransitionSuperseded;

        if ($state.transition !== transition) {
          $rootScope.$broadcast('$stateChangeCancel', to.self, toParams, from.self, fromParams);
          return TransitionSuperseded;
        }

        $state.transition = null;
        /**
         * @ngdoc event
         * @name ui.router.state.$state#$stateChangeError
         * @eventOf ui.router.state.$state
         * @eventType broadcast on root scope
         * @description
         * Fired when an **error occurs** during transition. It's important to note that if you
         * have any errors in your resolve functions (javascript errors, non-existent services, etc)
         * they will not throw traditionally. You must listen for this $stateChangeError event to
         * catch **ALL** errors.
         *
         * @param {Object} event Event object.
         * @param {State} toState The state being transitioned to.
         * @param {Object} toParams The params supplied to the `toState`.
         * @param {State} fromState The current state, pre-transition.
         * @param {Object} fromParams The params supplied to the `fromState`.
         * @param {Error} error The resolve error object.
         */
        evt = $rootScope.$broadcast('$stateChangeError', to.self, toParams, from.self, fromParams, error);

        if (!evt.defaultPrevented) {
          $urlRouter.update();
        }

        return $q.reject(error);
      });

      silenceUncaughtInPromise(transition);
      return transition;
    };

    /**
     * @ngdoc function
     * @name ui.router.state.$state#is
     * @methodOf ui.router.state.$state
     *
     * @description
     * Similar to {@link ui.router.state.$state#methods_includes $state.includes},
     * but only checks for the full state name. If params is supplied then it will be
     * tested for strict equality against the current active params object, so all params
     * must match with none missing and no extras.
     *
     * @example
     * <pre>
     * $state.$current.name = 'contacts.details.item';
     *
     * // absolute name
     * $state.is('contact.details.item'); // returns true
     * $state.is(contactDetailItemStateObject); // returns true
     *
     * // relative name (. and ^), typically from a template
     * // E.g. from the 'contacts.details' template
     * <div ng-class="{highlighted: $state.is('.item')}">Item</div>
     * </pre>
     *
     * @param {string|object} stateOrName The state name (absolute or relative) or state object you'd like to check.
     * @param {object=} params A param object, e.g. `{sectionId: section.id}`, that you'd like
     * to test against the current active state.
     * @param {object=} options An options object.  The options are:
     *
     * - **`relative`** - {string|object} -  If `stateOrName` is a relative state name and `options.relative` is set, .is will
     * test relative to `options.relative` state (or name).
     *
     * @returns {boolean} Returns true if it is the state.
     */
    $state.is = function is(stateOrName, params, options) {
      options = extend({ relative: $state.$current }, options || {});
      var state = findState(stateOrName, options.relative);

      if (!isDefined(state)) { return undefined; }
      if ($state.$current !== state) { return false; }

      return !params || objectKeys(params).reduce(function(acc, key) {
        var paramDef = state.params[key];
        return acc && (!paramDef || paramDef.type.equals($stateParams[key], params[key]));
      }, true);
    };

    /**
     * @ngdoc function
     * @name ui.router.state.$state#includes
     * @methodOf ui.router.state.$state
     *
     * @description
     * A method to determine if the current active state is equal to or is the child of the
     * state stateName. If any params are passed then they will be tested for a match as well.
     * Not all the parameters need to be passed, just the ones you'd like to test for equality.
     *
     * @example
     * Partial and relative names
     * <pre>
     * $state.$current.name = 'contacts.details.item';
     *
     * // Using partial names
     * $state.includes("contacts"); // returns true
     * $state.includes("contacts.details"); // returns true
     * $state.includes("contacts.details.item"); // returns true
     * $state.includes("contacts.list"); // returns false
     * $state.includes("about"); // returns false
     *
     * // Using relative names (. and ^), typically from a template
     * // E.g. from the 'contacts.details' template
     * <div ng-class="{highlighted: $state.includes('.item')}">Item</div>
     * </pre>
     *
     * Basic globbing patterns
     * <pre>
     * $state.$current.name = 'contacts.details.item.url';
     *
     * $state.includes("*.details.*.*"); // returns true
     * $state.includes("*.details.**"); // returns true
     * $state.includes("**.item.**"); // returns true
     * $state.includes("*.details.item.url"); // returns true
     * $state.includes("*.details.*.url"); // returns true
     * $state.includes("*.details.*"); // returns false
     * $state.includes("item.**"); // returns false
     * </pre>
     *
     * @param {string} stateOrName A partial name, relative name, or glob pattern
     * to be searched for within the current state name.
     * @param {object=} params A param object, e.g. `{sectionId: section.id}`,
     * that you'd like to test against the current active state.
     * @param {object=} options An options object.  The options are:
     *
     * - **`relative`** - {string|object=} -  If `stateOrName` is a relative state reference and `options.relative` is set,
     * .includes will test relative to `options.relative` state (or name).
     *
     * @returns {boolean} Returns true if it does include the state
     */
    $state.includes = function includes(stateOrName, params, options) {
      options = extend({ relative: $state.$current }, options || {});
      if (isString(stateOrName) && isGlob(stateOrName)) {
        if (!doesStateMatchGlob(stateOrName)) {
          return false;
        }
        stateOrName = $state.$current.name;
      }

      var state = findState(stateOrName, options.relative);
      if (!isDefined(state)) { return undefined; }
      if (!isDefined($state.$current.includes[state.name])) { return false; }
      if (!params) { return true; }

      var keys = objectKeys(params);
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i], paramDef = state.params[key];
        if (paramDef && !paramDef.type.equals($stateParams[key], params[key])) {
          return false;
        }
      }

      return objectKeys(params).reduce(function(acc, key) {
        var paramDef = state.params[key];
        return acc && !paramDef || paramDef.type.equals($stateParams[key], params[key]);
      }, true);
    };


    /**
     * @ngdoc function
     * @name ui.router.state.$state#href
     * @methodOf ui.router.state.$state
     *
     * @description
     * A url generation method that returns the compiled url for the given state populated with the given params.
     *
     * @example
     * <pre>
     * expect($state.href("about.person", { person: "bob" })).toEqual("/about/bob");
     * </pre>
     *
     * @param {string|object} stateOrName The state name or state object you'd like to generate a url from.
     * @param {object=} params An object of parameter values to fill the state's required parameters.
     * @param {object=} options Options object. The options are:
     *
     * - **`lossy`** - {boolean=true} -  If true, and if there is no url associated with the state provided in the
     *    first parameter, then the constructed href url will be built from the first navigable ancestor (aka
     *    ancestor with a valid url).
     * - **`inherit`** - {boolean=true}, If `true` will inherit url parameters from current url.
     * - **`relative`** - {object=$state.$current}, When transitioning with relative path (e.g '^'), 
     *    defines which state to be relative from.
     * - **`absolute`** - {boolean=false},  If true will generate an absolute url, e.g. "http://www.example.com/fullurl".
     * 
     * @returns {string} compiled state url
     */
    $state.href = function href(stateOrName, params, options) {
      options = extend({
        lossy:    true,
        inherit:  true,
        absolute: false,
        relative: $state.$current
      }, options || {});

      var state = findState(stateOrName, options.relative);

      if (!isDefined(state)) return null;
      if (options.inherit) params = inheritParams($stateParams, params || {}, $state.$current, state);
      
      var nav = (state && options.lossy) ? state.navigable : state;

      if (!nav || nav.url === undefined || nav.url === null) {
        return null;
      }
      return $urlRouter.href(nav.url, filterByKeys(state.params.$$keys().concat('#'), params || {}), {
        absolute: options.absolute
      });
    };

    /**
     * @ngdoc function
     * @name ui.router.state.$state#get
     * @methodOf ui.router.state.$state
     *
     * @description
     * Returns the state configuration object for any specific state or all states.
     *
     * @param {string|object=} stateOrName (absolute or relative) If provided, will only get the config for
     * the requested state. If not provided, returns an array of ALL state configs.
     * @param {string|object=} context When stateOrName is a relative state reference, the state will be retrieved relative to context.
     * @returns {Object|Array} State configuration object or array of all objects.
     */
    $state.get = function (stateOrName, context) {
      if (arguments.length === 0) return map(objectKeys(states), function(name) { return states[name].self; });
      var state = findState(stateOrName, context || $state.$current);
      return (state && state.self) ? state.self : null;
    };

    function resolveState(state, params, paramsAreFiltered, inherited, dst, options) {
      // Make a restricted $stateParams with only the parameters that apply to this state if
      // necessary. In addition to being available to the controller and onEnter/onExit callbacks,
      // we also need $stateParams to be available for any $injector calls we make during the
      // dependency resolution process.
      var $stateParams = (paramsAreFiltered) ? params : filterByKeys(state.params.$$keys(), params);
      var locals = { $stateParams: $stateParams };

      // Resolve 'global' dependencies for the state, i.e. those not specific to a view.
      // We're also including $stateParams in this; that way the parameters are restricted
      // to the set that should be visible to the state, and are independent of when we update
      // the global $state and $stateParams values.
      dst.resolve = $resolve.resolve(state.resolve, locals, dst.resolve, state);
      var promises = [dst.resolve.then(function (globals) {
        dst.globals = globals;
      })];
      if (inherited) promises.push(inherited);

      function resolveViews() {
        var viewsPromises = [];

        // Resolve template and dependencies for all views.
        forEach(state.views, function (view, name) {
          var injectables = (view.resolve && view.resolve !== state.resolve ? view.resolve : {});
          injectables.$template = [ function () {
            return $view.load(name, { view: view, locals: dst.globals, params: $stateParams, notify: options.notify }) || '';
          }];

          viewsPromises.push($resolve.resolve(injectables, dst.globals, dst.resolve, state).then(function (result) {
            // References to the controller (only instantiated at link time)
            if (isFunction(view.controllerProvider) || isArray(view.controllerProvider)) {
              var injectLocals = angular.extend({}, injectables, dst.globals);
              result.$$controller = $injector.invoke(view.controllerProvider, null, injectLocals);
            } else {
              result.$$controller = view.controller;
            }
            // Provide access to the state itself for internal use
            result.$$state = state;
            result.$$controllerAs = view.controllerAs;
            result.$$resolveAs = view.resolveAs;
            dst[name] = result;
          }));
        });

        return $q.all(viewsPromises).then(function(){
          return dst.globals;
        });
      }

      // Wait for all the promises and then return the activation object
      return $q.all(promises).then(resolveViews).then(function (values) {
        return dst;
      });
    }

    return $state;
  }

  function shouldSkipReload(to, toParams, from, fromParams, locals, options) {
    // Return true if there are no differences in non-search (path/object) params, false if there are differences
    function nonSearchParamsEqual(fromAndToState, fromParams, toParams) {
      // Identify whether all the parameters that differ between `fromParams` and `toParams` were search params.
      function notSearchParam(key) {
        return fromAndToState.params[key].location != "search";
      }
      var nonQueryParamKeys = fromAndToState.params.$$keys().filter(notSearchParam);
      var nonQueryParams = pick.apply({}, [fromAndToState.params].concat(nonQueryParamKeys));
      var nonQueryParamSet = new $$UMFP.ParamSet(nonQueryParams);
      return nonQueryParamSet.$$equals(fromParams, toParams);
    }

    // If reload was not explicitly requested
    // and we're transitioning to the same state we're already in
    // and    the locals didn't change
    //     or they changed in a way that doesn't merit reloading
    //        (reloadOnParams:false, or reloadOnSearch.false and only search params changed)
    // Then return true.
    if (!options.reload && to === from &&
      (locals === from.locals || (to.self.reloadOnSearch === false && nonSearchParamsEqual(from, fromParams, toParams)))) {
      return true;
    }
  }
}

angular.module('ui.router.state')
  .factory('$stateParams', function () { return {}; })
  .constant("$state.runtime", { autoinject: true })
  .provider('$state', $StateProvider)
  // Inject $state to initialize when entering runtime. #2574
  .run(['$injector', function ($injector) {
    // Allow tests (stateSpec.js) to turn this off by defining this constant
    if ($injector.get("$state.runtime").autoinject) {
      $injector.get('$state');
    }
  }]);


$ViewProvider.$inject = [];
function $ViewProvider() {

  this.$get = $get;
  /**
   * @ngdoc object
   * @name ui.router.state.$view
   *
   * @requires ui.router.util.$templateFactory
   * @requires $rootScope
   *
   * @description
   *
   */
  $get.$inject = ['$rootScope', '$templateFactory'];
  function $get(   $rootScope,   $templateFactory) {
    return {
      // $view.load('full.viewName', { template: ..., controller: ..., resolve: ..., async: false, params: ... })
      /**
       * @ngdoc function
       * @name ui.router.state.$view#load
       * @methodOf ui.router.state.$view
       *
       * @description
       *
       * @param {string} name name
       * @param {object} options option object.
       */
      load: function load(name, options) {
        var result, defaults = {
          template: null, controller: null, view: null, locals: null, notify: true, async: true, params: {}
        };
        options = extend(defaults, options);

        if (options.view) {
          result = $templateFactory.fromConfig(options.view, options.params, options.locals);
        }
        return result;
      }
    };
  }
}

angular.module('ui.router.state').provider('$view', $ViewProvider);

/**
 * @ngdoc object
 * @name ui.router.state.$uiViewScrollProvider
 *
 * @description
 * Provider that returns the {@link ui.router.state.$uiViewScroll} service function.
 */
function $ViewScrollProvider() {

  var useAnchorScroll = false;

  /**
   * @ngdoc function
   * @name ui.router.state.$uiViewScrollProvider#useAnchorScroll
   * @methodOf ui.router.state.$uiViewScrollProvider
   *
   * @description
   * Reverts back to using the core [`$anchorScroll`](http://docs.angularjs.org/api/ng.$anchorScroll) service for
   * scrolling based on the url anchor.
   */
  this.useAnchorScroll = function () {
    useAnchorScroll = true;
  };

  /**
   * @ngdoc object
   * @name ui.router.state.$uiViewScroll
   *
   * @requires $anchorScroll
   * @requires $timeout
   *
   * @description
   * When called with a jqLite element, it scrolls the element into view (after a
   * `$timeout` so the DOM has time to refresh).
   *
   * If you prefer to rely on `$anchorScroll` to scroll the view to the anchor,
   * this can be enabled by calling {@link ui.router.state.$uiViewScrollProvider#methods_useAnchorScroll `$uiViewScrollProvider.useAnchorScroll()`}.
   */
  this.$get = ['$anchorScroll', '$timeout', function ($anchorScroll, $timeout) {
    if (useAnchorScroll) {
      return $anchorScroll;
    }

    return function ($element) {
      return $timeout(function () {
        $element[0].scrollIntoView();
      }, 0, false);
    };
  }];
}

angular.module('ui.router.state').provider('$uiViewScroll', $ViewScrollProvider);

/**
 * @ngdoc directive
 * @name ui.router.state.directive:ui-view
 *
 * @requires ui.router.state.$state
 * @requires $compile
 * @requires $controller
 * @requires $injector
 * @requires ui.router.state.$uiViewScroll
 * @requires $document
 *
 * @restrict ECA
 *
 * @description
 * The ui-view directive tells $state where to place your templates.
 *
 * @param {string=} name A view name. The name should be unique amongst the other views in the
 * same state. You can have views of the same name that live in different states.
 *
 * @param {string=} autoscroll It allows you to set the scroll behavior of the browser window
 * when a view is populated. By default, $anchorScroll is overridden by ui-router's custom scroll
 * service, {@link ui.router.state.$uiViewScroll}. This custom service let's you
 * scroll ui-view elements into view when they are populated during a state activation.
 *
 * *Note: To revert back to old [`$anchorScroll`](http://docs.angularjs.org/api/ng.$anchorScroll)
 * functionality, call `$uiViewScrollProvider.useAnchorScroll()`.*
 *
 * @param {string=} onload Expression to evaluate whenever the view updates.
 *
 * @example
 * A view can be unnamed or named.
 * <pre>
 * <!-- Unnamed -->
 * <div ui-view></div>
 *
 * <!-- Named -->
 * <div ui-view="viewName"></div>
 * </pre>
 *
 * You can only have one unnamed view within any template (or root html). If you are only using a
 * single view and it is unnamed then you can populate it like so:
 * <pre>
 * <div ui-view></div>
 * $stateProvider.state("home", {
 *   template: "<h1>HELLO!</h1>"
 * })
 * </pre>
 *
 * The above is a convenient shortcut equivalent to specifying your view explicitly with the {@link ui.router.state.$stateProvider#methods_state `views`}
 * config property, by name, in this case an empty name:
 * <pre>
 * $stateProvider.state("home", {
 *   views: {
 *     "": {
 *       template: "<h1>HELLO!</h1>"
 *     }
 *   }    
 * })
 * </pre>
 *
 * But typically you'll only use the views property if you name your view or have more than one view
 * in the same template. There's not really a compelling reason to name a view if its the only one,
 * but you could if you wanted, like so:
 * <pre>
 * <div ui-view="main"></div>
 * </pre>
 * <pre>
 * $stateProvider.state("home", {
 *   views: {
 *     "main": {
 *       template: "<h1>HELLO!</h1>"
 *     }
 *   }    
 * })
 * </pre>
 *
 * Really though, you'll use views to set up multiple views:
 * <pre>
 * <div ui-view></div>
 * <div ui-view="chart"></div>
 * <div ui-view="data"></div>
 * </pre>
 *
 * <pre>
 * $stateProvider.state("home", {
 *   views: {
 *     "": {
 *       template: "<h1>HELLO!</h1>"
 *     },
 *     "chart": {
 *       template: "<chart_thing/>"
 *     },
 *     "data": {
 *       template: "<data_thing/>"
 *     }
 *   }    
 * })
 * </pre>
 *
 * Examples for `autoscroll`:
 *
 * <pre>
 * <!-- If autoscroll present with no expression,
 *      then scroll ui-view into view -->
 * <ui-view autoscroll/>
 *
 * <!-- If autoscroll present with valid expression,
 *      then scroll ui-view into view if expression evaluates to true -->
 * <ui-view autoscroll='true'/>
 * <ui-view autoscroll='false'/>
 * <ui-view autoscroll='scopeVariable'/>
 * </pre>
 *
 * Resolve data:
 *
 * The resolved data from the state's `resolve` block is placed on the scope as `$resolve` (this
 * can be customized using [[ViewDeclaration.resolveAs]]).  This can be then accessed from the template.
 *
 * Note that when `controllerAs` is being used, `$resolve` is set on the controller instance *after* the
 * controller is instantiated.  The `$onInit()` hook can be used to perform initialization code which
 * depends on `$resolve` data.
 *
 * Example usage of $resolve in a view template
 * <pre>
 * $stateProvider.state('home', {
 *   template: '<my-component user="$resolve.user"></my-component>',
 *   resolve: {
 *     user: function(UserService) { return UserService.fetchUser(); }
 *   }
 * });
 * </pre>
 */
$ViewDirective.$inject = ['$state', '$injector', '$uiViewScroll', '$interpolate', '$q'];
function $ViewDirective(   $state,   $injector,   $uiViewScroll,   $interpolate,   $q) {

  function getService() {
    return ($injector.has) ? function(service) {
      return $injector.has(service) ? $injector.get(service) : null;
    } : function(service) {
      try {
        return $injector.get(service);
      } catch (e) {
        return null;
      }
    };
  }

  var service = getService(),
      $animator = service('$animator'),
      $animate = service('$animate');

  // Returns a set of DOM manipulation functions based on which Angular version
  // it should use
  function getRenderer(attrs, scope) {
    var statics = function() {
      return {
        enter: function (element, target, cb) { target.after(element); cb(); },
        leave: function (element, cb) { element.remove(); cb(); }
      };
    };

    if ($animate) {
      return {
        enter: function(element, target, cb) {
          if (angular.version.minor > 2) {
            $animate.enter(element, null, target).then(cb);
          } else {
            $animate.enter(element, null, target, cb);
          }
        },
        leave: function(element, cb) {
          if (angular.version.minor > 2) {
            $animate.leave(element).then(cb);
          } else {
            $animate.leave(element, cb);
          }
        }
      };
    }

    if ($animator) {
      var animate = $animator && $animator(scope, attrs);

      return {
        enter: function(element, target, cb) {animate.enter(element, null, target); cb(); },
        leave: function(element, cb) { animate.leave(element); cb(); }
      };
    }

    return statics();
  }

  var directive = {
    restrict: 'ECA',
    terminal: true,
    priority: 400,
    transclude: 'element',
    compile: function (tElement, tAttrs, $transclude) {
      return function (scope, $element, attrs) {
        var previousEl, currentEl, currentScope, latestLocals,
            onloadExp     = attrs.onload || '',
            autoScrollExp = attrs.autoscroll,
            renderer      = getRenderer(attrs, scope),
            inherited     = $element.inheritedData('$uiView');

        scope.$on('$stateChangeSuccess', function() {
          updateView(false);
        });

        updateView(true);

        function cleanupLastView() {
          if (previousEl) {
            previousEl.remove();
            previousEl = null;
          }

          if (currentScope) {
            currentScope.$destroy();
            currentScope = null;
          }

          if (currentEl) {
            var $uiViewData = currentEl.data('$uiViewAnim');
            renderer.leave(currentEl, function() {
              $uiViewData.$$animLeave.resolve();
              previousEl = null;
            });

            previousEl = currentEl;
            currentEl = null;
          }
        }

        function updateView(firstTime) {
          var newScope,
              name            = getUiViewName(scope, attrs, $element, $interpolate),
              previousLocals  = name && $state.$current && $state.$current.locals[name];

          if (!firstTime && previousLocals === latestLocals) return; // nothing to do
          newScope = scope.$new();
          latestLocals = $state.$current.locals[name];

          /**
           * @ngdoc event
           * @name ui.router.state.directive:ui-view#$viewContentLoading
           * @eventOf ui.router.state.directive:ui-view
           * @eventType emits on ui-view directive scope
           * @description
           *
           * Fired once the view **begins loading**, *before* the DOM is rendered.
           *
           * @param {Object} event Event object.
           * @param {string} viewName Name of the view.
           */
          newScope.$emit('$viewContentLoading', name);

          var clone = $transclude(newScope, function(clone) {
            var animEnter = $q.defer(), animLeave = $q.defer();
            var viewAnimData = {
              $animEnter: animEnter.promise,
              $animLeave: animLeave.promise,
              $$animLeave: animLeave
            };

            clone.data('$uiViewAnim', viewAnimData);
            renderer.enter(clone, $element, function onUiViewEnter() {
              animEnter.resolve();
              if(currentScope) {
                currentScope.$emit('$viewContentAnimationEnded');
              }

              if (angular.isDefined(autoScrollExp) && !autoScrollExp || scope.$eval(autoScrollExp)) {
                $uiViewScroll(clone);
              }
            });
            cleanupLastView();
          });

          currentEl = clone;
          currentScope = newScope;
          /**
           * @ngdoc event
           * @name ui.router.state.directive:ui-view#$viewContentLoaded
           * @eventOf ui.router.state.directive:ui-view
           * @eventType emits on ui-view directive scope
           * @description
           * Fired once the view is **loaded**, *after* the DOM is rendered.
           *
           * @param {Object} event Event object.
           * @param {string} viewName Name of the view.
           */
          currentScope.$emit('$viewContentLoaded', name);
          currentScope.$eval(onloadExp);
        }
      };
    }
  };

  return directive;
}

$ViewDirectiveFill.$inject = ['$compile', '$controller', '$state', '$interpolate'];
function $ViewDirectiveFill (  $compile,   $controller,   $state,   $interpolate) {
  return {
    restrict: 'ECA',
    priority: -400,
    compile: function (tElement) {
      var initial = tElement.html();
      if (tElement.empty) {
        tElement.empty();
      } else {
        // ng 1.0.0 doesn't have empty(), which cleans up data and handlers
        tElement[0].innerHTML = null;
      }

      return function (scope, $element, attrs) {
        var current = $state.$current,
            name = getUiViewName(scope, attrs, $element, $interpolate),
            locals  = current && current.locals[name];

        if (! locals) {
          $element.html(initial);
          $compile($element.contents())(scope);
          return;
        }

        $element.data('$uiView', { name: name, state: locals.$$state });
        $element.html(locals.$template ? locals.$template : initial);

        var resolveData = angular.extend({}, locals);
        scope[locals.$$resolveAs] = resolveData;

        var link = $compile($element.contents());

        if (locals.$$controller) {
          locals.$scope = scope;
          locals.$element = $element;
          var controller = $controller(locals.$$controller, locals);
          if (locals.$$controllerAs) {
            scope[locals.$$controllerAs] = controller;
            scope[locals.$$controllerAs][locals.$$resolveAs] = resolveData;
          }
          if (isFunction(controller.$onInit)) controller.$onInit();
          $element.data('$ngControllerController', controller);
          $element.children().data('$ngControllerController', controller);
        }

        link(scope);
      };
    }
  };
}

/**
 * Shared ui-view code for both directives:
 * Given scope, element, and its attributes, return the view's name
 */
function getUiViewName(scope, attrs, element, $interpolate) {
  var name = $interpolate(attrs.uiView || attrs.name || '')(scope);
  var uiViewCreatedBy = element.inheritedData('$uiView');
  return name.indexOf('@') >= 0 ?  name :  (name + '@' + (uiViewCreatedBy ? uiViewCreatedBy.state.name : ''));
}

angular.module('ui.router.state').directive('uiView', $ViewDirective);
angular.module('ui.router.state').directive('uiView', $ViewDirectiveFill);

function parseStateRef(ref, current) {
  var preparsed = ref.match(/^\s*({[^}]*})\s*$/), parsed;
  if (preparsed) ref = current + '(' + preparsed[1] + ')';
  parsed = ref.replace(/\n/g, " ").match(/^([^(]+?)\s*(\((.*)\))?$/);
  if (!parsed || parsed.length !== 4) throw new Error("Invalid state ref '" + ref + "'");
  return { state: parsed[1], paramExpr: parsed[3] || null };
}

function stateContext(el) {
  var stateData = el.parent().inheritedData('$uiView');

  if (stateData && stateData.state && stateData.state.name) {
    return stateData.state;
  }
}

function getTypeInfo(el) {
  // SVGAElement does not use the href attribute, but rather the 'xlinkHref' attribute.
  var isSvg = Object.prototype.toString.call(el.prop('href')) === '[object SVGAnimatedString]';
  var isForm = el[0].nodeName === "FORM";

  return {
    attr: isForm ? "action" : (isSvg ? 'xlink:href' : 'href'),
    isAnchor: el.prop("tagName").toUpperCase() === "A",
    clickable: !isForm
  };
}

function clickHook(el, $state, $timeout, type, current) {
  return function(e) {
    var button = e.which || e.button, target = current();

    if (!(button > 1 || e.ctrlKey || e.metaKey || e.shiftKey || el.attr('target'))) {
      // HACK: This is to allow ng-clicks to be processed before the transition is initiated:
      var transition = $timeout(function() {
        $state.go(target.state, target.params, target.options);
      });
      e.preventDefault();

      // if the state has no URL, ignore one preventDefault from the <a> directive.
      var ignorePreventDefaultCount = type.isAnchor && !target.href ? 1: 0;

      e.preventDefault = function() {
        if (ignorePreventDefaultCount-- <= 0) $timeout.cancel(transition);
      };
    }
  };
}

function defaultOpts(el, $state) {
  return { relative: stateContext(el) || $state.$current, inherit: true };
}

/**
 * @ngdoc directive
 * @name ui.router.state.directive:ui-sref
 *
 * @requires ui.router.state.$state
 * @requires $timeout
 *
 * @restrict A
 *
 * @description
 * A directive that binds a link (`<a>` tag) to a state. If the state has an associated
 * URL, the directive will automatically generate & update the `href` attribute via
 * the {@link ui.router.state.$state#methods_href $state.href()} method. Clicking
 * the link will trigger a state transition with optional parameters.
 *
 * Also middle-clicking, right-clicking, and ctrl-clicking on the link will be
 * handled natively by the browser.
 *
 * You can also use relative state paths within ui-sref, just like the relative
 * paths passed to `$state.go()`. You just need to be aware that the path is relative
 * to the state that the link lives in, in other words the state that loaded the
 * template containing the link.
 *
 * You can specify options to pass to {@link ui.router.state.$state#methods_go $state.go()}
 * using the `ui-sref-opts` attribute. Options are restricted to `location`, `inherit`,
 * and `reload`.
 *
 * @example
 * Here's an example of how you'd use ui-sref and how it would compile. If you have the
 * following template:
 * <pre>
 * <a ui-sref="home">Home</a> | <a ui-sref="about">About</a> | <a ui-sref="{page: 2}">Next page</a>
 *
 * <ul>
 *     <li ng-repeat="contact in contacts">
 *         <a ui-sref="contacts.detail({ id: contact.id })">{{ contact.name }}</a>
 *     </li>
 * </ul>
 * </pre>
 *
 * Then the compiled html would be (assuming Html5Mode is off and current state is contacts):
 * <pre>
 * <a href="#/home" ui-sref="home">Home</a> | <a href="#/about" ui-sref="about">About</a> | <a href="#/contacts?page=2" ui-sref="{page: 2}">Next page</a>
 *
 * <ul>
 *     <li ng-repeat="contact in contacts">
 *         <a href="#/contacts/1" ui-sref="contacts.detail({ id: contact.id })">Joe</a>
 *     </li>
 *     <li ng-repeat="contact in contacts">
 *         <a href="#/contacts/2" ui-sref="contacts.detail({ id: contact.id })">Alice</a>
 *     </li>
 *     <li ng-repeat="contact in contacts">
 *         <a href="#/contacts/3" ui-sref="contacts.detail({ id: contact.id })">Bob</a>
 *     </li>
 * </ul>
 *
 * <a ui-sref="home" ui-sref-opts="{reload: true}">Home</a>
 * </pre>
 *
 * @param {string} ui-sref 'stateName' can be any valid absolute or relative state
 * @param {Object} ui-sref-opts options to pass to {@link ui.router.state.$state#methods_go $state.go()}
 */
$StateRefDirective.$inject = ['$state', '$timeout'];
function $StateRefDirective($state, $timeout) {
  return {
    restrict: 'A',
    require: ['?^uiSrefActive', '?^uiSrefActiveEq'],
    link: function(scope, element, attrs, uiSrefActive) {
      var ref    = parseStateRef(attrs.uiSref, $state.current.name);
      var def    = { state: ref.state, href: null, params: null };
      var type   = getTypeInfo(element);
      var active = uiSrefActive[1] || uiSrefActive[0];
      var unlinkInfoFn = null;
      var hookFn;

      def.options = extend(defaultOpts(element, $state), attrs.uiSrefOpts ? scope.$eval(attrs.uiSrefOpts) : {});

      var update = function(val) {
        if (val) def.params = angular.copy(val);
        def.href = $state.href(ref.state, def.params, def.options);

        if (unlinkInfoFn) unlinkInfoFn();
        if (active) unlinkInfoFn = active.$$addStateInfo(ref.state, def.params);
        if (def.href !== null) attrs.$set(type.attr, def.href);
      };

      if (ref.paramExpr) {
        scope.$watch(ref.paramExpr, function(val) { if (val !== def.params) update(val); }, true);
        def.params = angular.copy(scope.$eval(ref.paramExpr));
      }
      update();

      if (!type.clickable) return;
      hookFn = clickHook(element, $state, $timeout, type, function() { return def; });
      element[element.on ? 'on' : 'bind']("click", hookFn);
      scope.$on('$destroy', function() {
        element[element.off ? 'off' : 'unbind']("click", hookFn);
      });
    }
  };
}

/**
 * @ngdoc directive
 * @name ui.router.state.directive:ui-state
 *
 * @requires ui.router.state.uiSref
 *
 * @restrict A
 *
 * @description
 * Much like ui-sref, but will accept named $scope properties to evaluate for a state definition,
 * params and override options.
 *
 * @param {string} ui-state 'stateName' can be any valid absolute or relative state
 * @param {Object} ui-state-params params to pass to {@link ui.router.state.$state#methods_href $state.href()}
 * @param {Object} ui-state-opts options to pass to {@link ui.router.state.$state#methods_go $state.go()}
 */
$StateRefDynamicDirective.$inject = ['$state', '$timeout'];
function $StateRefDynamicDirective($state, $timeout) {
  return {
    restrict: 'A',
    require: ['?^uiSrefActive', '?^uiSrefActiveEq'],
    link: function(scope, element, attrs, uiSrefActive) {
      var type   = getTypeInfo(element);
      var active = uiSrefActive[1] || uiSrefActive[0];
      var group  = [attrs.uiState, attrs.uiStateParams || null, attrs.uiStateOpts || null];
      var watch  = '[' + group.map(function(val) { return val || 'null'; }).join(', ') + ']';
      var def    = { state: null, params: null, options: null, href: null };
      var unlinkInfoFn = null;
      var hookFn;

      function runStateRefLink (group) {
        def.state = group[0]; def.params = group[1]; def.options = group[2];
        def.href = $state.href(def.state, def.params, def.options);

        if (unlinkInfoFn) unlinkInfoFn();
        if (active) unlinkInfoFn = active.$$addStateInfo(def.state, def.params);
        if (def.href) attrs.$set(type.attr, def.href);
      }

      scope.$watch(watch, runStateRefLink, true);
      runStateRefLink(scope.$eval(watch));

      if (!type.clickable) return;
      hookFn = clickHook(element, $state, $timeout, type, function() { return def; });
      element[element.on ? 'on' : 'bind']("click", hookFn);
      scope.$on('$destroy', function() {
        element[element.off ? 'off' : 'unbind']("click", hookFn);
      });
    }
  };
}


/**
 * @ngdoc directive
 * @name ui.router.state.directive:ui-sref-active
 *
 * @requires ui.router.state.$state
 * @requires ui.router.state.$stateParams
 * @requires $interpolate
 *
 * @restrict A
 *
 * @description
 * A directive working alongside ui-sref to add classes to an element when the
 * related ui-sref directive's state is active, and removing them when it is inactive.
 * The primary use-case is to simplify the special appearance of navigation menus
 * relying on `ui-sref`, by having the "active" state's menu button appear different,
 * distinguishing it from the inactive menu items.
 *
 * ui-sref-active can live on the same element as ui-sref or on a parent element. The first
 * ui-sref-active found at the same level or above the ui-sref will be used.
 *
 * Will activate when the ui-sref's target state or any child state is active. If you
 * need to activate only when the ui-sref target state is active and *not* any of
 * it's children, then you will use
 * {@link ui.router.state.directive:ui-sref-active-eq ui-sref-active-eq}
 *
 * @example
 * Given the following template:
 * <pre>
 * <ul>
 *   <li ui-sref-active="active" class="item">
 *     <a href ui-sref="app.user({user: 'bilbobaggins'})">@bilbobaggins</a>
 *   </li>
 * </ul>
 * </pre>
 *
 *
 * When the app state is "app.user" (or any children states), and contains the state parameter "user" with value "bilbobaggins",
 * the resulting HTML will appear as (note the 'active' class):
 * <pre>
 * <ul>
 *   <li ui-sref-active="active" class="item active">
 *     <a ui-sref="app.user({user: 'bilbobaggins'})" href="/users/bilbobaggins">@bilbobaggins</a>
 *   </li>
 * </ul>
 * </pre>
 *
 * The class name is interpolated **once** during the directives link time (any further changes to the
 * interpolated value are ignored).
 *
 * Multiple classes may be specified in a space-separated format:
 * <pre>
 * <ul>
 *   <li ui-sref-active='class1 class2 class3'>
 *     <a ui-sref="app.user">link</a>
 *   </li>
 * </ul>
 * </pre>
 *
 * It is also possible to pass ui-sref-active an expression that evaluates
 * to an object hash, whose keys represent active class names and whose
 * values represent the respective state names/globs.
 * ui-sref-active will match if the current active state **includes** any of
 * the specified state names/globs, even the abstract ones.
 *
 * @Example
 * Given the following template, with "admin" being an abstract state:
 * <pre>
 * <div ui-sref-active="{'active': 'admin.*'}">
 *   <a ui-sref-active="active" ui-sref="admin.roles">Roles</a>
 * </div>
 * </pre>
 *
 * When the current state is "admin.roles" the "active" class will be applied
 * to both the <div> and <a> elements. It is important to note that the state
 * names/globs passed to ui-sref-active shadow the state provided by ui-sref.
 */

/**
 * @ngdoc directive
 * @name ui.router.state.directive:ui-sref-active-eq
 *
 * @requires ui.router.state.$state
 * @requires ui.router.state.$stateParams
 * @requires $interpolate
 *
 * @restrict A
 *
 * @description
 * The same as {@link ui.router.state.directive:ui-sref-active ui-sref-active} but will only activate
 * when the exact target state used in the `ui-sref` is active; no child states.
 *
 */
$StateRefActiveDirective.$inject = ['$state', '$stateParams', '$interpolate'];
function $StateRefActiveDirective($state, $stateParams, $interpolate) {
  return  {
    restrict: "A",
    controller: ['$scope', '$element', '$attrs', '$timeout', function ($scope, $element, $attrs, $timeout) {
      var states = [], activeClasses = {}, activeEqClass, uiSrefActive;

      // There probably isn't much point in $observing this
      // uiSrefActive and uiSrefActiveEq share the same directive object with some
      // slight difference in logic routing
      activeEqClass = $interpolate($attrs.uiSrefActiveEq || '', false)($scope);

      try {
        uiSrefActive = $scope.$eval($attrs.uiSrefActive);
      } catch (e) {
        // Do nothing. uiSrefActive is not a valid expression.
        // Fall back to using $interpolate below
      }
      uiSrefActive = uiSrefActive || $interpolate($attrs.uiSrefActive || '', false)($scope);
      if (isObject(uiSrefActive)) {
        forEach(uiSrefActive, function(stateOrName, activeClass) {
          if (isString(stateOrName)) {
            var ref = parseStateRef(stateOrName, $state.current.name);
            addState(ref.state, $scope.$eval(ref.paramExpr), activeClass);
          }
        });
      }

      // Allow uiSref to communicate with uiSrefActive[Equals]
      this.$$addStateInfo = function (newState, newParams) {
        // we already got an explicit state provided by ui-sref-active, so we
        // shadow the one that comes from ui-sref
        if (isObject(uiSrefActive) && states.length > 0) {
          return;
        }
        var deregister = addState(newState, newParams, uiSrefActive);
        update();
        return deregister;
      };

      $scope.$on('$stateChangeSuccess', update);

      function addState(stateName, stateParams, activeClass) {
        var state = $state.get(stateName, stateContext($element));
        var stateHash = createStateHash(stateName, stateParams);

        var stateInfo = {
          state: state || { name: stateName },
          params: stateParams,
          hash: stateHash
        };

        states.push(stateInfo);
        activeClasses[stateHash] = activeClass;

        return function removeState() {
          var idx = states.indexOf(stateInfo);
          if (idx !== -1) states.splice(idx, 1);
        };
      }

      /**
       * @param {string} state
       * @param {Object|string} [params]
       * @return {string}
       */
      function createStateHash(state, params) {
        if (!isString(state)) {
          throw new Error('state should be a string');
        }
        if (isObject(params)) {
          return state + toJson(params);
        }
        params = $scope.$eval(params);
        if (isObject(params)) {
          return state + toJson(params);
        }
        return state;
      }

      // Update route state
      function update() {
        for (var i = 0; i < states.length; i++) {
          if (anyMatch(states[i].state, states[i].params)) {
            addClass($element, activeClasses[states[i].hash]);
          } else {
            removeClass($element, activeClasses[states[i].hash]);
          }

          if (exactMatch(states[i].state, states[i].params)) {
            addClass($element, activeEqClass);
          } else {
            removeClass($element, activeEqClass);
          }
        }
      }

      function addClass(el, className) { $timeout(function () { el.addClass(className); }); }
      function removeClass(el, className) { el.removeClass(className); }
      function anyMatch(state, params) { return $state.includes(state.name, params); }
      function exactMatch(state, params) { return $state.is(state.name, params); }

      update();
    }]
  };
}

angular.module('ui.router.state')
  .directive('uiSref', $StateRefDirective)
  .directive('uiSrefActive', $StateRefActiveDirective)
  .directive('uiSrefActiveEq', $StateRefActiveDirective)
  .directive('uiState', $StateRefDynamicDirective);

/**
 * @ngdoc filter
 * @name ui.router.state.filter:isState
 *
 * @requires ui.router.state.$state
 *
 * @description
 * Translates to {@link ui.router.state.$state#methods_is $state.is("stateName")}.
 */
$IsStateFilter.$inject = ['$state'];
function $IsStateFilter($state) {
  var isFilter = function (state, params) {
    return $state.is(state, params);
  };
  isFilter.$stateful = true;
  return isFilter;
}

/**
 * @ngdoc filter
 * @name ui.router.state.filter:includedByState
 *
 * @requires ui.router.state.$state
 *
 * @description
 * Translates to {@link ui.router.state.$state#methods_includes $state.includes('fullOrPartialStateName')}.
 */
$IncludedByStateFilter.$inject = ['$state'];
function $IncludedByStateFilter($state) {
  var includesFilter = function (state, params, options) {
    return $state.includes(state, params, options);
  };
  includesFilter.$stateful = true;
  return  includesFilter;
}

angular.module('ui.router.state')
  .filter('isState', $IsStateFilter)
  .filter('includedByState', $IncludedByStateFilter);
})(window, window.angular);;
!function(e){"undefined"!=typeof exports?e(exports):(window.hljs=e({}),"function"==typeof define&&define.amd&&define([],function(){return window.hljs}))}(function(e){function t(e){return e.replace(/&/gm,"&amp;").replace(/</gm,"&lt;").replace(/>/gm,"&gt;")}function r(e){return e.nodeName.toLowerCase()}function a(e,t){var r=e&&e.exec(t);return r&&0==r.index}function i(e){var t=(e.className+" "+(e.parentNode?e.parentNode.className:"")).split(/\s+/);return t=t.map(function(e){return e.replace(/^lang(uage)?-/,"")}),t.filter(function(e){return _(e)||/no(-?)highlight|plain|text/.test(e)})[0]}function n(e,t){var r,a={};for(r in e)a[r]=e[r];if(t)for(r in t)a[r]=t[r];return a}function o(e){var t=[];return function a(e,i){for(var n=e.firstChild;n;n=n.nextSibling)3==n.nodeType?i+=n.nodeValue.length:1==n.nodeType&&(t.push({event:"start",offset:i,node:n}),i=a(n,i),r(n).match(/br|hr|img|input/)||t.push({event:"stop",offset:i,node:n}));return i}(e,0),t}function s(e,a,i){function n(){return e.length&&a.length?e[0].offset!=a[0].offset?e[0].offset<a[0].offset?e:a:"start"==a[0].event?e:a:e.length?e:a}function o(e){function a(e){return" "+e.nodeName+'="'+t(e.value)+'"'}d+="<"+r(e)+Array.prototype.map.call(e.attributes,a).join("")+">"}function s(e){d+="</"+r(e)+">"}function l(e){("start"==e.event?o:s)(e.node)}for(var c=0,d="",p=[];e.length||a.length;){var u=n();if(d+=t(i.substr(c,u[0].offset-c)),c=u[0].offset,u==e){p.reverse().forEach(s);do l(u.splice(0,1)[0]),u=n();while(u==e&&u.length&&u[0].offset==c);p.reverse().forEach(o)}else"start"==u[0].event?p.push(u[0].node):p.pop(),l(u.splice(0,1)[0])}return d+t(i.substr(c))}function l(e){function t(e){return e&&e.source||e}function r(r,a){return new RegExp(t(r),"m"+(e.cI?"i":"")+(a?"g":""))}function a(i,o){if(!i.compiled){if(i.compiled=!0,i.k=i.k||i.bK,i.k){var s={},l=function(t,r){e.cI&&(r=r.toLowerCase()),r.split(" ").forEach(function(e){var r=e.split("|");s[r[0]]=[t,r[1]?Number(r[1]):1]})};"string"==typeof i.k?l("keyword",i.k):Object.keys(i.k).forEach(function(e){l(e,i.k[e])}),i.k=s}i.lR=r(i.l||/\b\w+\b/,!0),o&&(i.bK&&(i.b="\\b("+i.bK.split(" ").join("|")+")\\b"),i.b||(i.b=/\B|\b/),i.bR=r(i.b),i.e||i.eW||(i.e=/\B|\b/),i.e&&(i.eR=r(i.e)),i.tE=t(i.e)||"",i.eW&&o.tE&&(i.tE+=(i.e?"|":"")+o.tE)),i.i&&(i.iR=r(i.i)),void 0===i.r&&(i.r=1),i.c||(i.c=[]);var c=[];i.c.forEach(function(e){e.v?e.v.forEach(function(t){c.push(n(e,t))}):c.push("self"==e?i:e)}),i.c=c,i.c.forEach(function(e){a(e,i)}),i.starts&&a(i.starts,o);var d=i.c.map(function(e){return e.bK?"\\.?("+e.b+")\\.?":e.b}).concat([i.tE,i.i]).map(t).filter(Boolean);i.t=d.length?r(d.join("|"),!0):{exec:function(){return null}}}}a(e)}function c(e,r,i,n){function o(e,t){for(var r=0;r<t.c.length;r++)if(a(t.c[r].bR,e))return t.c[r]}function s(e,t){if(a(e.eR,t)){for(;e.endsParent&&e.parent;)e=e.parent;return e}return e.eW?s(e.parent,t):void 0}function p(e,t){return!i&&a(t.iR,e)}function u(e,t){var r=C.cI?t[0].toLowerCase():t[0];return e.k.hasOwnProperty(r)&&e.k[r]}function m(e,t,r,a){var i=a?"":y.classPrefix,n='<span class="'+i,o=r?"":"</span>";return n+=e+'">',n+t+o}function b(){if(!w.k)return t(D);var e="",r=0;w.lR.lastIndex=0;for(var a=w.lR.exec(D);a;){e+=t(D.substr(r,a.index-r));var i=u(w,a);i?(M+=i[1],e+=m(i[0],t(a[0]))):e+=t(a[0]),r=w.lR.lastIndex,a=w.lR.exec(D)}return e+t(D.substr(r))}function g(){if(w.sL&&!x[w.sL])return t(D);var e=w.sL?c(w.sL,D,!0,N[w.sL]):d(D);return w.r>0&&(M+=e.r),"continuous"==w.subLanguageMode&&(N[w.sL]=e.top),m(e.language,e.value,!1,!0)}function f(){return void 0!==w.sL?g():b()}function h(e,r){var a=e.cN?m(e.cN,"",!0):"";e.rB?(k+=a,D=""):e.eB?(k+=t(r)+a,D=""):(k+=a,D=r),w=Object.create(e,{parent:{value:w}})}function v(e,r){if(D+=e,void 0===r)return k+=f(),0;var a=o(r,w);if(a)return k+=f(),h(a,r),a.rB?0:r.length;var i=s(w,r);if(i){var n=w;n.rE||n.eE||(D+=r),k+=f();do w.cN&&(k+="</span>"),M+=w.r,w=w.parent;while(w!=i.parent);return n.eE&&(k+=t(r)),D="",i.starts&&h(i.starts,""),n.rE?0:r.length}if(p(r,w))throw new Error('Illegal lexeme "'+r+'" for mode "'+(w.cN||"<unnamed>")+'"');return D+=r,r.length||1}var C=_(e);if(!C)throw new Error('Unknown language: "'+e+'"');l(C);var S,w=n||C,N={},k="";for(S=w;S!=C;S=S.parent)S.cN&&(k=m(S.cN,"",!0)+k);var D="",M=0;try{for(var T,E,A=0;;){if(w.t.lastIndex=A,T=w.t.exec(r),!T)break;E=v(r.substr(A,T.index-A),T[0]),A=T.index+E}for(v(r.substr(A)),S=w;S.parent;S=S.parent)S.cN&&(k+="</span>");return{r:M,value:k,language:e,top:w}}catch(L){if(-1!=L.message.indexOf("Illegal"))return{r:0,value:t(r)};throw L}}function d(e,r){r=r||y.languages||Object.keys(x);var a={r:0,value:t(e)},i=a;return r.forEach(function(t){if(_(t)){var r=c(t,e,!1);r.language=t,r.r>i.r&&(i=r),r.r>a.r&&(i=a,a=r)}}),i.language&&(a.second_best=i),a}function p(e){return y.tabReplace&&(e=e.replace(/^((<[^>]+>|\t)+)/gm,function(e,t){return t.replace(/\t/g,y.tabReplace)})),y.useBR&&(e=e.replace(/\n/g,"<br>")),e}function u(e,t,r){var a=t?C[t]:r,i=[e.trim()];return e.match(/\bhljs\b/)||i.push("hljs"),-1===e.indexOf(a)&&i.push(a),i.join(" ").trim()}function m(e){var t=i(e);if(!/no(-?)highlight|plain|text/.test(t)){var r;y.useBR?(r=document.createElementNS("http://www.w3.org/1999/xhtml","div"),r.innerHTML=e.innerHTML.replace(/\n/g,"").replace(/<br[ \/]*>/g,"\n")):r=e;var a=r.textContent,n=t?c(t,a,!0):d(a),l=o(r);if(l.length){var m=document.createElementNS("http://www.w3.org/1999/xhtml","div");m.innerHTML=n.value,n.value=s(l,o(m),a)}n.value=p(n.value),e.innerHTML=n.value,e.className=u(e.className,t,n.language),e.result={language:n.language,re:n.r},n.second_best&&(e.second_best={language:n.second_best.language,re:n.second_best.r})}}function b(e){y=n(y,e)}function g(){if(!g.called){g.called=!0;var e=document.querySelectorAll("pre code");Array.prototype.forEach.call(e,m)}}function f(){addEventListener("DOMContentLoaded",g,!1),addEventListener("load",g,!1)}function h(t,r){var a=x[t]=r(e);a.aliases&&a.aliases.forEach(function(e){C[e]=t})}function v(){return Object.keys(x)}function _(e){return x[e]||x[C[e]]}var y={classPrefix:"hljs-",tabReplace:null,useBR:!1,languages:void 0},x={},C={};return e.highlight=c,e.highlightAuto=d,e.fixMarkup=p,e.highlightBlock=m,e.configure=b,e.initHighlighting=g,e.initHighlightingOnLoad=f,e.registerLanguage=h,e.listLanguages=v,e.getLanguage=_,e.inherit=n,e.IR="[a-zA-Z]\\w*",e.UIR="[a-zA-Z_]\\w*",e.NR="\\b\\d+(\\.\\d+)?",e.CNR="\\b(0[xX][a-fA-F0-9]+|(\\d+(\\.\\d*)?|\\.\\d+)([eE][-+]?\\d+)?)",e.BNR="\\b(0b[01]+)",e.RSR="!|!=|!==|%|%=|&|&&|&=|\\*|\\*=|\\+|\\+=|,|-|-=|/=|/|:|;|<<|<<=|<=|<|===|==|=|>>>=|>>=|>=|>>>|>>|>|\\?|\\[|\\{|\\(|\\^|\\^=|\\||\\|=|\\|\\||~",e.BE={b:"\\\\[\\s\\S]",r:0},e.ASM={cN:"string",b:"'",e:"'",i:"\\n",c:[e.BE]},e.QSM={cN:"string",b:'"',e:'"',i:"\\n",c:[e.BE]},e.PWM={b:/\b(a|an|the|are|I|I'm|isn't|don't|doesn't|won't|but|just|should|pretty|simply|enough|gonna|going|wtf|so|such)\b/},e.C=function(t,r,a){var i=e.inherit({cN:"comment",b:t,e:r,c:[]},a||{});return i.c.push(e.PWM),i},e.CLCM=e.C("//","$"),e.CBCM=e.C("/\\*","\\*/"),e.HCM=e.C("#","$"),e.NM={cN:"number",b:e.NR,r:0},e.CNM={cN:"number",b:e.CNR,r:0},e.BNM={cN:"number",b:e.BNR,r:0},e.CSSNM={cN:"number",b:e.NR+"(%|em|ex|ch|rem|vw|vh|vmin|vmax|cm|mm|in|pt|pc|px|deg|grad|rad|turn|s|ms|Hz|kHz|dpi|dpcm|dppx)?",r:0},e.RM={cN:"regexp",b:/\//,e:/\/[gimuy]*/,i:/\n/,c:[e.BE,{b:/\[/,e:/\]/,r:0,c:[e.BE]}]},e.TM={cN:"title",b:e.IR,r:0},e.UTM={cN:"title",b:e.UIR,r:0},e}),hljs.registerLanguage("1c",function(e){var t="[a-zA-Zа-яА-Я][a-zA-Z0-9_а-яА-Я]*",r="возврат дата для если и или иначе иначеесли исключение конецесли конецпопытки конецпроцедуры конецфункции конеццикла константа не перейти перем перечисление по пока попытка прервать продолжить процедура строка тогда фс функция цикл число экспорт",a="ansitooem oemtoansi ввестивидсубконто ввестидату ввестизначение ввестиперечисление ввестипериод ввестиплансчетов ввестистроку ввестичисло вопрос восстановитьзначение врег выбранныйплансчетов вызватьисключение датагод датамесяц датачисло добавитьмесяц завершитьработусистемы заголовоксистемы записьжурналарегистрации запуститьприложение зафиксироватьтранзакцию значениевстроку значениевстрокувнутр значениевфайл значениеизстроки значениеизстрокивнутр значениеизфайла имякомпьютера имяпользователя каталогвременныхфайлов каталогиб каталогпользователя каталогпрограммы кодсимв командасистемы конгода конецпериодаби конецрассчитанногопериодаби конецстандартногоинтервала конквартала конмесяца коннедели лев лог лог10 макс максимальноеколичествосубконто мин монопольныйрежим названиеинтерфейса названиенабораправ назначитьвид назначитьсчет найти найтипомеченныенаудаление найтиссылки началопериодаби началостандартногоинтервала начатьтранзакцию начгода начквартала начмесяца начнедели номерднягода номерднянедели номернеделигода нрег обработкаожидания окр описаниеошибки основнойжурналрасчетов основнойплансчетов основнойязык открытьформу открытьформумодально отменитьтранзакцию очиститьокносообщений периодстр полноеимяпользователя получитьвремята получитьдатута получитьдокументта получитьзначенияотбора получитьпозициюта получитьпустоезначение получитьта прав праводоступа предупреждение префиксавтонумерации пустаястрока пустоезначение рабочаядаттьпустоезначение рабочаядата разделительстраниц разделительстрок разм разобратьпозициюдокумента рассчитатьрегистрына рассчитатьрегистрыпо сигнал симв символтабуляции создатьобъект сокрл сокрлп сокрп сообщить состояние сохранитьзначение сред статусвозврата стрдлина стрзаменить стрколичествострок стрполучитьстроку  стрчисловхождений сформироватьпозициюдокумента счетпокоду текущаядата текущеевремя типзначения типзначениястр удалитьобъекты установитьтана установитьтапо фиксшаблон формат цел шаблон",i={cN:"dquote",b:'""'},n={cN:"string",b:'"',e:'"|$',c:[i]},o={cN:"string",b:"\\|",e:'"|$',c:[i]};return{cI:!0,l:t,k:{keyword:r,built_in:a},c:[e.CLCM,e.NM,n,o,{cN:"function",b:"(процедура|функция)",e:"$",l:t,k:"процедура функция",c:[e.inherit(e.TM,{b:t}),{cN:"tail",eW:!0,c:[{cN:"params",b:"\\(",e:"\\)",l:t,k:"знач",c:[n,o]},{cN:"export",b:"экспорт",eW:!0,l:t,k:"экспорт",c:[e.CLCM]}]},e.CLCM]},{cN:"preprocessor",b:"#",e:"$"},{cN:"date",b:"'\\d{2}\\.\\d{2}\\.(\\d{2}|\\d{4})'"}]}}),hljs.registerLanguage("actionscript",function(e){var t="[a-zA-Z_$][a-zA-Z0-9_$]*",r="([*]|[a-zA-Z_$][a-zA-Z0-9_$]*)",a={cN:"rest_arg",b:"[.]{3}",e:t,r:10};return{aliases:["as"],k:{keyword:"as break case catch class const continue default delete do dynamic each else extends final finally for function get if implements import in include instanceof interface internal is namespace native new override package private protected public return set static super switch this throw try typeof use var void while with",literal:"true false null undefined"},c:[e.ASM,e.QSM,e.CLCM,e.CBCM,e.CNM,{cN:"package",bK:"package",e:"{",c:[e.TM]},{cN:"class",bK:"class interface",e:"{",eE:!0,c:[{bK:"extends implements"},e.TM]},{cN:"preprocessor",bK:"import include",e:";"},{cN:"function",bK:"function",e:"[{;]",eE:!0,i:"\\S",c:[e.TM,{cN:"params",b:"\\(",e:"\\)",c:[e.ASM,e.QSM,e.CLCM,e.CBCM,a]},{cN:"type",b:":",e:r,r:10}]}]}}),hljs.registerLanguage("apache",function(e){var t={cN:"number",b:"[\\$%]\\d+"};return{aliases:["apacheconf"],cI:!0,c:[e.HCM,{cN:"tag",b:"</?",e:">"},{cN:"keyword",b:/\w+/,r:0,k:{common:"order deny allow setenv rewriterule rewriteengine rewritecond documentroot sethandler errordocument loadmodule options header listen serverroot servername"},starts:{e:/$/,r:0,k:{literal:"on off all"},c:[{cN:"sqbracket",b:"\\s\\[",e:"\\]$"},{cN:"cbracket",b:"[\\$%]\\{",e:"\\}",c:["self",t]},t,e.QSM]}}],i:/\S/}}),hljs.registerLanguage("applescript",function(e){var t=e.inherit(e.QSM,{i:""}),r={cN:"params",b:"\\(",e:"\\)",c:["self",e.CNM,t]},a=e.C("--","$"),i=e.C("\\(\\*","\\*\\)",{c:["self",a]}),n=[a,i,e.HCM];return{aliases:["osascript"],k:{keyword:"about above after against and around as at back before beginning behind below beneath beside between but by considering contain contains continue copy div does eighth else end equal equals error every exit fifth first for fourth from front get given global if ignoring in into is it its last local me middle mod my ninth not of on onto or over prop property put ref reference repeat returning script second set seventh since sixth some tell tenth that the|0 then third through thru timeout times to transaction try until where while whose with without",constant:"AppleScript false linefeed return pi quote result space tab true",type:"alias application boolean class constant date file integer list number real record string text",command:"activate beep count delay launch log offset read round run say summarize write",property:"character characters contents day frontmost id item length month name paragraph paragraphs rest reverse running time version weekday word words year"},c:[t,e.CNM,{cN:"type",b:"\\bPOSIX file\\b"},{cN:"command",b:"\\b(clipboard info|the clipboard|info for|list (disks|folder)|mount volume|path to|(close|open for) access|(get|set) eof|current date|do shell script|get volume settings|random number|set volume|system attribute|system info|time to GMT|(load|run|store) script|scripting components|ASCII (character|number)|localized string|choose (application|color|file|file name|folder|from list|remote application|URL)|display (alert|dialog))\\b|^\\s*return\\b"},{cN:"constant",b:"\\b(text item delimiters|current application|missing value)\\b"},{cN:"keyword",b:"\\b(apart from|aside from|instead of|out of|greater than|isn't|(doesn't|does not) (equal|come before|come after|contain)|(greater|less) than( or equal)?|(starts?|ends|begins?) with|contained by|comes (before|after)|a (ref|reference))\\b"},{cN:"property",b:"\\b(POSIX path|(date|time) string|quoted form)\\b"},{cN:"function_start",bK:"on",i:"[${=;\\n]",c:[e.UTM,r]}].concat(n),i:"//|->|=>"}}),hljs.registerLanguage("xml",function(e){var t="[A-Za-z0-9\\._:-]+",r={b:/<\?(php)?(?!\w)/,e:/\?>/,sL:"php",subLanguageMode:"continuous"},a={eW:!0,i:/</,r:0,c:[r,{cN:"attribute",b:t,r:0},{b:"=",r:0,c:[{cN:"value",c:[r],v:[{b:/"/,e:/"/},{b:/'/,e:/'/},{b:/[^\s\/>]+/}]}]}]};return{aliases:["html","xhtml","rss","atom","xsl","plist"],cI:!0,c:[{cN:"doctype",b:"<!DOCTYPE",e:">",r:10,c:[{b:"\\[",e:"\\]"}]},e.C("<!--","-->",{r:10}),{cN:"cdata",b:"<\\!\\[CDATA\\[",e:"\\]\\]>",r:10},{cN:"tag",b:"<style(?=\\s|>|$)",e:">",k:{title:"style"},c:[a],starts:{e:"</style>",rE:!0,sL:"css"}},{cN:"tag",b:"<script(?=\\s|>|$)",e:">",k:{title:"script"},c:[a],starts:{e:"</script>",rE:!0,sL:""}},r,{cN:"pi",b:/<\?\w+/,e:/\?>/,r:10},{cN:"tag",b:"</?",e:"/?>",c:[{cN:"title",b:/[^ \/><\n\t]+/,r:0},a]}]}}),hljs.registerLanguage("asciidoc",function(e){return{aliases:["adoc"],c:[e.C("^/{4,}\\n","\\n/{4,}$",{r:10}),e.C("^//","$",{r:0}),{cN:"title",b:"^\\.\\w.*$"},{b:"^[=\\*]{4,}\\n",e:"\\n^[=\\*]{4,}$",r:10},{cN:"header",b:"^(={1,5}) .+?( \\1)?$",r:10},{cN:"header",b:"^[^\\[\\]\\n]+?\\n[=\\-~\\^\\+]{2,}$",r:10},{cN:"attribute",b:"^:.+?:",e:"\\s",eE:!0,r:10},{cN:"attribute",b:"^\\[.+?\\]$",r:0},{cN:"blockquote",b:"^_{4,}\\n",e:"\\n_{4,}$",r:10},{cN:"code",b:"^[\\-\\.]{4,}\\n",e:"\\n[\\-\\.]{4,}$",r:10},{b:"^\\+{4,}\\n",e:"\\n\\+{4,}$",c:[{b:"<",e:">",sL:"xml",r:0}],r:10},{cN:"bullet",b:"^(\\*+|\\-+|\\.+|[^\\n]+?::)\\s+"},{cN:"label",b:"^(NOTE|TIP|IMPORTANT|WARNING|CAUTION):\\s+",r:10},{cN:"strong",b:"\\B\\*(?![\\*\\s])",e:"(\\n{2}|\\*)",c:[{b:"\\\\*\\w",r:0}]},{cN:"emphasis",b:"\\B'(?!['\\s])",e:"(\\n{2}|')",c:[{b:"\\\\'\\w",r:0}],r:0},{cN:"emphasis",b:"_(?![_\\s])",e:"(\\n{2}|_)",r:0},{cN:"smartquote",v:[{b:"``.+?''"},{b:"`.+?'"}]},{cN:"code",b:"(`.+?`|\\+.+?\\+)",r:0},{cN:"code",b:"^[ \\t]",e:"$",r:0},{cN:"horizontal_rule",b:"^'{3,}[ \\t]*$",r:10},{b:"(link:)?(http|https|ftp|file|irc|image:?):\\S+\\[.*?\\]",rB:!0,c:[{b:"(link|image:?):",r:0},{cN:"link_url",b:"\\w",e:"[^\\[]+",r:0},{cN:"link_label",b:"\\[",e:"\\]",eB:!0,eE:!0,r:0}],r:10}]}}),hljs.registerLanguage("aspectj",function(e){var t="false synchronized int abstract float private char boolean static null if const for true while long throw strictfp finally protected import native final return void enum else extends implements break transient new catch instanceof byte super volatile case assert short package default double public try this switch continue throws privileged aspectOf adviceexecution proceed cflowbelow cflow initialization preinitialization staticinitialization withincode target within execution getWithinTypeName handler thisJoinPoint thisJoinPointStaticPart thisEnclosingJoinPointStaticPart declare parents warning error soft precedence thisAspectInstance",r="get set args call";return{k:t,i:/<\//,c:[{cN:"javadoc",b:"/\\*\\*",e:"\\*/",r:0,c:[{cN:"javadoctag",b:"(^|\\s)@[A-Za-z]+"}]},e.CLCM,e.CBCM,e.ASM,e.QSM,{cN:"aspect",bK:"aspect",e:/[{;=]/,eE:!0,i:/[:;"\[\]]/,c:[{bK:"extends implements pertypewithin perthis pertarget percflowbelow percflow issingleton"},e.UTM,{b:/\([^\)]*/,e:/[)]+/,k:t+" "+r,eE:!1}]},{cN:"class",bK:"class interface",e:/[{;=]/,eE:!0,r:0,k:"class interface",i:/[:"\[\]]/,c:[{bK:"extends implements"},e.UTM]},{bK:"pointcut after before around throwing returning",e:/[)]/,eE:!1,i:/["\[\]]/,c:[{b:e.UIR+"\\s*\\(",rB:!0,c:[e.UTM]}]},{b:/[:]/,rB:!0,e:/[{;]/,r:0,eE:!1,k:t,i:/["\[\]]/,c:[{b:e.UIR+"\\s*\\(",k:t+" "+r},e.QSM]},{bK:"new throw",r:0},{cN:"function",b:/\w+ +\w+(\.)?\w+\s*\([^\)]*\)\s*((throws)[\w\s,]+)?[\{;]/,rB:!0,e:/[{;=]/,k:t,eE:!0,c:[{b:e.UIR+"\\s*\\(",rB:!0,r:0,c:[e.UTM]},{cN:"params",b:/\(/,e:/\)/,r:0,k:t,c:[e.ASM,e.QSM,e.CNM,e.CBCM]},e.CLCM,e.CBCM]},e.CNM,{cN:"annotation",b:"@[A-Za-z]+"}]}}),hljs.registerLanguage("autohotkey",function(e){var t={cN:"escape",b:"`[\\s\\S]"},r=e.C(";","$",{r:0}),a=[{cN:"built_in",b:"A_[a-zA-Z0-9]+"},{cN:"built_in",bK:"ComSpec Clipboard ClipboardAll ErrorLevel"}];return{cI:!0,k:{keyword:"Break Continue Else Gosub If Loop Return While",literal:"A true false NOT AND OR"},c:a.concat([t,e.inherit(e.QSM,{c:[t]}),r,{cN:"number",b:e.NR,r:0},{cN:"var_expand",b:"%",e:"%",i:"\\n",c:[t]},{cN:"label",c:[t],v:[{b:'^[^\\n";]+::(?!=)'},{b:'^[^\\n";]+:(?!=)',r:0}]},{b:",\\s*,",r:10}])}}),hljs.registerLanguage("avrasm",function(e){return{cI:!0,l:"\\.?"+e.IR,k:{keyword:"adc add adiw and andi asr bclr bld brbc brbs brcc brcs break breq brge brhc brhs brid brie brlo brlt brmi brne brpl brsh brtc brts brvc brvs bset bst call cbi cbr clc clh cli cln clr cls clt clv clz com cp cpc cpi cpse dec eicall eijmp elpm eor fmul fmuls fmulsu icall ijmp in inc jmp ld ldd ldi lds lpm lsl lsr mov movw mul muls mulsu neg nop or ori out pop push rcall ret reti rjmp rol ror sbc sbr sbrc sbrs sec seh sbi sbci sbic sbis sbiw sei sen ser ses set sev sez sleep spm st std sts sub subi swap tst wdr",built_in:"r0 r1 r2 r3 r4 r5 r6 r7 r8 r9 r10 r11 r12 r13 r14 r15 r16 r17 r18 r19 r20 r21 r22 r23 r24 r25 r26 r27 r28 r29 r30 r31 x|0 xh xl y|0 yh yl z|0 zh zl ucsr1c udr1 ucsr1a ucsr1b ubrr1l ubrr1h ucsr0c ubrr0h tccr3c tccr3a tccr3b tcnt3h tcnt3l ocr3ah ocr3al ocr3bh ocr3bl ocr3ch ocr3cl icr3h icr3l etimsk etifr tccr1c ocr1ch ocr1cl twcr twdr twar twsr twbr osccal xmcra xmcrb eicra spmcsr spmcr portg ddrg ping portf ddrf sreg sph spl xdiv rampz eicrb eimsk gimsk gicr eifr gifr timsk tifr mcucr mcucsr tccr0 tcnt0 ocr0 assr tccr1a tccr1b tcnt1h tcnt1l ocr1ah ocr1al ocr1bh ocr1bl icr1h icr1l tccr2 tcnt2 ocr2 ocdr wdtcr sfior eearh eearl eedr eecr porta ddra pina portb ddrb pinb portc ddrc pinc portd ddrd pind spdr spsr spcr udr0 ucsr0a ucsr0b ubrr0l acsr admux adcsr adch adcl porte ddre pine pinf",preprocessor:".byte .cseg .db .def .device .dseg .dw .endmacro .equ .eseg .exit .include .list .listmac .macro .nolist .org .set"},c:[e.CBCM,e.C(";","$",{r:0}),e.CNM,e.BNM,{cN:"number",b:"\\b(\\$[a-zA-Z0-9]+|0o[0-7]+)"},e.QSM,{cN:"string",b:"'",e:"[^\\\\]'",i:"[^\\\\][^']"},{cN:"label",b:"^[A-Za-z0-9_.$]+:"},{cN:"preprocessor",b:"#",e:"$"},{cN:"localvars",b:"@[0-9]+"}]}}),hljs.registerLanguage("axapta",function(e){return{k:"false int abstract private char boolean static null if for true while long throw finally protected final return void enum else break new catch byte super case short default double public try this switch continue reverse firstfast firstonly forupdate nofetch sum avg minof maxof count order group by asc desc index hint like dispaly edit client server ttsbegin ttscommit str real date container anytype common div mod",c:[e.CLCM,e.CBCM,e.ASM,e.QSM,e.CNM,{cN:"preprocessor",b:"#",e:"$"},{cN:"class",bK:"class interface",e:"{",eE:!0,i:":",c:[{bK:"extends implements"},e.UTM]}]}}),hljs.registerLanguage("bash",function(e){var t={cN:"variable",v:[{b:/\$[\w\d#@][\w\d_]*/},{b:/\$\{(.*?)}/}]},r={cN:"string",b:/"/,e:/"/,c:[e.BE,t,{cN:"variable",b:/\$\(/,e:/\)/,c:[e.BE]}]},a={cN:"string",b:/'/,e:/'/};return{aliases:["sh","zsh"],l:/-?[a-z\.]+/,k:{keyword:"if then else elif fi for while in do done case esac function",literal:"true false",built_in:"break cd continue eval exec exit export getopts hash pwd readonly return shift test times trap umask unset alias bind builtin caller command declare echo enable help let local logout mapfile printf read readarray source type typeset ulimit unalias set shopt autoload bg bindkey bye cap chdir clone comparguments compcall compctl compdescribe compfiles compgroups compquote comptags comptry compvalues dirs disable disown echotc echoti emulate fc fg float functions getcap getln history integer jobs kill limit log noglob popd print pushd pushln rehash sched setcap setopt stat suspend ttyctl unfunction unhash unlimit unsetopt vared wait whence where which zcompile zformat zftp zle zmodload zparseopts zprof zpty zregexparse zsocket zstyle ztcp",operator:"-ne -eq -lt -gt -f -d -e -s -l -a"},c:[{cN:"shebang",b:/^#![^\n]+sh\s*$/,r:10},{cN:"function",b:/\w[\w\d_]*\s*\(\s*\)\s*\{/,rB:!0,c:[e.inherit(e.TM,{b:/\w[\w\d_]*/})],r:0},e.HCM,e.NM,r,a,t]}}),hljs.registerLanguage("brainfuck",function(e){var t={cN:"literal",b:"[\\+\\-]",r:0};return{aliases:["bf"],c:[e.C("[^\\[\\]\\.,\\+\\-<> \r\n]","[\\[\\]\\.,\\+\\-<> \r\n]",{rE:!0,r:0}),{cN:"title",b:"[\\[\\]]",r:0},{cN:"string",b:"[\\.,]",r:0},{b:/\+\+|\-\-/,rB:!0,c:[t]},t]}}),hljs.registerLanguage("capnproto",function(e){return{aliases:["capnp"],k:{keyword:"struct enum interface union group import using const annotation extends in of on as with from fixed",built_in:"Void Bool Int8 Int16 Int32 Int64 UInt8 UInt16 UInt32 UInt64 Float32 Float64 Text Data AnyPointer AnyStruct Capability List",literal:"true false"},c:[e.QSM,e.NM,e.HCM,{cN:"shebang",b:/@0x[\w\d]{16};/,i:/\n/},{cN:"number",b:/@\d+\b/},{cN:"class",bK:"struct enum",e:/\{/,i:/\n/,c:[e.inherit(e.TM,{starts:{eW:!0,eE:!0}})]},{cN:"class",bK:"interface",e:/\{/,i:/\n/,c:[e.inherit(e.TM,{starts:{eW:!0,eE:!0}})]}]}}),hljs.registerLanguage("clojure",function(e){var t={built_in:"def cond apply if-not if-let if not not= = < > <= >= == + / * - rem quot neg? pos? delay? symbol? keyword? true? false? integer? empty? coll? list? set? ifn? fn? associative? sequential? sorted? counted? reversible? number? decimal? class? distinct? isa? float? rational? reduced? ratio? odd? even? char? seq? vector? string? map? nil? contains? zero? instance? not-every? not-any? libspec? -> ->> .. . inc compare do dotimes mapcat take remove take-while drop letfn drop-last take-last drop-while while intern condp case reduced cycle split-at split-with repeat replicate iterate range merge zipmap declare line-seq sort comparator sort-by dorun doall nthnext nthrest partition eval doseq await await-for let agent atom send send-off release-pending-sends add-watch mapv filterv remove-watch agent-error restart-agent set-error-handler error-handler set-error-mode! error-mode shutdown-agents quote var fn loop recur throw try monitor-enter monitor-exit defmacro defn defn- macroexpand macroexpand-1 for dosync and or when when-not when-let comp juxt partial sequence memoize constantly complement identity assert peek pop doto proxy defstruct first rest cons defprotocol cast coll deftype defrecord last butlast sigs reify second ffirst fnext nfirst nnext defmulti defmethod meta with-meta ns in-ns create-ns import refer keys select-keys vals key val rseq name namespace promise into transient persistent! conj! assoc! dissoc! pop! disj! use class type num float double short byte boolean bigint biginteger bigdec print-method print-dup throw-if printf format load compile get-in update-in pr pr-on newline flush read slurp read-line subvec with-open memfn time re-find re-groups rand-int rand mod locking assert-valid-fdecl alias resolve ref deref refset swap! reset! set-validator! compare-and-set! alter-meta! reset-meta! commute get-validator alter ref-set ref-history-count ref-min-history ref-max-history ensure sync io! new next conj set! to-array future future-call into-array aset gen-class reduce map filter find empty hash-map hash-set sorted-map sorted-map-by sorted-set sorted-set-by vec vector seq flatten reverse assoc dissoc list disj get union difference intersection extend extend-type extend-protocol int nth delay count concat chunk chunk-buffer chunk-append chunk-first chunk-rest max min dec unchecked-inc-int unchecked-inc unchecked-dec-inc unchecked-dec unchecked-negate unchecked-add-int unchecked-add unchecked-subtract-int unchecked-subtract chunk-next chunk-cons chunked-seq? prn vary-meta lazy-seq spread list* str find-keyword keyword symbol gensym force rationalize"},r="a-zA-Z_\\-!.?+*=<>&#'",a="["+r+"]["+r+"0-9/;:]*",i="[-+]?\\d+(\\.\\d+)?",n={b:a,r:0},o={cN:"number",b:i,r:0},s=e.inherit(e.QSM,{i:null}),l=e.C(";","$",{r:0}),c={cN:"literal",b:/\b(true|false|nil)\b/},d={cN:"collection",b:"[\\[\\{]",e:"[\\]\\}]"},p={cN:"comment",b:"\\^"+a},u=e.C("\\^\\{","\\}"),m={cN:"attribute",b:"[:]"+a},b={cN:"list",b:"\\(",e:"\\)"},g={eW:!0,r:0},f={k:t,l:a,cN:"keyword",b:a,starts:g},h=[b,s,p,u,l,m,d,o,c,n];return b.c=[e.C("comment",""),f,g],g.c=h,d.c=h,{aliases:["clj"],i:/\S/,c:[b,s,p,u,l,m,d,o,c]}}),hljs.registerLanguage("clojure-repl",function(e){return{c:[{cN:"prompt",b:/^([\w.-]+|\s*#_)=>/,starts:{e:/$/,sL:"clojure",subLanguageMode:"continuous"}}]}}),hljs.registerLanguage("cmake",function(e){return{aliases:["cmake.in"],cI:!0,k:{keyword:"add_custom_command add_custom_target add_definitions add_dependencies add_executable add_library add_subdirectory add_test aux_source_directory break build_command cmake_minimum_required cmake_policy configure_file create_test_sourcelist define_property else elseif enable_language enable_testing endforeach endfunction endif endmacro endwhile execute_process export find_file find_library find_package find_path find_program fltk_wrap_ui foreach function get_cmake_property get_directory_property get_filename_component get_property get_source_file_property get_target_property get_test_property if include include_directories include_external_msproject include_regular_expression install link_directories load_cache load_command macro mark_as_advanced message option output_required_files project qt_wrap_cpp qt_wrap_ui remove_definitions return separate_arguments set set_directory_properties set_property set_source_files_properties set_target_properties set_tests_properties site_name source_group string target_link_libraries try_compile try_run unset variable_watch while build_name exec_program export_library_dependencies install_files install_programs install_targets link_libraries make_directory remove subdir_depends subdirs use_mangled_mesa utility_source variable_requires write_file qt5_use_modules qt5_use_package qt5_wrap_cpp on off true false and or",operator:"equal less greater strless strgreater strequal matches"},c:[{cN:"envvar",b:"\\${",e:"}"},e.HCM,e.QSM,e.NM]}}),hljs.registerLanguage("coffeescript",function(e){var t={keyword:"in if for while finally new do return else break catch instanceof throw try this switch continue typeof delete debugger super then unless until loop of by when and or is isnt not",literal:"true false null undefined yes no on off",reserved:"case default function var void with const let enum export import native __hasProp __extends __slice __bind __indexOf",built_in:"npm require console print module global window document"},r="[A-Za-z$_][0-9A-Za-z$_]*",a={cN:"subst",b:/#\{/,e:/}/,k:t},i=[e.BNM,e.inherit(e.CNM,{starts:{e:"(\\s*/)?",r:0}}),{cN:"string",v:[{b:/'''/,e:/'''/,c:[e.BE]},{b:/'/,e:/'/,c:[e.BE]},{b:/"""/,e:/"""/,c:[e.BE,a]},{b:/"/,e:/"/,c:[e.BE,a]}]},{cN:"regexp",v:[{b:"///",e:"///",c:[a,e.HCM]},{b:"//[gim]*",r:0},{b:/\/(?![ *])(\\\/|.)*?\/[gim]*(?=\W|$)/}]},{cN:"property",b:"@"+r},{b:"`",e:"`",eB:!0,eE:!0,sL:"javascript"}];a.c=i;var n=e.inherit(e.TM,{b:r}),o="(\\(.*\\))?\\s*\\B[-=]>",s={cN:"params",b:"\\([^\\(]",rB:!0,c:[{b:/\(/,e:/\)/,k:t,c:["self"].concat(i)}]};return{aliases:["coffee","cson","iced"],k:t,i:/\/\*/,c:i.concat([e.C("###","###"),e.HCM,{cN:"function",b:"^\\s*"+r+"\\s*=\\s*"+o,e:"[-=]>",rB:!0,c:[n,s]},{b:/[:\(,=]\s*/,r:0,c:[{cN:"function",b:o,e:"[-=]>",rB:!0,c:[s]}]},{cN:"class",bK:"class",e:"$",i:/[:="\[\]]/,c:[{bK:"extends",eW:!0,i:/[:="\[\]]/,c:[n]},n]},{cN:"attribute",b:r+":",e:":",rB:!0,rE:!0,r:0}])}}),hljs.registerLanguage("cpp",function(e){var t={keyword:"false int float while private char catch export virtual operator sizeof dynamic_cast|10 typedef const_cast|10 const struct for static_cast|10 union namespace unsigned long volatile static protected bool template mutable if public friend do goto auto void enum else break extern using true class asm case typeid short reinterpret_cast|10 default double register explicit signed typename try this switch continue wchar_t inline delete alignof char16_t char32_t constexpr decltype noexcept nullptr static_assert thread_local restrict _Bool complex _Complex _Imaginary intmax_t uintmax_t int8_t uint8_t int16_t uint16_t int32_t uint32_t  int64_t uint64_t int_least8_t uint_least8_t int_least16_t uint_least16_t int_least32_t uint_least32_t int_least64_t uint_least64_t int_fast8_t uint_fast8_t int_fast16_t uint_fast16_t int_fast32_t uint_fast32_t int_fast64_t uint_fast64_t intptr_t uintptr_t atomic_bool atomic_char atomic_schar atomic_uchar atomic_short atomic_ushort atomic_int atomic_uint atomic_long atomic_ulong atomic_llong atomic_ullong atomic_wchar_t atomic_char16_t atomic_char32_t atomic_intmax_t atomic_uintmax_t atomic_intptr_t atomic_uintptr_t atomic_size_t atomic_ptrdiff_t atomic_int_least8_t atomic_int_least16_t atomic_int_least32_t atomic_int_least64_t atomic_uint_least8_t atomic_uint_least16_t atomic_uint_least32_t atomic_uint_least64_t atomic_int_fast8_t atomic_int_fast16_t atomic_int_fast32_t atomic_int_fast64_t atomic_uint_fast8_t atomic_uint_fast16_t atomic_uint_fast32_t atomic_uint_fast64_t",built_in:"std string cin cout cerr clog stringstream istringstream ostringstream auto_ptr deque list queue stack vector map set bitset multiset multimap unordered_set unordered_map unordered_multiset unordered_multimap array shared_ptr abort abs acos asin atan2 atan calloc ceil cosh cos exit exp fabs floor fmod fprintf fputs free frexp fscanf isalnum isalpha iscntrl isdigit isgraph islower isprint ispunct isspace isupper isxdigit tolower toupper labs ldexp log10 log malloc memchr memcmp memcpy memset modf pow printf putchar puts scanf sinh sin snprintf sprintf sqrt sscanf strcat strchr strcmp strcpy strcspn strlen strncat strncmp strncpy strpbrk strrchr strspn strstr tanh tan vfprintf vprintf vsprintf"};return{aliases:["c","cc","h","c++","h++","hpp"],k:t,i:"</",c:[e.CLCM,e.CBCM,e.QSM,{cN:"string",b:"'\\\\?.",e:"'",i:"."},{cN:"number",b:"\\b(\\d+(\\.\\d*)?|\\.\\d+)(u|U|l|L|ul|UL|f|F)"},e.CNM,{cN:"preprocessor",b:"#",e:"$",k:"if else elif endif define undef warning error line pragma",c:[{b:/\\\n/,r:0},{b:'include\\s*[<"]',e:'[>"]',k:"include",i:"\\n"},e.CLCM]},{b:"\\b(deque|list|queue|stack|vector|map|set|bitset|multiset|multimap|unordered_map|unordered_set|unordered_multiset|unordered_multimap|array)\\s*<",e:">",k:t,c:["self"]},{b:e.IR+"::",k:t},{bK:"new throw return else",r:0},{cN:"function",b:"("+e.IR+"\\s+)+"+e.IR+"\\s*\\(",rB:!0,e:/[{;=]/,eE:!0,k:t,c:[{b:e.IR+"\\s*\\(",rB:!0,c:[e.TM],r:0},{cN:"params",b:/\(/,e:/\)/,k:t,r:0,c:[e.CBCM]},e.CLCM,e.CBCM]}]}}),hljs.registerLanguage("cs",function(e){
var t="abstract as base bool break byte case catch char checked const continue decimal dynamic default delegate do double else enum event explicit extern false finally fixed float for foreach goto if implicit in int interface internal is lock long null when object operator out override params private protected public readonly ref sbyte sealed short sizeof stackalloc static string struct switch this true try typeof uint ulong unchecked unsafe ushort using virtual volatile void while async protected public private internal ascending descending from get group into join let orderby partial select set value var where yield",r=e.IR+"(<"+e.IR+">)?";return{aliases:["csharp"],k:t,i:/::/,c:[e.C("///","$",{rB:!0,c:[{cN:"xmlDocTag",v:[{b:"///",r:0},{b:"<!--|-->"},{b:"</?",e:">"}]}]}),e.CLCM,e.CBCM,{cN:"preprocessor",b:"#",e:"$",k:"if else elif endif define undef warning error line region endregion pragma checksum"},{cN:"string",b:'@"',e:'"',c:[{b:'""'}]},e.ASM,e.QSM,e.CNM,{bK:"class namespace interface",e:/[{;=]/,i:/[^\s:]/,c:[e.TM,e.CLCM,e.CBCM]},{bK:"new return throw await",r:0},{cN:"function",b:"("+r+"\\s+)+"+e.IR+"\\s*\\(",rB:!0,e:/[{;=]/,eE:!0,k:t,c:[{b:e.IR+"\\s*\\(",rB:!0,c:[e.TM],r:0},{cN:"params",b:/\(/,e:/\)/,k:t,r:0,c:[e.ASM,e.QSM,e.CNM,e.CBCM]},e.CLCM,e.CBCM]}]}}),hljs.registerLanguage("css",function(e){var t="[a-zA-Z-][a-zA-Z0-9_-]*",r={cN:"function",b:t+"\\(",rB:!0,eE:!0,e:"\\("},a={cN:"rule",b:/[A-Z\_\.\-]+\s*:/,rB:!0,e:";",eW:!0,c:[{cN:"attribute",b:/\S/,e:":",eE:!0,starts:{cN:"value",eW:!0,eE:!0,c:[r,e.CSSNM,e.QSM,e.ASM,e.CBCM,{cN:"hexcolor",b:"#[0-9A-Fa-f]+"},{cN:"important",b:"!important"}]}}]};return{cI:!0,i:/[=\/|']/,c:[e.CBCM,a,{cN:"id",b:/\#[A-Za-z0-9_-]+/},{cN:"class",b:/\.[A-Za-z0-9_-]+/,r:0},{cN:"attr_selector",b:/\[/,e:/\]/,i:"$"},{cN:"pseudo",b:/:(:)?[a-zA-Z0-9\_\-\+\(\)"']+/},{cN:"at_rule",b:"@(font-face|page)",l:"[a-z-]+",k:"font-face page"},{cN:"at_rule",b:"@",e:"[{;]",c:[{cN:"keyword",b:/\S+/},{b:/\s/,eW:!0,eE:!0,r:0,c:[r,e.ASM,e.QSM,e.CSSNM]}]},{cN:"tag",b:t,r:0},{cN:"rules",b:"{",e:"}",i:/\S/,r:0,c:[e.CBCM,a]}]}}),hljs.registerLanguage("d",function(e){var t={keyword:"abstract alias align asm assert auto body break byte case cast catch class const continue debug default delete deprecated do else enum export extern final finally for foreach foreach_reverse|10 goto if immutable import in inout int interface invariant is lazy macro mixin module new nothrow out override package pragma private protected public pure ref return scope shared static struct super switch synchronized template this throw try typedef typeid typeof union unittest version void volatile while with __FILE__ __LINE__ __gshared|10 __thread __traits __DATE__ __EOF__ __TIME__ __TIMESTAMP__ __VENDOR__ __VERSION__",built_in:"bool cdouble cent cfloat char creal dchar delegate double dstring float function idouble ifloat ireal long real short string ubyte ucent uint ulong ushort wchar wstring",literal:"false null true"},r="(0|[1-9][\\d_]*)",a="(0|[1-9][\\d_]*|\\d[\\d_]*|[\\d_]+?\\d)",i="0[bB][01_]+",n="([\\da-fA-F][\\da-fA-F_]*|_[\\da-fA-F][\\da-fA-F_]*)",o="0[xX]"+n,s="([eE][+-]?"+a+")",l="("+a+"(\\.\\d*|"+s+")|\\d+\\."+a+a+"|\\."+r+s+"?)",c="(0[xX]("+n+"\\."+n+"|\\.?"+n+")[pP][+-]?"+a+")",d="("+r+"|"+i+"|"+o+")",p="("+c+"|"+l+")",u="\\\\(['\"\\?\\\\abfnrtv]|u[\\dA-Fa-f]{4}|[0-7]{1,3}|x[\\dA-Fa-f]{2}|U[\\dA-Fa-f]{8})|&[a-zA-Z\\d]{2,};",m={cN:"number",b:"\\b"+d+"(L|u|U|Lu|LU|uL|UL)?",r:0},b={cN:"number",b:"\\b("+p+"([fF]|L|i|[fF]i|Li)?|"+d+"(i|[fF]i|Li))",r:0},g={cN:"string",b:"'("+u+"|.)",e:"'",i:"."},f={b:u,r:0},h={cN:"string",b:'"',c:[f],e:'"[cwd]?'},v={cN:"string",b:'[rq]"',e:'"[cwd]?',r:5},_={cN:"string",b:"`",e:"`[cwd]?"},y={cN:"string",b:'x"[\\da-fA-F\\s\\n\\r]*"[cwd]?',r:10},x={cN:"string",b:'q"\\{',e:'\\}"'},C={cN:"shebang",b:"^#!",e:"$",r:5},S={cN:"preprocessor",b:"#(line)",e:"$",r:5},w={cN:"keyword",b:"@[a-zA-Z_][a-zA-Z_\\d]*"},N=e.C("\\/\\+","\\+\\/",{c:["self"],r:10});return{l:e.UIR,k:t,c:[e.CLCM,e.CBCM,N,y,h,v,_,x,b,m,g,C,S,w]}}),hljs.registerLanguage("markdown",function(e){return{aliases:["md","mkdown","mkd"],c:[{cN:"header",v:[{b:"^#{1,6}",e:"$"},{b:"^.+?\\n[=-]{2,}$"}]},{b:"<",e:">",sL:"xml",r:0},{cN:"bullet",b:"^([*+-]|(\\d+\\.))\\s+"},{cN:"strong",b:"[*_]{2}.+?[*_]{2}"},{cN:"emphasis",v:[{b:"\\*.+?\\*"},{b:"_.+?_",r:0}]},{cN:"blockquote",b:"^>\\s+",e:"$"},{cN:"code",v:[{b:"`.+?`"},{b:"^( {4}|	)",e:"$",r:0}]},{cN:"horizontal_rule",b:"^[-\\*]{3,}",e:"$"},{b:"\\[.+?\\][\\(\\[].*?[\\)\\]]",rB:!0,c:[{cN:"link_label",b:"\\[",e:"\\]",eB:!0,rE:!0,r:0},{cN:"link_url",b:"\\]\\(",e:"\\)",eB:!0,eE:!0},{cN:"link_reference",b:"\\]\\[",e:"\\]",eB:!0,eE:!0}],r:10},{b:"^\\[.+\\]:",rB:!0,c:[{cN:"link_reference",b:"\\[",e:"\\]:",eB:!0,eE:!0,starts:{cN:"link_url",e:"$"}}]}]}}),hljs.registerLanguage("dart",function(e){var t={cN:"subst",b:"\\$\\{",e:"}",k:"true false null this is new super"},r={cN:"string",v:[{b:"r'''",e:"'''"},{b:'r"""',e:'"""'},{b:"r'",e:"'",i:"\\n"},{b:'r"',e:'"',i:"\\n"},{b:"'''",e:"'''",c:[e.BE,t]},{b:'"""',e:'"""',c:[e.BE,t]},{b:"'",e:"'",i:"\\n",c:[e.BE,t]},{b:'"',e:'"',i:"\\n",c:[e.BE,t]}]};t.c=[e.CNM,r];var a={keyword:"assert break case catch class const continue default do else enum extends false final finally for if in is new null rethrow return super switch this throw true try var void while with",literal:"abstract as dynamic export external factory get implements import library operator part set static typedef",built_in:"print Comparable DateTime Duration Function Iterable Iterator List Map Match Null Object Pattern RegExp Set Stopwatch String StringBuffer StringSink Symbol Type Uri bool double int num document window querySelector querySelectorAll Element ElementList"};return{k:a,c:[r,{cN:"dartdoc",b:"/\\*\\*",e:"\\*/",sL:"markdown",subLanguageMode:"continuous"},{cN:"dartdoc",b:"///",e:"$",sL:"markdown",subLanguageMode:"continuous"},e.CLCM,e.CBCM,{cN:"class",bK:"class interface",e:"{",eE:!0,c:[{bK:"extends implements"},e.UTM]},e.CNM,{cN:"annotation",b:"@[A-Za-z]+"},{b:"=>"}]}}),hljs.registerLanguage("delphi",function(e){var t="exports register file shl array record property for mod while set ally label uses raise not stored class safecall var interface or private static exit index inherited to else stdcall override shr asm far resourcestring finalization packed virtual out and protected library do xorwrite goto near function end div overload object unit begin string on inline repeat until destructor write message program with read initialization except default nil if case cdecl in downto threadvar of try pascal const external constructor type public then implementation finally published procedure",r=[e.CLCM,e.C(/\{/,/\}/,{r:0}),e.C(/\(\*/,/\*\)/,{r:10})],a={cN:"string",b:/'/,e:/'/,c:[{b:/''/}]},i={cN:"string",b:/(#\d+)+/},n={b:e.IR+"\\s*=\\s*class\\s*\\(",rB:!0,c:[e.TM]},o={cN:"function",bK:"function constructor destructor procedure",e:/[:;]/,k:"function constructor|10 destructor|10 procedure|10",c:[e.TM,{cN:"params",b:/\(/,e:/\)/,k:t,c:[a,i]}].concat(r)};return{cI:!0,k:t,i:/"|\$[G-Zg-z]|\/\*|<\/|\|/,c:[a,i,e.NM,n,o].concat(r)}}),hljs.registerLanguage("diff",function(e){return{aliases:["patch"],c:[{cN:"chunk",r:10,v:[{b:/^@@ +\-\d+,\d+ +\+\d+,\d+ +@@$/},{b:/^\*\*\* +\d+,\d+ +\*\*\*\*$/},{b:/^\-\-\- +\d+,\d+ +\-\-\-\-$/}]},{cN:"header",v:[{b:/Index: /,e:/$/},{b:/=====/,e:/=====$/},{b:/^\-\-\-/,e:/$/},{b:/^\*{3} /,e:/$/},{b:/^\+\+\+/,e:/$/},{b:/\*{5}/,e:/\*{5}$/}]},{cN:"addition",b:"^\\+",e:"$"},{cN:"deletion",b:"^\\-",e:"$"},{cN:"change",b:"^\\!",e:"$"}]}}),hljs.registerLanguage("django",function(e){var t={cN:"filter",b:/\|[A-Za-z]+:?/,k:"truncatewords removetags linebreaksbr yesno get_digit timesince random striptags filesizeformat escape linebreaks length_is ljust rjust cut urlize fix_ampersands title floatformat capfirst pprint divisibleby add make_list unordered_list urlencode timeuntil urlizetrunc wordcount stringformat linenumbers slice date dictsort dictsortreversed default_if_none pluralize lower join center default truncatewords_html upper length phone2numeric wordwrap time addslashes slugify first escapejs force_escape iriencode last safe safeseq truncatechars localize unlocalize localtime utc timezone",c:[{cN:"argument",b:/"/,e:/"/},{cN:"argument",b:/'/,e:/'/}]};return{aliases:["jinja"],cI:!0,sL:"xml",subLanguageMode:"continuous",c:[e.C(/\{%\s*comment\s*%}/,/\{%\s*endcomment\s*%}/),e.C(/\{#/,/#}/),{cN:"template_tag",b:/\{%/,e:/%}/,k:"comment endcomment load templatetag ifchanged endifchanged if endif firstof for endfor in ifnotequal endifnotequal widthratio extends include spaceless endspaceless regroup by as ifequal endifequal ssi now with cycle url filter endfilter debug block endblock else autoescape endautoescape csrf_token empty elif endwith static trans blocktrans endblocktrans get_static_prefix get_media_prefix plural get_current_language language get_available_languages get_current_language_bidi get_language_info get_language_info_list localize endlocalize localtime endlocaltime timezone endtimezone get_current_timezone verbatim",c:[t]},{cN:"variable",b:/\{\{/,e:/}}/,c:[t]}]}}),hljs.registerLanguage("dockerfile",function(e){return{aliases:["docker"],cI:!0,k:{built_ins:"from maintainer cmd expose add copy entrypoint volume user workdir onbuild run env"},c:[e.HCM,{k:{built_in:"run cmd entrypoint volume add copy workdir onbuild"},b:/^ *(onbuild +)?(run|cmd|entrypoint|volume|add|copy|workdir) +/,starts:{e:/[^\\]\n/,sL:"bash",subLanguageMode:"continuous"}},{k:{built_in:"from maintainer expose env user onbuild"},b:/^ *(onbuild +)?(from|maintainer|expose|env|user|onbuild) +/,e:/[^\\]\n/,c:[e.ASM,e.QSM,e.NM,e.HCM]}]}}),hljs.registerLanguage("dos",function(e){var t=e.C(/@?rem\b/,/$/,{r:10}),r={cN:"label",b:"^\\s*[A-Za-z._?][A-Za-z0-9_$#@~.?]*(:|\\s+label)",r:0};return{aliases:["bat","cmd"],cI:!0,k:{flow:"if else goto for in do call exit not exist errorlevel defined",operator:"equ neq lss leq gtr geq",keyword:"shift cd dir echo setlocal endlocal set pause copy",stream:"prn nul lpt3 lpt2 lpt1 con com4 com3 com2 com1 aux",winutils:"ping net ipconfig taskkill xcopy ren del",built_in:"append assoc at attrib break cacls cd chcp chdir chkdsk chkntfs cls cmd color comp compact convert date dir diskcomp diskcopy doskey erase fs find findstr format ftype graftabl help keyb label md mkdir mode more move path pause print popd pushd promt rd recover rem rename replace restore rmdir shiftsort start subst time title tree type ver verify vol"},c:[{cN:"envvar",b:/%%[^ ]|%[^ ]+?%|![^ ]+?!/},{cN:"function",b:r.b,e:"goto:eof",c:[e.inherit(e.TM,{b:"([_a-zA-Z]\\w*\\.)*([_a-zA-Z]\\w*:)?[_a-zA-Z]\\w*"}),t]},{cN:"number",b:"\\b\\d+",r:0},t]}}),hljs.registerLanguage("dust",function(e){var t="if eq ne lt lte gt gte select default math sep";return{aliases:["dst"],cI:!0,sL:"xml",subLanguageMode:"continuous",c:[{cN:"expression",b:"{",e:"}",r:0,c:[{cN:"begin-block",b:"#[a-zA-Z- .]+",k:t},{cN:"string",b:'"',e:'"'},{cN:"end-block",b:"\\/[a-zA-Z- .]+",k:t},{cN:"variable",b:"[a-zA-Z-.]+",k:t,r:0}]}]}}),hljs.registerLanguage("elixir",function(e){var t="[a-zA-Z_][a-zA-Z0-9_]*(\\!|\\?)?",r="[a-zA-Z_]\\w*[!?=]?|[-+~]\\@|<<|>>|=~|===?|<=>|[<>]=?|\\*\\*|[-/+%^&*~`|]|\\[\\]=?",a="and false then defined module in return redo retry end for true self when next until do begin unless nil break not case cond alias while ensure or include use alias fn quote",i={cN:"subst",b:"#\\{",e:"}",l:t,k:a},n={cN:"string",c:[e.BE,i],v:[{b:/'/,e:/'/},{b:/"/,e:/"/}]},o={cN:"function",bK:"def defp defmacro",e:/\B\b/,c:[e.inherit(e.TM,{b:t,endsParent:!0})]},s=e.inherit(o,{cN:"class",bK:"defmodule defrecord",e:/\bdo\b|$|;/}),l=[n,e.HCM,s,o,{cN:"constant",b:"(\\b[A-Z_]\\w*(.)?)+",r:0},{cN:"symbol",b:":",c:[n,{b:r}],r:0},{cN:"symbol",b:t+":",r:0},{cN:"number",b:"(\\b0[0-7_]+)|(\\b0x[0-9a-fA-F_]+)|(\\b[1-9][0-9_]*(\\.[0-9_]+)?)|[0_]\\b",r:0},{cN:"variable",b:"(\\$\\W)|((\\$|\\@\\@?)(\\w+))"},{b:"->"},{b:"("+e.RSR+")\\s*",c:[e.HCM,{cN:"regexp",i:"\\n",c:[e.BE,i],v:[{b:"/",e:"/[a-z]*"},{b:"%r\\[",e:"\\][a-z]*"}]}],r:0}];return i.c=l,{l:t,k:a,c:l}}),hljs.registerLanguage("ruby",function(e){var t="[a-zA-Z_]\\w*[!?=]?|[-+~]\\@|<<|>>|=~|===?|<=>|[<>]=?|\\*\\*|[-/+%^&*~`|]|\\[\\]=?",r="and false then defined module in return redo if BEGIN retry end for true self when next until do begin unless END rescue nil else break undef not super class case require yield alias while ensure elsif or include attr_reader attr_writer attr_accessor",a={cN:"yardoctag",b:"@[A-Za-z]+"},i={cN:"value",b:"#<",e:">"},n=[e.C("#","$",{c:[a]}),e.C("^\\=begin","^\\=end",{c:[a],r:10}),e.C("^__END__","\\n$")],o={cN:"subst",b:"#\\{",e:"}",k:r},s={cN:"string",c:[e.BE,o],v:[{b:/'/,e:/'/},{b:/"/,e:/"/},{b:/`/,e:/`/},{b:"%[qQwWx]?\\(",e:"\\)"},{b:"%[qQwWx]?\\[",e:"\\]"},{b:"%[qQwWx]?{",e:"}"},{b:"%[qQwWx]?<",e:">"},{b:"%[qQwWx]?/",e:"/"},{b:"%[qQwWx]?%",e:"%"},{b:"%[qQwWx]?-",e:"-"},{b:"%[qQwWx]?\\|",e:"\\|"},{b:/\B\?(\\\d{1,3}|\\x[A-Fa-f0-9]{1,2}|\\u[A-Fa-f0-9]{4}|\\?\S)\b/}]},l={cN:"params",b:"\\(",e:"\\)",k:r},c=[s,i,{cN:"class",bK:"class module",e:"$|;",i:/=/,c:[e.inherit(e.TM,{b:"[A-Za-z_]\\w*(::\\w+)*(\\?|\\!)?"}),{cN:"inheritance",b:"<\\s*",c:[{cN:"parent",b:"("+e.IR+"::)?"+e.IR}]}].concat(n)},{cN:"function",bK:"def",e:" |$|;",r:0,c:[e.inherit(e.TM,{b:t}),l].concat(n)},{cN:"constant",b:"(::)?(\\b[A-Z]\\w*(::)?)+",r:0},{cN:"symbol",b:e.UIR+"(\\!|\\?)?:",r:0},{cN:"symbol",b:":",c:[s,{b:t}],r:0},{cN:"number",b:"(\\b0[0-7_]+)|(\\b0x[0-9a-fA-F_]+)|(\\b[1-9][0-9_]*(\\.[0-9_]+)?)|[0_]\\b",r:0},{cN:"variable",b:"(\\$\\W)|((\\$|\\@\\@?)(\\w+))"},{b:"("+e.RSR+")\\s*",c:[i,{cN:"regexp",c:[e.BE,o],i:/\n/,v:[{b:"/",e:"/[a-z]*"},{b:"%r{",e:"}[a-z]*"},{b:"%r\\(",e:"\\)[a-z]*"},{b:"%r!",e:"![a-z]*"},{b:"%r\\[",e:"\\][a-z]*"}]}].concat(n),r:0}].concat(n);o.c=c,l.c=c;var d="[>?]>",p="[\\w#]+\\(\\w+\\):\\d+:\\d+>",u="(\\w+-)?\\d+\\.\\d+\\.\\d(p\\d+)?[^>]+>",m=[{b:/^\s*=>/,cN:"status",starts:{e:"$",c:c}},{cN:"prompt",b:"^("+d+"|"+p+"|"+u+")",starts:{e:"$",c:c}}];return{aliases:["rb","gemspec","podspec","thor","irb"],k:r,c:n.concat(m).concat(c)}}),hljs.registerLanguage("erb",function(e){return{sL:"xml",subLanguageMode:"continuous",c:[e.C("<%#","%>"),{b:"<%[%=-]?",e:"[%-]?%>",sL:"ruby",eB:!0,eE:!0}]}}),hljs.registerLanguage("erlang-repl",function(e){return{k:{special_functions:"spawn spawn_link self",reserved:"after and andalso|10 band begin bnot bor bsl bsr bxor case catch cond div end fun if let not of or orelse|10 query receive rem try when xor"},c:[{cN:"prompt",b:"^[0-9]+> ",r:10},e.C("%","$"),{cN:"number",b:"\\b(\\d+#[a-fA-F0-9]+|\\d+(\\.\\d+)?([eE][-+]?\\d+)?)",r:0},e.ASM,e.QSM,{cN:"constant",b:"\\?(::)?([A-Z]\\w*(::)?)+"},{cN:"arrow",b:"->"},{cN:"ok",b:"ok"},{cN:"exclamation_mark",b:"!"},{cN:"function_or_atom",b:"(\\b[a-z'][a-zA-Z0-9_']*:[a-z'][a-zA-Z0-9_']*)|(\\b[a-z'][a-zA-Z0-9_']*)",r:0},{cN:"variable",b:"[A-Z][a-zA-Z0-9_']*",r:0}]}}),hljs.registerLanguage("erlang",function(e){var t="[a-z'][a-zA-Z0-9_']*",r="("+t+":"+t+"|"+t+")",a={keyword:"after and andalso|10 band begin bnot bor bsl bzr bxor case catch cond div end fun if let not of orelse|10 query receive rem try when xor",literal:"false true"},i=e.C("%","$"),n={cN:"number",b:"\\b(\\d+#[a-fA-F0-9]+|\\d+(\\.\\d+)?([eE][-+]?\\d+)?)",r:0},o={b:"fun\\s+"+t+"/\\d+"},s={b:r+"\\(",e:"\\)",rB:!0,r:0,c:[{cN:"function_name",b:r,r:0},{b:"\\(",e:"\\)",eW:!0,rE:!0,r:0}]},l={cN:"tuple",b:"{",e:"}",r:0},c={cN:"variable",b:"\\b_([A-Z][A-Za-z0-9_]*)?",r:0},d={cN:"variable",b:"[A-Z][a-zA-Z0-9_]*",r:0},p={b:"#"+e.UIR,r:0,rB:!0,c:[{cN:"record_name",b:"#"+e.UIR,r:0},{b:"{",e:"}",r:0}]},u={bK:"fun receive if try case",e:"end",k:a};u.c=[i,o,e.inherit(e.ASM,{cN:""}),u,s,e.QSM,n,l,c,d,p];var m=[i,o,u,s,e.QSM,n,l,c,d,p];s.c[1].c=m,l.c=m,p.c[1].c=m;var b={cN:"params",b:"\\(",e:"\\)",c:m};return{aliases:["erl"],k:a,i:"(</|\\*=|\\+=|-=|/\\*|\\*/|\\(\\*|\\*\\))",c:[{cN:"function",b:"^"+t+"\\s*\\(",e:"->",rB:!0,i:"\\(|#|//|/\\*|\\\\|:|;",c:[b,e.inherit(e.TM,{b:t})],starts:{e:";|\\.",k:a,c:m}},i,{cN:"pp",b:"^-",e:"\\.",r:0,eE:!0,rB:!0,l:"-"+e.IR,k:"-module -record -undef -export -ifdef -ifndef -author -copyright -doc -vsn -import -include -include_lib -compile -define -else -endif -file -behaviour -behavior -spec",c:[b]},n,e.QSM,p,c,d,l,{b:/\.$/}]}}),hljs.registerLanguage("fix",function(e){return{c:[{b:/[^\u2401\u0001]+/,e:/[\u2401\u0001]/,eE:!0,rB:!0,rE:!1,c:[{b:/([^\u2401\u0001=]+)/,e:/=([^\u2401\u0001=]+)/,rE:!0,rB:!1,cN:"attribute"},{b:/=/,e:/([\u2401\u0001])/,eE:!0,eB:!0,cN:"string"}]}],cI:!0}}),hljs.registerLanguage("fortran",function(e){var t={cN:"params",b:"\\(",e:"\\)"},r={constant:".False. .True.",type:"integer real character complex logical dimension allocatable|10 parameter external implicit|10 none double precision assign intent optional pointer target in out common equivalence data",keyword:"kind do while private call intrinsic where elsewhere type endtype endmodule endselect endinterface end enddo endif if forall endforall only contains default return stop then public subroutine|10 function program .and. .or. .not. .le. .eq. .ge. .gt. .lt. goto save else use module select case access blank direct exist file fmt form formatted iostat name named nextrec number opened rec recl sequential status unformatted unit continue format pause cycle exit c_null_char c_alert c_backspace c_form_feed flush wait decimal round iomsg synchronous nopass non_overridable pass protected volatile abstract extends import non_intrinsic value deferred generic final enumerator class associate bind enum c_int c_short c_long c_long_long c_signed_char c_size_t c_int8_t c_int16_t c_int32_t c_int64_t c_int_least8_t c_int_least16_t c_int_least32_t c_int_least64_t c_int_fast8_t c_int_fast16_t c_int_fast32_t c_int_fast64_t c_intmax_t C_intptr_t c_float c_double c_long_double c_float_complex c_double_complex c_long_double_complex c_bool c_char c_null_ptr c_null_funptr c_new_line c_carriage_return c_horizontal_tab c_vertical_tab iso_c_binding c_loc c_funloc c_associated  c_f_pointer c_ptr c_funptr iso_fortran_env character_storage_size error_unit file_storage_size input_unit iostat_end iostat_eor numeric_storage_size output_unit c_f_procpointer ieee_arithmetic ieee_support_underflow_control ieee_get_underflow_mode ieee_set_underflow_mode newunit contiguous pad position action delim readwrite eor advance nml interface procedure namelist include sequence elemental pure",built_in:"alog alog10 amax0 amax1 amin0 amin1 amod cabs ccos cexp clog csin csqrt dabs dacos dasin datan datan2 dcos dcosh ddim dexp dint dlog dlog10 dmax1 dmin1 dmod dnint dsign dsin dsinh dsqrt dtan dtanh float iabs idim idint idnint ifix isign max0 max1 min0 min1 sngl algama cdabs cdcos cdexp cdlog cdsin cdsqrt cqabs cqcos cqexp cqlog cqsin cqsqrt dcmplx dconjg derf derfc dfloat dgamma dimag dlgama iqint qabs qacos qasin qatan qatan2 qcmplx qconjg qcos qcosh qdim qerf qerfc qexp qgamma qimag qlgama qlog qlog10 qmax1 qmin1 qmod qnint qsign qsin qsinh qsqrt qtan qtanh abs acos aimag aint anint asin atan atan2 char cmplx conjg cos cosh exp ichar index int log log10 max min nint sign sin sinh sqrt tan tanh print write dim lge lgt lle llt mod nullify allocate deallocate adjustl adjustr all allocated any associated bit_size btest ceiling count cshift date_and_time digits dot_product eoshift epsilon exponent floor fraction huge iand ibclr ibits ibset ieor ior ishft ishftc lbound len_trim matmul maxexponent maxloc maxval merge minexponent minloc minval modulo mvbits nearest pack present product radix random_number random_seed range repeat reshape rrspacing scale scan selected_int_kind selected_real_kind set_exponent shape size spacing spread sum system_clock tiny transpose trim ubound unpack verify achar iachar transfer dble entry dprod cpu_time command_argument_count get_command get_command_argument get_environment_variable is_iostat_end ieee_arithmetic ieee_support_underflow_control ieee_get_underflow_mode ieee_set_underflow_mode is_iostat_eor move_alloc new_line selected_char_kind same_type_as extends_type_ofacosh asinh atanh bessel_j0 bessel_j1 bessel_jn bessel_y0 bessel_y1 bessel_yn erf erfc erfc_scaled gamma log_gamma hypot norm2 atomic_define atomic_ref execute_command_line leadz trailz storage_size merge_bits bge bgt ble blt dshiftl dshiftr findloc iall iany iparity image_index lcobound ucobound maskl maskr num_images parity popcnt poppar shifta shiftl shiftr this_image"};return{cI:!0,aliases:["f90","f95"],k:r,c:[e.inherit(e.ASM,{cN:"string",r:0}),e.inherit(e.QSM,{cN:"string",r:0}),{cN:"function",bK:"subroutine function program",i:"[${=\\n]",c:[e.UTM,t]},e.C("!","$",{r:0}),{cN:"number",b:"(?=\\b|\\+|\\-|\\.)(?=\\.\\d|\\d)(?:\\d+)?(?:\\.?\\d*)(?:[de][+-]?\\d+)?\\b\\.?",r:0}]}}),hljs.registerLanguage("fsharp",function(e){var t={b:"<",e:">",c:[e.inherit(e.TM,{b:/'[a-zA-Z0-9_]+/})]};return{aliases:["fs"],k:"yield! return! let! do!abstract and as assert base begin class default delegate do done downcast downto elif else end exception extern false finally for fun function global if in inherit inline interface internal lazy let match member module mutable namespace new null of open or override private public rec return sig static struct then to true try type upcast use val void when while with yield",c:[{cN:"string",b:'@"',e:'"',c:[{b:'""'}]},{cN:"string",b:'"""',e:'"""'},e.C("\\(\\*","\\*\\)"),{cN:"class",bK:"type",e:"\\(|=|$",eE:!0,c:[e.UTM,t]},{cN:"annotation",b:"\\[<",e:">\\]",r:10},{cN:"attribute",b:"\\B('[A-Za-z])\\b",c:[e.BE]},e.CLCM,e.inherit(e.QSM,{i:null}),e.CNM]}}),hljs.registerLanguage("gcode",function(e){var t="[A-Z_][A-Z0-9_.]*",r="\\%",a={literal:"",built_in:"",keyword:"IF DO WHILE ENDWHILE CALL ENDIF SUB ENDSUB GOTO REPEAT ENDREPEAT EQ LT GT NE GE LE OR XOR"},i={cN:"preprocessor",b:"([O])([0-9]+)"},n=[e.CLCM,e.CBCM,e.C(/\(/,/\)/),e.inherit(e.CNM,{b:"([-+]?([0-9]*\\.?[0-9]+\\.?))|"+e.CNR}),e.inherit(e.ASM,{i:null}),e.inherit(e.QSM,{i:null}),{cN:"keyword",b:"([G])([0-9]+\\.?[0-9]?)"},{cN:"title",b:"([M])([0-9]+\\.?[0-9]?)"},{cN:"title",b:"(VC|VS|#)",e:"(\\d+)"},{cN:"title",b:"(VZOFX|VZOFY|VZOFZ)"},{cN:"built_in",b:"(ATAN|ABS|ACOS|ASIN|SIN|COS|EXP|FIX|FUP|ROUND|LN|TAN)(\\[)",e:"([-+]?([0-9]*\\.?[0-9]+\\.?))(\\])"},{cN:"label",v:[{b:"N",e:"\\d+",i:"\\W"}]}];return{aliases:["nc"],cI:!0,l:t,k:a,c:[{cN:"preprocessor",b:r},i].concat(n)}}),hljs.registerLanguage("gherkin",function(e){return{aliases:["feature"],k:"Feature Background Ability Business Need Scenario Scenarios Scenario Outline Scenario Template Examples Given And Then But When",c:[{cN:"keyword",b:"\\*"},e.C("@[^@\r\n	 ]+","$"),{cN:"string",b:"\\|",e:"\\$"},{cN:"variable",b:"<",e:">"},e.HCM,{cN:"string",b:'"""',e:'"""'},e.QSM]}}),hljs.registerLanguage("glsl",function(e){return{k:{keyword:"atomic_uint attribute bool break bvec2 bvec3 bvec4 case centroid coherent const continue default discard dmat2 dmat2x2 dmat2x3 dmat2x4 dmat3 dmat3x2 dmat3x3 dmat3x4 dmat4 dmat4x2 dmat4x3 dmat4x4 do double dvec2 dvec3 dvec4 else flat float for highp if iimage1D iimage1DArray iimage2D iimage2DArray iimage2DMS iimage2DMSArray iimage2DRect iimage3D iimageBuffer iimageCube iimageCubeArray image1D image1DArray image2D image2DArray image2DMS image2DMSArray image2DRect image3D imageBuffer imageCube imageCubeArray in inout int invariant isampler1D isampler1DArray isampler2D isampler2DArray isampler2DMS isampler2DMSArray isampler2DRect isampler3D isamplerBuffer isamplerCube isamplerCubeArray ivec2 ivec3 ivec4 layout lowp mat2 mat2x2 mat2x3 mat2x4 mat3 mat3x2 mat3x3 mat3x4 mat4 mat4x2 mat4x3 mat4x4 mediump noperspective out patch precision readonly restrict return sample sampler1D sampler1DArray sampler1DArrayShadow sampler1DShadow sampler2D sampler2DArray sampler2DArrayShadow sampler2DMS sampler2DMSArray sampler2DRect sampler2DRectShadow sampler2DShadow sampler3D samplerBuffer samplerCube samplerCubeArray samplerCubeArrayShadow samplerCubeShadow smooth struct subroutine switch uimage1D uimage1DArray uimage2D uimage2DArray uimage2DMS uimage2DMSArray uimage2DRect uimage3D uimageBuffer uimageCube uimageCubeArray uint uniform usampler1D usampler1DArray usampler2D usampler2DArray usampler2DMS usampler2DMSArray usampler2DRect usampler3D usamplerBuffer usamplerCube usamplerCubeArray uvec2 uvec3 uvec4 varying vec2 vec3 vec4 void volatile while writeonly",built_in:"gl_BackColor gl_BackLightModelProduct gl_BackLightProduct gl_BackMaterial gl_BackSecondaryColor gl_ClipDistance gl_ClipPlane gl_ClipVertex gl_Color gl_DepthRange gl_EyePlaneQ gl_EyePlaneR gl_EyePlaneS gl_EyePlaneT gl_Fog gl_FogCoord gl_FogFragCoord gl_FragColor gl_FragCoord gl_FragData gl_FragDepth gl_FrontColor gl_FrontFacing gl_FrontLightModelProduct gl_FrontLightProduct gl_FrontMaterial gl_FrontSecondaryColor gl_InstanceID gl_InvocationID gl_Layer gl_LightModel gl_LightSource gl_MaxAtomicCounterBindings gl_MaxAtomicCounterBufferSize gl_MaxClipDistances gl_MaxClipPlanes gl_MaxCombinedAtomicCounterBuffers gl_MaxCombinedAtomicCounters gl_MaxCombinedImageUniforms gl_MaxCombinedImageUnitsAndFragmentOutputs gl_MaxCombinedTextureImageUnits gl_MaxDrawBuffers gl_MaxFragmentAtomicCounterBuffers gl_MaxFragmentAtomicCounters gl_MaxFragmentImageUniforms gl_MaxFragmentInputComponents gl_MaxFragmentUniformComponents gl_MaxFragmentUniformVectors gl_MaxGeometryAtomicCounterBuffers gl_MaxGeometryAtomicCounters gl_MaxGeometryImageUniforms gl_MaxGeometryInputComponents gl_MaxGeometryOutputComponents gl_MaxGeometryOutputVertices gl_MaxGeometryTextureImageUnits gl_MaxGeometryTotalOutputComponents gl_MaxGeometryUniformComponents gl_MaxGeometryVaryingComponents gl_MaxImageSamples gl_MaxImageUnits gl_MaxLights gl_MaxPatchVertices gl_MaxProgramTexelOffset gl_MaxTessControlAtomicCounterBuffers gl_MaxTessControlAtomicCounters gl_MaxTessControlImageUniforms gl_MaxTessControlInputComponents gl_MaxTessControlOutputComponents gl_MaxTessControlTextureImageUnits gl_MaxTessControlTotalOutputComponents gl_MaxTessControlUniformComponents gl_MaxTessEvaluationAtomicCounterBuffers gl_MaxTessEvaluationAtomicCounters gl_MaxTessEvaluationImageUniforms gl_MaxTessEvaluationInputComponents gl_MaxTessEvaluationOutputComponents gl_MaxTessEvaluationTextureImageUnits gl_MaxTessEvaluationUniformComponents gl_MaxTessGenLevel gl_MaxTessPatchComponents gl_MaxTextureCoords gl_MaxTextureImageUnits gl_MaxTextureUnits gl_MaxVaryingComponents gl_MaxVaryingFloats gl_MaxVaryingVectors gl_MaxVertexAtomicCounterBuffers gl_MaxVertexAtomicCounters gl_MaxVertexAttribs gl_MaxVertexImageUniforms gl_MaxVertexOutputComponents gl_MaxVertexTextureImageUnits gl_MaxVertexUniformComponents gl_MaxVertexUniformVectors gl_MaxViewports gl_MinProgramTexelOffsetgl_ModelViewMatrix gl_ModelViewMatrixInverse gl_ModelViewMatrixInverseTranspose gl_ModelViewMatrixTranspose gl_ModelViewProjectionMatrix gl_ModelViewProjectionMatrixInverse gl_ModelViewProjectionMatrixInverseTranspose gl_ModelViewProjectionMatrixTranspose gl_MultiTexCoord0 gl_MultiTexCoord1 gl_MultiTexCoord2 gl_MultiTexCoord3 gl_MultiTexCoord4 gl_MultiTexCoord5 gl_MultiTexCoord6 gl_MultiTexCoord7 gl_Normal gl_NormalMatrix gl_NormalScale gl_ObjectPlaneQ gl_ObjectPlaneR gl_ObjectPlaneS gl_ObjectPlaneT gl_PatchVerticesIn gl_PerVertex gl_Point gl_PointCoord gl_PointSize gl_Position gl_PrimitiveID gl_PrimitiveIDIn gl_ProjectionMatrix gl_ProjectionMatrixInverse gl_ProjectionMatrixInverseTranspose gl_ProjectionMatrixTranspose gl_SampleID gl_SampleMask gl_SampleMaskIn gl_SamplePosition gl_SecondaryColor gl_TessCoord gl_TessLevelInner gl_TessLevelOuter gl_TexCoord gl_TextureEnvColor gl_TextureMatrixInverseTranspose gl_TextureMatrixTranspose gl_Vertex gl_VertexID gl_ViewportIndex gl_in gl_out EmitStreamVertex EmitVertex EndPrimitive EndStreamPrimitive abs acos acosh all any asin asinh atan atanh atomicCounter atomicCounterDecrement atomicCounterIncrement barrier bitCount bitfieldExtract bitfieldInsert bitfieldReverse ceil clamp cos cosh cross dFdx dFdy degrees determinant distance dot equal exp exp2 faceforward findLSB findMSB floatBitsToInt floatBitsToUint floor fma fract frexp ftransform fwidth greaterThan greaterThanEqual imageAtomicAdd imageAtomicAnd imageAtomicCompSwap imageAtomicExchange imageAtomicMax imageAtomicMin imageAtomicOr imageAtomicXor imageLoad imageStore imulExtended intBitsToFloat interpolateAtCentroid interpolateAtOffset interpolateAtSample inverse inversesqrt isinf isnan ldexp length lessThan lessThanEqual log log2 matrixCompMult max memoryBarrier min mix mod modf noise1 noise2 noise3 noise4 normalize not notEqual outerProduct packDouble2x32 packHalf2x16 packSnorm2x16 packSnorm4x8 packUnorm2x16 packUnorm4x8 pow radians reflect refract round roundEven shadow1D shadow1DLod shadow1DProj shadow1DProjLod shadow2D shadow2DLod shadow2DProj shadow2DProjLod sign sin sinh smoothstep sqrt step tan tanh texelFetch texelFetchOffset texture texture1D texture1DLod texture1DProj texture1DProjLod texture2D texture2DLod texture2DProj texture2DProjLod texture3D texture3DLod texture3DProj texture3DProjLod textureCube textureCubeLod textureGather textureGatherOffset textureGatherOffsets textureGrad textureGradOffset textureLod textureLodOffset textureOffset textureProj textureProjGrad textureProjGradOffset textureProjLod textureProjLodOffset textureProjOffset textureQueryLod textureSize transpose trunc uaddCarry uintBitsToFloat umulExtended unpackDouble2x32 unpackHalf2x16 unpackSnorm2x16 unpackSnorm4x8 unpackUnorm2x16 unpackUnorm4x8 usubBorrow gl_TextureMatrix gl_TextureMatrixInverse",literal:"true false"},i:'"',c:[e.CLCM,e.CBCM,e.CNM,{cN:"preprocessor",b:"#",e:"$"}]}}),hljs.registerLanguage("go",function(e){var t={keyword:"break default func interface select case map struct chan else goto package switch const fallthrough if range type continue for import return var go defer",constant:"true false iota nil",typename:"bool byte complex64 complex128 float32 float64 int8 int16 int32 int64 string uint8 uint16 uint32 uint64 int uint uintptr rune",built_in:"append cap close complex copy imag len make new panic print println real recover delete"};return{aliases:["golang"],k:t,i:"</",c:[e.CLCM,e.CBCM,e.QSM,{cN:"string",b:"'",e:"[^\\\\]'"},{cN:"string",b:"`",e:"`"},{cN:"number",b:e.CNR+"[dflsi]?",r:0},e.CNM]}}),hljs.registerLanguage("gradle",function(e){return{cI:!0,k:{keyword:"task project allprojects subprojects artifacts buildscript configurations dependencies repositories sourceSets description delete from into include exclude source classpath destinationDir includes options sourceCompatibility targetCompatibility group flatDir doLast doFirst flatten todir fromdir ant def abstract break case catch continue default do else extends final finally for if implements instanceof native new private protected public return static switch synchronized throw throws transient try volatile while strictfp package import false null super this true antlrtask checkstyle codenarc copy boolean byte char class double float int interface long short void compile runTime file fileTree abs any append asList asWritable call collect compareTo count div dump each eachByte eachFile eachLine every find findAll flatten getAt getErr getIn getOut getText grep immutable inject inspect intersect invokeMethods isCase join leftShift minus multiply newInputStream newOutputStream newPrintWriter newReader newWriter next plus pop power previous print println push putAt read readBytes readLines reverse reverseEach round size sort splitEachLine step subMap times toInteger toList tokenize upto waitForOrKill withPrintWriter withReader withStream withWriter withWriterAppend write writeLine"},c:[e.CLCM,e.CBCM,e.ASM,e.QSM,e.NM,e.RM]}}),hljs.registerLanguage("groovy",function(e){return{k:{typename:"byte short char int long boolean float double void",literal:"true false null",keyword:"def as in assert trait super this abstract static volatile transient public private protected synchronized final class interface enum if else for while switch case break default continue throw throws try catch finally implements extends new import package return instanceof"},c:[e.CLCM,{cN:"javadoc",b:"/\\*\\*",e:"\\*//*",r:0,c:[{
cN:"javadoctag",b:"(^|\\s)@[A-Za-z]+"}]},e.CBCM,{cN:"string",b:'"""',e:'"""'},{cN:"string",b:"'''",e:"'''"},{cN:"string",b:"\\$/",e:"/\\$",r:10},e.ASM,{cN:"regexp",b:/~?\/[^\/\n]+\//,c:[e.BE]},e.QSM,{cN:"shebang",b:"^#!/usr/bin/env",e:"$",i:"\n"},e.BNM,{cN:"class",bK:"class interface trait enum",e:"{",i:":",c:[{bK:"extends implements"},e.UTM]},e.CNM,{cN:"annotation",b:"@[A-Za-z]+"},{cN:"string",b:/[^\?]{0}[A-Za-z0-9_$]+ *:/},{b:/\?/,e:/\:/},{cN:"label",b:"^\\s*[A-Za-z0-9_$]+:",r:0}]}}),hljs.registerLanguage("haml",function(e){return{cI:!0,c:[{cN:"doctype",b:"^!!!( (5|1\\.1|Strict|Frameset|Basic|Mobile|RDFa|XML\\b.*))?$",r:10},e.C("^\\s*(!=#|=#|-#|/).*$",!1,{r:0}),{b:"^\\s*(-|=|!=)(?!#)",starts:{e:"\\n",sL:"ruby"}},{cN:"tag",b:"^\\s*%",c:[{cN:"title",b:"\\w+"},{cN:"value",b:"[#\\.]\\w+"},{b:"{\\s*",e:"\\s*}",eE:!0,c:[{b:":\\w+\\s*=>",e:",\\s+",rB:!0,eW:!0,c:[{cN:"symbol",b:":\\w+"},{cN:"string",b:'"',e:'"'},{cN:"string",b:"'",e:"'"},{b:"\\w+",r:0}]}]},{b:"\\(\\s*",e:"\\s*\\)",eE:!0,c:[{b:"\\w+\\s*=",e:"\\s+",rB:!0,eW:!0,c:[{cN:"attribute",b:"\\w+",r:0},{cN:"string",b:'"',e:'"'},{cN:"string",b:"'",e:"'"},{b:"\\w+",r:0}]}]}]},{cN:"bullet",b:"^\\s*[=~]\\s*",r:0},{b:"#{",starts:{e:"}",sL:"ruby"}}]}}),hljs.registerLanguage("handlebars",function(e){var t="each in with if else unless bindattr action collection debugger log outlet template unbound view yield";return{aliases:["hbs","html.hbs","html.handlebars"],cI:!0,sL:"xml",subLanguageMode:"continuous",c:[{cN:"expression",b:"{{",e:"}}",c:[{cN:"begin-block",b:"#[a-zA-Z- .]+",k:t},{cN:"string",b:'"',e:'"'},{cN:"end-block",b:"\\/[a-zA-Z- .]+",k:t},{cN:"variable",b:"[a-zA-Z-.]+",k:t}]}]}}),hljs.registerLanguage("haskell",function(e){var t=[e.C("--","$"),e.C("{-","-}",{c:["self"]})],r={cN:"pragma",b:"{-#",e:"#-}"},a={cN:"preprocessor",b:"^#",e:"$"},i={cN:"type",b:"\\b[A-Z][\\w']*",r:0},n={cN:"container",b:"\\(",e:"\\)",i:'"',c:[r,a,{cN:"type",b:"\\b[A-Z][\\w]*(\\((\\.\\.|,|\\w+)\\))?"},e.inherit(e.TM,{b:"[_a-z][\\w']*"})].concat(t)},o={cN:"container",b:"{",e:"}",c:n.c};return{aliases:["hs"],k:"let in if then else case of where do module import hiding qualified type data newtype deriving class instance as default infix infixl infixr foreign export ccall stdcall cplusplus jvm dotnet safe unsafe family forall mdo proc rec",c:[{cN:"module",b:"\\bmodule\\b",e:"where",k:"module where",c:[n].concat(t),i:"\\W\\.|;"},{cN:"import",b:"\\bimport\\b",e:"$",k:"import|0 qualified as hiding",c:[n].concat(t),i:"\\W\\.|;"},{cN:"class",b:"^(\\s*)?(class|instance)\\b",e:"where",k:"class family instance where",c:[i,n].concat(t)},{cN:"typedef",b:"\\b(data|(new)?type)\\b",e:"$",k:"data family type newtype deriving",c:[r,i,n,o].concat(t)},{cN:"default",bK:"default",e:"$",c:[i,n].concat(t)},{cN:"infix",bK:"infix infixl infixr",e:"$",c:[e.CNM].concat(t)},{cN:"foreign",b:"\\bforeign\\b",e:"$",k:"foreign import export ccall stdcall cplusplus jvm dotnet safe unsafe",c:[i,e.QSM].concat(t)},{cN:"shebang",b:"#!\\/usr\\/bin\\/env runhaskell",e:"$"},r,a,e.QSM,e.CNM,i,e.inherit(e.TM,{b:"^[_a-z][\\w']*"}),{b:"->|<-"}].concat(t)}}),hljs.registerLanguage("haxe",function(e){var t="([*]|[a-zA-Z_$][a-zA-Z0-9_$]*)";return{aliases:["hx"],k:{keyword:"break callback case cast catch class continue default do dynamic else enum extends extern for function here if implements import in inline interface never new override package private public return static super switch this throw trace try typedef untyped using var while",literal:"true false null"},c:[e.ASM,e.QSM,e.CLCM,e.CBCM,e.CNM,{cN:"class",bK:"class interface",e:"{",eE:!0,c:[{bK:"extends implements"},e.TM]},{cN:"preprocessor",b:"#",e:"$",k:"if else elseif end error"},{cN:"function",bK:"function",e:"[{;]",eE:!0,i:"\\S",c:[e.TM,{cN:"params",b:"\\(",e:"\\)",c:[e.ASM,e.QSM,e.CLCM,e.CBCM]},{cN:"type",b:":",e:t,r:10}]}]}}),hljs.registerLanguage("http",function(e){return{aliases:["https"],i:"\\S",c:[{cN:"status",b:"^HTTP/[0-9\\.]+",e:"$",c:[{cN:"number",b:"\\b\\d{3}\\b"}]},{cN:"request",b:"^[A-Z]+ (.*?) HTTP/[0-9\\.]+$",rB:!0,e:"$",c:[{cN:"string",b:" ",e:" ",eB:!0,eE:!0}]},{cN:"attribute",b:"^\\w",e:": ",eE:!0,i:"\\n|\\s|=",starts:{cN:"string",e:"$"}},{b:"\\n\\n",starts:{sL:"",eW:!0}}]}}),hljs.registerLanguage("ini",function(e){return{cI:!0,i:/\S/,c:[e.C(";","$"),{cN:"title",b:"^\\[",e:"\\]"},{cN:"setting",b:"^[a-z0-9\\[\\]_-]+[ \\t]*=[ \\t]*",e:"$",c:[{cN:"value",eW:!0,k:"on off true false yes no",c:[e.QSM,e.NM],r:0}]}]}}),hljs.registerLanguage("java",function(e){var t=e.UIR+"(<"+e.UIR+">)?",r="false synchronized int abstract float private char boolean static null if const for true while long strictfp finally protected import native final void enum else break transient catch instanceof byte super volatile case assert short package default double public try this switch continue throws protected public private",a="(\\b(0b[01_]+)|\\b0[xX][a-fA-F0-9_]+|(\\b[\\d_]+(\\.[\\d_]*)?|\\.[\\d_]+)([eE][-+]?\\d+)?)[lLfF]?",i={cN:"number",b:a,r:0};return{aliases:["jsp"],k:r,i:/<\//,c:[{cN:"javadoc",b:"/\\*\\*",e:"\\*/",r:0,c:[{cN:"javadoctag",b:"(^|\\s)@[A-Za-z]+"}]},e.CLCM,e.CBCM,e.ASM,e.QSM,{cN:"class",bK:"class interface",e:/[{;=]/,eE:!0,k:"class interface",i:/[:"\[\]]/,c:[{bK:"extends implements"},e.UTM]},{bK:"new throw return",r:0},{cN:"function",b:"("+t+"\\s+)+"+e.UIR+"\\s*\\(",rB:!0,e:/[{;=]/,eE:!0,k:r,c:[{b:e.UIR+"\\s*\\(",rB:!0,r:0,c:[e.UTM]},{cN:"params",b:/\(/,e:/\)/,k:r,r:0,c:[e.ASM,e.QSM,e.CNM,e.CBCM]},e.CLCM,e.CBCM]},i,{cN:"annotation",b:"@[A-Za-z]+"}]}}),hljs.registerLanguage("javascript",function(e){return{aliases:["js"],k:{keyword:"in of if for while finally var new function do return void else break catch instanceof with throw case default try this switch continue typeof delete let yield const export super debugger as await",literal:"true false null undefined NaN Infinity",built_in:"eval isFinite isNaN parseFloat parseInt decodeURI decodeURIComponent encodeURI encodeURIComponent escape unescape Object Function Boolean Error EvalError InternalError RangeError ReferenceError StopIteration SyntaxError TypeError URIError Number Math Date String RegExp Array Float32Array Float64Array Int16Array Int32Array Int8Array Uint16Array Uint32Array Uint8Array Uint8ClampedArray ArrayBuffer DataView JSON Intl arguments require module console window document Symbol Set Map WeakSet WeakMap Proxy Reflect Promise"},c:[{cN:"pi",r:10,v:[{b:/^\s*('|")use strict('|")/},{b:/^\s*('|")use asm('|")/}]},e.ASM,e.QSM,{cN:"string",b:"`",e:"`",c:[e.BE,{cN:"subst",b:"\\$\\{",e:"\\}"}]},e.CLCM,e.CBCM,{cN:"number",b:"\\b(0[xXbBoO][a-fA-F0-9]+|(\\d+(\\.\\d*)?|\\.\\d+)([eE][-+]?\\d+)?)",r:0},{b:"("+e.RSR+"|\\b(case|return|throw)\\b)\\s*",k:"return throw case",c:[e.CLCM,e.CBCM,e.RM,{b:/</,e:/>\s*[);\]]/,r:0,sL:"xml"}],r:0},{cN:"function",bK:"function",e:/\{/,eE:!0,c:[e.inherit(e.TM,{b:/[A-Za-z$_][0-9A-Za-z$_]*/}),{cN:"params",b:/\(/,e:/\)/,c:[e.CLCM,e.CBCM],i:/["'\(]/}],i:/\[|%/},{b:/\$[(.]/},{b:"\\."+e.IR,r:0},{bK:"import",e:"[;$]",k:"import from as",c:[e.ASM,e.QSM]},{cN:"class",bK:"class",e:/[{;=]/,eE:!0,i:/[:"\[\]]/,c:[{bK:"extends"},e.UTM]}]}}),hljs.registerLanguage("json",function(e){var t={literal:"true false null"},r=[e.QSM,e.CNM],a={cN:"value",e:",",eW:!0,eE:!0,c:r,k:t},i={b:"{",e:"}",c:[{cN:"attribute",b:'\\s*"',e:'"\\s*:\\s*',eB:!0,eE:!0,c:[e.BE],i:"\\n",starts:a}],i:"\\S"},n={b:"\\[",e:"\\]",c:[e.inherit(a,{cN:null})],i:"\\S"};return r.splice(r.length,0,i,n),{c:r,k:t,i:"\\S"}}),hljs.registerLanguage("julia",function(e){var t={keyword:"in abstract baremodule begin bitstype break catch ccall const continue do else elseif end export finally for function global if immutable import importall let local macro module quote return try type typealias using while",literal:"true false ANY ARGS CPU_CORES C_NULL DL_LOAD_PATH DevNull ENDIAN_BOM ENV I|0 Inf Inf16 Inf32 InsertionSort JULIA_HOME LOAD_PATH MS_ASYNC MS_INVALIDATE MS_SYNC MergeSort NaN NaN16 NaN32 OS_NAME QuickSort RTLD_DEEPBIND RTLD_FIRST RTLD_GLOBAL RTLD_LAZY RTLD_LOCAL RTLD_NODELETE RTLD_NOLOAD RTLD_NOW RoundDown RoundFromZero RoundNearest RoundToZero RoundUp STDERR STDIN STDOUT VERSION WORD_SIZE catalan cglobal e eu eulergamma golden im nothing pi γ π φ",built_in:"ASCIIString AbstractArray AbstractRNG AbstractSparseArray Any ArgumentError Array Associative Base64Pipe Bidiagonal BigFloat BigInt BitArray BitMatrix BitVector Bool BoundsError Box CFILE Cchar Cdouble Cfloat Char CharString Cint Clong Clonglong ClusterManager Cmd Coff_t Colon Complex Complex128 Complex32 Complex64 Condition Cptrdiff_t Cshort Csize_t Cssize_t Cuchar Cuint Culong Culonglong Cushort Cwchar_t DArray DataType DenseArray Diagonal Dict DimensionMismatch DirectIndexString Display DivideError DomainError EOFError EachLine Enumerate ErrorException Exception Expr Factorization FileMonitor FileOffset Filter Float16 Float32 Float64 FloatRange FloatingPoint Function GetfieldNode GotoNode Hermitian IO IOBuffer IOStream IPv4 IPv6 InexactError Int Int128 Int16 Int32 Int64 Int8 IntSet Integer InterruptException IntrinsicFunction KeyError LabelNode LambdaStaticData LineNumberNode LoadError LocalProcess MIME MathConst MemoryError MersenneTwister Method MethodError MethodTable Module NTuple NewvarNode Nothing Number ObjectIdDict OrdinalRange OverflowError ParseError PollingFileWatcher ProcessExitedException ProcessGroup Ptr QuoteNode Range Range1 Ranges Rational RawFD Real Regex RegexMatch RemoteRef RepString RevString RopeString RoundingMode Set SharedArray Signed SparseMatrixCSC StackOverflowError Stat StatStruct StepRange String SubArray SubString SymTridiagonal Symbol SymbolNode Symmetric SystemError Task TextDisplay Timer TmStruct TopNode Triangular Tridiagonal Type TypeConstructor TypeError TypeName TypeVar UTF16String UTF32String UTF8String UdpSocket Uint Uint128 Uint16 Uint32 Uint64 Uint8 UndefRefError UndefVarError UniformScaling UnionType UnitRange Unsigned Vararg VersionNumber WString WeakKeyDict WeakRef Woodbury Zip"},r="[A-Za-z_\\u00A1-\\uFFFF][A-Za-z_0-9\\u00A1-\\uFFFF]*",a={l:r,k:t},i={cN:"type-annotation",b:/::/},n={cN:"subtype",b:/<:/},o={cN:"number",b:/(\b0x[\d_]*(\.[\d_]*)?|0x\.\d[\d_]*)p[-+]?\d+|\b0[box][a-fA-F0-9][a-fA-F0-9_]*|(\b\d[\d_]*(\.[\d_]*)?|\.\d[\d_]*)([eEfF][-+]?\d+)?/,r:0},s={cN:"char",b:/'(.|\\[xXuU][a-zA-Z0-9]+)'/},l={cN:"subst",b:/\$\(/,e:/\)/,k:t},c={cN:"variable",b:"\\$"+r},d={cN:"string",c:[e.BE,l,c],v:[{b:/\w*"/,e:/"\w*/},{b:/\w*"""/,e:/"""\w*/}]},p={cN:"string",c:[e.BE,l,c],b:"`",e:"`"},u={cN:"macrocall",b:"@"+r},m={cN:"comment",v:[{b:"#=",e:"=#",r:10},{b:"#",e:"$"}]};return a.c=[o,s,i,n,d,p,u,m,e.HCM],l.c=a.c,a}),hljs.registerLanguage("kotlin",function(e){var t="val var get set class trait object public open private protected final enum if else do while for when break continue throw try catch finally import package is as in return fun override default companion reified inline volatile transient native";return{k:{typename:"Byte Short Char Int Long Boolean Float Double Void Unit Nothing",literal:"true false null",keyword:t},c:[e.CLCM,{cN:"javadoc",b:"/\\*\\*",e:"\\*//*",r:0,c:[{cN:"javadoctag",b:"(^|\\s)@[A-Za-z]+"}]},e.CBCM,{cN:"type",b:/</,e:/>/,rB:!0,eE:!1,r:0},{cN:"function",bK:"fun",e:"[(]|$",rB:!0,eE:!0,k:t,i:/fun\s+(<.*>)?[^\s\(]+(\s+[^\s\(]+)\s*=/,r:5,c:[{b:e.UIR+"\\s*\\(",rB:!0,r:0,c:[e.UTM]},{cN:"type",b:/</,e:/>/,k:"reified",r:0},{cN:"params",b:/\(/,e:/\)/,k:t,r:0,i:/\([^\(,\s:]+,/,c:[{cN:"typename",b:/:\s*/,e:/\s*[=\)]/,eB:!0,rE:!0,r:0}]},e.CLCM,e.CBCM]},{cN:"class",bK:"class trait",e:/[:\{(]|$/,eE:!0,i:"extends implements",c:[e.UTM,{cN:"type",b:/</,e:/>/,eB:!0,eE:!0,r:0},{cN:"typename",b:/[,:]\s*/,e:/[<\(,]|$/,eB:!0,rE:!0}]},{cN:"variable",bK:"var val",e:/\s*[=:$]/,eE:!0},e.QSM,{cN:"shebang",b:"^#!/usr/bin/env",e:"$",i:"\n"},e.CNM]}}),hljs.registerLanguage("lasso",function(e){var t="[a-zA-Z_][a-zA-Z0-9_.]*",r="<\\?(lasso(script)?|=)",a="\\]|\\?>",i={literal:"true false none minimal full all void and or not bw nbw ew new cn ncn lt lte gt gte eq neq rx nrx ft",built_in:"array date decimal duration integer map pair string tag xml null boolean bytes keyword list locale queue set stack staticarray local var variable global data self inherited",keyword:"error_code error_msg error_pop error_push error_reset cache database_names database_schemanames database_tablenames define_tag define_type email_batch encode_set html_comment handle handle_error header if inline iterate ljax_target link link_currentaction link_currentgroup link_currentrecord link_detail link_firstgroup link_firstrecord link_lastgroup link_lastrecord link_nextgroup link_nextrecord link_prevgroup link_prevrecord log loop namespace_using output_none portal private protect records referer referrer repeating resultset rows search_args search_arguments select sort_args sort_arguments thread_atomic value_list while abort case else if_empty if_false if_null if_true loop_abort loop_continue loop_count params params_up return return_value run_children soap_definetag soap_lastrequest soap_lastresponse tag_name ascending average by define descending do equals frozen group handle_failure import in into join let match max min on order parent protected provide public require returnhome skip split_thread sum take thread to trait type where with yield yieldhome"},n=e.C("<!--","-->",{r:0}),o={cN:"preprocessor",b:"\\[noprocess\\]",starts:{cN:"markup",e:"\\[/noprocess\\]",rE:!0,c:[n]}},s={cN:"preprocessor",b:"\\[/noprocess|"+r},l={cN:"variable",b:"'"+t+"'"},c=[e.CLCM,{cN:"javadoc",b:"/\\*\\*!",e:"\\*/",c:[e.PWM]},e.CBCM,e.inherit(e.CNM,{b:e.CNR+"|(-?infinity|nan)\\b"}),e.inherit(e.ASM,{i:null}),e.inherit(e.QSM,{i:null}),{cN:"string",b:"`",e:"`"},{cN:"variable",v:[{b:"[#$]"+t},{b:"#",e:"\\d+",i:"\\W"}]},{cN:"tag",b:"::\\s*",e:t,i:"\\W"},{cN:"attribute",v:[{b:"-"+e.UIR,r:0},{b:"(\\.\\.\\.)"}]},{cN:"subst",v:[{b:"->\\s*",c:[l]},{b:":=|/(?!\\w)=?|[-+*%=<>&|!?\\\\]+",r:0}]},{cN:"built_in",b:"\\.\\.?\\s*",r:0,c:[l]},{cN:"class",bK:"define",rE:!0,e:"\\(|=>",c:[e.inherit(e.TM,{b:e.UIR+"(=(?!>))?"})]}];return{aliases:["ls","lassoscript"],cI:!0,l:t+"|&[lg]t;",k:i,c:[{cN:"preprocessor",b:a,r:0,starts:{cN:"markup",e:"\\[|"+r,rE:!0,r:0,c:[n]}},o,s,{cN:"preprocessor",b:"\\[no_square_brackets",starts:{e:"\\[/no_square_brackets\\]",l:t+"|&[lg]t;",k:i,c:[{cN:"preprocessor",b:a,r:0,starts:{cN:"markup",e:"\\[noprocess\\]|"+r,rE:!0,c:[n]}},o,s].concat(c)}},{cN:"preprocessor",b:"\\[",r:0},{cN:"shebang",b:"^#!.+lasso9\\b",r:10}].concat(c)}}),hljs.registerLanguage("less",function(e){var t="[\\w-]+",r="("+t+"|@{"+t+"})",a=[],i=[],n=function(e){return{cN:"string",b:"~?"+e+".*?"+e}},o=function(e,t,r){return{cN:e,b:t,r:r}},s=function(t,r,a){return e.inherit({cN:t,b:r+"\\(",e:"\\(",rB:!0,eE:!0,r:0},a)},l={b:"\\(",e:"\\)",c:i,r:0};i.push(e.CLCM,e.CBCM,n("'"),n('"'),e.CSSNM,o("hexcolor","#[0-9A-Fa-f]+\\b"),s("function","(url|data-uri)",{starts:{cN:"string",e:"[\\)\\n]",eE:!0}}),s("function",t),l,o("variable","@@?"+t,10),o("variable","@{"+t+"}"),o("built_in","~?`[^`]*?`"),{cN:"attribute",b:t+"\\s*:",e:":",rB:!0,eE:!0});var c=i.concat({b:"{",e:"}",c:a}),d={bK:"when",eW:!0,c:[{bK:"and not"}].concat(i)},p={cN:"attribute",b:r,e:":",eE:!0,c:[e.CLCM,e.CBCM],i:/\S/,starts:{e:"[;}]",rE:!0,c:i,i:"[<=$]"}},u={cN:"at_rule",b:"@(import|media|charset|font-face|(-[a-z]+-)?keyframes|supports|document|namespace|page|viewport|host)\\b",starts:{e:"[;{}]",rE:!0,c:i,r:0}},m={cN:"variable",v:[{b:"@"+t+"\\s*:",r:15},{b:"@"+t}],starts:{e:"[;}]",rE:!0,c:c}},b={v:[{b:"[\\.#:&\\[]",e:"[;{}]"},{b:r+"[^;]*{",e:"{"}],rB:!0,rE:!0,i:"[<='$\"]",c:[e.CLCM,e.CBCM,d,o("keyword","all\\b"),o("variable","@{"+t+"}"),o("tag",r+"%?",0),o("id","#"+r),o("class","\\."+r,0),o("keyword","&",0),s("pseudo",":not"),s("keyword",":extend"),o("pseudo","::?"+r),{cN:"attr_selector",b:"\\[",e:"\\]"},{b:"\\(",e:"\\)",c:c},{b:"!important"}]};return a.push(e.CLCM,e.CBCM,u,m,b,p),{cI:!0,i:"[=>'/<($\"]",c:a}}),hljs.registerLanguage("lisp",function(e){var t="[a-zA-Z_\\-\\+\\*\\/\\<\\=\\>\\&\\#][a-zA-Z0-9_\\-\\+\\*\\/\\<\\=\\>\\&\\#!]*",r="\\|[^]*?\\|",a="(\\-|\\+)?\\d+(\\.\\d+|\\/\\d+)?((d|e|f|l|s|D|E|F|L|S)(\\+|\\-)?\\d+)?",i={cN:"shebang",b:"^#!",e:"$"},n={cN:"literal",b:"\\b(t{1}|nil)\\b"},o={cN:"number",v:[{b:a,r:0},{b:"#(b|B)[0-1]+(/[0-1]+)?"},{b:"#(o|O)[0-7]+(/[0-7]+)?"},{b:"#(x|X)[0-9a-fA-F]+(/[0-9a-fA-F]+)?"},{b:"#(c|C)\\("+a+" +"+a,e:"\\)"}]},s=e.inherit(e.QSM,{i:null}),l=e.C(";","$",{r:0}),c={cN:"variable",b:"\\*",e:"\\*"},d={cN:"keyword",b:"[:&]"+t},p={b:t,r:0},u={b:r},m={b:"\\(",e:"\\)",c:["self",n,s,o,p]},b={cN:"quoted",c:[o,s,c,d,m,p],v:[{b:"['`]\\(",e:"\\)"},{b:"\\(quote ",e:"\\)",k:"quote"},{b:"'"+r}]},g={cN:"quoted",v:[{b:"'"+t},{b:"#'"+t+"(::"+t+")*"}]},f={cN:"list",b:"\\(\\s*",e:"\\)"},h={eW:!0,r:0};return f.c=[{cN:"keyword",v:[{b:t},{b:r}]},h],h.c=[b,g,f,n,o,s,l,c,d,u,p],{i:/\S/,c:[o,i,n,s,l,b,g,f,p]}}),hljs.registerLanguage("livecodeserver",function(e){var t={cN:"variable",b:"\\b[gtps][A-Z]+[A-Za-z0-9_\\-]*\\b|\\$_[A-Z]+",r:0},r=[e.CBCM,e.HCM,e.C("--","$"),e.C("[^:]//","$")],a=e.inherit(e.TM,{v:[{b:"\\b_*rig[A-Z]+[A-Za-z0-9_\\-]*"},{b:"\\b_[a-z0-9\\-]+"}]}),i=e.inherit(e.TM,{b:"\\b([A-Za-z0-9_\\-]+)\\b"});return{cI:!1,k:{keyword:"$_COOKIE $_FILES $_GET $_GET_BINARY $_GET_RAW $_POST $_POST_BINARY $_POST_RAW $_SESSION $_SERVER codepoint codepoints segment segments codeunit codeunits sentence sentences trueWord trueWords paragraph after byte bytes english the until http forever descending using line real8 with seventh for stdout finally element word words fourth before black ninth sixth characters chars stderr uInt1 uInt1s uInt2 uInt2s stdin string lines relative rel any fifth items from middle mid at else of catch then third it file milliseconds seconds second secs sec int1 int1s int4 int4s internet int2 int2s normal text item last long detailed effective uInt4 uInt4s repeat end repeat URL in try into switch to words https token binfile each tenth as ticks tick system real4 by dateItems without char character ascending eighth whole dateTime numeric short first ftp integer abbreviated abbr abbrev private case while if",constant:"SIX TEN FORMFEED NINE ZERO NONE SPACE FOUR FALSE COLON CRLF PI COMMA ENDOFFILE EOF EIGHT FIVE QUOTE EMPTY ONE TRUE RETURN CR LINEFEED RIGHT BACKSLASH NULL SEVEN TAB THREE TWO six ten formfeed nine zero none space four false colon crlf pi comma endoffile eof eight five quote empty one true return cr linefeed right backslash null seven tab three two RIVERSION RISTATE FILE_READ_MODE FILE_WRITE_MODE FILE_WRITE_MODE DIR_WRITE_MODE FILE_READ_UMASK FILE_WRITE_UMASK DIR_READ_UMASK DIR_WRITE_UMASK",operator:"div mod wrap and or bitAnd bitNot bitOr bitXor among not in a an within contains ends with begins the keys of keys",built_in:"put abs acos aliasReference annuity arrayDecode arrayEncode asin atan atan2 average avg avgDev base64Decode base64Encode baseConvert binaryDecode binaryEncode byteOffset byteToNum cachedURL cachedURLs charToNum cipherNames codepointOffset codepointProperty codepointToNum codeunitOffset commandNames compound compress constantNames cos date dateFormat decompress directories diskSpace DNSServers exp exp1 exp2 exp10 extents files flushEvents folders format functionNames geometricMean global globals hasMemory harmonicMean hostAddress hostAddressToName hostName hostNameToAddress isNumber ISOToMac itemOffset keys len length libURLErrorData libUrlFormData libURLftpCommand libURLLastHTTPHeaders libURLLastRHHeaders libUrlMultipartFormAddPart libUrlMultipartFormData libURLVersion lineOffset ln ln1 localNames log log2 log10 longFilePath lower macToISO matchChunk matchText matrixMultiply max md5Digest median merge millisec millisecs millisecond milliseconds min monthNames nativeCharToNum normalizeText num number numToByte numToChar numToCodepoint numToNativeChar offset open openfiles openProcesses openProcessIDs openSockets paragraphOffset paramCount param params peerAddress pendingMessages platform popStdDev populationStandardDeviation populationVariance popVariance processID random randomBytes replaceText result revCreateXMLTree revCreateXMLTreeFromFile revCurrentRecord revCurrentRecordIsFirst revCurrentRecordIsLast revDatabaseColumnCount revDatabaseColumnIsNull revDatabaseColumnLengths revDatabaseColumnNames revDatabaseColumnNamed revDatabaseColumnNumbered revDatabaseColumnTypes revDatabaseConnectResult revDatabaseCursors revDatabaseID revDatabaseTableNames revDatabaseType revDataFromQuery revdb_closeCursor revdb_columnbynumber revdb_columncount revdb_columnisnull revdb_columnlengths revdb_columnnames revdb_columntypes revdb_commit revdb_connect revdb_connections revdb_connectionerr revdb_currentrecord revdb_cursorconnection revdb_cursorerr revdb_cursors revdb_dbtype revdb_disconnect revdb_execute revdb_iseof revdb_isbof revdb_movefirst revdb_movelast revdb_movenext revdb_moveprev revdb_query revdb_querylist revdb_recordcount revdb_rollback revdb_tablenames revGetDatabaseDriverPath revNumberOfRecords revOpenDatabase revOpenDatabases revQueryDatabase revQueryDatabaseBlob revQueryResult revQueryIsAtStart revQueryIsAtEnd revUnixFromMacPath revXMLAttribute revXMLAttributes revXMLAttributeValues revXMLChildContents revXMLChildNames revXMLCreateTreeFromFileWithNamespaces revXMLCreateTreeWithNamespaces revXMLDataFromXPathQuery revXMLEvaluateXPath revXMLFirstChild revXMLMatchingNode revXMLNextSibling revXMLNodeContents revXMLNumberOfChildren revXMLParent revXMLPreviousSibling revXMLRootNode revXMLRPC_CreateRequest revXMLRPC_Documents revXMLRPC_Error revXMLRPC_GetHost revXMLRPC_GetMethod revXMLRPC_GetParam revXMLText revXMLRPC_Execute revXMLRPC_GetParamCount revXMLRPC_GetParamNode revXMLRPC_GetParamType revXMLRPC_GetPath revXMLRPC_GetPort revXMLRPC_GetProtocol revXMLRPC_GetRequest revXMLRPC_GetResponse revXMLRPC_GetSocket revXMLTree revXMLTrees revXMLValidateDTD revZipDescribeItem revZipEnumerateItems revZipOpenArchives round sampVariance sec secs seconds sentenceOffset sha1Digest shell shortFilePath sin specialFolderPath sqrt standardDeviation statRound stdDev sum sysError systemVersion tan tempName textDecode textEncode tick ticks time to tokenOffset toLower toUpper transpose truewordOffset trunc uniDecode uniEncode upper URLDecode URLEncode URLStatus uuid value variableNames variance version waitDepth weekdayNames wordOffset xsltApplyStylesheet xsltApplyStylesheetFromFile xsltLoadStylesheet xsltLoadStylesheetFromFile add breakpoint cancel clear local variable file word line folder directory URL close socket process combine constant convert create new alias folder directory decrypt delete variable word line folder directory URL dispatch divide do encrypt filter get include intersect kill libURLDownloadToFile libURLFollowHttpRedirects libURLftpUpload libURLftpUploadFile libURLresetAll libUrlSetAuthCallback libURLSetCustomHTTPHeaders libUrlSetExpect100 libURLSetFTPListCommand libURLSetFTPMode libURLSetFTPStopTime libURLSetStatusCallback load multiply socket prepare process post seek rel relative read from process rename replace require resetAll resolve revAddXMLNode revAppendXML revCloseCursor revCloseDatabase revCommitDatabase revCopyFile revCopyFolder revCopyXMLNode revDeleteFolder revDeleteXMLNode revDeleteAllXMLTrees revDeleteXMLTree revExecuteSQL revGoURL revInsertXMLNode revMoveFolder revMoveToFirstRecord revMoveToLastRecord revMoveToNextRecord revMoveToPreviousRecord revMoveToRecord revMoveXMLNode revPutIntoXMLNode revRollBackDatabase revSetDatabaseDriverPath revSetXMLAttribute revXMLRPC_AddParam revXMLRPC_DeleteAllDocuments revXMLAddDTD revXMLRPC_Free revXMLRPC_FreeAll revXMLRPC_DeleteDocument revXMLRPC_DeleteParam revXMLRPC_SetHost revXMLRPC_SetMethod revXMLRPC_SetPort revXMLRPC_SetProtocol revXMLRPC_SetSocket revZipAddItemWithData revZipAddItemWithFile revZipAddUncompressedItemWithData revZipAddUncompressedItemWithFile revZipCancel revZipCloseArchive revZipDeleteItem revZipExtractItemToFile revZipExtractItemToVariable revZipSetProgressCallback revZipRenameItem revZipReplaceItemWithData revZipReplaceItemWithFile revZipOpenArchive send set sort split start stop subtract union unload wait write"},c:[t,{cN:"keyword",b:"\\bend\\sif\\b"},{cN:"function",bK:"function",e:"$",c:[t,i,e.ASM,e.QSM,e.BNM,e.CNM,a]},{cN:"function",bK:"end",e:"$",c:[i,a]},{cN:"command",bK:"command on",e:"$",c:[t,i,e.ASM,e.QSM,e.BNM,e.CNM,a]},{cN:"command",bK:"end",e:"$",c:[i,a]},{cN:"preprocessor",b:"<\\?rev|<\\?lc|<\\?livecode",r:10},{cN:"preprocessor",b:"<\\?"},{cN:"preprocessor",b:"\\?>"},e.ASM,e.QSM,e.BNM,e.CNM,a].concat(r),i:";$|^\\[|^="}}),hljs.registerLanguage("livescript",function(e){var t={keyword:"in if for while finally new do return else break catch instanceof throw try this switch continue typeof delete debugger case default function var with then unless until loop of by when and or is isnt not it that otherwise from to til fallthrough super case default function var void const let enum export import native __hasProp __extends __slice __bind __indexOf",literal:"true false null undefined yes no on off it that void",built_in:"npm require console print module global window document"},r="[A-Za-z$_](?:-[0-9A-Za-z$_]|[0-9A-Za-z$_])*",a=e.inherit(e.TM,{b:r}),i={cN:"subst",b:/#\{/,e:/}/,k:t},n={cN:"subst",b:/#[A-Za-z$_]/,e:/(?:\-[0-9A-Za-z$_]|[0-9A-Za-z$_])*/,k:t},o=[e.BNM,{cN:"number",b:"(\\b0[xX][a-fA-F0-9_]+)|(\\b\\d(\\d|_\\d)*(\\.(\\d(\\d|_\\d)*)?)?(_*[eE]([-+]\\d(_\\d|\\d)*)?)?[_a-z]*)",r:0,starts:{e:"(\\s*/)?",r:0}},{cN:"string",v:[{b:/'''/,e:/'''/,c:[e.BE]},{b:/'/,e:/'/,c:[e.BE]},{b:/"""/,e:/"""/,c:[e.BE,i,n]},{b:/"/,e:/"/,c:[e.BE,i,n]},{b:/\\/,e:/(\s|$)/,eE:!0}]},{cN:"pi",v:[{b:"//",e:"//[gim]*",c:[i,e.HCM]},{b:/\/(?![ *])(\\\/|.)*?\/[gim]*(?=\W|$)/}]},{cN:"property",b:"@"+r},{b:"``",e:"``",eB:!0,eE:!0,sL:"javascript"}];i.c=o;var s={cN:"params",b:"\\(",rB:!0,c:[{b:/\(/,e:/\)/,k:t,c:["self"].concat(o)}]};return{aliases:["ls"],k:t,i:/\/\*/,c:o.concat([e.C("\\/\\*","\\*\\/"),e.HCM,{cN:"function",c:[a,s],rB:!0,v:[{b:"("+r+"\\s*(?:=|:=)\\s*)?(\\(.*\\))?\\s*\\B\\->\\*?",e:"\\->\\*?"},{b:"("+r+"\\s*(?:=|:=)\\s*)?!?(\\(.*\\))?\\s*\\B[-~]{1,2}>\\*?",e:"[-~]{1,2}>\\*?"},{b:"("+r+"\\s*(?:=|:=)\\s*)?(\\(.*\\))?\\s*\\B!?[-~]{1,2}>\\*?",e:"!?[-~]{1,2}>\\*?"}]},{cN:"class",bK:"class",e:"$",i:/[:="\[\]]/,c:[{bK:"extends",eW:!0,i:/[:="\[\]]/,c:[a]},a]},{cN:"attribute",b:r+":",e:":",rB:!0,rE:!0,r:0}])}}),hljs.registerLanguage("lua",function(e){var t="\\[=*\\[",r="\\]=*\\]",a={b:t,e:r,c:["self"]},i=[e.C("--(?!"+t+")","$"),e.C("--"+t,r,{c:[a],r:10})];return{l:e.UIR,k:{keyword:"and break do else elseif end false for if in local nil not or repeat return then true until while",built_in:"_G _VERSION assert collectgarbage dofile error getfenv getmetatable ipairs load loadfile loadstring module next pairs pcall print rawequal rawget rawset require select setfenv setmetatable tonumber tostring type unpack xpcall coroutine debug io math os package string table"},c:i.concat([{cN:"function",bK:"function",e:"\\)",c:[e.inherit(e.TM,{b:"([_a-zA-Z]\\w*\\.)*([_a-zA-Z]\\w*:)?[_a-zA-Z]\\w*"}),{cN:"params",b:"\\(",eW:!0,c:i}].concat(i)},e.CNM,e.ASM,e.QSM,{cN:"string",b:t,e:r,c:[a],r:5}])}}),hljs.registerLanguage("makefile",function(e){var t={cN:"variable",b:/\$\(/,e:/\)/,c:[e.BE]};return{aliases:["mk","mak"],c:[e.HCM,{b:/^\w+\s*\W*=/,rB:!0,r:0,starts:{cN:"constant",e:/\s*\W*=/,eE:!0,starts:{e:/$/,r:0,c:[t]}}},{cN:"title",b:/^[\w]+:\s*$/},{cN:"phony",b:/^\.PHONY:/,e:/$/,k:".PHONY",l:/[\.\w]+/},{b:/^\t+/,e:/$/,r:0,c:[e.QSM,t]}]}}),hljs.registerLanguage("mathematica",function(e){return{aliases:["mma"],l:"(\\$|\\b)"+e.IR+"\\b",k:"AbelianGroup Abort AbortKernels AbortProtect Above Abs Absolute AbsoluteCorrelation AbsoluteCorrelationFunction AbsoluteCurrentValue AbsoluteDashing AbsoluteFileName AbsoluteOptions AbsolutePointSize AbsoluteThickness AbsoluteTime AbsoluteTiming AccountingForm Accumulate Accuracy AccuracyGoal ActionDelay ActionMenu ActionMenuBox ActionMenuBoxOptions Active ActiveItem ActiveStyle AcyclicGraphQ AddOnHelpPath AddTo AdjacencyGraph AdjacencyList AdjacencyMatrix AdjustmentBox AdjustmentBoxOptions AdjustTimeSeriesForecast AffineTransform After AiryAi AiryAiPrime AiryAiZero AiryBi AiryBiPrime AiryBiZero AlgebraicIntegerQ AlgebraicNumber AlgebraicNumberDenominator AlgebraicNumberNorm AlgebraicNumberPolynomial AlgebraicNumberTrace AlgebraicRules AlgebraicRulesData Algebraics AlgebraicUnitQ Alignment AlignmentMarker AlignmentPoint All AllowedDimensions AllowGroupClose AllowInlineCells AllowKernelInitialization AllowReverseGroupClose AllowScriptLevelChange AlphaChannel AlternatingGroup AlternativeHypothesis Alternatives AmbientLight Analytic AnchoredSearch And AndersonDarlingTest AngerJ AngleBracket AngularGauge Animate AnimationCycleOffset AnimationCycleRepetitions AnimationDirection AnimationDisplayTime AnimationRate AnimationRepetitions AnimationRunning Animator AnimatorBox AnimatorBoxOptions AnimatorElements Annotation Annuity AnnuityDue Antialiasing Antisymmetric Apart ApartSquareFree Appearance AppearanceElements AppellF1 Append AppendTo Apply ArcCos ArcCosh ArcCot ArcCoth ArcCsc ArcCsch ArcSec ArcSech ArcSin ArcSinDistribution ArcSinh ArcTan ArcTanh Arg ArgMax ArgMin ArgumentCountQ ARIMAProcess ArithmeticGeometricMean ARMAProcess ARProcess Array ArrayComponents ArrayDepth ArrayFlatten ArrayPad ArrayPlot ArrayQ ArrayReshape ArrayRules Arrays Arrow Arrow3DBox ArrowBox Arrowheads AspectRatio AspectRatioFixed Assert Assuming Assumptions AstronomicalData Asynchronous AsynchronousTaskObject AsynchronousTasks AtomQ Attributes AugmentedSymmetricPolynomial AutoAction AutoDelete AutoEvaluateEvents AutoGeneratedPackage AutoIndent AutoIndentSpacings AutoItalicWords AutoloadPath AutoMatch Automatic AutomaticImageSize AutoMultiplicationSymbol AutoNumberFormatting AutoOpenNotebooks AutoOpenPalettes AutorunSequencing AutoScaling AutoScroll AutoSpacing AutoStyleOptions AutoStyleWords Axes AxesEdge AxesLabel AxesOrigin AxesStyle Axis BabyMonsterGroupB Back Background BackgroundTasksSettings Backslash Backsubstitution Backward Band BandpassFilter BandstopFilter BarabasiAlbertGraphDistribution BarChart BarChart3D BarLegend BarlowProschanImportance BarnesG BarOrigin BarSpacing BartlettHannWindow BartlettWindow BaseForm Baseline BaselinePosition BaseStyle BatesDistribution BattleLemarieWavelet Because BeckmannDistribution Beep Before Begin BeginDialogPacket BeginFrontEndInteractionPacket BeginPackage BellB BellY Below BenfordDistribution BeniniDistribution BenktanderGibratDistribution BenktanderWeibullDistribution BernoulliB BernoulliDistribution BernoulliGraphDistribution BernoulliProcess BernsteinBasis BesselFilterModel BesselI BesselJ BesselJZero BesselK BesselY BesselYZero Beta BetaBinomialDistribution BetaDistribution BetaNegativeBinomialDistribution BetaPrimeDistribution BetaRegularized BetweennessCentrality BezierCurve BezierCurve3DBox BezierCurve3DBoxOptions BezierCurveBox BezierCurveBoxOptions BezierFunction BilateralFilter Binarize BinaryFormat BinaryImageQ BinaryRead BinaryReadList BinaryWrite BinCounts BinLists Binomial BinomialDistribution BinomialProcess BinormalDistribution BiorthogonalSplineWavelet BipartiteGraphQ BirnbaumImportance BirnbaumSaundersDistribution BitAnd BitClear BitGet BitLength BitNot BitOr BitSet BitShiftLeft BitShiftRight BitXor Black BlackmanHarrisWindow BlackmanNuttallWindow BlackmanWindow Blank BlankForm BlankNullSequence BlankSequence Blend Block BlockRandom BlomqvistBeta BlomqvistBetaTest Blue Blur BodePlot BohmanWindow Bold Bookmarks Boole BooleanConsecutiveFunction BooleanConvert BooleanCountingFunction BooleanFunction BooleanGraph BooleanMaxterms BooleanMinimize BooleanMinterms Booleans BooleanTable BooleanVariables BorderDimensions BorelTannerDistribution Bottom BottomHatTransform BoundaryStyle Bounds Box BoxBaselineShift BoxData BoxDimensions Boxed Boxes BoxForm BoxFormFormatTypes BoxFrame BoxID BoxMargins BoxMatrix BoxRatios BoxRotation BoxRotationPoint BoxStyle BoxWhiskerChart Bra BracketingBar BraKet BrayCurtisDistance BreadthFirstScan Break Brown BrownForsytheTest BrownianBridgeProcess BrowserCategory BSplineBasis BSplineCurve BSplineCurve3DBox BSplineCurveBox BSplineCurveBoxOptions BSplineFunction BSplineSurface BSplineSurface3DBox BubbleChart BubbleChart3D BubbleScale BubbleSizes BulletGauge BusinessDayQ ButterflyGraph ButterworthFilterModel Button ButtonBar ButtonBox ButtonBoxOptions ButtonCell ButtonContents ButtonData ButtonEvaluator ButtonExpandable ButtonFrame ButtonFunction ButtonMargins ButtonMinHeight ButtonNote ButtonNotebook ButtonSource ButtonStyle ButtonStyleMenuListing Byte ByteCount ByteOrdering C CachedValue CacheGraphics CalendarData CalendarType CallPacket CanberraDistance Cancel CancelButton CandlestickChart Cap CapForm CapitalDifferentialD CardinalBSplineBasis CarmichaelLambda Cases Cashflow Casoratian Catalan CatalanNumber Catch CauchyDistribution CauchyWindow CayleyGraph CDF CDFDeploy CDFInformation CDFWavelet Ceiling Cell CellAutoOverwrite CellBaseline CellBoundingBox CellBracketOptions CellChangeTimes CellContents CellContext CellDingbat CellDynamicExpression CellEditDuplicate CellElementsBoundingBox CellElementSpacings CellEpilog CellEvaluationDuplicate CellEvaluationFunction CellEventActions CellFrame CellFrameColor CellFrameLabelMargins CellFrameLabels CellFrameMargins CellGroup CellGroupData CellGrouping CellGroupingRules CellHorizontalScrolling CellID CellLabel CellLabelAutoDelete CellLabelMargins CellLabelPositioning CellMargins CellObject CellOpen CellPrint CellProlog Cells CellSize CellStyle CellTags CellularAutomaton CensoredDistribution Censoring Center CenterDot CentralMoment CentralMomentGeneratingFunction CForm ChampernowneNumber ChanVeseBinarize Character CharacterEncoding CharacterEncodingsPath CharacteristicFunction CharacteristicPolynomial CharacterRange Characters ChartBaseStyle ChartElementData ChartElementDataFunction ChartElementFunction ChartElements ChartLabels ChartLayout ChartLegends ChartStyle Chebyshev1FilterModel Chebyshev2FilterModel ChebyshevDistance ChebyshevT ChebyshevU Check CheckAbort CheckAll Checkbox CheckboxBar CheckboxBox CheckboxBoxOptions ChemicalData ChessboardDistance ChiDistribution ChineseRemainder ChiSquareDistribution ChoiceButtons ChoiceDialog CholeskyDecomposition Chop Circle CircleBox CircleDot CircleMinus CirclePlus CircleTimes CirculantGraph CityData Clear ClearAll ClearAttributes ClearSystemCache ClebschGordan ClickPane Clip ClipboardNotebook ClipFill ClippingStyle ClipPlanes ClipRange Clock ClockGauge ClockwiseContourIntegral Close Closed CloseKernels ClosenessCentrality Closing ClosingAutoSave ClosingEvent ClusteringComponents CMYKColor Coarse Coefficient CoefficientArrays CoefficientDomain CoefficientList CoefficientRules CoifletWavelet Collect Colon ColonForm ColorCombine ColorConvert ColorData ColorDataFunction ColorFunction ColorFunctionScaling Colorize ColorNegate ColorOutput ColorProfileData ColorQuantize ColorReplace ColorRules ColorSelectorSettings ColorSeparate ColorSetter ColorSetterBox ColorSetterBoxOptions ColorSlider ColorSpace Column ColumnAlignments ColumnBackgrounds ColumnForm ColumnLines ColumnsEqual ColumnSpacings ColumnWidths CommonDefaultFormatTypes Commonest CommonestFilter CommonUnits CommunityBoundaryStyle CommunityGraphPlot CommunityLabels CommunityRegionStyle CompatibleUnitQ CompilationOptions CompilationTarget Compile Compiled CompiledFunction Complement CompleteGraph CompleteGraphQ CompleteKaryTree CompletionsListPacket Complex Complexes ComplexExpand ComplexInfinity ComplexityFunction ComponentMeasurements ComponentwiseContextMenu Compose ComposeList ComposeSeries Composition CompoundExpression CompoundPoissonDistribution CompoundPoissonProcess CompoundRenewalProcess Compress CompressedData Condition ConditionalExpression Conditioned Cone ConeBox ConfidenceLevel ConfidenceRange ConfidenceTransform ConfigurationPath Congruent Conjugate ConjugateTranspose Conjunction Connect ConnectedComponents ConnectedGraphQ ConnesWindow ConoverTest ConsoleMessage ConsoleMessagePacket ConsolePrint Constant ConstantArray Constants ConstrainedMax ConstrainedMin ContentPadding ContentsBoundingBox ContentSelectable ContentSize Context ContextMenu Contexts ContextToFilename ContextToFileName Continuation Continue ContinuedFraction ContinuedFractionK ContinuousAction ContinuousMarkovProcess ContinuousTimeModelQ ContinuousWaveletData ContinuousWaveletTransform ContourDetect ContourGraphics ContourIntegral ContourLabels ContourLines ContourPlot ContourPlot3D Contours ContourShading ContourSmoothing ContourStyle ContraharmonicMean Control ControlActive ControlAlignment ControllabilityGramian ControllabilityMatrix ControllableDecomposition ControllableModelQ ControllerDuration ControllerInformation ControllerInformationData ControllerLinking ControllerManipulate ControllerMethod ControllerPath ControllerState ControlPlacement ControlsRendering ControlType Convergents ConversionOptions ConversionRules ConvertToBitmapPacket ConvertToPostScript ConvertToPostScriptPacket Convolve ConwayGroupCo1 ConwayGroupCo2 ConwayGroupCo3 CoordinateChartData CoordinatesToolOptions CoordinateTransform CoordinateTransformData CoprimeQ Coproduct CopulaDistribution Copyable CopyDirectory CopyFile CopyTag CopyToClipboard CornerFilter CornerNeighbors Correlation CorrelationDistance CorrelationFunction CorrelationTest Cos Cosh CoshIntegral CosineDistance CosineWindow CosIntegral Cot Coth Count CounterAssignments CounterBox CounterBoxOptions CounterClockwiseContourIntegral CounterEvaluator CounterFunction CounterIncrements CounterStyle CounterStyleMenuListing CountRoots CountryData Covariance CovarianceEstimatorFunction CovarianceFunction CoxianDistribution CoxIngersollRossProcess CoxModel CoxModelFit CramerVonMisesTest CreateArchive CreateDialog CreateDirectory CreateDocument CreateIntermediateDirectories CreatePalette CreatePalettePacket CreateScheduledTask CreateTemporary CreateWindow CriticalityFailureImportance CriticalitySuccessImportance CriticalSection Cross CrossingDetect CrossMatrix Csc Csch CubeRoot Cubics Cuboid CuboidBox Cumulant CumulantGeneratingFunction Cup CupCap Curl CurlyDoubleQuote CurlyQuote CurrentImage CurrentlySpeakingPacket CurrentValue CurvatureFlowFilter CurveClosed Cyan CycleGraph CycleIndexPolynomial Cycles CyclicGroup Cyclotomic Cylinder CylinderBox CylindricalDecomposition D DagumDistribution DamerauLevenshteinDistance DampingFactor Darker Dashed Dashing DataCompression DataDistribution DataRange DataReversed Date DateDelimiters DateDifference DateFunction DateList DateListLogPlot DateListPlot DatePattern DatePlus DateRange DateString DateTicksFormat DaubechiesWavelet DavisDistribution DawsonF DayCount DayCountConvention DayMatchQ DayName DayPlus DayRange DayRound DeBruijnGraph Debug DebugTag Decimal DeclareKnownSymbols DeclarePackage Decompose Decrement DedekindEta Default DefaultAxesStyle DefaultBaseStyle DefaultBoxStyle DefaultButton DefaultColor DefaultControlPlacement DefaultDuplicateCellStyle DefaultDuration DefaultElement DefaultFaceGridsStyle DefaultFieldHintStyle DefaultFont DefaultFontProperties DefaultFormatType DefaultFormatTypeForStyle DefaultFrameStyle DefaultFrameTicksStyle DefaultGridLinesStyle DefaultInlineFormatType DefaultInputFormatType DefaultLabelStyle DefaultMenuStyle DefaultNaturalLanguage DefaultNewCellStyle DefaultNewInlineCellStyle DefaultNotebook DefaultOptions DefaultOutputFormatType DefaultStyle DefaultStyleDefinitions DefaultTextFormatType DefaultTextInlineFormatType DefaultTicksStyle DefaultTooltipStyle DefaultValues Defer DefineExternal DefineInputStreamMethod DefineOutputStreamMethod Definition Degree DegreeCentrality DegreeGraphDistribution DegreeLexicographic DegreeReverseLexicographic Deinitialization Del Deletable Delete DeleteBorderComponents DeleteCases DeleteContents DeleteDirectory DeleteDuplicates DeleteFile DeleteSmallComponents DeleteWithContents DeletionWarning Delimiter DelimiterFlashTime DelimiterMatching Delimiters Denominator DensityGraphics DensityHistogram DensityPlot DependentVariables Deploy Deployed Depth DepthFirstScan Derivative DerivativeFilter DescriptorStateSpace DesignMatrix Det DGaussianWavelet DiacriticalPositioning Diagonal DiagonalMatrix Dialog DialogIndent DialogInput DialogLevel DialogNotebook DialogProlog DialogReturn DialogSymbols Diamond DiamondMatrix DiceDissimilarity DictionaryLookup DifferenceDelta DifferenceOrder DifferenceRoot DifferenceRootReduce Differences DifferentialD DifferentialRoot DifferentialRootReduce DifferentiatorFilter DigitBlock DigitBlockMinimum DigitCharacter DigitCount DigitQ DihedralGroup Dilation Dimensions DiracComb DiracDelta DirectedEdge DirectedEdges DirectedGraph DirectedGraphQ DirectedInfinity Direction Directive Directory DirectoryName DirectoryQ DirectoryStack DirichletCharacter DirichletConvolve DirichletDistribution DirichletL DirichletTransform DirichletWindow DisableConsolePrintPacket DiscreteChirpZTransform DiscreteConvolve DiscreteDelta DiscreteHadamardTransform DiscreteIndicator DiscreteLQEstimatorGains DiscreteLQRegulatorGains DiscreteLyapunovSolve DiscreteMarkovProcess DiscretePlot DiscretePlot3D DiscreteRatio DiscreteRiccatiSolve DiscreteShift DiscreteTimeModelQ DiscreteUniformDistribution DiscreteVariables DiscreteWaveletData DiscreteWaveletPacketTransform DiscreteWaveletTransform Discriminant Disjunction Disk DiskBox DiskMatrix Dispatch DispersionEstimatorFunction Display DisplayAllSteps DisplayEndPacket DisplayFlushImagePacket DisplayForm DisplayFunction DisplayPacket DisplayRules DisplaySetSizePacket DisplayString DisplayTemporary DisplayWith DisplayWithRef DisplayWithVariable DistanceFunction DistanceTransform Distribute Distributed DistributedContexts DistributeDefinitions DistributionChart DistributionDomain DistributionFitTest DistributionParameterAssumptions DistributionParameterQ Dithering Div Divergence Divide DivideBy Dividers Divisible Divisors DivisorSigma DivisorSum DMSList DMSString Do DockedCells DocumentNotebook DominantColors DOSTextFormat Dot DotDashed DotEqual Dotted DoubleBracketingBar DoubleContourIntegral DoubleDownArrow DoubleLeftArrow DoubleLeftRightArrow DoubleLeftTee DoubleLongLeftArrow DoubleLongLeftRightArrow DoubleLongRightArrow DoubleRightArrow DoubleRightTee DoubleUpArrow DoubleUpDownArrow DoubleVerticalBar DoublyInfinite Down DownArrow DownArrowBar DownArrowUpArrow DownLeftRightVector DownLeftTeeVector DownLeftVector DownLeftVectorBar DownRightTeeVector DownRightVector DownRightVectorBar Downsample DownTee DownTeeArrow DownValues DragAndDrop DrawEdges DrawFrontFaces DrawHighlighted Drop DSolve Dt DualLinearProgramming DualSystemsModel DumpGet DumpSave DuplicateFreeQ Dynamic DynamicBox DynamicBoxOptions DynamicEvaluationTimeout DynamicLocation DynamicModule DynamicModuleBox DynamicModuleBoxOptions DynamicModuleParent DynamicModuleValues DynamicName DynamicNamespace DynamicReference DynamicSetting DynamicUpdating DynamicWrapper DynamicWrapperBox DynamicWrapperBoxOptions E EccentricityCentrality EdgeAdd EdgeBetweennessCentrality EdgeCapacity EdgeCapForm EdgeColor EdgeConnectivity EdgeCost EdgeCount EdgeCoverQ EdgeDashing EdgeDelete EdgeDetect EdgeForm EdgeIndex EdgeJoinForm EdgeLabeling EdgeLabels EdgeLabelStyle EdgeList EdgeOpacity EdgeQ EdgeRenderingFunction EdgeRules EdgeShapeFunction EdgeStyle EdgeThickness EdgeWeight Editable EditButtonSettings EditCellTagsSettings EditDistance EffectiveInterest Eigensystem Eigenvalues EigenvectorCentrality Eigenvectors Element ElementData Eliminate EliminationOrder EllipticE EllipticExp EllipticExpPrime EllipticF EllipticFilterModel EllipticK EllipticLog EllipticNomeQ EllipticPi EllipticReducedHalfPeriods EllipticTheta EllipticThetaPrime EmitSound EmphasizeSyntaxErrors EmpiricalDistribution Empty EmptyGraphQ EnableConsolePrintPacket Enabled Encode End EndAdd EndDialogPacket EndFrontEndInteractionPacket EndOfFile EndOfLine EndOfString EndPackage EngineeringForm Enter EnterExpressionPacket EnterTextPacket Entropy EntropyFilter Environment Epilog Equal EqualColumns EqualRows EqualTilde EquatedTo Equilibrium EquirippleFilterKernel Equivalent Erf Erfc Erfi ErlangB ErlangC ErlangDistribution Erosion ErrorBox ErrorBoxOptions ErrorNorm ErrorPacket ErrorsDialogSettings EstimatedDistribution EstimatedProcess EstimatorGains EstimatorRegulator EuclideanDistance EulerE EulerGamma EulerianGraphQ EulerPhi Evaluatable Evaluate Evaluated EvaluatePacket EvaluationCell EvaluationCompletionAction EvaluationElements EvaluationMode EvaluationMonitor EvaluationNotebook EvaluationObject EvaluationOrder Evaluator EvaluatorNames EvenQ EventData EventEvaluator EventHandler EventHandlerTag EventLabels ExactBlackmanWindow ExactNumberQ ExactRootIsolation ExampleData Except ExcludedForms ExcludePods Exclusions ExclusionsStyle Exists Exit ExitDialog Exp Expand ExpandAll ExpandDenominator ExpandFileName ExpandNumerator Expectation ExpectationE ExpectedValue ExpGammaDistribution ExpIntegralE ExpIntegralEi Exponent ExponentFunction ExponentialDistribution ExponentialFamily ExponentialGeneratingFunction ExponentialMovingAverage ExponentialPowerDistribution ExponentPosition ExponentStep Export ExportAutoReplacements ExportPacket ExportString Expression ExpressionCell ExpressionPacket ExpToTrig ExtendedGCD Extension ExtentElementFunction ExtentMarkers ExtentSize ExternalCall ExternalDataCharacterEncoding Extract ExtractArchive ExtremeValueDistribution FaceForm FaceGrids FaceGridsStyle Factor FactorComplete Factorial Factorial2 FactorialMoment FactorialMomentGeneratingFunction FactorialPower FactorInteger FactorList FactorSquareFree FactorSquareFreeList FactorTerms FactorTermsList Fail FailureDistribution False FARIMAProcess FEDisableConsolePrintPacket FeedbackSector FeedbackSectorStyle FeedbackType FEEnableConsolePrintPacket Fibonacci FieldHint FieldHintStyle FieldMasked FieldSize File FileBaseName FileByteCount FileDate FileExistsQ FileExtension FileFormat FileHash FileInformation FileName FileNameDepth FileNameDialogSettings FileNameDrop FileNameJoin FileNames FileNameSetter FileNameSplit FileNameTake FilePrint FileType FilledCurve FilledCurveBox Filling FillingStyle FillingTransform FilterRules FinancialBond FinancialData FinancialDerivative FinancialIndicator Find FindArgMax FindArgMin FindClique FindClusters FindCurvePath FindDistributionParameters FindDivisions FindEdgeCover FindEdgeCut FindEulerianCycle FindFaces FindFile FindFit FindGeneratingFunction FindGeoLocation FindGeometricTransform FindGraphCommunities FindGraphIsomorphism FindGraphPartition FindHamiltonianCycle FindIndependentEdgeSet FindIndependentVertexSet FindInstance FindIntegerNullVector FindKClan FindKClique FindKClub FindKPlex FindLibrary FindLinearRecurrence FindList FindMaximum FindMaximumFlow FindMaxValue FindMinimum FindMinimumCostFlow FindMinimumCut FindMinValue FindPermutation FindPostmanTour FindProcessParameters FindRoot FindSequenceFunction FindSettings FindShortestPath FindShortestTour FindThreshold FindVertexCover FindVertexCut Fine FinishDynamic FiniteAbelianGroupCount FiniteGroupCount FiniteGroupData First FirstPassageTimeDistribution FischerGroupFi22 FischerGroupFi23 FischerGroupFi24Prime FisherHypergeometricDistribution FisherRatioTest FisherZDistribution Fit FitAll FittedModel FixedPoint FixedPointList FlashSelection Flat Flatten FlattenAt FlatTopWindow FlipView Floor FlushPrintOutputPacket Fold FoldList Font FontColor FontFamily FontForm FontName FontOpacity FontPostScriptName FontProperties FontReencoding FontSize FontSlant FontSubstitutions FontTracking FontVariations FontWeight For ForAll Format FormatRules FormatType FormatTypeAutoConvert FormatValues FormBox FormBoxOptions FortranForm Forward ForwardBackward Fourier FourierCoefficient FourierCosCoefficient FourierCosSeries FourierCosTransform FourierDCT FourierDCTFilter FourierDCTMatrix FourierDST FourierDSTMatrix FourierMatrix FourierParameters FourierSequenceTransform FourierSeries FourierSinCoefficient FourierSinSeries FourierSinTransform FourierTransform FourierTrigSeries FractionalBrownianMotionProcess FractionalPart FractionBox FractionBoxOptions FractionLine Frame FrameBox FrameBoxOptions Framed FrameInset FrameLabel Frameless FrameMargins FrameStyle FrameTicks FrameTicksStyle FRatioDistribution FrechetDistribution FreeQ FrequencySamplingFilterKernel FresnelC FresnelS Friday FrobeniusNumber FrobeniusSolve FromCharacterCode FromCoefficientRules FromContinuedFraction FromDate FromDigits FromDMS Front FrontEndDynamicExpression FrontEndEventActions FrontEndExecute FrontEndObject FrontEndResource FrontEndResourceString FrontEndStackSize FrontEndToken FrontEndTokenExecute FrontEndValueCache FrontEndVersion FrontFaceColor FrontFaceOpacity Full FullAxes FullDefinition FullForm FullGraphics FullOptions FullSimplify Function FunctionExpand FunctionInterpolation FunctionSpace FussellVeselyImportance GaborFilter GaborMatrix GaborWavelet GainMargins GainPhaseMargins Gamma GammaDistribution GammaRegularized GapPenalty Gather GatherBy GaugeFaceElementFunction GaugeFaceStyle GaugeFrameElementFunction GaugeFrameSize GaugeFrameStyle GaugeLabels GaugeMarkers GaugeStyle GaussianFilter GaussianIntegers GaussianMatrix GaussianWindow GCD GegenbauerC General GeneralizedLinearModelFit GenerateConditions GeneratedCell GeneratedParameters GeneratingFunction Generic GenericCylindricalDecomposition GenomeData GenomeLookup GeodesicClosing GeodesicDilation GeodesicErosion GeodesicOpening GeoDestination GeodesyData GeoDirection GeoDistance GeoGridPosition GeometricBrownianMotionProcess GeometricDistribution GeometricMean GeometricMeanFilter GeometricTransformation GeometricTransformation3DBox GeometricTransformation3DBoxOptions GeometricTransformationBox GeometricTransformationBoxOptions GeoPosition GeoPositionENU GeoPositionXYZ GeoProjectionData GestureHandler GestureHandlerTag Get GetBoundingBoxSizePacket GetContext GetEnvironment GetFileName GetFrontEndOptionsDataPacket GetLinebreakInformationPacket GetMenusPacket GetPageBreakInformationPacket Glaisher GlobalClusteringCoefficient GlobalPreferences GlobalSession Glow GoldenRatio GompertzMakehamDistribution GoodmanKruskalGamma GoodmanKruskalGammaTest Goto Grad Gradient GradientFilter GradientOrientationFilter Graph GraphAssortativity GraphCenter GraphComplement GraphData GraphDensity GraphDiameter GraphDifference GraphDisjointUnion GraphDistance GraphDistanceMatrix GraphElementData GraphEmbedding GraphHighlight GraphHighlightStyle GraphHub Graphics Graphics3D Graphics3DBox Graphics3DBoxOptions GraphicsArray GraphicsBaseline GraphicsBox GraphicsBoxOptions GraphicsColor GraphicsColumn GraphicsComplex GraphicsComplex3DBox GraphicsComplex3DBoxOptions GraphicsComplexBox GraphicsComplexBoxOptions GraphicsContents GraphicsData GraphicsGrid GraphicsGridBox GraphicsGroup GraphicsGroup3DBox GraphicsGroup3DBoxOptions GraphicsGroupBox GraphicsGroupBoxOptions GraphicsGrouping GraphicsHighlightColor GraphicsRow GraphicsSpacing GraphicsStyle GraphIntersection GraphLayout GraphLinkEfficiency GraphPeriphery GraphPlot GraphPlot3D GraphPower GraphPropertyDistribution GraphQ GraphRadius GraphReciprocity GraphRoot GraphStyle GraphUnion Gray GrayLevel GreatCircleDistance Greater GreaterEqual GreaterEqualLess GreaterFullEqual GreaterGreater GreaterLess GreaterSlantEqual GreaterTilde Green Grid GridBaseline GridBox GridBoxAlignment GridBoxBackground GridBoxDividers GridBoxFrame GridBoxItemSize GridBoxItemStyle GridBoxOptions GridBoxSpacings GridCreationSettings GridDefaultElement GridElementStyleOptions GridFrame GridFrameMargins GridGraph GridLines GridLinesStyle GroebnerBasis GroupActionBase GroupCentralizer GroupElementFromWord GroupElementPosition GroupElementQ GroupElements GroupElementToWord GroupGenerators GroupMultiplicationTable GroupOrbits GroupOrder GroupPageBreakWithin GroupSetwiseStabilizer GroupStabilizer GroupStabilizerChain Gudermannian GumbelDistribution HaarWavelet HadamardMatrix HalfNormalDistribution HamiltonianGraphQ HammingDistance HammingWindow HankelH1 HankelH2 HankelMatrix HannPoissonWindow HannWindow HaradaNortonGroupHN HararyGraph HarmonicMean HarmonicMeanFilter HarmonicNumber Hash HashTable Haversine HazardFunction Head HeadCompose Heads HeavisideLambda HeavisidePi HeavisideTheta HeldGroupHe HeldPart HelpBrowserLookup HelpBrowserNotebook HelpBrowserSettings HermiteDecomposition HermiteH HermitianMatrixQ HessenbergDecomposition Hessian HexadecimalCharacter Hexahedron HexahedronBox HexahedronBoxOptions HiddenSurface HighlightGraph HighlightImage HighpassFilter HigmanSimsGroupHS HilbertFilter HilbertMatrix Histogram Histogram3D HistogramDistribution HistogramList HistogramTransform HistogramTransformInterpolation HitMissTransform HITSCentrality HodgeDual HoeffdingD HoeffdingDTest Hold HoldAll HoldAllComplete HoldComplete HoldFirst HoldForm HoldPattern HoldRest HolidayCalendar HomeDirectory HomePage Horizontal HorizontalForm HorizontalGauge HorizontalScrollPosition HornerForm HotellingTSquareDistribution HoytDistribution HTMLSave Hue HumpDownHump HumpEqual HurwitzLerchPhi HurwitzZeta HyperbolicDistribution HypercubeGraph HyperexponentialDistribution Hyperfactorial Hypergeometric0F1 Hypergeometric0F1Regularized Hypergeometric1F1 Hypergeometric1F1Regularized Hypergeometric2F1 Hypergeometric2F1Regularized HypergeometricDistribution HypergeometricPFQ HypergeometricPFQRegularized HypergeometricU Hyperlink HyperlinkCreationSettings Hyphenation HyphenationOptions HypoexponentialDistribution HypothesisTestData I Identity IdentityMatrix If IgnoreCase Im Image Image3D Image3DSlices ImageAccumulate ImageAdd ImageAdjust ImageAlign ImageApply ImageAspectRatio ImageAssemble ImageCache ImageCacheValid ImageCapture ImageChannels ImageClip ImageColorSpace ImageCompose ImageConvolve ImageCooccurrence ImageCorners ImageCorrelate ImageCorrespondingPoints ImageCrop ImageData ImageDataPacket ImageDeconvolve ImageDemosaic ImageDifference ImageDimensions ImageDistance ImageEffect ImageFeatureTrack ImageFileApply ImageFileFilter ImageFileScan ImageFilter ImageForestingComponents ImageForwardTransformation ImageHistogram ImageKeypoints ImageLevels ImageLines ImageMargins ImageMarkers ImageMeasurements ImageMultiply ImageOffset ImagePad ImagePadding ImagePartition ImagePeriodogram ImagePerspectiveTransformation ImageQ ImageRangeCache ImageReflect ImageRegion ImageResize ImageResolution ImageRotate ImageRotated ImageScaled ImageScan ImageSize ImageSizeAction ImageSizeCache ImageSizeMultipliers ImageSizeRaw ImageSubtract ImageTake ImageTransformation ImageTrim ImageType ImageValue ImageValuePositions Implies Import ImportAutoReplacements ImportString ImprovementImportance In IncidenceGraph IncidenceList IncidenceMatrix IncludeConstantBasis IncludeFileExtension IncludePods IncludeSingularTerm Increment Indent IndentingNewlineSpacings IndentMaxFraction IndependenceTest IndependentEdgeSetQ IndependentUnit IndependentVertexSetQ Indeterminate IndexCreationOptions Indexed IndexGraph IndexTag Inequality InexactNumberQ InexactNumbers Infinity Infix Information Inherited InheritScope Initialization InitializationCell InitializationCellEvaluation InitializationCellWarning InlineCounterAssignments InlineCounterIncrements InlineRules Inner Inpaint Input InputAliases InputAssumptions InputAutoReplacements InputField InputFieldBox InputFieldBoxOptions InputForm InputGrouping InputNamePacket InputNotebook InputPacket InputSettings InputStream InputString InputStringPacket InputToBoxFormPacket Insert InsertionPointObject InsertResults Inset Inset3DBox Inset3DBoxOptions InsetBox InsetBoxOptions Install InstallService InString Integer IntegerDigits IntegerExponent IntegerLength IntegerPart IntegerPartitions IntegerQ Integers IntegerString Integral Integrate Interactive InteractiveTradingChart Interlaced Interleaving InternallyBalancedDecomposition InterpolatingFunction InterpolatingPolynomial Interpolation InterpolationOrder InterpolationPoints InterpolationPrecision Interpretation InterpretationBox InterpretationBoxOptions InterpretationFunction InterpretTemplate InterquartileRange Interrupt InterruptSettings Intersection Interval IntervalIntersection IntervalMemberQ IntervalUnion Inverse InverseBetaRegularized InverseCDF InverseChiSquareDistribution InverseContinuousWaveletTransform InverseDistanceTransform InverseEllipticNomeQ InverseErf InverseErfc InverseFourier InverseFourierCosTransform InverseFourierSequenceTransform InverseFourierSinTransform InverseFourierTransform InverseFunction InverseFunctions InverseGammaDistribution InverseGammaRegularized InverseGaussianDistribution InverseGudermannian InverseHaversine InverseJacobiCD InverseJacobiCN InverseJacobiCS InverseJacobiDC InverseJacobiDN InverseJacobiDS InverseJacobiNC InverseJacobiND InverseJacobiNS InverseJacobiSC InverseJacobiSD InverseJacobiSN InverseLaplaceTransform InversePermutation InverseRadon InverseSeries InverseSurvivalFunction InverseWaveletTransform InverseWeierstrassP InverseZTransform Invisible InvisibleApplication InvisibleTimes IrreduciblePolynomialQ IsolatingInterval IsomorphicGraphQ IsotopeData Italic Item ItemBox ItemBoxOptions ItemSize ItemStyle ItoProcess JaccardDissimilarity JacobiAmplitude Jacobian JacobiCD JacobiCN JacobiCS JacobiDC JacobiDN JacobiDS JacobiNC JacobiND JacobiNS JacobiP JacobiSC JacobiSD JacobiSN JacobiSymbol JacobiZeta JankoGroupJ1 JankoGroupJ2 JankoGroupJ3 JankoGroupJ4 JarqueBeraALMTest JohnsonDistribution Join Joined JoinedCurve JoinedCurveBox JoinForm JordanDecomposition JordanModelDecomposition K KagiChart KaiserBesselWindow KaiserWindow KalmanEstimator KalmanFilter KarhunenLoeveDecomposition KaryTree KatzCentrality KCoreComponents KDistribution KelvinBei KelvinBer KelvinKei KelvinKer KendallTau KendallTauTest KernelExecute KernelMixtureDistribution KernelObject Kernels Ket Khinchin KirchhoffGraph KirchhoffMatrix KleinInvariantJ KnightTourGraph KnotData KnownUnitQ KolmogorovSmirnovTest KroneckerDelta KroneckerModelDecomposition KroneckerProduct KroneckerSymbol KuiperTest KumaraswamyDistribution Kurtosis KuwaharaFilter Label Labeled LabeledSlider LabelingFunction LabelStyle LaguerreL LambdaComponents LambertW LanczosWindow LandauDistribution Language LanguageCategory LaplaceDistribution LaplaceTransform Laplacian LaplacianFilter LaplacianGaussianFilter Large Larger Last Latitude LatitudeLongitude LatticeData LatticeReduce Launch LaunchKernels LayeredGraphPlot LayerSizeFunction LayoutInformation LCM LeafCount LeapYearQ LeastSquares LeastSquaresFilterKernel Left LeftArrow LeftArrowBar LeftArrowRightArrow LeftDownTeeVector LeftDownVector LeftDownVectorBar LeftRightArrow LeftRightVector LeftTee LeftTeeArrow LeftTeeVector LeftTriangle LeftTriangleBar LeftTriangleEqual LeftUpDownVector LeftUpTeeVector LeftUpVector LeftUpVectorBar LeftVector LeftVectorBar LegendAppearance Legended LegendFunction LegendLabel LegendLayout LegendMargins LegendMarkers LegendMarkerSize LegendreP LegendreQ LegendreType Length LengthWhile LerchPhi Less LessEqual LessEqualGreater LessFullEqual LessGreater LessLess LessSlantEqual LessTilde LetterCharacter LetterQ Level LeveneTest LeviCivitaTensor LevyDistribution Lexicographic LibraryFunction LibraryFunctionError LibraryFunctionInformation LibraryFunctionLoad LibraryFunctionUnload LibraryLoad LibraryUnload LicenseID LiftingFilterData LiftingWaveletTransform LightBlue LightBrown LightCyan Lighter LightGray LightGreen Lighting LightingAngle LightMagenta LightOrange LightPink LightPurple LightRed LightSources LightYellow Likelihood Limit LimitsPositioning LimitsPositioningTokens LindleyDistribution Line Line3DBox LinearFilter LinearFractionalTransform LinearModelFit LinearOffsetFunction LinearProgramming LinearRecurrence LinearSolve LinearSolveFunction LineBox LineBreak LinebreakAdjustments LineBreakChart LineBreakWithin LineColor LineForm LineGraph LineIndent LineIndentMaxFraction LineIntegralConvolutionPlot LineIntegralConvolutionScale LineLegend LineOpacity LineSpacing LineWrapParts LinkActivate LinkClose LinkConnect LinkConnectedQ LinkCreate LinkError LinkFlush LinkFunction LinkHost LinkInterrupt LinkLaunch LinkMode LinkObject LinkOpen LinkOptions LinkPatterns LinkProtocol LinkRead LinkReadHeld LinkReadyQ Links LinkWrite LinkWriteHeld LiouvilleLambda List Listable ListAnimate ListContourPlot ListContourPlot3D ListConvolve ListCorrelate ListCurvePathPlot ListDeconvolve ListDensityPlot Listen ListFourierSequenceTransform ListInterpolation ListLineIntegralConvolutionPlot ListLinePlot ListLogLinearPlot ListLogLogPlot ListLogPlot ListPicker ListPickerBox ListPickerBoxBackground ListPickerBoxOptions ListPlay ListPlot ListPlot3D ListPointPlot3D ListPolarPlot ListQ ListStreamDensityPlot ListStreamPlot ListSurfacePlot3D ListVectorDensityPlot ListVectorPlot ListVectorPlot3D ListZTransform Literal LiteralSearch LocalClusteringCoefficient LocalizeVariables LocationEquivalenceTest LocationTest Locator LocatorAutoCreate LocatorBox LocatorBoxOptions LocatorCentering LocatorPane LocatorPaneBox LocatorPaneBoxOptions LocatorRegion Locked Log Log10 Log2 LogBarnesG LogGamma LogGammaDistribution LogicalExpand LogIntegral LogisticDistribution LogitModelFit LogLikelihood LogLinearPlot LogLogisticDistribution LogLogPlot LogMultinormalDistribution LogNormalDistribution LogPlot LogRankTest LogSeriesDistribution LongEqual Longest LongestAscendingSequence LongestCommonSequence LongestCommonSequencePositions LongestCommonSubsequence LongestCommonSubsequencePositions LongestMatch LongForm Longitude LongLeftArrow LongLeftRightArrow LongRightArrow Loopback LoopFreeGraphQ LowerCaseQ LowerLeftArrow LowerRightArrow LowerTriangularize LowpassFilter LQEstimatorGains LQGRegulator LQOutputRegulatorGains LQRegulatorGains LUBackSubstitution LucasL LuccioSamiComponents LUDecomposition LyapunovSolve LyonsGroupLy MachineID MachineName MachineNumberQ MachinePrecision MacintoshSystemPageSetup Magenta Magnification Magnify MainSolve MaintainDynamicCaches Majority MakeBoxes MakeExpression MakeRules MangoldtLambda ManhattanDistance Manipulate Manipulator MannWhitneyTest MantissaExponent Manual Map MapAll MapAt MapIndexed MAProcess MapThread MarcumQ MardiaCombinedTest MardiaKurtosisTest MardiaSkewnessTest MarginalDistribution MarkovProcessProperties Masking MatchingDissimilarity MatchLocalNameQ MatchLocalNames MatchQ Material MathematicaNotation MathieuC MathieuCharacteristicA MathieuCharacteristicB MathieuCharacteristicExponent MathieuCPrime MathieuGroupM11 MathieuGroupM12 MathieuGroupM22 MathieuGroupM23 MathieuGroupM24 MathieuS MathieuSPrime MathMLForm MathMLText Matrices MatrixExp MatrixForm MatrixFunction MatrixLog MatrixPlot MatrixPower MatrixQ MatrixRank Max MaxBend MaxDetect MaxExtraBandwidths MaxExtraConditions MaxFeatures MaxFilter Maximize MaxIterations MaxMemoryUsed MaxMixtureKernels MaxPlotPoints MaxPoints MaxRecursion MaxStableDistribution MaxStepFraction MaxSteps MaxStepSize MaxValue MaxwellDistribution McLaughlinGroupMcL Mean MeanClusteringCoefficient MeanDegreeConnectivity MeanDeviation MeanFilter MeanGraphDistance MeanNeighborDegree MeanShift MeanShiftFilter Median MedianDeviation MedianFilter Medium MeijerG MeixnerDistribution MemberQ MemoryConstrained MemoryInUse Menu MenuAppearance MenuCommandKey MenuEvaluator MenuItem MenuPacket MenuSortingValue MenuStyle MenuView MergeDifferences Mesh MeshFunctions MeshRange MeshShading MeshStyle Message MessageDialog MessageList MessageName MessageOptions MessagePacket Messages MessagesNotebook MetaCharacters MetaInformation Method MethodOptions MexicanHatWavelet MeyerWavelet Min MinDetect MinFilter MinimalPolynomial MinimalStateSpaceModel Minimize Minors MinRecursion MinSize MinStableDistribution Minus MinusPlus MinValue Missing MissingDataMethod MittagLefflerE MixedRadix MixedRadixQuantity MixtureDistribution Mod Modal Mode Modular ModularLambda Module Modulus MoebiusMu Moment Momentary MomentConvert MomentEvaluate MomentGeneratingFunction Monday Monitor MonomialList MonomialOrder MonsterGroupM MorletWavelet MorphologicalBinarize MorphologicalBranchPoints MorphologicalComponents MorphologicalEulerNumber MorphologicalGraph MorphologicalPerimeter MorphologicalTransform Most MouseAnnotation MouseAppearance MouseAppearanceTag MouseButtons Mouseover MousePointerNote MousePosition MovingAverage MovingMedian MoyalDistribution MultiedgeStyle MultilaunchWarning MultiLetterItalics MultiLetterStyle MultilineFunction Multinomial MultinomialDistribution MultinormalDistribution MultiplicativeOrder Multiplicity Multiselection MultivariateHypergeometricDistribution MultivariatePoissonDistribution MultivariateTDistribution N NakagamiDistribution NameQ Names NamespaceBox Nand NArgMax NArgMin NBernoulliB NCache NDSolve NDSolveValue Nearest NearestFunction NeedCurrentFrontEndPackagePacket NeedCurrentFrontEndSymbolsPacket NeedlemanWunschSimilarity Needs Negative NegativeBinomialDistribution NegativeMultinomialDistribution NeighborhoodGraph Nest NestedGreaterGreater NestedLessLess NestedScriptRules NestList NestWhile NestWhileList NevilleThetaC NevilleThetaD NevilleThetaN NevilleThetaS NewPrimitiveStyle NExpectation Next NextPrime NHoldAll NHoldFirst NHoldRest NicholsGridLines NicholsPlot NIntegrate NMaximize NMaxValue NMinimize NMinValue NominalVariables NonAssociative NoncentralBetaDistribution NoncentralChiSquareDistribution NoncentralFRatioDistribution NoncentralStudentTDistribution NonCommutativeMultiply NonConstants None NonlinearModelFit NonlocalMeansFilter NonNegative NonPositive Nor NorlundB Norm Normal NormalDistribution NormalGrouping Normalize NormalizedSquaredEuclideanDistance NormalsFunction NormFunction Not NotCongruent NotCupCap NotDoubleVerticalBar Notebook NotebookApply NotebookAutoSave NotebookClose NotebookConvertSettings NotebookCreate NotebookCreateReturnObject NotebookDefault NotebookDelete NotebookDirectory NotebookDynamicExpression NotebookEvaluate NotebookEventActions NotebookFileName NotebookFind NotebookFindReturnObject NotebookGet NotebookGetLayoutInformationPacket NotebookGetMisspellingsPacket NotebookInformation NotebookInterfaceObject NotebookLocate NotebookObject NotebookOpen NotebookOpenReturnObject NotebookPath NotebookPrint NotebookPut NotebookPutReturnObject NotebookRead NotebookResetGeneratedCells Notebooks NotebookSave NotebookSaveAs NotebookSelection NotebookSetupLayoutInformationPacket NotebooksMenu NotebookWrite NotElement NotEqualTilde NotExists NotGreater NotGreaterEqual NotGreaterFullEqual NotGreaterGreater NotGreaterLess NotGreaterSlantEqual NotGreaterTilde NotHumpDownHump NotHumpEqual NotLeftTriangle NotLeftTriangleBar NotLeftTriangleEqual NotLess NotLessEqual NotLessFullEqual NotLessGreater NotLessLess NotLessSlantEqual NotLessTilde NotNestedGreaterGreater NotNestedLessLess NotPrecedes NotPrecedesEqual NotPrecedesSlantEqual NotPrecedesTilde NotReverseElement NotRightTriangle NotRightTriangleBar NotRightTriangleEqual NotSquareSubset NotSquareSubsetEqual NotSquareSuperset NotSquareSupersetEqual NotSubset NotSubsetEqual NotSucceeds NotSucceedsEqual NotSucceedsSlantEqual NotSucceedsTilde NotSuperset NotSupersetEqual NotTilde NotTildeEqual NotTildeFullEqual NotTildeTilde NotVerticalBar NProbability NProduct NProductFactors NRoots NSolve NSum NSumTerms Null NullRecords NullSpace NullWords Number NumberFieldClassNumber NumberFieldDiscriminant NumberFieldFundamentalUnits NumberFieldIntegralBasis NumberFieldNormRepresentatives NumberFieldRegulator NumberFieldRootsOfUnity NumberFieldSignature NumberForm NumberFormat NumberMarks NumberMultiplier NumberPadding NumberPoint NumberQ NumberSeparator NumberSigns NumberString Numerator NumericFunction NumericQ NuttallWindow NValues NyquistGridLines NyquistPlot O ObservabilityGramian ObservabilityMatrix ObservableDecomposition ObservableModelQ OddQ Off Offset OLEData On ONanGroupON OneIdentity Opacity Open OpenAppend Opener OpenerBox OpenerBoxOptions OpenerView OpenFunctionInspectorPacket Opening OpenRead OpenSpecialOptions OpenTemporary OpenWrite Operate OperatingSystem OptimumFlowData Optional OptionInspectorSettings OptionQ Options OptionsPacket OptionsPattern OptionValue OptionValueBox OptionValueBoxOptions Or Orange Order OrderDistribution OrderedQ Ordering Orderless OrnsteinUhlenbeckProcess Orthogonalize Out Outer OutputAutoOverwrite OutputControllabilityMatrix OutputControllableModelQ OutputForm OutputFormData OutputGrouping OutputMathEditExpression OutputNamePacket OutputResponse OutputSizeLimit OutputStream Over OverBar OverDot Overflow OverHat Overlaps Overlay OverlayBox OverlayBoxOptions Overscript OverscriptBox OverscriptBoxOptions OverTilde OverVector OwenT OwnValues PackingMethod PaddedForm Padding PadeApproximant PadLeft PadRight PageBreakAbove PageBreakBelow PageBreakWithin PageFooterLines PageFooters PageHeaderLines PageHeaders PageHeight PageRankCentrality PageWidth PairedBarChart PairedHistogram PairedSmoothHistogram PairedTTest PairedZTest PaletteNotebook PalettePath Pane PaneBox PaneBoxOptions Panel PanelBox PanelBoxOptions Paneled PaneSelector PaneSelectorBox PaneSelectorBoxOptions PaperWidth ParabolicCylinderD ParagraphIndent ParagraphSpacing ParallelArray ParallelCombine ParallelDo ParallelEvaluate Parallelization Parallelize ParallelMap ParallelNeeds ParallelProduct ParallelSubmit ParallelSum ParallelTable ParallelTry Parameter ParameterEstimator ParameterMixtureDistribution ParameterVariables ParametricFunction ParametricNDSolve ParametricNDSolveValue ParametricPlot ParametricPlot3D ParentConnect ParentDirectory ParentForm Parenthesize ParentList ParetoDistribution Part PartialCorrelationFunction PartialD ParticleData Partition PartitionsP PartitionsQ ParzenWindow PascalDistribution PassEventsDown PassEventsUp Paste PasteBoxFormInlineCells PasteButton Path PathGraph PathGraphQ Pattern PatternSequence PatternTest PauliMatrix PaulWavelet Pause PausedTime PDF PearsonChiSquareTest PearsonCorrelationTest PearsonDistribution PerformanceGoal PeriodicInterpolation Periodogram PeriodogramArray PermutationCycles PermutationCyclesQ PermutationGroup PermutationLength PermutationList PermutationListQ PermutationMax PermutationMin PermutationOrder PermutationPower PermutationProduct PermutationReplace Permutations PermutationSupport Permute PeronaMalikFilter Perpendicular PERTDistribution PetersenGraph PhaseMargins Pi Pick PIDData PIDDerivativeFilter PIDFeedforward PIDTune Piecewise PiecewiseExpand PieChart PieChart3D PillaiTrace PillaiTraceTest Pink Pivoting PixelConstrained PixelValue PixelValuePositions Placed Placeholder PlaceholderReplace Plain PlanarGraphQ Play PlayRange Plot Plot3D Plot3Matrix PlotDivision PlotJoined PlotLabel PlotLayout PlotLegends PlotMarkers PlotPoints PlotRange PlotRangeClipping PlotRangePadding PlotRegion PlotStyle Plus PlusMinus Pochhammer PodStates PodWidth Point Point3DBox PointBox PointFigureChart PointForm PointLegend PointSize PoissonConsulDistribution PoissonDistribution PoissonProcess PoissonWindow PolarAxes PolarAxesOrigin PolarGridLines PolarPlot PolarTicks PoleZeroMarkers PolyaAeppliDistribution PolyGamma Polygon Polygon3DBox Polygon3DBoxOptions PolygonBox PolygonBoxOptions PolygonHoleScale PolygonIntersections PolygonScale PolyhedronData PolyLog PolynomialExtendedGCD PolynomialForm PolynomialGCD PolynomialLCM PolynomialMod PolynomialQ PolynomialQuotient PolynomialQuotientRemainder PolynomialReduce PolynomialRemainder Polynomials PopupMenu PopupMenuBox PopupMenuBoxOptions PopupView PopupWindow Position Positive PositiveDefiniteMatrixQ PossibleZeroQ Postfix PostScript Power PowerDistribution PowerExpand PowerMod PowerModList PowerSpectralDensity PowersRepresentations PowerSymmetricPolynomial Precedence PrecedenceForm Precedes PrecedesEqual PrecedesSlantEqual PrecedesTilde Precision PrecisionGoal PreDecrement PredictionRoot PreemptProtect PreferencesPath Prefix PreIncrement Prepend PrependTo PreserveImageOptions Previous PriceGraphDistribution PrimaryPlaceholder Prime PrimeNu PrimeOmega PrimePi PrimePowerQ PrimeQ Primes PrimeZetaP PrimitiveRoot PrincipalComponents PrincipalValue Print PrintAction PrintForm PrintingCopies PrintingOptions PrintingPageRange PrintingStartingPageNumber PrintingStyleEnvironment PrintPrecision PrintTemporary Prism PrismBox PrismBoxOptions PrivateCellOptions PrivateEvaluationOptions PrivateFontOptions PrivateFrontEndOptions PrivateNotebookOptions PrivatePaths Probability ProbabilityDistribution ProbabilityPlot ProbabilityPr ProbabilityScalePlot ProbitModelFit ProcessEstimator ProcessParameterAssumptions ProcessParameterQ ProcessStateDomain ProcessTimeDomain Product ProductDistribution ProductLog ProgressIndicator ProgressIndicatorBox ProgressIndicatorBoxOptions Projection Prolog PromptForm Properties Property PropertyList PropertyValue Proportion Proportional Protect Protected ProteinData Pruning PseudoInverse Purple Put PutAppend Pyramid PyramidBox PyramidBoxOptions QBinomial QFactorial QGamma QHypergeometricPFQ QPochhammer QPolyGamma QRDecomposition QuadraticIrrationalQ Quantile QuantilePlot Quantity QuantityForm QuantityMagnitude QuantityQ QuantityUnit Quartics QuartileDeviation Quartiles QuartileSkewness QueueingNetworkProcess QueueingProcess QueueProperties Quiet Quit Quotient QuotientRemainder RadialityCentrality RadicalBox RadicalBoxOptions RadioButton RadioButtonBar RadioButtonBox RadioButtonBoxOptions Radon RamanujanTau RamanujanTauL RamanujanTauTheta RamanujanTauZ Random RandomChoice RandomComplex RandomFunction RandomGraph RandomImage RandomInteger RandomPermutation RandomPrime RandomReal RandomSample RandomSeed RandomVariate RandomWalkProcess Range RangeFilter RangeSpecification RankedMax RankedMin Raster Raster3D Raster3DBox Raster3DBoxOptions RasterArray RasterBox RasterBoxOptions Rasterize RasterSize Rational RationalFunctions Rationalize Rationals Ratios Raw RawArray RawBoxes RawData RawMedium RayleighDistribution Re Read ReadList ReadProtected Real RealBlockDiagonalForm RealDigits RealExponent Reals Reap Record RecordLists RecordSeparators Rectangle RectangleBox RectangleBoxOptions RectangleChart RectangleChart3D RecurrenceFilter RecurrenceTable RecurringDigitsForm Red Reduce RefBox ReferenceLineStyle ReferenceMarkers ReferenceMarkerStyle Refine ReflectionMatrix ReflectionTransform Refresh RefreshRate RegionBinarize RegionFunction RegionPlot RegionPlot3D RegularExpression Regularization Reinstall Release ReleaseHold ReliabilityDistribution ReliefImage ReliefPlot Remove RemoveAlphaChannel RemoveAsynchronousTask Removed RemoveInputStreamMethod RemoveOutputStreamMethod RemoveProperty RemoveScheduledTask RenameDirectory RenameFile RenderAll RenderingOptions RenewalProcess RenkoChart Repeated RepeatedNull RepeatedString Replace ReplaceAll ReplaceHeldPart ReplaceImageValue ReplaceList ReplacePart ReplacePixelValue ReplaceRepeated Resampling Rescale RescalingTransform ResetDirectory ResetMenusPacket ResetScheduledTask Residue Resolve Rest Resultant ResumePacket Return ReturnExpressionPacket ReturnInputFormPacket ReturnPacket ReturnTextPacket Reverse ReverseBiorthogonalSplineWavelet ReverseElement ReverseEquilibrium ReverseGraph ReverseUpEquilibrium RevolutionAxis RevolutionPlot3D RGBColor RiccatiSolve RiceDistribution RidgeFilter RiemannR RiemannSiegelTheta RiemannSiegelZ Riffle Right RightArrow RightArrowBar RightArrowLeftArrow RightCosetRepresentative RightDownTeeVector RightDownVector RightDownVectorBar RightTee RightTeeArrow RightTeeVector RightTriangle RightTriangleBar RightTriangleEqual RightUpDownVector RightUpTeeVector RightUpVector RightUpVectorBar RightVector RightVectorBar RiskAchievementImportance RiskReductionImportance RogersTanimotoDissimilarity Root RootApproximant RootIntervals RootLocusPlot RootMeanSquare RootOfUnityQ RootReduce Roots RootSum Rotate RotateLabel RotateLeft RotateRight RotationAction RotationBox RotationBoxOptions RotationMatrix RotationTransform Round RoundImplies RoundingRadius Row RowAlignments RowBackgrounds RowBox RowHeights RowLines RowMinHeight RowReduce RowsEqual RowSpacings RSolve RudvalisGroupRu Rule RuleCondition RuleDelayed RuleForm RulerUnits Run RunScheduledTask RunThrough RuntimeAttributes RuntimeOptions RussellRaoDissimilarity SameQ SameTest SampleDepth SampledSoundFunction SampledSoundList SampleRate SamplingPeriod SARIMAProcess SARMAProcess SatisfiabilityCount SatisfiabilityInstances SatisfiableQ Saturday Save Saveable SaveAutoDelete SaveDefinitions SawtoothWave Scale Scaled ScaleDivisions ScaledMousePosition ScaleOrigin ScalePadding ScaleRanges ScaleRangeStyle ScalingFunctions ScalingMatrix ScalingTransform Scan ScheduledTaskActiveQ ScheduledTaskData ScheduledTaskObject ScheduledTasks SchurDecomposition ScientificForm ScreenRectangle ScreenStyleEnvironment ScriptBaselineShifts ScriptLevel ScriptMinSize ScriptRules ScriptSizeMultipliers Scrollbars ScrollingOptions ScrollPosition Sec Sech SechDistribution SectionGrouping SectorChart SectorChart3D SectorOrigin SectorSpacing SeedRandom Select Selectable SelectComponents SelectedCells SelectedNotebook Selection SelectionAnimate SelectionCell SelectionCellCreateCell SelectionCellDefaultStyle SelectionCellParentStyle SelectionCreateCell SelectionDebuggerTag SelectionDuplicateCell SelectionEvaluate SelectionEvaluateCreateCell SelectionMove SelectionPlaceholder SelectionSetStyle SelectWithContents SelfLoops SelfLoopStyle SemialgebraicComponentInstances SendMail Sequence SequenceAlignment SequenceForm SequenceHold SequenceLimit Series SeriesCoefficient SeriesData SessionTime Set SetAccuracy SetAlphaChannel SetAttributes Setbacks SetBoxFormNamesPacket SetDelayed SetDirectory SetEnvironment SetEvaluationNotebook SetFileDate SetFileLoadingContext SetNotebookStatusLine SetOptions SetOptionsPacket SetPrecision SetProperty SetSelectedNotebook SetSharedFunction SetSharedVariable SetSpeechParametersPacket SetStreamPosition SetSystemOptions Setter SetterBar SetterBox SetterBoxOptions Setting SetValue Shading Shallow ShannonWavelet ShapiroWilkTest Share Sharpen ShearingMatrix ShearingTransform ShenCastanMatrix Short ShortDownArrow Shortest ShortestMatch ShortestPathFunction ShortLeftArrow ShortRightArrow ShortUpArrow Show ShowAutoStyles ShowCellBracket ShowCellLabel ShowCellTags ShowClosedCellArea ShowContents ShowControls ShowCursorTracker ShowGroupOpenCloseIcon ShowGroupOpener ShowInvisibleCharacters ShowPageBreaks ShowPredictiveInterface ShowSelection ShowShortBoxForm ShowSpecialCharacters ShowStringCharacters ShowSyntaxStyles ShrinkingDelay ShrinkWrapBoundingBox SiegelTheta SiegelTukeyTest Sign Signature SignedRankTest SignificanceLevel SignPadding SignTest SimilarityRules SimpleGraph SimpleGraphQ Simplify Sin Sinc SinghMaddalaDistribution SingleEvaluation SingleLetterItalics SingleLetterStyle SingularValueDecomposition SingularValueList SingularValuePlot SingularValues Sinh SinhIntegral SinIntegral SixJSymbol Skeleton SkeletonTransform SkellamDistribution Skewness SkewNormalDistribution Skip SliceDistribution Slider Slider2D Slider2DBox Slider2DBoxOptions SliderBox SliderBoxOptions SlideView Slot SlotSequence Small SmallCircle Smaller SmithDelayCompensator SmithWatermanSimilarity SmoothDensityHistogram SmoothHistogram SmoothHistogram3D SmoothKernelDistribution SocialMediaData Socket SokalSneathDissimilarity Solve SolveAlways SolveDelayed Sort SortBy Sound SoundAndGraphics SoundNote SoundVolume Sow Space SpaceForm Spacer Spacings Span SpanAdjustments SpanCharacterRounding SpanFromAbove SpanFromBoth SpanFromLeft SpanLineThickness SpanMaxSize SpanMinSize SpanningCharacters SpanSymmetric SparseArray SpatialGraphDistribution Speak SpeakTextPacket SpearmanRankTest SpearmanRho Spectrogram SpectrogramArray Specularity SpellingCorrection SpellingDictionaries SpellingDictionariesPath SpellingOptions SpellingSuggestionsPacket Sphere SphereBox SphericalBesselJ SphericalBesselY SphericalHankelH1 SphericalHankelH2 SphericalHarmonicY SphericalPlot3D SphericalRegion SpheroidalEigenvalue SpheroidalJoiningFactor SpheroidalPS SpheroidalPSPrime SpheroidalQS SpheroidalQSPrime SpheroidalRadialFactor SpheroidalS1 SpheroidalS1Prime SpheroidalS2 SpheroidalS2Prime Splice SplicedDistribution SplineClosed SplineDegree SplineKnots SplineWeights Split SplitBy SpokenString Sqrt SqrtBox SqrtBoxOptions Square SquaredEuclideanDistance SquareFreeQ SquareIntersection SquaresR SquareSubset SquareSubsetEqual SquareSuperset SquareSupersetEqual SquareUnion SquareWave StabilityMargins StabilityMarginsStyle StableDistribution Stack StackBegin StackComplete StackInhibit StandardDeviation StandardDeviationFilter StandardForm Standardize StandbyDistribution Star StarGraph StartAsynchronousTask StartingStepSize StartOfLine StartOfString StartScheduledTask StartupSound StateDimensions StateFeedbackGains StateOutputEstimator StateResponse StateSpaceModel StateSpaceRealization StateSpaceTransform StationaryDistribution StationaryWaveletPacketTransform StationaryWaveletTransform StatusArea StatusCentrality StepMonitor StieltjesGamma StirlingS1 StirlingS2 StopAsynchronousTask StopScheduledTask StrataVariables StratonovichProcess StreamColorFunction StreamColorFunctionScaling StreamDensityPlot StreamPlot StreamPoints StreamPosition Streams StreamScale StreamStyle String StringBreak StringByteCount StringCases StringCount StringDrop StringExpression StringForm StringFormat StringFreeQ StringInsert StringJoin StringLength StringMatchQ StringPosition StringQ StringReplace StringReplaceList StringReplacePart StringReverse StringRotateLeft StringRotateRight StringSkeleton StringSplit StringTake StringToStream StringTrim StripBoxes StripOnInput StripWrapperBoxes StrokeForm StructuralImportance StructuredArray StructuredSelection StruveH StruveL Stub StudentTDistribution Style StyleBox StyleBoxAutoDelete StyleBoxOptions StyleData StyleDefinitions StyleForm StyleKeyMapping StyleMenuListing StyleNameDialogSettings StyleNames StylePrint StyleSheetPath Subfactorial Subgraph SubMinus SubPlus SubresultantPolynomialRemainders SubresultantPolynomials Subresultants Subscript SubscriptBox SubscriptBoxOptions Subscripted Subset SubsetEqual Subsets SubStar Subsuperscript SubsuperscriptBox SubsuperscriptBoxOptions Subtract SubtractFrom SubValues Succeeds SucceedsEqual SucceedsSlantEqual SucceedsTilde SuchThat Sum SumConvergence Sunday SuperDagger SuperMinus SuperPlus Superscript SuperscriptBox SuperscriptBoxOptions Superset SupersetEqual SuperStar Surd SurdForm SurfaceColor SurfaceGraphics SurvivalDistribution SurvivalFunction SurvivalModel SurvivalModelFit SuspendPacket SuzukiDistribution SuzukiGroupSuz SwatchLegend Switch Symbol SymbolName SymletWavelet Symmetric SymmetricGroup SymmetricMatrixQ SymmetricPolynomial SymmetricReduction Symmetrize SymmetrizedArray SymmetrizedArrayRules SymmetrizedDependentComponents SymmetrizedIndependentComponents SymmetrizedReplacePart SynchronousInitialization SynchronousUpdating Syntax SyntaxForm SyntaxInformation SyntaxLength SyntaxPacket SyntaxQ SystemDialogInput SystemException SystemHelpPath SystemInformation SystemInformationData SystemOpen SystemOptions SystemsModelDelay SystemsModelDelayApproximate SystemsModelDelete SystemsModelDimensions SystemsModelExtract SystemsModelFeedbackConnect SystemsModelLabels SystemsModelOrder SystemsModelParallelConnect SystemsModelSeriesConnect SystemsModelStateFeedbackConnect SystemStub Tab TabFilling Table TableAlignments TableDepth TableDirections TableForm TableHeadings TableSpacing TableView TableViewBox TabSpacings TabView TabViewBox TabViewBoxOptions TagBox TagBoxNote TagBoxOptions TaggingRules TagSet TagSetDelayed TagStyle TagUnset Take TakeWhile Tally Tan Tanh TargetFunctions TargetUnits TautologyQ TelegraphProcess TemplateBox TemplateBoxOptions TemplateSlotSequence TemporalData Temporary TemporaryVariable TensorContract TensorDimensions TensorExpand TensorProduct TensorQ TensorRank TensorReduce TensorSymmetry TensorTranspose TensorWedge Tetrahedron TetrahedronBox TetrahedronBoxOptions TeXForm TeXSave Text Text3DBox Text3DBoxOptions TextAlignment TextBand TextBoundingBox TextBox TextCell TextClipboardType TextData TextForm TextJustification TextLine TextPacket TextParagraph TextRecognize TextRendering TextStyle Texture TextureCoordinateFunction TextureCoordinateScaling Therefore ThermometerGauge Thick Thickness Thin Thinning ThisLink ThompsonGroupTh Thread ThreeJSymbol Threshold Through Throw Thumbnail Thursday Ticks TicksStyle Tilde TildeEqual TildeFullEqual TildeTilde TimeConstrained TimeConstraint Times TimesBy TimeSeriesForecast TimeSeriesInvertibility TimeUsed TimeValue TimeZone Timing Tiny TitleGrouping TitsGroupT ToBoxes ToCharacterCode ToColor ToContinuousTimeModel ToDate ToDiscreteTimeModel ToeplitzMatrix ToExpression ToFileName Together Toggle ToggleFalse Toggler TogglerBar TogglerBox TogglerBoxOptions ToHeldExpression ToInvertibleTimeSeries TokenWords Tolerance ToLowerCase ToNumberField TooBig Tooltip TooltipBox TooltipBoxOptions TooltipDelay TooltipStyle Top TopHatTransform TopologicalSort ToRadicals ToRules ToString Total TotalHeight TotalVariationFilter TotalWidth TouchscreenAutoZoom TouchscreenControlPlacement ToUpperCase Tr Trace TraceAbove TraceAction TraceBackward TraceDepth TraceDialog TraceForward TraceInternal TraceLevel TraceOff TraceOn TraceOriginal TracePrint TraceScan TrackedSymbols TradingChart TraditionalForm TraditionalFunctionNotation TraditionalNotation TraditionalOrder TransferFunctionCancel TransferFunctionExpand TransferFunctionFactor TransferFunctionModel TransferFunctionPoles TransferFunctionTransform TransferFunctionZeros TransformationFunction TransformationFunctions TransformationMatrix TransformedDistribution TransformedField Translate TranslationTransform TransparentColor Transpose TreeForm TreeGraph TreeGraphQ TreePlot TrendStyle TriangleWave TriangularDistribution Trig TrigExpand TrigFactor TrigFactorList Trigger TrigReduce TrigToExp TrimmedMean True TrueQ TruncatedDistribution TsallisQExponentialDistribution TsallisQGaussianDistribution TTest Tube TubeBezierCurveBox TubeBezierCurveBoxOptions TubeBox TubeBSplineCurveBox TubeBSplineCurveBoxOptions Tuesday TukeyLambdaDistribution TukeyWindow Tuples TuranGraph TuringMachine Transparent UnateQ Uncompress Undefined UnderBar Underflow Underlined Underoverscript UnderoverscriptBox UnderoverscriptBoxOptions Underscript UnderscriptBox UnderscriptBoxOptions UndirectedEdge UndirectedGraph UndirectedGraphQ UndocumentedTestFEParserPacket UndocumentedTestGetSelectionPacket Unequal Unevaluated UniformDistribution UniformGraphDistribution UniformSumDistribution Uninstall Union UnionPlus Unique UnitBox UnitConvert UnitDimensions Unitize UnitRootTest UnitSimplify UnitStep UnitTriangle UnitVector Unprotect UnsameQ UnsavedVariables Unset UnsetShared UntrackedVariables Up UpArrow UpArrowBar UpArrowDownArrow Update UpdateDynamicObjects UpdateDynamicObjectsSynchronous UpdateInterval UpDownArrow UpEquilibrium UpperCaseQ UpperLeftArrow UpperRightArrow UpperTriangularize Upsample UpSet UpSetDelayed UpTee UpTeeArrow UpValues URL URLFetch URLFetchAsynchronous URLSave URLSaveAsynchronous UseGraphicsRange Using UsingFrontEnd V2Get ValidationLength Value ValueBox ValueBoxOptions ValueForm ValueQ ValuesData Variables Variance VarianceEquivalenceTest VarianceEstimatorFunction VarianceGammaDistribution VarianceTest VectorAngle VectorColorFunction VectorColorFunctionScaling VectorDensityPlot VectorGlyphData VectorPlot VectorPlot3D VectorPoints VectorQ Vectors VectorScale VectorStyle Vee Verbatim Verbose VerboseConvertToPostScriptPacket VerifyConvergence VerifySolutions VerifyTestAssumptions Version VersionNumber VertexAdd VertexCapacity VertexColors VertexComponent VertexConnectivity VertexCoordinateRules VertexCoordinates VertexCorrelationSimilarity VertexCosineSimilarity VertexCount VertexCoverQ VertexDataCoordinates VertexDegree VertexDelete VertexDiceSimilarity VertexEccentricity VertexInComponent VertexInDegree VertexIndex VertexJaccardSimilarity VertexLabeling VertexLabels VertexLabelStyle VertexList VertexNormals VertexOutComponent VertexOutDegree VertexQ VertexRenderingFunction VertexReplace VertexShape VertexShapeFunction VertexSize VertexStyle VertexTextureCoordinates VertexWeight Vertical VerticalBar VerticalForm VerticalGauge VerticalSeparator VerticalSlider VerticalTilde ViewAngle ViewCenter ViewMatrix ViewPoint ViewPointSelectorSettings ViewPort ViewRange ViewVector ViewVertical VirtualGroupData Visible VisibleCell VoigtDistribution VonMisesDistribution WaitAll WaitAsynchronousTask WaitNext WaitUntil WakebyDistribution WalleniusHypergeometricDistribution WaringYuleDistribution WatershedComponents WatsonUSquareTest WattsStrogatzGraphDistribution WaveletBestBasis WaveletFilterCoefficients WaveletImagePlot WaveletListPlot WaveletMapIndexed WaveletMatrixPlot WaveletPhi WaveletPsi WaveletScale WaveletScalogram WaveletThreshold WeaklyConnectedComponents WeaklyConnectedGraphQ WeakStationarity WeatherData WeberE Wedge Wednesday WeibullDistribution WeierstrassHalfPeriods WeierstrassInvariants WeierstrassP WeierstrassPPrime WeierstrassSigma WeierstrassZeta WeightedAdjacencyGraph WeightedAdjacencyMatrix WeightedData WeightedGraphQ Weights WelchWindow WheelGraph WhenEvent Which While White Whitespace WhitespaceCharacter WhittakerM WhittakerW WienerFilter WienerProcess WignerD WignerSemicircleDistribution WilksW WilksWTest WindowClickSelect WindowElements WindowFloating WindowFrame WindowFrameElements WindowMargins WindowMovable WindowOpacity WindowSelected WindowSize WindowStatusArea WindowTitle WindowToolbars WindowWidth With WolframAlpha WolframAlphaDate WolframAlphaQuantity WolframAlphaResult Word WordBoundary WordCharacter WordData WordSearch WordSeparators WorkingPrecision Write WriteString Wronskian XMLElement XMLObject Xnor Xor Yellow YuleDissimilarity ZernikeR ZeroSymmetric ZeroTest ZeroWidthTimes Zeta ZetaZero ZipfDistribution ZTest ZTransform $Aborted $ActivationGroupID $ActivationKey $ActivationUserRegistered $AddOnsDirectory $AssertFunction $Assumptions $AsynchronousTask $BaseDirectory $BatchInput $BatchOutput $BoxForms $ByteOrdering $Canceled $CharacterEncoding $CharacterEncodings $CommandLine $CompilationTarget $ConditionHold $ConfiguredKernels $Context $ContextPath $ControlActiveSetting $CreationDate $CurrentLink $DateStringFormat $DefaultFont $DefaultFrontEnd $DefaultImagingDevice $DefaultPath $Display $DisplayFunction $DistributedContexts $DynamicEvaluation $Echo $Epilog $ExportFormats $Failed $FinancialDataSource $FormatType $FrontEnd $FrontEndSession $GeoLocation $HistoryLength $HomeDirectory $HTTPCookies $IgnoreEOF $ImagingDevices $ImportFormats $InitialDirectory $Input $InputFileName $InputStreamMethods $Inspector $InstallationDate $InstallationDirectory $InterfaceEnvironment $IterationLimit $KernelCount $KernelID $Language $LaunchDirectory $LibraryPath $LicenseExpirationDate $LicenseID $LicenseProcesses $LicenseServer $LicenseSubprocesses $LicenseType $Line $Linked $LinkSupported $LoadedFiles $MachineAddresses $MachineDomain $MachineDomains $MachineEpsilon $MachineID $MachineName $MachinePrecision $MachineType $MaxExtraPrecision $MaxLicenseProcesses $MaxLicenseSubprocesses $MaxMachineNumber $MaxNumber $MaxPiecewiseCases $MaxPrecision $MaxRootDegree $MessageGroups $MessageList $MessagePrePrint $Messages $MinMachineNumber $MinNumber $MinorReleaseNumber $MinPrecision $ModuleNumber $NetworkLicense $NewMessage $NewSymbol $Notebooks $NumberMarks $Off $OperatingSystem $Output $OutputForms $OutputSizeLimit $OutputStreamMethods $Packages $ParentLink $ParentProcessID $PasswordFile $PatchLevelID $Path $PathnameSeparator $PerformanceGoal $PipeSupported $Post $Pre $PreferencesDirectory $PrePrint $PreRead $PrintForms $PrintLiteral $ProcessID $ProcessorCount $ProcessorType $ProductInformation $ProgramName $RandomState $RecursionLimit $ReleaseNumber $RootDirectory $ScheduledTask $ScriptCommandLine $SessionID $SetParentLink $SharedFunctions $SharedVariables $SoundDisplay $SoundDisplayFunction $SuppressInputFormHeads $SynchronousEvaluation $SyntaxHandler $System $SystemCharacterEncoding $SystemID $SystemWordLength $TemporaryDirectory $TemporaryPrefix $TextStyle $TimedOut $TimeUnit $TimeZone $TopDirectory $TraceOff $TraceOn $TracePattern $TracePostAction $TracePreAction $Urgent $UserAddOnsDirectory $UserBaseDirectory $UserDocumentsDirectory $UserName $Version $VersionNumber",
c:[{cN:"comment",b:/\(\*/,e:/\*\)/},e.ASM,e.QSM,e.CNM,{cN:"list",b:/\{/,e:/\}/,i:/:/}]}}),hljs.registerLanguage("matlab",function(e){var t=[e.CNM,{cN:"string",b:"'",e:"'",c:[e.BE,{b:"''"}]}],r={r:0,c:[{cN:"operator",b:/'['\.]*/}]};return{k:{keyword:"break case catch classdef continue else elseif end enumerated events for function global if methods otherwise parfor persistent properties return spmd switch try while",built_in:"sin sind sinh asin asind asinh cos cosd cosh acos acosd acosh tan tand tanh atan atand atan2 atanh sec secd sech asec asecd asech csc cscd csch acsc acscd acsch cot cotd coth acot acotd acoth hypot exp expm1 log log1p log10 log2 pow2 realpow reallog realsqrt sqrt nthroot nextpow2 abs angle complex conj imag real unwrap isreal cplxpair fix floor ceil round mod rem sign airy besselj bessely besselh besseli besselk beta betainc betaln ellipj ellipke erf erfc erfcx erfinv expint gamma gammainc gammaln psi legendre cross dot factor isprime primes gcd lcm rat rats perms nchoosek factorial cart2sph cart2pol pol2cart sph2cart hsv2rgb rgb2hsv zeros ones eye repmat rand randn linspace logspace freqspace meshgrid accumarray size length ndims numel disp isempty isequal isequalwithequalnans cat reshape diag blkdiag tril triu fliplr flipud flipdim rot90 find sub2ind ind2sub bsxfun ndgrid permute ipermute shiftdim circshift squeeze isscalar isvector ans eps realmax realmin pi i inf nan isnan isinf isfinite j why compan gallery hadamard hankel hilb invhilb magic pascal rosser toeplitz vander wilkinson"},i:'(//|"|#|/\\*|\\s+/\\w+)',c:[{cN:"function",bK:"function",e:"$",c:[e.UTM,{cN:"params",b:"\\(",e:"\\)"},{cN:"params",b:"\\[",e:"\\]"}]},{b:/[a-zA-Z_][a-zA-Z_0-9]*'['\.]*/,rB:!0,r:0,c:[{b:/[a-zA-Z_][a-zA-Z_0-9]*/,r:0},r.c[0]]},{cN:"matrix",b:"\\[",e:"\\]",c:t,r:0,starts:r},{cN:"cell",b:"\\{",e:/}/,c:t,r:0,starts:r},{b:/\)/,r:0,starts:r},e.C("^\\s*\\%\\{\\s*$","^\\s*\\%\\}\\s*$"),e.C("\\%","$")].concat(t)}}),hljs.registerLanguage("mel",function(e){return{k:"int float string vector matrix if else switch case default while do for in break continue global proc return about abs addAttr addAttributeEditorNodeHelp addDynamic addNewShelfTab addPP addPanelCategory addPrefixToName advanceToNextDrivenKey affectedNet affects aimConstraint air alias aliasAttr align alignCtx alignCurve alignSurface allViewFit ambientLight angle angleBetween animCone animCurveEditor animDisplay animView annotate appendStringArray applicationName applyAttrPreset applyTake arcLenDimContext arcLengthDimension arclen arrayMapper art3dPaintCtx artAttrCtx artAttrPaintVertexCtx artAttrSkinPaintCtx artAttrTool artBuildPaintMenu artFluidAttrCtx artPuttyCtx artSelectCtx artSetPaintCtx artUserPaintCtx assignCommand assignInputDevice assignViewportFactories attachCurve attachDeviceAttr attachSurface attrColorSliderGrp attrCompatibility attrControlGrp attrEnumOptionMenu attrEnumOptionMenuGrp attrFieldGrp attrFieldSliderGrp attrNavigationControlGrp attrPresetEditWin attributeExists attributeInfo attributeMenu attributeQuery autoKeyframe autoPlace bakeClip bakeFluidShading bakePartialHistory bakeResults bakeSimulation basename basenameEx batchRender bessel bevel bevelPlus binMembership bindSkin blend2 blendShape blendShapeEditor blendShapePanel blendTwoAttr blindDataType boneLattice boundary boxDollyCtx boxZoomCtx bufferCurve buildBookmarkMenu buildKeyframeMenu button buttonManip CBG cacheFile cacheFileCombine cacheFileMerge cacheFileTrack camera cameraView canCreateManip canvas capitalizeString catch catchQuiet ceil changeSubdivComponentDisplayLevel changeSubdivRegion channelBox character characterMap characterOutlineEditor characterize chdir checkBox checkBoxGrp checkDefaultRenderGlobals choice circle circularFillet clamp clear clearCache clip clipEditor clipEditorCurrentTimeCtx clipSchedule clipSchedulerOutliner clipTrimBefore closeCurve closeSurface cluster cmdFileOutput cmdScrollFieldExecuter cmdScrollFieldReporter cmdShell coarsenSubdivSelectionList collision color colorAtPoint colorEditor colorIndex colorIndexSliderGrp colorSliderButtonGrp colorSliderGrp columnLayout commandEcho commandLine commandPort compactHairSystem componentEditor compositingInterop computePolysetVolume condition cone confirmDialog connectAttr connectControl connectDynamic connectJoint connectionInfo constrain constrainValue constructionHistory container containsMultibyte contextInfo control convertFromOldLayers convertIffToPsd convertLightmap convertSolidTx convertTessellation convertUnit copyArray copyFlexor copyKey copySkinWeights cos cpButton cpCache cpClothSet cpCollision cpConstraint cpConvClothToMesh cpForces cpGetSolverAttr cpPanel cpProperty cpRigidCollisionFilter cpSeam cpSetEdit cpSetSolverAttr cpSolver cpSolverTypes cpTool cpUpdateClothUVs createDisplayLayer createDrawCtx createEditor createLayeredPsdFile createMotionField createNewShelf createNode createRenderLayer createSubdivRegion cross crossProduct ctxAbort ctxCompletion ctxEditMode ctxTraverse currentCtx currentTime currentTimeCtx currentUnit curve curveAddPtCtx curveCVCtx curveEPCtx curveEditorCtx curveIntersect curveMoveEPCtx curveOnSurface curveSketchCtx cutKey cycleCheck cylinder dagPose date defaultLightListCheckBox defaultNavigation defineDataServer defineVirtualDevice deformer deg_to_rad delete deleteAttr deleteShadingGroupsAndMaterials deleteShelfTab deleteUI deleteUnusedBrushes delrandstr detachCurve detachDeviceAttr detachSurface deviceEditor devicePanel dgInfo dgdirty dgeval dgtimer dimWhen directKeyCtx directionalLight dirmap dirname disable disconnectAttr disconnectJoint diskCache displacementToPoly displayAffected displayColor displayCull displayLevelOfDetail displayPref displayRGBColor displaySmoothness displayStats displayString displaySurface distanceDimContext distanceDimension doBlur dolly dollyCtx dopeSheetEditor dot dotProduct doubleProfileBirailSurface drag dragAttrContext draggerContext dropoffLocator duplicate duplicateCurve duplicateSurface dynCache dynControl dynExport dynExpression dynGlobals dynPaintEditor dynParticleCtx dynPref dynRelEdPanel dynRelEditor dynamicLoad editAttrLimits editDisplayLayerGlobals editDisplayLayerMembers editRenderLayerAdjustment editRenderLayerGlobals editRenderLayerMembers editor editorTemplate effector emit emitter enableDevice encodeString endString endsWith env equivalent equivalentTol erf error eval evalDeferred evalEcho event exactWorldBoundingBox exclusiveLightCheckBox exec executeForEachObject exists exp expression expressionEditorListen extendCurve extendSurface extrude fcheck fclose feof fflush fgetline fgetword file fileBrowserDialog fileDialog fileExtension fileInfo filetest filletCurve filter filterCurve filterExpand filterStudioImport findAllIntersections findAnimCurves findKeyframe findMenuItem findRelatedSkinCluster finder firstParentOf fitBspline flexor floatEq floatField floatFieldGrp floatScrollBar floatSlider floatSlider2 floatSliderButtonGrp floatSliderGrp floor flow fluidCacheInfo fluidEmitter fluidVoxelInfo flushUndo fmod fontDialog fopen formLayout format fprint frameLayout fread freeFormFillet frewind fromNativePath fwrite gamma gauss geometryConstraint getApplicationVersionAsFloat getAttr getClassification getDefaultBrush getFileList getFluidAttr getInputDeviceRange getMayaPanelTypes getModifiers getPanel getParticleAttr getPluginResource getenv getpid glRender glRenderEditor globalStitch gmatch goal gotoBindPose grabColor gradientControl gradientControlNoAttr graphDollyCtx graphSelectContext graphTrackCtx gravity grid gridLayout group groupObjectsByName HfAddAttractorToAS HfAssignAS HfBuildEqualMap HfBuildFurFiles HfBuildFurImages HfCancelAFR HfConnectASToHF HfCreateAttractor HfDeleteAS HfEditAS HfPerformCreateAS HfRemoveAttractorFromAS HfSelectAttached HfSelectAttractors HfUnAssignAS hardenPointCurve hardware hardwareRenderPanel headsUpDisplay headsUpMessage help helpLine hermite hide hilite hitTest hotBox hotkey hotkeyCheck hsv_to_rgb hudButton hudSlider hudSliderButton hwReflectionMap hwRender hwRenderLoad hyperGraph hyperPanel hyperShade hypot iconTextButton iconTextCheckBox iconTextRadioButton iconTextRadioCollection iconTextScrollList iconTextStaticLabel ikHandle ikHandleCtx ikHandleDisplayScale ikSolver ikSplineHandleCtx ikSystem ikSystemInfo ikfkDisplayMethod illustratorCurves image imfPlugins inheritTransform insertJoint insertJointCtx insertKeyCtx insertKnotCurve insertKnotSurface instance instanceable instancer intField intFieldGrp intScrollBar intSlider intSliderGrp interToUI internalVar intersect iprEngine isAnimCurve isConnected isDirty isParentOf isSameObject isTrue isValidObjectName isValidString isValidUiName isolateSelect itemFilter itemFilterAttr itemFilterRender itemFilterType joint jointCluster jointCtx jointDisplayScale jointLattice keyTangent keyframe keyframeOutliner keyframeRegionCurrentTimeCtx keyframeRegionDirectKeyCtx keyframeRegionDollyCtx keyframeRegionInsertKeyCtx keyframeRegionMoveKeyCtx keyframeRegionScaleKeyCtx keyframeRegionSelectKeyCtx keyframeRegionSetKeyCtx keyframeRegionTrackCtx keyframeStats lassoContext lattice latticeDeformKeyCtx launch launchImageEditor layerButton layeredShaderPort layeredTexturePort layout layoutDialog lightList lightListEditor lightListPanel lightlink lineIntersection linearPrecision linstep listAnimatable listAttr listCameras listConnections listDeviceAttachments listHistory listInputDeviceAxes listInputDeviceButtons listInputDevices listMenuAnnotation listNodeTypes listPanelCategories listRelatives listSets listTransforms listUnselected listerEditor loadFluid loadNewShelf loadPlugin loadPluginLanguageResources loadPrefObjects localizedPanelLabel lockNode loft log longNameOf lookThru ls lsThroughFilter lsType lsUI Mayatomr mag makeIdentity makeLive makePaintable makeRoll makeSingleSurface makeTubeOn makebot manipMoveContext manipMoveLimitsCtx manipOptions manipRotateContext manipRotateLimitsCtx manipScaleContext manipScaleLimitsCtx marker match max memory menu menuBarLayout menuEditor menuItem menuItemToShelf menuSet menuSetPref messageLine min minimizeApp mirrorJoint modelCurrentTimeCtx modelEditor modelPanel mouse movIn movOut move moveIKtoFK moveKeyCtx moveVertexAlongDirection multiProfileBirailSurface mute nParticle nameCommand nameField namespace namespaceInfo newPanelItems newton nodeCast nodeIconButton nodeOutliner nodePreset nodeType noise nonLinear normalConstraint normalize nurbsBoolean nurbsCopyUVSet nurbsCube nurbsEditUV nurbsPlane nurbsSelect nurbsSquare nurbsToPoly nurbsToPolygonsPref nurbsToSubdiv nurbsToSubdivPref nurbsUVSet nurbsViewDirectionVector objExists objectCenter objectLayer objectType objectTypeUI obsoleteProc oceanNurbsPreviewPlane offsetCurve offsetCurveOnSurface offsetSurface openGLExtension openMayaPref optionMenu optionMenuGrp optionVar orbit orbitCtx orientConstraint outlinerEditor outlinerPanel overrideModifier paintEffectsDisplay pairBlend palettePort paneLayout panel panelConfiguration panelHistory paramDimContext paramDimension paramLocator parent parentConstraint particle particleExists particleInstancer particleRenderInfo partition pasteKey pathAnimation pause pclose percent performanceOptions pfxstrokes pickWalk picture pixelMove planarSrf plane play playbackOptions playblast plugAttr plugNode pluginInfo pluginResourceUtil pointConstraint pointCurveConstraint pointLight pointMatrixMult pointOnCurve pointOnSurface pointPosition poleVectorConstraint polyAppend polyAppendFacetCtx polyAppendVertex polyAutoProjection polyAverageNormal polyAverageVertex polyBevel polyBlendColor polyBlindData polyBoolOp polyBridgeEdge polyCacheMonitor polyCheck polyChipOff polyClipboard polyCloseBorder polyCollapseEdge polyCollapseFacet polyColorBlindData polyColorDel polyColorPerVertex polyColorSet polyCompare polyCone polyCopyUV polyCrease polyCreaseCtx polyCreateFacet polyCreateFacetCtx polyCube polyCut polyCutCtx polyCylinder polyCylindricalProjection polyDelEdge polyDelFacet polyDelVertex polyDuplicateAndConnect polyDuplicateEdge polyEditUV polyEditUVShell polyEvaluate polyExtrudeEdge polyExtrudeFacet polyExtrudeVertex polyFlipEdge polyFlipUV polyForceUV polyGeoSampler polyHelix polyInfo polyInstallAction polyLayoutUV polyListComponentConversion polyMapCut polyMapDel polyMapSew polyMapSewMove polyMergeEdge polyMergeEdgeCtx polyMergeFacet polyMergeFacetCtx polyMergeUV polyMergeVertex polyMirrorFace polyMoveEdge polyMoveFacet polyMoveFacetUV polyMoveUV polyMoveVertex polyNormal polyNormalPerVertex polyNormalizeUV polyOptUvs polyOptions polyOutput polyPipe polyPlanarProjection polyPlane polyPlatonicSolid polyPoke polyPrimitive polyPrism polyProjection polyPyramid polyQuad polyQueryBlindData polyReduce polySelect polySelectConstraint polySelectConstraintMonitor polySelectCtx polySelectEditCtx polySeparate polySetToFaceNormal polySewEdge polyShortestPathCtx polySmooth polySoftEdge polySphere polySphericalProjection polySplit polySplitCtx polySplitEdge polySplitRing polySplitVertex polyStraightenUVBorder polySubdivideEdge polySubdivideFacet polyToSubdiv polyTorus polyTransfer polyTriangulate polyUVSet polyUnite polyWedgeFace popen popupMenu pose pow preloadRefEd print progressBar progressWindow projFileViewer projectCurve projectTangent projectionContext projectionManip promptDialog propModCtx propMove psdChannelOutliner psdEditTextureFile psdExport psdTextureFile putenv pwd python querySubdiv quit rad_to_deg radial radioButton radioButtonGrp radioCollection radioMenuItemCollection rampColorPort rand randomizeFollicles randstate rangeControl readTake rebuildCurve rebuildSurface recordAttr recordDevice redo reference referenceEdit referenceQuery refineSubdivSelectionList refresh refreshAE registerPluginResource rehash reloadImage removeJoint removeMultiInstance removePanelCategory rename renameAttr renameSelectionList renameUI render renderGlobalsNode renderInfo renderLayerButton renderLayerParent renderLayerPostProcess renderLayerUnparent renderManip renderPartition renderQualityNode renderSettings renderThumbnailUpdate renderWindowEditor renderWindowSelectContext renderer reorder reorderDeformers requires reroot resampleFluid resetAE resetPfxToPolyCamera resetTool resolutionNode retarget reverseCurve reverseSurface revolve rgb_to_hsv rigidBody rigidSolver roll rollCtx rootOf rot rotate rotationInterpolation roundConstantRadius rowColumnLayout rowLayout runTimeCommand runup sampleImage saveAllShelves saveAttrPreset saveFluid saveImage saveInitialState saveMenu savePrefObjects savePrefs saveShelf saveToolSettings scale scaleBrushBrightness scaleComponents scaleConstraint scaleKey scaleKeyCtx sceneEditor sceneUIReplacement scmh scriptCtx scriptEditorInfo scriptJob scriptNode scriptTable scriptToShelf scriptedPanel scriptedPanelType scrollField scrollLayout sculpt searchPathArray seed selLoadSettings select selectContext selectCurveCV selectKey selectKeyCtx selectKeyframeRegionCtx selectMode selectPref selectPriority selectType selectedNodes selectionConnection separator setAttr setAttrEnumResource setAttrMapping setAttrNiceNameResource setConstraintRestPosition setDefaultShadingGroup setDrivenKeyframe setDynamic setEditCtx setEditor setFluidAttr setFocus setInfinity setInputDeviceMapping setKeyCtx setKeyPath setKeyframe setKeyframeBlendshapeTargetWts setMenuMode setNodeNiceNameResource setNodeTypeFlag setParent setParticleAttr setPfxToPolyCamera setPluginResource setProject setStampDensity setStartupMessage setState setToolTo setUITemplate setXformManip sets shadingConnection shadingGeometryRelCtx shadingLightRelCtx shadingNetworkCompare shadingNode shapeCompare shelfButton shelfLayout shelfTabLayout shellField shortNameOf showHelp showHidden showManipCtx showSelectionInTitle showShadingGroupAttrEditor showWindow sign simplify sin singleProfileBirailSurface size sizeBytes skinCluster skinPercent smoothCurve smoothTangentSurface smoothstep snap2to2 snapKey snapMode snapTogetherCtx snapshot soft softMod softModCtx sort sound soundControl source spaceLocator sphere sphrand spotLight spotLightPreviewPort spreadSheetEditor spring sqrt squareSurface srtContext stackTrace startString startsWith stitchAndExplodeShell stitchSurface stitchSurfacePoints strcmp stringArrayCatenate stringArrayContains stringArrayCount stringArrayInsertAtIndex stringArrayIntersector stringArrayRemove stringArrayRemoveAtIndex stringArrayRemoveDuplicates stringArrayRemoveExact stringArrayToString stringToStringArray strip stripPrefixFromName stroke subdAutoProjection subdCleanTopology subdCollapse subdDuplicateAndConnect subdEditUV subdListComponentConversion subdMapCut subdMapSewMove subdMatchTopology subdMirror subdToBlind subdToPoly subdTransferUVsToCache subdiv subdivCrease subdivDisplaySmoothness substitute substituteAllString substituteGeometry substring surface surfaceSampler surfaceShaderList swatchDisplayPort switchTable symbolButton symbolCheckBox sysFile system tabLayout tan tangentConstraint texLatticeDeformContext texManipContext texMoveContext texMoveUVShellContext texRotateContext texScaleContext texSelectContext texSelectShortestPathCtx texSmudgeUVContext texWinToolCtx text textCurves textField textFieldButtonGrp textFieldGrp textManip textScrollList textToShelf textureDisplacePlane textureHairColor texturePlacementContext textureWindow threadCount threePointArcCtx timeControl timePort timerX toNativePath toggle toggleAxis toggleWindowVisibility tokenize tokenizeList tolerance tolower toolButton toolCollection toolDropped toolHasOptions toolPropertyWindow torus toupper trace track trackCtx transferAttributes transformCompare transformLimits translator trim trunc truncateFluidCache truncateHairCache tumble tumbleCtx turbulence twoPointArcCtx uiRes uiTemplate unassignInputDevice undo undoInfo ungroup uniform unit unloadPlugin untangleUV untitledFileName untrim upAxis updateAE userCtx uvLink uvSnapshot validateShelfName vectorize view2dToolCtx viewCamera viewClipPlane viewFit viewHeadOn viewLookAt viewManip viewPlace viewSet visor volumeAxis vortex waitCursor warning webBrowser webBrowserPrefs whatIs window windowPref wire wireContext workspace wrinkle wrinkleContext writeTake xbmLangPathList xform",i:"</",c:[e.CNM,e.ASM,e.QSM,{cN:"string",b:"`",e:"`",c:[e.BE]},{cN:"variable",v:[{b:"\\$\\d"},{b:"[\\$\\%\\@](\\^\\w\\b|#\\w+|[^\\s\\w{]|{\\w+}|\\w+)"},{b:"\\*(\\^\\w\\b|#\\w+|[^\\s\\w{]|{\\w+}|\\w+)",r:0}]},e.CLCM,e.CBCM]}}),hljs.registerLanguage("mercury",function(e){var t={keyword:"module use_module import_module include_module end_module initialise mutable initialize finalize finalise interface implementation pred mode func type inst solver any_pred any_func is semidet det nondet multi erroneous failure cc_nondet cc_multi typeclass instance where pragma promise external trace atomic or_else require_complete_switch require_det require_semidet require_multi require_nondet require_cc_multi require_cc_nondet require_erroneous require_failure",pragma:"inline no_inline type_spec source_file fact_table obsolete memo loop_check minimal_model terminates does_not_terminate check_termination promise_equivalent_clauses",preprocessor:"foreign_proc foreign_decl foreign_code foreign_type foreign_import_module foreign_export_enum foreign_export foreign_enum may_call_mercury will_not_call_mercury thread_safe not_thread_safe maybe_thread_safe promise_pure promise_semipure tabled_for_io local untrailed trailed attach_to_io_state can_pass_as_mercury_type stable will_not_throw_exception may_modify_trail will_not_modify_trail may_duplicate may_not_duplicate affects_liveness does_not_affect_liveness doesnt_affect_liveness no_sharing unknown_sharing sharing",built_in:"some all not if then else true fail false try catch catch_any semidet_true semidet_false semidet_fail impure_true impure semipure"},r={cN:"label",b:"XXX",e:"$",eW:!0,r:0},a=e.inherit(e.CLCM,{b:"%"}),i=e.inherit(e.CBCM,{r:0});a.c.push(r),i.c.push(r);var n={cN:"number",b:"0'.\\|0[box][0-9a-fA-F]*"},o=e.inherit(e.ASM,{r:0}),s=e.inherit(e.QSM,{r:0}),l={cN:"constant",b:"\\\\[abfnrtv]\\|\\\\x[0-9a-fA-F]*\\\\\\|%[-+# *.0-9]*[dioxXucsfeEgGp]",r:0};s.c.push(l);var c={cN:"built_in",v:[{b:"<=>"},{b:"<=",r:0},{b:"=>",r:0},{b:"/\\\\"},{b:"\\\\/"}]},d={cN:"built_in",v:[{b:":-\\|-->"},{b:"=",r:0}]};return{aliases:["m","moo"],k:t,c:[c,d,a,i,n,e.NM,o,s,{b:/:-/}]}}),hljs.registerLanguage("mizar",function(e){return{k:"environ vocabularies notations constructors definitions registrations theorems schemes requirements begin end definition registration cluster existence pred func defpred deffunc theorem proof let take assume then thus hence ex for st holds consider reconsider such that and in provided of as from be being by means equals implies iff redefine define now not or attr is mode suppose per cases set thesis contradiction scheme reserve struct correctness compatibility coherence symmetry assymetry reflexivity irreflexivity connectedness uniqueness commutativity idempotence involutiveness projectivity",c:[e.C("::","$")]}}),hljs.registerLanguage("monkey",function(e){var t={cN:"number",r:0,v:[{b:"[$][a-fA-F0-9]+"},e.NM]};return{cI:!0,k:{keyword:"public private property continue exit extern new try catch eachin not abstract final select case default const local global field end if then else elseif endif while wend repeat until forever for to step next return module inline throw",built_in:"DebugLog DebugStop Error Print ACos ACosr ASin ASinr ATan ATan2 ATan2r ATanr Abs Abs Ceil Clamp Clamp Cos Cosr Exp Floor Log Max Max Min Min Pow Sgn Sgn Sin Sinr Sqrt Tan Tanr Seed PI HALFPI TWOPI",literal:"true false null and or shl shr mod"},c:[e.C("#rem","#end"),e.C("'","$",{r:0}),{cN:"function",bK:"function method",e:"[(=:]|$",i:/\n/,c:[e.UTM]},{cN:"class",bK:"class interface",e:"$",c:[{bK:"extends implements"},e.UTM]},{cN:"variable",b:"\\b(self|super)\\b"},{cN:"preprocessor",bK:"import",e:"$"},{cN:"preprocessor",b:"\\s*#",e:"$",k:"if else elseif endif end then"},{cN:"pi",b:"^\\s*strict\\b"},{bK:"alias",e:"=",c:[e.UTM]},e.QSM,t]}}),hljs.registerLanguage("nginx",function(e){var t={cN:"variable",v:[{b:/\$\d+/},{b:/\$\{/,e:/}/},{b:"[\\$\\@]"+e.UIR}]},r={eW:!0,l:"[a-z/_]+",k:{built_in:"on off yes no true false none blocked debug info notice warn error crit select break last permanent redirect kqueue rtsig epoll poll /dev/poll"},r:0,i:"=>",c:[e.HCM,{cN:"string",c:[e.BE,t],v:[{b:/"/,e:/"/},{b:/'/,e:/'/}]},{cN:"url",b:"([a-z]+):/",e:"\\s",eW:!0,eE:!0,c:[t]},{cN:"regexp",c:[e.BE,t],v:[{b:"\\s\\^",e:"\\s|{|;",rE:!0},{b:"~\\*?\\s+",e:"\\s|{|;",rE:!0},{b:"\\*(\\.[a-z\\-]+)+"},{b:"([a-z\\-]+\\.)+\\*"}]},{cN:"number",b:"\\b\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}(:\\d{1,5})?\\b"},{cN:"number",b:"\\b\\d+[kKmMgGdshdwy]*\\b",r:0},t]};return{aliases:["nginxconf"],c:[e.HCM,{b:e.UIR+"\\s",e:";|{",rB:!0,c:[{cN:"title",b:e.UIR,starts:r}],r:0}],i:"[^\\s\\}]"}}),hljs.registerLanguage("nimrod",function(e){return{aliases:["nim"],k:{keyword:"addr and as asm bind block break|0 case|0 cast const|0 continue|0 converter discard distinct|10 div do elif else|0 end|0 enum|0 except export finally for from generic if|0 import|0 in include|0 interface is isnot|10 iterator|10 let|0 macro method|10 mixin mod nil not notin|10 object|0 of or out proc|10 ptr raise ref|10 return shl shr static template|10 try|0 tuple type|0 using|0 var|0 when while|0 with without xor yield",literal:"shared guarded stdin stdout stderr result|10 true false"},c:[{cN:"decorator",b:/{\./,e:/\.}/,r:10},{cN:"string",b:/[a-zA-Z]\w*"/,e:/"/,c:[{b:/""/}]},{cN:"string",b:/([a-zA-Z]\w*)?"""/,e:/"""/},e.QSM,{cN:"type",b:/\b[A-Z]\w+\b/,r:0},{cN:"type",b:/\b(int|int8|int16|int32|int64|uint|uint8|uint16|uint32|uint64|float|float32|float64|bool|char|string|cstring|pointer|expr|stmt|void|auto|any|range|array|openarray|varargs|seq|set|clong|culong|cchar|cschar|cshort|cint|csize|clonglong|cfloat|cdouble|clongdouble|cuchar|cushort|cuint|culonglong|cstringarray|semistatic)\b/},{cN:"number",b:/\b(0[xX][0-9a-fA-F][_0-9a-fA-F]*)('?[iIuU](8|16|32|64))?/,r:0},{cN:"number",b:/\b(0o[0-7][_0-7]*)('?[iIuUfF](8|16|32|64))?/,r:0},{cN:"number",b:/\b(0(b|B)[01][_01]*)('?[iIuUfF](8|16|32|64))?/,r:0},{cN:"number",b:/\b(\d[_\d]*)('?[iIuUfF](8|16|32|64))?/,r:0},e.HCM]}}),hljs.registerLanguage("nix",function(e){var t={keyword:"rec with let in inherit assert if else then",constant:"true false or and null",built_in:"import abort baseNameOf dirOf isNull builtins map removeAttrs throw toString derivation"},r={cN:"subst",b:/\$\{/,e:/}/,k:t},a={cN:"variable",b:/[a-zA-Z0-9-_]+(\s*=)/},i={cN:"string",b:"''",e:"''",c:[r]},n={cN:"string",b:'"',e:'"',c:[r]},o=[e.NM,e.HCM,e.CBCM,i,n,a];return r.c=o,{aliases:["nixos"],k:t,c:o}}),hljs.registerLanguage("nsis",function(e){var t={cN:"symbol",b:"\\$(ADMINTOOLS|APPDATA|CDBURN_AREA|CMDLINE|COMMONFILES32|COMMONFILES64|COMMONFILES|COOKIES|DESKTOP|DOCUMENTS|EXEDIR|EXEFILE|EXEPATH|FAVORITES|FONTS|HISTORY|HWNDPARENT|INSTDIR|INTERNET_CACHE|LANGUAGE|LOCALAPPDATA|MUSIC|NETHOOD|OUTDIR|PICTURES|PLUGINSDIR|PRINTHOOD|PROFILE|PROGRAMFILES32|PROGRAMFILES64|PROGRAMFILES|QUICKLAUNCH|RECENT|RESOURCES_LOCALIZED|RESOURCES|SENDTO|SMPROGRAMS|SMSTARTUP|STARTMENU|SYSDIR|TEMP|TEMPLATES|VIDEOS|WINDIR)"},r={cN:"constant",b:"\\$+{[a-zA-Z0-9_]+}"},a={cN:"variable",b:"\\$+[a-zA-Z0-9_]+",i:"\\(\\){}"},i={cN:"constant",b:"\\$+\\([a-zA-Z0-9_]+\\)"},n={cN:"params",b:"(ARCHIVE|FILE_ATTRIBUTE_ARCHIVE|FILE_ATTRIBUTE_NORMAL|FILE_ATTRIBUTE_OFFLINE|FILE_ATTRIBUTE_READONLY|FILE_ATTRIBUTE_SYSTEM|FILE_ATTRIBUTE_TEMPORARY|HKCR|HKCU|HKDD|HKEY_CLASSES_ROOT|HKEY_CURRENT_CONFIG|HKEY_CURRENT_USER|HKEY_DYN_DATA|HKEY_LOCAL_MACHINE|HKEY_PERFORMANCE_DATA|HKEY_USERS|HKLM|HKPD|HKU|IDABORT|IDCANCEL|IDIGNORE|IDNO|IDOK|IDRETRY|IDYES|MB_ABORTRETRYIGNORE|MB_DEFBUTTON1|MB_DEFBUTTON2|MB_DEFBUTTON3|MB_DEFBUTTON4|MB_ICONEXCLAMATION|MB_ICONINFORMATION|MB_ICONQUESTION|MB_ICONSTOP|MB_OK|MB_OKCANCEL|MB_RETRYCANCEL|MB_RIGHT|MB_RTLREADING|MB_SETFOREGROUND|MB_TOPMOST|MB_USERICON|MB_YESNO|NORMAL|OFFLINE|READONLY|SHCTX|SHELL_CONTEXT|SYSTEM|TEMPORARY)"},o={cN:"constant",b:"\\!(addincludedir|addplugindir|appendfile|cd|define|delfile|echo|else|endif|error|execute|finalize|getdllversionsystem|ifdef|ifmacrodef|ifmacrondef|ifndef|if|include|insertmacro|macroend|macro|makensis|packhdr|searchparse|searchreplace|tempfile|undef|verbose|warning)"};return{cI:!1,k:{keyword:"Abort AddBrandingImage AddSize AllowRootDirInstall AllowSkipFiles AutoCloseWindow BGFont BGGradient BrandingText BringToFront Call CallInstDLL Caption ChangeUI CheckBitmap ClearErrors CompletedText ComponentText CopyFiles CRCCheck CreateDirectory CreateFont CreateShortCut Delete DeleteINISec DeleteINIStr DeleteRegKey DeleteRegValue DetailPrint DetailsButtonText DirText DirVar DirVerify EnableWindow EnumRegKey EnumRegValue Exch Exec ExecShell ExecWait ExpandEnvStrings File FileBufSize FileClose FileErrorText FileOpen FileRead FileReadByte FileReadUTF16LE FileReadWord FileSeek FileWrite FileWriteByte FileWriteUTF16LE FileWriteWord FindClose FindFirst FindNext FindWindow FlushINI FunctionEnd GetCurInstType GetCurrentAddress GetDlgItem GetDLLVersion GetDLLVersionLocal GetErrorLevel GetFileTime GetFileTimeLocal GetFullPathName GetFunctionAddress GetInstDirError GetLabelAddress GetTempFileName Goto HideWindow Icon IfAbort IfErrors IfFileExists IfRebootFlag IfSilent InitPluginsDir InstallButtonText InstallColors InstallDir InstallDirRegKey InstProgressFlags InstType InstTypeGetText InstTypeSetText IntCmp IntCmpU IntFmt IntOp IsWindow LangString LicenseBkColor LicenseData LicenseForceSelection LicenseLangString LicenseText LoadLanguageFile LockWindow LogSet LogText ManifestDPIAware ManifestSupportedOS MessageBox MiscButtonText Name Nop OutFile Page PageCallbacks PageExEnd Pop Push Quit ReadEnvStr ReadINIStr ReadRegDWORD ReadRegStr Reboot RegDLL Rename RequestExecutionLevel ReserveFile Return RMDir SearchPath SectionEnd SectionGetFlags SectionGetInstTypes SectionGetSize SectionGetText SectionGroupEnd SectionIn SectionSetFlags SectionSetInstTypes SectionSetSize SectionSetText SendMessage SetAutoClose SetBrandingImage SetCompress SetCompressor SetCompressorDictSize SetCtlColors SetCurInstType SetDatablockOptimize SetDateSave SetDetailsPrint SetDetailsView SetErrorLevel SetErrors SetFileAttributes SetFont SetOutPath SetOverwrite SetPluginUnload SetRebootFlag SetRegView SetShellVarContext SetSilent ShowInstDetails ShowUninstDetails ShowWindow SilentInstall SilentUnInstall Sleep SpaceTexts StrCmp StrCmpS StrCpy StrLen SubCaption SubSectionEnd Unicode UninstallButtonText UninstallCaption UninstallIcon UninstallSubCaption UninstallText UninstPage UnRegDLL Var VIAddVersionKey VIFileVersion VIProductVersion WindowIcon WriteINIStr WriteRegBin WriteRegDWORD WriteRegExpandStr WriteRegStr WriteUninstaller XPStyle",literal:"admin all auto both colored current false force hide highest lastused leave listonly none normal notset off on open print show silent silentlog smooth textonly true user "},c:[e.HCM,e.CBCM,{cN:"string",b:'"',e:'"',i:"\\n",c:[{cN:"symbol",b:"\\$(\\\\(n|r|t)|\\$)"},t,r,a,i]},e.C(";","$",{r:0}),{cN:"function",bK:"Function PageEx Section SectionGroup SubSection",e:"$"},o,r,a,i,n,e.NM,{cN:"literal",b:e.IR+"::"+e.IR}]}}),hljs.registerLanguage("objectivec",function(e){var t={cN:"built_in",b:"(AV|CA|CF|CG|CI|MK|MP|NS|UI)\\w+"},r={keyword:"int float while char export sizeof typedef const struct for union unsigned long volatile static bool mutable if do return goto void enum else break extern asm case short default double register explicit signed typename this switch continue wchar_t inline readonly assign readwrite self @synchronized id typeof nonatomic super unichar IBOutlet IBAction strong weak copy in out inout bycopy byref oneway __strong __weak __block __autoreleasing @private @protected @public @try @property @end @throw @catch @finally @autoreleasepool @synthesize @dynamic @selector @optional @required",literal:"false true FALSE TRUE nil YES NO NULL",built_in:"BOOL dispatch_once_t dispatch_queue_t dispatch_sync dispatch_async dispatch_once"},a=/[a-zA-Z@][a-zA-Z0-9_]*/,i="@interface @class @protocol @implementation";return{aliases:["m","mm","objc","obj-c"],k:r,l:a,i:"</",c:[t,e.CLCM,e.CBCM,e.CNM,e.QSM,{cN:"string",v:[{b:'@"',e:'"',i:"\\n",c:[e.BE]},{b:"'",e:"[^\\\\]'",i:"[^\\\\][^']"}]},{cN:"preprocessor",b:"#",e:"$",c:[{cN:"title",v:[{b:'"',e:'"'},{b:"<",e:">"}]}]},{cN:"class",b:"("+i.split(" ").join("|")+")\\b",e:"({|$)",eE:!0,k:i,l:a,c:[e.UTM]},{cN:"variable",b:"\\."+e.UIR,r:0}]}}),hljs.registerLanguage("ocaml",function(e){return{aliases:["ml"],k:{keyword:"and as assert asr begin class constraint do done downto else end exception external for fun function functor if in include inherit! inherit initializer land lazy let lor lsl lsr lxor match method!|10 method mod module mutable new object of open! open or private rec sig struct then to try type val! val virtual when while with parser value",built_in:"array bool bytes char exn|5 float int int32 int64 list lazy_t|5 nativeint|5 string unit in_channel out_channel ref",literal:"true false"},i:/\/\/|>>/,l:"[a-z_]\\w*!?",c:[{cN:"literal",b:"\\[(\\|\\|)?\\]|\\(\\)"},e.C("\\(\\*","\\*\\)",{c:["self"]}),{cN:"symbol",b:"'[A-Za-z_](?!')[\\w']*"},{cN:"tag",b:"`[A-Z][\\w']*"},{cN:"type",b:"\\b[A-Z][\\w']*",r:0},{b:"[a-z_]\\w*'[\\w']*"},e.inherit(e.ASM,{cN:"char",r:0}),e.inherit(e.QSM,{i:null}),{cN:"number",b:"\\b(0[xX][a-fA-F0-9_]+[Lln]?|0[oO][0-7_]+[Lln]?|0[bB][01_]+[Lln]?|[0-9][0-9_]*([Lln]|(\\.[0-9_]*)?([eE][-+]?[0-9_]+)?)?)",r:0},{b:/[-=]>/}]}}),hljs.registerLanguage("oxygene",function(e){var t="abstract add and array as asc aspect assembly async begin break block by case class concat const copy constructor continue create default delegate desc distinct div do downto dynamic each else empty end ensure enum equals event except exit extension external false final finalize finalizer finally flags for forward from function future global group has if implementation implements implies in index inherited inline interface into invariants is iterator join locked locking loop matching method mod module namespace nested new nil not notify nullable of old on operator or order out override parallel params partial pinned private procedure property protected public queryable raise read readonly record reintroduce remove repeat require result reverse sealed select self sequence set shl shr skip static step soft take then to true try tuple type union unit unsafe until uses using var virtual raises volatile where while with write xor yield await mapped deprecated stdcall cdecl pascal register safecall overload library platform reference packed strict published autoreleasepool selector strong weak unretained",r=e.C("{","}",{
r:0}),a=e.C("\\(\\*","\\*\\)",{r:10}),i={cN:"string",b:"'",e:"'",c:[{b:"''"}]},n={cN:"string",b:"(#\\d+)+"},o={cN:"function",bK:"function constructor destructor procedure method",e:"[:;]",k:"function constructor|10 destructor|10 procedure|10 method|10",c:[e.TM,{cN:"params",b:"\\(",e:"\\)",k:t,c:[i,n]},r,a]};return{cI:!0,k:t,i:'("|\\$[G-Zg-z]|\\/\\*|</|=>|->)',c:[r,a,e.CLCM,i,n,e.NM,o,{cN:"class",b:"=\\bclass\\b",e:"end;",k:t,c:[i,n,r,a,e.CLCM,o]}]}}),hljs.registerLanguage("parser3",function(e){var t=e.C("{","}",{c:["self"]});return{sL:"xml",r:0,c:[e.C("^#","$"),e.C("\\^rem{","}",{r:10,c:[t]}),{cN:"preprocessor",b:"^@(?:BASE|USE|CLASS|OPTIONS)$",r:10},{cN:"title",b:"@[\\w\\-]+\\[[\\w^;\\-]*\\](?:\\[[\\w^;\\-]*\\])?(?:.*)$"},{cN:"variable",b:"\\$\\{?[\\w\\-\\.\\:]+\\}?"},{cN:"keyword",b:"\\^[\\w\\-\\.\\:]+"},{cN:"number",b:"\\^#[0-9a-fA-F]+"},e.CNM]}}),hljs.registerLanguage("perl",function(e){var t="getpwent getservent quotemeta msgrcv scalar kill dbmclose undef lc ma syswrite tr send umask sysopen shmwrite vec qx utime local oct semctl localtime readpipe do return format read sprintf dbmopen pop getpgrp not getpwnam rewinddir qqfileno qw endprotoent wait sethostent bless s|0 opendir continue each sleep endgrent shutdown dump chomp connect getsockname die socketpair close flock exists index shmgetsub for endpwent redo lstat msgctl setpgrp abs exit select print ref gethostbyaddr unshift fcntl syscall goto getnetbyaddr join gmtime symlink semget splice x|0 getpeername recv log setsockopt cos last reverse gethostbyname getgrnam study formline endhostent times chop length gethostent getnetent pack getprotoent getservbyname rand mkdir pos chmod y|0 substr endnetent printf next open msgsnd readdir use unlink getsockopt getpriority rindex wantarray hex system getservbyport endservent int chr untie rmdir prototype tell listen fork shmread ucfirst setprotoent else sysseek link getgrgid shmctl waitpid unpack getnetbyname reset chdir grep split require caller lcfirst until warn while values shift telldir getpwuid my getprotobynumber delete and sort uc defined srand accept package seekdir getprotobyname semop our rename seek if q|0 chroot sysread setpwent no crypt getc chown sqrt write setnetent setpriority foreach tie sin msgget map stat getlogin unless elsif truncate exec keys glob tied closedirioctl socket readlink eval xor readline binmode setservent eof ord bind alarm pipe atan2 getgrent exp time push setgrent gt lt or ne m|0 break given say state when",r={cN:"subst",b:"[$@]\\{",e:"\\}",k:t},a={b:"->{",e:"}"},i={cN:"variable",v:[{b:/\$\d/},{b:/[\$%@](\^\w\b|#\w+(::\w+)*|{\w+}|\w+(::\w*)*)/},{b:/[\$%@][^\s\w{]/,r:0}]},n=e.C("^(__END__|__DATA__)","\\n$",{r:5}),o=[e.BE,r,i],s=[i,e.HCM,n,e.C("^\\=\\w","\\=cut",{eW:!0}),a,{cN:"string",c:o,v:[{b:"q[qwxr]?\\s*\\(",e:"\\)",r:5},{b:"q[qwxr]?\\s*\\[",e:"\\]",r:5},{b:"q[qwxr]?\\s*\\{",e:"\\}",r:5},{b:"q[qwxr]?\\s*\\|",e:"\\|",r:5},{b:"q[qwxr]?\\s*\\<",e:"\\>",r:5},{b:"qw\\s+q",e:"q",r:5},{b:"'",e:"'",c:[e.BE]},{b:'"',e:'"'},{b:"`",e:"`",c:[e.BE]},{b:"{\\w+}",c:[],r:0},{b:"-?\\w+\\s*\\=\\>",c:[],r:0}]},{cN:"number",b:"(\\b0[0-7_]+)|(\\b0x[0-9a-fA-F_]+)|(\\b[1-9][0-9_]*(\\.[0-9_]+)?)|[0_]\\b",r:0},{b:"(\\/\\/|"+e.RSR+"|\\b(split|return|print|reverse|grep)\\b)\\s*",k:"split return print reverse grep",r:0,c:[e.HCM,n,{cN:"regexp",b:"(s|tr|y)/(\\\\.|[^/])*/(\\\\.|[^/])*/[a-z]*",r:10},{cN:"regexp",b:"(m|qr)?/",e:"/[a-z]*",c:[e.BE],r:0}]},{cN:"sub",bK:"sub",e:"(\\s*\\(.*?\\))?[;{]",r:5},{cN:"operator",b:"-\\w\\b",r:0}];return r.c=s,a.c=s,{aliases:["pl"],k:t,c:s}}),hljs.registerLanguage("pf",function(e){var t={cN:"variable",b:/\$[\w\d#@][\w\d_]*/},r={cN:"variable",b:/</,e:/>/};return{aliases:["pf.conf"],l:/[a-z0-9_<>-]+/,k:{built_in:"block match pass load anchor|5 antispoof|10 set table",keyword:"in out log quick on rdomain inet inet6 proto from port os to routeallow-opts divert-packet divert-reply divert-to flags group icmp-typeicmp6-type label once probability recieved-on rtable prio queuetos tag tagged user keep fragment for os dropaf-to|10 binat-to|10 nat-to|10 rdr-to|10 bitmask least-stats random round-robinsource-hash static-portdup-to reply-to route-toparent bandwidth default min max qlimitblock-policy debug fingerprints hostid limit loginterface optimizationreassemble ruleset-optimization basic none profile skip state-defaultsstate-policy timeoutconst counters persistno modulate synproxy state|5 floating if-bound no-sync pflow|10 sloppysource-track global rule max-src-nodes max-src-states max-src-connmax-src-conn-rate overload flushscrub|5 max-mss min-ttl no-df|10 random-id",literal:"all any no-route self urpf-failed egress|5 unknown"},c:[e.HCM,e.NM,e.QSM,t,r]}}),hljs.registerLanguage("php",function(e){var t={cN:"variable",b:"\\$+[a-zA-Z_-ÿ][a-zA-Z0-9_-ÿ]*"},r={cN:"preprocessor",b:/<\?(php)?|\?>/},a={cN:"string",c:[e.BE,r],v:[{b:'b"',e:'"'},{b:"b'",e:"'"},e.inherit(e.ASM,{i:null}),e.inherit(e.QSM,{i:null})]},i={v:[e.BNM,e.CNM]};return{aliases:["php3","php4","php5","php6"],cI:!0,k:"and include_once list abstract global private echo interface as static endswitch array null if endwhile or const for endforeach self var while isset public protected exit foreach throw elseif include __FILE__ empty require_once do xor return parent clone use __CLASS__ __LINE__ else break print eval new catch __METHOD__ case exception default die require __FUNCTION__ enddeclare final try switch continue endfor endif declare unset true false trait goto instanceof insteadof __DIR__ __NAMESPACE__ yield finally",c:[e.CLCM,e.HCM,e.C("/\\*","\\*/",{c:[{cN:"phpdoc",b:"\\s@[A-Za-z]+"},r]}),e.C("__halt_compiler.+?;",!1,{eW:!0,k:"__halt_compiler",l:e.UIR}),{cN:"string",b:"<<<['\"]?\\w+['\"]?$",e:"^\\w+;",c:[e.BE]},r,t,{b:/(::|->)+[a-zA-Z_\x7f-\xff][a-zA-Z0-9_\x7f-\xff]*/},{cN:"function",bK:"function",e:/[;{]/,eE:!0,i:"\\$|\\[|%",c:[e.UTM,{cN:"params",b:"\\(",e:"\\)",c:["self",t,e.CBCM,a,i]}]},{cN:"class",bK:"class interface",e:"{",eE:!0,i:/[:\(\$"]/,c:[{bK:"extends implements"},e.UTM]},{bK:"namespace",e:";",i:/[\.']/,c:[e.UTM]},{bK:"use",e:";",c:[e.UTM]},{b:"=>"},a,i]}}),hljs.registerLanguage("powershell",function(e){var t={b:"`[\\s\\S]",r:0},r={cN:"variable",v:[{b:/\$[\w\d][\w\d_:]*/}]},a={cN:"string",b:/"/,e:/"/,c:[t,r,{cN:"variable",b:/\$[A-z]/,e:/[^A-z]/}]},i={cN:"string",b:/'/,e:/'/};return{aliases:["ps"],l:/-?[A-z\.\-]+/,cI:!0,k:{keyword:"if else foreach return function do while until elseif begin for trap data dynamicparam end break throw param continue finally in switch exit filter try process catch",literal:"$null $true $false",built_in:"Add-Content Add-History Add-Member Add-PSSnapin Clear-Content Clear-Item Clear-Item Property Clear-Variable Compare-Object ConvertFrom-SecureString Convert-Path ConvertTo-Html ConvertTo-SecureString Copy-Item Copy-ItemProperty Export-Alias Export-Clixml Export-Console Export-Csv ForEach-Object Format-Custom Format-List Format-Table Format-Wide Get-Acl Get-Alias Get-AuthenticodeSignature Get-ChildItem Get-Command Get-Content Get-Credential Get-Culture Get-Date Get-EventLog Get-ExecutionPolicy Get-Help Get-History Get-Host Get-Item Get-ItemProperty Get-Location Get-Member Get-PfxCertificate Get-Process Get-PSDrive Get-PSProvider Get-PSSnapin Get-Service Get-TraceSource Get-UICulture Get-Unique Get-Variable Get-WmiObject Group-Object Import-Alias Import-Clixml Import-Csv Invoke-Expression Invoke-History Invoke-Item Join-Path Measure-Command Measure-Object Move-Item Move-ItemProperty New-Alias New-Item New-ItemProperty New-Object New-PSDrive New-Service New-TimeSpan New-Variable Out-Default Out-File Out-Host Out-Null Out-Printer Out-String Pop-Location Push-Location Read-Host Remove-Item Remove-ItemProperty Remove-PSDrive Remove-PSSnapin Remove-Variable Rename-Item Rename-ItemProperty Resolve-Path Restart-Service Resume-Service Select-Object Select-String Set-Acl Set-Alias Set-AuthenticodeSignature Set-Content Set-Date Set-ExecutionPolicy Set-Item Set-ItemProperty Set-Location Set-PSDebug Set-Service Set-TraceSource Set-Variable Sort-Object Split-Path Start-Service Start-Sleep Start-Transcript Stop-Process Stop-Service Stop-Transcript Suspend-Service Tee-Object Test-Path Trace-Command Update-FormatData Update-TypeData Where-Object Write-Debug Write-Error Write-Host Write-Output Write-Progress Write-Verbose Write-Warning",operator:"-ne -eq -lt -gt -ge -le -not -like -notlike -match -notmatch -contains -notcontains -in -notin -replace"},c:[e.HCM,e.NM,a,i,r]}}),hljs.registerLanguage("processing",function(e){return{k:{keyword:"BufferedReader PVector PFont PImage PGraphics HashMap boolean byte char color double float int long String Array FloatDict FloatList IntDict IntList JSONArray JSONObject Object StringDict StringList Table TableRow XML false synchronized int abstract float private char boolean static null if const for true while long throw strictfp finally protected import native final return void enum else break transient new catch instanceof byte super volatile case assert short package default double public try this switch continue throws protected public private",constant:"P2D P3D HALF_PI PI QUARTER_PI TAU TWO_PI",variable:"displayHeight displayWidth mouseY mouseX mousePressed pmouseX pmouseY key keyCode pixels focused frameCount frameRate height width",title:"setup draw",built_in:"size createGraphics beginDraw createShape loadShape PShape arc ellipse line point quad rect triangle bezier bezierDetail bezierPoint bezierTangent curve curveDetail curvePoint curveTangent curveTightness shape shapeMode beginContour beginShape bezierVertex curveVertex endContour endShape quadraticVertex vertex ellipseMode noSmooth rectMode smooth strokeCap strokeJoin strokeWeight mouseClicked mouseDragged mouseMoved mousePressed mouseReleased mouseWheel keyPressed keyPressedkeyReleased keyTyped print println save saveFrame day hour millis minute month second year background clear colorMode fill noFill noStroke stroke alpha blue brightness color green hue lerpColor red saturation modelX modelY modelZ screenX screenY screenZ ambient emissive shininess specular add createImage beginCamera camera endCamera frustum ortho perspective printCamera printProjection cursor frameRate noCursor exit loop noLoop popStyle pushStyle redraw binary boolean byte char float hex int str unbinary unhex join match matchAll nf nfc nfp nfs split splitTokens trim append arrayCopy concat expand reverse shorten sort splice subset box sphere sphereDetail createInput createReader loadBytes loadJSONArray loadJSONObject loadStrings loadTable loadXML open parseXML saveTable selectFolder selectInput beginRaw beginRecord createOutput createWriter endRaw endRecord PrintWritersaveBytes saveJSONArray saveJSONObject saveStream saveStrings saveXML selectOutput popMatrix printMatrix pushMatrix resetMatrix rotate rotateX rotateY rotateZ scale shearX shearY translate ambientLight directionalLight lightFalloff lights lightSpecular noLights normal pointLight spotLight image imageMode loadImage noTint requestImage tint texture textureMode textureWrap blend copy filter get loadPixels set updatePixels blendMode loadShader PShaderresetShader shader createFont loadFont text textFont textAlign textLeading textMode textSize textWidth textAscent textDescent abs ceil constrain dist exp floor lerp log mag map max min norm pow round sq sqrt acos asin atan atan2 cos degrees radians sin tan noise noiseDetail noiseSeed random randomGaussian randomSeed"},c:[e.CLCM,e.CBCM,e.ASM,e.QSM,e.CNM]}}),hljs.registerLanguage("profile",function(e){return{c:[e.CNM,{cN:"built_in",b:"{",e:"}$",eB:!0,eE:!0,c:[e.ASM,e.QSM],r:0},{cN:"filename",b:"[a-zA-Z_][\\da-zA-Z_]+\\.[\\da-zA-Z_]{1,3}",e:":",eE:!0},{cN:"header",b:"(ncalls|tottime|cumtime)",e:"$",k:"ncalls tottime|10 cumtime|10 filename",r:10},{cN:"summary",b:"function calls",e:"$",c:[e.CNM],r:10},e.ASM,e.QSM,{cN:"function",b:"\\(",e:"\\)$",c:[e.UTM],r:0}]}}),hljs.registerLanguage("prolog",function(e){var t={cN:"atom",b:/[a-z][A-Za-z0-9_]*/,r:0},r={cN:"name",v:[{b:/[A-Z][a-zA-Z0-9_]*/},{b:/_[A-Za-z0-9_]*/}],r:0},a={b:/\(/,e:/\)/,r:0},i={b:/\[/,e:/\]/},n={cN:"comment",b:/%/,e:/$/,c:[e.PWM]},o={cN:"string",b:/`/,e:/`/,c:[e.BE]},s={cN:"string",b:/0\'(\\\'|.)/},l={cN:"string",b:/0\'\\s/},c={b:/:-/},d=[t,r,a,c,i,n,e.CBCM,e.QSM,e.ASM,o,s,l,e.CNM];return a.c=d,i.c=d,{c:d.concat([{b:/\.$/}])}}),hljs.registerLanguage("protobuf",function(e){return{k:{keyword:"package import option optional required repeated group",built_in:"double float int32 int64 uint32 uint64 sint32 sint64 fixed32 fixed64 sfixed32 sfixed64 bool string bytes",literal:"true false"},c:[e.QSM,e.NM,e.CLCM,{cN:"class",bK:"message enum service",e:/\{/,i:/\n/,c:[e.inherit(e.TM,{starts:{eW:!0,eE:!0}})]},{cN:"function",bK:"rpc",e:/;/,eE:!0,k:"rpc returns"},{cN:"constant",b:/^\s*[A-Z_]+/,e:/\s*=/,eE:!0}]}}),hljs.registerLanguage("puppet",function(e){var t="augeas computer cron exec file filebucket host interface k5login macauthorization mailalias maillist mcx mount nagios_command nagios_contact nagios_contactgroup nagios_host nagios_hostdependency nagios_hostescalation nagios_hostextinfo nagios_hostgroup nagios_service firewall nagios_servicedependency nagios_serviceescalation nagios_serviceextinfo nagios_servicegroup nagios_timeperiod notify package resources router schedule scheduled_task selboolean selmodule service ssh_authorized_key sshkey stage tidy user vlan yumrepo zfs zone zpool",r="alias audit before loglevel noop require subscribe tag owner ensure group mode name|0 changes context force incl lens load_path onlyif provider returns root show_diff type_check en_address ip_address realname command environment hour monute month monthday special target weekday creates cwd ogoutput refresh refreshonly tries try_sleep umask backup checksum content ctime force ignore links mtime purge recurse recurselimit replace selinux_ignore_defaults selrange selrole seltype seluser source souirce_permissions sourceselect validate_cmd validate_replacement allowdupe attribute_membership auth_membership forcelocal gid ia_load_module members system host_aliases ip allowed_trunk_vlans description device_url duplex encapsulation etherchannel native_vlan speed principals allow_root auth_class auth_type authenticate_user k_of_n mechanisms rule session_owner shared options device fstype enable hasrestart directory present absent link atboot blockdevice device dump pass remounts poller_tag use message withpath adminfile allow_virtual allowcdrom category configfiles flavor install_options instance package_settings platform responsefile status uninstall_options vendor unless_system_user unless_uid binary control flags hasstatus manifest pattern restart running start stop allowdupe auths expiry gid groups home iterations key_membership keys managehome membership password password_max_age password_min_age profile_membership profiles project purge_ssh_keys role_membership roles salt shell uid baseurl cost descr enabled enablegroups exclude failovermethod gpgcheck gpgkey http_caching include includepkgs keepalive metadata_expire metalink mirrorlist priority protect proxy proxy_password proxy_username repo_gpgcheck s3_enabled skip_if_unavailable sslcacert sslclientcert sslclientkey sslverify mounted",a={keyword:"and case class default define else elsif false if in import enherits node or true undef unless main settings $string "+t,literal:r,built_in:"architecture augeasversion blockdevices boardmanufacturer boardproductname boardserialnumber cfkey dhcp_servers domain ec2_ ec2_userdata facterversion filesystems ldom fqdn gid hardwareisa hardwaremodel hostname id|0 interfaces ipaddress ipaddress_ ipaddress6 ipaddress6_ iphostnumber is_virtual kernel kernelmajversion kernelrelease kernelversion kernelrelease kernelversion lsbdistcodename lsbdistdescription lsbdistid lsbdistrelease lsbmajdistrelease lsbminordistrelease lsbrelease macaddress macaddress_ macosx_buildversion macosx_productname macosx_productversion macosx_productverson_major macosx_productversion_minor manufacturer memoryfree memorysize netmask metmask_ network_ operatingsystem operatingsystemmajrelease operatingsystemrelease osfamily partitions path physicalprocessorcount processor processorcount productname ps puppetversion rubysitedir rubyversion selinux selinux_config_mode selinux_config_policy selinux_current_mode selinux_current_mode selinux_enforced selinux_policyversion serialnumber sp_ sshdsakey sshecdsakey sshrsakey swapencrypted swapfree swapsize timezone type uniqueid uptime uptime_days uptime_hours uptime_seconds uuid virtual vlans xendomains zfs_version zonenae zones zpool_version"},i=e.C("#","$"),n={cN:"string",c:[e.BE],v:[{b:/'/,e:/'/},{b:/"/,e:/"/}]},o=[n,i,{cN:"keyword",bK:"class",e:"$|;",i:/=/,c:[e.inherit(e.TM,{b:"(::)?[A-Za-z_]\\w*(::\\w+)*"}),i,n]},{cN:"keyword",b:"([a-zA-Z_(::)]+ *\\{)",c:[n,i],r:0},{cN:"keyword",b:"(\\}|\\{)",r:0},{cN:"function",b:"[a-zA-Z_]+\\s*=>"},{cN:"constant",b:"(::)?(\\b[A-Z][a-z_]*(::)?)+",r:0},{cN:"number",b:"(\\b0[0-7_]+)|(\\b0x[0-9a-fA-F_]+)|(\\b[1-9][0-9_]*(\\.[0-9_]+)?)|[0_]\\b",r:0}];return{aliases:["pp"],k:a,c:o}}),hljs.registerLanguage("python",function(e){var t={cN:"prompt",b:/^(>>>|\.\.\.) /},r={cN:"string",c:[e.BE],v:[{b:/(u|b)?r?'''/,e:/'''/,c:[t],r:10},{b:/(u|b)?r?"""/,e:/"""/,c:[t],r:10},{b:/(u|r|ur)'/,e:/'/,r:10},{b:/(u|r|ur)"/,e:/"/,r:10},{b:/(b|br)'/,e:/'/},{b:/(b|br)"/,e:/"/},e.ASM,e.QSM]},a={cN:"number",r:0,v:[{b:e.BNR+"[lLjJ]?"},{b:"\\b(0o[0-7]+)[lLjJ]?"},{b:e.CNR+"[lLjJ]?"}]},i={cN:"params",b:/\(/,e:/\)/,c:["self",t,a,r]};return{aliases:["py","gyp"],k:{keyword:"and elif is global as in if from raise for except finally print import pass return exec else break not with class assert yield try while continue del or def lambda nonlocal|10 None True False",built_in:"Ellipsis NotImplemented"},i:/(<\/|->|\?)/,c:[t,a,r,e.HCM,{v:[{cN:"function",bK:"def",r:10},{cN:"class",bK:"class"}],e:/:/,i:/[${=;\n,]/,c:[e.UTM,i]},{cN:"decorator",b:/@/,e:/$/},{b:/\b(print|exec)\(/}]}}),hljs.registerLanguage("q",function(e){var t={keyword:"do while select delete by update from",constant:"0b 1b",built_in:"neg not null string reciprocal floor ceiling signum mod xbar xlog and or each scan over prior mmu lsq inv md5 ltime gtime count first var dev med cov cor all any rand sums prds mins maxs fills deltas ratios avgs differ prev next rank reverse iasc idesc asc desc msum mcount mavg mdev xrank mmin mmax xprev rotate distinct group where flip type key til get value attr cut set upsert raze union inter except cross sv vs sublist enlist read0 read1 hopen hclose hdel hsym hcount peach system ltrim rtrim trim lower upper ssr view tables views cols xcols keys xkey xcol xasc xdesc fkeys meta lj aj aj0 ij pj asof uj ww wj wj1 fby xgroup ungroup ej save load rsave rload show csv parse eval min max avg wavg wsum sin cos tan sum",typename:"`float `double int `timestamp `timespan `datetime `time `boolean `symbol `char `byte `short `long `real `month `date `minute `second `guid"};return{aliases:["k","kdb"],k:t,l:/\b(`?)[A-Za-z0-9_]+\b/,c:[e.CLCM,e.QSM,e.CNM]}}),hljs.registerLanguage("r",function(e){var t="([a-zA-Z]|\\.[a-zA-Z.])[a-zA-Z0-9._]*";return{c:[e.HCM,{b:t,l:t,k:{keyword:"function if in break next repeat else for return switch while try tryCatch stop warning require library attach detach source setMethod setGeneric setGroupGeneric setClass ...",literal:"NULL NA TRUE FALSE T F Inf NaN NA_integer_|10 NA_real_|10 NA_character_|10 NA_complex_|10"},r:0},{cN:"number",b:"0[xX][0-9a-fA-F]+[Li]?\\b",r:0},{cN:"number",b:"\\d+(?:[eE][+\\-]?\\d*)?L\\b",r:0},{cN:"number",b:"\\d+\\.(?!\\d)(?:i\\b)?",r:0},{cN:"number",b:"\\d+(?:\\.\\d*)?(?:[eE][+\\-]?\\d*)?i?\\b",r:0},{cN:"number",b:"\\.\\d+(?:[eE][+\\-]?\\d*)?i?\\b",r:0},{b:"`",e:"`",r:0},{cN:"string",c:[e.BE],v:[{b:'"',e:'"'},{b:"'",e:"'"}]}]}}),hljs.registerLanguage("rib",function(e){return{k:"ArchiveRecord AreaLightSource Atmosphere Attribute AttributeBegin AttributeEnd Basis Begin Blobby Bound Clipping ClippingPlane Color ColorSamples ConcatTransform Cone CoordinateSystem CoordSysTransform CropWindow Curves Cylinder DepthOfField Detail DetailRange Disk Displacement Display End ErrorHandler Exposure Exterior Format FrameAspectRatio FrameBegin FrameEnd GeneralPolygon GeometricApproximation Geometry Hider Hyperboloid Identity Illuminate Imager Interior LightSource MakeCubeFaceEnvironment MakeLatLongEnvironment MakeShadow MakeTexture Matte MotionBegin MotionEnd NuPatch ObjectBegin ObjectEnd ObjectInstance Opacity Option Orientation Paraboloid Patch PatchMesh Perspective PixelFilter PixelSamples PixelVariance Points PointsGeneralPolygons PointsPolygons Polygon Procedural Projection Quantize ReadArchive RelativeDetail ReverseOrientation Rotate Scale ScreenWindow ShadingInterpolation ShadingRate Shutter Sides Skew SolidBegin SolidEnd Sphere SubdivisionMesh Surface TextureCoordinates Torus Transform TransformBegin TransformEnd TransformPoints Translate TrimCurve WorldBegin WorldEnd",i:"</",c:[e.HCM,e.CNM,e.ASM,e.QSM]}}),hljs.registerLanguage("roboconf",function(e){var t="[a-zA-Z-_][^\n{\r\n]+\\{";return{aliases:["graph","instances"],cI:!0,k:"import",c:[{cN:"facet",b:"^facet "+t,e:"}",k:"facet installer exports children extends",c:[e.HCM]},{cN:"instance-of",b:"^instance of "+t,e:"}",k:"name count channels instance-data instance-state instance of",c:[{cN:"keyword",b:"[a-zA-Z-_]+( |	)*:"},e.HCM]},{cN:"component",b:"^"+t,e:"}",l:"\\(?[a-zA-Z]+\\)?",k:"installer exports children extends imports facets alias (optional)",c:[{cN:"string",b:"\\.[a-zA-Z-_]+",e:"\\s|,|;",eE:!0},e.HCM]},e.HCM]}}),hljs.registerLanguage("rsl",function(e){return{k:{keyword:"float color point normal vector matrix while for if do return else break extern continue",built_in:"abs acos ambient area asin atan atmosphere attribute calculatenormal ceil cellnoise clamp comp concat cos degrees depth Deriv diffuse distance Du Dv environment exp faceforward filterstep floor format fresnel incident length lightsource log match max min mod noise normalize ntransform opposite option phong pnoise pow printf ptlined radians random reflect refract renderinfo round setcomp setxcomp setycomp setzcomp shadow sign sin smoothstep specular specularbrdf spline sqrt step tan texture textureinfo trace transform vtransform xcomp ycomp zcomp"},i:"</",c:[e.CLCM,e.CBCM,e.QSM,e.ASM,e.CNM,{cN:"preprocessor",b:"#",e:"$"},{cN:"shader",bK:"surface displacement light volume imager",e:"\\("},{cN:"shading",bK:"illuminate illuminance gather",e:"\\("}]}}),hljs.registerLanguage("ruleslanguage",function(e){return{k:{keyword:"BILL_PERIOD BILL_START BILL_STOP RS_EFFECTIVE_START RS_EFFECTIVE_STOP RS_JURIS_CODE RS_OPCO_CODE INTDADDATTRIBUTE|5 INTDADDVMSG|5 INTDBLOCKOP|5 INTDBLOCKOPNA|5 INTDCLOSE|5 INTDCOUNT|5 INTDCOUNTSTATUSCODE|5 INTDCREATEMASK|5 INTDCREATEDAYMASK|5 INTDCREATEFACTORMASK|5 INTDCREATEHANDLE|5 INTDCREATEOVERRIDEDAYMASK|5 INTDCREATEOVERRIDEMASK|5 INTDCREATESTATUSCODEMASK|5 INTDCREATETOUPERIOD|5 INTDDELETE|5 INTDDIPTEST|5 INTDEXPORT|5 INTDGETERRORCODE|5 INTDGETERRORMESSAGE|5 INTDISEQUAL|5 INTDJOIN|5 INTDLOAD|5 INTDLOADACTUALCUT|5 INTDLOADDATES|5 INTDLOADHIST|5 INTDLOADLIST|5 INTDLOADLISTDATES|5 INTDLOADLISTENERGY|5 INTDLOADLISTHIST|5 INTDLOADRELATEDCHANNEL|5 INTDLOADSP|5 INTDLOADSTAGING|5 INTDLOADUOM|5 INTDLOADUOMDATES|5 INTDLOADUOMHIST|5 INTDLOADVERSION|5 INTDOPEN|5 INTDREADFIRST|5 INTDREADNEXT|5 INTDRECCOUNT|5 INTDRELEASE|5 INTDREPLACE|5 INTDROLLAVG|5 INTDROLLPEAK|5 INTDSCALAROP|5 INTDSCALE|5 INTDSETATTRIBUTE|5 INTDSETDSTPARTICIPANT|5 INTDSETSTRING|5 INTDSETVALUE|5 INTDSETVALUESTATUS|5 INTDSHIFTSTARTTIME|5 INTDSMOOTH|5 INTDSORT|5 INTDSPIKETEST|5 INTDSUBSET|5 INTDTOU|5 INTDTOURELEASE|5 INTDTOUVALUE|5 INTDUPDATESTATS|5 INTDVALUE|5 STDEV INTDDELETEEX|5 INTDLOADEXACTUAL|5 INTDLOADEXCUT|5 INTDLOADEXDATES|5 INTDLOADEX|5 INTDLOADEXRELATEDCHANNEL|5 INTDSAVEEX|5 MVLOAD|5 MVLOADACCT|5 MVLOADACCTDATES|5 MVLOADACCTHIST|5 MVLOADDATES|5 MVLOADHIST|5 MVLOADLIST|5 MVLOADLISTDATES|5 MVLOADLISTHIST|5 IF FOR NEXT DONE SELECT END CALL ABORT CLEAR CHANNEL FACTOR LIST NUMBER OVERRIDE SET WEEK DISTRIBUTIONNODE ELSE WHEN THEN OTHERWISE IENUM CSV INCLUDE LEAVE RIDER SAVE DELETE NOVALUE SECTION WARN SAVE_UPDATE DETERMINANT LABEL REPORT REVENUE EACH IN FROM TOTAL CHARGE BLOCK AND OR CSV_FILE RATE_CODE AUXILIARY_DEMAND UIDACCOUNT RS BILL_PERIOD_SELECT HOURS_PER_MONTH INTD_ERROR_STOP SEASON_SCHEDULE_NAME ACCOUNTFACTOR ARRAYUPPERBOUND CALLSTOREDPROC GETADOCONNECTION GETCONNECT GETDATASOURCE GETQUALIFIER GETUSERID HASVALUE LISTCOUNT LISTOP LISTUPDATE LISTVALUE PRORATEFACTOR RSPRORATE SETBINPATH SETDBMONITOR WQ_OPEN BILLINGHOURS DATE DATEFROMFLOAT DATETIMEFROMSTRING DATETIMETOSTRING DATETOFLOAT DAY DAYDIFF DAYNAME DBDATETIME HOUR MINUTE MONTH MONTHDIFF MONTHHOURS MONTHNAME ROUNDDATE SAMEWEEKDAYLASTYEAR SECOND WEEKDAY WEEKDIFF YEAR YEARDAY YEARSTR COMPSUM HISTCOUNT HISTMAX HISTMIN HISTMINNZ HISTVALUE MAXNRANGE MAXRANGE MINRANGE COMPIKVA COMPKVA COMPKVARFROMKQKW COMPLF IDATTR FLAG LF2KW LF2KWH MAXKW POWERFACTOR READING2USAGE AVGSEASON MAXSEASON MONTHLYMERGE SEASONVALUE SUMSEASON ACCTREADDATES ACCTTABLELOAD CONFIGADD CONFIGGET CREATEOBJECT CREATEREPORT EMAILCLIENT EXPBLKMDMUSAGE EXPMDMUSAGE EXPORT_USAGE FACTORINEFFECT GETUSERSPECIFIEDSTOP INEFFECT ISHOLIDAY RUNRATE SAVE_PROFILE SETREPORTTITLE USEREXIT WATFORRUNRATE TO TABLE ACOS ASIN ATAN ATAN2 BITAND CEIL COS COSECANT COSH COTANGENT DIVQUOT DIVREM EXP FABS FLOOR FMOD FREPM FREXPN LOG LOG10 MAX MAXN MIN MINNZ MODF POW ROUND ROUND2VALUE ROUNDINT SECANT SIN SINH SQROOT TAN TANH FLOAT2STRING FLOAT2STRINGNC INSTR LEFT LEN LTRIM MID RIGHT RTRIM STRING STRINGNC TOLOWER TOUPPER TRIM NUMDAYS READ_DATE STAGING",built_in:"IDENTIFIER OPTIONS XML_ELEMENT XML_OP XML_ELEMENT_OF DOMDOCCREATE DOMDOCLOADFILE DOMDOCLOADXML DOMDOCSAVEFILE DOMDOCGETROOT DOMDOCADDPI DOMNODEGETNAME DOMNODEGETTYPE DOMNODEGETVALUE DOMNODEGETCHILDCT DOMNODEGETFIRSTCHILD DOMNODEGETSIBLING DOMNODECREATECHILDELEMENT DOMNODESETATTRIBUTE DOMNODEGETCHILDELEMENTCT DOMNODEGETFIRSTCHILDELEMENT DOMNODEGETSIBLINGELEMENT DOMNODEGETATTRIBUTECT DOMNODEGETATTRIBUTEI DOMNODEGETATTRIBUTEBYNAME DOMNODEGETBYNAME"},c:[e.CLCM,e.CBCM,e.ASM,e.QSM,e.CNM,{cN:"array",b:"#[a-zA-Z .]+"}]}}),hljs.registerLanguage("rust",function(e){var t=e.inherit(e.CBCM);return t.c.push("self"),{aliases:["rs"],k:{keyword:"alignof as be box break const continue crate do else enum extern false fn for if impl in let loop match mod mut offsetof once priv proc pub pure ref return self sizeof static struct super trait true type typeof unsafe unsized use virtual while yield int i8 i16 i32 i64 uint u8 u32 u64 float f32 f64 str char bool",built_in:"assert! assert_eq! bitflags! bytes! cfg! col! concat! concat_idents! debug_assert! debug_assert_eq! env! panic! file! format! format_args! include_bin! include_str! line! local_data_key! module_path! option_env! print! println! select! stringify! try! unimplemented! unreachable! vec! write! writeln!"},l:e.IR+"!?",i:"</",c:[e.CLCM,t,e.inherit(e.QSM,{i:null}),{cN:"string",b:/r(#*)".*?"\1(?!#)/},{cN:"string",b:/'\\?(x\w{2}|u\w{4}|U\w{8}|.)'/},{b:/'[a-zA-Z_][a-zA-Z0-9_]*/},{cN:"number",b:/\b(0[xbo][A-Fa-f0-9_]+|\d[\d_]*(\.[0-9_]+)?([eE][+-]?[0-9_]+)?)([uif](8|16|32|64|size))?/,r:0},{cN:"function",bK:"fn",e:"(\\(|<)",eE:!0,c:[e.UTM]},{cN:"preprocessor",b:"#\\!?\\[",e:"\\]"},{bK:"type",e:"(=|<)",c:[e.UTM],i:"\\S"},{bK:"trait enum",e:"({|<)",c:[e.UTM],i:"\\S"},{b:e.IR+"::"},{b:"->"}]}}),hljs.registerLanguage("scala",function(e){var t={cN:"annotation",b:"@[A-Za-z]+"},r={cN:"string",b:'u?r?"""',e:'"""',r:10},a={cN:"symbol",b:"'\\w[\\w\\d_]*(?!')"},i={cN:"type",b:"\\b[A-Z][A-Za-z0-9_]*",r:0},n={cN:"title",b:/[^0-9\n\t "'(),.`{}\[\]:;][^\n\t "'(),.`{}\[\]:;]+|[^0-9\n\t "'(),.`{}\[\]:;=]/,r:0},o={cN:"class",bK:"class object trait type",e:/[:={\[(\n;]/,c:[{cN:"keyword",bK:"extends with",r:10},n]},s={cN:"function",bK:"def val",e:/[:={\[(\n;]/,c:[n]};return{k:{literal:"true false null",keyword:"type yield lazy override def with val var sealed abstract private trait object if forSome for while throw finally protected extends import final return else break new catch super class case package default try this match continue throws implicit"},c:[e.CLCM,e.CBCM,r,e.QSM,a,i,s,o,e.CNM,t]}}),hljs.registerLanguage("scheme",function(e){var t="[^\\(\\)\\[\\]\\{\\}\",'`;#|\\\\\\s]+",r="(\\-|\\+)?\\d+([./]\\d+)?",a=r+"[+\\-]"+r+"i",i={built_in:"case-lambda call/cc class define-class exit-handler field import inherit init-field interface let*-values let-values let/ec mixin opt-lambda override protect provide public rename require require-for-syntax syntax syntax-case syntax-error unit/sig unless when with-syntax and begin call-with-current-continuation call-with-input-file call-with-output-file case cond define define-syntax delay do dynamic-wind else for-each if lambda let let* let-syntax letrec letrec-syntax map or syntax-rules ' * + , ,@ - ... / ; < <= = => > >= ` abs acos angle append apply asin assoc assq assv atan boolean? caar cadr call-with-input-file call-with-output-file call-with-values car cdddar cddddr cdr ceiling char->integer char-alphabetic? char-ci<=? char-ci<? char-ci=? char-ci>=? char-ci>? char-downcase char-lower-case? char-numeric? char-ready? char-upcase char-upper-case? char-whitespace? char<=? char<? char=? char>=? char>? char? close-input-port close-output-port complex? cons cos current-input-port current-output-port denominator display eof-object? eq? equal? eqv? eval even? exact->inexact exact? exp expt floor force gcd imag-part inexact->exact inexact? input-port? integer->char integer? interaction-environment lcm length list list->string list->vector list-ref list-tail list? load log magnitude make-polar make-rectangular make-string make-vector max member memq memv min modulo negative? newline not null-environment null? number->string number? numerator odd? open-input-file open-output-file output-port? pair? peek-char port? positive? procedure? quasiquote quote quotient rational? rationalize read read-char real-part real? remainder reverse round scheme-report-environment set! set-car! set-cdr! sin sqrt string string->list string->number string->symbol string-append string-ci<=? string-ci<? string-ci=? string-ci>=? string-ci>? string-copy string-fill! string-length string-ref string-set! string<=? string<? string=? string>=? string>? string? substring symbol->string symbol? tan transcript-off transcript-on truncate values vector vector->list vector-fill! vector-length vector-ref vector-set! with-input-from-file with-output-to-file write write-char zero?"},n={cN:"shebang",b:"^#!",e:"$"},o={cN:"literal",b:"(#t|#f|#\\\\"+t+"|#\\\\.)"},s={cN:"number",v:[{b:r,r:0},{b:a,r:0},{b:"#b[0-1]+(/[0-1]+)?"},{b:"#o[0-7]+(/[0-7]+)?"},{b:"#x[0-9a-f]+(/[0-9a-f]+)?"}]},l=e.QSM,c=[e.C(";","$",{r:0}),e.C("#\\|","\\|#")],d={b:t,r:0},p={cN:"variable",b:"'"+t},u={eW:!0,r:0},m={cN:"list",v:[{b:"\\(",e:"\\)"},{b:"\\[",e:"\\]"}],c:[{cN:"keyword",b:t,l:t,k:i},u]};return u.c=[o,s,l,d,p,m].concat(c),{i:/\S/,c:[n,s,l,p,m].concat(c)}}),hljs.registerLanguage("scilab",function(e){var t=[e.CNM,{cN:"string",b:"'|\"",e:"'|\"",c:[e.BE,{b:"''"}]}];return{aliases:["sci"],k:{keyword:"abort break case clear catch continue do elseif else endfunction end for functionglobal if pause return resume select try then while%f %F %t %T %pi %eps %inf %nan %e %i %z %s",built_in:"abs and acos asin atan ceil cd chdir clearglobal cosh cos cumprod deff disp errorexec execstr exists exp eye gettext floor fprintf fread fsolve imag isdef isemptyisinfisnan isvector lasterror length load linspace list listfiles log10 log2 logmax min msprintf mclose mopen ones or pathconvert poly printf prod pwd rand realround sinh sin size gsort sprintf sqrt strcat strcmps tring sum system tanh tantype typename warning zeros matrix"},i:'("|#|/\\*|\\s+/\\w+)',c:[{cN:"function",bK:"function endfunction",e:"$",k:"function endfunction|10",c:[e.UTM,{cN:"params",b:"\\(",e:"\\)"}]},{cN:"transposed_variable",b:"[a-zA-Z_][a-zA-Z_0-9]*('+[\\.']*|[\\.']+)",e:"",r:0},{cN:"matrix",b:"\\[",e:"\\]'*[\\.']*",r:0,c:t},e.C("//","$")].concat(t)
}}),hljs.registerLanguage("scss",function(e){{var t="[a-zA-Z-][a-zA-Z0-9_-]*",r={cN:"variable",b:"(\\$"+t+")\\b"},a={cN:"function",b:t+"\\(",rB:!0,eE:!0,e:"\\("},i={cN:"hexcolor",b:"#[0-9A-Fa-f]+"};({cN:"attribute",b:"[A-Z\\_\\.\\-]+",e:":",eE:!0,i:"[^\\s]",starts:{cN:"value",eW:!0,eE:!0,c:[a,i,e.CSSNM,e.QSM,e.ASM,e.CBCM,{cN:"important",b:"!important"}]}})}return{cI:!0,i:"[=/|']",c:[e.CLCM,e.CBCM,a,{cN:"id",b:"\\#[A-Za-z0-9_-]+",r:0},{cN:"class",b:"\\.[A-Za-z0-9_-]+",r:0},{cN:"attr_selector",b:"\\[",e:"\\]",i:"$"},{cN:"tag",b:"\\b(a|abbr|acronym|address|area|article|aside|audio|b|base|big|blockquote|body|br|button|canvas|caption|cite|code|col|colgroup|command|datalist|dd|del|details|dfn|div|dl|dt|em|embed|fieldset|figcaption|figure|footer|form|frame|frameset|(h[1-6])|head|header|hgroup|hr|html|i|iframe|img|input|ins|kbd|keygen|label|legend|li|link|map|mark|meta|meter|nav|noframes|noscript|object|ol|optgroup|option|output|p|param|pre|progress|q|rp|rt|ruby|samp|script|section|select|small|span|strike|strong|style|sub|sup|table|tbody|td|textarea|tfoot|th|thead|time|title|tr|tt|ul|var|video)\\b",r:0},{cN:"pseudo",b:":(visited|valid|root|right|required|read-write|read-only|out-range|optional|only-of-type|only-child|nth-of-type|nth-last-of-type|nth-last-child|nth-child|not|link|left|last-of-type|last-child|lang|invalid|indeterminate|in-range|hover|focus|first-of-type|first-line|first-letter|first-child|first|enabled|empty|disabled|default|checked|before|after|active)"},{cN:"pseudo",b:"::(after|before|choices|first-letter|first-line|repeat-index|repeat-item|selection|value)"},r,{cN:"attribute",b:"\\b(z-index|word-wrap|word-spacing|word-break|width|widows|white-space|visibility|vertical-align|unicode-bidi|transition-timing-function|transition-property|transition-duration|transition-delay|transition|transform-style|transform-origin|transform|top|text-underline-position|text-transform|text-shadow|text-rendering|text-overflow|text-indent|text-decoration-style|text-decoration-line|text-decoration-color|text-decoration|text-align-last|text-align|tab-size|table-layout|right|resize|quotes|position|pointer-events|perspective-origin|perspective|page-break-inside|page-break-before|page-break-after|padding-top|padding-right|padding-left|padding-bottom|padding|overflow-y|overflow-x|overflow-wrap|overflow|outline-width|outline-style|outline-offset|outline-color|outline|orphans|order|opacity|object-position|object-fit|normal|none|nav-up|nav-right|nav-left|nav-index|nav-down|min-width|min-height|max-width|max-height|mask|marks|margin-top|margin-right|margin-left|margin-bottom|margin|list-style-type|list-style-position|list-style-image|list-style|line-height|letter-spacing|left|justify-content|initial|inherit|ime-mode|image-orientation|image-resolution|image-rendering|icon|hyphens|height|font-weight|font-variant-ligatures|font-variant|font-style|font-stretch|font-size-adjust|font-size|font-language-override|font-kerning|font-feature-settings|font-family|font|float|flex-wrap|flex-shrink|flex-grow|flex-flow|flex-direction|flex-basis|flex|filter|empty-cells|display|direction|cursor|counter-reset|counter-increment|content|column-width|column-span|column-rule-width|column-rule-style|column-rule-color|column-rule|column-gap|column-fill|column-count|columns|color|clip-path|clip|clear|caption-side|break-inside|break-before|break-after|box-sizing|box-shadow|box-decoration-break|bottom|border-width|border-top-width|border-top-style|border-top-right-radius|border-top-left-radius|border-top-color|border-top|border-style|border-spacing|border-right-width|border-right-style|border-right-color|border-right|border-radius|border-left-width|border-left-style|border-left-color|border-left|border-image-width|border-image-source|border-image-slice|border-image-repeat|border-image-outset|border-image|border-color|border-collapse|border-bottom-width|border-bottom-style|border-bottom-right-radius|border-bottom-left-radius|border-bottom-color|border-bottom|border|background-size|background-repeat|background-position|background-origin|background-image|background-color|background-clip|background-attachment|background-blend-mode|background|backface-visibility|auto|animation-timing-function|animation-play-state|animation-name|animation-iteration-count|animation-fill-mode|animation-duration|animation-direction|animation-delay|animation|align-self|align-items|align-content)\\b",i:"[^\\s]"},{cN:"value",b:"\\b(whitespace|wait|w-resize|visible|vertical-text|vertical-ideographic|uppercase|upper-roman|upper-alpha|underline|transparent|top|thin|thick|text|text-top|text-bottom|tb-rl|table-header-group|table-footer-group|sw-resize|super|strict|static|square|solid|small-caps|separate|se-resize|scroll|s-resize|rtl|row-resize|ridge|right|repeat|repeat-y|repeat-x|relative|progress|pointer|overline|outside|outset|oblique|nowrap|not-allowed|normal|none|nw-resize|no-repeat|no-drop|newspaper|ne-resize|n-resize|move|middle|medium|ltr|lr-tb|lowercase|lower-roman|lower-alpha|loose|list-item|line|line-through|line-edge|lighter|left|keep-all|justify|italic|inter-word|inter-ideograph|inside|inset|inline|inline-block|inherit|inactive|ideograph-space|ideograph-parenthesis|ideograph-numeric|ideograph-alpha|horizontal|hidden|help|hand|groove|fixed|ellipsis|e-resize|double|dotted|distribute|distribute-space|distribute-letter|distribute-all-lines|disc|disabled|default|decimal|dashed|crosshair|collapse|col-resize|circle|char|center|capitalize|break-word|break-all|bottom|both|bolder|bold|block|bidi-override|below|baseline|auto|always|all-scroll|absolute|table|table-cell)\\b"},{cN:"value",b:":",e:";",c:[a,r,i,e.CSSNM,e.QSM,e.ASM,{cN:"important",b:"!important"}]},{cN:"at_rule",b:"@",e:"[{;]",k:"mixin include extend for if else each while charset import debug media page content font-face namespace warn",c:[a,r,e.QSM,e.ASM,i,e.CSSNM,{cN:"preprocessor",b:"\\s[A-Za-z0-9_.-]+",r:0}]}]}}),hljs.registerLanguage("smali",function(e){var t=["add","and","cmp","cmpg","cmpl","const","div","double","float","goto","if","int","long","move","mul","neg","new","nop","not","or","rem","return","shl","shr","sput","sub","throw","ushr","xor"],r=["aget","aput","array","check","execute","fill","filled","goto/16","goto/32","iget","instance","invoke","iput","monitor","packed","sget","sparse"],a=["transient","constructor","abstract","final","synthetic","public","private","protected","static","bridge","system"];return{aliases:["smali"],c:[{cN:"string",b:'"',e:'"',r:0},e.C("#","$",{r:0}),{cN:"keyword",b:"\\s*\\.end\\s[a-zA-Z0-9]*",r:1},{cN:"keyword",b:"^[ ]*\\.[a-zA-Z]*",r:0},{cN:"keyword",b:"\\s:[a-zA-Z_0-9]*",r:0},{cN:"keyword",b:"\\s("+a.join("|")+")",r:1},{cN:"keyword",b:"\\[",r:0},{cN:"instruction",b:"\\s("+t.join("|")+")\\s",r:1},{cN:"instruction",b:"\\s("+t.join("|")+")((\\-|/)[a-zA-Z0-9]+)+\\s",r:10},{cN:"instruction",b:"\\s("+r.join("|")+")((\\-|/)[a-zA-Z0-9]+)*\\s",r:10},{cN:"class",b:"L[^(;:\n]*;",r:0},{cN:"function",b:'( |->)[^(\n ;"]*\\(',r:0},{cN:"function",b:"\\)",r:0},{cN:"variable",b:"[vp][0-9]+",r:0}]}}),hljs.registerLanguage("smalltalk",function(e){var t="[a-z][a-zA-Z0-9_]*",r={cN:"char",b:"\\$.{1}"},a={cN:"symbol",b:"#"+e.UIR};return{aliases:["st"],k:"self super nil true false thisContext",c:[e.C('"','"'),e.ASM,{cN:"class",b:"\\b[A-Z][A-Za-z0-9_]*",r:0},{cN:"method",b:t+":",r:0},e.CNM,a,r,{cN:"localvars",b:"\\|[ ]*"+t+"([ ]+"+t+")*[ ]*\\|",rB:!0,e:/\|/,i:/\S/,c:[{b:"(\\|[ ]*)?"+t}]},{cN:"array",b:"\\#\\(",e:"\\)",c:[e.ASM,r,e.CNM,a]}]}}),hljs.registerLanguage("sml",function(e){return{aliases:["ml"],k:{keyword:"abstype and andalso as case datatype do else end eqtype exception fn fun functor handle if in include infix infixr let local nonfix of op open orelse raise rec sharing sig signature struct structure then type val with withtype where while",built_in:"array bool char exn int list option order real ref string substring vector unit word",literal:"true false NONE SOME LESS EQUAL GREATER nil"},i:/\/\/|>>/,l:"[a-z_]\\w*!?",c:[{cN:"literal",b:"\\[(\\|\\|)?\\]|\\(\\)"},e.C("\\(\\*","\\*\\)",{c:["self"]}),{cN:"symbol",b:"'[A-Za-z_](?!')[\\w']*"},{cN:"tag",b:"`[A-Z][\\w']*"},{cN:"type",b:"\\b[A-Z][\\w']*",r:0},{b:"[a-z_]\\w*'[\\w']*"},e.inherit(e.ASM,{cN:"char",r:0}),e.inherit(e.QSM,{i:null}),{cN:"number",b:"\\b(0[xX][a-fA-F0-9_]+[Lln]?|0[oO][0-7_]+[Lln]?|0[bB][01_]+[Lln]?|[0-9][0-9_]*([Lln]|(\\.[0-9_]*)?([eE][-+]?[0-9_]+)?)?)",r:0},{b:/[-=]>/}]}}),hljs.registerLanguage("sql",function(e){var t=e.C("--","$");return{cI:!0,i:/[<>]/,c:[{cN:"operator",bK:"begin end start commit rollback savepoint lock alter create drop rename call delete do handler insert load replace select truncate update set show pragma grant merge describe use explain help declare prepare execute deallocate savepoint release unlock purge reset change stop analyze cache flush optimize repair kill install uninstall checksum restore check backup revoke",e:/;/,eW:!0,k:{keyword:"abs absolute acos action add adddate addtime aes_decrypt aes_encrypt after aggregate all allocate alter analyze and any are as asc ascii asin assertion at atan atan2 atn2 authorization authors avg backup before begin benchmark between bin binlog bit_and bit_count bit_length bit_or bit_xor both by cache call cascade cascaded case cast catalog ceil ceiling chain change changed char_length character_length charindex charset check checksum checksum_agg choose close coalesce coercibility collate collation collationproperty column columns columns_updated commit compress concat concat_ws concurrent connect connection connection_id consistent constraint constraints continue contributors conv convert convert_tz corresponding cos cot count count_big crc32 create cross cume_dist curdate current current_date current_time current_timestamp current_user cursor curtime data database databases datalength date_add date_format date_sub dateadd datediff datefromparts datename datepart datetime2fromparts datetimeoffsetfromparts day dayname dayofmonth dayofweek dayofyear deallocate declare decode default deferrable deferred degrees delayed delete des_decrypt des_encrypt des_key_file desc describe descriptor diagnostics difference disconnect distinct distinctrow div do domain double drop dumpfile each else elt enclosed encode encrypt end end-exec engine engines eomonth errors escape escaped event eventdata events except exception exec execute exists exp explain export_set extended external extract fast fetch field fields find_in_set first first_value floor flush for force foreign format found found_rows from from_base64 from_days from_unixtime full function get get_format get_lock getdate getutcdate global go goto grant grants greatest group group_concat grouping grouping_id gtid_subset gtid_subtract handler having help hex high_priority hosts hour ident_current ident_incr ident_seed identified identity if ifnull ignore iif ilike immediate in index indicator inet6_aton inet6_ntoa inet_aton inet_ntoa infile initially inner innodb input insert install instr intersect into is is_free_lock is_ipv4 is_ipv4_compat is_ipv4_mapped is_not is_not_null is_used_lock isdate isnull isolation join key kill language last last_day last_insert_id last_value lcase lead leading least leaves left len lenght level like limit lines ln load load_file local localtime localtimestamp locate lock log log10 log2 logfile logs low_priority lower lpad ltrim make_set makedate maketime master master_pos_wait match matched max md5 medium merge microsecond mid min minute mod mode module month monthname mutex name_const names national natural nchar next no no_write_to_binlog not now nullif nvarchar oct octet_length of old_password on only open optimize option optionally or ord order outer outfile output pad parse partial partition password patindex percent_rank percentile_cont percentile_disc period_add period_diff pi plugin position pow power pragma precision prepare preserve primary prior privileges procedure procedure_analyze processlist profile profiles public publishingservername purge quarter query quick quote quotename radians rand read references regexp relative relaylog release release_lock rename repair repeat replace replicate reset restore restrict return returns reverse revoke right rlike rollback rollup round row row_count rows rpad rtrim savepoint schema scroll sec_to_time second section select serializable server session session_user set sha sha1 sha2 share show sign sin size slave sleep smalldatetimefromparts snapshot some soname soundex sounds_like space sql sql_big_result sql_buffer_result sql_cache sql_calc_found_rows sql_no_cache sql_small_result sql_variant_property sqlstate sqrt square start starting status std stddev stddev_pop stddev_samp stdev stdevp stop str str_to_date straight_join strcmp string stuff subdate substr substring subtime subtring_index sum switchoffset sysdate sysdatetime sysdatetimeoffset system_user sysutcdatetime table tables tablespace tan temporary terminated tertiary_weights then time time_format time_to_sec timediff timefromparts timestamp timestampadd timestampdiff timezone_hour timezone_minute to to_base64 to_days to_seconds todatetimeoffset trailing transaction translation trigger trigger_nestlevel triggers trim truncate try_cast try_convert try_parse ucase uncompress uncompressed_length unhex unicode uninstall union unique unix_timestamp unknown unlock update upgrade upped upper usage use user user_resources using utc_date utc_time utc_timestamp uuid uuid_short validate_password_strength value values var var_pop var_samp variables variance varp version view warnings week weekday weekofyear weight_string when whenever where with work write xml xor year yearweek zon",literal:"true false null",built_in:"array bigint binary bit blob boolean char character date dec decimal float int integer interval number numeric real serial smallint varchar varying int8 serial8 text"},c:[{cN:"string",b:"'",e:"'",c:[e.BE,{b:"''"}]},{cN:"string",b:'"',e:'"',c:[e.BE,{b:'""'}]},{cN:"string",b:"`",e:"`",c:[e.BE]},e.CNM,e.CBCM,t]},e.CBCM,t]}}),hljs.registerLanguage("stata",function(e){return{aliases:["do","ado"],cI:!0,k:"if else in foreach for forv forva forval forvalu forvalue forvalues by bys bysort xi quietly qui capture about ac ac_7 acprplot acprplot_7 adjust ado adopath adoupdate alpha ameans an ano anov anova anova_estat anova_terms anovadef aorder ap app appe appen append arch arch_dr arch_estat arch_p archlm areg areg_p args arima arima_dr arima_estat arima_p as asmprobit asmprobit_estat asmprobit_lf asmprobit_mfx__dlg asmprobit_p ass asse asser assert avplot avplot_7 avplots avplots_7 bcskew0 bgodfrey binreg bip0_lf biplot bipp_lf bipr_lf bipr_p biprobit bitest bitesti bitowt blogit bmemsize boot bootsamp bootstrap bootstrap_8 boxco_l boxco_p boxcox boxcox_6 boxcox_p bprobit br break brier bro brow brows browse brr brrstat bs bs_7 bsampl_w bsample bsample_7 bsqreg bstat bstat_7 bstat_8 bstrap bstrap_7 ca ca_estat ca_p cabiplot camat canon canon_8 canon_8_p canon_estat canon_p cap caprojection capt captu captur capture cat cc cchart cchart_7 cci cd censobs_table centile cf char chdir checkdlgfiles checkestimationsample checkhlpfiles checksum chelp ci cii cl class classutil clear cli clis clist clo clog clog_lf clog_p clogi clogi_sw clogit clogit_lf clogit_p clogitp clogl_sw cloglog clonevar clslistarray cluster cluster_measures cluster_stop cluster_tree cluster_tree_8 clustermat cmdlog cnr cnre cnreg cnreg_p cnreg_sw cnsreg codebook collaps4 collapse colormult_nb colormult_nw compare compress conf confi confir confirm conren cons const constr constra constrai constrain constraint continue contract copy copyright copysource cor corc corr corr2data corr_anti corr_kmo corr_smc corre correl correla correlat correlate corrgram cou coun count cox cox_p cox_sw coxbase coxhaz coxvar cprplot cprplot_7 crc cret cretu cretur creturn cross cs cscript cscript_log csi ct ct_is ctset ctst_5 ctst_st cttost cumsp cumsp_7 cumul cusum cusum_7 cutil d datasig datasign datasigna datasignat datasignatu datasignatur datasignature datetof db dbeta de dec deco decod decode deff des desc descr descri describ describe destring dfbeta dfgls dfuller di di_g dir dirstats dis discard disp disp_res disp_s displ displa display distinct do doe doed doedi doedit dotplot dotplot_7 dprobit drawnorm drop ds ds_util dstdize duplicates durbina dwstat dydx e ed edi edit egen eivreg emdef en enc enco encod encode eq erase ereg ereg_lf ereg_p ereg_sw ereghet ereghet_glf ereghet_glf_sh ereghet_gp ereghet_ilf ereghet_ilf_sh ereghet_ip eret eretu eretur ereturn err erro error est est_cfexist est_cfname est_clickable est_expand est_hold est_table est_unhold est_unholdok estat estat_default estat_summ estat_vce_only esti estimates etodow etof etomdy ex exi exit expand expandcl fac fact facto factor factor_estat factor_p factor_pca_rotated factor_rotate factormat fcast fcast_compute fcast_graph fdades fdadesc fdadescr fdadescri fdadescrib fdadescribe fdasav fdasave fdause fh_st file open file read file close file filefilter fillin find_hlp_file findfile findit findit_7 fit fl fli flis flist for5_0 form forma format fpredict frac_154 frac_adj frac_chk frac_cox frac_ddp frac_dis frac_dv frac_in frac_mun frac_pp frac_pq frac_pv frac_wgt frac_xo fracgen fracplot fracplot_7 fracpoly fracpred fron_ex fron_hn fron_p fron_tn fron_tn2 frontier ftodate ftoe ftomdy ftowdate g gamhet_glf gamhet_gp gamhet_ilf gamhet_ip gamma gamma_d2 gamma_p gamma_sw gammahet gdi_hexagon gdi_spokes ge gen gene gener genera generat generate genrank genstd genvmean gettoken gl gladder gladder_7 glim_l01 glim_l02 glim_l03 glim_l04 glim_l05 glim_l06 glim_l07 glim_l08 glim_l09 glim_l10 glim_l11 glim_l12 glim_lf glim_mu glim_nw1 glim_nw2 glim_nw3 glim_p glim_v1 glim_v2 glim_v3 glim_v4 glim_v5 glim_v6 glim_v7 glm glm_6 glm_p glm_sw glmpred glo glob globa global glogit glogit_8 glogit_p gmeans gnbre_lf gnbreg gnbreg_5 gnbreg_p gomp_lf gompe_sw gomper_p gompertz gompertzhet gomphet_glf gomphet_glf_sh gomphet_gp gomphet_ilf gomphet_ilf_sh gomphet_ip gphdot gphpen gphprint gprefs gprobi_p gprobit gprobit_8 gr gr7 gr_copy gr_current gr_db gr_describe gr_dir gr_draw gr_draw_replay gr_drop gr_edit gr_editviewopts gr_example gr_example2 gr_export gr_print gr_qscheme gr_query gr_read gr_rename gr_replay gr_save gr_set gr_setscheme gr_table gr_undo gr_use graph graph7 grebar greigen greigen_7 greigen_8 grmeanby grmeanby_7 gs_fileinfo gs_filetype gs_graphinfo gs_stat gsort gwood h hadimvo hareg hausman haver he heck_d2 heckma_p heckman heckp_lf heckpr_p heckprob hel help hereg hetpr_lf hetpr_p hetprob hettest hexdump hilite hist hist_7 histogram hlogit hlu hmeans hotel hotelling hprobit hreg hsearch icd9 icd9_ff icd9p iis impute imtest inbase include inf infi infil infile infix inp inpu input ins insheet insp inspe inspec inspect integ inten intreg intreg_7 intreg_p intrg2_ll intrg_ll intrg_ll2 ipolate iqreg ir irf irf_create irfm iri is_svy is_svysum isid istdize ivprob_1_lf ivprob_lf ivprobit ivprobit_p ivreg ivreg_footnote ivtob_1_lf ivtob_lf ivtobit ivtobit_p jackknife jacknife jknife jknife_6 jknife_8 jkstat joinby kalarma1 kap kap_3 kapmeier kappa kapwgt kdensity kdensity_7 keep ksm ksmirnov ktau kwallis l la lab labe label labelbook ladder levels levelsof leverage lfit lfit_p li lincom line linktest lis list lloghet_glf lloghet_glf_sh lloghet_gp lloghet_ilf lloghet_ilf_sh lloghet_ip llogi_sw llogis_p llogist llogistic llogistichet lnorm_lf lnorm_sw lnorma_p lnormal lnormalhet lnormhet_glf lnormhet_glf_sh lnormhet_gp lnormhet_ilf lnormhet_ilf_sh lnormhet_ip lnskew0 loadingplot loc loca local log logi logis_lf logistic logistic_p logit logit_estat logit_p loglogs logrank loneway lookfor lookup lowess lowess_7 lpredict lrecomp lroc lroc_7 lrtest ls lsens lsens_7 lsens_x lstat ltable ltable_7 ltriang lv lvr2plot lvr2plot_7 m ma mac macr macro makecns man manova manova_estat manova_p manovatest mantel mark markin markout marksample mat mat_capp mat_order mat_put_rr mat_rapp mata mata_clear mata_describe mata_drop mata_matdescribe mata_matsave mata_matuse mata_memory mata_mlib mata_mosave mata_rename mata_which matalabel matcproc matlist matname matr matri matrix matrix_input__dlg matstrik mcc mcci md0_ md1_ md1debug_ md2_ md2debug_ mds mds_estat mds_p mdsconfig mdslong mdsmat mdsshepard mdytoe mdytof me_derd mean means median memory memsize meqparse mer merg merge mfp mfx mhelp mhodds minbound mixed_ll mixed_ll_reparm mkassert mkdir mkmat mkspline ml ml_5 ml_adjs ml_bhhhs ml_c_d ml_check ml_clear ml_cnt ml_debug ml_defd ml_e0 ml_e0_bfgs ml_e0_cycle ml_e0_dfp ml_e0i ml_e1 ml_e1_bfgs ml_e1_bhhh ml_e1_cycle ml_e1_dfp ml_e2 ml_e2_cycle ml_ebfg0 ml_ebfr0 ml_ebfr1 ml_ebh0q ml_ebhh0 ml_ebhr0 ml_ebr0i ml_ecr0i ml_edfp0 ml_edfr0 ml_edfr1 ml_edr0i ml_eds ml_eer0i ml_egr0i ml_elf ml_elf_bfgs ml_elf_bhhh ml_elf_cycle ml_elf_dfp ml_elfi ml_elfs ml_enr0i ml_enrr0 ml_erdu0 ml_erdu0_bfgs ml_erdu0_bhhh ml_erdu0_bhhhq ml_erdu0_cycle ml_erdu0_dfp ml_erdu0_nrbfgs ml_exde ml_footnote ml_geqnr ml_grad0 ml_graph ml_hbhhh ml_hd0 ml_hold ml_init ml_inv ml_log ml_max ml_mlout ml_mlout_8 ml_model ml_nb0 ml_opt ml_p ml_plot ml_query ml_rdgrd ml_repor ml_s_e ml_score ml_searc ml_technique ml_unhold mleval mlf_ mlmatbysum mlmatsum mlog mlogi mlogit mlogit_footnote mlogit_p mlopts mlsum mlvecsum mnl0_ mor more mov move mprobit mprobit_lf mprobit_p mrdu0_ mrdu1_ mvdecode mvencode mvreg mvreg_estat n nbreg nbreg_al nbreg_lf nbreg_p nbreg_sw nestreg net newey newey_7 newey_p news nl nl_7 nl_9 nl_9_p nl_p nl_p_7 nlcom nlcom_p nlexp2 nlexp2_7 nlexp2a nlexp2a_7 nlexp3 nlexp3_7 nlgom3 nlgom3_7 nlgom4 nlgom4_7 nlinit nllog3 nllog3_7 nllog4 nllog4_7 nlog_rd nlogit nlogit_p nlogitgen nlogittree nlpred no nobreak noi nois noisi noisil noisily note notes notes_dlg nptrend numlabel numlist odbc old_ver olo olog ologi ologi_sw ologit ologit_p ologitp on one onew onewa oneway op_colnm op_comp op_diff op_inv op_str opr opro oprob oprob_sw oprobi oprobi_p oprobit oprobitp opts_exclusive order orthog orthpoly ou out outf outfi outfil outfile outs outsh outshe outshee outsheet ovtest pac pac_7 palette parse parse_dissim pause pca pca_8 pca_display pca_estat pca_p pca_rotate pcamat pchart pchart_7 pchi pchi_7 pcorr pctile pentium pergram pergram_7 permute permute_8 personal peto_st pkcollapse pkcross pkequiv pkexamine pkexamine_7 pkshape pksumm pksumm_7 pl plo plot plugin pnorm pnorm_7 poisgof poiss_lf poiss_sw poisso_p poisson poisson_estat post postclose postfile postutil pperron pr prais prais_e prais_e2 prais_p predict predictnl preserve print pro prob probi probit probit_estat probit_p proc_time procoverlay procrustes procrustes_estat procrustes_p profiler prog progr progra program prop proportion prtest prtesti pwcorr pwd q\\s qby qbys qchi qchi_7 qladder qladder_7 qnorm qnorm_7 qqplot qqplot_7 qreg qreg_c qreg_p qreg_sw qu quadchk quantile quantile_7 que quer query range ranksum ratio rchart rchart_7 rcof recast reclink recode reg reg3 reg3_p regdw regr regre regre_p2 regres regres_p regress regress_estat regriv_p remap ren rena renam rename renpfix repeat replace report reshape restore ret retu retur return rm rmdir robvar roccomp roccomp_7 roccomp_8 rocf_lf rocfit rocfit_8 rocgold rocplot rocplot_7 roctab roctab_7 rolling rologit rologit_p rot rota rotat rotate rotatemat rreg rreg_p ru run runtest rvfplot rvfplot_7 rvpplot rvpplot_7 sa safesum sample sampsi sav save savedresults saveold sc sca scal scala scalar scatter scm_mine sco scob_lf scob_p scobi_sw scobit scor score scoreplot scoreplot_help scree screeplot screeplot_help sdtest sdtesti se search separate seperate serrbar serrbar_7 serset set set_defaults sfrancia sh she shel shell shewhart shewhart_7 signestimationsample signrank signtest simul simul_7 simulate simulate_8 sktest sleep slogit slogit_d2 slogit_p smooth snapspan so sor sort spearman spikeplot spikeplot_7 spikeplt spline_x split sqreg sqreg_p sret sretu sretur sreturn ssc st st_ct st_hc st_hcd st_hcd_sh st_is st_issys st_note st_promo st_set st_show st_smpl st_subid stack statsby statsby_8 stbase stci stci_7 stcox stcox_estat stcox_fr stcox_fr_ll stcox_p stcox_sw stcoxkm stcoxkm_7 stcstat stcurv stcurve stcurve_7 stdes stem stepwise stereg stfill stgen stir stjoin stmc stmh stphplot stphplot_7 stphtest stphtest_7 stptime strate strate_7 streg streg_sw streset sts sts_7 stset stsplit stsum sttocc sttoct stvary stweib su suest suest_8 sum summ summa summar summari summariz summarize sunflower sureg survcurv survsum svar svar_p svmat svy svy_disp svy_dreg svy_est svy_est_7 svy_estat svy_get svy_gnbreg_p svy_head svy_header svy_heckman_p svy_heckprob_p svy_intreg_p svy_ivreg_p svy_logistic_p svy_logit_p svy_mlogit_p svy_nbreg_p svy_ologit_p svy_oprobit_p svy_poisson_p svy_probit_p svy_regress_p svy_sub svy_sub_7 svy_x svy_x_7 svy_x_p svydes svydes_8 svygen svygnbreg svyheckman svyheckprob svyintreg svyintreg_7 svyintrg svyivreg svylc svylog_p svylogit svymarkout svymarkout_8 svymean svymlog svymlogit svynbreg svyolog svyologit svyoprob svyoprobit svyopts svypois svypois_7 svypoisson svyprobit svyprobt svyprop svyprop_7 svyratio svyreg svyreg_p svyregress svyset svyset_7 svyset_8 svytab svytab_7 svytest svytotal sw sw_8 swcnreg swcox swereg swilk swlogis swlogit swologit swoprbt swpois swprobit swqreg swtobit swweib symmetry symmi symplot symplot_7 syntax sysdescribe sysdir sysuse szroeter ta tab tab1 tab2 tab_or tabd tabdi tabdis tabdisp tabi table tabodds tabodds_7 tabstat tabu tabul tabula tabulat tabulate te tempfile tempname tempvar tes test testnl testparm teststd tetrachoric time_it timer tis tob tobi tobit tobit_p tobit_sw token tokeni tokeniz tokenize tostring total translate translator transmap treat_ll treatr_p treatreg trim trnb_cons trnb_mean trpoiss_d2 trunc_ll truncr_p truncreg tsappend tset tsfill tsline tsline_ex tsreport tsrevar tsrline tsset tssmooth tsunab ttest ttesti tut_chk tut_wait tutorial tw tware_st two twoway twoway__fpfit_serset twoway__function_gen twoway__histogram_gen twoway__ipoint_serset twoway__ipoints_serset twoway__kdensity_gen twoway__lfit_serset twoway__normgen_gen twoway__pci_serset twoway__qfit_serset twoway__scatteri_serset twoway__sunflower_gen twoway_ksm_serset ty typ type typeof u unab unabbrev unabcmd update us use uselabel var var_mkcompanion var_p varbasic varfcast vargranger varirf varirf_add varirf_cgraph varirf_create varirf_ctable varirf_describe varirf_dir varirf_drop varirf_erase varirf_graph varirf_ograph varirf_rename varirf_set varirf_table varlist varlmar varnorm varsoc varstable varstable_w varstable_w2 varwle vce vec vec_fevd vec_mkphi vec_p vec_p_w vecirf_create veclmar veclmar_w vecnorm vecnorm_w vecrank vecstable verinst vers versi versio version view viewsource vif vwls wdatetof webdescribe webseek webuse weib1_lf weib2_lf weib_lf weib_lf0 weibhet_glf weibhet_glf_sh weibhet_glfa weibhet_glfa_sh weibhet_gp weibhet_ilf weibhet_ilf_sh weibhet_ilfa weibhet_ilfa_sh weibhet_ip weibu_sw weibul_p weibull weibull_c weibull_s weibullhet wh whelp whi which whil while wilc_st wilcoxon win wind windo window winexec wntestb wntestb_7 wntestq xchart xchart_7 xcorr xcorr_7 xi xi_6 xmlsav xmlsave xmluse xpose xsh xshe xshel xshell xt_iis xt_tis xtab_p xtabond xtbin_p xtclog xtcloglog xtcloglog_8 xtcloglog_d2 xtcloglog_pa_p xtcloglog_re_p xtcnt_p xtcorr xtdata xtdes xtfront_p xtfrontier xtgee xtgee_elink xtgee_estat xtgee_makeivar xtgee_p xtgee_plink xtgls xtgls_p xthaus xthausman xtht_p xthtaylor xtile xtint_p xtintreg xtintreg_8 xtintreg_d2 xtintreg_p xtivp_1 xtivp_2 xtivreg xtline xtline_ex xtlogit xtlogit_8 xtlogit_d2 xtlogit_fe_p xtlogit_pa_p xtlogit_re_p xtmixed xtmixed_estat xtmixed_p xtnb_fe xtnb_lf xtnbreg xtnbreg_pa_p xtnbreg_refe_p xtpcse xtpcse_p xtpois xtpoisson xtpoisson_d2 xtpoisson_pa_p xtpoisson_refe_p xtpred xtprobit xtprobit_8 xtprobit_d2 xtprobit_re_p xtps_fe xtps_lf xtps_ren xtps_ren_8 xtrar_p xtrc xtrc_p xtrchh xtrefe_p xtreg xtreg_be xtreg_fe xtreg_ml xtreg_pa_p xtreg_re xtregar xtrere_p xtset xtsf_ll xtsf_llti xtsum xttab xttest0 xttobit xttobit_8 xttobit_p xttrans yx yxview__barlike_draw yxview_area_draw yxview_bar_draw yxview_dot_draw yxview_dropline_draw yxview_function_draw yxview_iarrow_draw yxview_ilabels_draw yxview_normal_draw yxview_pcarrow_draw yxview_pcbarrow_draw yxview_pccapsym_draw yxview_pcscatter_draw yxview_pcspike_draw yxview_rarea_draw yxview_rbar_draw yxview_rbarm_draw yxview_rcap_draw yxview_rcapsym_draw yxview_rconnected_draw yxview_rline_draw yxview_rscatter_draw yxview_rspike_draw yxview_spike_draw yxview_sunflower_draw zap_s zinb zinb_llf zinb_plf zip zip_llf zip_p zip_plf zt_ct_5 zt_hc_5 zt_hcd_5 zt_is_5 zt_iss_5 zt_sho_5 zt_smp_5 ztbase_5 ztcox_5 ztdes_5 ztereg_5 ztfill_5 ztgen_5 ztir_5 ztjoin_5 ztnb ztnb_p ztp ztp_p zts_5 ztset_5 ztspli_5 ztsum_5 zttoct_5 ztvary_5 ztweib_5",c:[{cN:"label",v:[{b:"\\$\\{?[a-zA-Z0-9_]+\\}?"},{b:"`[a-zA-Z0-9_]+'"}]},{cN:"string",v:[{b:'`"[^\r\n]*?"\''},{b:'"[^\r\n"]*"'}]},{cN:"literal",v:[{b:"\\b(abs|acos|asin|atan|atan2|atanh|ceil|cloglog|comb|cos|digamma|exp|floor|invcloglog|invlogit|ln|lnfact|lnfactorial|lngamma|log|log10|max|min|mod|reldif|round|sign|sin|sqrt|sum|tan|tanh|trigamma|trunc|betaden|Binomial|binorm|binormal|chi2|chi2tail|dgammapda|dgammapdada|dgammapdadx|dgammapdx|dgammapdxdx|F|Fden|Ftail|gammaden|gammap|ibeta|invbinomial|invchi2|invchi2tail|invF|invFtail|invgammap|invibeta|invnchi2|invnFtail|invnibeta|invnorm|invnormal|invttail|nbetaden|nchi2|nFden|nFtail|nibeta|norm|normal|normalden|normd|npnchi2|tden|ttail|uniform|abbrev|char|index|indexnot|length|lower|ltrim|match|plural|proper|real|regexm|regexr|regexs|reverse|rtrim|string|strlen|strlower|strltrim|strmatch|strofreal|strpos|strproper|strreverse|strrtrim|strtrim|strupper|subinstr|subinword|substr|trim|upper|word|wordcount|_caller|autocode|byteorder|chop|clip|cond|e|epsdouble|epsfloat|group|inlist|inrange|irecode|matrix|maxbyte|maxdouble|maxfloat|maxint|maxlong|mi|minbyte|mindouble|minfloat|minint|minlong|missing|r|recode|replay|return|s|scalar|d|date|day|dow|doy|halfyear|mdy|month|quarter|week|year|d|daily|dofd|dofh|dofm|dofq|dofw|dofy|h|halfyearly|hofd|m|mofd|monthly|q|qofd|quarterly|tin|twithin|w|weekly|wofd|y|yearly|yh|ym|yofd|yq|yw|cholesky|colnumb|colsof|corr|det|diag|diag0cnt|el|get|hadamard|I|inv|invsym|issym|issymmetric|J|matmissing|matuniform|mreldif|nullmat|rownumb|rowsof|sweep|syminv|trace|vec|vecdiag)(?=\\(|$)"}]},e.C("^[ 	]*\\*.*$",!1),e.CLCM,e.CBCM]}}),hljs.registerLanguage("step21",function(e){var t="[A-Z_][A-Z0-9_.]*",r="END-ISO-10303-21;",a={literal:"",built_in:"",keyword:"HEADER ENDSEC DATA"},i={cN:"preprocessor",b:"ISO-10303-21;",r:10},n=[e.CLCM,e.CBCM,e.C("/\\*\\*!","\\*/"),e.CNM,e.inherit(e.ASM,{i:null}),e.inherit(e.QSM,{i:null}),{cN:"string",b:"'",e:"'"},{cN:"label",v:[{b:"#",e:"\\d+",i:"\\W"}]}];return{aliases:["p21","step","stp"],cI:!0,l:t,k:a,c:[{cN:"preprocessor",b:r,r:10},i].concat(n)}}),hljs.registerLanguage("stylus",function(e){var t={cN:"variable",b:"\\$"+e.IR},r={cN:"hexcolor",b:"#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})",r:10},a=["charset","css","debug","extend","font-face","for","import","include","media","mixin","page","warn","while"],i=["after","before","first-letter","first-line","active","first-child","focus","hover","lang","link","visited"],n=["a","abbr","address","article","aside","audio","b","blockquote","body","button","canvas","caption","cite","code","dd","del","details","dfn","div","dl","dt","em","fieldset","figcaption","figure","footer","form","h1","h2","h3","h4","h5","h6","header","hgroup","html","i","iframe","img","input","ins","kbd","label","legend","li","mark","menu","nav","object","ol","p","q","quote","samp","section","span","strong","summary","sup","table","tbody","td","textarea","tfoot","th","thead","time","tr","ul","var","video"],o="[\\.\\s\\n\\[\\:,]",s=["align-content","align-items","align-self","animation","animation-delay","animation-direction","animation-duration","animation-fill-mode","animation-iteration-count","animation-name","animation-play-state","animation-timing-function","auto","backface-visibility","background","background-attachment","background-clip","background-color","background-image","background-origin","background-position","background-repeat","background-size","border","border-bottom","border-bottom-color","border-bottom-left-radius","border-bottom-right-radius","border-bottom-style","border-bottom-width","border-collapse","border-color","border-image","border-image-outset","border-image-repeat","border-image-slice","border-image-source","border-image-width","border-left","border-left-color","border-left-style","border-left-width","border-radius","border-right","border-right-color","border-right-style","border-right-width","border-spacing","border-style","border-top","border-top-color","border-top-left-radius","border-top-right-radius","border-top-style","border-top-width","border-width","bottom","box-decoration-break","box-shadow","box-sizing","break-after","break-before","break-inside","caption-side","clear","clip","clip-path","color","column-count","column-fill","column-gap","column-rule","column-rule-color","column-rule-style","column-rule-width","column-span","column-width","columns","content","counter-increment","counter-reset","cursor","direction","display","empty-cells","filter","flex","flex-basis","flex-direction","flex-flow","flex-grow","flex-shrink","flex-wrap","float","font","font-family","font-feature-settings","font-kerning","font-language-override","font-size","font-size-adjust","font-stretch","font-style","font-variant","font-variant-ligatures","font-weight","height","hyphens","icon","image-orientation","image-rendering","image-resolution","ime-mode","inherit","initial","justify-content","left","letter-spacing","line-height","list-style","list-style-image","list-style-position","list-style-type","margin","margin-bottom","margin-left","margin-right","margin-top","marks","mask","max-height","max-width","min-height","min-width","nav-down","nav-index","nav-left","nav-right","nav-up","none","normal","object-fit","object-position","opacity","order","orphans","outline","outline-color","outline-offset","outline-style","outline-width","overflow","overflow-wrap","overflow-x","overflow-y","padding","padding-bottom","padding-left","padding-right","padding-top","page-break-after","page-break-before","page-break-inside","perspective","perspective-origin","pointer-events","position","quotes","resize","right","tab-size","table-layout","text-align","text-align-last","text-decoration","text-decoration-color","text-decoration-line","text-decoration-style","text-indent","text-overflow","text-rendering","text-shadow","text-transform","text-underline-position","top","transform","transform-origin","transform-style","transition","transition-delay","transition-duration","transition-property","transition-timing-function","unicode-bidi","vertical-align","visibility","white-space","widows","width","word-break","word-spacing","word-wrap","z-index"],l=["\\{","\\}","\\?","(\\bReturn\\b)","(\\bEnd\\b)","(\\bend\\b)",";","#\\s","\\*\\s","===\\s","\\|","%"];

return{aliases:["styl"],cI:!1,i:"("+l.join("|")+")",k:"if else for in",c:[e.QSM,e.ASM,e.CLCM,e.CBCM,r,{b:"\\.[a-zA-Z][a-zA-Z0-9_-]*"+o,rB:!0,c:[{cN:"class",b:"\\.[a-zA-Z][a-zA-Z0-9_-]*"}]},{b:"\\#[a-zA-Z][a-zA-Z0-9_-]*"+o,rB:!0,c:[{cN:"id",b:"\\#[a-zA-Z][a-zA-Z0-9_-]*"}]},{b:"\\b("+n.join("|")+")"+o,rB:!0,c:[{cN:"tag",b:"\\b[a-zA-Z][a-zA-Z0-9_-]*"}]},{cN:"pseudo",b:"&?:?:\\b("+i.join("|")+")"+o},{cN:"at_rule",b:"@("+a.join("|")+")\\b"},t,e.CSSNM,e.NM,{cN:"function",b:"\\b[a-zA-Z][a-zA-Z0-9_-]*\\(.*\\)",i:"[\\n]",rB:!0,c:[{cN:"title",b:"\\b[a-zA-Z][a-zA-Z0-9_-]*"},{cN:"params",b:/\(/,e:/\)/,c:[r,t,e.ASM,e.CSSNM,e.NM,e.QSM]}]},{cN:"attribute",b:"\\b("+s.reverse().join("|")+")\\b"}]}}),hljs.registerLanguage("swift",function(e){var t={keyword:"class deinit enum extension func import init let protocol static struct subscript typealias var break case continue default do else fallthrough if in for return switch where while as dynamicType is new super self Self Type __COLUMN__ __FILE__ __FUNCTION__ __LINE__ associativity didSet get infix inout left mutating none nonmutating operator override postfix precedence prefix right set unowned unowned safe unsafe weak willSet",literal:"true false nil",built_in:"abs advance alignof alignofValue assert bridgeFromObjectiveC bridgeFromObjectiveCUnconditional bridgeToObjectiveC bridgeToObjectiveCUnconditional c contains count countElements countLeadingZeros debugPrint debugPrintln distance dropFirst dropLast dump encodeBitsAsWords enumerate equal false filter find getBridgedObjectiveCType getVaList indices insertionSort isBridgedToObjectiveC isBridgedVerbatimToObjectiveC isUniquelyReferenced join lexicographicalCompare map max maxElement min minElement nil numericCast partition posix print println quickSort reduce reflect reinterpretCast reverse roundUpToAlignment sizeof sizeofValue sort split startsWith strideof strideofValue swap swift toString transcode true underestimateCount unsafeReflect withExtendedLifetime withObjectAtPlusZero withUnsafePointer withUnsafePointerToObject withUnsafePointers withVaList"},r={cN:"type",b:"\\b[A-Z][\\w']*",r:0},a=e.C("/\\*","\\*/",{c:["self"]}),i={cN:"subst",b:/\\\(/,e:"\\)",k:t,c:[]},n={cN:"number",b:"\\b([\\d_]+(\\.[\\deE_]+)?|0x[a-fA-F0-9_]+(\\.[a-fA-F0-9p_]+)?|0b[01_]+|0o[0-7_]+)\\b",r:0},o=e.inherit(e.QSM,{c:[i,e.BE]});return i.c=[n],{k:t,c:[o,e.CLCM,a,r,n,{cN:"func",bK:"func",e:"{",eE:!0,c:[e.inherit(e.TM,{b:/[A-Za-z$_][0-9A-Za-z$_]*/,i:/\(/}),{cN:"generics",b:/</,e:/>/,i:/>/},{cN:"params",b:/\(/,e:/\)/,endsParent:!0,k:t,c:["self",n,o,e.CBCM,{b:":"}],i:/["']/}],i:/\[|%/},{cN:"class",bK:"struct protocol class extension enum",k:t,e:"\\{",eE:!0,c:[e.inherit(e.TM,{b:/[A-Za-z$_][0-9A-Za-z$_]*/})]},{cN:"preprocessor",b:"(@assignment|@class_protocol|@exported|@final|@lazy|@noreturn|@NSCopying|@NSManaged|@objc|@optional|@required|@auto_closure|@noreturn|@IBAction|@IBDesignable|@IBInspectable|@IBOutlet|@infix|@prefix|@postfix)"}]}}),hljs.registerLanguage("tcl",function(e){return{aliases:["tk"],k:"after append apply array auto_execok auto_import auto_load auto_mkindex auto_mkindex_old auto_qualify auto_reset bgerror binary break catch cd chan clock close concat continue dde dict encoding eof error eval exec exit expr fblocked fconfigure fcopy file fileevent filename flush for foreach format gets glob global history http if incr info interp join lappend|10 lassign|10 lindex|10 linsert|10 list llength|10 load lrange|10 lrepeat|10 lreplace|10 lreverse|10 lsearch|10 lset|10 lsort|10 mathfunc mathop memory msgcat namespace open package parray pid pkg::create pkg_mkIndex platform platform::shell proc puts pwd read refchan regexp registry regsub|10 rename return safe scan seek set socket source split string subst switch tcl_endOfWord tcl_findLibrary tcl_startOfNextWord tcl_startOfPreviousWord tcl_wordBreakAfter tcl_wordBreakBefore tcltest tclvars tell time tm trace unknown unload unset update uplevel upvar variable vwait while",c:[e.C(";[ \\t]*#","$"),e.C("^[ \\t]*#","$"),{bK:"proc",e:"[\\{]",eE:!0,c:[{cN:"symbol",b:"[ \\t\\n\\r]+(::)?[a-zA-Z_]((::)?[a-zA-Z0-9_])*",e:"[ \\t\\n\\r]",eW:!0,eE:!0}]},{cN:"variable",eE:!0,v:[{b:"\\$(\\{)?(::)?[a-zA-Z_]((::)?[a-zA-Z0-9_])*\\(([a-zA-Z0-9_])*\\)",e:"[^a-zA-Z0-9_\\}\\$]"},{b:"\\$(\\{)?(::)?[a-zA-Z_]((::)?[a-zA-Z0-9_])*",e:"(\\))?[^a-zA-Z0-9_\\}\\$]"}]},{cN:"string",c:[e.BE],v:[e.inherit(e.ASM,{i:null}),e.inherit(e.QSM,{i:null})]},{cN:"number",v:[e.BNM,e.CNM]}]}}),hljs.registerLanguage("tex",function(e){var t={cN:"command",b:"\\\\[a-zA-Zа-яА-я]+[\\*]?"},r={cN:"command",b:"\\\\[^a-zA-Zа-яА-я0-9]"},a={cN:"special",b:"[{}\\[\\]\\&#~]",r:0};return{c:[{b:"\\\\[a-zA-Zа-яА-я]+[\\*]? *= *-?\\d*\\.?\\d+(pt|pc|mm|cm|in|dd|cc|ex|em)?",rB:!0,c:[t,r,{cN:"number",b:" *=",e:"-?\\d*\\.?\\d+(pt|pc|mm|cm|in|dd|cc|ex|em)?",eB:!0}],r:10},t,r,a,{cN:"formula",b:"\\$\\$",e:"\\$\\$",c:[t,r,a],r:0},{cN:"formula",b:"\\$",e:"\\$",c:[t,r,a],r:0},e.C("%","$",{r:0})]}}),hljs.registerLanguage("thrift",function(e){var t="bool byte i16 i32 i64 double string binary";return{k:{keyword:"namespace const typedef struct enum service exception void oneway set list map required optional",built_in:t,literal:"true false"},c:[e.QSM,e.NM,e.CLCM,e.CBCM,{cN:"class",bK:"struct enum service exception",e:/\{/,i:/\n/,c:[e.inherit(e.TM,{starts:{eW:!0,eE:!0}})]},{b:"\\b(set|list|map)\\s*<",e:">",k:t,c:["self"]}]}}),hljs.registerLanguage("twig",function(e){var t={cN:"params",b:"\\(",e:"\\)"},r="attribute block constant cycle date dump include max min parent random range source template_from_string",a={cN:"function",bK:r,r:0,c:[t]},i={cN:"filter",b:/\|[A-Za-z_]+:?/,k:"abs batch capitalize convert_encoding date date_modify default escape first format join json_encode keys last length lower merge nl2br number_format raw replace reverse round slice sort split striptags title trim upper url_encode",c:[a]},n="autoescape block do embed extends filter flush for if import include macro sandbox set spaceless use verbatim";return n=n+" "+n.split(" ").map(function(e){return"end"+e}).join(" "),{aliases:["craftcms"],cI:!0,sL:"xml",subLanguageMode:"continuous",c:[e.C(/\{#/,/#}/),{cN:"template_tag",b:/\{%/,e:/%}/,k:n,c:[i,a]},{cN:"variable",b:/\{\{/,e:/}}/,c:[i,a]}]}}),hljs.registerLanguage("typescript",function(e){return{aliases:["ts"],k:{keyword:"in if for while finally var new function|0 do return void else break catch instanceof with throw case default try this switch continue typeof delete let yield const class public private get set super interface extendsstatic constructor implements enum export import declare type protected",literal:"true false null undefined NaN Infinity",built_in:"eval isFinite isNaN parseFloat parseInt decodeURI decodeURIComponent encodeURI encodeURIComponent escape unescape Object Function Boolean Error EvalError InternalError RangeError ReferenceError StopIteration SyntaxError TypeError URIError Number Math Date String RegExp Array Float32Array Float64Array Int16Array Int32Array Int8Array Uint16Array Uint32Array Uint8Array Uint8ClampedArray ArrayBuffer DataView JSON Intl arguments require module console window document any number boolean string void"},c:[{cN:"pi",b:/^\s*('|")use strict('|")/,r:0},e.ASM,e.QSM,e.CLCM,e.CBCM,e.CNM,{b:"("+e.RSR+"|\\b(case|return|throw)\\b)\\s*",k:"return throw case",c:[e.CLCM,e.CBCM,e.RM,{b:/</,e:/>;/,r:0,sL:"xml"}],r:0},{cN:"function",bK:"function",e:/\{/,eE:!0,c:[e.inherit(e.TM,{b:/[A-Za-z$_][0-9A-Za-z$_]*/}),{cN:"params",b:/\(/,e:/\)/,c:[e.CLCM,e.CBCM],i:/["'\(]/}],i:/\[|%/,r:0},{cN:"constructor",bK:"constructor",e:/\{/,eE:!0,r:10},{cN:"module",bK:"module",e:/\{/,eE:!0},{cN:"interface",bK:"interface",e:/\{/,eE:!0},{b:/\$[(.]/},{b:"\\."+e.IR,r:0}]}}),hljs.registerLanguage("vala",function(e){return{k:{keyword:"char uchar unichar int uint long ulong short ushort int8 int16 int32 int64 uint8 uint16 uint32 uint64 float double bool struct enum string void weak unowned owned async signal static abstract interface override while do for foreach else switch case break default return try catch public private protected internal using new this get set const stdout stdin stderr var",built_in:"DBus GLib CCode Gee Object",literal:"false true null"},c:[{cN:"class",bK:"class interface delegate namespace",e:"{",eE:!0,i:"[^,:\\n\\s\\.]",c:[e.UTM]},e.CLCM,e.CBCM,{cN:"string",b:'"""',e:'"""',r:5},e.ASM,e.QSM,e.CNM,{cN:"preprocessor",b:"^#",e:"$",r:2},{cN:"constant",b:" [A-Z_]+ ",r:0}]}}),hljs.registerLanguage("vbnet",function(e){return{aliases:["vb"],cI:!0,k:{keyword:"addhandler addressof alias and andalso aggregate ansi as assembly auto binary by byref byval call case catch class compare const continue custom declare default delegate dim distinct do each equals else elseif end enum erase error event exit explicit finally for friend from function get global goto group handles if implements imports in inherits interface into is isfalse isnot istrue join key let lib like loop me mid mod module mustinherit mustoverride mybase myclass namespace narrowing new next not notinheritable notoverridable of off on operator option optional or order orelse overloads overridable overrides paramarray partial preserve private property protected public raiseevent readonly redim rem removehandler resume return select set shadows shared skip static step stop structure strict sub synclock take text then throw to try unicode until using when where while widening with withevents writeonly xor",built_in:"boolean byte cbool cbyte cchar cdate cdec cdbl char cint clng cobj csbyte cshort csng cstr ctype date decimal directcast double gettype getxmlnamespace iif integer long object sbyte short single string trycast typeof uinteger ulong ushort",literal:"true false nothing"},i:"//|{|}|endif|gosub|variant|wend",c:[e.inherit(e.QSM,{c:[{b:'""'}]}),e.C("'","$",{rB:!0,c:[{cN:"xmlDocTag",b:"'''|<!--|-->",c:[e.PWM]},{cN:"xmlDocTag",b:"</?",e:">",c:[e.PWM]}]}),e.CNM,{cN:"preprocessor",b:"#",e:"$",k:"if else elseif end region externalsource"}]}}),hljs.registerLanguage("vbscript",function(e){return{aliases:["vbs"],cI:!0,k:{keyword:"call class const dim do loop erase execute executeglobal exit for each next function if then else on error option explicit new private property let get public randomize redim rem select case set stop sub while wend with end to elseif is or xor and not class_initialize class_terminate default preserve in me byval byref step resume goto",built_in:"lcase month vartype instrrev ubound setlocale getobject rgb getref string weekdayname rnd dateadd monthname now day minute isarray cbool round formatcurrency conversions csng timevalue second year space abs clng timeserial fixs len asc isempty maths dateserial atn timer isobject filter weekday datevalue ccur isdate instr datediff formatdatetime replace isnull right sgn array snumeric log cdbl hex chr lbound msgbox ucase getlocale cos cdate cbyte rtrim join hour oct typename trim strcomp int createobject loadpicture tan formatnumber mid scriptenginebuildversion scriptengine split scriptengineminorversion cint sin datepart ltrim sqr scriptenginemajorversion time derived eval date formatpercent exp inputbox left ascw chrw regexp server response request cstr err",literal:"true false null nothing empty"},i:"//",c:[e.inherit(e.QSM,{c:[{b:'""'}]}),e.C(/'/,/$/,{r:0}),e.CNM]}}),hljs.registerLanguage("vbscript-html",function(e){return{sL:"xml",subLanguageMode:"continuous",c:[{b:"<%",e:"%>",sL:"vbscript"}]}}),hljs.registerLanguage("verilog",function(e){return{aliases:["v"],cI:!0,k:{keyword:"always and assign begin buf bufif0 bufif1 case casex casez cmos deassign default defparam disable edge else end endcase endfunction endmodule endprimitive endspecify endtable endtask event for force forever fork function if ifnone initial inout input join macromodule module nand negedge nmos nor not notif0 notif1 or output parameter pmos posedge primitive pulldown pullup rcmos release repeat rnmos rpmos rtran rtranif0 rtranif1 specify specparam table task timescale tran tranif0 tranif1 wait while xnor xor",typename:"highz0 highz1 integer large medium pull0 pull1 real realtime reg scalared signed small strong0 strong1 supply0 supply0 supply1 supply1 time tri tri0 tri1 triand trior trireg vectored wand weak0 weak1 wire wor"},c:[e.CBCM,e.CLCM,e.QSM,{cN:"number",b:"\\b(\\d+'(b|h|o|d|B|H|O|D))?[0-9xzXZ]+",c:[e.BE],r:0},{cN:"typename",b:"\\.\\w+",r:0},{cN:"value",b:"#\\((?!parameter).+\\)"},{cN:"keyword",b:"\\+|-|\\*|/|%|<|>|=|#|`|\\!|&|\\||@|:|\\^|~|\\{|\\}",r:0}]}}),hljs.registerLanguage("vhdl",function(e){var t="\\d(_|\\d)*",r="[eE][-+]?"+t,a=t+"(\\."+t+")?("+r+")?",i="\\w+",n=t+"#"+i+"(\\."+i+")?#("+r+")?",o="\\b("+n+"|"+a+")";return{cI:!0,k:{keyword:"abs access after alias all and architecture array assert attribute begin block body buffer bus case component configuration constant context cover disconnect downto default else elsif end entity exit fairness file for force function generate generic group guarded if impure in inertial inout is label library linkage literal loop map mod nand new next nor not null of on open or others out package port postponed procedure process property protected pure range record register reject release rem report restrict restrict_guarantee return rol ror select sequence severity shared signal sla sll sra srl strong subtype then to transport type unaffected units until use variable vmode vprop vunit wait when while with xnor xor",typename:"boolean bit character severity_level integer time delay_length natural positive string bit_vector file_open_kind file_open_status std_ulogic std_ulogic_vector std_logic std_logic_vector unsigned signed boolean_vector integer_vector real_vector time_vector"},i:"{",c:[e.CBCM,e.C("--","$"),e.QSM,{cN:"number",b:o,r:0},{cN:"literal",b:"'(U|X|0|1|Z|W|L|H|-)'",c:[e.BE]},{cN:"attribute",b:"'[A-Za-z](_?[A-Za-z0-9])*",c:[e.BE]}]}}),hljs.registerLanguage("vim",function(e){return{l:/[!#@\w]+/,k:{keyword:"N|0 P|0 X|0 a|0 ab abc abo al am an|0 ar arga argd arge argdo argg argl argu as au aug aun b|0 bN ba bad bd be bel bf bl bm bn bo bp br brea breaka breakd breakl bro bufdo buffers bun bw c|0 cN cNf ca cabc caddb cad caddf cal cat cb cc ccl cd ce cex cf cfir cgetb cgete cg changes chd che checkt cl cla clo cm cmapc cme cn cnew cnf cno cnorea cnoreme co col colo com comc comp con conf cope cp cpf cq cr cs cst cu cuna cunme cw d|0 delm deb debugg delc delf dif diffg diffo diffp diffpu diffs diffthis dig di dl dell dj dli do doautoa dp dr ds dsp e|0 ea ec echoe echoh echom echon el elsei em en endfo endf endt endw ene ex exe exi exu f|0 files filet fin fina fini fir fix fo foldc foldd folddoc foldo for fu g|0 go gr grepa gu gv ha h|0 helpf helpg helpt hi hid his i|0 ia iabc if ij il im imapc ime ino inorea inoreme int is isp iu iuna iunme j|0 ju k|0 keepa kee keepj lN lNf l|0 lad laddb laddf la lan lat lb lc lch lcl lcs le lefta let lex lf lfir lgetb lgete lg lgr lgrepa lh ll lla lli lmak lm lmapc lne lnew lnf ln loadk lo loc lockv lol lope lp lpf lr ls lt lu lua luad luaf lv lvimgrepa lw m|0 ma mak map mapc marks mat me menut mes mk mks mksp mkv mkvie mod mz mzf nbc nb nbs n|0 new nm nmapc nme nn nnoreme noa no noh norea noreme norm nu nun nunme ol o|0 om omapc ome on ono onoreme opt ou ounme ow p|0 profd prof pro promptr pc ped pe perld po popu pp pre prev ps pt ptN ptf ptj ptl ptn ptp ptr pts pu pw py3 python3 py3d py3f py pyd pyf q|0 quita qa r|0 rec red redi redr redraws reg res ret retu rew ri rightb rub rubyd rubyf rund ru rv s|0 sN san sa sal sav sb sbN sba sbf sbl sbm sbn sbp sbr scrip scripte scs se setf setg setl sf sfir sh sim sig sil sl sla sm smap smapc sme sn sni sno snor snoreme sor so spelld spe spelli spellr spellu spellw sp spr sre st sta startg startr star stopi stj sts sun sunm sunme sus sv sw sy synti sync t|0 tN tabN tabc tabdo tabe tabf tabfir tabl tabm tabnew tabn tabo tabp tabr tabs tab ta tags tc tcld tclf te tf th tj tl tm tn to tp tr try ts tu u|0 undoj undol una unh unl unlo unm unme uns up v|0 ve verb vert vim vimgrepa vi viu vie vm vmapc vme vne vn vnoreme vs vu vunme windo w|0 wN wa wh wi winc winp wn wp wq wqa ws wu wv x|0 xa xmapc xm xme xn xnoreme xu xunme y|0 z|0 ~ Next Print append abbreviate abclear aboveleft all amenu anoremenu args argadd argdelete argedit argglobal arglocal argument ascii autocmd augroup aunmenu buffer bNext ball badd bdelete behave belowright bfirst blast bmodified bnext botright bprevious brewind break breakadd breakdel breaklist browse bunload bwipeout change cNext cNfile cabbrev cabclear caddbuffer caddexpr caddfile call catch cbuffer cclose center cexpr cfile cfirst cgetbuffer cgetexpr cgetfile chdir checkpath checktime clist clast close cmap cmapclear cmenu cnext cnewer cnfile cnoremap cnoreabbrev cnoremenu copy colder colorscheme command comclear compiler continue confirm copen cprevious cpfile cquit crewind cscope cstag cunmap cunabbrev cunmenu cwindow delete delmarks debug debuggreedy delcommand delfunction diffupdate diffget diffoff diffpatch diffput diffsplit digraphs display deletel djump dlist doautocmd doautoall deletep drop dsearch dsplit edit earlier echo echoerr echohl echomsg else elseif emenu endif endfor endfunction endtry endwhile enew execute exit exusage file filetype find finally finish first fixdel fold foldclose folddoopen folddoclosed foldopen function global goto grep grepadd gui gvim hardcopy help helpfind helpgrep helptags highlight hide history insert iabbrev iabclear ijump ilist imap imapclear imenu inoremap inoreabbrev inoremenu intro isearch isplit iunmap iunabbrev iunmenu join jumps keepalt keepmarks keepjumps lNext lNfile list laddexpr laddbuffer laddfile last language later lbuffer lcd lchdir lclose lcscope left leftabove lexpr lfile lfirst lgetbuffer lgetexpr lgetfile lgrep lgrepadd lhelpgrep llast llist lmake lmap lmapclear lnext lnewer lnfile lnoremap loadkeymap loadview lockmarks lockvar lolder lopen lprevious lpfile lrewind ltag lunmap luado luafile lvimgrep lvimgrepadd lwindow move mark make mapclear match menu menutranslate messages mkexrc mksession mkspell mkvimrc mkview mode mzscheme mzfile nbclose nbkey nbsart next nmap nmapclear nmenu nnoremap nnoremenu noautocmd noremap nohlsearch noreabbrev noremenu normal number nunmap nunmenu oldfiles open omap omapclear omenu only onoremap onoremenu options ounmap ounmenu ownsyntax print profdel profile promptfind promptrepl pclose pedit perl perldo pop popup ppop preserve previous psearch ptag ptNext ptfirst ptjump ptlast ptnext ptprevious ptrewind ptselect put pwd py3do py3file python pydo pyfile quit quitall qall read recover redo redir redraw redrawstatus registers resize retab return rewind right rightbelow ruby rubydo rubyfile rundo runtime rviminfo substitute sNext sandbox sargument sall saveas sbuffer sbNext sball sbfirst sblast sbmodified sbnext sbprevious sbrewind scriptnames scriptencoding scscope set setfiletype setglobal setlocal sfind sfirst shell simalt sign silent sleep slast smagic smapclear smenu snext sniff snomagic snoremap snoremenu sort source spelldump spellgood spellinfo spellrepall spellundo spellwrong split sprevious srewind stop stag startgreplace startreplace startinsert stopinsert stjump stselect sunhide sunmap sunmenu suspend sview swapname syntax syntime syncbind tNext tabNext tabclose tabedit tabfind tabfirst tablast tabmove tabnext tabonly tabprevious tabrewind tag tcl tcldo tclfile tearoff tfirst throw tjump tlast tmenu tnext topleft tprevious trewind tselect tunmenu undo undojoin undolist unabbreviate unhide unlet unlockvar unmap unmenu unsilent update vglobal version verbose vertical vimgrep vimgrepadd visual viusage view vmap vmapclear vmenu vnew vnoremap vnoremenu vsplit vunmap vunmenu write wNext wall while winsize wincmd winpos wnext wprevious wqall wsverb wundo wviminfo xit xall xmapclear xmap xmenu xnoremap xnoremenu xunmap xunmenu yank",built_in:"abs acos add and append argc argidx argv asin atan atan2 browse browsedir bufexists buflisted bufloaded bufname bufnr bufwinnr byte2line byteidx call ceil changenr char2nr cindent clearmatches col complete complete_add complete_check confirm copy cos cosh count cscope_connection cursor deepcopy delete did_filetype diff_filler diff_hlID empty escape eval eventhandler executable exists exp expand extend feedkeys filereadable filewritable filter finddir findfile float2nr floor fmod fnameescape fnamemodify foldclosed foldclosedend foldlevel foldtext foldtextresult foreground function garbagecollect get getbufline getbufvar getchar getcharmod getcmdline getcmdpos getcmdtype getcwd getfontname getfperm getfsize getftime getftype getline getloclist getmatches getpid getpos getqflist getreg getregtype gettabvar gettabwinvar getwinposx getwinposy getwinvar glob globpath has has_key haslocaldir hasmapto histadd histdel histget histnr hlexists hlID hostname iconv indent index input inputdialog inputlist inputrestore inputsave inputsecret insert invert isdirectory islocked items join keys len libcall libcallnr line line2byte lispindent localtime log log10 luaeval map maparg mapcheck match matchadd matcharg matchdelete matchend matchlist matchstr max min mkdir mode mzeval nextnonblank nr2char or pathshorten pow prevnonblank printf pumvisible py3eval pyeval range readfile reltime reltimestr remote_expr remote_foreground remote_peek remote_read remote_send remove rename repeat resolve reverse round screenattr screenchar screencol screenrow search searchdecl searchpair searchpairpos searchpos server2client serverlist setbufvar setcmdpos setline setloclist setmatches setpos setqflist setreg settabvar settabwinvar setwinvar sha256 shellescape shiftwidth simplify sin sinh sort soundfold spellbadword spellsuggest split sqrt str2float str2nr strchars strdisplaywidth strftime stridx string strlen strpart strridx strtrans strwidth submatch substitute synconcealed synID synIDattr synIDtrans synstack system tabpagebuflist tabpagenr tabpagewinnr tagfiles taglist tan tanh tempname tolower toupper tr trunc type undofile undotree values virtcol visualmode wildmenumode winbufnr wincol winheight winline winnr winrestcmd winrestview winsaveview winwidth writefile xor"},i:/[{:]/,c:[e.NM,e.ASM,{cN:"string",b:/"((\\")|[^"\n])*("|\n)/},{cN:"variable",b:/[bwtglsav]:[\w\d_]*/},{cN:"function",bK:"function function!",e:"$",r:0,c:[e.TM,{cN:"params",b:"\\(",e:"\\)"}]}]}}),hljs.registerLanguage("x86asm",function(e){return{cI:!0,l:"\\.?"+e.IR,k:{keyword:"lock rep repe repz repne repnz xaquire xrelease bnd nobnd aaa aad aam aas adc add and arpl bb0_reset bb1_reset bound bsf bsr bswap bt btc btr bts call cbw cdq cdqe clc cld cli clts cmc cmp cmpsb cmpsd cmpsq cmpsw cmpxchg cmpxchg486 cmpxchg8b cmpxchg16b cpuid cpu_read cpu_write cqo cwd cwde daa das dec div dmint emms enter equ f2xm1 fabs fadd faddp fbld fbstp fchs fclex fcmovb fcmovbe fcmove fcmovnb fcmovnbe fcmovne fcmovnu fcmovu fcom fcomi fcomip fcomp fcompp fcos fdecstp fdisi fdiv fdivp fdivr fdivrp femms feni ffree ffreep fiadd ficom ficomp fidiv fidivr fild fimul fincstp finit fist fistp fisttp fisub fisubr fld fld1 fldcw fldenv fldl2e fldl2t fldlg2 fldln2 fldpi fldz fmul fmulp fnclex fndisi fneni fninit fnop fnsave fnstcw fnstenv fnstsw fpatan fprem fprem1 fptan frndint frstor fsave fscale fsetpm fsin fsincos fsqrt fst fstcw fstenv fstp fstsw fsub fsubp fsubr fsubrp ftst fucom fucomi fucomip fucomp fucompp fxam fxch fxtract fyl2x fyl2xp1 hlt ibts icebp idiv imul in inc incbin insb insd insw int int01 int1 int03 int3 into invd invpcid invlpg invlpga iret iretd iretq iretw jcxz jecxz jrcxz jmp jmpe lahf lar lds lea leave les lfence lfs lgdt lgs lidt lldt lmsw loadall loadall286 lodsb lodsd lodsq lodsw loop loope loopne loopnz loopz lsl lss ltr mfence monitor mov movd movq movsb movsd movsq movsw movsx movsxd movzx mul mwait neg nop not or out outsb outsd outsw packssdw packsswb packuswb paddb paddd paddsb paddsiw paddsw paddusb paddusw paddw pand pandn pause paveb pavgusb pcmpeqb pcmpeqd pcmpeqw pcmpgtb pcmpgtd pcmpgtw pdistib pf2id pfacc pfadd pfcmpeq pfcmpge pfcmpgt pfmax pfmin pfmul pfrcp pfrcpit1 pfrcpit2 pfrsqit1 pfrsqrt pfsub pfsubr pi2fd pmachriw pmaddwd pmagw pmulhriw pmulhrwa pmulhrwc pmulhw pmullw pmvgezb pmvlzb pmvnzb pmvzb pop popa popad popaw popf popfd popfq popfw por prefetch prefetchw pslld psllq psllw psrad psraw psrld psrlq psrlw psubb psubd psubsb psubsiw psubsw psubusb psubusw psubw punpckhbw punpckhdq punpckhwd punpcklbw punpckldq punpcklwd push pusha pushad pushaw pushf pushfd pushfq pushfw pxor rcl rcr rdshr rdmsr rdpmc rdtsc rdtscp ret retf retn rol ror rdm rsdc rsldt rsm rsts sahf sal salc sar sbb scasb scasd scasq scasw sfence sgdt shl shld shr shrd sidt sldt skinit smi smint smintold smsw stc std sti stosb stosd stosq stosw str sub svdc svldt svts swapgs syscall sysenter sysexit sysret test ud0 ud1 ud2b ud2 ud2a umov verr verw fwait wbinvd wrshr wrmsr xadd xbts xchg xlatb xlat xor cmove cmovz cmovne cmovnz cmova cmovnbe cmovae cmovnb cmovb cmovnae cmovbe cmovna cmovg cmovnle cmovge cmovnl cmovl cmovnge cmovle cmovng cmovc cmovnc cmovo cmovno cmovs cmovns cmovp cmovpe cmovnp cmovpo je jz jne jnz ja jnbe jae jnb jb jnae jbe jna jg jnle jge jnl jl jnge jle jng jc jnc jo jno js jns jpo jnp jpe jp sete setz setne setnz seta setnbe setae setnb setnc setb setnae setcset setbe setna setg setnle setge setnl setl setnge setle setng sets setns seto setno setpe setp setpo setnp addps addss andnps andps cmpeqps cmpeqss cmpleps cmpless cmpltps cmpltss cmpneqps cmpneqss cmpnleps cmpnless cmpnltps cmpnltss cmpordps cmpordss cmpunordps cmpunordss cmpps cmpss comiss cvtpi2ps cvtps2pi cvtsi2ss cvtss2si cvttps2pi cvttss2si divps divss ldmxcsr maxps maxss minps minss movaps movhps movlhps movlps movhlps movmskps movntps movss movups mulps mulss orps rcpps rcpss rsqrtps rsqrtss shufps sqrtps sqrtss stmxcsr subps subss ucomiss unpckhps unpcklps xorps fxrstor fxrstor64 fxsave fxsave64 xgetbv xsetbv xsave xsave64 xsaveopt xsaveopt64 xrstor xrstor64 prefetchnta prefetcht0 prefetcht1 prefetcht2 maskmovq movntq pavgb pavgw pextrw pinsrw pmaxsw pmaxub pminsw pminub pmovmskb pmulhuw psadbw pshufw pf2iw pfnacc pfpnacc pi2fw pswapd maskmovdqu clflush movntdq movnti movntpd movdqa movdqu movdq2q movq2dq paddq pmuludq pshufd pshufhw pshuflw pslldq psrldq psubq punpckhqdq punpcklqdq addpd addsd andnpd andpd cmpeqpd cmpeqsd cmplepd cmplesd cmpltpd cmpltsd cmpneqpd cmpneqsd cmpnlepd cmpnlesd cmpnltpd cmpnltsd cmpordpd cmpordsd cmpunordpd cmpunordsd cmppd comisd cvtdq2pd cvtdq2ps cvtpd2dq cvtpd2pi cvtpd2ps cvtpi2pd cvtps2dq cvtps2pd cvtsd2si cvtsd2ss cvtsi2sd cvtss2sd cvttpd2pi cvttpd2dq cvttps2dq cvttsd2si divpd divsd maxpd maxsd minpd minsd movapd movhpd movlpd movmskpd movupd mulpd mulsd orpd shufpd sqrtpd sqrtsd subpd subsd ucomisd unpckhpd unpcklpd xorpd addsubpd addsubps haddpd haddps hsubpd hsubps lddqu movddup movshdup movsldup clgi stgi vmcall vmclear vmfunc vmlaunch vmload vmmcall vmptrld vmptrst vmread vmresume vmrun vmsave vmwrite vmxoff vmxon invept invvpid pabsb pabsw pabsd palignr phaddw phaddd phaddsw phsubw phsubd phsubsw pmaddubsw pmulhrsw pshufb psignb psignw psignd extrq insertq movntsd movntss lzcnt blendpd blendps blendvpd blendvps dppd dpps extractps insertps movntdqa mpsadbw packusdw pblendvb pblendw pcmpeqq pextrb pextrd pextrq phminposuw pinsrb pinsrd pinsrq pmaxsb pmaxsd pmaxud pmaxuw pminsb pminsd pminud pminuw pmovsxbw pmovsxbd pmovsxbq pmovsxwd pmovsxwq pmovsxdq pmovzxbw pmovzxbd pmovzxbq pmovzxwd pmovzxwq pmovzxdq pmuldq pmulld ptest roundpd roundps roundsd roundss crc32 pcmpestri pcmpestrm pcmpistri pcmpistrm pcmpgtq popcnt getsec pfrcpv pfrsqrtv movbe aesenc aesenclast aesdec aesdeclast aesimc aeskeygenassist vaesenc vaesenclast vaesdec vaesdeclast vaesimc vaeskeygenassist vaddpd vaddps vaddsd vaddss vaddsubpd vaddsubps vandpd vandps vandnpd vandnps vblendpd vblendps vblendvpd vblendvps vbroadcastss vbroadcastsd vbroadcastf128 vcmpeq_ospd vcmpeqpd vcmplt_ospd vcmpltpd vcmple_ospd vcmplepd vcmpunord_qpd vcmpunordpd vcmpneq_uqpd vcmpneqpd vcmpnlt_uspd vcmpnltpd vcmpnle_uspd vcmpnlepd vcmpord_qpd vcmpordpd vcmpeq_uqpd vcmpnge_uspd vcmpngepd vcmpngt_uspd vcmpngtpd vcmpfalse_oqpd vcmpfalsepd vcmpneq_oqpd vcmpge_ospd vcmpgepd vcmpgt_ospd vcmpgtpd vcmptrue_uqpd vcmptruepd vcmplt_oqpd vcmple_oqpd vcmpunord_spd vcmpneq_uspd vcmpnlt_uqpd vcmpnle_uqpd vcmpord_spd vcmpeq_uspd vcmpnge_uqpd vcmpngt_uqpd vcmpfalse_ospd vcmpneq_ospd vcmpge_oqpd vcmpgt_oqpd vcmptrue_uspd vcmppd vcmpeq_osps vcmpeqps vcmplt_osps vcmpltps vcmple_osps vcmpleps vcmpunord_qps vcmpunordps vcmpneq_uqps vcmpneqps vcmpnlt_usps vcmpnltps vcmpnle_usps vcmpnleps vcmpord_qps vcmpordps vcmpeq_uqps vcmpnge_usps vcmpngeps vcmpngt_usps vcmpngtps vcmpfalse_oqps vcmpfalseps vcmpneq_oqps vcmpge_osps vcmpgeps vcmpgt_osps vcmpgtps vcmptrue_uqps vcmptrueps vcmplt_oqps vcmple_oqps vcmpunord_sps vcmpneq_usps vcmpnlt_uqps vcmpnle_uqps vcmpord_sps vcmpeq_usps vcmpnge_uqps vcmpngt_uqps vcmpfalse_osps vcmpneq_osps vcmpge_oqps vcmpgt_oqps vcmptrue_usps vcmpps vcmpeq_ossd vcmpeqsd vcmplt_ossd vcmpltsd vcmple_ossd vcmplesd vcmpunord_qsd vcmpunordsd vcmpneq_uqsd vcmpneqsd vcmpnlt_ussd vcmpnltsd vcmpnle_ussd vcmpnlesd vcmpord_qsd vcmpordsd vcmpeq_uqsd vcmpnge_ussd vcmpngesd vcmpngt_ussd vcmpngtsd vcmpfalse_oqsd vcmpfalsesd vcmpneq_oqsd vcmpge_ossd vcmpgesd vcmpgt_ossd vcmpgtsd vcmptrue_uqsd vcmptruesd vcmplt_oqsd vcmple_oqsd vcmpunord_ssd vcmpneq_ussd vcmpnlt_uqsd vcmpnle_uqsd vcmpord_ssd vcmpeq_ussd vcmpnge_uqsd vcmpngt_uqsd vcmpfalse_ossd vcmpneq_ossd vcmpge_oqsd vcmpgt_oqsd vcmptrue_ussd vcmpsd vcmpeq_osss vcmpeqss vcmplt_osss vcmpltss vcmple_osss vcmpless vcmpunord_qss vcmpunordss vcmpneq_uqss vcmpneqss vcmpnlt_usss vcmpnltss vcmpnle_usss vcmpnless vcmpord_qss vcmpordss vcmpeq_uqss vcmpnge_usss vcmpngess vcmpngt_usss vcmpngtss vcmpfalse_oqss vcmpfalsess vcmpneq_oqss vcmpge_osss vcmpgess vcmpgt_osss vcmpgtss vcmptrue_uqss vcmptruess vcmplt_oqss vcmple_oqss vcmpunord_sss vcmpneq_usss vcmpnlt_uqss vcmpnle_uqss vcmpord_sss vcmpeq_usss vcmpnge_uqss vcmpngt_uqss vcmpfalse_osss vcmpneq_osss vcmpge_oqss vcmpgt_oqss vcmptrue_usss vcmpss vcomisd vcomiss vcvtdq2pd vcvtdq2ps vcvtpd2dq vcvtpd2ps vcvtps2dq vcvtps2pd vcvtsd2si vcvtsd2ss vcvtsi2sd vcvtsi2ss vcvtss2sd vcvtss2si vcvttpd2dq vcvttps2dq vcvttsd2si vcvttss2si vdivpd vdivps vdivsd vdivss vdppd vdpps vextractf128 vextractps vhaddpd vhaddps vhsubpd vhsubps vinsertf128 vinsertps vlddqu vldqqu vldmxcsr vmaskmovdqu vmaskmovps vmaskmovpd vmaxpd vmaxps vmaxsd vmaxss vminpd vminps vminsd vminss vmovapd vmovaps vmovd vmovq vmovddup vmovdqa vmovqqa vmovdqu vmovqqu vmovhlps vmovhpd vmovhps vmovlhps vmovlpd vmovlps vmovmskpd vmovmskps vmovntdq vmovntqq vmovntdqa vmovntpd vmovntps vmovsd vmovshdup vmovsldup vmovss vmovupd vmovups vmpsadbw vmulpd vmulps vmulsd vmulss vorpd vorps vpabsb vpabsw vpabsd vpacksswb vpackssdw vpackuswb vpackusdw vpaddb vpaddw vpaddd vpaddq vpaddsb vpaddsw vpaddusb vpaddusw vpalignr vpand vpandn vpavgb vpavgw vpblendvb vpblendw vpcmpestri vpcmpestrm vpcmpistri vpcmpistrm vpcmpeqb vpcmpeqw vpcmpeqd vpcmpeqq vpcmpgtb vpcmpgtw vpcmpgtd vpcmpgtq vpermilpd vpermilps vperm2f128 vpextrb vpextrw vpextrd vpextrq vphaddw vphaddd vphaddsw vphminposuw vphsubw vphsubd vphsubsw vpinsrb vpinsrw vpinsrd vpinsrq vpmaddwd vpmaddubsw vpmaxsb vpmaxsw vpmaxsd vpmaxub vpmaxuw vpmaxud vpminsb vpminsw vpminsd vpminub vpminuw vpminud vpmovmskb vpmovsxbw vpmovsxbd vpmovsxbq vpmovsxwd vpmovsxwq vpmovsxdq vpmovzxbw vpmovzxbd vpmovzxbq vpmovzxwd vpmovzxwq vpmovzxdq vpmulhuw vpmulhrsw vpmulhw vpmullw vpmulld vpmuludq vpmuldq vpor vpsadbw vpshufb vpshufd vpshufhw vpshuflw vpsignb vpsignw vpsignd vpslldq vpsrldq vpsllw vpslld vpsllq vpsraw vpsrad vpsrlw vpsrld vpsrlq vptest vpsubb vpsubw vpsubd vpsubq vpsubsb vpsubsw vpsubusb vpsubusw vpunpckhbw vpunpckhwd vpunpckhdq vpunpckhqdq vpunpcklbw vpunpcklwd vpunpckldq vpunpcklqdq vpxor vrcpps vrcpss vrsqrtps vrsqrtss vroundpd vroundps vroundsd vroundss vshufpd vshufps vsqrtpd vsqrtps vsqrtsd vsqrtss vstmxcsr vsubpd vsubps vsubsd vsubss vtestps vtestpd vucomisd vucomiss vunpckhpd vunpckhps vunpcklpd vunpcklps vxorpd vxorps vzeroall vzeroupper pclmullqlqdq pclmulhqlqdq pclmullqhqdq pclmulhqhqdq pclmulqdq vpclmullqlqdq vpclmulhqlqdq vpclmullqhqdq vpclmulhqhqdq vpclmulqdq vfmadd132ps vfmadd132pd vfmadd312ps vfmadd312pd vfmadd213ps vfmadd213pd vfmadd123ps vfmadd123pd vfmadd231ps vfmadd231pd vfmadd321ps vfmadd321pd vfmaddsub132ps vfmaddsub132pd vfmaddsub312ps vfmaddsub312pd vfmaddsub213ps vfmaddsub213pd vfmaddsub123ps vfmaddsub123pd vfmaddsub231ps vfmaddsub231pd vfmaddsub321ps vfmaddsub321pd vfmsub132ps vfmsub132pd vfmsub312ps vfmsub312pd vfmsub213ps vfmsub213pd vfmsub123ps vfmsub123pd vfmsub231ps vfmsub231pd vfmsub321ps vfmsub321pd vfmsubadd132ps vfmsubadd132pd vfmsubadd312ps vfmsubadd312pd vfmsubadd213ps vfmsubadd213pd vfmsubadd123ps vfmsubadd123pd vfmsubadd231ps vfmsubadd231pd vfmsubadd321ps vfmsubadd321pd vfnmadd132ps vfnmadd132pd vfnmadd312ps vfnmadd312pd vfnmadd213ps vfnmadd213pd vfnmadd123ps vfnmadd123pd vfnmadd231ps vfnmadd231pd vfnmadd321ps vfnmadd321pd vfnmsub132ps vfnmsub132pd vfnmsub312ps vfnmsub312pd vfnmsub213ps vfnmsub213pd vfnmsub123ps vfnmsub123pd vfnmsub231ps vfnmsub231pd vfnmsub321ps vfnmsub321pd vfmadd132ss vfmadd132sd vfmadd312ss vfmadd312sd vfmadd213ss vfmadd213sd vfmadd123ss vfmadd123sd vfmadd231ss vfmadd231sd vfmadd321ss vfmadd321sd vfmsub132ss vfmsub132sd vfmsub312ss vfmsub312sd vfmsub213ss vfmsub213sd vfmsub123ss vfmsub123sd vfmsub231ss vfmsub231sd vfmsub321ss vfmsub321sd vfnmadd132ss vfnmadd132sd vfnmadd312ss vfnmadd312sd vfnmadd213ss vfnmadd213sd vfnmadd123ss vfnmadd123sd vfnmadd231ss vfnmadd231sd vfnmadd321ss vfnmadd321sd vfnmsub132ss vfnmsub132sd vfnmsub312ss vfnmsub312sd vfnmsub213ss vfnmsub213sd vfnmsub123ss vfnmsub123sd vfnmsub231ss vfnmsub231sd vfnmsub321ss vfnmsub321sd rdfsbase rdgsbase rdrand wrfsbase wrgsbase vcvtph2ps vcvtps2ph adcx adox rdseed clac stac xstore xcryptecb xcryptcbc xcryptctr xcryptcfb xcryptofb montmul xsha1 xsha256 llwpcb slwpcb lwpval lwpins vfmaddpd vfmaddps vfmaddsd vfmaddss vfmaddsubpd vfmaddsubps vfmsubaddpd vfmsubaddps vfmsubpd vfmsubps vfmsubsd vfmsubss vfnmaddpd vfnmaddps vfnmaddsd vfnmaddss vfnmsubpd vfnmsubps vfnmsubsd vfnmsubss vfrczpd vfrczps vfrczsd vfrczss vpcmov vpcomb vpcomd vpcomq vpcomub vpcomud vpcomuq vpcomuw vpcomw vphaddbd vphaddbq vphaddbw vphadddq vphaddubd vphaddubq vphaddubw vphaddudq vphadduwd vphadduwq vphaddwd vphaddwq vphsubbw vphsubdq vphsubwd vpmacsdd vpmacsdqh vpmacsdql vpmacssdd vpmacssdqh vpmacssdql vpmacsswd vpmacssww vpmacswd vpmacsww vpmadcsswd vpmadcswd vpperm vprotb vprotd vprotq vprotw vpshab vpshad vpshaq vpshaw vpshlb vpshld vpshlq vpshlw vbroadcasti128 vpblendd vpbroadcastb vpbroadcastw vpbroadcastd vpbroadcastq vpermd vpermpd vpermps vpermq vperm2i128 vextracti128 vinserti128 vpmaskmovd vpmaskmovq vpsllvd vpsllvq vpsravd vpsrlvd vpsrlvq vgatherdpd vgatherqpd vgatherdps vgatherqps vpgatherdd vpgatherqd vpgatherdq vpgatherqq xabort xbegin xend xtest andn bextr blci blcic blsi blsic blcfill blsfill blcmsk blsmsk blsr blcs bzhi mulx pdep pext rorx sarx shlx shrx tzcnt tzmsk t1mskc valignd valignq vblendmpd vblendmps vbroadcastf32x4 vbroadcastf64x4 vbroadcasti32x4 vbroadcasti64x4 vcompresspd vcompressps vcvtpd2udq vcvtps2udq vcvtsd2usi vcvtss2usi vcvttpd2udq vcvttps2udq vcvttsd2usi vcvttss2usi vcvtudq2pd vcvtudq2ps vcvtusi2sd vcvtusi2ss vexpandpd vexpandps vextractf32x4 vextractf64x4 vextracti32x4 vextracti64x4 vfixupimmpd vfixupimmps vfixupimmsd vfixupimmss vgetexppd vgetexpps vgetexpsd vgetexpss vgetmantpd vgetmantps vgetmantsd vgetmantss vinsertf32x4 vinsertf64x4 vinserti32x4 vinserti64x4 vmovdqa32 vmovdqa64 vmovdqu32 vmovdqu64 vpabsq vpandd vpandnd vpandnq vpandq vpblendmd vpblendmq vpcmpltd vpcmpled vpcmpneqd vpcmpnltd vpcmpnled vpcmpd vpcmpltq vpcmpleq vpcmpneqq vpcmpnltq vpcmpnleq vpcmpq vpcmpequd vpcmpltud vpcmpleud vpcmpnequd vpcmpnltud vpcmpnleud vpcmpud vpcmpequq vpcmpltuq vpcmpleuq vpcmpnequq vpcmpnltuq vpcmpnleuq vpcmpuq vpcompressd vpcompressq vpermi2d vpermi2pd vpermi2ps vpermi2q vpermt2d vpermt2pd vpermt2ps vpermt2q vpexpandd vpexpandq vpmaxsq vpmaxuq vpminsq vpminuq vpmovdb vpmovdw vpmovqb vpmovqd vpmovqw vpmovsdb vpmovsdw vpmovsqb vpmovsqd vpmovsqw vpmovusdb vpmovusdw vpmovusqb vpmovusqd vpmovusqw vpord vporq vprold vprolq vprolvd vprolvq vprord vprorq vprorvd vprorvq vpscatterdd vpscatterdq vpscatterqd vpscatterqq vpsraq vpsravq vpternlogd vpternlogq vptestmd vptestmq vptestnmd vptestnmq vpxord vpxorq vrcp14pd vrcp14ps vrcp14sd vrcp14ss vrndscalepd vrndscaleps vrndscalesd vrndscaless vrsqrt14pd vrsqrt14ps vrsqrt14sd vrsqrt14ss vscalefpd vscalefps vscalefsd vscalefss vscatterdpd vscatterdps vscatterqpd vscatterqps vshuff32x4 vshuff64x2 vshufi32x4 vshufi64x2 kandnw kandw kmovw knotw kortestw korw kshiftlw kshiftrw kunpckbw kxnorw kxorw vpbroadcastmb2q vpbroadcastmw2d vpconflictd vpconflictq vplzcntd vplzcntq vexp2pd vexp2ps vrcp28pd vrcp28ps vrcp28sd vrcp28ss vrsqrt28pd vrsqrt28ps vrsqrt28sd vrsqrt28ss vgatherpf0dpd vgatherpf0dps vgatherpf0qpd vgatherpf0qps vgatherpf1dpd vgatherpf1dps vgatherpf1qpd vgatherpf1qps vscatterpf0dpd vscatterpf0dps vscatterpf0qpd vscatterpf0qps vscatterpf1dpd vscatterpf1dps vscatterpf1qpd vscatterpf1qps prefetchwt1 bndmk bndcl bndcu bndcn bndmov bndldx bndstx sha1rnds4 sha1nexte sha1msg1 sha1msg2 sha256rnds2 sha256msg1 sha256msg2 hint_nop0 hint_nop1 hint_nop2 hint_nop3 hint_nop4 hint_nop5 hint_nop6 hint_nop7 hint_nop8 hint_nop9 hint_nop10 hint_nop11 hint_nop12 hint_nop13 hint_nop14 hint_nop15 hint_nop16 hint_nop17 hint_nop18 hint_nop19 hint_nop20 hint_nop21 hint_nop22 hint_nop23 hint_nop24 hint_nop25 hint_nop26 hint_nop27 hint_nop28 hint_nop29 hint_nop30 hint_nop31 hint_nop32 hint_nop33 hint_nop34 hint_nop35 hint_nop36 hint_nop37 hint_nop38 hint_nop39 hint_nop40 hint_nop41 hint_nop42 hint_nop43 hint_nop44 hint_nop45 hint_nop46 hint_nop47 hint_nop48 hint_nop49 hint_nop50 hint_nop51 hint_nop52 hint_nop53 hint_nop54 hint_nop55 hint_nop56 hint_nop57 hint_nop58 hint_nop59 hint_nop60 hint_nop61 hint_nop62 hint_nop63",
literal:"ip eip rip al ah bl bh cl ch dl dh sil dil bpl spl r8b r9b r10b r11b r12b r13b r14b r15b ax bx cx dx si di bp sp r8w r9w r10w r11w r12w r13w r14w r15w eax ebx ecx edx esi edi ebp esp eip r8d r9d r10d r11d r12d r13d r14d r15d rax rbx rcx rdx rsi rdi rbp rsp r8 r9 r10 r11 r12 r13 r14 r15 cs ds es fs gs ss st st0 st1 st2 st3 st4 st5 st6 st7 mm0 mm1 mm2 mm3 mm4 mm5 mm6 mm7 xmm0  xmm1  xmm2  xmm3  xmm4  xmm5  xmm6  xmm7  xmm8  xmm9 xmm10  xmm11 xmm12 xmm13 xmm14 xmm15 xmm16 xmm17 xmm18 xmm19 xmm20 xmm21 xmm22 xmm23 xmm24 xmm25 xmm26 xmm27 xmm28 xmm29 xmm30 xmm31 ymm0  ymm1  ymm2  ymm3  ymm4  ymm5  ymm6  ymm7  ymm8  ymm9 ymm10  ymm11 ymm12 ymm13 ymm14 ymm15 ymm16 ymm17 ymm18 ymm19 ymm20 ymm21 ymm22 ymm23 ymm24 ymm25 ymm26 ymm27 ymm28 ymm29 ymm30 ymm31 zmm0  zmm1  zmm2  zmm3  zmm4  zmm5  zmm6  zmm7  zmm8  zmm9 zmm10  zmm11 zmm12 zmm13 zmm14 zmm15 zmm16 zmm17 zmm18 zmm19 zmm20 zmm21 zmm22 zmm23 zmm24 zmm25 zmm26 zmm27 zmm28 zmm29 zmm30 zmm31 k0 k1 k2 k3 k4 k5 k6 k7 bnd0 bnd1 bnd2 bnd3 cr0 cr1 cr2 cr3 cr4 cr8 dr0 dr1 dr2 dr3 dr8 tr3 tr4 tr5 tr6 tr7 r0 r1 r2 r3 r4 r5 r6 r7 r0b r1b r2b r3b r4b r5b r6b r7b r0w r1w r2w r3w r4w r5w r6w r7w r0d r1d r2d r3d r4d r5d r6d r7d r0h r1h r2h r3h r0l r1l r2l r3l r4l r5l r6l r7l r8l r9l r10l r11l r12l r13l r14l r15l",pseudo:"db dw dd dq dt ddq do dy dz resb resw resd resq rest resdq reso resy resz incbin equ times",preprocessor:"%define %xdefine %+ %undef %defstr %deftok %assign %strcat %strlen %substr %rotate %elif %else %endif %ifmacro %ifctx %ifidn %ifidni %ifid %ifnum %ifstr %iftoken %ifempty %ifenv %error %warning %fatal %rep %endrep %include %push %pop %repl %pathsearch %depend %use %arg %stacksize %local %line %comment %endcomment .nolist byte word dword qword nosplit rel abs seg wrt strict near far a32 ptr __FILE__ __LINE__ __SECT__  __BITS__ __OUTPUT_FORMAT__ __DATE__ __TIME__ __DATE_NUM__ __TIME_NUM__ __UTC_DATE__ __UTC_TIME__ __UTC_DATE_NUM__ __UTC_TIME_NUM__  __PASS__ struc endstruc istruc at iend align alignb sectalign daz nodaz up down zero default option assume public ",built_in:"bits use16 use32 use64 default section segment absolute extern global common cpu float __utf16__ __utf16le__ __utf16be__ __utf32__ __utf32le__ __utf32be__ __float8__ __float16__ __float32__ __float64__ __float80m__ __float80e__ __float128l__ __float128h__ __Infinity__ __QNaN__ __SNaN__ Inf NaN QNaN SNaN float8 float16 float32 float64 float80m float80e float128l float128h __FLOAT_DAZ__ __FLOAT_ROUND__ __FLOAT__"},c:[e.C(";","$",{r:0}),{cN:"number",b:"\\b(?:([0-9][0-9_]*)?\\.[0-9_]*(?:[eE][+-]?[0-9_]+)?|(0[Xx])?[0-9][0-9_]*\\.?[0-9_]*(?:[pP](?:[+-]?[0-9_]+)?)?)\\b",r:0},{cN:"number",b:"\\$[0-9][0-9A-Fa-f]*",r:0},{cN:"number",b:"\\b(?:[0-9A-Fa-f][0-9A-Fa-f_]*[HhXx]|[0-9][0-9_]*[DdTt]?|[0-7][0-7_]*[QqOo]|[0-1][0-1_]*[BbYy])\\b"},{cN:"number",b:"\\b(?:0[HhXx][0-9A-Fa-f_]+|0[DdTt][0-9_]+|0[QqOo][0-7_]+|0[BbYy][0-1_]+)\\b"},e.QSM,{cN:"string",b:"'",e:"[^\\\\]'",r:0},{cN:"string",b:"`",e:"[^\\\\]`",r:0},{cN:"string",b:"\\.[A-Za-z0-9]+",r:0},{cN:"label",b:"^\\s*[A-Za-z._?][A-Za-z0-9_$#@~.?]*(:|\\s+label)",r:0},{cN:"label",b:"^\\s*%%[A-Za-z0-9_$#@~.?]*:",r:0},{cN:"argument",b:"%[0-9]+",r:0},{cN:"built_in",b:"%!S+",r:0}]}}),hljs.registerLanguage("xl",function(e){var t="ObjectLoader Animate MovieCredits Slides Filters Shading Materials LensFlare Mapping VLCAudioVideo StereoDecoder PointCloud NetworkAccess RemoteControl RegExp ChromaKey Snowfall NodeJS Speech Charts",r={keyword:"if then else do while until for loop import with is as where when by data constant",literal:"true false nil",type:"integer real text name boolean symbol infix prefix postfix block tree",built_in:"in mod rem and or xor not abs sign floor ceil sqrt sin cos tan asin acos atan exp expm1 log log2 log10 log1p pi at",module:t,id:"text_length text_range text_find text_replace contains page slide basic_slide title_slide title subtitle fade_in fade_out fade_at clear_color color line_color line_width texture_wrap texture_transform texture scale_?x scale_?y scale_?z? translate_?x translate_?y translate_?z? rotate_?x rotate_?y rotate_?z? rectangle circle ellipse sphere path line_to move_to quad_to curve_to theme background contents locally time mouse_?x mouse_?y mouse_buttons"},a={cN:"constant",b:"[A-Z][A-Z_0-9]+",r:0},i={cN:"variable",b:"([A-Z][a-z_0-9]+)+",r:0},n={cN:"id",b:"[a-z][a-z_0-9]+",r:0},o={cN:"string",b:'"',e:'"',i:"\\n"},s={cN:"string",b:"'",e:"'",i:"\\n"},l={cN:"string",b:"<<",e:">>"},c={cN:"number",b:"[0-9]+#[0-9A-Z_]+(\\.[0-9-A-Z_]+)?#?([Ee][+-]?[0-9]+)?",r:10},d={cN:"import",bK:"import",e:"$",k:{keyword:"import",module:t},r:0,c:[o]},p={cN:"function",b:"[a-z].*->"};return{aliases:["tao"],l:/[a-zA-Z][a-zA-Z0-9_?]*/,k:r,c:[e.CLCM,e.CBCM,o,s,l,p,d,a,i,n,c,e.NM]}});;
(function(root) {
	"use strict";

	function codeHighlightLinenums(code, opts) {
		opts = opts || {};
		var hljs = opts.hljs,
			lang = opts.lang,
			start = opts.start || 0;
		// prevent errors by hljs
		code = code || '';
		if(lang && /:/.test(lang)) {
			start = +lang.split(/:/)[1];
			lang = lang.split(/:/)[0];
		} else {
			start = +start;
		}

		if(lang && hljs) {
			code = hljs.highlight(lang, code).value;
		}

		if(start) {
			// move all closing spans to the previous line
			code = code.replace(/([\r\n]\s*)(<\/span>)/ig, '$2$1');

			// replace spans with line-wraps inside them
			code = cleanLineBreaks(code);

			code = code.split(/\r\n|\r|\n/);
			var max = (start + code.length).toString().length;

			code = code
				.map(function(line, i) {
					return '<span class="line width-' + max + '" start="' + (start + i) + '">' + line + '</span>';
				})
				.join('\n');
		}

		return code;
	}

	// Simplified parser that looks for opening & closing spans, and walks the tree.
	// If there are any unclosed spans when a newline is encountered, we close them on the previous line,
	// and copy them forward to the next line.
	function cleanLineBreaks(code) {
		var openSpans = [],
			matcher = /<\/?span[^>]*>|\r\n|\r|\n/ig,
			newline = /\r\n|\r|\n/,
			closingTag = /^<\//;

		return code.replace(matcher, function(match) {
			if(newline.test(match)) {
				if(openSpans.length) {
					return openSpans.map(function() { return '</span>' }).join('') + match + openSpans.join('');
				} else {
					return match;
				}
			} else if(closingTag.test(match)) {
				openSpans.pop();
				return match;
			} else {
				openSpans.push(match);
				return match;
			}
		});
	}

	(function(factory) {
		if(typeof define === 'function' && define.amd) {
			// AMD. Register as an anonymous module.
			define(factory)
		} else if(typeof exports === 'object') {
			/**
			 * Node. Does not work with strict CommonJS, but
			 * only CommonJS-like environments that support module.exports,
			 * like Node.
			 */
			module.exports = factory();
		} else {
			// Browser globals (root is window)
			root.codeHighlightLinenums = factory();
		}
	}(function() {
		/**
		 * Just return a value to define the module export.
		 * This example returns an object, but the module
		 * can return a function as the exported value.
		 */
		return codeHighlightLinenums;
	}))

})(this);;
/**
 * marked - a markdown parser
 * Copyright (c) 2011-2014, Christopher Jeffrey. (MIT Licensed)
 * https://github.com/chjj/marked
 */

;(function() {

/**
 * Block-Level Grammar
 */

var block = {
  newline: /^\n+/,
  code: /^( {4}[^\n]+\n*)+/,
  fences: noop,
  hr: /^( *[-*_]){3,} *(?:\n+|$)/,
  heading: /^ *(#{1,6}) *([^\n]+?) *#* *(?:\n+|$)/,
  nptable: noop,
  lheading: /^([^\n]+)\n *(=|-){2,} *(?:\n+|$)/,
  blockquote: /^( *>[^\n]+(\n(?!def)[^\n]+)*\n*)+/,
  list: /^( *)(bull) [\s\S]+?(?:hr|def|\n{2,}(?! )(?!\1bull )\n*|\s*$)/,
  html: /^ *(?:comment *(?:\n|\s*$)|closed *(?:\n{2,}|\s*$)|closing *(?:\n{2,}|\s*$))/,
  def: /^ *\[([^\]]+)\]: *<?([^\s>]+)>?(?: +["(]([^\n]+)[")])? *(?:\n+|$)/,
  table: noop,
  paragraph: /^((?:[^\n]+\n?(?!hr|heading|lheading|blockquote|tag|def))+)\n*/,
  text: /^[^\n]+/
};

block.bullet = /(?:[*+-]|\d+\.)/;
block.item = /^( *)(bull) [^\n]*(?:\n(?!\1bull )[^\n]*)*/;
block.item = replace(block.item, 'gm')
  (/bull/g, block.bullet)
  ();

block.list = replace(block.list)
  (/bull/g, block.bullet)
  ('hr', '\\n+(?=\\1?(?:[-*_] *){3,}(?:\\n+|$))')
  ('def', '\\n+(?=' + block.def.source + ')')
  ();

block.blockquote = replace(block.blockquote)
  ('def', block.def)
  ();

block._tag = '(?!(?:'
  + 'a|em|strong|small|s|cite|q|dfn|abbr|data|time|code'
  + '|var|samp|kbd|sub|sup|i|b|u|mark|ruby|rt|rp|bdi|bdo'
  + '|span|br|wbr|ins|del|img)\\b)\\w+(?!:/|[^\\w\\s@]*@)\\b';

block.html = replace(block.html)
  ('comment', /<!--[\s\S]*?-->/)
  ('closed', /<(tag)[\s\S]+?<\/\1>/)
  ('closing', /<tag(?:"[^"]*"|'[^']*'|[^'">])*?>/)
  (/tag/g, block._tag)
  ();

block.paragraph = replace(block.paragraph)
  ('hr', block.hr)
  ('heading', block.heading)
  ('lheading', block.lheading)
  ('blockquote', block.blockquote)
  ('tag', '<' + block._tag)
  ('def', block.def)
  ();

/**
 * Normal Block Grammar
 */

block.normal = merge({}, block);

/**
 * GFM Block Grammar
 */

block.gfm = merge({}, block.normal, {
  fences: /^ *(`{3,}|~{3,})[ \.]*(\S+)? *\n([\s\S]*?)\s*\1 *(?:\n+|$)/,
  paragraph: /^/,
  heading: /^ *(#{1,6}) +([^\n]+?) *#* *(?:\n+|$)/
});

block.gfm.paragraph = replace(block.paragraph)
  ('(?!', '(?!'
    + block.gfm.fences.source.replace('\\1', '\\2') + '|'
    + block.list.source.replace('\\1', '\\3') + '|')
  ();

/**
 * GFM + Tables Block Grammar
 */

block.tables = merge({}, block.gfm, {
  nptable: /^ *(\S.*\|.*)\n *([-:]+ *\|[-| :]*)\n((?:.*\|.*(?:\n|$))*)\n*/,
  table: /^ *\|(.+)\n *\|( *[-:]+[-| :]*)\n((?: *\|.*(?:\n|$))*)\n*/
});

/**
 * Block Lexer
 */

function Lexer(options) {
  this.tokens = [];
  this.tokens.links = {};
  this.options = options || marked.defaults;
  this.rules = block.normal;

  if (this.options.gfm) {
    if (this.options.tables) {
      this.rules = block.tables;
    } else {
      this.rules = block.gfm;
    }
  }
}

/**
 * Expose Block Rules
 */

Lexer.rules = block;

/**
 * Static Lex Method
 */

Lexer.lex = function(src, options) {
  var lexer = new Lexer(options);
  return lexer.lex(src);
};

/**
 * Preprocessing
 */

Lexer.prototype.lex = function(src) {
  src = src
    .replace(/\r\n|\r/g, '\n')
    .replace(/\t/g, '    ')
    .replace(/\u00a0/g, ' ')
    .replace(/\u2424/g, '\n');

  return this.token(src, true);
};

/**
 * Lexing
 */

Lexer.prototype.token = function(src, top, bq) {
  var src = src.replace(/^ +$/gm, '')
    , next
    , loose
    , cap
    , bull
    , b
    , item
    , space
    , i
    , l;

  while (src) {
    // newline
    if (cap = this.rules.newline.exec(src)) {
      src = src.substring(cap[0].length);
      if (cap[0].length > 1) {
        this.tokens.push({
          type: 'space'
        });
      }
    }

    // code
    if (cap = this.rules.code.exec(src)) {
      src = src.substring(cap[0].length);
      cap = cap[0].replace(/^ {4}/gm, '');
      this.tokens.push({
        type: 'code',
        text: !this.options.pedantic
          ? cap.replace(/\n+$/, '')
          : cap
      });
      continue;
    }

    // fences (gfm)
    if (cap = this.rules.fences.exec(src)) {
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: 'code',
        lang: cap[2],
        text: cap[3] || ''
      });
      continue;
    }

    // heading
    if (cap = this.rules.heading.exec(src)) {
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: 'heading',
        depth: cap[1].length,
        text: cap[2]
      });
      continue;
    }

    // table no leading pipe (gfm)
    if (top && (cap = this.rules.nptable.exec(src))) {
      src = src.substring(cap[0].length);

      item = {
        type: 'table',
        header: cap[1].replace(/^ *| *\| *$/g, '').split(/ *\| */),
        align: cap[2].replace(/^ *|\| *$/g, '').split(/ *\| */),
        cells: cap[3].replace(/\n$/, '').split('\n')
      };

      for (i = 0; i < item.align.length; i++) {
        if (/^ *-+: *$/.test(item.align[i])) {
          item.align[i] = 'right';
        } else if (/^ *:-+: *$/.test(item.align[i])) {
          item.align[i] = 'center';
        } else if (/^ *:-+ *$/.test(item.align[i])) {
          item.align[i] = 'left';
        } else {
          item.align[i] = null;
        }
      }

      for (i = 0; i < item.cells.length; i++) {
        item.cells[i] = item.cells[i].split(/ *\| */);
      }

      this.tokens.push(item);

      continue;
    }

    // lheading
    if (cap = this.rules.lheading.exec(src)) {
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: 'heading',
        depth: cap[2] === '=' ? 1 : 2,
        text: cap[1]
      });
      continue;
    }

    // hr
    if (cap = this.rules.hr.exec(src)) {
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: 'hr'
      });
      continue;
    }

    // blockquote
    if (cap = this.rules.blockquote.exec(src)) {
      src = src.substring(cap[0].length);

      this.tokens.push({
        type: 'blockquote_start'
      });

      cap = cap[0].replace(/^ *> ?/gm, '');

      // Pass `top` to keep the current
      // "toplevel" state. This is exactly
      // how markdown.pl works.
      this.token(cap, top, true);

      this.tokens.push({
        type: 'blockquote_end'
      });

      continue;
    }

    // list
    if (cap = this.rules.list.exec(src)) {
      src = src.substring(cap[0].length);
      bull = cap[2];

      this.tokens.push({
        type: 'list_start',
        ordered: bull.length > 1
      });

      // Get each top-level item.
      cap = cap[0].match(this.rules.item);

      next = false;
      l = cap.length;
      i = 0;

      for (; i < l; i++) {
        item = cap[i];

        // Remove the list item's bullet
        // so it is seen as the next token.
        space = item.length;
        item = item.replace(/^ *([*+-]|\d+\.) +/, '');

        // Outdent whatever the
        // list item contains. Hacky.
        if (~item.indexOf('\n ')) {
          space -= item.length;
          item = !this.options.pedantic
            ? item.replace(new RegExp('^ {1,' + space + '}', 'gm'), '')
            : item.replace(/^ {1,4}/gm, '');
        }

        // Determine whether the next list item belongs here.
        // Backpedal if it does not belong in this list.
        if (this.options.smartLists && i !== l - 1) {
          b = block.bullet.exec(cap[i + 1])[0];
          if (bull !== b && !(bull.length > 1 && b.length > 1)) {
            src = cap.slice(i + 1).join('\n') + src;
            i = l - 1;
          }
        }

        // Determine whether item is loose or not.
        // Use: /(^|\n)(?! )[^\n]+\n\n(?!\s*$)/
        // for discount behavior.
        loose = next || /\n\n(?!\s*$)/.test(item);
        if (i !== l - 1) {
          next = item.charAt(item.length - 1) === '\n';
          if (!loose) loose = next;
        }

        this.tokens.push({
          type: loose
            ? 'loose_item_start'
            : 'list_item_start'
        });

        // Recurse.
        this.token(item, false, bq);

        this.tokens.push({
          type: 'list_item_end'
        });
      }

      this.tokens.push({
        type: 'list_end'
      });

      continue;
    }

    // html
    if (cap = this.rules.html.exec(src)) {
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: this.options.sanitize
          ? 'paragraph'
          : 'html',
        pre: !this.options.sanitizer
          && (cap[1] === 'pre' || cap[1] === 'script' || cap[1] === 'style'),
        text: cap[0]
      });
      continue;
    }

    // def
    if ((!bq && top) && (cap = this.rules.def.exec(src))) {
      src = src.substring(cap[0].length);
      this.tokens.links[cap[1].toLowerCase()] = {
        href: cap[2],
        title: cap[3]
      };
      continue;
    }

    // table (gfm)
    if (top && (cap = this.rules.table.exec(src))) {
      src = src.substring(cap[0].length);

      item = {
        type: 'table',
        header: cap[1].replace(/^ *| *\| *$/g, '').split(/ *\| */),
        align: cap[2].replace(/^ *|\| *$/g, '').split(/ *\| */),
        cells: cap[3].replace(/(?: *\| *)?\n$/, '').split('\n')
      };

      for (i = 0; i < item.align.length; i++) {
        if (/^ *-+: *$/.test(item.align[i])) {
          item.align[i] = 'right';
        } else if (/^ *:-+: *$/.test(item.align[i])) {
          item.align[i] = 'center';
        } else if (/^ *:-+ *$/.test(item.align[i])) {
          item.align[i] = 'left';
        } else {
          item.align[i] = null;
        }
      }

      for (i = 0; i < item.cells.length; i++) {
        item.cells[i] = item.cells[i]
          .replace(/^ *\| *| *\| *$/g, '')
          .split(/ *\| */);
      }

      this.tokens.push(item);

      continue;
    }

    // top-level paragraph
    if (top && (cap = this.rules.paragraph.exec(src))) {
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: 'paragraph',
        text: cap[1].charAt(cap[1].length - 1) === '\n'
          ? cap[1].slice(0, -1)
          : cap[1]
      });
      continue;
    }

    // text
    if (cap = this.rules.text.exec(src)) {
      // Top-level should never reach here.
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: 'text',
        text: cap[0]
      });
      continue;
    }

    if (src) {
      throw new
        Error('Infinite loop on byte: ' + src.charCodeAt(0));
    }
  }

  return this.tokens;
};

/**
 * Inline-Level Grammar
 */

var inline = {
  escape: /^\\([\\`*{}\[\]()#+\-.!_>])/,
  autolink: /^<([^ >]+(@|:\/)[^ >]+)>/,
  url: noop,
  tag: /^<!--[\s\S]*?-->|^<\/?\w+(?:"[^"]*"|'[^']*'|[^'">])*?>/,
  link: /^!?\[(inside)\]\(href\)/,
  reflink: /^!?\[(inside)\]\s*\[([^\]]*)\]/,
  nolink: /^!?\[((?:\[[^\]]*\]|[^\[\]])*)\]/,
  strong: /^__([\s\S]+?)__(?!_)|^\*\*([\s\S]+?)\*\*(?!\*)/,
  em: /^\b_((?:[^_]|__)+?)_\b|^\*((?:\*\*|[\s\S])+?)\*(?!\*)/,
  code: /^(`+)\s*([\s\S]*?[^`])\s*\1(?!`)/,
  br: /^ {2,}\n(?!\s*$)/,
  del: noop,
  text: /^[\s\S]+?(?=[\\<!\[_*`]| {2,}\n|$)/
};

inline._inside = /(?:\[[^\]]*\]|[^\[\]]|\](?=[^\[]*\]))*/;
inline._href = /\s*<?([\s\S]*?)>?(?:\s+['"]([\s\S]*?)['"])?\s*/;

inline.link = replace(inline.link)
  ('inside', inline._inside)
  ('href', inline._href)
  ();

inline.reflink = replace(inline.reflink)
  ('inside', inline._inside)
  ();

/**
 * Normal Inline Grammar
 */

inline.normal = merge({}, inline);

/**
 * Pedantic Inline Grammar
 */

inline.pedantic = merge({}, inline.normal, {
  strong: /^__(?=\S)([\s\S]*?\S)__(?!_)|^\*\*(?=\S)([\s\S]*?\S)\*\*(?!\*)/,
  em: /^_(?=\S)([\s\S]*?\S)_(?!_)|^\*(?=\S)([\s\S]*?\S)\*(?!\*)/
});

/**
 * GFM Inline Grammar
 */

inline.gfm = merge({}, inline.normal, {
  escape: replace(inline.escape)('])', '~|])')(),
  url: /^(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/,
  del: /^~~(?=\S)([\s\S]*?\S)~~/,
  text: replace(inline.text)
    (']|', '~]|')
    ('|', '|https?://|')
    ()
});

/**
 * GFM + Line Breaks Inline Grammar
 */

inline.breaks = merge({}, inline.gfm, {
  br: replace(inline.br)('{2,}', '*')(),
  text: replace(inline.gfm.text)('{2,}', '*')()
});

/**
 * Inline Lexer & Compiler
 */

function InlineLexer(links, options) {
  this.options = options || marked.defaults;
  this.links = links;
  this.rules = inline.normal;
  this.renderer = this.options.renderer || new Renderer;
  this.renderer.options = this.options;

  if (!this.links) {
    throw new
      Error('Tokens array requires a `links` property.');
  }

  if (this.options.gfm) {
    if (this.options.breaks) {
      this.rules = inline.breaks;
    } else {
      this.rules = inline.gfm;
    }
  } else if (this.options.pedantic) {
    this.rules = inline.pedantic;
  }
}

/**
 * Expose Inline Rules
 */

InlineLexer.rules = inline;

/**
 * Static Lexing/Compiling Method
 */

InlineLexer.output = function(src, links, options) {
  var inline = new InlineLexer(links, options);
  return inline.output(src);
};

/**
 * Lexing/Compiling
 */

InlineLexer.prototype.output = function(src) {
  var out = ''
    , link
    , text
    , href
    , cap;

  while (src) {
    // escape
    if (cap = this.rules.escape.exec(src)) {
      src = src.substring(cap[0].length);
      out += cap[1];
      continue;
    }

    // autolink
    if (cap = this.rules.autolink.exec(src)) {
      src = src.substring(cap[0].length);
      if (cap[2] === '@') {
        text = cap[1].charAt(6) === ':'
          ? this.mangle(cap[1].substring(7))
          : this.mangle(cap[1]);
        href = this.mangle('mailto:') + text;
      } else {
        text = escape(cap[1]);
        href = text;
      }
      out += this.renderer.link(href, null, text);
      continue;
    }

    // url (gfm)
    if (!this.inLink && (cap = this.rules.url.exec(src))) {
      src = src.substring(cap[0].length);
      text = escape(cap[1]);
      href = text;
      out += this.renderer.link(href, null, text);
      continue;
    }

    // tag
    if (cap = this.rules.tag.exec(src)) {
      if (!this.inLink && /^<a /i.test(cap[0])) {
        this.inLink = true;
      } else if (this.inLink && /^<\/a>/i.test(cap[0])) {
        this.inLink = false;
      }
      src = src.substring(cap[0].length);
      out += this.options.sanitize
        ? this.options.sanitizer
          ? this.options.sanitizer(cap[0])
          : escape(cap[0])
        : cap[0]
      continue;
    }

    // link
    if (cap = this.rules.link.exec(src)) {
      src = src.substring(cap[0].length);
      this.inLink = true;
      out += this.outputLink(cap, {
        href: cap[2],
        title: cap[3]
      });
      this.inLink = false;
      continue;
    }

    // reflink, nolink
    if ((cap = this.rules.reflink.exec(src))
        || (cap = this.rules.nolink.exec(src))) {
      src = src.substring(cap[0].length);
      link = (cap[2] || cap[1]).replace(/\s+/g, ' ');
      link = this.links[link.toLowerCase()];
      if (!link || !link.href) {
        out += cap[0].charAt(0);
        src = cap[0].substring(1) + src;
        continue;
      }
      this.inLink = true;
      out += this.outputLink(cap, link);
      this.inLink = false;
      continue;
    }

    // strong
    if (cap = this.rules.strong.exec(src)) {
      src = src.substring(cap[0].length);
      out += this.renderer.strong(this.output(cap[2] || cap[1]));
      continue;
    }

    // em
    if (cap = this.rules.em.exec(src)) {
      src = src.substring(cap[0].length);
      out += this.renderer.em(this.output(cap[2] || cap[1]));
      continue;
    }

    // code
    if (cap = this.rules.code.exec(src)) {
      src = src.substring(cap[0].length);
      out += this.renderer.codespan(escape(cap[2], true));
      continue;
    }

    // br
    if (cap = this.rules.br.exec(src)) {
      src = src.substring(cap[0].length);
      out += this.renderer.br();
      continue;
    }

    // del (gfm)
    if (cap = this.rules.del.exec(src)) {
      src = src.substring(cap[0].length);
      out += this.renderer.del(this.output(cap[1]));
      continue;
    }

    // text
    if (cap = this.rules.text.exec(src)) {
      src = src.substring(cap[0].length);
      out += this.renderer.text(escape(this.smartypants(cap[0])));
      continue;
    }

    if (src) {
      throw new
        Error('Infinite loop on byte: ' + src.charCodeAt(0));
    }
  }

  return out;
};

/**
 * Compile Link
 */

InlineLexer.prototype.outputLink = function(cap, link) {
  var href = escape(link.href)
    , title = link.title ? escape(link.title) : null;

  return cap[0].charAt(0) !== '!'
    ? this.renderer.link(href, title, this.output(cap[1]))
    : this.renderer.image(href, title, escape(cap[1]));
};

/**
 * Smartypants Transformations
 */

InlineLexer.prototype.smartypants = function(text) {
  if (!this.options.smartypants) return text;
  return text
    // em-dashes
    .replace(/---/g, '\u2014')
    // en-dashes
    .replace(/--/g, '\u2013')
    // opening singles
    .replace(/(^|[-\u2014/(\[{"\s])'/g, '$1\u2018')
    // closing singles & apostrophes
    .replace(/'/g, '\u2019')
    // opening doubles
    .replace(/(^|[-\u2014/(\[{\u2018\s])"/g, '$1\u201c')
    // closing doubles
    .replace(/"/g, '\u201d')
    // ellipses
    .replace(/\.{3}/g, '\u2026');
};

/**
 * Mangle Links
 */

InlineLexer.prototype.mangle = function(text) {
  if (!this.options.mangle) return text;
  var out = ''
    , l = text.length
    , i = 0
    , ch;

  for (; i < l; i++) {
    ch = text.charCodeAt(i);
    if (Math.random() > 0.5) {
      ch = 'x' + ch.toString(16);
    }
    out += '&#' + ch + ';';
  }

  return out;
};

/**
 * Renderer
 */

function Renderer(options) {
  this.options = options || {};
}

Renderer.prototype.code = function(code, lang, escaped) {
  if (this.options.highlight) {
    var out = this.options.highlight(code, lang);
    if (out != null && out !== code) {
      escaped = true;
      code = out;
    }
  }

  if (!lang) {
    return '<pre><code>'
      + (escaped ? code : escape(code, true))
      + '\n</code></pre>';
  }

  return '<pre><code class="'
    + this.options.langPrefix
    + escape(lang, true)
    + '">'
    + (escaped ? code : escape(code, true))
    + '\n</code></pre>\n';
};

Renderer.prototype.blockquote = function(quote) {
  return '<blockquote>\n' + quote + '</blockquote>\n';
};

Renderer.prototype.html = function(html) {
  return html;
};

Renderer.prototype.heading = function(text, level, raw) {
  return '<h'
    + level
    + ' id="'
    + this.options.headerPrefix
    + raw.toLowerCase().replace(/[^\w]+/g, '-')
    + '">'
    + text
    + '</h'
    + level
    + '>\n';
};

Renderer.prototype.hr = function() {
  return this.options.xhtml ? '<hr/>\n' : '<hr>\n';
};

Renderer.prototype.list = function(body, ordered) {
  var type = ordered ? 'ol' : 'ul';
  return '<' + type + '>\n' + body + '</' + type + '>\n';
};

Renderer.prototype.listitem = function(text) {
  return '<li>' + text + '</li>\n';
};

Renderer.prototype.paragraph = function(text) {
  return '<p>' + text + '</p>\n';
};

Renderer.prototype.table = function(header, body) {
  return '<table>\n'
    + '<thead>\n'
    + header
    + '</thead>\n'
    + '<tbody>\n'
    + body
    + '</tbody>\n'
    + '</table>\n';
};

Renderer.prototype.tablerow = function(content) {
  return '<tr>\n' + content + '</tr>\n';
};

Renderer.prototype.tablecell = function(content, flags) {
  var type = flags.header ? 'th' : 'td';
  var tag = flags.align
    ? '<' + type + ' style="text-align:' + flags.align + '">'
    : '<' + type + '>';
  return tag + content + '</' + type + '>\n';
};

// span level renderer
Renderer.prototype.strong = function(text) {
  return '<strong>' + text + '</strong>';
};

Renderer.prototype.em = function(text) {
  return '<em>' + text + '</em>';
};

Renderer.prototype.codespan = function(text) {
  return '<code>' + text + '</code>';
};

Renderer.prototype.br = function() {
  return this.options.xhtml ? '<br/>' : '<br>';
};

Renderer.prototype.del = function(text) {
  return '<del>' + text + '</del>';
};

Renderer.prototype.link = function(href, title, text) {
  if (this.options.sanitize) {
    try {
      var prot = decodeURIComponent(unescape(href))
        .replace(/[^\w:]/g, '')
        .toLowerCase();
    } catch (e) {
      return '';
    }
    if (prot.indexOf('javascript:') === 0 || prot.indexOf('vbscript:') === 0) {
      return '';
    }
  }
  var out = '<a href="' + href + '"';
  if (title) {
    out += ' title="' + title + '"';
  }
  out += '>' + text + '</a>';
  return out;
};

Renderer.prototype.image = function(href, title, text) {
  var out = '<img src="' + href + '" alt="' + text + '"';
  if (title) {
    out += ' title="' + title + '"';
  }
  out += this.options.xhtml ? '/>' : '>';
  return out;
};

Renderer.prototype.text = function(text) {
  return text;
};

/**
 * Parsing & Compiling
 */

function Parser(options) {
  this.tokens = [];
  this.token = null;
  this.options = options || marked.defaults;
  this.options.renderer = this.options.renderer || new Renderer;
  this.renderer = this.options.renderer;
  this.renderer.options = this.options;
}

/**
 * Static Parse Method
 */

Parser.parse = function(src, options, renderer) {
  var parser = new Parser(options, renderer);
  return parser.parse(src);
};

/**
 * Parse Loop
 */

Parser.prototype.parse = function(src) {
  this.inline = new InlineLexer(src.links, this.options, this.renderer);
  this.tokens = src.reverse();

  var out = '';
  while (this.next()) {
    out += this.tok();
  }

  return out;
};

/**
 * Next Token
 */

Parser.prototype.next = function() {
  return this.token = this.tokens.pop();
};

/**
 * Preview Next Token
 */

Parser.prototype.peek = function() {
  return this.tokens[this.tokens.length - 1] || 0;
};

/**
 * Parse Text Tokens
 */

Parser.prototype.parseText = function() {
  var body = this.token.text;

  while (this.peek().type === 'text') {
    body += '\n' + this.next().text;
  }

  return this.inline.output(body);
};

/**
 * Parse Current Token
 */

Parser.prototype.tok = function() {
  switch (this.token.type) {
    case 'space': {
      return '';
    }
    case 'hr': {
      return this.renderer.hr();
    }
    case 'heading': {
      return this.renderer.heading(
        this.inline.output(this.token.text),
        this.token.depth,
        this.token.text);
    }
    case 'code': {
      return this.renderer.code(this.token.text,
        this.token.lang,
        this.token.escaped);
    }
    case 'table': {
      var header = ''
        , body = ''
        , i
        , row
        , cell
        , flags
        , j;

      // header
      cell = '';
      for (i = 0; i < this.token.header.length; i++) {
        flags = { header: true, align: this.token.align[i] };
        cell += this.renderer.tablecell(
          this.inline.output(this.token.header[i]),
          { header: true, align: this.token.align[i] }
        );
      }
      header += this.renderer.tablerow(cell);

      for (i = 0; i < this.token.cells.length; i++) {
        row = this.token.cells[i];

        cell = '';
        for (j = 0; j < row.length; j++) {
          cell += this.renderer.tablecell(
            this.inline.output(row[j]),
            { header: false, align: this.token.align[j] }
          );
        }

        body += this.renderer.tablerow(cell);
      }
      return this.renderer.table(header, body);
    }
    case 'blockquote_start': {
      var body = '';

      while (this.next().type !== 'blockquote_end') {
        body += this.tok();
      }

      return this.renderer.blockquote(body);
    }
    case 'list_start': {
      var body = ''
        , ordered = this.token.ordered;

      while (this.next().type !== 'list_end') {
        body += this.tok();
      }

      return this.renderer.list(body, ordered);
    }
    case 'list_item_start': {
      var body = '';

      while (this.next().type !== 'list_item_end') {
        body += this.token.type === 'text'
          ? this.parseText()
          : this.tok();
      }

      return this.renderer.listitem(body);
    }
    case 'loose_item_start': {
      var body = '';

      while (this.next().type !== 'list_item_end') {
        body += this.tok();
      }

      return this.renderer.listitem(body);
    }
    case 'html': {
      var html = !this.token.pre && !this.options.pedantic
        ? this.inline.output(this.token.text)
        : this.token.text;
      return this.renderer.html(html);
    }
    case 'paragraph': {
      return this.renderer.paragraph(this.inline.output(this.token.text));
    }
    case 'text': {
      return this.renderer.paragraph(this.parseText());
    }
  }
};

/**
 * Helpers
 */

function escape(html, encode) {
  return html
    .replace(!encode ? /&(?!#?\w+;)/g : /&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function unescape(html) {
  return html.replace(/&([#\w]+);/g, function(_, n) {
    n = n.toLowerCase();
    if (n === 'colon') return ':';
    if (n.charAt(0) === '#') {
      return n.charAt(1) === 'x'
        ? String.fromCharCode(parseInt(n.substring(2), 16))
        : String.fromCharCode(+n.substring(1));
    }
    return '';
  });
}

function replace(regex, opt) {
  regex = regex.source;
  opt = opt || '';
  return function self(name, val) {
    if (!name) return new RegExp(regex, opt);
    val = val.source || val;
    val = val.replace(/(^|[^\[])\^/g, '$1');
    regex = regex.replace(name, val);
    return self;
  };
}

function noop() {}
noop.exec = noop;

function merge(obj) {
  var i = 1
    , target
    , key;

  for (; i < arguments.length; i++) {
    target = arguments[i];
    for (key in target) {
      if (Object.prototype.hasOwnProperty.call(target, key)) {
        obj[key] = target[key];
      }
    }
  }

  return obj;
}


/**
 * Marked
 */

function marked(src, opt, callback) {
  if (callback || typeof opt === 'function') {
    if (!callback) {
      callback = opt;
      opt = null;
    }

    opt = merge({}, marked.defaults, opt || {});

    var highlight = opt.highlight
      , tokens
      , pending
      , i = 0;

    try {
      tokens = Lexer.lex(src, opt)
    } catch (e) {
      return callback(e);
    }

    pending = tokens.length;

    var done = function(err) {
      if (err) {
        opt.highlight = highlight;
        return callback(err);
      }

      var out;

      try {
        out = Parser.parse(tokens, opt);
      } catch (e) {
        err = e;
      }

      opt.highlight = highlight;

      return err
        ? callback(err)
        : callback(null, out);
    };

    if (!highlight || highlight.length < 3) {
      return done();
    }

    delete opt.highlight;

    if (!pending) return done();

    for (; i < tokens.length; i++) {
      (function(token) {
        if (token.type !== 'code') {
          return --pending || done();
        }
        return highlight(token.text, token.lang, function(err, code) {
          if (err) return done(err);
          if (code == null || code === token.text) {
            return --pending || done();
          }
          token.text = code;
          token.escaped = true;
          --pending || done();
        });
      })(tokens[i]);
    }

    return;
  }
  try {
    if (opt) opt = merge({}, marked.defaults, opt);
    return Parser.parse(Lexer.lex(src, opt), opt);
  } catch (e) {
    e.message += '\nPlease report this to https://github.com/chjj/marked.';
    if ((opt || marked.defaults).silent) {
      return '<p>An error occured:</p><pre>'
        + escape(e.message + '', true)
        + '</pre>';
    }
    throw e;
  }
}

/**
 * Options
 */

marked.options =
marked.setOptions = function(opt) {
  merge(marked.defaults, opt);
  return marked;
};

marked.defaults = {
  gfm: true,
  tables: true,
  breaks: false,
  pedantic: false,
  sanitize: false,
  sanitizer: null,
  mangle: true,
  smartLists: false,
  silent: false,
  highlight: null,
  langPrefix: 'lang-',
  smartypants: false,
  headerPrefix: '',
  renderer: new Renderer,
  xhtml: false
};

/**
 * Expose
 */

marked.Parser = Parser;
marked.parser = Parser.parse;

marked.Renderer = Renderer;

marked.Lexer = Lexer;
marked.lexer = Lexer.lex;

marked.InlineLexer = InlineLexer;
marked.inlineLexer = InlineLexer.output;

marked.parse = marked;

if (typeof module !== 'undefined' && typeof exports === 'object') {
  module.exports = marked;
} else if (typeof define === 'function' && define.amd) {
  define(function() { return marked; });
} else {
  this.marked = marked;
}

}).call(function() {
  return this || (typeof window !== 'undefined' ? window : global);
}());
;
angular.module('ui.bootstrap.position', [])

/**
 * A set of utility methods that can be use to retrieve position of DOM elements.
 * It is meant to be used where we need to absolute-position DOM elements in
 * relation to other, existing elements (this is the case for tooltips, popovers,
 * typeahead suggestions etc.).
 */
  .factory('$position', ['$document', '$window', function ($document, $window) {

    function getStyle(el, cssprop) {
      if (el.currentStyle) { //IE
        return el.currentStyle[cssprop];
      } else if ($window.getComputedStyle) {
        return $window.getComputedStyle(el)[cssprop];
      }
      // finally try and get inline style
      return el.style[cssprop];
    }

    /**
     * Checks if a given element is statically positioned
     * @param element - raw DOM element
     */
    function isStaticPositioned(element) {
      return (getStyle(element, 'position') || 'static' ) === 'static';
    }

    /**
     * returns the closest, non-statically positioned parentOffset of a given element
     * @param element
     */
    var parentOffsetEl = function (element) {
      var docDomEl = $document[0];
      var offsetParent = element.offsetParent || docDomEl;
      while (offsetParent && offsetParent !== docDomEl && isStaticPositioned(offsetParent) ) {
        offsetParent = offsetParent.offsetParent;
      }
      return offsetParent || docDomEl;
    };

    return {
      /**
       * Provides read-only equivalent of jQuery's position function:
       * http://api.jquery.com/position/
       */
      position: function (element) {
        var elBCR = this.offset(element);
        var offsetParentBCR = { top: 0, left: 0 };
        var offsetParentEl = parentOffsetEl(element[0]);
        if (offsetParentEl != $document[0]) {
          offsetParentBCR = this.offset(angular.element(offsetParentEl));
          offsetParentBCR.top += offsetParentEl.clientTop - offsetParentEl.scrollTop;
          offsetParentBCR.left += offsetParentEl.clientLeft - offsetParentEl.scrollLeft;
        }

        var boundingClientRect = element[0].getBoundingClientRect();
        return {
          width: boundingClientRect.width || element.prop('offsetWidth'),
          height: boundingClientRect.height || element.prop('offsetHeight'),
          top: elBCR.top - offsetParentBCR.top,
          left: elBCR.left - offsetParentBCR.left
        };
      },

      /**
       * Provides read-only equivalent of jQuery's offset function:
       * http://api.jquery.com/offset/
       */
      offset: function (element) {
        var boundingClientRect = element[0].getBoundingClientRect();
        return {
          width: boundingClientRect.width || element.prop('offsetWidth'),
          height: boundingClientRect.height || element.prop('offsetHeight'),
          top: boundingClientRect.top + ($window.pageYOffset || $document[0].documentElement.scrollTop),
          left: boundingClientRect.left + ($window.pageXOffset || $document[0].documentElement.scrollLeft)
        };
      },

      /**
       * Provides coordinates for the targetEl in relation to hostEl
       */
      positionElements: function (hostEl, targetEl, positionStr, appendToBody) {

        var positionStrParts = positionStr.split('-');
        var pos0 = positionStrParts[0], pos1 = positionStrParts[1] || 'center';

        var hostElPos,
          targetElWidth,
          targetElHeight,
          targetElPos;

        hostElPos = appendToBody ? this.offset(hostEl) : this.position(hostEl);

        targetElWidth = targetEl.prop('offsetWidth');
        targetElHeight = targetEl.prop('offsetHeight');

        var shiftWidth = {
          center: function () {
            return hostElPos.left + hostElPos.width / 2 - targetElWidth / 2;
          },
          left: function () {
            return hostElPos.left;
          },
          right: function () {
            return hostElPos.left + hostElPos.width;
          }
        };

        var shiftHeight = {
          center: function () {
            return hostElPos.top + hostElPos.height / 2 - targetElHeight / 2;
          },
          top: function () {
            return hostElPos.top;
          },
          bottom: function () {
            return hostElPos.top + hostElPos.height;
          }
        };

        switch (pos0) {
          case 'right':
            targetElPos = {
              top: shiftHeight[pos1](),
              left: shiftWidth[pos0]()
            };
            break;
          case 'left':
            targetElPos = {
              top: shiftHeight[pos1](),
              left: hostElPos.left - targetElWidth
            };
            break;
          case 'bottom':
            targetElPos = {
              top: shiftHeight[pos0](),
              left: shiftWidth[pos1]()
            };
            break;
          default:
            targetElPos = {
              top: hostElPos.top - targetElHeight,
              left: shiftWidth[pos1]()
            };
            break;
        }

        return targetElPos;
      }
    };
  }]);
;
/*!
 * angular-input-match
 * Checks if one input matches another
 * @version v1.4.1
 * @link https://github.com/TheSharpieOne/angular-input-match
 * @license MIT License, http://www.opensource.org/licenses/MIT
 */
(function(window, angular, undefined){'use strict';

angular.module('validation.match', []);

angular.module('validation.match').directive('match', match);

function match ($parse) {
    return {
        require: '?ngModel',
        restrict: 'A',
        link: function(scope, elem, attrs, ctrl) {
            if(!ctrl) {
                if(console && console.warn){
                    console.warn('Match validation requires ngModel to be on the element');
                }
                return;
            }

            var matchGetter = $parse(attrs.match);

            scope.$watch(getMatchValue, function(){
                ctrl.$$parseAndValidate();
            });

            ctrl.$validators.match = function(){
                return ctrl.$viewValue === getMatchValue();
            };

            function getMatchValue(){
                var match = matchGetter(scope);
                if(angular.isObject(match) && match.hasOwnProperty('$viewValue')){
                    match = match.$viewValue;
                }
                return match;
            }
        }
    };
}
match.$inject = ["$parse"];
})(window, window.angular);;
//! moment.js
//! version : 2.13.0
//! authors : Tim Wood, Iskren Chernev, Moment.js contributors
//! license : MIT
//! momentjs.com

;(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
    typeof define === 'function' && define.amd ? define(factory) :
    global.moment = factory()
}(this, function () { 'use strict';

    var hookCallback;

    function utils_hooks__hooks () {
        return hookCallback.apply(null, arguments);
    }

    // This is done to register the method called with moment()
    // without creating circular dependencies.
    function setHookCallback (callback) {
        hookCallback = callback;
    }

    function isArray(input) {
        return input instanceof Array || Object.prototype.toString.call(input) === '[object Array]';
    }

    function isDate(input) {
        return input instanceof Date || Object.prototype.toString.call(input) === '[object Date]';
    }

    function map(arr, fn) {
        var res = [], i;
        for (i = 0; i < arr.length; ++i) {
            res.push(fn(arr[i], i));
        }
        return res;
    }

    function hasOwnProp(a, b) {
        return Object.prototype.hasOwnProperty.call(a, b);
    }

    function extend(a, b) {
        for (var i in b) {
            if (hasOwnProp(b, i)) {
                a[i] = b[i];
            }
        }

        if (hasOwnProp(b, 'toString')) {
            a.toString = b.toString;
        }

        if (hasOwnProp(b, 'valueOf')) {
            a.valueOf = b.valueOf;
        }

        return a;
    }

    function create_utc__createUTC (input, format, locale, strict) {
        return createLocalOrUTC(input, format, locale, strict, true).utc();
    }

    function defaultParsingFlags() {
        // We need to deep clone this object.
        return {
            empty           : false,
            unusedTokens    : [],
            unusedInput     : [],
            overflow        : -2,
            charsLeftOver   : 0,
            nullInput       : false,
            invalidMonth    : null,
            invalidFormat   : false,
            userInvalidated : false,
            iso             : false,
            parsedDateParts : [],
            meridiem        : null
        };
    }

    function getParsingFlags(m) {
        if (m._pf == null) {
            m._pf = defaultParsingFlags();
        }
        return m._pf;
    }

    var some;
    if (Array.prototype.some) {
        some = Array.prototype.some;
    } else {
        some = function (fun) {
            var t = Object(this);
            var len = t.length >>> 0;

            for (var i = 0; i < len; i++) {
                if (i in t && fun.call(this, t[i], i, t)) {
                    return true;
                }
            }

            return false;
        };
    }

    function valid__isValid(m) {
        if (m._isValid == null) {
            var flags = getParsingFlags(m);
            var parsedParts = some.call(flags.parsedDateParts, function (i) {
                return i != null;
            });
            m._isValid = !isNaN(m._d.getTime()) &&
                flags.overflow < 0 &&
                !flags.empty &&
                !flags.invalidMonth &&
                !flags.invalidWeekday &&
                !flags.nullInput &&
                !flags.invalidFormat &&
                !flags.userInvalidated &&
                (!flags.meridiem || (flags.meridiem && parsedParts));

            if (m._strict) {
                m._isValid = m._isValid &&
                    flags.charsLeftOver === 0 &&
                    flags.unusedTokens.length === 0 &&
                    flags.bigHour === undefined;
            }
        }
        return m._isValid;
    }

    function valid__createInvalid (flags) {
        var m = create_utc__createUTC(NaN);
        if (flags != null) {
            extend(getParsingFlags(m), flags);
        }
        else {
            getParsingFlags(m).userInvalidated = true;
        }

        return m;
    }

    function isUndefined(input) {
        return input === void 0;
    }

    // Plugins that add properties should also add the key here (null value),
    // so we can properly clone ourselves.
    var momentProperties = utils_hooks__hooks.momentProperties = [];

    function copyConfig(to, from) {
        var i, prop, val;

        if (!isUndefined(from._isAMomentObject)) {
            to._isAMomentObject = from._isAMomentObject;
        }
        if (!isUndefined(from._i)) {
            to._i = from._i;
        }
        if (!isUndefined(from._f)) {
            to._f = from._f;
        }
        if (!isUndefined(from._l)) {
            to._l = from._l;
        }
        if (!isUndefined(from._strict)) {
            to._strict = from._strict;
        }
        if (!isUndefined(from._tzm)) {
            to._tzm = from._tzm;
        }
        if (!isUndefined(from._isUTC)) {
            to._isUTC = from._isUTC;
        }
        if (!isUndefined(from._offset)) {
            to._offset = from._offset;
        }
        if (!isUndefined(from._pf)) {
            to._pf = getParsingFlags(from);
        }
        if (!isUndefined(from._locale)) {
            to._locale = from._locale;
        }

        if (momentProperties.length > 0) {
            for (i in momentProperties) {
                prop = momentProperties[i];
                val = from[prop];
                if (!isUndefined(val)) {
                    to[prop] = val;
                }
            }
        }

        return to;
    }

    var updateInProgress = false;

    // Moment prototype object
    function Moment(config) {
        copyConfig(this, config);
        this._d = new Date(config._d != null ? config._d.getTime() : NaN);
        // Prevent infinite loop in case updateOffset creates new moment
        // objects.
        if (updateInProgress === false) {
            updateInProgress = true;
            utils_hooks__hooks.updateOffset(this);
            updateInProgress = false;
        }
    }

    function isMoment (obj) {
        return obj instanceof Moment || (obj != null && obj._isAMomentObject != null);
    }

    function absFloor (number) {
        if (number < 0) {
            return Math.ceil(number);
        } else {
            return Math.floor(number);
        }
    }

    function toInt(argumentForCoercion) {
        var coercedNumber = +argumentForCoercion,
            value = 0;

        if (coercedNumber !== 0 && isFinite(coercedNumber)) {
            value = absFloor(coercedNumber);
        }

        return value;
    }

    // compare two arrays, return the number of differences
    function compareArrays(array1, array2, dontConvert) {
        var len = Math.min(array1.length, array2.length),
            lengthDiff = Math.abs(array1.length - array2.length),
            diffs = 0,
            i;
        for (i = 0; i < len; i++) {
            if ((dontConvert && array1[i] !== array2[i]) ||
                (!dontConvert && toInt(array1[i]) !== toInt(array2[i]))) {
                diffs++;
            }
        }
        return diffs + lengthDiff;
    }

    function warn(msg) {
        if (utils_hooks__hooks.suppressDeprecationWarnings === false &&
                (typeof console !==  'undefined') && console.warn) {
            console.warn('Deprecation warning: ' + msg);
        }
    }

    function deprecate(msg, fn) {
        var firstTime = true;

        return extend(function () {
            if (utils_hooks__hooks.deprecationHandler != null) {
                utils_hooks__hooks.deprecationHandler(null, msg);
            }
            if (firstTime) {
                warn(msg + '\nArguments: ' + Array.prototype.slice.call(arguments).join(', ') + '\n' + (new Error()).stack);
                firstTime = false;
            }
            return fn.apply(this, arguments);
        }, fn);
    }

    var deprecations = {};

    function deprecateSimple(name, msg) {
        if (utils_hooks__hooks.deprecationHandler != null) {
            utils_hooks__hooks.deprecationHandler(name, msg);
        }
        if (!deprecations[name]) {
            warn(msg);
            deprecations[name] = true;
        }
    }

    utils_hooks__hooks.suppressDeprecationWarnings = false;
    utils_hooks__hooks.deprecationHandler = null;

    function isFunction(input) {
        return input instanceof Function || Object.prototype.toString.call(input) === '[object Function]';
    }

    function isObject(input) {
        return Object.prototype.toString.call(input) === '[object Object]';
    }

    function locale_set__set (config) {
        var prop, i;
        for (i in config) {
            prop = config[i];
            if (isFunction(prop)) {
                this[i] = prop;
            } else {
                this['_' + i] = prop;
            }
        }
        this._config = config;
        // Lenient ordinal parsing accepts just a number in addition to
        // number + (possibly) stuff coming from _ordinalParseLenient.
        this._ordinalParseLenient = new RegExp(this._ordinalParse.source + '|' + (/\d{1,2}/).source);
    }

    function mergeConfigs(parentConfig, childConfig) {
        var res = extend({}, parentConfig), prop;
        for (prop in childConfig) {
            if (hasOwnProp(childConfig, prop)) {
                if (isObject(parentConfig[prop]) && isObject(childConfig[prop])) {
                    res[prop] = {};
                    extend(res[prop], parentConfig[prop]);
                    extend(res[prop], childConfig[prop]);
                } else if (childConfig[prop] != null) {
                    res[prop] = childConfig[prop];
                } else {
                    delete res[prop];
                }
            }
        }
        return res;
    }

    function Locale(config) {
        if (config != null) {
            this.set(config);
        }
    }

    var keys;

    if (Object.keys) {
        keys = Object.keys;
    } else {
        keys = function (obj) {
            var i, res = [];
            for (i in obj) {
                if (hasOwnProp(obj, i)) {
                    res.push(i);
                }
            }
            return res;
        };
    }

    // internal storage for locale config files
    var locales = {};
    var globalLocale;

    function normalizeLocale(key) {
        return key ? key.toLowerCase().replace('_', '-') : key;
    }

    // pick the locale from the array
    // try ['en-au', 'en-gb'] as 'en-au', 'en-gb', 'en', as in move through the list trying each
    // substring from most specific to least, but move to the next array item if it's a more specific variant than the current root
    function chooseLocale(names) {
        var i = 0, j, next, locale, split;

        while (i < names.length) {
            split = normalizeLocale(names[i]).split('-');
            j = split.length;
            next = normalizeLocale(names[i + 1]);
            next = next ? next.split('-') : null;
            while (j > 0) {
                locale = loadLocale(split.slice(0, j).join('-'));
                if (locale) {
                    return locale;
                }
                if (next && next.length >= j && compareArrays(split, next, true) >= j - 1) {
                    //the next array item is better than a shallower substring of this one
                    break;
                }
                j--;
            }
            i++;
        }
        return null;
    }

    function loadLocale(name) {
        var oldLocale = null;
        // TODO: Find a better way to register and load all the locales in Node
        if (!locales[name] && (typeof module !== 'undefined') &&
                module && module.exports) {
            try {
                oldLocale = globalLocale._abbr;
                require('./locale/' + name);
                // because defineLocale currently also sets the global locale, we
                // want to undo that for lazy loaded locales
                locale_locales__getSetGlobalLocale(oldLocale);
            } catch (e) { }
        }
        return locales[name];
    }

    // This function will load locale and then set the global locale.  If
    // no arguments are passed in, it will simply return the current global
    // locale key.
    function locale_locales__getSetGlobalLocale (key, values) {
        var data;
        if (key) {
            if (isUndefined(values)) {
                data = locale_locales__getLocale(key);
            }
            else {
                data = defineLocale(key, values);
            }

            if (data) {
                // moment.duration._locale = moment._locale = data;
                globalLocale = data;
            }
        }

        return globalLocale._abbr;
    }

    function defineLocale (name, config) {
        if (config !== null) {
            config.abbr = name;
            if (locales[name] != null) {
                deprecateSimple('defineLocaleOverride',
                        'use moment.updateLocale(localeName, config) to change ' +
                        'an existing locale. moment.defineLocale(localeName, ' +
                        'config) should only be used for creating a new locale');
                config = mergeConfigs(locales[name]._config, config);
            } else if (config.parentLocale != null) {
                if (locales[config.parentLocale] != null) {
                    config = mergeConfigs(locales[config.parentLocale]._config, config);
                } else {
                    // treat as if there is no base config
                    deprecateSimple('parentLocaleUndefined',
                            'specified parentLocale is not defined yet');
                }
            }
            locales[name] = new Locale(config);

            // backwards compat for now: also set the locale
            locale_locales__getSetGlobalLocale(name);

            return locales[name];
        } else {
            // useful for testing
            delete locales[name];
            return null;
        }
    }

    function updateLocale(name, config) {
        if (config != null) {
            var locale;
            if (locales[name] != null) {
                config = mergeConfigs(locales[name]._config, config);
            }
            locale = new Locale(config);
            locale.parentLocale = locales[name];
            locales[name] = locale;

            // backwards compat for now: also set the locale
            locale_locales__getSetGlobalLocale(name);
        } else {
            // pass null for config to unupdate, useful for tests
            if (locales[name] != null) {
                if (locales[name].parentLocale != null) {
                    locales[name] = locales[name].parentLocale;
                } else if (locales[name] != null) {
                    delete locales[name];
                }
            }
        }
        return locales[name];
    }

    // returns locale data
    function locale_locales__getLocale (key) {
        var locale;

        if (key && key._locale && key._locale._abbr) {
            key = key._locale._abbr;
        }

        if (!key) {
            return globalLocale;
        }

        if (!isArray(key)) {
            //short-circuit everything else
            locale = loadLocale(key);
            if (locale) {
                return locale;
            }
            key = [key];
        }

        return chooseLocale(key);
    }

    function locale_locales__listLocales() {
        return keys(locales);
    }

    var aliases = {};

    function addUnitAlias (unit, shorthand) {
        var lowerCase = unit.toLowerCase();
        aliases[lowerCase] = aliases[lowerCase + 's'] = aliases[shorthand] = unit;
    }

    function normalizeUnits(units) {
        return typeof units === 'string' ? aliases[units] || aliases[units.toLowerCase()] : undefined;
    }

    function normalizeObjectUnits(inputObject) {
        var normalizedInput = {},
            normalizedProp,
            prop;

        for (prop in inputObject) {
            if (hasOwnProp(inputObject, prop)) {
                normalizedProp = normalizeUnits(prop);
                if (normalizedProp) {
                    normalizedInput[normalizedProp] = inputObject[prop];
                }
            }
        }

        return normalizedInput;
    }

    function makeGetSet (unit, keepTime) {
        return function (value) {
            if (value != null) {
                get_set__set(this, unit, value);
                utils_hooks__hooks.updateOffset(this, keepTime);
                return this;
            } else {
                return get_set__get(this, unit);
            }
        };
    }

    function get_set__get (mom, unit) {
        return mom.isValid() ?
            mom._d['get' + (mom._isUTC ? 'UTC' : '') + unit]() : NaN;
    }

    function get_set__set (mom, unit, value) {
        if (mom.isValid()) {
            mom._d['set' + (mom._isUTC ? 'UTC' : '') + unit](value);
        }
    }

    // MOMENTS

    function getSet (units, value) {
        var unit;
        if (typeof units === 'object') {
            for (unit in units) {
                this.set(unit, units[unit]);
            }
        } else {
            units = normalizeUnits(units);
            if (isFunction(this[units])) {
                return this[units](value);
            }
        }
        return this;
    }

    function zeroFill(number, targetLength, forceSign) {
        var absNumber = '' + Math.abs(number),
            zerosToFill = targetLength - absNumber.length,
            sign = number >= 0;
        return (sign ? (forceSign ? '+' : '') : '-') +
            Math.pow(10, Math.max(0, zerosToFill)).toString().substr(1) + absNumber;
    }

    var formattingTokens = /(\[[^\[]*\])|(\\)?([Hh]mm(ss)?|Mo|MM?M?M?|Do|DDDo|DD?D?D?|ddd?d?|do?|w[o|w]?|W[o|W]?|Qo?|YYYYYY|YYYYY|YYYY|YY|gg(ggg?)?|GG(GGG?)?|e|E|a|A|hh?|HH?|kk?|mm?|ss?|S{1,9}|x|X|zz?|ZZ?|.)/g;

    var localFormattingTokens = /(\[[^\[]*\])|(\\)?(LTS|LT|LL?L?L?|l{1,4})/g;

    var formatFunctions = {};

    var formatTokenFunctions = {};

    // token:    'M'
    // padded:   ['MM', 2]
    // ordinal:  'Mo'
    // callback: function () { this.month() + 1 }
    function addFormatToken (token, padded, ordinal, callback) {
        var func = callback;
        if (typeof callback === 'string') {
            func = function () {
                return this[callback]();
            };
        }
        if (token) {
            formatTokenFunctions[token] = func;
        }
        if (padded) {
            formatTokenFunctions[padded[0]] = function () {
                return zeroFill(func.apply(this, arguments), padded[1], padded[2]);
            };
        }
        if (ordinal) {
            formatTokenFunctions[ordinal] = function () {
                return this.localeData().ordinal(func.apply(this, arguments), token);
            };
        }
    }

    function removeFormattingTokens(input) {
        if (input.match(/\[[\s\S]/)) {
            return input.replace(/^\[|\]$/g, '');
        }
        return input.replace(/\\/g, '');
    }

    function makeFormatFunction(format) {
        var array = format.match(formattingTokens), i, length;

        for (i = 0, length = array.length; i < length; i++) {
            if (formatTokenFunctions[array[i]]) {
                array[i] = formatTokenFunctions[array[i]];
            } else {
                array[i] = removeFormattingTokens(array[i]);
            }
        }

        return function (mom) {
            var output = '', i;
            for (i = 0; i < length; i++) {
                output += array[i] instanceof Function ? array[i].call(mom, format) : array[i];
            }
            return output;
        };
    }

    // format date using native date object
    function formatMoment(m, format) {
        if (!m.isValid()) {
            return m.localeData().invalidDate();
        }

        format = expandFormat(format, m.localeData());
        formatFunctions[format] = formatFunctions[format] || makeFormatFunction(format);

        return formatFunctions[format](m);
    }

    function expandFormat(format, locale) {
        var i = 5;

        function replaceLongDateFormatTokens(input) {
            return locale.longDateFormat(input) || input;
        }

        localFormattingTokens.lastIndex = 0;
        while (i >= 0 && localFormattingTokens.test(format)) {
            format = format.replace(localFormattingTokens, replaceLongDateFormatTokens);
            localFormattingTokens.lastIndex = 0;
            i -= 1;
        }

        return format;
    }

    var match1         = /\d/;            //       0 - 9
    var match2         = /\d\d/;          //      00 - 99
    var match3         = /\d{3}/;         //     000 - 999
    var match4         = /\d{4}/;         //    0000 - 9999
    var match6         = /[+-]?\d{6}/;    // -999999 - 999999
    var match1to2      = /\d\d?/;         //       0 - 99
    var match3to4      = /\d\d\d\d?/;     //     999 - 9999
    var match5to6      = /\d\d\d\d\d\d?/; //   99999 - 999999
    var match1to3      = /\d{1,3}/;       //       0 - 999
    var match1to4      = /\d{1,4}/;       //       0 - 9999
    var match1to6      = /[+-]?\d{1,6}/;  // -999999 - 999999

    var matchUnsigned  = /\d+/;           //       0 - inf
    var matchSigned    = /[+-]?\d+/;      //    -inf - inf

    var matchOffset    = /Z|[+-]\d\d:?\d\d/gi; // +00:00 -00:00 +0000 -0000 or Z
    var matchShortOffset = /Z|[+-]\d\d(?::?\d\d)?/gi; // +00 -00 +00:00 -00:00 +0000 -0000 or Z

    var matchTimestamp = /[+-]?\d+(\.\d{1,3})?/; // 123456789 123456789.123

    // any word (or two) characters or numbers including two/three word month in arabic.
    // includes scottish gaelic two word and hyphenated months
    var matchWord = /[0-9]*['a-z\u00A0-\u05FF\u0700-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+|[\u0600-\u06FF\/]+(\s*?[\u0600-\u06FF]+){1,2}/i;


    var regexes = {};

    function addRegexToken (token, regex, strictRegex) {
        regexes[token] = isFunction(regex) ? regex : function (isStrict, localeData) {
            return (isStrict && strictRegex) ? strictRegex : regex;
        };
    }

    function getParseRegexForToken (token, config) {
        if (!hasOwnProp(regexes, token)) {
            return new RegExp(unescapeFormat(token));
        }

        return regexes[token](config._strict, config._locale);
    }

    // Code from http://stackoverflow.com/questions/3561493/is-there-a-regexp-escape-function-in-javascript
    function unescapeFormat(s) {
        return regexEscape(s.replace('\\', '').replace(/\\(\[)|\\(\])|\[([^\]\[]*)\]|\\(.)/g, function (matched, p1, p2, p3, p4) {
            return p1 || p2 || p3 || p4;
        }));
    }

    function regexEscape(s) {
        return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    }

    var tokens = {};

    function addParseToken (token, callback) {
        var i, func = callback;
        if (typeof token === 'string') {
            token = [token];
        }
        if (typeof callback === 'number') {
            func = function (input, array) {
                array[callback] = toInt(input);
            };
        }
        for (i = 0; i < token.length; i++) {
            tokens[token[i]] = func;
        }
    }

    function addWeekParseToken (token, callback) {
        addParseToken(token, function (input, array, config, token) {
            config._w = config._w || {};
            callback(input, config._w, config, token);
        });
    }

    function addTimeToArrayFromToken(token, input, config) {
        if (input != null && hasOwnProp(tokens, token)) {
            tokens[token](input, config._a, config, token);
        }
    }

    var YEAR = 0;
    var MONTH = 1;
    var DATE = 2;
    var HOUR = 3;
    var MINUTE = 4;
    var SECOND = 5;
    var MILLISECOND = 6;
    var WEEK = 7;
    var WEEKDAY = 8;

    var indexOf;

    if (Array.prototype.indexOf) {
        indexOf = Array.prototype.indexOf;
    } else {
        indexOf = function (o) {
            // I know
            var i;
            for (i = 0; i < this.length; ++i) {
                if (this[i] === o) {
                    return i;
                }
            }
            return -1;
        };
    }

    function daysInMonth(year, month) {
        return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    }

    // FORMATTING

    addFormatToken('M', ['MM', 2], 'Mo', function () {
        return this.month() + 1;
    });

    addFormatToken('MMM', 0, 0, function (format) {
        return this.localeData().monthsShort(this, format);
    });

    addFormatToken('MMMM', 0, 0, function (format) {
        return this.localeData().months(this, format);
    });

    // ALIASES

    addUnitAlias('month', 'M');

    // PARSING

    addRegexToken('M',    match1to2);
    addRegexToken('MM',   match1to2, match2);
    addRegexToken('MMM',  function (isStrict, locale) {
        return locale.monthsShortRegex(isStrict);
    });
    addRegexToken('MMMM', function (isStrict, locale) {
        return locale.monthsRegex(isStrict);
    });

    addParseToken(['M', 'MM'], function (input, array) {
        array[MONTH] = toInt(input) - 1;
    });

    addParseToken(['MMM', 'MMMM'], function (input, array, config, token) {
        var month = config._locale.monthsParse(input, token, config._strict);
        // if we didn't find a month name, mark the date as invalid.
        if (month != null) {
            array[MONTH] = month;
        } else {
            getParsingFlags(config).invalidMonth = input;
        }
    });

    // LOCALES

    var MONTHS_IN_FORMAT = /D[oD]?(\[[^\[\]]*\]|\s+)+MMMM?/;
    var defaultLocaleMonths = 'January_February_March_April_May_June_July_August_September_October_November_December'.split('_');
    function localeMonths (m, format) {
        return isArray(this._months) ? this._months[m.month()] :
            this._months[MONTHS_IN_FORMAT.test(format) ? 'format' : 'standalone'][m.month()];
    }

    var defaultLocaleMonthsShort = 'Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec'.split('_');
    function localeMonthsShort (m, format) {
        return isArray(this._monthsShort) ? this._monthsShort[m.month()] :
            this._monthsShort[MONTHS_IN_FORMAT.test(format) ? 'format' : 'standalone'][m.month()];
    }

    function units_month__handleStrictParse(monthName, format, strict) {
        var i, ii, mom, llc = monthName.toLocaleLowerCase();
        if (!this._monthsParse) {
            // this is not used
            this._monthsParse = [];
            this._longMonthsParse = [];
            this._shortMonthsParse = [];
            for (i = 0; i < 12; ++i) {
                mom = create_utc__createUTC([2000, i]);
                this._shortMonthsParse[i] = this.monthsShort(mom, '').toLocaleLowerCase();
                this._longMonthsParse[i] = this.months(mom, '').toLocaleLowerCase();
            }
        }

        if (strict) {
            if (format === 'MMM') {
                ii = indexOf.call(this._shortMonthsParse, llc);
                return ii !== -1 ? ii : null;
            } else {
                ii = indexOf.call(this._longMonthsParse, llc);
                return ii !== -1 ? ii : null;
            }
        } else {
            if (format === 'MMM') {
                ii = indexOf.call(this._shortMonthsParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._longMonthsParse, llc);
                return ii !== -1 ? ii : null;
            } else {
                ii = indexOf.call(this._longMonthsParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._shortMonthsParse, llc);
                return ii !== -1 ? ii : null;
            }
        }
    }

    function localeMonthsParse (monthName, format, strict) {
        var i, mom, regex;

        if (this._monthsParseExact) {
            return units_month__handleStrictParse.call(this, monthName, format, strict);
        }

        if (!this._monthsParse) {
            this._monthsParse = [];
            this._longMonthsParse = [];
            this._shortMonthsParse = [];
        }

        // TODO: add sorting
        // Sorting makes sure if one month (or abbr) is a prefix of another
        // see sorting in computeMonthsParse
        for (i = 0; i < 12; i++) {
            // make the regex if we don't have it already
            mom = create_utc__createUTC([2000, i]);
            if (strict && !this._longMonthsParse[i]) {
                this._longMonthsParse[i] = new RegExp('^' + this.months(mom, '').replace('.', '') + '$', 'i');
                this._shortMonthsParse[i] = new RegExp('^' + this.monthsShort(mom, '').replace('.', '') + '$', 'i');
            }
            if (!strict && !this._monthsParse[i]) {
                regex = '^' + this.months(mom, '') + '|^' + this.monthsShort(mom, '');
                this._monthsParse[i] = new RegExp(regex.replace('.', ''), 'i');
            }
            // test the regex
            if (strict && format === 'MMMM' && this._longMonthsParse[i].test(monthName)) {
                return i;
            } else if (strict && format === 'MMM' && this._shortMonthsParse[i].test(monthName)) {
                return i;
            } else if (!strict && this._monthsParse[i].test(monthName)) {
                return i;
            }
        }
    }

    // MOMENTS

    function setMonth (mom, value) {
        var dayOfMonth;

        if (!mom.isValid()) {
            // No op
            return mom;
        }

        if (typeof value === 'string') {
            if (/^\d+$/.test(value)) {
                value = toInt(value);
            } else {
                value = mom.localeData().monthsParse(value);
                // TODO: Another silent failure?
                if (typeof value !== 'number') {
                    return mom;
                }
            }
        }

        dayOfMonth = Math.min(mom.date(), daysInMonth(mom.year(), value));
        mom._d['set' + (mom._isUTC ? 'UTC' : '') + 'Month'](value, dayOfMonth);
        return mom;
    }

    function getSetMonth (value) {
        if (value != null) {
            setMonth(this, value);
            utils_hooks__hooks.updateOffset(this, true);
            return this;
        } else {
            return get_set__get(this, 'Month');
        }
    }

    function getDaysInMonth () {
        return daysInMonth(this.year(), this.month());
    }

    var defaultMonthsShortRegex = matchWord;
    function monthsShortRegex (isStrict) {
        if (this._monthsParseExact) {
            if (!hasOwnProp(this, '_monthsRegex')) {
                computeMonthsParse.call(this);
            }
            if (isStrict) {
                return this._monthsShortStrictRegex;
            } else {
                return this._monthsShortRegex;
            }
        } else {
            return this._monthsShortStrictRegex && isStrict ?
                this._monthsShortStrictRegex : this._monthsShortRegex;
        }
    }

    var defaultMonthsRegex = matchWord;
    function monthsRegex (isStrict) {
        if (this._monthsParseExact) {
            if (!hasOwnProp(this, '_monthsRegex')) {
                computeMonthsParse.call(this);
            }
            if (isStrict) {
                return this._monthsStrictRegex;
            } else {
                return this._monthsRegex;
            }
        } else {
            return this._monthsStrictRegex && isStrict ?
                this._monthsStrictRegex : this._monthsRegex;
        }
    }

    function computeMonthsParse () {
        function cmpLenRev(a, b) {
            return b.length - a.length;
        }

        var shortPieces = [], longPieces = [], mixedPieces = [],
            i, mom;
        for (i = 0; i < 12; i++) {
            // make the regex if we don't have it already
            mom = create_utc__createUTC([2000, i]);
            shortPieces.push(this.monthsShort(mom, ''));
            longPieces.push(this.months(mom, ''));
            mixedPieces.push(this.months(mom, ''));
            mixedPieces.push(this.monthsShort(mom, ''));
        }
        // Sorting makes sure if one month (or abbr) is a prefix of another it
        // will match the longer piece.
        shortPieces.sort(cmpLenRev);
        longPieces.sort(cmpLenRev);
        mixedPieces.sort(cmpLenRev);
        for (i = 0; i < 12; i++) {
            shortPieces[i] = regexEscape(shortPieces[i]);
            longPieces[i] = regexEscape(longPieces[i]);
            mixedPieces[i] = regexEscape(mixedPieces[i]);
        }

        this._monthsRegex = new RegExp('^(' + mixedPieces.join('|') + ')', 'i');
        this._monthsShortRegex = this._monthsRegex;
        this._monthsStrictRegex = new RegExp('^(' + longPieces.join('|') + ')', 'i');
        this._monthsShortStrictRegex = new RegExp('^(' + shortPieces.join('|') + ')', 'i');
    }

    function checkOverflow (m) {
        var overflow;
        var a = m._a;

        if (a && getParsingFlags(m).overflow === -2) {
            overflow =
                a[MONTH]       < 0 || a[MONTH]       > 11  ? MONTH :
                a[DATE]        < 1 || a[DATE]        > daysInMonth(a[YEAR], a[MONTH]) ? DATE :
                a[HOUR]        < 0 || a[HOUR]        > 24 || (a[HOUR] === 24 && (a[MINUTE] !== 0 || a[SECOND] !== 0 || a[MILLISECOND] !== 0)) ? HOUR :
                a[MINUTE]      < 0 || a[MINUTE]      > 59  ? MINUTE :
                a[SECOND]      < 0 || a[SECOND]      > 59  ? SECOND :
                a[MILLISECOND] < 0 || a[MILLISECOND] > 999 ? MILLISECOND :
                -1;

            if (getParsingFlags(m)._overflowDayOfYear && (overflow < YEAR || overflow > DATE)) {
                overflow = DATE;
            }
            if (getParsingFlags(m)._overflowWeeks && overflow === -1) {
                overflow = WEEK;
            }
            if (getParsingFlags(m)._overflowWeekday && overflow === -1) {
                overflow = WEEKDAY;
            }

            getParsingFlags(m).overflow = overflow;
        }

        return m;
    }

    // iso 8601 regex
    // 0000-00-00 0000-W00 or 0000-W00-0 + T + 00 or 00:00 or 00:00:00 or 00:00:00.000 + +00:00 or +0000 or +00)
    var extendedIsoRegex = /^\s*((?:[+-]\d{6}|\d{4})-(?:\d\d-\d\d|W\d\d-\d|W\d\d|\d\d\d|\d\d))(?:(T| )(\d\d(?::\d\d(?::\d\d(?:[.,]\d+)?)?)?)([\+\-]\d\d(?::?\d\d)?|\s*Z)?)?/;
    var basicIsoRegex = /^\s*((?:[+-]\d{6}|\d{4})(?:\d\d\d\d|W\d\d\d|W\d\d|\d\d\d|\d\d))(?:(T| )(\d\d(?:\d\d(?:\d\d(?:[.,]\d+)?)?)?)([\+\-]\d\d(?::?\d\d)?|\s*Z)?)?/;

    var tzRegex = /Z|[+-]\d\d(?::?\d\d)?/;

    var isoDates = [
        ['YYYYYY-MM-DD', /[+-]\d{6}-\d\d-\d\d/],
        ['YYYY-MM-DD', /\d{4}-\d\d-\d\d/],
        ['GGGG-[W]WW-E', /\d{4}-W\d\d-\d/],
        ['GGGG-[W]WW', /\d{4}-W\d\d/, false],
        ['YYYY-DDD', /\d{4}-\d{3}/],
        ['YYYY-MM', /\d{4}-\d\d/, false],
        ['YYYYYYMMDD', /[+-]\d{10}/],
        ['YYYYMMDD', /\d{8}/],
        // YYYYMM is NOT allowed by the standard
        ['GGGG[W]WWE', /\d{4}W\d{3}/],
        ['GGGG[W]WW', /\d{4}W\d{2}/, false],
        ['YYYYDDD', /\d{7}/]
    ];

    // iso time formats and regexes
    var isoTimes = [
        ['HH:mm:ss.SSSS', /\d\d:\d\d:\d\d\.\d+/],
        ['HH:mm:ss,SSSS', /\d\d:\d\d:\d\d,\d+/],
        ['HH:mm:ss', /\d\d:\d\d:\d\d/],
        ['HH:mm', /\d\d:\d\d/],
        ['HHmmss.SSSS', /\d\d\d\d\d\d\.\d+/],
        ['HHmmss,SSSS', /\d\d\d\d\d\d,\d+/],
        ['HHmmss', /\d\d\d\d\d\d/],
        ['HHmm', /\d\d\d\d/],
        ['HH', /\d\d/]
    ];

    var aspNetJsonRegex = /^\/?Date\((\-?\d+)/i;

    // date from iso format
    function configFromISO(config) {
        var i, l,
            string = config._i,
            match = extendedIsoRegex.exec(string) || basicIsoRegex.exec(string),
            allowTime, dateFormat, timeFormat, tzFormat;

        if (match) {
            getParsingFlags(config).iso = true;

            for (i = 0, l = isoDates.length; i < l; i++) {
                if (isoDates[i][1].exec(match[1])) {
                    dateFormat = isoDates[i][0];
                    allowTime = isoDates[i][2] !== false;
                    break;
                }
            }
            if (dateFormat == null) {
                config._isValid = false;
                return;
            }
            if (match[3]) {
                for (i = 0, l = isoTimes.length; i < l; i++) {
                    if (isoTimes[i][1].exec(match[3])) {
                        // match[2] should be 'T' or space
                        timeFormat = (match[2] || ' ') + isoTimes[i][0];
                        break;
                    }
                }
                if (timeFormat == null) {
                    config._isValid = false;
                    return;
                }
            }
            if (!allowTime && timeFormat != null) {
                config._isValid = false;
                return;
            }
            if (match[4]) {
                if (tzRegex.exec(match[4])) {
                    tzFormat = 'Z';
                } else {
                    config._isValid = false;
                    return;
                }
            }
            config._f = dateFormat + (timeFormat || '') + (tzFormat || '');
            configFromStringAndFormat(config);
        } else {
            config._isValid = false;
        }
    }

    // date from iso format or fallback
    function configFromString(config) {
        var matched = aspNetJsonRegex.exec(config._i);

        if (matched !== null) {
            config._d = new Date(+matched[1]);
            return;
        }

        configFromISO(config);
        if (config._isValid === false) {
            delete config._isValid;
            utils_hooks__hooks.createFromInputFallback(config);
        }
    }

    utils_hooks__hooks.createFromInputFallback = deprecate(
        'moment construction falls back to js Date. This is ' +
        'discouraged and will be removed in upcoming major ' +
        'release. Please refer to ' +
        'https://github.com/moment/moment/issues/1407 for more info.',
        function (config) {
            config._d = new Date(config._i + (config._useUTC ? ' UTC' : ''));
        }
    );

    function createDate (y, m, d, h, M, s, ms) {
        //can't just apply() to create a date:
        //http://stackoverflow.com/questions/181348/instantiating-a-javascript-object-by-calling-prototype-constructor-apply
        var date = new Date(y, m, d, h, M, s, ms);

        //the date constructor remaps years 0-99 to 1900-1999
        if (y < 100 && y >= 0 && isFinite(date.getFullYear())) {
            date.setFullYear(y);
        }
        return date;
    }

    function createUTCDate (y) {
        var date = new Date(Date.UTC.apply(null, arguments));

        //the Date.UTC function remaps years 0-99 to 1900-1999
        if (y < 100 && y >= 0 && isFinite(date.getUTCFullYear())) {
            date.setUTCFullYear(y);
        }
        return date;
    }

    // FORMATTING

    addFormatToken('Y', 0, 0, function () {
        var y = this.year();
        return y <= 9999 ? '' + y : '+' + y;
    });

    addFormatToken(0, ['YY', 2], 0, function () {
        return this.year() % 100;
    });

    addFormatToken(0, ['YYYY',   4],       0, 'year');
    addFormatToken(0, ['YYYYY',  5],       0, 'year');
    addFormatToken(0, ['YYYYYY', 6, true], 0, 'year');

    // ALIASES

    addUnitAlias('year', 'y');

    // PARSING

    addRegexToken('Y',      matchSigned);
    addRegexToken('YY',     match1to2, match2);
    addRegexToken('YYYY',   match1to4, match4);
    addRegexToken('YYYYY',  match1to6, match6);
    addRegexToken('YYYYYY', match1to6, match6);

    addParseToken(['YYYYY', 'YYYYYY'], YEAR);
    addParseToken('YYYY', function (input, array) {
        array[YEAR] = input.length === 2 ? utils_hooks__hooks.parseTwoDigitYear(input) : toInt(input);
    });
    addParseToken('YY', function (input, array) {
        array[YEAR] = utils_hooks__hooks.parseTwoDigitYear(input);
    });
    addParseToken('Y', function (input, array) {
        array[YEAR] = parseInt(input, 10);
    });

    // HELPERS

    function daysInYear(year) {
        return isLeapYear(year) ? 366 : 365;
    }

    function isLeapYear(year) {
        return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    }

    // HOOKS

    utils_hooks__hooks.parseTwoDigitYear = function (input) {
        return toInt(input) + (toInt(input) > 68 ? 1900 : 2000);
    };

    // MOMENTS

    var getSetYear = makeGetSet('FullYear', true);

    function getIsLeapYear () {
        return isLeapYear(this.year());
    }

    // start-of-first-week - start-of-year
    function firstWeekOffset(year, dow, doy) {
        var // first-week day -- which january is always in the first week (4 for iso, 1 for other)
            fwd = 7 + dow - doy,
            // first-week day local weekday -- which local weekday is fwd
            fwdlw = (7 + createUTCDate(year, 0, fwd).getUTCDay() - dow) % 7;

        return -fwdlw + fwd - 1;
    }

    //http://en.wikipedia.org/wiki/ISO_week_date#Calculating_a_date_given_the_year.2C_week_number_and_weekday
    function dayOfYearFromWeeks(year, week, weekday, dow, doy) {
        var localWeekday = (7 + weekday - dow) % 7,
            weekOffset = firstWeekOffset(year, dow, doy),
            dayOfYear = 1 + 7 * (week - 1) + localWeekday + weekOffset,
            resYear, resDayOfYear;

        if (dayOfYear <= 0) {
            resYear = year - 1;
            resDayOfYear = daysInYear(resYear) + dayOfYear;
        } else if (dayOfYear > daysInYear(year)) {
            resYear = year + 1;
            resDayOfYear = dayOfYear - daysInYear(year);
        } else {
            resYear = year;
            resDayOfYear = dayOfYear;
        }

        return {
            year: resYear,
            dayOfYear: resDayOfYear
        };
    }

    function weekOfYear(mom, dow, doy) {
        var weekOffset = firstWeekOffset(mom.year(), dow, doy),
            week = Math.floor((mom.dayOfYear() - weekOffset - 1) / 7) + 1,
            resWeek, resYear;

        if (week < 1) {
            resYear = mom.year() - 1;
            resWeek = week + weeksInYear(resYear, dow, doy);
        } else if (week > weeksInYear(mom.year(), dow, doy)) {
            resWeek = week - weeksInYear(mom.year(), dow, doy);
            resYear = mom.year() + 1;
        } else {
            resYear = mom.year();
            resWeek = week;
        }

        return {
            week: resWeek,
            year: resYear
        };
    }

    function weeksInYear(year, dow, doy) {
        var weekOffset = firstWeekOffset(year, dow, doy),
            weekOffsetNext = firstWeekOffset(year + 1, dow, doy);
        return (daysInYear(year) - weekOffset + weekOffsetNext) / 7;
    }

    // Pick the first defined of two or three arguments.
    function defaults(a, b, c) {
        if (a != null) {
            return a;
        }
        if (b != null) {
            return b;
        }
        return c;
    }

    function currentDateArray(config) {
        // hooks is actually the exported moment object
        var nowValue = new Date(utils_hooks__hooks.now());
        if (config._useUTC) {
            return [nowValue.getUTCFullYear(), nowValue.getUTCMonth(), nowValue.getUTCDate()];
        }
        return [nowValue.getFullYear(), nowValue.getMonth(), nowValue.getDate()];
    }

    // convert an array to a date.
    // the array should mirror the parameters below
    // note: all values past the year are optional and will default to the lowest possible value.
    // [year, month, day , hour, minute, second, millisecond]
    function configFromArray (config) {
        var i, date, input = [], currentDate, yearToUse;

        if (config._d) {
            return;
        }

        currentDate = currentDateArray(config);

        //compute day of the year from weeks and weekdays
        if (config._w && config._a[DATE] == null && config._a[MONTH] == null) {
            dayOfYearFromWeekInfo(config);
        }

        //if the day of the year is set, figure out what it is
        if (config._dayOfYear) {
            yearToUse = defaults(config._a[YEAR], currentDate[YEAR]);

            if (config._dayOfYear > daysInYear(yearToUse)) {
                getParsingFlags(config)._overflowDayOfYear = true;
            }

            date = createUTCDate(yearToUse, 0, config._dayOfYear);
            config._a[MONTH] = date.getUTCMonth();
            config._a[DATE] = date.getUTCDate();
        }

        // Default to current date.
        // * if no year, month, day of month are given, default to today
        // * if day of month is given, default month and year
        // * if month is given, default only year
        // * if year is given, don't default anything
        for (i = 0; i < 3 && config._a[i] == null; ++i) {
            config._a[i] = input[i] = currentDate[i];
        }

        // Zero out whatever was not defaulted, including time
        for (; i < 7; i++) {
            config._a[i] = input[i] = (config._a[i] == null) ? (i === 2 ? 1 : 0) : config._a[i];
        }

        // Check for 24:00:00.000
        if (config._a[HOUR] === 24 &&
                config._a[MINUTE] === 0 &&
                config._a[SECOND] === 0 &&
                config._a[MILLISECOND] === 0) {
            config._nextDay = true;
            config._a[HOUR] = 0;
        }

        config._d = (config._useUTC ? createUTCDate : createDate).apply(null, input);
        // Apply timezone offset from input. The actual utcOffset can be changed
        // with parseZone.
        if (config._tzm != null) {
            config._d.setUTCMinutes(config._d.getUTCMinutes() - config._tzm);
        }

        if (config._nextDay) {
            config._a[HOUR] = 24;
        }
    }

    function dayOfYearFromWeekInfo(config) {
        var w, weekYear, week, weekday, dow, doy, temp, weekdayOverflow;

        w = config._w;
        if (w.GG != null || w.W != null || w.E != null) {
            dow = 1;
            doy = 4;

            // TODO: We need to take the current isoWeekYear, but that depends on
            // how we interpret now (local, utc, fixed offset). So create
            // a now version of current config (take local/utc/offset flags, and
            // create now).
            weekYear = defaults(w.GG, config._a[YEAR], weekOfYear(local__createLocal(), 1, 4).year);
            week = defaults(w.W, 1);
            weekday = defaults(w.E, 1);
            if (weekday < 1 || weekday > 7) {
                weekdayOverflow = true;
            }
        } else {
            dow = config._locale._week.dow;
            doy = config._locale._week.doy;

            weekYear = defaults(w.gg, config._a[YEAR], weekOfYear(local__createLocal(), dow, doy).year);
            week = defaults(w.w, 1);

            if (w.d != null) {
                // weekday -- low day numbers are considered next week
                weekday = w.d;
                if (weekday < 0 || weekday > 6) {
                    weekdayOverflow = true;
                }
            } else if (w.e != null) {
                // local weekday -- counting starts from begining of week
                weekday = w.e + dow;
                if (w.e < 0 || w.e > 6) {
                    weekdayOverflow = true;
                }
            } else {
                // default to begining of week
                weekday = dow;
            }
        }
        if (week < 1 || week > weeksInYear(weekYear, dow, doy)) {
            getParsingFlags(config)._overflowWeeks = true;
        } else if (weekdayOverflow != null) {
            getParsingFlags(config)._overflowWeekday = true;
        } else {
            temp = dayOfYearFromWeeks(weekYear, week, weekday, dow, doy);
            config._a[YEAR] = temp.year;
            config._dayOfYear = temp.dayOfYear;
        }
    }

    // constant that refers to the ISO standard
    utils_hooks__hooks.ISO_8601 = function () {};

    // date from string and format string
    function configFromStringAndFormat(config) {
        // TODO: Move this to another part of the creation flow to prevent circular deps
        if (config._f === utils_hooks__hooks.ISO_8601) {
            configFromISO(config);
            return;
        }

        config._a = [];
        getParsingFlags(config).empty = true;

        // This array is used to make a Date, either with `new Date` or `Date.UTC`
        var string = '' + config._i,
            i, parsedInput, tokens, token, skipped,
            stringLength = string.length,
            totalParsedInputLength = 0;

        tokens = expandFormat(config._f, config._locale).match(formattingTokens) || [];

        for (i = 0; i < tokens.length; i++) {
            token = tokens[i];
            parsedInput = (string.match(getParseRegexForToken(token, config)) || [])[0];
            // console.log('token', token, 'parsedInput', parsedInput,
            //         'regex', getParseRegexForToken(token, config));
            if (parsedInput) {
                skipped = string.substr(0, string.indexOf(parsedInput));
                if (skipped.length > 0) {
                    getParsingFlags(config).unusedInput.push(skipped);
                }
                string = string.slice(string.indexOf(parsedInput) + parsedInput.length);
                totalParsedInputLength += parsedInput.length;
            }
            // don't parse if it's not a known token
            if (formatTokenFunctions[token]) {
                if (parsedInput) {
                    getParsingFlags(config).empty = false;
                }
                else {
                    getParsingFlags(config).unusedTokens.push(token);
                }
                addTimeToArrayFromToken(token, parsedInput, config);
            }
            else if (config._strict && !parsedInput) {
                getParsingFlags(config).unusedTokens.push(token);
            }
        }

        // add remaining unparsed input length to the string
        getParsingFlags(config).charsLeftOver = stringLength - totalParsedInputLength;
        if (string.length > 0) {
            getParsingFlags(config).unusedInput.push(string);
        }

        // clear _12h flag if hour is <= 12
        if (getParsingFlags(config).bigHour === true &&
                config._a[HOUR] <= 12 &&
                config._a[HOUR] > 0) {
            getParsingFlags(config).bigHour = undefined;
        }

        getParsingFlags(config).parsedDateParts = config._a.slice(0);
        getParsingFlags(config).meridiem = config._meridiem;
        // handle meridiem
        config._a[HOUR] = meridiemFixWrap(config._locale, config._a[HOUR], config._meridiem);

        configFromArray(config);
        checkOverflow(config);
    }


    function meridiemFixWrap (locale, hour, meridiem) {
        var isPm;

        if (meridiem == null) {
            // nothing to do
            return hour;
        }
        if (locale.meridiemHour != null) {
            return locale.meridiemHour(hour, meridiem);
        } else if (locale.isPM != null) {
            // Fallback
            isPm = locale.isPM(meridiem);
            if (isPm && hour < 12) {
                hour += 12;
            }
            if (!isPm && hour === 12) {
                hour = 0;
            }
            return hour;
        } else {
            // this is not supposed to happen
            return hour;
        }
    }

    // date from string and array of format strings
    function configFromStringAndArray(config) {
        var tempConfig,
            bestMoment,

            scoreToBeat,
            i,
            currentScore;

        if (config._f.length === 0) {
            getParsingFlags(config).invalidFormat = true;
            config._d = new Date(NaN);
            return;
        }

        for (i = 0; i < config._f.length; i++) {
            currentScore = 0;
            tempConfig = copyConfig({}, config);
            if (config._useUTC != null) {
                tempConfig._useUTC = config._useUTC;
            }
            tempConfig._f = config._f[i];
            configFromStringAndFormat(tempConfig);

            if (!valid__isValid(tempConfig)) {
                continue;
            }

            // if there is any input that was not parsed add a penalty for that format
            currentScore += getParsingFlags(tempConfig).charsLeftOver;

            //or tokens
            currentScore += getParsingFlags(tempConfig).unusedTokens.length * 10;

            getParsingFlags(tempConfig).score = currentScore;

            if (scoreToBeat == null || currentScore < scoreToBeat) {
                scoreToBeat = currentScore;
                bestMoment = tempConfig;
            }
        }

        extend(config, bestMoment || tempConfig);
    }

    function configFromObject(config) {
        if (config._d) {
            return;
        }

        var i = normalizeObjectUnits(config._i);
        config._a = map([i.year, i.month, i.day || i.date, i.hour, i.minute, i.second, i.millisecond], function (obj) {
            return obj && parseInt(obj, 10);
        });

        configFromArray(config);
    }

    function createFromConfig (config) {
        var res = new Moment(checkOverflow(prepareConfig(config)));
        if (res._nextDay) {
            // Adding is smart enough around DST
            res.add(1, 'd');
            res._nextDay = undefined;
        }

        return res;
    }

    function prepareConfig (config) {
        var input = config._i,
            format = config._f;

        config._locale = config._locale || locale_locales__getLocale(config._l);

        if (input === null || (format === undefined && input === '')) {
            return valid__createInvalid({nullInput: true});
        }

        if (typeof input === 'string') {
            config._i = input = config._locale.preparse(input);
        }

        if (isMoment(input)) {
            return new Moment(checkOverflow(input));
        } else if (isArray(format)) {
            configFromStringAndArray(config);
        } else if (format) {
            configFromStringAndFormat(config);
        } else if (isDate(input)) {
            config._d = input;
        } else {
            configFromInput(config);
        }

        if (!valid__isValid(config)) {
            config._d = null;
        }

        return config;
    }

    function configFromInput(config) {
        var input = config._i;
        if (input === undefined) {
            config._d = new Date(utils_hooks__hooks.now());
        } else if (isDate(input)) {
            config._d = new Date(input.valueOf());
        } else if (typeof input === 'string') {
            configFromString(config);
        } else if (isArray(input)) {
            config._a = map(input.slice(0), function (obj) {
                return parseInt(obj, 10);
            });
            configFromArray(config);
        } else if (typeof(input) === 'object') {
            configFromObject(config);
        } else if (typeof(input) === 'number') {
            // from milliseconds
            config._d = new Date(input);
        } else {
            utils_hooks__hooks.createFromInputFallback(config);
        }
    }

    function createLocalOrUTC (input, format, locale, strict, isUTC) {
        var c = {};

        if (typeof(locale) === 'boolean') {
            strict = locale;
            locale = undefined;
        }
        // object construction must be done this way.
        // https://github.com/moment/moment/issues/1423
        c._isAMomentObject = true;
        c._useUTC = c._isUTC = isUTC;
        c._l = locale;
        c._i = input;
        c._f = format;
        c._strict = strict;

        return createFromConfig(c);
    }

    function local__createLocal (input, format, locale, strict) {
        return createLocalOrUTC(input, format, locale, strict, false);
    }

    var prototypeMin = deprecate(
         'moment().min is deprecated, use moment.max instead. https://github.com/moment/moment/issues/1548',
         function () {
             var other = local__createLocal.apply(null, arguments);
             if (this.isValid() && other.isValid()) {
                 return other < this ? this : other;
             } else {
                 return valid__createInvalid();
             }
         }
     );

    var prototypeMax = deprecate(
        'moment().max is deprecated, use moment.min instead. https://github.com/moment/moment/issues/1548',
        function () {
            var other = local__createLocal.apply(null, arguments);
            if (this.isValid() && other.isValid()) {
                return other > this ? this : other;
            } else {
                return valid__createInvalid();
            }
        }
    );

    // Pick a moment m from moments so that m[fn](other) is true for all
    // other. This relies on the function fn to be transitive.
    //
    // moments should either be an array of moment objects or an array, whose
    // first element is an array of moment objects.
    function pickBy(fn, moments) {
        var res, i;
        if (moments.length === 1 && isArray(moments[0])) {
            moments = moments[0];
        }
        if (!moments.length) {
            return local__createLocal();
        }
        res = moments[0];
        for (i = 1; i < moments.length; ++i) {
            if (!moments[i].isValid() || moments[i][fn](res)) {
                res = moments[i];
            }
        }
        return res;
    }

    // TODO: Use [].sort instead?
    function min () {
        var args = [].slice.call(arguments, 0);

        return pickBy('isBefore', args);
    }

    function max () {
        var args = [].slice.call(arguments, 0);

        return pickBy('isAfter', args);
    }

    var now = function () {
        return Date.now ? Date.now() : +(new Date());
    };

    function Duration (duration) {
        var normalizedInput = normalizeObjectUnits(duration),
            years = normalizedInput.year || 0,
            quarters = normalizedInput.quarter || 0,
            months = normalizedInput.month || 0,
            weeks = normalizedInput.week || 0,
            days = normalizedInput.day || 0,
            hours = normalizedInput.hour || 0,
            minutes = normalizedInput.minute || 0,
            seconds = normalizedInput.second || 0,
            milliseconds = normalizedInput.millisecond || 0;

        // representation for dateAddRemove
        this._milliseconds = +milliseconds +
            seconds * 1e3 + // 1000
            minutes * 6e4 + // 1000 * 60
            hours * 1000 * 60 * 60; //using 1000 * 60 * 60 instead of 36e5 to avoid floating point rounding errors https://github.com/moment/moment/issues/2978
        // Because of dateAddRemove treats 24 hours as different from a
        // day when working around DST, we need to store them separately
        this._days = +days +
            weeks * 7;
        // It is impossible translate months into days without knowing
        // which months you are are talking about, so we have to store
        // it separately.
        this._months = +months +
            quarters * 3 +
            years * 12;

        this._data = {};

        this._locale = locale_locales__getLocale();

        this._bubble();
    }

    function isDuration (obj) {
        return obj instanceof Duration;
    }

    // FORMATTING

    function offset (token, separator) {
        addFormatToken(token, 0, 0, function () {
            var offset = this.utcOffset();
            var sign = '+';
            if (offset < 0) {
                offset = -offset;
                sign = '-';
            }
            return sign + zeroFill(~~(offset / 60), 2) + separator + zeroFill(~~(offset) % 60, 2);
        });
    }

    offset('Z', ':');
    offset('ZZ', '');

    // PARSING

    addRegexToken('Z',  matchShortOffset);
    addRegexToken('ZZ', matchShortOffset);
    addParseToken(['Z', 'ZZ'], function (input, array, config) {
        config._useUTC = true;
        config._tzm = offsetFromString(matchShortOffset, input);
    });

    // HELPERS

    // timezone chunker
    // '+10:00' > ['10',  '00']
    // '-1530'  > ['-15', '30']
    var chunkOffset = /([\+\-]|\d\d)/gi;

    function offsetFromString(matcher, string) {
        var matches = ((string || '').match(matcher) || []);
        var chunk   = matches[matches.length - 1] || [];
        var parts   = (chunk + '').match(chunkOffset) || ['-', 0, 0];
        var minutes = +(parts[1] * 60) + toInt(parts[2]);

        return parts[0] === '+' ? minutes : -minutes;
    }

    // Return a moment from input, that is local/utc/zone equivalent to model.
    function cloneWithOffset(input, model) {
        var res, diff;
        if (model._isUTC) {
            res = model.clone();
            diff = (isMoment(input) || isDate(input) ? input.valueOf() : local__createLocal(input).valueOf()) - res.valueOf();
            // Use low-level api, because this fn is low-level api.
            res._d.setTime(res._d.valueOf() + diff);
            utils_hooks__hooks.updateOffset(res, false);
            return res;
        } else {
            return local__createLocal(input).local();
        }
    }

    function getDateOffset (m) {
        // On Firefox.24 Date#getTimezoneOffset returns a floating point.
        // https://github.com/moment/moment/pull/1871
        return -Math.round(m._d.getTimezoneOffset() / 15) * 15;
    }

    // HOOKS

    // This function will be called whenever a moment is mutated.
    // It is intended to keep the offset in sync with the timezone.
    utils_hooks__hooks.updateOffset = function () {};

    // MOMENTS

    // keepLocalTime = true means only change the timezone, without
    // affecting the local hour. So 5:31:26 +0300 --[utcOffset(2, true)]-->
    // 5:31:26 +0200 It is possible that 5:31:26 doesn't exist with offset
    // +0200, so we adjust the time as needed, to be valid.
    //
    // Keeping the time actually adds/subtracts (one hour)
    // from the actual represented time. That is why we call updateOffset
    // a second time. In case it wants us to change the offset again
    // _changeInProgress == true case, then we have to adjust, because
    // there is no such time in the given timezone.
    function getSetOffset (input, keepLocalTime) {
        var offset = this._offset || 0,
            localAdjust;
        if (!this.isValid()) {
            return input != null ? this : NaN;
        }
        if (input != null) {
            if (typeof input === 'string') {
                input = offsetFromString(matchShortOffset, input);
            } else if (Math.abs(input) < 16) {
                input = input * 60;
            }
            if (!this._isUTC && keepLocalTime) {
                localAdjust = getDateOffset(this);
            }
            this._offset = input;
            this._isUTC = true;
            if (localAdjust != null) {
                this.add(localAdjust, 'm');
            }
            if (offset !== input) {
                if (!keepLocalTime || this._changeInProgress) {
                    add_subtract__addSubtract(this, create__createDuration(input - offset, 'm'), 1, false);
                } else if (!this._changeInProgress) {
                    this._changeInProgress = true;
                    utils_hooks__hooks.updateOffset(this, true);
                    this._changeInProgress = null;
                }
            }
            return this;
        } else {
            return this._isUTC ? offset : getDateOffset(this);
        }
    }

    function getSetZone (input, keepLocalTime) {
        if (input != null) {
            if (typeof input !== 'string') {
                input = -input;
            }

            this.utcOffset(input, keepLocalTime);

            return this;
        } else {
            return -this.utcOffset();
        }
    }

    function setOffsetToUTC (keepLocalTime) {
        return this.utcOffset(0, keepLocalTime);
    }

    function setOffsetToLocal (keepLocalTime) {
        if (this._isUTC) {
            this.utcOffset(0, keepLocalTime);
            this._isUTC = false;

            if (keepLocalTime) {
                this.subtract(getDateOffset(this), 'm');
            }
        }
        return this;
    }

    function setOffsetToParsedOffset () {
        if (this._tzm) {
            this.utcOffset(this._tzm);
        } else if (typeof this._i === 'string') {
            this.utcOffset(offsetFromString(matchOffset, this._i));
        }
        return this;
    }

    function hasAlignedHourOffset (input) {
        if (!this.isValid()) {
            return false;
        }
        input = input ? local__createLocal(input).utcOffset() : 0;

        return (this.utcOffset() - input) % 60 === 0;
    }

    function isDaylightSavingTime () {
        return (
            this.utcOffset() > this.clone().month(0).utcOffset() ||
            this.utcOffset() > this.clone().month(5).utcOffset()
        );
    }

    function isDaylightSavingTimeShifted () {
        if (!isUndefined(this._isDSTShifted)) {
            return this._isDSTShifted;
        }

        var c = {};

        copyConfig(c, this);
        c = prepareConfig(c);

        if (c._a) {
            var other = c._isUTC ? create_utc__createUTC(c._a) : local__createLocal(c._a);
            this._isDSTShifted = this.isValid() &&
                compareArrays(c._a, other.toArray()) > 0;
        } else {
            this._isDSTShifted = false;
        }

        return this._isDSTShifted;
    }

    function isLocal () {
        return this.isValid() ? !this._isUTC : false;
    }

    function isUtcOffset () {
        return this.isValid() ? this._isUTC : false;
    }

    function isUtc () {
        return this.isValid() ? this._isUTC && this._offset === 0 : false;
    }

    // ASP.NET json date format regex
    var aspNetRegex = /^(\-)?(?:(\d*)[. ])?(\d+)\:(\d+)(?:\:(\d+)\.?(\d{3})?\d*)?$/;

    // from http://docs.closure-library.googlecode.com/git/closure_goog_date_date.js.source.html
    // somewhat more in line with 4.4.3.2 2004 spec, but allows decimal anywhere
    // and further modified to allow for strings containing both week and day
    var isoRegex = /^(-)?P(?:(-?[0-9,.]*)Y)?(?:(-?[0-9,.]*)M)?(?:(-?[0-9,.]*)W)?(?:(-?[0-9,.]*)D)?(?:T(?:(-?[0-9,.]*)H)?(?:(-?[0-9,.]*)M)?(?:(-?[0-9,.]*)S)?)?$/;

    function create__createDuration (input, key) {
        var duration = input,
            // matching against regexp is expensive, do it on demand
            match = null,
            sign,
            ret,
            diffRes;

        if (isDuration(input)) {
            duration = {
                ms : input._milliseconds,
                d  : input._days,
                M  : input._months
            };
        } else if (typeof input === 'number') {
            duration = {};
            if (key) {
                duration[key] = input;
            } else {
                duration.milliseconds = input;
            }
        } else if (!!(match = aspNetRegex.exec(input))) {
            sign = (match[1] === '-') ? -1 : 1;
            duration = {
                y  : 0,
                d  : toInt(match[DATE])        * sign,
                h  : toInt(match[HOUR])        * sign,
                m  : toInt(match[MINUTE])      * sign,
                s  : toInt(match[SECOND])      * sign,
                ms : toInt(match[MILLISECOND]) * sign
            };
        } else if (!!(match = isoRegex.exec(input))) {
            sign = (match[1] === '-') ? -1 : 1;
            duration = {
                y : parseIso(match[2], sign),
                M : parseIso(match[3], sign),
                w : parseIso(match[4], sign),
                d : parseIso(match[5], sign),
                h : parseIso(match[6], sign),
                m : parseIso(match[7], sign),
                s : parseIso(match[8], sign)
            };
        } else if (duration == null) {// checks for null or undefined
            duration = {};
        } else if (typeof duration === 'object' && ('from' in duration || 'to' in duration)) {
            diffRes = momentsDifference(local__createLocal(duration.from), local__createLocal(duration.to));

            duration = {};
            duration.ms = diffRes.milliseconds;
            duration.M = diffRes.months;
        }

        ret = new Duration(duration);

        if (isDuration(input) && hasOwnProp(input, '_locale')) {
            ret._locale = input._locale;
        }

        return ret;
    }

    create__createDuration.fn = Duration.prototype;

    function parseIso (inp, sign) {
        // We'd normally use ~~inp for this, but unfortunately it also
        // converts floats to ints.
        // inp may be undefined, so careful calling replace on it.
        var res = inp && parseFloat(inp.replace(',', '.'));
        // apply sign while we're at it
        return (isNaN(res) ? 0 : res) * sign;
    }

    function positiveMomentsDifference(base, other) {
        var res = {milliseconds: 0, months: 0};

        res.months = other.month() - base.month() +
            (other.year() - base.year()) * 12;
        if (base.clone().add(res.months, 'M').isAfter(other)) {
            --res.months;
        }

        res.milliseconds = +other - +(base.clone().add(res.months, 'M'));

        return res;
    }

    function momentsDifference(base, other) {
        var res;
        if (!(base.isValid() && other.isValid())) {
            return {milliseconds: 0, months: 0};
        }

        other = cloneWithOffset(other, base);
        if (base.isBefore(other)) {
            res = positiveMomentsDifference(base, other);
        } else {
            res = positiveMomentsDifference(other, base);
            res.milliseconds = -res.milliseconds;
            res.months = -res.months;
        }

        return res;
    }

    function absRound (number) {
        if (number < 0) {
            return Math.round(-1 * number) * -1;
        } else {
            return Math.round(number);
        }
    }

    // TODO: remove 'name' arg after deprecation is removed
    function createAdder(direction, name) {
        return function (val, period) {
            var dur, tmp;
            //invert the arguments, but complain about it
            if (period !== null && !isNaN(+period)) {
                deprecateSimple(name, 'moment().' + name  + '(period, number) is deprecated. Please use moment().' + name + '(number, period).');
                tmp = val; val = period; period = tmp;
            }

            val = typeof val === 'string' ? +val : val;
            dur = create__createDuration(val, period);
            add_subtract__addSubtract(this, dur, direction);
            return this;
        };
    }

    function add_subtract__addSubtract (mom, duration, isAdding, updateOffset) {
        var milliseconds = duration._milliseconds,
            days = absRound(duration._days),
            months = absRound(duration._months);

        if (!mom.isValid()) {
            // No op
            return;
        }

        updateOffset = updateOffset == null ? true : updateOffset;

        if (milliseconds) {
            mom._d.setTime(mom._d.valueOf() + milliseconds * isAdding);
        }
        if (days) {
            get_set__set(mom, 'Date', get_set__get(mom, 'Date') + days * isAdding);
        }
        if (months) {
            setMonth(mom, get_set__get(mom, 'Month') + months * isAdding);
        }
        if (updateOffset) {
            utils_hooks__hooks.updateOffset(mom, days || months);
        }
    }

    var add_subtract__add      = createAdder(1, 'add');
    var add_subtract__subtract = createAdder(-1, 'subtract');

    function moment_calendar__calendar (time, formats) {
        // We want to compare the start of today, vs this.
        // Getting start-of-today depends on whether we're local/utc/offset or not.
        var now = time || local__createLocal(),
            sod = cloneWithOffset(now, this).startOf('day'),
            diff = this.diff(sod, 'days', true),
            format = diff < -6 ? 'sameElse' :
                diff < -1 ? 'lastWeek' :
                diff < 0 ? 'lastDay' :
                diff < 1 ? 'sameDay' :
                diff < 2 ? 'nextDay' :
                diff < 7 ? 'nextWeek' : 'sameElse';

        var output = formats && (isFunction(formats[format]) ? formats[format]() : formats[format]);

        return this.format(output || this.localeData().calendar(format, this, local__createLocal(now)));
    }

    function clone () {
        return new Moment(this);
    }

    function isAfter (input, units) {
        var localInput = isMoment(input) ? input : local__createLocal(input);
        if (!(this.isValid() && localInput.isValid())) {
            return false;
        }
        units = normalizeUnits(!isUndefined(units) ? units : 'millisecond');
        if (units === 'millisecond') {
            return this.valueOf() > localInput.valueOf();
        } else {
            return localInput.valueOf() < this.clone().startOf(units).valueOf();
        }
    }

    function isBefore (input, units) {
        var localInput = isMoment(input) ? input : local__createLocal(input);
        if (!(this.isValid() && localInput.isValid())) {
            return false;
        }
        units = normalizeUnits(!isUndefined(units) ? units : 'millisecond');
        if (units === 'millisecond') {
            return this.valueOf() < localInput.valueOf();
        } else {
            return this.clone().endOf(units).valueOf() < localInput.valueOf();
        }
    }

    function isBetween (from, to, units, inclusivity) {
        inclusivity = inclusivity || '()';
        return (inclusivity[0] === '(' ? this.isAfter(from, units) : !this.isBefore(from, units)) &&
            (inclusivity[1] === ')' ? this.isBefore(to, units) : !this.isAfter(to, units));
    }

    function isSame (input, units) {
        var localInput = isMoment(input) ? input : local__createLocal(input),
            inputMs;
        if (!(this.isValid() && localInput.isValid())) {
            return false;
        }
        units = normalizeUnits(units || 'millisecond');
        if (units === 'millisecond') {
            return this.valueOf() === localInput.valueOf();
        } else {
            inputMs = localInput.valueOf();
            return this.clone().startOf(units).valueOf() <= inputMs && inputMs <= this.clone().endOf(units).valueOf();
        }
    }

    function isSameOrAfter (input, units) {
        return this.isSame(input, units) || this.isAfter(input,units);
    }

    function isSameOrBefore (input, units) {
        return this.isSame(input, units) || this.isBefore(input,units);
    }

    function diff (input, units, asFloat) {
        var that,
            zoneDelta,
            delta, output;

        if (!this.isValid()) {
            return NaN;
        }

        that = cloneWithOffset(input, this);

        if (!that.isValid()) {
            return NaN;
        }

        zoneDelta = (that.utcOffset() - this.utcOffset()) * 6e4;

        units = normalizeUnits(units);

        if (units === 'year' || units === 'month' || units === 'quarter') {
            output = monthDiff(this, that);
            if (units === 'quarter') {
                output = output / 3;
            } else if (units === 'year') {
                output = output / 12;
            }
        } else {
            delta = this - that;
            output = units === 'second' ? delta / 1e3 : // 1000
                units === 'minute' ? delta / 6e4 : // 1000 * 60
                units === 'hour' ? delta / 36e5 : // 1000 * 60 * 60
                units === 'day' ? (delta - zoneDelta) / 864e5 : // 1000 * 60 * 60 * 24, negate dst
                units === 'week' ? (delta - zoneDelta) / 6048e5 : // 1000 * 60 * 60 * 24 * 7, negate dst
                delta;
        }
        return asFloat ? output : absFloor(output);
    }

    function monthDiff (a, b) {
        // difference in months
        var wholeMonthDiff = ((b.year() - a.year()) * 12) + (b.month() - a.month()),
            // b is in (anchor - 1 month, anchor + 1 month)
            anchor = a.clone().add(wholeMonthDiff, 'months'),
            anchor2, adjust;

        if (b - anchor < 0) {
            anchor2 = a.clone().add(wholeMonthDiff - 1, 'months');
            // linear across the month
            adjust = (b - anchor) / (anchor - anchor2);
        } else {
            anchor2 = a.clone().add(wholeMonthDiff + 1, 'months');
            // linear across the month
            adjust = (b - anchor) / (anchor2 - anchor);
        }

        //check for negative zero, return zero if negative zero
        return -(wholeMonthDiff + adjust) || 0;
    }

    utils_hooks__hooks.defaultFormat = 'YYYY-MM-DDTHH:mm:ssZ';
    utils_hooks__hooks.defaultFormatUtc = 'YYYY-MM-DDTHH:mm:ss[Z]';

    function toString () {
        return this.clone().locale('en').format('ddd MMM DD YYYY HH:mm:ss [GMT]ZZ');
    }

    function moment_format__toISOString () {
        var m = this.clone().utc();
        if (0 < m.year() && m.year() <= 9999) {
            if (isFunction(Date.prototype.toISOString)) {
                // native implementation is ~50x faster, use it when we can
                return this.toDate().toISOString();
            } else {
                return formatMoment(m, 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]');
            }
        } else {
            return formatMoment(m, 'YYYYYY-MM-DD[T]HH:mm:ss.SSS[Z]');
        }
    }

    function format (inputString) {
        if (!inputString) {
            inputString = this.isUtc() ? utils_hooks__hooks.defaultFormatUtc : utils_hooks__hooks.defaultFormat;
        }
        var output = formatMoment(this, inputString);
        return this.localeData().postformat(output);
    }

    function from (time, withoutSuffix) {
        if (this.isValid() &&
                ((isMoment(time) && time.isValid()) ||
                 local__createLocal(time).isValid())) {
            return create__createDuration({to: this, from: time}).locale(this.locale()).humanize(!withoutSuffix);
        } else {
            return this.localeData().invalidDate();
        }
    }

    function fromNow (withoutSuffix) {
        return this.from(local__createLocal(), withoutSuffix);
    }

    function to (time, withoutSuffix) {
        if (this.isValid() &&
                ((isMoment(time) && time.isValid()) ||
                 local__createLocal(time).isValid())) {
            return create__createDuration({from: this, to: time}).locale(this.locale()).humanize(!withoutSuffix);
        } else {
            return this.localeData().invalidDate();
        }
    }

    function toNow (withoutSuffix) {
        return this.to(local__createLocal(), withoutSuffix);
    }

    // If passed a locale key, it will set the locale for this
    // instance.  Otherwise, it will return the locale configuration
    // variables for this instance.
    function locale (key) {
        var newLocaleData;

        if (key === undefined) {
            return this._locale._abbr;
        } else {
            newLocaleData = locale_locales__getLocale(key);
            if (newLocaleData != null) {
                this._locale = newLocaleData;
            }
            return this;
        }
    }

    var lang = deprecate(
        'moment().lang() is deprecated. Instead, use moment().localeData() to get the language configuration. Use moment().locale() to change languages.',
        function (key) {
            if (key === undefined) {
                return this.localeData();
            } else {
                return this.locale(key);
            }
        }
    );

    function localeData () {
        return this._locale;
    }

    function startOf (units) {
        units = normalizeUnits(units);
        // the following switch intentionally omits break keywords
        // to utilize falling through the cases.
        switch (units) {
        case 'year':
            this.month(0);
            /* falls through */
        case 'quarter':
        case 'month':
            this.date(1);
            /* falls through */
        case 'week':
        case 'isoWeek':
        case 'day':
        case 'date':
            this.hours(0);
            /* falls through */
        case 'hour':
            this.minutes(0);
            /* falls through */
        case 'minute':
            this.seconds(0);
            /* falls through */
        case 'second':
            this.milliseconds(0);
        }

        // weeks are a special case
        if (units === 'week') {
            this.weekday(0);
        }
        if (units === 'isoWeek') {
            this.isoWeekday(1);
        }

        // quarters are also special
        if (units === 'quarter') {
            this.month(Math.floor(this.month() / 3) * 3);
        }

        return this;
    }

    function endOf (units) {
        units = normalizeUnits(units);
        if (units === undefined || units === 'millisecond') {
            return this;
        }

        // 'date' is an alias for 'day', so it should be considered as such.
        if (units === 'date') {
            units = 'day';
        }

        return this.startOf(units).add(1, (units === 'isoWeek' ? 'week' : units)).subtract(1, 'ms');
    }

    function to_type__valueOf () {
        return this._d.valueOf() - ((this._offset || 0) * 60000);
    }

    function unix () {
        return Math.floor(this.valueOf() / 1000);
    }

    function toDate () {
        return this._offset ? new Date(this.valueOf()) : this._d;
    }

    function toArray () {
        var m = this;
        return [m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second(), m.millisecond()];
    }

    function toObject () {
        var m = this;
        return {
            years: m.year(),
            months: m.month(),
            date: m.date(),
            hours: m.hours(),
            minutes: m.minutes(),
            seconds: m.seconds(),
            milliseconds: m.milliseconds()
        };
    }

    function toJSON () {
        // new Date(NaN).toJSON() === null
        return this.isValid() ? this.toISOString() : null;
    }

    function moment_valid__isValid () {
        return valid__isValid(this);
    }

    function parsingFlags () {
        return extend({}, getParsingFlags(this));
    }

    function invalidAt () {
        return getParsingFlags(this).overflow;
    }

    function creationData() {
        return {
            input: this._i,
            format: this._f,
            locale: this._locale,
            isUTC: this._isUTC,
            strict: this._strict
        };
    }

    // FORMATTING

    addFormatToken(0, ['gg', 2], 0, function () {
        return this.weekYear() % 100;
    });

    addFormatToken(0, ['GG', 2], 0, function () {
        return this.isoWeekYear() % 100;
    });

    function addWeekYearFormatToken (token, getter) {
        addFormatToken(0, [token, token.length], 0, getter);
    }

    addWeekYearFormatToken('gggg',     'weekYear');
    addWeekYearFormatToken('ggggg',    'weekYear');
    addWeekYearFormatToken('GGGG',  'isoWeekYear');
    addWeekYearFormatToken('GGGGG', 'isoWeekYear');

    // ALIASES

    addUnitAlias('weekYear', 'gg');
    addUnitAlias('isoWeekYear', 'GG');

    // PARSING

    addRegexToken('G',      matchSigned);
    addRegexToken('g',      matchSigned);
    addRegexToken('GG',     match1to2, match2);
    addRegexToken('gg',     match1to2, match2);
    addRegexToken('GGGG',   match1to4, match4);
    addRegexToken('gggg',   match1to4, match4);
    addRegexToken('GGGGG',  match1to6, match6);
    addRegexToken('ggggg',  match1to6, match6);

    addWeekParseToken(['gggg', 'ggggg', 'GGGG', 'GGGGG'], function (input, week, config, token) {
        week[token.substr(0, 2)] = toInt(input);
    });

    addWeekParseToken(['gg', 'GG'], function (input, week, config, token) {
        week[token] = utils_hooks__hooks.parseTwoDigitYear(input);
    });

    // MOMENTS

    function getSetWeekYear (input) {
        return getSetWeekYearHelper.call(this,
                input,
                this.week(),
                this.weekday(),
                this.localeData()._week.dow,
                this.localeData()._week.doy);
    }

    function getSetISOWeekYear (input) {
        return getSetWeekYearHelper.call(this,
                input, this.isoWeek(), this.isoWeekday(), 1, 4);
    }

    function getISOWeeksInYear () {
        return weeksInYear(this.year(), 1, 4);
    }

    function getWeeksInYear () {
        var weekInfo = this.localeData()._week;
        return weeksInYear(this.year(), weekInfo.dow, weekInfo.doy);
    }

    function getSetWeekYearHelper(input, week, weekday, dow, doy) {
        var weeksTarget;
        if (input == null) {
            return weekOfYear(this, dow, doy).year;
        } else {
            weeksTarget = weeksInYear(input, dow, doy);
            if (week > weeksTarget) {
                week = weeksTarget;
            }
            return setWeekAll.call(this, input, week, weekday, dow, doy);
        }
    }

    function setWeekAll(weekYear, week, weekday, dow, doy) {
        var dayOfYearData = dayOfYearFromWeeks(weekYear, week, weekday, dow, doy),
            date = createUTCDate(dayOfYearData.year, 0, dayOfYearData.dayOfYear);

        this.year(date.getUTCFullYear());
        this.month(date.getUTCMonth());
        this.date(date.getUTCDate());
        return this;
    }

    // FORMATTING

    addFormatToken('Q', 0, 'Qo', 'quarter');

    // ALIASES

    addUnitAlias('quarter', 'Q');

    // PARSING

    addRegexToken('Q', match1);
    addParseToken('Q', function (input, array) {
        array[MONTH] = (toInt(input) - 1) * 3;
    });

    // MOMENTS

    function getSetQuarter (input) {
        return input == null ? Math.ceil((this.month() + 1) / 3) : this.month((input - 1) * 3 + this.month() % 3);
    }

    // FORMATTING

    addFormatToken('w', ['ww', 2], 'wo', 'week');
    addFormatToken('W', ['WW', 2], 'Wo', 'isoWeek');

    // ALIASES

    addUnitAlias('week', 'w');
    addUnitAlias('isoWeek', 'W');

    // PARSING

    addRegexToken('w',  match1to2);
    addRegexToken('ww', match1to2, match2);
    addRegexToken('W',  match1to2);
    addRegexToken('WW', match1to2, match2);

    addWeekParseToken(['w', 'ww', 'W', 'WW'], function (input, week, config, token) {
        week[token.substr(0, 1)] = toInt(input);
    });

    // HELPERS

    // LOCALES

    function localeWeek (mom) {
        return weekOfYear(mom, this._week.dow, this._week.doy).week;
    }

    var defaultLocaleWeek = {
        dow : 0, // Sunday is the first day of the week.
        doy : 6  // The week that contains Jan 1st is the first week of the year.
    };

    function localeFirstDayOfWeek () {
        return this._week.dow;
    }

    function localeFirstDayOfYear () {
        return this._week.doy;
    }

    // MOMENTS

    function getSetWeek (input) {
        var week = this.localeData().week(this);
        return input == null ? week : this.add((input - week) * 7, 'd');
    }

    function getSetISOWeek (input) {
        var week = weekOfYear(this, 1, 4).week;
        return input == null ? week : this.add((input - week) * 7, 'd');
    }

    // FORMATTING

    addFormatToken('D', ['DD', 2], 'Do', 'date');

    // ALIASES

    addUnitAlias('date', 'D');

    // PARSING

    addRegexToken('D',  match1to2);
    addRegexToken('DD', match1to2, match2);
    addRegexToken('Do', function (isStrict, locale) {
        return isStrict ? locale._ordinalParse : locale._ordinalParseLenient;
    });

    addParseToken(['D', 'DD'], DATE);
    addParseToken('Do', function (input, array) {
        array[DATE] = toInt(input.match(match1to2)[0], 10);
    });

    // MOMENTS

    var getSetDayOfMonth = makeGetSet('Date', true);

    // FORMATTING

    addFormatToken('d', 0, 'do', 'day');

    addFormatToken('dd', 0, 0, function (format) {
        return this.localeData().weekdaysMin(this, format);
    });

    addFormatToken('ddd', 0, 0, function (format) {
        return this.localeData().weekdaysShort(this, format);
    });

    addFormatToken('dddd', 0, 0, function (format) {
        return this.localeData().weekdays(this, format);
    });

    addFormatToken('e', 0, 0, 'weekday');
    addFormatToken('E', 0, 0, 'isoWeekday');

    // ALIASES

    addUnitAlias('day', 'd');
    addUnitAlias('weekday', 'e');
    addUnitAlias('isoWeekday', 'E');

    // PARSING

    addRegexToken('d',    match1to2);
    addRegexToken('e',    match1to2);
    addRegexToken('E',    match1to2);
    addRegexToken('dd',   function (isStrict, locale) {
        return locale.weekdaysMinRegex(isStrict);
    });
    addRegexToken('ddd',   function (isStrict, locale) {
        return locale.weekdaysShortRegex(isStrict);
    });
    addRegexToken('dddd',   function (isStrict, locale) {
        return locale.weekdaysRegex(isStrict);
    });

    addWeekParseToken(['dd', 'ddd', 'dddd'], function (input, week, config, token) {
        var weekday = config._locale.weekdaysParse(input, token, config._strict);
        // if we didn't get a weekday name, mark the date as invalid
        if (weekday != null) {
            week.d = weekday;
        } else {
            getParsingFlags(config).invalidWeekday = input;
        }
    });

    addWeekParseToken(['d', 'e', 'E'], function (input, week, config, token) {
        week[token] = toInt(input);
    });

    // HELPERS

    function parseWeekday(input, locale) {
        if (typeof input !== 'string') {
            return input;
        }

        if (!isNaN(input)) {
            return parseInt(input, 10);
        }

        input = locale.weekdaysParse(input);
        if (typeof input === 'number') {
            return input;
        }

        return null;
    }

    // LOCALES

    var defaultLocaleWeekdays = 'Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday'.split('_');
    function localeWeekdays (m, format) {
        return isArray(this._weekdays) ? this._weekdays[m.day()] :
            this._weekdays[this._weekdays.isFormat.test(format) ? 'format' : 'standalone'][m.day()];
    }

    var defaultLocaleWeekdaysShort = 'Sun_Mon_Tue_Wed_Thu_Fri_Sat'.split('_');
    function localeWeekdaysShort (m) {
        return this._weekdaysShort[m.day()];
    }

    var defaultLocaleWeekdaysMin = 'Su_Mo_Tu_We_Th_Fr_Sa'.split('_');
    function localeWeekdaysMin (m) {
        return this._weekdaysMin[m.day()];
    }

    function day_of_week__handleStrictParse(weekdayName, format, strict) {
        var i, ii, mom, llc = weekdayName.toLocaleLowerCase();
        if (!this._weekdaysParse) {
            this._weekdaysParse = [];
            this._shortWeekdaysParse = [];
            this._minWeekdaysParse = [];

            for (i = 0; i < 7; ++i) {
                mom = create_utc__createUTC([2000, 1]).day(i);
                this._minWeekdaysParse[i] = this.weekdaysMin(mom, '').toLocaleLowerCase();
                this._shortWeekdaysParse[i] = this.weekdaysShort(mom, '').toLocaleLowerCase();
                this._weekdaysParse[i] = this.weekdays(mom, '').toLocaleLowerCase();
            }
        }

        if (strict) {
            if (format === 'dddd') {
                ii = indexOf.call(this._weekdaysParse, llc);
                return ii !== -1 ? ii : null;
            } else if (format === 'ddd') {
                ii = indexOf.call(this._shortWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            } else {
                ii = indexOf.call(this._minWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            }
        } else {
            if (format === 'dddd') {
                ii = indexOf.call(this._weekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._shortWeekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._minWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            } else if (format === 'ddd') {
                ii = indexOf.call(this._shortWeekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._weekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._minWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            } else {
                ii = indexOf.call(this._minWeekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._weekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._shortWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            }
        }
    }

    function localeWeekdaysParse (weekdayName, format, strict) {
        var i, mom, regex;

        if (this._weekdaysParseExact) {
            return day_of_week__handleStrictParse.call(this, weekdayName, format, strict);
        }

        if (!this._weekdaysParse) {
            this._weekdaysParse = [];
            this._minWeekdaysParse = [];
            this._shortWeekdaysParse = [];
            this._fullWeekdaysParse = [];
        }

        for (i = 0; i < 7; i++) {
            // make the regex if we don't have it already

            mom = create_utc__createUTC([2000, 1]).day(i);
            if (strict && !this._fullWeekdaysParse[i]) {
                this._fullWeekdaysParse[i] = new RegExp('^' + this.weekdays(mom, '').replace('.', '\.?') + '$', 'i');
                this._shortWeekdaysParse[i] = new RegExp('^' + this.weekdaysShort(mom, '').replace('.', '\.?') + '$', 'i');
                this._minWeekdaysParse[i] = new RegExp('^' + this.weekdaysMin(mom, '').replace('.', '\.?') + '$', 'i');
            }
            if (!this._weekdaysParse[i]) {
                regex = '^' + this.weekdays(mom, '') + '|^' + this.weekdaysShort(mom, '') + '|^' + this.weekdaysMin(mom, '');
                this._weekdaysParse[i] = new RegExp(regex.replace('.', ''), 'i');
            }
            // test the regex
            if (strict && format === 'dddd' && this._fullWeekdaysParse[i].test(weekdayName)) {
                return i;
            } else if (strict && format === 'ddd' && this._shortWeekdaysParse[i].test(weekdayName)) {
                return i;
            } else if (strict && format === 'dd' && this._minWeekdaysParse[i].test(weekdayName)) {
                return i;
            } else if (!strict && this._weekdaysParse[i].test(weekdayName)) {
                return i;
            }
        }
    }

    // MOMENTS

    function getSetDayOfWeek (input) {
        if (!this.isValid()) {
            return input != null ? this : NaN;
        }
        var day = this._isUTC ? this._d.getUTCDay() : this._d.getDay();
        if (input != null) {
            input = parseWeekday(input, this.localeData());
            return this.add(input - day, 'd');
        } else {
            return day;
        }
    }

    function getSetLocaleDayOfWeek (input) {
        if (!this.isValid()) {
            return input != null ? this : NaN;
        }
        var weekday = (this.day() + 7 - this.localeData()._week.dow) % 7;
        return input == null ? weekday : this.add(input - weekday, 'd');
    }

    function getSetISODayOfWeek (input) {
        if (!this.isValid()) {
            return input != null ? this : NaN;
        }
        // behaves the same as moment#day except
        // as a getter, returns 7 instead of 0 (1-7 range instead of 0-6)
        // as a setter, sunday should belong to the previous week.
        return input == null ? this.day() || 7 : this.day(this.day() % 7 ? input : input - 7);
    }

    var defaultWeekdaysRegex = matchWord;
    function weekdaysRegex (isStrict) {
        if (this._weekdaysParseExact) {
            if (!hasOwnProp(this, '_weekdaysRegex')) {
                computeWeekdaysParse.call(this);
            }
            if (isStrict) {
                return this._weekdaysStrictRegex;
            } else {
                return this._weekdaysRegex;
            }
        } else {
            return this._weekdaysStrictRegex && isStrict ?
                this._weekdaysStrictRegex : this._weekdaysRegex;
        }
    }

    var defaultWeekdaysShortRegex = matchWord;
    function weekdaysShortRegex (isStrict) {
        if (this._weekdaysParseExact) {
            if (!hasOwnProp(this, '_weekdaysRegex')) {
                computeWeekdaysParse.call(this);
            }
            if (isStrict) {
                return this._weekdaysShortStrictRegex;
            } else {
                return this._weekdaysShortRegex;
            }
        } else {
            return this._weekdaysShortStrictRegex && isStrict ?
                this._weekdaysShortStrictRegex : this._weekdaysShortRegex;
        }
    }

    var defaultWeekdaysMinRegex = matchWord;
    function weekdaysMinRegex (isStrict) {
        if (this._weekdaysParseExact) {
            if (!hasOwnProp(this, '_weekdaysRegex')) {
                computeWeekdaysParse.call(this);
            }
            if (isStrict) {
                return this._weekdaysMinStrictRegex;
            } else {
                return this._weekdaysMinRegex;
            }
        } else {
            return this._weekdaysMinStrictRegex && isStrict ?
                this._weekdaysMinStrictRegex : this._weekdaysMinRegex;
        }
    }


    function computeWeekdaysParse () {
        function cmpLenRev(a, b) {
            return b.length - a.length;
        }

        var minPieces = [], shortPieces = [], longPieces = [], mixedPieces = [],
            i, mom, minp, shortp, longp;
        for (i = 0; i < 7; i++) {
            // make the regex if we don't have it already
            mom = create_utc__createUTC([2000, 1]).day(i);
            minp = this.weekdaysMin(mom, '');
            shortp = this.weekdaysShort(mom, '');
            longp = this.weekdays(mom, '');
            minPieces.push(minp);
            shortPieces.push(shortp);
            longPieces.push(longp);
            mixedPieces.push(minp);
            mixedPieces.push(shortp);
            mixedPieces.push(longp);
        }
        // Sorting makes sure if one weekday (or abbr) is a prefix of another it
        // will match the longer piece.
        minPieces.sort(cmpLenRev);
        shortPieces.sort(cmpLenRev);
        longPieces.sort(cmpLenRev);
        mixedPieces.sort(cmpLenRev);
        for (i = 0; i < 7; i++) {
            shortPieces[i] = regexEscape(shortPieces[i]);
            longPieces[i] = regexEscape(longPieces[i]);
            mixedPieces[i] = regexEscape(mixedPieces[i]);
        }

        this._weekdaysRegex = new RegExp('^(' + mixedPieces.join('|') + ')', 'i');
        this._weekdaysShortRegex = this._weekdaysRegex;
        this._weekdaysMinRegex = this._weekdaysRegex;

        this._weekdaysStrictRegex = new RegExp('^(' + longPieces.join('|') + ')', 'i');
        this._weekdaysShortStrictRegex = new RegExp('^(' + shortPieces.join('|') + ')', 'i');
        this._weekdaysMinStrictRegex = new RegExp('^(' + minPieces.join('|') + ')', 'i');
    }

    // FORMATTING

    addFormatToken('DDD', ['DDDD', 3], 'DDDo', 'dayOfYear');

    // ALIASES

    addUnitAlias('dayOfYear', 'DDD');

    // PARSING

    addRegexToken('DDD',  match1to3);
    addRegexToken('DDDD', match3);
    addParseToken(['DDD', 'DDDD'], function (input, array, config) {
        config._dayOfYear = toInt(input);
    });

    // HELPERS

    // MOMENTS

    function getSetDayOfYear (input) {
        var dayOfYear = Math.round((this.clone().startOf('day') - this.clone().startOf('year')) / 864e5) + 1;
        return input == null ? dayOfYear : this.add((input - dayOfYear), 'd');
    }

    // FORMATTING

    function hFormat() {
        return this.hours() % 12 || 12;
    }

    function kFormat() {
        return this.hours() || 24;
    }

    addFormatToken('H', ['HH', 2], 0, 'hour');
    addFormatToken('h', ['hh', 2], 0, hFormat);
    addFormatToken('k', ['kk', 2], 0, kFormat);

    addFormatToken('hmm', 0, 0, function () {
        return '' + hFormat.apply(this) + zeroFill(this.minutes(), 2);
    });

    addFormatToken('hmmss', 0, 0, function () {
        return '' + hFormat.apply(this) + zeroFill(this.minutes(), 2) +
            zeroFill(this.seconds(), 2);
    });

    addFormatToken('Hmm', 0, 0, function () {
        return '' + this.hours() + zeroFill(this.minutes(), 2);
    });

    addFormatToken('Hmmss', 0, 0, function () {
        return '' + this.hours() + zeroFill(this.minutes(), 2) +
            zeroFill(this.seconds(), 2);
    });

    function meridiem (token, lowercase) {
        addFormatToken(token, 0, 0, function () {
            return this.localeData().meridiem(this.hours(), this.minutes(), lowercase);
        });
    }

    meridiem('a', true);
    meridiem('A', false);

    // ALIASES

    addUnitAlias('hour', 'h');

    // PARSING

    function matchMeridiem (isStrict, locale) {
        return locale._meridiemParse;
    }

    addRegexToken('a',  matchMeridiem);
    addRegexToken('A',  matchMeridiem);
    addRegexToken('H',  match1to2);
    addRegexToken('h',  match1to2);
    addRegexToken('HH', match1to2, match2);
    addRegexToken('hh', match1to2, match2);

    addRegexToken('hmm', match3to4);
    addRegexToken('hmmss', match5to6);
    addRegexToken('Hmm', match3to4);
    addRegexToken('Hmmss', match5to6);

    addParseToken(['H', 'HH'], HOUR);
    addParseToken(['a', 'A'], function (input, array, config) {
        config._isPm = config._locale.isPM(input);
        config._meridiem = input;
    });
    addParseToken(['h', 'hh'], function (input, array, config) {
        array[HOUR] = toInt(input);
        getParsingFlags(config).bigHour = true;
    });
    addParseToken('hmm', function (input, array, config) {
        var pos = input.length - 2;
        array[HOUR] = toInt(input.substr(0, pos));
        array[MINUTE] = toInt(input.substr(pos));
        getParsingFlags(config).bigHour = true;
    });
    addParseToken('hmmss', function (input, array, config) {
        var pos1 = input.length - 4;
        var pos2 = input.length - 2;
        array[HOUR] = toInt(input.substr(0, pos1));
        array[MINUTE] = toInt(input.substr(pos1, 2));
        array[SECOND] = toInt(input.substr(pos2));
        getParsingFlags(config).bigHour = true;
    });
    addParseToken('Hmm', function (input, array, config) {
        var pos = input.length - 2;
        array[HOUR] = toInt(input.substr(0, pos));
        array[MINUTE] = toInt(input.substr(pos));
    });
    addParseToken('Hmmss', function (input, array, config) {
        var pos1 = input.length - 4;
        var pos2 = input.length - 2;
        array[HOUR] = toInt(input.substr(0, pos1));
        array[MINUTE] = toInt(input.substr(pos1, 2));
        array[SECOND] = toInt(input.substr(pos2));
    });

    // LOCALES

    function localeIsPM (input) {
        // IE8 Quirks Mode & IE7 Standards Mode do not allow accessing strings like arrays
        // Using charAt should be more compatible.
        return ((input + '').toLowerCase().charAt(0) === 'p');
    }

    var defaultLocaleMeridiemParse = /[ap]\.?m?\.?/i;
    function localeMeridiem (hours, minutes, isLower) {
        if (hours > 11) {
            return isLower ? 'pm' : 'PM';
        } else {
            return isLower ? 'am' : 'AM';
        }
    }


    // MOMENTS

    // Setting the hour should keep the time, because the user explicitly
    // specified which hour he wants. So trying to maintain the same hour (in
    // a new timezone) makes sense. Adding/subtracting hours does not follow
    // this rule.
    var getSetHour = makeGetSet('Hours', true);

    // FORMATTING

    addFormatToken('m', ['mm', 2], 0, 'minute');

    // ALIASES

    addUnitAlias('minute', 'm');

    // PARSING

    addRegexToken('m',  match1to2);
    addRegexToken('mm', match1to2, match2);
    addParseToken(['m', 'mm'], MINUTE);

    // MOMENTS

    var getSetMinute = makeGetSet('Minutes', false);

    // FORMATTING

    addFormatToken('s', ['ss', 2], 0, 'second');

    // ALIASES

    addUnitAlias('second', 's');

    // PARSING

    addRegexToken('s',  match1to2);
    addRegexToken('ss', match1to2, match2);
    addParseToken(['s', 'ss'], SECOND);

    // MOMENTS

    var getSetSecond = makeGetSet('Seconds', false);

    // FORMATTING

    addFormatToken('S', 0, 0, function () {
        return ~~(this.millisecond() / 100);
    });

    addFormatToken(0, ['SS', 2], 0, function () {
        return ~~(this.millisecond() / 10);
    });

    addFormatToken(0, ['SSS', 3], 0, 'millisecond');
    addFormatToken(0, ['SSSS', 4], 0, function () {
        return this.millisecond() * 10;
    });
    addFormatToken(0, ['SSSSS', 5], 0, function () {
        return this.millisecond() * 100;
    });
    addFormatToken(0, ['SSSSSS', 6], 0, function () {
        return this.millisecond() * 1000;
    });
    addFormatToken(0, ['SSSSSSS', 7], 0, function () {
        return this.millisecond() * 10000;
    });
    addFormatToken(0, ['SSSSSSSS', 8], 0, function () {
        return this.millisecond() * 100000;
    });
    addFormatToken(0, ['SSSSSSSSS', 9], 0, function () {
        return this.millisecond() * 1000000;
    });


    // ALIASES

    addUnitAlias('millisecond', 'ms');

    // PARSING

    addRegexToken('S',    match1to3, match1);
    addRegexToken('SS',   match1to3, match2);
    addRegexToken('SSS',  match1to3, match3);

    var token;
    for (token = 'SSSS'; token.length <= 9; token += 'S') {
        addRegexToken(token, matchUnsigned);
    }

    function parseMs(input, array) {
        array[MILLISECOND] = toInt(('0.' + input) * 1000);
    }

    for (token = 'S'; token.length <= 9; token += 'S') {
        addParseToken(token, parseMs);
    }
    // MOMENTS

    var getSetMillisecond = makeGetSet('Milliseconds', false);

    // FORMATTING

    addFormatToken('z',  0, 0, 'zoneAbbr');
    addFormatToken('zz', 0, 0, 'zoneName');

    // MOMENTS

    function getZoneAbbr () {
        return this._isUTC ? 'UTC' : '';
    }

    function getZoneName () {
        return this._isUTC ? 'Coordinated Universal Time' : '';
    }

    var momentPrototype__proto = Moment.prototype;

    momentPrototype__proto.add               = add_subtract__add;
    momentPrototype__proto.calendar          = moment_calendar__calendar;
    momentPrototype__proto.clone             = clone;
    momentPrototype__proto.diff              = diff;
    momentPrototype__proto.endOf             = endOf;
    momentPrototype__proto.format            = format;
    momentPrototype__proto.from              = from;
    momentPrototype__proto.fromNow           = fromNow;
    momentPrototype__proto.to                = to;
    momentPrototype__proto.toNow             = toNow;
    momentPrototype__proto.get               = getSet;
    momentPrototype__proto.invalidAt         = invalidAt;
    momentPrototype__proto.isAfter           = isAfter;
    momentPrototype__proto.isBefore          = isBefore;
    momentPrototype__proto.isBetween         = isBetween;
    momentPrototype__proto.isSame            = isSame;
    momentPrototype__proto.isSameOrAfter     = isSameOrAfter;
    momentPrototype__proto.isSameOrBefore    = isSameOrBefore;
    momentPrototype__proto.isValid           = moment_valid__isValid;
    momentPrototype__proto.lang              = lang;
    momentPrototype__proto.locale            = locale;
    momentPrototype__proto.localeData        = localeData;
    momentPrototype__proto.max               = prototypeMax;
    momentPrototype__proto.min               = prototypeMin;
    momentPrototype__proto.parsingFlags      = parsingFlags;
    momentPrototype__proto.set               = getSet;
    momentPrototype__proto.startOf           = startOf;
    momentPrototype__proto.subtract          = add_subtract__subtract;
    momentPrototype__proto.toArray           = toArray;
    momentPrototype__proto.toObject          = toObject;
    momentPrototype__proto.toDate            = toDate;
    momentPrototype__proto.toISOString       = moment_format__toISOString;
    momentPrototype__proto.toJSON            = toJSON;
    momentPrototype__proto.toString          = toString;
    momentPrototype__proto.unix              = unix;
    momentPrototype__proto.valueOf           = to_type__valueOf;
    momentPrototype__proto.creationData      = creationData;

    // Year
    momentPrototype__proto.year       = getSetYear;
    momentPrototype__proto.isLeapYear = getIsLeapYear;

    // Week Year
    momentPrototype__proto.weekYear    = getSetWeekYear;
    momentPrototype__proto.isoWeekYear = getSetISOWeekYear;

    // Quarter
    momentPrototype__proto.quarter = momentPrototype__proto.quarters = getSetQuarter;

    // Month
    momentPrototype__proto.month       = getSetMonth;
    momentPrototype__proto.daysInMonth = getDaysInMonth;

    // Week
    momentPrototype__proto.week           = momentPrototype__proto.weeks        = getSetWeek;
    momentPrototype__proto.isoWeek        = momentPrototype__proto.isoWeeks     = getSetISOWeek;
    momentPrototype__proto.weeksInYear    = getWeeksInYear;
    momentPrototype__proto.isoWeeksInYear = getISOWeeksInYear;

    // Day
    momentPrototype__proto.date       = getSetDayOfMonth;
    momentPrototype__proto.day        = momentPrototype__proto.days             = getSetDayOfWeek;
    momentPrototype__proto.weekday    = getSetLocaleDayOfWeek;
    momentPrototype__proto.isoWeekday = getSetISODayOfWeek;
    momentPrototype__proto.dayOfYear  = getSetDayOfYear;

    // Hour
    momentPrototype__proto.hour = momentPrototype__proto.hours = getSetHour;

    // Minute
    momentPrototype__proto.minute = momentPrototype__proto.minutes = getSetMinute;

    // Second
    momentPrototype__proto.second = momentPrototype__proto.seconds = getSetSecond;

    // Millisecond
    momentPrototype__proto.millisecond = momentPrototype__proto.milliseconds = getSetMillisecond;

    // Offset
    momentPrototype__proto.utcOffset            = getSetOffset;
    momentPrototype__proto.utc                  = setOffsetToUTC;
    momentPrototype__proto.local                = setOffsetToLocal;
    momentPrototype__proto.parseZone            = setOffsetToParsedOffset;
    momentPrototype__proto.hasAlignedHourOffset = hasAlignedHourOffset;
    momentPrototype__proto.isDST                = isDaylightSavingTime;
    momentPrototype__proto.isDSTShifted         = isDaylightSavingTimeShifted;
    momentPrototype__proto.isLocal              = isLocal;
    momentPrototype__proto.isUtcOffset          = isUtcOffset;
    momentPrototype__proto.isUtc                = isUtc;
    momentPrototype__proto.isUTC                = isUtc;

    // Timezone
    momentPrototype__proto.zoneAbbr = getZoneAbbr;
    momentPrototype__proto.zoneName = getZoneName;

    // Deprecations
    momentPrototype__proto.dates  = deprecate('dates accessor is deprecated. Use date instead.', getSetDayOfMonth);
    momentPrototype__proto.months = deprecate('months accessor is deprecated. Use month instead', getSetMonth);
    momentPrototype__proto.years  = deprecate('years accessor is deprecated. Use year instead', getSetYear);
    momentPrototype__proto.zone   = deprecate('moment().zone is deprecated, use moment().utcOffset instead. https://github.com/moment/moment/issues/1779', getSetZone);

    var momentPrototype = momentPrototype__proto;

    function moment__createUnix (input) {
        return local__createLocal(input * 1000);
    }

    function moment__createInZone () {
        return local__createLocal.apply(null, arguments).parseZone();
    }

    var defaultCalendar = {
        sameDay : '[Today at] LT',
        nextDay : '[Tomorrow at] LT',
        nextWeek : 'dddd [at] LT',
        lastDay : '[Yesterday at] LT',
        lastWeek : '[Last] dddd [at] LT',
        sameElse : 'L'
    };

    function locale_calendar__calendar (key, mom, now) {
        var output = this._calendar[key];
        return isFunction(output) ? output.call(mom, now) : output;
    }

    var defaultLongDateFormat = {
        LTS  : 'h:mm:ss A',
        LT   : 'h:mm A',
        L    : 'MM/DD/YYYY',
        LL   : 'MMMM D, YYYY',
        LLL  : 'MMMM D, YYYY h:mm A',
        LLLL : 'dddd, MMMM D, YYYY h:mm A'
    };

    function longDateFormat (key) {
        var format = this._longDateFormat[key],
            formatUpper = this._longDateFormat[key.toUpperCase()];

        if (format || !formatUpper) {
            return format;
        }

        this._longDateFormat[key] = formatUpper.replace(/MMMM|MM|DD|dddd/g, function (val) {
            return val.slice(1);
        });

        return this._longDateFormat[key];
    }

    var defaultInvalidDate = 'Invalid date';

    function invalidDate () {
        return this._invalidDate;
    }

    var defaultOrdinal = '%d';
    var defaultOrdinalParse = /\d{1,2}/;

    function ordinal (number) {
        return this._ordinal.replace('%d', number);
    }

    function preParsePostFormat (string) {
        return string;
    }

    var defaultRelativeTime = {
        future : 'in %s',
        past   : '%s ago',
        s  : 'a few seconds',
        m  : 'a minute',
        mm : '%d minutes',
        h  : 'an hour',
        hh : '%d hours',
        d  : 'a day',
        dd : '%d days',
        M  : 'a month',
        MM : '%d months',
        y  : 'a year',
        yy : '%d years'
    };

    function relative__relativeTime (number, withoutSuffix, string, isFuture) {
        var output = this._relativeTime[string];
        return (isFunction(output)) ?
            output(number, withoutSuffix, string, isFuture) :
            output.replace(/%d/i, number);
    }

    function pastFuture (diff, output) {
        var format = this._relativeTime[diff > 0 ? 'future' : 'past'];
        return isFunction(format) ? format(output) : format.replace(/%s/i, output);
    }

    var prototype__proto = Locale.prototype;

    prototype__proto._calendar       = defaultCalendar;
    prototype__proto.calendar        = locale_calendar__calendar;
    prototype__proto._longDateFormat = defaultLongDateFormat;
    prototype__proto.longDateFormat  = longDateFormat;
    prototype__proto._invalidDate    = defaultInvalidDate;
    prototype__proto.invalidDate     = invalidDate;
    prototype__proto._ordinal        = defaultOrdinal;
    prototype__proto.ordinal         = ordinal;
    prototype__proto._ordinalParse   = defaultOrdinalParse;
    prototype__proto.preparse        = preParsePostFormat;
    prototype__proto.postformat      = preParsePostFormat;
    prototype__proto._relativeTime   = defaultRelativeTime;
    prototype__proto.relativeTime    = relative__relativeTime;
    prototype__proto.pastFuture      = pastFuture;
    prototype__proto.set             = locale_set__set;

    // Month
    prototype__proto.months            =        localeMonths;
    prototype__proto._months           = defaultLocaleMonths;
    prototype__proto.monthsShort       =        localeMonthsShort;
    prototype__proto._monthsShort      = defaultLocaleMonthsShort;
    prototype__proto.monthsParse       =        localeMonthsParse;
    prototype__proto._monthsRegex      = defaultMonthsRegex;
    prototype__proto.monthsRegex       = monthsRegex;
    prototype__proto._monthsShortRegex = defaultMonthsShortRegex;
    prototype__proto.monthsShortRegex  = monthsShortRegex;

    // Week
    prototype__proto.week = localeWeek;
    prototype__proto._week = defaultLocaleWeek;
    prototype__proto.firstDayOfYear = localeFirstDayOfYear;
    prototype__proto.firstDayOfWeek = localeFirstDayOfWeek;

    // Day of Week
    prototype__proto.weekdays       =        localeWeekdays;
    prototype__proto._weekdays      = defaultLocaleWeekdays;
    prototype__proto.weekdaysMin    =        localeWeekdaysMin;
    prototype__proto._weekdaysMin   = defaultLocaleWeekdaysMin;
    prototype__proto.weekdaysShort  =        localeWeekdaysShort;
    prototype__proto._weekdaysShort = defaultLocaleWeekdaysShort;
    prototype__proto.weekdaysParse  =        localeWeekdaysParse;

    prototype__proto._weekdaysRegex      = defaultWeekdaysRegex;
    prototype__proto.weekdaysRegex       =        weekdaysRegex;
    prototype__proto._weekdaysShortRegex = defaultWeekdaysShortRegex;
    prototype__proto.weekdaysShortRegex  =        weekdaysShortRegex;
    prototype__proto._weekdaysMinRegex   = defaultWeekdaysMinRegex;
    prototype__proto.weekdaysMinRegex    =        weekdaysMinRegex;

    // Hours
    prototype__proto.isPM = localeIsPM;
    prototype__proto._meridiemParse = defaultLocaleMeridiemParse;
    prototype__proto.meridiem = localeMeridiem;

    function lists__get (format, index, field, setter) {
        var locale = locale_locales__getLocale();
        var utc = create_utc__createUTC().set(setter, index);
        return locale[field](utc, format);
    }

    function listMonthsImpl (format, index, field) {
        if (typeof format === 'number') {
            index = format;
            format = undefined;
        }

        format = format || '';

        if (index != null) {
            return lists__get(format, index, field, 'month');
        }

        var i;
        var out = [];
        for (i = 0; i < 12; i++) {
            out[i] = lists__get(format, i, field, 'month');
        }
        return out;
    }

    // ()
    // (5)
    // (fmt, 5)
    // (fmt)
    // (true)
    // (true, 5)
    // (true, fmt, 5)
    // (true, fmt)
    function listWeekdaysImpl (localeSorted, format, index, field) {
        if (typeof localeSorted === 'boolean') {
            if (typeof format === 'number') {
                index = format;
                format = undefined;
            }

            format = format || '';
        } else {
            format = localeSorted;
            index = format;
            localeSorted = false;

            if (typeof format === 'number') {
                index = format;
                format = undefined;
            }

            format = format || '';
        }

        var locale = locale_locales__getLocale(),
            shift = localeSorted ? locale._week.dow : 0;

        if (index != null) {
            return lists__get(format, (index + shift) % 7, field, 'day');
        }

        var i;
        var out = [];
        for (i = 0; i < 7; i++) {
            out[i] = lists__get(format, (i + shift) % 7, field, 'day');
        }
        return out;
    }

    function lists__listMonths (format, index) {
        return listMonthsImpl(format, index, 'months');
    }

    function lists__listMonthsShort (format, index) {
        return listMonthsImpl(format, index, 'monthsShort');
    }

    function lists__listWeekdays (localeSorted, format, index) {
        return listWeekdaysImpl(localeSorted, format, index, 'weekdays');
    }

    function lists__listWeekdaysShort (localeSorted, format, index) {
        return listWeekdaysImpl(localeSorted, format, index, 'weekdaysShort');
    }

    function lists__listWeekdaysMin (localeSorted, format, index) {
        return listWeekdaysImpl(localeSorted, format, index, 'weekdaysMin');
    }

    locale_locales__getSetGlobalLocale('en', {
        ordinalParse: /\d{1,2}(th|st|nd|rd)/,
        ordinal : function (number) {
            var b = number % 10,
                output = (toInt(number % 100 / 10) === 1) ? 'th' :
                (b === 1) ? 'st' :
                (b === 2) ? 'nd' :
                (b === 3) ? 'rd' : 'th';
            return number + output;
        }
    });

    // Side effect imports
    utils_hooks__hooks.lang = deprecate('moment.lang is deprecated. Use moment.locale instead.', locale_locales__getSetGlobalLocale);
    utils_hooks__hooks.langData = deprecate('moment.langData is deprecated. Use moment.localeData instead.', locale_locales__getLocale);

    var mathAbs = Math.abs;

    function duration_abs__abs () {
        var data           = this._data;

        this._milliseconds = mathAbs(this._milliseconds);
        this._days         = mathAbs(this._days);
        this._months       = mathAbs(this._months);

        data.milliseconds  = mathAbs(data.milliseconds);
        data.seconds       = mathAbs(data.seconds);
        data.minutes       = mathAbs(data.minutes);
        data.hours         = mathAbs(data.hours);
        data.months        = mathAbs(data.months);
        data.years         = mathAbs(data.years);

        return this;
    }

    function duration_add_subtract__addSubtract (duration, input, value, direction) {
        var other = create__createDuration(input, value);

        duration._milliseconds += direction * other._milliseconds;
        duration._days         += direction * other._days;
        duration._months       += direction * other._months;

        return duration._bubble();
    }

    // supports only 2.0-style add(1, 's') or add(duration)
    function duration_add_subtract__add (input, value) {
        return duration_add_subtract__addSubtract(this, input, value, 1);
    }

    // supports only 2.0-style subtract(1, 's') or subtract(duration)
    function duration_add_subtract__subtract (input, value) {
        return duration_add_subtract__addSubtract(this, input, value, -1);
    }

    function absCeil (number) {
        if (number < 0) {
            return Math.floor(number);
        } else {
            return Math.ceil(number);
        }
    }

    function bubble () {
        var milliseconds = this._milliseconds;
        var days         = this._days;
        var months       = this._months;
        var data         = this._data;
        var seconds, minutes, hours, years, monthsFromDays;

        // if we have a mix of positive and negative values, bubble down first
        // check: https://github.com/moment/moment/issues/2166
        if (!((milliseconds >= 0 && days >= 0 && months >= 0) ||
                (milliseconds <= 0 && days <= 0 && months <= 0))) {
            milliseconds += absCeil(monthsToDays(months) + days) * 864e5;
            days = 0;
            months = 0;
        }

        // The following code bubbles up values, see the tests for
        // examples of what that means.
        data.milliseconds = milliseconds % 1000;

        seconds           = absFloor(milliseconds / 1000);
        data.seconds      = seconds % 60;

        minutes           = absFloor(seconds / 60);
        data.minutes      = minutes % 60;

        hours             = absFloor(minutes / 60);
        data.hours        = hours % 24;

        days += absFloor(hours / 24);

        // convert days to months
        monthsFromDays = absFloor(daysToMonths(days));
        months += monthsFromDays;
        days -= absCeil(monthsToDays(monthsFromDays));

        // 12 months -> 1 year
        years = absFloor(months / 12);
        months %= 12;

        data.days   = days;
        data.months = months;
        data.years  = years;

        return this;
    }

    function daysToMonths (days) {
        // 400 years have 146097 days (taking into account leap year rules)
        // 400 years have 12 months === 4800
        return days * 4800 / 146097;
    }

    function monthsToDays (months) {
        // the reverse of daysToMonths
        return months * 146097 / 4800;
    }

    function as (units) {
        var days;
        var months;
        var milliseconds = this._milliseconds;

        units = normalizeUnits(units);

        if (units === 'month' || units === 'year') {
            days   = this._days   + milliseconds / 864e5;
            months = this._months + daysToMonths(days);
            return units === 'month' ? months : months / 12;
        } else {
            // handle milliseconds separately because of floating point math errors (issue #1867)
            days = this._days + Math.round(monthsToDays(this._months));
            switch (units) {
                case 'week'   : return days / 7     + milliseconds / 6048e5;
                case 'day'    : return days         + milliseconds / 864e5;
                case 'hour'   : return days * 24    + milliseconds / 36e5;
                case 'minute' : return days * 1440  + milliseconds / 6e4;
                case 'second' : return days * 86400 + milliseconds / 1000;
                // Math.floor prevents floating point math errors here
                case 'millisecond': return Math.floor(days * 864e5) + milliseconds;
                default: throw new Error('Unknown unit ' + units);
            }
        }
    }

    // TODO: Use this.as('ms')?
    function duration_as__valueOf () {
        return (
            this._milliseconds +
            this._days * 864e5 +
            (this._months % 12) * 2592e6 +
            toInt(this._months / 12) * 31536e6
        );
    }

    function makeAs (alias) {
        return function () {
            return this.as(alias);
        };
    }

    var asMilliseconds = makeAs('ms');
    var asSeconds      = makeAs('s');
    var asMinutes      = makeAs('m');
    var asHours        = makeAs('h');
    var asDays         = makeAs('d');
    var asWeeks        = makeAs('w');
    var asMonths       = makeAs('M');
    var asYears        = makeAs('y');

    function duration_get__get (units) {
        units = normalizeUnits(units);
        return this[units + 's']();
    }

    function makeGetter(name) {
        return function () {
            return this._data[name];
        };
    }

    var milliseconds = makeGetter('milliseconds');
    var seconds      = makeGetter('seconds');
    var minutes      = makeGetter('minutes');
    var hours        = makeGetter('hours');
    var days         = makeGetter('days');
    var months       = makeGetter('months');
    var years        = makeGetter('years');

    function weeks () {
        return absFloor(this.days() / 7);
    }

    var round = Math.round;
    var thresholds = {
        s: 45,  // seconds to minute
        m: 45,  // minutes to hour
        h: 22,  // hours to day
        d: 26,  // days to month
        M: 11   // months to year
    };

    // helper function for moment.fn.from, moment.fn.fromNow, and moment.duration.fn.humanize
    function substituteTimeAgo(string, number, withoutSuffix, isFuture, locale) {
        return locale.relativeTime(number || 1, !!withoutSuffix, string, isFuture);
    }

    function duration_humanize__relativeTime (posNegDuration, withoutSuffix, locale) {
        var duration = create__createDuration(posNegDuration).abs();
        var seconds  = round(duration.as('s'));
        var minutes  = round(duration.as('m'));
        var hours    = round(duration.as('h'));
        var days     = round(duration.as('d'));
        var months   = round(duration.as('M'));
        var years    = round(duration.as('y'));

        var a = seconds < thresholds.s && ['s', seconds]  ||
                minutes <= 1           && ['m']           ||
                minutes < thresholds.m && ['mm', minutes] ||
                hours   <= 1           && ['h']           ||
                hours   < thresholds.h && ['hh', hours]   ||
                days    <= 1           && ['d']           ||
                days    < thresholds.d && ['dd', days]    ||
                months  <= 1           && ['M']           ||
                months  < thresholds.M && ['MM', months]  ||
                years   <= 1           && ['y']           || ['yy', years];

        a[2] = withoutSuffix;
        a[3] = +posNegDuration > 0;
        a[4] = locale;
        return substituteTimeAgo.apply(null, a);
    }

    // This function allows you to set a threshold for relative time strings
    function duration_humanize__getSetRelativeTimeThreshold (threshold, limit) {
        if (thresholds[threshold] === undefined) {
            return false;
        }
        if (limit === undefined) {
            return thresholds[threshold];
        }
        thresholds[threshold] = limit;
        return true;
    }

    function humanize (withSuffix) {
        var locale = this.localeData();
        var output = duration_humanize__relativeTime(this, !withSuffix, locale);

        if (withSuffix) {
            output = locale.pastFuture(+this, output);
        }

        return locale.postformat(output);
    }

    var iso_string__abs = Math.abs;

    function iso_string__toISOString() {
        // for ISO strings we do not use the normal bubbling rules:
        //  * milliseconds bubble up until they become hours
        //  * days do not bubble at all
        //  * months bubble up until they become years
        // This is because there is no context-free conversion between hours and days
        // (think of clock changes)
        // and also not between days and months (28-31 days per month)
        var seconds = iso_string__abs(this._milliseconds) / 1000;
        var days         = iso_string__abs(this._days);
        var months       = iso_string__abs(this._months);
        var minutes, hours, years;

        // 3600 seconds -> 60 minutes -> 1 hour
        minutes           = absFloor(seconds / 60);
        hours             = absFloor(minutes / 60);
        seconds %= 60;
        minutes %= 60;

        // 12 months -> 1 year
        years  = absFloor(months / 12);
        months %= 12;


        // inspired by https://github.com/dordille/moment-isoduration/blob/master/moment.isoduration.js
        var Y = years;
        var M = months;
        var D = days;
        var h = hours;
        var m = minutes;
        var s = seconds;
        var total = this.asSeconds();

        if (!total) {
            // this is the same as C#'s (Noda) and python (isodate)...
            // but not other JS (goog.date)
            return 'P0D';
        }

        return (total < 0 ? '-' : '') +
            'P' +
            (Y ? Y + 'Y' : '') +
            (M ? M + 'M' : '') +
            (D ? D + 'D' : '') +
            ((h || m || s) ? 'T' : '') +
            (h ? h + 'H' : '') +
            (m ? m + 'M' : '') +
            (s ? s + 'S' : '');
    }

    var duration_prototype__proto = Duration.prototype;

    duration_prototype__proto.abs            = duration_abs__abs;
    duration_prototype__proto.add            = duration_add_subtract__add;
    duration_prototype__proto.subtract       = duration_add_subtract__subtract;
    duration_prototype__proto.as             = as;
    duration_prototype__proto.asMilliseconds = asMilliseconds;
    duration_prototype__proto.asSeconds      = asSeconds;
    duration_prototype__proto.asMinutes      = asMinutes;
    duration_prototype__proto.asHours        = asHours;
    duration_prototype__proto.asDays         = asDays;
    duration_prototype__proto.asWeeks        = asWeeks;
    duration_prototype__proto.asMonths       = asMonths;
    duration_prototype__proto.asYears        = asYears;
    duration_prototype__proto.valueOf        = duration_as__valueOf;
    duration_prototype__proto._bubble        = bubble;
    duration_prototype__proto.get            = duration_get__get;
    duration_prototype__proto.milliseconds   = milliseconds;
    duration_prototype__proto.seconds        = seconds;
    duration_prototype__proto.minutes        = minutes;
    duration_prototype__proto.hours          = hours;
    duration_prototype__proto.days           = days;
    duration_prototype__proto.weeks          = weeks;
    duration_prototype__proto.months         = months;
    duration_prototype__proto.years          = years;
    duration_prototype__proto.humanize       = humanize;
    duration_prototype__proto.toISOString    = iso_string__toISOString;
    duration_prototype__proto.toString       = iso_string__toISOString;
    duration_prototype__proto.toJSON         = iso_string__toISOString;
    duration_prototype__proto.locale         = locale;
    duration_prototype__proto.localeData     = localeData;

    // Deprecations
    duration_prototype__proto.toIsoString = deprecate('toIsoString() is deprecated. Please use toISOString() instead (notice the capitals)', iso_string__toISOString);
    duration_prototype__proto.lang = lang;

    // Side effect imports

    // FORMATTING

    addFormatToken('X', 0, 0, 'unix');
    addFormatToken('x', 0, 0, 'valueOf');

    // PARSING

    addRegexToken('x', matchSigned);
    addRegexToken('X', matchTimestamp);
    addParseToken('X', function (input, array, config) {
        config._d = new Date(parseFloat(input, 10) * 1000);
    });
    addParseToken('x', function (input, array, config) {
        config._d = new Date(toInt(input));
    });

    // Side effect imports


    utils_hooks__hooks.version = '2.13.0';

    setHookCallback(local__createLocal);

    utils_hooks__hooks.fn                    = momentPrototype;
    utils_hooks__hooks.min                   = min;
    utils_hooks__hooks.max                   = max;
    utils_hooks__hooks.now                   = now;
    utils_hooks__hooks.utc                   = create_utc__createUTC;
    utils_hooks__hooks.unix                  = moment__createUnix;
    utils_hooks__hooks.months                = lists__listMonths;
    utils_hooks__hooks.isDate                = isDate;
    utils_hooks__hooks.locale                = locale_locales__getSetGlobalLocale;
    utils_hooks__hooks.invalid               = valid__createInvalid;
    utils_hooks__hooks.duration              = create__createDuration;
    utils_hooks__hooks.isMoment              = isMoment;
    utils_hooks__hooks.weekdays              = lists__listWeekdays;
    utils_hooks__hooks.parseZone             = moment__createInZone;
    utils_hooks__hooks.localeData            = locale_locales__getLocale;
    utils_hooks__hooks.isDuration            = isDuration;
    utils_hooks__hooks.monthsShort           = lists__listMonthsShort;
    utils_hooks__hooks.weekdaysMin           = lists__listWeekdaysMin;
    utils_hooks__hooks.defineLocale          = defineLocale;
    utils_hooks__hooks.updateLocale          = updateLocale;
    utils_hooks__hooks.locales               = locale_locales__listLocales;
    utils_hooks__hooks.weekdaysShort         = lists__listWeekdaysShort;
    utils_hooks__hooks.normalizeUnits        = normalizeUnits;
    utils_hooks__hooks.relativeTimeThreshold = duration_humanize__getSetRelativeTimeThreshold;
    utils_hooks__hooks.prototype             = momentPrototype;

    var _moment = utils_hooks__hooks;

    return _moment;

}));;
/* angular-moment.js / v1.0.0-beta.6 / (c) 2013, 2014, 2015, 2016 Uri Shaked / MIT Licence */

'format amd';
/* global define */

(function () {
	'use strict';

	function isUndefinedOrNull(val) {
		return angular.isUndefined(val) || val === null;
	}

	function requireMoment() {
		try {
			return require('moment'); // Using nw.js or browserify?
		} catch (e) {
			throw new Error('Please install moment via npm. Please reference to: https://github.com/urish/angular-moment'); // Add wiki/troubleshooting section?
		}
	}

	function angularMoment(angular, moment) {

		if(typeof moment === 'undefined') {
			if(typeof require === 'function') {
				moment = requireMoment();
			}else{
				throw new Error('Moment cannot be found by angular-moment! Please reference to: https://github.com/urish/angular-moment'); // Add wiki/troubleshooting section?
			}
		}

		/**
		 * @ngdoc overview
		 * @name angularMoment
		 *
		 * @description
		 * angularMoment module provides moment.js functionality for angular.js apps.
		 */
		return angular.module('angularMoment', [])

		/**
		 * @ngdoc object
		 * @name angularMoment.config:angularMomentConfig
		 *
		 * @description
		 * Common configuration of the angularMoment module
		 */
			.constant('angularMomentConfig', {
				/**
				 * @ngdoc property
				 * @name angularMoment.config.angularMomentConfig#preprocess
				 * @propertyOf angularMoment.config:angularMomentConfig
				 * @returns {function} A preprocessor function that will be applied on all incoming dates
				 *
				 * @description
				 * Defines a preprocessor function to apply on all input dates (e.g. the input of `am-time-ago`,
				 * `amCalendar`, etc.). The function must return a `moment` object.
				 *
				 * @example
				 *   // Causes angular-moment to always treat the input values as unix timestamps
				 *   angularMomentConfig.preprocess = function(value) {
				 * 	   return moment.unix(value);
				 *   }
				 */
				preprocess: null,

				/**
				 * @ngdoc property
				 * @name angularMoment.config.angularMomentConfig#timezone
				 * @propertyOf angularMoment.config:angularMomentConfig
				 * @returns {string} The default timezone
				 *
				 * @description
				 * The default timezone (e.g. 'Europe/London'). Empty string by default (does not apply
				 * any timezone shift).
				 *
				 * NOTE: This option requires moment-timezone >= 0.3.0.
				 */
				timezone: null,

				/**
				 * @ngdoc property
				 * @name angularMoment.config.angularMomentConfig#format
				 * @propertyOf angularMoment.config:angularMomentConfig
				 * @returns {string} The pre-conversion format of the date
				 *
				 * @description
				 * Specify the format of the input date. Essentially it's a
				 * default and saves you from specifying a format in every
				 * element. Overridden by element attr. Null by default.
				 */
				format: null,

				/**
				 * @ngdoc property
				 * @name angularMoment.config.angularMomentConfig#statefulFilters
				 * @propertyOf angularMoment.config:angularMomentConfig
				 * @returns {boolean} Whether angular-moment filters should be stateless (or not)
				 *
				 * @description
				 * Specifies whether the filters included with angular-moment are stateful.
				 * Stateful filters will automatically re-evaluate whenever you change the timezone
				 * or locale settings, but may negatively impact performance. true by default.
				 */
				statefulFilters: true
			})

		/**
		 * @ngdoc object
		 * @name angularMoment.object:moment
		 *
		 * @description
		 * moment global (as provided by the moment.js library)
		 */
			.constant('moment', moment)

		/**
		 * @ngdoc object
		 * @name angularMoment.config:amTimeAgoConfig
		 * @module angularMoment
		 *
		 * @description
		 * configuration specific to the amTimeAgo directive
		 */
			.constant('amTimeAgoConfig', {
				/**
				 * @ngdoc property
				 * @name angularMoment.config.amTimeAgoConfig#withoutSuffix
				 * @propertyOf angularMoment.config:amTimeAgoConfig
				 * @returns {boolean} Whether to include a suffix in am-time-ago directive
				 *
				 * @description
				 * Defaults to false.
				 */
				withoutSuffix: false,

				/**
				 * @ngdoc property
				 * @name angularMoment.config.amTimeAgoConfig#serverTime
				 * @propertyOf angularMoment.config:amTimeAgoConfig
				 * @returns {number} Server time in milliseconds since the epoch
				 *
				 * @description
				 * If set, time ago will be calculated relative to the given value.
				 * If null, local time will be used. Defaults to null.
				 */
				serverTime: null,

				/**
				 * @ngdoc property
				 * @name angularMoment.config.amTimeAgoConfig#titleFormat
				 * @propertyOf angularMoment.config:amTimeAgoConfig
				 * @returns {string} The format of the date to be displayed in the title of the element. If null,
				 *        the directive set the title of the element.
				 *
				 * @description
				 * The format of the date used for the title of the element. null by default.
				 */
				titleFormat: null,

				/**
				 * @ngdoc property
				 * @name angularMoment.config.amTimeAgoConfig#fullDateThreshold
				 * @propertyOf angularMoment.config:amTimeAgoConfig
				 * @returns {number} The minimum number of days for showing a full date instead of relative time
				 *
				 * @description
				 * The threshold for displaying a full date. The default is null, which means the date will always
				 * be relative, and full date will never be displayed.
				 */
				fullDateThreshold: null,

				/**
				 * @ngdoc property
				 * @name angularMoment.config.amTimeAgoConfig#fullDateFormat
				 * @propertyOf angularMoment.config:amTimeAgoConfig
				 * @returns {string} The format to use when displaying a full date.
				 *
				 * @description
				 * Specify the format of the date when displayed as full date. null by default.
				 */
				fullDateFormat: null,

				fullDateThresholdUnit: 'day'
			})

		/**
		 * @ngdoc directive
		 * @name angularMoment.directive:amTimeAgo
		 * @module angularMoment
		 *
		 * @restrict A
		 */
			.directive('amTimeAgo', ['$window', 'moment', 'amMoment', 'amTimeAgoConfig', function ($window, moment, amMoment, amTimeAgoConfig) {

				return function (scope, element, attr) {
					var activeTimeout = null;
					var currentValue;
					var withoutSuffix = amTimeAgoConfig.withoutSuffix;
					var titleFormat = amTimeAgoConfig.titleFormat;
					var fullDateThreshold = amTimeAgoConfig.fullDateThreshold;
					var fullDateFormat = amTimeAgoConfig.fullDateFormat;
					var fullDateThresholdUnit = amTimeAgoConfig.fullDateThresholdUnit;

					var localDate = new Date().getTime();
					var modelName = attr.amTimeAgo;
					var currentFrom;
					var isTimeElement = ('TIME' === element[0].nodeName.toUpperCase());
					var setTitleTime = !element.attr('title');

					function getNow() {
						var now;
						if (currentFrom) {
							now = currentFrom;
						} else if (amTimeAgoConfig.serverTime) {
							var localNow = new Date().getTime();
							var nowMillis = localNow - localDate + amTimeAgoConfig.serverTime;
							now = moment(nowMillis);
						}
						else {
							now = moment();
						}
						return now;
					}

					function cancelTimer() {
						if (activeTimeout) {
							$window.clearTimeout(activeTimeout);
							activeTimeout = null;
						}
					}

					function updateTime(momentInstance) {
						var timeAgo = getNow().diff(momentInstance, fullDateThresholdUnit);
						var showFullDate = fullDateThreshold && timeAgo >= fullDateThreshold;

						if (showFullDate) {
							element.text(momentInstance.format(fullDateFormat));
						} else {
							element.text(momentInstance.from(getNow(), withoutSuffix));
						}

						if (titleFormat && setTitleTime) {
							element.attr('title', momentInstance.format(titleFormat));
						}

						if (!showFullDate) {
							var howOld = Math.abs(getNow().diff(momentInstance, 'minute'));
							var secondsUntilUpdate = 3600;
							if (howOld < 1) {
								secondsUntilUpdate = 1;
							} else if (howOld < 60) {
								secondsUntilUpdate = 30;
							} else if (howOld < 180) {
								secondsUntilUpdate = 300;
							}

							activeTimeout = $window.setTimeout(function () {
								updateTime(momentInstance);
							}, secondsUntilUpdate * 1000);
						}
					}

					function updateDateTimeAttr(value) {
						if (isTimeElement) {
							element.attr('datetime', value);
						}
					}

					function updateMoment() {
						cancelTimer();
						if (currentValue) {
							var momentValue = amMoment.preprocessDate(currentValue);
							updateTime(momentValue);
							updateDateTimeAttr(momentValue.toISOString());
						}
					}

					scope.$watch(modelName, function (value) {
						if (isUndefinedOrNull(value) || (value === '')) {
							cancelTimer();
							if (currentValue) {
								element.text('');
								updateDateTimeAttr('');
								currentValue = null;
							}
							return;
						}

						currentValue = value;
						updateMoment();
					});

					if (angular.isDefined(attr.amFrom)) {
						scope.$watch(attr.amFrom, function (value) {
							if (isUndefinedOrNull(value) || (value === '')) {
								currentFrom = null;
							} else {
								currentFrom = moment(value);
							}
							updateMoment();
						});
					}

					if (angular.isDefined(attr.amWithoutSuffix)) {
						scope.$watch(attr.amWithoutSuffix, function (value) {
							if (typeof value === 'boolean') {
								withoutSuffix = value;
								updateMoment();
							} else {
								withoutSuffix = amTimeAgoConfig.withoutSuffix;
							}
						});
					}

					attr.$observe('amFullDateThreshold', function (newValue) {
						fullDateThreshold = newValue;
						updateMoment();
					});

					attr.$observe('amFullDateFormat', function (newValue) {
						fullDateFormat = newValue;
						updateMoment();
					});

					attr.$observe('amFullDateThresholdUnit', function (newValue) {
						fullDateThresholdUnit = newValue;
						updateMoment();
					});

					scope.$on('$destroy', function () {
						cancelTimer();
					});

					scope.$on('amMoment:localeChanged', function () {
						updateMoment();
					});
				};
			}])

		/**
		 * @ngdoc service
		 * @name angularMoment.service.amMoment
		 * @module angularMoment
		 */
			.service('amMoment', ['moment', '$rootScope', '$log', 'angularMomentConfig', function (moment, $rootScope, $log, angularMomentConfig) {
				var defaultTimezone = null;

				/**
				 * @ngdoc function
				 * @name angularMoment.service.amMoment#changeLocale
				 * @methodOf angularMoment.service.amMoment
				 *
				 * @description
				 * Changes the locale for moment.js and updates all the am-time-ago directive instances
				 * with the new locale. Also broadcasts an `amMoment:localeChanged` event on $rootScope.
				 *
				 * @param {string} locale Locale code (e.g. en, es, ru, pt-br, etc.)
				 * @param {object} customization object of locale strings to override
				 */
				this.changeLocale = function (locale, customization) {
					var result = moment.locale(locale, customization);
					if (angular.isDefined(locale)) {
						$rootScope.$broadcast('amMoment:localeChanged');

					}
					return result;
				};

				/**
				 * @ngdoc function
				 * @name angularMoment.service.amMoment#changeTimezone
				 * @methodOf angularMoment.service.amMoment
				 *
				 * @description
				 * Changes the default timezone for amCalendar, amDateFormat and amTimeAgo. Also broadcasts an
				 * `amMoment:timezoneChanged` event on $rootScope.
				 *
				 * Note: this method works only if moment-timezone > 0.3.0 is loaded
				 *
				 * @param {string} timezone Timezone name (e.g. UTC)
				 */
				this.changeTimezone = function (timezone) {
					if (moment.tz && moment.tz.setDefault) {
						moment.tz.setDefault(timezone);
						$rootScope.$broadcast('amMoment:timezoneChanged');
					} else {
						$log.warn('angular-moment: changeTimezone() works only with moment-timezone.js v0.3.0 or greater.');
					}
					angularMomentConfig.timezone = timezone;
					defaultTimezone = timezone;
				};

				/**
				 * @ngdoc function
				 * @name angularMoment.service.amMoment#preprocessDate
				 * @methodOf angularMoment.service.amMoment
				 *
				 * @description
				 * Preprocess a given value and convert it into a Moment instance appropriate for use in the
				 * am-time-ago directive and the filters. The behavior of this function can be overriden by
				 * setting `angularMomentConfig.preprocess`.
				 *
				 * @param {*} value The value to be preprocessed
				 * @return {Moment} A `moment` object
				 */
				this.preprocessDate = function (value) {
					// Configure the default timezone if needed
					if (defaultTimezone !== angularMomentConfig.timezone) {
						this.changeTimezone(angularMomentConfig.timezone);
					}

					if (angularMomentConfig.preprocess) {
						return angularMomentConfig.preprocess(value);
					}

					if (!isNaN(parseFloat(value)) && isFinite(value)) {
						// Milliseconds since the epoch
						return moment(parseInt(value, 10));
					}

					// else just returns the value as-is.
					return moment(value);
				};
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amParse
		 * @module angularMoment
		 */
			.filter('amParse', ['moment', function (moment) {
				return function (value, format) {
					return moment(value, format);
				};
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amFromUnix
		 * @module angularMoment
		 */
			.filter('amFromUnix', ['moment', function (moment) {
				return function (value) {
					return moment.unix(value);
				};
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amUtc
		 * @module angularMoment
		 */
			.filter('amUtc', ['moment', function (moment) {
				return function (value) {
					return moment.utc(value);
				};
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amUtcOffset
		 * @module angularMoment
		 *
		 * @description
		 * Adds a UTC offset to the given timezone object. The offset can be a number of minutes, or a string such as
		 * '+0300', '-0300' or 'Z'.
		 */
			.filter('amUtcOffset', ['amMoment', function (amMoment) {
				function amUtcOffset(value, offset) {
					return amMoment.preprocessDate(value).utcOffset(offset);
				}

				return amUtcOffset;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amLocal
		 * @module angularMoment
		 */
			.filter('amLocal', ['moment', function (moment) {
				return function (value) {
					return moment.isMoment(value) ? value.local() : null;
				};
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amTimezone
		 * @module angularMoment
		 *
		 * @description
		 * Apply a timezone onto a given moment object, e.g. 'America/Phoenix').
		 *
		 * You need to include moment-timezone.js for timezone support.
		 */
			.filter('amTimezone', ['amMoment', 'angularMomentConfig', '$log', function (amMoment, angularMomentConfig, $log) {
				function amTimezone(value, timezone) {
					var aMoment = amMoment.preprocessDate(value);

					if (!timezone) {
						return aMoment;
					}

					if (aMoment.tz) {
						return aMoment.tz(timezone);
					} else {
						$log.warn('angular-moment: named timezone specified but moment.tz() is undefined. Did you forget to include moment-timezone.js ?');
						return aMoment;
					}
				}

				return amTimezone;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amCalendar
		 * @module angularMoment
		 */
			.filter('amCalendar', ['moment', 'amMoment', 'angularMomentConfig', function (moment, amMoment, angularMomentConfig) {
				function amCalendarFilter(value, referenceTime, formats) {
					if (isUndefinedOrNull(value)) {
						return '';
					}

					var date = amMoment.preprocessDate(value);
					return date.isValid() ? date.calendar(referenceTime, formats) : '';
				}

				// Since AngularJS 1.3, filters have to explicitly define being stateful
				// (this is no longer the default).
				amCalendarFilter.$stateful = angularMomentConfig.statefulFilters;

				return amCalendarFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amDifference
		 * @module angularMoment
		 */
			.filter('amDifference', ['moment', 'amMoment', 'angularMomentConfig', function (moment, amMoment, angularMomentConfig) {
				function amDifferenceFilter(value, otherValue, unit, usePrecision) {
					if (isUndefinedOrNull(value)) {
						return '';
					}

					var date = amMoment.preprocessDate(value);
					var date2 = !isUndefinedOrNull(otherValue) ? amMoment.preprocessDate(otherValue) : moment();

					if (!date.isValid() || !date2.isValid()) {
						return '';
					}

					return date.diff(date2, unit, usePrecision);
				}

				amDifferenceFilter.$stateful = angularMomentConfig.statefulFilters;

				return amDifferenceFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amDateFormat
		 * @module angularMoment
		 * @function
		 */
			.filter('amDateFormat', ['moment', 'amMoment', 'angularMomentConfig', function (moment, amMoment, angularMomentConfig) {
				function amDateFormatFilter(value, format) {
					if (isUndefinedOrNull(value)) {
						return '';
					}

					var date = amMoment.preprocessDate(value);
					if (!date.isValid()) {
						return '';
					}

					return date.format(format);
				}

				amDateFormatFilter.$stateful = angularMomentConfig.statefulFilters;

				return amDateFormatFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amDurationFormat
		 * @module angularMoment
		 * @function
		 */
			.filter('amDurationFormat', ['moment', 'angularMomentConfig', function (moment, angularMomentConfig) {
				function amDurationFormatFilter(value, format, suffix) {
					if (isUndefinedOrNull(value)) {
						return '';
					}

					return moment.duration(value, format).humanize(suffix);
				}

				amDurationFormatFilter.$stateful = angularMomentConfig.statefulFilters;

				return amDurationFormatFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amTimeAgo
		 * @module angularMoment
		 * @function
		 */
			.filter('amTimeAgo', ['moment', 'amMoment', 'angularMomentConfig', function (moment, amMoment, angularMomentConfig) {
				function amTimeAgoFilter(value, suffix, from) {
					var date, dateFrom;

					if (isUndefinedOrNull(value)) {
						return '';
					}

					value = amMoment.preprocessDate(value);
					date = moment(value);
					if (!date.isValid()) {
						return '';
					}

					dateFrom = moment(from);
					if (!isUndefinedOrNull(from) && dateFrom.isValid()) {
						return date.from(dateFrom, suffix);
					}

					return date.fromNow(suffix);
				}

				amTimeAgoFilter.$stateful = angularMomentConfig.statefulFilters;

				return amTimeAgoFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amSubtract
		 * @module angularMoment
		 * @function
		 */
			.filter('amSubtract', ['moment', 'angularMomentConfig', function (moment, angularMomentConfig) {
				function amSubtractFilter(value, amount, type) {

					if (isUndefinedOrNull(value)) {
						return '';
					}

					return moment(value).subtract(parseInt(amount, 10), type);
				}

				amSubtractFilter.$stateful = angularMomentConfig.statefulFilters;

				return amSubtractFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amAdd
		 * @module angularMoment
		 * @function
		 */
			.filter('amAdd', ['moment', 'angularMomentConfig', function (moment, angularMomentConfig) {
				function amAddFilter(value, amount, type) {

					if (isUndefinedOrNull(value)) {
						return '';
					}

					return moment(value).add(parseInt(amount, 10), type);
				}

				amAddFilter.$stateful = angularMomentConfig.statefulFilters;

				return amAddFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amStartOf
		 * @module angularMoment
		 * @function
		 */
			.filter('amStartOf', ['moment', 'angularMomentConfig', function (moment, angularMomentConfig) {
				function amStartOfFilter(value, type) {

					if (isUndefinedOrNull(value)) {
						return '';
					}

					return moment(value).startOf(type);
				}

				amStartOfFilter.$stateful = angularMomentConfig.statefulFilters;

				return amStartOfFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amEndOf
		 * @module angularMoment
		 * @function
		 */
			.filter('amEndOf', ['moment', 'angularMomentConfig', function (moment, angularMomentConfig) {
				function amEndOfFilter(value, type) {

					if (isUndefinedOrNull(value)) {
						return '';
					}

					return moment(value).endOf(type);
				}

				amEndOfFilter.$stateful = angularMomentConfig.statefulFilters;

				return amEndOfFilter;
 			}]);
	}

	if (typeof define === 'function' && define.amd) {
		define(['angular', 'moment'], angularMoment);
	} else if (typeof module !== 'undefined' && module && module.exports) {
		angularMoment(require('angular'), require('moment'));
		module.exports = 'angularMoment';
	} else {
		angularMoment(angular, (typeof global !== 'undefined' ? global : window).moment);
	}
})();
;
/*
 * Hamster.js v1.1.2
 * (c) 2013 Monospaced http://monospaced.com
 * License: MIT
 */

(function(window, document){
'use strict';

/**
 * Hamster
 * use this to create instances
 * @returns {Hamster.Instance}
 * @constructor
 */
var Hamster = function(element) {
  return new Hamster.Instance(element);
};

// default event name
Hamster.SUPPORT = 'wheel';

// default DOM methods
Hamster.ADD_EVENT = 'addEventListener';
Hamster.REMOVE_EVENT = 'removeEventListener';
Hamster.PREFIX = '';

// until browser inconsistencies have been fixed...
Hamster.READY = false;

Hamster.Instance = function(element){
  if (!Hamster.READY) {
    // fix browser inconsistencies
    Hamster.normalise.browser();

    // Hamster is ready...!
    Hamster.READY = true;
  }

  this.element = element;

  // store attached event handlers
  this.handlers = [];

  // return instance
  return this;
};

/**
 * create new hamster instance
 * all methods should return the instance itself, so it is chainable.
 * @param   {HTMLElement}       element
 * @returns {Hamster.Instance}
 * @constructor
 */
Hamster.Instance.prototype = {
  /**
   * bind events to the instance
   * @param   {Function}    handler
   * @param   {Boolean}     useCapture
   * @returns {Hamster.Instance}
   */
  wheel: function onEvent(handler, useCapture){
    Hamster.event.add(this, Hamster.SUPPORT, handler, useCapture);

    // handle MozMousePixelScroll in older Firefox
    if (Hamster.SUPPORT === 'DOMMouseScroll') {
      Hamster.event.add(this, 'MozMousePixelScroll', handler, useCapture);
    }

    return this;
  },

  /**
   * unbind events to the instance
   * @param   {Function}    handler
   * @param   {Boolean}     useCapture
   * @returns {Hamster.Instance}
   */
  unwheel: function offEvent(handler, useCapture){
    // if no handler argument,
    // unbind the last bound handler (if exists)
    if (handler === undefined && (handler = this.handlers.slice(-1)[0])) {
      handler = handler.original;
    }

    Hamster.event.remove(this, Hamster.SUPPORT, handler, useCapture);

    // handle MozMousePixelScroll in older Firefox
    if (Hamster.SUPPORT === 'DOMMouseScroll') {
      Hamster.event.remove(this, 'MozMousePixelScroll', handler, useCapture);
    }

    return this;
  }
};

Hamster.event = {
  /**
   * cross-browser 'addWheelListener'
   * @param   {Instance}    hamster
   * @param   {String}      eventName
   * @param   {Function}    handler
   * @param   {Boolean}     useCapture
   */
  add: function add(hamster, eventName, handler, useCapture){
    // store the original handler
    var originalHandler = handler;

    // redefine the handler
    handler = function(originalEvent){

      if (!originalEvent) {
        originalEvent = window.event;
      }

      // create a normalised event object,
      // and normalise "deltas" of the mouse wheel
      var event = Hamster.normalise.event(originalEvent),
          delta = Hamster.normalise.delta(originalEvent);

      // fire the original handler with normalised arguments
      return originalHandler(event, delta[0], delta[1], delta[2]);

    };

    // cross-browser addEventListener
    hamster.element[Hamster.ADD_EVENT](Hamster.PREFIX + eventName, handler, useCapture || false);

    // store original and normalised handlers on the instance
    hamster.handlers.push({
      original: originalHandler,
      normalised: handler
    });
  },

  /**
   * removeWheelListener
   * @param   {Instance}    hamster
   * @param   {String}      eventName
   * @param   {Function}    handler
   * @param   {Boolean}     useCapture
   */
  remove: function remove(hamster, eventName, handler, useCapture){
    // find the normalised handler on the instance
    var originalHandler = handler,
        lookup = {},
        handlers;
    for (var i = 0, len = hamster.handlers.length; i < len; ++i) {
      lookup[hamster.handlers[i].original] = hamster.handlers[i];
    }
    handlers = lookup[originalHandler];
    handler = handlers.normalised;

    // cross-browser removeEventListener
    hamster.element[Hamster.REMOVE_EVENT](Hamster.PREFIX + eventName, handler, useCapture || false);

    // remove original and normalised handlers from the instance
    for (var h in hamster.handlers) {
      if (hamster.handlers[h] == handlers) {
        hamster.handlers.splice(h, 1);
        break;
      }
    }
  }
};

/**
 * these hold the lowest deltas,
 * used to normalise the delta values
 * @type {Number}
 */
var lowestDelta,
    lowestDeltaXY;

Hamster.normalise = {
  /**
   * fix browser inconsistencies
   */
  browser: function normaliseBrowser(){
    // detect deprecated wheel events
    if (!('onwheel' in document || document.documentMode >= 9)) {
      Hamster.SUPPORT = document.onmousewheel !== undefined ?
                        'mousewheel' : // webkit and IE < 9 support at least "mousewheel"
                        'DOMMouseScroll'; // assume remaining browsers are older Firefox
    }

    // detect deprecated event model
    if (!window.addEventListener) {
      // assume IE < 9
      Hamster.ADD_EVENT = 'attachEvent';
      Hamster.REMOVE_EVENT = 'detachEvent';
      Hamster.PREFIX = 'on';
    }

  },

  /**
   * create a normalised event object
   * @param   {Function}    originalEvent
   * @returns {Object}      event
   */
   event: function normaliseEvent(originalEvent){
    var event = {
          // keep a reference to the original event object
          originalEvent: originalEvent,
          target: originalEvent.target || originalEvent.srcElement,
          type: 'wheel',
          deltaMode: originalEvent.type === 'MozMousePixelScroll' ? 0 : 1,
          deltaX: 0,
          deltaZ: 0,
          preventDefault: function(){
            if (originalEvent.preventDefault) {
              originalEvent.preventDefault();
            } else {
              originalEvent.returnValue = false;
            }
          },
          stopPropagation: function(){
            if (originalEvent.stopPropagation) {
              originalEvent.stopPropagation();
            } else {
              originalEvent.cancelBubble = false;
            }
          }
        };

    // calculate deltaY (and deltaX) according to the event

    // 'mousewheel'
    if (originalEvent.wheelDelta) {
      event.deltaY = - 1/40 * originalEvent.wheelDelta;
    }
    // webkit
    if (originalEvent.wheelDeltaX) {
      event.deltaX = - 1/40 * originalEvent.wheelDeltaX;
    }

    // 'DomMouseScroll'
    if (originalEvent.detail) {
      event.deltaY = originalEvent.detail;
    }

    return event;
  },

  /**
   * normalise 'deltas' of the mouse wheel
   * @param   {Function}    originalEvent
   * @returns {Array}       deltas
   */
  delta: function normaliseDelta(originalEvent){
    var delta = 0,
      deltaX = 0,
      deltaY = 0,
      absDelta = 0,
      absDeltaXY = 0,
      fn;

    // normalise deltas according to the event

    // 'wheel' event
    if (originalEvent.deltaY) {
      deltaY = originalEvent.deltaY * -1;
      delta  = deltaY;
    }
    if (originalEvent.deltaX) {
      deltaX = originalEvent.deltaX;
      delta  = deltaX * -1;
    }

    // 'mousewheel' event
    if (originalEvent.wheelDelta) {
      delta = originalEvent.wheelDelta;
    }
    // webkit
    if (originalEvent.wheelDeltaY) {
      deltaY = originalEvent.wheelDeltaY;
    }
    if (originalEvent.wheelDeltaX) {
      deltaX = originalEvent.wheelDeltaX * -1;
    }

    // 'DomMouseScroll' event
    if (originalEvent.detail) {
      delta = originalEvent.detail * -1;
    }

    // Don't return NaN
    if (delta === 0) {
      return [0, 0, 0];
    }

    // look for lowest delta to normalize the delta values
    absDelta = Math.abs(delta);
    if (!lowestDelta || absDelta < lowestDelta) {
      lowestDelta = absDelta;
    }
    absDeltaXY = Math.max(Math.abs(deltaY), Math.abs(deltaX));
    if (!lowestDeltaXY || absDeltaXY < lowestDeltaXY) {
      lowestDeltaXY = absDeltaXY;
    }

    // convert deltas to whole numbers
    fn = delta > 0 ? 'floor' : 'ceil';
    delta  = Math[fn](delta / lowestDelta);
    deltaX = Math[fn](deltaX / lowestDeltaXY);
    deltaY = Math[fn](deltaY / lowestDeltaXY);

    return [delta, deltaX, deltaY];
  }
};

if (typeof window.define === 'function' && window.define.amd) {
  // AMD
  window.define('hamster', [], function(){
    return Hamster;
  });
} else if (typeof exports === 'object') {
  // CommonJS
  module.exports = Hamster;
} else {
  // Browser global
  window.Hamster = Hamster;
}

})(window, window.document);
;
/*
 * angular-mousewheel v1.0.4
 * (c) 2013 Monospaced http://monospaced.com
 * License: MIT
 */

angular.module('monospaced.mousewheel', [])
  .directive('msdWheel', ['$parse', function($parse){
    return {
      restrict: 'A, C',
        link: function(scope, element, attr) {
        var expr = $parse(attr['msdWheel']),
            fn = function(event, delta, deltaX, deltaY){
              scope.$apply(function(){
                expr(scope, {
                  $event: event,
                  $delta: delta,
                  $deltaX: deltaX,
                  $deltaY: deltaY
                });
              });
            },
            hamster;

        if (typeof Hamster === 'undefined') {
          // fallback to standard wheel event
          element.bind('wheel', function(event){
            scope.$apply(function() {
              expr(scope, {
                $event: event
              });
            });
          });
          return;
        }

        // don't create multiple Hamster instances per element
        if (!(hamster = element.data('hamster'))) {
          hamster = Hamster(element[0]);
          element.data('hamster', hamster);
        }

        // bind Hamster wheel event
        hamster.wheel(fn);

        // unbind Hamster wheel event
        scope.$on('$destroy', function(){
          hamster.unwheel(fn);
        });
      }
    };
  }]);
;
/**!
 * AngularJS file upload directives and services. Supoorts: file upload/drop/paste, resume, cancel/abort,
 * progress, resize, thumbnail, preview, validation and CORS
 * @author  Danial  <danial.farid@gmail.com>
 * @version 10.1.8
 */

if (window.XMLHttpRequest && !(window.FileAPI && FileAPI.shouldLoad)) {
  window.XMLHttpRequest.prototype.setRequestHeader = (function (orig) {
    return function (header, value) {
      if (header === '__setXHR_') {
        var val = value(this);
        // fix for angular < 1.2.0
        if (val instanceof Function) {
          val(this);
        }
      } else {
        orig.apply(this, arguments);
      }
    };
  })(window.XMLHttpRequest.prototype.setRequestHeader);
}

var ngFileUpload = angular.module('ngFileUpload', []);

ngFileUpload.version = '10.1.8';

ngFileUpload.service('UploadBase', ['$http', '$q', '$timeout', function ($http, $q, $timeout) {
  var upload = this;

  this.isResumeSupported = function () {
    return window.Blob && (window.Blob instanceof Function) && new window.Blob().slice;
  };

  var resumeSupported = this.isResumeSupported();

  function sendHttp(config) {
    config.method = config.method || 'POST';
    config.headers = config.headers || {};

    var deferred = config._deferred = config._deferred || $q.defer();
    var promise = deferred.promise;

    function notifyProgress(e) {
      if (deferred.notify) {
        deferred.notify(e);
      }
      if (promise.progressFunc) {
        $timeout(function () {
          promise.progressFunc(e);
        });
      }
    }

    function getNotifyEvent(n) {
      if (config._start != null && resumeSupported) {
        return {
          loaded: n.loaded + config._start, total: config._file.size, type: n.type, config: config,
          lengthComputable: true, target: n.target
        };
      } else {
        return n;
      }
    }

    if (!config.disableProgress) {
      config.headers.__setXHR_ = function () {
        return function (xhr) {
          if (!xhr || !(xhr instanceof XMLHttpRequest)) return;
          config.__XHR = xhr;
          if (config.xhrFn) config.xhrFn(xhr);
          xhr.upload.addEventListener('progress', function (e) {
            e.config = config;
            notifyProgress(getNotifyEvent(e));
          }, false);
          //fix for firefox not firing upload progress end, also IE8-9
          xhr.upload.addEventListener('load', function (e) {
            if (e.lengthComputable) {
              e.config = config;
              notifyProgress(getNotifyEvent(e));
            }
          }, false);
        };
      };
    }

    function uploadWithAngular() {
      $http(config).then(function (r) {
        if (resumeSupported && config._chunkSize && !config._finished) {
          notifyProgress({loaded: config._end, total: config._file.size, config: config, type: 'progress'});
          upload.upload(config, true);
        } else {
          if (config._finished) delete config._finished;
          deferred.resolve(r);
        }
      }, function (e) {
        deferred.reject(e);
      }, function (n) {
        deferred.notify(n);
      });
    }

    if (!resumeSupported) {
      uploadWithAngular();
    } else if (config._chunkSize && config._end && !config._finished) {
      config._start = config._end;
      config._end += config._chunkSize;
      uploadWithAngular();
    } else if (config.resumeSizeUrl) {
      $http.get(config.resumeSizeUrl).then(function (resp) {
        if (config.resumeSizeResponseReader) {
          config._start = config.resumeSizeResponseReader(resp.data);
        } else {
          config._start = parseInt((resp.data.size == null ? resp.data : resp.data.size).toString());
        }
        if (config._chunkSize) {
          config._end = config._start + config._chunkSize;
        }
        uploadWithAngular();
      }, function (e) {
        throw e;
      });
    } else if (config.resumeSize) {
      config.resumeSize().then(function (size) {
        config._start = size;
        uploadWithAngular();
      }, function (e) {
        throw e;
      });
    } else {
      uploadWithAngular();
    }


    promise.success = function (fn) {
      promise.then(function (response) {
        fn(response.data, response.status, response.headers, config);
      });
      return promise;
    };

    promise.error = function (fn) {
      promise.then(null, function (response) {
        fn(response.data, response.status, response.headers, config);
      });
      return promise;
    };

    promise.progress = function (fn) {
      promise.progressFunc = fn;
      promise.then(null, null, function (n) {
        fn(n);
      });
      return promise;
    };
    promise.abort = promise.pause = function () {
      if (config.__XHR) {
        $timeout(function () {
          config.__XHR.abort();
        });
      }
      return promise;
    };
    promise.xhr = function (fn) {
      config.xhrFn = (function (origXhrFn) {
        return function () {
          if (origXhrFn) origXhrFn.apply(promise, arguments);
          fn.apply(promise, arguments);
        };
      })(config.xhrFn);
      return promise;
    };

    return promise;
  }

  this.rename = function (file, name) {
    file.ngfName = name;
    return file;
  };

  this.jsonBlob = function (val) {
    if (val != null && !angular.isString(val)) {
      val = JSON.stringify(val);
    }
    var blob = new window.Blob([val], {type: 'application/json'});
    blob._ngfBlob = true;
    return blob;
  };

  this.json = function (val) {
    return angular.toJson(val);
  };

  function copy(obj) {
    var clone = {};
    for (var key in obj) {
      if (obj.hasOwnProperty(key)) {
        clone[key] = obj[key];
      }
    }
    return clone;
  }

  this.upload = function (config, internal) {
    function isFile(file) {
      return file != null && (file instanceof window.Blob || (file.flashId && file.name && file.size));
    }

    function toResumeFile(file, formData) {
      if (file._ngfBlob) return file;
      config._file = config._file || file;
      if (config._start != null && resumeSupported) {
        if (config._end && config._end >= file.size) {
          config._finished = true;
          config._end = file.size;
        }
        var slice = file.slice(config._start, config._end || file.size);
        slice.name = file.name;
        slice.ngfName = file.ngfName;
        if (config._chunkSize) {
          formData.append('_chunkSize', config._end - config._start);
          formData.append('_chunkNumber', Math.floor(config._start / config._chunkSize));
          formData.append('_totalSize', config._file.size);
        }
        return slice;
      }
      return file;
    }

    function addFieldToFormData(formData, val, key) {
      if (val !== undefined) {
        if (angular.isDate(val)) {
          val = val.toISOString();
        }
        if (angular.isString(val)) {
          formData.append(key, val);
        } else if (isFile(val)) {
          var file = toResumeFile(val, formData);
          var split = key.split(',');
          if (split[1]) {
            file.ngfName = split[1].replace(/^\s+|\s+$/g, '');
            key = split[0];
          }
          config._fileKey = config._fileKey || key;
          formData.append(key, file, file.ngfName || file.name);
        } else {
          if (angular.isObject(val)) {
            if (val.$$ngfCircularDetection) throw 'ngFileUpload: Circular reference in config.data. Make sure specified data for Upload.upload() has no circular reference: ' + key;

            val.$$ngfCircularDetection = true;
            try {
              for (var k in val) {
                if (val.hasOwnProperty(k) && k !== '$$ngfCircularDetection') {
                  var objectKey = config.objectKey == null ? '[i]' : config.objectKey;
                  if (val.length && parseInt(k) > -1) {
                    objectKey = config.arrayKey == null ? objectKey : config.arrayKey;
                  }
                  addFieldToFormData(formData, val[k], key + objectKey.replace(/[ik]/g, k));
                }
              }
            } finally {
              delete val.$$ngfCircularDetection;
            }
          } else {
            formData.append(key, val);
          }
        }
      }
    }

    function digestConfig() {
      config._chunkSize = upload.translateScalars(config.resumeChunkSize);
      config._chunkSize = config._chunkSize ? parseInt(config._chunkSize.toString()) : null;

      config.headers = config.headers || {};
      config.headers['Content-Type'] = undefined;
      config.transformRequest = config.transformRequest ?
        (angular.isArray(config.transformRequest) ?
          config.transformRequest : [config.transformRequest]) : [];
      config.transformRequest.push(function (data) {
        var formData = new window.FormData(), key;
        data = data || config.fields || {};
        if (config.file) {
          data.file = config.file;
        }
        for (key in data) {
          if (data.hasOwnProperty(key)) {
            var val = data[key];
            if (config.formDataAppender) {
              config.formDataAppender(formData, key, val);
            } else {
              addFieldToFormData(formData, val, key);
            }
          }
        }

        return formData;
      });
    }

    if (!internal) config = copy(config);
    if (!config._isDigested) {
      config._isDigested = true;
      digestConfig();
    }

    return sendHttp(config);
  };

  this.http = function (config) {
    config = copy(config);
    config.transformRequest = config.transformRequest || function (data) {
        if ((window.ArrayBuffer && data instanceof window.ArrayBuffer) || data instanceof window.Blob) {
          return data;
        }
        return $http.defaults.transformRequest[0].apply(this, arguments);
      };
    config._chunkSize = upload.translateScalars(config.resumeChunkSize);
    config._chunkSize = config._chunkSize ? parseInt(config._chunkSize.toString()) : null;

    return sendHttp(config);
  };

  this.translateScalars = function (str) {
    if (angular.isString(str)) {
      if (str.search(/kb/i) === str.length - 2) {
        return parseFloat(str.substring(0, str.length - 2) * 1000);
      } else if (str.search(/mb/i) === str.length - 2) {
        return parseFloat(str.substring(0, str.length - 2) * 1000000);
      } else if (str.search(/gb/i) === str.length - 2) {
        return parseFloat(str.substring(0, str.length - 2) * 1000000000);
      } else if (str.search(/b/i) === str.length - 1) {
        return parseFloat(str.substring(0, str.length - 1));
      } else if (str.search(/s/i) === str.length - 1) {
        return parseFloat(str.substring(0, str.length - 1));
      } else if (str.search(/m/i) === str.length - 1) {
        return parseFloat(str.substring(0, str.length - 1) * 60);
      } else if (str.search(/h/i) === str.length - 1) {
        return parseFloat(str.substring(0, str.length - 1) * 3600);
      }
    }
    return str;
  };

  this.setDefaults = function (defaults) {
    this.defaults = defaults || {};
  };

  this.defaults = {};
  this.version = ngFileUpload.version;
}

]);

ngFileUpload.service('Upload', ['$parse', '$timeout', '$compile', '$q', 'UploadExif', function ($parse, $timeout, $compile, $q, UploadExif) {
  var upload = UploadExif;
  upload.getAttrWithDefaults = function (attr, name) {
    if (attr[name] != null) return attr[name];
    var def = upload.defaults[name];
    return (def == null ? def : (angular.isString(def) ? def : JSON.stringify(def)));
  };

  upload.attrGetter = function (name, attr, scope, params) {
    var attrVal = this.getAttrWithDefaults(attr, name);
    if (scope) {
      try {
        if (params) {
          return $parse(attrVal)(scope, params);
        } else {
          return $parse(attrVal)(scope);
        }
      } catch (e) {
        // hangle string value without single qoute
        if (name.search(/min|max|pattern/i)) {
          return attrVal;
        } else {
          throw e;
        }
      }
    } else {
      return attrVal;
    }
  };

  upload.shouldUpdateOn = function (type, attr, scope) {
    var modelOptions = upload.attrGetter('ngModelOptions', attr, scope);
    if (modelOptions && modelOptions.updateOn) {
      return modelOptions.updateOn.split(' ').indexOf(type) > -1;
    }
    return true;
  };

  upload.emptyPromise = function () {
    var d = $q.defer();
    var args = arguments;
    $timeout(function () {
      d.resolve.apply(d, args);
    });
    return d.promise;
  };

  upload.happyPromise = function (promise, data) {
    var d = $q.defer();
    promise.then(function (result) {
      d.resolve(result);
    }, function (error) {
      $timeout(function () {
        throw error;
      });
      d.resolve(data);
    });
    return d.promise;
  };

  function applyExifRotations(files, attr, scope) {
    var promises = [upload.emptyPromise()];
    angular.forEach(files, function (f, i) {
      if (f.type.indexOf('image/jpeg') === 0 && upload.attrGetter('ngfFixOrientation', attr, scope, {$file: f})) {
        promises.push(upload.happyPromise(upload.applyExifRotation(f), f).then(function (fixedFile) {
          files.splice(i, 1, fixedFile);
        }));
      }
    });
    return $q.all(promises);
  }

  function resize(files, attr, scope) {
    var param = upload.attrGetter('ngfResize', attr, scope);
    if (!param || !upload.isResizeSupported() || !files.length) return upload.emptyPromise();
    var promises = [upload.emptyPromise()];
    angular.forEach(files, function (f, i) {
      if (f.type.indexOf('image') === 0) {
        if (param.pattern && !upload.validatePattern(f, param.pattern)) return;
        var promise = upload.resize(f, param.width, param.height, param.quality,
          param.type, param.ratio, param.centerCrop);
        promises.push(promise);
        promise.then(function (resizedFile) {
          files.splice(i, 1, resizedFile);
        }, function (e) {
          f.$error = 'resize';
          f.$errorParam = (e ? (e.message ? e.message : e) + ': ' : '') + (f && f.name);
        });
      }
    });
    return $q.all(promises);
  }

  function handleKeep(files, prevFiles, attr, scope) {
    var dupFiles = [];
    var keep = upload.attrGetter('ngfKeep', attr, scope);
    if (keep) {
      var hasNew = false;

      if (keep === 'distinct' || upload.attrGetter('ngfKeepDistinct', attr, scope) === true) {
        var len = prevFiles.length;
        if (files) {
          for (var i = 0; i < files.length; i++) {
            for (var j = 0; j < len; j++) {
              if (files[i].name === prevFiles[j].name) {
                dupFiles.push(files[i]);
                break;
              }
            }
            if (j === len) {
              prevFiles.push(files[i]);
              hasNew = true;
            }
          }
        }
        files = prevFiles;
      } else {
        files = prevFiles.concat(files || []);
      }
    }
    return {files: files, dupFiles: dupFiles, keep: keep};
  }

  upload.updateModel = function (ngModel, attr, scope, fileChange, files, evt, noDelay) {
    function update(files, invalidFiles, newFiles, dupFiles, isSingleModel) {
      var file = files && files.length ? files[0] : null;

      if (ngModel) {
        upload.applyModelValidation(ngModel, files);
        ngModel.$ngfModelChange = true;
        ngModel.$setViewValue(isSingleModel ? file : files);
      }

      if (fileChange) {
        $parse(fileChange)(scope, {
          $files: files,
          $file: file,
          $newFiles: newFiles,
          $duplicateFiles: dupFiles,
          $invalidFiles: invalidFiles,
          $event: evt
        });
      }

      var invalidModel = upload.attrGetter('ngfModelInvalid', attr);
      if (invalidModel) {
        $timeout(function () {
          $parse(invalidModel).assign(scope, invalidFiles);
        });
      }
      $timeout(function () {
        // scope apply changes
      });
    }

    var newFiles = files;
    var prevFiles = ngModel && ngModel.$modelValue && (angular.isArray(ngModel.$modelValue) ?
        ngModel.$modelValue : [ngModel.$modelValue]);
    prevFiles = (prevFiles || attr.$$ngfPrevFiles || []).slice(0);
    var keepResult = handleKeep(files, prevFiles, attr, scope);
    files = keepResult.files;
    var dupFiles = keepResult.dupFiles;
    var isSingleModel = !upload.attrGetter('ngfMultiple', attr, scope) && !upload.attrGetter('multiple', attr) && !keepResult.keep;

    attr.$$ngfPrevFiles = files;

    if (keepResult.keep && (!newFiles || !newFiles.length)) return;

    upload.attrGetter('ngfBeforeModelChange', attr, scope, {$files: files,
      $file: files && files.length ? files[0] : null,
      $duplicateFiles: dupFiles,
      $event: evt});

    upload.validate(newFiles, ngModel, attr, scope).then(function () {
      if (noDelay) {
        update(files, [], newFiles, dupFiles, isSingleModel);
      } else {
        var options = upload.attrGetter('ngModelOptions', attr, scope);
        if (!options || !options.allowInvalid) {
          var valids = [], invalids = [];
          angular.forEach(files, function (file) {
            if (file.$error) {
              invalids.push(file);
            } else {
              valids.push(file);
            }
          });
          files = valids;
        }
        var fixOrientation = upload.emptyPromise(files);
        if (upload.attrGetter('ngfFixOrientation', attr, scope) && upload.isExifSupported()) {
          fixOrientation = applyExifRotations(files, attr, scope);
        }
        fixOrientation.then(function () {
          resize(files, attr, scope).then(function () {
            $timeout(function () {
              update(files, invalids, newFiles, dupFiles, isSingleModel);
            }, options && options.debounce ? options.debounce.change || options.debounce : 0);
          }, function (e) {
            throw 'Could not resize files ' + e;
          });
        });
      }
    });

    // cleaning object url memories
    var l = prevFiles.length;
    while (l--) {
      var prevFile = prevFiles[l];
      if (window.URL && prevFile.blobUrl) {
        URL.revokeObjectURL(prevFile.blobUrl);
        delete prevFile.blobUrl;
      }
    }
  };

  return upload;
}]);

ngFileUpload.directive('ngfSelect', ['$parse', '$timeout', '$compile', 'Upload', function ($parse, $timeout, $compile, Upload) {
  var generatedElems = [];

  function isDelayedClickSupported(ua) {
    // fix for android native browser < 4.4 and safari windows
    var m = ua.match(/Android[^\d]*(\d+)\.(\d+)/);
    if (m && m.length > 2) {
      var v = Upload.defaults.androidFixMinorVersion || 4;
      return parseInt(m[1]) < 4 || (parseInt(m[1]) === v && parseInt(m[2]) < v);
    }

    // safari on windows
    return ua.indexOf('Chrome') === -1 && /.*Windows.*Safari.*/.test(ua);
  }

  function linkFileSelect(scope, elem, attr, ngModel, $parse, $timeout, $compile, upload) {
    /** @namespace attr.ngfSelect */
    /** @namespace attr.ngfChange */
    /** @namespace attr.ngModel */
    /** @namespace attr.ngModelOptions */
    /** @namespace attr.ngfMultiple */
    /** @namespace attr.ngfCapture */
    /** @namespace attr.ngfValidate */
    /** @namespace attr.ngfKeep */
    var attrGetter = function (name, scope) {
      return upload.attrGetter(name, attr, scope);
    };

    function isInputTypeFile() {
      return elem[0].tagName.toLowerCase() === 'input' && attr.type && attr.type.toLowerCase() === 'file';
    }

    function fileChangeAttr() {
      return attrGetter('ngfChange') || attrGetter('ngfSelect');
    }

    function changeFn(evt) {
      if (upload.shouldUpdateOn('change', attr, scope)) {
        var fileList = evt.__files_ || (evt.target && evt.target.files), files = [];
        for (var i = 0; i < fileList.length; i++) {
          files.push(fileList[i]);
        }
        upload.updateModel(ngModel, attr, scope, fileChangeAttr(),
          files.length ? files : null, evt);
      }
    }

    upload.registerModelChangeValidator(ngModel, attr, scope);

    var unwatches = [];
    unwatches.push(scope.$watch(attrGetter('ngfMultiple'), function () {
      fileElem.attr('multiple', attrGetter('ngfMultiple', scope));
    }));
    unwatches.push(scope.$watch(attrGetter('ngfCapture'), function () {
      fileElem.attr('capture', attrGetter('ngfCapture', scope));
    }));
    unwatches.push(scope.$watch(attrGetter('ngfAccept'), function () {
      fileElem.attr('accept', attrGetter('ngfAccept', scope));
    }));
    attr.$observe('accept', function () {
      fileElem.attr('accept', attrGetter('accept'));
    });
    unwatches.push(function () {
      if (attr.$$observers) delete attr.$$observers.accept;
    });
    function bindAttrToFileInput(fileElem) {
      if (elem !== fileElem) {
        for (var i = 0; i < elem[0].attributes.length; i++) {
          var attribute = elem[0].attributes[i];
          if (attribute.name !== 'type' && attribute.name !== 'class' && attribute.name !== 'style') {
            if (attribute.value == null || attribute.value === '') {
              if (attribute.name === 'required') attribute.value = 'required';
              if (attribute.name === 'multiple') attribute.value = 'multiple';
            }
            fileElem.attr(attribute.name, attribute.name === 'id' ? 'ngf-' + attribute.value : attribute.value);
          }
        }
      }
    }

    function createFileInput() {
      if (isInputTypeFile()) {
        return elem;
      }

      var fileElem = angular.element('<input type="file">');

      bindAttrToFileInput(fileElem);

      var label = angular.element('<label>upload</label>');
      label.css('visibility', 'hidden').css('position', 'absolute').css('overflow', 'hidden')
        .css('width', '0px').css('height', '0px').css('border', 'none')
        .css('margin', '0px').css('padding', '0px').attr('tabindex', '-1');
      generatedElems.push({el: elem, ref: label});

      document.body.appendChild(label.append(fileElem)[0]);

      return fileElem;
    }

    var initialTouchStartY = 0;

    function clickHandler(evt) {
      if (elem.attr('disabled') || attrGetter('ngfSelectDisabled', scope)) return false;

      var r = handleTouch(evt);
      if (r != null) return r;

      resetModel(evt);

      // fix for md when the element is removed from the DOM and added back #460
      try {
        if (!isInputTypeFile() && !document.body.contains(fileElem[0])) {
          generatedElems.push({el: elem, ref: fileElem.parent()});
          document.body.appendChild(fileElem[0].parent());
          fileElem.bind('change', changeFn);
        }
      } catch(e){/*ignore*/}

      if (isDelayedClickSupported(navigator.userAgent)) {
        setTimeout(function () {
          fileElem[0].click();
        }, 0);
      } else {
        fileElem[0].click();
      }

      return false;
    }

    function handleTouch(evt) {
      var touches = evt.changedTouches || (evt.originalEvent && evt.originalEvent.changedTouches);
      if (evt.type === 'touchstart') {
        initialTouchStartY = touches ? touches[0].clientY : 0;
        return true; // don't block event default
      } else {
        evt.stopPropagation();
        evt.preventDefault();

        // prevent scroll from triggering event
        if (evt.type === 'touchend') {
          var currentLocation = touches ? touches[0].clientY : 0;
          if (Math.abs(currentLocation - initialTouchStartY) > 20) return false;
        }
      }
    }

    var fileElem = elem;

    function resetModel(evt) {
      if (upload.shouldUpdateOn('click', attr, scope) && fileElem.val()) {
        fileElem.val(null);
        upload.updateModel(ngModel, attr, scope, fileChangeAttr(), null, evt, true);
      }
    }

    if (!isInputTypeFile()) {
      fileElem = createFileInput();
    }
    fileElem.bind('change', changeFn);

    if (!isInputTypeFile()) {
      elem.bind('click touchstart touchend', clickHandler);
    } else {
      elem.bind('click', resetModel);
    }

    function ie10SameFileSelectFix(evt) {
      if (fileElem && !fileElem.attr('__ngf_ie10_Fix_')) {
        if (!fileElem[0].parentNode) {
          fileElem = null;
          return;
        }
        evt.preventDefault();
        evt.stopPropagation();
        fileElem.unbind('click');
        var clone = fileElem.clone();
        fileElem.replaceWith(clone);
        fileElem = clone;
        fileElem.attr('__ngf_ie10_Fix_', 'true');
        fileElem.bind('change', changeFn);
        fileElem.bind('click', ie10SameFileSelectFix);
        fileElem[0].click();
        return false;
      } else {
        fileElem.removeAttr('__ngf_ie10_Fix_');
      }
    }

    if (navigator.appVersion.indexOf('MSIE 10') !== -1) {
      fileElem.bind('click', ie10SameFileSelectFix);
    }

    if (ngModel) ngModel.$formatters.push(function (val) {
      if (val == null || val.length === 0) {
        if (fileElem.val()) {
          fileElem.val(null);
        }
      }
      return val;
    });

    scope.$on('$destroy', function () {
      if (!isInputTypeFile()) fileElem.parent().remove();
      angular.forEach(unwatches, function (unwatch) {
        unwatch();
      });
    });

    $timeout(function () {
      for (var i = 0; i < generatedElems.length; i++) {
        var g = generatedElems[i];
        if (!document.body.contains(g.el[0])) {
          generatedElems.splice(i, 1);
          g.ref.remove();
        }
      }
    });

    if (window.FileAPI && window.FileAPI.ngfFixIE) {
      window.FileAPI.ngfFixIE(elem, fileElem, changeFn);
    }
  }

  return {
    restrict: 'AEC',
    require: '?ngModel',
    link: function (scope, elem, attr, ngModel) {
      linkFileSelect(scope, elem, attr, ngModel, $parse, $timeout, $compile, Upload);
    }
  };
}]);

(function () {

  ngFileUpload.service('UploadDataUrl', ['UploadBase', '$timeout', '$q', function (UploadBase, $timeout, $q) {
    var upload = UploadBase;
    upload.base64DataUrl = function (file) {
      if (angular.isArray(file)) {
        var d = $q.defer(), count = 0;
        angular.forEach(file, function (f) {
          upload.dataUrl(f, true)['finally'](function () {
            count++;
            if (count === file.length) {
              var urls = [];
              angular.forEach(file, function (ff) {
                urls.push(ff.$ngfDataUrl);
              });
              d.resolve(urls, file);
            }
          });
        });
        return d.promise;
      } else {
        return upload.dataUrl(file, true);
      }
    };
    upload.dataUrl = function (file, disallowObjectUrl) {
      if (!file) return upload.emptyPromise(file, file);
      if ((disallowObjectUrl && file.$ngfDataUrl != null) || (!disallowObjectUrl && file.$ngfBlobUrl != null)) {
        return upload.emptyPromise(disallowObjectUrl ? file.$ngfDataUrl : file.$ngfBlobUrl, file);
      }
      var p = disallowObjectUrl ? file.$$ngfDataUrlPromise : file.$$ngfBlobUrlPromise;
      if (p) return p;

      var deferred = $q.defer();
      $timeout(function () {
        if (window.FileReader && file &&
          (!window.FileAPI || navigator.userAgent.indexOf('MSIE 8') === -1 || file.size < 20000) &&
          (!window.FileAPI || navigator.userAgent.indexOf('MSIE 9') === -1 || file.size < 4000000)) {
          //prefer URL.createObjectURL for handling refrences to files of all sizes
          //since it doesn´t build a large string in memory
          var URL = window.URL || window.webkitURL;
          if (URL && URL.createObjectURL && !disallowObjectUrl) {
            var url;
            try {
              url = URL.createObjectURL(file);
            } catch (e) {
              $timeout(function () {
                file.$ngfBlobUrl = '';
                deferred.reject();
              });
              return;
            }
            $timeout(function () {
              file.$ngfBlobUrl = url;
              if (url) deferred.resolve(url, file);
            });
          } else {
            var fileReader = new FileReader();
            fileReader.onload = function (e) {
              $timeout(function () {
                file.$ngfDataUrl = e.target.result;
                deferred.resolve(e.target.result, file);
              });
            };
            fileReader.onerror = function () {
              $timeout(function () {
                file.$ngfDataUrl = '';
                deferred.reject();
              });
            };
            fileReader.readAsDataURL(file);
          }
        } else {
          $timeout(function () {
            file[disallowObjectUrl ? 'dataUrl' : 'blobUrl'] = '';
            deferred.reject();
          });
        }
      });

      if (disallowObjectUrl) {
        p = file.$$ngfDataUrlPromise = deferred.promise;
      } else {
        p = file.$$ngfBlobUrlPromise = deferred.promise;
      }
      p['finally'](function () {
        delete file[disallowObjectUrl ? '$$ngfDataUrlPromise' : '$$ngfBlobUrlPromise'];
      });
      return p;
    };
    return upload;
  }]);

  function getTagType(el) {
    if (el.tagName.toLowerCase() === 'img') return 'image';
    if (el.tagName.toLowerCase() === 'audio') return 'audio';
    if (el.tagName.toLowerCase() === 'video') return 'video';
    return /./;
  }

  function linkFileDirective(Upload, $timeout, scope, elem, attr, directiveName, resizeParams, isBackground) {
    function constructDataUrl(file) {
      var disallowObjectUrl = Upload.attrGetter('ngfNoObjectUrl', attr, scope);
      Upload.dataUrl(file, disallowObjectUrl)['finally'](function () {
        $timeout(function () {
          var src = (disallowObjectUrl ? file.$ngfDataUrl : file.$ngfBlobUrl) || file.$ngfDataUrl;
          if (isBackground) {
            elem.css('background-image', 'url(\'' + (src || '') + '\')');
          } else {
            elem.attr('src', src);
          }
          if (src) {
            elem.removeClass('ng-hide');
          } else {
            elem.addClass('ng-hide');
          }
        });
      });
    }

    $timeout(function () {
      var unwatch = scope.$watch(attr[directiveName], function (file) {
        var size = resizeParams;
        if (directiveName === 'ngfThumbnail') {
          if (!size) {
            size = {width: elem[0].clientWidth, height: elem[0].clientHeight};
          }
          if (size.width === 0 && window.getComputedStyle) {
            var style = getComputedStyle(elem[0]);
            size = {
              width: parseInt(style.width.slice(0, -2)),
              height: parseInt(style.height.slice(0, -2))
            };
          }
        }

        if (angular.isString(file)) {
          elem.removeClass('ng-hide');
          if (isBackground) {
            return elem.css('background-image', 'url(\'' + file + '\')');
          } else {
            return elem.attr('src', file);
          }
        }
        if (file && file.type && file.type.search(getTagType(elem[0])) === 0 &&
          (!isBackground || file.type.indexOf('image') === 0)) {
          if (size && Upload.isResizeSupported()) {
            Upload.resize(file, size.width, size.height, size.quality).then(
              function (f) {
                constructDataUrl(f);
              }, function (e) {
                throw e;
              }
            );
          } else {
            constructDataUrl(file);
          }
        } else {
          elem.addClass('ng-hide');
        }
      });

      scope.$on('$destroy', function () {
        unwatch();
      });
    });
  }


  /** @namespace attr.ngfSrc */
  /** @namespace attr.ngfNoObjectUrl */
  ngFileUpload.directive('ngfSrc', ['Upload', '$timeout', function (Upload, $timeout) {
    return {
      restrict: 'AE',
      link: function (scope, elem, attr) {
        linkFileDirective(Upload, $timeout, scope, elem, attr, 'ngfSrc',
          Upload.attrGetter('ngfResize', attr, scope), false);
      }
    };
  }]);

  /** @namespace attr.ngfBackground */
  /** @namespace attr.ngfNoObjectUrl */
  ngFileUpload.directive('ngfBackground', ['Upload', '$timeout', function (Upload, $timeout) {
    return {
      restrict: 'AE',
      link: function (scope, elem, attr) {
        linkFileDirective(Upload, $timeout, scope, elem, attr, 'ngfBackground',
          Upload.attrGetter('ngfResize', attr, scope), true);
      }
    };
  }]);

  /** @namespace attr.ngfThumbnail */
  /** @namespace attr.ngfAsBackground */
  /** @namespace attr.ngfSize */
  /** @namespace attr.ngfNoObjectUrl */
  ngFileUpload.directive('ngfThumbnail', ['Upload', '$timeout', function (Upload, $timeout) {
    return {
      restrict: 'AE',
      link: function (scope, elem, attr) {
        var size = Upload.attrGetter('ngfSize', attr, scope);
        linkFileDirective(Upload, $timeout, scope, elem, attr, 'ngfThumbnail', size,
          Upload.attrGetter('ngfAsBackground', attr, scope));
      }
    };
  }]);

  ngFileUpload.config(['$compileProvider', function ($compileProvider) {
    if ($compileProvider.imgSrcSanitizationWhitelist) $compileProvider.imgSrcSanitizationWhitelist(/^\s*(https?|ftp|mailto|tel|local|file|data|blob):/);
    if ($compileProvider.aHrefSanitizationWhitelist) $compileProvider.aHrefSanitizationWhitelist(/^\s*(https?|ftp|mailto|tel|local|file|data|blob):/);
  }]);

  ngFileUpload.filter('ngfDataUrl', ['UploadDataUrl', '$sce', function (UploadDataUrl, $sce) {
    return function (file, disallowObjectUrl, trustedUrl) {
      if (angular.isString(file)) {
        return $sce.trustAsResourceUrl(file);
      }
      var src = file && ((disallowObjectUrl ? file.$ngfDataUrl : file.$ngfBlobUrl) || file.$ngfDataUrl);
      if (file && !src) {
        if (!file.$ngfDataUrlFilterInProgress && angular.isObject(file)) {
          file.$ngfDataUrlFilterInProgress = true;
          UploadDataUrl.dataUrl(file, disallowObjectUrl);
        }
        return '';
      }
      if (file) delete file.$ngfDataUrlFilterInProgress;
      return (file && src ? (trustedUrl ? $sce.trustAsResourceUrl(src) : src) : file) || '';
    };
  }]);

})();

ngFileUpload.service('UploadValidate', ['UploadDataUrl', '$q', '$timeout', function (UploadDataUrl, $q, $timeout) {
  var upload = UploadDataUrl;

  function globStringToRegex(str) {
    var regexp = '', excludes = [];
    if (str.length > 2 && str[0] === '/' && str[str.length - 1] === '/') {
      regexp = str.substring(1, str.length - 1);
    } else {
      var split = str.split(',');
      if (split.length > 1) {
        for (var i = 0; i < split.length; i++) {
          var r = globStringToRegex(split[i]);
          if (r.regexp) {
            regexp += '(' + r.regexp + ')';
            if (i < split.length - 1) {
              regexp += '|';
            }
          } else {
            excludes = excludes.concat(r.excludes);
          }
        }
      } else {
        if (str.indexOf('!') === 0) {
          excludes.push('^((?!' + globStringToRegex(str.substring(1)).regexp + ').)*$');
        } else {
          if (str.indexOf('.') === 0) {
            str = '*' + str;
          }
          regexp = '^' + str.replace(new RegExp('[.\\\\+*?\\[\\^\\]$(){}=!<>|:\\-]', 'g'), '\\$&') + '$';
          regexp = regexp.replace(/\\\*/g, '.*').replace(/\\\?/g, '.');
        }
      }
    }
    return {regexp: regexp, excludes: excludes};
  }

  upload.validatePattern = function (file, val) {
    if (!val) {
      return true;
    }
    var pattern = globStringToRegex(val), valid = true;
    if (pattern.regexp && pattern.regexp.length) {
      var regexp = new RegExp(pattern.regexp, 'i');
      valid = (file.type != null && regexp.test(file.type)) ||
        (file.name != null && regexp.test(file.name));
    }
    var len = pattern.excludes.length;
    while (len--) {
      var exclude = new RegExp(pattern.excludes[len], 'i');
      valid = valid && (file.type == null || exclude.test(file.type)) &&
        (file.name == null || exclude.test(file.name));
    }
    return valid;
  };

  upload.ratioToFloat = function(val) {
    var r = val.toString(), xIndex = r.search(/[x:]/i);
    if (xIndex > -1) {
      r = parseFloat(r.substring(0, xIndex)) / parseFloat(r.substring(xIndex + 1));
    } else {
      r = parseFloat(r);
    }
    return r;
  };

  upload.registerModelChangeValidator = function (ngModel, attr, scope) {
    if (ngModel) {
      ngModel.$formatters.push(function (files) {
        if (!ngModel.$ngfModelChange) {
          upload.validate(files, ngModel, attr, scope, function () {
            upload.applyModelValidation(ngModel, files);
          });
        } else {
          ngModel.$ngfModelChange = false;
        }
      });
    }
  };

  function markModelAsDirty(ngModel, files) {
    if (files != null && !ngModel.$dirty) {
      if (ngModel.$setDirty) {
        ngModel.$setDirty();
      } else {
        ngModel.$dirty = true;
      }
    }
  }

  upload.applyModelValidation = function (ngModel, files) {
    markModelAsDirty(ngModel, files);
    angular.forEach(ngModel.$ngfValidations, function (validation) {
      ngModel.$setValidity(validation.name, validation.valid);
    });
  };

  upload.validate = function (files, ngModel, attr, scope) {
    ngModel = ngModel || {};
    ngModel.$ngfValidations = ngModel.$ngfValidations || [];

    angular.forEach(ngModel.$ngfValidations, function (v) {
      v.valid = true;
    });

    var attrGetter = function (name, params) {
      return upload.attrGetter(name, attr, scope, params);
    };

    if (files == null || files.length === 0) {
      return upload.emptyPromise(ngModel);
    }

    files = files.length === undefined ? [files] : files.slice(0);

    function validateSync(name, validatorVal, fn) {
      if (files) {
        var dName = 'ngf' + name[0].toUpperCase() + name.substr(1);
        var i = files.length, valid = null;
        while (i--) {
          var file = files[i];
          if (file) {
            var val = attrGetter(dName, {'$file': file});
            if (val == null) {
              val = validatorVal(attrGetter('ngfValidate') || {});
              valid = valid == null ? true : valid;
            }
            if (val != null) {
              if (!fn(file, val)) {
                file.$error = name;
                file.$errorParam = val;
                files.splice(i, 1);
                valid = false;
              }
            }
          }
        }
        if (valid !== null) {
          ngModel.$ngfValidations.push({name: name, valid: valid});
        }
      }
    }

    validateSync('pattern', function (cons) {
      return cons.pattern;
    }, upload.validatePattern);
    validateSync('minSize', function (cons) {
      return cons.size && cons.size.min;
    }, function (file, val) {
      return file.size >= upload.translateScalars(val);
    });
    validateSync('maxSize', function (cons) {
      return cons.size && cons.size.max;
    }, function (file, val) {
      return file.size <= upload.translateScalars(val);
    });
    var totalSize = 0;
    validateSync('maxTotalSize', function (cons) {
      return cons.maxTotalSize && cons.maxTotalSize;
    }, function (file, val) {
      totalSize += file.size;
      if (totalSize > upload.translateScalars(val)) {
        files.splice(0, files.length);
        return false;
      }
      return true;
    });

    validateSync('validateFn', function () {
      return null;
    }, function (file, r) {
      return r === true || r === null || r === '';
    });

    if (!files.length) {
      return upload.emptyPromise(ngModel, ngModel.$ngfValidations);
    }

    function validateAsync(name, validatorVal, type, asyncFn, fn) {
      var promises = [upload.emptyPromise()];
      if (files) {
        var dName = 'ngf' + name[0].toUpperCase() + name.substr(1);
        files = files.length === undefined ? [files] : files;
        angular.forEach(files, function (file) {
          var defer = $q.defer();
          promises.push(defer.promise);
          if (type && (file.type == null || file.type.search(type) !== 0)) {
            defer.resolve();
            return;
          }
          var val = attrGetter(dName, {'$file': file}) || validatorVal(attrGetter('ngfValidate', {'$file': file}) || {});
          if (val) {
            asyncFn(file, val).then(function (d) {
              if (!fn(d, val)) {
                file.$error = name;
                file.$errorParam = val;
                defer.reject();
              } else {
                defer.resolve();
              }
            }, function () {
              if (attrGetter('ngfValidateForce', {'$file': file})) {
                file.$error = name;
                file.$errorParam = val;
                defer.reject();
              } else {
                defer.resolve();
              }
            });
          } else {
            defer.resolve();
          }
        });
        return $q.all(promises).then(function () {
          ngModel.$ngfValidations.push({name: name, valid: true});
        }, function () {
          ngModel.$ngfValidations.push({name: name, valid: false});
        });
      }
    }

    var deffer = $q.defer();
    var promises = [];

    promises.push(upload.happyPromise(validateAsync('maxHeight', function (cons) {
      return cons.height && cons.height.max;
    }, /image/, this.imageDimensions, function (d, val) {
      return d.height <= val;
    })));
    promises.push(upload.happyPromise(validateAsync('minHeight', function (cons) {
      return cons.height && cons.height.min;
    }, /image/, this.imageDimensions, function (d, val) {
      return d.height >= val;
    })));
    promises.push(upload.happyPromise(validateAsync('maxWidth', function (cons) {
      return cons.width && cons.width.max;
    }, /image/, this.imageDimensions, function (d, val) {
      return d.width <= val;
    })));
    promises.push(upload.happyPromise(validateAsync('minWidth', function (cons) {
      return cons.width && cons.width.min;
    }, /image/, this.imageDimensions, function (d, val) {
      return d.width >= val;
    })));
    promises.push(upload.happyPromise(validateAsync('ratio', function (cons) {
      return cons.ratio;
    }, /image/, this.imageDimensions, function (d, val) {
      var split = val.toString().split(','), valid = false;
      for (var i = 0; i < split.length; i++) {
        if (Math.abs((d.width / d.height) - upload.ratioToFloat(split[i])) < 0.0001) {
          valid = true;
        }
      }
      return valid;
    })));
    promises.push(upload.happyPromise(validateAsync('maxRatio', function (cons) {
      return cons.ratio;
    }, /image/, this.imageDimensions, function (d, val) {
      return (d.width / d.height) - upload.ratioToFloat(val) < 0.0001;
    })));
      promises.push(upload.happyPromise(validateAsync('minRatio', function (cons) {
      return cons.ratio;
    }, /image/, this.imageDimensions, function (d, val) {
      return (d.width / d.height) - upload.ratioToFloat(val) > -0.0001;
    })));
    promises.push(upload.happyPromise(validateAsync('maxDuration', function (cons) {
      return cons.duration && cons.duration.max;
    }, /audio|video/, this.mediaDuration, function (d, val) {
      return d <= upload.translateScalars(val);
    })));
    promises.push(upload.happyPromise(validateAsync('minDuration', function (cons) {
      return cons.duration && cons.duration.min;
    }, /audio|video/, this.mediaDuration, function (d, val) {
      return d >= upload.translateScalars(val);
    })));

    promises.push(upload.happyPromise(validateAsync('validateAsyncFn', function () {
      return null;
    }, null, function (file, val) {
      return val;
    }, function (r) {
      return r === true || r === null || r === '';
    })));

    return $q.all(promises).then(function () {
      deffer.resolve(ngModel, ngModel.$ngfValidations);
    });
  };

  upload.imageDimensions = function (file) {
    if (file.$ngfWidth && file.$ngfHeight) {
      var d = $q.defer();
      $timeout(function () {
        d.resolve({width: file.$ngfWidth, height: file.$ngfHeight});
      });
      return d.promise;
    }
    if (file.$ngfDimensionPromise) return file.$ngfDimensionPromise;

    var deferred = $q.defer();
    $timeout(function () {
      if (file.type.indexOf('image') !== 0) {
        deferred.reject('not image');
        return;
      }
      upload.dataUrl(file).then(function (dataUrl) {
        var img = angular.element('<img>').attr('src', dataUrl).css('visibility', 'hidden').css('position', 'fixed');

        function success() {
          var width = img[0].clientWidth;
          var height = img[0].clientHeight;
          img.remove();
          file.$ngfWidth = width;
          file.$ngfHeight = height;
          deferred.resolve({width: width, height: height});
        }

        function error() {
          img.remove();
          deferred.reject('load error');
        }

        img.on('load', success);
        img.on('error', error);
        var count = 0;

        function checkLoadError() {
          $timeout(function () {
            if (img[0].parentNode) {
              if (img[0].clientWidth) {
                success();
              } else if (count > 10) {
                error();
              } else {
                checkLoadError();
              }
            }
          }, 1000);
        }

        checkLoadError();

        angular.element(document.getElementsByTagName('body')[0]).append(img);
      }, function () {
        deferred.reject('load error');
      });
    });

    file.$ngfDimensionPromise = deferred.promise;
    file.$ngfDimensionPromise['finally'](function () {
      delete file.$ngfDimensionPromise;
    });
    return file.$ngfDimensionPromise;
  };

  upload.mediaDuration = function (file) {
    if (file.$ngfDuration) {
      var d = $q.defer();
      $timeout(function () {
        d.resolve(file.$ngfDuration);
      });
      return d.promise;
    }
    if (file.$ngfDurationPromise) return file.$ngfDurationPromise;

    var deferred = $q.defer();
    $timeout(function () {
      if (file.type.indexOf('audio') !== 0 && file.type.indexOf('video') !== 0) {
        deferred.reject('not media');
        return;
      }
      upload.dataUrl(file).then(function (dataUrl) {
        var el = angular.element(file.type.indexOf('audio') === 0 ? '<audio>' : '<video>')
          .attr('src', dataUrl).css('visibility', 'none').css('position', 'fixed');

        function success() {
          var duration = el[0].duration;
          file.$ngfDuration = duration;
          el.remove();
          deferred.resolve(duration);
        }

        function error() {
          el.remove();
          deferred.reject('load error');
        }

        el.on('loadedmetadata', success);
        el.on('error', error);
        var count = 0;

        function checkLoadError() {
          $timeout(function () {
            if (el[0].parentNode) {
              if (el[0].duration) {
                success();
              } else if (count > 10) {
                error();
              } else {
                checkLoadError();
              }
            }
          }, 1000);
        }

        checkLoadError();

        angular.element(document.body).append(el);
      }, function () {
        deferred.reject('load error');
      });
    });

    file.$ngfDurationPromise = deferred.promise;
    file.$ngfDurationPromise['finally'](function () {
      delete file.$ngfDurationPromise;
    });
    return file.$ngfDurationPromise;
  };
  return upload;
}
]);

ngFileUpload.service('UploadResize', ['UploadValidate', '$q', function (UploadValidate, $q) {
  var upload = UploadValidate;

  /**
   * Conserve aspect ratio of the original region. Useful when shrinking/enlarging
   * images to fit into a certain area.
   * Source:  http://stackoverflow.com/a/14731922
   *
   * @param {Number} srcWidth Source area width
   * @param {Number} srcHeight Source area height
   * @param {Number} maxWidth Nestable area maximum available width
   * @param {Number} maxHeight Nestable area maximum available height
   * @return {Object} { width, height }
   */
  var calculateAspectRatioFit = function (srcWidth, srcHeight, maxWidth, maxHeight, centerCrop) {
    var ratio = centerCrop ? Math.max(maxWidth / srcWidth, maxHeight / srcHeight) :
      Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
    return {width: srcWidth * ratio, height: srcHeight * ratio,
      marginX: srcWidth * ratio - maxWidth, marginY: srcHeight * ratio - maxHeight};
  };

  // Extracted from https://github.com/romelgomez/angular-firebase-image-upload/blob/master/app/scripts/fileUpload.js#L89
  var resize = function (imagen, width, height, quality, type, ratio, centerCrop) {
    var deferred = $q.defer();
    var canvasElement = document.createElement('canvas');
    var imageElement = document.createElement('img');

    imageElement.onload = function () {
      try {
        if (ratio) {
          var ratioFloat = upload.ratioToFloat(ratio);
          var imgRatio = imageElement.width /  imageElement.height;
          if (imgRatio < ratioFloat) {
            width = imageElement.width;
            height = width / ratioFloat;
          } else {
            height = imageElement.height;
            width = height * ratioFloat;
          }
        }
        if (!width) {
          width = imageElement.width;
        }
        if (!height) {
          height = imageElement.height;
        }
        var dimensions = calculateAspectRatioFit(imageElement.width, imageElement.height, width, height, centerCrop);
        canvasElement.width = Math.min(dimensions.width, width);
        canvasElement.height = Math.min(dimensions.height, height);
        var context = canvasElement.getContext('2d');
        context.drawImage(imageElement,
          Math.min(0, -dimensions.marginX / 2), Math.min(0, -dimensions.marginY / 2),
          dimensions.width, dimensions.height);
        deferred.resolve(canvasElement.toDataURL(type || 'image/WebP', quality || 1.0));
      } catch (e) {
        deferred.reject(e);
      }
    };
    imageElement.onerror = function () {
      deferred.reject();
    };
    imageElement.src = imagen;
    return deferred.promise;
  };

  upload.dataUrltoBlob = function (dataurl, name) {
    var arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    var blob = new window.Blob([u8arr], {type: mime});
    blob.name = name;
    return blob;
  };

  upload.isResizeSupported = function () {
    var elem = document.createElement('canvas');
    return window.atob && elem.getContext && elem.getContext('2d');
  };

  if (upload.isResizeSupported()) {
    // add name getter to the blob constructor prototype
    Object.defineProperty(window.Blob.prototype, 'name', {
      get: function () {
        return this.$ngfName;
      },
      set: function (v) {
        this.$ngfName = v;
      },
      configurable: true
    });
  }

  upload.resize = function (file, width, height, quality, type, ratio, centerCrop) {
    if (file.type.indexOf('image') !== 0) return upload.emptyPromise(file);

    var deferred = $q.defer();
    upload.dataUrl(file, true).then(function (url) {
      resize(url, width, height, quality, type || file.type, ratio, centerCrop).then(function (dataUrl) {
        deferred.resolve(upload.dataUrltoBlob(dataUrl, file.name));
      }, function () {
        deferred.reject();
      });
    }, function () {
      deferred.reject();
    });
    return deferred.promise;
  };

  return upload;
}]);

(function () {
  ngFileUpload.directive('ngfDrop', ['$parse', '$timeout', '$location', 'Upload', '$http',
    function ($parse, $timeout, $location, Upload, $http) {
      return {
        restrict: 'AEC',
        require: '?ngModel',
        link: function (scope, elem, attr, ngModel) {
          linkDrop(scope, elem, attr, ngModel, $parse, $timeout, $location, Upload, $http);
        }
      };
    }]);

  ngFileUpload.directive('ngfNoFileDrop', function () {
    return function (scope, elem) {
      if (dropAvailable()) elem.css('display', 'none');
    };
  });

  ngFileUpload.directive('ngfDropAvailable', ['$parse', '$timeout', 'Upload', function ($parse, $timeout, Upload) {
    return function (scope, elem, attr) {
      if (dropAvailable()) {
        var model = $parse(Upload.attrGetter('ngfDropAvailable', attr));
        $timeout(function () {
          model(scope);
          if (model.assign) {
            model.assign(scope, true);
          }
        });
      }
    };
  }]);

  function linkDrop(scope, elem, attr, ngModel, $parse, $timeout, $location, upload, $http) {
    var available = dropAvailable();

    var attrGetter = function (name, scope, params) {
      return upload.attrGetter(name, attr, scope, params);
    };

    if (attrGetter('dropAvailable')) {
      $timeout(function () {
        if (scope[attrGetter('dropAvailable')]) {
          scope[attrGetter('dropAvailable')].value = available;
        } else {
          scope[attrGetter('dropAvailable')] = available;
        }
      });
    }
    if (!available) {
      if (attrGetter('ngfHideOnDropNotAvailable', scope) === true) {
        elem.css('display', 'none');
      }
      return;
    }

    function isDisabled() {
      return elem.attr('disabled') || attrGetter('ngfDropDisabled', scope);
    }

    if (attrGetter('ngfSelect') == null) {
      upload.registerModelChangeValidator(ngModel, attr, scope);
    }

    var leaveTimeout = null;
    var stopPropagation = $parse(attrGetter('ngfStopPropagation'));
    var dragOverDelay = 1;
    var actualDragOverClass;

    elem[0].addEventListener('dragover', function (evt) {
      if (isDisabled()) return;
      evt.preventDefault();
      if (stopPropagation(scope)) evt.stopPropagation();
      // handling dragover events from the Chrome download bar
      if (navigator.userAgent.indexOf('Chrome') > -1) {
        var b = evt.dataTransfer.effectAllowed;
        evt.dataTransfer.dropEffect = ('move' === b || 'linkMove' === b) ? 'move' : 'copy';
      }
      $timeout.cancel(leaveTimeout);
      if (!actualDragOverClass) {
        actualDragOverClass = 'C';
        calculateDragOverClass(scope, attr, evt, function (clazz) {
          actualDragOverClass = clazz;
          elem.addClass(actualDragOverClass);
          attrGetter('ngfDrag', scope, {$isDragging: true, $class: actualDragOverClass, $event: evt});
        });
      }
    }, false);
    elem[0].addEventListener('dragenter', function (evt) {
      if (isDisabled()) return;
      evt.preventDefault();
      if (stopPropagation(scope)) evt.stopPropagation();
    }, false);
    elem[0].addEventListener('dragleave', function (evt) {
      if (isDisabled()) return;
      evt.preventDefault();
      if (stopPropagation(scope)) evt.stopPropagation();
      leaveTimeout = $timeout(function () {
        if (actualDragOverClass) elem.removeClass(actualDragOverClass);
        actualDragOverClass = null;
        attrGetter('ngfDrag', scope, {$isDragging: false, $event: evt});
      }, dragOverDelay || 100);
    }, false);
    elem[0].addEventListener('drop', function (evt) {
      if (isDisabled() || !upload.shouldUpdateOn('drop', attr, scope)) return;
      evt.preventDefault();
      if (stopPropagation(scope)) evt.stopPropagation();
      if (actualDragOverClass) elem.removeClass(actualDragOverClass);
      actualDragOverClass = null;
      var html;
      try {
        html = (evt.dataTransfer && evt.dataTransfer.getData && evt.dataTransfer.getData('text/html'));
      } catch (e) {/* Fix IE11 that throw error calling getData */}
      if (upload.shouldUpdateOn('dropUrl', attr, scope) && html) {
        var url;
        html.replace(/<img .*src *=\"([^\"]*)\"/, function (m, src) {
          url = src;
        });
        if (url) {
          $http({url: url, method: 'get', responseType: 'arraybuffer'}).then(function (resp) {
            var arrayBufferView = new Uint8Array(resp.data);
            var type = resp.headers('content-type') || 'image/WebP';
            var blob = new window.Blob([arrayBufferView], {type: type});
            //var split = type.split('[/;]');
            //blob.name = url.substring(0, 150).replace(/\W+/g, '') + '.' + (split.length > 1 ? split[1] : 'jpg');
            upload.updateModel(ngModel, attr, scope, attrGetter('ngfChange') || attrGetter('ngfDrop'), [blob], evt);
          });
        }
      } else {
        extractFiles(evt, function (files) {
            upload.updateModel(ngModel, attr, scope, attrGetter('ngfChange') || attrGetter('ngfDrop'), files, evt);
          }, attrGetter('ngfAllowDir', scope) !== false,
          attrGetter('multiple') || attrGetter('ngfMultiple', scope));
      }
    }, false);
    elem[0].addEventListener('paste', function (evt) {
      if (isDisabled() || !upload.shouldUpdateOn('paste', attr, scope)) return;
      var files = [];
      var clipboard = evt.clipboardData || evt.originalEvent.clipboardData;
      if (clipboard && clipboard.items) {
        for (var k = 0; k < clipboard.items.length; k++) {
          if (clipboard.items[k].type.indexOf('image') !== -1) {
            files.push(clipboard.items[k].getAsFile());
          }
        }
        upload.updateModel(ngModel, attr, scope, attrGetter('ngfChange') || attrGetter('ngfDrop'), files, evt);
      }
    }, false);

    function calculateDragOverClass(scope, attr, evt, callback) {
      var obj = attrGetter('ngfDragOverClass', scope, {$event: evt}), dClass = 'dragover';
      if (angular.isString(obj)) {
        dClass = obj;
      } else if (obj) {
        if (obj.delay) dragOverDelay = obj.delay;
        if (obj.accept || obj.reject) {
          var items = evt.dataTransfer.items;
          if (items == null || !items.length) {
            dClass = obj.accept;
          } else {
            var pattern = obj.pattern || attrGetter('ngfPattern', scope, {$event: evt});
            var len = items.length;
            while (len--) {
              if (!upload.validatePattern(items[len], pattern)) {
                dClass = obj.reject;
                break;
              } else {
                dClass = obj.accept;
              }
            }
          }
        }
      }
      callback(dClass);
    }

    function extractFiles(evt, callback, allowDir, multiple) {
      var files = [], processing = 0;

      function traverseFileTree(files, entry, path) {
        if (entry != null) {
          if (entry.isDirectory) {
            var filePath = (path || '') + entry.name;
            files.push({name: entry.name, type: 'directory', path: filePath});
            var dirReader = entry.createReader();
            var entries = [];
            processing++;
            var readEntries = function () {
              dirReader.readEntries(function (results) {
                try {
                  if (!results.length) {
                    for (var i = 0; i < entries.length; i++) {
                      traverseFileTree(files, entries[i], (path ? path : '') + entry.name + '/');
                    }
                    processing--;
                  } else {
                    entries = entries.concat(Array.prototype.slice.call(results || [], 0));
                    readEntries();
                  }
                } catch (e) {
                  processing--;
                  console.error(e);
                }
              }, function () {
                processing--;
              });
            };
            readEntries();
          } else {
            processing++;
            entry.file(function (file) {
              try {
                processing--;
                file.path = (path ? path : '') + file.name;
                files.push(file);
              } catch (e) {
                processing--;
                console.error(e);
              }
            }, function () {
              processing--;
            });
          }
        }
      }

      var items = evt.dataTransfer.items;

      if (items && items.length > 0 && $location.protocol() !== 'file') {
        for (var i = 0; i < items.length; i++) {
          if (items[i].webkitGetAsEntry && items[i].webkitGetAsEntry() && items[i].webkitGetAsEntry().isDirectory) {
            var entry = items[i].webkitGetAsEntry();
            if (entry.isDirectory && !allowDir) {
              continue;
            }
            if (entry != null) {
              traverseFileTree(files, entry);
            }
          } else {
            var f = items[i].getAsFile();
            if (f != null) files.push(f);
          }
          if (!multiple && files.length > 0) break;
        }
      } else {
        var fileList = evt.dataTransfer.files;
        if (fileList != null) {
          for (var j = 0; j < fileList.length; j++) {
            var file = fileList.item(j);
            if (file.type || file.size > 0) {
              files.push(file);
            }
            if (!multiple && files.length > 0) {
              break;
            }
          }
        }
      }
      var delays = 0;
      (function waitForProcess(delay) {
        $timeout(function () {
          if (!processing) {
            if (!multiple && files.length > 1) {
              i = 0;
              while (files[i].type === 'directory') i++;
              files = [files[i]];
            }
            callback(files);
          } else {
            if (delays++ * 10 < 20 * 1000) {
              waitForProcess(10);
            }
          }
        }, delay || 0);
      })();
    }
  }

  function dropAvailable() {
    var div = document.createElement('div');
    return ('draggable' in div) && ('ondrop' in div) && !/Edge\/12./i.test(navigator.userAgent);
  }

})();

// customized version of https://github.com/exif-js/exif-js
ngFileUpload.service('UploadExif', ['UploadResize', '$q', function (UploadResize, $q) {
  var upload = UploadResize;

  function findEXIFinJPEG(file) {
    var dataView = new DataView(file);

    if ((dataView.getUint8(0) !== 0xFF) || (dataView.getUint8(1) !== 0xD8)) {
      return 'Not a valid JPEG';
    }

    var offset = 2,
      length = file.byteLength,
      marker;

    while (offset < length) {
      if (dataView.getUint8(offset) !== 0xFF) {
        return 'Not a valid marker at offset ' + offset + ', found: ' + dataView.getUint8(offset);
      }

      marker = dataView.getUint8(offset + 1);
      if (marker === 225) {
        return readEXIFData(dataView, offset + 4, dataView.getUint16(offset + 2) - 2);
      } else {
        offset += 2 + dataView.getUint16(offset + 2);
      }
    }
  }

  function readOrientation(file, tiffStart, dirStart, bigEnd) {
    var entries = file.getUint16(dirStart, !bigEnd),
      entryOffset, i;

    for (i = 0; i < entries; i++) {
      entryOffset = dirStart + i * 12 + 2;
      var val = file.getUint16(entryOffset, !bigEnd);
      if (0x0112 === val) {
        return readTagValue(file, entryOffset, tiffStart, bigEnd);
      }
    }
    return null;
  }

  function readTagValue(file, entryOffset, tiffStart, bigEnd) {
    var numValues = file.getUint32(entryOffset + 4, !bigEnd),
      valueOffset = file.getUint32(entryOffset + 8, !bigEnd) + tiffStart, offset, vals, n;

    if (numValues === 1) {
      return file.getUint16(entryOffset + 8, !bigEnd);
    } else {
      offset = numValues > 2 ? valueOffset : (entryOffset + 8);
      vals = [];
      for (n = 0; n < numValues; n++) {
        vals[n] = file.getUint16(offset + 2 * n, !bigEnd);
      }
      return vals;
    }
  }

  function getStringFromDB(buffer, start, length) {
    var outstr = '';
    for (var n = start; n < start + length; n++) {
      outstr += String.fromCharCode(buffer.getUint8(n));
    }
    return outstr;
  }

  function readEXIFData(file, start) {
    if (getStringFromDB(file, start, 4) !== 'Exif') {
      return 'Not valid EXIF data! ' + getStringFromDB(file, start, 4);
    }

    var bigEnd,
      tiffOffset = start + 6;

    // test for TIFF validity and endianness
    if (file.getUint16(tiffOffset) === 0x4949) {
      bigEnd = false;
    } else if (file.getUint16(tiffOffset) === 0x4D4D) {
      bigEnd = true;
    } else {
      return 'Not valid TIFF data! (no 0x4949 or 0x4D4D)';
    }

    if (file.getUint16(tiffOffset + 2, !bigEnd) !== 0x002A) {
      return 'Not valid TIFF data! (no 0x002A)';
    }

    var firstIFDOffset = file.getUint32(tiffOffset + 4, !bigEnd);

    if (firstIFDOffset < 0x00000008) {
      return 'Not valid TIFF data! (First offset less than 8)', file.getUint32(tiffOffset + 4, !bigEnd);
    }

    return readOrientation(file, tiffOffset, tiffOffset + firstIFDOffset, bigEnd);

  }

  upload.isExifSupported = function() {
    return window.FileReader && new FileReader().readAsArrayBuffer && upload.isResizeSupported();
  };

  upload.orientation = function (file) {
    if (file.$ngfOrientation != null) {
      return upload.emptyPromise(file.$ngfOrientation);
    }
    var defer = $q.defer();
    var fileReader = new FileReader();
    fileReader.onload = function (e) {
      var orientation;
      try {
        orientation = findEXIFinJPEG(e.target.result);
      } catch (e) {
        defer.reject(e);
        return;
      }
      if (angular.isString(orientation)) {
        defer.resolve(1);
      } else {
        file.$ngfOrientation = orientation;
        defer.resolve(orientation);
      }
    };
    fileReader.onerror = function (e) {
      defer.reject(e);
    };

    fileReader.readAsArrayBuffer(file);
    return defer.promise;
  };


  function applyTransform(ctx, orientation, width, height) {
    switch (orientation) {
      case 2:
        return ctx.transform(-1, 0, 0, 1, width, 0);
      case 3:
        return ctx.transform(-1, 0, 0, -1, width, height);
      case 4:
        return ctx.transform(1, 0, 0, -1, 0, height);
      case 5:
        return ctx.transform(0, 1, 1, 0, 0, 0);
      case 6:
        return ctx.transform(0, 1, -1, 0, height, 0);
      case 7:
        return ctx.transform(0, -1, -1, 0, height, width);
      case 8:
        return ctx.transform(0, -1, 1, 0, 0, width);
    }
  }

  upload.applyExifRotation = function (file) {
    if (file.type.indexOf('image/jpeg') !== 0) {
      return upload.emptyPromise(file);
    }

    var deferred = $q.defer();
    upload.orientation(file).then(function (orientation) {
      if (!orientation || orientation < 2 || orientation > 8) {
        deferred.resolve(file);
      }
      upload.dataUrl(file, true).then(function (url) {
        var canvas = document.createElement('canvas');
        var img = document.createElement('img');

        img.onload = function () {
          try {
            canvas.width = orientation > 4 ? img.height : img.width;
            canvas.height = orientation > 4 ? img.width : img.height;
            var ctx = canvas.getContext('2d');
            applyTransform(ctx, orientation, img.width, img.height);
            ctx.drawImage(img, 0, 0);
            var dataUrl = canvas.toDataURL(file.type || 'image/WebP', 1.0);
            var blob = upload.dataUrltoBlob(dataUrl, file.name);
            deferred.resolve(blob);
          } catch (e) {
            deferred.reject(e);
          }
        };
        img.onerror = function () {
          deferred.reject();
        };
        img.src = url;
      }, function (e) {
        deferred.reject(e);
      });
    }, function (e) {
      deferred.reject(e);
    });
    return deferred.promise;
  };

  return upload;
}]);

;
/*!***************************************************
 * mark.js v8.11.0
 * https://github.com/julmot/mark.js
 * Copyright (c) 2014–2017, Julian Motz
 * Released under the MIT license https://git.io/vwTVl
 *****************************************************/

"use strict";

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

(function (factory, window, document) {
    if (typeof define === "function" && define.amd) {
        define([], function () {
            return factory(window, document);
        });
    } else if ((typeof module === "undefined" ? "undefined" : _typeof(module)) === "object" && module.exports) {
        module.exports = factory(window, document);
    } else {
        factory(window, document);
    }
})(function (window, document) {
    var Mark = function () {
        function Mark(ctx) {
            _classCallCheck(this, Mark);

            this.ctx = ctx;

            this.ie = false;
            var ua = window.navigator.userAgent;
            if (ua.indexOf("MSIE") > -1 || ua.indexOf("Trident") > -1) {
                this.ie = true;
            }
        }

        _createClass(Mark, [{
            key: "log",
            value: function log(msg) {
                var level = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "debug";

                var log = this.opt.log;
                if (!this.opt.debug) {
                    return;
                }
                if ((typeof log === "undefined" ? "undefined" : _typeof(log)) === "object" && typeof log[level] === "function") {
                    log[level]("mark.js: " + msg);
                }
            }
        }, {
            key: "escapeStr",
            value: function escapeStr(str) {
                return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&");
            }
        }, {
            key: "createRegExp",
            value: function createRegExp(str) {
                if (this.opt.wildcards !== "disabled") {
                    str = this.setupWildcardsRegExp(str);
                }
                str = this.escapeStr(str);
                if (Object.keys(this.opt.synonyms).length) {
                    str = this.createSynonymsRegExp(str);
                }
                if (this.opt.ignoreJoiners || this.opt.ignorePunctuation.length) {
                    str = this.setupIgnoreJoinersRegExp(str);
                }
                if (this.opt.diacritics) {
                    str = this.createDiacriticsRegExp(str);
                }
                str = this.createMergedBlanksRegExp(str);
                if (this.opt.ignoreJoiners || this.opt.ignorePunctuation.length) {
                    str = this.createJoinersRegExp(str);
                }
                if (this.opt.wildcards !== "disabled") {
                    str = this.createWildcardsRegExp(str);
                }
                str = this.createAccuracyRegExp(str);
                return str;
            }
        }, {
            key: "createSynonymsRegExp",
            value: function createSynonymsRegExp(str) {
                var syn = this.opt.synonyms,
                    sens = this.opt.caseSensitive ? "" : "i",
                    joinerPlaceholder = this.opt.ignoreJoiners || this.opt.ignorePunctuation.length ? "\0" : "";
                for (var index in syn) {
                    if (syn.hasOwnProperty(index)) {
                        var value = syn[index],
                            k1 = this.opt.wildcards !== "disabled" ? this.setupWildcardsRegExp(index) : this.escapeStr(index),
                            k2 = this.opt.wildcards !== "disabled" ? this.setupWildcardsRegExp(value) : this.escapeStr(value);
                        if (k1 !== "" && k2 !== "") {
                            str = str.replace(new RegExp("(" + k1 + "|" + k2 + ")", "gm" + sens), joinerPlaceholder + ("(" + this.processSynomyms(k1) + "|") + (this.processSynomyms(k2) + ")") + joinerPlaceholder);
                        }
                    }
                }
                return str;
            }
        }, {
            key: "processSynomyms",
            value: function processSynomyms(str) {
                if (this.opt.ignoreJoiners || this.opt.ignorePunctuation.length) {
                    str = this.setupIgnoreJoinersRegExp(str);
                }
                return str;
            }
        }, {
            key: "setupWildcardsRegExp",
            value: function setupWildcardsRegExp(str) {
                str = str.replace(/(?:\\)*\?/g, function (val) {
                    return val.charAt(0) === "\\" ? "?" : "\x01";
                });

                return str.replace(/(?:\\)*\*/g, function (val) {
                    return val.charAt(0) === "\\" ? "*" : "\x02";
                });
            }
        }, {
            key: "createWildcardsRegExp",
            value: function createWildcardsRegExp(str) {
                var spaces = this.opt.wildcards === "withSpaces";
                return str.replace(/\u0001/g, spaces ? "[\\S\\s]?" : "\\S?").replace(/\u0002/g, spaces ? "[\\S\\s]*?" : "\\S*");
            }
        }, {
            key: "setupIgnoreJoinersRegExp",
            value: function setupIgnoreJoinersRegExp(str) {
                return str.replace(/[^(|)\\]/g, function (val, indx, original) {
                    var nextChar = original.charAt(indx + 1);
                    if (/[(|)\\]/.test(nextChar) || nextChar === "") {
                        return val;
                    } else {
                        return val + "\0";
                    }
                });
            }
        }, {
            key: "createJoinersRegExp",
            value: function createJoinersRegExp(str) {
                var joiner = [];
                var ignorePunctuation = this.opt.ignorePunctuation;
                if (Array.isArray(ignorePunctuation) && ignorePunctuation.length) {
                    joiner.push(this.escapeStr(ignorePunctuation.join("")));
                }
                if (this.opt.ignoreJoiners) {
                    joiner.push("\\u00ad\\u200b\\u200c\\u200d");
                }
                return joiner.length ? str.split(/\u0000+/).join("[" + joiner.join("") + "]*") : str;
            }
        }, {
            key: "createDiacriticsRegExp",
            value: function createDiacriticsRegExp(str) {
                var sens = this.opt.caseSensitive ? "" : "i",
                    dct = this.opt.caseSensitive ? ["aàáâãäåāąă", "AÀÁÂÃÄÅĀĄĂ", "cçćč", "CÇĆČ", "dđď", "DĐĎ", "eèéêëěēę", "EÈÉÊËĚĒĘ", "iìíîïī", "IÌÍÎÏĪ", "lł", "LŁ", "nñňń", "NÑŇŃ", "oòóôõöøō", "OÒÓÔÕÖØŌ", "rř", "RŘ", "sšśșş", "SŠŚȘŞ", "tťțţ", "TŤȚŢ", "uùúûüůū", "UÙÚÛÜŮŪ", "yÿý", "YŸÝ", "zžżź", "ZŽŻŹ"] : ["aàáâãäåāąăAÀÁÂÃÄÅĀĄĂ", "cçćčCÇĆČ", "dđďDĐĎ", "eèéêëěēęEÈÉÊËĚĒĘ", "iìíîïīIÌÍÎÏĪ", "lłLŁ", "nñňńNÑŇŃ", "oòóôõöøōOÒÓÔÕÖØŌ", "rřRŘ", "sšśșşSŠŚȘŞ", "tťțţTŤȚŢ", "uùúûüůūUÙÚÛÜŮŪ", "yÿýYŸÝ", "zžżźZŽŻŹ"];
                var handled = [];
                str.split("").forEach(function (ch) {
                    dct.every(function (dct) {
                        if (dct.indexOf(ch) !== -1) {
                            if (handled.indexOf(dct) > -1) {
                                return false;
                            }

                            str = str.replace(new RegExp("[" + dct + "]", "gm" + sens), "[" + dct + "]");
                            handled.push(dct);
                        }
                        return true;
                    });
                });
                return str;
            }
        }, {
            key: "createMergedBlanksRegExp",
            value: function createMergedBlanksRegExp(str) {
                return str.replace(/[\s]+/gmi, "[\\s]+");
            }
        }, {
            key: "createAccuracyRegExp",
            value: function createAccuracyRegExp(str) {
                var _this = this;

                var chars = "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~\xA1\xBF";
                var acc = this.opt.accuracy,
                    val = typeof acc === "string" ? acc : acc.value,
                    ls = typeof acc === "string" ? [] : acc.limiters,
                    lsJoin = "";
                ls.forEach(function (limiter) {
                    lsJoin += "|" + _this.escapeStr(limiter);
                });
                switch (val) {
                    case "partially":
                    default:
                        return "()(" + str + ")";
                    case "complementary":
                        lsJoin = "\\s" + (lsJoin ? lsJoin : this.escapeStr(chars));
                        return "()([^" + lsJoin + "]*" + str + "[^" + lsJoin + "]*)";
                    case "exactly":
                        return "(^|\\s" + lsJoin + ")(" + str + ")(?=$|\\s" + lsJoin + ")";
                }
            }
        }, {
            key: "getSeparatedKeywords",
            value: function getSeparatedKeywords(sv) {
                var _this2 = this;

                var stack = [];
                sv.forEach(function (kw) {
                    if (!_this2.opt.separateWordSearch) {
                        if (kw.trim() && stack.indexOf(kw) === -1) {
                            stack.push(kw);
                        }
                    } else {
                        kw.split(" ").forEach(function (kwSplitted) {
                            if (kwSplitted.trim() && stack.indexOf(kwSplitted) === -1) {
                                stack.push(kwSplitted);
                            }
                        });
                    }
                });
                return {
                    "keywords": stack.sort(function (a, b) {
                        return b.length - a.length;
                    }),
                    "length": stack.length
                };
            }
        }, {
            key: "isNumeric",
            value: function isNumeric(value) {
                return Number(parseFloat(value)) == value;
            }
        }, {
            key: "checkRanges",
            value: function checkRanges(array) {
                var _this3 = this;

                if (!Array.isArray(array) || Object.prototype.toString.call(array[0]) !== "[object Object]") {
                    this.log("markRanges() will only accept an array of objects");
                    this.opt.noMatch(array);
                    return [];
                }
                var stack = [];
                var last = 0;
                array.sort(function (a, b) {
                    return a.start - b.start;
                }).forEach(function (item) {
                    var _callNoMatchOnInvalid = _this3.callNoMatchOnInvalidRanges(item, last),
                        start = _callNoMatchOnInvalid.start,
                        end = _callNoMatchOnInvalid.end,
                        valid = _callNoMatchOnInvalid.valid;

                    if (valid) {
                        item.start = start;
                        item.length = end - start;
                        stack.push(item);
                        last = end;
                    }
                });
                return stack;
            }
        }, {
            key: "callNoMatchOnInvalidRanges",
            value: function callNoMatchOnInvalidRanges(range, last) {
                var start = void 0,
                    end = void 0,
                    valid = false;
                if (range && typeof range.start !== "undefined") {
                    start = parseInt(range.start, 10);
                    end = start + parseInt(range.length, 10);

                    if (this.isNumeric(range.start) && this.isNumeric(range.length) && end - last > 0 && end - start > 0) {
                        valid = true;
                    } else {
                        this.log("Ignoring invalid or overlapping range: " + ("" + JSON.stringify(range)));
                        this.opt.noMatch(range);
                    }
                } else {
                    this.log("Ignoring invalid range: " + JSON.stringify(range));
                    this.opt.noMatch(range);
                }
                return {
                    start: start,
                    end: end,
                    valid: valid
                };
            }
        }, {
            key: "checkWhitespaceRanges",
            value: function checkWhitespaceRanges(range, originalLength, string) {
                var end = void 0,
                    valid = true,
                    max = string.length,
                    offset = originalLength - max,
                    start = parseInt(range.start, 10) - offset;

                start = start > max ? max : start;
                end = start + parseInt(range.length, 10);
                if (end > max) {
                    end = max;
                    this.log("End range automatically set to the max value of " + max);
                }
                if (start < 0 || end - start < 0 || start > max || end > max) {
                    valid = false;
                    this.log("Invalid range: " + JSON.stringify(range));
                    this.opt.noMatch(range);
                } else if (string.substring(start, end).replace(/\s+/g, "") === "") {
                    valid = false;

                    this.log("Skipping whitespace only range: " + JSON.stringify(range));
                    this.opt.noMatch(range);
                }
                return {
                    start: start,
                    end: end,
                    valid: valid
                };
            }
        }, {
            key: "getTextNodes",
            value: function getTextNodes(cb) {
                var _this4 = this;

                var val = "",
                    nodes = [];
                this.iterator.forEachNode(NodeFilter.SHOW_TEXT, function (node) {
                    nodes.push({
                        start: val.length,
                        end: (val += node.textContent).length,
                        node: node
                    });
                }, function (node) {
                    if (_this4.matchesExclude(node.parentNode)) {
                        return NodeFilter.FILTER_REJECT;
                    } else {
                        return NodeFilter.FILTER_ACCEPT;
                    }
                }, function () {
                    cb({
                        value: val,
                        nodes: nodes
                    });
                });
            }
        }, {
            key: "matchesExclude",
            value: function matchesExclude(el) {
                return DOMIterator.matches(el, this.opt.exclude.concat(["script", "style", "title", "head", "html"]));
            }
        }, {
            key: "wrapRangeInTextNode",
            value: function wrapRangeInTextNode(node, start, end) {
                var hEl = !this.opt.element ? "mark" : this.opt.element,
                    startNode = node.splitText(start),
                    ret = startNode.splitText(end - start);
                var repl = document.createElement(hEl);
                repl.setAttribute("data-markjs", "true");
                if (this.opt.className) {
                    repl.setAttribute("class", this.opt.className);
                }
                repl.textContent = startNode.textContent;
                startNode.parentNode.replaceChild(repl, startNode);
                return ret;
            }
        }, {
            key: "wrapRangeInMappedTextNode",
            value: function wrapRangeInMappedTextNode(dict, start, end, filterCb, eachCb) {
                var _this5 = this;

                dict.nodes.every(function (n, i) {
                    var sibl = dict.nodes[i + 1];
                    if (typeof sibl === "undefined" || sibl.start > start) {
                        if (!filterCb(n.node)) {
                            return false;
                        }

                        var s = start - n.start,
                            e = (end > n.end ? n.end : end) - n.start,
                            startStr = dict.value.substr(0, n.start),
                            endStr = dict.value.substr(e + n.start);
                        n.node = _this5.wrapRangeInTextNode(n.node, s, e);

                        dict.value = startStr + endStr;
                        dict.nodes.forEach(function (k, j) {
                            if (j >= i) {
                                if (dict.nodes[j].start > 0 && j !== i) {
                                    dict.nodes[j].start -= e;
                                }
                                dict.nodes[j].end -= e;
                            }
                        });
                        end -= e;
                        eachCb(n.node.previousSibling, n.start);
                        if (end > n.end) {
                            start = n.end;
                        } else {
                            return false;
                        }
                    }
                    return true;
                });
            }
        }, {
            key: "wrapMatches",
            value: function wrapMatches(regex, ignoreGroups, filterCb, eachCb, endCb) {
                var _this6 = this;

                var matchIdx = ignoreGroups === 0 ? 0 : ignoreGroups + 1;
                this.getTextNodes(function (dict) {
                    dict.nodes.forEach(function (node) {
                        node = node.node;
                        var match = void 0;
                        while ((match = regex.exec(node.textContent)) !== null && match[matchIdx] !== "") {
                            if (!filterCb(match[matchIdx], node)) {
                                continue;
                            }
                            var pos = match.index;
                            if (matchIdx !== 0) {
                                for (var i = 1; i < matchIdx; i++) {
                                    pos += match[i].length;
                                }
                            }
                            node = _this6.wrapRangeInTextNode(node, pos, pos + match[matchIdx].length);
                            eachCb(node.previousSibling);

                            regex.lastIndex = 0;
                        }
                    });
                    endCb();
                });
            }
        }, {
            key: "wrapMatchesAcrossElements",
            value: function wrapMatchesAcrossElements(regex, ignoreGroups, filterCb, eachCb, endCb) {
                var _this7 = this;

                var matchIdx = ignoreGroups === 0 ? 0 : ignoreGroups + 1;
                this.getTextNodes(function (dict) {
                    var match = void 0;
                    while ((match = regex.exec(dict.value)) !== null && match[matchIdx] !== "") {
                        var start = match.index;
                        if (matchIdx !== 0) {
                            for (var i = 1; i < matchIdx; i++) {
                                start += match[i].length;
                            }
                        }
                        var end = start + match[matchIdx].length;

                        _this7.wrapRangeInMappedTextNode(dict, start, end, function (node) {
                            return filterCb(match[matchIdx], node);
                        }, function (node, lastIndex) {
                            regex.lastIndex = lastIndex;
                            eachCb(node);
                        });
                    }
                    endCb();
                });
            }
        }, {
            key: "wrapRangeFromIndex",
            value: function wrapRangeFromIndex(ranges, filterCb, eachCb, endCb) {
                var _this8 = this;

                this.getTextNodes(function (dict) {
                    var originalLength = dict.value.length;
                    ranges.forEach(function (range, counter) {
                        var _checkWhitespaceRange = _this8.checkWhitespaceRanges(range, originalLength, dict.value),
                            start = _checkWhitespaceRange.start,
                            end = _checkWhitespaceRange.end,
                            valid = _checkWhitespaceRange.valid;

                        if (valid) {
                            _this8.wrapRangeInMappedTextNode(dict, start, end, function (node) {
                                return filterCb(node, range, dict.value.substring(start, end), counter);
                            }, function (node) {
                                eachCb(node, range);
                            });
                        }
                    });
                    endCb();
                });
            }
        }, {
            key: "unwrapMatches",
            value: function unwrapMatches(node) {
                var parent = node.parentNode;
                var docFrag = document.createDocumentFragment();
                while (node.firstChild) {
                    docFrag.appendChild(node.removeChild(node.firstChild));
                }
                parent.replaceChild(docFrag, node);
                if (!this.ie) {
                    parent.normalize();
                } else {
                    this.normalizeTextNode(parent);
                }
            }
        }, {
            key: "normalizeTextNode",
            value: function normalizeTextNode(node) {
                if (!node) {
                    return;
                }
                if (node.nodeType === 3) {
                    while (node.nextSibling && node.nextSibling.nodeType === 3) {
                        node.nodeValue += node.nextSibling.nodeValue;
                        node.parentNode.removeChild(node.nextSibling);
                    }
                } else {
                    this.normalizeTextNode(node.firstChild);
                }
                this.normalizeTextNode(node.nextSibling);
            }
        }, {
            key: "markRegExp",
            value: function markRegExp(regexp, opt) {
                var _this9 = this;

                this.opt = opt;
                this.log("Searching with expression \"" + regexp + "\"");
                var totalMatches = 0,
                    fn = "wrapMatches";
                var eachCb = function eachCb(element) {
                    totalMatches++;
                    _this9.opt.each(element);
                };
                if (this.opt.acrossElements) {
                    fn = "wrapMatchesAcrossElements";
                }
                this[fn](regexp, this.opt.ignoreGroups, function (match, node) {
                    return _this9.opt.filter(node, match, totalMatches);
                }, eachCb, function () {
                    if (totalMatches === 0) {
                        _this9.opt.noMatch(regexp);
                    }
                    _this9.opt.done(totalMatches);
                });
            }
        }, {
            key: "mark",
            value: function mark(sv, opt) {
                var _this10 = this;

                this.opt = opt;
                var totalMatches = 0,
                    fn = "wrapMatches";

                var _getSeparatedKeywords = this.getSeparatedKeywords(typeof sv === "string" ? [sv] : sv),
                    kwArr = _getSeparatedKeywords.keywords,
                    kwArrLen = _getSeparatedKeywords.length,
                    sens = this.opt.caseSensitive ? "" : "i",
                    handler = function handler(kw) {
                    var regex = new RegExp(_this10.createRegExp(kw), "gm" + sens),
                        matches = 0;
                    _this10.log("Searching with expression \"" + regex + "\"");
                    _this10[fn](regex, 1, function (term, node) {
                        return _this10.opt.filter(node, kw, totalMatches, matches);
                    }, function (element) {
                        matches++;
                        totalMatches++;
                        _this10.opt.each(element);
                    }, function () {
                        if (matches === 0) {
                            _this10.opt.noMatch(kw);
                        }
                        if (kwArr[kwArrLen - 1] === kw) {
                            _this10.opt.done(totalMatches);
                        } else {
                            handler(kwArr[kwArr.indexOf(kw) + 1]);
                        }
                    });
                };

                if (this.opt.acrossElements) {
                    fn = "wrapMatchesAcrossElements";
                }
                if (kwArrLen === 0) {
                    this.opt.done(totalMatches);
                } else {
                    handler(kwArr[0]);
                }
            }
        }, {
            key: "markRanges",
            value: function markRanges(rawRanges, opt) {
                var _this11 = this;

                this.opt = opt;
                var totalMatches = 0,
                    ranges = this.checkRanges(rawRanges);
                if (ranges && ranges.length) {
                    this.log("Starting to mark with the following ranges: " + JSON.stringify(ranges));
                    this.wrapRangeFromIndex(ranges, function (node, range, match, counter) {
                        return _this11.opt.filter(node, range, match, counter);
                    }, function (element, range) {
                        totalMatches++;
                        _this11.opt.each(element, range);
                    }, function () {
                        _this11.opt.done(totalMatches);
                    });
                } else {
                    this.opt.done(totalMatches);
                }
            }
        }, {
            key: "unmark",
            value: function unmark(opt) {
                var _this12 = this;

                this.opt = opt;
                var sel = this.opt.element ? this.opt.element : "*";
                sel += "[data-markjs]";
                if (this.opt.className) {
                    sel += "." + this.opt.className;
                }
                this.log("Removal selector \"" + sel + "\"");
                this.iterator.forEachNode(NodeFilter.SHOW_ELEMENT, function (node) {
                    _this12.unwrapMatches(node);
                }, function (node) {
                    var matchesSel = DOMIterator.matches(node, sel),
                        matchesExclude = _this12.matchesExclude(node);
                    if (!matchesSel || matchesExclude) {
                        return NodeFilter.FILTER_REJECT;
                    } else {
                        return NodeFilter.FILTER_ACCEPT;
                    }
                }, this.opt.done);
            }
        }, {
            key: "opt",
            set: function set(val) {
                this._opt = _extends({}, {
                    "element": "",
                    "className": "",
                    "exclude": [],
                    "iframes": false,
                    "iframesTimeout": 5000,
                    "separateWordSearch": true,
                    "diacritics": true,
                    "synonyms": {},
                    "accuracy": "partially",
                    "acrossElements": false,
                    "caseSensitive": false,
                    "ignoreJoiners": false,
                    "ignoreGroups": 0,
                    "ignorePunctuation": [],
                    "wildcards": "disabled",
                    "each": function each() {},
                    "noMatch": function noMatch() {},
                    "filter": function filter() {
                        return true;
                    },
                    "done": function done() {},
                    "debug": false,
                    "log": window.console
                }, val);
            },
            get: function get() {
                return this._opt;
            }
        }, {
            key: "iterator",
            get: function get() {
                return new DOMIterator(this.ctx, this.opt.iframes, this.opt.exclude, this.opt.iframesTimeout);
            }
        }]);

        return Mark;
    }();

    var DOMIterator = function () {
        function DOMIterator(ctx) {
            var iframes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
            var exclude = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
            var iframesTimeout = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 5000;

            _classCallCheck(this, DOMIterator);

            this.ctx = ctx;

            this.iframes = iframes;

            this.exclude = exclude;

            this.iframesTimeout = iframesTimeout;
        }

        _createClass(DOMIterator, [{
            key: "getContexts",
            value: function getContexts() {
                var ctx = void 0,
                    filteredCtx = [];
                if (typeof this.ctx === "undefined" || !this.ctx) {
                    ctx = [];
                } else if (NodeList.prototype.isPrototypeOf(this.ctx)) {
                    ctx = Array.prototype.slice.call(this.ctx);
                } else if (Array.isArray(this.ctx)) {
                    ctx = this.ctx;
                } else if (typeof this.ctx === "string") {
                    ctx = Array.prototype.slice.call(document.querySelectorAll(this.ctx));
                } else {
                    ctx = [this.ctx];
                }

                ctx.forEach(function (ctx) {
                    var isDescendant = filteredCtx.filter(function (contexts) {
                        return contexts.contains(ctx);
                    }).length > 0;
                    if (filteredCtx.indexOf(ctx) === -1 && !isDescendant) {
                        filteredCtx.push(ctx);
                    }
                });
                return filteredCtx;
            }
        }, {
            key: "getIframeContents",
            value: function getIframeContents(ifr, successFn) {
                var errorFn = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function () {};

                var doc = void 0;
                try {
                    var ifrWin = ifr.contentWindow;
                    doc = ifrWin.document;
                    if (!ifrWin || !doc) {
                        throw new Error("iframe inaccessible");
                    }
                } catch (e) {
                    errorFn();
                }
                if (doc) {
                    successFn(doc);
                }
            }
        }, {
            key: "isIframeBlank",
            value: function isIframeBlank(ifr) {
                var bl = "about:blank",
                    src = ifr.getAttribute("src").trim(),
                    href = ifr.contentWindow.location.href;
                return href === bl && src !== bl && src;
            }
        }, {
            key: "observeIframeLoad",
            value: function observeIframeLoad(ifr, successFn, errorFn) {
                var _this13 = this;

                var called = false,
                    tout = null;
                var listener = function listener() {
                    if (called) {
                        return;
                    }
                    called = true;
                    clearTimeout(tout);
                    try {
                        if (!_this13.isIframeBlank(ifr)) {
                            ifr.removeEventListener("load", listener);
                            _this13.getIframeContents(ifr, successFn, errorFn);
                        }
                    } catch (e) {
                        errorFn();
                    }
                };
                ifr.addEventListener("load", listener);
                tout = setTimeout(listener, this.iframesTimeout);
            }
        }, {
            key: "onIframeReady",
            value: function onIframeReady(ifr, successFn, errorFn) {
                try {
                    if (ifr.contentWindow.document.readyState === "complete") {
                        if (this.isIframeBlank(ifr)) {
                            this.observeIframeLoad(ifr, successFn, errorFn);
                        } else {
                            this.getIframeContents(ifr, successFn, errorFn);
                        }
                    } else {
                        this.observeIframeLoad(ifr, successFn, errorFn);
                    }
                } catch (e) {
                    errorFn();
                }
            }
        }, {
            key: "waitForIframes",
            value: function waitForIframes(ctx, done) {
                var _this14 = this;

                var eachCalled = 0;
                this.forEachIframe(ctx, function () {
                    return true;
                }, function (ifr) {
                    eachCalled++;
                    _this14.waitForIframes(ifr.querySelector("html"), function () {
                        if (! --eachCalled) {
                            done();
                        }
                    });
                }, function (handled) {
                    if (!handled) {
                        done();
                    }
                });
            }
        }, {
            key: "forEachIframe",
            value: function forEachIframe(ctx, filter, each) {
                var _this15 = this;

                var end = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : function () {};

                var ifr = ctx.querySelectorAll("iframe"),
                    open = ifr.length,
                    handled = 0;
                ifr = Array.prototype.slice.call(ifr);
                var checkEnd = function checkEnd() {
                    if (--open <= 0) {
                        end(handled);
                    }
                };
                if (!open) {
                    checkEnd();
                }
                ifr.forEach(function (ifr) {
                    if (DOMIterator.matches(ifr, _this15.exclude)) {
                        checkEnd();
                    } else {
                        _this15.onIframeReady(ifr, function (con) {
                            if (filter(ifr)) {
                                handled++;
                                each(con);
                            }
                            checkEnd();
                        }, checkEnd);
                    }
                });
            }
        }, {
            key: "createIterator",
            value: function createIterator(ctx, whatToShow, filter) {
                return document.createNodeIterator(ctx, whatToShow, filter, false);
            }
        }, {
            key: "createInstanceOnIframe",
            value: function createInstanceOnIframe(contents) {
                return new DOMIterator(contents.querySelector("html"), this.iframes);
            }
        }, {
            key: "compareNodeIframe",
            value: function compareNodeIframe(node, prevNode, ifr) {
                var compCurr = node.compareDocumentPosition(ifr),
                    prev = Node.DOCUMENT_POSITION_PRECEDING;
                if (compCurr & prev) {
                    if (prevNode !== null) {
                        var compPrev = prevNode.compareDocumentPosition(ifr),
                            after = Node.DOCUMENT_POSITION_FOLLOWING;
                        if (compPrev & after) {
                            return true;
                        }
                    } else {
                        return true;
                    }
                }
                return false;
            }
        }, {
            key: "getIteratorNode",
            value: function getIteratorNode(itr) {
                var prevNode = itr.previousNode();
                var node = void 0;
                if (prevNode === null) {
                    node = itr.nextNode();
                } else {
                    node = itr.nextNode() && itr.nextNode();
                }
                return {
                    prevNode: prevNode,
                    node: node
                };
            }
        }, {
            key: "checkIframeFilter",
            value: function checkIframeFilter(node, prevNode, currIfr, ifr) {
                var key = false,
                    handled = false;
                ifr.forEach(function (ifrDict, i) {
                    if (ifrDict.val === currIfr) {
                        key = i;
                        handled = ifrDict.handled;
                    }
                });
                if (this.compareNodeIframe(node, prevNode, currIfr)) {
                    if (key === false && !handled) {
                        ifr.push({
                            val: currIfr,
                            handled: true
                        });
                    } else if (key !== false && !handled) {
                        ifr[key].handled = true;
                    }
                    return true;
                }
                if (key === false) {
                    ifr.push({
                        val: currIfr,
                        handled: false
                    });
                }
                return false;
            }
        }, {
            key: "handleOpenIframes",
            value: function handleOpenIframes(ifr, whatToShow, eCb, fCb) {
                var _this16 = this;

                ifr.forEach(function (ifrDict) {
                    if (!ifrDict.handled) {
                        _this16.getIframeContents(ifrDict.val, function (con) {
                            _this16.createInstanceOnIframe(con).forEachNode(whatToShow, eCb, fCb);
                        });
                    }
                });
            }
        }, {
            key: "iterateThroughNodes",
            value: function iterateThroughNodes(whatToShow, ctx, eachCb, filterCb, doneCb) {
                var _this17 = this;

                var itr = this.createIterator(ctx, whatToShow, filterCb);
                var ifr = [],
                    elements = [],
                    node = void 0,
                    prevNode = void 0,
                    retrieveNodes = function retrieveNodes() {
                    var _getIteratorNode = _this17.getIteratorNode(itr);

                    prevNode = _getIteratorNode.prevNode;
                    node = _getIteratorNode.node;

                    return node;
                };
                while (retrieveNodes()) {
                    if (this.iframes) {
                        this.forEachIframe(ctx, function (currIfr) {
                            return _this17.checkIframeFilter(node, prevNode, currIfr, ifr);
                        }, function (con) {
                            _this17.createInstanceOnIframe(con).forEachNode(whatToShow, function (ifrNode) {
                                return elements.push(ifrNode);
                            }, filterCb);
                        });
                    }

                    elements.push(node);
                }
                elements.forEach(function (node) {
                    eachCb(node);
                });
                if (this.iframes) {
                    this.handleOpenIframes(ifr, whatToShow, eachCb, filterCb);
                }
                doneCb();
            }
        }, {
            key: "forEachNode",
            value: function forEachNode(whatToShow, each, filter) {
                var _this18 = this;

                var done = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : function () {};

                var contexts = this.getContexts();
                var open = contexts.length;
                if (!open) {
                    done();
                }
                contexts.forEach(function (ctx) {
                    var ready = function ready() {
                        _this18.iterateThroughNodes(whatToShow, ctx, each, filter, function () {
                            if (--open <= 0) {
                                done();
                            }
                        });
                    };

                    if (_this18.iframes) {
                        _this18.waitForIframes(ctx, ready);
                    } else {
                        ready();
                    }
                });
            }
        }], [{
            key: "matches",
            value: function matches(element, selector) {
                var selectors = typeof selector === "string" ? [selector] : selector,
                    fn = element.matches || element.matchesSelector || element.msMatchesSelector || element.mozMatchesSelector || element.oMatchesSelector || element.webkitMatchesSelector;
                if (fn) {
                    var match = false;
                    selectors.every(function (sel) {
                        if (fn.call(element, sel)) {
                            match = true;
                            return false;
                        }
                        return true;
                    });
                    return match;
                } else {
                    return false;
                }
            }
        }]);

        return DOMIterator;
    }();

    window.Mark = function (ctx) {
        var _this19 = this;

        var instance = new Mark(ctx);
        this.mark = function (sv, opt) {
            instance.mark(sv, opt);
            return _this19;
        };
        this.markRegExp = function (sv, opt) {
            instance.markRegExp(sv, opt);
            return _this19;
        };
        this.markRanges = function (sv, opt) {
            instance.markRanges(sv, opt);
            return _this19;
        };
        this.unmark = function (opt) {
            instance.unmark(opt);
            return _this19;
        };
        return this;
    };

    return window.Mark;
}, window, document);
;
(function (root, factory) {
    /* istanbul ignore next */
    if (typeof define === 'function' && define.amd) {
        define(['angular'], factory);
    } else if (typeof module === 'object' && module.exports) {
        module.exports = factory(require('angular'));
    } else {
        root.angularClipboard = factory(root.angular);
  }
}(this, function (angular) {

return angular.module('angular-clipboard', [])
    .factory('clipboard', ['$document', function ($document) {
        function createNode(text, context) {
            var node = $document[0].createElement('textarea');
            node.style.position = 'absolute';
            node.textContent = text;
            node.style.left = '-10000px';
            if (context instanceof HTMLElement) {
                node.style.top = context.getBoundingClientRect().top + 'px';
            }
            return node;
        }

        function copyNode(node) {
            try {
                // Set inline style to override css styles
                $document[0].body.style.webkitUserSelect = 'initial';

                var selection = $document[0].getSelection();
                selection.removeAllRanges();
                node.select();

                if(!$document[0].execCommand('copy')) {
                    throw('failure copy');
                }
                selection.removeAllRanges();
            } finally {
                // Reset inline style
                $document[0].body.style.webkitUserSelect = '';
            }
        }

        function copyText(text, context) {
            var node = createNode(text, context);
            $document[0].body.appendChild(node);
            copyNode(node);
            $document[0].body.removeChild(node);
        }

        return {
            copyText: copyText,
            supported: 'queryCommandSupported' in document && document.queryCommandSupported('copy')
        };
    }])
    .directive('clipboard', ['clipboard', function (clipboard) {
        return {
            restrict: 'A',
            scope: {
                onCopied: '&',
                onError: '&',
                text: '=',
                supported: '=?'
            },
            link: function (scope, element) {
                scope.supported = clipboard.supported;

                element.on('click', function (event) {
                    try {
                        clipboard.copyText(scope.text, element[0]);
                        if (angular.isFunction(scope.onCopied)) {
                            scope.$evalAsync(scope.onCopied());
                        }
                    } catch (err) {
                        if (angular.isFunction(scope.onError)) {
                            scope.$evalAsync(scope.onError({err: err}));
                        }
                    }
                });
            }
        };
    }]);

}));
;
angular.module('ui.bootstrap.buttons', [])

.constant('buttonConfig', {
  activeClass: 'active',
  toggleEvent: 'click'
})

.controller('ButtonsController', ['buttonConfig', function(buttonConfig) {
  this.activeClass = buttonConfig.activeClass || 'active';
  this.toggleEvent = buttonConfig.toggleEvent || 'click';
}])

.directive('btnRadio', function () {
  return {
    require: ['btnRadio', 'ngModel'],
    controller: 'ButtonsController',
    link: function (scope, element, attrs, ctrls) {
      var buttonsCtrl = ctrls[0], ngModelCtrl = ctrls[1];

      //model -> UI
      ngModelCtrl.$render = function () {
        element.toggleClass(buttonsCtrl.activeClass, angular.equals(ngModelCtrl.$modelValue, scope.$eval(attrs.btnRadio)));
      };

      //ui->model
      element.bind(buttonsCtrl.toggleEvent, function () {
        var isActive = element.hasClass(buttonsCtrl.activeClass);

        if (!isActive || angular.isDefined(attrs.uncheckable)) {
          scope.$apply(function () {
            ngModelCtrl.$setViewValue(isActive ? null : scope.$eval(attrs.btnRadio));
            ngModelCtrl.$render();
          });
        }
      });
    }
  };
})

.directive('btnCheckbox', function () {
  return {
    require: ['btnCheckbox', 'ngModel'],
    controller: 'ButtonsController',
    link: function (scope, element, attrs, ctrls) {
      var buttonsCtrl = ctrls[0], ngModelCtrl = ctrls[1];

      function getTrueValue() {
        return getCheckboxValue(attrs.btnCheckboxTrue, true);
      }

      function getFalseValue() {
        return getCheckboxValue(attrs.btnCheckboxFalse, false);
      }

      function getCheckboxValue(attributeValue, defaultValue) {
        var val = scope.$eval(attributeValue);
        return angular.isDefined(val) ? val : defaultValue;
      }

      //model -> UI
      ngModelCtrl.$render = function () {
        element.toggleClass(buttonsCtrl.activeClass, angular.equals(ngModelCtrl.$modelValue, getTrueValue()));
      };

      //ui->model
      element.bind(buttonsCtrl.toggleEvent, function () {
        scope.$apply(function () {
          ngModelCtrl.$setViewValue(element.hasClass(buttonsCtrl.activeClass) ? getFalseValue() : getTrueValue());
          ngModelCtrl.$render();
        });
      });
    }
  };
});
;
(function () {
  'use strict';

  var noassessmentTemplate = "<p> If you need assistance in accessing your assessment, please <a href=\"mailto:team@qualified.io?subject=Unable%20to%20access%20assessment&body={{ ::error.onError.msgBody() }}\" target=\"_blank\"> contact the team at Qualified. </a> </p> <p ng-if=\"!error.error\"> You can also visit the <a href=\"/\">Qualified home page</a> to learn more about our services. </p> ";

  angular.extend(window.config, {
    appId: 'assess',
    candidateApp: true,
    appPath: window.config.assessRoute,
    appName: 'Assess',

    // no intercom for the assess app
    intercomWidget: false,

    onResetReturn: false,
    onError: {
      template: noassessmentTemplate,
      msgBody: function msgBody() {
        return window.encodeURIComponent('Hello,\nI was unable to access my assessment using this link:\n' + window.location.toString() + '\n');
      }
    }
  });

  var appConfig = window.config;
  var appConfigModule = angular.module('Q.config.app-config', []).run(['$rootScope', function ($rootScope) {
    $rootScope.appConfig = appConfig;
  }]);

  var angularCoreConfigModule = angular.module('Q.config.angular-log', []).config(['$logProvider', '$compileProvider', '$qProvider', function ($logProvider, $compileProvider, $qProvider) {
    // Only log debug events on dev mode
    $logProvider.debugEnabled(appConfig.devMode);

    // Only render debug data on dev mode, or when debug=true in the URL
    $compileProvider.debugInfoEnabled(appConfig.devMode || /debug=true/.test(window.location.search));

    // Prevent $q from throwing errors on unhandled rejections on newer versions
    if ($qProvider.errorOnUnhandledRejections) {
      // TODO: is it worth cleaning up the codebase for this?
      $qProvider.errorOnUnhandledRejections(false);
    }
  }]).run(['$timeout', function ($timeout) {
    // Disables pre-load error handling after the application loads
    $timeout(function () {
      // At this point, we should be able to rely on Angular for rendering errors
      appConfig.isLoaded = true;
    }, 1, false);
  }]);

  var loadingBarModule = 'cfp.loadingBarInterceptor';

  var loadingBarConfigModule = angular.module('Q.config.loading-bar', [loadingBarModule])

  /**
   * Overrides the default loading bar interceptor during a route change for
   * a smoother loading animation
   */
  .factory('loadingBarRouteHandlerInterceptor', ['$rootScope', '$timeout', 'cfpLoadingBar', function loadingBarRouteHandlerInterceptor($rootScope, $timeout, cfpLoadingBar) {
    // Used to track when we are overriding the default loading bar, so we can prevent it
    var isLoadingRoute = false;

    // always delay the loading bar a bit, so we don't show it on quick route changes
    var starterTimeout = null;

    // Automatically ignore any state without resolves, or if a state has explicitly requested that
    // the default behavior is preserved.
    function ignoreState(state) {
      return !state || !state.resolve || state.data && state.data.defaultLoadingBar;
    }

    $rootScope.$on('$stateChangeStart', function (event, toState) {
      if (ignoreState(toState)) return;

      isLoadingRoute = true;
      starterTimeout = $timeout(function () {
        cfpLoadingBar.start();
        starterTimeout = null;
      }, 100);
    });

    function complete(event, toState) {
      if (ignoreState(toState)) return;

      isLoadingRoute = false;
      if (starterTimeout) {
        $timeout.cancel(starterTimeout);
        starterTimeout = null;
      }
      cfpLoadingBar.complete();
    }

    $rootScope.$on('$stateChangeSuccess', complete);
    $rootScope.$on('$stateChangeError', complete);

    return {
      request: function request(config) {
        if (isLoadingRoute) {
          config.ignoreLoadingBar = true;
        }
        return config;
      }
    };
  }]).config(['cfpLoadingBarProvider', '$httpProvider', function (cfpLoadingBarProvider, $httpProvider) {
    // make sure our route handler interceptor is first
    $httpProvider.interceptors.unshift('loadingBarRouteHandlerInterceptor');

    cfpLoadingBarProvider.includeSpinner = false;
  }]);

  var jsDataModule = 'js-data';

  var jsDataConfigModule = angular.module('Q.config.js-data', [jsDataModule]).run(['DS', 'DSHttpAdapter', '$log', function (DS, DSHttpAdapter, $log) {
    // make sure we always re-query the local store on cached remote calls, in case the result as changed
    DS.defaults.useFilter = true;
    DSHttpAdapter.defaults.basePath = appConfig.apiRoot;
    DSHttpAdapter.defaults.log = $log.debug;
    DSHttpAdapter.defaults.error = $log.error;
  }]);

  /*
   * Largely copied from https://github.com/gsklee/ngStorage
   * Moved to an internal service so we could manage it easier
   */

  function isStorageSupported($window, storageType) {
    // Some installations of IE, for an unknown reason, throw "SCRIPT5: Error: Access is denied"
    // when accessing window.localStorage. This happens before you try to do anything with it. Catch
    // that error and allow execution to continue.

    // fix 'SecurityError: DOM Exception 18' exception in Desktop Safari, Mobile Safari
    // when "Block cookies": "Always block" is turned on
    var supported = void 0;
    try {
      supported = $window[storageType];
    } catch (err) {
      supported = false;
    }

    // When Safari (OS X or iOS) is in private browsing mode, it appears as though localStorage
    // is available, but trying to call .setItem throws an exception below:
    // "QUOTA_EXCEEDED_ERR: DOM Exception 22: An attempt was made to add something to storage that exceeded the quota."
    if (supported && storageType === 'localStorage') {
      var key = '__' + Math.round(Math.random() * 1e7);

      try {
        localStorage.setItem(key, key);
        localStorage.removeItem(key);
      } catch (err) {
        supported = false;
      }
    }

    return supported;
  }

  /**
   * @ngdoc overview
   * @name ngStorage
   */

  var storageServiceModule = angular.module('Q.services.storage', [])

  /**
   * @ngdoc object
   * @name ngStorage.$localStorage
   * @requires $rootScope
   * @requires $window
   */
  .factory('$localStorage', _storageProvider('localStorage'))

  /**
   * @ngdoc object
   * @name ngStorage.$sessionStorage
   * @requires $rootScope
   * @requires $window
   */
  .factory('$sessionStorage', _storageProvider('sessionStorage'));

  function _storageProvider(storageType) {
    return (/* @ngInject */['$rootScope', '$window', '$log', '$timeout', '$document', function ($rootScope, $window, $log, $timeout, $document) {
        var errorReported = false;

        var storageKeyPrefix = 'ngStorage-';

        var serializer = angular.toJson;
        var deserializer = angular.fromJson;

        // The magic number 10 is used which only works for some keyPrefixes...
        // See https://github.com/gsklee/ngStorage/issues/137
        var prefixLength = storageKeyPrefix.length;
        var _last$storage = void 0;
        var _debounce = void 0;

        // #9: Assign a placeholder object if Web Storage is unavailable to prevent breaking the entire AngularJS app
        // Note: recheck mainly for testing (so we can use $window[storageType] rather than window[storageType])
        var isSupported = isStorageSupported($window, storageType);
        var webStorage = isSupported;
        if (!isSupported) {
          // warn on page load
          $window.alert('Unable to use "localStorage". It is possible that cookies are disabled, this is a private tab, or there is some other storage issue.\n\nYou can still use Qualified, but you may have to re-log in every time you load it, and you could lose data.');
          $log.warn('Issue with this browser and web storage');
          webStorage = {
            setItem: angular.noop,
            getItem: angular.noop,
            removeItem: angular.noop
          };
        }
        var $storage = {
          $sync: function $sync() {
            var i = 0;
            var l = webStorage.length;
            var k = void 0;
            for (; i < l; i++) {
              // #8, #10: `webStorage.key(i)` may be an empty string (or throw an exception in IE9 if `webStorage` is empty)
              (k = webStorage.key(i)) && storageKeyPrefix === k.slice(0, prefixLength) && ($storage[k.slice(prefixLength)] = deserializer(webStorage.getItem(k)));
            }
          },
          $apply: function $apply() {
            try {
              var temp$storage = void 0;

              _debounce = null;

              if (!angular.equals($storage, _last$storage)) {
                temp$storage = angular.copy(_last$storage);
                angular.forEach($storage, function (v, k) {
                  if (angular.isDefined(v) && '$' !== k[0]) {
                    webStorage.setItem(storageKeyPrefix + k, serializer(v));
                    delete temp$storage[k];
                  }
                });

                for (var k in temp$storage) {
                  webStorage.removeItem(storageKeyPrefix + k);
                }

                _last$storage = angular.copy($storage);
              }
            } catch (ex) {
              // angular.copy may throw an error:
              // TypeError: Attempted to assign to readonly property.
              if (!errorReported) {
                errorReported = true;
                $log.error(ex);
              } else {
                // once we've reported storage errors, only log the error to the console
                $log.warn(ex);
              }
            }
          }
        };

        $storage.$sync();

        _last$storage = angular.copy($storage);

        $rootScope.$watch(function () {
          _debounce || (_debounce = $timeout($storage.$apply, 100, false));
        });

        // #6: Use `$window.addEventListener` instead of `angular.element` to avoid the jQuery-specific `event.originalEvent`
        $window.addEventListener && $window.addEventListener('storage', function (event) {
          if (!event.key) {
            return;
          }

          var doc = $document[0];

          if ((!doc.hasFocus || !doc.hasFocus()) && storageKeyPrefix === event.key.slice(0, prefixLength)) {
            event.newValue ? $storage[event.key.slice(prefixLength)] = deserializer(event.newValue) : delete $storage[event.key.slice(prefixLength)];

            _last$storage = angular.copy($storage);

            // trigger an apply if necessary
            $rootScope.$applyAsync();
          }
        });

        $window.addEventListener && $window.addEventListener('beforeunload', function () {
          $storage.$apply();
        });

        return $storage;
      }]
    );
  }

  // TAKEN FROM https://developer.mozilla.org/en-US/docs/Web/API/document/cookie
  // 1.3 version of angular-cookies does not support setting path values which we require, so using the MDN version
  // TODO: upgrade once angular 1.4 is upgraded
  function escapeCookieKey(sKey) {
    return encodeURIComponent(sKey).replace(/[\-\.\+\*]/g, '\\$&');
  }

  function getCookie(sKey) {
    if (!sKey) {
      return null;
    }
    var searchPattern = new RegExp('(?:(?:^|.*;)\\s*' + escapeCookieKey(sKey) + '\\s*\\=\\s*([^;]*).*$)|^.*$');
    return decodeURIComponent(document.cookie.replace(searchPattern, '$1')) || null;
  }

  var authInterceptorModule = angular.module('Q.interceptors.auth-interceptor', [storageServiceModule]).constant('authInterceptorKeys', {
    modes: {
      candidate: {
        loginType: 'candidate',
        storeName: 'authInterceptor.candidateToken'
      },
      embed: {
        // do not use loginType, we always rely on the manually set token instead
        storeName: 'authInterceptor.embedToken' // not used, here just in case
      },
      team: {
        loginType: 'team_member',
        storeName: 'authInterceptor.teamMemberToken'
      },
      admin: {
        loginType: 'admin',
        storeName: 'authInterceptor.adminToken'
      }
    },
    authorizationHeader: 'Authorization',
    loginTypeHeader: 'X-Login-Type'
  })

  /**
   * @docType shared
   * @category provider
   * @description
   * Provides authorization token headers
   * @name Auth-Interceptor
   * @returns {object}
   */
  .provider('authInterceptor', ['authInterceptorKeys', function authInterceptorProvider(authInterceptorKeys) {
    var _mode = void 0;

    this.setMode = function (mode) {
      if (!authInterceptorKeys.modes[mode]) {
        throw new Error('authInterceptorProvider: Invalid mode: ' + mode);
      }
      _mode = mode;
    };

    this.$get = /* @ngInject */['$localStorage', function ($localStorage) {
      var hasOldAuthToken = void 0;
      var currentAuthToken = void 0;
      if (_mode) {
        _checkMode(_mode);
        // look up localStorage cookie on load, but only on initial load
        // we'll delete this token on a successful response with a cookie
        hasOldAuthToken = !hasLoginCookie() && $localStorage[_getModeValues().storeName];
        currentAuthToken = hasOldAuthToken && $localStorage[_getModeValues().storeName];
      }

      /**
       * @parent Auth-Interceptor
       * @name _checkMode
       * @private true
       * @description
       * If there is an overridden mode, check if it's valid
       * otherwise check if the authInterceptorProvider was configured with a mode
       * @param mode The overridden mode string (or undefined)
       * @returns {undefined}
       */
      function _checkMode(mode) {
        if (mode && !authInterceptorKeys.modes[mode]) {
          throw new Error('authInterceptorProvider: Invalid mode: ' + mode);
        } else if (!authInterceptorKeys.modes[_mode]) {
          throw new Error('authInterceptor: authInterceptorProvider was never configured with a mode!');
        }
      }

      /**
       * @parent Auth-Interceptor
       * @name _getModeValues
       * @private true
       * @description
       * Manually set the authorization token
       * @param mode The overridden mode string (or the pre-configured one)
       * @returns {undefined}
       */
      function _getModeValues() {
        var mode = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : _mode;

        _checkMode(mode);
        return authInterceptorKeys.modes[mode];
      }

      /**
       * @parent Auth-Interceptor
       * @name setAuthToken
       * @description
       * Manually set the authorization token
       * @param token Value of the token
       * @returns {undefined}
       */
      function setAuthToken(token) {
        if (!token || token === 'null' || token === 'false') token = null;
        // we no longer store auth tokens in localStorage, so always delete it if we're setting the token
        // TODO: remove this eventually
        delete $localStorage[_getModeValues().storeName];
        if (token === null) {
          currentAuthToken = null;
        } else {
          currentAuthToken = token;
        }
      }

      /**
       * @parent Auth-Interceptor
       * @name getAuthToken
       * @description
       * Returns the current auth token
       * @returns {*}
       */
      function getAuthToken() {
        return currentAuthToken || null;
      }

      /**
       * @parent Auth-Interceptor
       * @name clearAuthToken
       * @description
       * Clears the current authorization token, but does not log the current user out
       * @returns {*}
       */
      function clearAuthToken() {
        setAuthToken(null);
      }

      /**
       * @parent Auth-Interceptor
       * @name hasAuthToken
       * @description
       * Returns true if the current user has an authorization token.
       * Does not guarantee that the current token is valid.
       * @returns {boolean} true if there is a token, false otherwise
       */
      function hasAuthToken() {
        return !!getAuthToken();
      }

      /**
       * @parent Auth-Interceptor
       * @name setRequestAuthToken
       * @description
       * Sets the Authorization header during any $http request, if there is a current
       * authorization token.
       * @param config $http config
       * @returns {*} possibly modified $http config.
       */
      function setRequestAuthToken(config) {
        if (_authDisabled(config)) {
          return config;
        }

        config.headers = config.headers || {};

        if (hasOldAuthToken || !getAuthToken()) {
          // always set the login type header if we have a cookie or are being asked to do so
          config.headers[authInterceptorKeys.loginTypeHeader] = _getModeValues().loginType;
        }

        if (getAuthToken()) {
          config.headers[authInterceptorKeys.authorizationHeader] = getAuthToken();
        }
        return config;
      }

      /**
       * @parent Auth-Interceptor
       * @name _authDisabled
       * @description
       * Checks to see if the http config disabled authorization
       * @param config $http config
       * @returns {boolean} whether auth is disabled
       */
      function _authDisabled(config) {
        return 'authorization' in config && !config.authorization;
      }

      /**
       * Returns true if we can find a logged in cookie or localStorage value
       * @returns {boolean}
       */
      function loggedIn() {
        return hasLoginCookie() || hasAuthToken();
      }

      /**
       * Returns true if the correct login cookie is set
       * @returns {boolean}
       */
      function hasLoginCookie() {
        return getCookie(_getModeValues().loginType + '_login') === 'true';
      }

      return {
        loggedIn: loggedIn,
        setAuthToken: setAuthToken,
        getAuthToken: getAuthToken,
        clearAuthToken: clearAuthToken,
        request: setRequestAuthToken,
        // TODO: remove this whole method eventually
        response: function response(_response) {
          // clear auth token if we get a cookie
          if (hasLoginCookie() && $localStorage[_getModeValues().storeName]) {
            // we no longer store auth tokens in localStorage, so always delete it if we're setting the token
            delete $localStorage[_getModeValues().storeName];
            // also revert the auth token
            currentAuthToken = null;
            hasOldAuthToken = false;
          }
          return _response;
        }
      };
    }];
  }]).config(['$httpProvider', function ($httpProvider) {
    $httpProvider.interceptors.push('authInterceptor');
  }]);

  var uiRouterModule = 'ui.router';
  var uiRouterStateModule = 'ui.router.state';

  var ACCOUNT_SERVICE_KEY = 'accountService';

  var accountService = void 0;

  function getAccountService($injector) {
    if (!accountService) {
      accountService = $injector.has(ACCOUNT_SERVICE_KEY) && $injector.get(ACCOUNT_SERVICE_KEY);
    }
    return accountService;
  }

  function getUser($injector) {
    var account = getAccountService($injector);
    return account && account.get().user;
  }

  function isLoggedIn($injector) {
    return !!getUser($injector);
  }

  function getUserId($injector) {
    var user = getUser($injector);
    return user ? user.id : undefined;
  }

  function getTeam($injector) {
    var user = getUser($injector);
    return user && user.team;
  }

  var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  };

  var asyncToGenerator = function (fn) {
    return function () {
      var gen = fn.apply(this, arguments);
      return new Promise(function (resolve, reject) {
        function step(key, arg) {
          try {
            var info = gen[key](arg);
            var value = info.value;
          } catch (error) {
            reject(error);
            return;
          }

          if (info.done) {
            resolve(value);
          } else {
            return Promise.resolve(value).then(function (value) {
              step("next", value);
            }, function (err) {
              step("throw", err);
            });
          }
        }

        return step("next");
      });
    };
  };

  var classCallCheck = function (instance, Constructor) {
    if (!(instance instanceof Constructor)) {
      throw new TypeError("Cannot call a class as a function");
    }
  };

  var createClass = function () {
    function defineProperties(target, props) {
      for (var i = 0; i < props.length; i++) {
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
      }
    }

    return function (Constructor, protoProps, staticProps) {
      if (protoProps) defineProperties(Constructor.prototype, protoProps);
      if (staticProps) defineProperties(Constructor, staticProps);
      return Constructor;
    };
  }();

  var defineProperty = function (obj, key, value) {
    if (key in obj) {
      Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
      });
    } else {
      obj[key] = value;
    }

    return obj;
  };

  var _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  var get = function get(object, property, receiver) {
    if (object === null) object = Function.prototype;
    var desc = Object.getOwnPropertyDescriptor(object, property);

    if (desc === undefined) {
      var parent = Object.getPrototypeOf(object);

      if (parent === null) {
        return undefined;
      } else {
        return get(parent, property, receiver);
      }
    } else if ("value" in desc) {
      return desc.value;
    } else {
      var getter = desc.get;

      if (getter === undefined) {
        return undefined;
      }

      return getter.call(receiver);
    }
  };

  var inherits = function (subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
      throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
    }

    subClass.prototype = Object.create(superClass && superClass.prototype, {
      constructor: {
        value: subClass,
        enumerable: false,
        writable: true,
        configurable: true
      }
    });
    if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
  };

  var possibleConstructorReturn = function (self, call) {
    if (!self) {
      throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }

    return call && (typeof call === "object" || typeof call === "function") ? call : self;
  };

  var slicedToArray = function () {
    function sliceIterator(arr, i) {
      var _arr = [];
      var _n = true;
      var _d = false;
      var _e = undefined;

      try {
        for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
          _arr.push(_s.value);

          if (i && _arr.length === i) break;
        }
      } catch (err) {
        _d = true;
        _e = err;
      } finally {
        try {
          if (!_n && _i["return"]) _i["return"]();
        } finally {
          if (_d) throw _e;
        }
      }

      return _arr;
    }

    return function (arr, i) {
      if (Array.isArray(arr)) {
        return arr;
      } else if (Symbol.iterator in Object(arr)) {
        return sliceIterator(arr, i);
      } else {
        throw new TypeError("Invalid attempt to destructure non-iterable instance");
      }
    };
  }();

  var toConsumableArray = function (arr) {
    if (Array.isArray(arr)) {
      for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

      return arr2;
    } else {
      return Array.from(arr);
    }
  };

  var stateHelperModule = angular.module('Q.helpers.state-helper', [uiRouterStateModule])

  /**
   * Provides some simple helpers for dealing with uiRouter $state issues
   */
  .factory('stateHelper', ['$state', '$q', '$rootScope', '$injector', '$timeout', function ($state, $q, $rootScope, $injector, $timeout) {
    var stateHelper = {
      /**
       * Redirects immediately during a resolve, by rejecting the current resolve.
       * This style redirect only works if you don't care about the current state name or parameters.
       *
       * @example
       *
       * resolve: {
       *     foo(stateHelper) {
       *         if(test) {
       *             return stateHelper.redirectInResolve('someState', { param: true });
       *         } // else like normal
       *     }
       * }
       *
       * @param state State to redirect to (required)
       * @param params {*?} Parameters for the state (optional)
       * @param options {*?} Options for $state.go (optional)
       * @returns {Promise}
       */
      redirectInResolve: function redirectInResolve(state, params, options) {
        return $q.reject({ redirectTo: { state: state, params: params, options: options } });
      },


      /**
       * Redirects after a state change. Only redirects if the state change is successful.
       * This uses scope events to detect a successful state change, and redirects at that time.
       * If there's a state change error, the redirect is canceled.
       *
       * @param state State to redirect to (required)
       * @param params {*?} Parameters for the state (optional)
       * @param options {*?} Options for $state.go (optional)
       */
      redirectAfterResolve: function redirectAfterResolve(state, params, options) {
        $rootScope.stateHelperRedirecting = true;
        var unWatchSuccess = void 0;
        var unWatchError = void 0;
        unWatchSuccess = $rootScope.$on('$stateChangeSuccess', function () {
          unWatchError();
          unWatchSuccess();
          $timeout(function () {
            $rootScope.stateHelperRedirecting = false;
            $state.go(state || '.', params, options);
          });
        });
        unWatchError = $rootScope.$on('$stateChangeError', function () {
          unWatchError();
          unWatchSuccess();
          $rootScope.stateHelperRedirecting = false;
        });
      },


      /**
       * Enables automatic redirecting to the default filter or user's preference if no filter is
       * defined.
       *
       * @param stateName
       * @param stateFilters
       * @returns {*|Promise}
       */
      defaultFilters: function defaultFilters(stateName, stateFilters) {
        var filterId = stateName + 'StateFilter';
        var redirectParams = false;
        if (!$state.activeParams[filterId]) {
          // switch to users or systems default route

          // set to default in case we can't use the user's
          redirectParams = defineProperty({}, filterId, stateFilters.default.id);

          var accountService = getAccountService($injector);
          var defaultViews = {};
          if (accountService) {
            defaultViews = accountService.prefs().defaultviews || {};
          }
          if (defaultViews[stateName]) {
            var params = defaultViews[stateName];
            // only redirect if it's a valid state filter, to prevent immediate 404s
            if (stateFilters.find(function (filter) {
              return filter.id === params[filterId];
            })) {
              // assume we can handle all the other params without completely breaking
              redirectParams = params;
            }
          }
        }

        if (redirectParams) {
          // using active state prevents us from breaking deeper redirects
          var updatedState = $state.activeState || stateName;
          // merge the default params with any active params
          var updatedParams = angular.extend({}, $state.activeParams, redirectParams);
          return stateHelper.redirectInResolve(updatedState, updatedParams);
        }
      }
    };

    return stateHelper;
  }]);

  var paginationHelperModule = angular.module('Q.helpers.pagination-helper', [uiRouterStateModule, stateHelperModule]).factory('paginationHelper', ['$state', 'stateHelper', function ($state, stateHelper) {
    var clamp = function clamp(min, n, max) {
      return Math.max(min, Math.min(max, n));
    };
    var protect = function protect(n) {
      return isNaN(n) ? 0 : n;
    };

    return {
      /**
       * Calculate pagination data for a set of items
       * @param total {number} Total number of items
       * @param perPage {number} Number of items per page
       * @param page {number} Current page, defaults to 1
       * @param currentIndex {number} Current item index, will override opts.page
       * @param minPerPage {number} minimum allowed page size, defaults to 1
       * @param maxPerPage {number} max allowed page size, defaults to Infinity
       * @param buffer {number} Allowed overage to disable pagination (defaults to 1 if perPage is > 2)
       * @returns {*} A collection of properties that can be used to render information about the current page
       */
      calc: function calc() {
        var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
            total = _ref.total,
            perPage = _ref.perPage,
            page = _ref.page,
            currentIndex = _ref.currentIndex,
            _ref$minPerPage = _ref.minPerPage,
            minPerPage = _ref$minPerPage === undefined ? 1 : _ref$minPerPage,
            _ref$maxPerPage = _ref.maxPerPage,
            maxPerPage = _ref$maxPerPage === undefined ? Infinity : _ref$maxPerPage,
            _ref$buffer = _ref.buffer,
            buffer = _ref$buffer === undefined ? 1 : _ref$buffer;

        perPage = +perPage;
        var currentPerPage = clamp(minPerPage, perPage || minPerPage, maxPerPage);

        if (buffer === 1 && currentPerPage < 3) buffer = 0;

        if (total <= currentPerPage + buffer) {
          // override perPage since we only have «buffer» items on the next page
          currentPerPage = total;
        }

        var pages = Math.ceil(protect(total / currentPerPage));

        currentIndex = Math.floor(currentIndex);

        if (isNaN(currentIndex) || currentIndex < 0 || currentIndex >= total) {
          currentIndex = false;
          page = clamp(1, Math.floor(page) || 1, pages);
        } else {
          page = Math.floor(protect(currentIndex / currentPerPage)) + 1;
        }

        var first = (page - 1) * currentPerPage;

        return {
          total: total,
          perPage: perPage,
          currentPerPage: currentPerPage,
          minPerPage: minPerPage,
          maxPerPage: maxPerPage,
          page: page,
          pages: pages,
          first: first,
          last: Math.min(total, first + currentPerPage) - 1,
          hasPrev: page > 1,
          hasNext: page < pages,
          currentIndex: currentIndex
        };
      },


      /**
       * Updates an autolist's pagination, and, if provided, sets the uiRouter param for the correct
       * page.
       *
       * Designed to be used within a promise chain for the item being paged to.
       *
       * @usage
       * Model.get(id)
       *     .then(paginationHelper.updateList(modelList, 'modelPage'));
       *
       * @param list List to update
       * @param param Optional uiRouter state param to update
       * @returns {Function} to be added to the promise chain
       */
      updateList: function updateList(list, param) {
        return function (item) {
          if (item && (angular.isString(item) || item.id)) {
            var page = list.setPageFor(item);
            if (param && page !== +$state.params[param] && $state.current && !$state.current.abstract) {
              stateHelper.redirectAfterResolve('.', defineProperty({}, param, page));
            }
          }
          return item;
        };
      }
    };
  }]);

  function noop() {}

  function identity(v) {
    return v;
  }

  /**
   * Creates a memoized function based on the first argument. Any repeated calls to the generated function with the
   * same first argument will return the original value.
   *
   * Return values are stored in a Map, so they will persist as long as there's a reference to the returned function
   * in memory.
   *
   * @param fn
   * @returns {function(...[*]): any}
   */
  function memo(fn) {
    var memory = new Map();
    var memoFn = function memoFn(key) {
      if (!memory.has(key)) {
        for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }

        memory.set(key, fn.apply(undefined, [key].concat(args)));
      }
      return memory.get(key);
    };
    memoFn.delete = function (key) {
      return memory.delete(key);
    };
    memoFn.clear = function () {
      return memory.clear();
    };
    return memoFn;
  }

  /**
   * Creates a memoized function based on a mapped value from the arguments. Any repeated calls to the generated
   * that lead to the same key will return the original value.
   *
   * This method takes two methods, one to translate the arguments to a key, and a second that processes the arguments
   * if we have not already processed that key.
   *
   * The second argument will be provided with an additional parameter, the key, which will be the first argument to
   * the function.
   *
   * Return values are stored in a Map, so they will persist as long as there's a reference to the returned function
   * in memory.
   *
   * @param {function(...[*])} mapper - Method to map the arguments to a key
   * @param {function(*, ...[*])} fn
   * @returns {function(...[*]): any}
   */
  function mappedMemo(mapper, fn) {
    var memory = new Map();
    var memoFn = function memoFn() {
      for (var _len2 = arguments.length, args = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        args[_key2] = arguments[_key2];
      }

      var key = mapper.apply(undefined, args);
      if (!memory.has(key)) {
        memory.set(key, fn.apply(undefined, [key].concat(args)));
      }
      return memory.get(key);
    };
    memoFn.delete = function () {
      return memory.delete(mapper.apply(undefined, arguments));
    };
    memoFn.clear = function () {
      memory.clear();
    };
    return memoFn;
  }

  function isPrimitive(v) {
    return !isFunction(v) && !isObject(v);
  }

  function isString(s) {
    return typeof s === 'string';
  }

  function isObject(value) {
    return value !== null && (typeof value === 'undefined' ? 'undefined' : _typeof(value)) === 'object';
  }

  function isFunction(v) {
    return typeof v === 'function';
  }

  var isArray = Array.isArray;

  function isDate(v) {
    return Object.prototype.toString.call(v) === '[object Date]';
  }

  var separatedWords = /([a-z0-9])[^a-z0-9]([a-z0-9])/gi;
  var camelCasedWords = /([a-z0-9])([A-Z])/g;

  var htmlSymbols = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;'
  };

  var toString = String;

  var pastTenseExceptions = {
    are: 'were',
    eat: 'ate',
    go: 'went',
    have: 'had',
    inherit: 'inherited',
    is: 'was',
    run: 'ran',
    sit: 'sat',
    visit: 'visited'
  };

  var specialOrdinalSuffixes = {
    1: 'st',
    2: 'nd',
    3: 'rd',
    11: 'th',
    12: 'th',
    13: 'th'
  };

  // taken from https://gist.github.com/letsgetrandy/1e05a68ea74ba6736eb5
  function pastTense(verb) {
    // if already past tensed
    if (/ed$/.test(verb)) {
      return verb;
    }
    if (pastTenseExceptions[verb]) {
      return pastTenseExceptions[verb];
    }
    if (/e$/i.test(verb)) {
      return verb + 'd';
    }
    if (/[aeiou]c$/i.test(verb)) {
      return verb + 'ked';
    }
    if (/el$/i.test(verb)) {
      return verb + 'ed';
    }
    if (/[aeio][aeiou][dlmnprst]$/.test(verb)) {
      return verb + 'ed';
    }
    if (/[aeiou][b-df-hj-np-tv-z]+[aeiou][bdglmnprst]$/i.test(verb)) {
      return verb + 'ed';
    }
    if (/[aeiou][bdglmnprst]$/i.test(verb)) {
      return verb.replace(/(.+[aeiou])([bdglmnprst])/, '$1$2$2ed');
    }
    return verb + 'ed';
  }

  function upperCaseWords(str) {
    return toString(str).replace(/(?:^|\s)\S/g, function (a) {
      return a.toUpperCase();
    });
  }

  function fieldToLabel(field) {
    return upperCaseWords(trim(snakeCase(field).replace(/_/g, ' ')));
  }

  function snakeCase(str) {
    return _replaceCase(str, '$1_$2').toLowerCase();
  }

  function pascalCase(str) {
    return ucFirst(toString(str).replace(separatedWords, function (m, l1, l2) {
      return l1 + l2.toUpperCase();
    }));
  }

  function camelCase(str) {
    return lcFirst(pascalCase(str));
  }

  function ucFirst(str) {
    str = toString(str);
    return str.length === 0 ? str : str[0].toUpperCase() + str.slice(1);
  }

  function lcFirst(str) {
    str = toString(str);
    return str.length === 0 ? str : str[0].toLowerCase() + str.slice(1);
  }

  function compare(a, b) {
    a = toStringEmpty(a);
    b = toStringEmpty(b);
    return a === b ? 0 : a < b ? -1 : 1;
  }

  function compareIgnoreCase(a, b) {
    return compare(toStringEmpty(a).toUpperCase(), toStringEmpty(b).toUpperCase());
  }

  function escapeHTML(str) {
    return toString(str).replace(/&amp;/g, '&').replace(/[&<>]/g, function (m) {
      return htmlSymbols[m];
    });
  }

  function escapeRegex(str) {
    return toString(str).replace(/[.?*+^$[\]\\(){}|-]/g, '\\$&');
  }

  function slugify(str) {
    return toString(str).replace(/^\W+|\W+$/g, '').replace(/\W+/g, '-').toLowerCase();
  }

  function encodeForFirebaseRef(str) {
    return encodeURIComponent(toString(str)).replace(/\./g, '%46');
  }

  function toStringEmpty(str) {
    if (str === null || str === undefined) {
      return '';
    } else {
      return toString(str);
    }
  }

  function uuid() {
    // taken from http://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
    var d = new Date().getTime();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : r & 0x3 | 0x8).toString(16);
    });
  }

  /**
   * Based on Java's String hash method, converts a string to a 32-bit integer hash
   * @param data Anything, will be converted to a string if it isn't already
   * @returns {number}
   */
  function hashCode(data) {
    data = toString(data);
    // from http://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
    var hash = 0;
    for (var i = 0; i < data.length; i++) {
      hash = (hash << 5) - hash + data.charCodeAt(i);
      hash = hash & hash;
    }
    return hash;
  }

  function ordinalSuffix(n) {
    var nString = toString(n);
    return specialOrdinalSuffixes[nString.slice(-2)] || specialOrdinalSuffixes[nString.slice(-1)] || 'th';
  }

  function trim(any) {
    if (any === null || any === undefined) any = '';
    return toString(any).trim();
  }

  /**
   * Adds enough leading '0' to ensure the string is the expected width
   * @param {*} n - Number to pad
   * @param {number} len - Minimum length of the new string (defaults to 2)
   * @returns {string}
   */
  function padNum(n) {
    var len = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;

    var s = toStringEmpty(n);
    return '0'.repeat(Math.max(0, len - s.length)) + s;
  }

  function limit(str) {
    var len = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 100;
    var append = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '…';
    var trimAtNewlines = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;

    str = trim(str);
    if (trimAtNewlines && /\n/.test(str)) {
      str = str.match(/^[^\n]+/) + append;
    }
    return str.length <= len ? str : str.slice(0, len - append.length) + append;
  }

  /**
   * Ensures the string starts with a leading slash
   * @param {string} str
   * @returns {string}
   */
  function ensureLeadingSlash(str) {
    if (str[0] !== '/') str = '/' + str;
    return str;
  }

  function absoluteUrl(path) {
    var link = document.createElement('a');
    link.href = path;
    return link.protocol + '//' + link.host + link.pathname + link.search + link.hash;
  }

  //// private functions

  function _replaceCase(str, replacement) {
    return toString(str).replace(separatedWords, replacement).replace(camelCasedWords, replacement);
  }

  /**
   * Adds an item to an array if it doesn't already exist.
   * @param {Array} array
   * @param {*} item
   */
  function addUnique(array, item) {
    if (!array.includes(item)) {
      array.push(item);
    }
  }

  /**
   * Removes an item from an array if that item is found.
   * @param {Array} array
   * @param {*} item
   * @returns {boolean} True if the item is found.
   */
  function remove(array, item) {
    var i = array.indexOf(item);
    var found = i >= 0;
    if (found) array.splice(i, 1);
    return found;
  }

  /**
   * Removes any items in an array based on a matcher function. If the matcher function returns true, the item is
   * removed from the array.
   * @param {Array} array
   * @param {Function} matcher - Receives the item, index, and original array. If it returns `true`, the
   *   item is removed from the array.
   */
  function removeMatching(array, matcher) {
    for (var i = array.length - 1; i >= 0; i--) {
      if (matcher(array[i], i, array)) {
        array.splice(i, 1);
      }
    }
  }

  /**
   * Sorts array value objects by the key or function provided.
   * @param {array} values - Array of values to be sorted
   * @param {function|string} field - First field to sort by
   * @param {function|string?} field2 - Optional second field to sort by
   * @return {array}
   */
  function sortBy(values, field, field2) {
    return Array.from(values).sort(function (a, b) {
      var result = sortValue(a, b, field);
      return field2 ? result || sortValue(a, b, field2) : result;
    });
  }

  function sortValue(aObj, bObj, field) {
    var a = aObj && aObj[field];
    var b = bObj && bObj[field];
    if (typeof field === 'function') {
      a = field(aObj);
      b = field(bObj);
    }

    if (typeof a === 'string' || typeof b === 'string') {
      if (a === b) return 0;
      return a < b ? -1 : 1;
    } else {
      return a - b;
    }
  }

  /**
   * Safely returns a new array without any falsy items in it.
   * @param {*} array
   * @returns {Array} New array without any falsy items in it.
   */
  function compactFalsy(array) {
    return empty(array) ? [] : array.filter(identity);
  }

  /**
   * Removes any duplicates from an array, returning a new array.
   * @param {Array} array
   * @param {Function?} finder - Optional function for identifier
   * @returns {Array} New array without duplicates
   */
  function unique(array) {
    var finder = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : identity;

    var found = {};
    array.forEach(function (v) {
      var k = finder(v);
      if (!(k in found)) {
        found[k] = v;
      }
    });

    return Object.values(found);
  }

  /**
   * Returns true if the provided object is not array-like or has a length of 0
   * @param {*} array
   * @returns {boolean}
   */
  function empty(array) {
    return !array || array.length === 0;
  }

  /**
   * Inserts new items before an existing element in the provided array, or at the beginning if the item doesn't exist.
   *
   * @param {Array} array
   * @param {*} existing - Element to use as a reference
   * @param {*} newItems - New items to insert
   */
  function insertBefore(array, existing) {
    var idx = array.indexOf(existing);

    for (var _len = arguments.length, newItems = Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
      newItems[_key - 2] = arguments[_key];
    }

    if (idx === -1) {
      array.unshift.apply(array, newItems);
    } else {
      array.splice.apply(array, [idx, 0].concat(newItems));
    }
  }

  /* eslint-disable angular/no-private-call */

  var autolistHelperModule = angular.module('Q.helpers.autolist', [paginationHelperModule])

  /**
   * This service is used to create lists that will automatically update when items
   * are added or removed during push state events.
   *
   * It also can be used to return a list that is filled asynchronously, or a list that needs to be
   * manually re-updated.
   *
   * The list can also track a filter or sort, and automatically reapply the filter and sort to the
   * source list when updating.
   *
   * Autolists also provide a consistent promise interface even to synchronous lookup functions, via
   * list.promise.
   *
   */

  .factory('autolist', ['$rootScope', '$q', '$timeout', 'paginationHelper', function ($rootScope, $q, $timeout, paginationHelper) {
    var uniqueLists = {};

    var SOCKET_DEBOUNCE = 'socketDebounceTimeout';
    var WATCHER_DEBOUNCE = 'watcherDebounceTimeout';

    return {
      create: create
    };

    //// Public Functions

    /**
     * Creates a new autolist based on the provided lookup function
     *
     * @param lookupFunction {Function} function that generates the initial list
     * @param Model {Object?} Model that this list will contain (helps narrow down event listeners)
     * @param options {Object?}
     * @param options.autolistId {String?} If provided, will only update if this list is the current version of the unique ID
     * @param options.modifyResult {Function<result,autolist>?} function called after list is processed
     * @returns {Array}
     */
    function create(lookupFunction, Model) {
      var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
          autolistId = _ref.autolistId,
          _modifyResult = _ref.modifyResult;

      var list = [];

      angular.forEach({
        $$cache: {
          lookupFunction: lookupFunction,
          Model: Model,
          autolistId: autolistId
        },
        canUpdate: canUpdate,
        update: update,
        autoSort: autoSort,
        autoFilter: autoFilter,
        paginate: paginate,
        setPageFor: setPageFor,
        modifyResult: modifyResult,
        processUpdate: processUpdate,
        listen: listen,
        stopListening: stopListening,
        destroy: destroy,
        originalList: [],
        filteredList: null,
        sortedList: null,
        pg: {},
        hasLoaded: false,
        isEventUpdate: false,
        isWatchUpdate: false
      }, function (value, prop) {
        Object.defineProperty(list, prop, { value: value, writable: true });
      });

      if (autolistId) {
        uniqueLists[autolistId] = list;
      }

      if (_modifyResult) {
        list.modifyResult(_modifyResult);
      }

      list.update();

      return list;
    }

    //// Public Functions on an autolist

    /**
     * Returns false if this model should not be updated
     * @returns {boolean}
     */
    function canUpdate() {
      var list = this;
      return !list.$destroyed && (!list.$$cache.autolistId || uniqueLists[list.$$cache.autolistId] === list);
    }

    /**
     * Updates the list by reprocessing the original lookupFunction,
     * and reapplying any filters or sorts on the result.
     *
     * A promise for the async (or even synchronous) lookup can be accessed via
     * list.promise
     *
     * @param options {object} Options for processing the list this time only
     * @param options.sticky {bool} Override the sticky state for this update
     * @param options.changes {object} Change info for this update
     * @param options.event {object} Event info for this update
     * @returns {Array} self
     */
    function update() {
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      var list = this;

      if (!list.canUpdate()) return list;
      list.updatingError = false;

      function markLoaded() {
        list.hasLoaded = true;
        list.updatingError = false;
        list.dirty = false;
        $timeout(function () {
          return list.finishedLoading = true;
        }, 1);
      }

      if (options.changes && Object.keys(options.changes).length && !options.changes.all && list.originalList.length) {
        // don't reload if we're only handling local sort or pagination changes
        markLoaded();
        _processResult(list, list.originalList, options);
        list.promise = $q.when(list);
      } else {
        var result = list.$$cache.lookupFunction(options);
        if (result.then) {
          list.updating = true;
          list.promise = result.then(function (data) {
            list.updating = false;
            markLoaded();
            return _processResult(list, data, options);
          }, function (err) {
            markLoaded();
            list.updatingError = err || true;
            return $q.reject(err);
          });
        } else {
          markLoaded();
          _processResult(list, result, options);
          list.promise = $q.when(list);
        }
      }

      return list;
    }

    /**
     * Creates an auto sorting function on this list.  This sort will be reapplied
     * if the list contents get updated.
     *
     * @param fn {function|bool} sort function, true for default sort, or false to disable the sort
     * @returns {Array} self
     */
    function autoSort(fn) {
      var list = this;
      list.$$cache.sort = fn;
      _processResult(list, undefined, { changes: { sort: true } });
      return list;
    }

    /**
     * Creates an auto filtering function on this list.  This filter will be reapplied
     * if the list contents get updated.
     *
     * @param fn {function} filter function
     * @param sticky {bool} If provided, don't remove items from the list when updating
     * @returns {Array} self
     */
    function autoFilter(fn) {
      var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
          sticky = _ref2.sticky;

      var list = this;
      list.$$cache.filter = fn;
      _processResult(list);
      // we set sticky _after_ the first time the list is filtered, otherwise the list is
      // never filtered!
      list.$$cache.sticky = sticky;
      return list;
    }

    /**
     * Configures automatic pagination
     * @param opts {object|function} The options to pass into paginate
     * @returns {Array} self
     */
    function paginate(opts) {
      var list = this;
      list.$$cache.pagination = opts;
      _processResult(list, undefined, { changes: { pagination: true } });
      return list;
    }

    /**
     * Switches to the page for the provided item or id
     * @param itemOrId
     * @returns {number} page we switched to (for updating routes, etc)
     */
    function setPageFor(itemOrId) {
      var list = this;
      _processResult(list, undefined, {
        paginationCurrentItem: itemOrId,
        changes: { pagination: true }
      });
      return list.pg.page;
    }

    /**
     * If provided, allows modification of the final list after updates, filtering, sorting, and pagination
     * @param fn {function<Array,autolist>}
     */
    function modifyResult(fn) {
      var list = this;
      list.$$cache.modifyResult = fn;
      if (list.hasLoaded) {
        _processResult(list);
      }
      return this;
    }

    /**
     * Runs a function on the list during every update.  This function is called before the filter
     * or sort are applied.
     *
     * The function is provided with two arguments: the new data, and the autolist in it's current state.
     *
     * If the function returns an array, that array is assumed to replace the new data.
     * If the function returns false, the update is halted.
     * All other results from the function are discarded.
     *
     * This function could be used to load missing relationships, or perform per-item modifications.
     *
     * @param fn Function to run after every update
     * @returns {Array} self
     */
    function processUpdate(fn) {
      this.$$cache.processUpdate = fn;
      return _processResult(this);
    }

    /**
     * Creates a listener on the provided scope (recommended) or $rootScope if one is not provided.
     * This listener will listen to create and destroy events for the provided model, as well as the optional
     * watcher function.
     *
     * Watcher events are assumed to be important, and are updated immediately. They are debounced to .5s by
     * default, ensuring relatively quick updates while ignoring multiple sequential event triggers.
     *
     * Socket and model events are delayed and debounced to 15s by default. This helps cut down on refresh noise.
     * Socket events can also be disabled completely using `updated: false`, which will only mark the list as
     * `dirty`, and not cause an update. In this case, calling `list.update()` will reload the list immediately
     * and clear the dirty flag.
     *
     * If no local scope is provided, it is up to the endpoint to call stopListening or the returned
     * deregistration function on this list, or there *will* be a memory leak and performance issues.
     *
     * @param {$rootScope.Scope} scope Local scope to attach to
     * @param {boolean?} update If false, don't update the list, just mark it as dirty
     * @param {number?} socketDebounce Adjust the debounce rate for socket updates. Defaults to 15,000ms (15 seconds).
     * @param {number?} socketInitialDelay Always delay socket updates by this amount. Defaults to 100ms.
     * @param {number?} watcherDebounce Adjust the debounce rate for watcher updates. Defaults to 500ms.
     * @param {number?} watcherInitialDelay Always delay watched updates by this amount. Defaults to 0ms, which still groups multiple events per digest.
     * @param {Function?} watcher Additional watcher function (updates on change except the first)
     * @param {boolean?} deepWatch If using the additional watcher, is this a deep watch?  Defaults to false.
     * @param {string?} sortKey If provided, helps determine if we only need to sort (and paginate) the results without reloading (during watch)
     * @param {string?} pageKey If provided, helps determine if we only need to paginate the results without reloading (during watch)
     * @param {Function|boolean?} sticky If provided, only update items within the list (using Model.find()) when we can.
     * @returns {Function} deregistration function
     */
    function listen() {
      var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref3$scope = _ref3.scope,
          scope = _ref3$scope === undefined ? $rootScope : _ref3$scope,
          _ref3$update = _ref3.update,
          update = _ref3$update === undefined ? true : _ref3$update,
          _ref3$socketDebounce = _ref3.socketDebounce,
          socketDebounce = _ref3$socketDebounce === undefined ? 15000 : _ref3$socketDebounce,
          _ref3$socketInitialDe = _ref3.socketInitialDelay,
          socketInitialDelay = _ref3$socketInitialDe === undefined ? 100 : _ref3$socketInitialDe,
          _ref3$watcherDebounce = _ref3.watcherDebounce,
          watcherDebounce = _ref3$watcherDebounce === undefined ? 500 : _ref3$watcherDebounce,
          _ref3$watcherInitialD = _ref3.watcherInitialDelay,
          watcherInitialDelay = _ref3$watcherInitialD === undefined ? 0 : _ref3$watcherInitialD,
          _ref3$watcher = _ref3.watcher,
          watcher = _ref3$watcher === undefined ? null : _ref3$watcher,
          _ref3$deepWatch = _ref3.deepWatch,
          deepWatch = _ref3$deepWatch === undefined ? false : _ref3$deepWatch,
          _ref3$sortKey = _ref3.sortKey,
          sortKey = _ref3$sortKey === undefined ? null : _ref3$sortKey,
          _ref3$pageKey = _ref3.pageKey,
          pageKey = _ref3$pageKey === undefined ? null : _ref3$pageKey,
          _ref3$sticky = _ref3.sticky,
          sticky = _ref3$sticky === undefined ? null : _ref3$sticky;

      var list = this;
      list.stopListening();

      var $$cache = list.$$cache;
      $$cache.listeners = [];

      var changes = {};
      var event = {};
      var ids = [];

      function _handleUpdate() {
        // reset any updates that came in before the update started.
        delete $$cache.hasDebouncedUpdates;

        // if we're in sticky mode, and every id we've caught is in our visible list,
        // then don't reload the list
        if (sticky && ids.length && ids.every(function (id) {
          return list.some(function (item) {
            return item.id === id;
          });
        })) {
          list.forEach(function (item, index) {
            if (ids.includes(item.id)) {
              $$cache.Model.find(item.id, { bypassCache: true }).then(function (newItem) {
                list[index] = newItem;
              });
            }
          });

          // prevent reloading the whole list if we can simply refresh individual items
          changes.all = false;
        }
        list.update({ changes: changes, event: event });
        ids = [];
        changes = {};
        event = {};
      }

      // sets up a debounced update, which will automatically update after the timeout if there's a change
      // while waiting. Never updates faster than `debounce` milliseconds
      function _startDebounce(key, debounce) {
        $$cache[key] = $timeout(function () {
          delete $$cache[key];
          if ($$cache.hasDebouncedUpdates) {
            delete $$cache.hasDebouncedUpdates;
            _debouncedUpdate(key, 0, debounce);
          }
        }, debounce);
      }

      // If there's a delay in progress, marks that we have more changes and returns
      // otherwise starts an update immediately, then sets up a delay
      function _debouncedUpdate(key, initialDelay, debounce) {
        if ($$cache[key]) {
          $$cache.hasDebouncedUpdates = true;
          return;
        }
        list.dirty = true;

        // update after initialDelay to capture multiple rapid events
        $timeout(_handleUpdate, initialDelay);

        _startDebounce(key, debounce);
      }

      // listens to changes on sockets and model events, triggering debounced updates whenever one happens
      function modelChangedListener($event, data, type) {
        list.isEventUpdate = true;
        changes.all = true;
        event.data = data;
        event.type = type;
        if (data && data.id) {
          addUnique(ids, data.id);
        }
        list.dirty = true;
        if (update) {
          _debouncedUpdate(SOCKET_DEBOUNCE, socketInitialDelay, socketDebounce);
        }
      }

      if (update) {
        // Prevent updating immediately if we're performing updates.
        // This way the first update cannot happen until after the first socketDebounce.
        _startDebounce(SOCKET_DEBOUNCE, socketDebounce);
      }

      // configure all socket and model events
      var events = ['socket:model.changed', 'model.changed'];
      if ($$cache.Model) {
        events = events.map(function (n) {
          return n + ':' + $$cache.Model.name;
        });
      }
      events.forEach(function (event) {
        $$cache.listeners.push(scope.$on(event, modelChangedListener));
      });

      // configure watcher listener
      if (watcher) {
        var watchListener = scope.$watch(watcher, function (newVal, oldVal) {
          if (newVal && !angular.equals(newVal, oldVal)) {
            if (list.$$cache.clearTypeFlagTimer) {
              $timeout.cancel(list.$$cache.clearTypeFlagTimer);
            }
            list.isWatchUpdate = true;
            Object.keys(newVal).forEach(function (k) {
              if (newVal[k] !== oldVal[k]) {
                if (k.match(pageKey)) {
                  changes.pagination = true;
                } else if (k.match(sortKey)) {
                  changes.sort = true;
                } else {
                  changes.all = true;
                }
              }
            });

            _debouncedUpdate(WATCHER_DEBOUNCE, watcherInitialDelay, watcherDebounce);
          }
        }, deepWatch);
        $$cache.listeners.push(watchListener);
      }

      return list.stopListening.bind(list);
    }

    /**
     * Deregisters the listener on this list.
     */
    function stopListening() {
      var $$cache = this.$$cache;
      if ($$cache.listeners) {
        $$cache.listeners.forEach(function (l) {
          return l();
        });
        $$cache.listeners = null;
      }
      [WATCHER_DEBOUNCE, SOCKET_DEBOUNCE].forEach(function (k) {
        if ($$cache[k]) {
          $timeout.cancel($$cache[k]);
          delete $$cache[k];
        }
      });
    }

    /**
     * Destroys the auto list, stops listening, prevents further handling, etc.
     */
    function destroy() {
      var list = this;
      list.$destroyed = true;
      list.stopListening();
      list.length = 0;
      if (list.$$cache.autolistId && uniqueLists[list.$$cache.autolistId] === list) {
        delete uniqueLists[list.$$cache.autolistId];
      }
      Object.keys(list.$$cache).forEach(function (k) {
        delete list.$$cache[k];
      });
      ['originalList', 'filteredList', 'sortedList', 'pg'].forEach(function (k) {
        list[k] = undefined;
      });
      ['update', 'autoSort', 'autoFilter', 'paginate', 'setPageFor', 'processUpdate', 'listen', 'stopListening', 'destroy'].forEach(function (k) {
        list[k] = angular.noop;
      });
    }

    //// Private Functions

    /**
     * Handles processing a result, by running the auto filter and auto sort over it
     *
     * @param list autolist
     * @param result optional new list data
     * @param options {Object} pass in extra options to override the autolist's settings
     * @returns {Array} original autolist, now updated
     * @private
     */
    function _processResult(list) {
      var result = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : list.originalList;
      var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

      if (!list.canUpdate()) return;

      var $$cache = angular.extend({}, list.$$cache, options);

      var changes = options.changes;
      if (!changes || !(changes.sort || changes.pagination)) changes = { all: true };
      changes.newResult = result !== list.originalList;

      if (changes.all || !result.length || !list.filteredList) {
        result = _processAndFilter(list, result, $$cache);
      } else {
        result = list.filteredList;
      }

      if (changes.all || changes.sort || !list.sortedList) {
        // sort the list (only happens if there is a cached sort function)
        result = _processSort(list, result, $$cache);
      } else {
        result = list.sortedList;
      }

      // pagination is last, we always process it
      result = _processPagination(list, result, $$cache);

      if (changes.newResult && $$cache.modifyResult) {
        result = $$cache.modifyResult(result, list, changes) || result;
      }

      // overwrite the original array contents with the new contents
      list.length = 0;
      list.push.apply(list, toConsumableArray(result));

      if (list.isEventUpdate || list.isWatchUpdate) {
        if (list.$$cache.clearTypeFlagTimer) {
          $timeout.cancel(list.$$cache.clearTypeFlagTimer);
        }
        list.$$cache.clearTypeFlagTimer = $timeout(function () {
          if (!$$cache.hasDebouncedUpdates) {
            list.isEventUpdate = false;
            list.isWatchUpdate = false;
          }
          delete list.$$cache.clearTypeFlagTimer;
        }, 2500);
      }

      if (angular.isFunction(list.onUpdate)) {
        list.onUpdate(list);
      }

      return list;
    }

    /**
     * Handles post-load processing and filtering of the results
     * @param list
     * @param result
     * @param $$cache
     * @returns {*}
     * @private
     */
    function _processAndFilter(list, result, $$cache) {
      // enable post-update processing
      if ($$cache.processUpdate) {
        var processUpdateResult = $$cache.processUpdate(result, list);
        // bail on false
        if (processUpdateResult === false) return list;

        // store original result no matter what
        list.originalList = result;

        // work on returned data
        if (Array.isArray(processUpdateResult)) {
          result = processUpdateResult;
        }
      } else {
        list.originalList = result;
      }

      // enable filtering
      if ($$cache.filter) {
        result = result.filter(function (item) {
          var match = $$cache.filter(item);
          if (!match && $$cache.sticky && !list.isWatchUpdate) {
            match = list.filter(function (i) {
              return i === item || item.id && i.id === item.id;
            }).length > 0;
          }
          return match;
        });
        list.filteredList = result;
      }

      return result;
    }

    /**
     * Handles post-filter sorting of the results
     * @param list
     * @param result
     * @param $$cache
     * @returns {*}
     * @private
     */
    function _processSort(list, result, $$cache) {
      var fn = $$cache.sort;
      if (fn) {
        if (fn === true) {
          result.sort();
        } else {
          if (fn.length !== 2) {
            fn = fn();
          }
          result.sort(fn);
        }
      }
      list.sortedList = result;

      return result;
    }

    /**
     * Handles post-sort pagination of the results
     * @param list
     * @param result
     * @param $$cache
     * @returns {*}
     * @private
     */
    function _processPagination(list, result, $$cache) {
      if ($$cache.pagination) {
        var opts = angular.isFunction($$cache.pagination) ? $$cache.pagination() : $$cache.pagination;
        if (opts.client === false) {
          // use the server details if this is a server query, except we don't support the buffer option
          list.pg = paginationHelper.calc(angular.extend({}, opts, { buffer: 0 }));
        } else {
          var currentIndex = -1;
          if (opts.currentItem || $$cache.paginationCurrentItem) {
            var currentItem = opts.currentItem || $$cache.paginationCurrentItem;
            currentIndex = result.findIndex(function (item) {
              return item === currentItem || item.id === currentItem;
            });
          }
          list.pg = paginationHelper.calc(angular.extend({ total: result.length, currentIndex: currentIndex }, opts));
          result = result.slice(list.pg.first, list.pg.last + 1);
        }
      } else {
        list.pg = {};
      }
      return result;
    }
  }]);

  function toSafeObject(data) {
    var newData = data;
    if (isFunction(data)) {
      newData = undefined;
    } else if (isArray(data)) {
      newData = data.map(toSafeObject);
    } else if (isObject(data)) {
      newData = {};
      var keys = Object.keys(data);
      // if there are no enumerable keys, try non-enumerable ones
      if (!keys.length) keys = Object.getOwnPropertyNames(data);
      keys.forEach(function (k) {
        newData[k] = toSafeObject(data[k]);
      });
    }
    return newData;
  }

  /**
   * Returns an object with all cyclical references replaced with `«cyclical value»`
   * @param {*} data - Any value
   * @param {Set} foundObjects - A set of already found objects when recursing
   * @returns {*} A copy of the data without any cycles
   */
  function toNonCyclicalObject(data) {
    var foundObjects = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Set();

    var result = void 0;
    if (foundObjects.has(data)) {
      result = '«cyclical value»';
    } else if (isArray(data)) {
      foundObjects.add(data);
      result = data.map(function (v) {
        return toNonCyclicalObject(v, foundObjects);
      });
      foundObjects.delete(data);
    } else if (isObject(data)) {
      foundObjects.add(data);
      var newObj = {};
      Object.keys(data).forEach(function (k) {
        newObj[k] = toNonCyclicalObject(data[k], foundObjects);
      });
      result = newObj;
      foundObjects.delete(data);
    } else {
      result = data;
    }
    return result;
  }

  /**
   * Returns a copy of the data safe for Sentry logging
   * @param {*} data - Any data we want to send to Sentry
   * @param {number} maxDepth - The maximum depth before we render the data as a JSON string (so it isn't lost)
   * @returns {*} A copy of the data safe to log to Sentry
   */
  function toSentrySafeObject(data) {
    var maxDepth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;

    var newObj = {};
    Object.keys(data).forEach(function (k) {
      var v = data[k];
      if (isPrimitive(v)) {
        newObj[k] = v;
      } else if (isArray(v)) {
        if (maxDepth > 0) {
          newObj[k] = v.map(function (v) {
            return toSentrySafeObject(v, maxDepth - 1);
          });
        } else {
          newObj[k] = JSON.stringify(toNonCyclicalObject(v));
        }
      } else if (isObject(v)) {
        if (maxDepth > 0) {
          newObj[k] = toSentrySafeObject(v, maxDepth - 1);
        } else {
          newObj[k] = JSON.stringify(toNonCyclicalObject(v));
        }
      }
    });
    return newObj;
  }

  function defineValue(parent, key, value) {
    Object.defineProperty(parent, key, {
      value: value,
      enumerable: false,
      configurable: true
    });
  }

  function lazyProperty(parent, key, getter) {
    var prop = void 0;
    Object.defineProperty(parent, key, {
      get: function get$$1() {
        if (prop === undefined) {
          prop = getter.call(parent, key);
        }
        return prop;
      },

      enumerable: false,
      configurable: true
    });
  }

  /**
   * Returns true if both objects are null or undefined, or if both objects exists and have the same value for prop
   * @param {*} o1
   * @param {*} o2
   * @param {string?} prop
   * @return {boolean}
   */
  function idEquals(o1, o2) {
    var prop = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'id';

    var o2Null = isNullOrUndefined(o2);
    return isNullOrUndefined(o1) ? o2Null : !o2Null && o1[prop] === o2[prop];
  }

  /**
   * Returns true if the values are different, with special cases for `null` and `undefined`
   * @param {*} originalVal The original value
   * @param {*} updatedVal The updated value
   * @param {String?} k Optional key being used. If the key is `$$hasKey`, we ignore it
   * @returns {boolean}
   */
  function isDiff(originalVal, updatedVal, k) {
    return k !== '$$hashKey' && !angular.equals(originalVal, updatedVal) && updatedVal !== undefined && !(updatedVal === null && originalVal === undefined);
  }

  /**
   * Can be used to return the differences between an existing object and another one.
   * Does NOT identify removed properties on the updated object (instead, set them to null).
   * @param original {Object} Existing model
   * @param updated {Object} Updated model or changes
   * @param skipProperty {Function} If provided, use to filter out properties
   * @returns {{$empty}} Hash of changes, plus a param $empty which is true if there are no changes
   */
  function diff(original, updated) {
    var skipProperty = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : angular.noop;

    var changes = original ? {} : angular.extend({}, updated);
    if (original) {
      angular.forEach(updated, function (v, k) {
        if (!skipProperty(k, v) && isDiff(original[k], v, k)) {
          changes[k] = v;
        }
      });
    }
    defineValue(changes, '$empty', !Object.keys(changes).length);
    return changes;
  }

  function isNullOrUndefined(o) {
    return o === null || o === undefined;
  }

  function empty$1(o) {
    for (var k in o) {
      if (o.hasOwnProperty(k)) delete o[k];
    }
  }

  function pluck(k, o) {
    if (o === undefined) {
      return pluck.bind(null, k);
    }
    if (k.indexOf('.')) {
      k = k.split('.');
      for (var i = 0; i < k.length; i++) {
        o = o[k[i]];
        if (o === undefined || o === null) return o;
      }
      return o;
    } else {
      return o[k];
    }
  }

  /**
   * Returns a flattened object with all of the prototype chain properties merged in.
   *
   * Stops at one of the following prototypes:
   *  - non-Object (e.g., null)
   *  - Object
   *  - if provided, the base class
   *
   *  The final object is not included in the assigned model.
   *
   *  NOTE: the returned model is a SHALLOW copy of the properties using Object.assign! Modifying nested objects
   *    *will* modify the original data.
   *
   * @param {*} object - Object we want to flatten
   * @param {*?} baseClass - Optional class for stopping
   * @returns {null|Object} If object is not an Object, is exactly Object or is the baseClass prototype, returns null.
   *   Otherwise returns a new object containing the flattened prototype chain.
   */
  function flattenPrototypeChain(object, baseClass) {
    if (!isObject(object) || object === Object || baseClass && object === baseClass.prototype) {
      return null;
    }
    return Object.assign(flattenPrototypeChain(Object.getPrototypeOf(object), baseClass) || {}, object);
  }

  /**
   * Compares two dates for sorting
   * @param {Date} a
   * @param {Date} b
   * @returns {number}
   */
  function compareDate(a, b) {
    a = a && a.getTime ? a.getTime() : 0;
    b = b && b.getTime ? b.getTime() : 0;
    return a === b ? 0 : a > b ? -1 : 1;
  }

  function dateNullOrBefore(d) {
    var when = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Date();

    return !d || d <= when;
  }

  function dateNullOrAfter(d) {
    var when = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Date();

    return !d || d >= when;
  }

  var _dateMatcher = /^\d{4,6}(-\d\d){2}(T\d\d(:\d\d){0,2}(\.\d{3})?(Z?([-+]\d\d:\d\d)?)?)?$/;

  /**
   * Returns the ISO string of the date if we are passed a date or valid date string
   * @param {*} d
   * @return {string|null} ISOString or null if not passed in a date
   */
  function toISOString(d) {
    if (isDate(d)) {
      return d.toISOString();
    } else if (isString(d) && _dateMatcher.test(d)) {
      return new Date(d).toISOString();
    }
    return null;
  }

  /**
   * Walks an object tree and replaces strings that look like ISO8601 dates and timestamps
   * with actual Date objects
   *
   * @param data Anything for input
   * @returns {*} Output with date strings replaced with dates
   */
  function parseDates(data) {
    if (!isObject(data)) return data;

    angular.forEach(data, function (val, key) {
      if (isString(val) && _dateMatcher.test(val)) {
        try {
          data[key] = new Date(val);
        } catch (e) {} // ignoring date errors is acceptable
      } else if (isObject(val)) {
        parseDates(val);
      }
    });

    return data;
  }

  function roundToMinutes(ms) {
    return Math.round(ms / 60000) * 60000;
  }

  /* eslint-disable angular/no-private-call */

  var ACTIVE_STATIC_PROMISES = {};

  function _createCommonStaticMethods(DS, DSUtils, DSHttpAdapter, $q, $injector, autolist) {
    var _commonStaticMethods = {
      /**
       * Generates a complete URL for an endpoint on a JS-Data model
       * @param {string|Object?} id - The string ID or object with an ID of a model. If not included, then the URL would be on the collection
       * @param {string?} segment - The segment we want to check. Can be excluded if just looking for the base model URL
       * @param {*} options - Additional properties to pass to the DSHttpAdapter
       * @param {Object<String>} params - Additional (string-only) parameters to encode in the URL
       * @returns {string} The absolute URL
       */
      getEndpointUrl: function getEndpointUrl() {
        var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
            id = _ref.id,
            _ref$segment = _ref.segment,
            segment = _ref$segment === undefined ? null : _ref$segment,
            _ref$options = _ref.options,
            options = _ref$options === undefined ? {} : _ref$options,
            _ref$params = _ref.params,
            params = _ref$params === undefined ? {} : _ref$params;

        var Model = this;
        id = DSUtils.resolveId(Model, id);

        var url = DSUtils.makePath(options.basePath || DSHttpAdapter.defaults.basePath, DSHttpAdapter.getEndpoint(Model, id, options), id || segment, id && segment);

        if (Object.keys(params).length) {
          url += '?' + Object.keys(params).map(function (k) {
            return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
          }).join('&');
        }

        return url;
      },
      saveOrCreate: function saveOrCreate(instance, changes, options) {
        var _this = this;

        if (!instance) return $q.reject();
        var promise = void 0;
        var isNew = !instance.id;
        var newObj = void 0;
        var getInstanceChanges = function getInstanceChanges(changes) {
          var instanceChanges = angular.copy(changes);
          if (_this.meta.excludeSaveOrCreateProps) {
            _this.meta.excludeSaveOrCreateProps.forEach(function (prop) {
              delete instanceChanges[prop];
            });
          }
          return instanceChanges;
        };
        if (isNew) {
          // for new items, the entire instance is our changes
          changes = angular.extend({}, instance, changes || {});
          promise = this.create(getInstanceChanges(changes), options).then(function (o) {
            return newObj = o;
          });
        } else {
          var _angular;

          // include any client-side changes in the model
          changes = (_angular = angular).extend.apply(_angular, [{}].concat(toConsumableArray(Object.values(this.changes(instance))), [changes || {}]));
          var instanceChanges = getInstanceChanges(changes);
          var original = this.get(instance.id);
          var includeChange = this.meta.includeSaveOrCreateChange || function () {
            return true;
          };
          var hasChanges = original && Object.keys(instanceChanges).some(function (k) {
            return includeChange(k, instanceChanges[k]) && !angular.equals(original[k], instanceChanges[k]);
          });
          if (hasChanges || changes.tag && changes.event) {
            instanceChanges.$type = instance.$type;
            promise = this.update(instance.id, instanceChanges, options);
          } else {
            promise = $q.resolve(original);
          }
        }

        if (this.meta.afterSaveOrCreate) {
          promise = promise.then(this.meta.afterSaveOrCreate(instance, changes, options)).catch(function (err) {
            if (isNew && newObj) {
              // send off a destroy if we fail on after-save events
              _this.destroy(newObj);
            }
            return $q.reject(err);
          });
        }

        return promise;
      },


      //// old-style Find method

      /**
       * Enables making a server call to find the item at least once, even if it's already in the store
       *
       * @param {string} id - ID of item to get
       * @param {*} options - Options passed to get or find
       * @returns {Promise} Promise to be resolved if the item is found, or rejected if there was an error
       *    communicating with the server
       */
      findOnce: function findOnce(id, options) {
        var wasFound = this.markFoundOnce(id);

        // if we haven't already looked up the item, force a reload
        options = options || {};
        options.bypassCache = !wasFound;

        return this.find(id, options);
      },

      /**
       * Marks that this item has been looked up directly at least once. This means using find()
       * rather than findAll() or coming in as a nested item.
       * @param {string} id - ID of item
       * @returns {boolean} True if was found previously
       */
      markFoundOnce: function markFoundOnce(id) {
        var foundOnce = this.meta.foundOnce = this.meta.foundOnce || {};
        var wasFound = foundOnce[id];
        foundOnce[id] = true;
        return wasFound;
      },

      //// fetch methods

      /**
       * Fetches an item from either the backing store, or looks to the server if
       * not already present. Options will be passed to both the `get` and `find`
       * methods, but note that if this is requested with server-side parameters
       * yet already exists in the backing store, the parameters will be ignored.
       *
       * @param {string} id - ID of item to get
       * @param {*} options - Options passed to get or find
       * @returns {Promise} Promise to be resolved if the item is found, or rejected if there was an error
       *    communicating with the server
       */
      fetch: function fetch(id, options) {
        var self = this;
        var bypassCache = !!(options && options.bypassCache);
        var model = bypassCache ? null : self.get(id, options);
        if (model) {
          if (options && options.rels) {
            return self.loadMissingRelations(model, options.rels);
          } else {
            return $q.when(model);
          }
        } else {
          return self.find(id, options);
        }
      },

      /**
       * Fetches an array of items from either the backing store, or loads from the server if the
       * requested search params return nothing.
       * @param {*} params - Parameters to search on
       * @param {*?} options - Any additional properties to pass along to the `findAll` method
       * @returns {Promise} Found models
       */
      fetchAll: function fetchAll(params, options) {
        var self = this;
        var bypassCache = !!(options && options.bypassCache);
        var models = bypassCache ? [] : self.filter(params, options);
        // not optimal, because the filter might return the empty set correctly, but anyway...
        if (models.length) {
          return $q.when(models);
        } else {
          return self.findAll(params, options);
        }
      },

      /**
       * Fetches all models with the matching IDs. All IDs that are not already in the store are
       * grouped together into a single query to load missing items.
       *
       * @param {Array<string>} ids - IDs to load
       * @param {*?} options - Any additional properties to pass along to the `findAll` method
       * @param {boolean} options.bypassCache - If true, don't look in the local store
       * @returns {Promise} Result including all matching items.
       */
      fetchAllIds: function fetchAllIds(ids) {
        var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

        var self = this;
        var bypassCache = !!options.bypassCache;
        var searchIds = bypassCache ? ids : [];
        var found = [];
        if (!bypassCache) {
          ids.forEach(function (id) {
            var item = self.get(id, options);
            if (item) {
              found.push(item);
            } else {
              searchIds.push(id);
            }
          });
        }
        return $q.when().then(function () {
          if (searchIds.length) {
            var opts = {
              where: {
                id: { in: searchIds }
              },
              per: searchIds.length
            };
            angular.merge(opts, options);
            return self.findAll(opts);
          } else {
            return [];
          }
        }).then(function (newItems) {
          return found.concat(newItems);
        });
      },

      createWithFetch: function createWithFetch(attrs, options) {
        var k = 'createWithFetch:' + JSON.stringify(attrs);

        if (!ACTIVE_STATIC_PROMISES[k]) {
          options = angular.copy(options) || {};
          options.params = options.params || {};
          options.params.fetch = true;
          ACTIVE_STATIC_PROMISES[k] = this.create(attrs, options).finally(function () {
            delete ACTIVE_STATIC_PROMISES[k];
          });
        }

        return ACTIVE_STATIC_PROMISES[k];
      },

      /**
       * A specialized function designed to aid in finding items where only one or zero are
       * expected, and if zero are returned, then the item is created with the given attributes.
       *
       * @param params {object} Query parameters
       * @param attrs {object?} Attributes for the newly created item if the ID does not exist (params
       *    will be used if attrs is not provided)
       * @param options {object?} Options to be passed to fetchAll AND create
       * @returns {*} Promise A promise that will resolve with a single instance, OR reject if anything
       *    prevents a single instance from being created
       */
      fetchOrCreateWhere: function fetchOrCreateWhere(params) {
        var attrs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
        var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

        var k = 'fetchOrCreateWhere:' + JSON.stringify(params);

        if (!ACTIVE_STATIC_PROMISES[k]) {
          var self = this;

          var create = function create() {
            var method = options.createInstance ? 'createInstance' : 'create';
            return self[method](attrs || params || {}, options);
          };

          ACTIVE_STATIC_PROMISES[k] = self.fetchAll(params, options).then(function (models) {
            var resp = models.find(function (m) {
              return Object.keys(params).every(function (k) {
                return k === 'where' ? true : m[k] === params[k];
              });
            });
            return resp || $q.reject({ status: 404 });
          }).catch(function (response) {
            return response.status === 404 ? create() : $q.reject(response);
          }).finally(function () {
            delete ACTIVE_STATIC_PROMISES[k];
          });
        }

        return ACTIVE_STATIC_PROMISES[k];
      },

      /**
       * A method that asynchronously loads any unknown items by creating a query that
       * excludes any known IDs.
       *
       * By default this method will only go to the server once, afterwards it returns `getAll`
       * wrapped in a promise. You can bypass this behavior by using the `bypassCache`
       *
       * Note: the results of this method will always contains _all_ the items in the system, even if
       * only some came from the server, which makes it a safe alternative to `fetchAll` or
       * `findAll`.
       *
       * @param {*} options Options to pass to findAll
       * @returns {Promise}
       */
      findMissing: function findMissing(options) {
        var self = this;
        if (self.meta.$$didFindMissing && !(options && options.bypassCache)) {
          return $q.when(self.getAll());
        } else {
          var where = {
            id: {
              notIn: self.getAll().map(function (item) {
                return item.id;
              })
            }
          };
          if (options.where) {
            angular.merge(where, options.where);
          }
          return self.findAll({ where: where }, options).then(function () {
            self.meta.$$didFindMissing = true;
            return self.getAll();
          });
        }
      },

      //// Relationships

      /**
       * Loads any missing default includes if they are not already defined on the instance.
       *
       * @param instance
       * @param rels Names of relations to load (see DI.loadRelations)
       * @returns {*} Promise
       */
      loadMissingRelations: function loadMissingRelations(instance, rels) {
        var self = this;
        var _rels = [];
        angular.forEach(rels, function (rel) {
          angular.forEach(self.relationList, function (fieldDef) {
            if (fieldDef.relation === rel && _rels.indexOf(rel) === -1) {
              var localField = fieldDef.localField;
              // force loading if the field is missing OR an empty array on hasMany
              if (localField && (!instance[localField] || fieldDef.type === 'hasMany' && !instance[localField].length)) {
                _rels.push(rel);
              }
            }
          });
        });
        return _rels.length ? self.loadRelations(instance, _rels) : $q.when(instance);
      },

      //// Meta Data

      /**
       * Asynchronously returns the meta data for this Model
       * @param {boolean} force If true, force reloading
       * @returns {*} Promise
       */
      getMetaData: function getMetaData() {
        var force = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

        var self = this;

        if (self._metaDataPromise) return self._metaDataPromise;

        if (!force && self.metaData && self.metaData._loaded) {
          return $q.when(self.metaData);
        } else {
          if (!self.metaData) {
            self.metaData = {
              enums: {},
              flags: {},
              includes: []
            };
          }
          var returnMeta = function returnMeta() {
            self.metaData._loaded = true;
            delete self._metaDataPromise;
            return $q.when(self.metaData);
          };
          // reuse DS to handle the meta-data load
          self._metaDataPromise = self.find('meta', {
            cacheResponse: false,
            ignoreLoadingBar: true
          }).then(returnMeta, returnMeta);

          return self._metaDataPromise;
        }
      },

      /**
       * Loads an enum array or enum value
       * @param {string} name Name of the enum (can include nested enums using `.`, as in `office.seating`)
       * @param {string} key (Optional) Looks up the mapped value of the enum based on the key
       * @param {boolean} async If true, the response will be a promise that resolves once metadata has been loaded
       * @returns {Promise|Array|string} Promise for async, array of enum objects if key is not provided, and a string if the key is provided.
       * This will always return a value, even if the metadata has not been loaded or we cannot find a matching enum or key
       */
      getEnum: function getEnum(name, key) {
        var _this2 = this;

        var async = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

        if (async) {
          return this.getMetaData().then(function () {
            return _this2.getEnum(name, key);
          });
        } else {
          var enumData = this.metaData && this.metaData.enums && pluck(name, this.metaData.enums);
          var keyExists = key !== null && key !== undefined;

          if (!enumData) {
            // enable looking up the metadata so it's available on a later $digest cycle
            this.getMetaData();
            return keyExists ? '' : [];
          }

          if (keyExists) {
            if (!enumData.keyMap) {
              enumData.keyMap = enumData.reduce(function (map, item) {
                map[item.value] = item.text;
                return map;
              }, {});
            }
            return enumData.keyMap[key] || '';
          } else {
            return enumData;
          }
        }
      }
    };

    //--------------------------------------------------------------------
    //
    // Autolist functions
    //
    //--------------------------------------------------------------------

    ['getAll', 'filter', 'fetchAll'].forEach(function (fnName) {
      _commonStaticMethods['auto' + pascalCase(fnName)] = function (params, options) {
        var Model = this;
        return autolist.create(function () {
          return Model[fnName](params, options);
        }, Model, options);
      };
    });

    // Auto find missing doesn't take any arguments
    _commonStaticMethods.autoFindMissing = function (options) {
      var Model = this;
      return autolist.create(function () {
        return Model.findMissing(options);
      }, Model, options);
    };

    // autoFindAll is treated special, so it can be used for server-side-only lists
    // It includes internal support for dynamic params and server-side pagination
    // (All other methods are assumed to be used for client-side handling)
    _commonStaticMethods.autoFindAll = function (params) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      var Model = this;
      var list = autolist.create(function autoFindAllAutoListMethod() {
        // get the query parameters (may be dynamic)
        var findAllParams = angular.isFunction(params) ? params() : params;
        // do not modify the original options
        var findAllOptions = angular.copy(options);

        // the following block is for tracking the full response data on the returned Array
        var responseDataCacheKey = JSON.stringify(findAllParams) || 'null';
        findAllOptions.responseDataCacheKey = responseDataCacheKey;
        var cacheResponse = findAllOptions.cacheResponse !== undefined ? findAllOptions.cacheResponse : Model.cacheResponse;
        var afterFn = cacheResponse === false ? 'afterFindAll' : 'afterInject';
        var originalAfterFn = findAllOptions[afterFn] || Model[afterFn];
        findAllOptions[afterFn] = function autoFindAllAfterFn(options, data) {
          var InjectedModel = options;
          while (InjectedModel && !InjectedModel.hasOwnProperty('$dsModel')) {
            // When injecting a nested data model, JS-Data decides to use the overridden `after*` method,
            // (meaning this one) rather than the one on the injected model
            // So we need to look for the correct model for the injected resource by walking the prototype
            // chain
            InjectedModel = Object.getPrototypeOf(InjectedModel);
          }

          if (InjectedModel.name !== Model.name) {
            // this is a different model than the one the method was called on, use the default after*
            // function for that model
            if (InjectedModel[afterFn]) {
              InjectedModel[afterFn](InjectedModel, data);
            }
          } else {
            // this is the top-level model, so we can use the original method instead
            if (originalAfterFn) {
              originalAfterFn(options, data);
            }
            if (Array.isArray(data) && Model.$responseData) {
              // store the response data on the final output array from the find all operation
              // we do it this way because the result of Model.findAll can be returned to *different*
              // requests to findAll! Even if they come from different sources, if the params match,
              // JS-Data will only make a single request at a time.
              data.$responseData = Model.$responseData[responseDataCacheKey];
              delete Model.$responseData[responseDataCacheKey];
            }
          }
          return data;
        };

        if (findAllOptions.bypassCache !== false && findAllOptions.cacheResponse !== true) {
          findAllOptions.bypassCache = true;
        }

        return Model.findAll(findAllParams, findAllOptions).then(function (resp) {
          // catch pagination errors if we have no data
          if (resp.length === 0 && findAllParams && findAllParams.page > 1) {
            return Model.findAll(angular.extend({}, findAllParams, { page: 1 }), findAllOptions);
          }
          // store the response data back on the autolist for pagination
          list.responseData = resp.$responseData;
          return resp;
        });
      }, Model, options);

      // configure default server-side pagination
      list.paginate(function autoFindAllPagination() {
        var data = { client: false };
        if (list.responseData && list.responseData.params && list.responseData.params.per) {
          data.total = list.responseData.totalCount;
          data.perPage = list.responseData.params.per;
          data.page = list.responseData.currentPage;
        }
        return data;
      });

      return list;
    };

    /**
     * A variation of autoFindAll and findAll that recursively loads every matching item across multiple pages
     * Use the 3rd parameter to `then` to receive progress notifications
     *
     * @param params {Object}
     * @param params.per {number} By default this is 100, but can be adjusted based on needs
     * @param options
     * @returns {Promise}
     */
    _commonStaticMethods.retrieveAll = function () {
      var params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      var deferred = $q.defer();
      params.per = params.per || 100;
      params.page = 1;

      var cfpLoadingBar = $injector.has('cfpLoadingBar') && $injector.get('cfpLoadingBar');

      var showProgress = !options.ignoreLoadingBar && cfpLoadingBar;
      if (showProgress) cfpLoadingBar.start();
      options.ignoreLoadingBar = true;

      var result = [];
      var autolist = this.autoFindAll(params, options);
      var _processNext = function _processNext() {
        autolist.update().promise.then(function (list) {
          result.push.apply(result, toConsumableArray(list));
          if (list.pg.hasNext) {
            var retrieved = result.length;
            var total = list.pg.total;
            var progress = retrieved / total;
            deferred.notify({ progress: progress, retrieved: retrieved, total: total });
            if (showProgress) cfpLoadingBar.set(progress);

            params.page += 1;
            _processNext();
          } else {
            if (showProgress) cfpLoadingBar.complete();
            autolist.destroy();
            deferred.resolve(result);
          }
        }, deferred.reject);
      };
      _processNext();

      return deferred.promise;
    };

    // Attach static methods to DS defaults for simple inheritance
    angular.forEach(_commonStaticMethods, function (fn, key) {
      defineValue(DS.defaults, key, fn);
    });
  }

  function _createSaveEmbedded($q) {
    /**
     * Creates a method that can be used after a normal save to automatically save an embedded
     * array of children on a parent model.
     *
     * @param options {Object|Array<{Model,fieldName,foreignKey,changes,orderable}>}
     * @param options.Model {Resource|Array<Array>} JS-Data resource of the children.
     *        If this is an array, each item is passed as an argument to saveEmbedded
     * @param options.fieldName {String?} Name of the field for the children on the parent object
     * @param options.foreignKey {String?} Name of the reference field to point to the parent's ID on the child
     * @param options.changes {*?} Optional set of changes on the parent model, to prevent unnecessary calls out
     * @param options.orderable {boolean?}
     * @param options.exclude {Function?} if provided, used to exclude changes on the embedded
     * @param options.diff {Function?} if provided, use instead of object-utils.diff for determining changes
     * @param options.uniqueChildField {String?} If provided, use to prevent uniqueness constraint errors
     * @param options.copyFields {Array<String>?} If provided, copy these values from changes to any modified child
     * @param options.requestOptions {*?} Params to send along with the request
     * @returns {Function} Function to call with the parent model, usually used in a `.then` after a save or update. Parent instance is returned.
     */
    return function saveEmbedded(options) {
      if (Array.isArray(options)) {
        return function (parent) {
          return $q.all(options.map(function (options) {
            return saveEmbedded(options)(parent);
          })).then(function () {
            return parent;
          });
        };
      }
      var Model = options.Model,
          fieldName = options.fieldName,
          _options$foreignKey = options.foreignKey,
          foreignKey = _options$foreignKey === undefined ? null : _options$foreignKey,
          _options$changes = options.changes,
          changes = _options$changes === undefined ? null : _options$changes,
          _options$requestOptio = options.requestOptions,
          requestOptions = _options$requestOptio === undefined ? {} : _options$requestOptio,
          _options$orderable = options.orderable,
          orderable = _options$orderable === undefined ? true : _options$orderable,
          _options$exclude = options.exclude,
          exclude = _options$exclude === undefined ? angular.noop : _options$exclude,
          _options$uniqueChildF = options.uniqueChildField,
          uniqueChildField = _options$uniqueChildF === undefined ? null : _options$uniqueChildF,
          _options$copyFields = options.copyFields,
          copyFields = _options$copyFields === undefined ? ['tag'] : _options$copyFields,
          _options$diff = options.diff,
          diff$$1 = _options$diff === undefined ? diff : _options$diff;

      var baseFields = function baseFields() {
        var fields = {};
        if (changes) {
          copyFields.forEach(function (f) {
            fields[f] = changes[f];
          });
        }
        return fields;
      };
      return function (parent) {
        var updates = changes && changes[fieldName];
        if (!updates && (changes || !parent[fieldName])) {
          // don't bother if we have no changes to report
          return $q.resolve(parent);
        }

        var _addForeignKeyParam = function _addForeignKeyParam(options) {
          if (foreignKey) {
            if (options) {
              options[foreignKey] = parent.id;
            } else {
              options = angular.copy(requestOptions);
              options.params = options.params || {};
              options.params[foreignKey] = parent.id;
            }
          }
          return options || requestOptions;
        };

        // handle deletions first (for unique constraints)
        var delPromises = $q.when();
        if (updates && parent[fieldName] && parent[fieldName].length) {
          if (uniqueChildField) {
            // Delete the IDs of any updated items that would cause uniqueness constraint issues (they'll
            // be deleted and recreated instead).
            // This happens, for example, if two files have swapped paths.
            updates.forEach(function (newChild) {
              var oldChild = parent[fieldName].find(function (oldChild) {
                return oldChild.id !== newChild.id && oldChild[uniqueChildField] === newChild[uniqueChildField];
              });
              if (oldChild) {
                delete newChild.id;
                var updateToOldChild = updates.find(function (nc) {
                  return nc.id === oldChild.id;
                });
                if (updateToOldChild) {
                  // If only one child was renamed, it's possible to get into a scenario where the
                  // updates happen out-of-order, causing a uniqueness constraint, so we go ahead
                  // and treat that file as deleted-and-recreated as well.
                  delete updateToOldChild.id;
                }
              }
            });
          }
          delPromises = $q.all(parent[fieldName].filter(function (oldChild) {
            return oldChild.id && !oldChild.softDeletedAt && updates.every(function (newChild) {
              return oldChild.id !== newChild.id;
            });
          }).map(function (oldChild) {
            return Model.destroy(oldChild, _addForeignKeyParam());
          }));
        }

        return delPromises.then(function () {
          // Save or create any changed or new child models
          var promises = void 0;
          promises = (updates || parent[fieldName]).map(function (child, i) {
            // Ensure files with softDeletedAt are included
            if (exclude(child) && !child.softDeletedAt) {
              // return the internally stored file if we don't save changes here
              return Model.get(child.id);
            }
            if (orderable) {
              child.position = i + 1;
            }
            var createModel = function createModel() {
              var params = _addForeignKeyParam(baseFields());
              angular.extend(params, child);
              delete params.id;
              return Model.create(params, requestOptions);
            };
            if (child.id) {
              var childOrig = Model.get(child.id);
              var updatePromise = void 0;
              if (childOrig) {
                var childCopy = Object.assign({}, child);
                // strip any special $ prefixed values out of the object to do a proper diff
                Object.keys(childCopy).filter(function (k) {
                  return k[0] === '$';
                }).forEach(function (key) {
                  delete childCopy[key];
                });

                var diffData = diff$$1(childOrig, childCopy);

                if (diffData.$empty) {
                  // no need to update child, just return the local reference
                  return childOrig;
                } else {
                  // always include type
                  diffData.$type = childOrig.$type;
                  angular.extend(diffData, _addForeignKeyParam(baseFields()));
                  updatePromise = Model.update(childOrig, diffData, requestOptions);
                }
              } else {
                // no local copy?
                var params = angular.extend(_addForeignKeyParam(baseFields()), child);
                updatePromise = Model.update(child.id, params, requestOptions);
              }
              return updatePromise.catch(function (resp) {
                // on 404 errors, just create a new file instead
                return resp.status === 404 ? createModel() : $q.reject(resp);
              });
            } else {
              // save a new one
              return createModel();
            }
          });

          // Wait for any and all changes
          return $q.all(promises);
        }).then(function (children) {
          // Clean up any old children on the parent model missed above
          if (parent[fieldName] && parent[fieldName].length) {
            return $q.all(parent[fieldName].filter(function (oldChild) {
              return !oldChild.softDeletedAt && children.every(function (newChild) {
                return newChild.id !== oldChild.id;
              });
            }).map(function (oldChild) {
              return oldChild.id && Model.destroy(oldChild, _addForeignKeyParam()).catch(function (resp) {
                // ignore 404 errors on deleted embedded saves
                return resp.status === 404 ? true : $q.reject(resp);
              });
            })).then(function () {
              return children;
            });
          }
          return children;
        }).then(function (children) {
          if (parent[fieldName]) {
            var _parent$fieldName;

            // replace the array of children with the updated models
            parent[fieldName].length = 0;
            (_parent$fieldName = parent[fieldName]).push.apply(_parent$fieldName, toConsumableArray(children));
            return parent;
          }
        });
      };
    };
  }

  /* eslint-disable angular/no-private-call */

  var modelHelperModule = angular.module('Q.models.model-helper', [jsDataModule, jsDataConfigModule, autolistHelperModule])

  /**
   * This library is used to make more advanced models by including some extra methods, and also
   * wrapping default methods for js-data.
   *
   * The main improvements are:
   *
   * - Typed Resources
   *   A resource may define .meta.types, which will enable type-specific functionality for that
   *   model.
   *
   * - fetch and fetchAll
   *   These methods are added to the model, and will provide asynchronous access to instances whether they
   *   are already in the backing store or not.
   *
   * - loadMissingRelations
   *   Will only load relations that are missing on the instance.
   *
   */
  .factory('ModelHelper', ['autolist', 'DS', '$q', 'DSUtils', '$rootScope', 'DSHttpAdapter', '$timeout', '$log', '$injector', function (autolist, DS, $q, DSUtils, $rootScope, DSHttpAdapter, $timeout, $log, $injector) {
    //--------------------------------------------------------------------
    //
    //  COMMON STATIC METHODS: Methods added to the Model object itself
    //
    //--------------------------------------------------------------------

    var _commonStaticProperties = {
      first: function first() {
        return this.getAll()[0];
      },
      last: function last() {
        var a = this.getAll();
        return a[a.length - 1];
      }
    };

    // attach static properties to DS defaults for inheritance
    angular.forEach(_commonStaticProperties, function (fn, key) {
      Object.defineProperty(DS.defaults, key, { get: fn });
    });

    _createCommonStaticMethods(DS, DSUtils, DSHttpAdapter, $q, $injector, autolist);

    //--------------------------------------------------------------------
    //
    // MODEL EVENTS
    //
    //--------------------------------------------------------------------

    function _addModelChangedEvents(config, Model) {
      Model.$broadcast = function (type, attrs) {
        var action = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : type;

        var instance = DS.get(Model.name, attrs.id);

        var props = {
          id: attrs.id,
          config: config,
          instance: instance,
          attrs: attrs,
          resourceName: Model.name,
          action: action
        };

        $rootScope.$broadcast('model.changed:' + Model.name + ':' + attrs.id, props, type);
        $rootScope.$broadcast('model.changed:' + Model.name, props, type);
        $rootScope.$broadcast('model.changed', props, type);
      };
      angular.forEach({ create: 'created', update: 'updated', destroy: 'destroyed' }, function (type, action) {
        Model.on('DS.after' + pascalCase(action), function (resource, attrs) {
          $timeout(function () {
            Model.$broadcast(type, attrs, action);
          }, 100);
        });
      });
    }

    //--------------------------------------------------------------------
    //
    // ACTION ENDPOINTS
    //
    //--------------------------------------------------------------------

    /**
     * Enables new static methods that call to specific endpoints, with the ability to
     * automatically inject the resulting object.
     *
     * Configuration comes from config.meta.actionEndpoints, a hash of method names to options. If the options
     * value is true or an empty object, the defaults apply.  All options are passed to the HTTP adapter, as well,
     * allowing you to override endpoints and other settings in a general manner.
     *
     * The created methods have the form of
     *
     * .[methodName]( id|resource, options )
     *
     * Options is mixed on top of the action, allowing you to override some configuration on a per-call basis.
     *
     * @param config
     * @param config.segment: the end segment, defaults to the method name
     * @param config.endpoint: override the model's default endpoint
     * @param config.method: HTTP method, defaults to POST,
     * @param config.responseHandler: if provided, overrides the built-in response handler (which deserializes
     *              and attempts to inject the result if it has an ID).
     *              Set to angular.identity to get direct access to the HTTP response.
     * @param config.cacheResponse: prevent the default handler from attempting to inject the response.
     * @param config.serialize: unless falsey, serialize the data object (if present) using the model's serialize method
     */
    function _addActionEndpoints(config) {
      config.staticMethods = config.staticMethods || {};

      angular.forEach(config.meta.actionEndpoints, function (action, name) {
        if (!angular.isObject(action)) {
          if (action === true) action = {};
          if (angular.isFunction(action)) action = { responseHandler: action };
        }

        action = angular.extend({
          segment: snakeCase(name),
          method: 'POST',
          responseHandler: false,
          onSuccess: null,
          cacheResponse: true,
          serialize: true
        }, action);

        config.staticMethods[name] = function (id, options) {
          var Model = this;

          if (!id) {
            var msg = 'Called to actionEndpoint ' + Model.name + '.' + name + ' without an id or instance: "' + id + '"';
            $log.warn(msg);
            return $q.reject(msg);
          }

          var instance = id;
          id = DSUtils.resolveId(Model, id);
          if (!instance.id) {
            instance = Model.get(id) || { id: id };
          }

          options = angular.extend({}, action, options || {});

          options.url = Model.getEndpointUrl({
            id: id,
            segment: action.segment,
            options: options
          });

          if (instance.$type) {
            // add in type param for typed models
            var what = options.method === 'GET' ? 'params' : 'data';
            options[what] = options[what] || {};
            options[what].$type = options[what].$type || instance.$type;
          }

          if (options.data && options.serialize) {
            // serialize the data model by default
            options.data = Model.serialize(Model.name, options.data || {});
          }

          function broadcast() {
            Model.$broadcast('update', instance);
            // currently only used by tracker service
            $rootScope.$broadcast('model.actionEndpoint.success', {
              options: options,
              model: Model,
              instance: instance,
              name: name
            });
          }

          var responseHandler = void 0;
          // if a response handler was provided, wrap it so that we can pass in other useful information
          if (options.responseHandler) {
            responseHandler = function responseHandler(res) {
              broadcast();
              return options.responseHandler(res, Model, instance, options);
            };
          } else {
            responseHandler = function responseHandler(res) {
              broadcast();
              var data = options.deserialize ? options.deserialize(Model.name, res) : Model.deserialize(Model.name, res);
              if (data && data.id && options.cacheResponse) {
                return Model.inject(data);
              } else if (data && data.id) {
                return Model.createInstance(data);
              } else {
                return data;
              }
            };
          }

          return DSHttpAdapter.HTTP(options).then(responseHandler).then(function (val) {
            if (options.onSuccess) options.onSuccess(val);
            return val;
          });
        };

        config.staticMethods[name + 'WithAlert'] = function () {
          var returnedPromise = this[name].apply(this, arguments);
          var appAlert = $injector.has('appAlert') && $injector.get('appAlert');
          if (appAlert) {
            returnedPromise = returnedPromise.catch(appAlert.modelError);
          }
          return returnedPromise;
        };
      });
    }

    //--------------------------------------------------------------------
    //
    // SERIALIZATION & DESERIALIZATION
    //
    //--------------------------------------------------------------------

    /**
     * Looks at all top-level Array properties. If the first child contains a numerical `position` property,
     * then we sort the array based on `position`
     * @param data
     */
    function sortOrderables(data) {
      if (Array.isArray(data)) {
        data.forEach(sortOrderables);
      } else {
        angular.forEach(data, function (prop) {
          if (Array.isArray(prop) && prop[0] && angular.isNumber(prop[0].position)) {
            prop.sort(function (a, b) {
              return a.position - b.position;
            });
            // recurse
            prop.forEach(sortOrderables);
          } else if (angular.isObject(prop)) {
            // recurse
            sortOrderables(prop);
          }
        });
      }
    }

    /**
     * Extracts data and attempts to replace dates
     * @param resource
     * @param resp
     * @returns {*} parsed and extracted data
     */
    function deserialize(resource, resp) {
      var body = resp && resp.data ? resp.data : resp;
      if (!body) return body;
      if (angular.isString(resource)) {
        resource = DS.definitions[resource];
      }

      var result = parseDates(body.data || body);
      sortOrderables(result);

      if (Array.isArray(result) && resp.config.responseDataCacheKey) {
        var responseDataCacheKey = resp.config.responseDataCacheKey;
        resource.$responseData = resource.$responseData || {};
        resource.$responseData[responseDataCacheKey] = angular.copy(body);
        resource.$responseData[responseDataCacheKey].params = angular.copy(resp.config.params);
      }

      var meta = body.meta;
      if (meta && !Array.isArray(result)) {
        if (resource.metaData) {
          angular.merge(resource.metaData, meta);
        } else {
          resource.metaData = meta;
        }
      }

      return result;
    }

    // handles the actual parsing of UI prefs
    function _parseUiPrefs(instance) {
      var uiPrefs = instance.uiPrefs;
      if (uiPrefs) {
        angular.forEach(uiPrefs, function (v, k) {
          try {
            uiPrefs[k] = JSON.parse(v);
          } catch (e) {
            delete uiPrefs[k];
          }
        });
      }
    }

    /**
     * Adds JSON parsing for uiPrefs field
     * @param resource
     * @param data
     * @returns {*}
     */
    function deserializeWithUiPrefs(resource, data) {
      var parsedData = deserialize(resource, data);
      if (angular.isArray(parsedData)) {
        parsedData.forEach(_parseUiPrefs);
      } else {
        _parseUiPrefs(parsedData);
      }
      return parsedData;
    }

    /**
     * Cleans an item of undesired properties before serialization
     * @param resource
     * @param data
     * @returns {*}
     */
    function cleanItemForSave(resource, data) {
      if (angular.isString(resource)) {
        resource = DS.definitions[resource];
      }
      var saved = void 0,
          notSaved = void 0,
          newData = void 0;
      data = angular.copy(data, {});
      if (resource.meta && resource.meta.saveIncludes) {
        saved = resource.meta.saveIncludes;
        newData = {};
        angular.forEach(data, function (val, id) {
          if (saved.indexOf(id) !== -1) {
            newData[id] = val;
          }
        });
        data = newData;
      } else if (resource.meta && resource.meta.saveExcludes) {
        notSaved = resource.meta.saveExcludes;
        newData = {};
        angular.forEach(data, function (val, id) {
          // do not include fields that are prefixed with _ or that are explicitly excluded
          if (id[0] !== '_' && notSaved.indexOf(id) === -1) {
            newData[id] = val;
          }
        });
        data = newData;
      }
      // recursively clean relationships
      angular.forEach(resource.relationList, function (rel) {
        var local = rel.localField;
        if (local && data[local]) {
          if (rel.type === 'hasMany') {
            var acc = [];
            angular.forEach(data[local], function (val, i) {
              acc[i] = cleanItemForSave(rel.relation, val);
            });
            data[local] = acc;
          } else {
            data[local] = cleanItemForSave(rel.relation, data[local]);
          }
        }
      });
      return data;
    }

    /**
     * Cleans item and encapsulates it. Angular or JSON.stringify handles dates for us.
     * @param resource
     * @param data
     * @returns {{data: *}} cleaned data
     */
    function serialize(resource, data) {
      return { data: cleanItemForSave(resource, data) };
    }

    /**
     * Adds JSON stringification for uiPrefs
     * @param resource
     * @param data
     * @returns {{data: *}}
     */
    function serializeWithUiPrefs(resource, data) {
      var out = serialize(resource, data);
      if (out.data.uiPrefs) {
        angular.forEach(out.data.uiPrefs, function (v, k) {
          return out.data.uiPrefs[k] = JSON.stringify(v);
        });
      }
      return out;
    }

    function afterInject(resource, data) {
      if (angular.isArray(data)) {
        data.forEach(function (d) {
          return afterInject(resource, d);
        });
      } else if (angular.isObject(data)) {
        angular.forEach(resource.relationList, function (rel) {
          var local = rel.localField;
          var $type = rel.$type || data.$type;
          var linked = typeof rel.link === 'boolean' ? rel.link : !!resource.linkRelations;
          if (!linked && local && $type === data.$type && data[local]) {
            // inject any references
            data[local] = DS.inject(rel.relation, data[local]);
          }
        });

        if (resource.meta.cleanInjected) {
          resource.meta.cleanInjected(data);
        }

        if (resource.meta.hasDigest && !data.digest) {
          // insert dummy digest if this model usually has one
          data.digest = {};
        }
      }
    }

    function _addDefaultValues(config) {
      var defaults = config.meta.defaults;
      ['afterInject', 'afterCreateInstance'].forEach(function (method) {
        var oldMethod = config[method];
        config[method] = function setDefaultsAfter(resource, items) {
          if (!Array.isArray(items)) items = [items];
          items.forEach(function (item) {
            angular.forEach(defaults, function (val, k) {
              if (item[k] === undefined) {
                item[k] = angular.copy(val);
              }
            });
          });
          if (oldMethod) {
            oldMethod(resource, items);
          }
        };
      });
    }

    //--------------------------------------------------------------------
    //
    // DEFAULT CONFIGURATION: Default properties for model configs
    //
    //--------------------------------------------------------------------

    var _defaultConfig = {
      deserialize: deserialize,

      serialize: serialize,

      afterInject: afterInject
    };

    //--------------------------------------------------------------------
    //
    // PRIMARY METHODS
    //
    //--------------------------------------------------------------------

    /**
     * Defines a resource using the ModelHelper improvements.
     * @param config
     * @returns {*}
     */
    function defineResource(config) {
      var Model = void 0;

      config = angular.extend({ meta: {}, methods: {} }, _defaultConfig, config);

      // Add a specialized method in for retrieving the value of enumerated properties
      config.methods.getEnum = function (name, property) {
        var key = pluck(property || name, this);
        if (key) {
          return Model.getEnum(name, key);
        } else {
          return '';
        }
      };

      if (config.cleanAfterDestroy) {
        var _config$cleanAfterDes = config.cleanAfterDestroy,
            parent = _config$cleanAfterDes.parent,
            collection = _config$cleanAfterDes.collection;

        config.afterEject = config.afterDestroy = cleanDestroyed(parent, collection);
        delete config.cleanAfterDestroy;
      }

      if (config.meta.actionEndpoints) {
        _addActionEndpoints(config);
      }

      if (config.meta.defaults) {
        _addDefaultValues(config);
      }

      /**
       * Returns a cloned instance of this model.
       *
       * First, this flattens the prototype chain, to ensure we're getting a deep copy of all the properties.
       * This is specifically used for solution-review-dialog, which uses a prototype chain to make shallow
       * changes for playback. Then angular.copy is used to create a deep copy of the model.
       *
       * Finally, we use Model.createInstance to create a new instance of the flattened, cloned data.
       *
       * Just using `Model.createInstance(this)` would only create a shallow copy, and would also
       * lose properties set higher up the prototype chain.
       *
       * @returns {*} Deep, flattened clone of this model.
       */
      config.methods.$clone = function () {
        var dataFlattened = flattenPrototypeChain(this, Model);
        var dataCopy = angular.copy(dataFlattened);
        return Model.createInstance(dataCopy);
      };

      if (config.meta.types) {
        Model = _defineTypedResource(config);
      } else {
        Model = DS.defineResource(config);
      }

      Model.$dsModel = config.name;

      _addModelChangedEvents(config, Model);

      if (config.staticMethods) {
        angular.extend(Model, config.staticMethods);
      }

      // Monkey Patch to prevent errors when checking hasChanges on new models
      Model.hasChanges = function (model) {
        for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }

        if (model && !model.id) return true;
        return DS.hasChanges.apply(DS, [config.name, model].concat(args));
      };

      $rootScope.$on('app.logout', function () {
        delete Model.meta.$$didFindMissing;
      });

      return Model;
    }

    /**
     * Fix for js-data items no longer being cleaned up after removal.
     * This looks for any parents that might be referencing the given
     * resource item, and updates their collection
     * @param parent
     * @param collection
     */
    function cleanDestroyed(parent, collection) {
      return function (resource, data) {
        if (angular.isArray(data)) {
          data.forEach(cleanDestroyed);
        } else {
          DS.definitions[parent].getAll().forEach(function (p) {
            if (p[collection]) {
              var idx = p[collection].findIndex(function (ci) {
                return ci.id === data.id;
              });
              if (idx > -1) {
                p[collection].splice(idx, 1);
              }
            }
          });
        }
        return $q.when(data);
      };
    }

    /**
     * Defines a typed resource, that includes type-specific functionality.
     *
     * @param config
     * @returns {*}
     */
    function _defineTypedResource(config) {
      // auto type items
      function asType(instance) {
        if (instance && instance.$type && instance.$asType) {
          instance.$asType();
        }
      }

      ['afterInject', 'afterCreateInstance'].forEach(function (method) {
        var oldMethod = config[method];
        config[method] = function typedResourceAfter(options, instance) {
          if (Array.isArray(instance)) {
            instance.forEach(asType);
          } else {
            asType(instance);
          }
          if (oldMethod) {
            oldMethod(options, instance);
          }
        };
      });

      config.methods = config.methods || {};

      // provide the ability to "import" sub-typed methods into the instance
      config.methods.$asType = function () {
        if (!this.hasOwnProperty('$$typed') && this.$type) {
          defineValue(this, '$$typed', true);
          var self = this;
          angular.forEach(config.meta.types[this.$type], function (value, key) {
            if (key === 'afterTyped') return;
            if (key === 'computed') {
              Object.defineProperties(self, value);
            } else {
              defineValue(self, key, value);
            }
          });
          if (config.meta.types[this.$type].afterTyped) {
            config.meta.types[this.$type].afterTyped(this);
          }
        }

        return this;
      };

      return DS.defineResource(config);
    }

    function _createDefineQuickList(autolist) {
      // special case to load in a list for quick lookups
      return function defineQuickList(Model, findAllProps) {
        var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
            sort = _ref.sort;

        lazyProperty(Model, 'quickList', function () {
          var loaded = false;
          var lookup = {};
          var updateList = function updateList(list) {
            // mixin any existing items
            Model.getAll().forEach(function (instance) {
              var idx = list.findIndex(function (v) {
                return v.id === instance.id;
              });
              if (idx > -1) {
                list[idx] = instance;
              } else {
                list.push(instance);
              }
            });
            lookup = {};
            list.forEach(function (item) {
              lookup[item.id] = item;
            });
            return list;
          };
          var quickList = autolist.create(function (options) {
            if (loaded) {
              var list = quickList.slice();
              // remove any destroyed items
              var event = options.event;
              if (event && event.type === 'destroyed' && event.data) {
                removeMatching(list, function (v) {
                  return v.id === event.data.id;
                });
              }
              return updateList(list);
            } else {
              // asynchronously load the list
              return Model.retrieveAll(angular.extend({
                quickList: true,
                orderBy: 'id ASC',
                per: 1000
              }, findAllProps), { cacheResponse: false, bypassCache: true }).then(function (list) {
                loaded = true;
                return updateList(list);
              });
            }
          }, Model);
          if (sort) {
            quickList.autoSort(sort);
          }
          quickList.listen();
          quickList.get = function (id) {
            return lookup[id];
          };
          return quickList;
        });
      };
    }

    return {
      defineResource: defineResource,
      deserialize: deserialize,
      deserializeWithUiPrefs: deserializeWithUiPrefs,
      serialize: serialize,
      serializeWithUiPrefs: serializeWithUiPrefs,
      afterInject: afterInject,
      saveEmbedded: _createSaveEmbedded($q),
      defineQuickList: _createDefineQuickList(autolist)
    };
  }]);

  var assessmentCohortModelModule = angular.module('Q.models.assessment-cohort', [modelHelperModule]).factory('AssessmentCohort', ['ModelHelper', function (ModelHelper) {
    return ModelHelper.defineResource({
      name: 'AssessmentCohort',
      endpoint: 'assessment_cohorts',

      relations: {
        belongsTo: {
          Assessment: {
            localField: 'assessment',
            localKey: 'assessmentId'
          },
          Team: {
            localField: 'team',
            localKey: 'teamId'
          }
        },
        hasMany: {
          TeamCandidate: {
            localField: 'teamCandidates',
            localKeys: 'teamCandidateIds'
          }
        }
      },
      meta: {},
      afterInject: function afterInject(resource, instance) {
        ModelHelper.afterInject(resource, instance);
        // after we inject or update a solution, we should always remove any cached revisions,
        // because they will never be updated
        var _afterInject = function _afterInject(instance) {
          instance.allEmails = unique([].concat(instance.pendingEmails, instance.rejectedEmails, instance.invitedEmails));
          var total = instance.total = instance.allEmails.length;
          instance.pending = instance.pendingEmails.length;
          instance.sent = instance.invitedEmails.length;
          instance.rejected = instance.rejectedEmails.length;
          instance.progress = 100 * (total - instance.pending) / total;
          instance.complete = instance.state === 'completed';
          instance.incomplete = !instance.complete;
        };
        if (Array.isArray(instance)) {
          instance.forEach(_afterInject);
        } else {
          _afterInject(instance);
        }
      }
    });
  }]);

  var assessmentInvitationModelModule = angular.module('Q.models.assessment-invitation', [modelHelperModule]).factory('AssessmentInvitation', ['ModelHelper', function (ModelHelper) {
    return ModelHelper.defineResource({
      name: 'AssessmentInvitation',
      endpoint: 'assessment_invitations',

      methods: {
        inviter: function inviter() {
          var self = this;
          var workflow = self.invitedByWorkflowLabel;
          var inviter = '';
          if (workflow) {
            if (self.invitedByAssessmentTitle) {
              inviter = self.invitedByAssessmentTitle + ' - ';
            }
            inviter += workflow;
            if (!/workflow/i.test(workflow)) {
              inviter += ' Workflow';
            }
          } else if (self.inviterRole === 'staff') {
            inviter = 'Automated Invitation';
          } else if (self.inviterRole === 'api') {
            inviter = 'Automated Invitation - API';
          } else if (self.teamMember) {
            inviter = self.teamMember.nameOrEmail;
          }
          return inviter || '(Inviter Data Missing)';
        },
        isActive: function isActive() {
          return this.state === 'active';
        },
        isAccepted: function isAccepted() {
          return this.state === 'accepted';
        },
        isCanceled: function isCanceled() {
          return this.state === 'cancelled';
        },
        isExpired: function isExpired() {
          return this.state === 'expired';
        },
        inviteLink: function inviteLink() {
          return this.invitePath && absoluteUrl(this.invitePath);
        },
        invitePracticeLink: function invitePracticeLink() {
          return this.invitePracticePath && absoluteUrl(this.invitePracticePath);
        },
        shouldCollapse: function shouldCollapse(last) {
          var inv = this;
          return (inv.isActive() || inv.isAccepted()) && inv.state === last.state && inv.teamMemberId === last.teamMemberId && inv.action === last.action;
        }
      },

      relations: {
        belongsTo: {
          Assessment: {
            localField: 'assessment',
            localKey: 'assessmentId'
          },
          AssessmentResult: {
            localField: 'assessmentResult',
            localKey: 'assessmentResultId'
          },
          TeamCandidate: {
            localField: 'teamCandidate',
            localKey: 'teamCandidateId'
          },
          TeamMember: {
            localField: 'teamMember',
            localKey: 'teamMemberId'
          }
        }
      },

      meta: {
        saveExcludes: ['assessment', 'teamCandidate'],
        actionEndpoints: {
          cancel: { method: 'DELETE' },
          resend: { method: 'POST' },
          accept: { method: 'POST' }
        }
      }
    });
  }]);

  var uniqueIndex = 0;

  var workflowTypeLabels = {
    simple_approve: 'Approve Workflow',
    simple_reject: 'Reject Workflow'
  };

  var teamCandidateStateLabels = {
    approved: 'Auto-Approve',
    rejected: 'Auto-Reject',
    archived: 'Auto-Archive'
  };
  var teamCandidateStateIcons = {
    approved: 'thumbs-up',
    rejected: 'thumbs-down',
    archived: 'archive'
  };

  function definePropKey(model, enKey, initial) {
    if (model[enKey] === undefined) {
      Object.defineProperty(model, enKey, {
        value: initial,
        configurable: true,
        writable: true
      });
    }
  }

  var whenTypeLabels = {
    scoreMax: 'at or below',
    scoreMin: 'at least',
    timeMax: 'less than',
    timeMin: 'more than'
  };

  var assessmentWorkflowPathModelModule = angular.module('Q.models.assessment-workflow-path', [modelHelperModule])

  /**
   * Utility class to help with assessment workflows, which are partially shared between
   * custom workflows and the default workflow on the Assessment
   */
  .factory('AssessmentWorkflowHelper', ['$injector', 'DS', function ($injector, DS) {
    function createAction(msg, icon, tooltip) {
      return {
        toString: function toString$$1() {
          return msg;
        },

        icon: 'icon-' + icon,
        tooltip: tooltip
      };
    }

    var percUnit = '<span class="unit">%</span>';
    var hourUnit = '<span class="unit">h</span>';
    var minUnit = '<span class="unit">m</span>';
    var ltCompare = '<span class="comparator">≤</span>';
    var gtCompare = '<span class="comparator">≥</span>';
    var isCompare = '<span class="comparator">is</span>';
    var minSymbol = function minSymbol(v) {
      return v ? ltCompare : isCompare;
    };
    var maxSymbol = function maxSymbol(v) {
      return v < 100 ? gtCompare : isCompare;
    };
    var timeRenderer = function timeRenderer(t) {
      var timeString = t + minUnit;
      if (t > 60) {
        var h = Math.floor(t / 60);
        var m = t % 60;
        timeString = h + hourUnit;
        if (m) timeString += ' ' + m + minUnit;
      }
      return timeString;
    };

    return {
      /**
       * Returns an array of the actions that will be taken by the workflow or assessment
       * passed in.
       * @param data {Object} AssessmentWorkflowPath or Assessment to be scanned
       * @returns {Array<Object>}
       */
      actions: function actions(data) {
        var PT = $injector.has('PT') && $injector.get('PT');
        var participant = PT && PT.participant || 'candidate';
        var actions = [];

        if (data.submissionMessage) {
          actions.push(createAction('Show custom message to ' + participant, 'description', 'Message: "' + limit(data.submissionMessage, 50, ' […]') + '"'));
        }

        if (data.retryAction) {
          actions.push(createAction((data.retryAction === 'retake' ? 'Retake' : 'Reopen') + ' Assessment', 'undo'));
        }

        if (data.nextAssessmentId) {
          var nextAssessment = DS.get('Assessment', data.nextAssessmentId);
          actions.push(createAction('Send ' + participant + ' to ' + (nextAssessment ? '"' + nextAssessment.title + '"' : 'Next Assessment'), 'substitute'));
        }

        if (data.redirectUrl) {
          actions.push(createAction('Redirect ' + participant + ' to URL', 'link', data.redirectUrl));
        }

        var tcState = teamCandidateStateLabels[data.assignmentTeamCandidateState];
        if (tcState) {
          actions.push(createAction(tcState + ' ' + participant, teamCandidateStateIcons[data.assignmentTeamCandidateState]));
        }

        var teamMemberIds = data.assignmentTeamMemberIds;
        var teamMemberCount = teamMemberIds && teamMemberIds.length;
        if (teamMemberCount) {
          var plural = teamMemberCount > 1 ? 's' : '';
          var fn = DS.get('TeamMember', teamMemberIds[0]);
          var msg = plural ? teamMemberCount + ' team members' : fn && fn.name || 'team member';
          actions.push(createAction('Assign to ' + msg, 'user' + plural, plural && teamMemberIds.map(function (tmid) {
            return DS.get('TeamMember', tmid);
          }).filter(angular.identity).map(function (tm) {
            return tm.nameOrEmail;
          }).join(', ')));
        }

        var labels = data.assignmentLabels || [];
        if (labels.length > 0) {
          var _plural = labels.length === 1 ? '' : 's';
          actions.push(createAction('Add to label' + _plural + ' "' + labels.join('", "') + '"', 'label' + _plural));
        }

        if (data.webhookUrl) {
          actions.push(createAction('Send Webhook Event', 'webhooks', data.webhookUrl));
        }
        return actions;
      },


      /**
       * Returns a formatted strings representing the conditions for the given workflow
       * @param data
       * @returns {string}
       */
      conditions: function conditions(data) {
        var cond = [];

        if (data.scoreMin && data.scoreMax) {
          cond.push('' + data.scoreMin + percUnit + ' ' + ltCompare + ' Score ' + ltCompare + ' ' + data.scoreMax + percUnit);
        } else if (angular.isNumber(data.scoreMax)) {
          cond.push('Score ' + minSymbol(data.scoreMax) + ' ' + data.scoreMax + percUnit);
        } else if (angular.isNumber(data.scoreMin)) {
          cond.push('Score ' + maxSymbol(data.scoreMin) + ' ' + data.scoreMin + percUnit);
        }

        var timeLabel = data.timeType === 'total' ? 'Total Time' : 'Active Time';
        if (data.timeMin && data.timeMax) {
          cond.push(timeRenderer(data.timeMin) + ' ' + ltCompare + ' ' + timeLabel + ' ' + ltCompare + ' ' + timeRenderer(data.timeMax));
        } else if (data.timeMin) {
          cond.push(timeLabel + ' ' + gtCompare + ' ' + timeRenderer(data.timeMin));
        } else if (data.timeMax) {
          cond.push(timeLabel + ' ' + ltCompare + ' ' + timeRenderer(data.timeMax));
        }

        return cond.join(' <span class="op">' + data.conditionsOp + '</span> ');
      },


      /**
       * Creates a utility method for the workflow action toggles. This method acts as a getter/setter,
       * taking care of setting the default value or removing the value for the various string-format actions
       * @param key {String} Action key
       * @param initialValue {String?} The value to set when the action is enabled.
       * @returns {Function} Returns a getter/setter function for the key
       */
      actionEnabledString: function actionEnabledString(key) {
        var initialValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';

        var enKey = '$' + key + 'Enabled';
        return function actionEnabled(val) {
          var currentAction = !!this[key];
          definePropKey(this, enKey, currentAction);
          if (val === undefined) return currentAction || this[enKey];
          this[enKey] = val;
          if (val === true) {
            this[key] = this[key] || initialValue;
          } else {
            this[key] = null;
          }
        };
      },


      /**
       * Creates a utility method for the workflow action toggles. This method acts as a getter/setter,
       * taking care of removing the values for the various array-format actions
       * @param key {String} Action key
       * @returns {Function} Returns a getter/setter function for the key
       */
      actionEnabledArray: function actionEnabledArray(key) {
        var enKey = '$' + key + 'Enabled';
        return function actionEnabled(val) {
          var currentAction = !!(this[key] || []).length;
          definePropKey(this, enKey, currentAction);
          if (val === undefined) return currentAction || this[enKey];
          this[enKey] = val;
          if (val === false) {
            this[key] = [];
          }
        };
      }
    };
  }]).factory('AssessmentWorkflowPath', ['ModelHelper', 'AssessmentWorkflowHelper', function (ModelHelper, AssessmentWorkflowHelper) {
    function initData(data) {
      if (!data.$uniqueIndex) {
        // ensure we have a unique ID for repeaters
        var id = void 0;
        if (data.assessmentId && data.workflowType) {
          // these are unique to the assessment, so we use a static value
          id = data.assessmentId + '-' + data.workflowType;
        } else if (data.id) {
          // custom workflows, it's OK if they get refreshed
          id = data.id;
        } else {
          // Otherwise, use a generated ID for client-side ordering
          id = ++uniqueIndex;
        }
        data.$uniqueIndex = id;
      }
      if (!data.name && data.workflowType) {
        // set the default name
        data.name = workflowTypeLabels[data.workflowType];
      }
    }

    var AssessmentWorkflowPath = ModelHelper.defineResource({
      name: 'AssessmentWorkflowPath',
      endpoint: 'assessment_workflow_paths',

      beforeCreateInstance: function beforeCreateInstance(resource, data) {
        initData(data);
      },
      beforeInject: function beforeInject(resource, data) {
        if (!Array.isArray(data)) data = [data];
        data.forEach(function (d) {
          initData(d);
          // denormalize the actions and conditions
          d.$actions = AssessmentWorkflowHelper.actions(d);
          d.$conditions = AssessmentWorkflowHelper.conditions(d);
        });
      },


      staticMethods: {
        scoreWhenTypes: [{ value: 'scoreMin', text: whenTypeLabels['scoreMin'] }, { value: 'scoreMax', text: whenTypeLabels['scoreMax'] }],
        timeWhenTypes: [{ value: 'timeMax', text: whenTypeLabels['timeMax'] }, { value: 'timeMin', text: whenTypeLabels['timeMin'] }]
      },

      methods: {
        isValid: function isValid() {
          var _this = this;

          // filters out invalid workflows to prevent errors when saving
          // these are workflows without a condition
          // returns undefined for invalid so it can be used in an ngModel
          return ['timeMin', 'timeMax', 'scoreMin', 'scoreMax'].some(function (k) {
            return angular.isNumber(_this[k]);
          }) || undefined;
        },
        label: function label() {
          return this.name || workflowTypeLabels[this.workflowType] || 'Custom Workflow';
        },
        isSimpleApprove: function isSimpleApprove() {
          return this.workflowType === 'simple_approve';
        },
        isSimpleReject: function isSimpleReject() {
          return this.workflowType === 'simple_reject';
        },


        // Action getter/setters
        assignmentTeamCandidateStateEnabled: AssessmentWorkflowHelper.actionEnabledString('assignmentTeamCandidateState', 'approved'),
        submissionMessageEnabled: AssessmentWorkflowHelper.actionEnabledString('submissionMessage'),
        nextAssessmentIdEnabled: AssessmentWorkflowHelper.actionEnabledString('nextAssessmentId', null),
        assignmentTeamMemberIdsEnabled: AssessmentWorkflowHelper.actionEnabledArray('assignmentTeamMemberIds'),
        assignmentLabelsEnabled: AssessmentWorkflowHelper.actionEnabledArray('assignmentLabels'),
        redirectUrlEnabled: AssessmentWorkflowHelper.actionEnabledString('redirectUrl'),
        webhookUrlEnabled: AssessmentWorkflowHelper.actionEnabledString('webhookUrl'),
        retryActionEnabled: AssessmentWorkflowHelper.actionEnabledString('retryAction', 'reopen'),

        // Used to show or hide extended actions & conditions on simple workflows
        advancedEnabled: function advancedEnabled(val) {
          definePropKey(this, '$advancedShowing', !this.workflowType);
          if (val === undefined) return this.$advancedShowing;
          this.$advancedShowing = val || !this.workflowType;
        },
        scoreWhenType: function scoreWhenType(newVal) {
          // defaults to scoreMin if nothing is set
          definePropKey(this, '$scoreWhenType', this.scoreMax ? 'scoreMax' : 'scoreMin');
          if (newVal === undefined) return this.$scoreWhenType;
          if (this.$scoreWhenType !== newVal) {
            this[newVal] = this[this.$scoreWhenType];
            this[this.$scoreWhenType] = null;
            this.$scoreWhenType = newVal;
          }
        },
        scoreWhenTypeLabel: function scoreWhenTypeLabel(scoreWhenType) {
          return whenTypeLabels[scoreWhenType || this.scoreWhenType()];
        },
        timeWhenType: function timeWhenType(newVal) {
          // defaults to timeMax if nothing is set
          definePropKey(this, '$timeWhenType', this.timeMin ? 'timeMin' : 'timeMax');
          if (newVal === undefined) return this.$timeWhenType;
          if (this.$timeWhenType !== newVal) {
            this[newVal] = this[this.$timeWhenType];
            this[this.$timeWhenType] = null;
            this.$timeWhenType = newVal;
          }
        },
        timeWhenTypeLabel: function timeWhenTypeLabel(timeWhenType) {
          return whenTypeLabels[timeWhenType || this.timeWhenType()];
        }
      },

      relations: {
        belongsTo: {
          Assessment: {
            localField: 'assessment',
            localKey: 'assessmentId'
          }
        },
        hasMany: {
          TeamMember: {
            localField: 'assignmentTeamMembers',
            localKeys: 'assignmentTeamMemberIds'
          }
        },
        hasOne: {
          Assessment: {
            localField: 'nextAssessment',
            localKey: 'nextAssessmentId'
          }
        }
      },

      meta: {
        track: {
          create: true,
          update: true
        }
      }
    });

    return AssessmentWorkflowPath;
  }]);

  var ASSESSMENTS_STATE_ORDER = {
    published: 0,
    draft: 1,
    archived: 3
  };

  var defaultWorkflowProperties = ['submissionMessage', 'redirectUrl', 'assignmentTeamMemberIds', 'assignmentLabels', 'webhookUrl'];

  var MAX_RECOMMENDED_ASSESSMENT_TIME = 240; // 4 hours
  var VERY_LONG_ASSESSMENT_TIME = 480; // 8 hours
  var MAX_RECOMMENDED_NUMBER_OF_CHALLENGES = 10;
  var VERY_LONG_NUMBER_OF_CHALLENGES = 20;

  var assessmentModelModule = angular.module('Q.models.assessment', [modelHelperModule, assessmentWorkflowPathModelModule]).factory('Assessment', ['ModelHelper', '$window', '$q', 'DS', 'AssessmentWorkflowHelper', '$injector', function (ModelHelper, $window, $q, DS, AssessmentWorkflowHelper, $injector) {
    var stateNameMappings = {
      draft: 'Draft',
      published: 'Published',
      archived: 'Archived'
    };

    var Assessment = ModelHelper.defineResource({
      name: 'Assessment',
      endpoint: 'assessments',

      serialize: function serialize(resource, data) {
        if (data.workflowPaths && data.workflowPaths.length) {
          // We need to remove any disabled workflows before serializing
          data.workflowPaths = data.workflowPaths.filter(function (wf) {
            return wf.$enabled !== false;
          });
        }
        return ModelHelper.serialize(resource, data);
      },
      beforeInject: function beforeInject(resource, data) {
        if (!Array.isArray(data)) data = [data];
        data.forEach(function (d) {
          // denormalize any default workflow actions
          d.$defaultWorkflowActions = AssessmentWorkflowHelper.actions(d);
        });
      },


      computed: {
        selectTitle: /* @ngInject */['title', 'demoData', function selectTitle(title, demoData) {
          return title + (demoData ? ' (DEMO)' : '');
        }],
        isSample: /* @ngInject */['isPractice', 'isDemo', function isSample(isPractice, isDemo) {
          return isPractice || isDemo;
        }],
        difficulty: {
          get: function get$$1() {
            if (this.$difficulty === undefined) {
              var difficulties = this.challenges().map(function (challenge) {
                return +challenge.difficulty || 0;
              });
              this.$difficulty = Math.max.apply(Math, toConsumableArray(difficulties));
            }
            return this.$difficulty;
          }
        }
      },

      methods: {
        challenges: function challenges() {
          if (this.digest && this.digest.challengeStats) return this.digest.challengeStats;
          if (!this.challengeItems || !this.challengeItems.length) return [];
          return this.challengeItems.reduce(function (arr, ci) {
            if (ci.challenge) {
              arr.push(ci.challenge);
            } else {
              // load missing challenge for next time, but don't include it right now
              ci.getChallenge();
            }
            return arr;
          }, []);
        },
        topics: function topics() {
          var allTopics = this.challenges().reduce(function (arr, challenge) {
            return arr.concat(challenge.topics || []);
          }, []);
          return unique(allTopics);
        },
        stateName: function stateName() {
          return stateNameMappings[this.state];
        },
        assessUrl: function assessUrl(setupMode) {
          return $window.location.origin + appConfig.assessRoute + '/' + this.id + (setupMode ? '?fromSetup=true' : '');
        },
        isDraft: function isDraft() {
          return this.state === 'draft';
        },
        isArchived: function isArchived() {
          return this.state === 'archived';
        },
        isPublished: function isPublished() {
          return this.state === 'published';
        },
        isPreviewable: function isPreviewable() {
          return !this.demoData && (this.isPublished() || this.challengeItems && this.challengeItems.length > 0);
        },
        isSoftDeleted: function isSoftDeleted() {
          return !!this.softDeletedAt;
        },
        estimatedTime: function estimatedTime() {
          return this.manualEstimatedTime || this.suggestedEstimatedTime;
        },
        checkIfOverLong: function checkIfOverLong(challenge) {
          var assessment = this;

          var team = getTeam($injector) || assessment.team;
          if (team && !team.warnOnLongAssessments) return false;

          var totalChallengeCount = assessment.challengeItems.length + (challenge ? 1 : 0);
          var tooManyChallenges = totalChallengeCount > MAX_RECOMMENDED_NUMBER_OF_CHALLENGES;

          var baseTime = assessment.estimatedTime();
          if (assessment.challengeItems.every(function (c) {
            return c.challenge;
          })) {
            // use internally estimated time for challenges;
            baseTime = assessment.challengeItems.reduce(function (s, ci) {
              return s + ci.challenge.expectedTime();
            }, 0);
          }
          var totalTime = baseTime + (challenge ? challenge.expectedTime() : 0);
          // only warn on too long if there's 2 or more challenges, in case there's one really long challenge.
          var tooLong = totalChallengeCount > 1 && totalTime > MAX_RECOMMENDED_ASSESSMENT_TIME;

          var veryLong = totalTime > VERY_LONG_ASSESSMENT_TIME || totalChallengeCount > VERY_LONG_NUMBER_OF_CHALLENGES;

          if (tooLong || tooManyChallenges) {
            return {
              veryLong: veryLong,
              tooManyChallenges: tooManyChallenges,
              totalChallengeCount: totalChallengeCount,
              tooLong: tooLong,
              totalTime: totalTime
            };
          } else {
            return false;
          }
        },
        hasTimedChallenges: function hasTimedChallenges() {
          return this.challenges().some(function (c) {
            return !c.isUntimed();
          });
        },

        trackProperties: function trackProperties() {
          return {
            title: this.title,
            id: this.id,
            teamId: this.teamId,
            state: this.state,
            summary: this.summary,
            availablePositions: this.availablePositions
          };
        },
        loadCopiedFrom: function loadCopiedFrom() {
          var _this = this;

          if (!this.$copiedFromLoaded && !this.copiedFrom && this.copiedFromId) {
            // HACK: because templates and normal assessments are the same model,
            // we have to look up the template and inject it manually
            return Assessment.find(this.copiedFromId, {
              cacheResponse: false
            }).then(function (a) {
              return DS.inject('AssessmentTemplate', a);
            }).catch(function () {
              return null;
            }).finally(function () {
              // to prevent reloading errors repeatedly
              _this.$copiedFromLoaded = true;
            });
          } else {
            return $q.resolve(this.copiedFrom);
          }
        },
        difficultyName: function difficultyName() {
          return DS.definitions.Challenge.difficulties[this.difficulty] || 'Warmup';
        },
        templateAssessment: function templateAssessment() {
          if (this.copiedFrom) {
            var template$$1 = this.copiedFrom;
            if (template$$1.isTemplate) {
              return template$$1;
            }
          }
          return null;
        },
        hasStats: function hasStats(type) {
          var stats = this[type + 'Stats'];
          var globalStats = this.globalStats(type);
          // ignore if only one result
          return !!(stats && stats.count > 1) || !!(globalStats && globalStats.count > 1);
        },
        globalStats: function globalStats(type) {
          var template$$1 = this.templateAssessment();
          return template$$1 && template$$1[type + 'Stats'];
        },
        proctoringEnabled: function proctoringEnabled() {
          var team = this.team;
          return team && team.hasFeature('proctoring') && (this.enableProctoring === 'enabled' || this.enableProctoring === 'default' && team.defaultEnableProctoring);
        },


        // workflow methods
        getSimpleWorkflow: function getSimpleWorkflow(type, createIfNotFound) {
          var workflowType = 'simple_' + type;
          var wfp = this.workflowPaths;
          if (!wfp) {
            wfp = this.workflowPaths = [];
          }
          var wf = wfp.find(function (wf) {
            return wf.workflowType === workflowType;
          });
          if (!wf && createIfNotFound) {
            wf = DS.createInstance('AssessmentWorkflowPath', {
              assessmentId: this.id,
              order: wfp.length,
              workflowType: workflowType,
              timeType: 'accumulated'
            });
            wfp.push(wf);
          }
          return wf;
        },
        hasCustomWorkflow: function hasCustomWorkflow() {
          return this.workflowPaths && this.workflowPaths.some(function (wf) {
            return !wf.workflowType;
          });
        },
        hasDefaultWorkflow: function hasDefaultWorkflow() {
          var _this2 = this;

          return defaultWorkflowProperties.some(function (k) {
            var prop = _this2[k];
            return Array.isArray(prop) ? prop.length : !!prop;
          });
        },
        clearDefaultWorkflow: function clearDefaultWorkflow() {
          var _this3 = this;

          defaultWorkflowProperties.forEach(function (k) {
            _this3[k] = Array.isArray(_this3[k]) ? [] : null;
            _this3['$' + k + 'Enabled'] = false;
          });
        },


        // default workflow actions
        submissionMessageEnabled: AssessmentWorkflowHelper.actionEnabledString('submissionMessage'),
        assignmentTeamMemberIdsEnabled: AssessmentWorkflowHelper.actionEnabledArray('assignmentTeamMemberIds'),
        assignmentLabelsEnabled: AssessmentWorkflowHelper.actionEnabledArray('assignmentLabels'),
        redirectUrlEnabled: AssessmentWorkflowHelper.actionEnabledString('redirectUrl'),
        webhookUrlEnabled: AssessmentWorkflowHelper.actionEnabledString('webhookUrl')
      },

      relations: {
        hasMany: {
          AssessmentResult: {
            localField: 'assessmentResults',
            foreignKey: 'assessmentId'
          },
          AssessmentCohort: {
            localField: 'cohorts',
            foreignKey: 'assessmentId'
          },
          ChallengeItem: {
            localField: 'challengeItems',
            link: false
          },
          AssessmentWorkflowPath: {
            localField: 'workflowPaths',
            foreignKey: 'assessmentId',
            link: false
          },
          TeamMember: [{
            localField: 'assignmentTeamMembers',
            localKeys: 'assignmentTeamMemberIds'
          }, {
            localField: 'defaultAssignedTeamMembers',
            localKeys: 'defaultAssignedTeamMemberIds'
          }]
        },
        belongsTo: {
          Team: {
            localField: 'team',
            localKey: 'teamId'
          },
          TeamMember: [{
            localField: 'createdBy',
            localKey: 'createdById'
          }]
        },
        hasOne: {
          AssessmentTemplate: {
            localField: 'copiedFrom',
            localKey: 'copiedFromId'
          }
        }
      },

      beforeEject: function beforeEject(resource, instances) {
        if (!Array.isArray(instances)) instances = [instances];
        instances.forEach(function (instance) {
          if (instance) {
            if (instance.challengeItems) {
              instance.challengeItems.forEach(function (ci) {
                return ci.DSEject();
              });
            }
            if (instance.assessmentWorkflowPaths) {
              instance.assessmentWorkflowPaths.forEach(function (awp) {
                return awp.DSEject();
              });
            }
          }
        });
      },


      meta: {
        hasDigest: true,
        cleanInjected: function cleanInjected(instance) {
          delete instance.$difficulty;
        },

        includeOnPull: ['digest'],
        saveExcludes: ['team'],
        actionEndpoints: {
          publish: { method: 'PUT', track: true },
          archive: { method: 'PUT', track: true },
          unarchive: { method: 'PUT', track: true },
          unlockAll: { method: 'PUT', track: true },
          export: { method: 'POST', track: true, cacheResponse: false },
          recover: {
            method: 'PUT',
            track: true,
            onSuccess: function onSuccess(model) {
              // clean up local model
              delete model.softDeletedAt;
              delete model.softDeletedBy;
            }
          }
        },
        track: {
          create: true,
          update: true
        }
      }
    });

    ModelHelper.defineQuickList(Assessment, {}, {
      sort: function sort(a, b) {
        return ASSESSMENTS_STATE_ORDER[a.state] - ASSESSMENTS_STATE_ORDER[b.state] || compareIgnoreCase(a.title, b.title);
      }
    });

    return Assessment;
  }]);

  var teamCandidateModelModule = angular.module('Q.models.team-candidate', [modelHelperModule]).factory('TeamCandidate', ['ModelHelper', '$injector', '$http', function (ModelHelper, $injector, $http) {
    var STATE_ORDER = {
      idle: 5,
      invited: 0,
      started: 1,
      submitted: 2,
      risk_detected: 2,
      reviewed: 3,
      approved: 4,
      rejected: 4,
      archived: 4
    };

    var liveInterviewService = void 0;

    function clearLiveinterviewData(tc) {
      // clear any old live interview data
      if (tc && tc.assessmentResults && tc.assessmentResults.length) {
        if (!liveInterviewService) {
          // avoid hard-requirements against the service
          liveInterviewService = $injector.has('liveInterviewService') && $injector.get('liveInterviewService');
        }
        if (liveInterviewService) {
          tc.assessmentResults.forEach(liveInterviewService.destroy);
        }
      }
    }

    var TeamCandidate = ModelHelper.defineResource({
      name: 'TeamCandidate',
      endpoint: 'team_candidates',

      computed: {
        nameOrEmail: /* @ngInject */['name', 'email', 'forgotten', function nameOrEmail(name, email, forgotten) {
          return name || email || (forgotten ? '(Contact Info Removed)' : '(No Contact Information)');
        }],
        hasNameOrEmail: /* @ngInject */['name', 'email', function hasNameOrEmail(name, email) {
          return !!(name || email);
        }]
      },

      staticMethods: {
        // shared labels between TeamCandidate and AssessmentResult
        stateNameMappings: {
          idle: 'Idle',
          invited: 'Invited',
          opened: 'Opened',
          started: 'Started',
          submitting: 'Submitting',
          submitted: 'Submitted',
          risk_detected: 'Plagiarism Risk',
          reviewed: 'Reviewed',
          'submitted-candidateSubmitted': 'Submitted by candidate',
          'submitted-timerRanOut': 'Autosubmitted after timer ran out',
          'submitted-teamDecided': 'Closed by team decision',
          'submitted-teamForced': 'Closed by team member',
          'submitted-invitationExpired': 'Closed by invitation expiration',
          'submitted-assessmentArchived': 'Closed by assessment archival',
          approved: 'Approved',
          rejected: 'Rejected',
          archived: 'Archived',
          terminated: 'Closed without submitting'
        },
        /**
         *
         * @param data {Object}
         * @param data.bulkAction {String} Action to carry out
         * @param data.ids {Array<String>} Array of ids
         * @param data.data {Object} additional data to pass along
         * @param config {Object} $http config
         * @returns {HttpPromise}
         */
        bulkUpdate: function bulkUpdate(data, config) {
          return $http.post(appConfig.apiRoot + 'team_candidates/bulk_update/', data, config);
        }
      },

      methods: {
        openAssessmentCount: function openAssessmentCount() {
          return (this.assessmentResults || []).filter(function (ar) {
            return ar.state === 'opened' || ar.state === 'started';
          }).length;
        },
        invitedAssessmentCount: function invitedAssessmentCount() {
          return (this.assessmentResults || []).filter(function (ar) {
            return ar.state === 'invited';
          }).length;
        },
        isIdle: function isIdle() {
          return this.state === 'idle';
        },
        isInvited: function isInvited() {
          return this.state === 'invited';
        },
        isStarted: function isStarted() {
          return this.state === 'started';
        },
        isSubmitted: function isSubmitted() {
          return this.state === 'submitted';
        },
        isSubmittedOrRisky: function isSubmittedOrRisky() {
          return this.state === 'submitted' || this.state === 'risk_detected';
        },
        isReviewed: function isReviewed() {
          return this.state === 'reviewed';
        },
        isDecided: function isDecided() {
          return ['approved', 'rejected', 'archived'].indexOf(this.state) !== -1;
        },
        isUndecided: function isUndecided() {
          return this.testTeamMember || !this.isDecided();
        },
        isArchived: function isArchived() {
          return this.state === 'archived';
        },
        isApproved: function isApproved() {
          return this.state === 'approved';
        },
        isRejected: function isRejected() {
          return this.state === 'rejected';
        },
        canDecide: function canDecide() {
          return ['started', 'submitted', 'risk_detected', 'reviewed'].indexOf(this.state) !== -1;
        },
        canRestore: function canRestore() {
          return this.isDecided() && this.prevState && this.prevState !== 'archived';
        },
        canBeArchived: function canBeArchived() {
          return !this.isArchived();
        },
        stateName: function stateName(value, includeArchived) {
          var val = TeamCandidate.stateNameMappings[value || this.state];
          var prevState = TeamCandidate.stateNameMappings[this.prevState];
          if (includeArchived && this.isArchived() && this.prevState !== 'archived' && prevState) {
            val += ', was ' + prevState;
          }
          return val;
        },
        hasState: function hasState(state) {
          return STATE_ORDER[state] <= (STATE_ORDER[this.state] || 0);
        },
        toString: function toString() {
          return this.nameOrEmail;
        },
        toSelection: function toSelection() {
          var tc = this;
          if (!tc.$selection) {
            var _sel = { id: tc.id };
            defineValue(_sel, 'reload', function () {
              tc.toSelection();
            });
            defineValue(tc, '$selection', _sel);
          }
          var sel = tc.$selection;
          sel.name = tc.nameOrEmail;
          sel.email = tc.email; // for inviting
          sel.stateName = tc.stateName(null, true);
          sel.flags = [tc.canBeArchived() && 'archive', tc.canRestore() && 'restore', tc.canDecide() && 'decide', tc.invitedAssessmentCount() && 'invited', tc.openAssessmentCount() && 'open'].filter(angular.identity);
          return sel;
        }
      },
      relations: {
        hasMany: {
          Assessment: {
            localField: 'assessments',
            localKeys: 'assessmentIds'
          },
          AssessmentCohort: {
            localField: 'cohorts',
            localKeys: 'cohortIds'
          },
          AssessmentResult: {
            localField: 'assessmentResults',
            foreignKey: 'teamCandidateId',
            get: function get(Resource, relationDef, instance, origGetter) {
              return origGetter().filter(function (ar) {
                return ar.currentTake;
              });
            }
          },
          Solution: {
            localField: 'solutions',
            foreignKey: 'teamCandidateId'
          },
          Note: {
            localField: 'notes',
            foreignKey: 'notableId'
          },
          TeamMember: {
            localField: 'assignedTeamMembers',
            localKeys: 'assignedTeamMemberIds'
          },
          AssessmentResultReview: {
            localField: 'assessmentResultReviews',
            foreignKey: 'teamCandidateId'
          },
          SolutionReview: {
            localField: 'solutionReviews',
            foreignKey: 'teamCandidateId'
          },
          AssessmentInvitation: {
            localField: 'assessmentInvitations',
            foreignKey: 'teamCandidateId'
          }
        },
        hasOne: {
          Candidate: {
            localField: 'candidate',
            localKey: 'candidateId'
          },
          Team: {
            localField: 'team',
            localKey: 'teamId'
          }
        }
      },

      meta: {
        includeOnPull: ['digest', 'signals_profile'],
        hasDigest: true,
        actionEndpoints: {
          approve: {
            method: 'PUT',
            track: true,
            onSuccess: clearLiveinterviewData
          },
          cancel: {
            method: 'PUT',
            track: true,
            onSuccess: clearLiveinterviewData
          },
          reject: {
            method: 'PUT',
            track: true,
            onSuccess: clearLiveinterviewData
          },
          restore: {
            method: 'PUT',
            track: true,
            onSuccess: clearLiveinterviewData
          },
          close: {
            method: 'PUT',
            track: true,
            onSuccess: clearLiveinterviewData
          },
          archive: {
            method: 'PUT',
            track: true,
            onSuccess: clearLiveinterviewData
          },
          assign: { method: 'PUT', track: true },
          forget: { method: 'PUT', track: true }
        }
      }
    });

    return TeamCandidate;
  }]);

  var DEFAULT_PERCENTILES = [10, 25, 50, 60, 70, 75, 80, 85, 90, 95, 99].map(function (v) {
    return [v, Infinity];
  });
  var DEFAULT_PERCENTILES_INVERTED = DEFAULT_PERCENTILES.map(function (p) {
    return [p[0], -Infinity];
  });

  function getPercentile(value, stats) {
    if (!stats || !stats.percentiles) return '';
    var arr = getPercentilesArray(stats);
    var ret = 0;
    var i = 0;
    while (arr[i] && (stats.inverted && value <= arr[i][1] || !stats.inverted && value >= arr[i][1])) {
      ret = arr[i][0];
      i++;
    }
    if (ret === 0) {
      return '';
    }
    return '' + ret + ordinalSuffix(ret);
  }

  /**
   * Converts a percentiles map into an array, sorted and mildly cleaned up
   * @param stats
   * @returns {Array}
   */
  function getPercentilesArray(stats) {
    if (!stats || !stats.percentiles) return DEFAULT_PERCENTILES;
    if (!stats.percentilesArray) {
      var percentilesArray = Object.keys(stats.percentiles).map(function (k) {
        return [+k, +stats.percentiles[k] || 0];
      }).sort(function (a, b) {
        return a[0] - b[0];
      });
      if (!percentilesArray.length) {
        percentilesArray = stats.inverted ? DEFAULT_PERCENTILES_INVERTED : DEFAULT_PERCENTILES;
      }
      defineValue(stats, 'percentilesArray', percentilesArray);
    }
    return stats.percentilesArray;
  }

  var _EDIT_MODE_LABELS;

  var CLOSED_REASONS = {
    candidateSubmitted: 'submitted by candidate',
    timerRanOut: 'auto-submitted after timer ran out',
    teamDecided: 'closed before submission by team decision',
    teamForced: 'closed before submission by team',
    invitationExpired: 'closed by invitation expiration',
    assessmentArchived: 'closed by assessment archival'
  };

  var RETRY_ACTION_RETAKE = 'retake';
  var RETRY_ACTION_REOPEN = 'reopen';

  var EDIT_MODE_READONLY = 'readonly';
  var EDIT_MODE_RESTRICTED = 'restricted';
  var EDIT_MODE_NORMAL = 'normal';
  var EDIT_MODE_LABELS = (_EDIT_MODE_LABELS = {}, defineProperty(_EDIT_MODE_LABELS, EDIT_MODE_NORMAL, 'Normal'), defineProperty(_EDIT_MODE_LABELS, EDIT_MODE_READONLY, 'Read-Only'), defineProperty(_EDIT_MODE_LABELS, EDIT_MODE_RESTRICTED, 'Restricted'), _EDIT_MODE_LABELS);

  var assessmentResultModelModule = angular.module('Q.models.assessment-result', [modelHelperModule, teamCandidateModelModule]).factory('AssessmentResult', ['ModelHelper', 'DS', '$injector', '$log', function (ModelHelper, DS, $injector, $log) {
    var currentUser = getUser.bind(null, $injector);

    // Don't require a hard dependency on $state, mock if missing
    var $state = $injector.has('$state') && $injector.get('$state') || {
      params: {},
      href: noop
    };

    var AssessmentResult = ModelHelper.defineResource({
      name: 'AssessmentResult',
      endpoint: 'assessment_results',

      computed: {
        // enables overriding the Assessment redirectUrl with a custom one in the app URL
        //
        $redirectUrl: {
          get: function get$$1() {
            return $state.params.redirectUrl || this.redirectUrl;
          }
        }
      },

      methods: {
        canEdit: function canEdit() {
          return !this.accessLocked && !this.isClosed();
        },
        isInRetryAccessWindow: function isInRetryAccessWindow() {
          return !!this.retrySchedule && this.currentTake && dateNullOrBefore(this.retrySchedule.accessStartsAt) && dateNullOrAfter(this.retrySchedule.accessEndsAt);
        },
        isReopenScheduled: function isReopenScheduled() {
          return this.isInRetryAccessWindow() && this.retrySchedule.action === RETRY_ACTION_REOPEN;
        },
        isRetakeScheduled: function isRetakeScheduled() {
          return this.isInRetryAccessWindow() && this.retrySchedule.action === RETRY_ACTION_RETAKE;
        },
        isReadOnly: function isReadOnly() {
          return this.isActive() && this.editMode === EDIT_MODE_READONLY;
        },
        isRestricted: function isRestricted() {
          return this.isActive() && (this.isReadOnly() || this.editMode === EDIT_MODE_RESTRICTED);
        },
        canUnsubmit: function canUnsubmit() {
          return this.isClosed() && this.teamCandidate && !this.teamCandidate.forgotten && !this.teamCandidate.demoData;
        },
        submitStartedAt: function submitStartedAt() {
          return this.submittingAt || this.submittedAt;
        },
        isInvited: function isInvited() {
          return this.state === 'invited';
        },
        isOpened: function isOpened() {
          return this.state === 'opened';
        },
        accessUnlockedByLabel: function accessUnlockedByLabel() {
          return this.accessUnlockedBy && this.accessUnlockedBy.nameOrEmail || '(User Data Missing)';
        },
        isStarted: function isStarted() {
          return this.state === 'started';
        },
        isActive: function isActive() {
          return this.isOpened() || this.isStarted();
        },
        isSubmitting: function isSubmitting() {
          return this.state === 'submitting';
        },
        isSubmitted: function isSubmitted() {
          return this.state === 'submitted';
        },
        isTerminated: function isTerminated() {
          return this.state === 'terminated';
        },
        isInProgress: function isInProgress() {
          return !this.isSubmitted() && !this.isTerminated();
        },
        isClosed: function isClosed() {
          return this.isSubmitting() || this.isSubmitted() || this.isTerminated();
        },
        candidateSubmitted: function candidateSubmitted() {
          return this.closedReason === 'candidate_submitted';
        },
        timerRanOut: function timerRanOut() {
          return this.closedReason === 'timer_ran_out';
        },
        closedReasonText: function closedReasonText() {
          return CLOSED_REASONS[camelCase(this.closedReason)] || '';
        },
        cutOffTime: function cutOffTime() {
          if (this.timeLimit() && this.startedAt) {
            if (!isDate(this.startedAt)) {
              $log.error('this.startedAt is not a date', this.startedAt);
              return;
            }
            var milliseconds = this.timeLimitSeconds() * 1000;
            return new Date(this.startedAt.getTime() + milliseconds);
          }
        },
        retakeTimeLimitChange: function retakeTimeLimitChange() {
          return this.enforcedTimeLimit !== this.retrySchedule.enforcedTimeLimit;
        },
        timeLimit: function timeLimit() {
          return this.assessment && this.assessment.enforcedTimeLimit;
        },
        timeLimitSeconds: function timeLimitSeconds() {
          var limit$$1 = this.timeLimit();
          return limit$$1 && limit$$1 * 60;
        },
        lastHeartbeat: function lastHeartbeat() {
          var solutions = this.solutions;
          if (solutions && solutions.length) {
            return solutions.map(function (x) {
              return x.lastHeartbeat;
            }).sort(compareDate)[0];
          }
        },
        isAbandoned: function isAbandoned() {
          var tc = this.teamCandidate;
          var isPreview = tc && tc.testTeamMember;
          return this.abandoned && this.isStarted() && !isPreview;
        },
        detailedState: function detailedState() {
          var state = this.state;
          if (this.isSubmitted() && this.closedReason) {
            state += '-' + camelCase(this.closedReason);
          }
          return state;
        },
        stateName: function stateName() {
          var name = DS.definitions.TeamCandidate.stateNameMappings[this.detailedState()];
          if (this.isInRetryAccessWindow()) {
            name += ' (' + (this.isRetakeScheduled() ? 'Retake' : 'Reopen') + ' is scheduled)';
          }
          return name;
        },
        retryState: function retryState() {
          if (this.isReopenScheduled()) {
            return 'state-candidate-reopen-scheduled';
          } else if (this.isRetakeScheduled()) {
            return 'state-candidate-retake-scheduled';
          } else {
            return '';
          }
        },
        percentile: function percentile(type) {
          return getPercentile(this[type], this.assessment && this.assessment[type + 'Stats']);
        },
        hasStats: function hasStats(type) {
          return !!this.assessment && this.assessment.hasStats(type);
        },
        hasSubmittedReviews: function hasSubmittedReviews() {
          return (this.assessmentResultReviews || []).filter(function (r) {
            return r.state === 'submitted';
          }).length > 0;
        },

        // when a blind reviewer has access to all reviews, we should still hide them when they haven't actually submitted thier own.
        shouldHideOtherReviews: function shouldHideOtherReviews() {
          var teamMember = currentUser();
          return teamMember && teamMember.isBlindReviewer() && (!this.myReview() || this.myReview().state !== 'submitted');
        },
        blindedAssessmentResultReviews: function blindedAssessmentResultReviews() {
          if (this.shouldHideOtherReviews()) {
            return this.assessmentResultReviews.filter(function (arr) {
              return arr.reviewedById === currentUser().id;
            });
          } else {
            return this.assessmentResultReviews;
          }
        },
        myReview: function myReview() {
          if (!this._myReview) {
            var teamMember = currentUser();
            if (teamMember) {
              var arr = this.assessmentResultReviews.find(function (arr) {
                return arr.reviewedById === teamMember.id;
              });
              if (arr) {
                defineValue(this, '_myReview', arr);
              }
            }
          }
          return this._myReview;
        },


        /*
         * if there are result specific challenge items, return those, otherwise return
         * the items on the assessment itself.
         * The reason that challenge items sometimes exist on the result is that they act as a snapshot
         * of the assessment's state at the time of submission. This allows an assessment to be modified
         * without it affecting past submissions.
         */
        getChallengeItems: function getChallengeItems() {
          // use the local challenge items if there are any
          if (this.challengeItems && this.challengeItems.length) {
            return this.challengeItems;
          } else {
            return this.assessment ? this.assessment.challengeItems : [];
          }
        },
        getChallengeItem: function getChallengeItem(challenge) {
          return this.getChallengeItems().find(function (ci) {
            return ci.challengeId === challenge.id;
          });
        },


        /**
         * Returns true if this assessment result's snapshot is current with it's assessment
         * @returns {boolean}
         */
        isSnapshotCurrent: function isSnapshotCurrent() {
          var ar = this;
          var assessment = ar.assessment;
          var arChallengeItems = ar.challengeItems;
          var aChallengeItems = assessment && assessment.challengeItems;
          return !arChallengeItems || !aChallengeItems || aChallengeItems.length === arChallengeItems.length && aChallengeItems.every(function (ci) {
            var ciCopy = arChallengeItems.find(function (cic) {
              return cic.challengeId === ci.challengeId;
            });
            return ciCopy && ci.position === ciCopy.position && ci.disabledLanguages.sort().join() === ciCopy.disabledLanguages.sort().join();
          });
        },


        /**
         * Returns the solutions attached to this assessment result that have a corresponding
         * valid challenge item. This handles the case where the challenge items snapshot was reset
         * and we now have solutions pointing to an older assessment result
         * @returns {Array.<Solution>}
         */
        validSolutions: function validSolutions() {
          var challengeItems = this.getChallengeItems();
          return (this.solutions || []).filter(function (sol) {
            return challengeItems.some(function (ci) {
              return ci.challengeId === sol.challengeId;
            });
          });
        },

        trackProperties: function trackProperties() {
          return {
            candidate: this.teamCandidate ? this.teamCandidate.name : undefined,
            candidateId: this.candidateId,
            assessment: this.assessment ? this.assessment.title : undefined,
            assessmentId: this.assessmentId,
            teamCandidateId: this.teamCandidateId,
            teamId: this.teamId
          };
        },

        hasPostAssessmentActions: function hasPostAssessmentActions() {
          return !!this.submissionMessage || this.hasPostAssessmentRedirect();
        },
        hasPostAssessmentRedirect: function hasPostAssessmentRedirect() {
          return !!(this.$redirectUrl || this.nextAssessmentId || this.retrySchedule);
        },
        candidateLink: function candidateLink() {
          if (this.invitation) {
            return this.invitation.inviteLink();
          } else if (this.assessment) {
            return this.assessment.assessUrl();
          }
        },
        reviewerLink: function reviewerLink() {
          return absoluteUrl($state.href('liveInterview', { assessmentResultId: this.id }));
        },
        reopenedByName: function reopenedByName() {
          if (this.reopenedBy) {
            return this.reopenedBy.name;
          } else if (this.teamCandidate && this.teamCandidate.candidateId === this.reopenedById) {
            return this.teamCandidate.nameOrEmail || 'Candidate';
          } else {
            return '(Missing Contact Info)';
          }
        }
      },

      relations: {
        hasOne: {
          Team: {
            localField: 'team',
            localKey: 'teamId'
          },
          AssessmentInvitation: {
            localField: 'invitation',
            localKey: 'assessmentResultId',
            get: function get$$1(AssessmentResult, relationDef, instance) {
              return instance.invitations.sort(function (a, b) {
                return compareDate(a.createdAt, b.createdAt);
              })[0];
            }
          }
        },
        hasMany: {
          Solution: {
            localField: 'solutions',
            foreignKey: 'assessmentResultId'
          },
          ChallengeItem: {
            localField: 'challengeItems',
            link: false
          },
          TeamMember: {
            localField: 'interviewers',
            localKeys: 'interviewerIds'
          },
          AssessmentResultReview: {
            localField: 'assessmentResultReviews',
            foreignKey: 'assessmentResultId'
          },
          SolutionReview: {
            localField: 'solutionReviews',
            foreignKey: 'assessmentResultId'
          },
          AssessmentInvitation: {
            localField: 'invitations',
            foreignKey: 'assessmentResultId'
          }
        },
        belongsTo: {
          Assessment: {
            localField: 'assessment',
            localKey: 'assessmentId'
          },
          Candidate: {
            localField: 'candidate',
            localKey: 'candidateId'
          },
          TeamCandidate: {
            localField: 'teamCandidate',
            localKey: 'teamCandidateId'
          },
          TeamMember: [{
            localField: 'reopenedBy',
            localKey: 'reopenedById'
          }, {
            localField: 'accessUnlockedBy',
            localKey: 'accessUnlockedById'
          }]
        }
      },

      meta: {
        fetchOnPush: function fetchOnPush(data) {
          // only fetch result if we have the team candidate or candidate
          return DS.get('TeamCandidate', data.teamCandidateId || '') || DS.get('Candidate', data.candidateId || '');
        },
        includeOnPull: ['signals_profile'],
        saveExcludes: ['assessment', 'team', 'solutions', 'candidate', 'teamCandidate', 'challengeItems'],
        actionEndpoints: {
          open: { method: 'PUT', track: true },
          unlockAccess: { method: 'PUT', track: true },
          start: { method: 'PUT', track: true },
          submit: { method: 'PUT', track: true },
          close: { method: 'PUT', track: true },
          terminate: { method: 'PUT', track: true },
          scheduleRetry: { method: 'PUT', track: true },
          cancelRetry: { method: 'PUT', track: true },
          restoreSubmit: { method: 'PUT', track: true },
          reset: { method: 'PUT', track: true },
          joinedInterview: { method: 'PUT', track: true },
          leftInterview: { method: 'PUT', track: true }
        },
        track: {
          create: true
        }
      }
    });

    return AssessmentResult;
  }]);

  var assessmentResultReviewModelModule = angular.module('Q.models.assessment-result-review', [modelHelperModule]).factory('AssessmentResultReview', ['DS', 'ModelHelper', '$injector', function (DS, ModelHelper, $injector) {
    var currentUser = getUser.bind(null, $injector);

    var AssessmentResultReview = ModelHelper.defineResource({
      name: 'AssessmentResultReview',
      endpoint: 'assessment_result_reviews',

      methods: {
        isMyReview: function isMyReview() {
          return this.reviewedBy === currentUser();
        },
        reviewerName: function reviewerName() {
          var staffName = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'Qualified Staff';

          return this.reviewedBy && this.reviewedBy.nameOrEmail || (this.reviewedByStaff ? staffName : '(Unknown Reviewer)');
        },
        isSubmitted: function isSubmitted() {
          return this.state === 'submitted';
        },
        shouldShow: function shouldShow() {
          return this.isMyReview() || !this.reviewedByStaff || this.isSubmitted();
        }
      },
      staticMethods: {
        // starts or retrieves an assessment-result-review for the given assessment result
        start: function start(assessmentResult, teamMember) {
          var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

          var tm = teamMember || currentUser();
          if (!tm || tm.$type !== 'TeamMember') {
            throw new Error('Unable to create assessment result review without a team member');
          }
          var attr = {
            assessmentResultId: assessmentResult.id,
            reviewedById: tm.id
          };
          return AssessmentResultReview.fetchOrCreateWhere({ where: attr }, attr, opts);
        },
        getFor: function getFor(assessmentResult, teamMember) {
          var tm = teamMember || currentUser();
          return tm && AssessmentResultReview.filter({
            assessmentResultId: assessmentResult.id,
            reviewedById: tm.id
          })[0];
        },
        reviewSorter: function reviewSorter(a, b) {
          var user = currentUser();
          if (a.reviewedBy === user) {
            return -1;
          } else if (b.reviewedBy === user) {
            return 1;
          } else if (a.reviewedByStaff !== b.reviewedByStaff) {
            // puts staff reviews at the bottom
            return a.reviewedByStaff - b.reviewedByStaff;
          } else if (a.reviewedBy && b.reviewedBy) {
            return compareIgnoreCase(a.reviewerName(), b.reviewerName());
          }
          return 0;
        }
      },

      relations: {
        hasOne: {
          TeamMember: {
            localField: 'reviewedBy',
            localKey: 'reviewedById'
          }
        },
        hasMany: {
          SolutionReview: {
            localField: 'solutionReviews',
            foreignKey: 'assessmentResultReviewId'
          }
        },
        belongsTo: {
          Assessment: {
            localField: 'assessment',
            localKey: 'assessmentId'
          },
          AssessmentResult: {
            localField: 'assessmentResult',
            localKey: 'assessmentResultId'
          },
          TeamCandidate: {
            localField: 'teamCandidate',
            localKey: 'teamCandidateId'
          }
        }
      },

      meta: {
        fetchOnPush: function fetchOnPush(data) {
          // only store the review if we have the assessment result
          return DS.get('AssessmentResult', data.assessmentResultId || '');
        },
        track: {
          create: true
        }
      }
    });

    return AssessmentResultReview;
  }]);

  var assessmentTemplateModelModule = angular.module('Q.models.assessment-template', [modelHelperModule, assessmentModelModule]).factory('AssessmentTemplate', ['ModelHelper', 'Assessment', '$injector', function defineResource(ModelHelper, Assessment, $injector) {
    return ModelHelper.defineResource({
      name: 'AssessmentTemplate',
      endpoint: 'assessment_templates',
      computed: Assessment.computed,
      methods: angular.extend({}, Assessment.methods, {
        // returns true if some sort of upgrade is required. Pass 'full' if you wish to check
        // if a full upgrade is required, 'partial' if you wish to check if a partial,
        // otherwise either type will be considered
        upgradeRequired: function upgradeRequired(checkType, allowStaff) {
          if (allowStaff) {
            // look up permissions service here to make sure it happens after the app has been bootstrapped
            var permissionsService = $injector.has('permissionsService') && $injector.get('permissionsService');
            if (permissionsService && permissionsService.role('staff')) {
              return false;
            }
          }

          if (this._upgradeRequired === undefined) {
            var needs = this.upgradeRequiredItems().length;
            if (needs === this.challengeItems.length) {
              // 2 == full upgrade required
              this._upgradeRequired = 'full';
            } else {
              this._upgradeRequired = needs > 0 ? 'partial' : false;
            }
          }

          return checkType ? this._upgradeRequired === checkType : !!this._upgradeRequired;
        },
        upgradeRequiredItems: function upgradeRequiredItems() {
          return this.challengeItems.filter(function (item) {
            return item.challenge.requiresUpgrade || item.challenge.teamRequiresUpgrade;
          });
        },
        quizOnly: function quizOnly() {
          return this.challengeItems.every(function (ci) {
            return ci.challenge.isQuiz();
          });
        },
        challengesWithLanguages: function challengesWithLanguages() {
          return this.challengeItems.filter(function (ci) {
            return ci.challenge.languages && ci.challenge.languages.length;
          });
        },
        leastAvailableLanguages: function leastAvailableLanguages() {
          // find the challenge item with the least number of languages, or return an empty array
          return this.challengesWithLanguages().map(function (ci) {
            return ci.challenge.languages;
          }).reduce(function (prev, next) {
            return !prev || next.length < prev.length ? next : prev;
          }, null) || [];
        },
        defaultEnabledLanguages: function defaultEnabledLanguages() {
          var challengeItems = this.challengesWithLanguages();
          // return the languages enabled in all challlenges
          return this.leastAvailableLanguages().filter(function (language) {
            var name = language.name;
            return challengeItems.every(function (ci) {
              var languages = ci.challenge.languages.map(function (l) {
                return l.name;
              });
              var disabledLanguages = ci.disabledLanguages;
              return languages.indexOf(name) > -1 && disabledLanguages.indexOf(name) === -1;
            });
          }).map(function (l) {
            return l.name;
          });
        },
        availableLanguages: function availableLanguages() {
          var challengeItems = this.challengesWithLanguages();
          // return the languages that are in all the challenges
          return this.leastAvailableLanguages().filter(function (language) {
            var name = language.name;
            return challengeItems.every(function (ci) {
              var languages = ci.challenge.languages.map(function (l) {
                return l.name;
              });
              return languages.indexOf(name) > -1;
            });
          }).map(function (l) {
            return l.name;
          });
        }
      }),

      relations: {
        hasMany: {
          ChallengeItem: {
            localField: 'challengeItems',
            link: false
          }
        }
      },

      meta: {
        actionEndpoints: {
          import: {
            method: 'PUT',
            responseHandler: function responseHandler(response) {
              var data = ModelHelper.deserialize(Assessment, response);
              return Assessment.inject(data);
            }
          }
        }
      }
    });
  }]);

  var PRIVILEGE_MANAGE_CHALLENGES = 'manageChallenges';
  var PRIVILEGE_MANAGE_ASSESSMENTS = 'manageAssessments';
  var PRIVILEGE_DECIDE_ON_CANDIDATES = 'decideOnCandidates';

  var ALL_PRIVILEGES = [{
    id: PRIVILEGE_MANAGE_ASSESSMENTS,
    name: 'Manage Assessments',
    shortName: 'Manage',
    info: 'Ability to create, modify, and delete assessments',
    icon: 'tasks',
    icon2: 'pencil',
    category: 'Assessments',
    toggle: false
  }, {
    id: PRIVILEGE_MANAGE_CHALLENGES,
    name: 'Manage Challenges',
    shortName: 'Manage',
    info: 'Ability to create, modify, and delete challenges',
    icon: 'code',
    icon2: 'pencil',
    category: 'Challenges',
    toggle: false
  }, {
    id: PRIVILEGE_DECIDE_ON_CANDIDATES,
    name: 'Decide On Candidates',
    shortName: 'Decide',
    info: 'Ability to decide whether candidates are approved or rejected',
    icon: 'thumbs-up',
    icon2: 'pencil',
    category: 'Candidates',
    toggle: true
  }];

  // enables hash access
  ALL_PRIVILEGES.forEach(function (p) {
    ALL_PRIVILEGES[p.id] = p;
  });

  var PRIVILEGE_WITH_VALUE = /^(\w+):(\w+)$/;

  /**
   * Checks to see if the given model has a privilege.
   *
   * @param {*} model - Model to check. Will look at model.privileges if present
   * @param {string} privilege - Name of privilege. Can also be in the form of "name:value" to check for a specific value
   * @param {boolean|string?} value - If not provided, just check to see if the privilege is truthy and not 'none'. Otherwise, check to see if the privilege matches the value exactly
   * @returns {boolean}
   */
  function checkPrivilege(model, privilege, value) {
    if (PRIVILEGE_WITH_VALUE.test(privilege)) {
      var _privilege$match = privilege.match(PRIVILEGE_WITH_VALUE);

      var _privilege$match2 = slicedToArray(_privilege$match, 3);

      privilege = _privilege$match2[1];
      value = _privilege$match2[2];
    }

    var privileges = model.privileges || model || {};

    if (value === undefined) {
      return !!privileges[privilege] && privileges[privilege] !== 'none';
    } else {
      return privileges[privilege] === value;
    }
  }

  var candidateModelModule = angular.module('Q.models.candidate', [modelHelperModule]).factory('Candidate', ['ModelHelper', function (ModelHelper) {
    return ModelHelper.defineResource({
      name: 'Candidate',
      endpoint: 'candidates',

      serialize: ModelHelper.serializeWithUiPrefs,
      deserialize: ModelHelper.deserializeWithUiPrefs,

      relations: {
        hasMany: {
          Assessment: {
            localField: 'assessments'
          },
          AssessmentResult: {
            localField: 'assessmentResults',
            foreignKey: 'candidateId'
          },
          Solution: {
            localField: 'solutions',
            foreignKey: 'candidateId'
          },
          TeamCandidate: {
            localField: 'teamCandidates',
            foreignKey: 'candidateId'
          }
        }
      },

      methods: {
        loginType: 'candidate',
        hasRole: function hasRole() /* role */{
          return true;
        },
        hasFeature: function hasFeature(feature) {
          return !!(this.features && this.features[feature]);
        },
        hasPrivilege: function hasPrivilege(privilege, value) {
          return checkPrivilege(this, privilege, value);
        },
        isTeamMember: function isTeamMember() {
          var teamId = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

          var teamIds = this.teamIds;
          return teamIds && teamIds.length > 0 && (!teamId || teamIds.indexOf(teamId) > -1);
        },
        isActive: function isActive() {
          return this.state === 'active';
        }
      },

      meta: {
        saveExcludes: ['assessments', 'assessmentResults', 'solutions'],
        actionEndpoints: {
          forget: { method: 'POST', track: true }
        }
      }
    });
  }]);

  var challengeItemModelModule = angular.module('Q.models.challenge-item', [modelHelperModule]).factory('ChallengeItem', ['DS', 'ModelHelper', '$q', '$injector', function (DS, ModelHelper, $q, $injector) {
    return ModelHelper.defineResource({
      name: 'ChallengeItem',
      endpoint: 'challenge_items',

      cleanAfterDestroy: {
        parent: 'Assessment',
        collection: 'challengeItems'
      },

      methods: {
        getChallenge: function getChallenge() {
          var promise = void 0;

          if (this.challenge) {
            promise = $q.when(this.challenge);
          } else if (this.challengeId && isLoggedIn($injector)) {
            promise = DS.definitions.Challenge.fetch(this.challengeId, {
              params: { includes: ['sourced_from'] }
            });
          } else {
            promise = $q.when(null);
          }
          return promise.then(function (c) {
            return c && c.loadSourcedFrom();
          }).catch(function () {
            return null;
          });
        },
        availableLanguages: function availableLanguages() {
          var _this = this;

          // get a list of all available challenge languages that aren't filtered out
          return this.challenge.getLanguages().filter(function (lang) {
            return !_this.disabledLanguages.includes(lang.id);
          });
        }
      },

      staticMethods: {
        /**
         * Loads all missing challenges in a given array of challengeItems
         *
         * @param {Array} challengeItems
         * @returns {Promise} promise upon completion with the challengeItems
         */
        loadMissingChallenges: function loadMissingChallenges(challengeItems) {
          // don't look up challenges on reports
          if (appConfig.appId === 'reports') {
            return $q.resolve(challengeItems);
          }

          var challengeIds = [];
          var missingIdsMap = {};
          var Challenge = DS.definitions.Challenge;
          challengeItems.forEach(function (ci) {
            if (!ci.challenge && ci.challengeId) {
              var c = Challenge.get(ci.challengeId);
              if (!c) {
                if (!missingIdsMap[ci.challengeId]) {
                  challengeIds.push(ci.challengeId);
                  missingIdsMap[ci.challengeId] = [ci];
                } else {
                  missingIdsMap[ci.challengeId].push(ci);
                }
              } else {
                // grab sourced from in the same load if we need it
                if (!c.sourcedFrom && c.sourcedFromId) {
                  challengeIds.push(c.sourcedFromId);
                }
              }
            }
          });

          var promise = void 0;

          if (challengeIds.length) {
            promise = DS.definitions.Challenge.findAll({
              where: { id: { in: challengeIds } },
              includes: ['sourced_from']
            }).then(function (challenges) {
              challenges.forEach(function (c) {
                missingIdsMap[c.id].forEach(function (ci) {
                  return ci.challenge = c;
                });
              });
              return challengeItems;
            });
          } else {
            promise = $q.resolve(challengeItems);
          }

          return promise
          // load missing copied from (for stats)
          .then(function (cis) {
            return Challenge.loadSourcedFrom(cis.map(function (ci) {
              return ci.challenge;
            }).filter(angular.identity));
          }).then(function () {
            return challengeItems;
          });
        }
      },

      relations: {
        hasOne: {
          Challenge: {
            localField: 'challenge',
            localKey: 'challengeId'
          }
        }
      },

      meta: {
        saveExcludes: ['challenge']
      }
    });
  }]);

  /* eslint-disable camelcase */

  // the extension stuff lives here otherwise we cause circular dependencies :-\
  var EXT_MATCHER = /\.(\w+)$/;

  function getExtension(filename) {
    return EXT_MATCHER.test(filename) ? filename.match(EXT_MATCHER)[1].toLowerCase() : '';
  }

  var frameworks = {
    'clojure.test': 'Clojure clojure.test',
    'cw-2': 'Qualified Testing Framework',
    busted: 'Busted',
    criterion: 'Criterion',
    cw: 'Qualified Testing Framework',
    eunit: 'eunit',
    ex_unit: 'ExUnit',
    fuchu: 'Fuchu',
    ginkgo: 'Ginkgo',
    gtest: 'Google Test',
    hspec: 'hspec Testing Framework',
    igloo: 'Igloo BDD',
    jest: 'Jest',
    junit5: 'JUnit 5',
    junit: 'JUnit',
    karma_bdd: 'Karma + Mocha BDD',
    karma_tdd: 'Karma + Mocha TDD',
    kotlintest: 'KotlinTest',
    mocha_bdd: 'Mocha BDD',
    mocha_tdd: 'Mocha TDD',
    mocha: 'Mocha',
    nunit: 'NUnit',
    ounit: 'OUnit',
    pester: 'Pester',
    phpunit: 'PHPUnit',
    rspec: 'RSpec',
    rust: 'Rust',
    scalatest: 'ScalaTest',
    spek: 'Spek',
    spock: 'Spock',
    test: 'Dart Test',
    testthat: 'testthat',
    truffle_with_mocha: 'Truffle + Mocha BDD',
    unitkit: 'UnitKit',
    unittest: 'Python unittest',
    xctest: 'XCTest'
  };

  var COMMENT_C_LIKE = ['//', '/*', '*/'];
  var COMMENT_SHELL_LIKE = ['#'];
  var COMMENT_RUBY_LIKE = ['#', '\n=begin', '\n=end'];
  var COMMENT_HTML = ['', '<!--', '-->'];

  /**
   * List of languages, with the name or id as the key for each language.
   *
   * Languages can have the following properties:
   *
   * - n: Name (if the name does not make for a good key [C#, F#])
   * - x: extension or list of extensions, defaults to lowercase id if not provided
   * - m: codemirror type or mimetype, defaults to lowercase id if not provided, set to false to exclude
   * - c: set to false if not a Challenge language,
   * - f: framework or frameworks
   * - p: true if a primary language
   * - h: highlight language, if not the id
   * - cm: comments mode (see constants above)
   * - ac: enable autocomplete
   * - filenames: Special filenames that map back onto this mode (e.x., files without extensions)
   * - l: Language Id override for Language Server if necessary.
   * - ls: Language Servers.
   *
   * Note: this is all compacted like this to make it take up less space in the final form
   *
   * Given the most basic form, such as Java:{}, The resulting object will look like:
   *
   * {
   *   id: 'java',
   *   name: 'Java',
   *   ext: ['java'],
   *   codemirror: 'java'
   *   challenge: true,
   *   highlight: 'java',
   *   primary: false,
   *   cm: ['//', '/*', '*\/'],
   * }
   *
   * The language will be reinserted into the hash based on it's lowercase name, id, and all extensions,
   * on a first-come, first-serve basis.
   *
   * @type {{[NameOrId]: {n: string, x: string[], m:string}}}
   */
  var languages = {
    C: {
      x: ['c', 'h'],
      m: 'text/x-csrc',
      f: ['criterion']
    },
    Clojure: {
      x: ['clj', 'cljs'],
      f: ['clojure.test'],
      C: [';']
    },
    CoffeeScript: {
      x: 'coffee',
      f: ['cw-2'],
      cm: ['#', '###', '###']
    },
    cpp: {
      n: 'C++',
      x: ['cc', 'cpp', 'hpp'],
      m: 'text/x-c++src',
      f: ['gtest', 'igloo'],
      p: true
    },
    CSharp: {
      n: 'C#',
      x: 'cs',
      m: 'text/x-csharp',
      f: ['nunit'],
      p: true,
      ls: ['omnisharp-roslyn']
    },
    css: {
      n: 'CSS3',
      m: 'text/css',
      c: false,
      ac: true,
      ls: ['css-language-server']
    },
    Dart: {
      x: 'dart',
      f: ['test']
    },
    Elixir: {
      x: ['ex', 'exs'],
      f: ['ex_unit'],
      m: 'ruby',
      cm: COMMENT_RUBY_LIKE
    },
    Erb: {
      x: 'erb',
      m: 'application/x-erb',
      c: false
    },
    Erlang: {
      x: 'erl',
      f: ['eunit'],
      m: 'text/x-erlang',
      cm: ['%']
    },
    FSharp: {
      n: 'F#',
      x: 'fs',
      m: 'text/x-fsharp',
      f: ['nunit', 'fuchu'],
      cm: ['//', '(*', '*)']
    },
    Go: {
      m: 'text/x-go',
      f: ['ginkgo']
    },
    Groovy: {
      x: ['groovy'],
      f: ['junit5', 'junit', 'spock'],
      m: 'text/x-groovy'
    },
    Haskell: {
      x: ['hs', 'lhs'],
      f: ['hspec'],
      cm: ['--']
    },
    HTML: {
      x: ['htm', 'html'],
      m: 'text/html',
      c: false,
      cm: COMMENT_HTML,
      ac: true,
      ls: ['html-language-server']
    },
    Java: {
      m: 'text/x-java',
      f: ['junit5', 'junit'],
      p: true,
      ls: ['jdt-language-server']
    },
    JavaScript: {
      x: ['js', 'json'],
      f: ['jest', 'mocha_bdd', 'mocha_tdd', 'karma_bdd', 'karma_tdd', 'cw-2'],
      m: 'text/jsx',
      p: true,
      ls: ['typescript-language-server', 'vue-language-server']
    },
    JSP: {
      x: 'jsp',
      m: 'application/x-jsp',
      ac: true,
      c: false
    },
    JSX: {
      m: 'text/jsx',
      c: false,
      ac: true,
      h: 'javascript',
      l: 'javascriptreact',
      ls: ['typescript-language-server']
    },
    Kotlin: {
      x: ['kt', 'kts'],
      f: ['junit5', 'junit', 'spek', 'kotlintest'],
      m: 'text/x-kotlin'
    },
    Less: {
      m: 'text/x-less',
      n: 'LessCSS',
      ac: true,
      c: false,
      ls: ['css-language-server']
    },
    Lua: {
      x: ['lua'],
      f: ['busted'],
      m: 'text/x-lua'
    },
    Markdown: {
      x: ['md', 'mark', 'markdown'],
      m: 'better-markdown',
      c: false,
      cm: COMMENT_HTML
    },
    ObjC: {
      n: 'Objective-C',
      x: ['m', 'mm'],
      m: 'text/x-objectivec',
      f: ['unitkit', 'cw']
    },
    OCaml: {
      m: 'text/x-ocaml',
      x: 'ml',
      f: ['ounit'],
      cm: ['', '(*', '*)']
    },
    PHP: {
      x: ['php', 'php3'],
      f: ['phpunit'],
      m: 'text/x-php',
      p: true
    },
    PowerShell: {
      x: ['ps1', 'psm1'],
      f: ['pester'],
      m: 'application/x-powershell',
      cm: COMMENT_SHELL_LIKE
    },
    Python: {
      x: 'py',
      f: ['unittest', 'cw-2'],
      p: true,
      cm: COMMENT_SHELL_LIKE,
      ls: ['pyright-langserver']
    },
    R: {
      x: 'r',
      f: ['testthat'],
      m: 'text/x-rsrc',
      cm: COMMENT_SHELL_LIKE
    },
    Ruby: {
      x: 'rb',
      f: ['rspec', 'cw-2'],
      p: true,
      cm: COMMENT_RUBY_LIKE,
      filenames: ['Gemfile'],
      ls: ['solargraph']
    },
    Rust: {
      x: 'rs',
      f: ['rust'],
      m: 'text/x-rustsrc'
    },
    Sass: {
      m: 'text/x-sass',
      n: 'Sass (SASS flavor)',
      ac: true,
      c: false,
      ls: ['css-language-server']
    },
    Scss: {
      m: 'text/x-scss',
      n: 'Sass (SCSS Flavor)',
      ac: true,
      c: false,
      ls: ['css-language-server']
    },
    Scala: {
      x: 'scala',
      f: ['junit', 'scalatest'],
      m: 'text/x-scala'
    },
    Shell: {
      m: 'text/x-sh',
      f: ['rspec'],
      fl: ['ruby'],
      x: 'sh',
      h: 'bash',
      cm: COMMENT_SHELL_LIKE
    },
    SQL: {
      f: ['rspec'],
      fl: ['ruby'],
      // had to pick one, chose x-pgsql for now
      m: 'text/x-pgsql',
      cm: ['--', '/*', '*/'],
      ac: true,
      p: true
    },
    Solidity: {
      x: 'sol',
      // Need to duplicate because `language.frameworkLanguage` uses the framework's index.
      fl: ['javascript', 'javascript'],
      f: ['mocha', 'truffle_with_mocha']
    },
    SVG: {
      m: 'text/xml',
      c: false,
      cm: COMMENT_HTML
    },
    Swift: {
      f: ['xctest']
    },
    Text: {
      x: ['txt'],
      m: 'text/plain',
      c: false,
      filenames: ['Dockerfile']
    },
    TSX: {
      m: 'text/typescript-jsx',
      c: false,
      h: 'typescript',
      l: 'typscriptreact',
      ls: ['typescript-language-server']
    },
    TypeScript: {
      x: 'ts',
      f: ['jest', 'mocha_bdd', 'mocha_tdd', 'karma_bdd', 'karma_tdd'],
      m: 'text/typescript',
      p: true,
      ls: ['typescript-language-server']
    },
    Vue: {
      x: 'vue',
      c: false,
      ac: true,
      m: 'text/x-vue',
      ls: ['vue-language-server']
    },
    XML: {
      x: ['xslt'],
      m: 'text/xml',
      c: false,
      cm: COMMENT_HTML
    },
    Yaml: {
      x: ['yml', 'yaml'],
      m: 'text/x-yaml',
      c: false,
      cm: COMMENT_SHELL_LIKE
    }
  };
  var fileMap = {};

  var all = [];
  var known = [];
  var allExt = [];
  var allNames = [];

  angular.forEach(languages, function (lang, id) {
    function add(key) {
      if (!languages[key]) languages[key] = lang;
    }

    var idlc = id.toLowerCase();
    lang.id = idlc;
    lang.name = lang.namePlain = lang.n || id;
    lang.ext = lang.x || idlc;
    lang.codemirror = lang.mime = lang.m === undefined ? idlc : lang.m;
    lang.challenge = lang.c !== false;
    lang.highlight = lang.h || idlc;
    if (lang.f) {
      lang.frameworks = lang.f;
      // enables frameworks to use a different language
      lang.frameworkLanguages = lang.fl || lang.f.map(function () {
        return idlc;
      });
    }
    lang.primary = !!lang.p;
    lang.cm = lang.cm || COMMENT_C_LIKE;
    lang.languageId = lang.l || lang.id;
    lang.languageServerIds = lang.ls || [];

    add(idlc);
    add(lang.name);
    add(lang.name.toLowerCase());

    if (!angular.isArray(lang.ext)) lang.ext = [lang.ext];
    allExt.push.apply(allExt, toConsumableArray(lang.ext));
    if (lang.filenames) {
      lang.filenames.forEach(add);
      allNames.push.apply(allNames, toConsumableArray(lang.filenames));
    }
    angular.forEach(lang.ext, add);

    known.push(lang);
    if (lang.challenge) {
      all.push(lang);
    }
  });

  // Potential sorting by primary / secondary, but then we need to deal with
  // visually separating the results
  function langCompare(a, b) {
    if (a === b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    if (a.primary && !b.primary) return -1;
    if (b.primary && !a.primary) return 1;
    return compareIgnoreCase(a.name || a, b.name || b);
  }

  // attempts to find a valid language based on several variations of the input
  function getLang(l) {
    return l && (languages[l] || // exact string match (l = 'js')
    languages[l.id] || // id match (l = {id:'js'})
    languages[l.name] || // name match (l = {name:'js'})
    languages[fileMap[l]] || // fileMapped by user
    languages[fileMap['*.' + getExtension(l)]]) // file extension mapped by user
    ;
  }

  languages.primaryCompare = function (a, b) {
    return langCompare(getLang(a), getLang(b));
  };

  languages.compare = function (a, b) {
    if (a.id) {
      return compareIgnoreCase(a.name, b.name);
    } else if (a.name) {
      return compareIgnoreCase(languages[a.name].name, languages[b.name].name);
    } else {
      return compareIgnoreCase(a, b);
    }
  };

  languages.frameworkLanguage = function (lang, framework) {
    var l = getLang(lang);
    if (!l) {
      return lang && lang.id || lang;
    }
    var fwidx = l.frameworks.indexOf(framework);
    if (fwidx < 0) fwidx = 0;
    return l.frameworkLanguages[fwidx] || l.id;
  };

  languages.stripComments = function (l, code) {
    code = code || '';
    var lang = getLang(l);
    var cm = lang && lang.cm || [];
    if (cm[0]) {
      // single line comments
      code = code.replace(new RegExp(escapeRegex(cm[0]) + '.*$', 'gm'), '');
    }
    if (cm[1]) {
      // multi line comments
      code = code.replace(new RegExp(escapeRegex(cm[1]) + '[\\s\\S]+?' + escapeRegex(cm[2]), 'g'), '');
    }
    // remove whitespace
    return code.trim();
  };

  languages.getLang = getLang;
  languages.toName = function (l) {
    return languages[l] ? languages[l].name : l;
  };

  all.sort(languages.compare);
  languages.all = all;
  known.sort(languages.compare);
  languages.known = known;
  languages.primary = all.slice().sort(languages.primaryCompare);
  languages.frameworks = frameworks;
  var VALID_EXT_REGEX = void 0;
  languages.validExt = function (name) {
    if (!VALID_EXT_REGEX) {
      VALID_EXT_REGEX = new RegExp('(^' + allNames.join('|') + ')|(.(' + allExt.join('|') + '))$');
    }
    return VALID_EXT_REGEX.test(name);
  };
  languages.langFromFilename = function (filename) {
    var ext = getExtension(filename);
    var lang = ext && getLang(ext);
    if (!lang && filename) {
      // check to match full-name files
      filename = filename.replace(/^(.*\/)/g, '');
      lang = allNames.includes(filename) && getLang(filename);
    }
    return lang;
  };

  window.$languages = languages;

  var _BASE_TYPES, _DEFAULT_OPTS;

  var TEXT_QUESTION = 'QuizTextQuestion';
  var WEIGHTED_CHOICE_QUESTION = 'QuizWeightedChoiceQuestion';

  var BASE_TYPES = (_BASE_TYPES = {}, defineProperty(_BASE_TYPES, TEXT_QUESTION, 'text'), defineProperty(_BASE_TYPES, WEIGHTED_CHOICE_QUESTION, 'multiple-choice'), _BASE_TYPES);

  // used to configure default options before injection
  var DEFAULT_OPTS = (_DEFAULT_OPTS = {}, defineProperty(_DEFAULT_OPTS, TEXT_QUESTION, {
    subtype: 'short',
    language: 'text',
    lengthValidation: {
      enabled: false,
      expectedLength: 0
    }
  }), defineProperty(_DEFAULT_OPTS, WEIGHTED_CHOICE_QUESTION, {
    shuffle: false,
    automax: true,
    autosum: true
  }), _DEFAULT_OPTS);

  var TEXT_SUBTYPES_ORDERED = [{ id: 'short', label: 'Short Answer' }, { id: 'lplain', label: 'Long Answer (Plain Text)' }, { id: 'long', label: 'Long Answer (Markdown-Enabled)' }, { id: 'essayplain', label: 'Essay (Plain Text)' }, { id: 'essay', label: 'Essay (Markdown-Enabled)' }, { id: 'scode', label: 'Code (Short)' }, { id: 'lcode', label: 'Code (Multiline)' }];

  var TEXT_SUBTYPES = {};
  TEXT_SUBTYPES_ORDERED.forEach(function (t) {
    TEXT_SUBTYPES[t.id] = t.label;
  });

  var MC_SUBTYPES = {
    mcma: 'Multiple Choice (Multiple Answer)',
    mcsa: 'Multiple Choice (Single Answer)'
  };

  var INPUT_TYPE_MAP = {
    short: 'text',
    lplain: 'text',
    long: 'markdown',
    essayplain: 'text',
    essay: 'markdown',
    scode: 'code',
    lcode: 'code',
    mcma: 'checkbox',
    mcsa: 'radio'
  };

  var MATCHER_LABELS = {
    includes: 'Includes Text',
    includesIncorrect: 'Should Include Text',
    notincludes: 'Excludes Text',
    notincludesIncorrect: 'Should Exclude Text',
    exact: 'Equals Exactly',
    exactIncorrect: 'Should Equal',
    notexact: 'Does Not Equal',
    notexactIncorrect: 'Should Not Equal',
    regex: 'Matches Pattern',
    regexIncorrect: 'Should Match Pattern',
    notregex: 'Does Not Match Pattern',
    notregexIncorrect: 'Should Not Match Pattern'
  };

  function cleanUpQuizQuestion(instance) {
    if (instance.$type === 'QuizMultipleChoiceQuestion' && instance.choices && angular.isString(instance.choices[0])) {
      // convert to new weighted format
      // if this question is saved, it'll get replaced at that time, otherwise we'll just pretend it's been updated locally
      instance.$type = WEIGHTED_CHOICE_QUESTION;
      instance.choices = instance.choices.map(function (c) {
        return {
          label: c,
          value: 0 // TODO: determine default non-answer value?
        };
      });
      instance.maxSum = 0;
      if (instance.answers) {
        instance.answers.forEach(function (a) {
          return instance.choices[+a].value = 1;
        });
        instance.maxSum = instance.answers.length;
        delete instance.answers;
      }
    }

    // handle case swaps in options, because the API doesn't
    var opts = instance.options = instance.options || {};
    Object.keys(opts).forEach(function (k) {
      if (/_/.test(k)) {
        opts[camelCase(k)] = opts[k];
        delete opts[k];
      }
    });

    angular.forEach(DEFAULT_OPTS[instance.$type] || {}, function (v, k) {
      if (opts[k] === undefined) {
        opts[k] = angular.copy(v);
      }
    });

    if (instance.$type === WEIGHTED_CHOICE_QUESTION) {
      instance.weightedScoring = instance.weightedScoring || false;
    }

    return instance;
  }

  var quizQuestionModelModule = angular.module('Q.models.quiz-question', [modelHelperModule]).factory('QuizQuestion', ['ModelHelper', '$filter', '$injector', function (ModelHelper, $filter, $injector) {
    var QuizQuestion = ModelHelper.defineResource({
      name: 'QuizQuestion',
      endpoint: 'quiz_questions',

      cleanAfterDestroy: {
        parent: 'Challenge',
        collection: 'questions'
      },

      // Convert API-style answers into something we can work on locally
      beforeInject: function QuizQuestion$beforeInject(resource, instances) {
        if (!Array.isArray(instances)) {
          instances = [instances];
        }
        instances.forEach(cleanUpQuizQuestion);
      },
      // Convert local formatted answers into API-style
      serialize: function serialize(resource, data) {
        // TODO: always include the $type!
        QuizQuestion.syncChoices(data);
        return ModelHelper.serialize(resource, data);
      },


      methods: {
        baseType: function baseType() {
          return BASE_TYPES[this.$type];
        },
        isText: function isText() {
          return this.$type === TEXT_QUESTION;
        },
        isMultipleChoice: function isMultipleChoice() {
          return this.$type === WEIGHTED_CHOICE_QUESTION;
        },
        baseTypeLabel: function baseTypeLabel() {
          if (this.isText()) {
            return 'Free Text Question';
          } else if (this.isMultipleChoice()) {
            return 'Multiple Choice Question';
          }
          return 'Question';
        },
        answerType: function answerType() {
          if (this.isText()) {
            return this.options.subtype || 'short';
          } else if (this.isMultipleChoice()) {
            return this.maxSelections === 1 ? 'mcsa' : 'mcma';
          }
        },
        answerTypeLabel: function answerTypeLabel() {
          if (this.isCode()) {
            return this.languageLabel() + TEXT_SUBTYPES[this.answerType()];
          } else if (this.isText()) {
            return TEXT_SUBTYPES[this.answerType()];
          } else if (this.isMultipleChoice()) {
            return MC_SUBTYPES[this.answerType()];
          }
        },
        hasLanguage: function hasLanguage() {
          var lang = this.options.language;
          return !!lang && lang !== 'text' && !!languages[lang];
        },
        editorLanguage: function editorLanguage() {
          if (this.isCode()) {
            return this.options.language || 'text';
          } else {
            return this.isMultiline() ? 'markdown' : 'text';
          }
        },
        languageLabel: function languageLabel() {
          var lang = this.options.language || '';
          if (lang) {
            lang = languages.toName(lang) + ' ';
          }
          return lang;
        },
        inputType: function inputType() {
          return INPUT_TYPE_MAP[this.answerType()] || 'text';
        },
        isAnswerType: function isAnswerType() {
          for (var _len = arguments.length, types = Array(_len), _key = 0; _key < _len; _key++) {
            types[_key] = arguments[_key];
          }

          return types.includes(this.answerType());
        },
        isPlain: function isPlain() {
          return this.isAnswerType('short', 'lplain', 'essayplain');
        },
        isMarkdown: function isMarkdown() {
          return this.isAnswerType('long', 'essay');
        },
        isMultiline: function isMultiline() {
          return this.isEssay() || this.isAnswerType('long', 'lcode');
        },
        isEssay: function isEssay() {
          return this.isAnswerType('essay', 'essayplain');
        },
        isCode: function isCode() {
          return this.isAnswerType('scode', 'lcode');
        },
        isMultipleAnswer: function isMultipleAnswer() {
          return this.answerType() === 'mcma';
        },
        isAnswer: function isAnswer(index, solution) {
          if (solution && solution.values) {
            return solution.values.indexOf(index) > -1;
          } else {
            return this.choices[index].value > 0;
          }
        },
        isCorrect: function isCorrect(index, solution) {
          if (!solution) return false;
          return solution.score >= 100 || this.isAnswer(index) === this.isAnswer(index, solution);
        },
        maxAnswers: function maxAnswers() {
          var question = this;
          if (question.isMultipleChoice()) {
            return question.choices.filter(function (a) {
              return a.value > 0;
            }).length;
          } else if (question.isText()) {
            return (question.matchers || []).length;
          }
        },
        maxCorrectSum: function maxCorrectSum() {
          return this.choices.map(function (c) {
            return c.value;
          }).filter(function (v) {
            return v > 0;
          }).sort(function (a, b) {
            return b - a;
          }).slice(0, this.maxSelections || 1).reduce(function (a, b) {
            return a + b;
          }, 0);
        },
        maxWeight: function maxWeight() {
          return this.choices.map(function (c) {
            return c.value;
          }).filter(function (v) {
            return v > 0;
          }).sort(function (a, b) {
            return b - a;
          })[0] || 0;
        },
        shouldDisable: function shouldDisable(solutionAnswers, $index) {
          solutionAnswers = solutionAnswers || {};
          var remaining = Math.max(0, this.updateMaxSelections() - (solutionAnswers.values || []).length);
          var selected = (solutionAnswers.$mcmaValues || [])[$index];
          return !remaining && !selected;
        },
        updateMaxSelections: function updateMaxSelections(force) {
          var question = this;
          if (!question.isMultipleChoice()) return question.maxSelections;

          if (force || !question.maxSelections) {
            var totalAnswers = (question.choices || []).length;
            if (question.options.automax === 'all') {
              question.maxSelections = totalAnswers;
            } else if (question.options.automax) {
              question.maxSelections = question.maxAnswers();
            }
            // ensure we don't an invalid selection
            question.maxSelections = Math.max(1, Math.min(question.maxSelections, totalAnswers));
          }

          return question.maxSelections;
        },
        remainingSelectionsMessage: function remainingSelectionsMessage(solutionAnswers) {
          solutionAnswers = solutionAnswers || {};
          var maxSelections = this.updateMaxSelections();
          var remaining = Math.max(0, maxSelections - (solutionAnswers.values || []).length);
          var msg = 'Max allowed answers selected';
          if (maxSelections === this.choices.length) {
            msg = 'Choose all correct answers below';
          } else if (remaining === maxSelections) {
            msg = 'Choose up to <strong>' + remaining + '</strong> answers below';
          } else if (remaining > 0) {
            msg = 'Choose up to <strong>' + remaining + '</strong> more answer' + (remaining === 1 ? '' : 's');
          }
          return msg;
        },
        updateMaxSum: function updateMaxSum(force) {
          var question = this;
          if (question.isMultipleChoice() && (force || !question.maxSum)) {
            if (question.options.autosum === 'highest') {
              question.maxSum = question.maxWeight();
            } else if (question.options.autosum) {
              question.maxSum = question.maxCorrectSum();
            }
            // ensure we don't an invalid selection
            question.maxSum = Math.max(0, question.maxSum || 0);
          }
          return question.maxSum;
        },
        optionsSummary: function optionsSummary(challenge, answer) {
          var numFilter = $injector.has('fuzzyNumberFilter') ? $filter('fuzzyNumber') : $filter('number');
          var summary = [];
          var self = this;
          var weight = angular.isNumber(+self.weight) ? +self.weight : 1;
          var score = '';
          if (weight === 0) {
            score = 'Not scored';
          } else {
            if (answer) {
              if (answer.values.length) {
                // Calculate length score if not present

                if (self.options.lengthValidation && self.options.lengthValidation.enabled) {
                  var answerText = answer.values[0] || '';
                  answer.lengthScore = self.calculateLengthScore(answerText);
                }
                var lengthScore = answer.lengthScore || 0; // Already between 0-1
                var accuracyScore = (answer.accuracyScore || 0) / 100; // Convert from percentage to 0-1

                // Calculate final score based on whether length validation is enabled
                var finalScore = void 0;
                if (self.options.lengthValidation && self.options.lengthValidation.enabled) {
                  finalScore = (accuracyScore + lengthScore) / 2; // Average of both scores
                } else {
                  finalScore = accuracyScore;
                }

                if (self.options.lengthValidation && self.options.lengthValidation.enabled) {
                  score = numFilter(finalScore * weight) + ' out of ' + numFilter(weight) + ' point' + (weight === 1 ? '' : 's') + ' (Matchers: ' + numFilter(accuracyScore * 100) + '%, Length: ' + numFilter(lengthScore * 100) + '%)';
                } else {
                  score = numFilter(finalScore * weight) + ' out of ' + numFilter(weight) + ' point' + (weight === 1 ? '' : 's');
                }
              } else {
                score = 'Not Answered';
              }
            } else {
              score = numFilter(weight) + ' point' + (weight === 1 ? '' : 's');
            }

            if (answer && challenge.isQuestionTimed() && answer.timeTakenSec) {
              score += ' (answered in ' + answer.timeTakenSec + 's)';
            }
          }

          summary.push(score);

          if (!challenge.isBonusTimed() && self.maxAnswers() > 1 && weight !== 0) {
            if (self.allOrNothing) {
              summary.push('no partial credit');
            } else {
              summary.push('with partial credit');
            }
          }

          if (self.isMultipleChoice()) {
            var sel = self.updateMaxSelections();
            var weighted = self.weightedScoring ? ' weighted' : '';
            if (sel === 1) {
              summary.push('single' + weighted + ' answer');
            } else {
              summary.push('select up to ' + sel + weighted + ' answers');
            }
          }

          if (self.options.lengthValidation && self.options.lengthValidation.enabled) {
            var actualLength = answer && answer.values.length > 0 ? answer.values[0].length : 0;
            summary.push('length: ' + actualLength + '/' + self.options.lengthValidation.expectedLength);
          }

          if (challenge.isQuestionTimed()) {
            var limit$$1 = self._timeLimitSec(challenge);
            if (limit$$1) {
              summary.push(limit$$1 + 's time limit');
              if (challenge.isBonusTimed()) {
                summary.push('bonus countdown at ' + self._timeLimitStartSec(challenge) + 's');
              }
            }
          }

          if (!answer && self.isText()) {
            summary.push((self.defaultValue ? 'with' : 'no') + ' default answer');
          }

          return summary.join(', ');
        },
        _timeLimitSec: function _timeLimitSec(challenge) {
          return this.timeLimitSec || challenge.questionTimeLimitSec;
        },
        _timeLimitStartSec: function _timeLimitStartSec(challenge) {
          return angular.isNumber(this.timeLimitStartSec) ? this.timeLimitStartSec : challenge.questionTimeLimitStartSec || 0;
        },
        matcherTest: function matcherTest() {
          if (!this.$matcherTest) {
            defineValue(this, '$matcherTest', {
              open: false,
              text: '',
              notes: {}
            });
          }
          return this.$matcherTest;
        },
        calculateLengthScore: function calculateLengthScore(answer) {
          if (!this.options.lengthValidation || !this.options.lengthValidation.enabled) {
            return 1;
          }

          var expectedLength = this.options.lengthValidation.expectedLength;

          if (expectedLength <= 0) {
            return 1;
          }

          // Handle both string input and answer object with values array
          var answerText = '';
          if (typeof answer === 'string') {
            answerText = answer;
          } else if (answer && answer.values && answer.values.length > 0) {
            answerText = answer.values[0];
          }

          var actualLength = answerText.length;

          return Math.min(1, actualLength / expectedLength);
        },
        getFinalScore: function getFinalScore(answer, ruleScore) {
          if (!this.options.lengthValidation || !this.options.lengthValidation.enabled) {
            return ruleScore;
          }

          var lengthScore = this.calculateLengthScore(answer);
          return (lengthScore + ruleScore) / 2;
        }
      },

      staticMethods: {
        getMatcherLabel: function getMatcherLabel(item, result) {
          var k = (item.negate ? 'not' : '') + (item.type || 'regex') + (result && !result.correct ? 'Incorrect' : '');
          return MATCHER_LABELS[k];
        },
        syncChoices: function syncChoices(question) {
          if (question.choices && question.maxSelections > question.choices.length) {
            // revert to the default
            question.maxSelections = null;
          }
        }
      },

      relations: {},

      meta: {}
    });

    return QuizQuestion;
  }]);

  function getFileLanguage(file) {
    if (!file || !file.path) return '';
    var lang = languages.langFromFilename(file.path);
    return lang ? lang.id : getExtension(file.path);
  }

  function getFileIcon(file) {
    if (!file) return '';
    if (isString(file.icon)) return file.icon;
    return file.directory ? '' : 'lang-' + getFileLanguage(file);
  }

  function splitPath(path) {
    path = path || '';
    // clean up and split path
    var pathParts = path.replace(/^\/+|\/+$/g, '').split('/');
    var filename = pathParts.pop();
    return { pathParts: pathParts, filename: filename };
  }

  function parentPath(path) {
    return splitPath(path).pathParts.join('/');
  }

  function cleanPath(path) {
    path = path || '';
    return path.replace(/[\\\/]+/g, '/') // ensure correct slashes, and remove duplicate slashes
    .replace(/^\/|\/$/g, '') // remove leading and trailing slashes
    .replace(/(^|\/)\.\.+\//g, '/'); // strip relative path expressions
  }

  /**
   * Converts a layout string to layout properties
   * @see layoutString
   * @param {string} layout
   * @returns {{tabindex: number, active: boolean, tabset: string}}
   */
  var layoutProps = function layoutProps(layout) {
    var _ref = layout && layout.match(/^(\w+):(\d+)(?::(\d))?/) || [],
        _ref2 = slicedToArray(_ref, 4),
        tabset = _ref2[1],
        tabindex = _ref2[2],
        active = _ref2[3];

    return { tabset: tabset, tabindex: +tabindex, active: !!+active };
  };

  /**
   * Configures a file with initial layout data
   * @param {CodeFile} file - File to configure
   * @param {boolean} always - If true, always override the file's layout data
   * @param {boolean} lockedLayout - If true, set the file's layout data if missing
   * @param {string?} initialLayout - Layout data, usually provided by file.initialLayout. {@see layoutString} for more details
   */
  var configureInitiallyOpenFile = function configureInitiallyOpenFile(_ref4) {
    var file = _ref4.file,
        always = _ref4.always,
        lockedLayout = _ref4.lockedLayout,
        _ref4$initialLayout = _ref4.initialLayout,
        initialLayout = _ref4$initialLayout === undefined ? file.initialLayout : _ref4$initialLayout;

    var _layoutProps = layoutProps(file.hidden ? '' : initialLayout),
        tabset = _layoutProps.tabset,
        tabindex = _layoutProps.tabindex,
        active = _layoutProps.active;
    // save default tabset if available


    file.lastTabset = tabset || file.lastTabset || file.defaultTabset;
    file.defaultTabset = tabset;
    file.defaultTabindex = tabindex;
    file.defaultActive = active;
    if (always || lockedLayout && !file.tabset && tabset) {
      file.tabset = tabset;
      file.tabindex = tabindex;
      file.active = active;
    }
  };

  var codeFileSubclasses = ['AdvancedCodeChallengeFile', 'AdvancedCodeChallengeReferenceFile', 'AdvancedCodeSolutionFile'];

  var SOLUTION_FILE_CHANGE_KEYS = ['path', 'contents', 'cursor'];
  var SOLUTION_FILE_COPY_KEYS = ['id', 'challengeFileId', 'access', 'deletable', 'directory', 'includeOnSubmit', 'includeOnTest', 'testGroups', 'initialLayout', 'field', 'isClassicCode'].concat(SOLUTION_FILE_CHANGE_KEYS);

  function addOptionProperties(modelConfig, props) {
    props.forEach(function (prop) {
      var optionKeySafe = prop.toLowerCase();
      modelConfig.computed[prop] = {
        get: function get$$1() {
          return (this.options || {})[optionKeySafe];
        },
        set: function set$$1(val) {
          this.options = this.options || {};
          this.options[optionKeySafe] = val;
        },

        enumerable: false
      };
    });
  }

  function addHiddenProperties(modelConfig, props) {
    props.forEach(function (prop) {
      modelConfig.computed[prop] = {
        get: function get$$1() {
          return (this.$hidden || {})[prop];
        },
        set: function set$$1(val) {
          if (!this.$hidden) {
            defineValue(this, '$hidden', {});
          }
          this.$hidden[prop] = val;
        },

        enumerable: false
      };
    });
  }

  var codeFileModelModule = angular.module('Q.models.code-file', [modelHelperModule]).factory('CodeFile', ['ModelHelper', function (ModelHelper) {
    // shared functions for CodeFiles
    function define(modelConfig) {
      // property getters & setters
      addOptionProperties(modelConfig, ['collapsed', 'tabset', 'tabindex', 'active', 'closeable']);
      addHiddenProperties(modelConfig, ['$deleted', 'defaultTabset', 'defaultTabindex', 'defaultActive', 'lastTabset', 'ignored', 'parentAccess', 'tabClasses', '$readOnly', 'selections', '$interact', '$hideFromInteract']);

      var computed = modelConfig.computed;

      if (!computed.readOnly) {
        computed.readOnly = {
          get: function get$$1() {
            return this.$deleted;
          }
        };
      }
      computed.$markdown = {
        get: function get$$1() {
          return (/\.(md|mark(down)?)$/.test(this.path)
          );
        }
      };
      computed.$markdownReadOnly = {
        get: function get$$1() {
          return this.$markdown && this.$type === 'AdvancedCodeSolutionFile' && this.$access && this.$access !== 'readwrite' && !this.$interact;
        }
      };
      computed.$access = {
        get: function get$$1() {
          var access = this.$interactMode ? 'readwrite' : this.access;
          if (access !== 'hidden') {
            var parentAccess = this.parentAccess;
            var parentHiding = parentAccess === 'restricted' || parentAccess === 'hidden';
            if (parentHiding) {
              // allows the parent access level to override this access level
              access = 'hidden';
            } else if (parentAccess === 'readonly' && access === 'readwrite' && this.directory) {
              access = 'readonly';
            }
          }
          return access;
        },
        set: function set$$1(val) {
          // set the requested value, even though we may end up with something different based
          // on parent rules
          this.access = val;
        }
      };
      computed.$deletable = {
        get: function get$$1() {
          var deletable = this.deletable || false;
          if (deletable) {
            var parentAccess = this.parentAccess;
            if (parentAccess && parentAccess !== 'readwrite') {
              deletable = false;
            }
          }
          return deletable;
        },
        set: function set$$1(val) {
          // set the requested value, even though we may end up with something different based
          // on parent rules
          this.deletable = val;
        }
      };
      computed.language = {
        get: function get$$1() {
          return getFileLanguage(this);
        }
      };

      modelConfig.afterCreateInstance = function (resource, files) {
        if (!Array.isArray(files)) files = [files];
        files.forEach(function (file) {
          file.$type = modelConfig.name;
        });
      };

      return ModelHelper.defineResource(modelConfig);
    }

    return {
      define: define,
      configureAccessLevels: function configureAccessLevels(files) {
        var parentRules = {};
        files.slice()
        // sorting ensures we'll go through them in the right order
        .sort(function (a, b) {
          return compareIgnoreCase(a.path, b.path);
        }).forEach(function (f) {
          f.parentAccess = null;
          // try to set the parent access if there is any
          var parentPath$$1 = splitPath(f.path).pathParts;
          while (parentPath$$1.length) {
            var parentAccess = parentRules[parentPath$$1.join('/')];
            if (parentAccess) {
              f.parentAccess = parentAccess;
              break;
            }
            parentPath$$1.pop();
          }
          // store the new $access (based on parent access)
          parentRules[f.path] = f.$access;
        });
      }
    };
  }]).factory('AdvancedCodeChallengeFile', ['CodeFile', function (CodeFile) {
    var modelConfig = {
      name: 'AdvancedCodeChallengeFile',
      endpoint: 'advanced_code_challenge_files',

      cleanAfterDestroy: {
        parent: 'Challenge',
        collection: 'files'
      },

      meta: {
        defaults: {
          access: 'readwrite',
          accessLocked: false,
          codeSimilarity: false,
          contents: '',
          deletable: true,
          directory: false,
          includeOnSubmit: true,
          includeOnTest: true,
          initialLayout: null,
          options: {},
          path: '',
          testGroups: []
        }
      },

      computed: {
        candidateCanOpen: {
          get: function get$$1() {
            return this.$access === 'readwrite' || this.$access === 'readonly';
          }
        }
      },
      methods: {}
    };

    addHiddenProperties(modelConfig, ['hasReference', 'wasDeletable']);

    return CodeFile.define(modelConfig);
  }]).factory('AdvancedCodeChallengeReferenceFile', ['CodeFile', function (CodeFile) {
    var modelConfig = {
      name: 'AdvancedCodeChallengeReferenceFile',
      endpoint: 'advanced_code_challenge_reference_files',

      cleanAfterDestroy: {
        parent: 'Challenge',
        collection: 'referenceFiles'
      },

      meta: {
        defaults: {
          contents: '',
          directory: false,
          options: {},
          path: '',
          testGroups: []
        }
      },

      computed: {},
      methods: {
        reference: true
      }
    };

    addHiddenProperties(modelConfig, ['hasReference', 'notAllowed']);

    return CodeFile.define(modelConfig);
  }]).factory('AdvancedCodeSolutionFile', ['DS', 'CodeFile', function (DS, CodeFile) {
    var modelConfig = {
      name: 'AdvancedCodeSolutionFile',
      endpoint: 'advanced_code_solution_files',

      cleanAfterDestroy: {
        parent: 'Solution',
        collection: 'files'
      },

      computed: {
        interactId: {
          get: function get$$1() {
            if (this.field) {
              return this.field + '.' + getExtension(this.path);
            } else {
              return this.path;
            }
          }
        },
        hidden: {
          get: function get$$1() {
            return !this.$reviewMode && this.$access === 'hidden';
          }
        },
        disabled: {
          get: function get$$1() {
            return !this.$reviewMode && (this.$access === 'restricted' || this.$access === 'hidden');
          }
        },
        alwaysReadOnly: {
          get: function get$$1() {
            return this.$reviewMode || this.$deleted || this.$access !== 'readwrite';
          }
        },
        readOnly: {
          get: function get$$1() {
            return this.alwaysReadOnly || this.$readOnly;
          }
        },
        alwaysLocked: {
          get: function get$$1() {
            return this.disabled || this.alwaysReadOnly || this.lockedLayout || !this.$deletable;
          }
        },
        locked: {
          get: function get$$1() {
            return this.disabled || this.readOnly || this.lockedLayout || !this.$deletable;
          }
        },
        directoryLocked: {
          get: function get$$1() {
            return this.directory && (this.disabled || this.readOnly);
          }
        }
      },

      methods: {
        expirable: true,
        readWriteCopy: function readWriteCopy() {
          var file = this;
          var props = {
            options: {},
            // override read/write
            $hideFromInteract: file.$hideFromInteract || file.disabled,
            $interactMode: true
          };
          SOLUTION_FILE_COPY_KEYS.forEach(function (k) {
            props[k] = file[k];
          });
          var fileCopy = DS.createInstance('AdvancedCodeSolutionFile', props);
          if (this.isClassicCode) {
            // have to write back to parent model
            Object.defineProperty(fileCopy, 'contents', {
              get: function get$$1() {
                return file.contents;
              },
              set: function set$$1(val) {
                file.contents = val;
              }
            });
          }
          return fileCopy;
        },
        readOnlyCopy: function readOnlyCopy() {
          var _this = this;

          var props = {
            options: {},
            $reviewMode: true
          };
          SOLUTION_FILE_COPY_KEYS.forEach(function (k) {
            props[k] = _this[k];
          });
          return DS.createInstance('AdvancedCodeSolutionFile', props);
        },
        changesIgnored: function changesIgnored() {
          return this.restricted || this.includeOnSubmit && this.hasChallengeFile;
        },
        ignoredWarningTooltip: function ignoredWarningTooltip() {
          return 'You may edit this file for your own tests, but <strong>' + (this.changesIgnored() ? 'changes to this file are' : 'this file is') + ' not included when assessing your code.</strong><br>(Your edits will still be saved, and can be viewed by anyone reviewing your code.)';
        }
      },

      staticMethods: {
        /**
         * Returns a filtered and sorted list of files to initially show
         * @param {Array<CodeFile>} files - Files to filter and sort
         * @returns {Array<CodeFile>} - Filtered and sorted list
         */
        initialLayout: function initialLayout(files) {
          return files.filter(function (f) {
            return !f.directory && f.initialLayout && f.$access !== 'hidden' && f.$access !== 'restricted';
          }).sort(function (a, b) {
            // ensure readwrite files are opened before readonly
            return a.$access === b.$access ? compareIgnoreCase(a.path, b.path) : a.$access === 'readwrite' ? -1 : 1;
          });
        },


        /**
         * Configures the initial layout for each file
         * @param {Array<CodeFile>} files - Array of files
         * @param {boolean} always - If true, always set the layout, even if the file has a different one stored
         * @param {boolean} lockedLayout - If true, set the layout if missing from the file
         * @param {multiFileChallengeService} mfcs - Used to provide a default layout if we're missing all layouts
         * @returns {Array<CodeFile>}
         */
        configureInitialLayout: function configureInitialLayout(files) {
          var _ref5 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
              always = _ref5.always,
              lockedLayout = _ref5.lockedLayout,
              mfcs = _ref5.mfcs;

          var initialLayoutFiles = this.initialLayout(files);
          if (initialLayoutFiles.length === 0 && mfcs) {
            initialLayoutFiles = [mfcs.ideSettingsFile];
          }
          initialLayoutFiles.forEach(function (file) {
            configureInitiallyOpenFile({
              file: file,
              always: always,
              lockedLayout: lockedLayout
            });
          });
          return initialLayoutFiles;
        }
      },

      meta: {
        defaults: {
          access: 'readwrite',
          contents: '',
          deletable: true,
          directory: false,
          includeOnSubmit: true,
          includeOnTest: true,
          options: {},
          path: '',
          testGroups: []
        }
      }
    };

    addHiddenProperties(modelConfig, ['canReset', 'unchanged', 'ignored', 'restricted', 'hasChallengeFile', 'lockedLayout', '$interactMode']);

    return CodeFile.define(modelConfig);
  }]);

  // highlight

  /* eslint-disable camelcase */
  /* globals hljs:true */

  /**
   * highlight.js Solidity syntax highlighting definition
   *
   * @see https://github.com/pospi/highlightjs-solidity
   *
   * @package: highlightjs-solidity
   * @author:  Sam Pospischil <sam@changegiving.com>
   * @since:   2016-07-01
   */

  hljs.registerLanguage('solidity', function hljsDefineSolidity(hljs) {
    var SOL_KEYWORDS = {
      keyword: 'var bool string ' + 'int uint int8 uint8 int16 uint16 int24 uint24 int32 uint32 ' + 'int40 uint40 int48 uint48 int56 uint56 int64 uint64 ' + 'int72 uint72 int80 uint80 int88 uint88 int96 uint96 ' + 'int104 uint104 int112 uint112 int120 uint120 int128 uint128 ' + 'int104 uint104 int112 uint112 int120 uint120 int128 uint128 ' + 'int136 uint136 int144 uint144 int152 uint152 int160 uint160 ' + 'int168 uint168 int176 uint176 int184 uint184 int192 uint192 ' + 'int200 uint200 int208 uint208 int216 uint216 int224 uint224 ' + 'int232 uint232 int240 uint240 int248 uint248 int256 uint256 ' + 'byte bytes bytes1 bytes2 bytes3 bytes4 bytes5 bytes6 bytes7 bytes8 ' + 'bytes9 bytes10 bytes11 bytes12 bytes13 bytes14 bytes15 bytes16 ' + 'bytes17 bytes18 bytes19 bytes20 bytes21 bytes22 bytes23 bytes24 ' + 'bytes25 bytes26 bytes27 bytes28 bytes29 bytes30 bytes31 bytes32 ' + 'enum struct mapping address ' + 'new delete ' + 'if else for while continue break return throw ' + 'function modifier event ' + 'constant anonymous indexed ' + 'storage memory ' + 'external public internal private returns ' + 'import using ' + 'contract library ' + 'assembly',
      literal: 'true false ' + 'wei szabo finney ether ' + 'second seconds minute minutes hour hours day days week weeks year years',
      built_in: 'self ' + // :NOTE: not a real keyword, but a convention used in storage manipulation libraries
      'this super selfdestruct ' + 'now ' + 'msg ' + 'block ' + 'tx ' + 'sha3 sha256 ripemd160 erecover addmod mulmod ' +
      // :NOTE: not really toplevel, but advantageous to have highlighted as if reserved to
      //        avoid newcomers making mistakes due to accidental name collisions.
      'send call callcode delegatecall'
    };

    var SOL_NUMBER = {
      cN: 'number',
      v: [{ b: '\\b(0[bB][01]+)' }, { b: '\\b(0[oO][0-7]+)' }, { b: hljs.CNR }],
      r: 0
    };

    var SOL_FUNC_PARAMS = {
      cN: 'params',
      b: /\(/,
      e: /\)/,
      eB: true,
      eE: true,
      k: SOL_KEYWORDS,
      c: [hljs.CLCM, hljs.CBCM, hljs.ASM, hljs.QSM, SOL_NUMBER]
    };

    var SOL_RESERVED_MEMBERS = {
      b: /\.\s*/, // match any property access up to start of prop
      e: /[^A-Za-z0-9$_.]/,
      eB: true,
      eE: true,
      k: {
        built_in: 'gas value send call callcode delegatecall balance length push'
      },
      r: 2
    };

    function makeBuiltinProps(obj, props) {
      return {
        b: obj + '\\.\\s*',
        e: /[^A-Za-z0-9$_.]/,
        eB: false,
        eE: true,
        k: {
          built_in: obj + ' ' + props
        },
        c: [SOL_RESERVED_MEMBERS],
        r: 10
      };
    }

    return {
      aliases: ['sol'],
      l: SOL_KEYWORDS,
      c: [
      // basic literal definitions
      hljs.ASM, hljs.QSM, hljs.CLCM, hljs.CBCM, SOL_NUMBER, {
        // functions
        cN: 'function',
        bK: 'function modifier event',
        e: /[{;]/,
        eE: true,
        c: [hljs.inherit(hljs.TM, {
          b: /[A-Za-z$_][0-9A-Za-z$_]*/,
          k: SOL_KEYWORDS
        }), SOL_FUNC_PARAMS],
        i: /[\[%]/
      },
      // built-in members
      makeBuiltinProps('msg', 'data sender sig'), makeBuiltinProps('block', 'blockhash coinbase difficulty gaslimit number timestamp '), makeBuiltinProps('tx', 'gasprice origin'), SOL_RESERVED_MEMBERS, {
        // contracts & libraries
        cN: 'class',
        bK: 'contract library',
        e: /[{]/,
        eE: true,
        i: /[:"\[\]]/,
        c: [{ bK: 'is' }, hljs.UTM, SOL_FUNC_PARAMS]
      }, {
        // imports
        bK: 'import',
        e: '[;$]',
        k: 'import * from as',
        c: [hljs.ASM, hljs.QSM]
      }],
      i: /#/
    };
  });

  // highlight

  /**
   * Stops an event from happening
   * @param $event
   */
  function stopEvent($event) {
    if (isFunction($event)) {
      return function (ev) {
        stopEvent(ev);

        for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }

        return $event.apply(undefined, args);
      };
    }
    if ($event) {
      if ($event.preventDefault) $event.preventDefault();
      $event.returnValue = false;
      stopBubbling($event);
    }
  }

  /**
   * Stops an event from propagating
   * @param $event
   */
  function stopBubbling($event) {
    if ($event) {
      if ($event.stopPropagation) $event.stopPropagation();
      $event.cancelBubble = true;
    }
  }

  var CustomEvent = function () {
    if (isFunction(window.CustomEvent)) return window.CustomEvent; // For modern browsers

    function CustomEvent(event) {
      var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
          _ref$bubbles = _ref.bubbles,
          bubbles = _ref$bubbles === undefined ? false : _ref$bubbles,
          _ref$cancelable = _ref.cancelable,
          cancelable = _ref$cancelable === undefined ? false : _ref$cancelable,
          detail = _ref.detail;

      var evt = document.createEvent('CustomEvent');
      evt.initCustomEvent(event, bubbles, cancelable, detail);
      return evt;
    }

    CustomEvent.prototype = window.Event.prototype;

    return window.CustomEvent = CustomEvent;
  }();

  function isFileEvent(event) {
    var edt = event.dataTransfer || {};
    return edt.files && edt.files.length || edt.items && Array.prototype.some.call(edt.items, function (item) {
      return item.kind === 'file';
    });
  }

  /**
   * Adds simple event channels to any object (on, off, emit)
   * @param obj
   * @returns obj
   */
  function eventEmitter(obj) {
    var _listeners = {};

    /**
     * Registers an event listener on a named event
     * @param name {string} Name of event
     * @param listener {function} Listener
     * @returns {function} Deregistration function
     */
    function on(name, listener) {
      var namedListeners = _listeners[name];
      if (!namedListeners) {
        _listeners[name] = namedListeners = [];
      }
      namedListeners.push(listener);
      return off.bind(obj, name, listener);
    }

    /**
     * Deregisters an event listener on a named event
     * @param name {string|true} Name of event. If true, destroys all listeners
     * @param listener {function} Listener to deregister
     */
    function off(name, listener) {
      if (name === true) {
        // disable _all_ listeners
        Object.keys(_listeners).forEach(function (k) {
          delete _listeners[k];
        });
      } else {
        var namedListeners = _listeners[name];
        if (namedListeners) {
          var indexOfListener = namedListeners.indexOf(listener);
          if (indexOfListener !== -1) {
            namedListeners.splice(indexOfListener, 1);
          }
        }
      }
    }

    /**
     * Emits a named event
     * @param name {string} Name of event
     * @param args {*} Additional arguments to the event
     * @returns {{name: *, preventDefault: (function()), defaultPrevented: boolean}} Resulting event object
     */
    function emit(name) {
      for (var _len2 = arguments.length, args = Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }

      var namedListeners = _listeners[name];
      var event = {
        name: name,
        preventDefault: function preventDefault() {
          event.defaultPrevented = true;
        },

        defaultPrevented: false
      };
      if (namedListeners) {
        namedListeners.forEach(function (l) {
          return isFunction(l) && l.apply(undefined, [event].concat(args));
        });
      }
      return event;
    }

    obj.on = on;
    obj.off = off;
    obj.emit = emit;

    return obj;
  }

  var PLAIN_TEXT = ['text', 'plain', 'plaintext', 'none', 'null'];

  var codeHighlightComponentModule = angular.module('Q.components.code-highlight', []).directive('codeHighlight', ['$window', '$sanitize', 'highlightCode', '$injector', function ($window, $sanitize, highlightCode, $injector) {
    // no hard dependencies
    var Challenge = null;
    if ($injector.has('Challenge')) {
      Challenge = $injector.get('Challenge');
    }

    return {
      restrict: 'A',
      replace: true,
      scope: {
        language: '=',
        framework: '=',
        codeHighlight: '=',
        lineNumberStart: '=?'
      },
      link: function link(scope, element, attrs) {
        element.addClass('markdown');

        var shouldWrap = element[0].nodeName !== 'CODE';

        render(scope.codeHighlight || element.text() || '');
        if (attrs.codeHighlight) {
          scope.$watch('codeHighlight', render);
        }

        function render(code) {
          var lang = scope.language;
          if (scope.framework) {
            lang = languages.frameworkLanguage(lang, scope.framework);
          }
          var highlightedCode = $sanitize(highlightCode(code, lang, scope.lineNumberStart));
          highlightedCode = replaceIncludeSolution(highlightedCode);
          if (shouldWrap) {
            highlightedCode = '<pre><code>' + highlightedCode + '</code></pre>';
          }
          element.html(highlightedCode);
        }

        var loaded = false;

        function replaceIncludeSolution(highlightedCode) {
          if (Challenge) {
            var needsReload = false;
            highlightedCode = highlightedCode.replace(Challenge.INCLUDE_SOLUTIONS_REGEX_GLOBAL, function (match, leading, id) {
              var c = Challenge.get(id);
              if (c) {
                return leading + '<span class="carry-forward-notification">Dynamically pulls previous solution from <a href="challenges/' + id + '" target="_blank">' + c.title + '</a></span>';
              } else {
                needsReload = true;
                return match;
              }
            });

            if (needsReload && !loaded) {
              Challenge.findAll().then(function () {
                loaded = true;
                render(scope.codeHighlight || element.text() || '');
              });
            }
          }
          return highlightedCode;
        }
      }
    };
  }]).factory('highlightCode', ['$window', '$log', function ($window, $log) {
    var reported = {};
    var fn = function highlightCode(code, lang) {
      var start = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;

      if ($window.codeHighlightLinenums) {
        if (lang) {
          if (PLAIN_TEXT.includes(lang.toLowerCase())) {
            lang = '';
          } else {
            lang = lang.replace(/^[^:]+/, function (l) {
              return languages[l] && languages[l].highlight || '';
            });
          }
        }
        try {
          code = $window.codeHighlightLinenums(code, {
            lang: lang,
            hljs: $window.hljs,
            start: start
          });
        } catch (err) {
          // make sure we don't render HTML if the language didn't load
          code = escapeHTML(code);
          if (!reported[lang]) {
            reported[lang] = true;
            $log.warn(err);
          }
        }
      }

      return code;
    };
    fn.withCircular = function (code, lang, start) {
      return fn(code, lang, start).replace(/\[Circular\]/g, '<span class="hljs-circular">[Circular]</span>');
    };
    return fn;
  }]);

  var zoomableImageTemplate = "<span class=\"zoomable-image-controls\" ng-if=\"$ctrl.enabled\"> <button class=\"btn-default btn-sm icon-{{ $ctrl.expanded ? 'contract' : 'expand' }}\" ng-click=\"$ctrl.expandOrContract($event)\" tooltip=\"{{ $ctrl.expanded ? 'Scale image back down' : 'Make this image as large as possible'}}\"></button> <button class=\"btn-default btn-sm icon-minus\" ng-click=\"$ctrl.zoomOut($event)\" ng-disabled=\"$ctrl.zoomOutDisabled\" tooltip=\"Zoom Out\"></button> <button class=\"btn-default btn-sm\" ng-class=\"{ active: $ctrl.is100 }\" ng-click=\"$ctrl.zoom100($event)\" tooltip=\"Zoom 1:1 pixels\"> 1:1 </button> <button class=\"btn-default btn-sm icon-plus\" ng-click=\"$ctrl.zoomIn($event)\" ng-disabled=\"$ctrl.zoomInDisabled\" tooltip=\"Zoom In\"></button> </span> <div class=\"zoomable-image-scrollbox\" ng-transclude ng-dblclick=\"$ctrl.autoZoom($event)\" tooltip=\"{{ $ctrl.enabled ? 'You can zoom into this image using the controls, or double-clicking on it' : '' }}\" tooltip-position=\"top\" scroll-on-drag=\"$ctrl.enabled && $ctrl.zoomed\"></div> ";

  var WINDOW_EVT_RESIZE = 'window:resize';

  var windowEventHelperModule = angular.module('Q.helpers.window-event-helper', []).run(['$rootScope', '$document', '$window', '$timeout', function ($rootScope, $document, $window, $timeout) {
    function _configEvents(element, prefix, events) {
      events.forEach(function (evtName) {
        var names = evtName.split(';');
        element.on(names[0], function (event) {
          names.forEach(function (n) {
            $rootScope.$broadcast(prefix + ':' + n, event);
          });
        });
      });
    }

    _configEvents(angular.element($window), 'window', ['resize', 'focus', 'blur', 'orientationchange;resize', 'message']);

    _configEvents($document, 'document', ['focusin']);

    // Special case for focus out, because we usually want to see if the active element changed, which needs a delay
    $document.on('focusout', function (event) {
      $rootScope.$broadcast('document:focusout', event);
      $timeout(function () {
        $rootScope.$broadcast('document:focusoutActiveElement', event, $document[0].activeElement);
      }, 0);
    });

    // set up document focus events based on polling
    var doc = $document[0];
    if (angular.isFunction(doc.hasFocus)) {
      var docHadFocus = doc.hasFocus();
      $window.setInterval(function checkFocus() {
        if (docHadFocus !== doc.hasFocus()) {
          docHadFocus = doc.hasFocus();
          $rootScope.$applyAsync(function () {
            $rootScope.$broadcast('document:' + (docHadFocus ? 'focus' : 'blur'));
          });
        }
      }, 250);
    }
  }]);

  var domHelperModule = angular.module('Q.helpers.dom-helper', []).factory('domHelper', ['$q', function ($q) {
    function whenImageLoaded(img) {
      var $img = angular.element(img);
      var _img = $img[0];
      if (_img && (!_img.complete || _img.naturalWidth === 0)) {
        // Image is not complete, set up listeners
        return $q(function (resolve, reject) {
          $img.on('load', function () {
            resolve(img);
          }).on('error', function () {
            reject(img);
          });
        });
      } else {
        return $q.resolve(img);
      }
    }

    return {
      whenImageLoaded: whenImageLoaded
    };
  }]);

  var scrollOnDragDirectiveModule = angular.module('Q.components.scroll-on-drag', [])

  // Note: this directive is not designed to work on window/body, it only works on dom nodes
  .directive('scrollOnDrag', ['$document', function ($document) {
    return {
      restrict: 'A',
      link: function link(scope, el, attr) {
        var startXPos = void 0;
        var startYPos = void 0;
        var scrollXPos = void 0;
        var scrollYPos = void 0;
        var curDown = false;
        var enabled = false;

        function stopDrag() {
          // safe-to-call method to stop dragging whenever we want
          el.removeClass('scroll-on-drag-active');
          curDown = false;
          $document.off('mouseup', stopDrag);
          $document.off('mousemove', mouseMoveListener);
        }

        function mouseDownListener(e) {
          if (enabled) {
            stopEvent(e);
            el.addClass('scroll-on-drag-active');
            curDown = true;
            // store initial values
            startXPos = e.pageX;
            startYPos = e.pageY;
            scrollXPos = el[0].scrollLeft;
            scrollYPos = el[0].scrollTop;

            // start listening to document-level movement, so we can track outside the element
            $document.on('mouseup', stopDrag);
            $document.on('mousemove', mouseMoveListener);
          }
        }

        function mouseMoveListener(e) {
          if (enabled && curDown === true) {
            stopEvent(e);
            el[0].scrollLeft = scrollXPos + startXPos - e.pageX;
            el[0].scrollTop = scrollYPos + startYPos - e.pageY;
          }
        }

        checkEnable();

        // if we only need a link function, use this
        scope.$watch(attr.scrollOnDrag, checkEnable);

        function checkEnable() {
          if (attr.scrollOnDrag && scope.$eval(attr.scrollOnDrag) === false) {
            disable();
          } else {
            enable();
          }
        }

        function enable() {
          if (!enabled) {
            stopDrag();
            el.on('mousedown', mouseDownListener);
            el.addClass('scroll-on-drag-enabled');
            enabled = true;
          }
        }

        function disable() {
          if (enabled) {
            stopDrag();
            el.off('mousedown', mouseDownListener);
            el.removeClass('scroll-on-drag-enabled');
            enabled = false;
          }
        }
      }
    };
  }]);

  /**
   * From angular-ui UI.Layout
   */

  // broadcast when the layout is being resized (manually or otherwise)
  var UI_LAYOUT_EVT_RESIZE = 'ui.layout.resize';
  // broadcast after redrawing during manual resize
  var UI_LAYOUT_EVT_DRAW_COMPLETE = 'ui.layout.draw-complete';

  var UI_LAYOUT_AUTO_SIZE = 'auto';

  var uiLayoutComponentModule = angular.module('Q.components.ui-layout', [windowEventHelperModule]).controller('uiLayoutCtrl', ['$scope', '$attrs', '$element', '$timeout', 'LayoutContainer', 'Layout', '$log', function uiLayoutCtrl($scope, $attrs, $element, $timeout, LayoutContainer, Layout, $log) {
    var ctrl = this;
    var opts = angular.extend({}, $scope.$eval($attrs.uiLayout), $scope.$eval($attrs.options));
    var numOfSplitbars = 0;
    //var cache = {};
    var animationFrameRequested = void 0;
    var lastPos = void 0;

    // regex to verify size is properly set to pixels or percent
    var sizePattern = /\d+\s*(px|%)\s*$/i;

    Layout.addLayout(ctrl);

    ctrl.containers = [];
    ctrl.movingSplitbar = null;
    ctrl.bounds = $element[0].getBoundingClientRect();
    ctrl.isUsingColumnFlow = opts.flow === 'column';
    ctrl.sizeProperties = !ctrl.isUsingColumnFlow ? {
      sizeProperty: 'height',
      offsetSize: 'offsetHeight',
      offsetPos: 'top',
      flowProperty: 'top',
      oppositeFlowProperty: 'bottom',
      mouseProperty: 'clientY',
      flowPropertyPosition: 'y'
    } : {
      sizeProperty: 'width',
      offsetSize: 'offsetWidth',
      offsetPos: 'left',
      flowProperty: 'left',
      oppositeFlowProperty: 'right',
      mouseProperty: 'clientX',
      flowPropertyPosition: 'x'
    };

    $element
    // Force the layout to fill the parent space
    // fix no height layout...
    .addClass('stretch')
    // set the layout css class
    .addClass('ui-layout-' + (opts.flow || 'row'));

    // Initial global size definition
    opts.sizes = opts.sizes || [];
    opts.maxSizes = opts.maxSizes || [];
    opts.minSizes = opts.minSizes || [];
    opts.dividerSize = opts.dividerSize === undefined ? 10 : opts.dividerSize;
    ctrl.opts = opts;

    $scope.updateDisplay = function () {
      ctrl.calculate();
    };

    var debounceEvent = void 0;

    function draw() {
      var position = ctrl.sizeProperties.flowProperty;
      var dividerSize = parseInt(opts.dividerSize);
      var elementSize = $element[0][ctrl.sizeProperties.offsetSize];

      if (ctrl.movingSplitbar !== null) {
        var splitbarIndex = ctrl.containers.indexOf(ctrl.movingSplitbar);
        var nextSplitbarIndex = splitbarIndex + 2 < ctrl.containers.length ? splitbarIndex + 2 : null;

        if (splitbarIndex > -1) {
          var processedContainers = ctrl.processSplitbar(ctrl.containers[splitbarIndex]);
          var beforeContainer = processedContainers.beforeContainer;
          var afterContainer = processedContainers.afterContainer;

          // calculate container positons
          var difference = ctrl.movingSplitbar[position] - lastPos;
          var newPosition = ctrl.movingSplitbar[position] - difference;

          // Keep the bar in the window (no left/top 100%)
          newPosition = Math.min(elementSize - dividerSize, newPosition);

          // Keep the bar from going past the previous element min/max values
          if (angular.isNumber(beforeContainer.beforeMinValue) && newPosition < beforeContainer.beforeMinValue) {
            newPosition = beforeContainer.beforeMinValue;
          }
          if (angular.isNumber(beforeContainer.beforeMaxValue) && newPosition > beforeContainer.beforeMaxValue) {
            newPosition = beforeContainer.beforeMaxValue;
          }

          // Keep the bar from going past the next element min/max values
          if (afterContainer !== null && angular.isNumber(afterContainer.afterMinValue) && newPosition > afterContainer.afterMinValue - dividerSize) {
            newPosition = afterContainer.afterMinValue - dividerSize;
          }
          if (afterContainer !== null && angular.isNumber(afterContainer.afterMaxValue) && newPosition < afterContainer.afterMaxValue) {
            newPosition = afterContainer.afterMaxValue;
          }

          // resize the before container
          beforeContainer.size = newPosition - beforeContainer[position];
          // store the current value to preserve this size during onResize
          beforeContainer.previousSize = beforeContainer.size;

          // update after container position
          var oldAfterContainerPosition = afterContainer[position];
          afterContainer[position] = newPosition + dividerSize;

          //update after container size if the position has changed
          if (afterContainer[position] !== oldAfterContainerPosition) {
            afterContainer.size = nextSplitbarIndex !== null ? oldAfterContainerPosition + afterContainer.size - (newPosition + dividerSize) : elementSize - (newPosition + dividerSize);
            // store the current value to preserve this size during onResize
            afterContainer.previousSize = afterContainer.size;
          }

          // move the splitbar
          ctrl.movingSplitbar[position] = newPosition;

          // broadcast an event that resize happened (debounced to 50ms)
          if (debounceEvent) $timeout.cancel(debounceEvent);
          debounceEvent = $timeout(function () {
            $scope.$broadcast(UI_LAYOUT_EVT_RESIZE, 'draw');
            $scope.$emit(UI_LAYOUT_EVT_DRAW_COMPLETE);
            debounceEvent = null;
          }, 50);
        }
      }

      //Enable a new animation frame
      animationFrameRequested = null;
    }

    function offset(element) {
      var rawDomNode = element[0];
      var body = document.documentElement || document.body;
      var scrollX = window.pageXOffset || body.scrollLeft;
      var scrollY = window.pageYOffset || body.scrollTop;
      var clientRect = rawDomNode.getBoundingClientRect();
      var x = clientRect.left + scrollX;
      var y = clientRect.top + scrollY;
      return { left: x, top: y };
    }

    /**
     * Returns the current value for an option
     * @param  option   The option to get the value for
     * @return The value of the option. Returns null if there was no option set.
     */
    function optionValue(option) {
      if (angular.isNumber(option) || angular.isString(option) && option.match(sizePattern)) {
        return option;
      } else {
        return null;
      }
    }

    //================================================================================
    // Public Controller Functions
    //================================================================================
    ctrl.mouseDownHandler = function (splitbar) {
      // only trigger when left mouse button is pressed:
      $element.addClass('ui-layout-resizing');
      ctrl.movingSplitbar = splitbar;
      ctrl.processSplitbar(splitbar);
    };
    ctrl.mouseUpHandler = function () {
      $element.removeClass('ui-layout-resizing');
      ctrl.movingSplitbar = null;
    };

    ctrl.mouseMoveHandler = function (mouseEvent) {
      var mousePos = mouseEvent[ctrl.sizeProperties.mouseProperty] || mouseEvent.originalEvent && mouseEvent.originalEvent[ctrl.sizeProperties.mouseProperty] || (mouseEvent.targetTouches ? mouseEvent.targetTouches[0][ctrl.sizeProperties.mouseProperty] : 0);

      lastPos = mousePos - offset($element)[ctrl.sizeProperties.offsetPos];

      //Cancel previous rAF call
      if (animationFrameRequested) {
        window.cancelAnimationFrame(animationFrameRequested);
      }

      //TODO: cache layout values

      //Animate the page outside the event
      animationFrameRequested = window.requestAnimationFrame(draw);
    };

    /**
     * Returns the min and max values of the ctrl.containers on each side of the container submitted
     * @param container
     * @returns {*}
     */
    ctrl.processSplitbar = function (container) {
      var index = ctrl.containers.indexOf(container);

      var setValues = function setValues(container) {
        var start = container[ctrl.sizeProperties.flowProperty];
        var end = container[ctrl.sizeProperties.flowProperty] + container.size;

        container.beforeMinValue = angular.isNumber(container.minSize) ? start + container.minSize : start;
        container.beforeMaxValue = angular.isNumber(container.maxSize) ? start + container.maxSize : null;

        container.afterMinValue = angular.isNumber(container.minSize) ? end - container.minSize : end;
        container.afterMaxValue = angular.isNumber(container.maxSize) ? end - container.maxSize : null;
      };

      //verify the container was found in the list
      if (index > -1) {
        var beforeContainer = index > 0 ? ctrl.containers[index - 1] : null;
        var afterContainer = index + 1 <= ctrl.containers.length ? ctrl.containers[index + 1] : null;

        if (beforeContainer !== null) setValues(beforeContainer);
        if (afterContainer !== null) setValues(afterContainer);

        return {
          beforeContainer: beforeContainer,
          afterContainer: afterContainer
        };
      }

      return null;
    };

    /**
     * Checks if a string has a percent symbol in it.
     * @param num
     * @returns {boolean}
     */
    ctrl.isPercent = function (num) {
      return !!(num && angular.isString(num) && num.indexOf('%') > -1);
    };

    /**
     * Converts a number to pixels from percent.
     * @param size
     * @param parentSize
     * @returns {number}
     */
    ctrl.convertToPixels = function (size, parentSize) {
      return Math.floor(parentSize * (parseInt(size) / 100));
    };

    var isAuto = function isAuto(i) {
      return opts.sizes[i] === UI_LAYOUT_AUTO_SIZE;
    };

    /**
     * Sets the size for each container initially and on window resize.
     */
    ctrl.calculate = function () {
      var dividerSize = parseInt(opts.dividerSize);
      var elementSize = $element[0].getBoundingClientRect()[ctrl.sizeProperties.sizeProperty];
      var originalSize = elementSize - dividerSize * numOfSplitbars;
      var containers = ctrl.containers;
      var containerCount = containers.length;

      if (originalSize > 0 && containerCount > 0 && $element.children().length > 0) {
        var availableSize = originalSize;
        var usedSpace = 0;
        var numOfAutoContainers = 0;
        var lastAuto = void 0;
        var autoSize = void 0;
        var sizes = opts.sizes;
        var minSizes = opts.minSizes;
        var maxSizes = opts.maxSizes;

        // calculate sizing for containers
        for (var i = 0; i < containerCount; i++) {
          if (!LayoutContainer.isSplitbar(containers[i])) {
            var c = containers[i];
            if (!isAuto(i)) {
              sizes[i] = c.isCentral ? UI_LAYOUT_AUTO_SIZE : optionValue(c.previousSize) || UI_LAYOUT_AUTO_SIZE;
            }
            minSizes[i] = optionValue(c.minSize);
            maxSizes[i] = optionValue(c.maxSize);

            if (!isAuto(i)) {
              if (ctrl.isPercent(sizes[i])) {
                sizes[i] = ctrl.convertToPixels(sizes[i], originalSize);
              } else {
                sizes[i] = parseInt(sizes[i]);
              }
            }

            if (minSizes[i] !== null) {
              if (ctrl.isPercent(minSizes[i])) {
                minSizes[i] = ctrl.convertToPixels(minSizes[i], originalSize);
              } else {
                minSizes[i] = parseInt(minSizes[i]);
              }

              // don't allow the container size to be smaller than the minSize
              if (sizes[i] < minSizes[i]) sizes[i] = minSizes[i];
            }

            if (maxSizes[i] !== null) {
              if (ctrl.isPercent(maxSizes[i])) {
                maxSizes[i] = ctrl.convertToPixels(maxSizes[i], originalSize);
              } else {
                maxSizes[i] = parseInt(maxSizes[i]);
              }

              // don't allow the container size to be larger than the maxSize
              if (sizes[i] > maxSizes[i]) sizes[i] = maxSizes[i];
            }

            if (isAuto(i)) {
              numOfAutoContainers++;
            } else {
              availableSize -= sizes[i];
            }
          }
        }

        // calculate initial autoSize
        autoSize = numOfAutoContainers > 0 ? Math.floor(availableSize / numOfAutoContainers) : 0;

        // Now scan again to check for any auto container outside it's min/max limits
        for (var _i = 0; _i < containerCount; _i++) {
          if (!LayoutContainer.isSplitbar(containers[_i]) && isAuto(_i)) {
            var maxSize = maxSizes[_i];
            var minSize = minSizes[_i];
            if (maxSize && maxSize < autoSize) {
              // adjust available size if the container would be too wide
              availableSize -= maxSize;
              numOfAutoContainers--;
            } else if (minSize && minSize > autoSize) {
              // adjust available size if the container would be too narrow
              // Note: this _could_ leave us with too much content for the space, so it might overflow
              //   this is likely preferable to crushing content
              availableSize -= minSize;
              numOfAutoContainers--;
            } else {
              // track the last truly auto container for the remainder
              lastAuto = _i;
            }
          }
        }

        // recalculate autosize in case we converted an autosize container
        autoSize = numOfAutoContainers > 0 ? Math.floor(availableSize / numOfAutoContainers) : 0;
        /*
         * When the parent size is odd, rounding down the `autoSize` leaves a remainder.
         * This remainder is added to the last auto-sized container in a layout.
         */
        var remainder = Math.max(0, availableSize - autoSize * numOfAutoContainers);
        for (var _i2 = 0; _i2 < containerCount; _i2++) {
          var _c = containers[_i2];
          _c[ctrl.sizeProperties.flowProperty] = usedSpace;
          _c.maxSize = maxSizes[_i2];
          _c.minSize = minSizes[_i2];

          if (!LayoutContainer.isSplitbar(_c)) {
            var newSize = void 0;
            if (isAuto(_i2)) {
              newSize = Math.min(_c.maxSize || Infinity, Math.max(_c.minSize || 0, autoSize));
              // add the rounding down remainder to the last auto-sized container in the layout
              if (_i2 === lastAuto) {
                newSize += remainder;
              }
            } else {
              newSize = sizes[_i2];
              if (availableSize < 0) {
                // the first fixed container must lose some width if we have negative space due to min/max
                newSize = Math.min(_c.maxSize || Infinity, Math.max(_c.minSize || 0, newSize + availableSize));
                availableSize = 0;
              }
            }

            _c.size = newSize !== null ? newSize : autoSize;
          } else {
            _c.size = dividerSize;
          }

          usedSpace += _c.size;
        }
      }

      if (debounceEvent) $timeout.cancel(debounceEvent);
      debounceEvent = $timeout(function () {
        $scope.$broadcast(UI_LAYOUT_EVT_RESIZE, 'calculate');
        debounceEvent = null;
      }, 50);
    };

    /**
     * Adds a container to the list of layout ctrl.containers.
     * @param container The container to add
     */
    ctrl.addContainer = function (container) {
      var index = ctrl.indexOfElement(container.element);
      if (!angular.isDefined(index) || index < 0 || ctrl.containers.length < index) {
        $log.error('Invalid index to add container; i=' + index + ', len=', ctrl.containers.length);
        return;
      }

      if (LayoutContainer.isSplitbar(container)) {
        numOfSplitbars++;
      }

      container.index = index;
      ctrl.containers.splice(index, 0, container);

      ctrl.calculate();
    };

    /**
     * Remove a container from the list of layout ctrl.containers.
     * @param  container
     */
    ctrl.removeContainer = function (container) {
      var index = ctrl.containers.indexOf(container);
      if (index >= 0) {
        if (!LayoutContainer.isSplitbar(container)) {
          if (ctrl.containers.length > 2) {
            // Assume there's a sidebar between each container
            // We need to remove this container and the sidebar next to it
            if (index === ctrl.containers.length - 1) {
              // We're removing the last element, the side bar is on the left
              ctrl.containers[index - 1].element.remove();
            } else {
              // The side bar is on the right
              ctrl.containers[index + 1].element.remove();
            }
          }
        } else {
          numOfSplitbars--;
        }

        // Need to re-check the index, as a side bar may have been removed
        var newIndex = ctrl.containers.indexOf(container);
        if (newIndex >= 0) {
          ctrl.containers.splice(newIndex, 1);
          ctrl.opts.maxSizes.splice(newIndex, 1);
          ctrl.opts.minSizes.splice(newIndex, 1);
          ctrl.opts.sizes.splice(newIndex, 1);
        }
        ctrl.calculate();
      } else {
        $log.error('removeContainer for container that did not exist!');
      }
    };

    /**
     * Returns an array of layout ctrl.containers.
     * @returns {Array}
     */
    ctrl.getContainers = function () {
      return ctrl.containers;
    };

    /**
     * Returns the container object of the splitbar that is before the one passed in.
     * @param currentSplitbar
     */
    ctrl.getPreviousSplitbarContainer = function (currentSplitbar) {
      if (LayoutContainer.isSplitbar(currentSplitbar)) {
        var currentSplitbarIndex = ctrl.containers.indexOf(currentSplitbar);
        var previousSplitbarIndex = currentSplitbarIndex - 2;
        if (previousSplitbarIndex >= 0) {
          return ctrl.containers[previousSplitbarIndex];
        }
        return null;
      }
      return null;
    };

    /**
     * Returns the container object of the splitbar that is after the one passed in.
     * @param currentSplitbar
     */
    ctrl.getNextSplitbarContainer = function (currentSplitbar) {
      if (LayoutContainer.isSplitbar(currentSplitbar)) {
        var currentSplitbarIndex = ctrl.containers.indexOf(currentSplitbar);
        var nextSplitbarIndex = currentSplitbarIndex + 2;
        if (currentSplitbarIndex > 0 && nextSplitbarIndex < ctrl.containers.length) {
          return ctrl.containers[nextSplitbarIndex];
        }
        return null;
      }
      return null;
    };

    /**
     * Checks whether the container before this one is a split bar
     * @param  {container}  container The container to check
     * @return {Boolean}    true if the element before is a splitbar, false otherwise
     */
    ctrl.hasSplitbarBefore = function (container) {
      var index = ctrl.containers.indexOf(container);
      if (1 <= index) {
        return LayoutContainer.isSplitbar(ctrl.containers[index - 1]);
      }

      return false;
    };

    /**
     * Checks whether the container after this one is a split bar
     * @param  {container}  container The container to check
     * @return {Boolean}    true if the element after is a splitbar, false otherwise
     */
    ctrl.hasSplitbarAfter = function (container) {
      var index = ctrl.containers.indexOf(container);
      if (index < ctrl.containers.length - 1) {
        return LayoutContainer.isSplitbar(ctrl.containers[index + 1]);
      }

      return false;
    };

    /**
     * Checks whether the passed in element is a ui-layout type element.
     * @param  {element}  element The element to check
     * @return {Boolean}          true if the element is a layout element, false otherwise.
     */
    ctrl.isLayoutElement = function (element) {
      return element.hasAttribute('ui-layout-container') || element.hasAttribute('ui-splitbar') || element.nodeName === 'UI-LAYOUT-CONTAINER';
    };

    /**
     * Retrieve the index of an element within it's parents context.
     * @param  {element} element The element to get the index of
     * @return {int}             The index of the element within it's parent
     */
    ctrl.indexOfElement = function (element) {
      var parent = element.parent();
      var children = parent.children();
      var containerIndex = 0;
      for (var i = 0; i < children.length; i++) {
        var child = children[i];
        if (ctrl.isLayoutElement(child)) {
          if (element[0] === children[i]) {
            return containerIndex;
          }
          containerIndex++;
        }
      }
      return -1;
    };

    return ctrl;
  }]).directive('uiLayout', ['$timeout', function ($timeout) {
    return {
      restrict: 'AE',
      controller: 'uiLayoutCtrl',
      require: ['^uiLayout', '?rememberUi'],
      link: function link(scope, element, attrs, _ref) {
        var _ref2 = slicedToArray(_ref, 2),
            uiLayoutCtrl = _ref2[0],
            rememberUiCtrl = _ref2[1];

        scope.$watch(function () {
          return element[0][uiLayoutCtrl.sizeProperties.offsetSize];
        }, function () {
          uiLayoutCtrl.calculate();
        });

        function onResize() {
          scope.$evalAsync(function () {
            uiLayoutCtrl.calculate();
          });
        }

        scope.$on(WINDOW_EVT_RESIZE, onResize);

        // enable storing sizes to localstorage
        $timeout(function () {
          if (rememberUiCtrl) {
            // attempt to reload the previous setup
            var sizes = rememberUiCtrl.retrieve();
            if (sizes && angular.isArray(sizes)) {
              _writeBackSizes(sizes);
              // force an update
              uiLayoutCtrl.calculate();
            }

            scope.$on(UI_LAYOUT_EVT_RESIZE, function (evt, method) {
              var total = element[0].getBoundingClientRect()[uiLayoutCtrl.sizeProperties.sizeProperty];

              var sizes = uiLayoutCtrl.opts.sizes.map(function (s, i) {
                var pctSize = Math.min(100, 100 * (uiLayoutCtrl.containers[i].size + 10) / total);
                return s !== 'auto' ? pctSize : s;
              });
              if (sizes.length) {
                rememberUiCtrl.store(sizes);

                // when the user makes an adjustment, we replace the current attribute sizes with
                // percentages, so window resizes work
                if (method !== 'calculate') {
                  _writeBackSizes(sizes);
                }
              }
            });
          }
        });

        // Writes the current sizes as percentages back onto the child elements
        function _writeBackSizes(sizes) {
          uiLayoutCtrl.containers.forEach(function (c, i) {
            // ignore splitter bars, auto-sized panes, and completely hidden panes
            if (i % 2 === 0 && sizes[i] !== 'auto' && sizes[i] !== 0) {
              c.element.attr('size', Math.min(100, sizes[i]) + '%');
              c.previousSize = sizes[i] + '%';
            }
          });
        }
      }
    };
  }]).directive('uiSplitbar', ['LayoutContainer', function (LayoutContainer) {
    // Get all the page.
    var htmlElement = angular.element(document.body.parentElement);

    return {
      restrict: 'EAC',
      require: '^uiLayout',
      scope: {},

      link: function link(scope, element, attrs, ctrl) {
        element.addClass('stretch');
        element.addClass('ui-splitbar');

        var animationClass = ctrl.isUsingColumnFlow ? 'animate-column' : 'animate-row';
        element.addClass(animationClass);

        scope.splitbar = LayoutContainer.Splitbar();
        scope.splitbar.element = element;

        element.on('mousedown touchstart', function handleMouseDown(e) {
          if (e.button === 0 || e.type === 'touchstart') {
            // only trigger when left mouse button is pressed:
            e.preventDefault();
            e.stopPropagation();

            ctrl.mouseDownHandler(scope.splitbar);

            htmlElement.on('mousemove touchmove', handleMouseMove);
            return false;
          }
        });

        function handleMouseMove(event) {
          scope.$apply(function () {
            return ctrl.mouseMoveHandler(event);
          });
        }

        function handleMouseUp() {
          scope.$apply(ctrl.mouseUpHandler);
          htmlElement.off('mousemove touchmove', handleMouseMove);
        }

        htmlElement.on('mouseup touchend', handleMouseUp);

        scope.$watch('splitbar.size', function (newValue) {
          element.css(ctrl.sizeProperties.sizeProperty, newValue + 'px');
        });

        scope.$watch('splitbar.' + ctrl.sizeProperties.flowProperty, function (newValue) {
          element.css(ctrl.sizeProperties.flowProperty, newValue + 'px');
        });

        //Add splitbar to layout container list
        ctrl.addContainer(scope.splitbar);

        element.on('$destroy', function () {
          ctrl.removeContainer(scope.splitbar);
          htmlElement.off('mouseup touchend', handleMouseUp);
          htmlElement.off('mousemove touchmove', handleMouseMove);
          scope.$evalAsync();
        });
      }
    };
  }]).directive('uiLayoutContainer', ['LayoutContainer', '$compile', function (LayoutContainer, $compile) {
    return {
      restrict: 'AE',
      require: '^uiLayout',
      scope: {
        resizable: '=',
        size: '@',
        minSize: '@',
        maxSize: '@'
      },

      compile: function compile() {
        return {
          pre: function pre(scope, element, attrs, ctrl) {
            scope.container = LayoutContainer.Container();
            scope.container.element = element;
            scope.container.id = element.attr('id') || null;
            scope.container.layoutId = ctrl.id;
            scope.container.isCentral = attrs.uiLayoutContainer === 'central';

            if (angular.isDefined(scope.resizable)) {
              scope.container.resizable = scope.resizable;
            }
            scope.container.size = scope.size;
            scope.container.previousSize = scope.size;
            scope.container.minSize = scope.minSize;
            scope.container.maxSize = scope.maxSize;
            ctrl.addContainer(scope.container);

            element.on('$destroy', function () {
              ctrl.removeContainer(scope.container);
              scope.$evalAsync();
            });
          },
          post: function post(scope, element, attrs, ctrl) {
            element.addClass('stretch');
            element.addClass('ui-layout-container');

            var animationClass = ctrl.isUsingColumnFlow ? 'animate-column' : 'animate-row';
            element.addClass(animationClass);

            scope.$watch('container.size', function (newValue) {
              element.css(ctrl.sizeProperties.sizeProperty, newValue + 'px');
              if (newValue === 0) {
                element.addClass('ui-layout-hidden');
              } else {
                element.removeClass('ui-layout-hidden');
              }
            });

            scope.$watch('container.' + ctrl.sizeProperties.flowProperty, function (newValue) {
              element.css(ctrl.sizeProperties.flowProperty, newValue + 'px');
            });

            //TODO: add ability to disable auto-adding a splitbar after the container
            var parent = element.parent();
            var children = parent.children();
            var index = ctrl.indexOfElement(element);
            var splitbar = angular.element('<div ui-splitbar></div>');
            if (0 < index && !ctrl.hasSplitbarBefore(scope.container)) {
              angular.element(children[index - 1]).after(splitbar);
              $compile(splitbar)(scope);
            } else if (index < children.length - 1) {
              element.after(splitbar);
              $compile(splitbar)(scope);
            }
          }
        };
      }
    };
  }]).factory('Layout', function () {
    var layouts = [];

    return {
      addLayout: function addLayout(ctrl) {
        ctrl.id = layouts.length;
        layouts.push(ctrl);
      }
    };
  }).factory('LayoutContainer', function () {
    function BaseContainer() {
      /**
       * Stores element's id if provided
       * @type {string}
       */
      this.id = null;

      /**
       * Id of the parent layout
       * @type {number}
       */
      this.layoutId = null;

      /**
       * Central container that is always resized
       * @type {boolean}
       */
      this.isCentral = false;

      /**
       * actual size of the container, which is changed on every updateDisplay
       * @type {any}
       */
      this.size = UI_LAYOUT_AUTO_SIZE;

      /**
       * cache for the last uncollapsed size
       * @type {any}
       */
      this.previousSize = null;

      this.maxSize = null;
      this.minSize = null;
      this.resizable = true;
      this.element = null;
    }

    // Splitbar container
    function SplitbarContainer() {
      this.size = 10;
      this.left = 0;
      this.top = 0;
      this.element = null;
    }

    return {
      Container: function Container(initialSize) {
        return new BaseContainer(initialSize);
      },
      Splitbar: function Splitbar() {
        return new SplitbarContainer();
      },
      isSplitbar: function isSplitbar(container) {
        return container instanceof SplitbarContainer;
      }
    };
  });

  var zoomableImageComponentModule = angular.module('Q.components.zoomable-image', [windowEventHelperModule, domHelperModule, scrollOnDragDirectiveModule]).component('zoomableImage', {
    bindings: {
      zoomDisabled: '<'
    },
    controller: 'ZoomableImageCtrl',
    template: zoomableImageTemplate,
    transclude: true
  }).controller('ZoomableImageCtrl', ['$scope', '$element', '$window', '$document', '$timeout', 'domHelper', function ZoomableImageCtrl($scope, $element, $window, $document, $timeout, domHelper) {
    var ctrl = this;

    var SCROLLBOX_CLASS = '.zoomable-image-scrollbox';
    var CONTROLS_CLASS = '.zoomable-image-controls';

    var zoomLevel = -2;

    ctrl.$onInit = function () {
      _initializeImage();
      $timeout(function () {
        // timeout ensures the DOM has finished rendering
        domHelper.whenImageLoaded($element.find('image')).then(_initializeImage);
      });

      ctrl.$onChanges = function (changes) {
        if (changes.zoomDisabled) {
          _initializeImage();
        }
      };
    };

    ctrl.expandOrContract = stopEvent(function () {
      ctrl.expanded = !ctrl.expanded;
      if (ctrl.expanded) {
        _expand();
      } else {
        _contract();
      }
    });

    ctrl.zoomOut = stopEvent(function () {
      _adjustZoomLevel(-1);
    });

    ctrl.zoom100 = stopEvent(function () {
      _adjustZoomLevel(0);
    });

    ctrl.zoomIn = stopEvent(function () {
      _adjustZoomLevel(1);
    });

    ctrl.autoZoom = stopEvent(function () {
      if (ctrl.enabled) {
        _adjustZoomLevel(zoomLevel > 1 ? -10 : 1);
      }
    });

    var lastImageUrl = void 0;
    var lastWidth = void 0;
    var listeningToEvents = false;

    function _reset() {
      ctrl.expanded = ctrl.enabled = false;
      ctrl.zoomInDisabled = ctrl.zoomOutDisabled = true;
      $element.removeClass('enabled zoomed expanded');
      // revert everything to default CSS
      $element.css('height', null);
      $element.querySelector(SCROLLBOX_CLASS).css({
        left: null,
        right: null
      });
      $element.querySelector(CONTROLS_CLASS).css({
        left: null
      });
      $element.find('img').css({
        width: null,
        height: null,
        'max-width': null
      });
    }

    function _initializeImage() {
      var $img = $element.find('img');
      var img = $img[0];

      if (!img || ctrl.zoomDisabled) {
        lastImageUrl = null;
        _reset();
        return;
      }
      if (ctrl.enabled && img.src === lastImageUrl && $element.offsetWidth() === lastWidth) {
        if (ctrl.expanded) {
          _expand();
        } else {
          _contract();
        }
        return;
      }

      _reset();

      lastWidth = $element.offsetWidth();
      lastImageUrl = img.src;

      if (!listeningToEvents) {
        listeningToEvents = true;
        $scope.$on(WINDOW_EVT_RESIZE, _initializeImage);
        $scope.$on(UI_LAYOUT_EVT_RESIZE, _initializeImage);
        $scope.$watch(function () {
          return $element[0].offsetWidth + img.src;
        }, _initializeImage);
      }

      if (img.naturalWidth > $element.offsetWidth() * 0.9) {
        // if the image is greater than 90% of the available width, enable the tool
        ctrl.enabled = true;
        $element.addClass('enabled');
        _contract();
      }
    }

    function _expand() {
      var elOffset = $element.offset();
      var maxBoundary = _maxBoundary();
      var $img = $element.find('img');
      var img = $img[0];
      if (img && img.naturalHeight < maxBoundary.height) {
        // don't bother expanding above the image's natural height
        maxBoundary.height = img.naturalHeight + 2;
      }

      // now allow the scrollbox to expand up to our max boundary
      $element.addClass('expanded');
      $element.css('height', maxBoundary.height + 'px');
      var left = maxBoundary.left - elOffset.left;
      $element.querySelector(SCROLLBOX_CLASS).css({
        left: left + 'px',
        right: elOffset.right - maxBoundary.right + 'px'
      });
      $element.querySelector(CONTROLS_CLASS).css({
        left: left + 'px'
      });

      // enable the zoom buttons by using _setZoomLevel, zoom to 1:1 by default
      _adjustZoomLevel(0, maxBoundary.width, maxBoundary.height);
      var scrollbox = $element.querySelector(SCROLLBOX_CLASS)[0];
      if (scrollbox && img) {
        $timeout(function () {
          // scroll to center
          scrollbox.scrollLeft = (img.naturalWidth - maxBoundary.width) / 2;
          scrollbox.scrollTop = (img.naturalHeight - maxBoundary.height) / 2;
        }, 200);
      }
    }

    function _contract() {
      $element.removeClass('expanded');
      var $img = $element.find('img');
      var img = $img[0];
      $element.css('height', null);
      var width = $element.offsetWidth();
      var height = $element.offsetHeight();
      if (img) {
        var ratio = img.naturalHeight / img.naturalWidth;
        height = Math.min(img.naturalHeight, width * ratio);
        // default to height of the image
        $element.css('height', height + 'px');
      }
      // revert sizing mostly to CSS
      $element.querySelector(SCROLLBOX_CLASS).css({
        left: null,
        right: null
      });
      $element.querySelector(CONTROLS_CLASS).css({
        left: null
      });
      // enable the zoom buttons by using _setZoomLevel
      _adjustZoomLevel(null, width, height);
    }

    // currently, zoom levels are defined as follows:
    // -2: fits container
    // -1: 50% between container and 1:1
    // 0: 1x
    // 1: 1.5x
    // 2: 2x
    function _adjustZoomLevel(zlAdjustment, scrollboxWidth, scrollboxHeight) {
      if (zlAdjustment === null) {
        zoomLevel = -2;
      } else if (zlAdjustment === 0) {
        zoomLevel = 0;
      } else {
        zoomLevel = Math.min(2, Math.max(-2, zoomLevel + zlAdjustment));
      }

      var $img = $element.find('img');
      var img = $img[0];
      if (!img) return;
      var scrollbox = $element.querySelector(SCROLLBOX_CLASS);
      if (!scrollboxWidth || !scrollboxHeight) {
        scrollboxWidth = scrollbox.offsetWidth();
        scrollboxHeight = scrollbox.offsetHeight();
      }
      var widthRatio = (scrollboxWidth - 2) / img.naturalWidth;
      var heightRatio = (scrollboxHeight - 2) / img.naturalHeight;
      var baseScale = Math.min(widthRatio, heightRatio);

      if (baseScale >= 1 && zoomLevel <= 0) {
        // if we're already 1:1, don't bother shrinking it down
        zoomLevel = 0;
        ctrl.zoomOutDisabled = true;
      } else {
        if (zoomLevel === -1 && baseScale > 0.6) {
          // skip zoomLevel -1 if we're pretty close images
          zoomLevel = zlAdjustment < 0 ? -2 : 0;
        }
        ctrl.zoomOutDisabled = zoomLevel <= -2;
      }
      ctrl.zoomInDisabled = zoomLevel >= 2;
      ctrl.is100 = zoomLevel === 0;
      ctrl.zoomed = !ctrl.zoomOutDisabled;
      $element.toggleClass('zoomed', ctrl.zoomed);

      var scale = baseScale;
      switch (zoomLevel) {
        case -1:
          scale = baseScale / 2 + 0.5; // 50% of fit and 1:1
          break;
        case 0:
          scale = 1;
          break;
        case 1:
          scale = 1.25;
          break;
        case 2:
          scale = 1.5;
          break;
      }
      var height = img.naturalHeight * scale;
      $img.css({
        width: img.naturalWidth * scale + 'px',
        height: height + 'px',
        'max-width': 'none'
      });
    }

    function _maxBoundary() {
      // base boundary
      var scrollParent = $element.scrollableParent();
      var left = scrollParent.offsetLeft();
      var top = scrollParent.offsetTop();
      var width = scrollParent.offsetWidth();
      if (scrollParent[0].clientWidth < width) {
        // accounts for the scrollbar automatically
        width = scrollParent[0].clientWidth;
      }
      var height = scrollParent.offsetHeight();

      var $win = angular.element($window);
      var $sidebar = $document.querySelector('.sidebar');
      var $navbar = $document.querySelector('.navbar-fixed-top');
      var visibleWidth = $win.offsetWidth() - ($sidebar.isVisible() ? $sidebar.offsetWidth() : 0);
      var visibleHeight = $win.offsetHeight() - ($navbar.isVisible() ? $navbar.offsetHeight() : 0);

      // adjust to visible boundary (excluding sidebar and navbar)
      width = Math.min(visibleWidth || width, width);
      height = Math.min(visibleHeight || height, height);

      // reduce maxBoundary size by 10px on all used sides
      left += 10;
      top += 10;
      width -= 20;
      height -= 20;
      return {
        left: left,
        top: top,
        width: width,
        height: height,
        right: left + width,
        bottom: top + height
      };
    }
  }]);

  var KNOWN_HTML_ELEMENTS = new Set('a,abbr,acronym,address,area,article,aside,b,bdi,bdo,big,blockquote,br,caption,center,cite,code,col,colgroup,dd,del,dfn,dir,div,dl,dt,em,figcaption,figure,font,footer,h1,h2,h3,h4,h5,h6,header,hgroup,hr,i,img,ins,kbd,label,li,map,mark,menu,nav,ol,p,pre,q,rp,rt,ruby,s,samp,section,small,span,strike,strong,sub,sup,table,tbody,td,tfoot,th,thead,time,tr,tt,u,ul,var,wbr'.split(','));
  var COMMENT_NODE = '!--';

  var HTML_MATCHER = /<\/?([^\s>]+)[^>]*>/gi;

  var markdownComponentModule = angular.module('Q.components.markdown', [codeHighlightComponentModule, zoomableImageComponentModule])

  // The start of this code was copied from angular-marked

  .provider('marked', function () {
    var self = this;

    self.defaults = {
      gfm: true,
      tables: true,
      breaks: true,
      smartLists: true
    };

    self.setOptions = function (opts) {
      angular.extend(self.defaults, opts);
    };

    self.$get = ['$window', 'highlightCode', function ($window, highlightCode) {
      var m = $window.marked;

      if (!self.defaults.highlight) {
        self.defaults.highlight = highlightCode;
      }

      self.setOptions = m.setOptions;
      m.setOptions(self.defaults);

      if ($window.markedExtensions) {
        m.process = $window.markedExtensions.process;
      }

      return m;
    }];
  })

  /**
   * @docType shared
   * @category directive
   * @name markdown
   * @example
   *   <div markdown>#A Sample Header
   *---
   *##Wooohooo a subheader
   *> "Always code as if the guy who ends up maintaining your code will be a violent psychopath who knows where you live."
   *
   ** List Item A
   ** List Item B
   ** List Item C
   *     </div>
   * @description
   * Transcludes some HTML and also re-uses it in a sample code block
   * It will be good to keep all the HTML examples of the components in this directive so they can all be changed from one central location
   * @returns Directive Definition Object {object}
   */
  .directive('markdown', ['marked', '$sanitize', '$timeout', '$compile', function (marked, $sanitize, $timeout, $compile) {
    return {
      restrict: 'A',
      scope: {
        opts: '<',
        markdown: '<',
        language: '<',
        sanitize: '@',
        linkTarget: '@',
        linkRel: '@',
        afterRender: '&',
        expandable: '<',
        expanded: '<?',
        expandableInline: '<',
        maxLines: '<',
        multiLanguage: '<'
      },
      link: function link(scope, el, attrs) {
        var node = el[0];
        var lastVal = void 0;

        el.addClass('markdown');
        if (scope.expandable) {
          scope.expanded = !!scope.expanded;
          el.addClass('expandable ' + (scope.expanded ? 'expanded' : 'collapsed'));
          if (scope.expandableInline) {
            el.addClass('expandable-inline');
          }
        }
        var lastExpandedState = scope.expanded;
        var expandingPromise = void 0;
        var $expandLink = void 0;
        var expandListener = void 0;
        var toggleExpandable = function toggleExpandable(val) {
          var exp = scope.expanded = val === undefined ? !scope.expanded : val;

          if (exp === lastExpandedState) return;
          lastExpandedState = exp;

          if (expandingPromise) $timeout.cancel(expandingPromise);

          el.removeClass('expanding-init collapsing-init expanding collapsed expanded collapsed');
          el.addClass(exp ? 'expanding-init' : 'collapsing-init');
          expandingPromise = $timeout(function () {
            el.removeClass(exp ? 'expanding-init' : 'collapsing-init');
            el.addClass(exp ? 'expanding' : 'collapsing');
            expandingPromise = $timeout(function () {
              el.removeClass(exp ? 'expanding' : 'collapsing');
              el.addClass(exp ? 'expanded' : 'collapsed');

              if ($expandLink) {
                $expandLink.text(scope.expanded ? 'Less' : 'More');
              }
            }, 210);
          });
        };

        if (scope.expandable && attrs.expanded) {
          scope.$watch('expanded', toggleExpandable);
        }

        _render(scope.markdown || el.text() || '');
        if (attrs.markdown !== undefined) {
          scope.$watch('markdown', _render);
        }

        scope.$watch('language', function () {
          return _render();
        });

        function _shouldExpand(val) {
          return scope.expandable && (!scope.maxLines && val.length > 200 || el.children().length > (scope.maxLines || 2) || el[0].querySelector('img,h1,h2,h3,ul,ol,table,pre'));
        }

        function _render(val) {
          if (val || val === '') {
            lastVal = val;
          } else {
            val = lastVal || '';
          }

          var opts = scope.opts || {};
          var result = _process(val, scope.language, opts);
          var html = result.html();
          if (scope.sanitize !== 'false') {
            html = _sanitizeHTML(html);
          }
          el.html(html);

          if (scope.linkTarget !== 'false' || scope.linkRel !== 'false') {
            var anchors = el.find('a');
            if (scope.linkTarget !== 'false') {
              anchors.attr('target', scope.linkTarget || '_blank');
            }
            if (scope.linkRel !== 'false') {
              anchors.attr('rel', scope.linkRel || 'noopener');
            }
          }

          if (scope.multiLanguage) {
            _replaceLanguageBlocks(node, scope.language);
          }

          _compileZoomableImages(node, scope);

          // if we have more than 200 characters, more than two nodes, or images, make it expandable
          if (_shouldExpand(val)) {
            el.addClass('expandable');
            el.toggleClass('has-max-lines', !!scope.maxLines);
            if (scope.maxLines) {
              var children = el.children().removeClass('always-show');
              for (var i = 0; i < scope.maxLines; i++) {
                children.eq(i).addClass('always-show');
              }
            }
            if (!scope.expandableInline) {
              $expandLink = angular.element('<a href class="expand-link">More</a>');
              $expandLink.on('click', function (event) {
                stopEvent(event);
                toggleExpandable();
                scope.$apply();
              });
              var $p = angular.element('<p class="expand-link-wrapper">');
              $p.append($expandLink);
              el.append($p);
              $p.parent().children().eq(-2).addClass('last-markdown-node');
            } else if (!expandListener) {
              expandListener = function expandListener(event) {
                if (event.target && event.target.nodeName === 'A') return;
                stopEvent(event);
                toggleExpandable();
                scope.$apply();
              };
              el.on('click touch', expandListener);
            }
            toggleExpandable(scope.expanded);
          } else {
            $expandLink = null;
            el.removeClass('expandable');
            toggleExpandable(false);
            if (expandListener) {
              el.off('click touch', expandListener);
            }
          }

          scope.afterRender();
        }
      }
    };

    //// private functions

    /**
     * processes the markdown using the marked-extensions library, which will handle a number of details
     * such as handling @@doc tokens, if/if-not blocks, hidden non-relevant language code, and %doc blocks.
     * @param markdown
     * @param defaultLang
     * @param opts
     * @private
     */
    function _process(markdown, defaultLang, opts) {
      markdown = '' + markdown;
      if (marked.process) {
        var options = {
          marked: opts || {},
          lineNumbers: false, // we provide our own implementation instead
          language: defaultLang,
          filterLanguages: false,
          findLanguage: function findLanguage(language) {
            return languages[language] ? languages[language].id : null;
          },
          languageWrapper: function languageWrapper(code, language) {
            return '<div class="language-tabset"><div class="language-tab language-' + language + '">' + code + '</div></div>';
          }
          // NOTE: this isn't really doing anything until we add in support for loading scripts dynamically
          // via the scriptjs package. the extensions library also supports mermaid/CodeMirror.runMode - which also
          // need the scriptjs package loaded and referenced as an option before they can be used.
          // onLoaded: result => $timeout(() => result.afterRender(), 100)
        };
        var result = marked.process(marked, markdown, options);
        // make sure we include some whitespace around brs
        result.renderer.br = function () {
          return '<br>\n';
        };
        return result;
      }

      // if markedExtensions are not loaded on the page, then just render using basic markdown without
      // any additional logic.
      return {
        html: function html() {
          return marked(markdown, opts);
        }
      };
    }

    /**
     * @name ReplaceLanguageBlocks
     * @docType shared
     * @category function
     * @description
     * Replaces sequential code blocks with wrapped blocks for hiding/showing languages
     * @param node {object}
     * @param lang {string}
     * @private
     */
    function _replaceLanguageBlocks(node, lang) {
      if (!lang) return;
      // walk the tree looking for sequential pre/code blocks,
      // then convert them into a set of tabs
      var tabsToGroup = node.querySelectorAll('.language-tabset + .language-tabset');

      angular.forEach(tabsToGroup, function (tab) {
        tab.previousElementSibling.appendChild(tab.firstChild);
        tab.parentNode.removeChild(tab);
      });

      // now, in every group that has multiple tabs, remove the non-current languages
      var tabsets = node.querySelectorAll('.language-tabset');
      angular.forEach(tabsets, function (tabset) {
        if (tabset.children.length > 1) {
          angular.forEach(tabset.querySelectorAll('.language-tab'), function (tab) {
            var $tab = angular.element(tab);
            if (!$tab.hasClass('language-' + lang)) {
              tab.parentNode.removeChild(tab);
            }
          });
        }
      });
    }

    function _compileZoomableImages(node, scope) {
      if (scope.expandableInline) {
        // don't enable zoomable images for inline markdown
        return;
      }
      angular.forEach(node.querySelectorAll('img'), function (img) {
        // wrap standalone images to make them zoomable if necessary
        if (_nonTextSibling(img.previousSibling) && _nonTextSibling(img.nextSibling)) {
          $compile(angular.element(img).wrap('<zoomable-image zoom-disabled="expandable && !expanded">').parent())(scope.$new());
        }
      });
    }

    // Only returns true if the sibling is empty or a hard line break
    function _nonTextSibling(sibling) {
      return !sibling || (sibling.nodeName || '').toUpperCase() === 'BR';
    }

    function _sanitizeHTML(html) {
      // escape unknown HTML
      var escapedHTML = html.replace(HTML_MATCHER, function (match, nodeName) {
        // check for comments or known HTML elements
        if (nodeName.startsWith(COMMENT_NODE) || KNOWN_HTML_ELEMENTS.has(nodeName)) {
          return match;
        } else {
          return escapeHTML(match);
        }
      });
      // sanitize remaining HTML
      return $sanitize(escapedHTML);
    }
  }]);

  var IGNORED_ERRORS = /InvalidAccessError|SecurityError/;

  // Use to decorate a function to prevent errors.
  var tryFunctionHelperModule = angular.module('Q.helpers.try-function', []).factory('tryFunction', ['$log', function ($log) {
    return function tryFunction(fn) {
      return function () {
        try {
          return fn.apply(undefined, arguments);
        } catch (ex) {
          if (IGNORED_ERRORS.test(ex)) {
            // Log security errors as warnings instead of debug so they're visible in production
            $log.warn('[SmartIDE] Security error (expected):', ex);
          } else {
            // $log.error automatically sends to Sentry
            $log.error(ex);
          }
        }
      };
    };
  }]);

  var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

  function unwrapExports (x) {
  	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
  }

  function createCommonjsModule(fn, module) {
  	return module = { exports: {} }, fn(module, module.exports), module.exports;
  }

  var nunit = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;
      var toFiles = function toFiles(_ref) {
          var solution = _ref.solution,
              tests = _ref.tests,
              preloaded = _ref.preloaded;

          var files = [{ path: "src/Solution.cs", contents: solution, source: "solution" }, { path: "tests/Fixture.cs", contents: tests, source: "tests" }];
          if (preloaded) {
              files.push({
                  path: "src/Preloaded.cs",
                  contents: preloaded,
                  source: "preloaded"
              });
          }
          return files;
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(nunit);
  var nunit_1 = nunit.toFiles;

  var facade = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;

      var toFiles = function toFiles(ccc) {
          switch (ccc.testFramework) {
              case "nunit":
                  return (0, nunit.toFiles)(ccc);
              default:
                  throw new Error('Unknown test framework: ' + ccc.testFramework);
          }
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(facade);
  var facade_1 = facade.toFiles;

  var csharp = createCommonjsModule(function (module, exports) {

    Object.defineProperty(exports, "__esModule", { value: true });
    exports.nunit = exports.toFiles = void 0;

    Object.defineProperty(exports, "toFiles", { enumerable: true, get: function get() {
        return facade.toFiles;
      } });

    Object.defineProperty(exports, "nunit", { enumerable: true, get: function get() {
        return nunit.toFiles;
      } });
    
  });

  unwrapExports(csharp);
  var csharp_1 = csharp.nunit;
  var csharp_2 = csharp.toFiles;

  var test = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;
      var toFiles = function toFiles(_ref) {
          var solution = _ref.solution,
              tests = _ref.tests,
              preloaded = _ref.preloaded;

          var files = [{ path: "lib/solution.dart", contents: solution, source: "solution" }, { path: "test/solution_test.dart", contents: tests, source: "tests" }];
          if (preloaded) {
              files.push({
                  path: "lib/preloaded.dart",
                  contents: preloaded,
                  source: "preloaded"
              });
          }
          return files;
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(test);
  var test_1 = test.toFiles;

  var facade$2 = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;

      var toFiles = function toFiles(ccc) {
          switch (ccc.testFramework) {
              case "test":
                  return (0, test.toFiles)(ccc);
              default:
                  throw new Error('Unknown test framework: ' + ccc.testFramework);
          }
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(facade$2);
  var facade_1$1 = facade$2.toFiles;

  var dart = createCommonjsModule(function (module, exports) {

    Object.defineProperty(exports, "__esModule", { value: true });
    exports.test = exports.toFiles = void 0;

    Object.defineProperty(exports, "toFiles", { enumerable: true, get: function get() {
        return facade$2.toFiles;
      } });

    Object.defineProperty(exports, "test", { enumerable: true, get: function get() {
        return test.toFiles;
      } });
    
  });

  unwrapExports(dart);
  var dart_1 = dart.test;
  var dart_2 = dart.toFiles;

  var utils = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.getClassName = void 0;
      var getClassName = function getClassName(code, defaultClass) {
          // Check public class first because this must match the source file name and
          // a file can contain non-public classes
          var patterns = [/\bpublic\s+(?:(?:abstract|final|static)\s+)?class\s+([A-Za-z][A-Za-z\d_]*)/m, /\bpublic\s+record\s+([A-Za-z][A-Za-z\d_]*)/m, /\bpublic\s+?interface\s+([A-Za-z][A-Za-z\d_]*)/m, /\b(?:(?:abstract|final|static)\s+)?class\s+([A-Za-z][A-Za-z\d_]*)/m, /\brecord\s+([A-Za-z][A-Za-z\d_]*)/m, /\binterface\s+([A-Za-z][A-Za-z\d_]*)/m];
          var _iteratorNormalCompletion = true;
          var _didIteratorError = false;
          var _iteratorError = undefined;

          try {
              for (var _iterator = patterns[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                  var pattern = _step.value;

                  var match = code.match(pattern);
                  if (match !== null) return getPrefix(code) + match[1];
              }
          } catch (err) {
              _didIteratorError = true;
              _iteratorError = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion && _iterator.return) {
                      _iterator.return();
                  }
              } finally {
                  if (_didIteratorError) {
                      throw _iteratorError;
                  }
              }
          }

          if (defaultClass) return defaultClass;
          throw new Error("Failed to detect class name:\n" + code);
      };
      exports.getClassName = getClassName;
      var getPrefix = function getPrefix(code) {
          var m = code.match(/^\s*package\s+(\S+);/m);
          return m === null ? "" : m[1] + ".";
      };
      
  });

  unwrapExports(utils);
  var utils_1 = utils.getClassName;

  var junit = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;

      var getPath = function getPath(code, defaultClass) {
          return (0, utils.getClassName)(code, defaultClass).replace(/\./g, "/");
      };
      var toFiles = function toFiles(_ref) {
          var solution = _ref.solution,
              tests = _ref.tests,
              preloaded = _ref.preloaded;

          var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
              fallback = _ref2.fallback;

          var testsPath = getPath(tests, fallback ? "Tests" : "");
          var solutionPath = getPath(solution, fallback ? "Solution" : "");
          if (testsPath == solutionPath) {
              throw new Error("File Name Conflict: tests and solution");
          }
          var files = [{
              path: 'src/test/java/' + testsPath + '.java',
              contents: tests,
              source: "tests"
          }, {
              path: 'src/main/java/' + solutionPath + '.java',
              contents: solution,
              source: "solution"
          }];
          if (preloaded) {
              var preloadedPath = getPath(preloaded, fallback ? "Preloaded" : "");
              if (testsPath == preloadedPath) {
                  throw new Error("File Name Conflict: tests and preloaded");
              }
              if (solutionPath == preloadedPath) {
                  throw new Error("File Name Conflict: solution and preloaded");
              }
              files.push({
                  path: 'src/main/java/' + preloadedPath + '.java',
                  contents: preloaded,
                  source: "preloaded"
              });
          }
          return files;
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(junit);
  var junit_1 = junit.toFiles;

  var facade$4 = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;

      var toFiles = function toFiles(ccc, opts) {
          switch (ccc.testFramework) {
              case "junit":
              case "junit4":
              case "junit5":
              case "jqwik":
                  return (0, junit.toFiles)(ccc, opts);
              default:
                  throw new Error('Unknown test framework: ' + ccc.testFramework);
          }
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(facade$4);
  var facade_1$2 = facade$4.toFiles;

  var java = createCommonjsModule(function (module, exports) {

    Object.defineProperty(exports, "__esModule", { value: true });
    exports.junit = exports.getClassName = exports.toFiles = void 0;

    Object.defineProperty(exports, "toFiles", { enumerable: true, get: function get() {
        return facade$4.toFiles;
      } });

    Object.defineProperty(exports, "getClassName", { enumerable: true, get: function get() {
        return utils.getClassName;
      } });

    Object.defineProperty(exports, "junit", { enumerable: true, get: function get() {
        return junit.toFiles;
      } });
    
  });

  unwrapExports(java);
  var java_1 = java.junit;
  var java_2 = java.getClassName;
  var java_3 = java.toFiles;

  var mocha = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;
      // Note that CR actually concatenates these into a single file.
      var toFiles = function toFiles(_ref) {
          var solution = _ref.solution,
              tests = _ref.tests,
              preloaded = _ref.preloaded;

          var files = [{
              path: "solution.js",
              contents: solution,
              source: "solution"
          }, {
              path: "test.js",
              contents: tests,
              source: "tests"
          }];
          if (preloaded) {
              files.push({
                  path: "preloaded.js",
                  contents: preloaded,
                  source: "preloaded"
              });
          }
          return files;
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(mocha);
  var mocha_1 = mocha.toFiles;

  var jest = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;
      var toFiles = function toFiles(_ref) {
          var solution = _ref.solution,
              tests = _ref.tests,
              preloaded = _ref.preloaded;

          var files = [{ path: "src/solution.js", contents: solution, source: "solution" }, { path: "src/solution.test.js", contents: tests, source: "tests" }];
          if (preloaded) {
              files.push({
                  path: "src/preloaded.js",
                  contents: preloaded,
                  source: "preloaded"
              });
          }
          return files;
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(jest);
  var jest_1 = jest.toFiles;

  var facade$6 = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;

      var toFiles = function toFiles(ccc) {
          switch (ccc.testFramework) {
              case "mocha":
              case "mocha_bdd":
              case "mocha_tdd":
                  return (0, mocha.toFiles)(ccc);
              case "jest":
                  return (0, jest.toFiles)(ccc);
              default:
                  throw new Error('Unknown test framework: ' + ccc.testFramework);
          }
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(facade$6);
  var facade_1$3 = facade$6.toFiles;

  var javascript = createCommonjsModule(function (module, exports) {

    Object.defineProperty(exports, "__esModule", { value: true });
    exports.jest = exports.mocha = exports.toFiles = void 0;

    Object.defineProperty(exports, "toFiles", { enumerable: true, get: function get() {
        return facade$6.toFiles;
      } });

    Object.defineProperty(exports, "mocha", { enumerable: true, get: function get() {
        return mocha.toFiles;
      } });

    Object.defineProperty(exports, "jest", { enumerable: true, get: function get() {
        return jest.toFiles;
      } });
    
  });

  unwrapExports(javascript);
  var javascript_1 = javascript.jest;
  var javascript_2 = javascript.mocha;
  var javascript_3 = javascript.toFiles;

  var unittest = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;
      // Python 3.7+
      var toFiles = function toFiles(_ref) {
          var solution = _ref.solution,
              tests = _ref.tests,
              preloaded = _ref.preloaded;

          var files = [{ path: "solution.py", contents: solution, source: "solution" }, { path: "tests/test_solution.py", contents: tests, source: "tests" }];
          if (preloaded) {
              files.push({
                  path: "preloaded.py",
                  contents: preloaded,
                  source: "preloaded"
              });
          }
          return files;
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(unittest);
  var unittest_1 = unittest.toFiles;

  var facade$8 = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;

      var toFiles = function toFiles(ccc) {
          if (ccc.testFramework === "unittest") {
              return (0, unittest.toFiles)(ccc);
          }
          throw new Error('Unknown test framework: ' + ccc.testFramework);
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(facade$8);
  var facade_1$4 = facade$8.toFiles;

  var python = createCommonjsModule(function (module, exports) {

    Object.defineProperty(exports, "__esModule", { value: true });
    exports.unittest = exports.toFiles = void 0;

    Object.defineProperty(exports, "toFiles", { enumerable: true, get: function get() {
        return facade$8.toFiles;
      } });

    Object.defineProperty(exports, "unittest", { enumerable: true, get: function get() {
        return unittest.toFiles;
      } });
    
  });

  unwrapExports(python);
  var python_1 = python.unittest;
  var python_2 = python.toFiles;

  var rspec = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;
      // Note that this is for Ruby 3.x.
      // Ruby 2.x has a different mapping, and concatenates the preloaded with test helper.
      // This might work for Ruby 2.x as well because neither of them `require`s explicitly.
      var toFiles = function toFiles(_ref) {
          var solution = _ref.solution,
              tests = _ref.tests,
              preloaded = _ref.preloaded;

          var files = [{ path: "lib/solution.rb", contents: solution, source: "solution" }, { path: "spec/solution_spec.rb", contents: tests, source: "tests" }];
          var specHelper = ["require 'solution'"];
          if (preloaded) {
              files.push({
                  path: "lib/preloaded.rb",
                  contents: preloaded,
                  source: "preloaded"
              });
              specHelper.unshift("require 'preloaded'");
          }
          files.push({
              path: "spec/spec_helper.rb",
              contents: specHelper.join("\n"),
              source: "generated"
          });
          return files;
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(rspec);
  var rspec_1 = rspec.toFiles;

  var facade$10 = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;

      var toFiles = function toFiles(ccc) {
          if (ccc.testFramework === "rspec") {
              return (0, rspec.toFiles)(ccc);
          }
          throw new Error('Unknown test framework: ' + ccc.testFramework);
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(facade$10);
  var facade_1$5 = facade$10.toFiles;

  var ruby = createCommonjsModule(function (module, exports) {

    Object.defineProperty(exports, "__esModule", { value: true });
    exports.rspec = exports.toFiles = void 0;

    Object.defineProperty(exports, "toFiles", { enumerable: true, get: function get() {
        return facade$10.toFiles;
      } });

    Object.defineProperty(exports, "rspec", { enumerable: true, get: function get() {
        return rspec.toFiles;
      } });
    
  });

  unwrapExports(ruby);
  var ruby_1 = ruby.rspec;
  var ruby_2 = ruby.toFiles;

  var mocha$2 = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;
      var toFiles = function toFiles(_ref) {
          var solution = _ref.solution,
              tests = _ref.tests,
              preloaded = _ref.preloaded;

          var files = [{
              path: "solution.ts",
              contents: solution,
              source: "solution"
          }, {
              path: "test.ts",
              contents: tests,
              source: "tests"
          }];
          if (preloaded) {
              files.push({
                  path: "preloaded.ts",
                  contents: preloaded,
                  source: "preloaded"
              });
          }
          return files;
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(mocha$2);
  var mocha_1$1 = mocha$2.toFiles;

  var jest$2 = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;
      var toFiles = function toFiles(_ref) {
          var solution = _ref.solution,
              tests = _ref.tests,
              preloaded = _ref.preloaded,
              preset = _ref.preset;

          var ext = preset === "react" ? "tsx" : "ts";
          var files = [{ path: "src/solution." + ext, contents: solution, source: "solution" }, { path: "src/solution.test." + ext, contents: tests, source: "tests" }];
          if (preloaded) {
              files.push({
                  path: "src/preloaded." + ext,
                  contents: preloaded,
                  source: "preloaded"
              });
          }
          return files;
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(jest$2);
  var jest_1$1 = jest$2.toFiles;

  var facade$12 = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;

      var toFiles = function toFiles(ccc) {
          switch (ccc.testFramework) {
              case "mocha":
              case "mocha_bdd":
              case "mocha_tdd":
                  return (0, mocha$2.toFiles)(ccc);
              case "jest":
                  return (0, jest$2.toFiles)(ccc);
              default:
                  throw new Error('Unknown test framework: ' + ccc.testFramework);
          }
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(facade$12);
  var facade_1$6 = facade$12.toFiles;

  var typescript = createCommonjsModule(function (module, exports) {

    Object.defineProperty(exports, "__esModule", { value: true });
    exports.mocha = exports.jest = exports.toFiles = void 0;

    Object.defineProperty(exports, "toFiles", { enumerable: true, get: function get() {
        return facade$12.toFiles;
      } });

    Object.defineProperty(exports, "jest", { enumerable: true, get: function get() {
        return jest$2.toFiles;
      } });

    Object.defineProperty(exports, "mocha", { enumerable: true, get: function get() {
        return mocha$2.toFiles;
      } });
    
  });

  unwrapExports(typescript);
  var typescript_1 = typescript.mocha;
  var typescript_2 = typescript.jest;
  var typescript_3 = typescript.toFiles;

  var facade$14 = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      exports.toFiles = void 0;

      var mappers = {
          csharp: csharp.toFiles,
          dart: dart.toFiles,
          java: java.toFiles,
          javascript: javascript.toFiles,
          python: python.toFiles,
          ruby: ruby.toFiles,
          typescript: typescript.toFiles
      };
      var toFiles = function toFiles(ccc) {
          var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

          var language = ccc.language;
          if (!mappers.hasOwnProperty(language)) {
              throw new Error("Unknown language: " + language);
          }
          return mappers[language](ccc, opts);
      };
      exports.toFiles = toFiles;
      
  });

  unwrapExports(facade$14);
  var facade_1$7 = facade$14.toFiles;

  var cjs = createCommonjsModule(function (module, exports) {

    Object.defineProperty(exports, "__esModule", { value: true });
    exports.toFiles = void 0;

    Object.defineProperty(exports, "toFiles", { enumerable: true, get: function get() {
        return facade$14.toFiles;
      } });
    
  });

  unwrapExports(cjs);
  var cjs_1 = cjs.toFiles;

  var _WORKSPACE_STATUS_LAB;

  var WORKSPACE_STATUS = {
    DISABLED: 'disabled',
    NEW: 'new',
    CONNECTING: 'connecting',
    CONNECTION_DELAY: 'connection-delay',
    CONNECTED: 'connected',
    CONNECTION_ERROR: 'connection-error',
    CONNECTION_RETRYING: 'connection-retrying',
    CONNECTION_FAILED: 'connection-failed',
    DESTROYING: 'destroying'
  };

  var WORKSPACE_STATUS_LABELS = (_WORKSPACE_STATUS_LAB = {}, defineProperty(_WORKSPACE_STATUS_LAB, WORKSPACE_STATUS.DISABLED, 'SmartIDE Disabled'), defineProperty(_WORKSPACE_STATUS_LAB, WORKSPACE_STATUS.NEW, 'Starting SmartIDE'), defineProperty(_WORKSPACE_STATUS_LAB, WORKSPACE_STATUS.CONNECTING, 'Connecting to SmartIDE'), defineProperty(_WORKSPACE_STATUS_LAB, WORKSPACE_STATUS.CONNECTION_DELAY, 'Connecting to SmartIDE is taking longer than expected'), defineProperty(_WORKSPACE_STATUS_LAB, WORKSPACE_STATUS.CONNECTED, 'SmartIDE connected and available for code completion and inspection'), defineProperty(_WORKSPACE_STATUS_LAB, WORKSPACE_STATUS.CONNECTION_ERROR, 'Unable to connect to SmartIDE service'), defineProperty(_WORKSPACE_STATUS_LAB, WORKSPACE_STATUS.CONNECTION_RETRYING, 'SmartIDE service was interrupted, trying to reconnect'), defineProperty(_WORKSPACE_STATUS_LAB, WORKSPACE_STATUS.CONNECTION_FAILED, 'SmartIDE service cannot connect. Please restart the service.'), defineProperty(_WORKSPACE_STATUS_LAB, WORKSPACE_STATUS.DESTROYING, 'Shutting down the SmartIDE service'), _WORKSPACE_STATUS_LAB);

  /**
   * Simple mapping of classic code challenge fields to files
   * @param {string} field - Field to map
   * @param {string} language - Language being used
   * @param {string?} testFramework - Framework being used (for `testCases` and `exampleTestCases` only)
   * @returns {string} Language Server filename for a given
   */
  function mapClassicCodeFieldToPath(field, language, testFramework) {
    if (/testcases/i.test(field)) {
      language = languages.frameworkLanguage(language, testFramework);
    }
    var lang = languages.getLang(language) || languages.Text;
    return field + '.' + lang.ext[0];
  }

  var classFilePathMap = {
    code: 'solution',
    testCases: 'tests',
    setup: 'solution',
    preloaded: 'preloaded',
    exampleTestCases: 'tests',
    solution: 'solution'
  };

  /**
   * Sets the paths on classic code files. If we can map them using the ccc-files method, we use that. Otherwise,
   * we revert to a simple "field.ext" format.
   *
   * @param {Array<CodeFile>} files - Array of files to be updated
   * @param {string} language - current language
   * @param {string} testFramework - current test framework
   * @param {string?} languageVersion - optional language version
   * @param {string?} preset - optional runner preset
   * @param {Object?} $log - optional AngularJS $log service for debugging
   *
   * @returns {Array<{file:*, oldPath:string}>} Array of renamed files
   */
  function setClassicFilePaths(_ref) {
    var files = _ref.files,
        language = _ref.language,
        testFramework = _ref.testFramework,
        languageVersion = _ref.languageVersion,
        preset = _ref.preset,
        $log = _ref.$log;

    // arguments passed into the ccc-files#toFiles method
    var toFilesArgs = {
      language: language,
      languageVersion: languageVersion,
      testFramework: testFramework,
      preset: preset
    };
    // makes it easier to trace the files back to the field
    var fieldToFile = {};
    files.forEach(function (file) {
      file._oldPath = file.path;
      // reset the path
      file.path = null;
      var mappedField = classFilePathMap[file.field];
      if (mappedField) {
        toFilesArgs[mappedField] = file.contents;
        fieldToFile[mappedField] = file;
      }
    });

    if (language && testFramework) {
      try {
        var mappedFiles = cjs_1(toFilesArgs);
        mappedFiles.forEach(function (mf) {
          // if we get mapped files back, store the path back on the file
          if (fieldToFile[mf.source]) {
            fieldToFile[mf.source].path = mf.path;
          }
        });
      } catch (e) {
        // Some errors are expected (e.g., unsupported language/framework combos), but log for debugging
        if ($log && $log.debug) {
          $log.debug('[SmartIDE] File path mapping failed, using fallback paths:', {
            language: language,
            testFramework: testFramework,
            languageVersion: languageVersion,
            preset: preset,
            error: e.message || String(e)
          });
        }
      }
    }

    var renamedFiles = [];

    files.forEach(function (file) {
      if (!file.path) {
        // if a file didn't get a path mapped, we just revert to the simple classic file path
        file.path = mapClassicCodeFieldToPath(file.field, language, testFramework);
      }

      if (file._oldPath !== file.path) {
        // track any files that got renamed
        renamedFiles.push({
          file: file,
          oldPath: file._oldPath
        });
      }
      delete file._oldPath;
    });

    return renamedFiles;
  }

  var CONNECTING_WAIT_ERROR = 120000; // only wait 120s for a server to connect
  var CONNECTING_WAIT_DELAY = 30000; // at 30s note there's a delay

  var DEFAULT_PROXY_READY_TIMEOUT_MS = 120000;
  var AUTOCOMPLETE_DURATION_CAP_MS = CONNECTING_WAIT_ERROR;
  // Max time to wait for prewarm to complete before returning the proxy.
  // Slow LSes (OmniSharp ~3-5s, JDT ~4s) need time to parse the project.
  // If prewarm exceeds this, the proxy is returned anyway — the LS will
  // still be loading but the user won't be blocked indefinitely.
  var PREWARM_AWAIT_TIMEOUT_MS = 8000;

  // Retry policy for the `language_server` create call. A single 500 from that
  // endpoint used to end SmartIDE for the whole session (QUAL-2118), so we retry —
  // but the assessment timer keeps running while we back off, which is exactly the
  // harm that was reported, so the total added delay is tightly capped.
  var PROXY_CREATE_MAX_RETRIES = 3;
  var PROXY_CREATE_RETRY_BASE_MS = 1000;
  var PROXY_CREATE_RETRY_BUDGET_MS = 10000;

  // Deadline for one request to the ephemeron API. `fetch` has no timeout of its
  // own, and `DEFAULT_PROXY_READY_TIMEOUT_MS` is only checked between polls, so
  // without this a single unanswered request runs past every budget we have —
  // a candidate lost 85s to exactly one of them (QUAL-2250).
  var EPHEMERON_REQUEST_TIMEOUT_MS = 10000;
  // Pace between polls of the pod-ready endpoint.
  var PROXY_POLL_INTERVAL_MS = 1000;
  // Consecutive poll failures ridden out before giving up. The create call was
  // answered seconds earlier, so a single blip says nothing about the pod; a run
  // of them says the proxy is unreachable.
  var PROXY_POLL_MAX_NETWORK_FAILURES = 3;

  // Sentry performance helpers for LSP observability
  var sentryBreadcrumb = function sentryBreadcrumb(message, data, level) {
    if (window.Sentry) {
      window.Sentry.addBreadcrumb({
        category: 'smartide.lsp',
        message: message,
        data: data,
        level: level || 'info'
      });
    }
  };

  var sentryConfigureScope = function sentryConfigureScope(cb) {
    if (window.Sentry && window.Sentry.configureScope) {
      try {
        window.Sentry.configureScope(cb);
      } catch (e) {
        // ignore
      }
    }
  };

  var sentrySetSmartIdeContext = function sentrySetSmartIdeContext(context) {
    sentryConfigureScope(function (scope) {
      if (!scope || !context) return;

      // Tags: indexed + good for filtering/aggregation.
      if (context.smartIdeSessionId) scope.setTag('smartide.session_id', context.smartIdeSessionId);
      if (context.proxyId) scope.setTag('smartide.proxy_id', context.proxyId);
      if (context.proxyHost) scope.setTag('smartide.proxy_host', context.proxyHost);
      if (context.preset) scope.setTag('smartide.preset', context.preset);
      if (context.serverId) scope.setTag('smartide.server_id', context.serverId);
      if (context.challengeId) scope.setTag('challenge_id', context.challengeId);
      if (context.solutionId) scope.setTag('solution_id', context.solutionId);

      // Extras: helpful but not always indexed.
      scope.setExtra('smartide', Object.assign({}, context));
    });
  };

  var sentryClearSmartIdeContext = function sentryClearSmartIdeContext() {
    sentryConfigureScope(function (scope) {
      if (!scope) return;
      scope.setTag('smartide.session_id', null);
      scope.setTag('smartide.proxy_id', null);
      scope.setTag('smartide.proxy_host', null);
      scope.setTag('smartide.preset', null);
      scope.setTag('smartide.server_id', null);
      scope.setTag('challenge_id', null);
      scope.setTag('solution_id', null);
      scope.setExtra('smartide', null);
    });
  };

  var generateSmartIdeSessionId = function generateSmartIdeSessionId() {
    return [Date.now().toString(36), Math.random().toString(36).slice(2, 10)].join('-');
  };

  // --- SmartIDE: time to first autocomplete (typing -> first hint shown) ---
  // We instrument CodeMirror at runtime to avoid depending on CodeMirrorWorkspace internals.
  // 1) When the user first types in an editor, we record a timestamp on the CodeMirror instance.
  // 2) When CodeMirror shows the first completion popup (showHint), we record the duration.
  //
  // NOTE: This is intentionally a breadcrumb-style metric (via sentryMeasure) because the app
  // already ships Sentry and we want a low-friction, low-risk implementation.

  var isLikelyUserTypingChange = function isLikelyUserTypingChange(changeObj) {
    // CodeMirror change origins we want to treat as "user started typing".
    // We intentionally exclude setValue and other programmatic updates.
    if (!changeObj || !changeObj.origin) return false;
    var origin = changeObj.origin;
    if (origin === 'setValue') return false;
    return origin === '+input' || origin === 'paste';
  };

  var TYPING_BLOCK_IDLE_MS = 1200;

  var attachSmartIdeFirstTypingTracker = function attachSmartIdeFirstTypingTracker(codemirror, context, $interval, $timeout) {
    if (!codemirror || codemirror.__smartIdeFirstTypingTrackerAttached) return;

    // Keep the context lean; this gets attached to the CodeMirror instance.
    codemirror.__smartIdeFirstTypingTrackerAttached = true;
    codemirror.__smartIdeAutocompleteContext = context || {};
    codemirror.__smartIdeFirstUserTypingAt = null;
    codemirror.__smartIdeFirstAutocompleteRecorded = false;
    codemirror.__smartIdeFirstAutocompleteTimeoutRecorded = false;
    codemirror.__smartIdeCurrentTypingBlockStartedAt = null;
    codemirror.__smartIdeLastTypingAt = null;
    codemirror.__smartIdeTypingVelocityWindowStartedAt = null;
    codemirror.__smartIdeTypingVelocityWindowCount = 0;
    codemirror.__smartIdeActivePollInterval = null;
    codemirror.__smartIdeActivePollTimeout = null;

    codemirror.on('change', function (_cm, changeObj) {
      if (!isLikelyUserTypingChange(changeObj)) return;

      var nowAt = Date.now();
      var ctx = codemirror.__smartIdeAutocompleteContext || {};
      var workspaceState = ctx.workspaceState;

      if (!codemirror.__smartIdeFirstUserTypingAt) {
        codemirror.__smartIdeFirstUserTypingAt = nowAt;

        if (workspaceState && !workspaceState.firstTypingRecorded && workspaceState.startTime) {
          workspaceState.firstTypingRecorded = true;
          trackPerformance('smartide.time_to_first_typing', {
            duration_ms: nowAt - workspaceState.startTime,
            preset: ctx.preset,
            language: ctx.language,
            session_id: ctx.smartIdeSessionId,
            challenge_id: ctx.challengeId,
            solution_id: ctx.solutionId,
            assessment_id: ctx.assessmentId
          });
        }

        sentryBreadcrumb('SmartIDE first user typing detected', {
          filePath: context && context.filePath,
          language: context && context.language,
          languageVersion: context && context.languageVersion,
          preset: context && context.preset,
          challengeId: context && context.challengeId,
          solutionId: context && context.solutionId
        });
      }

      if (!codemirror.__smartIdeCurrentTypingBlockStartedAt || !codemirror.__smartIdeLastTypingAt || nowAt - codemirror.__smartIdeLastTypingAt > TYPING_BLOCK_IDLE_MS) {
        codemirror.__smartIdeCurrentTypingBlockStartedAt = nowAt;
      }
      codemirror.__smartIdeLastTypingAt = nowAt;

      if (!codemirror.__smartIdeTypingVelocityWindowStartedAt) {
        codemirror.__smartIdeTypingVelocityWindowStartedAt = nowAt;
      }
      codemirror.__smartIdeTypingVelocityWindowCount += 1;

      var typingVelocityElapsedMs = nowAt - codemirror.__smartIdeTypingVelocityWindowStartedAt;
      if (typingVelocityElapsedMs >= 1000) {
        trackPerformance('smartide.key_typing_velocity', {
          value: codemirror.__smartIdeTypingVelocityWindowCount * 1000 / typingVelocityElapsedMs,
          preset: ctx.preset,
          language: ctx.language,
          session_id: ctx.smartIdeSessionId,
          challenge_id: ctx.challengeId,
          solution_id: ctx.solutionId,
          assessment_id: ctx.assessmentId
        });
        codemirror.__smartIdeTypingVelocityWindowStartedAt = nowAt;
        codemirror.__smartIdeTypingVelocityWindowCount = 0;
      }
    });

    codemirror.on('blur', function () {
      codemirror.__smartIdeCurrentTypingBlockStartedAt = null;
      codemirror.__smartIdeLastTypingAt = null;
    });

    // Per-instance keyup listener — polls cm.state.completionActive to detect when LSP
    // completions appear. We cannot patch window.CodeMirror.showHint because
    // CodeMirrorWorkspace uses its own bundled CodeMirror reference (not window.CodeMirror),
    // so the global showHint patch never intercepts LSP-driven completions.
    codemirror.on('keyup', function (cm) {
      if (!cm.__smartIdeCurrentTypingBlockStartedAt) return;

      // Cancel any existing poll+timeout from the previous keyup before starting new ones.
      // Without this, every keyup accumulates a new interval — if the user types N characters
      // before completions appear, N intervals all fire simultaneously and emit N duplicate
      // metrics, many with inflated durations anchored to the start of the typing block.
      if (cm.__smartIdeActivePollInterval) {
        $interval.cancel(cm.__smartIdeActivePollInterval);
        cm.__smartIdeActivePollInterval = null;
      }
      if (cm.__smartIdeActivePollTimeout) {
        $timeout.cancel(cm.__smartIdeActivePollTimeout);
        cm.__smartIdeActivePollTimeout = null;
      }

      var typingBlockStartedAt = cm.__smartIdeCurrentTypingBlockStartedAt;
      // Capture the time of this specific keyup so that autocomplete.general measures
      // "time from last keystroke to completion popup", not "time from block start".
      var keyupAt = Date.now();

      cm.__smartIdeActivePollInterval = $interval(function () {
        try {
          if (!(cm.state && cm.state.completionActive)) return;

          // Completions are now visible — stop polling and record metrics.
          $interval.cancel(cm.__smartIdeActivePollInterval);
          cm.__smartIdeActivePollInterval = null;

          var ctx = cm.__smartIdeAutocompleteContext || {};
          var autocompleteDurationMs = Date.now() - keyupAt;
          var cappedAutocompleteDurationMs = Math.min(autocompleteDurationMs, AUTOCOMPLETE_DURATION_CAP_MS);

          var tabVisible = document.visibilityState !== 'hidden';

          if (!cm.__smartIdeFirstAutocompleteRecorded) {
            // Record first autocomplete separately — don't pollute the
            // general metric with the slow first-completion time.
            cm.__smartIdeFirstAutocompleteRecorded = true;

            if (typingBlockStartedAt) {
              var firstAutocompleteDurationMs = Math.min(Date.now() - typingBlockStartedAt, AUTOCOMPLETE_DURATION_CAP_MS);
              trackPerformance('smartide.time_to_first_autocomplete', {
                duration_ms: firstAutocompleteDurationMs,
                preset: ctx.preset,
                language: ctx.language,
                tab_visible: tabVisible,
                session_id: ctx.smartIdeSessionId,
                status: 'ok',
                reason: 'completion_detected',
                challenge_id: ctx.challengeId,
                solution_id: ctx.solutionId,
                assessment_id: ctx.assessmentId
              });
            }
          } else {
            // Only track general autocomplete after the first one, so the
            // metric reflects steady-state LS performance, not cold start.
            trackPerformance('smartide.autocomplete.general', {
              duration_ms: cappedAutocompleteDurationMs,
              preset: ctx.preset,
              language: ctx.language,
              tab_visible: tabVisible,
              session_id: ctx.smartIdeSessionId,
              challenge_id: ctx.challengeId,
              solution_id: ctx.solutionId,
              assessment_id: ctx.assessmentId
            });
          }

          cm.__smartIdeCurrentTypingBlockStartedAt = null;
        } catch (e) {
          // Never let observability code impact editor functionality.
          $interval.cancel(cm.__smartIdeActivePollInterval);
          cm.__smartIdeActivePollInterval = null;
        }
      }, 20);

      // Safety valve — stop polling after 15 seconds (completions should appear quickly).
      cm.__smartIdeActivePollTimeout = $timeout(function () {
        if (cm.__smartIdeActivePollInterval) {
          $interval.cancel(cm.__smartIdeActivePollInterval);
          cm.__smartIdeActivePollInterval = null;
        }
        cm.__smartIdeActivePollTimeout = null;

        if (!cm.__smartIdeCurrentTypingBlockStartedAt) return;
        if (cm.__smartIdeCurrentTypingBlockStartedAt !== typingBlockStartedAt) {
          return;
        }
        if (cm.state && cm.state.completionActive) return;

        var timeoutCtx = cm.__smartIdeAutocompleteContext || {};
        trackPerformance('smartide.autocomplete.timeout', {
          preset: timeoutCtx.preset,
          language: timeoutCtx.language,
          session_id: timeoutCtx.smartIdeSessionId,
          status: 'timeout',
          reason: 'no_completion_active',
          challenge_id: timeoutCtx.challengeId,
          solution_id: timeoutCtx.solutionId,
          assessment_id: timeoutCtx.assessmentId
        });

        if (cm.__smartIdeFirstAutocompleteRecorded || cm.__smartIdeFirstAutocompleteTimeoutRecorded) {
          return;
        }

        cm.__smartIdeFirstAutocompleteTimeoutRecorded = true;
        var timeoutDurationMs = Math.min(Date.now() - typingBlockStartedAt, AUTOCOMPLETE_DURATION_CAP_MS);

        trackPerformance('smartide.first_autocomplete_timeout', {
          duration_ms: timeoutDurationMs,
          preset: timeoutCtx.preset,
          language: timeoutCtx.language,
          session_id: timeoutCtx.smartIdeSessionId,
          status: 'timeout',
          reason: 'no_completion_active',
          challenge_id: timeoutCtx.challengeId,
          solution_id: timeoutCtx.solutionId,
          assessment_id: timeoutCtx.assessmentId
        });
      }, 15000);
    });
  };

  // Sends a performance metric to the Rails backend, which fans it out to:
  //   - StatsD  → Datadog dashboard widgets (avg, p95, count, breakdown by preset/language)
  //   - Structured log → Datadog Log Management with full context (userId, challengeId, etc.)
  //
  // Fire-and-forget: errors are swallowed so broken telemetry never affects the editor.
  // High-cardinality IDs (challengeId, solutionId, assessmentId) are included in the
  // POST body but the Rails controller routes them to logs only — never to StatsD tags.
  function trackPerformance(event, data) {
    try {
      if (!window.fetch) return;
      window.fetch(appConfig.apiRoot + 'lsp_telemetry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify(Object.assign({ event: event }, data || {}))
      }).catch(function () {});
    } catch (e) {
      // Never let telemetry break the editor.
    }
  }

  function sentryMeasure(name, durationMs, data) {
    if (window.Sentry) {
      window.Sentry.addBreadcrumb({
        category: 'smartide.performance',
        message: name,
        data: Object.assign({}, data || {}, { durationMs: durationMs }),
        level: durationMs > 10000 ? 'warning' : 'info'
      });
      // Also capture as a Sentry event if it's abnormally slow
      if (durationMs > 30000) {
        window.Sentry.captureMessage('[SmartIDE] Slow LSP operation: ' + name + ' (' + durationMs + 'ms)', {
          level: 'warning',
          extra: Object.assign({}, data || {}, { durationMs: durationMs })
        });
      }
    }
  }

  // we can get away with async/await here because this service doesn't interact with AngularJS
  // Normally we'd have to convert this to an AngularJS component, and also convert it to use Promises.
  // This class is not currently exported on purpose (we don't want it used outside this factory)
  // DO NOT USE ASYNC/AWAIT WITH ANGULARJS

  var now = function now() {
    return Math.floor(Date.now() / 1000);
  };
  // eslint-disable-next-line angular/timeout-service
  var delay = function delay(ms) {
    return new Promise(function (resolve) {
      return setTimeout(resolve, ms);
    });
  };

  /**
   * Whether a failed `create()` call is worth trying again.
   *
   * Only server-side faults and network blips can succeed on a retry. A 4xx will
   * fail identically, and a `SmartIDEProxyWaitTimeout` has already burned
   * `DEFAULT_PROXY_READY_TIMEOUT_MS` of the candidate's time.
   *
   * @param {Error?} err - Error thrown by `create()`
   * @returns {boolean} True if the call should be retried
   */
  function isRetryableProxyCreateError(err) {
    if (!err) return false;
    if (isNetworkLevelError(err)) return true;
    if (err.name !== 'SmartIDEProxyCreateError') return false;
    var status = err.details && err.details.statusCode;
    return typeof status === 'number' && status >= 500;
  }

  /**
   * Whether a request failed without ever coming back with an answer.
   *
   * `TypeError` is how `fetch` reports a request that never reached the server;
   * `AbortError` is our own `EPHEMERON_REQUEST_TIMEOUT_MS` firing. Neither tells
   * us anything about the state of what we asked about, so both are worth asking
   * again. Anything else is a real answer, to act on rather than retry.
   *
   * @param {Error?} err - Error thrown by the request
   * @returns {boolean} True if the request is worth retrying
   */
  function isNetworkLevelError(err) {
    if (!err) return false;
    return err.name === 'TypeError' || err.name === 'AbortError';
  }

  /**
   * `fetch` with a deadline.
   *
   * A bare `fetch` has no timeout: a request that opens a connection and then
   * goes unanswered hangs for as long as the OS keeps retransmitting.
   *
   * @param {string} url - Request URL
   * @param {Object} options - `fetch` options
   * @param {number?} timeoutMs - Abort the request after this long
   * @param {Function?} fetchFn - Injectable for tests
   * @returns {Promise} Resolves with the Response, rejects with `AbortError` on timeout
   */
  var fetchWithTimeout = function () {
    var _ref2 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee(url, options) {
      var timeoutMs = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : EPHEMERON_REQUEST_TIMEOUT_MS;
      var fetchFn = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : fetch;
      var controller, timer;
      return regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              controller = new AbortController();
              // eslint-disable-next-line angular/timeout-service

              timer = setTimeout(function () {
                return controller.abort();
              }, timeoutMs);
              _context.prev = 2;
              _context.next = 5;
              return fetchFn(url, Object.assign({}, options, { signal: controller.signal }));

            case 5:
              return _context.abrupt('return', _context.sent);

            case 6:
              _context.prev = 6;

              clearTimeout(timer);
              return _context.finish(6);

            case 9:
            case 'end':
              return _context.stop();
          }
        }
      }, _callee, this, [[2,, 6, 9]]);
    }));

    return function fetchWithTimeout(_x3, _x4) {
      return _ref2.apply(this, arguments);
    };
  }();

  /**
   * Exponential backoff with +/-25% jitter, so a degraded proxy pool isn't
   * stampeded by every candidate retrying in lockstep.
   *
   * @param {number} attempt - Zero-based attempt index
   * @param {Function?} random - Source of randomness, injectable for tests
   * @returns {number} Delay in milliseconds
   */
  function proxyCreateRetryDelay(attempt) {
    var random = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Math.random;

    var base = PROXY_CREATE_RETRY_BASE_MS * Math.pow(2, attempt);
    return Math.round(base * (0.75 + random() * 0.5));
  }

  /**
   * Runs `create` until it succeeds, the error is not retryable, or the retry
   * budget is spent. Rethrows the last error when it gives up.
   *
   * @param {Function} create - Returns a Promise for the created proxy
   * @param {Object?} options - Overrides, all injectable for tests
   * @param {number?} options.maxRetries - Retries after the initial attempt
   * @param {number?} options.budgetMs - Ceiling on total time spent sleeping
   * @param {Function?} options.sleep - Delay function
   * @param {Function?} options.random - Source of randomness for jitter
   * @param {Function?} options.onRetry - Called as (err, attemptNumber, waitMs)
   * @returns {Promise} Resolves with whatever `create` resolved with
   */
  var retryProxyCreate = function () {
    var _ref3 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee2(create) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      var _options$maxRetries, maxRetries, _options$budgetMs, budgetMs, _options$sleep, sleep, _options$random, random, _options$onRetry, onRetry, spentMs, attempt, waitMs;

      return regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              _options$maxRetries = options.maxRetries, maxRetries = _options$maxRetries === undefined ? PROXY_CREATE_MAX_RETRIES : _options$maxRetries, _options$budgetMs = options.budgetMs, budgetMs = _options$budgetMs === undefined ? PROXY_CREATE_RETRY_BUDGET_MS : _options$budgetMs, _options$sleep = options.sleep, sleep = _options$sleep === undefined ? delay : _options$sleep, _options$random = options.random, random = _options$random === undefined ? Math.random : _options$random, _options$onRetry = options.onRetry, onRetry = _options$onRetry === undefined ? noop : _options$onRetry;
              spentMs = 0;
              attempt = 0;

            case 3:
              _context2.prev = 3;
              _context2.next = 6;
              return create(attempt);

            case 6:
              return _context2.abrupt('return', _context2.sent);

            case 9:
              _context2.prev = 9;
              _context2.t0 = _context2['catch'](3);
              waitMs = proxyCreateRetryDelay(attempt, random);

              if (!(attempt >= maxRetries || spentMs + waitMs > budgetMs || !isRetryableProxyCreateError(_context2.t0))) {
                _context2.next = 14;
                break;
              }

              throw _context2.t0;

            case 14:
              onRetry(_context2.t0, attempt + 1, waitMs);
              spentMs += waitMs;
              _context2.next = 18;
              return sleep(waitMs);

            case 18:
              attempt++;
              _context2.next = 3;
              break;

            case 21:
            case 'end':
              return _context2.stop();
          }
        }
      }, _callee2, this, [[3, 9]]);
    }));

    return function retryProxyCreate(_x7) {
      return _ref3.apply(this, arguments);
    };
  }();

  /**
   * Rebinds the editors a candidate has open onto a freshly respawned proxy.
   *
   * The replacement pod is a new pod with an image-default workspace: its files
   * only ever come from `writeFiles` (prewarm just opens one document over a
   * throwaway socket). So the challenge files have to be written again before the
   * editors reattach, or cross-file resolution -- imports, sibling modules,
   * `$ref/` files -- is silently dead on the new proxy.
   *
   * @param {Object} options - Injected so this stays testable outside AngularJS
   * @param {Object} options.svc - The workspace service
   * @param {Array} options.editors - `{file, codemirror}` pairs to reattach
   * @param {Array?} options.files - Current challenge files to seed the pod with
   * @param {Function} options.all - `Promise.all` equivalent (`$q.all` in the app)
   * @returns {Promise} Resolves once the editors are reattached
   */
  function reattachEditors(_ref4) {
    var svc = _ref4.svc,
        editors = _ref4.editors,
        files = _ref4.files,
        all = _ref4.all;

    // Close first: the documents still hold a handle to the dead connection.
    return all(editors.map(function (e) {
      return svc.closeFile(e.file);
    })).then(function () {
      return Array.isArray(files) && files.length ? svc.initializeFiles(files) : null;
    }).then(function () {
      return all(editors.map(function (e) {
        return svc.openEditor(e.file, e.codemirror);
      }));
    });
  }

  /**
   * Builds a `report(label, err, data)` that sends each distinct failure to
   * Sentry once instead of once per occurrence.
   *
   * A dead LSP proxy fails every subsequent file operation, and the workspace
   * catch handlers hang off `updateFile`/`saveOpenFile`, which fire per edit and
   * per autosave. Reporting unconditionally meant one Sentry event per keystroke
   * for the rest of the session: `[SmartIDE] Workspace operation failed:` billed
   * 1.67M events in the twelve days after it shipped, with single sessions
   * responsible for 28k, and it was 89% of the org's entire error quota.
   *
   * The tally is per-reporter and in-memory, so a workspace that is torn down and
   * rebuilt re-reports its first failure -- that is deliberate, since a failure
   * that survives a rebuild is worth seeing again. If the repeat volume itself
   * becomes the interesting signal, have the dispose path log the counts.
   *
   * @param {Object} log - Angular's `$log` (`$log.error` forwards to Sentry)
   * @returns {Function} `report(label, err, data)`
   */
  function createFailureReporter(log) {
    var seen = new Map();

    return function report(label, err, data) {
      // Keyed by label too, so the cleanup path is not silenced by an identical
      // message already reported from the workspace path.
      var key = label + ':' + (err.message || String(err));
      var count = (seen.get(key) || 0) + 1;
      seen.set(key, count);
      if (count > 1) return false;

      log.error(label, data, err);
      return true;
    };
  }

  // Pods pre-created from the assessment lobby (keyed by preset name).
  // Consumed on first use; one pod per preset, fire-and-forget errors fall back to normal flow.
  var _preWarmedProxies = new Map();
  var urlPath = function urlPath(url, path) {
    return url + (url.endsWith('/') ? '' : '/') + path;
  };

  var parseUidFromTokenSub = function parseUidFromTokenSub(sub) {
    if (!sub || typeof sub !== 'string') return '';
    var idx = sub.indexOf('.');
    if (idx <= 0) return '';
    return sub.slice(0, idx);
  };

  var LanguageServerController = function () {
    function LanguageServerController(_ref5) {
      var url = _ref5.url,
          authorize = _ref5.authorize,
          runConfig = _ref5.runConfig,
          $log = _ref5.$log,
          context = _ref5.context,
          initialFilesGetter = _ref5.initialFilesGetter;
      classCallCheck(this, LanguageServerController);

      this.token = '';
      this.exp = 0;
      this.url = url;
      this.authorize = authorize;
      this.$log = $log;
      this.context = context || {};
      this.startupAttemptSent = false;
      this.startupResultSent = false;
      this.respawned = false;
      this.initialFilesGetter = initialFilesGetter || null;
      this.name = [runConfig.language, runConfig.languageVersion, runConfig.preset || 'default'].join('/');
      // Create a promise that resolves with Proxy service's info.
      this.proxy = this.createProxy();
    }

    /**
     * Ensure proxy is created, and resolve with WebSocket URL for the Language Server.
     */


    createClass(LanguageServerController, [{
      key: 'start',
      value: function () {
        var _ref6 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee3(name) {
          var proxy;
          return regeneratorRuntime.wrap(function _callee3$(_context3) {
            while (1) {
              switch (_context3.prev = _context3.next) {
                case 0:
                  if (!this.proxy) this.proxy = this.createProxy();

                  _context3.next = 3;
                  return this.proxy;

                case 3:
                  proxy = _context3.sent;
                  return _context3.abrupt('return', proxy.proxyUrl + '?name=' + name);

                case 5:
                case 'end':
                  return _context3.stop();
              }
            }
          }, _callee3, this);
        }));

        function start(_x8) {
          return _ref6.apply(this, arguments);
        }

        return start;
      }()
    }, {
      key: 'createProxy',
      value: function () {
        var _ref7 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee4() {
          var _this = this;

          var proxyStartTime, _cached, proxy, id, host, tls, createStartTime, json, waitStartTime, waitResult, totalDuration, _proxy, prewarmStartTime, podReason, createReason;

          return regeneratorRuntime.wrap(function _callee4$(_context4) {
            while (1) {
              switch (_context4.prev = _context4.next) {
                case 0:
                  proxyStartTime = Date.now();

                  this.emitStartupAttempt();
                  sentryBreadcrumb('Creating LSP proxy', { preset: this.name });

                  _cached = _preWarmedProxies.get(this.name);

                  if (!_cached) {
                    _context4.next = 23;
                    break;
                  }

                  _preWarmedProxies.delete(this.name);
                  _context4.prev = 6;
                  _context4.next = 9;
                  return _cached;

                case 9:
                  proxy = _context4.sent;

                  if (!proxy) {
                    _context4.next = 18;
                    break;
                  }

                  sentryMeasure('lsp.proxy.total', Date.now() - proxyStartTime, {
                    preset: this.name,
                    proxyId: proxy.id
                  });
                  sentrySetSmartIdeContext(Object.assign({}, this.context || {}, {
                    proxyId: proxy.id,
                    preset: this.name
                  }));
                  sentryBreadcrumb('Using pre-warmed LSP proxy', {
                    preset: this.name,
                    proxyId: proxy.id
                  });
                  this.emitStartupResult('connected', { reason: 'pre_warmed' });
                  // Resolve this.proxy now so prewarm() can access proxy.id without circular deadlock,
                  // then fire prewarm v3 with the real challenge file content.
                  this.proxy = Promise.resolve(proxy);
                  Promise.race([this.prewarm(), delay(PREWARM_AWAIT_TIMEOUT_MS)]).catch(function () {});
                  return _context4.abrupt('return', proxy);

                case 18:
                  _context4.next = 23;
                  break;

                case 20:
                  _context4.prev = 20;
                  _context4.t0 = _context4['catch'](6);

                  sentryBreadcrumb('Pre-warmed proxy failed, creating new', { preset: this.name }, 'warning');

                case 23:
                  id = null;
                  host = null;
                  tls = false;
                  _context4.prev = 26;
                  createStartTime = Date.now();
                  _context4.next = 30;
                  return retryProxyCreate(function () {
                    return _this.create(_this.name);
                  }, {
                    onRetry: function onRetry(retryErr, attempt, waitMs) {
                      sentryBreadcrumb('Retrying LSP proxy creation', {
                        preset: _this.name,
                        attempt: attempt,
                        waitMs: waitMs,
                        statusCode: retryErr.details && retryErr.details.statusCode,
                        errorName: retryErr.name
                      }, 'warning');
                    }
                  });

                case 30:
                  json = _context4.sent;

                  sentryMeasure('lsp.ephemeron.create', Date.now() - createStartTime, {
                    preset: this.name,
                    proxyId: json.id
                  });

                  id = json.id;
                  waitStartTime = Date.now();
                  _context4.next = 36;
                  return this.wait(id);

                case 36:
                  waitResult = _context4.sent;

                  host = waitResult.host;
                  tls = waitResult.tls;
                  sentryMeasure('lsp.ephemeron.wait', Date.now() - waitStartTime, {
                    preset: this.name,
                    proxyId: id
                  });

                  totalDuration = Date.now() - proxyStartTime;

                  sentryMeasure('lsp.proxy.total', totalDuration, {
                    preset: this.name,
                    proxyId: id,
                    host: host
                  });
                  sentryBreadcrumb('LSP proxy created', {
                    preset: this.name,
                    proxyId: id,
                    host: host,
                    durationMs: totalDuration
                  });

                  // Make proxy metadata available on all subsequent Sentry events for this workspace.
                  sentrySetSmartIdeContext(Object.assign({}, this.context || {}, {
                    proxyId: id,
                    proxyHost: host,
                    preset: this.name
                  }));

                  _proxy = {
                    id: id,
                    proxyUrl: 'ws' + (tls ? 's' : '') + '://' + host,
                    filesUrl: 'http' + (tls ? 's' : '') + '://' + host + '/files',
                    // ping before it expires to extend its lifetime
                    // eslint-disable-next-line angular/interval-service
                    timer: setInterval(this.createPinger(id), 60 * 4 * 1000)
                  };

                  // Prewarm the language server before the client connects. We await it
                  // (with a timeout) so the LS has finished parsing the real file by the
                  // time the user starts typing. Without this, slow LSes like OmniSharp
                  // would still be loading when the first completion fires.

                  prewarmStartTime = Date.now();
                  _context4.prev = 46;
                  _context4.next = 49;
                  return Promise.race([this.prewarm(), delay(PREWARM_AWAIT_TIMEOUT_MS)]);

                case 49:
                  _context4.next = 54;
                  break;

                case 51:
                  _context4.prev = 51;
                  _context4.t1 = _context4['catch'](46);

                  // Prewarm failure is non-critical — the LS will still work,
                  // just with a slower first autocomplete.
                  sentryBreadcrumb('SmartIDE early prewarm failed', {
                    preset: this.name,
                    errorName: _context4.t1 && _context4.t1.name,
                    errorMessage: _context4.t1 && _context4.t1.message
                  }, 'warning');

                case 54:
                  sentryMeasure('lsp.prewarm.await', Date.now() - prewarmStartTime, {
                    preset: this.name,
                    proxyId: id
                  });

                  return _context4.abrupt('return', _proxy);

                case 58:
                  _context4.prev = 58;
                  _context4.t2 = _context4['catch'](26);

                  sentryBreadcrumb('LSP proxy creation failed', {
                    preset: this.name,
                    proxyId: id,
                    errorName: _context4.t2 && _context4.t2.name,
                    errorMessage: _context4.t2 && _context4.t2.message,
                    durationMs: Date.now() - proxyStartTime
                  }, 'error');
                  if (_context4.t2 && _context4.t2.name === 'SmartIDEPodStartupError') {
                    podReason = _context4.t2.details && _context4.t2.details.reason;

                    this.emitStartupResult('pod_startup_error', { reason: podReason });
                    trackPerformance('smartide.lsp.startup_error', {
                      preset: this.name,
                      language: this.context && this.context.language,
                      session_id: this.context && this.context.smartIdeSessionId,
                      status: 'error',
                      reason: podReason,
                      challenge_id: this.context && this.context.challengeId,
                      solution_id: this.context && this.context.solutionId,
                      assessment_id: this.context && this.context.assessmentId
                    });
                  } else if (_context4.t2 && _context4.t2.name === 'SmartIDEProxyWaitTimeout') {
                    this.emitStartupResult('timeout', {
                      reason: 'lsp_pod_ready_wait_timeout'
                    });
                    trackPerformance('smartide.proxy_wait_timeout', {
                      preset: this.name,
                      language: this.context && this.context.language,
                      session_id: this.context && this.context.smartIdeSessionId,
                      status: 'timeout',
                      reason: 'lsp_pod_ready_wait_timeout',
                      challenge_id: this.context && this.context.challengeId,
                      solution_id: this.context && this.context.solutionId,
                      assessment_id: this.context && this.context.assessmentId
                    });
                  } else if (_context4.t2 && _context4.t2.name === 'SmartIDEProxyCreateError') {
                    createReason = _context4.t2.details && _context4.t2.details.reason;

                    this.emitStartupResult('connection_failed', {
                      reason: 'proxy_create_error'
                    });
                    trackPerformance('smartide.lsp.startup_error', {
                      preset: this.name,
                      language: this.context && this.context.language,
                      session_id: this.context && this.context.smartIdeSessionId,
                      status: 'error',
                      reason: createReason,
                      challenge_id: this.context && this.context.challengeId,
                      solution_id: this.context && this.context.solutionId,
                      assessment_id: this.context && this.context.assessmentId
                    });
                  } else {
                    this.emitStartupResult('connection_failed', {
                      reason: 'proxy_creation_failed'
                    });
                  }
                  throw _context4.t2;

                case 63:
                  _context4.prev = 63;
                  return _context4.finish(63);

                case 65:
                case 'end':
                  return _context4.stop();
              }
            }
          }, _callee4, this, [[6, 20], [26, 58, 63, 65], [46, 51]]);
        }));

        function createProxy() {
          return _ref7.apply(this, arguments);
        }

        return createProxy;
      }()

      /**
       * Replaces a proxy that died mid-session with a fresh one.
       *
       * One-shot on purpose: if the replacement also dies, something is wrong on the
       * server side and looping would just spend the candidate's remaining time.
       *
       * @returns {Promise<Object|null>} The new proxy, or null if already respawned
       */

    }, {
      key: 'respawn',
      value: function () {
        var _ref8 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee5() {
          return regeneratorRuntime.wrap(function _callee5$(_context5) {
            while (1) {
              switch (_context5.prev = _context5.next) {
                case 0:
                  if (!this.respawned) {
                    _context5.next = 2;
                    break;
                  }

                  return _context5.abrupt('return', null);

                case 2:
                  this.respawned = true;

                  _context5.prev = 3;
                  _context5.next = 6;
                  return this.destroyProxy();

                case 6:
                  _context5.next = 11;
                  break;

                case 8:
                  _context5.prev = 8;
                  _context5.t0 = _context5['catch'](3);

                  // The old proxy is already gone — that is why we're here.
                  sentryBreadcrumb('Discarding unreachable LSP proxy', { preset: this.name, errorName: _context5.t0 && _context5.t0.name }, 'warning');

                case 11:
                  this.proxy = null;

                  sentryBreadcrumb('Respawning LSP proxy', { preset: this.name }, 'warning');
                  // Let the respawn report its own outcome rather than inheriting the
                  // already-sent result of the original startup.
                  this.startupResultSent = false;
                  this.proxy = this.createProxy();
                  return _context5.abrupt('return', this.proxy);

                case 16:
                case 'end':
                  return _context5.stop();
              }
            }
          }, _callee5, this, [[3, 8]]);
        }));

        function respawn() {
          return _ref8.apply(this, arguments);
        }

        return respawn;
      }()
    }, {
      key: 'destroyProxy',
      value: function () {
        var _ref9 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee6() {
          var proxy, id;
          return regeneratorRuntime.wrap(function _callee6$(_context6) {
            while (1) {
              switch (_context6.prev = _context6.next) {
                case 0:
                  if (this.proxy) {
                    _context6.next = 2;
                    break;
                  }

                  return _context6.abrupt('return');

                case 2:
                  _context6.next = 4;
                  return this.proxy;

                case 4:
                  proxy = _context6.sent;

                  clearInterval(proxy.timer);
                  id = proxy.id;
                  _context6.next = 9;
                  return this.auth();

                case 9:
                  _context6.next = 11;
                  return fetch(urlPath(this.url, id), {
                    method: 'DELETE',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: 'Bearer ' + this.token
                    }
                  });

                case 11:
                  this.proxy = null;

                case 12:
                case 'end':
                  return _context6.stop();
              }
            }
          }, _callee6, this);
        }));

        function destroyProxy() {
          return _ref9.apply(this, arguments);
        }

        return destroyProxy;
      }()
    }, {
      key: 'wait',
      value: function () {
        var _ref10 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee7(id, timeoutMs) {
          var json, pollCount, networkFailures, startTime, timeout, err, pollStartTime, response, errorJson, _err;

          return regeneratorRuntime.wrap(function _callee7$(_context7) {
            while (1) {
              switch (_context7.prev = _context7.next) {
                case 0:
                  // Poll until ready
                  json = void 0;
                  pollCount = 0;
                  networkFailures = 0;
                  startTime = Date.now();
                  timeout = typeof timeoutMs === 'number' ? timeoutMs : DEFAULT_PROXY_READY_TIMEOUT_MS;

                case 5:
                  if (json && json.host) {
                    _context7.next = 54;
                    break;
                  }

                  if (!(Date.now() - startTime > timeout)) {
                    _context7.next = 12;
                    break;
                  }

                  err = new Error('[SmartIDE] Timed out waiting for LSP pod to become ready');

                  err.name = 'SmartIDEProxyWaitTimeout';
                  err.details = {
                    proxyId: id,
                    preset: this.name,
                    timeoutMs: timeout,
                    pollCount: pollCount
                  };
                  if (this.$log && this.$log.error) {
                    this.$log.error('[SmartIDE] LSP pod ready wait timed out', Object.assign({}, this.context || {}, err.details), err);
                  }
                  throw err;

                case 12:
                  _context7.next = 14;
                  return this.auth();

                case 14:
                  pollStartTime = Date.now();
                  response = void 0;
                  _context7.prev = 16;
                  _context7.next = 19;
                  return fetchWithTimeout(urlPath(this.url, id), {
                    method: 'GET',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: 'Bearer ' + this.token
                    }
                  });

                case 19:
                  response = _context7.sent;
                  _context7.next = 33;
                  break;

                case 22:
                  _context7.prev = 22;
                  _context7.t0 = _context7['catch'](16);

                  if (isNetworkLevelError(_context7.t0)) {
                    _context7.next = 26;
                    break;
                  }

                  throw _context7.t0;

                case 26:
                  networkFailures++;

                  if (!(networkFailures > PROXY_POLL_MAX_NETWORK_FAILURES)) {
                    _context7.next = 29;
                    break;
                  }

                  throw _context7.t0;

                case 29:

                  sentryBreadcrumb('LSP pod poll failed, retrying', {
                    proxyId: id,
                    preset: this.name,
                    consecutiveFailures: networkFailures,
                    errorName: _context7.t0 && _context7.t0.name
                  }, 'warning');
                  // Pace from the start of the failed poll, not from now: a request that
                  // burned the full deadline has already left the pod alone long enough.
                  _context7.next = 32;
                  return delay(Math.max(0, PROXY_POLL_INTERVAL_MS - (Date.now() - pollStartTime)));

                case 32:
                  return _context7.abrupt('continue', 5);

                case 33:
                  // A poll that answered clears the streak — the budget is consecutive.
                  networkFailures = 0;

                  if (response.ok) {
                    _context7.next = 42;
                    break;
                  }

                  _context7.next = 37;
                  return response.json().catch(function () {
                    return {};
                  });

                case 37:
                  errorJson = _context7.sent;
                  _err = new Error('[SmartIDE] LSP pod failed to start');

                  _err.name = 'SmartIDEPodStartupError';
                  _err.details = {
                    proxyId: id,
                    preset: this.name,
                    statusCode: response.status,
                    reason: errorJson.reason || 'unknown',
                    message: errorJson.message || ''
                  };
                  throw _err;

                case 42:
                  _context7.next = 44;
                  return response.json();

                case 44:
                  json = _context7.sent;

                  pollCount++;

                  if (!json.host) {
                    _context7.next = 49;
                    break;
                  }

                  sentryBreadcrumb('LSP pod ready', {
                    proxyId: id,
                    pollCount: pollCount,
                    pollDurationMs: Date.now() - pollStartTime
                  });
                  return _context7.abrupt('break', 54);

                case 49:
                  if (pollCount % 5 === 0) {
                    sentryBreadcrumb('LSP pod still waiting', { proxyId: id, pollCount: pollCount }, 'warning');
                  }
                  _context7.next = 52;
                  return delay(PROXY_POLL_INTERVAL_MS);

                case 52:
                  _context7.next = 5;
                  break;

                case 54:
                  return _context7.abrupt('return', json);

                case 55:
                case 'end':
                  return _context7.stop();
              }
            }
          }, _callee7, this, [[16, 22]]);
        }));

        function wait(_x9, _x10) {
          return _ref10.apply(this, arguments);
        }

        return wait;
      }()
    }, {
      key: 'create',
      value: function () {
        var _ref11 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee8(preset) {
          var custom, context, body, response, errorJson, err;
          return regeneratorRuntime.wrap(function _callee8$(_context8) {
            while (1) {
              switch (_context8.prev = _context8.next) {
                case 0:
                  _context8.next = 2;
                  return this.auth();

                case 2:
                  custom = {};
                  context = this.context || {};

                  if (context.candidateId) custom.candidateId = String(context.candidateId);
                  if (context.teamMemberId) custom.teamMemberId = String(context.teamMemberId);
                  if (context.assessmentId) custom.assessmentId = String(context.assessmentId);
                  if (context.challengeId) custom.challengeId = String(context.challengeId);
                  if (context.solutionId) custom.solutionId = String(context.solutionId);

                  body = {
                    preset: preset,
                    lifetimeMinutes: 5
                  };

                  if (Object.keys(custom).length) {
                    body.custom = custom;
                  }

                  // Deadlined for the same reason the poll is: an unanswered POST here hangs
                  // the whole creation before the poll's protection can apply, and
                  // `retryProxyCreate`'s budget only caps the sleeping between attempts.
                  _context8.next = 13;
                  return fetchWithTimeout(this.url, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: 'Bearer ' + this.token
                    },
                    body: JSON.stringify(body)
                  });

                case 13:
                  response = _context8.sent;

                  if (response.ok) {
                    _context8.next = 22;
                    break;
                  }

                  _context8.next = 17;
                  return response.json().catch(function () {
                    return {};
                  });

                case 17:
                  errorJson = _context8.sent;
                  err = new Error('[SmartIDE] Failed to create LSP proxy');

                  err.name = 'SmartIDEProxyCreateError';
                  err.details = {
                    preset: preset,
                    statusCode: response.status,
                    reason: errorJson.reason || 'unknown',
                    message: errorJson.message || ''
                  };
                  throw err;

                case 22:
                  _context8.next = 24;
                  return response.json();

                case 24:
                  return _context8.abrupt('return', _context8.sent);

                case 25:
                case 'end':
                  return _context8.stop();
              }
            }
          }, _callee8, this);
        }));

        function create(_x11) {
          return _ref11.apply(this, arguments);
        }

        return create;
      }()
    }, {
      key: 'auth',
      value: function () {
        var _ref12 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee9() {
          var authStartTime, payload, uid;
          return regeneratorRuntime.wrap(function _callee9$(_context9) {
            while (1) {
              switch (_context9.prev = _context9.next) {
                case 0:
                  if (!(now() + 30 >= this.exp)) {
                    _context9.next = 10;
                    break;
                  }

                  authStartTime = Date.now();
                  _context9.next = 4;
                  return this.authorize();

                case 4:
                  this.token = _context9.sent;
                  payload = JSON.parse(atob(this.token.split('.')[1]));

                  this.exp = payload.exp;
                  uid = parseUidFromTokenSub(payload.sub);

                  if (uid) {
                    if (appConfig.candidateApp) {
                      if (!this.context.candidateId) this.context.candidateId = uid;
                    } else {
                      if (!this.context.teamMemberId) this.context.teamMemberId = uid;
                    }
                  }
                  sentryMeasure('lsp.auth', Date.now() - authStartTime, {
                    preset: this.name
                  });

                case 10:
                case 'end':
                  return _context9.stop();
              }
            }
          }, _callee9, this);
        }));

        function auth() {
          return _ref12.apply(this, arguments);
        }

        return auth;
      }()
    }, {
      key: 'createPinger',
      value: function createPinger(id) {
        var _this2 = this;

        var url = urlPath(this.url, id);
        return asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee10() {
          return regeneratorRuntime.wrap(function _callee10$(_context10) {
            while (1) {
              switch (_context10.prev = _context10.next) {
                case 0:
                  _context10.next = 2;
                  return _this2.auth();

                case 2:
                  _context10.next = 4;
                  return fetch(url, {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: 'Bearer ' + _this2.token
                    },
                    // Update the expiration time to 5 minutes from now.
                    body: JSON.stringify({ lifetimeMinutes: 5 })
                  });

                case 4:
                case 'end':
                  return _context10.stop();
              }
            }
          }, _callee10, _this2);
        }));
      }
    }, {
      key: 'writeFiles',
      value: function () {
        var _ref14 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee11(files) {
          var operations;
          return regeneratorRuntime.wrap(function _callee11$(_context11) {
            while (1) {
              switch (_context11.prev = _context11.next) {
                case 0:
                  operations = files.map(function (file) {
                    return {
                      op: 'write',
                      path: file.path,
                      contents: file.contents
                    };
                  });
                  return _context11.abrupt('return', this.changeRemoteFiles(operations));

                case 2:
                case 'end':
                  return _context11.stop();
              }
            }
          }, _callee11, this);
        }));

        function writeFiles(_x12) {
          return _ref14.apply(this, arguments);
        }

        return writeFiles;
      }()
    }, {
      key: 'writeFile',
      value: function () {
        var _ref15 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee12(file) {
          return regeneratorRuntime.wrap(function _callee12$(_context12) {
            while (1) {
              switch (_context12.prev = _context12.next) {
                case 0:
                  return _context12.abrupt('return', this.changeRemoteFiles([{ op: 'write', path: file.path, contents: file.contents }]));

                case 1:
                case 'end':
                  return _context12.stop();
              }
            }
          }, _callee12, this);
        }));

        function writeFile(_x13) {
          return _ref15.apply(this, arguments);
        }

        return writeFile;
      }()
    }, {
      key: 'removeFile',
      value: function () {
        var _ref16 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee13(path) {
          return regeneratorRuntime.wrap(function _callee13$(_context13) {
            while (1) {
              switch (_context13.prev = _context13.next) {
                case 0:
                  return _context13.abrupt('return', this.changeRemoteFiles([{ op: 'remove', path: path }]));

                case 1:
                case 'end':
                  return _context13.stop();
              }
            }
          }, _callee13, this);
        }));

        function removeFile(_x14) {
          return _ref16.apply(this, arguments);
        }

        return removeFile;
      }()
    }, {
      key: 'renameFile',
      value: function () {
        var _ref17 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee14(from, to) {
          return regeneratorRuntime.wrap(function _callee14$(_context14) {
            while (1) {
              switch (_context14.prev = _context14.next) {
                case 0:
                  return _context14.abrupt('return', this.changeRemoteFiles([{ op: 'rename', from: from, to: to }]));

                case 1:
                case 'end':
                  return _context14.stop();
              }
            }
          }, _callee14, this);
        }));

        function renameFile(_x15, _x16) {
          return _ref17.apply(this, arguments);
        }

        return renameFile;
      }()
    }, {
      key: 'changeRemoteFiles',
      value: function () {
        var _ref18 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee15(operations) {
          var proxy, res, json, errorDetails, error;
          return regeneratorRuntime.wrap(function _callee15$(_context15) {
            while (1) {
              switch (_context15.prev = _context15.next) {
                case 0:
                  if (this.proxy) {
                    _context15.next = 2;
                    break;
                  }

                  return _context15.abrupt('return');

                case 2:
                  _context15.next = 4;
                  return this.proxy;

                case 4:
                  proxy = _context15.sent;
                  _context15.next = 7;
                  return fetch(proxy.filesUrl, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ operations: operations })
                  });

                case 7:
                  res = _context15.sent;
                  _context15.next = 10;
                  return res.json();

                case 10:
                  json = _context15.sent;

                  if (res.ok) {
                    _context15.next = 18;
                    break;
                  }

                  // Handle file operation errors
                  errorDetails = {
                    status: res.status,
                    statusText: res.statusText,
                    operations: operations.map(function (op) {
                      return {
                        op: op.op,
                        path: op.path || op.from
                      };
                    }),
                    errors: json.errors || [],
                    languageServer: this.name
                  };
                  error = new Error('[SmartIDE] File operation failed: ' + res.statusText);

                  error.name = 'SmartIDEFileOperationError';
                  error.details = errorDetails;

                  // Log error details - will be sent to Sentry via $log.error
                  if (this.$log && this.$log.error) {
                    this.$log.error('[SmartIDE] File operation error:', errorDetails, error);
                  }

                  throw error;

                case 18:
                  return _context15.abrupt('return', json.changes);

                case 19:
                case 'end':
                  return _context15.stop();
              }
            }
          }, _callee15, this);
        }));

        function changeRemoteFiles(_x17) {
          return _ref18.apply(this, arguments);
        }

        return changeRemoteFiles;
      }()
    }, {
      key: 'prewarm',
      value: function () {
        var _ref19 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee16() {
          var proxy, primaryFile, initFiles, lang, sourceExts, exts, isSourceFile, prewarmVersion, reqBody, res, body;
          return regeneratorRuntime.wrap(function _callee16$(_context16) {
            while (1) {
              switch (_context16.prev = _context16.next) {
                case 0:
                  if (this.proxy) {
                    _context16.next = 2;
                    break;
                  }

                  return _context16.abrupt('return');

                case 2:
                  _context16.next = 4;
                  return this.proxy;

                case 4:
                  proxy = _context16.sent;

                  if (!(!proxy || !proxy.id)) {
                    _context16.next = 7;
                    break;
                  }

                  return _context16.abrupt('return');

                case 7:
                  _context16.next = 9;
                  return this.auth();

                case 9:

                  // Resolve the primary solution file for prewarm v3.
                  // Pick a source file the LS can actually analyze — skip markdown, text,
                  // config, and other files the language server won't benefit from parsing.
                  primaryFile = null;

                  if (this.initialFilesGetter) {
                    try {
                      initFiles = this.initialFilesGetter();

                      if (Array.isArray(initFiles)) {
                        lang = this.name.split('/')[0];
                        sourceExts = {
                          javascript: ['.js', '.jsx', '.mjs'],
                          typescript: ['.ts', '.tsx'],
                          python: ['.py'],
                          csharp: ['.cs'],
                          ruby: ['.rb'],
                          java: ['.java']
                        };
                        exts = sourceExts[lang] || [];

                        isSourceFile = function isSourceFile(f) {
                          if (!f || !f.path || !f.contents) return false;
                          if (f.directory || f.hidden || f.reference) return false;
                          if (!exts.length) return true;
                          var p = f.path.toLowerCase();
                          return exts.some(function (ext) {
                            return p.endsWith(ext);
                          });
                        };

                        // Prefer solution file over fixture/test file.


                        primaryFile = initFiles.find(function (f) {
                          return isSourceFile(f) && !/test|spec|fixture/i.test(f.path);
                        }) || initFiles.find(function (f) {
                          return isSourceFile(f);
                        });
                      }
                    } catch (e) {
                      // non-critical — fall back to v2 prewarm
                    }
                  }

                  prewarmVersion = primaryFile ? 'v3' : 'v2';


                  sentryBreadcrumb('SmartIDE prewarm request start', {
                    proxyId: proxy.id,
                    preset: this.name,
                    prewarm_version: prewarmVersion
                  });

                  reqBody = { name: this.name };

                  if (primaryFile) {
                    reqBody.fileUri = 'source://' + primaryFile.path;
                    // Cap at 50 KB to avoid blowing up the request.
                    reqBody.fileContent = (primaryFile.contents || '').substring(0, 50000);
                    // Derive languageId from the preset name (e.g. "typescript/5.7/default" → "typescript").
                    reqBody.fileLanguageId = this.name.split('/')[0];
                  }

                  _context16.next = 17;
                  return fetch(urlPath(this.url, proxy.id + '/prewarm'), {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: 'Bearer ' + this.token
                    },
                    body: JSON.stringify(reqBody)
                  });

                case 17:
                  res = _context16.sent;
                  body = null;
                  _context16.prev = 19;
                  _context16.next = 22;
                  return res.json();

                case 22:
                  body = _context16.sent;
                  _context16.next = 27;
                  break;

                case 25:
                  _context16.prev = 25;
                  _context16.t0 = _context16['catch'](19);

                case 27:

                  sentryBreadcrumb('SmartIDE prewarm request complete', {
                    proxyId: proxy.id,
                    statusCode: res.status,
                    warmed: body && body.warmed,
                    reason: body && body.reason,
                    prewarm_version: prewarmVersion
                  }, res.ok ? 'info' : 'warning');

                case 28:
                case 'end':
                  return _context16.stop();
              }
            }
          }, _callee16, this, [[19, 25]]);
        }));

        function prewarm() {
          return _ref19.apply(this, arguments);
        }

        return prewarm;
      }()
    }, {
      key: 'emitStartupAttempt',
      value: function emitStartupAttempt() {
        if (this.startupAttemptSent) return;
        this.startupAttemptSent = true;
        trackPerformance('smartide.lsp.startup_attempt', {
          preset: this.name,
          language: this.context && this.context.language,
          session_id: this.context && this.context.smartIdeSessionId,
          challenge_id: this.context && this.context.challengeId,
          solution_id: this.context && this.context.solutionId,
          assessment_id: this.context && this.context.assessmentId
        });
      }
    }, {
      key: 'emitStartupResult',
      value: function emitStartupResult(status, extra) {
        if (this.startupResultSent) return;
        this.startupResultSent = true;
        trackPerformance('smartide.lsp.startup_result', Object.assign({
          preset: this.name,
          language: this.context && this.context.language,
          session_id: this.context && this.context.smartIdeSessionId,
          status: status,
          challenge_id: this.context && this.context.challengeId,
          solution_id: this.context && this.context.solutionId,
          assessment_id: this.context && this.context.assessmentId
        }, extra || {}));
      }
    }]);
    return LanguageServerController;
  }();

  var codemirrorWorkspaceServiceModule = angular.module('Q.services.codemirror-workspace-service', [markdownComponentModule, tryFunctionHelperModule])

  /**
   * A service to help simplify the process of enabling codemirror editors with LSP
   */
  .factory('codemirrorWorkspaceService', ['$window', '$http', '$log', 'configService', '$q', 'marked', 'tryFunction', '$interval', '$timeout', function ($window, $http, $log, configService, $q, marked, tryFunction, $interval, $timeout) {
    return {
      /**
       * Pre-creates an LSP pod for a preset so it's warmed by the time the candidate opens the challenge.
       * Called from the assessment lobby when the candidate clicks Start/Resume.
       * Fire-and-forget — errors fall back to normal pod creation.
       *
       * @param {string} preset - e.g. 'javascript/18.x/default'
       * @param {string} teamId - Used to obtain a service_auth token
       */
      preWarmProxy: function preWarmProxy(_ref20) {
        var preset = _ref20.preset,
            teamId = _ref20.teamId,
            initialFiles = _ref20.initialFiles;

        if (_preWarmedProxies.has(preset)) return;
        var parts = preset.split('/');
        var authorize = function authorize() {
          return $http.post(appConfig.baseApiRoot + 'service_auth', {
            service: 'language_server',
            teamId: teamId
          }).then(function (resp) {
            return resp.data.token;
          });
        };
        var controller = new LanguageServerController({
          url: configService.config.languageServerUrl,
          authorize: authorize,
          runConfig: {
            language: parts[0],
            languageVersion: parts[1],
            preset: parts[2] || 'default',
            languageServers: []
          },
          $log: $log,
          context: {},
          initialFilesGetter: initialFiles && initialFiles.length ? function () {
            return initialFiles;
          } : null
        });
        _preWarmedProxies.set(preset, controller.proxy);
      },


      /**
       * Creates either a functional workspace, or a dummy model with the same method signatures
       *
       * @param {boolean} enabled - If true, returns a workspace. Otherwise returns a dummy object.
       * @param {Solution?} solution - If provided, will be used to determine language server
       * @param {Challenge?} challenge - If provided, will be used to determine language server
       * @param {Function?} initialFilesGetter - If provided, configures the unopened files automatically.
       */
      createIfEnabled: function createIfEnabled(_ref21) {
        var enabled = _ref21.enabled,
            solution = _ref21.solution,
            challenge = _ref21.challenge,
            initialFilesGetter = _ref21.initialFilesGetter;

        var svc = void 0;
        if (enabled) {
          try {
            svc = this.create({ solution: solution, challenge: challenge, initialFilesGetter: initialFilesGetter });
          } catch (err) {
            // $log.error automatically sends to Sentry
            $log.error('[SmartIDE] Failed to create workspace', {
              error: err.message || String(err),
              challengeId: challenge && challenge.id,
              language: challenge && challenge.runConfig && challenge.runConfig.language,
              languageVersion: challenge && challenge.runConfig && challenge.runConfig.languageVersion,
              hasLanguageServers: !!(challenge && challenge.runConfig && challenge.runConfig.languageServers && challenge.runConfig.languageServers.length),
              solutionId: solution && solution.id
            }, err);
          }
        }
        return svc || eventEmitter({
          enabled: false,
          status: WORKSPACE_STATUS.DISABLED,
          initializeFiles: $q.resolve,
          addFile: $q.resolve,
          updateFile: $q.resolve,
          deleteFile: $q.resolve,
          renameFile: $q.resolve,
          hideAllPopups: noop,
          enablePopups: noop,
          openEditor: noop,
          saveOpenFile: $q.resolve,
          saveAllFiles: $q.resolve,
          closeFile: $q.resolve,
          destroy: noop
        });
      },

      /**
       * Creates a workspace manager. Requires either challenge or solution & solution.challenge.
       *
       * @param {Solution?} solution - If provided, will be used to determine language server
       * @param {Challenge?} challenge - If provided, will be used to determine language server
       * @param {Function?} initialFilesGetter - If provided, configures the unopened files automatically.
       */
      // eslint-disable-next-line no-unused-vars
      create: function create() {
        var _this3 = this;

        var _ref22 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
            solution = _ref22.solution,
            _ref22$challenge = _ref22.challenge,
            challenge = _ref22$challenge === undefined ? solution && solution.challenge : _ref22$challenge,
            _ref22$initialFilesGe = _ref22.initialFilesGetter,
            initialFilesGetter = _ref22$initialFilesGe === undefined ? noop : _ref22$initialFilesGe;

        if (!$window.CodeMirrorWorkspace) {
          throw new Error('Unable to find CodeMirrorWorkspace');
        }
        if (!challenge) {
          throw new Error('Missing required challenge');
        }

        var runConfig = challenge.runConfig;
        var supportedServers = new Set(runConfig.languageServers || []);
        var _disconnectedAt = null;
        var smartIdeSessionId = generateSmartIdeSessionId();
        if (supportedServers.size === 0) {
          // REVIEW This should probably be checked in `createIfEnabled` instead.
          throw new Error('No Language Server is available');
        }
        // Shared state for this workspace — passed by reference to every openEditor call
        // so all editors report startup metrics against the same workspace start time.
        var workspaceState = {
          startTime: Date.now(),
          lspStartupRecorded: false,
          firstTypingRecorded: false
        };

        // REVIEW Creating `LanguageServerController` starts Proxy service based on `runConfig`.
        var controller = new LanguageServerController({
          url: configService.config.languageServerUrl,
          runConfig: runConfig,
          $log: $log,
          initialFilesGetter: initialFilesGetter,
          context: {
            smartIdeSessionId: smartIdeSessionId,
            assessmentId: solution && solution.assessmentId || solution && solution.assessment && solution.assessment.id,
            challengeId: challenge && challenge.id,
            solutionId: solution && solution.id,
            candidateId: appConfig.candidateApp && solution && solution.candidateId,
            teamMemberId: !appConfig.candidateApp && solution && solution.teamMemberId,
            language: runConfig && runConfig.language,
            languageVersion: runConfig && runConfig.languageVersion,
            preset: runConfig && runConfig.preset
          },
          authorize: function authorize() {
            return $http.post(appConfig.baseApiRoot + 'service_auth', {
              service: 'language_server',
              teamId: challenge.teamId
            }).then(function (resp) {
              return resp.data.token;
            });
          }
        });

        var getLanguageInfo = function getLanguageInfo(uri) {
          var lang = languages.langFromFilename(uri);
          if (!lang || !(lang.languageServerIds || []).length) return null;

          return {
            languageId: lang.languageId,
            languageServerIds: lang.languageServerIds
          };
        };

        // This error happens when we dispose (destroy) the workspace while a request is in-progress.
        // We can always ignore these errors.
        var isDisposeError = function isDisposeError(err) {
          return (/pending response rejected/i.test(err) || /request cancelled/i.test(err)
          );
        };
        // Both handlers below run per file operation, so an unrecoverable
        // proxy reports through them on every keystroke. Dedupe per distinct
        // failure for the life of this workspace.
        var reportFailure = createFailureReporter($log);

        var catchAndIgnoreDisposeErrors = function catchAndIgnoreDisposeErrors(err) {
          if (!isDisposeError(err)) {
            // Reported once per distinct error - $log.error sends to Sentry
            reportFailure('[SmartIDE] Workspace operation failed:', err, {
              error: err.message || String(err),
              language: runConfig.language,
              languageVersion: runConfig.languageVersion,
              challengeId: challenge.id,
              solutionId: solution && solution.id
            });
            return $q.reject(err);
          }
        };

        var catchLog = function catchLog(err) {
          if (!isDisposeError(err)) {
            // Reported once per distinct error - $log.error sends to Sentry
            reportFailure('[SmartIDE] Cleanup operation failed:', err, {
              error: err.message || String(err),
              language: runConfig.language,
              challengeId: challenge.id
            });
          }
          return err;
        };

        // Wrap workspace creation to catch any errors from CodeMirrorWorkspace
        var workspace = void 0;
        try {
          // Set base context immediately so any early errors get tagged.
          sentrySetSmartIdeContext({
            smartIdeSessionId: smartIdeSessionId,
            challengeId: challenge && challenge.id,
            solutionId: solution && solution.id,
            language: runConfig && runConfig.language,
            languageVersion: runConfig && runConfig.languageVersion,
            preset: runConfig && runConfig.preset
          });

          workspace = new $window.CodeMirrorWorkspace({
            // Using relative URI.
            // Proxy remaps to appropriate file path.
            rootUri: 'source://',
            getLanguageAssociation: function getLanguageAssociation(uri) {
              var info = getLanguageInfo(uri);
              if (!info) return null;

              var languageServerIds = info.languageServerIds.filter(function (id) {
                return supportedServers.has(id);
              });
              if (languageServerIds.length === 0) return null;
              return {
                languageId: info.languageId,
                languageServerIds: languageServerIds
              };
            },
            getConnectionString: function () {
              var _ref23 = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee17(id) {
                var connStartTime, url;
                return regeneratorRuntime.wrap(function _callee17$(_context17) {
                  while (1) {
                    switch (_context17.prev = _context17.next) {
                      case 0:
                        if (id) {
                          _context17.next = 2;
                          break;
                        }

                        return _context17.abrupt('return', '');

                      case 2:
                        connStartTime = Date.now();

                        sentryBreadcrumb('LSP getConnectionString', {
                          serverId: id,
                          language: runConfig.language
                        });

                        // Keep server id on the Sentry scope for any connection-related errors.
                        sentrySetSmartIdeContext({
                          smartIdeSessionId: smartIdeSessionId,
                          serverId: id,
                          challengeId: challenge && challenge.id,
                          solutionId: solution && solution.id,
                          language: runConfig && runConfig.language,
                          languageVersion: runConfig && runConfig.languageVersion,
                          preset: runConfig && runConfig.preset
                        });
                        _context17.next = 7;
                        return controller.start(id);

                      case 7:
                        url = _context17.sent;

                        sentryMeasure('lsp.getConnectionString', Date.now() - connStartTime, { serverId: id, language: runConfig.language });
                        return _context17.abrupt('return', url);

                      case 10:
                      case 'end':
                        return _context17.stop();
                    }
                  }
                }, _callee17, _this3);
              }));

              function getConnectionString(_x19) {
                return _ref23.apply(this, arguments);
              }

              return getConnectionString;
            }(),
            onDisconnected: function onDisconnected(serverId) {
              _disconnectedAt = Date.now();
              var tabHidden = document.visibilityState === 'hidden';
              sentryBreadcrumb('LSP server disconnected', {
                serverId: serverId,
                language: runConfig.language,
                languageVersion: runConfig.languageVersion,
                challengeId: challenge.id,
                tabHidden: tabHidden,
                cause: tabHidden ? 'browser_tab_background' : 'unknown'
              }, 'warning');
              $log.warn('[SmartIDE] LSP server disconnected, attempting to reconnect...', {
                serverId: serverId,
                language: runConfig.language,
                languageVersion: runConfig.languageVersion,
                challengeId: challenge.id,
                solutionId: solution && solution.id,
                tabHidden: tabHidden,
                cause: tabHidden ? 'browser_tab_background' : 'unknown'
              });
              setStatus(WORKSPACE_STATUS.CONNECTION_RETRYING);
            },
            onReconnected: function onReconnected(serverId) {
              var downtime = _disconnectedAt ? Date.now() - _disconnectedAt : 0;
              sentryMeasure('lsp.reconnection.downtime', downtime, {
                serverId: serverId,
                language: runConfig.language,
                challengeId: challenge.id
              });
              sentryBreadcrumb('LSP server reconnected', {
                serverId: serverId,
                language: runConfig.language,
                downtimeMs: downtime
              });
              _disconnectedAt = null;
              $log.info('[SmartIDE] LSP server reconnected successfully', {
                serverId: serverId,
                language: runConfig.language
              });
              setStatus();
              // Reconnected to the server and finished re-initialization.
              // Sync the files if necessary.
              // It's possible that the files are out of sync now if files were changed while the connection was down.
              // This function should return `Promise<void>` that resolves after `workspace.notifyFilesChanged(changes)`.
              // The event handlers related to the connection is reattached to the editors after this.
              return Promise.resolve();
            },
            onConnectionFailed: function onConnectionFailed(serverId) {
              var downtime = _disconnectedAt ? Date.now() - _disconnectedAt : 0;
              var tabHidden = document.visibilityState === 'hidden';
              var cause = tabHidden ? 'browser_tab_background' : 'server_or_network_error';
              sentryBreadcrumb('LSP connection failed permanently', {
                serverId: serverId,
                language: runConfig.language,
                languageVersion: runConfig.languageVersion,
                preset: runConfig.preset,
                challengeId: challenge.id,
                downtimeMs: downtime,
                tabHidden: tabHidden,
                cause: cause
              }, tabHidden ? 'warning' : 'error');
              _disconnectedAt = null;
              if (tabHidden) {
                // Tab was in background - browser throttled WebSocket pings.
                // Not a real error, log as warning to avoid Sentry noise.
                $log.warn('[SmartIDE] LSP connection lost (tab was in background)', {
                  serverId: serverId,
                  language: runConfig.language,
                  languageVersion: runConfig.languageVersion,
                  preset: runConfig.preset,
                  challengeId: challenge.id,
                  solutionId: solution && solution.id,
                  downtimeMs: downtime,
                  cause: cause
                });
              } else {
                // Real failure - $log.error automatically sends to Sentry
                $log.error('[SmartIDE] LSP server connection failed permanently', {
                  serverId: serverId,
                  language: runConfig.language,
                  languageVersion: runConfig.languageVersion,
                  preset: runConfig.preset,
                  challengeId: challenge.id,
                  solutionId: solution && solution.id,
                  downtimeMs: downtime,
                  cause: cause
                });
              }
              // Failed all the reconnection attempts — the proxy is gone.
              // Respawn it once so the candidate isn't left with a dead IDE
              // for the rest of a timed assessment.
              respawnProxy(cause);
            },
            renderMarkdown: marked,
            configs: {
              'pyright-langserver': {
                settings: {
                  python: {
                    analysis: {
                      autoSearchPaths: true,
                      useLibraryCodeForTypes: true,
                      diagnosticMode: 'openFiles'
                    }
                  }
                }
              },
              'typescript-language-server': {
                settings: {
                  diagnostics: {
                    ignoredCodes: [
                    // Ignore confusing "File is a CommonJS module; it may be converted to an ES6 module."
                    80001]
                  }
                }
              },
              'vue-language-server': {
                initOptions: {
                  typescript: {
                    // Need to pass absolute path to `tsserverlibrary` :(
                    serverPath: '/workspace/vue/node_modules/typescript/lib/tsserverlibrary.js',
                    localizedPath: null
                  },
                  languageFeatures: {
                    hover: true,
                    signatureHelp: true,
                    diagnostics: true,
                    references: true,
                    implementation: true,
                    definition: true,
                    typeDefinition: true,
                    documentHighlight: true,
                    documentLink: true,
                    completion: {
                      defaultTagNameCase: 'both',
                      defaultAttrNameCase: 'kebabCase',
                      getDocumentNameCasesRequest: false,
                      getDocumentSelectionRequest: false
                    }
                  }
                }
              }
            }
          });
        } catch (err) {
          $log.error('[SmartIDE] Failed to initialize CodeMirrorWorkspace', {
            error: err.message || String(err),
            language: runConfig.language,
            languageVersion: runConfig.languageVersion,
            challengeId: challenge.id
          }, err);
          throw err;
        }

        // Monkey-patch workspace methods to capture LSP errors that might be swallowed
        var originalOpenTextDocument = workspace.openTextDocument;
        workspace.openTextDocument = function (path, codemirror) {
          return originalOpenTextDocument.call(workspace, path, codemirror).catch(function (err) {
            // This catch will capture errors from the LSP that openTextDocument might swallow
            if (!isDisposeError(err)) {
              $log.error('[SmartIDE] LSP error in openTextDocument', {
                path: path,
                error: err.message || String(err),
                language: runConfig.language
              }, err);
            }
            throw err;
          });
        };

        /**
         * Returns a simplified file object with a mapped path (mapped for reference files).
         * The method returns false in the following circumstances
         * - the file is not present
         * - it is a directory
         * - it is missing the path
         * - it is hidden, and we have a solution
         * - it is a reference file, and we have a solution
         * @param file
         * @returns {boolean|{path: string, contents: string}}
         * @private
         */
        var _mappedFile = function _mappedFile(file) {
          if (!file || file.directory || !file.path || solution && file.hidden || solution && file.reference) {
            return false;
          }
          var path = file.path;
          if (file.reference) {
            path = '$ref/' + path;
          }
          return { contents: file.contents, path: path };
        };

        var connectingEditors = {};
        // path -> {file, codemirror}, so a respawned proxy can reattach the
        // editors the candidate actually has open.
        var openEditors = {};
        var openEditorList = function openEditorList() {
          return Object.keys(openEditors).map(function (p) {
            return openEditors[p];
          });
        };

        var svc = {
          enabled: true,
          status: WORKSPACE_STATUS.NEW,

          initializeFiles: tryFunction(function (files) {
            var mapped = files.map(_mappedFile).filter(identity);
            return $q.resolve(controller.writeFiles(mapped)).catch(catchAndIgnoreDisposeErrors).then(function (changes) {
              return changes && workspace.notifyFilesChanged(changes);
            });
          }),
          addFile: tryFunction(function (file) {
            var mappedFile = _mappedFile(file);
            if (!mappedFile) return $q.resolve();

            return $q.resolve(controller.writeFile(mappedFile)).catch(catchAndIgnoreDisposeErrors).then(function (changes) {
              return changes && workspace.notifyFilesChanged(changes);
            });
          }),
          updateFile: tryFunction(function (file) {
            var mappedFile = _mappedFile(file);
            if (!mappedFile) return $q.resolve();

            return $q.resolve(controller.writeFile(mappedFile)).catch(catchAndIgnoreDisposeErrors).then(function (changes) {
              return changes && workspace.notifyFilesChanged(changes);
            });
          }),
          deleteFile: tryFunction(function (file) {
            var mappedFile = _mappedFile(file);
            if (!mappedFile) return $q.resolve();

            return $q.resolve(controller.removeFile(mappedFile.path)).catch(catchAndIgnoreDisposeErrors).then(function (changes) {
              return changes && workspace.notifyFilesChanged(changes);
            });
          }),
          renameFile: tryFunction(function (file, oldPath) {
            var mappedFile = _mappedFile(file);
            if (!mappedFile) return $q.resolve();

            return $q.resolve(controller.renameFile(oldPath, mappedFile.path)).catch(catchAndIgnoreDisposeErrors).then(function (changes) {
              return changes && workspace.notifyFilesChanged(changes);
            });
          }),
          hideAllPopups: tryFunction(function () {
            workspace.hideAllPopups();
          }),
          enablePopups: tryFunction(function (popupsEnabled) {
            workspace.enablePopups(popupsEnabled);
          }),

          /**
           * Opens a file in an editor within the workspace
           */
          openEditor: tryFunction(function (file, codemirror) {
            var mappedFile = _mappedFile(file);
            if (!mappedFile) return;
            var path = mappedFile.path;

            openEditors[path] = { file: file, codemirror: codemirror };
            connectingEditors[path] = Date.now();
            var openStartTime = Date.now();
            sentryBreadcrumb('LSP openEditor start', {
              path: path,
              language: runConfig.language
            });

            setStatus();

            return $q.resolve(workspace.openTextDocument(path, codemirror)).then(function () {
              var openDuration = Date.now() - openStartTime;
              sentryMeasure('lsp.openEditor', openDuration, {
                path: path,
                language: runConfig.language,
                challengeId: challenge.id
              });
              sentryBreadcrumb('LSP openEditor complete', {
                path: path,
                durationMs: openDuration
              });

              if (!workspaceState.lspStartupRecorded && workspaceState.startTime) {
                workspaceState.lspStartupRecorded = true;
                controller.emitStartupResult('ok', {
                  duration_ms: Date.now() - workspaceState.startTime,
                  reason: 'open_text_document_ready'
                });
                trackPerformance('smartide.lsp.startup', {
                  duration_ms: Date.now() - workspaceState.startTime,
                  preset: runConfig.preset,
                  language: runConfig.language,
                  session_id: smartIdeSessionId,
                  status: 'ok',
                  challenge_id: challenge.id,
                  solution_id: solution && solution.id,
                  assessment_id: solution && solution.assessmentId || solution && solution.assessment && solution.assessment.id
                });
              }

              // Attach typing tracker now that the editor is opened within the workspace.
              // This enables the global showHint wrapper to record time-to-first-autocomplete.
              attachSmartIdeFirstTypingTracker(codemirror, {
                filePath: path,
                language: runConfig.language,
                languageVersion: runConfig.languageVersion,
                preset: runConfig.preset,
                challengeId: challenge.id,
                solutionId: solution && solution.id,
                assessmentId: solution && solution.assessmentId || solution && solution.assessment && solution.assessment.id,
                workspaceState: workspaceState
              }, $interval, $timeout);

              delete connectingEditors[path];
              setStatus();
            }).catch(function (err) {
              delete connectingEditors[path]; // should we keep it here?
              if (isDisposeError(err)) return;

              // $log.error automatically sends to Sentry with context
              $log.error('[SmartIDE] Failed to open editor', {
                filePath: path,
                language: runConfig.language,
                languageVersion: runConfig.languageVersion,
                errorMessage: err.message || String(err),
                challengeId: challenge.id,
                solutionId: solution && solution.id
              }, err);

              controller.emitStartupResult('connection_failed', {
                reason: 'open_text_document_failed'
              });

              // A failed proxy takes every open editor down with it. Don't
              // let those knock-on failures downgrade the terminal status
              // the candidate was just alerted about.
              if (svc.status !== WORKSPACE_STATUS.CONNECTION_FAILED) {
                setStatus(WORKSPACE_STATUS.CONNECTION_ERROR);
              }
              return $q.reject(err);
            });
          }),

          /**
           * Saves changes to an open file back to the workspace,
           * which can be used even while the file isn't open
           */
          saveOpenFile: tryFunction(function (file) {
            var mappedFile = _mappedFile(file);
            if (!mappedFile) return $q.resolve();

            return $q.resolve(workspace.saveTextDocument(mappedFile.path)).catch(catchAndIgnoreDisposeErrors);
          }),

          /**
           * saves all open files
           */
          saveAllFiles: function saveAllFiles() {
            // `saveOpenFile` expects a file, not a path -- passing a path
            // makes `_mappedFile` return false and the save a no-op.
            return $q.all(openEditorList().map(function (e) {
              return svc.saveOpenFile(e.file);
            }));
          },

          /**
           * Closes an open file (*does not save the file*
           */
          closeFile: tryFunction(function (file) {
            var mappedFile = _mappedFile(file);
            if (!mappedFile) return $q.resolve();
            var path = mappedFile.path;

            delete openEditors[path];

            return $q.resolve(workspace.closeTextDocument(path)).catch(catchLog).then(function () {
              delete connectingEditors[path];
              setStatus();
            });
          }),
          destroy: tryFunction(function () {
            // destroy all listeners
            svc.off(true);
            setStatus(WORKSPACE_STATUS.DESTROYING);
            var destroyed = controller.destroyProxy().catch(catchLog);
            // The closes now actually run (they used to be handed a path,
            // which `_mappedFile` rejected), so they have to finish before
            // dispose or every `closeTextDocument` races the teardown.
            return $q.all(openEditorList().map(function (e) {
              return svc.closeFile(e.file);
            })).then(function () {
              return workspace.dispose();
            }).catch(catchLog).then(function () {
              return destroyed;
            }).then(function () {
              setStatus(WORKSPACE_STATUS.DISABLED);
              sentryClearSmartIdeContext();
            });
          })
        };

        eventEmitter(svc);

        function setStatus(status) {
          if (!status) {
            // if there are no editors, we're connected
            status = WORKSPACE_STATUS.CONNECTED;

            var _now = Date.now();
            Object.keys(connectingEditors).forEach(function (k) {
              var delay = _now - connectingEditors[k];
              if (delay > CONNECTING_WAIT_ERROR) {
                // if it has taken too long, show an error
                status = WORKSPACE_STATUS.CONNECTION_ERROR;
              } else if (status === WORKSPACE_STATUS.CONNECTED) {
                if (delay > CONNECTING_WAIT_DELAY) {
                  // if it is taking a while for any of them, show a warning color
                  status = WORKSPACE_STATUS.CONNECTION_DELAY;
                } else {
                  // otherwise just show that we're connecting
                  status = WORKSPACE_STATUS.CONNECTING;
                }
              }
            });
          }
          var oldStatus = svc.status;
          svc.status = status;
          if (oldStatus !== status) {
            svc.emit(status);
          }
        }

        /**
         * Replaces a dead proxy, re-seeds its workspace files, and reattaches
         * the open editors to it. `controller.respawn()` is one-shot, so a
         * second failure lands the workspace in CONNECTION_FAILED for good.
         *
         * @param {string} cause - Why the connection failed, for telemetry
         * @returns {Promise} Resolves once the workspace has settled
         */
        function respawnProxy(cause) {
          var failed = function failed() {
            controller.emitStartupResult('connection_failed', {
              reason: cause
            });
            setStatus(WORKSPACE_STATUS.CONNECTION_FAILED);
          };

          var editors = openEditorList();

          setStatus(WORKSPACE_STATUS.CONNECTION_RETRYING);
          return $q.resolve(controller.respawn()).then(function (proxy) {
            if (!proxy) return failed();
            return reattachEditors({
              svc: svc,
              editors: editors,
              files: initialFilesGetter && initialFilesGetter(),
              all: function all(promises) {
                return $q.all(promises);
              }
            }).then(function () {
              return setStatus();
            });
          }).catch(failed);
        }

        controller.proxy.then(function () {
          if (initialFilesGetter) {
            svc.initializeFiles(initialFilesGetter());
          }
        }).catch(function () {
          // A pod that never came up is a dead proxy like any other, so it
          // recovers through the same one path: `respawnProxy` re-seeds the
          // replacement's files, reattaches the editors, moves the status
          // and reports the outcome. Rebuilding from `start()` instead
          // would hand the candidate a pod with none of the challenge
          // files on it, which reads as a working IDE giving wrong answers.
          // One shot, then CONNECTION_FAILED and its actionable alert.
          respawnProxy('proxy_creation_failed');
        });

        return svc;
      }
    };
  }]);

  var _TYPE_KEYS, _TYPE_NAMES, _TYPE_TOOLTIPS;

  var GLOBAL_COLLECTION = 'commons';
  var TEAM_COLLECTION = 'team';

  var INCLUDE_SOLUTIONS_REGEX = /(\s*(?:\/\/|#|--|;;|%%)\s*)@include-solution-from ([0-9a-fA-F]{24})[^\n]*/;
  var INCLUDE_SOLUTIONS_REGEX_GLOBAL = new RegExp(INCLUDE_SOLUTIONS_REGEX, 'g');

  var CODE_CHALLENGE_TYPE = 'CodeChallenge';
  var ADVANCED_CODE_CHALLENGE_TYPE = 'AdvancedCodeChallenge';
  var QUIZ_CHALLENGE_TYPE = 'QuizChallenge';
  var TYPE_KEYS = (_TYPE_KEYS = {}, defineProperty(_TYPE_KEYS, CODE_CHALLENGE_TYPE, 'code'), defineProperty(_TYPE_KEYS, ADVANCED_CODE_CHALLENGE_TYPE, 'advanced-code'), defineProperty(_TYPE_KEYS, QUIZ_CHALLENGE_TYPE, 'quiz'), _TYPE_KEYS);
  var TYPE_NAMES = (_TYPE_NAMES = {}, defineProperty(_TYPE_NAMES, CODE_CHALLENGE_TYPE, 'Code'), defineProperty(_TYPE_NAMES, ADVANCED_CODE_CHALLENGE_TYPE, 'Project Code'), defineProperty(_TYPE_NAMES, QUIZ_CHALLENGE_TYPE, 'Q&A'), _TYPE_NAMES);
  var TYPE_TOOLTIPS = (_TYPE_TOOLTIPS = {}, defineProperty(_TYPE_TOOLTIPS, CODE_CHALLENGE_TYPE, 'A simplified coding based challenge, designed around a single editor for the solution'), defineProperty(_TYPE_TOOLTIPS, ADVANCED_CODE_CHALLENGE_TYPE, 'An advanced coding based challenge, with multiple file support and additional features'), defineProperty(_TYPE_TOOLTIPS, QUIZ_CHALLENGE_TYPE, 'A quiz-like challenge with questions and answers, not necessarily code-related'), _TYPE_TOOLTIPS);

  var UPDATE_VERSION = {
    MAJOR: 'major',
    MINOR: 'minor',
    PATCH: 'patch'
  };

  var LIBRARY_ACCESS = ['limited', 'standard', 'extended'];

  var CHALLENGE_INITIAL_LAYOUT_DEFAULT = {
    instructions: 'topRight:1:1',
    webpreview: 'topRight:2',
    candidatenotes: 'topRight:3',
    help: 'topRight:4'
  };

  var DEFAULT_ESTIMATED_TIME = 5;

  var challengeModelModule = angular.module('Q.models.challenge', [modelHelperModule, challengeItemModelModule, quizQuestionModelModule, codeFileModelModule]).run(['$rootScope', function ($rootScope) {
    var unlisten = $rootScope.$on('app.login', function (event) {
      var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
          account = _ref.account;

      if (account && account.user && account.user.team) {
        if (account.user.team.hasFeature('advancedCodeChallenges')) {
          // if you have access to project challenges, we rename classic challenges
          TYPE_NAMES[CODE_CHALLENGE_TYPE] = 'Classic Code';
        }
        unlisten();
      }
    });
  }]).factory('Challenge', ['DS', 'ModelHelper', '$q', '$rootScope', '$injector', '$log', 'AdvancedCodeChallengeFile', 'AdvancedCodeChallengeReferenceFile', function (DS, ModelHelper, $q, $rootScope, $injector, $log, AdvancedCodeChallengeFile, AdvancedCodeChallengeReferenceFile) {
    var difficultiesList = [{
      id: 1,
      label: 'Basic',
      filter: { '<=': 1 } // includes any value lower than 0 for filtering
    }, {
      id: 2,
      label: 'Intermediate',
      filter: 2
    }, {
      id: 3,
      label: 'Advanced',
      filter: { '>=': 3 } // Include Advanced and up for filtering
    }, {
      id: 4,
      label: 'Advanced (Brutal)',
      filter: { '>=': 3 }, // Include Advanced and up for filtering
      hidden: true // hide for filters, meters, etc
    }];

    var difficulties = {};
    difficultiesList.forEach(function (_ref2) {
      var id = _ref2.id,
          label = _ref2.label;

      difficulties[id] = label;
    });

    var stateNameMappings = {
      draft: 'Draft',
      published: 'Published',
      archived: 'Archived'
    };

    var contentTypeMappings = {
      normal: 'Ready To Use',
      template: 'Template',
      tutorial: 'Tutorials'
    };

    var _afterInject = function _afterInject(instance) {
      delete instance.$languages;
      delete instance.$smartIdeAvailable;
      if (instance.$type === ADVANCED_CODE_CHALLENGE_TYPE && !instance.initialLayout) {
        instance.initialLayout = angular.copy(CHALLENGE_INITIAL_LAYOUT_DEFAULT);
      }
    };

    /**
     * Returns the correct team to be looking at for feature checks
     * If the current user has a team (i.e., is a TeamMember), then we use that. Otherwise we use the team
     * associated with this instance (if available)
     * @param {Challenge} instance
     * @returns {Team|null}
     */
    var getFeatureTeam = function getFeatureTeam(instance) {
      return getTeam($injector) || instance.team;
    };

    var shouldFind = function shouldFind() {
      var user = getUser($injector);
      return user && user.$type !== 'Candidate';
    };

    var Challenge = ModelHelper.defineResource({
      name: 'Challenge',
      endpoint: 'challenges',
      afterInject: function afterInject(resource, instance) {
        ModelHelper.afterInject(resource, instance);
        // after we inject or update a solution, we should always remove any cached revisions,
        // because they will never be updated
        if (Array.isArray(instance)) {
          instance.forEach(_afterInject);
        } else {
          _afterInject(instance);
        }
      },

      relations: {
        hasMany: {
          QuizQuestion: {
            $type: 'QuizChallenge',
            localField: 'questions',
            link: false
          },
          AdvancedCodeChallengeFile: {
            $type: 'AdvancedCodeChallenge',
            localField: 'files',
            link: false
          },
          AdvancedCodeChallengeReferenceFile: {
            $type: 'AdvancedCodeChallenge',
            localField: 'referenceFiles',
            link: false
          }
        },
        hasOne: {
          ChallengeItem: {
            foreignKey: 'challengeId',
            foreignField: 'challenge'
          },
          Challenge: [{
            localField: 'sourcedFrom',
            localKey: 'sourcedFromId'
          }, {
            localField: 'copiedFrom',
            localKey: 'copiedFromId'
          }]
        },
        belongsTo: {
          Team: {
            localField: 'team',
            localKey: 'teamId'
          },
          TeamMember: [{
            localField: 'createdBy',
            localKey: 'createdById'
          }]
        }
      },
      computed: {
        selectTitle: /* @ngInject */['title', 'demoData', function selectTitle(title, demoData) {
          return title + (demoData ? ' (DEMO)' : '');
        }],
        icon: {
          get: function get$$1() {
            if (!this.galleryImage) {
              if (this.isClassicCode() && this.languages.length === 1 && languages[this.languages[0].name]) {
                return 'lang-' + languages[this.languages[0].name].id;
              } else if (this.isAdvancedCode() && this.runConfig && languages[this.runConfig.language]) {
                return 'lang-' + languages[this.runConfig.language].id;
              } else if (this.isQuiz()) {
                return 'quiz';
              }
            }
          }
        },
        // Returns topics which may be filtered due to similar signal subjects being shown
        signalTopics: {
          get: function get$$1() {
            var topics = this.topics;
            var subjects = compactFalsy((this.getSubjects() || []).map(function (s) {
              return s.label;
            }));
            if (empty(topics) || empty(subjects)) return topics;

            // filter out any topics which are redundant with subjects
            return topics.filter(function (topic) {
              return !subjects.find(function (s) {
                return s.includes(topic);
              });
            });
          }
        }
      },
      methods: {
        _runConfig: function _runConfig() {
          return this.runConfig || {};
        },
        isGlobal: function isGlobal() {
          return this.collection === GLOBAL_COLLECTION;
        },
        isLinked: function isLinked() {
          return !this.isGlobal() && this.linkedStatus;
        },
        isTeam: function isTeam() {
          return this.collection === TEAM_COLLECTION;
        },
        difficultyName: function difficultyName() {
          return difficulties[this.difficulty];
        },
        stateName: function stateName() {
          return this.isGlobal() ? contentTypeMappings[this.contentType] : stateNameMappings[this.state];
        },
        typeKey: function typeKey() {
          return TYPE_KEYS[this.$type] || TYPE_KEYS[CODE_CHALLENGE_TYPE];
        },
        typeName: function typeName() {
          return TYPE_NAMES[this.$type] || TYPE_NAMES[CODE_CHALLENGE_TYPE];
        },
        typeTooltip: function typeTooltip() {
          return TYPE_TOOLTIPS[this.$type] || TYPE_TOOLTIPS[CODE_CHALLENGE_TYPE];
        },
        typeState: function typeState() {
          return lcFirst(this.$type || CODE_CHALLENGE_TYPE);
        },
        typeSuffix: function typeSuffix() {
          return this.isQuestionTimed() ? ' with question time limits' : '';
        },
        isCode: function isCode() {
          return this.isClassicCode() || this.isAdvancedCode();
        },
        isClassicCode: function isClassicCode() {
          return this.$type === CODE_CHALLENGE_TYPE;
        },
        isAdvancedCode: function isAdvancedCode() {
          return this.$type === ADVANCED_CODE_CHALLENGE_TYPE;
        },
        isQuiz: function isQuiz() {
          return this.$type === QUIZ_CHALLENGE_TYPE;
        },
        webPreviews: function webPreviews() {
          return this._runConfig().webPreviews;
        },
        versionsArray: function versionsArray() {
          return [this.majorVersion, this.minorVersion, this.patchVersion];
        },
        hasVersion: function hasVersion() {
          var versions = this.versionsArray();
          return versions.filter(angular.isNumber).length === versions.length && versions.some(function (v) {
            return v > 0;
          });
        },
        fullVersion: function fullVersion() {
          return this.versionsArray().join('.');
        },
        scoringSummary: function scoringSummary() {
          if (this.isQuiz()) {
            var questions = this.questions || [];
            var points = questions.reduce(function (s, q) {
              return s + (angular.isNumber(+q.weight) ? q.weight : 1);
            }, 0);
            var summary = questions.length + ' question' + (questions.length === 1 ? '' : 's');
            if (points === 0) {
              summary += ' (none scored)';
            } else {
              summary += ' worth ' + points + ' point' + (points === 1 ? '' : 's');
            }
            return summary;
          }
          return '';
        },
        canEdit: function canEdit() {
          return this.isTeam() && !this.protected && $rootScope.permitted({ privilege: 'manageChallenges' });
        },
        canPublish: function canPublish() {
          return this.canEdit() && Challenge.canPublish(this);
        },
        trackProperties: function trackProperties() {
          return {
            title: this.title,
            id: this.id,
            teamId: this.teamId,
            state: this.state,
            summary: this.summary,
            copiedById: this.copiedById,
            createdById: this.createdById,
            difficulty: this.difficulty
          };
        },
        loadSourcedFrom: function loadSourcedFrom() {
          var _this = this;

          return Challenge.loadSourcedFrom([this]).then(function () {
            return _this;
          });
        },
        globalChallenge: function globalChallenge() {
          if (!this.isGlobal() && this.sourcedFrom) {
            var cf = this.sourcedFrom;
            if (cf.contentType === 'normal' && cf.isGlobal()) {
              return cf;
            }
          }
          return null;
        },
        hasStats: function hasStats(type, language) {
          var stats = this.stats(type, language);
          var globalStats = this.globalStats(type, language);
          // ignore if only one result
          return !!(stats && stats.count > 1) || !!(globalStats && globalStats.count > 1);
        },
        stats: function stats(type, language) {
          var name = type + 'Stats';
          var challengeLanguage = language && this.language && this.language(language);
          return challengeLanguage && challengeLanguage[name] || this[name];
        },
        globalStats: function globalStats(type, language) {
          var gc = this.globalChallenge();
          return gc && gc.stats(type, language);
        },
        hasPremiumAccess: function hasPremiumAccess() {
          return this.libraryAccess && this.libraryAccess !== 'limited';
        },
        hasChallengeTimeLimit: function hasChallengeTimeLimit(val) {
          if (val === undefined) {
            // we're specifically only tracking null here so you can safely edit the number
            return this.timeLimitMin !== null;
          } else {
            this.timeLimitMin = val ? 60 : null;
            // scored notes are only locked by a time limit, so don't leave the flag set without one
            if (!val) this.candidateNotesScored = false;
          }
        },

        // Placeholder for the candidate Your Notes tab: author text, else the default
        candidateNotesPlaceholder: function candidateNotesPlaceholder() {
          var noun = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'solution';

          var custom = (this.candidateNotesTemplate || '').trim();
          return custom || 'Add notes about your ' + noun + ' to help explain your process.\n\nThis is markdown-enabled, so feel free to include code snippets, lists, etc.';
        },

        // For quizzes, rely on timeLimitType, otherwise use timeLimitMin
        isUntimed: function isUntimed() {
          return this.timeLimitType ? this.timeLimitType === 'none' : !this.isTimed();
        },

        // For quizzes, rely on timeLimitType, otherwise use timeLimitMin
        isTimed: function isTimed() {
          return this.timeLimitType ? this.timeLimitType === 'challenge' : this.timeLimitMin > 0;
        },
        isQuestionTimed: function isQuestionTimed() {
          return (/^per_question/.test(this.timeLimitType)
          );
        },
        isBonusTimed: function isBonusTimed() {
          return this.timeLimitType === 'per_question_bonus';
        },
        totalTimeLimitMin: function totalTimeLimitMin() {
          var _this2 = this;

          if (this.isQuestionTimed()) {
            var timeLimitSec = (this.questions || []).map(function (q) {
              return q._timeLimitSec(_this2);
            }).reduce(function (sum, sec) {
              return sum + sec;
            }, 0);
            return Math.round(timeLimitSec / 60) || Infinity;
          } else {
            return this.isTimed() ? this.timeLimitMin : Infinity;
          }
        },
        timeLimitTypeLabel: function timeLimitTypeLabel() {
          return Challenge.getEnum('timeLimitType', this.timeLimitType || 'none');
        },
        expectedTime: function expectedTime() {
          var timeLimit = this.totalTimeLimitMin();
          var estimatedTime = this.estimatedTime;
          var time = DEFAULT_ESTIMATED_TIME;
          if (estimatedTime) {
            time = Math.min(estimatedTime, timeLimit);
          } else if (timeLimit < Infinity) {
            time = timeLimit;
          }
          return time;
        },
        hasLimitedSubmissionAttempts: function hasLimitedSubmissionAttempts(val) {
          if (val === undefined) {
            // we're specifically only tracking null here so you can safely edit the number
            return this.limitSubmissionAttempts !== null;
          } else {
            this.limitSubmissionAttempts = val ? 3 : null;
          }
        },

        // matches assessment-template function for template consistency
        upgradeRequired: function upgradeRequired() {
          return this.requiresUpgrade;
        },
        requiresUpgradeLabel: function requiresUpgradeLabel() {
          var team = getTeam($injector);
          if (this.isAdvancedCode() && (!team || !team.hasFeature('advancedCodeChallenges'))) {
            return 'Project Code Challenges';
          }
          return ucFirst(this.libraryAccess) + ' Library';
        },
        premiumFeature: function premiumFeature() {
          var team = getTeam($injector);
          if (this.isAdvancedCode() && (!team || !team.hasFeature('advancedCodeChallenges'))) {
            return 'projectCodeChallenges';
          }
          return this.libraryAccess + 'ChallengeLibrary';
        },
        getLanguages: function getLanguages(assessmentResult) {
          if (!this.$languages) {
            this.$languages = this._getLanguages(assessmentResult).filter(angular.identity);
          }
          return this.$languages;
        },
        getSubjects: function getSubjects() {
          return this.signalMappings ? this.signalMappings.subjects : this.digest && this.digest.subjects || [];
        },
        externalIdeEnabled: function externalIdeEnabled() {
          var team = getFeatureTeam(this);
          return team && team.hasFeature('externalIde') && this.isAdvancedCode() && (this.enableExternalIde === 'enabled' || this.enableExternalIde === 'default' && team.defaultEnableExternalIde);
        },
        codeOfConductConfig: function codeOfConductConfig() {
          return {
            enabled: !!this.cocEnabled,
            aiUsageAllowed: this.cocAiUsageAllowed === true,
            externalIdeAllowed: this.cocExternalIdeAllowed === true,
            requireAcknowledgement: this.cocRequireAcknowledgement !== false
          };
        },
        codeOfConductRuleMessage: function codeOfConductRuleMessage(type, allowed) {
          var messages = {
            aiUsage: {
              true: 'You may use external AI tools/assistance to help solve this challenge. We encourage you to leverage AI with your own thinking to create the best work possible.',
              false: 'You may not use AI tools/assistance while solving this challenge. We want to see your own work, not AI-generated work. Using AI will result in a failed assessment.'
            },
            externalIde: {
              true: '',
              false: 'You must perform your work within this browser. Using an external IDE/tool to develop your work will result in a failed assessment.'
            }
          };
          return messages[type] && messages[type][allowed ? 'true' : 'false'];
        },
        codeOfConductRuleAcknowledgementLabel: function codeOfConductRuleAcknowledgementLabel(type, allowed) {
          var labels = {
            aiUsage: {
              true: 'I understand that I am allowed to use AI',
              false: 'I agree to not use AI'
            },
            externalIde: {
              true: '',
              false: 'I agree to work only in this browser'
            }
          };
          return labels[type] && labels[type][allowed ? 'true' : 'false'];
        },
        codeOfConductRules: function codeOfConductRules() {
          var _this3 = this;

          var config = this.codeOfConductConfig();
          var rules = [{
            id: 'aiUsage',
            allowed: config.aiUsageAllowed
          }];
          // External IDE policy is only relevant when AI usage is disallowed.
          if (!config.aiUsageAllowed) {
            rules.push({
              id: 'externalIde',
              allowed: config.externalIdeAllowed
            });
          }
          return rules.map(function (rule) {
            rule.message = _this3.codeOfConductRuleMessage(rule.id, rule.allowed);
            rule.acknowledgementLabel = _this3.codeOfConductRuleAcknowledgementLabel(rule.id, rule.allowed);
            return rule;
          }).filter(function (rule) {
            return rule.message !== null && rule.message !== undefined && String(rule.message).trim().length > 0;
          });
        },

        /**
         * Determines if SmartIDE is available on this challenge at all.
         *
         * @param {*} opts - Optional values
         * @param {boolean?} opts.overrideChallenge - If true, ignore the challenge's internal value
         * @param {boolean?} opts.enabledCheck - Private, true if we're checking strictly for currently enabled
         * @returns {boolean|string} - `true` if enabled for all languages, `'partial'` if enabled on some languages, `false` otherwise
         */
        smartIdeAvailable: function smartIdeAvailable() {
          var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

          if (!this.$smartIdeAvailable) {
            this.$smartIdeAvailable = {};
          }
          var enabledCheck = opts.enabledCheck,
              overrideChallenge = opts.overrideChallenge;

          var language = enabledCheck ? this._runConfig().language : '';
          var key = language + '|' + overrideChallenge;

          if (!this.$smartIdeAvailable[key]) {
            var user = getUser($injector);
            var team = getFeatureTeam(this);
            var hasLanguageServers = false;
            if (enabledCheck || this.isAdvancedCode()) {
              hasLanguageServers = (this._runConfig().languageServers || this.languageServers || []).length > 0;
            } else if (this.isClassicCode()) {
              var langs = this.languages.length;
              var smartIdeLangs = this.languages.filter(function (l) {
                return l.languageServers && l.languageServers.length > 0;
              }).length;
              if (smartIdeLangs === langs) {
                hasLanguageServers = true;
              } else if (smartIdeLangs > 0) {
                hasLanguageServers = 'partial';
              }
            }

            if (!user || !team || !hasLanguageServers) {
              this.$smartIdeAvailable[key] = false;
            } else {
              var featureEnabled = team.hasFeature('smartIde');

              var enabledOnChallenge = this.enableSmartIde === 'enabled';
              var enabledByDefault = this.enableSmartIde === 'default' && team.defaultEnableSmartIde;
              var enabledByOverride = overrideChallenge && team.defaultEnableSmartIde;

              this.$smartIdeAvailable[key] = featureEnabled && (enabledOnChallenge || enabledByDefault || enabledByOverride) && hasLanguageServers;
            }
          }
          return this.$smartIdeAvailable[key];
        },

        /**
         * Determines if smartIDE is currently enabled on this challenge
         * @param {boolean} overrideChallenge - If true, ignore the challenge's internal value
         * @returns {boolean} - `true` if enabled, `false` otherwise
         */
        smartIdeEnabled: function smartIdeEnabled(overrideChallenge) {
          return !!this.smartIdeAvailable({
            enabledCheck: true,
            overrideChallenge: overrideChallenge
          });
        },

        /**
         * Creates a function to determine if a path is ignored
         * @returns {Function<String>} Function that takes a string and compares it against the ignored
         * paths. Returns `true` if ignored, false otherwise;
         */
        createIgnoreCheckFunction: function createIgnoreCheckFunction() {
          var paths = (this._runConfig().submissionIgnorePaths || []).filter(angular.identity);
          if (paths.length) {
            var pathRegexes = paths.map(function (r) {
              return new RegExp(r);
            });
            return function (path) {
              return pathRegexes.some(function (r) {
                return r.test(path);
              });
            };
          } else {
            return function () {
              return false;
            };
          }
        },
        lockedLayout: function lockedLayout() {
          return this.isClassicCode() || this.initialLayout && this.initialLayout.locked;
        },
        responsiveMode: function responsiveMode() {
          return this.initialLayout && this.initialLayout.responsive;
        },
        canCloseTabs: function canCloseTabs() {
          return !this.lockedLayout();
        }
      },
      staticMethods: {
        GLOBAL_COLLECTION: GLOBAL_COLLECTION,
        TEAM_COLLECTION: TEAM_COLLECTION,
        UPDATE_VERSION: UPDATE_VERSION,
        collections: [{ id: TEAM_COLLECTION, label: 'Team Library', shortLabel: 'Team' }, {
          id: GLOBAL_COLLECTION,
          label: 'Qualified Library',
          shortLabel: 'Qualified'
        }],

        loadSourcedFrom: function loadSourcedFrom(challenges) {
          var ids = [];

          challenges.forEach(function (c) {
            if (!c.$sourcedFromLoaded && !c.sourcedFrom && c.sourcedFromId) {
              ids.push(c.sourcedFromId);
              // only try once per challenge
              c.$sourcedFromLoaded = true;
            }
          });
          return ids.length && shouldFind() ? Challenge.findAll({ where: { id: { in: ids } } }).then(function () {
            return challenges;
          }) : $q.resolve(challenges);
        },
        getTypesList: function getTypesList() {
          var suffix = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';

          var list = [{
            id: CODE_CHALLENGE_TYPE,
            label: TYPE_NAMES[CODE_CHALLENGE_TYPE] + suffix,
            c: TYPE_KEYS[CODE_CHALLENGE_TYPE]
          }, {
            id: QUIZ_CHALLENGE_TYPE,
            label: TYPE_NAMES[QUIZ_CHALLENGE_TYPE] + suffix,
            c: TYPE_KEYS[QUIZ_CHALLENGE_TYPE]
          }];
          if ($rootScope.permitted && $rootScope.permitted({ feature: 'advancedCodeChallenges' })) {
            list.splice(0, 0, {
              id: ADVANCED_CODE_CHALLENGE_TYPE,
              label: TYPE_NAMES[ADVANCED_CODE_CHALLENGE_TYPE] + suffix,
              c: TYPE_KEYS[ADVANCED_CODE_CHALLENGE_TYPE]
            });
          }
          return list;
        },


        difficulties: difficulties,
        difficultiesList: difficultiesList,
        difficultiesVisibleList: difficultiesList.filter(function (v) {
          return !v.hidden;
        }),

        LIBRARY_ACCESS: LIBRARY_ACCESS,
        premiumAccess: function premiumAccess(accessLevel) {
          var accessIndex = LIBRARY_ACCESS.indexOf(accessLevel);
          if (accessIndex < 0) accessIndex = 0;
          return LIBRARY_ACCESS.slice(accessIndex + 1);
        },
        showAccessTags: function showAccessTags() {
          return $rootScope.permitted && $rootScope.permitted({ staffContent: true });
        },


        INCLUDE_SOLUTIONS_REGEX: INCLUDE_SOLUTIONS_REGEX,
        INCLUDE_SOLUTIONS_REGEX_GLOBAL: INCLUDE_SOLUTIONS_REGEX_GLOBAL,

        isCarriedForward: function isCarriedForward(challenge, assessment) {
          return assessment.challengeItems.some(function (ci) {
            return Challenge.getCarriedForwardIds(ci.challenge || {}).some(function (cid) {
              return challenge.id === cid;
            });
          });
        },
        hasBeenCarriedForward: function hasBeenCarriedForward(challenge, assessmentResult) {
          return assessmentResult.validSolutions().some(function (sol) {
            return sol.challengeId !== challenge.id && sol.challenge && Challenge.getCarriedForwardIds(sol.challenge).some(function (cid) {
              return cid === challenge.id;
            });
          });
        },
        getCarriedForwardIds: function getCarriedForwardIds(challenge) {
          var ids = [];
          (challenge.languages || []).forEach(function (lang) {
            var matches = (lang.setup || '').match(INCLUDE_SOLUTIONS_REGEX_GLOBAL);
            if (matches) {
              matches.forEach(function (m) {
                var id = m.match(INCLUDE_SOLUTIONS_REGEX)[2];
                if (ids.indexOf(id) === -1) {
                  ids.push(id);
                }
              });
            }
          });
          return ids;
        },
        loadCarriedForward: function loadCarriedForward(challenge) {
          var ids = Challenge.getCarriedForwardIds(challenge).filter(function (cid) {
            return !!Challenge.get(cid);
          });
          if (ids.length && shouldFind()) {
            return Challenge.find({ where: { id: { in: ids } } }).then(function () {
              return true;
            }, function () {
              return false;
            });
          } else {
            return $q.resolve(true);
          }
        },
        replaceCarriedForward: function replaceCarriedForward(language, code) {
          return (code || '').replace(INCLUDE_SOLUTIONS_REGEX_GLOBAL, function (match, prefix, id) {
            var c = Challenge.get(id);
            if (!c) {
              return prefix + '(Unable to find reference challenge for previous solution)';
            }

            var lang = c.language(language);
            if (lang && lang.solution) {
              return prefix + ' (Using reference solution from ' + c.title + ')\n' + lang.solution;
            } else {
              return prefix + ' (Reference solution not available for ' + c.title + ')';
            }
          });
        },
        canPublish: function canPublish(challengeData) {
          var hasContent = true;
          var type = challengeData.$type;
          if (type === QUIZ_CHALLENGE_TYPE) {
            hasContent = challengeData.questions && challengeData.questions.length && challengeData.questions.every(function (q) {
              return q && q.prompt;
            });
          } else if (type === CODE_CHALLENGE_TYPE) {
            hasContent = challengeData.languages && challengeData.languages.length && challengeData.languages.every(function (l) {
              return l && l.solution && l.testCases;
            });
          } else if (type === ADVANCED_CODE_CHALLENGE_TYPE) {
            hasContent = challengeData.files && challengeData.files.length;
          }
          return hasContent && challengeData.title && challengeData.instructions;
        },
        getRunFiles: function getRunFiles(challenge) {
          var filesContainer = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : challenge;
          var submission = arguments[2];
          var reference = arguments[3];

          var includeKey = submission ? 'includeOnSubmit' : 'includeOnTest';
          var baseFiles = filesContainer.files.filter(function (f) {
            return !f.directory && f[includeKey];
          });
          var ignored = challenge.createIgnoreCheckFunction();
          if (reference) {
            var refFiles = Array.isArray(reference) ? reference : filesContainer.referenceFiles;
            // add or override valid reference files
            refFiles.filter(function (f) {
              return !f.directory && (!submission || !ignored(f.path));
            }).forEach(function (rf) {
              var setupFileIndex = baseFiles.findIndex(function (f) {
                return f.path === rf.path;
              });
              if (setupFileIndex > -1) {
                if (baseFiles[setupFileIndex].$access === 'readwrite') {
                  baseFiles[setupFileIndex] = rf;
                }
              } else {
                baseFiles.push(rf);
              }
            });
          }
          return baseFiles.map(function (f) {
            return {
              path: f.path,
              contents: f.contents,
              get encrypted() {
                return f.encrypted;
              },
              get reference() {
                return f.reference;
              },
              get $access() {
                return f.$access || f.access;
              }
            };
          });
        }
      },
      meta: {
        hasDigest: true,
        types: {
          CodeChallenge: {
            afterTyped: function afterTyped(instance) {
              // ensure languages is not missing
              instance.languages = instance.languages || [];

              // configure current language (if already configured, reconfigure it)
              instance.setCurrentLanguage(instance.$currentLanguage);

              // Mock files & referenceFiles arrays to simulate an AdvancedCodeChallenge
              var createFile = function createFile(field) {
                var reference = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

                // const testCases = /testcases/i.test(field);
                var FileModel = reference ? AdvancedCodeChallengeReferenceFile : AdvancedCodeChallengeFile;
                var file = FileModel.createInstance({
                  id: 'challenge$' + field.toLowerCase(),
                  field: field,
                  deletable: false,
                  access: 'readwrite',
                  isClassicCode: true,
                  $hideFromInteract: true
                });
                Object.defineProperties(file, {
                  contents: {
                    get: function get$$1() {
                      return instance.$currentLanguage[field];
                    },
                    set: function set$$1(val) {
                      instance.$currentLanguage[field] = val;
                    }
                  }
                });
                return file;
              };

              var createFiles = function createFiles(key, fields, reference) {
                lazyProperty(instance, key, function () {
                  var files = fields.map(function (f) {
                    return createFile(f, reference);
                  });

                  files.updatePaths = function () {
                    return setClassicFilePaths({
                      files: files,
                      language: instance.$currentLanguage.name,
                      testFramework: instance.$currentLanguage.testFramework,
                      languageVersion: instance.$currentLanguage.languageVersion,
                      $log: $log
                    });
                  };
                  defineValue(instance, '_updatePaths', files.updatePaths);

                  files.updatePaths();
                  // TODO: do we ever need to track renamed files here?
                  instance.on('afterInject', files.updatePaths);

                  return files;
                });
              };

              createFiles('files', ['setup', 'preloaded', 'exampleTestCases']);
              createFiles('referenceFiles', ['solution', 'testCases'], true);
            },

            /**
             * Stores the current working language on the challenge to make it easier to worth with a
             * classic code challenge in multi-file components.
             *
             * @param {Object|string?} nameOrLanguage - Name of a language, or a language object to set
             */
            setCurrentLanguage: function setCurrentLanguage(nameOrLanguage) {
              var challenge = this;
              var langName = isObject(nameOrLanguage) && nameOrLanguage.name || nameOrLanguage;
              // always look up the actual language object
              var lang = challenge.language(langName) || {};
              defineValue(challenge, '$currentLanguage', lang);
              defineValue(challenge, 'runConfig', {
                language: lang.name,
                languageVersion: lang.languageVersion,
                testFramework: lang.testFramework,
                preset: null,
                webPreviews: false,
                services: challenge.enabledServices,
                languageServers: lang.languageServers
              });
              if (challenge._updatePaths) {
                challenge._updatePaths();
              }
            },
            language: function language(name) {
              return name ? this.languages.find(function (language) {
                return language.name === name;
              }) : undefined;
            },
            _getLanguages: function _getLanguages(assessmentResult) {
              var langs = this.languages.map(function (l) {
                return languages.getLang(l.name);
              });
              if (assessmentResult) {
                var ci = assessmentResult.getChallengeItem(this);
                langs = langs.filter(function (lang) {
                  return !ci.disabledLanguages.includes(lang.id);
                });
              }
              return langs;
            }
          },
          AdvancedCodeChallenge: {
            getRunFiles: function getRunFiles(submission, reference, updates) {
              var filesContainer = this;
              if (updates) {
                filesContainer = {
                  files: updates.files || this.files,
                  referenceFiles: updates.referenceFiles || this.referenceFiles
                };
              }
              return Challenge.getRunFiles(this, filesContainer, submission, reference);
            },
            _getLanguages: function _getLanguages() {
              var lang = this._runConfig().language || this.languages && this.languages[0] && this.languages[0].name;
              if (lang) {
                return [languages[lang]];
              } else {
                return [];
              }
            }
          },
          QuizChallenge: {
            _getLanguages: function _getLanguages() {
              var allLangs = this.questions.map(function (q) {
                return q.language;
              }).map(languages.getLang).filter(angular.identity);
              return unique(allLangs, function (l) {
                return l.id;
              });
            }
          }
        },
        defaults: {
          initialLayout: CHALLENGE_INITIAL_LAYOUT_DEFAULT
        },
        actionEndpoints: {
          import: { track: true },
          duplicate: { segment: 'import' },
          unpublish: { method: 'PUT', track: true },
          archive: { method: 'PUT', track: true },
          unarchive: { method: 'PUT', track: true },
          duplicateToShared: { method: 'PUT', track: true },
          export: { method: 'POST', track: true, cacheResponse: false }
        },
        track: {
          create: true,
          update: true
        },
        includeOnPull: ['digest'],
        excludeSaveOrCreateProps: ['questions', 'files', 'referenceFiles'],
        afterSaveOrCreate: function afterSaveOrCreate(instance, changes) {
          var fields = [];
          if (instance.$type === 'AdvancedCodeChallenge') {
            fields = [{
              Model: DS.definitions.AdvancedCodeChallengeFile,
              fieldName: 'files',
              foreignKey: 'challengeId',
              changes: changes,
              orderable: false,
              uniqueChildField: 'path'
            }, {
              Model: DS.definitions.AdvancedCodeChallengeReferenceFile,
              fieldName: 'referenceFiles',
              foreignKey: 'challengeId',
              changes: changes,
              orderable: false,
              uniqueChildField: 'path'
            }];
          }
          if (instance.$type === 'QuizChallenge') {
            fields = {
              Model: DS.definitions.QuizQuestion,
              fieldName: 'questions',
              foreignKey: 'challengeId',
              changes: changes
            };
          }
          return ModelHelper.saveEmbedded(fields);
        }
      }
    });

    return Challenge;
  }]);

  var customerModelModule = angular.module('Q.models.customer', [modelHelperModule]).factory('Customer', ['ModelHelper', function (ModelHelper) {
    var Customer = ModelHelper.defineResource({
      name: 'Customer',
      endpoint: 'billing/customers',

      relations: {
        hasMany: {}
      },

      methods: {
        hasFeature: function hasFeature(feature) {
          return !!(this.features && this.features[feature]);
        },
        getFeature: function getFeature(feature) {
          return !feature || this.features && this.features[feature];
        },
        subscriptionExpired: function subscriptionExpired(teamMember) {
          if (teamMember && teamMember.hasRole('staff')) return false;
          return !this.isActive;
        },
        isPayg: function isPayg() {
          return this.plan && this.plan.isPayg;
        },
        isInTrial: function isInTrial() {
          return this.state === 'in_trial';
        },
        hasQualifiedPlan: function hasQualifiedPlan() {
          return this.isSubscribed || this.isInTrial();
        },
        usageMeasure: function usageMeasure() {
          return this.plan ? this.plan.usageMeasure : 'assessment_result';
        },

        // this version of usageMeasure doesn't know if a customer is for education or not, so always returns Candidate/Candidates.
        // if you have access to the specific team, use the method defined there instead which can distinguish between them.
        usageMeasureName: function usageMeasureName(plural) {
          switch (this.usageMeasure()) {
            case 'assessment_result':
              return plural ? 'Assessment Results' : 'Assessment Result';

            case 'team_candidate':
              return plural ? 'Candidate' : 'Candidates';
          }
        },

        // combines any prepaid add-on seats with the seats included with the plan
        includedPremiumSeats: function includedPremiumSeats() {
          var seats = this.prepaidPremiumSeats || 0;
          if (this.plan) seats += this.plan.includedPremiumSeats || 0;
          return seats;
        },

        // combines any prepaid add-on seats with the seats included with the plan
        includedRestrictedSeats: function includedRestrictedSeats() {
          var seats = this.prepaidRestrictedSeats || 0;
          if (this.plan) seats += this.plan.includedRestrictedSeats;
          return seats;
        },
        seatNextBillingAt: function seatNextBillingAt() {
          if (this.plan) {
            if (this.plan.seatSchedule === 'base') {
              return this.remainingBillingCycles === 0 ? null : this.baseNextBillingAt;
            }

            return this.usageNextBillingAt || this.baseNextBillingAt;
          }
        },
        hasAdditionalSeats: function hasAdditionalSeats() {
          return this.additionalPremiumSeatsInUse > 0 || this.additionalRestrictedSeatsInUse > 0;
        },
        outstandingBurstBalance: function outstandingBurstBalance() {
          if (this.plan) {
            return this.currentTermBurstUsage * this.plan.burstUnitPriceFloat;
          }

          return 0;
        },
        hasStandardPlan: function hasStandardPlan() {
          return this.plan && ['payg', 'starter'].indexOf(this.plan.planCategory) !== -1;
        },
        hasValidCreditCard: function hasValidCreditCard() {
          return this.paymentGateway === 'stripe' && this.paymentMethod === 'card' && this.cardStatus === 'valid';
        },

        /**
         * indicates if a subscription was just updated, useful for when we may want to hold off on showing certain UI elements
         * while we wait for a full sync from the billing system.
         * @returns {*|boolean} true if subscription was changed less than 30 seconds ago
         */
        hasRecentSubscriptionChanges: function hasRecentSubscriptionChanges() {
          return this.subscriptionUpdatedAt && new Date() - this.subscriptionUpdatedAt < 30000;
        }
      },

      staticMethods: {},

      meta: {
        actionEndpoints: {
          updatePayment: { segment: 'payment', method: 'PUT', track: true },
          subscribe: { segment: 'subscription', method: 'POST', track: true },
          reactivate: {
            segment: 'subscription/reactivate',
            method: 'PUT',
            track: true
          },
          cancelDowngrade: {
            segment: 'subscription/cancel_downgrade',
            method: 'PUT',
            track: true
          },
          applyCoupon: {
            segment: 'subscription/coupon',
            method: 'PUT',
            track: true
          },
          cancel: {
            segment: 'subscription/cancel',
            method: 'PUT',
            track: true
          },
          cancelNow: { segment: 'subscription', method: 'DELETE', track: true },
          startTrial: {
            segment: 'subscription/trial',
            method: 'POST',
            track: true
          },
          endTrial: {
            segment: 'subscription/trial',
            method: 'DELETE',
            track: true
          }
        },
        saveExcludes: []
      }
    });

    return Customer;
  }]);

  var externalRevisionModelModule = angular.module('Q.models.externalRevision', [modelHelperModule]).factory('ExternalRevision', ['ModelHelper', '$rootScope', function (ModelHelper, $rootScope) {
    var ExternalRevision = ModelHelper.defineResource({
      name: 'ExternalRevision',
      endpoint: 'external_revisions',
      methods: {},
      staticMethods: {
        loadFor: function loadFor(reviseable) {
          if ($rootScope.playbackToken) {
            ExternalRevision.endpoint = 'external_revisions/share_revisions/' + reviseable.id;
          }
          return ExternalRevision.retrieveAll({ referenceId: reviseable.id, per: 250 }, { cacheResponse: false, bypassCache: true }).then(function (revisions) {
            return reviseable.revisions = revisions;
          });
        }
      }
    });

    return ExternalRevision;
  }]);

  var invoiceModelModule = angular.module('Q.models.invoice', [modelHelperModule]).factory('Invoice', ['ModelHelper', function (ModelHelper) {
    return ModelHelper.defineResource({
      name: 'Invoice',
      endpoint: 'invoices',

      relations: {
        belongsTo: {
          Team: {
            localField: 'team',
            localKey: 'teamId'
          }
        }
      },
      meta: {
        neverFetchOnPush: true
      }
    });
  }]);

  var noteModelModule = angular.module('Q.models.note', [modelHelperModule]).factory('Note', ['ModelHelper', function (ModelHelper) {
    var Note = ModelHelper.defineResource({
      name: 'Note',
      endpoint: 'notes',
      methods: {},
      staticMethods: {
        // automatically loads notes for a given notable, and listens for changes to reload
        watchFor: function watchFor(notable, $scope) {
          var notableId = notable.id;
          var notableType = notable.$type || notable.constructor.name;

          var updatePromise = null;

          var updateEvent = function updateEvent() {
            if (!updatePromise) {
              updatePromise = Note.findAll({ notableId: notableId, notableType: notableType }, {
                ignoreLoadingBar: true,
                bypassCache: true
              }).finally(function () {
                updatePromise = null;
              });
            }
          };

          $scope.$on('socket:model.changed:' + notableType + ':' + notableId, updateEvent);
          $scope.$on('model.changed:' + notableType + ':' + notableId, updateEvent);
        }
      },
      relations: {
        hasOne: {
          TeamMember: {
            localField: 'user',
            localKey: 'userId'
          },
          TeamCandidate: {
            localField: 'notable',
            localKey: 'notableId'
          }
        }
      }
    });

    return Note;
  }]);

  var reviewQualityModelModule = angular.module('Q.models.review-quality', [modelHelperModule]).factory('ReviewQuality', ['ModelHelper', function (ModelHelper) {
    return ModelHelper.defineResource({
      name: 'ReviewQuality',
      endpoint: 'review_qualities',
      methods: {},
      staticMethods: {},
      relations: {
        hasOne: {
          Team: {
            localField: 'team',
            localKey: 'teamId'
          }
        }
      }
    });
  }]);

  var CODE_SOLUTION_TYPE = 'CodeSolution';
  var ADVANCED_CODE_SOLUTION_TYPE = 'AdvancedCodeSolution';
  var QUIZ_SOLUTION_TYPE = 'QuizSolution';

  var MAX_OUTPUT_LEN = 50000; // truncate output longer than this
  var MAX_OUTPUT_TRUNCATE = 40000; // truncate long output to this

  var SOLUTION_FORM_DATA_IGNORED_PROPERTIES = ['$challengeSnapshot', 'score', 'challengeSnapshot', 'accumulatedSeconds', 'lastHeartbeat', 'revisionCount', 'submittedAt', 'submittingAt', 'failedTestNames', 'attempts', 'submissionStatus', 'passingScore', 'timerStartedAt', 'submissionHash', 'revisionsCount', 'firebaseRef'];

  var SUBMISSION_STATUS_ERROR_MESSAGES = {
    invalid: 'Unable to run test suite. Please check for errors within your code.',
    timed_out: 'Timed Out: The solution could not be tested within the allotted time'
  };

  var solutionModelModule = angular.module('Q.models.solution', [modelHelperModule, codeFileModelModule]).factory('Solution', ['ModelHelper', '$q', 'DS', '$window', '$log', 'AdvancedCodeSolutionFile', '$rootScope', function (ModelHelper, $q, DS, $window, $log, AdvancedCodeSolutionFile, $rootScope) {
    var statuses = {
      new: 'new',
      incomplete: 'incomplete',
      completed: 'completed',
      okscore: 'ok',
      lowscore: 'lowscore',
      failed: 'failed',
      failedhidden: 'failedhidden'
    };
    var statusTitles = {
      new: 'New',
      incomplete: 'Incomplete',
      completed: 'Completed',
      okscore: 'OK Score',
      lowscore: 'Low Score',
      failed: 'Failed',
      failedhidden: 'Passed Visible, Failed Hidden'
    };
    // any quizzes scored at or below the number has the status beside it
    var scoreToStatus = [[60, 'failed'], [85, 'lowscore'], [95, 'okscore'], [Infinity, 'completed']];

    var isTeamMember = !appConfig.candidateApp;

    var Solution = void 0;

    var codeTypeMethods = function codeTypeMethods(overridesAndExtras) {
      return angular.extend({
        totalTests: function totalTests() {
          return this.totalTestCases;
        },
        passedTests: function passedTests() {
          return this.passedTestCases;
        },
        _passedInfo: function _passedInfo() {
          var passed = this.passedTests();
          var total = this.totalTests();
          if (isTeamMember) {
            passed += this.hiddenPassedTestCases || 0;
            total += this.hiddenTestCases || 0;
          } else if (!this.candidateTestsEnabled() || !total) {
            return {
              msg: this.attempted() ? 'Submitted' : 'Not Submitted'
            };
          }
          return { passed: passed, total: total };
        },
        passedSummary: function passedSummary() {
          var _passedInfo2 = this._passedInfo(),
              passed = _passedInfo2.passed,
              total = _passedInfo2.total,
              msg = _passedInfo2.msg;

          return msg || (passed || 0) + ' / ' + (total || '?') + ' Tests';
        },
        passedSummaryLong: function passedSummaryLong() {
          var _passedInfo3 = this._passedInfo(),
              passed = _passedInfo3.passed,
              total = _passedInfo3.total,
              msg = _passedInfo3.msg;

          return msg || (passed || 0) + ' of ' + (total || '?') + ' tests passed';
        },
        getStatus: function getStatus() {
          if (this.passedTests() > 0) {
            if (!this.passed()) {
              return statuses.failed;
            } else if (isTeamMember && !this.passedWeighted()) {
              return statuses.failedhidden;
            } else {
              return statuses.completed;
            }
          } else {
            return statuses.incomplete;
          }
        },
        _output: function _output() {
          return this.output;
        },
        _response: function _response() {
          return this.response;
        },
        _wallTime: function _wallTime() {
          return this.wallTime;
        },
        getRunResponse: function getRunResponse() {
          var totalTests = this.totalTests();
          var passed = this.passedTests();
          var output = Solution.cleanOutput(this._output());
          var failed = output ? totalTests - passed : 0;
          var wallTime = this._wallTime();
          var response = this._response();
          return angular.merge({
            $mock: !response,
            result: {
              passed: passed,
              failed: failed,
              output: output,
              wallTime: wallTime,
              type: 'attempt',
              successMode: this.successMode,
              assertions: this.assertions,
              specs: this.specs,
              tags: this.tags,
              completed: this.passed()
            },
            stderr: '',
            stdout: ' '
          }, response);
        },
        passed: function passed() {
          if (typeof this.passingScore === 'boolean') {
            return this.passingScore;
          }

          return this.passedTests() && this.passedTests() >= this.totalTests();
        },
        passedWeighted: function passedWeighted() {
          if (this.passingScore) {
            return true;
          }

          if (this.score) return this.score >= 100;
          if (this.weightedTestCases) {
            return this.weightedPassedTestCases >= this.weightedTestCases;
          }
          return this.passed();
        },
        attempted: function attempted() {
          return this.passedTests() > 0 || this.attempts > 0;
        },
        fastestWallTime: function fastestWallTime() {
          if (this.codeBenchmark) {
            return this.codeBenchmark.fastestTime || this.wallTime;
          } else {
            return this.wallTime;
          }
        },
        updateScore: function updateScore() {
          var totalTests = this.totalTests();
          if (totalTests > 0) {
            this.score = (this.passedTests() || 0) / totalTests * 100;
          }
        },
        loadRevisions: function loadRevisions(force) {
          if (force || !angular.isArray(this.revisions) || !this.revisions.length) {
            if (this.isAdvancedCode() || $rootScope.playbackToken) {
              return DS.definitions.ExternalRevision.loadFor(this);
            } else {
              return Solution.loadRevisions(this);
            }
          } else {
            this.revisions = parseDates(this.revisions);
            return $q.when(this.revisions);
          }
        },
        logAttempt: function logAttempt(data) {
          return Solution.logAttempt(this, { data: data });
        },
        logTest: function logTest(data) {
          return Solution.logTest(this, { data: data });
        },
        hasTags: function hasTags(tag) {
          if (tag !== undefined) {
            return !!this.tagProps(tag).total;
          } else {
            return this.usedSmartIde || this.usedExternalIde || this.hasTags('') || !!(this.tags && Object.keys(this.tags).length);
          }
        },
        hiddenProps: function hiddenProps() {
          return {
            passed: this.hiddenPassedTestCases,
            total: this.hiddenTestCases
          };
        },
        tagProps: function tagProps(tag) {
          var tagProps = (tag ? this.tags && this.tags[tag] : this.hiddenProps()) || {};
          var passed = tagProps.passed = tagProps.passed || 0;
          var total = tagProps.total || (tag && this.tagTestCases ? this.tagTestCases[tag] : this.hiddenTestCases) || 0;
          tagProps.failed = Math.max(tagProps.failed || 0, total - passed || 0);
          tagProps.total = total || 0;
          return tagProps;
        },
        tagClass: function tagClass(tag) {
          var tagProps = this.tagProps(tag);
          if (tagProps.passed >= tagProps.total) return 'success';
          if (tagProps.passed === 0 && tagProps.failed) return 'danger';
          if (tagProps.passed && tagProps.failed) return 'warning';
          return '';
        },
        tagSummary: function tagSummary(tag) {
          var tagProps = this.tagProps(tag);
          return tagProps.passed + ' of ' + (tagProps.total || '?');
        },
        candidateTestsEnabled: function candidateTestsEnabled() {
          return !this.challenge || this.challenge.candidateTestsEnabled !== false;
        },
        hasFailedSubmissionAttempts: function hasFailedSubmissionAttempts() {
          return !!(!this.expired() && this.challenge && this.challenge.limitSubmissionAttempts && this.submissionFailedMessages().length);
        },
        updateSubmissionFailedMessages: function updateSubmissionFailedMessages(reviewing) {
          var errs = this.$submissionFailedMessages = [];
          if (this.submissionResponseError) {
            errs.push(reviewing ? 'An exception was caught running the code.' : 'An exception was caught within your code. Please check for issues that could cause the program to fail to run properly.');
          }
          errs.push.apply(errs, toConsumableArray(this.failedTestNames || []));
          var statusError = SUBMISSION_STATUS_ERROR_MESSAGES[this.submissionStatus];
          if (statusError) {
            errs.push(statusError);
          }
          if (!errs.length && this.isSubmitted() && this.passingScore === false) {
            // if we have no errors and we submitted
            errs.push(reviewing ? 'Not all of the expected tests completed' : 'Not all of the expected tests completed, please make sure your code is not preventing tests from running');
          }
          return errs;
        },
        submissionFailedMessages: function submissionFailedMessages(reviewing) {
          return this.$submissionFailedMessages || this.updateSubmissionFailedMessages(reviewing);
        }
      }, overridesAndExtras);
    };

    Solution = ModelHelper.defineResource({
      name: 'Solution',
      endpoint: 'solutions',

      staticMethods: {
        /**
         * Configures initial layout for challenge config and editor tool tabs
         * @param {multiFileChallengeService} mfcs - Source of tabs
         * @param {boolean} always - If true, always set the layout, even if the tab has a different one stored
         * @param {boolean} lockedLayout - If true, set the layout if missing from the tab
         */
        configureInitialLayout: function configureInitialLayout(mfcs, _ref) {
          var always = _ref.always,
              lockedLayout = _ref.lockedLayout;

          var props = mfcs.challenge.initialLayout;
          [].concat(mfcs.challengeConfig, mfcs.challengeEditorTools).forEach(function (file) {
            configureInitiallyOpenFile({
              file: file,
              always: always,
              lockedLayout: lockedLayout,
              initialLayout: props[file.id]
            });
          });
        },

        /**
         * Destructively cleans up any problems with extremely long log lines in the output
         * @param output
         * @returns {*}
         */
        cleanOutput: function cleanOutput(output) {
          angular.forEach(output, function (v, k) {
            if (isString(v) && v.length > MAX_OUTPUT_LEN) {
              output[k] = v.slice(0, MAX_OUTPUT_TRUNCATE) + '\n[Truncated ' + (v.length - MAX_OUTPUT_TRUNCATE) + ' characters]';
            } else if (isObject(v) || isArray(v)) {
              output[k] = Solution.cleanOutput(v);
            }
          });
          return output;
        }
      },

      methods: {
        getStatusTitle: function getStatusTitle() {
          return statusTitles[this.getStatus()];
        },
        isCode: function isCode() {
          return this.isClassicCode() || this.isAdvancedCode();
        },
        isClassicCode: function isClassicCode() {
          return this.$type === CODE_SOLUTION_TYPE;
        },
        isAdvancedCode: function isAdvancedCode() {
          return this.$type === ADVANCED_CODE_SOLUTION_TYPE;
        },
        isQuiz: function isQuiz() {
          return this.$type === QUIZ_SOLUTION_TYPE;
        },
        getChallengeSnapshot: function getChallengeSnapshot() {
          if (!this.$challengeSnapshot && this.challengeSnapshot) {
            this.$challengeSnapshot = DS.createInstance('Challenge', this.challengeSnapshot);
            if (this.$challengeSnapshot.questions) {
              this.$challengeSnapshot.questions = this.$challengeSnapshot.questions.map(function (q) {
                return DS.createInstance('QuizQuestion', cleanUpQuizQuestion(q));
              }).sort(function (a, b) {
                return a.position - b.position;
              });
            }
          }
          return this.$challengeSnapshot || this.challenge;
        },

        // dummy method for non-code solutions
        getRunResponse: function getRunResponse() {
          return {
            $mock: true,
            result: {
              passed: 0,
              failed: 0,
              wallTime: 0,
              type: 'attempt'
            },
            stderr: '',
            stdout: ' '
          };
        },
        percentile: function percentile(type) {
          return getPercentile(this[type], this.challenge && this.challenge[type + 'Stats']);
        },
        hasStats: function hasStats(type) {
          return !!this.challenge && this.challenge.hasStats(type, this.language);
        },
        showStats: function showStats(type, value) {
          // some pieces of information are tracked under the similarity object
          var sim = this.similarity || {};
          if (type === 'similarity') {
            value = sim.maxSimilarity;
          } else if (type === 'mass') {
            value = sim[type];
          } else {
            value = this[type];
          }
          return !!this.score && !isNullOrUndefined(value) && this.hasStats(type);
        },
        blindedSolutionReviews: function blindedSolutionReviews() {
          // if we should be hiding assessment reviews for blind reviews, then we will hide
          // the solution reviews as well.
          if (this.assessmentResult && this.assessmentResult.shouldHideOtherReviews()) {
            var arr = this.assessmentResult.myReview();
            return arr ? arr.solutionReviews : [];
          }

          return this.solutionReviews;
        },

        // true if the candidate's scored notes can no longer be edited. Mirrors
        // the server, which allows a 45s grace window for the last autosave.
        notesLocked: function notesLocked() {
          return !!(this.challenge && this.challenge.candidateNotesScored && this.challenge.isTimed() && !this.pendingTimerReset && this.timerStartedAt && new Date(this.timerStartedAt).getTime() + this.challenge.timeLimitMin * 60000 + 45000 < new Date());
        },
        expired: function expired() {
          // Convert timerStartedAt to Date if it's a string (handles FormData sync issues)
          if (this.challenge && this.challenge.isTimed() && !this.pendingTimerReset && isString(this.timerStartedAt)) {
            this.timerStartedAt = new Date(this.timerStartedAt);
          }

          if (this.challenge && this.challenge.isTimed() && !this.pendingTimerReset && !isDate(this.timerStartedAt)) {
            $log.error('this.timerStartedAt is not a date', this.timerStartedAt);
            return;
          }
          // returns true if this solution can no longer be edited
          return this.challenge && (this.challenge.isTimed() && !this.pendingTimerReset && this.timerStartedAt.getTime() + this.challenge.timeLimitMin * 60000 < new Date() || this.isAdvancedCode() && this.remainingAttempts() < 1);
        }
      },

      relations: {
        belongsTo: {
          Assessment: {
            localField: 'assessment',
            localKey: 'assessmentId'
          },
          AssessmentResult: {
            localKey: 'assessmentResultId',
            localField: 'assessmentResult'
          },
          Candidate: {
            localField: 'candidate',
            localKey: 'candidateId'
          },
          Challenge: {
            localKey: 'challengeId',
            localField: 'challenge'
          }
        },
        hasMany: {
          SolutionReview: {
            localField: 'solutionReviews',
            foreignKey: 'solutionId'
          },
          AdvancedCodeSolutionFile: {
            $type: 'AdvancedCodeSolution',
            localField: 'files',
            link: false
          }
        }
      },

      meta: {
        hasDigest: true,
        cleanInjected: function cleanInjected(instance) {
          if (!$rootScope.playbackToken) {
            delete instance.revisions;
          }
          delete instance.$changedFiles;
          delete instance.$submissionFailedMessages;
        },

        fetchOnPush: function fetchOnPush(data) {
          // only fetch solution if we have the assessment result
          return DS.get('AssessmentResult', data.assessmentResultId || '');
        },
        includeOnPull: ['digest', 'similarity'],
        saveExcludes: ['challenge', 'assessmentResult', 'candidate', 'teamCandidate', 'challengeSnapshot', '$challengeSnapshot', 'failedTestNames', 'revisionsCount'],
        types: {
          CodeSolution: codeTypeMethods({
            // Mock files array to simulate an AdvancedCodeSolution
            afterTyped: function afterTyped(instance) {
              var createFile = function createFile(field) {
                // const testCases = /testcases/i.test(field);
                var file = AdvancedCodeSolutionFile.createInstance({
                  id: field.toLowerCase(),
                  field: field,
                  deletable: false,
                  access: 'readwrite',
                  isClassicCode: true
                });
                Object.defineProperty(file, 'contents', {
                  get: function get$$1() {
                    return instance[field];
                  },
                  set: function set$$1(val) {
                    instance[field] = val;
                  },

                  configurable: true
                });
                return file;
              };
              lazyProperty(instance, 'files', function () {
                var preloaded = createFile('preloaded');
                preloaded.access = 'readonly';
                var files = [createFile('code'), createFile('testCases'), preloaded];

                files.updatePaths = function () {
                  var challenge = instance.challenge;
                  var runConfig = challenge && challenge._runConfig() || {};
                  // use this time to update preloaded
                  var lang = instance.challengeLanguage();
                  preloaded.contents = lang && lang.preloaded || '';
                  return setClassicFilePaths({
                    files: files,
                    language: instance.language,
                    testFramework: runConfig.testFramework,
                    languageVersion: runConfig.languageVersion,
                    $log: $log
                  });
                };

                files.updatePaths();
                // TODO: do we ever need to track renamed files here?
                instance.on('afterInject', files.updatePaths);

                return files;
              });
            },
            challengeLanguage: function challengeLanguage() {
              var challenge = this.getChallengeSnapshot();
              return challenge && challenge.language(this.language);
            },
            isSubmitting: function isSubmitting() {
              return false;
            },
            isSubmitted: function isSubmitted() {
              return this.attempted();
            }
          }),
          AdvancedCodeSolution: codeTypeMethods({
            totalTests: function totalTests() {
              return isTeamMember ? this.totalTestCases : this.testTotalTestCases;
            },
            passedTests: function passedTests() {
              return isTeamMember ? this.passedTestCases : this.testPassedTestCases;
            },
            attempted: function attempted() {
              var attempts = isTeamMember ? this.attempts : this.testAttempts;
              return this.passedTests() > 0 || attempts > 0 || this.isSubmitted();
            },
            remainingAttempts: function remainingAttempts() {
              if (this.challenge && this.challenge.limitSubmissionAttempts) {
                var attempts = this.attempts || 0;
                return Math.max(0, this.challenge.limitSubmissionAttempts - attempts);
              } else {
                return Infinity;
              }
            },
            _output: function _output() {
              return isTeamMember ? this.output : this.testOutput;
            },
            _wallTime: function _wallTime() {
              return isTeamMember ? this.wallTime : this.testWallTime;
            },
            _response: function _response() {
              return isTeamMember ? this.response : this.testResponse;
            },
            changedFiles: function changedFiles() {
              var solution = this;
              var challenge = solution.getChallengeSnapshot();
              var challengeFiles = challenge && challenge.files || [];
              if (!solution.hasOwnProperty('$changedFiles')) {
                defineValue(solution, '$changedFiles', solution.files.filter(function (sf) {
                  var cf = challengeFiles.find(function (cf) {
                    return cf.id === sf.challengeFileId || cf.path === sf.path;
                  });
                  return !cf || cf.$access === 'readwrite' && cf.contents !== sf.contents;
                }));
              }
              return solution.$changedFiles;
            },
            isCompleted: function isCompleted() {
              return !!this.submissionStatus;
            },
            isSubmitting: function isSubmitting() {
              return this.submissionStatus === 'submitting' || this.isRetrying();
            },
            isRetrying: function isRetrying() {
              return this.submissionStatus === 'retrying';
            },
            isSubmitted: function isSubmitted() {
              return !!this.submissionStatus && !this.isSubmitting();
            },
            isSubmissionSuccess: function isSubmissionSuccess() {
              return this.submissionStatus === 'successful';
            },
            isSubmissionInvalid: function isSubmissionInvalid() {
              return this.submissionStatus === 'invalid';
            },
            isSubmissionTimeout: function isSubmissionTimeout() {
              return this.submissionStatus === 'timed_out';
            },
            generateSubmissionHash: function generateSubmissionHash() {
              var data = this.files.map(function (f) {
                return f.path + '\n' + f.contents;
              }).join('\n');
              return hashCode(data);
            },
            download: function download(user) {
              try {
                var params = {
                  x_login_type: user.loginType,
                  $type: this.$type
                };
                var url = Solution.getEndpointUrl({
                  id: this,
                  segment: 'download',
                  params: params
                });
                if (!$window.open(url)) {
                  throw new Error('Unable to open a new window');
                }
              } catch (ex) {
                $window.alert('Unable to open the download file: ' + ex);
              }
            }
          }),
          QuizSolution: {
            answered: function answered(index) {
              var _didAnswer = function _didAnswer(a) {
                return !!(a && a.values && (a.values[0] || a.values[0] === 0));
              };
              var answers = this.answers || [];
              if (angular.isNumber(index)) {
                return _didAnswer(answers[index]);
              } else {
                return answers.reduce(function (sum, a) {
                  if (_didAnswer(a)) {
                    sum++;
                  }
                  return sum;
                }, 0);
              }
            },
            answeredCorrectly: function answeredCorrectly() {
              return (this.answers || []).reduce(function (sum, a) {
                if (a.score === 100) {
                  sum++;
                }
                return sum;
              }, 0);
            },
            totalTests: function totalTests() {
              var challenge = this.getChallengeSnapshot();
              var questions = challenge && challenge.questions;
              if (questions) {
                return questions.length;
              } else {
                // guestimate
                return (this.answers || []).length;
              }
            },
            passedSummary: function passedSummary() {
              return (isTeamMember ? this.answeredCorrectly() : this.answered()) + ' / ' + this.totalTests() + ' Questions';
            },
            passedSummaryLong: function passedSummaryLong() {
              return (isTeamMember ? this.answeredCorrectly() : this.answered()) + ' of ' + this.totalTests() + ' questions answered correctly';
            },
            getStatus: function getStatus() {
              var _this = this;

              var answered = this.answered();
              var total = this.totalTests();
              if (this.passed()) {
                // got 100% or answered every question as a candidate
                return statuses.completed;
              } else if (answered < total) {
                // didn't answer all
                return statuses.incomplete;
              } else {
                // answered all && didn't get 100%
                return scoreToStatus.find(function (ss) {
                  return _this.score <= ss[0];
                })[1];
              }
            },
            allAnswered: function allAnswered() {
              return this.answered() >= this.totalTests();
            },
            passed: function passed() {
              return this.score !== undefined ? this.score === 100 : this.allAnswered();
            },
            attempted: function attempted() {
              return this.answered() > 0;
            },

            // returns the first question that needs an answer still, or -1 if there are no
            needsAnswerIdx: function needsAnswerIdx() {
              var challenge = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.getChallengeSnapshot();
              var answers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.answers || [];

              return answers.findIndex(function (ans) {
                var q = challenge.questions.find(function (q) {
                  return q.id === ans.questionId;
                });
                return q && !ans.completedAt && (!ans.startedAt || new Date() - ans.startedAt < q._timeLimitSec(challenge) * 1000);
              });
            },
            canStillAnswer: function canStillAnswer() {
              var challenge = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.getChallengeSnapshot();

              if (challenge.isTimed() && this.timerStartedAt) {
                var timeoutMs = challenge.timeLimitMin * 60 * 1000 - (new Date() - this.timerStartedAt);
                return timeoutMs > 0;
              } else if (challenge.isQuestionTimed()) {
                return this.needsAnswerIdx(challenge) > -1;
              }
              return true;
            },
            hasEmptyMultipleChoice: function hasEmptyMultipleChoice() {
              var _this2 = this;

              var challenge = this.getChallengeSnapshot();
              var questions = challenge.questions || [];
              return this.canStillAnswer(challenge) && questions.some(function (q) {
                var a = (_this2.answers || []).find(function (a) {
                  return a.questionId === q.id;
                });
                return q.isMultipleChoice() && (!a || !a.values || a.values.length === 0);
              });
            },

            updateScore: angular.noop,
            hasTags: function hasTags() {
              return false;
            },
            tagSummary: function tagSummary() {
              return '';
            }
          }
        },
        actionEndpoints: {
          resetTimer: { method: 'PUT' },
          reset: { method: 'PUT' },
          resetTestCases: { method: 'PUT' },
          resetSolution: { method: 'PUT' },
          logAttempt: { method: 'PUT' },
          logTest: { method: 'PUT' },
          submit: { method: 'PUT', ignoreLoadingBar: true },
          heartbeat: {
            method: 'PUT',
            ignoreLoadingBar: true,
            ignoreNetworkErrors: true,
            log: false,
            debug: false
          },
          loadRevisions: {
            method: 'GET',
            segment: 'revisions',
            responseHandler: function responseHandler(res, Model, instance) {
              if (angular.isArray(res.data.data)) {
                return instance.revisions = parseDates(res.data.data);
              } else {
                return $q.reject();
              }
            }
          },
          updateAuthenticity: {
            method: 'PATCH',
            segment: 'authenticity',
            responseHandler: function responseHandler(res, Model, instance) {
              // Update the instance's authenticity with the response data
              if (res.data && res.data.data && res.data.data.authenticity) {
                instance.authenticity = res.data.data.authenticity;
              }
              return instance;
            }
          }
        },
        excludeSaveOrCreateProps: ['files'],
        includeSaveOrCreateChange: function includeSaveOrCreateChange(k, value) {
          return k !== 'cursor' && (k !== 'tag' || value !== 'autosave');
        },
        afterSaveOrCreate: function afterSaveOrCreate(instance, changes, requestOptions) {
          var fields = [];
          if (instance.$type === 'AdvancedCodeSolution') {
            // Ensure files with softDeletedAt are included in the updates
            var files = instance.files || [];
            var updates = changes && changes.files ? changes.files : [];

            // Add files with softDeletedAt to the updates if they are not already included
            files.forEach(function (file) {
              if (file.softDeletedAt && !updates.some(function (update) {
                return update.id === file.id;
              })) {
                updates.push(file);
              }
            });

            // If there are updates, ensure they are included in the changes
            if (updates.length > 0 && changes) {
              changes.files = updates;
            }

            fields = {
              Model: DS.definitions.AdvancedCodeSolutionFile,
              fieldName: 'files',
              foreignKey: 'solutionId',
              changes: changes,
              requestOptions: requestOptions,
              orderable: false,
              exclude: function exclude(file) {
                return file.encrypted || file.$access !== 'readwrite' && file.$access !== 'readonly' && !file.softDeletedAt;
              },
              diff: function diff$$1(file, fileChanges) {
                // If the file has been soft deleted, make sure to include that in the diff
                if (fileChanges.softDeletedAt) {
                  return { softDeletedAt: fileChanges.softDeletedAt };
                }

                var uiChanges = {
                  options: fileChanges.options,
                  cursor: fileChanges.cursor
                };
                // only allow UI settings to be stored for readonly files
                return diff(file, file.$access !== 'readwrite' ? uiChanges : fileChanges);
              },

              uniqueChildField: 'path'
            };
          }
          return ModelHelper.saveEmbedded(fields);
        }
      }
    });

    return Solution;
  }]);

  var solutionReviewModelModule = angular.module('Q.models.solution-review', [modelHelperModule]).factory('SolutionReview', ['DS', 'ModelHelper', '$injector', function (DS, ModelHelper, $injector) {
    var currentUser = getUser.bind(null, $injector);

    var SolutionReview = ModelHelper.defineResource({
      name: 'SolutionReview',
      endpoint: 'solution_reviews',

      methods: {
        isMyReview: function isMyReview() {
          return this.reviewedBy === currentUser();
        },
        reviewerName: function reviewerName() {
          var staffName = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'Qualified Staff';

          return this.reviewedBy && this.reviewedBy.nameOrEmail || (this.reviewedByStaff ? staffName : '(Unknown Reviewer)');
        },
        isSubmitted: function isSubmitted() {
          return !!this.assessmentResultReview && this.assessmentResultReview.isSubmitted();
        },
        shouldShow: function shouldShow() {
          return this.isMyReview() || !this.reviewedByStaff || this.isSubmitted();
        }
      },
      staticMethods: {
        getFor: function getFor(solution, teamMember) {
          var tm = teamMember || currentUser();
          return tm && SolutionReview.filter({
            solutionId: solution.id,
            reviewedById: tm.id
          })[0];
        }
      },

      relations: {
        hasOne: {
          TeamMember: {
            localField: 'reviewedBy',
            localKey: 'reviewedById'
          }
        },
        belongsTo: {
          Assessment: {
            localField: 'assessment',
            localKey: 'assessmentId'
          },
          AssessmentResult: {
            localField: 'assessmentResult',
            localKey: 'assessmentResultId'
          },
          AssessmentResultReview: {
            localField: 'assessmentResultReview',
            localKey: 'assessmentResultReviewId'
          },
          Challenge: {
            localField: 'challenge',
            localKey: 'challengeId'
          },
          Solution: {
            localField: 'solution',
            localKey: 'solutionId'
          },
          TeamCandidate: {
            localField: 'teamCandidate',
            localKey: 'teamCandidateId'
          }
        },
        hasMany: {
          ReviewQuality: [{
            localField: 'positiveQualities',
            localKeys: 'positiveQualityIds'
          }, {
            localField: 'negativeQualities',
            localKeys: 'negativeQualityIds'
          }]
        }
      },

      meta: {
        fetchOnPush: function fetchOnPush(data) {
          // only store the review if we have the assessment result
          return DS.get('AssessmentResult', data.assessmentResultId || '');
        }
      }
    });

    return SolutionReview;
  }]);

  var ROLES_ARRAY = [{
    id: 'owner',
    level: 50,
    premium: true,
    name: 'Account Manager',
    plural: 'Account Managers',
    info: 'This role has full access to the team account.',
    setupHelp: 'have full access to the account including billing and team management. This is typically the Hiring Manager, whether in Recruiting or Engineering.',
    order: 4
  }, {
    id: 'inviter',
    level: 10,
    premium: true,
    name: 'Manager',
    plural: 'Managers',
    info: 'This role has full access to the team account except for billing and user management.',
    setupHelp: 'can view all the {{participants}} in a process and invite new {{participants}}. This role is usually a Team Lead, Recruiter, or Technical Interviewer.',
    order: 3
  }, {
    id: 'reviewer',
    level: 5,
    premium: false,
    name: 'Reviewer',
    plural: 'Reviewers',
    info: 'This role is restricted to reviewing {{participants}} only; no manager permissions.',
    setupHelp: 'have the ability to view all {{participant}} details and review their submissions. This is typically an Engineer, Technical Evaluator or Assistant Recruiter.',
    order: 2
  }, {
    id: 'blind_reviewer',
    level: 2,
    premium: false,
    name: 'Blind Reviewer',
    plural: 'Blind Reviewers',
    info: 'This role can review {{participants}} while seeing no personally identifiable information.',
    setupHelp: "are Reviewers that can't see personally identifiable details. This removes unconscious bias from the hiring process. It removes race, gender, or other subjective factors, and focuses the {{participant}} review on performance and skill.",
    order: 1
  }, {
    id: 'staff',
    level: 100,
    name: 'Staff',
    plural: 'Staff',
    info: "Looks like you're one of us!",
    setupHelp: 'for Qualified internal use only.',
    order: 0,
    hidden: true
  }];

  var ROLES = ROLES_ARRAY.reduce(function (ROLES, r) {
    ROLES[r.id] = r;
    return ROLES;
  }, {});

  var ROLE_GROUPS = [{
    id: 'premium',
    name: 'Premium',
    roles: ROLES_ARRAY.filter(function (r) {
      return r.premium === true;
    }).map(function (r) {
      return r.id;
    })
  }, {
    id: 'restricted',
    name: 'Restricted',
    roles: ROLES_ARRAY.filter(function (r) {
      return r.premium === false;
    }).map(function (r) {
      return r.id;
    })
  }];

  var teamMemberModelModule = angular.module('Q.models.team-member', [modelHelperModule]).factory('TeamMember', ['ModelHelper', function (ModelHelper) {
    var roleIds = ROLES_ARRAY.map(function (role) {
      return role.id;
    });
    var findRoleById = function findRoleById(id) {
      return ROLES_ARRAY.filter(function (role) {
        return role.id === id;
      })[0];
    };

    function isRole(currentRoleId, minimumRoleId) {
      var currentRole = findRoleById(currentRoleId),
          minimumRole = findRoleById(minimumRoleId);
      if (!currentRole || !minimumRole) return false;
      return currentRole.level >= minimumRole.level;
    }

    var TeamMember = ModelHelper.defineResource({
      name: 'TeamMember',
      endpoint: 'team_members',

      serialize: ModelHelper.serializeWithUiPrefs,
      deserialize: ModelHelper.deserializeWithUiPrefs,

      relations: {
        hasMany: {
          Assessment: {
            foreignKey: 'teamMemberId',
            foreignField: 'teamMember'
          }
        },
        belongsTo: {
          Team: {
            localKey: 'teamId',
            localField: 'team'
          }
        }
      },

      computed: {
        nameOrEmail: /* @ngInject */['name', 'email', function nameOrEmail(name, email) {
          return name || email;
        }],
        assignable: /* @ngInject */['state', 'role', function assignable(state, role) {
          return state !== 'inactive' && role !== 'demo';
        }]
      },

      methods: {
        loginType: 'team_member',
        publishedFirstJob: function publishedFirstJob() {
          return !this.team || this.team.publishedFirstJob;
        },
        hasRole: function hasRole(role) {
          return !role || isRole(this.mockRole || this.role, role);
        },
        hasPrivilege: function hasPrivilege(privilege, value) {
          return checkPrivilege(this, privilege, value);
        },
        isActive: function isActive() {
          return this.state === 'active';
        },
        isInactive: function isInactive() {
          return this.state === 'inactive';
        },
        isPremiumRole: function isPremiumRole() {
          return this.role === 'owner' || this.role === 'inviter';
        },
        isRestrictedRole: function isRestrictedRole() {
          return this.role === 'reviewer' || this.role === 'viewer' || this.role === 'blind_reviewer';
        },
        isBlindReviewer: function isBlindReviewer() {
          return !this.privileges.viewPiiInfo;
        },
        isDemo: function isDemo() {
          return this.role === 'demo';
        },
        hasFeature: function hasFeature(feature, featureLevels) {
          if (this.features && this.features[feature] !== undefined) {
            return !!(this.features && this.features[feature]);
          }
          if (this.team) {
            return this.team.hasFeature(feature, featureLevels);
          }
          return false;
        },
        getRoleName: function getRoleName() {
          var role = findRoleById(this.role);
          return role && role.name;
        },
        stateName: function stateName() {
          return this.getEnum('state');
        },
        toString: function toString() {
          return this.nameOrEmail;
        },
        setPrivilege: function setPrivilege(privilege, value, options) {
          var privileges = angular.copy(this.privileges);
          privileges[privilege] = value;
          return TeamMember.updatePrivileges(this, { data: privileges }, options);
        },
        setPrivileges: function setPrivileges(privileges, options) {
          return TeamMember.updatePrivileges(this, { data: privileges }, options);
        }
      },

      staticMethods: {
        getUsableRoles: function getUsableRoles(user) {
          return roleIds.filter(user.hasRole.bind(user)).map(findRoleById);
        }
      },

      meta: {
        actionEndpoints: {
          resendInvite: { method: 'PUT', track: true },
          activate: { method: 'PUT', track: true },
          deactivate: { method: 'PUT', track: true },
          updatePrivileges: {
            method: 'PUT',
            segment: 'privileges',
            responseHandler: function responseHandler(res, TeamMember, instance) {
              return TeamMember.refresh(instance);
            }
          }
        },
        saveExcludes: ['team']
      }
    });

    return TeamMember;
  }]);

  var appAlertTemplate = "<div ng-if=\"appAlert.active\" class=\"app-alert alert alert-dismissable alert-{{ appAlert.style || 'warning' }}\"> {{ appAlert.message }}<span ng-bind-html=\"appAlert.htmlMessage\"></span> <a ng-if=\"appAlert.link\" class=\"alert-link\" ui-sref=\"{{ appAlert.link }}\" ng-click=\"onLinkClick()\" ng-bind=\"appAlert.linkTitle\"></a> <a ng-if=\"appAlert.linkAction && !appAlert.link\" class=\"alert-link\" href ng-click=\"onLinkClick()\" ng-bind=\"appAlert.linkTitle\"></a> <button class=\"close\" data-dismiss=\"alert\" aria-label=\"Close\" ng-show=\"appAlert.closeable\" ng-click=\"appAlert.clear()\"> <span aria-hidden=\"true\">&times;</span> </button> </div> ";

  var RETRY_TIMEOUT = 500;
  // retry these codes on GET requests
  var RETRY_ON_GET_STATUS_CODES = [0, 502, 503, 504, 520, 598, 599];
  // these are errors returned by Cloudflare, and we should retry them even if it's not a GET request, because the backend didn't respond at all
  var RETRY_ALWAYS_STATUS_CODES = [521, 522, 523, 524, 525, 526, 527, 530];
  // don't bother logging these codes (errors due to client mistakes)
  var NEVER_LOGGED_STATUS_CODES = [-1, 400, 401, 404, 423];

  // these codes mean a network error has happened somewhere
  var NETWORK_ERROR_CODES = [-1].concat(RETRY_ON_GET_STATUS_CODES, RETRY_ALWAYS_STATUS_CODES);

  /**
   * This interceptor will automatically retry any GET requests that have one of the
   * status codes listed above.  It will only retry once until there is at least one successful request.
   * It has a timeout between the first and follow-up request.
   */
  var autoRetryInterceptorModule = angular.module('Q.interceptors.auto-retry-interceptor', []).factory('autoRetryInterceptor', ['$q', '$injector', '$timeout', '$log', function ($q, $injector, $timeout, $log) {
    var shouldRetry = true;

    return {
      responseError: function responseError(response) {
        var config = response && response.config || {};
        var methodIsGet = config.method === 'GET';
        var invalidStatus = methodIsGet && response.status < 0;
        var validGetRequest = methodIsGet && RETRY_ON_GET_STATUS_CODES.indexOf(response.status) !== -1;
        var validOtherRequest = RETRY_ALWAYS_STATUS_CODES.indexOf(response.status) !== -1;

        if (shouldRetry && config.autoRetry !== false && (invalidStatus || validGetRequest || validOtherRequest)) {
          shouldRetry = false;

          return $timeout(function () {
            var $http = $injector.get('$http');
            return $http(response.config);
          }, RETRY_TIMEOUT);
        } else if (response && NEVER_LOGGED_STATUS_CODES.indexOf(response.status) === -1 && config.ignoreNetworkErrors !== true) {
          // log to sentry
          $log.error('$http error: ' + response.status + ' - ' + response.statusText, response);
        }

        // give up
        return $q.reject(response);
      },
      response: function response(_response) {
        shouldRetry = true;
        // Leaving this here (commented out) as a tool for future testing.
        // To use: set any values on window.mock, such as status, statusText, or data, and they'll override
        // the actual values for testing handling of http responses
        // if(window.mock) {
        // 	angular.extend(response, window.mock);
        // 	$log.debug('[mocked http response]', response);
        // }
        return _response;
      }
    };
  }]).config(['$httpProvider', function ($httpProvider) {
    $httpProvider.interceptors.push('autoRetryInterceptor');
  }]);

  var _modelErrorTypeMappin;

  var modelErrorTypeMappings = (_modelErrorTypeMappin = {}, defineProperty(_modelErrorTypeMappin, WEIGHTED_CHOICE_QUESTION, 'Multiple Choice Questions'), defineProperty(_modelErrorTypeMappin, TEXT_QUESTION, 'Text Questions'), _modelErrorTypeMappin);

  var errorMessages = {
    serverUnavailable: 'Unable to save your changes, the server is temporarily unavailable. Please try again.',
    unknownErrorTeamMember: 'An error has occurred and our team has been alerted. Please try again soon. If the issue persists, please reach out to us via in-app messaging or team@qualified.io.',
    unknownErrorCandidate: 'An error has occurred and our team has been alerted. Please try again soon. If the issue persists, please reach out to the team that invited you.'
  };

  var serverAccessErrors = [].concat(RETRY_ON_GET_STATUS_CODES, RETRY_ALWAYS_STATUS_CODES);

  var appAlertComponentModule = angular.module('Q.components.app-alert', []).directive('appAlert', ['appAlert', function (appAlert) {
    return {
      restrict: 'AE',
      scope: {},
      template: appAlertTemplate,
      link: function link(scope) {
        scope.appAlert = appAlert;
        scope.onLinkClick = function () {
          if (appAlert.linkAction) {
            appAlert.linkAction();
          }
          appAlert.clear();
        };
      }
    };
  }]).factory('appAlert', ['$timeout', '$q', '$log', '$sce', '$compile', '$rootScope', '$document', function ($timeout, $q, $log, $sce, $compile, $rootScope, $document) {
    var lastCloser = void 0;
    var appAlertEl = void 0;

    var alert = {
      active: false,
      style: null,
      message: null,
      timer: null,
      clear: clear,
      show: function show(msg, type) {
        if (!angular.isFunction(alert[type])) {
          type = 'info';
        }
        alert[type](msg);
      }
    };

    ['success', 'info', 'warning', 'danger'].forEach(function (s) {
      alert[s] = _show.bind(alert, s);
    });
    alert.error = alert.danger;

    /**
     * Returns an error message based on some unknown error object
     * @param {*} resp - Error object
     * @param {Object} options - Additional Options
     * @param {string} options.prefix - Prefix for returned message
     * @returns {{message: string, status: string|boolean}} Message and the error status code (or false if none)
     */
    alert.modelErrorMessage = function (resp) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      var errors = [];

      var data = resp;
      if (data && data.data) {
        // so we can pass responses directly to this method
        data = data.data;
      }

      function pushError(field, msg) {
        field = fieldToLabel(field);
        var type = '';
        if (data && modelErrorTypeMappings[data.type]) {
          type = modelErrorTypeMappings[data.type] + ': ';
        }
        errors.push('' + type + field + ' ' + msg);
      }

      var message = false;
      var status = false;

      if (!data) {
        message = errorMessages.serverUnavailable;
      } else if (angular.isString(data) && !/<html>/.test(data) && (resp.status === undefined || resp.status < 500)) {
        // don't show raw 5xx errors in the alert
        message = data;
      } else if (data.reason === 'ValidationErrors') {
        // each field is returned with its own array of error messages
        angular.forEach(data.errors, function (msgs, field) {
          if (angular.isObject(msgs)) {
            angular.forEach(msgs, function (msg) {
              // embedded documents can also have their errors included, so we need to
              // go another level deep in this case
              if (angular.isObject(msg)) {
                angular.forEach(msg, function (msg, field) {
                  pushError(field, msg);
                });
              } else {
                pushError(field, msg);
              }
            });
          } else {
            pushError(field, msgs);
          }
        });

        message = errors.join(', ');
      } else if (serverAccessErrors.includes(resp.status)) {
        message = errorMessages.serverUnavailable;
        status = resp.status;
      } else {
        if (resp instanceof Error) {
          // in case something else is triggering the error
          $log.error(resp);
          message = resp.toString();
          if (resp.cause) {
            message += ' (' + resp.cause + ')';
          }
        } else if (data.reason) {
          message = data.reason;
        } else {
          if (resp.statusText) {
            message = resp.statusText;
          } else if (appConfig.candidateApp) {
            message = errorMessages.unknownErrorCandidate;
          } else {
            message = errorMessages.unknownErrorTeamMember;
          }
          status = resp.status;
        }
      }
      if (options.prefix) {
        message = options.prefix + ' ' + message;
      }
      return { message: message, status: status };
    };

    /**
     * Handles rejected promises automatically, or can be used independently
     * @param {*} resp - Error object
     * @param {Object} options - Additional Options
     * @param {string} options.prefix - Prefix for returned message
     * @returns {PromiseRejectedResult} Rejected promise with original response
     */
    alert.modelError = function (resp) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      if (resp && !resp.didAlert) {
        var _alert$modelErrorMess = alert.modelErrorMessage(resp, options),
            message = _alert$modelErrorMess.message,
            status = _alert$modelErrorMess.status;

        if (status) {
          message = $sce.trustAsHtml(escapeHTML(message) + ' <span class="extra-detail">(' + status + ')</span>');
        }
        alert.error(message, options);
      }

      return $q.reject(resp);
    };

    //
    /**
     * Enables providing options to the modelError function
     * @param {Object} options - Additional Options
     * @param {string} options.prefix - Prefix for returned message
     * @returns {Function} Rejected promise handler
     */
    alert.modelError.with = function (options) {
      return function (resp) {
        return alert.modelError(resp, options);
      };
    };

    function clear() {
      alert.active = false;
      _cancelTimer();
      // delay so the fade looks OK
      $timeout(function () {
        if (!alert.active) {
          alert.message = null;
          alert.htmlMessage = null;
          alert.link = null;
          alert.linkTitle = null;
          alert.action = null;
        }
      }, 150);
    }

    function _cancelTimer() {
      if (alert.timer) $timeout.cancel(alert.timer);
      alert.timer = null;
    }

    /**
     * Shows a message in the given style
     * @param style {String} Style for the alert
     * @param msg {String} Message to show
     * @param options {Number|boolean|Object?} If a number or boolean, represents the timeout, otherwise an object with the following
     * @param options.showFor {Number|boolean?} Delay, or false to show until canceled with appAlert.clear()
     * @param options.linkTitle {String?} Title for the link
     * @param options.link {String?} uiSref-style link for the link
     * @param options.linkAction {Function} Function to call if the link is clicked
     * @returns {Promise}
     * @private
     */
    function _show(style, msg) {
      var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

      if (!appAlertEl) {
        appAlertEl = $compile('<app-alert></app-alert>')($rootScope);
        $document.find('body').append(appAlertEl);
      }

      _cancelTimer();

      alert.style = style;
      if (angular.isString(msg)) {
        alert.message = msg;
        alert.htmlMessage = '';
      } else {
        alert.message = '';
        alert.htmlMessage = msg;
      }

      if (!angular.isObject(options)) {
        options = { showFor: options };
      }
      var _options = options,
          _options$showFor = _options.showFor,
          showFor = _options$showFor === undefined ? style === 'danger' ? 0 : 6000 : _options$showFor,
          _options$linkTitle = _options.linkTitle,
          linkTitle = _options$linkTitle === undefined ? null : _options$linkTitle,
          _options$link = _options.link,
          link = _options$link === undefined ? null : _options$link,
          _options$linkAction = _options.linkAction,
          linkAction = _options$linkAction === undefined ? null : _options$linkAction;


      alert.linkTitle = linkTitle;
      alert.link = link;
      alert.linkAction = linkAction;
      if (!alert.active) {
        $timeout(function () {
          return alert.active = true;
        });
      }
      alert.closeable = showFor !== false;
      var promise = void 0;
      if (showFor > 0) {
        alert.timer = $timeout(clear, showFor);
        promise = lastCloser = alert.timer;
      } else {
        promise = lastCloser = $q.when();
      }
      promise.close = function () {
        if (lastCloser === promise) {
          alert.clear();
        }
      };
      return promise;
    }

    return alert;
  }]);

  var teamModelModule = angular.module('Q.models.team', [modelHelperModule, appAlertComponentModule]).factory('Team', ['ModelHelper', '$q', 'appAlert', function (ModelHelper, $q, appAlert) {
    var Team = ModelHelper.defineResource({
      name: 'Team',
      endpoint: 'teams',

      computed: {
        isSampleTeam: /* @ngInject */['isPracticeTeam', 'isDemoTeam', function isSampleTeam(isPracticeTeam, isDemoTeam) {
          return isPracticeTeam || isDemoTeam;
        }]
      },

      relations: {
        belongsTo: {
          Customer: {
            localField: 'customer',
            localKey: 'customerId',
            link: false
          }
        },
        hasMany: {
          Assessment: {
            localField: 'assessments',
            foreignKey: 'teamId'
          },
          TeamMember: {
            localField: 'teamMembers',
            foreignKey: 'teamId'
          }
        }
      },

      methods: {
        hasManualFeature: function hasManualFeature(feature) {
          return !!(this.manualFeatures && this.manualFeatures[feature]);
        },
        hasFeature: function hasFeature(feature, featureLevels) {
          if (featureLevels) {
            if (!Array.isArray(featureLevels)) {
              featureLevels = [featureLevels];
            }
            feature = this.getFeature(feature);
            return featureLevels.some(function (f) {
              return f === feature;
            });
          } else {
            return !!this.getFeature(feature);
          }
        },
        labKeys: function labKeys() {
          var _this = this;

          return Object.keys(this.labs || {}).filter(function (k) {
            return _this.customer.getFeature(k);
          });
        },
        getFeature: function getFeature(feature) {
          if (!feature) {
            return true;
          }
          if (feature === 'labs') {
            // see if we have access to any labs features
            return this.labKeys().length;
          }

          var f = this.manualFeatures && this.manualFeatures[feature];
          if (f) {
            return f;
          }

          // check if the feature is in labs
          f = this.labs && this.labs[feature];
          if (typeof f === 'boolean') {
            return f && this.customer.getFeature(feature);
          }

          return this.customer.getFeature(feature);
        },
        isNewTeam: function isNewTeam() {
          // 604,800,000 = 7 days in milliseconds
          var sevenDaysAgo = Date.now() - 604800000;
          return new Date(this.createdAt || new Date()).getTime() > sevenDaysAgo;
        },

        /**
         * Returns true if the team is allowed to use credits
         */
        usageAllowed: function usageAllowed() {
          return this.customer.isActive;
        },

        /**
         * Returns true if the team hasn't had any candidate submissions yet,
         * unless they already completed setup
         * Used mainly to know when to switch to the setup guide screen
         * @returns {boolean}
         */
        accountNeedsSetup: function accountNeedsSetup(teamMember) {
          if (teamMember && !teamMember.hasRole('reviewer')) return false;
          var progress = this.progressEvents || {};
          return !this.setupComplete && !progress.candidateInvited;
        },
        subscriptionExpired: function subscriptionExpired(teamMember) {
          if (teamMember && teamMember.role === 'staff') return false;
          return this.customer.subscriptionExpired(teamMember);
        },
        isSubscribed: function isSubscribed() {
          return this.customer.isSubscribed;
        },
        isManualSubscriber: function isManualSubscriber() {
          return !!this.customer.currentContractId;
        },
        isSystemTeam: function isSystemTeam() {
          return !!this.customer.isNonBillable;
        },
        payAsYouGo: function payAsYouGo() {
          return this.customer.isPayg();
        },
        canRenew: function canRenew() {
          return true;
        },
        hasQualifiedTrial: function hasQualifiedTrial() {
          return this.customer.isInTrial();
        },
        hasQualifiedPlan: function hasQualifiedPlan() {
          return this.customer.hasQualifiedPlan();
        },
        isEducation: function isEducation() {
          return this.productTypes && !this.productTypes.recruitment && this.productTypes.education;
        },
        isQualifiedLoginAllowed: function isQualifiedLoginAllowed() {
          return this.loginProviders.includes('qualified');
        },
        usageMeasureName: function usageMeasureName(plural) {
          switch (this.customer.usageMeasure()) {
            case 'assessment_result':
              return plural ? 'Assessment Results' : 'Assessment Result';

            case 'team_candidate':
              if (this.isEducation()) {
                return plural ? 'Student' : 'Students';
              }

              return plural ? 'Candidate' : 'Candidates';
          }
        },

        /**
         * Returns the collection of all known tags for this team
         */
        findAllTags: function findAllTags() {
          var _this2 = this;

          var self = this;
          return $q.resolve().then(function () {
            if (!self._tags) {
              return Team.tags(_this2).then(function (tags) {
                var rootTags = {};
                angular.forEach(tags, function (modelTags) {
                  angular.forEach(modelTags, function (typeTags, type) {
                    if (Array.isArray(typeTags)) {
                      rootTags[type] = rootTags[type] || [];
                      typeTags.forEach(function (t) {
                        if (rootTags[type].indexOf(t) === -1) rootTags[type].push(t);
                      });
                    }
                  });
                });
                angular.forEach(rootTags, function (rt, type) {
                  tags[type] = rt;
                });
                defineValue(self, '_tags', tags);
                return tags;
              });
            } else {
              return self._tags;
            }
          });
        },

        /**
         * Returns a matching set of tags for this team
         * @param type {String} currently either `labels` or `topics`
         * @param model {String?} if provided, only return types for the given model
         */
        findTags: function findTags(type, model) {
          return this.findAllTags().then(function (tags) {
            var tagsList = tags[type];
            if (model) {
              tagsList = tags[model] && tags[model][type];
            }
            tagsList = tagsList || [];
            return tagsList.sort(compareIgnoreCase);
          });
        },

        /**
         * Updates the existing tags (if we've already downloaded them)
         * @param model {String} Model type being updated
         * @param type {String} Tag type (labels or topics)
         * @param tags {Array<String>} Updated list of tags
         */
        updateTags: function updateTags(model, type, tags) {
          if (this._tags && tags && tags.length) {
            var add = function add(to) {
              tags.forEach(function (t) {
                if (to.indexOf(t) === -1) {
                  to.push(t);
                }
              });
            };
            if (!this._tags[model]) this._tags[model] = {};
            if (!this._tags[model][type]) this._tags[model][type] = [];
            add(this._tags[model][type], tags);
            if (!this._tags[type]) this._tags[type] = [];
            add(this._tags[type], tags);
          }
        },
        flagProgressEvent: function flagProgressEvent(progressEvent) {
          var data = { progressEvent: progressEvent };
          Team.flagProgressEvent(this, { data: data });
        },
        customerState: function customerState() {
          return fieldToLabel(this.customer && this.customer.state || '') || '—';
        },
        disableWarnOnLongAssessments: function disableWarnOnLongAssessments() {
          this.warnOnLongAssessments = false;
          return Team.update(this, { warnOnLongAssessments: false }).catch(appAlert.modelError);
        }
      },

      staticMethods: {
        progressFlags: {
          demoCandidateViewed: 'demo_data_candidate_viewed',
          demoPlaybackViewed: 'demo_data_playback_viewed',
          demoReviewViewed: 'demo_data_code_review_viewed'
        }
      },

      meta: {
        actionEndpoints: {
          generateApiKey: { method: 'PUT' },
          buildDemoData: {
            method: 'PUT',
            responseHandler: function responseHandler(res, TeamMember, instance) {
              instance.buildingDemoData = true;
              return res;
            }
          },
          destroyDemoData: {
            method: 'PUT',
            responseHandler: function responseHandler(res, TeamMember, instance) {
              instance.destroyingDemoData = true;
              return res;
            }
          },
          flagProgressEvent: { method: 'PUT' },
          signMsa: { method: 'PUT', track: true },
          updateBilling: { method: 'PUT', track: true },
          subscribe: { method: 'PUT', track: true },
          renewSubscription: { method: 'PUT', track: true },
          endTrial: { method: 'PUT', track: true },
          bambooSubdomain: { method: 'PUT', track: true },
          leverTriggerStage: { method: 'PUT', track: true },
          resetLever: { method: 'PUT', track: true },
          tags: {
            method: 'GET',
            responseHandler: function responseHandler(resp) {
              var data = resp && resp.data;
              return data.data || data;
            }
          }
        },
        saveExcludes: ['assessments', 'teamMembers']
      }
    });

    return Team;
  }]);

  var userFeedbackModelModule = angular.module('Q.models.user-feedback', [modelHelperModule]).factory('UserFeedback', ['ModelHelper', 'DS', '$q', function (ModelHelper, DS, $q) {
    var UserFeedback = ModelHelper.defineResource({
      name: 'UserFeedback',
      endpoint: 'user_feedbacks',
      computed: {
        teamMember: {
          get: function get() {
            return DS.get('TeamMember', this.userId);
          }
        },
        candidate: {
          get: function get() {
            return DS.get('Candidate', this.userId);
          }
        }
      },
      staticMethods: {
        findExisting: function findExisting(_ref) {
          var feedbackType = _ref.feedbackType,
              featureName = _ref.featureName,
              challenge = _ref.challenge,
              solution = _ref.solution,
              assessment = _ref.assessment,
              assessmentResult = _ref.assessmentResult;

          var where = { feedbackType: feedbackType, featureName: featureName };
          if (solution && solution.id) {
            where.solutionId = solution.id;
          } else if (challenge && challenge.id) {
            // only reviewing a challenge, no solution
            where.challengeId = challenge.id;
            where.solutionId = null;
          }
          if (assessmentResult && assessmentResult.id) {
            where.assessmentResultId = assessmentResult.id;
          } else if (assessment && assessment.id) {
            // only reviewing an assessment without an assessment result
            where.assessmentId = assessment.id;
            where.assessmentResultId = null;
          }
          if (Object.values(where).some(function (v) {
            return v !== undefined;
          })) {
            return UserFeedback.findAll({
              where: where,
              orderBy: 'createdAt DESC',
              per: 1
            }, {
              bypassCache: true,
              cacheResult: false
            }).then(function (items) {
              return items[0];
            }).catch(function () {
              return null;
            });
          } else {
            return $q.when(null);
          }
        }
      },
      methods: {},
      relations: {
        hasOne: {
          AssessmentResult: {
            localField: 'assessmentResult',
            localKey: 'assessmentResultId'
          },
          Assessment: {
            localField: 'assessment',
            localKey: 'assessmentId'
          },
          Challenge: {
            localField: 'challenge',
            localKey: 'challengeId'
          },
          Solution: {
            localField: 'solution',
            localKey: 'solutionId'
          }
        }
      }
    });

    return UserFeedback;
  }]);

  var webhookSubscriptionModelModule = angular.module('Q.models.webhookSubscription', [modelHelperModule]).factory('WebhookSubscription', ['ModelHelper', function (ModelHelper) {
    return ModelHelper.defineResource({
      name: 'WebhookSubscription',
      endpoint: 'webhook_subscriptions',

      relations: {
        belongsTo: {
          Team: {
            localField: 'team',
            localKey: 'teamId'
          }
        }
      }
    });
  }]);

  /**
   * Hacky way to ensure that all models are full-loaded at runtime
   */
  var modelModules = [assessmentCohortModelModule, assessmentInvitationModelModule, assessmentModelModule, assessmentResultModelModule, assessmentResultReviewModelModule, assessmentTemplateModelModule, assessmentWorkflowPathModelModule, candidateModelModule, challengeItemModelModule, challengeModelModule, codeFileModelModule, customerModelModule, externalRevisionModelModule, invoiceModelModule, noteModelModule, quizQuestionModelModule, reviewQualityModelModule, solutionModelModule, solutionReviewModelModule, teamCandidateModelModule, teamMemberModelModule, teamModelModule, userFeedbackModelModule, webhookSubscriptionModelModule].map(function (mod) {
    return mod.name;
  });
  var allModels = {};

  // used to instantiate all the model factories
  function runtimeFn() {
    for (var _len = arguments.length, modelClasses = Array(_len), _key = 0; _key < _len; _key++) {
      modelClasses[_key] = arguments[_key];
    }

    angular.forEach(runtimeFn.$inject, function (m, i) {
      if (modelClasses[i].name) {
        allModels[m] = modelClasses[i];
      }
    });
  }

  // set all the models as the injected objects
  runtimeFn.$inject = modelModules.map(function (mod) {
    // converts name-with-hyphens to PascalCase (NameWithHyphens)
    var m = '-' + mod.match(/\.([^\.]+)$/)[1];
    return m.replace(/\-([a-z])/g, function (m, l) {
      return l.toUpperCase();
    });
  }).concat(codeFileSubclasses);

  // Using `defineProperty` to make sure this method is not iterable.
  Object.defineProperty(allModels, 'ejectAll', {
    get: function get() {
      /**
       * Ejects all instances of all models from the in-memory cache.
       *
       * opts {{include:Array, exclude:Array}} Include or Exclude models from ejecting by name
       */
      return function () {
        var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : { include: false, exclude: [] };

        angular.forEach(allModels, function (model, name) {
          if (opts.include) {
            if (opts.include.indexOf(name) !== -1) {
              model.ejectAll();
            }
          } else if (opts.exclude.indexOf(name) === -1) {
            model.ejectAll();
          }
        });
      };
    }
  });

  // create the module and initialize all factories on startup
  var allModelsModule = angular.module('Q.models.all-models', modelModules).run(runtimeFn).factory('allModels', function () {
    return allModels;
  });

  var accountServiceModule = angular.module('Q.services.account-service', [authInterceptorModule, allModelsModule, storageServiceModule, jsDataModule]).provider('accountService', function () {
    var _modelName = '';
    var STORAGE_KEY = void 0;
    var _oldStorageValue = null;
    var _includes = [];
    var logoutEjectExcludes = [];
    var _accountUrl = null;
    var _reload = true;
    var _temporary = false;
    var _skipLookup = false;

    var svc = null;

    return {
      //// Factory Configuration

      setModelName: function setModelName(modelName) {
        _modelName = modelName;
      },
      setTemporary: function setTemporary(temporary) {
        _temporary = temporary;
      },
      setIncludes: function setIncludes(includes) {
        _includes = includes;
      },
      setLogoutEjectExcludes: function setLogoutEjectExcludes(excludes) {
        logoutEjectExcludes = excludes;
      },
      setAccountUrl: function setAccountUrl(accountUrl) {
        _accountUrl = accountUrl;
      },
      setSkipLookup: function setSkipLookup(value) {
        _skipLookup = value;
      },
      getAccount: function getAccount() {
        return svc && svc.get() || {};
      },


      //// Factory Method

      $get: ['allModels', 'authInterceptor', '$localStorage', '$sessionStorage', '$http', '$q', '$rootScope', '$location', '$window', '$log', '$timeout', 'DS', function $get(allModels, authInterceptor, $localStorage, $sessionStorage, $http, $q, $rootScope, $location, $window, $log, $timeout, DS) {
        var SESSIONS_API = appConfig.apiRoot + 'sessions';

        var LOGOUT_REASON_REGEX = /^QUALIFIED_LOGOUT!(\w+)!/;
        var LOGOUT_REASONS = {
          OTHER_TAB: 'Your account was logged out in a different window',
          INACTIVE: 'Your account was logged out due to inactivity'
        };
        var logoutReason = ($window.name.match(LOGOUT_REASON_REGEX) || [0, ''])[1];

        function getStorageKey(model) {
          return 'accountService.' + lcFirst(model) + 'Id';
        }

        STORAGE_KEY = getStorageKey(_modelName);
        _oldStorageValue = $localStorage[STORAGE_KEY];

        var Model = DS.definitions[_modelName];
        var _current = {
          verified: false
        };

        var readyDeferred = $q.defer();

        $rootScope.$on('$stateChangeSuccess', _updateAccountStorage);
        $rootScope.account = _current;

        var accountChangeListener = function () {
          var unbindFn = void 0;
          return {
            enable: function enable() {
              if (!unbindFn) {
                unbindFn = listenForAccountChanges();
              }
            },
            disable: function disable() {
              // they may not have an unbindFn if the user continues
              // to use a token from before the code was updated
              if (unbindFn) {
                unbindFn();
                unbindFn = null;
              }
            }
          };
        }();

        $rootScope.$on('app.login', accountChangeListener.enable);
        $rootScope.$on('app.logout', accountChangeListener.disable);

        svc = {
          modelName: _modelName,
          model: Model,
          onLoginRedirectTo: null,
          get: getAccount,
          get loggedIn() {
            return !!_current.verified;
          },
          createAccount: createAccount,
          setAccount: setAccount,
          setLoginAccount: setLoginAccount,
          setPassword: setPassword,
          lookupCurrent: lookupCurrent,
          forgotPassword: forgotPassword,
          resetPassword: resetPassword,
          activateCheck: activateCheck,
          activate: activate,
          confirm: confirm,
          prefs: prefs,
          savePrefs: savePrefs,
          login: login,
          setRemoteAccount: setAccountStorage,
          accountChangeListener: accountChangeListener,
          reLogin: reLogin,
          logout: logout,
          logoutAndReload: logoutAndReload,
          reloadAccount: reloadAccount,
          isReady: false,
          whenReady: whenReady,
          logoutReason: logoutReason,
          LOGOUT_REASONS: LOGOUT_REASONS
        };

        readyDeferred.promise.then(function () {
          return svc.isReady = true;
        });

        return svc;

        /**
         * Generates the default includes when making an account request.
         * @returns {Array.<*>}
         * @private
         */
        function _getIncludes() {
          return [snakeCase(_modelName)].concat(_includes);
        }

        /**
         * Stores the current account info into $localStorage, updating the expiration
         * @private
         */
        function _updateAccountStorage() {
          if (_current.user && !_temporary) {
            setAccountStorage(_modelName, _current.user.id);
          }
        }

        /**
         * Returns the current account information
         * @returns {{verified: boolean, user: {Model}}}
         */
        function getAccount() {
          return _current;
        }

        /**
         * Stores the current account info, including the current user
         *
         * @param {*} user User object or ID
         * @returns {Promise} Promise if user was an ID
         */
        function setAccount(user) {
          if (angular.isObject(user)) {
            _current.user = user;
            _updateAccountStorage();
            _current.verified = true;

            $rootScope.$broadcast('app.accountSet', _current);

            readyDeferred.resolve(_current);
            return _current;
          }
        }

        /**
         * Sets the account and notifies that the user is logged in
         * @param {*} user
         * @param {*?} notifyOptions
         */
        function setLoginAccount(user) {
          var notifyOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

          setAccount(user);
          $rootScope.$broadcast('app.login', angular.extend({
            existing: true,
            account: getAccount()
          }, notifyOptions));
        }

        /**
         * Looks up the current user based on the current token
         * @returns {Promise}
         */
        function lookupCurrent() {
          if (_skipLookup) {
            readyDeferred.resolve(_current);
            return $q.resolve(_current);
          }

          var data = $localStorage[STORAGE_KEY] || {};
          return $http.get(SESSIONS_API, {
            params: {
              includes: _includes,
              remember: data.rememberMe
            },
            ignoreLoadingBar: true
          }).then(function (userData) {
            var user = Model.inject(Model.deserialize(Model.name, userData));
            setLoginAccount(user);
            return _current;
          }).catch(function (resp) {
            if (resp.status === 0 || resp.status >= 500 && resp.status < 599) {
              // don't log out due to network errors
              readyDeferred.reject('Unable to load account');
              throw new Error('Unable to load account');
            } else {
              _logout();
              readyDeferred.resolve(_current);
            }
            return $q.reject(_current);
          });
        }

        /**
         * Changes the user's password
         * @param newPassword
         * @param oldPassword
         * @returns {Promise} Promise resolved when the password has been changed (or rejected on error)
         */
        function setPassword(newPassword, oldPassword) {
          if (!_current.user) return $q.reject();

          return $http.put(appConfig.apiRoot + 'account/password', {
            data: {
              new_password: newPassword,
              old_password: oldPassword
            }
          });
        }

        /**
         * Sends off a forgot password email.
         * @param email
         * @returns {Promise}
         */
        function forgotPassword(email) {
          var redirectTo = _accountUrl + 'reset-password/:token';
          if (svc.onLoginRedirectTo) {
            // use stored redirect
            redirectTo += '?stateName=' + encodeURIComponent(svc.onLoginRedirectTo.stateName);
            if (svc.onLoginRedirectTo.params && Object.keys(svc.onLoginRedirectTo.params).length > 0) {
              redirectTo += '&params=' + encodeURIComponent(JSON.stringify(svc.onLoginRedirectTo.params));
            }
          } else {
            // otherwise redirect to the current location, minus any "forgot-password/" at the end
            // of the URL.  It's a bit hacky, but it works.
            var url = $location.url().replace(/forgot-password\/?$/, '');
            if (url && url !== '/') {
              redirectTo += '?url=' + encodeURIComponent(url);
            }
          }
          return $http.post(appConfig.apiRoot + 'account/forgot_password', {
            data: {
              $type: Model.name,
              email: email,
              redirectTo: redirectTo
            }
          });
        }

        /**
         * Resets the users password if we can find a valid token
         * @param token
         * @param password
         * @param options
         * @returns {Promise}
         */
        function resetPassword(token, password, options) {
          return $http.post(appConfig.apiRoot + 'account/reset_password', {
            data: {
              $type: Model.name,
              token: token,
              password: password,
              remember: options.rememberMe
            },
            includes: _getIncludes()
          }).then(function (res) {
            return _finishLogin(res, options);
          }, _loginErrorHandler);
        }

        /**
         * Checks an activation token, returning the user's current data (if there is no error)
         * @param token
         * @returns {Promise}
         */
        function activateCheck(token) {
          if (!token) return $q.reject(new Error('Cannot activate without a token'));
          return $http.post(appConfig.apiRoot + 'account/activate_check', {
            data: { token: token },
            includes: _getIncludes()
          }).then(function (res) {
            return res && res.data && res.data.data;
          });
        }

        /**
         * Activates an account
         * @param params {Object}
         * @param params.token
         * @param params.name
         * @param params.password
         * @param options
         * @returns {Promise}
         */
        function activate(params, options) {
          if (!params.token) return $q.reject(new Error('Cannot activate without a token'));
          return $http.post(appConfig.apiRoot + 'account/activate', {
            data: params,
            includes: _getIncludes(),
            remember: options.rememberMe
          }).then(function (res) {
            return _finishLogin(res, options);
          }, _loginErrorHandler);
        }

        /**
         * Confirms a user's email
         * @param token
         * @returns {Promise}
         */
        function confirm(token) {
          _reload = false;
          return $http.post(appConfig.apiRoot + 'account/confirm', {
            data: {
              token: token
            },
            includes: _getIncludes()
          }).then(function (res) {
            return _finishLogin(res, { showLoadingPanel: false });
          }, _loginErrorHandler);
        }

        /**
         * Get the current user preferences (or a dummy object if the user isn't logged in)
         * @returns {*}
         */
        function prefs() {
          if (_current.user) {
            if (!_current.user.uiPrefs) {
              _current.user.uiPrefs = {};
            }
            return _current.user.uiPrefs;
          } else {
            return {};
          }
        }

        /**
         * Saves the current user's preferences if the user has any changes.
         * Promise rejects if the user is not logged in
         * @returns {Promise}
         */
        function savePrefs(newPrefs) {
          if (!_current.user) {
            return $q.reject();
          } else {
            if (newPrefs) {
              angular.extend(prefs(), newPrefs);
            }
            if (!savePrefs.$promise) {
              var changes = Model.changes(_current.user);
              if (changes && (changes.changed.uiPrefs || changes.added.uiPrefs)) {
                // debounce changes
                savePrefs.$promise = $timeout(function () {
                  // delete the save promise before storing changes,
                  // so if a change comes in between we save it as a separate task
                  delete savePrefs.$promise;
                  return Model.update(_current.user, { uiPrefs: _current.user.uiPrefs }, { ignoreLoadingBar: true });
                }, 1000);
              }
            }
            // return save promise OR just resolve
            return savePrefs.$promise || $q.when(_current.user);
          }
        }

        /**
         * Logs the user in
         * @param email
         * @param password
         * @param options
         * @returns {Promise}
         */
        function login(email, password, options) {
          // clear authToken (if it exists) to allow for a clean login
          authInterceptor.clearAuthToken();
          return $http.post(SESSIONS_API, {
            data: {
              $type: Model.name,
              email: email,
              password: password,
              remember: options.rememberMe
            },
            includes: _getIncludes()
          }, {
            alwaysPassLoginType: true
          }).then(function (res) {
            return _finishLogin(res, options);
          }, _loginErrorHandler);
        }

        /**
         * Private method to complete the login process
         * @param res
         * @param options
         * @returns {*}
         * @private
         */
        function _finishLogin(res) {
          var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

          var data = res.data;
          if (data && data.data && data.data.id) {
            setLoginAccount(Model.inject(Model.deserialize(Model.name, data)), options);
            return getAccount();
          } else {
            return _loginErrorHandler(res);
          }
        }

        /**
         * Handles login server errors in a generic way
         * @param response The HTTP response
         * @returns {Promise}
         */
        function _loginErrorHandler(response) {
          return $q.reject(response);
        }

        /**
         * Creates a new account
         * @param emailOrAttributes A string with the user's email, or an object with extra attributes
         * @param options
         * @returns {Promise}
         */
        function createAccount(emailOrAttributes) {
          var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

          if (angular.isString(emailOrAttributes)) {
            emailOrAttributes = { email: emailOrAttributes };
          }
          return Model.create(emailOrAttributes).then(function (user) {
            setLoginAccount(user, angular.extend({ existing: false }, options));
            return getAccount();
          });
        }

        function _logout() {
          var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

          angular.forEach(_current, function (v, k) {
            delete _current[k];
          });
          _current.verified = false;
          delete $localStorage[STORAGE_KEY];
          _oldStorageValue = null;
          if (_modelName === 'TeamMember') {
            // also force a log out on the candidate model
            delete $localStorage[getStorageKey('Candidate')];
          }
          authInterceptor.clearAuthToken();
          if (options.ejectModels) {
            // clear all cached models on logout
            allModels.ejectAll({ exclude: logoutEjectExcludes });
          }
          $localStorage.$apply();
          var $event = $rootScope.$broadcast('app.logout', options);
          $localStorage.$apply();
          $sessionStorage.$apply();
          // delete the session on the server unless disabled
          var remoteLogout = options.destroySession === false ? $q.when() : $http.delete(SESSIONS_API);
          return remoteLogout.finally(function () {
            if (options.forceReload || options.reload !== false && $event.reload) {
              if (appConfig.resetOnLogout) {
                // force reload, but also clear path
                $window.location = isString(appConfig.resetOnLogout) ? appConfig.resetOnLogout : appConfig.appPath;
              } else {
                $window.location.reload();
              }
            }
          });
        }

        /**
         * Log out of the application
         * @param options
         */
        function logout(options) {
          if (!_current.verified) {
            // the user is already logged out, do not logout
            return $q.reject(true);
          }

          var $event = $rootScope.$broadcast('app.beforeLogout');
          if (!$event.defaultPrevented) {
            return _logout(options).then(angular.identity, angular.identity);
          } else if ($event.promise) {
            return $event.promise.then(function () {
              return _logout(options);
            });
          } else {
            return $q.reject(false);
          }
        }

        /**
         * Log out of the application and forcibly reload
         */
        function logoutAndReload() {
          $rootScope.showLoadingPanel = true;
          return logout({ forceReload: true });
        }

        /**
         * Logs out of the application and then reloads using the cookie
         * This method is most useful for local impersonations/account switching
         * @param reload {boolean} If false, the calling function must reload the page with a new URL
         */
        function reLogin() {
          var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
              _ref$reload = _ref.reload,
              reload = _ref$reload === undefined ? true : _ref$reload;

          var baseAppUrl = $location.absUrl().replace($location.url(), '');

          return logout({ reload: false, destroySession: false }).finally(function () {
            if (reload) {
              $window.location.href = baseAppUrl;
            }
          });
        }

        /**
         * Edits a localstorage value stored for a remote account
         * @param modelType The remote model type so we know which localstorage key to edit
         * @param id The user id
         */
        function setAccountStorage(modelType, id) {
          var key = getStorageKey(modelType);
          // we still track this in local storage so we can perform some smart checks
          $localStorage[key] = {
            id: id,
            lastActivity: Date.now()
          };
          if (key === STORAGE_KEY) {
            _oldStorageValue = $localStorage[key];
          }
          $localStorage.$apply();
        }

        /**
         * Reloads the account on page load based on the auth token
         */
        function reloadAccount() {
          if (!_reload || _temporary) {
            readyDeferred.resolve(_current);
          } else if (authInterceptor.loggedIn()) {
            $rootScope.$broadcast('app.checkingLogin');
            lookupCurrent();
          } else {
            // make sure that we don't retain an invalid auth token at this point
            // because we're clearing an expired account, we don't need to eject the models
            // clearing them triggers a $digest, and we do _not_ want one at this stage
            _logout({ ejectModels: false, reload: false }).catch(angular.identity).then(function () {
              readyDeferred.resolve(_current);
            });
          }
        }

        function whenReady() {
          return readyDeferred.promise;
        }

        function listenForAccountChanges() {
          $window.name = $window.name.replace(LOGOUT_REASON_REGEX, '');
          var lastCheck = Date.now();
          var windowFocusListener = function windowFocusListener() {
            if (Date.now() - lastCheck > 60 * 60 * 1000) {
              // check to see if we can still log in if it's been an hour since we last checked
              $http.head(SESSIONS_API, { ignoreLoadingBar: true }).then(function () {
                lastCheck = Date.now();
              }).catch(function (resp) {
                if (resp && resp.status === 401) {
                  $window.name = 'QUALIFIED_LOGOUT!INACTIVE!' + $window.name;
                  _logout();
                }
              });
            }
          };
          angular.element($window).on('focus', windowFocusListener);
          var storageChangeListener = $rootScope.$watch(function () {
            return $localStorage[STORAGE_KEY];
          }, function (newValue) {
            // check if the user logged out, but is still verified in this window
            if (!newValue && _oldStorageValue && _current.verified) {
              // they logged out of another window so log out of this one
              window.name = 'QUALIFIED_LOGOUT!OTHER_TAB!' + window.name;
              _logout();
            } else if (newValue && _oldStorageValue && newValue.id !== _oldStorageValue.id && newValue.lastActivity > _oldStorageValue.lastActivity) {
              // check if the user account has changed
              // most likely they logged in from another window
              reLogin();
            } else if (!_oldStorageValue && newValue && newValue.id && !_current.verified) {
              // see if they logged in from another window when we were logged out
              reLogin({ reload: false }).then(function () {
                $rootScope.$broadcast('app.accountSetRemotely', _current);
              });
            } else {
              _oldStorageValue = newValue;
            }
          });

          return function () {
            angular.element($window).off('focus', windowFocusListener);
            storageChangeListener();
          };
        }
      }]
    };
  }).run(['accountService', '$timeout', function (accountService, $timeout) {
    $timeout(accountService.reloadAccount);
  }]);

  var embedded = window.parent !== window;

  var LOGOUT_EJECT_EXCLUDES = ['Assessment', 'Team', 'Customer', 'ChallengeItem', 'Challenge'];

  var accountServiceConfigModule = angular.module('Q.config.account-service', [accountServiceModule]).config(['accountServiceProvider', function (accountServiceProvider) {
    accountServiceProvider.setModelName('Candidate');
    accountServiceProvider.setLogoutEjectExcludes(LOGOUT_EJECT_EXCLUDES);
    accountServiceProvider.setIncludes(['team_ids']);
    accountServiceProvider.setAccountUrl(appConfig.assessRoute + '/');
    // Assess app exclusively relies on assessment_result_auth#authorize API to handle user authentication
    // as part of its init code, so no need to lookup existing session.
    accountServiceProvider.setSkipLookup(true);

    // check to see if embedded, because we're in temporary mode then
    if (embedded) {
      accountServiceProvider.setTemporary(true); // embeds do not preserve login info
    }
  }]);

  var authInterceptorConfigModule = angular.module('Q.config.auth-interceptor', [authInterceptorModule.name]).config(['authInterceptorProvider', function (authInterceptorProvider) {
    // if this page is embedded, then we need to use the embedded authorization mode. Due to 3rd party cookie
    // restrictions, we cannot set or rely on server-set cookies.
    authInterceptorProvider.setMode(embedded ? 'embed' : 'candidate');
  }]);

  var googleAnalyticsConfigModule = angular.module('Q.config.google-analytics', [])

  /*
   * Fires off a page view to Google Analytics every time the state is changed.
   */
  .run(['$rootScope', '$location', '$window', '$log', function ($rootScope, $location, $window, $log) {
    $rootScope.$on('$stateChangeSuccess', function () {
      if ($window.ga) {
        $log.debug('[GA]', 'Tracking page view of ' + $location.url());
        $window.ga('send', 'pageview', { page: $location.url() });
      }
    });
  }]);

  var configModule = angular.module('Q.config', [appConfigModule, angularCoreConfigModule, loadingBarConfigModule, jsDataConfigModule, authInterceptorConfigModule, accountServiceConfigModule, googleAnalyticsConfigModule]);

  /**
   * Provides logic to hide or show the loading panel based on the application state.
   */
  var loadingPanelServiceModule = angular.module('Q.services.loading-panel', []).run(['$rootScope', function ($rootScope) {
    $rootScope.showLoadingPanel = false;
    var error = false;

    var showPanel = function showPanel($event) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      if (!error && options.showLoadingPanel !== false) {
        $rootScope.showLoadingPanel = true;
      }
    };
    var hidePanel = function hidePanel() {
      return $rootScope.showLoadingPanel = false;
    };
    var errorPanel = function errorPanel() {
      error = true;
      hidePanel();
    };

    $rootScope.$on('app.checkingLogin', showPanel);
    $rootScope.$on('app.login', showPanel);
    $rootScope.$on('app.logout', showPanel);
    $rootScope.$on('$stateChangeSuccess', hidePanel);
    $rootScope.$on('app.error', errorPanel);
  }]);

  var railsInterceptorModule = angular.module('Q.interceptors.rails-interceptor', [])

  /*
   * Makes changes to the request to help rails.
   */
  .factory('railsInterceptor', function () {
    return {
      request: function request(config) {
        if (config.params) {
          angular.forEach(config.params, function (val, name) {
            if (angular.isArray(val) && !/\[\]$/.test(name)) {
              delete config.params[name];
              config.params[name + '[]'] = val;
            }
          });
        }
        return config;
      }
    };
  }).config(['$httpProvider', function ($httpProvider) {
    $httpProvider.interceptors.push('railsInterceptor');
  }]);

  /* eslint-disable angular/no-private-call,angular/timeout-service */

  var timerHelperModule = angular.module('Q.helpers.timer-helper', []).factory('timerHelper', ['$rootScope', '$window', '$q', '$exceptionHandler', '$$q', function ($rootScope, $window, $q, $exceptionHandler, $$q) {
    var deferreds = {};

    /**
     * Drop-in replacement for Angular's $timeout, but designed to run outside the framework a bit,
     * so it doesn't cause Angular to stay in a "non-stable" state for testing purposes.
     *
     * @param fn
     * @param delay
     * @param invokeApply
     * @param args
     */
    function longTimeout(fn, delay) {
      for (var _len = arguments.length, args = Array(_len > 3 ? _len - 3 : 0), _key = 3; _key < _len; _key++) {
        args[_key - 3] = arguments[_key];
      }

      var invokeApply = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;

      if (!angular.isFunction(fn)) {
        invokeApply = delay;
        delay = fn;
        fn = angular.noop;
      }

      var deferred = (invokeApply ? $q : $$q).defer();
      var promise = deferred.promise;
      var timeoutId = void 0;

      timeoutId = $window.setTimeout(function () {
        try {
          deferred.resolve(fn.apply(null, args));
        } catch (e) {
          deferred.reject(e);
          $exceptionHandler(e);
        } finally {
          delete deferreds[promise.$$timeoutId];
        }

        if (invokeApply) $rootScope.$apply();
      }, delay);

      promise.$$timeoutId = timeoutId;
      deferreds[timeoutId] = deferred;

      return promise;
    }

    /**
     * Cancels a previously configured longTimeout
     * @param promise Promise to cancel that was returned from longTimeout()
     * @returns {boolean}
     */
    longTimeout.cancel = function (promise) {
      if (promise && promise.$$timeoutId in deferreds) {
        // Timeout cancels should not report an unhandled promise.
        deferreds[promise.$$timeoutId].promise.catch(angular.noop);
        deferreds[promise.$$timeoutId].reject('canceled');
        delete deferreds[promise.$$timeoutId];
        return $window.clearTimeout(promise.$$timeoutId);
      }
      return false;
    };

    /**
     * Creates a recurring timer that is more guaranteed to run every [delay] milliseconds than $interval
     * @param fn Function to call on every tick
     * @param delay How often to run the timer
     * @param invokeApply Whether or not to invoke apply after each event
     * @param args Extra args passed to the function
     * @returns {{ stop, start, running }} An object that can be used to cancel the timer.
     */
    function runEvery(fn, delay) {
      for (var _len2 = arguments.length, args = Array(_len2 > 3 ? _len2 - 3 : 0), _key2 = 3; _key2 < _len2; _key2++) {
        args[_key2 - 3] = arguments[_key2];
      }

      var invokeApply = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;

      var nextEventTime = Date.now() + delay;

      var ret = {
        stop: function stop() {
          runEvery.cancel(this);
        },
        start: function start() {
          if (!this.running) {
            queueNextEvent();
          }
        }
      };

      function event() {
        // queue next event first, so it can be stopped/canceled by the timed event
        queueNextEvent();
        fn.apply(undefined, args);
      }

      function queueNextEvent() {
        if (ret.$$runEveryPromise) {
          runEvery.cancel(ret);
        }
        var now = Date.now();
        // determine the next trigger delay--skipping over any frames we've already passed if necessary
        while (nextEventTime < now) {
          nextEventTime += delay;
        }var eventDelay = nextEventTime - now;
        nextEventTime += delay;

        ret.$$runEveryPromise = longTimeout(event, eventDelay, invokeApply);
        event.running = true;
      }

      event();

      return ret;
    }

    /**
     * Cancels a runEvery event
     * @param event
     */
    runEvery.cancel = function (event) {
      if (event && event.$$runEveryPromise) {
        longTimeout.cancel(event.$$runEveryPromise);
        event.$$runEveryPromise = false;
        event.running = false;
      }
    };

    return {
      longTimeout: longTimeout,
      runEvery: runEvery
    };
  }]);

  // Intercom is no longer used. This module remains as a compatibility layer for
  // existing support entrypoints (eg. `intercom-button`) and now routes users to
  // email-based support.
  var intercomServiceModule = angular.module('Q.services.intercom-service', [timerHelperModule]).factory('intercomService', ['$window', 'timerHelper', '$rootScope', '$log', function ($window, timerHelper, $rootScope, $log) {
    var SUPPORT_EMAIL = 'support@qualified.io';
    var DEFAULT_SUBJECT = 'Qualified Support';

    var updateTimer = null;

    function buildMailtoHref() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          subject = _ref.subject,
          body = _ref.body;

      var params = [];
      if (subject) params.push('subject=' + encodeURIComponent(subject));
      if (body) params.push('body=' + encodeURIComponent(body));
      var query = params.length ? '?' + params.join('&') : '';
      return 'mailto:' + SUPPORT_EMAIL + query;
    }

    function openMailto() {
      var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          subject = _ref2.subject,
          body = _ref2.body;

      try {
        $window.location.href = buildMailtoHref({ subject: subject, body: body });
      } catch (e) {
        // Don't break the UI if navigation fails in some embedded contexts.
        $log.debug(e);
      }
    }

    var service = {
      // Compatibility no-ops (Intercom removed)
      setUser: function setUser() {
        $rootScope.$broadcast('intercom.isActive', false);
        return service;
      },
      unsetUser: function unsetUser() {
        $rootScope.$broadcast('intercom.isActive', false);
        return service;
      },
      trackPage: function trackPage() {
        return service;
      },
      trackEvent: function trackEvent() {
        return service;
      },
      delayUpdate: function delayUpdate(delay, data) {
        timerHelper.longTimeout.cancel(updateTimer);

        updateTimer = timerHelper.longTimeout(function () {
          service.update(data);
        }, delay || 2000, false);
      },
      update: function update() {
        return service;
      },
      show: function show(msg) {
        var body = '';
        if (msg && msg !== true) body = String(msg);
        openMailto({ subject: DEFAULT_SUBJECT, body: body });
        return service;
      },
      hide: function hide() {
        return service;
      },
      toggle: function toggle() {
        return service.show();
      },
      isActive: function isActive() {
        return false;
      },
      showWidget: function showWidget() {
        // Always allow support entrypoints to render, even though Intercom is gone.
        return true;
      },
      supportMailtoHref: function supportMailtoHref() {
        var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
            subject = _ref3.subject,
            body = _ref3.body;

        return buildMailtoHref({
          subject: subject || DEFAULT_SUBJECT,
          body: body
        });
      }
    };

    return service;
  }]).run(['$rootScope', 'intercomService', function ($rootScope, intercomService) {
    $rootScope.$on('$stateChangeSuccess', function () {
      return intercomService.delayUpdate(1000);
    });
  }]).directive('intercomHide', function () {
    return {
      restrict: 'A',
      link: function link(scope, elem) {
        // Intercom is removed, but keep the element visible for support entrypoints.
        elem.toggleClass('ng-hide', false);
      }
    };
  }).directive('intercomButton', ['intercomService', function (intercomService) {
    return {
      restrict: 'A',
      link: function link(scope, elem, attr) {
        elem.addClass('intercom-button');
        elem.attr('tabindex', 0);
        var msgAttr = attr.intercomButtonMessage;
        var msg = msgAttr === 'true' || msgAttr === '' ? '' : msgAttr;

        var existingHref = elem.attr('href');
        var shouldSetHref = !existingHref || existingHref === '' || existingHref === '#';
        if (shouldSetHref) {
          elem.attr('href', intercomService.supportMailtoHref({ body: msg }));
          elem.attr('target', '_blank');
          elem.attr('rel', 'noopener');
        }

        // Ensure mailto works even if something prevents default navigation.
        elem.on('click', function (event) {
          if (shouldSetHref) {
            stopEvent(event);
            intercomService.show(msg);
          }
        });
        if (!attr.title && !attr.tooltip) {
          elem.attr('title', 'Email Qualified Support');
        }
      }
    };
  }]).directive('modalContent', ['$compile', function ($compile) {
    return {
      restrict: 'C',
      link: function link(scope, elem) {
        var nodeLink = $compile('<a intercom-button track-click="Support Email Clicked" track-data="{ui: {region: \'dialog\'}}"></a>')(scope);
        elem.append(nodeLink);
      }
    };
  }]);

  var UTM_KEY = '$qualified$utm';

  // captures UTM properties on page load and preserves them across the site
  var utmProps = {};

  try {
    var storage = JSON.parse(window.localStorage[UTM_KEY] || '{}');
    Object.assign(utmProps, storage);
  } catch (err) {}
  /* ignore errors */


  // even if we couldn't parse the stored props, lets try to get new ones
  try {
    if (window.URL) {
      var utmTester = /^utm_/;
      var params = new URL(document.location).searchParams;
      if (params) {
        params.forEach(function (val, key) {
          var k = key.toLowerCase();
          if (utmTester.test(k) && val) {
            if (val === 'null') {
              delete utmProps[k];
            } else {
              utmProps[k] = val;
            }
          }
        });
        utmProps.referrer = document.referrer;
        utmProps.initialReferrer = utmProps.initialReferrer || document.referrer;
        window.localStorage[UTM_KEY] = JSON.stringify(utmProps);
      }
    }
  } catch (err) {
    /* ignore errors */
  }

  function getImpersonateId() {
    return getCookie('impersonator_id');
  }

  var trackerServiceModule = angular.module('Q.services.tracker-service', [tryFunctionHelperModule, intercomServiceModule]).factory('tracker', ['$window', 'intercomService', '$injector', 'tryFunction', '$log', function ($window, intercomService, $injector, tryFunction, $log) {
    // assign the appname as a custom session variable
    if ($window.ga) {
      $window.ga('set', 'dimension1', appConfig.appName);
    }

    var service = {
      shouldTrack: shouldTrack,

      setUser: tryFunction(function (user) {
        if (shouldTrack()) {
          if ($window.analytics) {
            if (user.user) {
              user = user.user;
            }

            service.user = user;

            var userData = {};

            if (user.team) {
              $window.analytics.group(user.teamId, user.team);
              // some analytics services don't respond to analytics.group, so track the key team properties as fields
              userData = {
                customer: {
                  id: user.team.customerId,
                  name: user.team.customer && user.team.customer.name,
                  state: user.team.customer && user.team.customer.state,
                  trialEndedAt: user.team.customer && user.team.customer.trialEndedAt
                },
                team: {
                  name: user.team.name,
                  primary: user.team.primaryTeam,
                  // legacy trait
                  subscriptionState: user.team.customer && user.team.customer.state,
                  state: user.team.state
                }
              };
            }

            $window.analytics.identify(user.id, angular.extend({}, user, userData), setContext());
          }

          // Implement fullstory set user here (Datadog rum replacement)

          intercomService.setUser(angular.extend({}, user, utmProps));
        }
      }),
      unsetUser: tryFunction(function () {
        service.user = null;

        if ($window.analytics) {
          $window.analytics.reset();
        }
        if ($window.mixpanel && $window.mixpanel.cookie) {
          $window.mixpanel.cookie.clear();
        }
        // Implement fullstory clear user here (Datadog rum replacement)

        intercomService.unsetUser();
      }),
      trackPage: tryFunction(function (name, props, options) {
        props = props || {};
        props.app = appConfig.appName;
        props.env = appConfig.env;

        if ($window.analytics && shouldTrack()) {
          $window.analytics.page(name, props, setContext(options));
        }
        intercomService.trackPage();
      }),
      trackEvent: tryFunction(function (name, properties) {
        var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

        if (appConfig.devMode) {
          $log.debug('trackEvent:', name, properties, options);
        }

        if (shouldTrack()) {
          var $state = $injector.has('$state') && $injector.get('$state');
          // always include these basic properties
          var props = angular.extend({
            app: appConfig.appName,
            env: appConfig.env,
            ui: {}
          }, properties);
          props.ui.$state = $state && $state.current && $state.current.name;

          if ($window.analytics) {
            $window.analytics.track(name, props, setContext(options));
          } else if ($window.ga) {
            var _props = properties || {};
            $window.ga('send', 'event', {
              eventCategory: appConfig.appName,
              eventAction: name,
              // try to guess which key--if any--might work as the event label
              eventLabel: _props[['name', 'title', 'state', 'label', 'message', 'id'].find(function (k) {
                return _props[k];
              })],
              eventValue: !isNaN(_props.value) ? _props.value : null
            });
          }
          // Implement fullstory add action here (Datadog rum replacement)

          if (options.intercom !== false) {
            intercomService.trackEvent(name, properties);
          }
        }
      }),
      trackError: tryFunction(function (name, properties) {
        var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

        $log.error(name, properties);
        service.trackEvent('Error: ' + name, properties, options);
      }),
      // extended version of trackEvent, which takes a model instance. If the properties are provided as a
      // function, the result of that function will be be returned. If no properties are provided, then
      // the properties will be derived from the model instance.
      trackModelEvent: tryFunction(function (instance, name, properties) {
        if (!instance) return;
        // if track properties were specifically configured then use those instead
        if (properties) {
          if (angular.isFunction(properties)) {
            properties = properties(instance);
          }
          // if the instance support a trackProperties method, then use that as the default. Otherwise
          // just use key fields from the instance itself.
        } else {
          var defaults = {
            id: instance.id,
            $type: instance.$type,
            name: instance.name,
            title: instance.title,
            candidate: instance.candidate ? instance.candidate.email : undefined,
            team: instance.team ? instance.team.name : undefined
          };
          properties = instance.trackProperties ? instance.trackProperties(defaults) : defaults;
        }
        service.trackEvent(name, properties, { intercom: false });
      })
    };

    function shouldTrack() {
      var accountService = getAccountService($injector);
      var account = accountService && accountService.get();

      // If there is a current user
      if (account.user) {
        return account.verified && // must be verified in the service
        account.user.role !== 'staff' && // must not be a staff member
        !account.user.demoAccount && ( // must not be a demo account
        account.user.$type !== 'Candidate' || !account.user.isActive || account.user.isActive()) && // must not be an inactive candidate
        !getImpersonateId(); // must not be impersonating this user
      }
      // otherwise we do not currently track anonymous users
      return false;
    }

    function setContext(options) {
      options = options || {};
      // by default segment only passes along utm values if they are in the current url.
      // We are going to pull them from the cookie so that everything gets tracked back to the source referrer.
      options.context = {
        campaign: {
          name: utmProps.utm_campaign,
          source: utmProps.utm_source,
          medium: utmProps.utm_medium,
          term: utmProps.utm_term,
          content: utmProps.utm_content
        }
      };

      return options;
    }

    return service;
  }]).run(['tracker', '$rootScope', '$injector', 'tryFunction', function (tracker, $rootScope, $injector, tryFunction) {
    var rebroadcastEvents = ['app.accountSet', 'app.login', 'app.logout'];

    rebroadcastEvents.forEach(function (event) {
      $rootScope.$on(event, function (e, args) {
        if (tracker.shouldTrack()) {
          $rootScope.$broadcast('tracker.' + event, args);
        }
      });
    });

    $rootScope.$on('model.actionEndpoint.success', tryFunction(function (e, args) {
      var track = args.options.track;
      if (track) {
        if (angular.isString(track)) {
          track = { event: track };
        }

        if (track === true) {
          track = { event: true };
        } else if (!track.event) {
          track.event = true;
        }

        if (track.event === true) {
          var verb = upperCaseWords(pastTense(args.name));
          track.event = args.model.name + ' ' + verb;
        }

        tracker.trackModelEvent(args.instance, track.event, track.properties);
      }
    }));

    // handles model that are configured to automatically track their CUD events
    $rootScope.$on('model.changed', tryFunction(function (e, args) {
      var track = args.config.meta && args.config.meta.track && args.config.meta.track[args.action];
      if (track) {
        var event = args.action + 'd';
        if (event === 'destroyd') event = 'destroyed';
        event = upperCaseWords(pastTense(event));
        tracker.trackModelEvent(args.instance, args.resourceName + ' ' + event, track === true ? null : track);
      }
    }));

    // collect the previous state on the ui.router $stateChangeStart
    // because the nestedPageTitleHelper pulls the current title
    var previousState = void 0;
    var stateDelimiter = ':';

    $rootScope.$on('tracker.app.accountSet', function (e, account) {
      return tracker.setUser(account);
    });
    $rootScope.$on('tracker.app.logout', function () {
      return tracker.unsetUser();
    });

    $rootScope.$on('$stateChangeStart', tryFunction(function (event, toState, toParams, fromState) {
      // default to tracking state changes unless the
      // route explicitly sets track to false
      if (fromState.track !== false) {
        var fromPageTitle = fromState && fromState.data && fromState.data.pageTitle;
        if (fromPageTitle && !angular.isString(fromPageTitle)) {
          previousState = $injector.invoke(fromPageTitle);
          if (angular.isArray(previousState)) {
            previousState = previousState.join(stateDelimiter);
          }
        } else {
          previousState = '';
        }
      } else {
        previousState = '';
      }
    }));

    $rootScope.$on('$stateChangeSuccess', tryFunction(function (event, toState, toParams, fromState) {
      // these events are noisy as hell so lets not track them in the development environment and save
      // ourselves some money
      // default to tracking state changes unless the
      // route explicitly sets track to false
      if (toState.track !== false && toState.name !== 'error') {
        var toPageTitle = toState.data && toState.data.pageTitle;
        if (toPageTitle && !angular.isString(toPageTitle)) {
          toPageTitle = $injector.invoke(toPageTitle);
          if (angular.isArray(toPageTitle)) {
            toPageTitle = toPageTitle.join(stateDelimiter);
          }
        }

        var props = {
          title: toPageTitle,
          fromStateName: previousState || fromState.name || '',
          toParams: toParams
        };

        // srefProps are set by the ui-sref directive, which utilize the triggering
        // element's track-data attribute.
        angular.merge(props, tracker.srefProps);
        tracker.srefProps = null;

        // turn mixpanel off in dev mode
        var options = {
          mixpanel: appConfig.env !== 'dev'
        };

        var pageName = toState.data && toState.data.label;
        if (!pageName && toPageTitle) {
          pageName = toPageTitle.split(':')[0];
        }

        tracker.trackPage(pageName || toState.name, props, options);
      }
    }));
  }]);

  var oldOnerror = window.onerror;
  var booted = false;
  var doc = window.document;
  window.onerror = prebootError;
  // used because we have to re-throw errors to see them with source maps
  var lastException = null;

  function prebootError(msg /*, url, line, col, error*/) {
    if (oldOnerror) {
      oldOnerror.apply(this, arguments);
    }
    if (!booted && doc) {
      var appName = '';
      if (window.config && window.config.appName) {
        appName = encodeURIComponent(window.config.appName + ' ');
      }
      // wipe out the body content
      doc.body.innerHTML = '<div class="loading-exception">\n\t<h2>Error: Unable to start the application!</h2>\n\t<p>We were unable to get the application started! You can try reloading the page, or <a href="mailto:team@qualified.io?subject=' + appName + 'Application%20Startup%20Issues" target="_blank">contact the team at Qualified.</a></p>\n\t<p>The error that occurred is:</p>\n\t<pre><code>' + msg + '</code></pre>\n</div>';
    }
  }

  function isPlainObject(obj) {
    return angular.isObject(obj) && Object.getPrototypeOf(obj) === Object.prototype;
  }

  function sentryOpts(args) {
    var opts = {};
    if (args.length) {
      if (angular.isString(args[0])) {
        opts.extra = { extraMessage: args.shift() };
      } else if (angular.isNumber(args[0].status) || isPlainObject(args[0])) {
        // capture additional data if this is an $http request or it's a plain object
        opts.extra = toSentrySafeObject(args.shift());
      }
    }
    return opts;
  }

  function sendToSentry($log) {
    for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    try {
      if (window.Sentry && (!window.config || !window.config.devMode)) {
        args = args.filter(angular.identity);
        var sent = false;
        // handle simple string arguments
        if (args.length && angular.isString(args[0])) {
          window.Sentry.captureMessage(args.shift(), sentryOpts(args));
          sent = true;
        }
        // handle Error arguments (we might capture 2 events)
        if (args.length && args[0] instanceof Error) {
          window.Sentry.captureException(args.shift(), sentryOpts(args));
          sent = true;
        }
        // handle anything else
        if (!sent) {
          window.Sentry.captureMessage('Non-Error Logged', sentryOpts(args));
        }
      }
    } catch (e) {
      $log.warn('Error logging failure:');
      $log.warn(e);
    }
  }

  var exceptionHandlerServiceModule = angular.module('Q.services.exception-handler', []).run(['$log', '$rootScope', '$injector', '$q', function ($log, $rootScope, $injector, $q) {
    booted = true;
    if (window.onerror === prebootError) {
      window.onerror = oldOnerror;
    } // otherwise, someone else overwrote the onerror, so we just leave it

    // wrap the built-in $log.error function so it sends it to Sentry (after booting)
    var oldError = $log.error;
    $log.error = function () {
      for (var _len2 = arguments.length, args = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        args[_key2] = arguments[_key2];
      }

      oldError.apply($log, args);
      sendToSentry.apply(undefined, [$log].concat(args));
    };

    $log.reject = function () {
      $log.error.apply($log, arguments);
      return $q.reject(arguments.length <= 0 ? undefined : arguments[0]);
    };

    if (window.Sentry) {
      window.Sentry.setTags({
        appName: appConfig.appName,
        env: appConfig.env
      });
      $rootScope.$on('app.accountSet', function ($event, account) {
        if (account && account.user) {
          var user = account.user;
          window.Sentry.setUser({
            id: user.id,
            email: user.email
          });
          var impersonateId = getImpersonateId();
          if (impersonateId) {
            // track that this is an impersonated session
            window.Sentry.setTags({ impersonateId: impersonateId });
          }
        }
      });
      $rootScope.$on('app.logout', function () {
        return window.Sentry.setUser();
      });
    }
  }]).factory('$exceptionHandler', ['$log', '$injector', function ($log, $injector) {
    return function log(exception, cause) {
      if (lastException === exception) return;

      if (cause) {
        try {
          exception.message += ' (caused by "' + cause + '")';
        } catch (e) {
          $log.error('Cause: ', cause);
          // we cannot modify the message without error sometimes
        }
      }
      $log.error(exception);

      if (!booted) {
        // send any uncaught errors to Sentry directly before booting
        sendToSentry($log, exception);
      }

      if (appConfig.devMode && $injector.has('$state')) {
        try {
          var $state = $injector.get('$state');
          $state.go('error', { error: exception, didLog: true });
        } catch (e) {}
      }

      lastException = exception;
      if (exception.stack) {
        // re-throw so we get stack traces with source maps
        // eslint-disable-next-line angular/timeout-service
        setTimeout(function () {
          throw exception;
        });
      }
    };
  }]);

  var configServiceModule = angular.module('Q.services.config-service', []).factory('configService', ['$http', '$log', '$q', function ($http, $log, $q) {
    var data = null;
    var loading = null;
    var overrodeDelay = void 0;

    return {
      get config() {
        return data || {};
      },
      load: function load() {
        var force = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

        if (!force && data) {
          return $q.when(data);
        } else {
          var ts = force ? '?' + Date.now() : '';
          if (!loading) {
            loading = $http.get(appConfig.baseApiRoot + 'config' + ts).then(function (response) {
              if (!response || !response.data) {
                return $q.reject();
              }
              data = parseDates(response.data);
              data.timeDiffMS = 0;
              data.ignoreTimeDelay = function () {
                overrodeDelay = true;
                data.significantTimeDelay = false;
              };
              if (isDate(data.currentTime)) {
                data.timeDiffMS = new Date() - data.currentTime;
                if (!overrodeDelay) {
                  // true if server-client time difference is greater than 5 minutes
                  data.significantTimeDelay = Math.abs(data.timeDiffMS) > 300000;
                }
              }
              return data;
            }).catch(function (err) {
              $log.error('Failed to obtain api config', err);
              return $q.reject();
            }).finally(function () {
              loading = null;
            });
          }
          return loading;
        }
      }
    };
  }]);

  var modalWindowTemplate = "<div class=\"modal-dialog {{size ? 'modal-' + size : ''}} {{ allowDarkMode ? $root.$ideDarkClass : ''}}\"> <div class=\"modal-content\" uib-modal-transclude></div> </div> ";

  /* eslint angular/no-private-call:0 */

  /**
   * A helper, internal data structure that acts as a map but also allows getting / removing
   * elements in the LIFO order
   */
  var $$stackedMap = {
    createNew: function createNew() {
      var stack = [];

      return {
        add: function add(key, value) {
          stack.push({
            key: key,
            value: value
          });
        },
        get: function get(key) {
          for (var i = 0; i < stack.length; i++) {
            if (key === stack[i].key) {
              return stack[i];
            }
          }
        },
        keys: function keys() {
          var keys = [];
          for (var i = 0; i < stack.length; i++) {
            keys.push(stack[i].key);
          }
          return keys;
        },
        top: function top() {
          return stack[stack.length - 1];
        },
        remove: function remove(key) {
          var idx = -1;
          for (var i = 0; i < stack.length; i++) {
            if (key === stack[i].key) {
              idx = i;
              break;
            }
          }
          return stack.splice(idx, 1)[0];
        },
        length: function length() {
          return stack.length;
        }
      };
    }
  };

  /**
   * A helper, internal data structure that stores all references attached to key
   */
  var $$multiMap = {
    createNew: function createNew() {
      var map = {};

      return {
        get: function get(key) {
          return map[key];
        },
        hasKey: function hasKey(key) {
          return !!map[key];
        },
        keys: function keys() {
          return Object.keys(map);
        },
        put: function put(key, value) {
          if (!map[key]) {
            map[key] = [];
          }

          map[key].push(value);
        },
        remove: function remove(key, value) {
          var values = map[key];

          if (!values) {
            return;
          }

          var idx = values.indexOf(value);

          if (idx !== -1) {
            values.splice(idx, 1);
          }

          if (!values.length) {
            delete map[key];
          }
        }
      };
    }
  };

  var modalComponentModule = angular.module('Q.components.modal', [])
  /**
   * Pluggable resolve mechanism for the modal resolve resolution
   * Supports UI Router's $resolve service
   */
  .provider('$uibResolve', function () {
    this.$get = ['$injector', '$q', function ($injector, $q) {
      return {
        resolve: function resolve(invocables) {
          var promises = [];

          angular.forEach(invocables, function (value) {
            if (angular.isFunction(value) || angular.isArray(value)) {
              promises.push($q.resolve($injector.invoke(value)));
            } else if (angular.isString(value)) {
              promises.push($q.resolve($injector.get(value)));
            } else {
              promises.push($q.resolve(value));
            }
          });

          return $q.all(promises).then(function (resolves) {
            var resolveObj = {};
            var resolveIter = 0;
            angular.forEach(invocables, function (value, key) {
              resolveObj[key] = resolves[resolveIter++];
            });

            return resolveObj;
          });
        }
      };
    }];
  })

  /**
   * A helper directive for the $modal service. It creates a backdrop element.
   */
  .directive('uibModalBackdrop', ['$animate', '$injector', '$modalStack', function ($animate, $injector, $modalStack) {
    return {
      restrict: 'A',
      compile: function compile(tElement, tAttrs) {
        tElement.addClass(tAttrs.backdropClass);
        return linkFn;
      }
    };

    function linkFn(scope, element, attrs) {
      if (attrs.modalInClass) {
        $animate.addClass(element, attrs.modalInClass);

        scope.$on($modalStack.NOW_CLOSING_EVENT, function (e, setIsAsync) {
          var done = setIsAsync();
          if (scope.modalOptions.animation) {
            $animate.removeClass(element, attrs.modalInClass).then(done);
          } else {
            done();
          }
        });
      }
    }
  }]).directive('uibModalWindow', ['$modalStack', '$q', '$animateCss', '$document', function ($modalStack, $q, $animateCss, $document) {
    return {
      scope: {
        index: '@'
      },
      restrict: 'A',
      transclude: true,
      template: function template(tElement) {
        return tElement.data('window-template') || modalWindowTemplate;
      },
      link: function link(scope, element, attrs) {
        element.addClass(attrs.windowTopClass || '');
        scope.size = attrs.size;
        scope.allowDarkMode = !!attrs.allowDarkMode;

        scope.close = function (evt) {
          var modal = $modalStack.getTop();
          if (modal && modal.value.backdrop && modal.value.backdrop !== 'static' && evt.target === evt.currentTarget) {
            evt.preventDefault();
            evt.stopPropagation();
            $modalStack.dismiss(modal.key, 'backdrop click');
          }
        };

        // moved from template to fix issue #2280
        element.on('click', scope.close);

        // This property is only added to the scope for the purpose of detecting when this directive is rendered.
        // We can detect that by using this property in the template associated with this directive and then use
        // {@link Attribute#$observe} on it. For more details please see {@link TableColumnResize}.
        scope.$isRendered = true;

        // Deferred object that will be resolved when this modal is rendered.
        var modalRenderDeferObj = $q.defer();
        // Resolve render promise post-digest
        scope.$$postDigest(function () {
          modalRenderDeferObj.resolve();
        });

        modalRenderDeferObj.promise.then(function () {
          var animationPromise = null;

          if (attrs.modalInClass) {
            animationPromise = $animateCss(element, {
              addClass: attrs.modalInClass
            }).start();

            scope.$on($modalStack.NOW_CLOSING_EVENT, function (e, setIsAsync) {
              var done = setIsAsync();
              $animateCss(element, {
                removeClass: attrs.modalInClass
              }).start().then(done);
            });
          }

          $q.when(animationPromise).then(function () {
            // Notify {@link $modalStack} that modal is rendered.
            var modal = $modalStack.getTop();
            if (modal) {
              $modalStack.modalRendered(modal.key);
            }

            /**
             * If something within the freshly-opened modal already has focus (perhaps via a
             * directive that causes focus) then there's no need to try to focus anything.
             */
            if (!($document[0].activeElement && element[0].contains($document[0].activeElement))) {
              var inputWithAutofocus = element[0].querySelector('[autofocus]');
              /**
               * Auto-focusing of a freshly-opened modal element causes any child elements
               * with the autofocus attribute to lose focus. This is an issue on touch
               * based devices which will show and then hide the onscreen keyboard.
               * Attempts to refocus the autofocus element via JavaScript will not reopen
               * the onscreen keyboard. Fixed by updated the focusing logic to only autofocus
               * the modal element if the modal does not contain an autofocus element.
               */
              if (inputWithAutofocus) {
                inputWithAutofocus.focus();
              } else {
                element[0].focus();
              }
            }
          });
        });
      }
    };
  }]).directive('uibModalAnimationClass', function () {
    return {
      compile: function compile(tElement, tAttrs) {
        if (tAttrs.modalAnimation) {
          tElement.addClass(tAttrs.uibModalAnimationClass);
        }
      }
    };
  }).directive('uibModalTransclude', ['$animate', function ($animate) {
    return {
      link: function link(scope, element, attrs, controller, transclude) {
        transclude(scope.$parent, function (clone) {
          element.empty();
          $animate.enter(clone, element);
        });
      }
    };
  }]).factory('$modalStack', ['$animate', '$animateCss', '$document', '$compile', '$rootScope', '$q', function ($animate, $animateCss, $document, $compile, $rootScope, $q) {
    var OPENED_MODAL_CLASS = 'modal-open';

    var backdropDomEl = void 0;
    var backdropScope = void 0;
    var openedWindows = $$stackedMap.createNew();
    var openedClasses = $$multiMap.createNew();
    var $modalStack = {
      NOW_CLOSING_EVENT: 'modal.stack.now-closing'
    };
    var topModalIndex = 0;
    var previousTopOpenedModal = null;
    var ARIA_HIDDEN_ATTRIBUTE_NAME = 'data-bootstrap-modal-aria-hidden-count';

    //Modal focus behavior
    var tabbableSelector = "a[href], area[href], input:not([disabled]):not([tabindex='-1']), " + "button:not([disabled]):not([tabindex='-1']),select:not([disabled]):not([tabindex='-1']), textarea:not([disabled]):not([tabindex='-1']), " + "iframe, object, embed, *[tabindex]:not([tabindex='-1']), *[contenteditable=true]";
    var scrollbarPadding = void 0;
    var SNAKE_CASE_REGEXP = /[A-Z]/g;

    // TODO: extract into common dependency with tooltip
    function snakeCase(name) {
      var separator = '-';
      return name.replace(SNAKE_CASE_REGEXP, function (letter, pos) {
        return (pos ? separator : '') + letter.toLowerCase();
      });
    }

    function isVisible(element) {
      return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
    }

    function backdropIndex() {
      var topBackdropIndex = -1;
      var opened = openedWindows.keys();
      for (var i = 0; i < opened.length; i++) {
        if (openedWindows.get(opened[i]).value.backdrop) {
          topBackdropIndex = i;
        }
      }

      // If any backdrop exist, ensure that it's index is always
      // right below the top modal
      if (topBackdropIndex > -1 && topBackdropIndex < topModalIndex) {
        topBackdropIndex = topModalIndex;
      }
      return topBackdropIndex;
    }

    $rootScope.$watch(backdropIndex, function (newBackdropIndex) {
      if (backdropScope) {
        backdropScope.index = newBackdropIndex;
      }
    });

    function removeModalWindow(modalInstance, elementToReceiveFocus) {
      var modalWindow = openedWindows.get(modalInstance).value;
      var appendToElement = modalWindow.appendTo;

      //clean up the stack
      openedWindows.remove(modalInstance);
      previousTopOpenedModal = openedWindows.top();
      if (previousTopOpenedModal) {
        topModalIndex = parseInt(previousTopOpenedModal.value.modalDomEl.attr('index'), 10);
      }

      removeAfterAnimate(modalWindow.modalDomEl, modalWindow.modalScope, function () {
        var modalBodyClass = modalWindow.openedClass || OPENED_MODAL_CLASS;
        openedClasses.remove(modalBodyClass, modalInstance);
        var areAnyOpen = openedClasses.hasKey(modalBodyClass);
        appendToElement.toggleClass(modalBodyClass, areAnyOpen);
        if (!areAnyOpen && scrollbarPadding && scrollbarPadding.heightOverflow && scrollbarPadding.scrollbarWidth) {
          if (scrollbarPadding.originalRight) {
            appendToElement.css({
              paddingRight: scrollbarPadding.originalRight + 'px'
            });
          } else {
            appendToElement.css({ paddingRight: '' });
          }
          scrollbarPadding = null;
        }
        toggleTopWindowClass(true);
      }, modalWindow.closedDeferred);
      checkRemoveBackdrop();

      //move focus to specified element if available, or else to body
      if (elementToReceiveFocus && elementToReceiveFocus.focus) {
        elementToReceiveFocus.focus();
      } else if (appendToElement.focus) {
        appendToElement.focus();
      }
    }

    // Add or remove "windowTopClass" from the top window in the stack
    function toggleTopWindowClass(toggleSwitch) {
      var modalWindow = void 0;

      if (openedWindows.length() > 0) {
        modalWindow = openedWindows.top().value;
        modalWindow.modalDomEl.toggleClass(modalWindow.windowTopClass || '', toggleSwitch);
      }
    }

    function checkRemoveBackdrop() {
      //remove backdrop if no longer needed
      if (backdropDomEl && backdropIndex() === -1) {
        removeAfterAnimate(backdropDomEl, backdropScope);
        backdropDomEl = undefined;
        backdropScope = undefined;
      }
    }

    function removeAfterAnimate(domEl, scope, done, closedDeferred) {
      var asyncDeferred = void 0;
      var asyncPromise = null;
      var setIsAsync = function setIsAsync() {
        if (!asyncDeferred) {
          asyncDeferred = $q.defer();
          asyncPromise = asyncDeferred.promise;
        }

        return function asyncDone() {
          asyncDeferred.resolve();
        };
      };
      scope.$broadcast($modalStack.NOW_CLOSING_EVENT, setIsAsync);

      // Note that it's intentional that asyncPromise might be null.
      // That's when setIsAsync has not been called during the
      // NOW_CLOSING_EVENT broadcast.
      return $q.when(asyncPromise).then(afterAnimating);

      function afterAnimating() {
        if (afterAnimating.done) {
          return;
        }
        afterAnimating.done = true;

        $animate.leave(domEl).then(function () {
          if (done) {
            done();
          }

          domEl.remove();
          if (closedDeferred) {
            closedDeferred.resolve();
          }
        });

        scope.$destroy();
      }
    }

    $document.on('keydown', keydownListener);

    $rootScope.$on('$destroy', function () {
      $document.off('keydown', keydownListener);
    });

    function keydownListener(evt) {
      if (evt.isDefaultPrevented()) {
        return evt;
      }

      var modal = openedWindows.top();
      if (modal) {
        switch (evt.which) {
          case 27:
            {
              if (modal.value.keyboard) {
                evt.preventDefault();
                $rootScope.$apply(function () {
                  $modalStack.dismiss(modal.key, 'escape key press');
                });
              }
              break;
            }
          case 9:
            {
              var list = $modalStack.loadFocusElementList(modal);
              var focusChanged = false;
              if (evt.shiftKey) {
                if ($modalStack.isFocusInFirstItem(evt, list) || $modalStack.isModalFocused(evt, modal)) {
                  focusChanged = $modalStack.focusLastFocusableElement(list);
                }
              } else {
                if ($modalStack.isFocusInLastItem(evt, list)) {
                  focusChanged = $modalStack.focusFirstFocusableElement(list);
                }
              }

              if (focusChanged) {
                evt.preventDefault();
                evt.stopPropagation();
              }

              break;
            }
        }
      }
    }

    $modalStack.open = function (modalInstance, modal) {
      var modalOpener = $document[0].activeElement;
      var modalBodyClass = modal.openedClass || OPENED_MODAL_CLASS;

      toggleTopWindowClass(false);

      // Store the current top first, to determine what index we ought to use
      // for the current top modal
      previousTopOpenedModal = openedWindows.top();

      openedWindows.add(modalInstance, {
        deferred: modal.deferred,
        renderDeferred: modal.renderDeferred,
        closedDeferred: modal.closedDeferred,
        modalScope: modal.scope,
        backdrop: modal.backdrop,
        keyboard: modal.keyboard,
        openedClass: modal.openedClass,
        windowTopClass: modal.windowTopClass,
        animation: modal.animation,
        appendTo: modal.appendTo
      });

      openedClasses.put(modalBodyClass, modalInstance);

      var appendToElement = modal.appendTo;
      var currBackdropIndex = backdropIndex();

      if (currBackdropIndex >= 0 && !backdropDomEl) {
        backdropScope = $rootScope.$new(true);
        backdropScope.modalOptions = modal;
        backdropScope.index = currBackdropIndex;
        backdropDomEl = angular.element('<div uib-modal-backdrop="modal-backdrop"></div>');
        backdropDomEl.attr({
          class: 'modal-backdrop',
          'ng-style': "{'z-index': 1040 + (index && 1 || 0) + index*10}",
          'uib-modal-animation-class': 'fade',
          'modal-in-class': 'in'
        });
        if (modal.backdropClass) {
          backdropDomEl.addClass(modal.backdropClass);
        }

        if (modal.animation) {
          backdropDomEl.attr('modal-animation', 'true');
        }
        $compile(backdropDomEl)(backdropScope);
        var animatedBackdropEl = backdropDomEl;
        $animate.enter(animatedBackdropEl, appendToElement).then(function () {
          if (animatedBackdropEl !== backdropDomEl) {
            // backdrop got added after everything was closed, so just remove it immediately
            animatedBackdropEl.remove();
          }
        });
      }

      var content = void 0;
      if (modal.component) {
        content = document.createElement(snakeCase(modal.component.name));
        content = angular.element(content);
        content.attr({
          resolve: '$resolve',
          'modal-instance': '$modalInstance',
          close: '$close($value)',
          dismiss: '$dismiss($value)'
        });
      } else {
        content = modal.content;
      }

      // Set the top modal index based on the index of the previous top modal
      topModalIndex = previousTopOpenedModal ? parseInt(previousTopOpenedModal.value.modalDomEl.attr('index'), 10) + 1 : 0;
      var angularDomEl = angular.element('<div uib-modal-window="modal-window"></div>');
      angularDomEl.attr({
        class: 'modal',
        'window-top-class': modal.windowTopClass,
        'allow-dark-mode': modal.allowDarkMode || '',
        role: 'dialog',
        'aria-labelledby': modal.ariaLabelledBy,
        'aria-describedby': modal.ariaDescribedBy,
        size: modal.size,
        index: topModalIndex,
        animate: 'animate',
        'ng-style': "{'z-index': 1050 + $$topModalIndex*10, display: 'block'}",
        tabindex: -1,
        'uib-modal-animation-class': 'fade',
        'modal-in-class': 'in'
      }).data({
        windowTemplate: modal.windowTemplate
      }).append(content);
      if (modal.windowClass) {
        angularDomEl.addClass(modal.windowClass);
      }

      if (modal.animation) {
        angularDomEl.attr('modal-animation', 'true');
      }

      appendToElement.addClass(modalBodyClass);
      if (modal.scope) {
        // we need to explicitly add the modal index to the modal scope
        // because it is needed by ngStyle to compute the zIndex property.
        modal.scope.$$topModalIndex = topModalIndex;
      }
      $animate.enter($compile(angularDomEl)(modal.scope), appendToElement);

      openedWindows.top().value.modalDomEl = angularDomEl;
      openedWindows.top().value.modalOpener = modalOpener;

      applyAriaHidden(angularDomEl);

      function applyAriaHidden(el) {
        if (!el || el[0].tagName === 'BODY') {
          return;
        }

        getSiblings(el).forEach(function (sibling) {
          var elemIsAlreadyHidden = sibling.getAttribute('aria-hidden') === 'true';
          var ariaHiddenCount = parseInt(sibling.getAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME), 10);

          if (!ariaHiddenCount) {
            ariaHiddenCount = elemIsAlreadyHidden ? 1 : 0;
          }

          sibling.setAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME, ariaHiddenCount + 1);
          sibling.setAttribute('aria-hidden', 'true');
        });

        return applyAriaHidden(el.parent());

        function getSiblings(el) {
          var children = el.parent() ? el.parent().children() : [];

          return Array.prototype.filter.call(children, function (child) {
            return child !== el[0];
          });
        }
      }
    };

    function broadcastClosing(modalWindow, resultOrReason, closing) {
      return !modalWindow.value.modalScope.$broadcast('modal.closing', resultOrReason, closing).defaultPrevented;
    }

    function unhideBackgroundElements() {
      Array.prototype.forEach.call(document.querySelectorAll('[' + ARIA_HIDDEN_ATTRIBUTE_NAME + ']'), function (hiddenEl) {
        var ariaHiddenCount = parseInt(hiddenEl.getAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME), 10);
        var newHiddenCount = ariaHiddenCount - 1;
        hiddenEl.setAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME, newHiddenCount);

        if (!newHiddenCount) {
          hiddenEl.removeAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME);
          hiddenEl.removeAttribute('aria-hidden');
        }
      });
    }

    $modalStack.close = function (modalInstance, result) {
      var modalWindow = openedWindows.get(modalInstance);
      unhideBackgroundElements();
      if (modalWindow && broadcastClosing(modalWindow, result, true)) {
        modalWindow.value.modalScope.$$uibDestructionScheduled = true;
        modalWindow.value.deferred.resolve(result);
        removeModalWindow(modalInstance, modalWindow.value.modalOpener);
        return true;
      }

      return !modalWindow;
    };

    $modalStack.dismiss = function (modalInstance, reason) {
      var modalWindow = openedWindows.get(modalInstance);
      unhideBackgroundElements();
      if (modalWindow && broadcastClosing(modalWindow, reason, false)) {
        modalWindow.value.modalScope.$$uibDestructionScheduled = true;
        modalWindow.value.deferred.reject(reason);
        removeModalWindow(modalInstance, modalWindow.value.modalOpener);
        return true;
      }
      return !modalWindow;
    };

    $modalStack.dismissAll = function (reason) {
      var topModal = this.getTop();
      while (topModal && this.dismiss(topModal.key, reason)) {
        topModal = this.getTop();
      }
    };

    $modalStack.getElement = function (modalInstance) {
      var modalWindow = openedWindows.get(modalInstance);
      return modalWindow && modalWindow.value && modalWindow.value.modalDomEl;
    };

    $modalStack.getTop = function () {
      return openedWindows.top();
    };

    $modalStack.modalRendered = function (modalInstance) {
      var modalWindow = openedWindows.get(modalInstance);
      if (modalWindow) {
        modalWindow.value.renderDeferred.resolve();
      }
    };

    $modalStack.focusFirstFocusableElement = function (list) {
      if (list.length > 0) {
        list[0].focus();
        return true;
      }
      return false;
    };

    $modalStack.focusLastFocusableElement = function (list) {
      if (list.length > 0) {
        list[list.length - 1].focus();
        return true;
      }
      return false;
    };

    $modalStack.isModalFocused = function (evt, modalWindow) {
      if (evt && modalWindow) {
        var modalDomEl = modalWindow.value.modalDomEl;
        if (modalDomEl && modalDomEl.length) {
          return (evt.target || evt.srcElement) === modalDomEl[0];
        }
      }
      return false;
    };

    $modalStack.isFocusInFirstItem = function (evt, list) {
      if (list.length > 0) {
        return (evt.target || evt.srcElement) === list[0];
      }
      return false;
    };

    $modalStack.isFocusInLastItem = function (evt, list) {
      if (list.length > 0) {
        return (evt.target || evt.srcElement) === list[list.length - 1];
      }
      return false;
    };

    $modalStack.loadFocusElementList = function (modalWindow) {
      if (modalWindow) {
        var modalDomE1 = modalWindow.value.modalDomEl;
        if (modalDomE1 && modalDomE1.length) {
          var elements = modalDomE1[0].querySelectorAll(tabbableSelector);
          return elements ? Array.prototype.filter.call(elements, function (element) {
            return isVisible(element);
          }) : elements;
        }
      }
    };

    return $modalStack;
  }]).provider('$modal', function () {
    var $modalProvider = {
      options: {
        animation: true,
        backdrop: true, //can also be false or 'static'
        keyboard: true
      },
      $get: /* @ngInject */['$rootScope', '$q', '$document', '$templateRequest', '$controller', '$uibResolve', '$modalStack', '$log', function $get($rootScope, $q, $document, $templateRequest, $controller, $uibResolve, $modalStack, $log) {
        var $modal = {};

        function getTemplatePromise(options) {
          return options.template ? $q.when(options.template) : $templateRequest(angular.isFunction(options.templateUrl) ? options.templateUrl() : options.templateUrl);
        }

        var promiseChain = null;
        $modal.getPromiseChain = function () {
          return promiseChain;
        };

        var openModals = {};

        /**
         * Makes it easy to create modals that should only ever open once
         * @param {String} key Unique identifier for this modal
         * @param {*} modalOptions Options to pass to $modal.open
         * @returns {*}
         */
        $modal.openOnce = function (key, modalOptions) {
          if (openModals[key]) {
            return openModals[key];
          } else {
            var modal = $modal.open(modalOptions);
            modal.result.finally(function () {
              delete openModals[key];
            });
            return openModals[key] = modal;
          }
        };

        $modal.open = function (modalOptions) {
          if (modalOptions && modalOptions.scope && modalOptions.scope.$$destroyed) {
            throw new Error('Tried to create modal on destroyed scope');
          }
          var modalResultDeferred = $q.defer();
          var modalOpenedDeferred = $q.defer();
          var modalClosedDeferred = $q.defer();
          var modalRenderDeferred = $q.defer();

          //prepare an instance of a modal to be injected into controllers and returned to a caller
          var modalInstance = {
            result: modalResultDeferred.promise,
            opened: modalOpenedDeferred.promise,
            closed: modalClosedDeferred.promise,
            rendered: modalRenderDeferred.promise,
            close: function close(result) {
              return $modalStack.close(modalInstance, result);
            },
            dismiss: function dismiss(reason) {
              return $modalStack.dismiss(modalInstance, reason);
            },
            getElement: function getElement() {
              return $modalStack.getElement(modalInstance);
            }
          };

          //merge and clean up options
          modalOptions = angular.extend({}, $modalProvider.options, modalOptions);
          modalOptions.resolve = modalOptions.resolve || {};
          modalOptions.appendTo = modalOptions.appendTo || $document.find('body').eq(0);

          if (!modalOptions.appendTo.length) {
            throw new Error('appendTo element not found. Make sure that the element passed is in DOM.');
          }

          //verify options
          if (!modalOptions.component && !modalOptions.template && !modalOptions.templateUrl) {
            throw new Error('One of component or template or templateUrl options is required.');
          }

          var templateAndResolvePromise = void 0;
          if (modalOptions.component) {
            templateAndResolvePromise = $q.when($uibResolve.resolve(modalOptions.resolve, {}, null, null));
          } else {
            templateAndResolvePromise = $q.all([getTemplatePromise(modalOptions), $uibResolve.resolve(modalOptions.resolve, {}, null, null)]);
          }

          function resolveWithTemplate() {
            return templateAndResolvePromise;
          }

          // Wait for the resolution of the existing promise chain.
          // Then switch to our own combined promise dependency (regardless of how the previous modal fared).
          // Then add to $modalStack and resolve opened.
          // Finally clean up the chain variable if no subsequent modal has overwritten it.
          var samePromise = void 0;
          samePromise = promiseChain = $q.all([promiseChain]).then(resolveWithTemplate, resolveWithTemplate).then(function resolveSuccess(tplAndVars) {
            var providedScope = modalOptions.scope || $rootScope;

            var modalScope = providedScope.$new();
            modalScope.$close = modalInstance.close;
            modalScope.$dismiss = modalInstance.dismiss;

            /**
             * Can be used to dynamically prevent accidental dismissals. Pass in a function.
             * If the function returns a truthy value, then the modal will not be dismissable via
             * the backdrop or ESC
             * @param when {Function} A function called to see if we should cancel the modal dismissal
             */
            modalInstance.preventDismissWhen = function (when) {
              modalScope.$on('modal.closing', function ($event, reason, closing) {
                if (!closing && (reason === 'backdrop click' || reason === 'escape key press') && when()) {
                  $event.preventDefault();
                }
              });
            };

            modalScope.$on('$destroy', function () {
              if (!modalScope.$$uibDestructionScheduled) {
                modalScope.$dismiss('$uibUnscheduledDestruction');
              }
            });

            var modal = {
              scope: modalScope,
              deferred: modalResultDeferred,
              renderDeferred: modalRenderDeferred,
              closedDeferred: modalClosedDeferred,
              animation: modalOptions.animation,
              backdrop: modalOptions.backdrop,
              keyboard: modalOptions.keyboard,
              backdropClass: modalOptions.backdropClass,
              windowTopClass: modalOptions.windowTopClass,
              windowClass: modalOptions.windowClass,
              allowDarkMode: modalOptions.allowDarkMode,
              windowTemplate: modalOptions.windowTemplate,
              ariaLabelledBy: modalOptions.ariaLabelledBy,
              ariaDescribedBy: modalOptions.ariaDescribedBy,
              size: modalOptions.size,
              openedClass: modalOptions.openedClass,
              appendTo: modalOptions.appendTo
            };

            var component = {};
            var ctrlInstance = void 0;
            var ctrlInstantiate = void 0;
            var ctrlLocals = {};

            if (modalOptions.component) {
              constructLocals(component, false, true, false);
              component.name = modalOptions.component;
              modal.component = component;
            } else if (modalOptions.controller) {
              constructLocals(ctrlLocals, true, false, true);

              // the third param will make the controller instantiate later,private api
              // @see https://github.com/angular/angular.js/blob/master/src/ng/controller.js#L126
              ctrlInstantiate = $controller(modalOptions.controller, ctrlLocals, true, modalOptions.controllerAs);
              if (modalOptions.controllerAs && modalOptions.bindToController) {
                ctrlInstance = ctrlInstantiate.instance;
                ctrlInstance.$close = modalScope.$close;
                ctrlInstance.$dismiss = modalScope.$dismiss;
                angular.extend(ctrlInstance, {
                  $resolve: ctrlLocals.$scope.$resolve
                }, providedScope);
              }

              ctrlInstance = ctrlInstantiate();

              if (angular.isFunction(ctrlInstance.$onInit)) {
                ctrlInstance.$onInit();
              }
            }

            if (!modalOptions.component) {
              modal.content = tplAndVars[0];
            }

            $modalStack.open(modalInstance, modal);
            modalOpenedDeferred.resolve(true);

            function constructLocals(obj, template, instanceOnScope, injectable) {
              obj.$scope = modalScope;
              obj.$scope.$resolve = {};
              if (instanceOnScope) {
                obj.$scope.$modalInstance = modalInstance;
              } else {
                obj.$modalInstance = modalInstance;
              }

              var resolves = template ? tplAndVars[1] : tplAndVars;
              angular.forEach(resolves, function (value, key) {
                if (injectable) {
                  obj[key] = value;
                }

                obj.$scope.$resolve[key] = value;
              });
            }
          }).catch(function resolveError(reason) {
            $log.error('Unable to open modal', reason);
            modalOpenedDeferred.reject(reason);
            modalResultDeferred.reject(reason);
          }).finally(function () {
            if (promiseChain === samePromise) {
              promiseChain = null;
            }
          });

          return modalInstance;
        };

        return $modal;
      }]
    };

    return $modalProvider;
  })

  // Simple directive that can be used to render a sliding wizard inside a dialog
  .directive('modalWizard', ['$animate', function ($animate) {
    return {
      restrict: 'E',
      link: function link(scope, $element) {
        var element = $element[0];
        var els = {};

        function checkEl(type) {
          return function ($animElement, phase) {
            var animElement = $animElement[0];
            if (animElement.parentNode === element) {
              var isStart = phase === 'start';
              $element.toggleClass('active', isStart);
              els[type] = isStart && animElement;
              // determine if the entering node is before or after the leaving node
              var reverse = els.enter && els.leave && els.enter.compareDocumentPosition(els.leave) & Node.DOCUMENT_POSITION_FOLLOWING;
              $element.toggleClass('reverse', !!reverse);
            }
          };
        }

        $animate.on('enter', $element, checkEl('enter'));
        $animate.on('leave', $element, checkEl('leave'));
      }
    };
  }]);

  var dialogTemplate = "<div class=\"modal-header\" ng-if=\"dialog.title\"> <h4 class=\"modal-title\">{{ ::dialog.title }}</h4> </div> <div class=\"modal-body\"> <div class=\"message\" ng-bind-html=\"dialog.msg\"></div> </div> <div class=\"modal-footer\" ng-if=\"dialog.buttons.length\"> <button ng-repeat=\"btn in dialog.buttons\" class=\"{{ ::btn.id }} btn-{{ ::btn.style }}\" track-click=\"Dialog Button Clicked\" track-data=\"{type: btn.id}\" track-options=\"{intercom: false}\" on-confirm=\"dialog.click(btn)\" on-confirm-yes=\"!btn.confirmMessage\"> {{ ::btn.text }} </button> </div> ";

  var bubbleDialogTemplate = "<div class=\"bubble-dialog popover {{::className}} {{::positionClass}} {{::closeable?'bubble-dialog-closeable':''}}\"> <div class=\"arrow\"></div> <div class=\"popover-inner\"> <div class=\"popover-close\" ng-click=\"remove()\"></div> <div class=\"popover-title\" ng-if=\"title\">{{title}}</div> <div class=\"popover-content\"> <div class=\"content\" ng-bind-html=\"content\" ng-if=\"!contentTemplate\"></div> <div class=\"content\" compile-template=\"contentTemplate\" ng-if=\"contentTemplate\"></div> <div class=\"btn-container\" ng-if=\"buttons\"> <button ng-repeat=\"button in buttons\" class=\"{{button.className || 'btn btn-default'}}\" ng-click=\"button.resolve($event)\"> {{button.title}} </button> </div> </div> </div> </div> ";

  var bubbleDialogMenuTemplate = "<div class=\"bubble-dialog popover bubble-dialog-menu {{::className}} {{::positionClass}}\"> <div class=\"arrow\"></div> <div class=\"popover-inner\" compile-template=\"menu\"></div> </div> ";

  /* eslint-disable angular/timeout-service */

  var MutationObserver$1 = window.MutationObserver || window.webkitMutationObserver;

  var nodeChangeListenerServiceModule = angular.module('Q.services.node-change-listener', []).factory('nodeChangeListener', ['$window', function ($window) {
    /**
     * Listens to DOM changes on a given node using browser methods.
     *
     * @param node Node to listen on
     * @param fn {Function} Function to call on DOM changes
     * @returns {Function} Function to disconnect the listener
     */
    function listenForChanges(node, fn) {
      var stopListening = angular.noop;

      if (node instanceof angular.element) {
        node = node[0];
      }

      if (MutationObserver$1) {
        // listen for any subtree changes
        var mutationObserverOpts = {
          childList: true,
          characterData: true,
          attributes: true,
          subtree: true
        };
        var observer = new MutationObserver$1(function () {
          observer.disconnect();
          var event = new CustomEvent('MutationEvent');
          fn(event);
          observer.observe(node, mutationObserverOpts);
        });
        observer.observe(node, mutationObserverOpts);

        stopListening = function stopListening() {
          return observer.disconnect();
        };
      } else {
        var ignoringMutations = false;
        var subtreeDebounce = null;
        var subtreeTrigger = function subtreeTrigger(event) {
          if (subtreeDebounce) $window.clearTimeout(subtreeDebounce);
          subtreeDebounce = $window.setTimeout(function () {
            if (ignoringMutations) return;
            ignoringMutations = true;
            fn(event);
            subtreeDebounce = null;
            ignoringMutations = false;
          }, 10);
        };

        var $node = angular.element(node);
        $node.on('DOMSubtreeModified', subtreeTrigger);

        stopListening = function stopListening() {
          return $node.off('DOMSubtreeModified', subtreeTrigger);
        };
      }

      return stopListening;
    }

    return {
      listen: listenForChanges
    };
  }]);

  /* eslint-disable angular/timeout-service */

  var scrollMonitorServiceModule = angular.module('Q.services.scroll-monitor-service', [nodeChangeListenerServiceModule, windowEventHelperModule])

  /**
   * A service to make it easier to monitor scrolling and perform updates based on it.
   *
   * The service reduces event listeners by grouping scroll listeners per item, and also sharing a single
   * window.resize listener across all scroll listeners.
   *
   * The individual node scroll listeners can further benefit by including an optional element with the event
   * listener.  In this case, the single onScroll event will also update whether or not the item is visible,
   * and set it on the scroll listener return object.
   *
   */
  .factory('scrollMonitorService', ['$document', '$window', 'nodeChangeListener', '$rootScope', '$timeout', '$log', function ($document, $window, nodeChangeListener, $rootScope, $timeout, $log) {
    var scrollListeners = [];
    var doc = $document[0];

    /**
     * Creates a Listener for scroll-related events on a given viewport (DOM node or $window)
     */

    var Listener = function () {
      /**
       * Create a new Listener object
       * @param viewport {string|HTMLElement} The viewport on which we want to listen, defaults to window
       */
      function Listener(viewport) {
        var _this = this;

        classCallCheck(this, Listener);

        this.viewport = viewport;
        this.$viewport = angular.element(viewport);
        this.scrollWatchers = [];
        this.lastScrollTop = 0;
        this.lastHeight = 0;
        this.$destroyed = false;

        this.scrollTrigger = this.triggerUpdate.bind(this);
        this.$viewport.on('scroll touchmove', this.scrollTrigger);
        // ensure we always clean up if the dom node is destroyed
        this.$viewport.on('$destroy', function () {
          return _this.destroy();
        });

        // for non-window-level watches, we also look for DOM changes
        // otherwise, the resize event should be good enough
        if (viewport.nodeType) {
          this.nodeChangeListenerStop = nodeChangeListener.listen(viewport, function (event) {
            _this.triggerUpdate(event, true);
          });
        }

        scrollListeners.push(this);
      }

      /**
       * Trigger an update to all registered scrollWatchers
       * The goal with this design was to minimize calculation of the same data as much as possible.
       * If we have hundreds of event listeners, we don't want to perform the same work looking them up
       * every time.
       *
       * @param {Event} event The Event object for this update
       * @param {boolean} forceUpdate If true, forces an update regardless of scroll or height changes
       */


      createClass(Listener, [{
        key: 'triggerUpdate',
        value: function triggerUpdate(event, forceUpdate) {
          if (this.$destroyed) return;

          var viewport = this.$viewport;
          var viewportScrollTop = viewport.scrollTop();
          var viewportHeight = viewport.offsetHeight();

          if (forceUpdate !== true && viewportScrollTop === this.lastScrollTop && viewportHeight === this.lastHeight) return;

          this.lastScrollTop = viewportScrollTop;
          this.lastHeight = viewportHeight;

          var viewportTop = this.$viewport.offsetTop();

          var len = this.scrollWatchers.length;
          var i = len - 1;
          var sw = void 0;
          var elementTop = void 0;
          var elementHeight = void 0;
          var data = void 0;

          for (; i >= 0; i--) {
            if (viewport.length === 0) {
              $log.error("Invariant violation: expected viewport[0] to be defined but it wasn't");
              continue;
            }

            data = {
              viewport: viewport,
              viewportTop: viewportTop,
              viewportHeight: viewportHeight,
              viewportScrollTop: viewportScrollTop,
              viewportScrollHeight: viewport[0].scrollHeight
            };
            sw = this.scrollWatchers[i];
            if (sw) {
              if (sw.$elem) {
                // note whether the element is visible
                elementTop = sw.$elem.offsetTop();
                elementHeight = sw.elem.offsetHeight;
                data.element = sw.elem;
                data.elementTop = elementTop;
                data.elementHeight = elementHeight;
                Listener._setIsVisible(viewportTop, viewportHeight, viewportScrollTop, sw, data);
              }
              sw.notify(event, data);
            }
          }
        }

        /**
         * Mainly used to set the initial isVisible value on a ScrollWatcher, but it can also be used to
         * update the isVisible value out-of-band
         *
         * @param scrollWatcher
         */

      }, {
        key: 'setIsVisible',
        value: function setIsVisible(scrollWatcher) {
          Listener._setIsVisible(this.$viewport.offsetTop(), this.$viewport.offsetHeight(), this.$viewport.scrollTop(), scrollWatcher, {});
        }
      }, {
        key: 'addScrollWatcher',
        value: function addScrollWatcher(scrollWatcher) {
          var _this2 = this;

          if (this.$destroyed) return;
          this.scrollWatchers.push(scrollWatcher);
          $timeout.cancel(this._swAddedEvent);
          this._swAddedEvent = $timeout(function () {
            _this2.triggerUpdate(new CustomEvent('scrollWatcherAdded'), true);
          });
        }
      }, {
        key: 'removeScrollWatcher',
        value: function removeScrollWatcher(scrollWatcher) {
          var idx = this.scrollWatchers.indexOf(scrollWatcher);
          if (idx > -1) {
            this.scrollWatchers.splice(idx, 1);
            if (this.scrollWatchers.length === 0) {
              this.destroy();
            }
          }
        }
      }, {
        key: 'destroy',
        value: function destroy() {
          this.$viewport.off('scroll touchmove', this.scrollTrigger);
          if (this.nodeChangeListenerStop) {
            this.nodeChangeListenerStop();
          }
          this.scrollWatchers.length = 0;
          var idx = scrollListeners.indexOf(this);
          if (idx > -1) {
            scrollListeners.splice(idx, 1);
          }
          this.$destroyed = true;
        }
      }], [{
        key: '_setIsVisible',
        value: function _setIsVisible(viewportTop, viewportHeight, viewportScrollTop, scrollWatcher, data) {
          var elementTop = scrollWatcher.$elem.offsetTop() - viewportTop;
          var elementHeight = scrollWatcher.elem.offsetHeight;
          data.element = scrollWatcher.elem;
          data.elementTop = elementTop;
          data.elementHeight = elementHeight;
          scrollWatcher.isVisible = data.isVisible =
          // ensure the top is above the bottom of the viewport (with optional offset)
          viewportScrollTop + viewportHeight + scrollWatcher.offset > elementTop &&
          // ensure the bottoms is below the top of the viewport (with optional offset)
          viewportScrollTop - scrollWatcher.offset < elementTop + elementHeight;
        }
      }]);
      return Listener;
    }();

    var ScrollWatcher = function () {
      function ScrollWatcher(listener, callback, elem) {
        var offset = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
        classCallCheck(this, ScrollWatcher);

        this.listener = listener;
        this.callback = callback;
        if (elem) {
          elem = _getElement(elem);
          this.elem = elem;
          this.$elem = angular.element(elem);
          this.offset = offset;
          this.update();
        }
      }

      createClass(ScrollWatcher, [{
        key: 'update',
        value: function update() {
          this.listener.setIsVisible(this);
        }
      }, {
        key: 'notify',
        value: function notify(event, data) {
          this.callback(event, data, this);
        }
      }, {
        key: 'destroy',
        value: function destroy() {
          this.listener.removeScrollWatcher(this);
        }
      }]);
      return ScrollWatcher;
    }();

    var svc = {
      /**
       * Adds a scroll listener to the provided viewport.
       *
       * @param viewport {HTMLElement|String} The node or selector for the node to watch
       * @param callback {function} The callback function on scroll (will be called with event, viewportData, and ScrollWatcher arguments
       * @param elem {HTMLElement|String} Optional element to monitor for visibility and offset information
       * @param offset Optional value for adding an offset when calculating whether the elem is visible or not
       * @returns {ScrollWatcher}
       */
      addListener: function addListener(viewport, callback) {
        var elem = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
        var offset = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;

        viewport = _getElement(viewport);
        var listener = scrollListeners.find(function (e) {
          return e.viewport === viewport;
        });
        if (!listener) {
          listener = new Listener(viewport);
        }

        var scrollWatcher = new ScrollWatcher(listener, callback, elem, offset);

        listener.addScrollWatcher(scrollWatcher);
        return scrollWatcher;
      },
      updateListeners: function updateListeners(event) {
        var i = 0;
        var len = scrollListeners.length;
        for (; i < len; i++) {
          if (!scrollListeners[i]) {
            $log.error("Invariant violation: scrollListeners[i] was supposed to be defined but wasn't");
            continue;
          }

          scrollListeners[i].triggerUpdate(event, true);
        }
      }
    };

    // on resize, fire all scroll listeners, but use only one listener for the whole application
    $rootScope.$on(WINDOW_EVT_RESIZE, function ($e, event) {
      return svc.updateListeners(event);
    });

    // also capture ui layout events
    $rootScope.$on(UI_LAYOUT_EVT_DRAW_COMPLETE, svc.updateListeners);

    return svc;

    //// private methods

    // returns a pure DOM element, or window if none provided
    function _getElement(elem) {
      if (!elem) return $window;

      if (angular.isString(elem)) {
        elem = doc.querySelector(elem);
      } else if (elem.length) {
        elem = elem[0];
      }

      if (!elem) {
        throw new Error('Unable to find element for scrollMonitorService: ' + elem);
      }

      if (elem.nodeType === 9) {
        elem = $window;
      }

      return elem;
    }
  }]);

  var uiBootstrapPositionModule = 'ui.bootstrap.position';

  var positionHelperModule = angular.module('Q.helpers.position-helper', [uiBootstrapPositionModule, scrollMonitorServiceModule]).factory('positionHelper', ['$window', '$document', '$position', 'scrollMonitorService', '$timeout', function ($window, $document, $position, scrollMonitorService, $timeout) {
    /**
     * Positions the popup around the element once
     *
     * @param element {Node} Element to position around
     * @param popup {Node} Popup element
     * @param position {String} position property
     * @param hasArrow {boolean} If true, align the nested arrow as well
     * @param setMinWidth {boolean} If true, set the min width to the element width
     * @param mousePosition {Object} If provided, the popup is oriented around the {pageX,pageY} properties
     */
    function positionPopup(element, popup) {
      var position = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'bottom';

      var _ref = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {},
          _ref$hasArrow = _ref.hasArrow,
          hasArrow = _ref$hasArrow === undefined ? false : _ref$hasArrow,
          _ref$setMinWidth = _ref.setMinWidth,
          setMinWidth = _ref$setMinWidth === undefined ? false : _ref$setMinWidth,
          _ref$mousePosition = _ref.mousePosition,
          mousePosition = _ref$mousePosition === undefined ? null : _ref$mousePosition;

      // don't move the popup if the element isn't attached
      if (!element.isVisible()) return;

      var popupPosition = $position.positionElements(element, popup, position, true);

      // params for making sure the node stays on-screen
      var horizontal = /^left/.test(position) || /^right/.test(position);
      var vertical = /^bottom/.test(position) || /^top/.test(position);
      var $body = angular.element($document[0].body);
      var $win = angular.element($window);
      var winWidth = Math.min($body.offsetWidth(), $win.offsetWidth());
      var winHeight = Math.max($body.offsetHeight(), $win.offsetHeight());
      var popupWidth = popup.offsetWidth();
      var popupHeight = popup.offsetHeight();
      var arrowOffsetAdjustment = 18;
      var arrowAdjustments = { x: 0, y: 0 };

      if (mousePosition) {
        var elBounds = element[0].getBoundingClientRect();
        if (vertical) {
          // top and bottom are horizontally centered
          popupPosition.left += mousePosition.pageX - (elBounds.left + elBounds.width / 2);
        } else if (position === 'right') {
          popupPosition.left -= elBounds.right - mousePosition.pageX;
        } else {
          popupPosition.left += mousePosition.pageX - elBounds.left;
        }
        if (horizontal) {
          // left and right are vertically centered
          popupPosition.top += mousePosition.pageY - (elBounds.top + elBounds.height / 2);
        } else if (position === 'top') {
          popupPosition.top -= elBounds.top - mousePosition.pageY;
        } else {
          popupPosition.top += elBounds.top - mousePosition.pageY;
        }
      }

      popupPosition['margin-left'] = null;
      popupPosition['margin-right'] = null;
      popupPosition['margin-top'] = null;
      popupPosition['margin-bottom'] = null;

      if (setMinWidth) {
        popupPosition['min-width'] = element.offsetWidth() + 'px';
      }

      // ensure we're not off the right edge
      if (popupPosition.left + popupWidth > winWidth - 2) {
        var finalLeft = winWidth - 2 - popupWidth;
        if (vertical && hasArrow) {
          arrowAdjustments.x = Math.min((popupWidth - arrowOffsetAdjustment) / 2, popupPosition.left - finalLeft);
        }
        popupPosition['margin-right'] = '0px';
        popupPosition.left = finalLeft;
      }
      // ensure we're not off the left edge (overrides right side)
      if (popupPosition.left < 2) {
        if (vertical && hasArrow) {
          arrowAdjustments.x = Math.max((arrowOffsetAdjustment - popupWidth) / 2, popupPosition.left - 2);
        }
        popupPosition['margin-left'] = '0px';
        popupPosition.left = 2;
      }
      // ensure we're not off the bottom edge
      if (popupPosition.top + popupHeight > winHeight - 2) {
        var finalTop = winHeight - 2 - popupHeight;
        if (horizontal && hasArrow) {
          arrowAdjustments.y = Math.min((popupHeight - arrowOffsetAdjustment) / 2, popupPosition.top - finalTop);
        }
        popupPosition['margin-top'] = '0px';
        popupPosition.top = finalTop;
      }
      // ensure we're not off the top edge (overrides bottom side)
      if (popupPosition.top < 2) {
        if (horizontal && hasArrow) {
          arrowAdjustments.y = Math.max((arrowOffsetAdjustment - popupHeight) / 2, popupPosition.top - 2);
        }
        popupPosition['margin-bottom'] = '0px';
        popupPosition.top = 2;
      }

      // final styling changes
      popupPosition.top += 'px';
      popupPosition.left += 'px';

      popup.css(popupPosition);
      // offset arrow if necessary
      if (hasArrow) {
        popup.querySelector('.arrow').css({
          transform: 'translate(' + arrowAdjustments.x + 'px, ' + arrowAdjustments.y + 'px)'
        });
      }
    }

    /**
     * Automatically positions the popup near the element, and watches to ensure it stays in place.
     *
     * @param $scope Scope for listening on
     * @param element Element to position around
     * @param popup Popup element
     * @param position position property
     * @param opts Options passed to the positionPopup function
     * @returns {function()} Deregistration function, only necessary if the popup is closed before the scope is destroyed
     */
    function autoPositionPopup($scope, element, popup, position, opts) {
      popup.css({ opacity: 0 });

      function setPosition() {
        $timeout(function () {
          positionPopup(element, popup, position, opts);
          popup.css({ opacity: null });
        }, 0, false);
      }

      setPosition();

      if (opts.mousePosition) {
        // only position once if we're using mouse position
        return angular.noop;
      }

      var scrollMonitor = scrollMonitorService.addListener(element.scrollableParent(), setPosition);
      var unlistenDestroy = $scope.$on('$destroy', function () {
        return scrollMonitor.destroy();
      });
      var unlistenWatch = $scope.$watch(setPosition);

      return function () {
        scrollMonitor.destroy();
        unlistenDestroy();
        unlistenWatch();
      };
    }

    function topOrBottom(el, estimatedHeight) {
      var position = 'bottom';
      // if within 200px (or 1/3) of the bottom, switch to top
      var elOffset = el.offset();
      var parent = el.scrollableParent();
      var parentOffset = parent.offset();
      var variance = Math.max(estimatedHeight || 0, Math.min(200, parentOffset.height / 3));
      if (elOffset.bottom - parent.scrollTop() > parentOffset.bottom - variance) {
        position = 'top';
      }
      return position;
    }

    return {
      autoPositionPopup: autoPositionPopup,
      positionPopup: positionPopup,
      topOrBottom: topOrBottom
    };
  }]);

  // This really doesn't do anything, but makes the import code more consistent;

  var ngAriaModule = 'ngAria';
  var ngSanitizeModule = 'ngSanitize';
  var ngAnimateModule = 'ngAnimate';

  /* eslint-disable angular/timeout-service */

  var doc$1 = window.document;

  /**
   * Creates a before unload event handler.
   *
   * @param {function(Event): string?} handler - A function that returns a message to warn when the window should not be
   *   closed. Return a falsy value or nothing if there's no need to warn.
   * @returns {(function(): void)} Deregistration function, call to stop listening to the event.
   */
  function warnBeforeClosing(handler) {
    var _beforeUnload = function _beforeUnload(event) {
      var result = handler(event);
      if (result) {
        event.preventDefault();
        event.returnValue = result;
        return result;
      }
    };
    window.addEventListener('beforeunload', _beforeUnload);
    return function () {
      window.removeEventListener('beforeunload', _beforeUnload);
    };
  }

  function fixQuery(s) {
    // corrects IDs that aren't valid CSS-style IDs
    return s.replace(/#([^a-z][-\w]+)/gi, '[id="$1"]');
  }

  function querySelector(s) {
    return isString(s) ? doc$1.querySelector(fixQuery(s)) : s;
  }

  function focusElement() {
    var focusedEl = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document.activeElement;
    var backwards = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

    var focussableElements = 'a:not([disabled]), button:not([disabled]), input[type=text]:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';
    if (focusedEl) {
      var focusable = Array.prototype.filter.call(document.querySelectorAll(focussableElements), basicIsVisible);
      var index = focusable.indexOf(focusedEl);
      focusable[index + (backwards ? -1 : 1)].focus();
    }
  }

  function focusNextElement(focusedEl) {
    return focusElement(focusedEl);
  }

  function focusPreviousElement(focusedEl) {
    return focusElement(focusedEl, true);
  }

  function anchorFor(url) {
    var anchor = document.createElement('a');
    anchor.href = url;
    return anchor;
  }

  function hostname(url) {
    return anchorFor(url).hostname;
  }

  function equivalentUrl(urlA, urlB) {
    return hostname(urlA) === hostname(urlB);
  }

  // Returns true if neither this node nor any of it's parents are not explicitly hidden via display/visibility ONLY
  // Ignores opacity, etc.
  function basicIsVisible(el) {
    var p = el.parentNode;

    if (!p) {
      return false;
    }

    //-- Return true for document node
    if (9 === p.nodeType) {
      return true;
    }

    //-- Return false if our element is invisible
    if ('none' === getStyle(el, 'display') || 'hidden' === getStyle(el, 'visibility')) {
      return false;
    }

    // recurse upwards
    return basicIsVisible(p);
  }

  //-- Cross browser method to get style properties:
  function getStyle(el, property) {
    if (el.nodeType === 9) return null;
    if (window.getComputedStyle) {
      return document.defaultView.getComputedStyle(el, null).getPropertyValue(property);
    }
    if (el.currentStyle) {
      return el.currentStyle[property];
    }
  }

  var scrollToComponentModule = angular.module('Q.components.scroll-to', []).directive('scrollTo', ['$window', 'scrollToElement', '$timeout', function ($window, scrollToElement, $timeout) {
    return {
      restrict: 'A',
      link: function link(scope, elem, attrs) {
        // used so we don't replace the history state on page load
        var initialLoad = false;

        elem.on('click', function (event) {
          var target = _getTarget(attrs);

          event.preventDefault();
          if (!initialLoad && _isAnchor(target) && $window.history && $window.history.replaceState) {
            $window.history.replaceState({}, '', $window.location.href.replace(/#.*/, '') + target);
          }
          initialLoad = false;

          scrollToElement({
            target: target,
            scrollTarget: attrs.scrollTarget,
            relativeParent: attrs.relativeParent
          });

          elem[0].blur();
        });

        var hash = $window.location.hash;

        $timeout(function () {
          // handle page-loads
          var target = _getTarget(attrs);
          if (_isAnchor(target) && hash === target) {
            initialLoad = true;
            $timeout(function () {
              return elem.triggerHandler('click');
            });
          }
        }, 250);
      }
    };

    //// private functions

    function _isAnchor(target) {
      return (/^#/.test(target)
      );
    }

    function _getTarget(attrs) {
      var target = attrs.scrollTo;
      if (!target) {
        target = attrs.href;
        if (target) {
          target = target.replace(/.*#/, '#');
        }
      }
      return target;
    }
  }]).directive('scrollHere', ['scrollToElement', '$timeout', function (scrollToElement, $timeout) {
    return {
      restrict: 'A',
      link: function link(scope, elem, attrs) {
        $timeout(function () {
          if (attrs.scrollHere && !scope.$eval(attrs.scrollHere)) {
            return;
          }
          scrollToElement({
            target: elem[0],
            scrollTarget: attrs.scrollTarget,
            relativeParent: attrs.relativeParent,
            offset: attrs.scrollOffset || 0,
            ignoreIfVisible: true
          });
        }, 250);
      }
    };
  }]).factory('preserveScroll', ['$timeout', function ($timeout) {
    return function (target, changeEvent) {
      var $target = angular.element(querySelector(target));
      var scrollParent = $target.scrollableParent();
      // determine current offset from top of parent frame
      var diff = $target.offsetTop() - scrollParent.scrollTop();

      // enable event to change
      var newTarget = changeEvent();
      $timeout(function () {
        // after a digest cycle, adjust the scroll so we don't appear to have changed
        if (newTarget) {
          target = newTarget;
        }
        // re-look up the node if necessary
        var $target = angular.element(querySelector(target));
        scrollParent.scrollTop($target.offsetTop() - diff);
      });
    };
  }]).factory('scrollToElement', ['$window', '$document', '$rootScope', '$q', function ($window, $document, $rootScope, $q) {
    var animFrame = void 0;

    /**
     * Scrolls to a target element
     * @param target {string|HTMLElement|angular.element} Element to scroll to (required)
     * @param scrollTarget {string|HTMLElement|angular.element?} Element to scroll. If not provided, determined automatically
     * @param relativeParent {string|HTMLElement|angular.element?} If provided, used to determine the top of the element for relatively positioned elements
     * @param ignoreIfVisible {boolean?} If true, don't scroll if the element is already visible
     * @param offset {number?} If provided, offset the final value by this much
     * @param center {boolean|number?} If true, center the item in the view. If a number, it should be between 0 and 1, and is used to offset the value by that %. .5 is centered, .25 is 1/4 from the top, etc.
     * @returns {*}
     */
    function scrollToElement(_ref) {
      var target = _ref.target,
          scrollTarget = _ref.scrollTarget,
          relativeParent = _ref.relativeParent,
          ignoreIfVisible = _ref.ignoreIfVisible,
          _ref$offset = _ref.offset,
          offset = _ref$offset === undefined ? 0 : _ref$offset,
          _ref$center = _ref.center,
          center = _ref$center === undefined ? false : _ref$center;

      if (animFrame) {
        $window.cancelAnimationFrame(animFrame);
        animFrame = null;
      }

      var _calculateScrollTo2 = _calculateScrollTo(target, scrollTarget, relativeParent, offset, center),
          $scrollTarget = _calculateScrollTo2.$scrollTarget,
          scrollOffset = _calculateScrollTo2.scrollOffset,
          scrollRelative = _calculateScrollTo2.scrollRelative,
          isVisible$$1 = _calculateScrollTo2.isVisible;

      if (!$scrollTarget || ignoreIfVisible && isVisible$$1 || !scrollRelative) {
        return $q.resolve(false);
      }

      var animTime = Math.min(500, Math.abs(scrollRelative));
      var start = Date.now();

      var deferred = $q.defer();

      $rootScope.$broadcast('scrollTo.scrolling', true);
      _animateScroll();

      return deferred.promise;

      //// private functions

      function _animateScroll() {
        var now = Date.now();
        var percentTime = (now - start) / animTime;
        var percentScroll = percentTime >= 1 ? 1 : 0.5 + Math.cos(percentTime * Math.PI - Math.PI) / 2;
        var currentScrollTo = scrollOffset + Math.ceil(percentScroll * scrollRelative);

        $scrollTarget.scrollTop(currentScrollTo);

        // continue until done
        if (percentScroll < 1) {
          animFrame = $window.requestAnimationFrame(_animateScroll);
        } else {
          $rootScope.$broadcast('scrollTo.scrolling', false);
          animFrame = null;
          deferred.resolve(true);
        }
      }
    }

    scrollToElement.query = _query;

    return scrollToElement;

    function _query(node) {
      return angular.isString(node) ? querySelector(node) : node;
    }

    function _calculateScrollTo(target, scrollTarget, relativeParent) {
      var offset = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
      var center = arguments[4];

      target = _query(target);
      if (!target) return {};

      var $target = angular.element(target);
      var targetHeight = $target.offsetHeight();

      scrollTarget = scrollTarget ? _query(scrollTarget) : $target.scrollableParent()[0];
      relativeParent = relativeParent || scrollTarget;

      var $scrollTarget = angular.element(scrollTarget);
      var scrollTargetHeight = $scrollTarget.offsetHeight();
      var relativeParentTop = 0;
      if (relativeParent && relativeParent !== $window) {
        relativeParentTop = angular.element(_query(relativeParent)).offsetTop();
      }
      if (offset && angular.isString(offset)) {
        offset = angular.element(_query(offset)).offsetHeight();
      }

      // calculate the final scroll value
      var scrollTo = $target.offsetTop() - relativeParentTop - $scrollTarget.getFixedNodeOffsets();
      if (center && targetHeight < scrollTargetHeight) {
        var centeringOffsetRatio = angular.isNumber(center) ? center : 0.5;
        // try to center the node in the view
        scrollTo -= (scrollTargetHeight - targetHeight) * centeringOffsetRatio;
      }
      scrollTo -= offset;

      var scrollOffset = $scrollTarget.scrollTop();
      var scrollRelative = scrollTo - scrollOffset;
      var targetBottom = scrollTo + targetHeight;
      var scrollTargetBottom = scrollOffset + scrollTargetHeight;
      var isVisible$$1 = scrollTo > scrollOffset && targetBottom < scrollTargetBottom;

      return {
        scrollTo: scrollTo,
        scrollOffset: scrollOffset,
        scrollRelative: scrollRelative,
        $scrollTarget: $scrollTarget,
        isVisible: isVisible$$1
      };
    }
  }]);

  var IS_MAC = window.navigator.userAgent.includes('Mac OS X');
  var IS_WINDOWS = window.navigator.userAgent.includes('Windows');
  var TERMINAL_CONTINUE = (IS_WINDOWS ? '^' : '\\') + '\n    ';
  // we can't safely use `&` on windows, so we're just going to run the commands one after the other.
  var TERMINAL_COMMAND_SEP = IS_WINDOWS ? '\n' : ' && ' + TERMINAL_CONTINUE;

  var keycodesModule = angular.module('Q.constants.keycodes', []).constant('KeyCodes', {
    // Use $event.which === KeyCodes.KEY_NAME for correct matching
    BACKSPACE: 8,
    TAB: 9,
    ENTER: 13,
    RETURN: 13,
    SHIFT: 16,
    CTRL: 17,
    ALT: 18,
    ESC: 27,
    ESCAPE: 27,
    SPACE: 32,
    PAGE_UP: 33,
    PAGE_DOWN: 34,
    END: 35,
    HOME: 36,
    LEFT_ARROW: 37,
    UP_ARROW: 38,
    RIGHT_ARROW: 39,
    DOWN_ARROW: 40,
    INSERT: 45,
    DELETE: 46,
    COMMAND: 224,
    // some useful utility & display properties
    IS_MAC: IS_MAC,
    COMMAND_KEY: IS_MAC ? 'Cmd' : 'Ctrl',
    isCommand: function isCommand(event) {
      return !!event[IS_MAC ? 'metaKey' : 'ctrlKey'];
    },
    isShift: function isShift(event) {
      return !!event.shiftKey;
    },
    isAlt: function isAlt(event) {
      return !!event.altKey;
    },

    SYMBOLS: {
      COMMAND_KEY: IS_MAC ? '\u2318' : 'Ctrl',
      ALT_KEY: IS_MAC ? '\u2325' : 'Alt',
      CONTROL_KEY: IS_MAC ? '\u2303' : 'Ctrl',
      SHIFT_KEY: IS_MAC ? '\u21E7' : 'Shift',
      LEFT_ARROW: '\u2190',
      UP_ARROW_ARROW: '\u2190',
      RIGHT_ARROW: '\u2192',
      DOWN_ARROW: '\u2193',
      ENTER: IS_MAC ? '\u23CE' : 'Enter'
    }
  }).run(['$rootScope', 'KeyCodes', function ($rootScope, KeyCodes) {
    $rootScope.KeyCodes = KeyCodes;
  }]);

  var _compiledTemplate = void 0;
  var _compiledMenuTemplate = void 0;

  var bubbleDialogComponentModule = angular.module('Q.components.bubble-dialog', [ngSanitizeModule, positionHelperModule, scrollToComponentModule, keycodesModule])

  /**
   * @docType shared
   * @category service
   * @name Bubble-Dialog-Service
   * @description
   * A service that allows you to create a bubble dialog
   * @returns A factory object {object}
   */
  .factory('bubbleDialogService', ['$compile', '$rootScope', '$templateRequest', '$timeout', '$q', '$document', '$window', '$animate', 'positionHelper', 'scrollToElement', 'KeyCodes', function ($compile, $rootScope, $templateRequest, $timeout, $q, $document, $window, $animate, positionHelper, scrollToElement, KeyCodes) {
    /**
     * @parent Bubble-Dialog-Service
     * @name open
     * @description
     * A method that will open a bubble dialog on an element
     * @param scope {$rootScope.Scope} The angular scope to use for the bubble dialog's content (will be inherited)
     * @param {Object} el An element to position the bubble dialog relative to
     * @param {Object} opts A series of options that allow you configure the bubble dialog (title, content, buttons, position)
     * @param {string?} opts.content Optional content for the dialog (HTML OK) (req'd if no template)
     * @param {string?} opts.contentTemplate Optional content as template for the dialog (will be compiled)
     * @param {string?} opts.template Optional complete template for the dialog (must include all of the popover elements)
     * @param {string?} opts.menu Optional menu template for the dialog, which is used to render the bubble as a dropdown menu (handles layout, styling)
     * @param {string?} opts.position Optional text value indicating where to show the popup. Default is above or below based on how close `el` is to the window edge
     * @param {string?} opts.positionArrow If provided, use to center the tooltip
     * @param {boolean?} opts.closeable If true, then show a close button when hovering over the bubble
     * @param {string?} opts.title Optional title for the dialog
     * @param {string?} opts.className Optional class name to add to the dialog
     * @param {Array<Object>?} opts.buttons Optional buttons to append to the end
     * @param {number?} opts.delay If provided, delays showing the bubble for delay milliseconds
     * @param {number?} opts.estimatedHeight If provided, will be used to help determine default placement (top or bottom)
     * @param {Object?} opts.mousePosition If provided, anchors the bubble around this {pageX,pageY} point
     * @param {boolean?|string?} opts.autofocus If provided, enables focusing on the menu and keyboard navigation. If a string, used as the selector for the initial item
     * @returns {object} object representative of the bubble dialog instance just opened {object}
     */
    function open(scope, el, opts) {
      el = angular.element(el);
      var position = opts.position || positionHelper.topOrBottom(el, opts.estimatedHeight);
      var delayTimeout = null;
      var cancelDelayedRender = function cancelDelayedRender() {
        if (delayTimeout) {
          $timeout.cancel(delayTimeout);
          delayTimeout = null;
        }
      };

      var existingBubbleInstance = el.data('bubbleDialog');
      if (existingBubbleInstance && existingBubbleInstance.scope && !existingBubbleInstance.closed) {
        if (!opts.buttons && !opts.template && !opts.menu) {
          // just update scope
          existingBubbleInstance.scope.title = opts.title;
          existingBubbleInstance.scope.content = opts.content;
          existingBubbleInstance.scope.contentTemplate = opts.contentTemplate;
          existingBubbleInstance.scope.position = position;
          return existingBubbleInstance;
        } else {
          // destroy the existing bubble (can't have multiple on an element)
          existingBubbleInstance.closeImmediately();
        }
      }

      var bubbleScope = void 0;
      var bubbleEl = void 0;
      var stateChangeDereg = void 0;
      var scopeDereg = void 0;
      var deferred = $q.defer();

      var bubbleInstance = {
        scope: null,
        bubbleElement: null,
        target: el[0],
        showNow: function showNow() {
          if (!bubbleInstance.rendering) {
            renderBubble();
          }
        },
        close: function close() {
          bubbleInstance.closed = true;
          stateChangeDereg && stateChangeDereg();
          scopeDereg && scopeDereg();
          cancelDelayedRender();
          if (bubbleScope) {
            bubbleScope.remove();
          } else if (bubbleEl) {
            // ensure the node isn't stuck on screen for some reason
            bubbleEl.remove();
          }
          // remove from element
          el.data('bubbleDialog', null);
          deferred.reject('closed');
        },
        closeImmediately: function closeImmediately() {
          bubbleInstance.immediate = true;
          bubbleInstance.close();
        },

        result: deferred.promise
      };
      // never persist across page changes
      stateChangeDereg = scope.$on('$stateChangeStart', bubbleInstance.closeImmediately);
      scopeDereg = scope.$on('$destroy', bubbleInstance.closeImmediately);

      function setHandlers(enable) {
        var method = enable ? 'on' : 'off';
        bubbleEl[method]('click touchstart', stopBubbling);
        // Removed because it was causing unexpected behavior with tooltips nested inside links
        // Could not resolve the original reasoning behind stopping bubbling
        // el[method]("click touchstart", EventUtils.stopBubbling);
        $document[method]('keydown', escapeKeyDown);
        $document[method]('mousedown', escapeClick);
        // on mobile, these functions make sure we don't close on scroll (or long press) events
        $document[method]('touchstart', escapeTouchStart);
        $document[method]('touchend', escapeTouchEnd);
      }

      function isOutsideNode(event) {
        var target = event.target;
        var bubbleNode = bubbleInstance.bubbleElement;
        return target && bubbleNode && !bubbleNode[0].contains(target) && !el[0].contains(target);
      }

      function escapeKeyDown(event) {
        if (event.which === 27) {
          deferred.reject('escape key press');
        }
      }

      function escapeClick(event) {
        if (isOutsideNode(event)) {
          deferred.reject('user clicked elsewhere');
        }
      }

      var touchStartTime = -Infinity;

      function escapeTouchStart() {
        touchStartTime = Date.now();
      }

      function escapeTouchEnd(event) {
        if (Date.now() - touchStartTime < 150) {
          escapeClick(event);
        }
      }

      function renderBubble() {
        if (bubbleInstance.closed || !bubbleInstance.compiledTemplate) return;
        bubbleInstance.rendering = true;
        cancelDelayedRender();
        el.addClass('bubble-showing');

        bubbleInstance.scope = bubbleScope = scope.$new();
        bubbleScope.closeable = opts.closeable;
        bubbleScope.title = opts.title;
        bubbleScope.content = opts.content;
        bubbleScope.contentTemplate = opts.contentTemplate;
        bubbleScope.buttons = opts.buttons;
        bubbleScope.className = (opts.className || '') + (opts.title ? ' titled' : '');
        bubbleScope.menu = opts.menu;
        bubbleScope.position = position;
        bubbleScope.positionClass = position.split('-')[0] + ' ' + position;
        bubbleScope.removing = false;

        bubbleScope.remove = function () {
          if (bubbleScope.removing) return;
          el.removeClass('bubble-showing');

          bubbleScope.removing = true;

          if (bubbleEl) {
            setHandlers(false);
            bubbleScope.$applyAsync(function () {
              if (bubbleInstance.immediate) {
                bubbleEl.remove();
                bubbleScope.$destroy();
              } else {
                $animate.removeClass(bubbleEl, 'showing').then(function () {
                  bubbleEl.remove();
                  bubbleScope.$destroy();
                });
              }
            });
          } else {
            bubbleScope.$destroy();
          }
        };
        deferred.promise.finally(bubbleInstance.close);

        bubbleInstance.compiledTemplate(bubbleScope, function (_bubbleEl) {
          if (bubbleInstance.closed) return;

          bubbleInstance.bubbleElement = bubbleEl = _bubbleEl;

          var $body = angular.element($document[0].body);

          $body.append(bubbleEl);

          $window.requestAnimationFrame(function () {
            positionHelper.autoPositionPopup(bubbleScope, opts.positionArrow || el, bubbleEl, position, {
              hasArrow: true,
              mousePosition: opts.mousePosition
            });
          });

          if (opts.menu) {
            // auto close on click for menus
            bubbleEl.on('click', bubbleInstance.close);
          }

          $animate.addClass(bubbleEl, 'showing').then(function () {
            bubbleInstance.rendering = true;
            deferred.notify({
              ready: true,
              element: bubbleEl
            });
          });

          if (opts.autofocus) {
            // focuses in on the first selectable item
            // Note: this only works well if everything has an explicit `tabindex`
            var selector = angular.isString(opts.autofocus) ? opts.autofocus : '[tabindex]';
            $timeout(function () {
              bubbleEl.querySelector(selector).focus();
              scrollToElement({
                target: opts.positionArrow || el,
                ignoreIfVisible: true,
                center: true
              });

              // setup keystrokes
              bubbleEl.querySelectorAll('[tabindex]').on('keydown', function (event) {
                var target = event.target;
                if (!target || KeyCodes.isCommand(event) || KeyCodes.isShift(event) || KeyCodes.isAlt(event)) {
                  return;
                }
                var which = event.which;
                if (which === KeyCodes.ENTER) {
                  stopEvent(event);
                  angular.element(target).triggerHandler('click');
                } else if (which === KeyCodes.DOWN_ARROW || which === KeyCodes.UP_ARROW) {
                  var nodes = bubbleEl.querySelectorAll('[tabindex]');
                  var idx = Array.from(nodes).indexOf(target) + (which === KeyCodes.DOWN_ARROW ? 1 : -1);
                  var focusNode = nodes[idx];
                  if (focusNode && focusNode.focus) {
                    stopEvent(event);
                    focusNode.focus();
                  }
                }
              });
            }, 100, false);
          }

          setHandlers(true);
        });
      }

      function setupBubble() {
        delayTimeout = $timeout(renderBubble, opts.delay || 0);
      }

      var compiledTemplate = _compiledTemplate;
      if (opts.template) {
        compiledTemplate = $compile(opts.template);
      } else if (opts.menu) {
        if (!_compiledMenuTemplate) {
          _compiledMenuTemplate = $compile(bubbleDialogMenuTemplate);
        }
        compiledTemplate = _compiledMenuTemplate;
      }
      if (!compiledTemplate) {
        compiledTemplate = _compiledTemplate = $compile(bubbleDialogTemplate);
      }
      bubbleInstance.compiledTemplate = compiledTemplate;
      $timeout(setupBubble, 0);

      el.data('bubbleDialog', bubbleInstance);

      if (opts.buttons) {
        opts.buttons.forEach(function (button) {
          button.resolve = function ($event) {
            stopEvent($event);
            deferred.resolve(button.title);
          };
        });
      }

      return bubbleInstance;
    }

    return {
      open: open
    };
  }])
  /**
   * @ngdoc directive
   * @docType shared
   * @category directive
   * @name bubbleDialog
   * @restrict A
   * @element ANY
   * @description
   * Pops up a bubble dialog window with a message.
   *
   * @param {expression} bubbleDialog Message to show
   * @param {expression} bubbleDialogTemplate content as template to show (overrides content)
   * @param {template} bubbleDialogTitle Optional title
   * @param {template} bubbleDialogPosition Optional position argument
   *
   * @returns Directive Definition Object {object}
   */
  .directive('bubbleDialog', ['bubbleDialogService', function (bubbleDialogService) {
    var ACTIVE_CLASS = 'bubble-dialog-active';
    return {
      restrict: 'A',
      require: '?^tooltip',
      link: function link(scope, element, attrs, tooltipCtrl) {
        var instance = null;

        element.on('click', function (event) {
          stopEvent(event);

          if (instance && !instance.closed) {
            instance.close();
            instance = null;
            if (tooltipCtrl) tooltipCtrl.setDisabled(false);
            element.removeClass(ACTIVE_CLASS);
          } else {
            if (tooltipCtrl) tooltipCtrl.setDisabled(true);
            element.addClass(ACTIVE_CLASS);

            instance = bubbleDialogService.open(scope, element, {
              title: attrs.bubbleDialogTitle,
              content: scope.$eval(attrs.bubbleDialog),
              contentTemplate: scope.$eval(attrs.bubbleDialogTemplate),
              position: attrs.bubbleDialogPosition || undefined,
              className: 'bubble-dialog-simple ' + (attrs.bubbleDialogClassName || '')
            });
          }
        });
      }
    };
  }]);

  var onConfirmComponentModule = angular.module('Q.components.on-confirm', [bubbleDialogComponentModule])

  /**
   * @ngdoc directive
   * @docType shared
   * @category directive
   * @name onConfirm
   * @restrict A
   * @element ANY
   * @description
   * Pops up a bubble dialog window asking the user to confirm before continuing with the action
   * It's like ng-click, but with a confirmation first!
   * @param {expression} onConfirm Action to evaluate on click
   * @param {expression} onConfirmYes If true, always confirm
   * @param {template} onConfirmTitle Optional title
   * @param {template} onConfirmMessage Optional message
   * @param {template} onConfirmYesLabel Override default yes label
   * @param {template} onConfirmYesType Override default yes type (primary)
   * @param {template} onConfirmNoLabel Override default no label
   * @param {template} onConfirmPosition Optional position argument
   *
   * @example
   * <example>
   * <div>
   * <div class="btn btn-default" on-confirm="count = count + 1" ng-init="count=0">Click here to blow up the world!</div>
   * <div> You've blown up the world {{count}} times</div>
   * </div>
   * </example>
   * @returns Directive Definition Object {object}
   */
  .directive('onConfirm', ['bubbleDialogService', '$parse', function (bubbleDialogService, $parse) {
    var ACTIVE_CLASS = 'on-confirm-active';
    return {
      restrict: 'A',
      require: ['?^tooltip', '?^dropdown'],
      compile: function compile($element, attr) {
        var fn = $parse(attr.onConfirm);

        return function (scope, element, attrs, _ref) {
          var _ref2 = slicedToArray(_ref, 2),
              tooltipCtrl = _ref2[0],
              dropdownCtrl = _ref2[1];

          var instance = null;

          var close = function close() {
            instance.close();
            instance = null;
            if (tooltipCtrl) tooltipCtrl.setDisabled(false);
            element.removeClass(ACTIVE_CLASS);
          };

          element.on('click', function (event) {
            stopEvent(event);

            if (attrs.disabled) {
              return;
            }

            if (attrs.onConfirmYes && scope.$eval(attrs.onConfirmYes)) {
              scope.$apply(function () {
                if (dropdownCtrl) dropdownCtrl.toggle(false);
                fn(scope, {
                  $event: event
                });
              });
              return;
            }

            if (instance && !instance.closed) {
              close();
            } else {
              if (tooltipCtrl) tooltipCtrl.setDisabled(true);
              element.addClass(ACTIVE_CLASS);

              var yesTitle = attrs.onConfirmYesLabel || 'Yes';
              var noTitle = attrs.onConfirmNoLabel || 'No';

              instance = bubbleDialogService.open(scope, element, {
                title: attrs.onConfirmTitle,
                content: attrs.onConfirmMessage || 'Are you sure?',
                position: attrs.onConfirmPosition || undefined,
                className: 'on-confirm-dialog',
                buttons: [{
                  title: yesTitle,
                  className: 'btn-' + (attrs.onConfirmYesType || 'primary') + ' btn-sm'
                }, {
                  title: noTitle,
                  className: 'btn-default btn-sm'
                }]
              });

              instance.result.then(function (result) {
                if (result === yesTitle) {
                  if (dropdownCtrl) dropdownCtrl.toggle(false);
                  fn(scope, {
                    $event: event
                  });
                }
              }).finally(close);
            }
          });
        };
      }
    };
  }]);

  var DIALOG_BUTTON_STYLES = {
    ok: 'primary',
    yes: 'primary',
    save: 'primary',
    discard: 'danger'
  };

  var DIALOG_CANCEL_BUTTONS = {
    cancel: true
  };

  var dialogComponentModule = angular.module('Q.components.dialog', [modalComponentModule, onConfirmComponentModule]).factory('dialogService', ['$modal', '$sce', function ($modal, $sce) {
    function _open(msg, title, buttons) {
      var modalOptions = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};

      return $modal.open(angular.extend({
        template: dialogTemplate,
        controller: 'DialogCtrl as dialog',
        windowClass: 'no-close-button',
        resolve: { opts: function opts() {
            return { msg: msg, title: title, buttons: buttons };
          } }
      }, modalOptions)).result;
    }

    return {
      busyOverlay: function busyOverlay(msg, helpText) {
        var _msg = '<p><span class="loading-spinner loading-spinner-large"></span></p><h2>' + escapeHTML(msg) + '</h2>';
        if (helpText) {
          _msg += '<p class="help-block">' + escapeHTML(helpText) + '</p>';
        }
        return $modal.open({
          template: dialogTemplate,
          controller: 'DialogCtrl as dialog',
          windowClass: 'no-close-button busy-overlay-dialog',
          resolve: {
            opts: function opts() {
              return { msg: $sce.trustAsHtml(_msg), buttons: [] };
            }
          },
          backdrop: 'static',
          keyboard: false
        });
      },

      alert: function alert(msg, title) {
        return _open(msg, title, ['OK']);
      },
      confirm: function confirm(msg, title) {
        var buttons = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ['No', 'Yes'];

        return _open(msg, title, buttons);
      },
      save: function save(msg, title) {
        var buttons = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ['Cancel', 'Discard', 'Save'];

        return _open(msg, title, buttons);
      },
      open: _open
    };
  }]).controller('DialogCtrl', ['opts', '$modalInstance', function (opts, $modalInstance) {
    var ctrl = this;
    var msg = opts.msg,
        title = opts.title,
        buttons = opts.buttons;

    ctrl.msg = msg;
    ctrl.title = title;
    ctrl.buttons = buttons.map(function (b, i) {
      if (angular.isString(b)) {
        b = { text: b };
      }
      if (!b.id) {
        b.id = slugify(b.text);
      }
      if (!b.style) {
        b.style = DIALOG_BUTTON_STYLES[b.id] || 'default';
      }
      if (!b.isCancel) {
        b.isCancel = DIALOG_CANCEL_BUTTONS[b.id];
      }
      b.index = i;
      return b;
    });

    ctrl.click = function (btn) {
      if (btn.isCancel) {
        $modalInstance.dismiss(btn);
      } else {
        $modalInstance.close(btn);
      }
    };
  }]);

  var productMaintenanceServiceModule = angular.module('Q.services.product-maintenance-service', [configServiceModule, accountServiceModule, uiRouterStateModule, timerHelperModule, dialogComponentModule]).run(['productMaintenanceService', function (productMaintenanceService) {
    productMaintenanceService.init();
  }]).factory('productMaintenanceService', ['configService', 'accountService', '$state', '$q', '$injector', 'timerHelper', '$filter', 'dialogService', function (configService, accountService, $state, $q, $injector, timerHelper, $filter, dialogService) {
    var precheck = function precheck(action) {
      return configService.load().then(accountService.whenReady).then(action, action) // ignore any loading errors
      .then(function (ok) {
        return ok ? $q.resolve(ok) : $q.reject(ok);
      });
    };

    var _event = void 0;

    var WARNING_TIMES = {
      INFO: 4 * 60 * 60 * 1000, // 4 hours before event
      WARNING: 60 * 60 * 1000, // 1 hour before event
      DANGER: 15 * 60 * 1000, // 15 minutes
      DANGER2: 5 * 60 * 1000 // 5 minutes
    };

    var svc = {
      get active() {
        var event = configService.config.productMaintenance;
        if (event && event !== _event) {
          event.startTime = new Date(event.startTime);
          _event = event;
        }
        return event;
      },
      canAccess: function canAccess() {
        var active = svc.active;
        return !active || svc.isStaff() || Date.now() < active.startTime.getTime();
      },
      checkAccess: function checkAccess() {
        return precheck(svc.canAccess);
      },

      // if assessment is passed in, we'll use the estimated time for candidate lockouts
      canLogin: function canLogin(assessment) {
        var active = svc.active;
        if (active) {
          var startTime = active.startTime.getTime();
          var minute = 60000;
          var now = Date.now();
          if (accountService.modelName === 'TeamMember' && active.preventTeamLoginMinutes) {
            return now < startTime - minute * active.preventTeamLoginMinutes;
          } else if (accountService.modelName === 'Candidate' && active.preventCandidateLogin) {
            // always lock candidates from starting at least 60 minutes before a lockout
            var offset = Math.max(60, assessment && assessment.estimatedTime() || 0);
            return now < startTime - minute * offset;
          }
        }
        return true;
      },
      checkLogin: function checkLogin(assessment) {
        return precheck(function () {
          return svc.canLogin(assessment);
        });
      },
      isStaff: function isStaff() {
        var account = accountService.get();
        return !!(account.user && account.user.hasRole && account.user.hasRole('staff'));
      },
      warning: function warning() {
        var active = svc.active;
        if (active) {
          // set up a timer for alerts if there's an active event
          var now = Date.now();
          var then = active.startTime.getTime();
          var time = $filter('date')(active.startTime, 'shortTime');
          if (then - WARNING_TIMES.DANGER2 < now) {
            // 5 minutes before lockout show danger again
            return {
              mode: 'danger',
              message: 'WARNING: System maintenance is starting at ' + time
            };
          } else if (then - WARNING_TIMES.DANGER < now) {
            // 15 minutes before lockout show danger
            return {
              mode: 'danger',
              message: 'WARNING: System maintenance will begin soon, at ' + time
            };
          } else if (then - WARNING_TIMES.WARNING < now) {
            // 1 hour before lockout show a warning
            return {
              mode: 'warning',
              message: 'System maintenance will begin at ' + time
            };
          } else if (then - WARNING_TIMES.INFO < now) {
            // 4 hours before lockout, show info
            return {
              mode: 'info',
              message: 'System maintenance is scheduled for ' + time
            };
          }
        }
      },
      init: function init() {
        svc.checkAccess().then(function () {
          var active = svc.active;
          if (active) {
            // set up timers for alerts if there's an active event
            var now = Date.now();
            var then = active.startTime.getTime();
            // 4 hours before lockout, show info
            _showBannerIn(then - WARNING_TIMES.INFO - now);
            // 1 hour before lockout show a warning
            _showBannerIn(then - WARNING_TIMES.WARNING - now);
            // 15 minutes before lockout show danger
            _showBannerIn(then - WARNING_TIMES.DANGER - now);
            // 5 minutes before lockout show danger again
            _showBannerIn(then - WARNING_TIMES.DANGER2 - now);
          }
        }).catch(function () {
          return svc.goToError();
        });
      },
      errorState: function errorState() {
        return ['error', { maintenanceEvent: svc.active }];
      },
      goToError: function goToError() {
        return $state.go.apply($state, toConsumableArray(svc.errorState()));
      },
      showDetails: function showDetails() {
        var active = svc.active;
        if (!active) return;
        var startTime = $filter('date')(active.startTime, 'medium');
        dialogService.alert('<h3>' + (active.reason || 'The system is undergoing scheduled maintenance') + '</h3>' + active.description + '<p>Work is scheduled to start at <strong>' + startTime + '</strong>, and is expected to take <strong>' + active.estimatedDowntimeMinutes + ' minutes</strong>.</p>');
      }
    };

    function _showBannerIn(delay) {
      var showBanner = function showBanner() {
        var displayService = void 0;
        if ($injector.has('productMessageBannerService')) {
          displayService = $injector.get('productMessageBannerService');
        } else if ($injector.has('appAlert')) {
          displayService = $injector.get('appAlert');
        }
        var warningText = svc.warning();
        if (displayService && warningText) {
          var mode = warningText.mode,
              message = warningText.message;

          var props = {
            linkAction: svc.showDetails,
            linkTitle: 'Learn More'
          };
          if (mode === 'danger') {
            props.closeable = props.showFor = false;
          }
          displayService['show' + ucFirst(mode)](message, props);
        }
      };
      if (delay > 0) {
        timerHelper.longTimeout(showBanner, delay);
      } else {
        showBanner();
      }
    }

    return svc;
  }]);

  /* eslint-disable angular/no-private-call */

  var permissionsServiceModule = angular.module('Q.services.permissions-service', [uiRouterStateModule, accountServiceModule]).factory('permissionsService', ['accountService', '$q', function (accountService, $q) {
    var AUTHORIZE_KEYS = ['loggedIn', 'expired', 'role', 'privilege', 'staff', 'feature', 'featureLevels', 'productType', 'userProperty', 'devOnly', 'when', 'systemTeam', 'templateTeam', 'staffContent', 'or'];

    return {
      canAuthorize: canAuthorize,
      isLoggedIn: isLoggedIn,
      role: role,
      privilege: privilege,
      feature: feature,
      authorize: authorize,
      authorizeAny: authorizeAny,
      authorizeAsync: authorizeAsync,
      // special case which hard-checks the current user's
      isStaff: function isStaff() {
        var account = accountService.get();
        var user = account.user;
        return user && user.role === 'staff';
      }
    };

    /**
     * Returns false if permissions cannot be determined synchronously due to the app not being
     * fully loaded.
     * @returns {boolean}
     */
    function canAuthorize() {
      return !!accountService.isReady;
    }

    /**
     * Returns the authorization via a promise.
     *
     * Note this result is guaranteed to pass or fail as it will wait until the app is initialized.
     *
     * @param {object} properties
     * @returns {Promise} Promise resolved if the authorization succeeds, or rejected if it fails.
     */
    function authorizeAsync(properties) {
      return accountService.whenReady().then(function () {
        return authorize(properties) ? $q.when(true) : $q.reject(false);
      });
    }

    function _notPermitted(prop, value) {
      // only returns true if prop is a boolean and the opposite of value's truthiness
      return prop === !value;
    }

    /**
     * Determines if the current account is authorized
     * @param {boolean?} options.loggedIn Pass true or false to only allow logged in or not logged in users, respectively.  Boolean true or false is _required_!
     * @param {boolean?} options.expired Pass true or false to only allow expired or non-expired teams, respectively.  Boolean true or false is _required_!
     * @param {string?} options.role A string to validate the currently logged in user against a role.  If there is no logged in user, the authorization fails.
     * @param {string?} options.privilege A string to validate the currently logged in user against a privilege.  If there is no logged in user, the authorization fails.
     * @param {boolean?} options.staff A special role that can be used to filter out staff members when set to false.
     * @param {string?} options.feature A string to validate the currently logged in user against a feature flag.  If there is no logged in user, the authorization fails.
     * @param {string|Array<string>?} options.featureLevels specific feature levels for complex features
     * @param {string?} options.productType If the current user's team has the given product type
     * @param {object?} options.userProperty Pass in a set of key-value pairs to be matched _exactly_ against the user object
     * @param {boolean?} options.devOnly If truthy, only authorize when in the development environment.
     * @param {function?} options.when If provided, tests that the return of this function is truthy.
     * @param {boolean?} options.systemTeam Pass true or false to only allow system or non-system teams, respectively.  Boolean true or false is _required_!
     * @param {boolean?} options.templateTeam Pass true or false to only allow the template or other teams, respectively.  Boolean true or false is _required_!
     * @param {boolean?} options.staffContent Pass true or false to only allow the template/system/staff.  Boolean true is _required_!
     * @param {object?} options.or Provide a set of authorization keys, returns true if any of them are true
     * @returns {boolean} True if authorized, or false if unauthorized or authorization fails.
     */
    function authorize(options) {
      var _ref = options || {},
          _ref$loggedIn = _ref.loggedIn,
          loggedIn = _ref$loggedIn === undefined ? null : _ref$loggedIn,
          _ref$expired = _ref.expired,
          expired = _ref$expired === undefined ? null : _ref$expired,
          _ref$role = _ref.role,
          role = _ref$role === undefined ? null : _ref$role,
          _ref$privilege = _ref.privilege,
          privilege = _ref$privilege === undefined ? null : _ref$privilege,
          _ref$staff = _ref.staff,
          staff = _ref$staff === undefined ? null : _ref$staff,
          _ref$feature = _ref.feature,
          feature = _ref$feature === undefined ? null : _ref$feature,
          _ref$featureLevels = _ref.featureLevels,
          featureLevels = _ref$featureLevels === undefined ? null : _ref$featureLevels,
          _ref$productType = _ref.productType,
          productType = _ref$productType === undefined ? null : _ref$productType,
          _ref$userProperty = _ref.userProperty,
          userProperty = _ref$userProperty === undefined ? null : _ref$userProperty,
          _ref$devOnly = _ref.devOnly,
          devOnly = _ref$devOnly === undefined ? false : _ref$devOnly,
          _ref$when = _ref.when,
          when = _ref$when === undefined ? null : _ref$when,
          _ref$systemTeam = _ref.systemTeam,
          systemTeam = _ref$systemTeam === undefined ? null : _ref$systemTeam,
          _ref$templateTeam = _ref.templateTeam,
          templateTeam = _ref$templateTeam === undefined ? null : _ref$templateTeam,
          _ref$staffContent = _ref.staffContent,
          staffContent = _ref$staffContent === undefined ? null : _ref$staffContent,
          _ref$or = _ref.or,
          or = _ref$or === undefined ? null : _ref$or;

      _validateAuthorizationOptions(options);

      // We cannot authorize before accountService has logged in
      if (!canAuthorize()) return false;

      var account = accountService.get();
      var user = account.user;
      var team = user && user.team;

      if (_notPermitted(loggedIn, account.verified)) return false;

      // ignore expiration rules for staff or non-logged-in
      if (user && !user.hasRole('staff')) {
        if (_notPermitted(expired, !team || team.subscriptionExpired())) return false;
      }

      if (role && (!user || !user.hasRole(role))) return false;

      if (privilege && (!user || !user.hasPrivilege(privilege))) return false;

      if (staff !== null && (!user || user.hasRole('staff') !== staff)) return false;

      if (feature && (!user || !user.hasFeature(feature, featureLevels))) return false;

      if (productType && (!team || !team.productTypes || !team.productTypes[productType])) return false;

      if (userProperty && (!user || Object.keys(userProperty).some(function (k) {
        return _getProperty(userProperty, k) !== _getProperty(user, k);
      }))) return false;

      if (devOnly && !appConfig.devMode) return false;

      if (when && angular.isFunction(when)) return when(account, user);

      if (_notPermitted(systemTeam, team && team.isSystemTeam())) return false;
      if (_notPermitted(templateTeam, team && team.isTemplateTeam)) return false;

      if (_notPermitted(staffContent, team && user && (team.isSystemTeam() || team.isTemplateTeam || user.hasRole('staff')))) return false;

      if (or !== null && !authorizeAny(or)) return false;

      return true;
    }

    /**
     * returns true if any option is true via authorize
     * @param options
     */
    function authorizeAny(options) {
      return Object.keys(options).some(function (k) {
        return authorize(defineProperty({}, k, options[k]));
      });
    }

    function isLoggedIn() {
      return authorize({ loggedIn: true });
    }

    /**
     * Convenience method for permissionsService.authorize({ role: roleName });
     * @param {string} roleName
     * @returns {boolean}
     */
    function role(roleName) {
      return authorize({ role: roleName });
    }

    /**
     * Convenience method for permissionsService.authorize({ privilege: privilegeName });
     * @param {string} privilegeName
     * @return {boolean}
     */
    function privilege(privilegeName) {
      return authorize({ privilege: privilegeName });
    }

    /**
     * Convenience method for permissionsService.authorize({ feature: featureName });
     * @param {string} featureName
     * @returns {boolean}
     */
    function feature(featureName) {
      return authorize({ feature: featureName });
    }

    //// private functions

    function _getProperty(obj, k) {
      return angular.isFunction(obj[k]) ? obj[k]() : obj[k];
    }

    function _validateAuthorizationOptions(options) {
      if (AUTHORIZE_KEYS.every(function (k) {
        return options[k] === undefined || options[k] === null;
      })) {
        throw new Error('No Valid Permission Checks Requested: ' + JSON.stringify(options));
      }
    }
  }]).run(['$rootScope', 'permissionsService', '$state', function ($rootScope, permissionsService, $state) {
    // Enable using permissions service within any scope via $root.permit(...)
    $rootScope.permitted = permissionsService.authorize;
    $rootScope.anyPermitted = permissionsService.authorizeAny;
    $rootScope.notPermitted = function (arg) {
      return !permissionsService.authorize(arg);
    };
    $rootScope.isStaff = permissionsService.isStaff;

    // Handle
    $rootScope.$on('$stateChangeStart', function (event, toState, toParams, fromState, fromParams, options) {
      if (toParams.$$finishAuthorize) {
        delete toParams.$$finishAuthorize;
        return;
      }

      if (event.defaultPrevented) return;

      // If there are permissions set then prevent default and attempt to authorize
      var permissions = void 0;
      if (toState.data && toState.data.permissions) {
        permissions = toState.data.permissions;
      }

      if (permissions) {
        if (permissionsService.canAuthorize()) {
          if (!permissionsService.authorize(permissions)) {
            event.preventDefault();
            handleUnauthorizedRedirect();
          } else {
            $rootScope.$broadcast('$stateChangePermissionAccepted', toState, toParams);
          }
        } else {
          event.preventDefault();

          permissionsService.authorizeAsync(permissions).then(function () {
            $rootScope.$broadcast('$stateChangePermissionAccepted', toState, toParams);
            toParams.$$finishAuthorize = true;
            $state.go(toState.name, toParams, options);
          }).catch(handleUnauthorizedRedirect);
        }
      }

      function handleUnauthorizedRedirect() {
        $rootScope.$broadcast('$stateChangePermissionDenied', toState, toParams);
        // If not authorized, redirect to wherever the route has defined, if defined at all
        var redirectTo = permissions.redirectTo;
        if (redirectTo) {
          $state.go(redirectTo, toParams, { location: 'replace' });
        }
      }
    });
  }]);

  var activateTemplate = "<div class=\"inline-modal login-content\" ng-if=\"!activate.user\"> <div class=\"modal-content\"> <div class=\"modal-header\"> <span class=\"qualified-logo-full-color\"><span>Qualified</span></span> </div> <div class=\"modal-body status-message\"> <h3 class=\"status-failed\">Invalid Token</h3> <p> We were unable to find an account for that token. It's possible the account has already been activated. </p> </div> <div class=\"modal-footer\"> <a class=\"btn-primary\" ui-sref=\"{{ ::appConfig.defaultRoute }}\">Return to Log In</a> </div> </div> </div> <div class=\"inline-modal login-content\" ng-if=\"activate.user\"> <div class=\"modal-content\"> <div class=\"modal-header\"> <span class=\"qualified-logo-full-color\"><span>Qualified</span></span> <h3>Account Activation</h3> </div> <div class=\"modal-body\"> <signup-or-login user=\"activate.user\" next-button-label=\"Activate\" activate-mode=\"true\" activate-token=\"activate.token\" disable-login=\"true\" enable-remember-me=\"true\" on-activate=\"activate.finish(promise)\" show-newsletter=\"true\" show-terms=\"true\"></signup-or-login> </div> </div> </div> <div class=\"inline-modal-footer\"> <legal-links></legal-links> </div> ";

  var legalLinksTemplate = "<ul> <li><a href=\"/\">&copy; {{ ::appConfig.siteCopyright }} Qualified</a></li> <li><a href=\"/kb/legal/terms\">Terms</a></li> <li><a href=\"/kb/legal/privacy-policy\">Privacy</a></li> <li><a href=\"/about\">About</a></li> <li ng-if=\"!$ctrl.hideContact\"> <a href=\"mailto::team@qualified.io\" target=\"_blank\">Contact</a> </li> </ul> ";

  var legalLinksComponentModule = angular.module('Q.components.legal-links', []).component('legalLinks', {
    bindings: {
      hideContact: '<'
    },
    template: legalLinksTemplate
  });

  var checkEmailServiceModule = angular.module('Q.services.check-email-service', []).factory('checkEmailService', ['$http', function ($http) {
    return {
      check: function check(email, type) {
        return $http.post(appConfig.apiRoot + 'account/precheck', {
          data: {
            $type: type,
            email: email
          }
        }, {
          ignoreLoadingBar: true
        }).then(function (res) {
          var data = res.data;
          return data && data.data || {};
        }).catch(function () {
          return {};
        });
      }
    };
  }]);

  /* eslint-disable angular/no-private-call */

  var compileTemplateComponentModule = angular.module('Q.components.compile-template', [])

  /**
   * @docType shared
   * @category directive
   *
   * @ngdoc directive
   *
   * @name compileTemplate
   * @restrict EA
   * @scope
   * @priority 400
   *
   * @description
   * Compiles and injects a template, similar to ng-include, but works with templates stored in strings, rather than
   * asynchronous template loading.
   *
   * Note: Unlike ng-include, this directive is always synchronous, and therefore doesn't have an onload attribute,
   * doesn't autoscroll, and doesn't broadcast any events.
   *
   * @param {expression} compileTemplate|src Angular expression returning the template to compile and inject
   * @param {expression} scope Override scope source
   * @param {expression} scopeVars Add custom vars to the new scope
   *
   */
  .directive('compileTemplate', ['$compile', '$animate', function ($compile, $animate) {
    return {
      restrict: 'EA',
      priority: 400,
      terminal: true,
      transclude: 'element',
      controller: angular.noop,
      compile: function compile(tElement, tAttr) {
        var srcExp = tAttr.compileTemplate || tAttr.src;

        return function (scope, el, attr, ctrl, $transclude) {
          var currentScope = void 0;
          var previousElement = void 0;
          var currentElement = void 0;

          var cleanupCurrentContent = function cleanupCurrentContent() {
            if (previousElement) {
              // in case we somehow get new content while the old content is still animating away
              previousElement.remove();
              previousElement = null;
            }
            if (currentScope) {
              currentScope.$destroy();
              currentScope = null;
            }
            if (currentElement) {
              $animate.leave(currentElement).done(function (response) {
                if (response !== false) previousElement = null;
              });
              previousElement = currentElement;
              currentElement = null;
            }
          };

          scope.$watch(srcExp, function compileTemplateWatchAction(template) {
            cleanupCurrentContent();

            // only render if the template is truthy, or any string (including '')
            if (template || angular.isString(template)) {
              if (scope.$$destroyed) return;
              ctrl.template = template;
              currentScope = (tAttr.scope ? scope.$eval(tAttr.scope) : scope).$new();
              if (tAttr.scopeVars) {
                angular.extend(currentScope, scope.$eval(tAttr.scopeVars));
              }
              currentElement = $transclude(currentScope, function (clone) {
                $animate.enter(clone, null, el);
              });
            }
          });
        };
      }
    };
  }])

  // This directive is called during the $transclude call of the first `ngInclude` directive.
  // It will replace and compile the content of the element with the loaded template.
  // We need this directive so that the element content is already filled when
  // the link function of another directive on the same element as ngInclude
  // is called.
  .directive('compileTemplate', ['$compile', function ($compile) {
    return {
      restrict: 'ECA',
      priority: -400,
      require: 'compileTemplate',
      link: function link(scope, $element, $attr, ctrl) {
        // Copied from ngInclude, but we don't need it right now
        // if(toString.call($element[0]).match(/SVG/)) {
        // 	// WebKit: https://bugs.webkit.org/show_bug.cgi?id=135698 --- SVG elements do not
        // 	// support innerHTML, so detect this here and try to generate the contents
        // 	// specially.
        // 	$element.empty();
        // 	$compile(jqLiteBuildFragment(ctrl.template, window.document).childNodes)(scope,
        // 		function namespaceAdaptedClone(clone) {
        // 			$element.append(clone);
        // 		}, { futureParentElement: $element });
        // 	return;
        // }

        $element.html(ctrl.template);
        $compile($element.contents())(scope);
      }
    };
  }]);

  var FORM_GROUP_MESSAGES = [];

  function addFormGroupMessage(key, message) {
    FORM_GROUP_MESSAGES.push({
      key: key,
      message: message
    });
  }

  var formGroupMessagesComponentModule = angular.module('Q.components.forms.form-group-messages', [compileTemplateComponentModule]).factory('FORM_GROUP_MESSAGES', function () {
    FORM_GROUP_MESSAGES.add = addFormGroupMessage;
    addFormGroupMessage('required', function ($input, formGroupField) {
      return 'Please provide ' + formGroupField.name + '.';
    });
    addFormGroupMessage('match', 'This password doesn\'t match the first one.');
    addFormGroupMessage('minLength', function ($input, formGroupField) {
      if (formGroupField.minAndMaxLength) {
        return formGroupField.nameCapitalized + ' must be between ' + formGroupField.minlength + ' and ' + formGroupField.maxlength + ' characters long.';
      } else if (formGroupField.minlength) {
        return 'Please enter at least ' + formGroupField.minlength + ' character' + (formGroupField.minlength === 1 ? '' : 's') + '.';
      } else {
        return formGroupField.nameCapitalized + ' is too short.';
      }
    });
    addFormGroupMessage('maxLength', function ($input, formGroupField) {
      if (formGroupField.minAndMaxLength) {
        return formGroupField.nameCapitalized + ' must be between ' + formGroupField.minlength + ' and ' + formGroupField.maxlength + ' characters long.';
      } else if (formGroupField.maxlength) {
        return 'Please enter no more than ' + formGroupField.maxlength + ' character' + (formGroupField.maxlength === 1 ? '' : 's') + '.';
      } else {
        return formGroupField.nameCapitalized + ' is too long.';
      }
    });
    addFormGroupMessage('min', function ($input, formGroupField) {
      if (formGroupField.minAndMax) {
        return formGroupField.nameCapitalized + ' must be between ' + formGroupField.min + ' and ' + formGroupField.max + ' (inclusive).';
      } else if (formGroupField.min) {
        return formGroupField.nameCapitalized + ' must be ' + formGroupField.min + ' or greater.';
      } else {
        return formGroupField.nameCapitalized + ' is too small.';
      }
    });
    addFormGroupMessage('max', function ($input, formGroupField) {
      if (formGroupField.minAndMax) {
        return formGroupField.nameCapitalized + ' must be between ' + formGroupField.min + ' and ' + formGroupField.max + ' (inclusive).';
      } else if (formGroupField.max) {
        return formGroupField.nameCapitalized + ' must be ' + formGroupField.max + ' or smaller.';
      } else {
        return formGroupField.nameCapitalized + ' is too big.';
      }
    });
    addFormGroupMessage('minTags', function ($input, formGroupField) {
      if (formGroupField.minAndMaxTags) {
        return formGroupField.nameCapitalized + ' must have between ' + formGroupField.minTags + ' and ' + formGroupField.maxTags + ' items (inclusive).';
      } else {
        return formGroupField.nameCapitalized + ' must have at least ' + formGroupField.minTags + ' item' + (formGroupField.minTags === 1 ? '' : 's') + '.';
      }
    });
    addFormGroupMessage('maxTags', function ($input, formGroupField) {
      if (formGroupField.minAndMaxTags) {
        return formGroupField.nameCapitalized + ' must have between ' + formGroupField.minTags + ' and ' + formGroupField.maxTags + ' items (inclusive).';
      } else {
        return formGroupField.nameCapitalized + ' must have no more than ' + formGroupField.maxTags + ' item' + (formGroupField.maxTags === 1 ? '' : 's') + '.';
      }
    });
    addFormGroupMessage('url', 'Please provide a valid website URL.');
    addFormGroupMessage('email', 'Please provide a valid email address.');
    addFormGroupMessage('date', 'Please provide a valid date.');
    addFormGroupMessage('number', 'Please provide a valid number.');
    addFormGroupMessage('loginIncorrect', 'Incorrect password or username.');
    addFormGroupMessage('accountLocked', 'This account is no longer active.');

    return FORM_GROUP_MESSAGES;
  }).directive('formGroupMessages', ['$animate', '$timeout', 'FORM_GROUP_MESSAGES', function ($animate, $timeout, FORM_GROUP_MESSAGES) {
    var SHOULD_SHOW_CLASS = 'msg-shouldshow';
    var SHOULD_HIDE_CLASS = 'msg-shouldhide';

    return {
      restrict: 'E',
      require: ['^^form', '^^formGroup'],
      scope: true,
      compile: function compile(tElement) {
        var customMessages = tElement.html().trim();
        tElement.html('<compile-template src="messageContent"></compile-template>');

        return function postLink(scope, elem, attrs, _ref) {
          var _ref2 = slicedToArray(_ref, 2),
              ngFormCtrl = _ref2[0],
              formGroupCtrl = _ref2[1];

          elem.addClass('help-block');

          formGroupCtrl.configureMessages = function ($inputs) {
            var input = $inputs && $inputs[0];
            var $input = scope.$input = input && angular.element(input);
            var formGroupCtrlAttrs = formGroupCtrl.attrs || {};

            if (!$input) return;

            // --- configuring field properties ---

            var min = $input.attr('min');
            var max = $input.attr('max');
            var minLength = $input.attr('minlength');
            var ngMinLength = $input.attr('ng-minlength');
            var maxLength = $input.attr('maxlength');
            var ngMaxLength = $input.attr('ng-maxlength');
            var minTags = $input.attr('min-tags');
            var maxTags = $input.attr('max-tags');

            var formGroupField = formGroupCtrl.formGroupField = scope.formGroupField = {
              name: formGroupCtrlAttrs.fieldName || formGroupCtrlAttrs.label || 'a value',
              nameCapitalized: formGroupCtrlAttrs.fieldNameCapitalized || ucFirst(formGroupCtrlAttrs.fieldName || formGroupCtrlAttrs.label || 'the value'),
              min: min,
              max: max,
              minAndMax: min && max,
              minlength: minLength || ngMinLength && scope.$eval(ngMinLength),
              maxlength: maxLength || ngMaxLength && scope.$eval(ngMaxLength),
              minTags: minTags && scope.$eval(minTags),
              maxTags: maxTags && scope.$eval(maxTags)
            };
            formGroupField.minAndMaxLength = formGroupField.minlength && formGroupField.maxlength;
            formGroupField.minAndMaxTags = formGroupField.minTags && formGroupField.maxTags;

            var getFormField = function getFormField() {
              return ngFormCtrl[$input.attr('name')];
            };
            var getFormFieldErrors = function getFormFieldErrors() {
              var field = getFormField();
              return field && field.$error || {};
            };

            // --- setting the message ---

            var lastErrorClass = '';

            function updateMessage() {
              elem.removeClass(lastErrorClass);
              var hadError = false;
              var errors = getFormFieldErrors();
              if (Object.keys(errors).length) {
                var msgs = FORM_GROUP_MESSAGES;
                if (customMessages) {
                  var _msgs;

                  msgs = [];
                  angular.forEach(scope.$eval(customMessages), function (message, key) {
                    msgs.push({ key: key, message: message });
                  });
                  (_msgs = msgs).push.apply(_msgs, toConsumableArray(FORM_GROUP_MESSAGES));
                }
                scope.messageContent = '';
                hadError = msgs.some(function (_ref3) {
                  var key = _ref3.key,
                      message = _ref3.message;

                  if (errors[key]) {
                    lastErrorClass = key;
                    elem.addClass(lastErrorClass);
                    scope.messageContent = angular.isFunction(message) ? message($input, formGroupField) : message;
                    return true;
                  }
                });
              }
              elem.toggleClass('ng-active', hadError);
              elem.toggleClass('ng-inactive', !hadError);
            }

            scope.$watch(function () {
              return Object.keys(getFormFieldErrors()).join();
            }, updateMessage);

            // --- hiding if the form wasn't submitted ---

            elem.addClass(SHOULD_HIDE_CLASS);
            scope.$watch(function () {
              var field = getFormField();
              // leftoverText is because tagsInput is marking the field as pristine if we error on the first item
              return ngFormCtrl && isFormSubmitted(ngFormCtrl) || field && (!field.$pristine || field.$error.leftoverText) && field.$touched;
            }, function (shouldShow) {
              $animate.setClass(elem, shouldShow ? SHOULD_SHOW_CLASS : SHOULD_HIDE_CLASS, shouldShow ? SHOULD_HIDE_CLASS : SHOULD_SHOW_CLASS);
            });
          };
        };
      }
    };
  }]);

  function isFormSubmitted(form) {
    /* eslint-disable angular/no-private-call */
    while (!form.$submitted && form.$$parentForm && form.$$parentForm.$name) {
      form = form.$$parentForm;
    }
    /* eslint-enable */
    return form.$submitted;
  }

  var uniqueEmailValidatorComponentModule = angular.module('Q.components.forms.unique-email-validator', [checkEmailServiceModule, formGroupMessagesComponentModule]).directive('uniqueEmail', ['checkEmailService', '$q', '$timeout', 'FORM_GROUP_MESSAGES', function (checkEmailService, $q, $timeout, FORM_GROUP_MESSAGES) {
    var DEBOUNCE_RATE = 1000;

    FORM_GROUP_MESSAGES.add('uniqueEmail', 'That email has already been used to sign up.');

    return {
      require: 'ngModel',
      link: function link(scope, el, attrs, ngModel) {
        // tracks whether we have a delayed check in progress
        var activeTimer = null;
        // tracks number of active checks heading to the server
        var activeRequests = 0;

        ngModel.$asyncValidators.uniqueEmail = function (modelValue) {
          // no value
          if (!modelValue) return $q.when(true);

          // disabled
          if (attrs.uniqueEmail && !scope.$eval(attrs.uniqueEmail)) return $q.when(true);

          // We just cancel any timer still running
          if (activeTimer) {
            $timeout.cancel(activeTimer);
          }

          // if we already have an active request, debounce!
          if (activeRequests) {
            // NOTE: Do NOT combine these statements.
            // We can only cancel a timer if it's the original promise returned from $timeout!
            activeTimer = $timeout(angular.noop, DEBOUNCE_RATE);

            return activeTimer.then(function () {
              return activeTimer = null;
            }).then(check);
          } else {
            return check();
          }

          function check() {
            activeRequests++;
            return checkEmailService.check(modelValue, attrs.uniqueEmailModel).then(function (data) {
              activeRequests--;
              if (!data.exists) {
                ngModel.flagInvalid = !!data.invalid;
                ngModel.flagJunk = !!data.junk;
                ngModel.flagGeneric = !!(data.generic || data.junk);
                return true;
              } else {
                return $q.reject();
              }
            });
          }
        };
      }
    };
  }]);

  var serverValidationErrorsTemplate = "<div class=\"server-validation-errors alert alert-danger\" ng-show=\"serverErrors.length || serverErrorsMessage\"> <p> {{ serverErrorsMessage || 'We were unable to save your information due to\n    the following errors:' }} </p> <ul ng-if=\"serverErrors.length\"> <li ng-repeat=\"err in serverErrors\"> <strong>{{ err.field }}</strong> {{ err.message }} </li> </ul> </div> ";

  var ERRORS_CHANGED_EVENT = 'serverValidationService.errorsChanged';

  var serverValidationErrorsComponentModule = angular.module('Q.components.forms.server-validation-errors', [formGroupMessagesComponentModule]).factory('serverValidationService', ['$rootScope', '$q', function ($rootScope, $q) {
    var _msg = null;
    var _errors = void 0;

    var svc = {
      getErrorHandler: getErrorHandler,
      setErrors: function setErrors(errors) {
        var msg = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

        if (angular.isString(errors)) {
          _errors = [];
          _msg = errors;
        } else {
          _errors = errors;
          _msg = msg;
        }
        $rootScope.$broadcast(ERRORS_CHANGED_EVENT, _errors, _msg);
      },
      clearErrors: function clearErrors(resp) {
        _msg = null;
        _errors = null;
        $rootScope.$broadcast(ERRORS_CHANGED_EVENT, _errors, _msg);
        return $q.when(resp);
      },
      getErrors: function getErrors() {
        return _errors;
      },
      getMessage: function getMessage() {
        return _msg;
      }
    };

    return svc;

    /**
     * Returns an HTTP response (catch) handler.  Note: this is not to intended to be used on $http().error(),
     * use it on `.catch(err)` or `.then(..., err)`.
     *
     * If provided a status code, or an array of status codes, or regular expressions that can test status
     * codes, these codes will be ignored and passed on unmodified.
     *
     * @param ignoredCodes one or an array of 40x status codes to ignore
     * @returns {Function}
     */
    function getErrorHandler() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref$ignoredCodes = _ref.ignoredCodes,
          ignoredCodes = _ref$ignoredCodes === undefined ? [] : _ref$ignoredCodes;

      if (!Array.isArray(ignoredCodes)) {
        ignoredCodes = [ignoredCodes];
      }
      return function (resp) {
        svc.clearErrors();
        if (resp && resp.status === 0) {
          // handle server communication errors (network down, unreachable, etc)
          svc.setErrors('There was a communication problem with the server.');
        } else if (resp && resp.status >= 500 && resp.status < 600) {
          // unrecoverable server errors
          svc.setErrors({ '': resp.statusText }, 'There was an error on the server we could not continue from.');
        } else if (resp && resp.status >= 400 && resp.status < 500) {
          // client errors
          if (resp.data && !ignoredCodes.some(function (code) {
            return resp.status === code || code.test && code.test(resp.status);
          })) {
            if (resp.data.errors) {
              svc.setErrors(resp.data.errors);
            } else if (resp.data.reason) {
              svc.setErrors(resp.data.reason);
            }
          }
        }

        return $q.reject(_errors ? null : resp);
      };
    }
  }])

  /**
   * Directive to render server validation errors on every model on the form.
   */
  .directive('serverValidationErrors', ['FORM_GROUP_MESSAGES', function (FORM_GROUP_MESSAGES) {
    FORM_GROUP_MESSAGES.add('serverValidation', function ($input, formGroupField) {
      return formGroupField.nameCapitalized + ' ' + (formGroupField.serverValidationMessage || 'has an unknown server validation error');
    });

    return {
      restrict: 'AE',
      require: '^form',
      template: serverValidationErrorsTemplate,
      scope: true,
      controller: function controller() {},
      link: function link(scope, elem, attrs, ngFormCtrl) {
        var activeErrors = [];
        var base = attrs.serverValidationObject;

        scope.$on(ERRORS_CHANGED_EVENT, function ($event, errs, msg) {
          scope.serverErrorsMessage = msg;
          scope.serverErrors = [];

          angular.forEach(activeErrors, function (key) {
            ngFormCtrl[key].$setValidity('serverValidation', true);
          });
          activeErrors = [];

          if (errs) {
            ngFormCtrl.$setSubmitted();
            angular.forEach(errs, function (err, key) {
              if (ngFormCtrl.serverValidationMap && ngFormCtrl.serverValidationMap[key]) {
                key = ngFormCtrl.serverValidationMap[key];
              }

              var field = ngFormCtrl[key],
                  baseKey = base + '.' + key;

              if (base && ngFormCtrl[baseKey]) {
                key = baseKey;
                field = ngFormCtrl[baseKey];
              }

              if (angular.isArray(err)) {
                err = err.join(', ');
              }

              if (field) {
                // whenever possible, render errors at their point of origin
                ngFormCtrl[key].$setValidity('serverValidation', false);
                if (ngFormCtrl[key].formGroupCtrl && ngFormCtrl[key].formGroupCtrl.formGroupField) {
                  ngFormCtrl[key].formGroupCtrl.formGroupField.serverValidationMessage = err;
                }
                activeErrors.push(key);
              } else {
                // otherwise, render them in a generic list
                scope.serverErrors.push({
                  field: upperCaseWords(fieldToLabel(key)),
                  message: err
                });
              }
              if (ngFormCtrl.scrollToError) {
                ngFormCtrl.scrollToError();
              }
            });
          }
        });
      }
    };
  }])

  // clears any server validation errors if the field is updated
  // (otherwise the form might not be able to submit)
  .directive('ngModel', function () {
    return {
      restrict: 'A',
      require: ['ngModel', '?^form', '?^^formGroup'],
      link: function link(scope, elem, attrs, _ref2) {
        var _ref3 = slicedToArray(_ref2, 3),
            ngModelCtrl = _ref3[0],
            ngFormCtrl = _ref3[1],
            formGroupCtrl = _ref3[2];

        if (formGroupCtrl) {
          ngModelCtrl.formGroupCtrl = formGroupCtrl;

          var last = null;

          if (ngFormCtrl && attrs.serverValidationKey) {
            if (!ngFormCtrl.serverValidationMap) {
              ngFormCtrl.serverValidationMap = {};
            }
            ngFormCtrl.serverValidationMap[attrs.serverValidationKey] = attrs.name;
          }

          ngModelCtrl.$parsers.push(function (value) {
            if (value !== last) {
              ngModelCtrl.$setValidity('serverValidation', true);
              last = value;
            }
            return value;
          });
        }
      }
    };
  });

  /* eslint-disable angular/module-getter, angular/module-setter */

  /**
   * @ngdoc directive
   * @name trackClick
   * @description
   * Tracks a click on an element
   *
   * @restrict A
   * @element ANY
   * @param {template} trackClick String to use as the tracked event name, not interpolated
   * @param {expression=} trackData Hash of key-values to store with the track
   * @param {string} track Allows using the same event name across multiple event types on one element
   *
   * @example
   * <example>
   * <a track-click="event.name" track-data="{ properties }"></a>
   * </example>
   */

  /**
   * @ngdoc directive
   * @name trackChange
   * @description
   * Tracks a change event on an element
   *
   * @restrict A
   * @element ANY
   * @param {template} trackChange String to use as the tracked event name, not interpolated
   * @param {expression=} trackData Hash of key-values to store with the track
   * @param {string} track Allows using the same event name across multiple event types on one element
   *
   * @example
   * <example>
   * <input track-change="event.name" track-data="{ properties }">
   * </example>
   */

  /**
   * @ngdoc directive
   * @name trackFocus
   * @description
   * Tracks focusing on an element
   *
   * @restrict A
   * @element ANY
   * @param {template} trackFocus String to use as the tracked event name, not interpolated
   * @param {expression=} trackData Hash of key-values to store with the track
   * @param {string} track Allows using the same event name across multiple event types on one element
   *
   * @example
   * <example>
   * <input track-focus="event.name" track-data="{ properties }">
   * </example>
   */

  /**
   * @ngdoc directive
   * @name trackSubmit
   * @description
   * Tracks a form submission
   *
   * @restrict A
   * @element ANY
   * @param {template} trackSubmit String to use as the tracked event name, not interpolated
   * @param {expression=} trackData Hash of key-values to store with the track
   * @param {string} track Allows using the same event name across multiple event types on one element
   *
   * @example
   * <example>
   * <form track-submit="event.name" track-data="{ properties }"></form>
   * </example>
   */

  var trackComponentModule = angular.module('Q.components.track', [trackerServiceModule]);

  ['uiSref', 'ngClick'].forEach(function createLinkDirective(directiveName) {
    // setup the ui-sref directive to store the track-data on the tracker service so that it will know to
    // include those props on the next state change event
    trackComponentModule.directive(directiveName,
    /* @ngInject */['tracker', function (tracker) {
      return {
        restrict: 'A',
        link: function link(scope, elem, attrs) {
          elem.bind('click', function () {
            tracker.srefProps = setProps(scope, elem, attrs, 'click');
          });
        }
      };
    }]);
  });

  ['click', 'change', 'focus', 'submit'].forEach(function createTrackDirective(event) {
    var directiveName = 'track' + event[0].toUpperCase() + event.slice(1);
    trackComponentModule.directive(directiveName,
    /* @ngInject */['tracker', function (tracker) {
      return {
        restrict: 'A',
        link: function link(scope, elem, attrs) {
          elem.bind(event, function () {
            var eventName = attrs[directiveName];
            var properties = setProps(scope, elem, attrs, event);
            var options = attrs.trackOptions ? scope.$eval(attrs.trackOptions) : {};

            tracker.trackEvent(eventName, properties, options);
          });
        }
      };
    }]);
  });

  function setProps(scope, elem, attrs, event) {
    var properties = attrs.trackData ? scope.$eval(attrs.trackData) : {};
    properties.ui = properties.ui || {};
    properties.ui.event = event;
    properties.ui.text = elem.text().trim();
    properties.ui.class = elem.attr('class');
    properties.ui.id = elem.attr('id');
    properties.ui.tag = elem.prop('tagName');
    properties.ui.tooltip = elem.attr('tooltip');
    return properties;
  }

  var signupOrLoginTemplate = "<form name=\"signupOrLoginForm\" class=\"signup-or-login\" ng-class=\"{ 'forgot-password-mode': signupOrLogin.forgotPasswordMode, 'auto-activated': signupOrLogin.autoActivated }\" novalidate ng-submit=\"signupOrLogin.updateModels() && signupOrLoginForm.$valid && signupOrLogin.login()\"> <div class=\"signup-or-login-components\"> <div class=\"outer-server-validation-errors\"> <server-validation-errors ng-disabled=\"signupOrLogin.disabled\"></server-validation-errors> </div> <div ng-if=\"signupOrLogin.maintenanceWarning\"> <div class=\"alert alert-{{signupOrLogin.maintenanceWarning.mode}}\" ng-bind=\"signupOrLogin.maintenanceWarning.message\"></div> </div> <div ng-if=\"signupOrLogin.logoutReason\"> <div class=\"alert alert-warning\" ng-bind=\"signupOrLogin.logoutReason\"></div> </div> <div class=\"email-and-password\"> <div class=\"qualified-login\"> <p class=\"needs-email\" ng-show=\"signupOrLogin.forgotPasswordMode && !signupOrLogin.forgotPasswordSent\"> Enter the email you used to sign up and we'll send you information on how to reset your password. </p> <p class=\"needs-email\" ng-show=\"signupOrLogin.forgotPasswordMode && signupOrLogin.forgotPasswordSent\"> We've sent an email with instructions on how to reset your password to <strong>{{ signupOrLogin.user.email }}</strong>. </p> <div class=\"name-fields layout-two-columns\" ng-show=\"signupOrLogin.signupNameRequired()\"> <form-group label=\"First Name\" field-name=\"your first name\"> <input class=\"form-control allow-form-fills\" name=\"firstname\" type=\"text\" ng-required=\"signupOrLogin.signupNameRequired()\" placeholder=\"John\" ng-model=\"signupOrLogin.user.firstName\" ng-disabled=\"signupOrLogin.disabled\"> </form-group> <form-group label=\"Last Name\" field-name=\"your last name\"> <input class=\"form-control allow-form-fills\" name=\"lastname\" type=\"text\" ng-required=\"signupOrLogin.signupNameRequired()\" placeholder=\"Smith\" ng-model=\"signupOrLogin.user.lastName\" ng-disabled=\"signupOrLogin.disabled\"> </form-group> </div> <form-group label=\"Email\" class=\"has-feedback\" field-name=\"your email\" ng-hide=\"signupOrLogin.forgotPasswordSent\"> <input class=\"form-control allow-form-fills\" name=\"email\" type=\"email\" required unique-email=\"!signupOrLogin.loginMode && !signupOrLogin.forgotPasswordMode && !signupOrLogin.activateMode\" unqiue-email-model=\"signupOrLogin.modelName\" login-verification=\"{ loginIncorrect: signupOrLogin.loginFailed, accountLocked: signupOrLogin.accountLocked }\" placeholder=\"your.name@example.com\" ng-model=\"signupOrLogin.user.email\" ng-change=\"signupOrLogin.resetErrors()\" ng-disabled=\"signupOrLogin.disabled\" ng-readonly=\"signupOrLogin.activateMode\"> <span class=\"form-control-feedback\" aria-hidden=\"true\" ng-show=\"signupOrLoginForm.email.$pending\"> <span class=\"loading-spinner\"></span> </span> <form-group-messages> { required: 'Your email is required to continue.', uniqueEmail: 'This email has an account. <a ng-click=\"signupOrLogin.switchLoginMode()\" track-click=\"Login Initiated\" track-data=\"{ui: {region: \\'signup.existingEmail\\'}}\" tabindex=\"0\">Log in here</a><br> <a ng-click=\"signupOrLogin.switchForgotPasswordMode()\" track-click=\"Forgot Password Initiated\" track-data=\"{ui: {region: \\'signup.existingEmail\\'}}\" tabindex=\"0\">Don\\'t know your password?</a>', accountLocked: '', loginIncorrect: signupOrLogin.forgotPasswordMode ? 'We can\\'t find an account with that email address.' : '' } </form-group-messages> </form-group> <form-group label=\"Password\" class=\"has-feedback\" field-name=\"your password\" ng-if=\"signupOrLogin.passwordRequired()\"> <input class=\"form-control\" name=\"password\" type=\"password\" ng-required=\"signupOrLogin.passwordRequired()\" minlength=\"8\" maxlength=\"500\" login-verification=\"{ loginIncorrect: signupOrLogin.loginFailed, accountLocked: signupOrLogin.accountLocked }\" ng-model=\"signupOrLogin.user.password\" ng-change=\"signupOrLogin.resetErrors()\" ng-disabled=\"signupOrLogin.disabled\"> <span class=\"form-control-feedback\" ng-class=\"{ 'icon-checkmark': signupOrLoginForm.password.$valid, 'icon-warning': !signupOrLoginForm.password.$valid }\" aria-hidden=\"true\" ng-show=\"signupOrLoginForm.password.$touched || signupOrLoginForm.password.$valid\"></span> <div class=\"help-block focused-help-block hide-on-error\" ng-hide=\"signupOrLogin.loginMode\"> Your password should be a least 8 characters. </div> <form-group-messages>{required: 'Please provide your password to '+(signupOrLogin.loginMode ? 'log in' : 'sign up')+'.'}</form-group-messages> </form-group> </div> </div> <div class=\"msa-agreement\" ng-if=\"signupOrLogin.showMsa && !signupOrLogin.loginMode\"> <form-group> <div class=\"qualified-checkbox\"> <input type=\"checkbox\" ng-model=\"signupOrLogin.agreeToMsa\" required name=\"agreeToMsa\" id=\"agreeToMsa\"> <label for=\"agreeToMsa\"> I agree to the <a href=\"/msa\" target=\"_blank\" rel=\"noopener nofollow\">Master Subscription Agreement</a> </label> </div> <form-group-messages>{required: 'You must agree to the service agreement to continue.'}</form-group-messages> </form-group> </div> <div class=\"toggle-mode-forgot\"> <div class=\"remember-me\" ng-show=\"signupOrLogin.enableRememberMe && !signupOrLogin.forgotPasswordMode\"> <div class=\"qualified-checkbox\"> <input type=\"checkbox\" ng-model=\"signupOrLogin.rememberMe\" name=\"rememberMe\" id=\"rememberMe\"> <label for=\"rememberMe\">Remember Me</label> </div> </div> <div class=\"forgot-password\" ng-show=\"signupOrLogin.loginMode && !signupOrLogin.forgotPasswordMode\"> <span class=\"link\" ng-click=\"signupOrLogin.switchForgotPasswordMode()\" track-click=\"Forgot Password Initiated\"> Forgot Password </span> </div> </div> <div class=\"subscribe-newsletter\" ng-if=\"signupOrLogin.showNewsletter && !signupOrLogin.loginMode\"> <form-group> <div class=\"qualified-checkbox\"> <input type=\"checkbox\" ng-model=\"signupOrLogin.user.subscribeToNewsletter\" name=\"subscribeToNewsletter\" id=\"subscribeToNewsletter\"> <label for=\"subscribeToNewsletter\"> I want to receive a monthly newsletter with product updates and tips </label> </div> </form-group> </div> <div class=\"terms-agreement\" ng-show=\"signupOrLogin.showTerms && !signupOrLogin.loginMode\"> By continuing you agree to our <a href=\"/kb/legal/terms\" target=\"_blank\">Terms of Service</a> </div> <div class=\"terms-agreement\" ng-show=\"signupOrLogin.showPrivacy && !signupOrLogin.loginMode\"> By continuing you agree to having your data processed per our <a href=\"/kb/legal/privacy-policy\" target=\"_blank\">Privacy Policy</a> </div> <div class=\"toggle-mode\" ng-hide=\"signupOrLogin.forgotPasswordMode\"> <a ng-href=\"{{ (signupOrLogin.loginMode && signupOrLogin.signupLink) || '' }}\" ng-click=\"signupOrLogin.switchLoginMode()\" ng-if=\"!signupOrLogin.disableSignup && !signupOrLogin.disableLogin\"> <span ng-hide=\"signupOrLogin.loginMode\" track-click=\"Login Initiated\" track-data=\"{ui: {region: 'signup'}}\">I have an account</span> <span ng-show=\"signupOrLogin.loginMode\" track-click=\"Signup Initiated\" track-data=\"{ui: {region: 'signin'}}\">I need to sign up</span> </a> </div> </div> <div class=\"buttons\"> <button class=\"btn-default\" ng-if=\"signupOrLogin.hasCancel && !signupOrLogin.forgotPasswordMode\" ng-disabled=\"signupOrLogin.disabled\" track-click=\"{{signupOrLogin.trackerName()}} Cancelled\" ng-click=\"signupOrLogin.onCancel()\"> Cancel </button> <input type=\"submit\" class=\"btn-primary next\" track-click=\"{{signupOrLogin.trackerName()}} Submitted\" ng-if=\"!signupOrLogin.forgotPasswordMode\" ng-disabled=\"signupOrLogin.disabled\" value=\"{{ signupOrLogin.loginMode ? 'Log In' : (signupOrLogin.nextButtonLabel || 'Next') }}\"> <a class=\"btn-primary next\" track-click=\"SAML Login\" ng-if=\"signupOrLogin.samlUrl\" ng-href=\"{{ signupOrLogin.samlUrl }}\"> SSO Log in </a> <input type=\"button\" class=\"btn-default prev\" track-click=\"{{signupOrLogin.trackerName()}} Stepped Back\" ng-if=\"signupOrLogin.forgotPasswordMode\" ng-click=\"signupOrLogin.switchForgotPasswordMode()\" value=\"Back\"> <input type=\"submit\" class=\"btn-primary next\" track-click=\"{{signupOrLogin.trackerName()}} Stepped Forward\" ng-if=\"signupOrLogin.forgotPasswordMode && !signupOrLogin.forgotPasswordSent\" ng-disabled=\"signupOrLogin.disabled\" value=\"Continue\"> </div> </form> ";

  var signupOrLoginComponentModule = angular.module('Q.components.signup-or-login', [accountServiceModule, checkEmailServiceModule, uniqueEmailValidatorComponentModule, serverValidationErrorsComponentModule, trackComponentModule]).directive('signupOrLogin', ['$timeout', function ($timeout) {
    return {
      restrict: 'E',
      scope: {
        nextButtonLabel: '@',
        user: '=?',
        loginMode: '=?',
        forgotPasswordMode: '=?',
        activateMode: '<',
        activateToken: '<',
        passwordNotRequired: '<',
        disableSignup: '<',
        signupLink: '@',
        disableLogin: '<',
        showMsa: '<',
        showTerms: '<',
        showPrivacy: '<',
        showNewsletter: '<',
        enableRememberMe: '<',
        allowSaml: '<',
        onSignup: '&',
        onLogin: '&',
        onActivate: '&',
        onCancel: '&',
        hideLoadingPanel: '<'
      },
      template: signupOrLoginTemplate,
      controller: 'SignupOrLoginCtrl as signupOrLogin',
      bindToController: true,
      require: 'signupOrLogin',
      link: function link(scope, elem, attr, signupOrLoginCtrl) {
        if (attr.onCancel) {
          signupOrLoginCtrl.hasCancel = true;
        }
        signupOrLoginCtrl.focusInput = function () {
          $timeout(function () {
            var inputs = elem[0].querySelectorAll('a, button, input');
            var i = 0;
            while (i < inputs.length) {
              if (inputs[i].focus && angular.element(inputs[i]).isVisible()) {
                inputs[i].focus();
                break;
              }
              i++;
            }
          }, 500);
        };
        signupOrLoginCtrl.focusInput();
      }
    };
  }]).controller('SignupOrLoginCtrl', ['$scope', '$element', 'accountService', 'checkEmailService', 'serverValidationService', '$q', '$injector', function SignupOrLoginCtrl($scope, $element, accountService, checkEmailService, serverValidationService, $q, $injector) {
    var ctrl = this;
    var alreadyHasName = false;
    ctrl.$onInit = function () {
      ctrl.logoutReason = accountService.LOGOUT_REASONS[accountService.logoutReason];

      if (!ctrl.user) {
        ctrl.user = {};
      } else {
        alreadyHasName = !!(ctrl.user.firstName && ctrl.user.lastName);
      }

      if (ctrl.allowSaml) {
        ctrl.samlUrl = getCookie('saml_login');
      }

      if (ctrl.disableSignup) {
        ctrl.loginMode = true;
      }

      if (ctrl.user && ctrl.user.hasPassword && ctrl.activateToken && ctrl.activateMode) {
        ctrl.autoActivated = true;
        var promise = _activate();
        promise.catch(function () {
          ctrl.autoActivated = false;
        });
        ctrl.onActivate({ promise: promise });
      }

      ctrl.modelName = accountService.modelName;

      ctrl.focusInput = angular.noop;
      ctrl.trackerName = function () {
        return ctrl.loginMode ? 'Login' : 'Signup';
      };

      ctrl.forgotPasswordSent = false;
      ctrl.loginFailed = false;
      ctrl.accountLocked = false;
      ctrl.disabled = false;

      ctrl.switchLoginMode = function () {
        if (ctrl.loginMode && ctrl.signupLink) return;
        ctrl.loginMode = !ctrl.loginMode;
        _clearFormErrors();
      };
      ctrl.switchForgotPasswordMode = function () {
        ctrl.forgotPasswordMode = !ctrl.forgotPasswordMode;
        _clearFormErrors();
      };

      // Hack for LastPass and other poor password managers to try and validate changed inputs before submitting.
      ctrl.updateModels = function () {
        var inputs = angular.element($element.find('input'));
        inputs.triggerHandler('input');
        return true;
      };

      ctrl.login = function () {
        if (ctrl.forgotPasswordMode) {
          _forgotPassword();
        } else if (ctrl.activateMode) {
          ctrl.onActivate({ promise: _activate() });
        } else if (ctrl.loginMode) {
          ctrl.onLogin({ promise: _emailLogin() });
        } else {
          ctrl.onSignup({ promise: _emailSignup() });
        }
      };

      /**
       * Returns true if any login has been configured
       * @returns {*|boolean}
       */
      ctrl.hasAnyLogin = function () {
        return ctrl.user.email && ctrl.user.password && ctrl.user.firstName && ctrl.user.lastName || ctrl.user.id && ctrl.user.hasPassword;
      };

      /**
       * Returns true if first/last name are required
       * @returns {boolean}
       */
      ctrl.signupNameRequired = function () {
        return !ctrl.loginMode && !ctrl.forgotPasswordMode && !alreadyHasName;
      };

      /**
       * Returns true if password field is required
       * @returns {boolean}
       */
      ctrl.passwordRequired = function () {
        return !ctrl.forgotPasswordMode && !ctrl.passwordNotRequired;
      };

      ctrl.resetErrors = function () {
        ctrl.loginFailed = false;
        ctrl.accountLocked = false;
      };

      if ($injector.has('productMaintenanceService')) {
        ctrl.maintenanceWarning = $injector.get('productMaintenanceService').warning();
      }

      //// private functions

      function _clearFormErrors() {
        ctrl.forgotPasswordSent = false;
        ctrl.loginFailed = false;
        ctrl.accountLocked = false;
        ctrl.agreeToMsa = false;
        serverValidationService.clearErrors();
        $scope.signupOrLoginForm.email.$validate();
        $scope.signupOrLoginForm.$setPristine();
        $scope.signupOrLoginForm.$setUntouched();
        ctrl.focusInput();
      }

      function _forgotPassword() {
        ctrl.disabled = true;
        return accountService.forgotPassword(ctrl.user.email).then(function () {
          ctrl.forgotPasswordSent = true;
        }).catch(function (resp) {
          if (resp && resp.status === 404) {
            ctrl.loginFailed = true;
          }
        }).then(serverValidationService.clearErrors, serverValidationService.getErrorHandler({ ignoredCodes: 404 })).finally(function () {
          return ctrl.disabled = false;
        });
      }

      /**
       * Logs in via email and password
       * @returns {*}
       * @private
       */
      function _emailLogin() {
        ctrl.disabled = true;
        return accountService.login(ctrl.user.email, ctrl.user.password, {
          rememberMe: !!ctrl.rememberMe,
          showLoadingPanel: !ctrl.hideLoadingPanel
        }).then(function (account) {
          return account.user;
        }, function (response) {
          if (response.data && response.data.maintenanceMode) {
            return $q.reject(response);
          } else {
            ctrl[response.status === 423 ? 'accountLocked' : 'loginFailed'] = true;
            return $q.reject();
          }
        }).then(serverValidationService.clearErrors, serverValidationService.getErrorHandler()).finally(function () {
          return ctrl.disabled = false;
        });
      }

      /**
       * Signs up via email and password
       * @returns {*}
       * @private
       */
      function _emailSignup() {
        ctrl.disabled = true;
        return accountService.createAccount(ctrl.user, {
          rememberMe: !!ctrl.rememberMe,
          showLoadingPanel: !ctrl.hideLoadingPanel
        }).then(function (account) {
          return account.user;
        }).then(serverValidationService.clearErrors, serverValidationService.getErrorHandler()).finally(function () {
          return ctrl.disabled = false;
        });
      }

      /**
       * Activates a user via email
       * @returns {*}
       * @private
       */
      function _activate() {
        ctrl.disabled = true;
        return accountService.activate({
          token: ctrl.activateToken,
          firstName: ctrl.user.firstName,
          lastName: ctrl.user.lastName,
          password: ctrl.user.password,
          subscribeToNewsletter: ctrl.user.subscribeToNewsletter
        }, {
          rememberMe: !!ctrl.rememberMe,
          showLoadingPanel: !ctrl.hideLoadingPanel
        }).then(function (account) {
          return account.user;
        }).then(serverValidationService.clearErrors, serverValidationService.getErrorHandler({
          ignoredCodes: [403, 404, 423]
        })).catch(function (resp) {
          if (resp) {
            if (resp.status === 403) {
              serverValidationService.setErrors('This account has either already been activated or the activation token is no longer valid.');
            } else if (resp.status === 404) {
              serverValidationService.setErrors('The activation token is no longer valid. Did you already activate your account?');
            } else if (resp.status === 423) {
              serverValidationService.setErrors('This account is locked, so it cannot be activated at this time.');
            }
          }
          return $q.reject(resp);
        }).finally(function () {
          return ctrl.disabled = false;
        });
      }
    };
  }]);

  var activateRouteModule = angular.module('Q.routes.activate', [uiRouterStateModule, signupOrLoginComponentModule, legalLinksComponentModule]).config(['$stateProvider', function ($stateProvider) {
    function activationCompleteHandler($stateParams, $location, $state, $window) {
      return function () {
        if ($stateParams.redirect) {
          var params = $location.search(),
              redirect = $stateParams.redirect;
          delete params.token;
          delete params.redirect;
          if (/^http/.test(redirect)) {
            $window.location = redirect;
          } else {
            $state.go($stateParams.redirect, params, { location: 'replace' });
          }
        } else {
          $state.go(appConfig.defaultRoute, null, { location: 'replace' });
        }
      };
    }

    $stateProvider.state('activate', {
      url: '/activate/:token?redirect',
      template: activateTemplate,
      data: {
        pageTitle: 'Activate Your Account',
        bodyClass: 'activate-account'
      },
      resolve: {
        accountServiceCheck: /* @ngInject */['accountService', function accountServiceCheck(accountService) {
          return accountService.whenReady();
        }],
        user: /* @ngInject */['accountService', '$stateParams', '$state', '$window', '$location', function user(accountService, $stateParams, $state, $window, $location) {
          return accountService.activateCheck($stateParams.token).catch(activationCompleteHandler($stateParams, $location, $state, $window));
        }]
      },
      controllerAs: 'activate',
      controller: /* @ngInject */['$state', '$stateParams', 'serverValidationService', 'accountService', 'user', 'appAlert', '$location', function controller($state, $stateParams, serverValidationService, accountService, user, appAlert, $location) {
        var ctrl = this;

        ctrl.user = user;
        ctrl.token = $stateParams.token;

        if (user) {
          // always logout on this page, to avoid potential errors.
          // also, clear all models, to prevent stale data
          accountService.logout({
            showLoadingPanel: false,
            ejectModels: true,
            reload: false
          });

          ctrl.finish = function (promise) {
            promise.then(activationCompleteHandler($stateParams, $location, $state));
          };
        }
      }]
    });
  }]);

  var confirmTemplate = "<div class=\"inline-modal\"> <div class=\"modal-content\"> <div class=\"modal-header\"> <span class=\"qualified-logo-full-color\"><span>Qualified</span></span> </div> <div class=\"modal-body status-message\"> <h3 class=\"status-completed\" ng-show=\"confirmed\"> Thank you for confirming your email. </h3> <h3 class=\"status-failed\" ng-show=\"!confirmed\" ng-bind=\"error\"></h3> </div> <div class=\"modal-footer\"> <a class=\"btn-primary\" ng-click=\"go()\" ng-show=\"confirmed\">Continue</a> <a class=\"btn-primary\" ui-sref=\"{{ ::appConfig.defaultRoute }}\" ng-hide=\"confirmed\">Return to Log In</a> </div> </div> </div> <div class=\"inline-modal-footer\"> <legal-links></legal-links> </div> ";

  var confirmRouteModule = angular.module('Q.routes.confirm', [uiRouterStateModule, accountServiceModule, legalLinksComponentModule]).config(['$stateProvider', function ($stateProvider) {
    $stateProvider.state('confirm', {
      url: '/confirm/:token',
      template: confirmTemplate,
      data: {
        pageTitle: 'Confirm Your Email',
        bodyClass: 'confirm-account'
      },
      resolve: {
        confirmation: /* @ngInject */['$stateParams', 'accountService', function confirmation($stateParams, accountService) {
          return accountService.confirm($stateParams.token).catch(function (res) {
            return res;
          });
        }]
      },
      controller: /* @ngInject */['$scope', 'confirmation', '$window', '$location', function controller($scope, confirmation, $window, $location) {
        if (confirmation.status) {
          $scope.confirmed = false;
          $scope.error = confirmation.status === 423 ? 'This account is locked and cannot be confirmed.' : 'There was an issue confirming your email.';
        } else {
          $scope.confirmed = true;
        }

        $scope.go = function () {
          // force a reload
          $window.location.replace($location.absUrl().replace($location.url(), ensureLeadingSlash(appConfig.defaultRouteUrl || '/')));
        };
      }]
    });
  }]);

  var errorTemplate = "<h1> <span class=\"qualified-logo-full-color\"><span>Qualified</span></span> </h1> <div class=\"error-unknown\"> <h2>Whoops!</h2> <p>An error has occurred that we could not recover from!</p> <p ng-if=\"error.onError.sref\"> <a ui-sref=\"{{ error.onError.sref }}\">Click here to go to {{ error.onError.srefLabel }}.</a> </p> <div ng-if=\"error.onError.template\" compile-template=\"error.onError.template\"></div> <p ng-if=\"!error.onError && appConfig.defaultRoute\"> <a ui-sref=\"{{ appConfig.defaultRoute }}\">Click here to go to your dashboard.</a> </p> <p ng-if=\"!error.onError && !appConfig.defaultRoute\"> <a href=\"/\">Click here to go to the landing page.</a> </p> <p class=\"reset-link\"> If you continue to have issues, you can <a href=\"/reset?auto=true{{ error.resetParams }}\">click here to clear stored browser data and try again.</a> </p> <div class=\"error-message markdown\" ng-show=\"error.error.message\"> <strong>Error Message:</strong> <span ng-show=\"error.simpleError\">{{ error.error.message }}</span> <pre ng-hide=\"error.simpleError\"><code>{{ error.error.message }}</code></pre> </div> </div> <div class=\"footer\"> <legal-links></legal-links> </div> ";

  var notFoundTemplate = "<h1> <span class=\"qualified-logo-full-color\"><span>Qualified</span></span> </h1> <div class=\"error-404\"> <h2>Nothing to see here!</h2> <p> We looked all over, but couldn't find the {{error.itemLabel}} you requested. </p> <p ng-if=\"error.onError.sref\"> <a ui-sref=\"{{ error.onError.sref }}\">Click here to go to {{ error.onError.srefLabel }}.</a> </p> <div ng-if=\"error.onError.template\" compile-template=\"error.onError.template\"></div> <p ng-if=\"!error.onError && appConfig.defaultRoute\"> <a ui-sref=\"{{ appConfig.defaultRoute }}\">Click here to go to your dashboard.</a> </p> <p ng-if=\"!error.onError && !appConfig.defaultRoute\"> <a href=\"/\">Click here to go to the landing page.</a> </p> <p class=\"error-message\"> <strong>Error Message:</strong> 404 {{error.error.statusText || 'Not\n    Found'}} </p> </div> <div class=\"footer\"> <legal-links></legal-links> </div> ";

  var maintenanceTemplate = "<h1> <span class=\"qualified-logo-full-color\"><span>Qualified</span></span> </h1> <div class=\"error-maintenance\"> <h2> {{error.maintenance.reason || 'The system is undergoing scheduled\n    maintenance'}} </h2> <div ng-bind-html=\"error.maintenance.description\"></div> <p ng-if=\"error.startTimeLater\"> Logging in has been disabled at this time to ensure everyone has time to complete their tasks before maintenance mode begins. </p> <p class=\"error-message\"> Work {{ error.startTimeLater ? 'is scheduled to start' : 'was started' }} at <strong>{{ error.maintenance.startTime | date:'medium' }}</strong>, and is expected to take <strong>{{ error.maintenance.estimatedDowntimeMinutes | number:0 }} minutes</strong>. </p> </div> <div class=\"footer\"> <legal-links></legal-links> </div> ";

  var switchTeamOnErrorDialogTemplate = "<div class=\"modal-header\"> <h4 class=\"modal-title\"> {{$ctrl.itemLabel | capitalize}} is on a different team </h4> </div> <div class=\"modal-body modal-body-icon icon-status-access\"> <p> The {{$ctrl.itemLabel}} you requested is available on <strong>{{$ctrl.switchTeam.name}}</strong>. </p> <p> Would you like to switch to that team now to view the {{$ctrl.itemLabel}}? </p> </div> <div class=\"modal-footer\"> <input type=\"button\" class=\"cancel btn-default\" value=\"Cancel\" ng-click=\"$ctrl.cancel()\" ng-disabled=\"$ctrl.disabled\"> <input type=\"button\" class=\"submit btn-primary\" value=\"Switch Team\" ng-click=\"$ctrl.switch()\" ng-disabled=\"$ctrl.disabled\"> </div> ";

  var impersonateServiceModule = angular.module('Q.services.impersonate-service', [accountServiceModule, teamMemberModelModule]).factory('impersonateService', ['$http', '$window', '$timeout', '$q', 'TeamMember', 'accountService', function ($http, $window, $timeout, $q, TeamMember, accountService) {
    function impersonate(id, mode, url) {
      var data = {
        data: {
          impersonate: id
        }
      };
      var httpOptions = {
        mode: mode
      };

      return $http.post(appConfig.apiRoot + 'sessions/impersonate', data, httpOptions).then(function (resp) {
        if (accountService.get().user && accountService.get().user.$type === resp.data.type) {
          // local impersonation, we'll want to logout first and then log back in
          return accountService.reLogin({
            id: resp.data.id,
            rememberMe: resp.data.rememberMe,
            reload: !url
          });
        } else {
          // remote impersonation, no need to logout locally
          return accountService.setRemoteAccount(resp.data.type, resp.data.id);
        }
      }).then(function () {
        if (url) {
          $window.location.href = url;
        }
      });
    }

    function isStaff() {
      var user = accountService.get().user || {};
      return user.role === 'staff';
    }

    function impersonateStaff(teamId, url) {
      return TeamMember.findAll({
        teamId: teamId,
        where: { role: 'staff' },
        per: 1
      }).then(function (teamMembers) {
        var staffMember = teamMembers[0];
        return staffMember ? staffMember.id : $q.reject('No staff account available on that team.');
      }).then(function (id) {
        return impersonate(id, undefined, url);
      });
    }

    var baseRoute = appConfig.apiRoot + 'team_members';

    function switchTeam(teamId, url) {
      return $http.post(baseRoute + '/switch_team', { teamId: teamId }).then(function (_ref) {
        var data = _ref.data;
        return accountService.reLogin({ id: data.id, reload: !url });
      }).then(function () {
        if (url) {
          $window.location.href = url;
        }
      });
    }

    return {
      impersonate: impersonate,
      isStaff: isStaff,
      impersonateStaff: impersonateStaff,
      switchTeam: switchTeam
    };
  }]);

  var switchTeamOnErrorDialogComponentModule = angular.module('Q.components.switch-team-on-error-dialog', [modalComponentModule, impersonateServiceModule, appAlertComponentModule]).factory('switchTeamOnErrorDialog', ['$modal', function changeTeamDialog($modal) {
    return {
      open: function open(_url, _switchTeam, _stateOnError) {
        return $modal.openOnce('switchTeamOnErrorDialog', {
          template: switchTeamOnErrorDialogTemplate,
          resolve: {
            url: function url() {
              return _url;
            },
            switchTeam: function switchTeam() {
              return _switchTeam;
            },
            stateOnError: function stateOnError() {
              return _stateOnError;
            }
          },
          controller: 'SwitchTeamOnErrorDialogCtrl',
          controllerAs: '$ctrl'
        });
      }
    };
  }]).controller('SwitchTeamOnErrorDialogCtrl', ['$scope', '$modalInstance', 'impersonateService', 'url', 'switchTeam', 'stateOnError', 'appAlert', function SwitchTeamOnErrorDialogCtrl($scope, $modalInstance, impersonateService, url, switchTeam, stateOnError, appAlert) {
    var ctrl = this;

    ctrl.switchTeam = switchTeam;
    ctrl.itemLabel = stateOnError.itemLabel || 'item';

    ctrl.switch = function () {
      ctrl.disabled = true;
      var impersonationPromise = void 0;
      if (impersonateService.isStaff()) {
        impersonationPromise = impersonateService.impersonateStaff(switchTeam.id, url);
      } else {
        impersonationPromise = impersonateService.switchTeam(switchTeam.id, url);
      }
      impersonationPromise.then(function () {
        $modalInstance.close();
      }).catch(appAlert.modelError).finally(function () {
        ctrl.disabled = false;
      });
    };

    ctrl.cancel = $modalInstance.dismiss;
  }]);

  var errorRouteModule = angular.module('Q.routes.error', [uiRouterStateModule, legalLinksComponentModule, compileTemplateComponentModule, appAlertComponentModule, switchTeamOnErrorDialogComponentModule]).factory('objectNotFound', ['$q', function ($q) {
    return function (type) {
      return $q.reject({
        status: 404,
        statusText: (type ? type + ' ' : '') + 'Not Found',
        itemLabel: type
      });
    };
  }]).factory('showObjectNotFound', ['$state', function ($state) {
    return function (type) {
      return $state.go('error', {
        error: {
          status: 404,
          statusText: (type ? type + ' ' : '') + 'Not Found',
          itemLabel: type
        }
      });
    };
  }]).config(['$stateProvider', '$urlRouterProvider', function ($stateProvider, $urlRouterProvider) {
    $urlRouterProvider.otherwise(function ($injector) {
      // switch to error state without changing the URL
      $injector.get('$state').go('error');
    });

    // Checks to see if we can simply redirect to without showing a "hard" error
    // This is generally only for 404 and authorization errors
    var shouldRedirect = function shouldRedirect($stateParams) {
      var error = $stateParams.error;
      var toState = $stateParams.toState;
      var onError = toState && toState.data && toState.data.onError;
      return error && onError && onError.sref && [401, 403, 404].find(function (c) {
        return error.status === c;
      });
    };

    $stateProvider.state('error', {
      params: {
        error: false,
        didLog: false,
        fromState: {},
        fromParams: {},
        toState: {},
        toParams: {},
        maintenanceEvent: null
      },

      data: {
        bodyClass: 'error-route',
        pageTitle: /* @ngInject */['$stateParams', function pageTitle($stateParams) {
          return $stateParams.maintenanceEvent ? 'System Maintenance' : 'Error';
        }]
      },

      template: function template(stateParams) {
        if (shouldRedirect(stateParams)) {
          // don't render if we're going to redirect
          return '';
        } else if (stateParams.maintenanceEvent) {
          return maintenanceTemplate;
        } else if (stateParams.error && stateParams.error.status !== 404) {
          return errorTemplate;
        } else {
          return notFoundTemplate;
        }
      },
      controller: /* @ngInject */['$rootScope', '$state', '$stateParams', '$log', '$location', 'switchTeamOnErrorDialog', 'appAlert', function controller($rootScope, $state, $stateParams, $log, $location, switchTeamOnErrorDialog, appAlert) {
        var ctrl = this;

        var toState = $stateParams.toState;
        var onError = ctrl.onError = toState && toState.data && toState.data.onError || appConfig.onError;
        var error = ctrl.error = $stateParams.error;
        ctrl.itemLabel = onError && onError.itemLabel || error && error.itemLabel || 'item';

        if (shouldRedirect($stateParams)) {
          $rootScope.showLoadingPanel = true;

          var errorData = error.data || {};
          if (errorData.switchTeam) {
            // let the user switch to the correct team
            switchTeamOnErrorDialog.open($location.absUrl(), errorData.switchTeam, onError);
          } else if (error.status === 404) {
            // show a simple banner alerting them to the missing object
            appAlert.error('We\'re sorry, we were unable to find that ' + ctrl.itemLabel + '.');
          } else {
            // show a simpler banner showing access error
            appAlert.error('We\'re sorry, but you do not have access to that ' + ctrl.itemLabel + '.');
          }

          // redirect immediately
          $state.go(onError.sref);
          return;
        }

        ctrl.maintenance = $stateParams.maintenanceEvent;
        ctrl.startTimeLater = ctrl.maintenance && new Date() < ctrl.maintenance.startTime;

        // only broadcast an error if we can't safely recover from it
        $rootScope.$broadcast('app.error');

        if (error) {
          if ($stateParams.fromState && $stateParams.fromState.name) {
            var msg = 'Error transitioning from ' + $stateParams.fromState.name + '(' + JSON.stringify($stateParams.fromParams) + ') to ' + toState.name + '(' + JSON.stringify($stateParams.toParams) + ')';
            $log.error(msg, $stateParams.didLog ? '' : error);
          }
          ctrl.simpleError = !angular.isString(error.message) || error.message.indexOf('\n') === -1;

          if (appConfig.appPath && appConfig.onResetReturn !== false) {
            ctrl.resetParams = '&go=' + (appConfig.onResetReturn || appConfig.appPath) + '/';
          }
        }
      }],
      controllerAs: 'error'
    });
  }]);

  var resetPasswordTemplate = "<form name=\"resetPasswordForm\" ng-submit=\"resetPasswordForm.$valid && resetPassword.submit()\" novalidate> <div class=\"inline-modal\"> <div class=\"modal-content\"> <div class=\"modal-header\"> <span class=\"qualified-logo-full-color\"><span>Qualified</span></span> </div> <div class=\"modal-body\"> <p>Please complete the following information to reset your password:</p> <server-validation-errors ng-disabled=\"resetPassword.disabled\"></server-validation-errors> <form-group label=\"Password\"> <input class=\"form-control\" name=\"password\" type=\"password\" required ng-minlength=\"8\" ng-maxlength=\"500\" ng-model=\"resetPassword.password\" ng-disabled=\"resetPassword.disabled\" autofocus> </form-group> <form-group label=\"Confirm Password\"> <input class=\"form-control\" name=\"confirmPassword\" type=\"password\" required minlength=\"8\" maxlength=\"500\" match=\"resetPassword.password\" ng-model=\"resetPassword.confirmPassword\" ng-disabled=\"resetPassword.disabled\"> <form-group-messages>{required: 'Please confirm your new password.'}</form-group-messages> </form-group> <div class=\"checkbox\"> <label><input type=\"checkbox\" ng-model=\"resetPassword.rememberMe\"> Remember Me</label> </div> </div> <div class=\"modal-footer\"> <input type=\"submit\" class=\"btn-primary\" value=\"Reset Password\"> </div> </div> </div> </form> <div class=\"inline-modal-footer\"> <legal-links></legal-links> </div> ";

  var validationMatchComponentModule = 'validation.match';

  var resetPasswordRouteModule = angular.module('Q.routes.reset-password', [uiRouterStateModule, accountServiceModule, validationMatchComponentModule, serverValidationErrorsComponentModule, legalLinksComponentModule]).config(['$stateProvider', function ($stateProvider) {
    $stateProvider.state('reset-password', {
      url: '/reset-password/:token?url&stateName&params',
      params: {
        url: null,
        stateName: null,
        params: null
      },
      template: resetPasswordTemplate,
      data: {
        pageTitle: 'Reset Your Password',
        bodyClass: 'reset-password'
      },
      resolve: {
        accountServiceCheck: /* @ngInject */['accountService', function accountServiceCheck(accountService) {
          return accountService.whenReady();
        }]
      },
      controllerAs: 'resetPassword',
      controller: /* @ngInject */['$state', '$stateParams', '$location', 'accountService', 'serverValidationService', '$q', function controller($state, $stateParams, $location, accountService, serverValidationService, $q) {
        // always logout on this page, to avoid potential errors.
        accountService.logout({ showLoadingPanel: false, reload: false });

        var ctrl = this;
        ctrl.password = '';
        ctrl.confirmPassword = '';
        ctrl.rememberMe = false;

        ctrl.submit = function () {
          ctrl.disabled = true;

          return accountService.resetPassword($stateParams.token, ctrl.password, {
            rememberMe: ctrl.rememberMe
          }).then(function (account) {
            if (!account) {
              return $q.reject({ status: 403 });
            } else {
              if ($stateParams.url) {
                $location.url($stateParams.url).replace();
              } else if ($stateParams.stateName) {
                $state.go($stateParams.stateName, $stateParams.params || {}, {
                  location: 'replace'
                });
              } else {
                $location.url(appConfig.defaultRouteUrl || '/').replace();
              }
            }
          }).then(serverValidationService.clearErrors, serverValidationService.getErrorHandler({
            ignoredCodes: [403, 404, 423]
          })).catch(function (resp) {
            if (resp && resp.status) {
              if (resp.status === 403 || resp.status === 404) {
                serverValidationService.setErrors('The reset password token has expired.  Please try again.');
              } else if (resp.status === 423) {
                serverValidationService.setErrors('This account is inactive, unable to reset password.');
              }
            } else if (!resp) {
              serverValidationService.setErrors('An error has occurred that we were not able to recover from.  Please try again.');
            }
          }).finally(function () {
            return ctrl.disabled = false;
          });
        };
      }]
    });
  }]);

  var sharedRouteConfigModule = angular.module('Q.routes.shared-config', [uiRouterStateModule, activateRouteModule, confirmRouteModule, errorRouteModule, resetPasswordRouteModule]).config(['$locationProvider', '$urlMatcherFactoryProvider', '$anchorScrollProvider', function ($locationProvider, $urlMatcherFactoryProvider, $anchorScrollProvider) {
    $locationProvider.html5Mode(true);

    // allow trailing slashes
    $urlMatcherFactoryProvider.strictMode(false);
    // always squash default params
    $urlMatcherFactoryProvider.defaultSquashPolicy(true);

    $urlMatcherFactoryProvider.type('ObjectId', {
      pattern: /[a-fA-F0-9]{24}/
    });
    $urlMatcherFactoryProvider.type('assessmentResultIdList', {
      pattern: /[a-fA-F0-9]{24}|list/
    });
    $urlMatcherFactoryProvider.type('ObjectOrQId', {
      pattern: /(?:[a-fA-F0-9]{24}|s_[a-zA-Z0-9-_=]{10,})/
    });

    $anchorScrollProvider.disableAutoScrolling();
  }]).run(['$rootScope', '$state', '$stateParams', '$window', '$timeout', '$injector', function ($rootScope, $state, $stateParams, $window, $timeout, $injector) {
    $rootScope.$state = $state;
    $rootScope.$stateParams = $stateParams;

    var stateLoaded = false;

    // Enforce scrolling back to the top if the state (not just the parameters) changes.
    $rootScope.$on('$stateChangeSuccess', function (event, toState, toParams, fromState /*, fromParams*/) {
      stateLoaded = true;
      if (toState.name !== fromState.name) {
        $window.scrollTo(0, 0);
      }
    });

    var _getService = function _getService(svc) {
      return $injector.has(svc) && $injector.get(svc);
    };

    var _showError = function _showError(msg) {
      var appAlert = _getService('appAlert');
      if (appAlert) {
        appAlert.error(msg);
      } else {
        $window.alert(msg);
      }
    };

    $rootScope.$on('$stateChangeError', function (event, toState, toParams, fromState, fromParams, error) {
      event.preventDefault();
      if (error) {
        if (error.redirectTo) {
          return $timeout(function () {
            return $state.go(error.redirectTo.state, error.redirectTo.params, error.redirectTo.options);
          });
        } else if (error.data && error.data.maintenanceMode === true && $injector.has('productMaintenanceService')) {
          return $injector.get('productMaintenanceService').goToError();
        } else if (error.status === 401) {
          $window.name = 'Q_LOGGED_OUT!' + $window.name;
          $window.location.reload();
          return;
        } else if (stateLoaded && NETWORK_ERROR_CODES.includes(error.status)) {
          var msg = 'There appears to be an issue communicating with the server.';
          if (error.status < 500) {
            msg += ' Please check your network connection and try again.';
          } else {
            msg += ' Please wait a moment and try again.';
          }
          if (error.status > -1) {
            msg += ' (' + error.status;
            if (error.statusText) {
              msg += ': ' + error.statusText;
            }
            msg += ')';
          }
          _showError(msg);
          // stay on the current route
          return;
        }
      }
      return $state.go('error', {
        toState: toState,
        toParams: toParams,
        fromState: fromState,
        fromParams: fromParams,
        error: error
      });
    });

    $rootScope.$on('$stateNotFoundError', function (event, unfoundState, fromState, fromParams) {
      event.preventDefault();
      var toState = { name: unfoundState.to };
      return $state.go('error', {
        toState: toState,
        toParams: unfoundState.toParams,
        fromState: fromState,
        fromParams: fromParams,
        error: 'State Not Found: ' + toState.name
      });
    });
  }]);

  var moment = window.moment;
  // configure relative times, which are dumb by default
  // now we'll always expect a full unit of time before reducing precision
  moment.relativeTimeThreshold('s', 59);
  moment.relativeTimeThreshold('m', 59);
  moment.relativeTimeThreshold('h', 24);
  moment.relativeTimeThreshold('d', 30);
  moment.relativeTimeThreshold('M', 12);

  var momentModule = 'angularMoment';

  var durationFormatFilterModule = angular.module('Q.filters.duration-format', [momentModule]).filter('durationFormat', ['moment', function (moment$$1) {
    return function durationFormat(value, format, lowercase) {
      if (value === null || isNaN(value)) {
        return '';
      }

      var d = moment$$1.duration(value, format);
      var days = d.days();
      var hours = d.hours();
      var minutes = d.minutes();
      var seconds = d.seconds();

      var parts = [];

      var push = function push(amt, unit) {
        if (!amt || amt <= 0) return;

        parts.push(amt);
        var u = lowercase ? unit.toLowerCase() : unit;
        if (amt !== 1) {
          u += 's';
        }
        parts.push(u);
      };

      push(days, 'Day');
      push(hours, 'Hour');
      push(minutes, 'Minute');
      push(seconds, 'Second');
      return parts.join(' ');
    };
  }]);

  var RECRUITMENT = {
    $recruitment: true,
    $education: false,
    participant: 'candidate',
    Participant: 'Candidate',
    participants: 'candidates',
    Participants: 'Candidates'
  };
  var EDUCATION = {
    $recruitment: false,
    $education: true,
    participant: 'student',
    Participant: 'Student',
    participants: 'students',
    Participants: 'Students'
  };
  var types = {
    recruitment: RECRUITMENT,
    education: EDUCATION
  };

  // Sort of hackish way to configure variances based on a recruiting or education target

  var productTypeServiceModule = angular.module('Q.services.product-type-service', []).constant('PT', angular.extend({}, RECRUITMENT)).factory('productTypeService', ['$rootScope', 'PT', '$document', function ($rootScope, PT, $document) {
    /**
     * Replaces occurrences `{{word}}` with either the matching product type string, or leaving `word`
     * untouched if there's no match.
     * @param {String} string String to modify
     */
    function replace(string) {
      return string.replace(/\{\{(\w+)\}\}/g, function (_, w) {
        return PT[w] || w;
      });
    }

    /**
     * Returns the correct for for the given count
     * @param {String} single
     * @param {String} plural
     * @param {Number} count
     * @returns {String}
     */
    function pluralize(single, plural, count) {
      return count === 1 ? single : plural;
    }

    return {
      types: types,
      // Intital setup, sets the product to default recruitment mode, to ensure we have something
      // if the app has not loaded
      init: function init() {
        this.setProduct('recruitment');
      },

      /**
       * Sets the current product type based on the team
       * Can be called after setup, but should be set as early as possible.
       * @param {Team|String} team Team object to use for configuring the app
       */
      setProduct: function setProduct(team) {
        $rootScope.PT = RECRUITMENT;
        var productTypes = team && team.productTypes;
        if (team === 'education') {
          $rootScope.PT = EDUCATION;
        } else if (productTypes && !productTypes.recruitment) {
          if (productTypes.education) {
            $rootScope.PT = EDUCATION;
          }
        }
        var rPT = $rootScope.PT;
        rPT.$replace = replace;
        rPT.$pluralize = pluralize.bind(rPT, rPT.participant, rPT.participants);
        rPT.$Pluralize = pluralize.bind(rPT, rPT.Participant, rPT.Participants);

        angular.extend(PT, rPT);
        $document.toggleClass('product-type-recruitment', PT.$recruitment);
        $document.toggleClass('product-type-education', PT.$education);
      },

      replace: replace
    };
  }]).directive('qPt', ['PT', function (PT) {
    return {
      link: function link(scope, elem) {
        var text = elem.text();
        var key = text.replace(/\W+/, '');
        if (PT[key]) {
          // we do it this way to preserve whitespace & punctuation
          elem.text(text.replace(key, PT[key]));
        }
      }
    };
  }]).run(['$rootScope', 'productTypeService', function ($rootScope, productTypeService) {
    productTypeService.init();
    $rootScope.$on('app.accountSet', function ($event, account) {
      if (account && account.user && account.user.team) {
        productTypeService.setProduct(account.user.team);
      }
    });
  }]);

  var invitationsServiceModule = angular.module('Q.services.invitations-service', [candidateModelModule, assessmentModelModule, assessmentResultModelModule, assessmentInvitationModelModule, challengeModelModule, teamCandidateModelModule, productTypeServiceModule, modelHelperModule]).factory('invitationsService', ['$http', 'Candidate', 'Challenge', 'Assessment', 'AssessmentResult', 'AssessmentInvitation', 'TeamCandidate', 'accountService', '$q', 'PT', 'ModelHelper', function ($http, Candidate, Challenge, Assessment, AssessmentResult, AssessmentInvitation, TeamCandidate, accountService, $q, PT, ModelHelper) {
    var baseRoute = appConfig.apiRoot + 'assessment_invitations';
    var authRoute = appConfig.apiRoot + 'assessment_result_auths';

    var arRecapState = {
      invited: 'has already been Invited',
      started: 'has already been Invited and has Started',
      submitted: 'has already Submitted',
      submitting: 'has already Submitted'
    };

    var injectAuth = function injectAuth(auth) {
      if (auth) {
        if (auth.assessment) {
          auth.assessment = Assessment.inject(ModelHelper.deserialize(Assessment, auth.assessment));
        }
        if (auth.assessmentResult) {
          auth.assessmentResult = AssessmentResult.inject(ModelHelper.deserialize(AssessmentResult, auth.assessmentResult));
        }
        if (auth.challenges) {
          auth.challenges = auth.challenges.map(function (challenge) {
            return Challenge.inject(ModelHelper.deserialize(Challenge, challenge));
          });
        }
        if (auth.invitation) {
          auth.invitation = AssessmentInvitation.inject(ModelHelper.deserialize(AssessmentInvitation, auth.invitation));
        }
        if (auth.candidate) {
          auth.candidate = Candidate.inject(ModelHelper.deserializeWithUiPrefs(Candidate, auth.candidate));
          accountService.setLoginAccount(auth.candidate, {
            showLoadingPanel: false
          });
        }
      }
      return auth;
    };

    var AUTH_INCLUDES = ['challenges.digest', 'assessment.team', 'assessment.team.customer', 'assessment_result.team_candidate', 'assessment_result.solutions', 'team_ids'];

    var svc = {
      existingInviteData: function existingInviteData(tc, assessmentId) {
        var arRecap = (tc.assessmentResults || []).find(function (ar) {
          return ar.assessmentId === assessmentId;
        }) || {};
        var stateLabel = '';
        if (tc.isArchived() || tc.isDecided()) {
          stateLabel = PT.Participant + ' has already been ' + tc.stateName();
        } else if (arRecap.state) {
          stateLabel = PT.Participant + ' ' + arRecapState[arRecap.state];
        }
        return {
          id: tc.id,
          teamCandidate: tc,
          email: tc.email,
          name: tc.name,
          state: arRecap.state || 'none',
          stateLabel: stateLabel,
          submitted: arRecap.state === 'submitted' || arRecap.state === 'submitting',
          decided: tc.isDecided(),
          archived: tc.isArchived(),
          assessmentResultId: arRecap.id
        };
      },


      /**
       * Checks to see if any of the candidate emails listed have already been invited to the given assessment
       * @param candidateEmails {Array} emails to check
       * @param assessmentId {String} ID of assessment
       * @returns {Promise<Array<{name, state, assessmentResultId}>>} Promise that resolves to an array of already invited summary data
       */
      findAlreadyInvited: function findAlreadyInvited(candidateEmails, assessmentId) {
        if (candidateEmails.length) {
          return TeamCandidate.findAll({
            where: {
              assessmentIds: assessmentId,
              email: { in: candidateEmails }
            }
          }).then(function (tcs) {
            return tcs.map(function (tc) {
              return svc.existingInviteData(tc, assessmentId);
            });
          });
        } else {
          return $q.resolve([]);
        }
      },


      /**
       * Invites candidates
       * @param data
       */
      inviteCandidates: function inviteCandidates(data) {
        return $http.post(baseRoute + '/invite_candidates', { data: data }).then(function (response) {
          return response.data && response.data.data;
        });
      },
      authorize: function authorize(accessToken, assessmentId) {
        return $http.post(authRoute + '/authorize', {
          data: {
            assessmentId: assessmentId,
            accessToken: accessToken
          }
        }, { params: { includes: AUTH_INCLUDES } }).then(function (response) {
          return injectAuth(response.data && response.data.data);
        });
      },
      register: function register(data) {
        return $http.post(authRoute + '/register', { data: data }, { params: { includes: AUTH_INCLUDES } }).then(function (response) {
          return injectAuth(response.data && response.data.data);
        });
      },
      findAll: function findAll(params) {
        return AssessmentInvitation.findAll(params, { useFilter: true });
      },
      cancel: function cancel(invitation) {
        return AssessmentInvitation.destroy(invitation);
      },
      resend: function resend(invitation) {
        return AssessmentInvitation.resend(invitation);
      }
    };

    return svc;
  }]);

  var registerAccountTemplate = "<form name=\"registerForm\" class=\"register-account\" novalidate ng-submit=\"$ctrl.submitForm(registerForm)\"> <div> <div class=\"outer-server-validation-errors\"> <server-validation-errors ng-disabled=\"registerForm.disabled\"></server-validation-errors> </div> <div ng-if=\"$ctrl.maintenanceWarning\"> <div class=\"alert alert-{{signupOrLogin.maintenanceWarning.mode}}\" ng-bind=\"$ctrl.maintenanceWarning.message\"></div> </div> <div ng-if=\"!$ctrl.registered\"> <div class=\"email-and-password\"> <div class=\"qualified-login\"> <form-group label=\"Email\" class=\"has-feedback\" field-name=\"your email\"> <input class=\"form-control allow-form-fills\" name=\"email\" type=\"email\" required placeholder=\"your.name@example.com\" ng-model=\"$ctrl.email\" ng-change=\"$ctrl.clearEmailCheck()\" ng-disabled=\"$ctrl.disabled\" ng-readonly=\"!!$ctrl.user || !!$ctrl.auth.verifiedInvitation\"> <span class=\"form-control-feedback\" aria-hidden=\"true\" ng-show=\"signupOrLoginForm.email.$pending\"> <span class=\"loading-spinner\"></span> </span> </form-group> <div class=\"name-fields layout-two-columns\" ng-if=\"$ctrl.nameRequested\"> <form-group label=\"First Name\" field-name=\"your first name\"> <input class=\"form-control allow-form-fills\" name=\"firstname\" type=\"text\" required placeholder=\"John\" ng-model=\"$ctrl.firstName\" ng-disabled=\"$ctrl.disabled\"> </form-group> <form-group label=\"Last Name\" field-name=\"your last name\"> <input class=\"form-control allow-form-fills\" name=\"lastname\" type=\"text\" required placeholder=\"Smith\" ng-model=\"$ctrl.lastName\" ng-disabled=\"$ctrl.disabled\"> </form-group> </div> </div> </div> <div ng-if=\"!$ctrl.checkEmailNeeded && $ctrl.registrationRequired && $ctrl.nameRequested\"> <form-group> <div class=\"qualified-checkbox\"> <input type=\"checkbox\" ng-model=\"$ctrl.agreeToNotShare\" required name=\"agreeToNotShare\" id=\"agreeToNotShare\"> <label for=\"agreeToNotShare\"> I agree to not share any aspect of this assessment with outside parties. </label> </div> <form-group-messages>{required: 'You must agree to not share in order to continue.'}</form-group-messages> </form-group> </div> <div class=\"terms-agreement\" ng-show=\"$ctrl.emailCheck && !$ctrl.emailCheck.exists\"> By continuing you agree to having your data processed per our <a href=\"/kb/legal/privacy-policy\" target=\"_blank\">Privacy Policy</a> </div> <div class=\"buttons\"> <input type=\"submit\" class=\"btn-primary\" value=\"Next\" ng-disabled=\"$ctrl.disabled\"> </div> <div class=\"alert alert-warning\" ng-show=\"!!$ctrl.error\"> There was an error registering your account. If the issue persists after trying again, please contact <a href=\"mailto:team@qualified.io?subject=Unable%20to%20access%20assessment&body=I%20am%20not%20able%20to%20access%20this%20assessment%3A%20{{assessments.assessment.id}}\" target=\"_blank\"> contact Qualified Support </a>. </div> </div> <div ng-if=\"$ctrl.registered\" class=\"verification-required\"> <div class=\"alert\" ng-show=\"$ctrl.auth.emailVerificationRequired\"> <h4> {{ $ctrl.auth.invitationSent ? 'Verification Email Sent' : 'Check your\n          email for your invite' }} </h4> <p> Access to this assessment requires you to verify your account first. This is to protect your account from unauthorized access. Please search through your recent emails. </p> <p ng-if=\"!$ctrl.invitationResent\"> Email was sent from <br><code>Qualified &lt;accounts@qualified.io&gt;</code> <br>to <br><code>{{ $ctrl.auth.email }}</code> </p> <div class=\"resend-email\" ng-if=\"!$ctrl.auth.invitationSent && $ctrl.auth.invitationId\"> <a class=\"btn-default btn-sm\" ng-click=\"$ctrl.requestNewEmail()\" ng-disabled=\"$ctrl.invitationResent\">Email me a new invite</a> <div ng-if=\"$ctrl.invitationResent\"> We sent an email to <code>{{ $ctrl.auth.email }}</code> with a link to confirm your email address. Follow the link to continue with this assessment. </div> </div> </div> </div> </div> </form> ";

  var registerAccountComponentModule = angular.module('Q.components.register-account', [invitationsServiceModule, accountServiceModule, checkEmailServiceModule]).component('registerAccount', {
    bindings: {
      auth: '<',
      onRegistered: '&'
    },
    template: registerAccountTemplate,
    controller: 'RegisterAccountCtrl'
  }).controller('RegisterAccountCtrl', ['$scope', '$element', '$injector', '$stateParams', '$q', 'invitationsService', 'accountService', 'checkEmailService', '$timeout', function RegisterAccountCtrl($scope, $element, $injector, $stateParams, $q, invitationsService, accountService, checkEmailService, $timeout) {
    var ctrl = this;

    ctrl.$onInit = function () {
      // assign default state variables
      ctrl.email = ctrl.auth.email ? ctrl.auth.email : ctrl.user ? ctrl.user.email : $stateParams.email;
      ctrl.firstName = $stateParams.firstName;
      ctrl.lastName = $stateParams.lastName;
      _assignState();
      _checkEmail();

      $timeout(function () {
        if ($stateParams.autoRegister) {
          ctrl.submit();
        }
      });
    };

    if ($injector.has('productMaintenanceService')) {
      ctrl.maintenanceWarning = $injector.get('productMaintenanceService').warning();
    }

    ctrl.submit = function () {
      if (ctrl.emailCheckNeeded) {
        $scope.registerForm.$setPristine();
        $scope.registerForm.$setUntouched();
        _checkEmail().then(function () {
          if (ctrl.registrationRequired && !ctrl.nameRequested) {
            _register();
          }
        });
      } else {
        _register();
      }
    };

    ctrl.submitForm = function (registerForm) {
      // Hack for LastPass and other poor password managers to try and validate changed inputs before submitting.
      var inputs = angular.element($element.find('input'));
      inputs.triggerHandler('input');
      if (registerForm.$valid) {
        ctrl.submit();
      }
    };

    ctrl.clearEmailCheck = function () {
      ctrl.emailCheck = null;
      _assignState();
    };

    ctrl.requestNewEmail = function () {
      invitationsService.resend({
        id: ctrl.auth.invitationId
      });
      ctrl.invitationResent = true;
    };

    // recalc dynamic state variables. Done this way instead of using helper methods to make the logic
    // easier to follow.
    function _assignState() {
      ctrl.error = null;
      ctrl.user = ctrl.auth.candidate;
      ctrl.emailCheckNeeded = !ctrl.emailCheck && !ctrl.auth.user;
      ctrl.registrationRequired = ctrl.auth.registrationRequired && !ctrl.emailCheckNeeded;

      if (ctrl.auth.invitation) {
        ctrl.nameRequested = !ctrl.auth.invitation.skippedRegistration && (!ctrl.auth.candidate.firstName || !ctrl.auth.candidate.lastName);
      } else {
        ctrl.nameRequested = ctrl.emailCheck && (ctrl.emailCheck.nameNeeded || !ctrl.emailCheck.exists);
      }
    }

    function _checkEmail() {
      if (ctrl.email) {
        return checkEmailService.check(ctrl.email).then(function (result) {
          ctrl.emailCheck = result;
          _assignState();
        });
      } else {
        return $q.when();
      }
    }

    function _register() {
      ctrl.disabled = true;
      ctrl.error = null;
      invitationsService.register({
        email: ctrl.email,
        firstName: ctrl.firstName,
        lastName: ctrl.lastName,
        assessmentId: ctrl.auth.assessment.id,
        accessToken: $stateParams.invite
      }).then(function (auth) {
        ctrl.registered = true;

        // update the existing auth with the one just returned
        Object.assign(ctrl.auth, auth);

        if (auth.emailVerificationRequired) {
          ctrl.registrationRequired = false;
        } else {
          accountService.setLoginAccount(auth.candidate, {
            showLoadingPanel: false
          });
          ctrl.onRegistered({ auth: auth });
        }
      }).catch(function (error) {
        ctrl.disabled = false;
        ctrl.error = error;
      });
    }
  }]);

  var webcamRestartTemplate = "<div class=\"webcam-start alert alert-warning\"> <h4>Webcam Required</h4> <p> To continue with the assessment, please turn on your webcam for identification purposes. </p> <p> By continuing, you agree to be recorded throughout the assessment. Please ensure your webcam is running and your face is visible at all times. </p> <p> You may refuse to turn on your webcam and discontinue the assessment. Information on your recorded data and privacy can be found <a href=\"https://www.andela.com/privacy-policy\" target=\"_blank\" rel=\"noopener\">here</a>. </p> <p> <button type=\"button\" class=\"btn-default btn-sm\" ng-click=\"$ctrl.startPhotoManager()\"> Start Webcam </button> </p> </div> ";

  var photoCaptureServiceModule = angular.module('Q.services.photo-capture-service', []).factory('photoCaptureService', ['$q', '$window', '$log', function ($q, $window, $log) {
    var OVERSIZE_SENTRY_THROTTLE_MS = 10 * 60 * 1000; // 10 minutes
    var lastOversizeSentryAt = 0;
    var lastOversizeSentryKey = null;

    var sendOversizeToSentry = function sendOversizeToSentry(message, data) {
      try {
        if (!window.Sentry || window.config && window.config.devMode) {
          return;
        }

        var now = Date.now();
        var keyParts = [message, data && data.maxSizeKB,
        // Bucket size so we don't create too many unique signatures
        data && data.estimatedSizeKB ? Math.round(data.estimatedSizeKB / 25) * 25 : null, data && data.canvasWidth, data && data.canvasHeight];
        var key = keyParts.join('|');

        // Rate-limit per signature.
        if (lastOversizeSentryKey === key && now - lastOversizeSentryAt < OVERSIZE_SENTRY_THROTTLE_MS) {
          return;
        }

        lastOversizeSentryKey = key;
        lastOversizeSentryAt = now;

        window.Sentry.captureMessage(message, {
          level: 'warning',
          tags: {
            feature: 'proctoring-images'
          },
          extra: data,
          fingerprint: ['proctoring-photo-oversize', String(data && data.maxSizeKB)]
        });
      } catch (e) {
        // never allow observability to break proctoring flow
      }
    };
    var build = function build(_ref) {
      var onEnd = _ref.onEnd;

      var makeVideoElement = function makeVideoElement() {
        var videoElement = angular.element('<video />')[0];
        videoElement.onerror = onEnd;
        return videoElement;
      };

      // This video element is public-facing and is used to show the
      // cam feed to the user. It may be destroyed on route changes
      // during normal app operation but can be re-created by the
      // client with newExternalVideo() as needed.
      // A destroyed video element manifests as a "frozen" frame.
      var externalVideo = void 0;

      // This video element is used for all internal operations, and
      // should not be exposed to the user so we can ensure that the
      // element exists for the duration of this service's operation.
      var internalVideo = void 0;

      var canvas = angular.element('<canvas />')[0];
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      var stream = void 0;

      var running = function running() {
        return !!stream;
      };

      var ensureRunning = function ensureRunning(fn) {
        return function () {
          if (running()) {
            return fn.apply(undefined, arguments);
          }

          throw Error('Photo capture service must be running');
        };
      };

      var newExternalVideo = function newExternalVideo() {
        if (!running()) {
          return $q.reject('Photo capture service must be running');
        }

        externalVideo = makeVideoElement();
        externalVideo.srcObject = stream;

        var p = externalVideo.play();
        // older browsers may not return a promise
        return p ? p.then(function () {
          return externalVideo;
        }) : $q.resolve(externalVideo);
      };

      var imageData = ensureRunning(function () {
        ctx.drawImage(internalVideo, 0, 0);
        return ctx.getImageData(0, 0, canvas.width, canvas.height);
      });

      var brightness = ensureRunning(function () {
        // https://stackoverflow.com/a/13763063/6243352
        var _imageData = imageData(),
            data = _imageData.data;

        var total = 0;

        for (var i = 0; i < data.length; i += 4) {
          var r = data[i];
          var g = data[i + 1];
          var b = data[i + 2];
          total += Math.floor((r + g + b) / 3);
        }

        return Math.floor(total / (canvas.width * canvas.height)) / 255;
      });

      var stop = function stop() {
        if (stream) {
          stream.getTracks().forEach(function (track) {
            return track.stop();
          });
          stream = null;
        }

        if (internalVideo) {
          internalVideo.srcObject = null;
        }

        if (externalVideo) {
          externalVideo.srcObject = null;
        }
      };

      var _startStream = function _startStream(stream_) {
        stream = stream_;
        stream.getVideoTracks().forEach(function (track) {
          return track.addEventListener('ended', function () {
            stop();

            if (angular.isFunction(onEnd)) {
              onEnd();
            }
          });
        });
        return $q(function (resolve, reject) {
          internalVideo = makeVideoElement();
          internalVideo.addEventListener('loadeddata', function () {
            if (internalVideo.readyState === internalVideo.HAVE_ENOUGH_DATA) {
              canvas.height = internalVideo.videoHeight;
              canvas.width = internalVideo.videoWidth;
              resolve();
            }
          });
          internalVideo.addEventListener('error', reject);
          internalVideo.srcObject = stream_;

          var p = internalVideo.play();
          // older browsers may not return a promise
          if (p) {
            p.catch(reject);
          }
        });
      };

      var start = function start() {
        stop();

        return $q(function (resolve, reject) {
          if (!$window.navigator.mediaDevices) {
            reject('window.navigator.mediaDevices is not supported by this browser');
            return;
          }

          $window.navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: 'user',
              width: 640,
              height: 480
            }
          }).then(_startStream).then(resolve).catch(reject);
        });
      };

      var takePhoto = ensureRunning(function () {
        var maxSizeKB = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 250;

        // Adaptive quality control to ensure images don't exceed size limit
        // NOTE: maxSizeKB is set to 50 for testing purposes - change to 250 for production
        var quality = 0.6;
        var dataURL = void 0;
        var attempts = 0;
        var estimatedSizeKB = void 0;
        var maxAttempts = 5;
        var minQuality = 0.3;

        do {
          ctx.drawImage(internalVideo, 0, 0);
          dataURL = canvas.toDataURL('image/jpeg', quality);

          // Calculate size: base64 string length * 0.75 ≈ actual bytes
          // (base64 encoding has ~33% overhead, so we divide by 1.33 or multiply by 0.75)
          estimatedSizeKB = dataURL.length * 0.75 / 1024;

          if (estimatedSizeKB <= maxSizeKB || quality <= minQuality) {
            if (quality <= minQuality && estimatedSizeKB > maxSizeKB) {
              var data = {
                estimatedSizeKB: Math.round(estimatedSizeKB),
                maxSizeKB: maxSizeKB,
                quality: quality,
                attempts: attempts,
                canvasWidth: canvas.width,
                canvasHeight: canvas.height,
                videoWidth: internalVideo ? internalVideo.videoWidth : null,
                videoHeight: internalVideo ? internalVideo.videoHeight : null
              };

              $log.warn('Proctoring photo exceeds size limit after compression', data);
              sendOversizeToSentry('Proctoring photo exceeds size limit after compression', data);
            }
            // Size is acceptable or we've reached minimum quality
            return dataURL;
          }

          // Reduce quality by 15% for next attempt
          quality = Math.max(minQuality, quality * 0.85);
          attempts++;
        } while (attempts < maxAttempts);

        if (estimatedSizeKB > maxSizeKB) {
          var _data = {
            estimatedSizeKB: Math.round(estimatedSizeKB),
            maxSizeKB: maxSizeKB,
            quality: quality,
            attempts: attempts,
            canvasWidth: canvas.width,
            canvasHeight: canvas.height,
            videoWidth: internalVideo ? internalVideo.videoWidth : null,
            videoHeight: internalVideo ? internalVideo.videoHeight : null
          };

          $log.warn('Proctoring photo exceeds size limit after maxAttempts', _data);
          sendOversizeToSentry('Proctoring photo exceeds size limit after maxAttempts', _data);
        }

        // Return whatever we have after max attempts
        return dataURL;
      });

      return {
        start: start,
        stop: stop,
        imageData: imageData,
        running: running,
        brightness: brightness,
        takePhoto: takePhoto,
        newExternalVideo: newExternalVideo
      };
    };
    return {
      build: build
    };
  }]);

  var photoStorageServiceModule = angular.module('Q.services.photo-storage-service', []).factory('photoStorageService', ['$http', '$log', function ($http, $log) {
    var uploadPhoto = function uploadPhoto(_ref) {
      var dataURL = _ref.dataURL,
          isBright = _ref.isBright,
          isDim = _ref.isDim,
          isStatic = _ref.isStatic,
          assessmentResultId = _ref.assessmentResultId,
          teamCandidateId = _ref.teamCandidateId;

      // The uuid separates two photos taken in the same millisecond, which used to share a
      // Cloudinary public_id and overwrite each other.
      var currentTimeInMiliseconds = Date.now();
      var customId = teamCandidateId + '_' + assessmentResultId + '_' + currentTimeInMiliseconds + '_' + uuid().slice(-8);

      // base64 overhead ~33%, so multiply by 0.75 to estimate actual bytes
      var estimatedSizeKB = dataURL.length * 0.75 / 1024;

      var proctoringHeaders = {
        'X-Proctoring-Custom-Id': customId,
        'X-Team-Candidate-Id': teamCandidateId,
        'X-Assessment-Result-Id': assessmentResultId,
        'X-Proctoring-Estimated-Kb': String(Math.round(estimatedSizeKB))
      };

      return $http.post(appConfig.proctoringImagesUploadRoute, { dataURL: dataURL, customId: customId }, { ignoreLoadingBar: true, headers: proctoringHeaders }).then(function (response) {
        var ok = response.status === 200;
        var errorPresent = response.data && response.data.error;

        if (!ok || errorPresent) {
          throw Error(response.data.error || response.statusText);
        }

        return response.data.cloudinary;
      }).then(function (cloudinaryData) {
        var data = {
          assessmentResultId: assessmentResultId,
          teamCandidateId: teamCandidateId,
          isBright: isBright,
          isDim: isDim,
          isStatic: isStatic,
          fileId: cloudinaryData.asset_id,
          fileFormat: 'jpg',
          url: cloudinaryData.url
        };
        return $http.post(appConfig.proctoringImagesRoute, { data: data }).then(function (response) {
          if (response.status !== 200) {
            throw Error(response.statusText);
          }

          return response.data;
        });
      }).catch(function (error) {
        var meta = {
          customId: customId,
          teamCandidateId: teamCandidateId,
          assessmentResultId: assessmentResultId,
          estimatedSizeKB: Math.round(estimatedSizeKB)
        };

        if (error && typeof error.status !== 'undefined') {
          meta.status = error.status;
        }
        if (error && error.data) {
          meta.responseData = error.data;
        }

        $log.error('Error in uploadPhoto:', meta, error);
        throw error;
      });
    };

    return {
      uploadPhoto: uploadPhoto
    };
  }]);

  // Webcams need a beat to calibrate brightness before the first usable frame.
  var CAMERA_WARMUP_MS = 1000;

  var photoManagerServiceModule = angular.module('Q.services.photo-manager-service', [photoCaptureServiceModule, photoStorageServiceModule]).factory('photoManagerService', ['photoCaptureService', 'photoStorageService', '$q', '$timeout', function (photoCaptureService, photoStorageService, $q, $timeout) {
    var WELCOME_PHOTO_RETRY_MS = 1000;

    // Take a photo in 4 to 9 minute intervals during the assessment
    var LOWER_BOUND_ASSESSMENT_PHOTO_MS = 4 * 60 * 1000;
    var UPPER_BOUND_ASSESSMENT_PHOTO_MS = 9 * 60 * 1000;

    // Flag brightness levels outside of the 20-80% range
    var LOWER_BOUND_BRIGHTNESS = 0.2;
    var UPPER_BOUND_BRIGHTNESS = 0.8;

    /**
     * Compares two image data objects to check if they are identical within a threshold.
     * @param {ImageData} a - The first image data object.
     * @param {ImageData} b - The second image data object.
     * @param {number} threshold - The maximum allowed number of differing pixels.
     * @returns {boolean} True if the images are considered identical, false otherwise.
     */
    var imagesEqual = function imagesEqual(a, b) {
      var threshold = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;

      if (!a || !b || a.width !== b.width || a.height !== b.height) {
        return false;
      }

      var differingPixels = 0;

      for (var i = 0; i < a.data.length; i++) {
        if (a.data[i] !== b.data[i] && ++differingPixels > threshold) {
          return false;
        }
      }

      return true;
    };

    var build = function build() {
      var _photosTaken = [];
      var lastPhotoData = void 0;
      var assessmentResultId = void 0;
      var teamCandidateId = void 0;
      var assessmentPhotoTimeout = void 0;
      var welcomePhotoRetryTimeout = void 0;
      var onError = void 0;
      var startPromise = void 0;

      var photoCapture = photoCaptureService.build({
        onEnd: function onEnd() {
          $timeout.cancel(welcomePhotoRetryTimeout);
          $timeout.cancel(assessmentPhotoTimeout);

          if (onError) {
            onError({
              message: 'Feed ended unexpectedly'
            });
          }
        }
      });

      // running() stays false for the whole getUserMedia window, so the Start Webcam button is
      // still showing while a reload's resume is coming up. A second press would restart the
      // camera - leaking the first stream, since stop() has no stream to release yet - and
      // reschedule the capture loop past the resumed session's first photo.
      var start = function start() {
        var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

        assessmentResultId = opts.assessmentResultId || assessmentResultId;
        teamCandidateId = opts.teamCandidateId || teamCandidateId;

        if (startPromise) {
          return startPromise;
        }

        if (photoCapture.running()) {
          return $q.when();
        }

        startPromise = photoCapture.start().finally(function () {
          startPromise = null;
        });

        return startPromise;
      };

      var takePhoto = function takePhoto() {
        if (!assessmentResultId) {
          var msg = 'Proctoring Manager: no assessmentResultId was set when attempting to take a photo';
          return $q.reject(msg);
        }

        if (!teamCandidateId) {
          var _msg = 'Proctoring Manager: no teamCandidateId was set when attempting to take a photo';
          return $q.reject(_msg);
        }

        var newPhotoData = photoCapture.imageData();
        var isStatic = !!lastPhotoData && imagesEqual(lastPhotoData, newPhotoData, 100);
        lastPhotoData = newPhotoData;

        var brightness = void 0;
        try {
          brightness = photoCapture.brightness();
        } catch (err) {
          return $q.reject(err);
        }

        return photoStorageService.uploadPhoto({
          dataURL: photoCapture.takePhoto(),
          assessmentResultId: assessmentResultId,
          teamCandidateId: teamCandidateId,
          isStatic: isStatic,
          isDim: brightness < LOWER_BOUND_BRIGHTNESS,
          isBright: brightness > UPPER_BOUND_BRIGHTNESS
        }).then(function (photoFileData) {
          _photosTaken.push(photoFileData);
          return $q.when(photoFileData);
        });
      };

      // Retries until a photo sticks, and never rejects - so nothing can be sequenced after it.
      var takeWelcomePhoto = function takeWelcomePhoto() {
        $timeout.cancel(welcomePhotoRetryTimeout);
        var tryTakingPhotoUntilOK = function tryTakingPhotoUntilOK(resolve) {
          takePhoto().then(resolve).catch(function (err) {
            if (onError) {
              onError(err);
            }

            welcomePhotoRetryTimeout = $timeout(function () {
              return tryTakingPhotoUntilOK(resolve);
            }, WELCOME_PHOTO_RETRY_MS);
          });
        };
        return $q(tryTakingPhotoUntilOK);
      };

      var nextPhotoDelayMs = function nextPhotoDelayMs() {
        return Math.random() * (UPPER_BOUND_ASSESSMENT_PHOTO_MS - LOWER_BOUND_ASSESSMENT_PHOTO_MS) + LOWER_BOUND_ASSESSMENT_PHOTO_MS;
      };

      // The first photo is an interval away because takeWelcomePhoto normally covers the start
      // of the session, and two captures in one beat upload near-identical frames. A resumed
      // session has no welcome photo, so it asks for one up front.
      var takeAssessmentPhotos = function takeAssessmentPhotos() {
        var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
            _ref$coversSessionSta = _ref.coversSessionStart,
            coversSessionStart = _ref$coversSessionSta === undefined ? false : _ref$coversSessionSta;

        $timeout.cancel(assessmentPhotoTimeout);

        var firstPhotoMs = coversSessionStart ? CAMERA_WARMUP_MS : nextPhotoDelayMs();

        assessmentPhotoTimeout = $timeout(function () {
          takePhoto().catch(function (err) {
            if (onError) {
              onError(err);
            }
          });
          takeAssessmentPhotos();
        }, firstPhotoMs);
      };

      return {
        start: start,
        takeWelcomePhoto: takeWelcomePhoto,
        takeAssessmentPhotos: takeAssessmentPhotos,
        running: function running() {
          return photoCapture.running();
        },
        starting: function starting() {
          return !!startPromise;
        },
        photosTaken: function photosTaken() {
          return _photosTaken;
        },
        // Stored, not merely attempted - the start button gates on this.
        hasStoredPhoto: function hasStoredPhoto() {
          return _photosTaken.length > 0;
        },
        setErrorHandler: function setErrorHandler(errorHandlerFn) {
          onError = errorHandlerFn;
        },
        stop: function stop() {
          $timeout.cancel(welcomePhotoRetryTimeout);
          $timeout.cancel(assessmentPhotoTimeout);
          photoCapture.stop();
        },
        newExternalVideo: function newExternalVideo() {
          return photoCapture.newExternalVideo();
        }
      };
    };

    return { build: build };
  }]);

  var welcomeTemplate = "<article class=\"assessments-welcome\" ng-hide=\"redirectingToPractice\" ng-class=\"{ 'has-code-challenges': assessments.hasCodeChallenges, 'ignore-all-issues': process.ignoreAllIssues }\"> <div class=\"assessments-welcome-layout\" ng-hide=\"assessments.process.embedOptions.hideWelcome\" ng-if=\"assessmentPublished && !process.invitationIssue && hasCredits\"> <div class=\"assessments-welcome-body\"> <div class=\"company-logo\" ng-if=\"assessments.assessment.team.logoImg\"> <uploaded-image file=\"assessments.assessment.team.logoImg\" width=\"300\" hardcode-size=\"false\" tooltip=\"{{ assessments.assessment.team.name }}\" tooltip-position=\"right\"></uploaded-image> </div> <h2> {{ assessments.assessment.team.name }} {{\n        assessments.assessment.nounLower }} {{ assessments.isTeamMember() ?\n        'preview' : '' }} </h2> <p> Please complete the following challenge{{\n        assessments.process.challengeItems.length > 1 ? 's' : '' }} to the best of your ability. </p> <div class=\"alert alert-danger ie-warning\"> <h4>Warning: Outdated Browser Detected</h4> <p> This {{ assessments.assessment.nounLower }} requires a modern browser to ensure you can provide your best work. </p> <p> Please choose any modern browser before continuing, such as <a class=\"alert-link\" href=\"https://www.google.com/chrome/index.html\">Chrome</a>, <a class=\"alert-link\" href=\"https://www.mozilla.org/en-US/firefox/new/\">Firefox</a>, <a class=\"alert-link\" href=\"https://www.apple.com/safari/\">Safari</a>, or <a class=\"alert-link\" href=\"https://www.microsoft.com/en-us/windows/microsoft-edge\">Edge</a>. </p> </div> <div class=\"alert alert-warning small-screen-warning\"> <h4>Warning: Small Screen Detected</h4> <p> This {{ assessments.assessment.nounLower }} is not designed for mobile or other small devices. </p> <p> You may find it difficult to navigate through the tasks. If possible, make the browser bigger, or we recommend switching to a larger device. </p> </div> <div class=\"assessment-details\"> <h3 ng-if=\"assessments.assessment.showTitle\"> {{ assessments.assessment.title }} </h3> <div markdown=\"assessments.assessment.summary\" ng-if=\"assessments.assessment.summary\"></div> <ul> <li class=\"challenges\"> <h4> {{ process.challengeCount }} Challenge{{\n              process.challengeCount===1 ? '' : 's' }} </h4> <p> This is the total number of challenges in the {{\n              assessments.assessment.nounLower }} represented and navigable by the <a class=\"preview-timer\" ng-click=\"process.highlight('stage')\">dots on the left-hand sidebar</a>. You must attempt each challenge <a class=\"preview-timer\" ng-click=\"process.highlight('submit')\">before submitting</a>. </p> <div ng-if=\"langsFull.length || langsPartial.length\" class=\"available-languages\" ng-class=\"{ 'has-full-and-partial': langsPartial.length && langsFull.length }\"> <p> The following languages may be used in this {{\n                assessments.assessment.nounLower }}: </p> <div class=\"available-languages-list\"> <div ng-if=\"langsFull.length\"> <div class=\"help-block\" ng-if=\"langsPartial.length\"> On All Challenges </div> <div class=\"icon-wrapper\"> <span ng-repeat=\"lang in langsFull\" class=\"lang icon-lang-{{lang.id}}\" tooltip=\"{{lang.name}}\" tooltip-delay=\"0\"></span> </div> </div> <div ng-if=\"langsPartial.length\"> <div class=\"help-block\" tooltip=\"Some challenges may require using different languages for your solution.\" ng-if=\"langsFull.length\"> Only On Some Challenges <tooltip-help-icon></tooltip-help-icon> </div> <div class=\"icon-wrapper\"> <span ng-repeat=\"lang in langsPartial\" class=\"lang icon-lang-{{lang.id}}\" tooltip=\"{{lang.name}}\" tooltip-delay=\"0\"></span> </div> </div> </div> <div class=\"available-languages-partial-notice help-block\" tooltip=\"Some challenges will require using different languages for your solution.\" ng-show=\"langsPartial.length && !langsFull.length\"> Not every language is available on all challenges <tooltip-help-icon></tooltip-help-icon> </div> </div> </li> <li class=\"time\" ng-show=\"process.estimatedTime && !process.enforcedTimeLimit\"> <h4> Estimated {{ process.estimatedTime | durationFormat:'minutes' }} </h4> <p> This is the estimated amount of time that this {{\n              assessments.assessment.nounLower }} should take. However, you can take more time if you need it, and you can always check the timer in the <a class=\"preview-timer\" ng-click=\"process.highlight('timer')\">bottom left corner</a> to see how you're doing. </p> <p ng-show=\"process.hasTimedChallenges\"> <strong>Note that some of the challenges have their own time limit.</strong> You will be notified before beginning any timed challenge, and you can pause between challenges if you need. </p> </li> <li class=\"time time-limit\" ng-show=\"process.enforcedTimeLimit\"> <h4> {{ (process.timer.timeAllotted ||\n              process.assessment.enforcedTimeLimit * 60) | durationFormat :\n              'seconds' }} Time Limit </h4> <p> This is the amount of time you are allotted to take this full {{\n              assessments.assessment.nounLower }}. The timer begins when you start your first challenge and will not stop until the {{assessments.assessment.nounLower}} has been submitted or the limit is reached. After which point the system will lock the {{assessments.assessment.nounLower}} from any further changes. </p> <p> You can view the time remaining in the <a class=\"preview-timer\" ng-click=\"process.highlight('timer')\">bottom left hand corner</a> and it will change color to indicate time is running out. You can also toggle to see time remaining. </p> <p ng-show=\"process.hasTimedChallenges\"> <strong>Some of the challenges have their own time limit as well.</strong> You will be notified before beginning any timed challenge, and you can pause between challenges if you need, but the overall clock will continue to count down. </p> </li> <li class=\"voice-to-text\" ng-if=\"hasVoiceToTextQuestions\"> <h4>Voice Input Required</h4> <p class=\"voice-text-description\"> This assessment contains questions that require voice input. Please verify that your microphone is working before starting the assessment. </p> <ul class=\"voice-requirements\"> <li> <i class=\"icon-check text-success\" ng-if=\"microphoneWorks\"></i> <i class=\"icon-times text-danger\" ng-if=\"microphoneWorks === false\"></i> <i class=\"icon-question text-warning\" ng-if=\"microphoneWorks === null\"></i> Your microphone is working <button ng-if=\"microphoneWorks === null\" class=\"btn btn-primary\" ng-click=\"testMicrophone()\"> Test Microphone </button> <button ng-if=\"microphoneWorks !== null\" class=\"btn btn-default\" ng-click=\"resetMicrophoneTest()\"> Test Again </button> </li> </ul> <div class=\"alert alert-danger\" ng-if=\"microphoneError\"> <strong>Microphone Error:</strong> {{ microphoneError }} </div> </li> </ul> </div> </div> <div class=\"assessments-user-info well has-issues\"> <h3>Browser Issues Detected</h3> <p> Please fix the browser issues noted <span class=\"small-screen\">above</span> <span class=\"not-small-screen\">to the left</span> before continuing. </p> <p> You can continue anyway, but you may experience difficulties during the assessment. </p> <p> <a class=\"btn-default btn-sm\" ng-click=\"process.ignoreAllIssues = true\">Continue the {{ assessments.assessment.nounUpper }} Anyway</a> </p> </div> <div class=\"assessments-user-info well\"> <div class=\"assessments-welcome-login\" ng-if=\"userInfo.needsRegistration\"> <h3>Registration</h3> <register-account auth=\"process.auth\"></register-account> </div> <div class=\"assessments-welcome-post-login candidate start-locked\" ng-if=\"userInfo.accessLocked && !userInfo.accessPending && !userInfo.accessClosed\"> <welcome-header user-info=\"userInfo\"></welcome-header> <p class=\"waiting-notice\" ng-show=\"process.assessmentResult.accessLocked\"> We're almost ready to start your {{assessments.assessment.nounLower}}.<br> We're just waiting on someone from {{ assessments.assessment.team.name\n          }} to unlock the {{ assessments.assessment.nounLower }}. </p> <p class=\"start-unlocked\" ng-hide=\"process.assessmentResult.accessLocked\"> OK, all set! Click the button below to begin! </p> <div class=\"continue-buttons\"> <button class=\"btn-primary\" ng-click=\"process.next()\" ng-disabled=\"process.assessmentResult.accessLocked || process.significantTimeDelay || !canStartAssessment()\"> Start the {{ assessments.assessment.nounUpper }} </button> <p class=\"help-block\" ng-if=\"hasVoiceToTextQuestions && !canStartAssessment()\"> <i class=\"icon-warning text-warning\"></i> Please verify that your microphone is working before starting the assessment. </p> </div> <p class=\"help-block\" ng-show=\"process.assessmentResult.accessLocked\"> While you are waiting, feel free to <a class=\"link\" ui-sref=\"assessments.welcome(practiceParams)\" ui-sref-opts=\"{ reload: true }\"> check out the practice assessment. </a> </p> </div> <div class=\"assessments-welcome-post-login candidate start-locked\" ng-if=\"userInfo.accessPending && !userInfo.accessClosed\"> <welcome-header user-info=\"userInfo\"></welcome-header> <p class=\"waiting-notice\"> Access to start this {{assessments.assessment.nounLower}} is not available yet.<br> You will be able to begin at <strong>{{process.assessmentResult.accessStartsAt | date:'MMMM d, y @ h:mm\n            a'}}</strong>. Please refresh this page at that time. </p> <p ng-show=\"userInfo.willBeReminded\"> An email reminder will be sent to you on {{process.auth.invitation.reminderAt | date: 'longDate'}}. </p> <p class=\"help-block\"> While you are waiting, feel free to <a class=\"link\" ui-sref=\"assessments.welcome(practiceParams)\" ui-sref-opts=\"{ reload: true }\"> check out the practice assessment. </a> </p> </div> <div class=\"assessments-welcome-post-login candidate start-locked\" ng-if=\"userInfo.accessClosed\"> <welcome-header user-info=\"userInfo\"></welcome-header> <p class=\"waiting-notice\"> Access to take this {{assessments.assessment.nounLower}} ended on <strong>{{process.assessmentResult.accessEndsAt | date:'MMMM d, y @ h:mm\n            a'}}</strong>.<br> Please contact your {{assessments.assessment.nounLower}} administrator if you have any questions. </p> </div> <div class=\"assessments-welcome-post-login\" ng-if=\"userInfo.readyForCleanStart\"> <welcome-header user-info=\"userInfo\"></welcome-header> <div ng-show=\"assessments.isTeamMember()\"> <p> This is just a preview of your {{ assessments.assessment.nounLower\n            }}.<br>It does not count toward your usage. </p> <p> Once you've completed this {{ assessments.assessment.nounLower }}, you will be able to review the complete results. </p> </div> <div ng-hide=\"assessments.isTeamMember()\"> <p>Once you are ready, let's begin!</p> </div> <div class=\"alert alert-warning\" ng-show=\"process.assessmentResult.accessEndsAt\"> <h4>Limited Time Access!</h4> <p> Please note that this {{ assessments.assessment.nounUpper }} must be submitted no later <br> than by <strong>{{process.assessmentResult.accessEndsAt | date:'MMMM d, y @ h:mm\n              a'}}</strong>. </p> <p ng-show=\"process.assessment.enforcedTimeLimit\"> Once you start this {{ assessments.assessment.nounLower }}, you must complete it within <strong>{{process.assessment.enforcedTimeLimit}} minutes</strong>, or by the date & time shown above (whichever comes sooner). </p> </div> <webcam-start process=\"process\" photo-manager=\"photoManager\" on-error-while-photo-manager-starts=\"handleErrorWhilePhotoManagerStarts()\" on-photo-manager-started=\"handlePhotoManagerStart()\"></webcam-start> <div class=\"returning-buttons\"> <button type=\"button\" class=\"btn-primary\" ng-click=\"resumeAssessment(true)\" ng-disabled=\"process.loadingAssessmentResult || process.significantTimeDelay || !proctoringSatisfied() || !canStartAssessment()\"> Start the {{ assessments.assessment.nounUpper }} </button> </div> </div> <div class=\"assessments-welcome-post-login candidate\" ng-if=\"userInfo.readyForContinuedStart && !assessments.isTeamMember()\"> <welcome-header greeting=\"Welcome Back\" user-info=\"userInfo\"></welcome-header> <p> Once you are ready, you may continue the {{\n          assessments.assessment.nounLower }}. </p> <div class=\"alert alert-warning\" ng-if=\"process.assessmentResult.accessEndsAt\"> <h4>Limited Time Access!</h4> <p> Please note that this {{ assessments.assessment.nounUpper }} must be submitted no later <br> than by <strong>{{process.assessmentResult.accessEndsAt | date:'MMMM d, y @ h:mm\n              a'}}</strong>. </p> </div> <webcam-start process=\"process\" photo-manager=\"photoManager\" on-error-while-photo-manager-starts=\"handleErrorWhilePhotoManagerStarts()\" on-photo-manager-started=\"handlePhotoManagerStart()\"></webcam-start> <div class=\"continue-buttons\"> <button class=\"btn-primary\" ng-disabled=\"process.significantTimeDelay || !proctoringSatisfied()\" ng-click=\"resumeAssessment()\"> Continue the {{ assessments.assessment.nounUpper }} </button> </div> </div> <div class=\"assessments-welcome-post-login team-member\" ng-if=\"(userInfo.readyForContinuedStart || userInfo.currentlyTakingAssessment) && assessments.isTeamMember()\"> <welcome-header greeting=\"Hello Again\" user-info=\"userInfo\"></welcome-header> <p> As a reminder, this is just a preview of your {{\n          assessments.assessment.nounLower }}.<br>It does not count as a candidate {{ assessments.assessment.nounLower }}. </p> <p> Once you've completed this {{ assessments.assessment.nounLower }}, you will be able to review the complete results. </p> <div class=\"alert alert-danger\" ng-if=\"!process.assessmentResult.isSnapshotCurrent()\"> <p> This {{ assessments.assessment.nounLower }} is outdated from the published version.<br> Would you like to reset it before continuing? </p> <p> <button class=\"btn-danger\" ng-disabled=\"process.significantTimeDelay\" ng-click=\"assessments.restart()\"> Reset Your {{ assessments.assessment.nounUpper }}… </button> </p> </div> <div class=\"alert alert-info\" ng-if=\"process.assessmentResult.solutions.length && process.assessmentResult.isSnapshotCurrent()\"> <p> You can reset your {{ assessments.assessment.nounLower }} to take it from the beginning,<br> including removing all of the existing solutions. </p> <p> <button class=\"btn-info btn-sm\" ng-disabled=\"process.significantTimeDelay\" ng-click=\"assessments.restart()\"> Reset Your {{ assessments.assessment.nounUpper }}… </button> </p> </div> <webcam-start process=\"process\" photo-manager=\"photoManager\" on-error-while-photo-manager-starts=\"handleErrorWhilePhotoManagerStarts()\" on-photo-manager-started=\"handlePhotoManagerStart()\"></webcam-start> <div class=\"continue-buttons\"> <button class=\"btn-{{ process.assessmentResult.isSnapshotCurrent() ? 'primary' : 'default' }}\" ng-disabled=\"process.significantTimeDelay || !proctoringSatisfied()\" ng-click=\"resumeAssessment()\"> Continue the {{ assessments.assessment.nounUpper }} </button> </div> </div> <div class=\"assessments-welcome-post-login candidate\" ng-if=\"userInfo.currentlyTakingAssessment && !assessments.isTeamMember()\"> <welcome-header greeting=\"Keep it Up\" user-info=\"userInfo\"></welcome-header> <p> Click the button below to continue the {{\n          assessments.assessment.nounLower }}. </p> <webcam-start process=\"process\" photo-manager=\"photoManager\" on-error-while-photo-manager-starts=\"handleErrorWhilePhotoManagerStarts()\" on-photo-manager-started=\"handlePhotoManagerStart()\"></webcam-start> <div class=\"continue-buttons\"> <button class=\"btn-primary\" ng-disabled=\"process.significantTimeDelay || !proctoringSatisfied()\" ng-click=\"resumeAssessment()\"> Continue the {{ assessments.assessment.nounUpper }} </button> </div> </div> <div class=\"assessments-welcome-post-login candidate\" ng-if=\"!userInfo.needsRegistration && !process.assessmentResult\"> <h3>Hmm, something went wrong :(</h3> <p ng-if=\"!process.auth.denyReason\"> There seems to have been an issue logging you into your {{\n          assessments.assessment.nounLower }}. Try refreshing the site. If you're still having problems, please <a href=\"mailto:team@qualified.io?subject=Unable%20to%20access%20assessment&body=I%20am%20not%20able%20to%20access%20this%20assessment%3A%20{{assessments.assessment.id}}\" target=\"_blank\"> contact Qualified Support </a>. </p> <div ng-if=\"process.auth.denyReason\"> <p> There is an issue authorizing you for this {{\n            assessments.assessment.nounLower }}. </p> <p><strong>Reason Given:</strong> {{ process.auth.denyReason }}</p> <p class=\"contact-qualified\"> Please reach out to your contact at <strong>{{ assessments.assessment.team.name }}</strong> to troubleshoot. If you have just received a new invitation and the link in the email is still not working, please <a href=\"mailto:team@qualified.io?subject=Unable%20to%20access%20assessment&body=I%20am%20not%20able%20to%20access%20this%20assessment%3A%20{{assessments.assessment.id}}\" target=\"_blank\"> contact Qualified Support </a>. </p> </div> </div> <div class=\"help-block team-support-link\" ng-if=\"assessments.assessment.team.supportLink\"> If you need help during your {{ assessments.assessment.nounLower }}, <a ng-href=\"{{assessments.assessment.team.supportLink}}\"> please contact {{assessments.assessment.team.name}} here. </a> </div> <div class=\"practice-assessment\" ng-show=\"!assessments.isPractice() && assessments.assessment.showPracticeAssessment && !assessments.process.isEmbedded && !!assessments.process.auth.candidate\"> <h4>New to using Qualified?</h4> <p> Before you start you can get some practice so that you are used to the interface. You will be given a sample {{\n          assessments.assessment.nounLower }} which walks you through the basics. </p> <p> <a class=\"btn-default\" ui-sref=\"assessments.welcome(practiceParams)\" ui-sref-opts=\"{ reload: true, inherit: false }\"> Practice </a> </p> </div> </div> </div> <div ng-if=\"!hasCredits\" class=\"assessment-unavailable out-of-credits\"> <h2>{{ assessments.assessment.nounUpper }} Unavailable</h2> <p> This {{ assessments.assessment.nounLower }} is currently unavailable due to its configuration. </p> <p> An email has been sent to <strong>{{ assessments.assessment.team.name }}</strong> to let them know. </p> </div> <div ng-if=\"hasCredits && assessmentPublished && process.invitationIssue\" class=\"assessment-unavailable invite-issue\"> <h2> There is an issue with your {{ assessments.assessment.nounUpper }} invitation </h2> <p> The {{ assessments.assessment.nounLower }} <em>{{ assessments.assessment.title }}</em> is only available with an active invitation. </p> <p ng-if=\"process.auth.denyReason\"> <strong>Reason Given:</strong> {{ process.auth.denyReason }} </p> <p class=\"contact-qualified\"> Please check if you have a more recent invitation email, or reach out to your contact at <strong>{{ assessments.assessment.team.name }}</strong> to receive a new invitation. </p> </div> <div ng-if=\"hasCredits && !assessmentPublished\" class=\"assessment-unavailable unpublished\"> <h2>{{ assessments.assessment.nounUpper }} Unavailable</h2> <p> The {{ assessments.assessment.nounLower }} <em>{{ assessments.assessment.title }}</em> <span ng-if=\"assessments.assessment.isArchived()\">is no longer available for testing.</span> <span ng-if=\"assessments.assessment.isDraft()\">is not currently available.</span> </p> <p class=\"contact-team\"> If you believe you have reached this message in error, please reach out to your contact at <strong>{{ assessments.assessment.team.name }}</strong> for help on receiving an invitation. </p> </div> <div class=\"footer legal-links\" ng-show=\"!assessments.process.isEmbedded\"> <legal-links hide-contact=\"true\"></legal-links> </div> </article> ";

  var resetAssessmentDialogTemplate = "<div class=\"reset-assessment-dialog\"> <div class=\"modal-header\"> <h4 class=\"modal-title\">Reset Assessment</h4> <button class=\"close\" aria-label=\"Close\" ng-click=\"$ctrl.cancel()\"> <span aria-hidden=\"true\">&times;</span> </button> </div> <div class=\"modal-body modal-long-form\"> <section ng-if=\"!$ctrl.processing\"> <div ng-if=\"$ctrl.fullResetOnly\"> <p> Would you like to completely reset this assessment, including removing all existing solutions? </p> <p>Note, this action cannot be undone!</p> </div> <div ng-if=\"$ctrl.autoOpened\"> <p> Looks like this assessment has been updated since you last previewed it. </p> <p> Would you like to reset the assessment and your solutions before continuing? </p> </div> <div ng-if=\"!$ctrl.autoOpened && !$ctrl.fullResetOnly\"> <p>How would you like to reset this assessment?</p> </div> <ul class=\"action-links\"> <li ng-if=\"$ctrl.isSubmitted || $ctrl.autoOpened\"> <a ng-click=\"$ctrl.continue()\" tabindex=\"0\"> Continue where you left off, without updating challenges or solutions. </a> </li> <li ng-if=\"$ctrl.isOutdated\"> <a ng-click=\"$ctrl.reset(false)\" tabindex=\"0\"> Reset to the newest version of the assessment, but keep existing solutions when possible. </a> </li> <li> <a ng-click=\"$ctrl.reset(true)\" tabindex=\"0\"> Completely start over on this assessment, including removing all existing solutions. </a> </li> <li class=\"cancel\"> <a ng-click=\"$ctrl.cancel()\" tabindex=\"0\"> <strong>Cancel</strong>: Do not reset anything. </a> </li> </ul> </section> <section ng-if=\"$ctrl.processing\" class=\"processing-animation\"> <p><span class=\"loading-spinner loading-spinner-large\"></span></p> <p>Please wait...</p> </section> </div> </div> ";

  var resetAssessmentComponentModule = angular.module('Q.components.reset-assessment-dialog', [modalComponentModule, appAlertComponentModule]).factory('resetAssessmentDialog', ['$modal', function resetAssessmentDialog($modal) {
    return {
      open: function open(_assessmentResultProcess, _autoOpened) {
        return $modal.openOnce('resetAssessmentDialog', {
          template: resetAssessmentDialogTemplate,
          controller: 'ResetAssessmentDialogCtrl as $ctrl',
          resolve: {
            assessmentResultProcess: function assessmentResultProcess() {
              return _assessmentResultProcess;
            },
            autoOpened: function autoOpened() {
              return _autoOpened;
            }
          }
        }).result;
      }
    };
  }]).controller('ResetAssessmentDialogCtrl', ['$modalInstance', 'assessmentResultProcess', 'autoOpened', 'appAlert', '$document', '$window', '$state', function ResetAssessmentDialogCtrl($modalInstance, assessmentResultProcess, autoOpened, appAlert, $document, $window, $state) {
    var ctrl = this;

    ctrl.cancel = function () {
      return $modalInstance.dismiss();
    };

    ctrl.isSubmitted = assessmentResultProcess.isFinished();
    ctrl.isOutdated = !assessmentResultProcess.assessmentResult.isSnapshotCurrent();
    ctrl.autoOpened = autoOpened;

    ctrl.fullResetOnly = !(ctrl.isSubmitted || ctrl.isOutdated || ctrl.autoOpened);

    var toggleProcessing = function toggleProcessing(processing) {
      ctrl.processing = processing;
      $document.querySelector('#app').css('opacity', processing ? 0 : null);
    };

    var catchResetError = function catchResetError(resp) {
      toggleProcessing(false);
      ctrl.cancel();
      return appAlert.modelError(resp);
    };

    var completeReset = function completeReset() {
      // forces a reload back to welcome
      $window.location.href = $state.href('assessments.welcome');
    };

    ctrl.continue = function () {
      if (ctrl.processing) return;
      if (ctrl.isSubmitted) {
        toggleProcessing(true);
        assessmentResultProcess.restart().catch(catchResetError).then(completeReset);
      } else {
        ctrl.cancel();
      }
    };

    ctrl.reset = function (resetAll) {
      if (ctrl.processing) return;
      toggleProcessing(true);
      assessmentResultProcess.reset(resetAll).catch(catchResetError).then(completeReset);
    };
  }]);

  var webcamStartTemplate = "<div class=\"webcam-start alert alert-warning\" ng-if=\"$ctrl.showProctoring()\"> <h4>Webcam Required</h4> <p> To continue with the assessment, please turn on your webcam for identification purposes. </p> <p> By continuing, you agree to be recorded throughout the assessment. Please ensure your webcam is running and your face is visible at all times. </p> <p> You may refuse to turn on your webcam and discontinue the assessment. Information on your recorded data and privacy can be found <a href=\"https://www.andela.com/privacy-policy\" target=\"_blank\" rel=\"noopener\">here</a>. </p> <p> <button type=\"button\" class=\"btn-default btn-sm\" ng-show=\"!$ctrl.photoManager.running() && !$ctrl.photoManager.starting()\" ng-click=\"$ctrl.startPhotoManager()\"> Start Webcam </button> <button type=\"button\" class=\"btn-default btn-sm\" ng-show=\"$ctrl.photoManager.running()\" ng-disabled=\"!$ctrl.photoManager.running()\" ng-click=\"$ctrl.toggleWebcamVisibility()\"> {{ $ctrl.webcamVisible ? \"Hide\" : \"Show\" }} Webcam Feed </button> <span class=\"webcam-feed-container\" ng-show=\"$ctrl.photoManager.running() && $ctrl.webcamVisible\"> </span> </p> </div> ";

  var findPhotoManagerErrorMessage = function findPhotoManagerErrorMessage(err) {
    if (err.statusText || err.message) {
      return err.statusText || err.message;
    } else if (err) {
      return err;
    }
    return 'Unknown Photo Manager error';
  };

  var webcamStartComponentModule = angular.module('Q.components.webcam-start', [appAlertComponentModule]).component('webcamStart', {
    bindings: {
      process: '=',
      photoManager: '=',
      onErrorWhilePhotoManagerStarts: '&',
      onPhotoManagerStarted: '&'
    },
    template: webcamStartTemplate,
    controller: 'WebcamStartCtrl',
    controllerAs: '$ctrl'
  }).controller('WebcamStartCtrl', ['$element', '$log', '$modal', '$timeout', 'appAlert', function WebcamStartCtrl($element, $log, $modal, $timeout, appAlert) {
    var ctrl = this;

    ctrl.showProctoring = function () {
      return ctrl.process.assessment.proctoringEnabled() && !ctrl.process.assessmentResult.proctoringDisabled;
    };

    var addProctoringVideo = function addProctoringVideo() {
      ctrl.photoManager.newExternalVideo().then(function (video) {
        var container = $element.querySelector('.webcam-feed-container');
        container.empty();
        container.append(video);
      }).catch(function (err) {
        $log.error(err);
        appAlert.error('Photo Manager: Webcam preview failed');
      });
    };

    ctrl.webcamVisible = false;
    ctrl.toggleWebcamVisibility = function () {
      ctrl.webcamVisible = !ctrl.webcamVisible;

      if (ctrl.webcamVisible) {
        addProctoringVideo();
      }
    };

    var handlePhotoManagerError = function handlePhotoManagerError() {
      ctrl.webcamVisible = false;
      ctrl.onErrorWhilePhotoManagerStarts();
    };

    ctrl.startPhotoManager = function () {
      var result = ctrl.process.assessmentResult;

      if (!result) {
        $log.error('process.assessmentResult was undefined starting photoManager');
        appAlert.error('Unable to start webcam; please try again');
        return;
      }

      ctrl.photoManager.start({
        assessmentResultId: result.id,
        teamCandidateId: result.teamCandidateId
      })
      // Start is gated on a stored photo, so capture cannot wait for the candidate to press it.
      .then(function () {
        return ctrl.onPhotoManagerStarted();
      }).catch(handlePhotoManagerError);
    };
  }]).controller('WebcamRestartCtrl', ['$log', '$modalInstance', '$timeout', 'appAlert', 'photoManager', function WebcamRestartCtrl($log, $modalInstance, $timeout, appAlert, photoManager) {
    var ctrl = this;
    ctrl.photoManager = photoManager;

    var handlePhotoManagerError = function handlePhotoManagerError(err) {
      var msg = findPhotoManagerErrorMessage(err);

      if (typeof msg === 'string') {
        $log.error('Webcam error: ' + msg);
        appAlert.error('Webcam error: ' + msg);
      } else {
        $log.error('Webcam error', msg);
        appAlert.error('Webcam error: unknown error');
      }
    };

    var handlePhotoManagerStart = function handlePhotoManagerStart() {
      $timeout(function () {
        if (!photoManager.running()) {
          return;
        }

        photoManager.takeAssessmentPhotos();

        photoManager.takeWelcomePhoto().then(function () {
          if (!photoManager.running()) {
            return;
          }

          appAlert.clear();
          appAlert.success('Webcam ready');
          $modalInstance.dismiss();
        });
      }, CAMERA_WARMUP_MS);
    };

    ctrl.startPhotoManager = function () {
      photoManager.start().then(handlePhotoManagerStart).catch(handlePhotoManagerError);
    };
  }]);

  var findPhotoManagerErrorMessage$1 = function findPhotoManagerErrorMessage(err) {
    if (err.statusText || err.message) {
      return err.statusText || err.message;
    } else if (err) {
      return err;
    }
    return 'Unknown Photo Manager error';
  };
  var welcomeRouteModule = angular.module('Q.routes.assessments.welcome', [uiRouterStateModule, durationFormatFilterModule, invitationsServiceModule, accountServiceModule, registerAccountComponentModule, legalLinksComponentModule, resetAssessmentComponentModule, dialogComponentModule, trackerServiceModule, durationFormatFilterModule, webcamStartComponentModule, challengeItemModelModule, codemirrorWorkspaceServiceModule]).component('welcomeHeader', {
    bindings: {
      greeting: '@',
      userInfo: '<'
    },
    template: '<h3>\n{{$ctrl.greeting || \'Hello\'}}{{ $ctrl.userInfo.welcomeName }}!\n<button ng-if="$ctrl.userInfo.welcomeName" ng-click="$ctrl.userInfo.edit()" class="btn-default btn-sm btn-hover-only edit" tooltip="Edit Your Profile"></button>\n</h3>'
  }).config(['$stateProvider', function ($stateProvider) {
    var didShowReset = false;

    $stateProvider.state('assessments.welcome', {
      url: '?activate&email&firstName&lastName&autoRegister&practice',
      template: welcomeTemplate,
      data: {
        bodyClass: 'assessments welcome',
        label: 'Assessment Welcome'
      },
      controller: ['$scope', '$rootScope', 'invitationsService', 'process', 'assessmentResultAuth', 'assessment', 'accountService', '$state', '$stateParams', 'resetAssessmentDialog', '$timeout', 'config', 'dialogService', '$window', 'tracker', 'durationFormatFilter', 'photoManager', 'proctoringSatisfied', '$log', 'appAlert', '$modal', 'codemirrorWorkspaceService', function welcomeController($scope, $rootScope, invitationsService, process, assessmentResultAuth, assessment, accountService, $state, $stateParams, resetAssessmentDialog, $timeout, config, dialogService, $window, tracker, durationFormatFilter, photoManager, proctoringSatisfied, $log, appAlert, $modal, codemirrorWorkspaceService) {
        process.whenAssessmentResultReady().then(function () {
          if (!process.invitationIssue && (process.isFinished() || process.embedOptions.hideWelcome)) {
            process.next();
          }
        });
        $scope.process = process;

        if (process.liveInterview.setActiveChallengeId) {
          process.liveInterview.setActiveChallengeId(null);
        }

        $scope.activateToken = $stateParams.activate;
        $scope.activateMode = !!$scope.activateToken;

        var hasEnoughCredit = assessment.team.usageAllowed() || process.isTeamMember();
        var result = process.assessmentResult;
        var beganAssessment = ['started', 'submitted'].indexOf(result && result.state) !== -1;
        $scope.hasCredits = hasEnoughCredit || beganAssessment;

        var user = $scope.user = assessmentResultAuth.candidate || {
          email: $stateParams.email
        };

        $scope.assessmentPublished = process.isTeamMember() || assessment.isPublished();

        var account = accountService.get();

        // determine which languages are available on code challenges (ignoring quizzes)
        var languageCounts = {};
        var codeChallengeItems = process.challengeItems.filter(function (ci) {
          return ci && ci.challenge && ci.challenge.isCode();
        });
        $scope.langsFull = [];
        $scope.langsPartial = [];
        codeChallengeItems.forEach(function (ci) {
          var langs = ci.availableLanguages();
          langs.forEach(function (l) {
            languageCounts[l.id] = (languageCounts[l.id] || 0) + 1;
          });
        });
        angular.forEach(languageCounts, function (count, lang) {
          $scope[count === codeChallengeItems.length ? 'langsFull' : 'langsPartial'].push(languages[lang]);
        });
        $scope.langsFull.sort(languages.primaryCompare);
        $scope.langsPartial.sort(languages.primaryCompare);

        $scope.practiceParams = {
          assessmentId: assessment.practiceSlug || 'practice',
          email: user.email,
          returnTo: $state.href('.', {
            email: null,
            practice: null,
            firstName: null,
            lastName: null
          }),
          // clear out unwanted params
          firstName: null,
          lastName: null,
          practice: null
        };

        // Detect voice-to-text questions after loading full challenge data
        function checkForVoiceToTextQuestions() {
          $log.info('Challenge items:', process.challengeItems);

          // Load all challenges with their questions
          var promises = process.challengeItems.map(function (ci) {
            return ci.getChallenge();
          });

          Promise.all(promises).then(function (challenges) {
            $log.info('Challenges loaded:', challenges);

            $scope.hasVoiceToTextQuestions = challenges.some(function (challenge) {
              return challenge && challenge.hasVoiceToTextQuestions;
            });

            $log.info('Has voice to text questions:', $scope.hasVoiceToTextQuestions);
            $scope.$apply(); // Need to apply since we're using native Promise
          }).catch(function (err) {
            $log.error('Error loading challenges:', err);
          });
        }

        // Initial check for voice-to-text questions
        checkForVoiceToTextQuestions();

        // Check if browser is Chrome-based
        $scope.isChromeBased = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);

        // Function to test microphone
        $scope.testMicrophone = asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
          var stream;
          return regeneratorRuntime.wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  _context.prev = 0;
                  _context.next = 3;
                  return navigator.mediaDevices.getUserMedia({
                    audio: true
                  });

                case 3:
                  stream = _context.sent;

                  $scope.microphoneWorks = true;
                  stream.getTracks().forEach(function (track) {
                    return track.stop();
                  });
                  $scope.$apply();
                  _context.next = 14;
                  break;

                case 9:
                  _context.prev = 9;
                  _context.t0 = _context['catch'](0);

                  $scope.microphoneWorks = false;
                  $scope.microphoneError = _context.t0.message;
                  $scope.$apply();

                case 14:
                case 'end':
                  return _context.stop();
              }
            }
          }, _callee, this, [[0, 9]]);
        }));

        // Reset microphone test
        $scope.resetMicrophoneTest = function () {
          $scope.microphoneWorks = null;
          $scope.microphoneError = null;
        };

        // Check if assessment can be started
        $scope.canStartAssessment = function () {
          if (!$scope.hasVoiceToTextQuestions) return true;
          return $scope.microphoneWorks;
        };

        $scope.proctoringSatisfied = proctoringSatisfied;
        $scope.photoManager = photoManager;

        $scope.photoManagerError = null;
        // This fires the moment the camera goes live now, so it has to wait out the warm-up
        // itself: the welcome photo is the identification photo, and frame one is dark.
        $scope.handlePhotoManagerStart = function () {
          $timeout(function () {
            if (!photoManager.running()) {
              return;
            }
            photoManager.takeAssessmentPhotos();

            photoManager.takeWelcomePhoto().then(function () {
              if (!photoManager.running()) {
                return;
              }

              appAlert.clear();
              appAlert.success('Webcam ready');
              // Only once uploads work: the welcome retry would otherwise alert once a second.
              photoManager.setErrorHandler($scope.handlePhotoManagerAssessmentError);
            });
          }, CAMERA_WARMUP_MS);
        };
        $scope.handlePhotoManagerAssessmentError = function (err) {
          $scope.handlePhotoManagerError(err);

          var _process$getCurrentSt = process.getCurrentStage(),
              state = _process$getCurrentSt.state;

          if (state === 'assessments.welcome') {
            return;
          }

          $modal.openOnce('webcamRestartDialog', {
            template: webcamRestartTemplate,
            controller: 'WebcamRestartCtrl as $ctrl',
            backdrop: 'static',
            keyboard: false,
            resolve: {
              photoManager: function photoManager() {
                return $scope.photoManager;
              }
            }
          });
        };
        $scope.handlePhotoManagerError = function (err) {
          var msg = findPhotoManagerErrorMessage$1(err);

          if (typeof msg === 'string') {
            var errorMessage = msg === 'Internal Server Error' ? 'If you have an ad blocker, try turning it off to show WebCam Feed' : msg;
            appAlert.error('Webcam error: ' + errorMessage);

            $log.error('Webcam error: ' + msg);
          } else {
            $log.error('Webcam error', msg);
            appAlert.error('Webcam error: unknown error');
          }
        };
        /**
         * Method to resume or start the assessment and handle the photo manager
         *
         * @param {boolean} isStart It is true if the assessment is being executed for the first time
         * @param isStart
         */
        $scope.resumeAssessment = function (isStart) {
          // Pre-warm LSP pods for all code challenges so they're ready when the candidate opens them.
          var seenPresets = new Set();
          (process.challengeItems || []).forEach(function (ci) {
            var challenge = ci && ci.challenge;
            var rc = challenge && challenge.runConfig;
            if (!rc || !rc.languageServers || !rc.languageServers.length) return;
            var preset = [rc.language, rc.languageVersion, rc.preset || 'default'].join('/');
            if (seenPresets.has(preset)) return;
            seenPresets.add(preset);

            // Build initial files so the LSP prewarm has real code to index from the start.
            // CCC: use challenge.files (multi-file workspace).
            // Classic: use setup field (the candidate's starting template).
            var initialFiles = [];
            try {
              if (challenge.files && challenge.files.length) {
                initialFiles = challenge.files.filter(function (f) {
                  return f && f.contents && !f.hidden && !f.reference;
                }).map(function (f) {
                  return { path: f.path, contents: f.contents };
                });
              } else {
                var langObj = languages.getLang(rc.language);
                var ext = langObj && langObj.ext && langObj.ext[0] || 'txt';
                var lang = challenge.$currentLanguage;
                var body = lang && (lang.setup || lang.solution);
                if (body) initialFiles = [{ path: 'solution.' + ext, contents: body }];
              }
            } catch (e) {
              // non-critical — falls back to v2 prewarm (no file content)
            }

            codemirrorWorkspaceService.preWarmProxy({
              preset: preset,
              teamId: challenge.teamId,
              initialFiles: initialFiles
            });
          });

          if (isStart) {
            process.startAssessmentResult().then(process.next);
          } else {
            process.next();
          }
        };

        $scope.userInfo = {
          edit: process.editProfile,
          get hasSolutions() {
            return process.assessmentResult && !!process.assessmentResult.validSolutions().length;
          },
          get needsRegistration() {
            return !!process.auth.registrationRequired || !!process.auth.emailVerificationRequired;
          },
          get accessRestricted() {
            return this.accessPending || this.accessClosed || this.accessLocked;
          },
          get accessLocked() {
            return !this.needsRegistration && process.wasAccessLocked;
          },
          get accessPending() {
            return !this.needsRegistration && process.assessmentResult && process.assessmentResult.accessStartsAt && new Date(process.assessmentResult.accessStartsAt) > new Date();
          },
          get accessClosed() {
            return !this.needsRegistration && process.assessmentResult && !process.assessmentResult.isSubmitted() && process.assessmentResult.accessEndsAt && new Date(process.assessmentResult.accessEndsAt) < new Date();
          },
          // True if the process has not started yet but the candidate is logged in and ready and has not yet completed any work on this assessment result
          get readyForCleanStart() {
            return !this.needsRegistration && !process.started && process.assessmentResult && !this.hasSolutions && !this.accessRestricted;
          },
          // True if the process has not started yet but the candidate is logged in and ready and has previously started work on this assessment result
          get readyForContinuedStart() {
            return !this.needsRegistration && !process.started && process.assessmentResult && this.hasSolutions && !this.accessRestricted;
          },
          get currentlyTakingAssessment() {
            return !this.needsRegistration && process.started && !this.accessRestricted;
          },
          get willBeReminded() {
            return process.auth.invitation && process.auth.invitation.reminderAt && new Date(process.auth.invitation.reminderAt) > new Date();
          },
          get welcomeName() {
            if (process.assessmentResult && process.assessmentResult.teamCandidate && process.assessmentResult.teamCandidate.forgotten) {
              return '';
            }

            var name = process.auth.candidate && process.auth.candidate.name;
            return name ? ', ' + name : '';
          }
        };

        if (!account.verified) {
          // make sure we're listening to external logins, even if they aren't logged in yet
          accountService.accountChangeListener.enable();
          $scope.$on('app.accountSetRemotely', function () {
            $state.reload();
          });
        } else if (!didShowReset && process.assessmentResult && process.isTeamMember() && !process.assessmentResult.isSnapshotCurrent()) {
          if (process.assessmentResult.solutions.length) {
            didShowReset = true;
            // show the reset dialog for team members who arrive with an outdated result
            resetAssessmentDialog.open(process, true);
          } else {
            // always reset an assessment that wasn't actually started yet
            process.reset(true);
          }
        }

        process.postUpdated(null, {
          id: 'welcome',
          title: 'Welcome',
          index: 0
        });

        var _goToPractice = function _goToPractice() {
          if (!$scope.userInfo.needsRegistration) {
            $scope.redirectingToPractice = true;
            $timeout(function () {
              $state.go('assessments.welcome', $scope.practiceParams);
            });
          }
        };
        if ($stateParams.practice && !assessment.isPractice) {
          if ($scope.userInfo.needsRegistration) {
            $scope.$watch(function () {
              return $scope.userInfo.needsRegistration;
            }, _goToPractice);
          } else {
            _goToPractice();
          }
        }

        if (process.significantTimeDelay) {
          var diffMin = durationFormatFilter(roundToMinutes(Math.abs(config.timeDiffMS)));
          tracker.trackError('Significant Time Delay', {
            delayMS: config.timeDiffMS,
            diffMin: diffMin
          });
          dialogService.open('<p>We\'ve detected a significant difference between the local time on this machine and our servers of\n<strong>' + diffMin + '</strong>.</p>\n<p>This can prevent you from having the fully allotted time to complete your challenges.</p>\n<p>Please correct this issue and reload this page before continuing.</p>', 'Significant Time Difference Detected', [{ id: 'yes', text: 'Reload' }, {
            id: 'no',
            style: 'link',
            text: 'Continue Anyway',
            confirmMessage: 'You may have trouble completing the ' + assessment.nounLower + '. Are you sure?'
          }], {
            windowClass: 'no-close-button significant-time-delay-dialog',
            backdrop: 'static',
            keyboard: false
          }).then(function (_ref2) {
            var id = _ref2.id;

            if (id === 'no') {
              config.ignoreTimeDelay();
            } else {
              $window.location.reload(true);
            }
          });
        }
      }]
    });
  }]);

  var assessmentResultsServiceModule = angular.module('Q.services.assessment-results-service', [assessmentResultModelModule]).factory('assessmentResultsService', ['AssessmentResult', '$q', '$timeout', function (AssessmentResult, $q, $timeout) {
    var defaultIncludes = ['assessment', 'challenge_items.challenge', 'solutions', 'team_candidate', 'signals_profile'];

    return {
      start: start,
      find: find,
      submit: submit,
      update: update
    };

    function update(instance) {
      var updates = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var candidateRemarks = instance.candidateRemarks,
          closedReason = instance.closedReason;

      return AssessmentResult.update(instance, angular.extend({ candidateRemarks: candidateRemarks, closedReason: closedReason }, updates));
    }

    function start(assessmentId) {
      var a = AssessmentResult.get(assessmentId);
      var shouldStart = !a || a.isOpened() || a.isInvited();
      return shouldStart ? AssessmentResult.start(assessmentId) : $q.when(a);
    }

    function find(assessmentId, candidateId) {
      return AssessmentResult.fetchAll({ assessmentId: assessmentId, candidateId: candidateId }, {
        params: {
          includes: defaultIncludes
        }
      }).then(function (assessmentApps) {
        return assessmentApps[0];
      });
    }

    function submit(instance) {
      if (instance.isClosed()) {
        return $q.when(instance);
      } else {
        return update(instance).then(function () {
          return AssessmentResult.submit(instance);
        }).catch(function (resp) {
          if (resp.status === 403 && resp.data && /pending solution/i.test(resp.data.reason)) {
            // try a second time after 15 seconds for pending solutions
            return $timeout(function () {
              return AssessmentResult.submit(instance);
            }, 15000);
          } else {
            return $q.reject(resp);
          }
        });
      }
    }
  }]);

  var solutionsServiceModule = angular.module('Q.services.solutions-service', [solutionModelModule]).factory('solutionsService', ['Solution', function (Solution) {
    return {
      get: get,
      create: create
    };

    function _replaceType(type) {
      return type.replace(/Challenge$/, 'Solution');
    }

    function get(challenge, assessmentResult) {
      if (!challenge || !assessmentResult) return null;
      var type = _replaceType(challenge.$type);
      var sol = Solution.filter({
        $type: type,
        challengeId: challenge.id,
        assessmentResultId: assessmentResult.id
      });
      return sol[0];
    }

    function create(challenge, assessmentResult, language) {
      var type = _replaceType(challenge.$type);
      return Solution.createWithFetch({
        $type: type,
        challengeId: challenge.id,
        assessmentResultId: assessmentResult.id,
        language: language
      }, {
        params: {
          // re-include challenge to get missing data
          includes: ['challenge']
        }
      });
    }
  }]);

  var firebaseModule = 'firebase';

  var firebaseServiceModule = angular.module('Q.services.firebase', [firebaseModule, timerHelperModule])

  /**
   * Provides a service that makes interacting with Firebase in an asynchronous manner easier.
   * This service wraps up a lot of the angularfire $firebaseObject methods in simple utility methods
   * that enable automatic configuring of FB only once it's needed, and low-effort access to some useful
   * features, like using onDisconnect to signal logged in status.
   */
  .factory('firebase', ['$firebaseObject', '$q', 'timerHelper', function ($firebaseObject, $q, timerHelper) {
    var fb = null;
    var REMOVE_ON_DISCONNECT = {};

    // 10 seconds to assume a failed connection
    var CONNECTION_FAILURE_TIMEOUT = 10000;

    return {
      init: init,
      ifConnected: ifConnected,
      get: get,
      bind: bind,
      set: set,
      setWhenConnected: setWhenConnected,
      remove: remove,
      REMOVE_ON_DISCONNECT: REMOVE_ON_DISCONNECT
    };

    /**
     * Initializes Firebase
     * Note: this is purposefully async in case we decide to only load the FB code when it's being used.
     * @returns {Promise}
     */
    function init() {
      if (fb === null) {
        fb = window.firebase;
        if (!fb) {
          return $q.reject(new Error('Unable to load Firebase'));
        }

        // if on staging, we have to configure a different firebase manually.
        var config = appConfig.firebase;
        var Modernizr = window && window.Modernizr;
        if (Modernizr && Modernizr['qualified-staging'] && appConfig.firebaseStaging) {
          config = appConfig.firebaseStaging;
        }
        fb.initializeApp(config);
      }
      return $q.when(fb);
    }

    /**
     * Returns a promise that resolves if the connection is up or resolved within the specified timeout
     * @param timeout {Number?} Seconds to timeout, defaults to 10s
     * @returns {Promise} A promise that resolves if the connection is up within the timeout
     */
    function ifConnected(timeout) {
      return init().then(function (fb) {
        return $q(function (resolve, reject) {
          var onlineCheck = fb.database().ref('.info/connected');

          var notConnectedTimer = null;

          function initCheck(connectedCheck) {
            // determine if we've connected or not
            if (connectedCheck.val() === true) {
              // if we've connected, we're set
              onlineCheck.off('value', initCheck);
              timerHelper.longTimeout.cancel(notConnectedTimer);
              resolve(fb);
            } else if (!notConnectedTimer) {
              // if we don't connect within the timeout, reject
              notConnectedTimer = timerHelper.longTimeout(function () {
                // unable to connect in a timely fashion
                onlineCheck.off('value', initCheck);
                reject(new Error('Unable to connect to Firebase'));
              }, timeout || CONNECTION_FAILURE_TIMEOUT);
            }
          }

          onlineCheck.on('value', initCheck);
        });
      });
    }

    /**
     * Utility method to make it easier to retrieve an AngularFire reference to a Firebase node
     * @param ref ID String for the Firebase key
     * @returns {Promise}
     */
    function get(ref) {
      return init().then(function (fb) {
        return $firebaseObject(fb.database().ref(ref)).$loaded();
      });
    }

    /**
     * Utility method to quickly bind a reference to a scope variable
     * @param ref
     * @param scope
     * @param varName
     * @returns {Promise}
     */
    function bind(ref, scope, varName) {
      return get(ref).then(function (obj) {
        return obj.$bindTo(scope, varName).then(function () {
          return obj;
        });
      });
    }

    /**
     * Utility method to easily set a value you don't need to track
     * @param ref ID String for the Firebase key
     * @param value Value to store
     * @param onDisconnect If provided, set this ref to the provided value when the user gets disconnected
     * @returns {Promise}
     */
    function set(ref, value) {
      var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
          onDisconnect = _ref.onDisconnect;

      return get(ref).then(function (obj) {
        if (onDisconnect !== undefined) {
          if (onDisconnect === REMOVE_ON_DISCONNECT) {
            obj.$ref().onDisconnect().remove();
          } else {
            obj.$ref().onDisconnect().set(onDisconnect);
          }
        }
        return _setFirebaseObject(obj, value);
      });
    }

    /**
     * Utility method to set a value when connected, and clear it or update it when you aren't
     * @param ref
     * @param value
     * @param onDisconnect
     * @returns {Function} manual disconnect function, which also removes the change event listener
     */
    function setWhenConnected(ref, value) {
      var onDisconnect = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : REMOVE_ON_DISCONNECT;

      var _disconnect = null;

      function disconnect() {
        if (_disconnect) {
          _disconnect();
          _disconnect = null;
        }
      }

      disconnect.promise = get(ref).then(function (obj) {
        var onlineCheck = fb.database().ref('.info/connected');

        function onValueChange(snapshot) {
          if (snapshot.val()) {
            if (onDisconnect === REMOVE_ON_DISCONNECT) {
              obj.$ref().onDisconnect().remove();
            } else {
              obj.$ref().onDisconnect().set(onDisconnect);
            }
            return _setFirebaseObject(obj, value);
          }
          return obj.$save().then(function () {
            return obj;
          });
        }

        onlineCheck.on('value', onValueChange);

        _disconnect = function _disconnect() {
          onlineCheck.off('value', onValueChange);
          if (onDisconnect === REMOVE_ON_DISCONNECT) {
            obj.$ref().remove();
          } else {
            obj.$ref().set(onDisconnect);
          }
        };

        return obj;
      });

      disconnect.update = function () {
        return disconnect.promise.then(function (obj) {
          return _setFirebaseObject(obj, value);
        });
      };

      return disconnect;
    }

    /**
     * Utility method to quickly remove a Firebase value
     * @param ref ID String for the Firebase key
     * @returns {Promise}
     */
    function remove(ref) {
      return init().then(function (fb) {
        var obj = $firebaseObject(fb.database().ref(ref));
        return obj.$remove();
      });
    }

    /**
     * Sets the $firebaseObject obj values. If value is a primitive, this automatically handles setting
     * $value.  If it is an object, it extends the values in.
     *
     * @param obj Object to update
     * @param value Value or values to set
     * @returns {Promise}
     * @private
     */
    function _setFirebaseObject(obj, value) {
      if (angular.isObject(value)) {
        for (var k in obj) {
          if (obj.hasOwnProperty(k) && k[0] !== '$') {
            delete obj[k];
          }
        }
        angular.extend(obj, value);
        stripUndefined(obj);
      } else {
        obj.$value = value;
      }
      return obj.$save().then(function () {
        return obj;
      });
    }

    function stripUndefined(obj) {
      angular.forEach(obj, function (v, k) {
        if (v === undefined) {
          delete obj[k];
        } else if (Array.isArray(v)) {
          v.forEach(stripUndefined);
        } else if (angular.isObject(v)) {
          stripUndefined(v);
        }
      });
    }
  }]);

  var runnerFrameTemplate = "<div class=\"loading\" ng-if=\"$ctrl.loading && !$ctrl.loadError\"> <p ng-show=\"$ctrl.loading === 'show'\"> <span class=\"loading-spinner\"></span> {{ $ctrl.loadWarning ? 'Still connecting' : 'Connecting' }} to Qualified Code Runner... </p> <p ng-if=\"$ctrl.loadWarning\"> <compile-template src=\"$ctrl.contactTemplate\"></compile-template> </p> </div> <div class=\"error\" ng-if=\"$ctrl.loadError\"> <div class=\"alert alert-danger\"> <h1>Error</h1> <p>There was an issue connecting to the code runner service.</p> <p ng-show=\"$ctrl.allowReload\" class=\"reload-link\"> <a class=\"alert-link\" ng-click=\"$ctrl.reload()\">Click here to try reloading the runner</a> </p> <hr> <div ng-bind-html=\"$ctrl.loadError\"></div> </div> <p> <compile-template src=\"$ctrl.contactTemplate\"></compile-template> <br> Please include any details visible below: </p> </div> ";

  var runnerFrameContactCandidateTemplate = "If you continue to have issues, please contact the team that invited you. ";

  var runnerFrameContactTeamTemplate = "If you continue to have issues, please contact us at <a href=\"mailto:team@qualified.io\" target=\"_blank\"> team@qualified.io </a>. ";

  var createDestructor = (function () {
      var callbacks = [];
      var destroyed = false;
      return {
          destroy: function destroy(error) {
              destroyed = true;
              callbacks.forEach(function (callback) {
                  callback(error);
              });
          },
          onDestroy: function onDestroy(callback) {
              destroyed ? callback() : callbacks.push(callback);
          }
      };
  });

  var DEFAULT_PORT_BY_PROTOCOL = {
      'http:': '80',
      'https:': '443'
  };
  var URL_REGEX = /^(https?:)?\/\/([^/:]+)?(:(\d+))?/;
  var opaqueOriginSchemes = ['file:', 'data:'];
  /**
   * Converts a src value into an origin.
   */
  var getOriginFromSrc = (function (src) {
      if (src && opaqueOriginSchemes.find(function (scheme) {
          return src.startsWith(scheme);
      })) {
          // The origin of the child document is an opaque origin and its
          // serialization is "null"
          // https://html.spec.whatwg.org/multipage/origin.html#origin
          return 'null';
      }
      // Note that if src is undefined, then srcdoc is being used instead of src
      // and we can follow this same logic below to get the origin of the parent,
      // which is the origin that we will need to use.
      var location = document.location;
      var regexResult = URL_REGEX.exec(src);
      var protocol = void 0;
      var hostname = void 0;
      var port = void 0;
      if (regexResult) {
          // It's an absolute URL. Use the parsed info.
          // regexResult[1] will be undefined if the URL starts with //
          protocol = regexResult[1] ? regexResult[1] : location.protocol;
          hostname = regexResult[2];
          port = regexResult[4];
      } else {
          // It's a relative path. Use the current location's info.
          protocol = location.protocol;
          hostname = location.hostname;
          port = location.port;
      }
      // If the port is the default for the protocol, we don't want to add it to the origin string
      // or it won't match the message's event.origin.
      var portSuffix = port && port !== DEFAULT_PORT_BY_PROTOCOL[protocol] ? ':' + port : '';
      return protocol + '//' + hostname + portSuffix;
  });

  var createLogger = (function (debug) {
      /**
       * Logs a message if debug is enabled.
       */
      return function () {
          for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
              args[_key] = arguments[_key];
          }

          if (debug) {
              var _console;

              (_console = console).log.apply(_console, ['[Penpal]'].concat(args)); // eslint-disable-line no-console
          }
      };
  });

  var MessageType;
  (function (MessageType) {
      MessageType["Call"] = "call";
      MessageType["Reply"] = "reply";
      MessageType["Syn"] = "syn";
      MessageType["SynAck"] = "synAck";
      MessageType["Ack"] = "ack";
  })(MessageType || (MessageType = {}));
  var Resolution;
  (function (Resolution) {
      Resolution["Fulfilled"] = "fulfilled";
      Resolution["Rejected"] = "rejected";
  })(Resolution || (Resolution = {}));
  var ErrorCode;
  (function (ErrorCode) {
      ErrorCode["ConnectionDestroyed"] = "ConnectionDestroyed";
      ErrorCode["ConnectionTimeout"] = "ConnectionTimeout";
      ErrorCode["NotInIframe"] = "NotInIframe";
      ErrorCode["NoIframeSrc"] = "NoIframeSrc";
  })(ErrorCode || (ErrorCode = {}));
  var NativeErrorName;
  (function (NativeErrorName) {
      NativeErrorName["DataCloneError"] = "DataCloneError";
  })(NativeErrorName || (NativeErrorName = {}));
  var NativeEventType;
  (function (NativeEventType) {
      NativeEventType["Message"] = "message";
  })(NativeEventType || (NativeEventType = {}));

  /**
   * Handles a SYN handshake message.
   */
  var handleSynMessageFactory = (function (log, methods, childOrigin, originForSending) {
      return function (event) {
          if (event.origin !== childOrigin) {
              log('Parent: Handshake - Received SYN message from origin ' + event.origin + ' which did not match expected origin ' + childOrigin);
              return;
          }
          log('Parent: Handshake - Received SYN, responding with SYN-ACK');
          var synAckMessage = {
              penpal: MessageType.SynAck,
              methodNames: Object.keys(methods)
          };
          event.source.postMessage(synAckMessage, originForSending);
      };
  });

  /**
   * Converts an error object into a plain object.
   */
  var serializeError = function serializeError(_ref) {
      var name = _ref.name,
          message = _ref.message,
          stack = _ref.stack;
      return {
          name: name,
          message: message,
          stack: stack
      };
  };
  /**
   * Converts a plain object into an error object.
   */
  var deserializeError = function deserializeError(obj) {
      var deserializedError = new Error();
      // @ts-ignore
      Object.keys(obj).forEach(function (key) {
          return deserializedError[key] = obj[key];
      });
      return deserializedError;
  };

  /**
   * Listens for "call" messages coming from the remote, executes the corresponding method, and
   * responds with the return value.
   */
  var connectCallReceiver = (function (info, methods, log) {
      var localName = info.localName,
          local = info.local,
          remote = info.remote,
          originForSending = info.originForSending,
          originForReceiving = info.originForReceiving;

      var destroyed = false;
      var handleMessageEvent = function handleMessageEvent(event) {
          if (event.source !== remote || event.data.penpal !== MessageType.Call) {
              return;
          }
          if (event.origin !== originForReceiving) {
              log(localName + ' received message from origin ' + event.origin + ' which did not match expected origin ' + originForReceiving);
              return;
          }
          var callMessage = event.data;
          var methodName = callMessage.methodName,
              args = callMessage.args,
              id = callMessage.id;

          log(localName + ': Received ' + methodName + '() call');
          var createPromiseHandler = function createPromiseHandler(resolution) {
              return function (returnValue) {
                  log(localName + ': Sending ' + methodName + '() reply');
                  if (destroyed) {
                      // It's possible to throw an error here, but it would need to be thrown asynchronously
                      // and would only be catchable using window.onerror. This is because the consumer
                      // is merely returning a value from their method and not calling any function
                      // that they could wrap in a try-catch. Even if the consumer were to catch the error,
                      // the value of doing so is questionable. Instead, we'll just log a message.
                      log(localName + ': Unable to send ' + methodName + '() reply due to destroyed connection');
                      return;
                  }
                  var message = {
                      penpal: MessageType.Reply,
                      id: id,
                      resolution: resolution,
                      returnValue: returnValue
                  };
                  if (resolution === Resolution.Rejected && returnValue instanceof Error) {
                      message.returnValue = serializeError(returnValue);
                      message.returnValueIsError = true;
                  }
                  try {
                      remote.postMessage(message, originForSending);
                  } catch (err) {
                      // If a consumer attempts to send an object that's not cloneable (e.g., window),
                      // we want to ensure the receiver's promise gets rejected.
                      if (err.name === NativeErrorName.DataCloneError) {
                          var errorReplyMessage = {
                              penpal: MessageType.Reply,
                              id: id,
                              resolution: Resolution.Rejected,
                              returnValue: serializeError(err),
                              returnValueIsError: true
                          };
                          remote.postMessage(errorReplyMessage, originForSending);
                      }
                      throw err;
                  }
              };
          };
          new Promise(function (resolve) {
              return resolve(methods[methodName].apply(methods, args));
          }).then(createPromiseHandler(Resolution.Fulfilled), createPromiseHandler(Resolution.Rejected));
      };
      local.addEventListener(NativeEventType.Message, handleMessageEvent);
      return function () {
          destroyed = true;
          local.removeEventListener(NativeEventType.Message, handleMessageEvent);
      };
  });

  var id = 0;
  /**
   * @return {number} A unique ID (not universally unique)
   */
  var generateId = (function () {
    return ++id;
  });

  /**
   * Augments an object with methods that match those defined by the remote. When these methods are
   * called, a "call" message will be sent to the remote, the remote's corresponding method will be
   * executed, and the method's return value will be returned via a message.
   * @param {Object} callSender Sender object that should be augmented with methods.
   * @param {Object} info Information about the local and remote windows.
   * @param {Array} methodNames Names of methods available to be called on the remote.
   * @param {Promise} destructionPromise A promise resolved when destroy() is called on the penpal
   * connection.
   * @returns {Object} The call sender object with methods that may be called.
   */
  var connectCallSender = (function (callSender, info, methodNames, destroyConnection, log) {
      var localName = info.localName,
          local = info.local,
          remote = info.remote,
          originForSending = info.originForSending,
          originForReceiving = info.originForReceiving;

      var destroyed = false;
      log(localName + ': Connecting call sender');
      var createMethodProxy = function createMethodProxy(methodName) {
          return function () {
              for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
                  args[_key] = arguments[_key];
              }

              log(localName + ': Sending ' + methodName + '() call');
              // This handles the case where the iframe has been removed from the DOM
              // (and therefore its window closed), the consumer has not yet
              // called destroy(), and the user calls a method exposed by
              // the remote. We detect the iframe has been removed and force
              // a destroy() immediately so that the consumer sees the error saying
              // the connection has been destroyed. We wrap this check in a try catch
              // because Edge throws an "Object expected" error when accessing
              // contentWindow.closed on a contentWindow from an iframe that's been
              // removed from the DOM.
              var iframeRemoved = void 0;
              try {
                  if (remote.closed) {
                      iframeRemoved = true;
                  }
              } catch (e) {
                  iframeRemoved = true;
              }
              if (iframeRemoved) {
                  destroyConnection();
              }
              if (destroyed) {
                  var error = new Error('Unable to send ' + methodName + '() call due ' + 'to destroyed connection');
                  error.code = ErrorCode.ConnectionDestroyed;
                  throw error;
              }
              return new Promise(function (resolve, reject) {
                  var id = generateId();
                  var handleMessageEvent = function handleMessageEvent(event) {
                      if (event.source !== remote || event.data.penpal !== MessageType.Reply || event.data.id !== id) {
                          return;
                      }
                      if (event.origin !== originForReceiving) {
                          log(localName + ' received message from origin ' + event.origin + ' which did not match expected origin ' + originForReceiving);
                          return;
                      }
                      var replyMessage = event.data;
                      log(localName + ': Received ' + methodName + '() reply');
                      local.removeEventListener(NativeEventType.Message, handleMessageEvent);
                      var returnValue = replyMessage.returnValue;
                      if (replyMessage.returnValueIsError) {
                          returnValue = deserializeError(returnValue);
                      }
                      (replyMessage.resolution === Resolution.Fulfilled ? resolve : reject)(returnValue);
                  };
                  local.addEventListener(NativeEventType.Message, handleMessageEvent);
                  var callMessage = {
                      penpal: MessageType.Call,
                      id: id,
                      methodName: methodName,
                      args: args
                  };
                  remote.postMessage(callMessage, originForSending);
              });
          };
      };
      methodNames.reduce(function (api, methodName) {
          api[methodName] = createMethodProxy(methodName);
          return api;
      }, callSender);
      return function () {
          destroyed = true;
      };
  });

  /**
   * Handles an ACK handshake message.
   */
  var handleAckMessageFactory = (function (methods, childOrigin, originForSending, destructor, log) {
      var destroy = destructor.destroy,
          onDestroy = destructor.onDestroy;

      var destroyCallReceiver = void 0;
      var receiverMethodNames = void 0;
      // We resolve the promise with the call sender. If the child reconnects
      // (for example, after refreshing or navigating to another page that
      // uses Penpal, we'll update the call sender with methods that match the
      // latest provided by the child.
      var callSender = {};
      return function (event) {
          if (event.origin !== childOrigin) {
              log('Parent: Handshake - Received ACK message from origin ' + event.origin + ' which did not match expected origin ' + childOrigin);
              return;
          }
          log('Parent: Handshake - Received ACK');
          var info = {
              localName: 'Parent',
              local: window,
              remote: event.source,
              originForSending: originForSending,
              originForReceiving: childOrigin
          };
          // If the child reconnected, we need to destroy the prior call receiver
          // before setting up a new one.
          if (destroyCallReceiver) {
              destroyCallReceiver();
          }
          destroyCallReceiver = connectCallReceiver(info, methods, log);
          onDestroy(destroyCallReceiver);
          // If the child reconnected, we need to remove the methods from the
          // previous call receiver off the sender.
          if (receiverMethodNames) {
              receiverMethodNames.forEach(function (receiverMethodName) {
                  delete callSender[receiverMethodName];
              });
          }
          receiverMethodNames = event.data.methodNames;
          var destroyCallSender = connectCallSender(callSender, info, receiverMethodNames, destroy, log);
          onDestroy(destroyCallSender);
          return callSender;
      };
  });

  var validateIframeHasSrcOrSrcDoc = (function (iframe) {
      if (!iframe.src && !iframe.srcdoc) {
          var error = new Error('Iframe must have src or srcdoc property defined.');
          error.code = ErrorCode.NoIframeSrc;
          throw error;
      }
  });

  var CHECK_IFRAME_IN_DOC_INTERVAL = 60000;
  /**
   * Monitors for iframe removal and destroys connection if iframe
   * is found to have been removed from DOM. This is to prevent memory
   * leaks when the iframe is removed from the document and the consumer
   * hasn't called destroy(). Without this, event listeners attached to
   * the window would stick around and since the event handlers have a
   * reference to the iframe in their closures, the iframe would stick
   * around too.
   */
  var monitorIframeRemoval = (function (iframe, destructor) {
      var destroy = destructor.destroy,
          onDestroy = destructor.onDestroy;

      var checkIframeInDocIntervalId = setInterval(function () {
          if (!document.contains(iframe)) {
              clearInterval(checkIframeInDocIntervalId);
              destroy();
          }
      }, CHECK_IFRAME_IN_DOC_INTERVAL);
      onDestroy(function () {
          clearInterval(checkIframeInDocIntervalId);
      });
  });

  /**
   * Starts a timeout and calls the callback with an error
   * if the timeout completes before the stop function is called.
   */
  var startConnectionTimeout = (function (timeout, callback) {
      var timeoutId = void 0;
      if (timeout !== undefined) {
          timeoutId = window.setTimeout(function () {
              var error = new Error('Connection timed out after ' + timeout + 'ms');
              error.code = ErrorCode.ConnectionTimeout;
              callback(error);
          }, timeout);
      }
      return function () {
          clearTimeout(timeoutId);
      };
  });

  /**
   * Attempts to establish communication with an iframe.
   */
  var connectToChild = (function (options) {
      var iframe = options.iframe,
          _options$methods = options.methods,
          methods = _options$methods === undefined ? {} : _options$methods,
          childOrigin = options.childOrigin,
          timeout = options.timeout,
          _options$debug = options.debug,
          debug = _options$debug === undefined ? false : _options$debug;

      var log = createLogger(debug);
      var destructor = createDestructor();
      var onDestroy = destructor.onDestroy,
          _destroy = destructor.destroy;

      if (!childOrigin) {
          validateIframeHasSrcOrSrcDoc(iframe);
          childOrigin = getOriginFromSrc(iframe.src);
      }
      // If event.origin is "null", the remote protocol is file: or data: and we
      // must post messages with "*" as targetOrigin when sending messages.
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage#Using_window.postMessage_in_extensions
      var originForSending = childOrigin === 'null' ? '*' : childOrigin;
      var handleSynMessage = handleSynMessageFactory(log, methods, childOrigin, originForSending);
      var handleAckMessage = handleAckMessageFactory(methods, childOrigin, originForSending, destructor, log);
      var promise = new Promise(function (resolve, reject) {
          var stopConnectionTimeout = startConnectionTimeout(timeout, _destroy);
          var handleMessage = function handleMessage(event) {
              if (event.source !== iframe.contentWindow || !event.data) {
                  return;
              }
              if (event.data.penpal === MessageType.Syn) {
                  handleSynMessage(event);
                  return;
              }
              if (event.data.penpal === MessageType.Ack) {
                  var callSender = handleAckMessage(event);
                  if (callSender) {
                      stopConnectionTimeout();
                      resolve(callSender);
                  }
                  return;
              }
          };
          window.addEventListener(NativeEventType.Message, handleMessage);
          log('Parent: Awaiting handshake');
          monitorIframeRemoval(iframe, destructor);
          onDestroy(function (error) {
              window.removeEventListener(NativeEventType.Message, handleMessage);
              if (!error) {
                  error = new Error('Connection destroyed');
                  error.code = ErrorCode.ConnectionDestroyed;
              }
              reject(error);
          });
      });
      return {
          promise: promise,
          destroy: function destroy() {
              // Don't allow consumer to pass an error into destroy.
              _destroy();
          }
      };
  });

  var validateWindowIsIframe = (function () {
      if (window === window.top) {
          var error = new Error('connectToParent() must be called within an iframe');
          error.code = ErrorCode.NotInIframe;
          throw error;
      }
  });

  /**
   * Handles a SYN-ACK handshake message.
   */
  var handleSynAckMessageFactory = (function (parentOrigin, methods, destructor, log) {
      var destroy = destructor.destroy,
          onDestroy = destructor.onDestroy;

      return function (event) {
          var originQualifies = parentOrigin instanceof RegExp ? parentOrigin.test(event.origin) : parentOrigin === '*' || parentOrigin === event.origin;
          if (!originQualifies) {
              log('Child: Handshake - Received SYN-ACK from origin ' + event.origin + ' which did not match expected origin ' + parentOrigin);
              return;
          }
          log('Child: Handshake - Received SYN-ACK, responding with ACK');
          // If event.origin is "null", the remote protocol is file: or data: and we
          // must post messages with "*" as targetOrigin when sending messages.
          // https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage#Using_window.postMessage_in_extensions
          var originForSending = event.origin === 'null' ? '*' : event.origin;
          var ackMessage = {
              penpal: MessageType.Ack,
              methodNames: Object.keys(methods)
          };
          window.parent.postMessage(ackMessage, originForSending);
          var info = {
              localName: 'Child',
              local: window,
              remote: window.parent,
              originForSending: originForSending,
              originForReceiving: event.origin
          };
          var destroyCallReceiver = connectCallReceiver(info, methods, log);
          onDestroy(destroyCallReceiver);
          var callSender = {};
          var destroyCallSender = connectCallSender(callSender, info, event.data.methodNames, destroy, log);
          onDestroy(destroyCallSender);
          return callSender;
      };
  });

  var areGlobalsAccessible = function areGlobalsAccessible() {
      try {
          clearTimeout();
      } catch (e) {
          return false;
      }
      return true;
  };
  /**
   * Attempts to establish communication with the parent window.
   */
  var connectToParent = (function () {
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var _options$parentOrigin = options.parentOrigin,
          parentOrigin = _options$parentOrigin === undefined ? '*' : _options$parentOrigin,
          _options$methods = options.methods,
          methods = _options$methods === undefined ? {} : _options$methods,
          timeout = options.timeout,
          _options$debug = options.debug,
          debug = _options$debug === undefined ? false : _options$debug;

      var log = createLogger(debug);
      var destructor = createDestructor();
      var _destroy = destructor.destroy,
          onDestroy = destructor.onDestroy;

      validateWindowIsIframe();
      var handleSynAckMessage = handleSynAckMessageFactory(parentOrigin, methods, destructor, log);
      var sendSynMessage = function sendSynMessage() {
          log('Child: Handshake - Sending SYN');
          var synMessage = { penpal: MessageType.Syn };
          var parentOriginForSyn = parentOrigin instanceof RegExp ? '*' : parentOrigin;
          window.parent.postMessage(synMessage, parentOriginForSyn);
      };
      var promise = new Promise(function (resolve, reject) {
          var stopConnectionTimeout = startConnectionTimeout(timeout, _destroy);
          var handleMessage = function handleMessage(event) {
              // Under niche scenarios, we get into this function after
              // the iframe has been removed from the DOM. In Edge, this
              // results in "Object expected" errors being thrown when we
              // try to access properties on window (global properties).
              // For this reason, we try to access a global up front (clearTimeout)
              // and if it fails we can assume the iframe has been removed
              // and we ignore the message event.
              if (!areGlobalsAccessible()) {
                  return;
              }
              if (event.source !== parent || !event.data) {
                  return;
              }
              if (event.data.penpal === MessageType.SynAck) {
                  var callSender = handleSynAckMessage(event);
                  if (callSender) {
                      window.removeEventListener(NativeEventType.Message, handleMessage);
                      stopConnectionTimeout();
                      resolve(callSender);
                  }
              }
          };
          window.addEventListener(NativeEventType.Message, handleMessage);
          sendSynMessage();
          onDestroy(function (error) {
              window.removeEventListener(NativeEventType.Message, handleMessage);
              if (!error) {
                  error = new Error('Connection destroyed');
                  error.code = ErrorCode.ConnectionDestroyed;
              }
              reject(error);
          });
      });
      return {
          promise: promise,
          destroy: function destroy() {
              // Don't allow consumer to pass an error into destroy.
              _destroy();
          }
      };
  });

  var MAX_BUFFER_SIZE = 1024 * 1024; // limit total output to 1Mb

  var runnerFrames = [];
  var runnerIFrame = null;

  // v2 of the runner frame protocol: Penpal messaging with token authorization
  var RUNNER_API_VERSION = 'v2';

  var crThemeMap = {
    light: 'day',
    dark: 'night'
  };

  var messageIdPrefix = 'Q' + Math.random().toString(16).substr(2) + '-';
  var messageIdCounter = 0;

  // shared runnerUrl so we don't have to keep looking it up
  var runnerUrl = null;
  var didReportRunnerError = void 0;

  var runnerFrameComponentModule = angular.module('Q.components.runner-frame', [timerHelperModule, configServiceModule, appAlertComponentModule, windowEventHelperModule, compileTemplateComponentModule]).component('runnerFrame', {
    bindings: {
      utmSource: '<',
      utmCampaign: '<',
      utmMedium: '<',
      response: '<',
      initialResponse: '<',
      privileged: '<',
      onFocus: '&',
      onReady: '&'
    },
    template: runnerFrameTemplate,
    controller: ['$sce', 'runnerFrameService', '$scope', '$document', '$timeout', '$element', '$injector', 'timerHelper', '$log', '$http', function controller($sce, runnerFrameService, $scope, $document, $timeout, $element, $injector, timerHelper, $log, $http) {
      var ctrl = this;

      ctrl.contactTemplate = appConfig.candidateApp ? runnerFrameContactCandidateTemplate : runnerFrameContactTeamTemplate;

      // create a stack of runner frames to automatically reload the frame when we return
      runnerFrames.unshift(ctrl);

      $scope.$on('document:focusoutActiveElement', function ($e, event, activeElement) {
        if (runnerIFrame && activeElement === runnerIFrame[0]) {
          ctrl.onFocus();
        }
      });

      var cmOpts = $injector.has('codemirrorOptions') && $injector.get('codemirrorOptions').sharedOpts;

      function setUrl() {
        // if we haven't got our URL yet, ignore this request
        if (!runnerUrl) return;

        var queryParams = {
          utm_source: ctrl.utmSource || 'qualified',
          utm_medium: ctrl.utmMedium || 'web',
          utm_campaign: ctrl.utmCampaign,
          api: RUNNER_API_VERSION
        };
        var hashParams = {
          theme: 'light',
          pro: true,
          detectHTML: false
        };
        if (cmOpts && cmOpts.theme === 'dark') {
          hashParams.theme = 'dark';
        }
        if (ctrl.privileged) {
          hashParams.privileged = true;
        }
        if (ctrl.response) {
          hashParams.displayOnly = true;
        }

        var runnerSrc = runnerUrl + '?' + parameterize(queryParams) + '&#' + parameterize(hashParams);
        var oldSrc = runnerIFrame.attr('src');
        if (oldSrc !== runnerSrc) {
          var isLoaded = oldSrc && oldSrc !== 'about:blank';
          runnerIFrame.attr('src', $sce.trustAsResourceUrl(runnerSrc));
          // only the hash changed: the frame won't reload, so just update the theme
          if (isLoaded && oldSrc.split('#')[0] === runnerSrc.split('#')[0]) {
            runnerFrameService.setTheme(crThemeMap[hashParams.theme]);
          } else {
            // Penpal must be listening before the frame loads
            runnerFrameService.connect(runnerIFrame[0]);
          }
        }
      }

      function clearLoadingAlert(success) {
        ctrl.loading = false;
        if (runnerIFrame) {
          runnerIFrame.css({ opacity: 1 });

          // if we're not successful loading the runner, or the iFrame itself is marked as hidden
          // (due to adblockers), we need to let the user know
          if (ctrl.loaded || success && runnerIFrame.isVisible( /* don't recurse */false)) {
            ctrl.loadError = false;
            ctrl.loaded = true;
          } else {
            // there was an error loading the runner frame
            var message = '<p>Please try reloading the page to continue (your work will be saved).</p>';

            if (runnerUrl && !didReportRunnerError) {
              didReportRunnerError = true;
              var errorType = success ? 'iframe was hidden externally' : 'content did not load within 20 seconds';
              $log.error('Unable to load runner frame: ' + errorType + '.');
              // attempt to load the CR directly, to see if we get an error
              $http.get(runnerIFrame.attr('src'), { autoRetry: false }).catch(function (response) {
                // if we didn't get a network error, report what we can
                if (response.status > 0) {
                  $log.error('Error details when attempting to load runner frame: ' + response.status + ' - ' + response.statusText, response);
                }
              });

              ctrl.allowReload = true;
              message = '<p>Please verify that nothing is preventing access to <a class="alert-link" href="' + runnerUrl + '" target="_blank">' + runnerUrl + '</a>. Make sure to check any ad-blockers or firewall rules.</p><p>If you are able to connect to this URL, please refresh this page to try again (your work will be saved).</p>';
            }

            ctrl.loadError = $sce.trustAsHtml(message);
          }
        }
      }

      if (!runnerIFrame) {
        // create the shared runner iframe element
        runnerIFrame = angular.element('<iframe id="runner-frame" sandbox="allow-scripts allow-same-origin allow-pointer-lock"></iframe>');
      }

      // loads the runner & handles notification details
      function loadRunner(force) {
        ctrl.loading = true;
        ctrl.loadWarning = false;
        ctrl.loadError = false;
        ctrl.loaded = false;

        var timeouts = [
        // wait at least 2s before showing the loading message
        timerHelper.longTimeout(function () {
          ctrl.loading = ctrl.loading && 'show';
        }, 2000),
        // in case the iFrame never loads correctly, we'll clear the alert so it can be seen by the user
        timerHelper.longTimeout(function () {
          ctrl.loadWarning = true;
        }, 10000),
        // after 20 seconds, switch to an error message (it might still be cleared later)
        timerHelper.longTimeout(function () {
          clearLoadingAlert(false);
        }, 20000)];
        $scope.$on('$destroy', function () {
          // if we're destroyed _before_ loading, don't show errors!
          timeouts.forEach(timerHelper.longTimeout.cancel);
        });

        runnerIFrame.css({ opacity: 0 });
        // must be ready to receive `onReady` before the frame is connected
        runnerFrameService.init();
        if (!runnerUrl) {
          runnerFrameService.getRunnerUrl().then(function (url) {
            runnerUrl = url;
            $element.append(runnerIFrame);
            setUrl();
          }).catch(function () {
            return clearLoadingAlert(false);
          });
        } else {
          $element.append(runnerIFrame);
          setUrl();
        }

        if (cmOpts) {
          $scope.$watch(function () {
            return cmOpts.theme;
          }, setUrl);
        }

        runnerFrameService.ready().then(function () {
          // once the runner has loaded, clear our loading alert
          clearLoadingAlert(true);
          if (ctrl.response) {
            if (!ctrl.outputWatcher) {
              ctrl.outputWatcher = $scope.$watch('$ctrl.response', function () {
                displayResponse(ctrl.response);
              });
            } else if (force) {
              displayResponse(ctrl.response);
            }
          } else if (ctrl.initialResponse && !ctrl.initialResponse.$mock) {
            displayResponse(ctrl.initialResponse);
          }

          ctrl.onReady({
            setResponse: function setResponse(response) {
              ctrl.response = response;
              displayResponse(response);
            }
          });
        });
      }

      ctrl.$onInit = loadRunner;

      ctrl.reload = function (force) {
        if (runnerIFrame) {
          // force reloading of the iframe
          runnerIFrame.attr('src', 'about:blank');
        }
        loadRunner(force);
      };

      $scope.$on('$destroy', function () {
        if (runnerIFrame.parent()[0] === $element[0]) {
          // save this for later if it's not already been moved
          runnerIFrame.attr('src', 'about:blank');
          runnerIFrame.detach();
          // make sure we clear any waiting processes
          runnerFrameService.reset();
        }

        // if we're the top frame, try to reload the next frame in a tick (allows for multiple frames
        // to get removed if we're shutting down.
        if (runnerFrames[0] === ctrl && runnerFrames.length > 1 && !runnerFrames.reloadTimeout) {
          runnerFrames.reloadTimeout = $timeout(function () {
            if (runnerFrames.length) {
              runnerFrames[0].reload(true);
            }
            runnerFrames.reloadTimeout = null;
          });
        }
        // remove this frame from anywhere in the set
        remove(runnerFrames, ctrl);
      });

      function displayResponse(response) {
        response = angular.extend({
          // stdout needs to be truthy so CR doesnt show a missing output message
          // TODO: bit of a hack so lets revisit this after the CR UI switch to vue -> entrefuse/coderunner#44
          stdout: ' ',
          stderr: '',
          completed: response.passed > 0 && response.failed === 0 && !response.stderr,
          output: []
        }, response);
        runnerFrameService.display({ response: response });
      }

      function parameterize(obj) {
        var params = [];
        angular.forEach(obj, function (value, key) {
          if (value !== undefined) {
            params.push(key + '=' + value);
          }
        });
        return params.join('&');
      }
    }]
  }).factory('runnerFrameService', ['$q', '$log', '$http', 'configService', 'timerHelper', '$sce', 'appAlert', function ($q, $log, $http, configService, timerHelper, $sce, appAlert) {
    var readyPromise = void 0;
    var connection = null;
    var childPromise = null;
    var configPromise = configService.load();
    var postDeferreds = {};
    // set by the embed app: anonymous visitors get a runner token from the embed endpoint
    var embedAuth = null;

    // Methods the runner frame (v2) calls on us through Penpal
    var parentMethods = {
      // the frame calls this for a token and again when the current one is close to expiring
      authorize: function authorize() {
        return (embedAuth ? $http.post(appConfig.baseApiRoot + 'service_auth/embed', embedAuth) : $http.post(appConfig.baseApiRoot + 'service_auth', {
          service: 'runner'
        })).then(function (resp) {
          return resp.data.token;
        },
        // Penpal can't clone an $http response, so reject with an Error
        function (resp) {
          throw new Error('service_auth failed (HTTP ' + (resp && resp.status) + ')');
        });
      },
      onReady: function onReady() {
        if (readyPromise) readyPromise.resolve();
      },
      onReceipt: function onReceipt() {}
    };

    return {
      init: init,

      /**
       * Authorize runs through the anonymous embed endpoint instead of a user session
       * @param auth {{embedClientKey: String, challengeId: String}}
       */
      setEmbedAuth: function setEmbedAuth(auth) {
        embedAuth = auth;
      },


      /**
       * posts a message to our remote code runner through an iframe
       * @param data {object} should be an object with the following values:
       * @param data.ciphered {Array<String>} Array of fields which are encrypted
       * @param data.code {String} solution code
       * @param data.files {Array<{path,contents,encrypted}>} Array of file contents
       * @param data.fixture {String} test cases
       * @param data.language {String} language to run in
       * @param data.languageVersion {String} language version
       * @param data.privilegeMode {String} 'none' or 'full' depending on what to show the candidate
       * @param data.relayId {String}
       * @param data.services {Array<String>} List of services to enable
       * @param data.setup {String} Setup code
       * @param data.successMode {String}
       * @param data.testFramework {String} Testing framework to run
       * @param data.maxBufferSize {Number} override the max buffer size set internally
       * @returns {Promise} Deferred Promise for a the response
       */
      run: function run(data) {
        if (!data || !(data.code || data.files && data.files.length)) {
          appAlert.warning('Nothing to run! Please add some code and try again.');
          return $q.reject();
        }
        data = Object.assign({
          maxBufferSize: MAX_BUFFER_SIZE
        }, data);
        return post('run', data, true).catch(function (e) {
          if (!(e && e.canceled)) {
            if (e && !e.runnerFrameReset && !e.timeout) {
              $log.error(e);
            }
            appAlert.warning('There was an error running your code. Please try again.' + (e && e.timeout ? ' (' + e.msg + ')' : ''));
          }
          return $q.reject(e);
        });
      },

      reset: reset,
      connect: connect,
      setTheme: setTheme,
      display: function display(data) {
        return post('display', data);
      },
      ready: function ready() {
        return readyPromise ? readyPromise.promise : $q.reject();
      },
      getRunnerUrl: getRunnerUrl
    };

    function init() {
      readyPromise = $q.defer();
    }

    /**
     * Connects to the runner frame. Must be called after the frame's src is set, and before it loads.
     * @param iframe {HTMLIFrameElement}
     * @param connectFn {Function} Penpal's connectToChild, overridable for tests
     */
    function connect(iframe) {
      var connectFn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : connectToChild;

      disconnect();
      // No handshake timeout: Penpal destroys the connection when it fires, so a slow
      // frame could never connect. The loading alert already covers the 20s error.
      var conn = connectFn({ iframe: iframe, methods: parentMethods });
      connection = conn;
      childPromise = conn.promise;
      // destroying a connection rejects its promise, which is expected
      childPromise.catch(function () {});
    }

    function disconnect() {
      if (connection) {
        try {
          connection.destroy();
        } catch (err) {
          /* ignore errors */
        }
      }
      connection = null;
      childPromise = null;
    }

    function setTheme(theme) {
      if (!childPromise) return $q.resolve();
      return $q.when(childPromise.then(function (child) {
        return child.setOptions({ theme: theme });
      })).catch(function (e) {
        return $log.warn(e);
      });
    }

    function post(method, data, showRunTimeout) {
      var postDeferred = $q.defer();
      var messageId = messageIdPrefix + ++messageIdCounter;
      postDeferreds[messageId] = postDeferred;
      var runTimeout = null;
      // cleared once the request settles (canceled, timed out or reset), so a frame that
      // connects late doesn't start a run nobody is waiting for
      var active = true;

      if (!childPromise) {
        rejectPostPromise(messageId, new Error('[RunnerFrame] No iFrame'));
      } else {
        $q.when(childPromise.then(function (child) {
          return active && child[method](data);
        })).then(function (response) {
          if (response && response.error) {
            rejectPostPromise(messageId, response.error);
          } else {
            resolvePostPromise(messageId, response && response.result);
          }
        }).catch(function (err) {
          $log.error(err);
          rejectPostPromise(messageId, err);
        });

        if (showRunTimeout) {
          // if we dont' get a response within 15s, give the user a chance to cancel the run
          runTimeout = timerHelper.longTimeout(function () {
            var msg = $sce.trustAsHtml('<span class="loading-spinner"></span> <strong>Waiting on Results...</strong> If you are experiencing issues running your code, you may cancel the run.');

            appAlert.info(msg, {
              showFor: 40000,
              linkTitle: 'Cancel Run',
              linkAction: function linkAction() {
                appAlert.clear();
                postDeferred.reject({ canceled: true });
              }
            });
            // after another 45s (60 total), we'll re-enable the buttons so the UI doesn't lock up
            runTimeout = timerHelper.longTimeout(function () {
              appAlert.clear();
              postDeferred.reject({
                timeout: true,
                msg: 'No response in 60 seconds'
              });
            }, 45000);
          }, 15000);
        }
      }
      postDeferred.promise.finally(function () {
        active = false;
        timerHelper.longTimeout.cancel(runTimeout);
      });
      return postDeferred.promise;
    }

    /**
     * Resets any running requests.
     */
    function reset() {
      var err = new Error('[RunnerFrame] Runner Frame Reset');
      err.runnerFrameReset = true;
      rejectPostPromise(null, err);
    }

    function getRunnerUrl() {
      return configPromise.then(function (cfg) {
        return cfg.runnerClientUrl;
      });
    }

    /**
     *
     * @param responseMessage {*} If string, the key for the deferred, otherwise try to look up the
     * messageId based on the passed in object
     * @param remove {boolean} if true, also delete the postDeferred at the same time
     * @returns {Deferred?}
     */
    function getPostDeferred(responseMessage) {
      var remove$$1 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

      var messageId = null;
      if (angular.isString(responseMessage)) {
        messageId = responseMessage;
      } else if (responseMessage) {
        if (responseMessage.messageId) {
          messageId = responseMessage.messageId;
        } else if (responseMessage.message) {
          messageId = responseMessage.message.messageId;
        }
      }
      if (messageId) {
        var postDeferred = postDeferreds[messageId];
        if (remove$$1) delete postDeferreds[messageId];
        return postDeferred;
      }
    }

    function resolvePostPromise(responseMessage) {
      var postDeferred = getPostDeferred(responseMessage, true);
      if (postDeferred) {
        for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }

        postDeferred.resolve.apply(postDeferred, args);
      }
    }

    function rejectPostPromise(responseMessage) {
      for (var _len2 = arguments.length, args = Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }

      if (!responseMessage) {
        Object.keys(postDeferreds).forEach(function (k) {
          return rejectPostPromise.apply(undefined, [k].concat(args));
        });
      } else {
        var postDeferred = getPostDeferred(responseMessage, true);
        if (postDeferred) {
          postDeferred.reject.apply(postDeferred, args);
        }
      }
    }
  }]);

  var NOTES_DEBOUNCE_MS = 2000;

  // How long to hold a notes edit before saving. null means no save is needed (the edit rides along with other saves); 0 means save now.
  // - Notes that aren't scored don't schedule a save, since the debounce only avoids a revision per keystroke.
  // - A pending edit is never held past the time limit, and once the limit has passed the edit is
  //   saved immediately so it lands inside the server's grace window instead of after it.
  function notesDebounceMs(_ref) {
    var scored = _ref.scored,
        timed = _ref.timed,
        timerStartedAt = _ref.timerStartedAt,
        timeLimitMin = _ref.timeLimitMin;
    var now = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Date.now();

    if (!scored) return null;
    if (!timed || !timerStartedAt) return NOTES_DEBOUNCE_MS;
    var limitEnd = new Date(timerStartedAt).getTime() + timeLimitMin * 60000;
    return Math.max(0, Math.min(NOTES_DEBOUNCE_MS, limitEnd - now));
  }

  /**
   * Provides a way to access the currently active stateParams, which
   * are different during a stateChange than they are afterwards.
   */

  var uiRouterActiveParamsHelperModule = angular.module('Q.helpers.ui-router-active-params', []).config(['$provide', '$stateProvider', function ($provide, $stateProvider) {
    // gets a copy of the internal method from ui-router used to look up a parent
    // Note: this method has nothing to do with normal decorators, it's internal to ui-router.
    var parentGetter = $stateProvider.decorator('parent');

    return $provide.decorator('$state', ['$delegate', '$rootScope', function ($delegate, $rootScope) {
      var activeState = null;
      var activeParams = null;

      // prevent throwing an error on `go` if `state` is null or undefined
      var _go = $delegate.go;
      $delegate.go = function (state) {
        for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }

        if (state === null || state === undefined) return;
        return _go.apply(undefined, [state].concat(args));
      };

      /**
       * Returns true if the parentState is a parent of childState.
       * @param parentState {String|Object} parent ui state
       * @param childState {String|Object} child ui state
       * @returns {boolean}
       */
      $delegate.isParent = function (parentState, childState) {
        if (angular.isString(parentState)) parentState = $delegate.get(parentState);
        if (!parentState || !parentState.name) return false;

        if (angular.isString(childState)) childState = $delegate.get(childState);

        while (childState && childState.name) {
          if (parentState.name === childState.name) return true;
          childState = parentGetter(childState);
        }
        return false;
      };
      var isParent = $delegate.isParent;

      /**
       * Returns the correct active or current parameters for the provided state.
       * If the state isn't currently loaded or being loaded, returns false
       * @param state {String|Object} State we're trying to get parameters for
       * @param filter {boolean} If true, filter the result to only the params defined on the state
       * @param exclude {Array} A list of additional keys to exclude
       * @returns {Object|false} Parameters if we have them, false otherwise
       */
      $delegate.paramsFor = function (state) {
        var filter = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
        var exclude = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];

        var params = false;
        if (activeState === state || isParent(state, activeState)) {
          // use the active params by default, for when we're switching state params only
          params = activeParams;
        } else if ($delegate.includes(state)) {
          params = $delegate.params;
        }
        if (params && filter) {
          var paramKeys = Object.keys($delegate.get(state).params);
          var newParams = {};
          paramKeys.filter(function (k) {
            return exclude.indexOf(k) === -1;
          }).forEach(function (k) {
            newParams[k] = params[k];
          });
          params = newParams;
        }
        return params;
      };

      $delegate.toggleParam = function (name, value) {
        var defaultValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;

        return angular.equals(toString($delegate.activeParams[name]), toString(value)) ? defaultValue : value;
      };

      Object.defineProperties($delegate, {
        /**
         * Returns the active state name. If we're in-transition, it's the toState, otherwise
         * it's $state.current.name.
         */
        activeState: {
          get: function get() {
            return activeState || $delegate.current.name;
          }
        },
        /**
         * Returns the active state parameters. If we're in-transition, it's the toParams, otherwise
         * it's $state.params.
         */
        activeParams: {
          get: function get() {
            return activeParams || $delegate.params;
          }
        }
      });

      $rootScope.$on('$stateChangeStart', function (event, toState, toParams) {
        if (!event.defaultPrevented) {
          activeState = toState.name;
          activeParams = toParams;

          if ($delegate.transition) {
            $delegate.transition.finally(clear);
          }
        }
      });

      function clear() {
        activeState = null;
        activeParams = null;
      }

      $rootScope.$on('$stateChangeSuccess', clear);

      $rootScope.$on('$stateChangeError', clear);

      return $delegate;
    }]);
  }]);

  var scopedKeyHelperModule = angular.module('Q.helpers.scoped-key', [uiRouterStateModule, uiRouterActiveParamsHelperModule])

  /* eslint no-unused-vars: [2, { "args": "none" }] */ // fixes bug in ESLint that isn't seeing the usage in the default argument below
  .factory('scopedKey', ['$state', '$stateParams', '$location', function ($state, $stateParams, $location) {
    var FN_PREFIX = '?';
    var STATE_PREFIX = '@';
    var URL_PREFIX = '~';
    var STRING_PREFIX = '=';
    var PARENT_STATE_MATCHER = /\.\*$/;

    /**
     * Returns a key based on a given scope value.
     *
     * Note: It's confusing, but there really isn't a better word for this intended usage
     * than "scope".
     *
     * @param scope One of the following:
     *      'state' = current state id
     *      '<state>.*' = Matches specified parent state or any descendents
     *      array = use current state id followed by specified $stateParams
     *      'url' = full url without the hash
     *      function = return a string of your choosing
     *      anything else = converted to string, used as is
     *
     * @param stateName If provided, use this as the state name for 'state' and array scope
     * @param stateParams If provided, use this as the stateParams for array scope
     * @param url If provided, use this as the url for 'url' scope
     *
     * @returns {String}
     *
     */
    function getScopedKey(scope) {
      var stateName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : $state.current.name;
      var stateParams = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : $stateParams;
      var url = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : $location.url();

      var key = '';

      if (angular.isFunction(scope)) {
        // user-defined
        key = FN_PREFIX + String(scope(stateName, stateParams, url));
      } else if (angular.isArray(scope)) {
        // state + params
        key = STATE_PREFIX + stateName + _getStateParams(scope, stateParams);
      } else if (scope === 'state') {
        // state id
        key = STATE_PREFIX + stateName;
      } else if (PARENT_STATE_MATCHER.test(scope)) {
        key = STATE_PREFIX + (_findParentScope(scope.replace(PARENT_STATE_MATCHER, ''), stateName) || stateName);
      } else if (scope === 'url') {
        // url
        key = URL_PREFIX + url.replace(/#.*/, '');
      } else {
        // some other thing
        key = STRING_PREFIX + String(scope);
      }

      return key;
    }

    /**
     * Works the same as scopedKey(), except this defaults to the activeState and activeParams, meaning if we're
     * in the middle of a transition (ie: a resolve), these will be the "to" state and params, rather than the
     * current state and params.
     *
     * @param scope
     * @param stateName
     * @param stateParams
     * @param url
     * @returns {String}
     */
    getScopedKey.activeState = function (scope) {
      var stateName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : $state.activeState;
      var stateParams = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : $state.activeParams;
      var url = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : $location.url();

      return getScopedKey(scope, stateName, stateParams, url);
    };

    return getScopedKey;

    //// private methods

    function _getStateParams(scope, stateParams) {
      return '({' + scope.filter(function (k) {
        return stateParams[k] !== undefined;
      }).map(function (k) {
        return k + ':' + String(stateParams[k]);
      }).join(',') + '})';
    }

    function _findParentScope(parent, stateName) {
      if (!stateName) {
        return null;
      } else if (stateName === parent) {
        // at parent
        return parent;
      } else if (new RegExp('(^|\\.)' + parent + '\\.').test(stateName)) {
        // at parent.child
        return parent;
      } else {
        // look for manual parents
        return _findParentScope(parent, $state.get(stateName).parent);
      }
    }
  }]);

  /* eslint-disable angular/timeout-service */

  var DEFAULT_MESSAGES = {
    unloadMessage: 'You have unsaved changes that will be lost if you leave this page. Are you sure you want to leave this page?',
    routeChangeMessage: '<p>You have unsaved changes that will be lost if you leave this page.</p><p>Are you sure you want to leave this page?</p>',
    routeChangeWithSaveMessage: '<p>You have unsaved changes.</p><p>Would you like to save them before continuing?</p>'
  };

  var isDialogVisible = false;

  var warnOnChangesComponentModule = angular.module('Q.components.forms.warn-on-changes', [dialogComponentModule])

  /**
   * @description
   * Allows a generic form to alert the user if they are going to lose changes
   * The warnOnChanges attribute can take an optional hash of these properties:
   *
   * - handleSave A function that will be called if the user clicks "Save" on the Save/Discard/Cancel dialog.
   * - handleDiscard A function that will be called if the user clicks "Discard" on the Save/Discard/Cancel dialog.
   * - handleFinish A function that will be called if the user leaves the view, either by clicking Discard or if the
   *       form did not have any changes, or after the user successfully saves
   * - unloadMessage Override default message to show if the page changes
   * - routeChangeMessage Override message to show if the route changes
   * - routeChangeWithSaveMessage Override message to show if the route changes and you provide an handleSave method
   *
   */
  .directive('warnOnChanges', ['warnOnChangesService', function (warnOnChangesService) {
    return {
      restrict: 'A',
      require: 'form',
      link: function link(scope, elem, attrs, ngForm) {
        var options = scope.$eval(attrs.warnOnChanges) || {};

        warnOnChangesService.enable(angular.extend(options, {
          hasChanges: function hasChanges() {
            return ngForm.$dirty;
          }
        }));

        scope.$on('$destroy', warnOnChangesService.disable);
      }
    };
  }]).factory('warnOnChangesService', ['$window', '$rootScope', '$state', '$q', 'dialogService', function ($window, $rootScope, $state, $q, dialogService) {
    var _options = void 0;
    var _stateListener = void 0;
    var _logoutListener = void 0;
    var _warnBeforeClosingListener = void 0;

    return {
      enable: enable,
      disable: disable
    };

    /**
     * Parameters are provided as an object
     * @param options Options
     * @param options.hasChanges Required: a method that determines if there are any changes
     * @param options.ignoreRouteChange: optional method that should return true if the route change is ignored
     * @param options.handleSave A function that will be called if the user clicks "Save" on the Save/Discard/Cancel dialog.
     * @param options.handleDiscard A function that will be called if the user clicks "Discard" on the Save/Discard/Cancel dialog.
     * @param options.handleFinish A function that will be called if the user leaves the view, either by clicking Discard or if the
     *       form did not have any changes
     * @param options.unloadMessage Override default message to show if the page changes
     * @param options.routeChangeMessage Override message to show if the route changes
     * @param options.routeChangeWithSaveMessage Override message to show if the route changes and you provide an handleSave method
     * @returns {{}}
     */
    function enable(options) {
      _options = angular.extend({
        ignoreRouteChange: angular.noop,
        handleDiscard: angular.noop,
        handleFinish: angular.noop
      }, DEFAULT_MESSAGES, options || {});

      if (!_options.hasChanges) {
        throw new Error('warnOnChangesService: Missing required option hasChanges!');
      }

      // listen to whole page changes
      _warnBeforeClosingListener = warnBeforeClosing(_onBeforeUnload);
      // listen to state changes
      _stateListener = $rootScope.$on('$stateChangeStart', _onBeforeStateChange);
      // listen to logout
      _logoutListener = $rootScope.$on('app.beforeLogout', _onBeforeLogout);
    }

    function disable() {
      if (_warnBeforeClosingListener) _warnBeforeClosingListener();
      if (_stateListener) _stateListener();
      if (_logoutListener) _logoutListener();
      _stateListener = null;
      _logoutListener = null;
      $window.setTimeout(function () {
        return _options = null;
      });
    }

    //// private functions

    /**
     * Used to handle the window onBeforeUnload event.
     * @returns {*} Message if there are changes
     * @private
     */
    function _onBeforeUnload(evt) {
      if (isDialogVisible || !_options) return undefined;

      if (_options.hasChanges(evt)) {
        if (_options.warnOnUnload === 'auto') {
          if (_options.handleSave) {
            _options.handleSave();
          }
          _options.handleFinish();
        }
        // we can't clean up if they close the page even after the warning
        return angular.isFunction(_options.unloadMessage) ? _options.unloadMessage() : _options.unloadMessage;
      } else {
        _options.handleFinish();
      }
    }

    /**
     * Handles state change events.  If there is a change, AND we are not navigating to the same
     * scope (as in, scopedKey scope, not angular scope), we stop the navigation and throw up a
     * dialog.
     *
     * If the developer provided a save callback, we can use a dialog that provides three options:
     *     - Save: save and go
     *     - Discard: forget changes and go
     *     - Cancel: don't go
     *
     * @param evt State change event
     * @param toState State we are transitioning to
     * @param toParams StateParams we are transitioning to
     * @param fromState State we are transitioning from
     * @param fromParams StateParams we are transitioning from
     * @param options State change options
     * @returns {Promise}
     * @private
     */
    function _onBeforeStateChange(evt, toState, toParams, fromState, fromParams, options) {
      if (!_stateListener || !_options) return $q.reject();

      if (toState && toState.name === 'error') return $q.reject();

      if (_options.ignoreRouteChange(evt, toState, toParams, fromState, fromParams, options)) return $q.reject();

      return _onBeforeEvent(evt).then(function () {
        if (_options) {
          // async check!
          _options.handleFinish();
        }
        disable();
        $state.transitionTo(toState.name, toParams, options);
      });
    }

    /**
     * Handles logout events.  If there is a change we stop the navigation and throw up a dialog.
     *
     * If the developer provided a save callback, we can use a dialog that provides three options:
     *     - Save: save and go
     *     - Discard: forget changes and go
     *     - Cancel: don't go
     *
     * @param evt State change event
     * @returns {Promise}
     * @private
     */
    function _onBeforeLogout(evt) {
      if (evt.defaultPrevented || !_logoutListener) return $q.reject();

      return _onBeforeEvent(evt, true).then(function () {
        if (_options) {
          _options.handleFinish();
        }
        disable();
      });
    }

    /**
     * Handles an event broadcast. This method does the bulk of the work handling an event broadcast.
     * It returns a promise that is resolved if the event was canceled and the event should continue,
     * or rejected if the event was canceled and should remain canceled.
     *
     * @param evt
     * @param preventDefaultOnAuto
     * @returns {Promise}
     * @private
     */
    function _onBeforeEvent(evt) {
      var preventDefaultOnAuto = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

      var _reject = $q.reject();
      var cleanup = function cleanup() {
        // clean up after ourselves if there are no changes, and we are leaving the page
        _options.handleFinish();
        disable();
        return _reject;
      };

      if (evt.defaultPrevented || !_options) return _reject;

      if (_options.warnOnUnload === 'auto') {
        if (_options.hasChanges(evt) && _options.handleSave) {
          var ret = _options.handleSave();
          if (preventDefaultOnAuto) {
            evt.preventDefault();
            evt.promise = ret;
          }
          return ret;
        } else {
          return cleanup();
        }
      }

      // allows stop-route-change-if-invalid directive to cancel this functionality
      if ($rootScope.$broadcast('warnOnChanges.shouldContinue').defaultPrevented) return _reject;

      if (_options.hasChanges(evt)) {
        isDialogVisible = true;
        evt.preventDefault();
        var msg = _options.handleSave ? _options.routeChangeWithSaveMessage : _options.routeChangeMessage;
        msg = angular.isFunction(msg) ? msg() : msg;
        if (_options.handleSave) {
          evt.promise = dialogService.save(msg).then(function (result) {
            if (_options) {
              if (result.id === 'save') {
                var _ret = _options.handleSave();
                if (_ret && _ret.then) {
                  return _ret;
                }
              } else if (result.id === 'discard') {
                _options.handleDiscard();
              }
            }
            return $q.when();
          });
        } else {
          evt.promise = dialogService.confirm(msg).then(function (result) {
            return result.id === 'yes' ? $q.when() : _reject;
          });
        }
        evt.promise.finally(function () {
          return isDialogVisible = false;
        });
        return evt.promise;
      } else {
        return cleanup();
      }
    }
  }]);

  // not constant so we can append the "app" id for unique data per "app"
  var FORM_DATA_KEY = 'FORM_DATA';
  var ALWAYS_IGNORE = ['createdAt', 'publishedAt', 'updatedAt', 'archivedAt', 'digest'];
  var FORM_DATA_CACHE_EXPIRE = 5 * 60 * 1000; // expire stale caches after 5 minutes

  var formDataServiceModule = angular.module('Q.services.form-data-service', [storageServiceModule, scopedKeyHelperModule, warnOnChangesComponentModule]).run(['$localStorage', '$rootScope', function ($localStorage, $rootScope) {
    FORM_DATA_KEY += '_' + appConfig.appId;
    $rootScope.$on('app.logout', function () {
      delete $localStorage[FORM_DATA_KEY];
      $localStorage.$apply();
    });
  }]).factory('formDataService', ['scopedKey', 'warnOnChangesService', '$localStorage', '$window', '$state', '$injector', '$timeout', function (scopedKey, warnOnChangesService, $localStorage, $window, $state, $injector, $timeout) {
    // The cache only stores the single, most recently used form data.
    // If we visit a different form, we'll overwrite it.
    // The cache also stores a timestamp, and if we exceed the cache time since it was last used, we disregard
    // updates.
    var cache = null;

    function initCache() {
      var force = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

      if (force || !$localStorage[FORM_DATA_KEY] || !$localStorage[FORM_DATA_KEY].key) {
        $localStorage[FORM_DATA_KEY] = cache = {};
      } else {
        cache = $localStorage[FORM_DATA_KEY];
      }
      var user = getUser($injector);
      if (user) {
        var id = user.id;
        if (cache._id !== id) {
          $localStorage[FORM_DATA_KEY] = cache = { _id: id };
        }
      }
    }

    function setCacheExpiration() {
      if (cache) {
        cache.stored = Date.now();
      }
    }

    return {
      create: create,
      reset: reset
    };

    /**
     * Parameters are provided as an object
     * @param options - Options
     * @param {*} options.originalData - Data that will be used on the form
     * @param {Function} options.handleSave - A function that will be called if the user clicks "Save" on the Save/Discard/Cancel dialog. Can also be set on the return object.
     * @param {string} options.id - Optional ID, defaults to data.id
     * @param {Scope} options.scope - Optional scope for the key to store this object, defaults to 'state' @see scopedKeys helper
     * @param {Array<string>} options.ignoredProperties - A list of properties to ignore on the data object
     * @param {boolean} options.warnOnUnload - If you don't want to warn the user if they leave this scope or unload the page, pass in false
     * @param {string} options.unloadMessage - Override default message to show if the page changes
     * @param {string} options.routeChangeMessage - Override message to show if the route changes
     * @param {string} options.routeChangeWithSaveMessage-  Override message to show if the route changes and you provide an handleSave method
     * @param {boolean} options.storeChanges - If set to false, the service will not store a copy of the changes in $localStorage (useful for autosave forms)
     * @param {boolean} options.autoRestoreCache - If true, automatically restores the cache if we have one
     * @param {boolean} options.resetCache - If true, clears any previous cached value
     * @param {Function} options.isDiff - If provided, used when diffing for hasChanges. Can also be set on the return object
     * @returns {{}}
     */
    function create(options) {
      var originalData = options.originalData,
          handleSave = options.handleSave,
          _options$id = options.id,
          id = _options$id === undefined ? originalData.id : _options$id,
          _options$scope = options.scope,
          scope = _options$scope === undefined ? 'state' : _options$scope,
          _options$ignoredPrope = options.ignoredProperties,
          ignoredProperties = _options$ignoredPrope === undefined ? [] : _options$ignoredPrope,
          _options$warnOnUnload = options.warnOnUnload,
          warnOnUnload = _options$warnOnUnload === undefined ? true : _options$warnOnUnload,
          _options$storeChanges = options.storeChanges,
          storeChanges = _options$storeChanges === undefined ? true : _options$storeChanges,
          _options$autoRestoreC = options.autoRestoreCache,
          autoRestoreCache = _options$autoRestoreC === undefined ? false : _options$autoRestoreC,
          _options$resetCache = options.resetCache,
          resetCache = _options$resetCache === undefined ? false : _options$resetCache;


      var key = _getKey(scope, id);
      var data = Object.create(originalData); // our active form data
      var dataSyncCopy = Object.create(originalData); // a copy of the original data, for 3-way diffing
      var cacheCopy = null; // backup of copy from the cache

      var ignorePropertyRegex = new RegExp([].concat(ignoredProperties, ALWAYS_IGNORE).join('|'));
      var isTrackedProperty = function isTrackedProperty(k) {
        var tracked = false;
        if (k && !ignorePropertyRegex.test(k) && originalData.propertyIsEnumerable(k)) {
          var pd = Object.getOwnPropertyDescriptor(originalData, k);
          // ignore any properties that are not writeable or settable, or functions
          if (!pd || pd.configurable && (pd.writable || pd.set) && !angular.isFunction(originalData[k])) {
            tracked = true;
          }
        }
        return tracked;
      };

      var fd = {
        forms: [], // array of associated forms
        data: data,
        originalData: originalData,
        handleSave: handleSave,
        updateData: updateData,
        syncUnchanged: syncUnchanged,
        reset: reset,
        hasCache: hasCache,
        restoreCache: restoreCache,
        initForm: initForm,
        finish: finish,
        cancel: cancel,
        close: close,
        hasChanges: hasChanges,
        getChanges: getChanges,
        isDiff: options.isDiff || isDiff
      };

      reset();

      if (storeChanges) {
        initCache();
        if (!resetCache && cache.key === key && cache.data && cache.stored > Date.now() - FORM_DATA_CACHE_EXPIRE) {
          // the cache copy is stored on FD for debugging purposes
          fd.$cacheCopy = cacheCopy = parseDates(angular.copy(cache.data));
          if (autoRestoreCache) restoreCache();
        }
        cache.key = key;
        cache.data = data;
        setCacheExpiration();
      }

      if (warnOnUnload) {
        warnOnChangesService.enable(angular.extend({}, options, {
          hasChanges: function hasChanges() {
            // enables overriding hasChanges
            return fd.hasChanges();
          },
          ignoreRouteChange: function ignoreRouteChange(evt, toState, toParams) {
            var fromScope = scopedKey(scope);
            var toScope = scopedKey(scope, toState.name, toParams, $state.href(toState.name, toParams));

            return fromScope === toScope;
          },
          handleSave: function handleSave() {
            return options.handleSave();
          },
          handleFinish: function handleFinish() {
            return fd.finish();
          }
        }));
      }

      return fd;

      //// public methods

      function initForm(scope, form) {
        fd.forms.push(form);
        // start listening for model changes
        scope.$on('socket:model.changed', _syncUnchangedDebounce);
        scope.$on('model.changed', function () {
          return _syncUnchangedDebounce;
        });
        // cleanup for multiple forms
        scope.$on('$destroy', function () {
          remove(fd.forms, form);
        });
      }

      /**
       * Replace the current "original data" with new data.  Does NOT reset the form data,
       * to do so call reset() after this method.
       *
       * If we want to retain the current changed form data, we can use syncUnchanged(),
       * which only resets fields that have have not been changed by the user.
       *
       * @param newData
       */
      function updateData(newData) {
        fd.originalData = originalData = newData;
      }

      /**
       * Gets all the keys in the original data, minus
       * the ignored properties
       * @returns {Array.<T>}
       */
      function dataKeys() {
        return Object.keys(originalData).filter(isTrackedProperty);
      }

      /**
       * Resets the form data to the original object
       */
      function reset(k) {
        if (k) {
          data[k] = angular.copy(originalData[k]);
          // this copy tracks 3-way changes
          dataSyncCopy[k] = angular.copy(originalData[k]);
        } else {
          empty$1(data);
          empty$1(dataSyncCopy);
          dataKeys().forEach(reset);
        }

        setCacheExpiration();
      }

      /**
       * Returns true if there is a cached copy
       * @returns {boolean}
       */
      function hasCache() {
        return !!cacheCopy && hasChanges(null, cacheCopy);
      }

      /**
       * Restores the cache copy (if there is one)
       */
      function restoreCache() {
        if (!cacheCopy) return;

        angular.forEach(cacheCopy, function (v, k) {
          if (isTrackedProperty(k)) {
            data[k] = v;
          }
        });

        if (fd.afterRestoreCache) {
          fd.afterRestoreCache();
        }

        // Should we ever enable re-restoring cache changes?
        cacheCopy = null;
        setCacheExpiration();
      }

      /**
       * Synchronizes all unchanged values back from the original data object
       */
      function syncUnchanged(k) {
        if (!k || !angular.isString(k)) {
          dataKeys().forEach(syncUnchanged);
        } else if (angular.equals(data[k], dataSyncCopy[k]) && !angular.equals(data[k], originalData[k])) {
          data[k] = angular.copy(originalData[k]);
          dataSyncCopy[k] = angular.copy(originalData[k]);
        }
        setCacheExpiration();
      }

      function _syncUnchangedDebounce() {
        if (_syncUnchangedDebounce.timer) return;
        _syncUnchangedDebounce.timer = $timeout(function () {
          _syncUnchangedDebounce.timer = null;
          syncUnchanged();
        }, 10);
      }

      /**
       * Used to end working with the form via user action.
       * This method will:
       *     - clear the window beforeunload handler if used
       *     - clear the state change listener if used
       *     - Remove the saved data from the cache
       */
      function finish() {
        if (cache) {
          initCache(true);
        }
        _stopListening();
      }

      /**
       * Used to stop listening for changes and terminate this form data object.
       * Only necessary when switching between routes that are editing the same object.
       */
      function cancel() {
        _stopListening();
      }

      /**
       * Closes this form data. If there are no changes (i.e., it's saved), we also clear the cache
       */
      function close() {
        if (hasChanges()) {
          cancel();
        } else {
          finish();
        }
      }

      /**
       * Checks to see if there are differences between the original object
       * and the current one.
       * @returns {boolean}
       */
      function hasChanges(k, compareData) {
        compareData = compareData || originalData;
        var changes = false;
        if (angular.isString(k)) {
          changes = isTrackedProperty(k) && !fd.isDiff(data[k], compareData[k], k);
        } else {
          changes = Object.keys(data).some(function (k) {
            return isTrackedProperty(k) && fd.isDiff(compareData[k], data[k], k);
          });
        }
        if (changes) {
          setCacheExpiration();
        }
        return changes;
      }

      /**
       * Simple method to return top-level changes between the data object and the
       * original object. This method considers the whole sub-object changed if any
       * part of it changed
       */
      function getChanges(compareData) {
        var changes = diff(compareData || originalData, data, function (k) {
          return !isTrackedProperty(k);
        });
        if (!changes.$empty) {
          setCacheExpiration();
        }
        return changes;
      }

      //// private

      function _stopListening() {
        if (warnOnUnload) {
          warnOnChangesService.disable();
        }
      }
    }

    function reset() {
      delete $localStorage[FORM_DATA_KEY];
      $localStorage.$apply();
    }

    //// private functions

    function _getKey(scope, id) {
      return scopedKey.activeState(scope) + '(' + id + ')';
    }
  }]);

  var formDataComponentModule = angular.module('Q.components.forms.form-data', [dialogComponentModule]).factory('formDataRestorePrompt', ['dialogService', '$q', '$state', '$rootScope', function (dialogService, $q, $state, $rootScope) {
    return {
      prompt: function prompt(fd) {
        var item = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'this item';

        return $q.when(fd.hasCache()).then(function (cache) {
          if (cache && !$state.current || !$state.current.name) {
            return $q(function (resolve) {
              var unlisten = $rootScope.$on('$stateChangeSuccess', function () {
                unlisten();
                resolve(cache);
              });
            });
          }
          return cache;
        }).then(function (cache) {
          return cache && dialogService.confirm('<p>Looks like you have some changes that didn\'t get saved for ' + item + '.</p><p>Would you like to restore those changes?</p>');
        }).then(function (resp) {
          var restore = !!resp && resp.id === 'yes';
          if (restore) {
            fd.restoreCache();
          }
          return restore;
        }, function () {
          return false;
        });
      }
    };
  }]).directive('formData', function () {
    return {
      restrict: 'A',
      require: ['^form', 'formData'],
      controller: function controller() {},
      link: function link(scope, elem, attrs, _ref) {
        var _ref2 = slicedToArray(_ref, 2),
            ngForm = _ref2[0],
            formData = _ref2[1];

        var fd = scope.$eval(attrs.formData);

        formData.fd = fd;

        fd.initForm(scope, ngForm);
      }
    };
  });

  var unableToSaveDialogTemplate = "<div class=\"modal-header\"> <h4 class=\"modal-title\">Error saving your assessment solution</h4> </div> <div class=\"modal-body modal-long-form\"> <section> <p> Unable to save your code in the last {{$ctrl.attempts}} attempts. Please verify that your computer is online and connected, and try again. </p> <p class=\"alert alert-warning\" ng-if=\"$ctrl.error && $ctrl.error.message\"> {{ $ctrl.error.message }} <span ng-show=\"$ctrl.error.status\">({{$ctrl.error.status}})</span> </p> <p> Your current solution has also been stored in temporary storage, so you can also try refreshing the page. </p> <div class=\"alert alert-warning\" ng-show=\"$ctrl.tryAgainFailed\"> Still unable to save. </div> <ul class=\"action-links\" ng-disabled=\"$ctrl.saving\"> <li class=\"try-again\"> <a ng-click=\"$ctrl.tryAgain()\" ng-disabled=\"$ctrl.saving\"> <strong>Try Again</strong> if you think the network connection was down </a> </li> <li class=\"reload-page\"> <a class=\"icon-reload\" ng-click=\"$ctrl.reload()\" ng-disabled=\"$ctrl.saving\"> <strong>Reload the Page</strong>, but try to preserve any unsaved changes </a> </li> <li class=\"lose-changes\"> <a class=\"action-link-danger\" on-confirm=\"$ctrl.reload(true)\" on-confirm-title=\"Erase Changes and Reload?\" on-confirm-message=\"Are you sure you want to lose any unsaved changes?\" ng-disabled=\"$ctrl.saving\"> <strong>Lose My Changes</strong> if nothing else works </a> </li> </ul> </section> </div> ";

  /* eslint-disable no-unused-vars */

  /**
   * Error Contexts
   * @readonly
   * @enum {string}
   */
  var ERROR_CONTEXTS = {
    /** An error that occurred before the candidate can start the assessment */
    load: 'load',
    /** A repeated error saving the solution */
    saveSolution: 'saveSolution',
    /** A repeated error submitting a project challenge solution */
    submitSolution: 'submitSolution',
    /** A repeated error submitting the entire assessment */
    submitAssessment: 'submitAssessment'
  };

  // Note: JSDoc cannot handle exporting an enum directly, so it has to be separate like this.
  var ERROR_CONTEXT = ERROR_CONTEXTS;

  var messageHubServiceModule = angular.module('Q.services.message-hub-service', [appAlertComponentModule]).constant('embedOptions', {}).factory('messageHubService', ['$window', '$document', 'appAlert', 'embedOptions', '$rootScope', '$q', '$http', 'authInterceptor', function ($window, $document, appAlert, embedOptions, $rootScope, $q, $http, authInterceptor) {
    var _this = this;

    var readyDeferred = $q.defer();

    var svc = {
      authorize: authorize,
      postError: postError,
      postLoaded: postLoaded,
      postLoadError: postLoadError,
      postUpdated: postUpdated,
      postSolutionUpdated: postSolutionUpdated,
      postSubmitted: postSubmitted,
      embedded: embedded,
      onSetOptions: function onSetOptions(options) {
        angular.extend(embedOptions, options);
        if (options.authToken) {
          authInterceptor.setAuthToken(options.authToken);
        }
        // we're finally ready the first time we get options
        readyDeferred.resolve(svc);
        $rootScope.$applyAsync();
      },
      whenReady: function whenReady() {
        return readyDeferred.promise;
      }
    };

    var methods = {};
    ['setOptions', 'next', 'previous', 'switchChallenge', 'submit'].forEach(function (method) {
      // wrap the event handler so we can call the method on messageHubService after it gets set
      methods[method] = function () {
        var handler = 'on' + ucFirst(method);
        return svc[handler] && svc[handler].apply(svc, arguments);
      };
    });

    var parent = void 0;

    var parentCheck = function parentCheck(fn) {
      return parent && isFunction(parent[fn]);
    };

    var connection = { destroy: noop };

    $q.resolve().then(function () {
      if (embedded) {
        connection = connectToParent({
          timeout: 10000,
          methods: methods
        });
        return connection.promise;
      } else {
        return null;
      }
    }).then(function (_parent) {
      parent = _parent;
      if (parent) {
        // Notify the outer page that the page is loaded
        parent.ready();
      } else {
        // nothing to wait for
        readyDeferred.resolve(svc);
      }
    }).catch(function (err) {
      readyDeferred.resolve(svc);
      if (err && !_this._destroyed) {
        appAlert.warning('Error connecting iframe:', err);
      }
    });

    return svc;

    function authorize(assessment) {
      return readyDeferred.promise.then(function () {
        return embedded && $http.post(appConfig.apiRoot + 'embed_assessment/' + assessment.id, {
          embedClientKey: embedOptions.embedClientKey,
          embedDomain: hostname($document[0].referrer)
        });
      }).then(function () {
        return assessment;
      }).catch(function (err) {
        var errData = err;
        if (err && err.data) {
          errData = err.data;
          errData.status = err.status;
          errData.statusText = err.statusText;
        }
        return $q.reject(errData);
      });
    }

    function _loadData(assessment, assessmentResult, challenge, stateInfo) {
      var stageId = challenge ? challenge.id : stateInfo ? stateInfo.id : undefined;
      var challengeItems = assessmentResult ? assessmentResult.getChallengeItems() : assessment.challengeItems;
      var stageIndex = stateInfo ? stateInfo.index : 1 + challengeItems.findIndex(function (ci) {
        return ci.challengeId === stageId;
      });
      return {
        assessmentId: assessment.id,
        title: assessment.title,
        summary: assessment.summary,
        assessmentResultId: assessmentResult ? assessmentResult.id : null,
        started: !!assessmentResult && assessmentResult.isActive(),
        submitted: !!assessmentResult && assessmentResult.isSubmitted(),
        startedAt: toISOString(assessmentResult && assessmentResult.startedAt),
        timeLimit: assessmentResult ? assessmentResult.timeLimit() : null,
        cutOffTime: assessmentResult ? assessmentResult.cutOffTime() : null,
        score: assessmentResult ? assessmentResult.score : 0,
        challenges: challengeItems.map(function (ci) {
          return _challengeData(ci.challenge, assessmentResult);
        }),
        stageId: stageId,
        stageIndex: stageIndex
      };
    }

    function _challengeData(challenge, assessmentResult) {
      var data = {
        id: null
      };
      if (challenge) {
        data.id = challenge.id;
        data.index = assessmentResult && 1 + assessmentResult.getChallengeItems().findIndex(function (ci) {
          return ci.challengeId === challenge.id;
        });
        data.title = challenge.title;
        data.type = challenge.$type;
        data.summary = challenge.summary;
        var sol = assessmentResult && assessmentResult.solutions.find(function (sol) {
          return sol.challengeId === challenge.id;
        });
        data.started = !!sol;
        if (sol) {
          data.solutionId = sol.id;
          data.score = sol.score;
          data.solutionLanguage = sol.language;
          data.attempted = !!sol.attempted();
          data.completed = !!sol.passed();
        }
      }
      return data;
    }

    function _cleanUpError(context, msg) {
      var errorData = toSafeObject(msg);
      if (!isObject(errorData)) errorData = { message: errorData };
      errorData.context = context;
      return errorData;
    }

    function postError(context, msg) {
      if (!parentCheck('error')) return;
      parent.error(_cleanUpError(context, msg));
    }

    function postLoaded(_ref) {
      var assessment = _ref.assessment,
          assessmentResult = _ref.assessmentResult,
          challenge = _ref.challenge;

      if (!parentCheck('loaded')) return;
      parent.loaded(toSafeObject(_loadData(assessment, assessmentResult, challenge)));
    }

    function postLoadError(msg) {
      postError(ERROR_CONTEXT.load, msg);
      if (!parentCheck('loaded')) return;
      parent.loaded({
        error: _cleanUpError(ERROR_CONTEXT.load, msg),
        started: false
      });
    }

    function postUpdated(_ref2) {
      var assessmentResult = _ref2.assessmentResult,
          _ref2$assessment = _ref2.assessment,
          assessment = _ref2$assessment === undefined ? assessmentResult.assessment : _ref2$assessment,
          challenge = _ref2.challenge,
          stateInfo = _ref2.stateInfo;

      if (!parentCheck('updated')) return;
      parent.updated(toSafeObject(_loadData(assessment, assessmentResult, challenge, stateInfo)));

      // Also always post a solution updated event
      postSolutionUpdated({ assessmentResult: assessmentResult, challenge: challenge, stateInfo: stateInfo });
    }

    function postSolutionUpdated(_ref3) {
      var assessmentResult = _ref3.assessmentResult,
          challenge = _ref3.challenge,
          stateInfo = _ref3.stateInfo;

      if (!parentCheck('solutionUpdated')) return;
      var data = stateInfo || _challengeData(challenge, assessmentResult);
      parent.solutionUpdated(toSafeObject(data));
    }

    function postSubmitted(assessmentResult) {
      if (!parentCheck('submitted')) return;
      var data = {
        assessmentId: assessmentResult.assessmentId,
        assessmentResultId: assessmentResult.id,
        score: assessmentResult.score,
        challenges: assessmentResult.getChallengeItems().map(function (ci) {
          return _challengeData(ci.challenge, assessmentResult);
        }),
        timerRanOut: assessmentResult.timerRanOut(),
        solutionsStarted: assessmentResult.solutions.length,
        solutionsAttempted: assessmentResult.solutions.filter(function (sol) {
          return sol.attempted();
        }).length,
        solutionsCompleted: assessmentResult.solutions.filter(function (sol) {
          return sol.passed();
        }).length
      };
      parent.submitted(toSafeObject(data));
    }
  }]);

  var unableToSaveDialogComponentModule = angular.module('Q.components.unable-to-save-dialog', [modalComponentModule, formDataServiceModule, onConfirmComponentModule, messageHubServiceModule]).factory('unableToSaveDialog', ['$modal', function ($modal) {
    return {
      open: function open(_attempts, _saveFn, _resp) {
        var dialog = $modal.openOnce('unableToSaveDialog', {
          template: unableToSaveDialogTemplate,
          controller: 'UnableToSaveDialogCtrl as $ctrl',
          backdrop: 'static',
          windowClass: 'unable-to-save-dialog',
          keyboard: false,
          resolve: {
            attempts: function attempts() {
              return _attempts;
            },
            saveFn: function saveFn() {
              return _saveFn;
            },
            resp: function resp() {
              return _resp;
            }
          }
        });

        return dialog.result;
      }
    };
  }]).controller('UnableToSaveDialogCtrl', ['$modalInstance', 'attempts', 'saveFn', 'resp', 'appAlert', 'formDataService', '$window', 'messageHubService', function UnableToSaveDialogCtrl($modalInstance, attempts, saveFn, resp, appAlert, formDataService, $window, messageHubService) {
    var ctrl = this;
    ctrl.attempts = attempts;

    ctrl.error = resp && appAlert.modelErrorMessage(resp);

    var notifyError = function notifyError() {
      messageHubService.postError(ERROR_CONTEXT.saveSolution, {
        message: 'Unable to save solution',
        attempts: ctrl.attempts
      });
    };
    notifyError();

    ctrl.tryAgain = function () {
      ctrl.saving = true;
      ctrl.tryAgainFailed = false;
      saveFn().then(function () {
        $modalInstance.dismiss();
      }).catch(function (resp) {
        ctrl.error = resp && appAlert.modelErrorMessage(resp);
        ctrl.tryAgainFailed = true;
        ctrl.attempts++;
        notifyError();
      }).finally(function () {
        ctrl.saving = false;
      });
    };

    ctrl.reload = function (trashEdits) {
      ctrl.saving = true;
      if (trashEdits) {
        formDataService.reset();
      }
      $window.location.reload(true);
    };
  }]);

  var solutionAutosaveHelperModule = angular.module('Q.helpers.solution-autosave-helper', [solutionModelModule]).factory('solutionAutosaveHelper', ['$q', 'Solution', '$injector', function ($q, Solution, $injector) {
    /**
     * Handles an automatic save of a solution
     * @param {string?} tag - Optional tag for this save
     * @param {formDataService?} fd - FormDataService if available (required for saving)
     * @param {Team} team - User team model
     * @param {Solution} solution - Original solution model
     * @param {*?} cursor - Cursor data
     * @param {interactService?} interact - Interact service
     * @return {Promise|boolean} - Promise of save attempt, or false if no changes or unsaved for other reasons
     */
    function solutionAutosaveHelper(_ref) {
      var _ref$tag = _ref.tag,
          tag = _ref$tag === undefined ? 'autosave' : _ref$tag,
          fd = _ref.fd,
          team = _ref.team,
          solution = _ref.solution,
          cursor = _ref.cursor,
          interact = _ref.interact;

      // ignore autosave events if we don't have any changes
      if (fd && solution && (fd.hasChanges() || tag !== 'autosave')) {
        var changes = fd.getChanges();
        if (team) {
          changes = Object.assign({}, changes, {
            ipTracking: team.hasFeature('ipTracking')
          });
        }

        // Ignore file/code changes when being edited by others (even if they aren't currently editing)
        if (interact && !interact.isUserActiveEditor) {
          delete changes.files;
          delete changes.code;
          delete changes.testCases;
          if (!Object.keys(changes).length) {
            // no remaining changes, so just return
            return false;
          }
          // ignore cursor updates, too
          cursor = undefined;
        }

        if (solution.expired()) {
          // only allow candidate notes if the solution is expired
          if (!changes.candidateNotes) {
            return false;
          }
          changes = {
            candidateNotes: changes.candidateNotes,
            lastEditedById: getUserId($injector)
          };
        } else {
          // track additional data as needed
          changes.tag = tag;
          changes.event = tag && tag !== 'autosubmit';
          changes.cursor = cursor;
          changes.lastEditedById = getUserId($injector);
        }

        if (changes.files && changes.cursor) {
          // Also store cursor on the correct file for advanced code
          var f = changes.files.find(function (f) {
            return f.path === changes.cursor.path;
          });
          if (f) {
            f.cursor = changes.cursor;
          }
        }

        if (interact) {
          interact.markSaving();
        }

        return Solution.saveOrCreate(solution, changes, {
          ignoreLoadingBar: true
        }).then(function (resp) {
          if (interact) {
            // TODO: what happens if the save fails?
            interact.markSaved();
          }
          return resp;
        });
      } else {
        return false;
      }
    }

    return solutionAutosaveHelper;
  }]);

  var MAX_SAVE_FAILS = 5;

  var TAG_STREAM_BUFFER_LENGTH = 100;

  // When these events are matched in the stream, then we track an additional event
  // The stream is stored in time order (the oldest event is at the start of the string)
  var TAG_STREAM_EVENTS = [
  // external edits are a copy, followed by losing focus, followed by an external paste
  // We allow for multiple window blur/focus events, in case the candidate is flipping between the WebIDE and their
  // editor
  {
    matcher: /copy(-windowBlur|-windowFocus)+(-externalPaste|-largePaste)$/,
    tag: 'externalEdit'
  }];

  var challengeProcessHelperModule = angular.module('Q.routes.assessments.challenges.challenge-process-helper', [uiRouterStateModule, solutionModelModule, solutionsServiceModule, formDataServiceModule, firebaseServiceModule, formDataComponentModule, unableToSaveDialogComponentModule, keycodesModule, trackerServiceModule, solutionAutosaveHelperModule]).factory('challengeProcessHelper', ['$q', 'Solution', 'formDataService', 'formDataRestorePrompt', 'firebase', 'solutionsService', 'unableToSaveDialog', 'KeyCodes', '$timeout', '$state', 'appAlert', 'tracker', 'solutionAutosaveHelper', function ($q, Solution, formDataService, formDataRestorePrompt, firebase, solutionsService, unableToSaveDialog, KeyCodes, $timeout, $state, appAlert, tracker, solutionAutosaveHelper) {
    /**
     * Provides basic shared functionality between different types of challenge processes.
     */
    return function (process, assessmentResultProcess) {
      var activeChallengeIdRef = void 0;
      var activeChallengeIdRefUnwatch = void 0;

      process.beginChallengeKeyStroke = KeyCodes.SYMBOLS.COMMAND_KEY + ' + ' + KeyCodes.SYMBOLS.ENTER;

      process.startRun = function (type) {
        process.running = true;
        if (type) {
          firebase.set(process.firebaseRef + '/runData/runType', type);
        }
        return firebase.set(process.firebaseRef + '/runData/running', true);
      };
      process.endRun = function () {
        process.running = false;
        firebase.set(process.firebaseRef + '/runData/running', false);
      };

      assessmentResultProcess.liveInterview.setActiveChallengeId(process.challenge.id).then(function (active) {
        activeChallengeIdRef = active;
        activeChallengeIdRefUnwatch = activeChallengeIdRef.$watch(function () {
          if (process.passive && !embedded && activeChallengeIdRef.$value && activeChallengeIdRef.$value !== process.challenge.id && $state.params.challengeId === process.challenge.id) {
            // we're in the background, and the candidate switched to another challenge, so
            // we're going to a safe place, the welcome screen
            appAlert.info('We\'ve detected changes in a different tab or window. Please only work on your solutions in a single tab.', 0);
            $state.go('assessments.welcome');
          }
        });
      });

      function _saveSuccess(resp) {
        process.saveFails = 0;
        return resp;
      }

      function _saveFail(resp) {
        process.saveFails++;
        if (process.saveFails >= MAX_SAVE_FAILS) {
          // don't log network / connection errors
          if (!resp || ![-1, 502, 504, 520, 521, 523, 524, 525, 526, 527].includes(resp.status)) {
            var assessmentResult = assessmentResultProcess.assessmentResult;
            var assessment = assessmentResultProcess.assessment;
            tracker.trackError('Save Solution Failed', {
              id: process.solution.id,
              value: process.saveFails, // for GA
              numberOfFailedAttempts: process.saveFails,
              challengeId: process.challenge.id,
              solutionId: process.solution.id,
              assessmentId: assessmentResult.assessmentId,
              assessmentResultId: assessmentResult.id,
              assessmentTitle: assessment.title,
              teamCandidateId: assessmentResult.teamCandidateId,
              teamId: assessmentResult.teamId,
              team: assessment.team && assessment.team.name,
              respData: resp && resp.data,
              respConfig: resp && resp.config,
              // TODO: remove for security reasons later?
              respHeaders: resp && resp.headers && resp.headers()
            });
          }
          unableToSaveDialog.open(process.saveFails, process.resaveAfterFail, resp);
        }
        return $q.reject(resp);
      }

      process.hasEdited = false;
      process.initializing = true;

      Object.defineProperties(process, {
        originalSolution: {
          get: function get() {
            return this.fd && this.fd.originalData || null;
          }
        },
        solution: {
          get: function get() {
            return this.fd && this.fd.data || null;
          },

          // noinspection JSUnusedLocalSymbols
          // eslint-disable-next-line no-unused-vars
          set: function set(v) {
            /* ignored, can be triggered by ngModel */
          }
        },
        readOnly: {
          get: function get() {
            return !!(assessmentResultProcess.readOnly || assessmentResultProcess.background || process.initializing || process.usingAttach);
          }
        },
        restricted: {
          get: function get() {
            return this.readOnly || assessmentResultProcess.restricted;
          }
        },
        /**
         * Passive means we accept any changes from the server, since we either are in read-only mode,
         * or have not made any changes to our code.
         */
        passive: {
          get: function get() {
            // passive is true if we're in readOnly mode, and we don't have unsaved edits
            return this.readOnly && !process.hasEdited;
          }
        },
        files: {
          get: function get() {
            return this.solution && this.solution.files || [];
          }
        }
      });

      function _afterSaveOrUpdate(force) {
        if (process.cleanupAfterSave) process.cleanupAfterSave(force);
        process.solution.updateScore();
        assessmentResultProcess.refreshStageStatuses();
        if ($state.activeParams.challengeId === process.challenge.id) {
          assessmentResultProcess.postUpdated(process.challenge);
        }
      }

      var notesTimer = void 0;

      var lastCopy = null;
      var tagStream = [];

      angular.extend(process, {
        firebaseRef: assessmentResultProcess.liveInterview.firebaseRef + '/' + process.challenge.id,
        saveFails: 0,

        autosave: function autosave(tag) {
          if (assessmentResultProcess.restricted || process.readOnly && !process.usingAttach) {
            return $q.when(process.solution);
          }
          var assessment = assessmentResultProcess.assessment;

          var savePromise = solutionAutosaveHelper({
            tag: tag,
            fd: process.fd,
            team: assessment.team,
            solution: process.originalSolution,
            cursor: process.cursor,
            interact: process.interact
          });
          if (savePromise) {
            process.hasEdited = true;
            return savePromise.then(function () {
              // keep a history of recent tags, ignoring duplicate tags and generic save events
              if (tag && tag !== 'autosave' && tagStream[tagStream.length - 1] !== tag) {
                tagStream.push(tag);
                // cap tag stream length
                while (tagStream.length > TAG_STREAM_BUFFER_LENGTH) {
                  tagStream.shift();
                }
              }
              _afterSaveOrUpdate();
              return process.solution;
            }).then(_saveSuccess, _saveFail).then(function (result) {
              // TODO: this would be better if we could push up multiple tags
              // check for a known tagStream event and save it if we have one
              var tsString = tagStream.join('-');
              var tsEvent = TAG_STREAM_EVENTS.find(function (tsEvent) {
                return tsEvent.matcher.test(tsString);
              });
              if (tsEvent) {
                return process.autosave(tsEvent.tag);
              } else {
                return result;
              }
            });
          } else {
            return $q.when(process.solution);
          }
        },
        onCopy: function onCopy(copied) {
          // save that the user copied code
          $timeout(function () {
            return process.autosave('copy');
          });
          lastCopy = copied.trim();
        },
        onPaste: function onPaste(file, pasted) {
          pasted = pasted.trim();
          // only store paste events if the paste came from outside the editor & the file is an editor file (ignore comments, etc)
          if (process.files.includes(file)) {
            var tag = 'paste';
            // let externalEdit = false;
            if (pasted !== lastCopy) {
              tag = pasted.length > 50 ? 'largePaste' : 'externalPaste';
            }
            $timeout(function () {
              return process.autosave(tag);
            });
          }
        },
        initSolution: function initSolution(solution) {
          return assessmentResultProcess.startAssessmentResult().then(function () {
            if (process.challenge.isTimed() && solution.pendingTimerReset) {
              return Solution.resetTimer(solution);
            } else {
              return solution;
            }
          }).then(function (sol) {
            if (process.challenge.isTimed()) {
              process.timerStartedAt = sol.timerStartedAt;
              process.timerTimeoutSeconds = process.challenge.timeLimitMin * 60;
            }
            process.fd = formDataService.create({
              originalData: sol,
              ignoredProperties: SOLUTION_FORM_DATA_IGNORED_PROPERTIES,
              warnOnUnload: false
            });
            // enable typed methods & actions directly on the solution
            process.fd.data.$asType();
            if (!assessmentResultProcess.readOnly && !process.solution.expired()) {
              formDataRestorePrompt.prompt(process.fd, 'this solution').then(function () {
                if (process.afterInitSolution) {
                  process.afterInitSolution();
                }
                process.autosave();
              });
            }
          });
        },
        onEnter: function onEnter($scope) {
          var extraModels = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];

          $scope.$on('document:focus', function () {
            if (process.fd) {
              // try to quietly revert to the last saved version when focusing
              process.originalSolution.DSRefresh({ ignoreLoadingBar: true }).then(function () {
                if (!process.hasEdited) {
                  // try to reset the editor, but only if the candidate hasn't edited anything yet
                  process.fd.syncUnchanged();
                  _afterSaveOrUpdate();
                }
                // trigger a save of re-focusing
                process.autosave('windowFocus');
                // make sure we continue to accept server edits
                process.hasEdited = false;
              });
            }
          });
          $scope.$on('document:blur', function () {
            process.autosave('windowBlur');
            process.hasEdited = false;
            process.background = true;
          });
          var updateChanges = function updateChanges() {
            $timeout(function () {
              if (process.passive && process.fd) {
                if (!process.interact) {
                  process.fd.reset();
                  _afterSaveOrUpdate(true);
                } else if (!process.hasEdited) {
                  // try to reset the editor, but only if the candidate hasn't edited anything yet
                  process.fd.syncUnchanged();
                  _afterSaveOrUpdate();
                }
              }
            }, 1);
          };
          $scope.$on('socket:model.changed:Solution', updateChanges);
          extraModels.forEach(function (model) {
            $scope.$on('socket:model.changed:' + model, updateChanges);
          });
          process.initializing = false;

          assessmentResultProcess.postUpdated(process.challenge);
        },
        onExit: function onExit() {
          if (activeChallengeIdRefUnwatch) {
            activeChallengeIdRefUnwatch();
          }
          if (activeChallengeIdRef && activeChallengeIdRef.$value === process.challenge.id) {
            // clears the current active challenge ID reference, without going through the async route,
            // otherwise we might accidentally clear it after the user switches to a new section
            activeChallengeIdRef.$value = null;
            activeChallengeIdRef.$save();
          }
          $timeout.cancel(notesTimer);
          process.autosave();
          if (process.fd) {
            process.fd.close();
          }
        },


        _saveSuccess: _saveSuccess,
        _saveFail: _saveFail,

        mfcChallengeConfig: function mfcChallengeConfig() {
          return [{
            id: 'instructions',
            icon: 'info',
            label: 'Instructions',
            template: 'instructionsViewer',
            challenge: process.challenge,
            get language() {
              return process.language;
            }
          }, {
            id: 'candidatenotes',
            icon: 'note',
            label: 'Your Notes',
            template: 'markdown',
            alwaysEdit: true,
            get contents() {
              return process.solution.candidateNotes;
            },
            set contents(val) {
              var delay = notesDebounceMs({
                scored: process.challenge.candidateNotesScored && process.solution.isClassicCode(),
                timed: process.challenge.isTimed(),
                timerStartedAt: process.solution.timerStartedAt,
                timeLimitMin: process.challenge.timeLimitMin
              });
              // the form value stays live (so it survives a reload); only the save is debounced
              process.solution.candidateNotes = val;
              if (delay === null) return;
              // trailing debounce so scored notes don't write a revision per keystroke;
              // a 0 delay (time limit passed) saves right away. A timer that outlives a
              // tagged save is a no-op because the form has no changes left.
              $timeout.cancel(notesTimer);
              notesTimer = $timeout(function () {
                return process.autosave();
              }, delay);
            },
            get readOnly() {
              return process.solution.notesLocked();
            },
            get notice() {
              return process.solution.notesLocked() ? 'The time limit has passed, so your notes can no longer be edited.' : null;
            },
            placeholder: process.challenge.candidateNotesPlaceholder()
          }];
        }
      });

      // Initialize the existing solution if we have one
      var sol = solutionsService.get(process.challenge, assessmentResultProcess.assessmentResult);

      if (sol) {
        return process.initSolution(sol).catch(appAlert.modelError).then(function () {
          return process;
        });
      } else {
        return $q.resolve(process);
      }
    };
  }]);

  var codeChallengeProcessServiceModule = angular.module('Q.routes.assessments.challenges.code-challenge.code-challenge-process-service', [assessmentResultsServiceModule, solutionsServiceModule, firebaseServiceModule, runnerFrameComponentModule, appAlertComponentModule, challengeProcessHelperModule]).factory('codeChallengeProcessService', ['solutionsService', 'AssessmentResult', 'Solution', '$q', 'firebase', 'runnerFrameService', 'appAlert', '$log', 'assessmentResultsService', 'challengeProcessHelper', function (solutionsService, AssessmentResult, Solution, $q, firebase, runnerFrameService, appAlert, $log, assessmentResultsService, challengeProcessHelper) {
    var _languageHistories = {};
    // string values correspond to the reset routes
    var _SUBMISSION_PART = {
      TEST_CASES: 'TestCases',
      SOLUTION: 'Solution',
      ALL: ''
    };

    var _lastLanguageName = null;

    return {
      create: create
    };

    //// functions:

    function _mapHistory(from) {
      var to = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      ['code', 'hiddenPassedTestCases', 'output', 'passedTestCases', 'response', 'score', 'testCases', 'testFramework', 'totalTestCases', 'wallTime', 'weightedPassedTestCases'].forEach(function (k) {
        to[k] = from[k];
      });
      return to;
    }

    function _updateFromLanguageHistory(solution, language) {
      if (!_languageHistories[solution.id]) {
        _languageHistories[solution.id] = {};
      }
      var history = _languageHistories[solution.id];

      if (solution.language) {
        history[solution.language] = _mapHistory(solution);
      }

      solution.language = language;

      var previous = history[language];
      // if we have some history then lets use it
      if (previous) {
        _mapHistory(previous, solution);
        return true;
      } else {
        return false;
      }
    }

    function create(challenge, assessmentResultProcess) {
      var assessment = assessmentResultProcess.assessment;
      var assessmentResult = assessmentResultProcess.assessmentResult;
      var process = {
        language: null,
        challenge: challenge,
        assessmentResult: assessmentResult,
        challengeLanguage: null,
        beginChallenge: beginChallenge,
        switchToLanguage: switchToLanguage,
        reset: reset,
        resetSolution: reset.bind(null, _SUBMISSION_PART.SOLUTION),
        resetTestCases: reset.bind(null, _SUBMISSION_PART.TEST_CASES),
        resetAll: reset.bind(null, _SUBMISSION_PART.ALL),
        submitSolution: submitSolution,
        runTests: runTests,
        setResponse: setResponse
      };

      return challengeProcessHelper(process, assessmentResultProcess).then(function (process) {
        if (process.solution) {
          switchToLanguage(process.solution.language);
        } else {
          var challengeItem = assessmentResult.getChallengeItem(challenge);
          var langNameEnabled = function langNameEnabled(lName) {
            return languages[lName] && !challengeItem.disabledLanguages.includes(lName);
          };
          var defaultLanguageName = assessment.defaultLanguage;
          var defaultLanguage = challenge.languages.find(function (l) {
            return l.name === defaultLanguageName;
          });
          var lastLanguage = _lastLanguageName && challenge.languages.find(function (l) {
            return l.name === _lastLanguageName;
          });

          if (defaultLanguage && langNameEnabled(defaultLanguageName)) {
            process.challengeLanguage = defaultLanguage;
            process.challengeLanguagePicked = true;
          } else if (lastLanguage && langNameEnabled(lastLanguage.name)) {
            process.challengeLanguage = lastLanguage;
            process.challengeLanguagePicked = true;
          } else {
            // select the first language on the challenge that is not disabled
            process.challengeLanguage = challenge.languages.filter(function (l) {
              return langNameEnabled(l.name);
            })[0];
          }

          if (!process.challengeLanguage) {
            $log.error("Invariant violation: process.challengeLanguage was expected but be defined but wasn't");
            return process;
          }

          process.language = process.challengeLanguage.name;
          firebase.set(process.firebaseRef + '/runData/language', process.language);
        }

        return process;
      });

      //// functions:

      function beginChallenge() {
        return assessmentResultProcess.startAssessmentResult().then(function () {
          return solutionsService.create(challenge, assessmentResult, process.language);
        }).then(process.initSolution).then(function () {
          firebase.set(process.firebaseRef + '/runData/language', process.language);
          switchToLanguage(process.language);
        }).catch(appAlert.modelError);
      }

      function _updateTestFrameworkName() {
        if (process.challengeLanguage) {
          process.testFrameworkName = languages.frameworks[process.challengeLanguage.testFramework];
        }
        if (process.solution && process.solution.language === process.language) {
          process.testFrameworkName = languages.frameworks[process.solution.testFramework] || process.testFrameworkName;
        }
      }

      function switchToLanguage(language) {
        return $q(function (resolve, reject) {
          if (angular.isNumber(language)) {
            process.challengeLanguage = challenge.languages[language];
            language = process.challengeLanguage.name;
          } else {
            angular.forEach(challenge.languages, function (lang) {
              if (lang.name === language) {
                process.challengeLanguage = lang;
              }
            });
          }

          process.language = language;
          challenge.setCurrentLanguage(language);
          if (process.challengeLanguage) {
            process.challengeLanguagePicked = true;
          }

          // if we have a change in solution language, reset or revert it
          if (process.solution && process.solution.language !== language) {
            var updatePromise = void 0;
            if (_updateFromLanguageHistory(process.solution, language)) {
              updatePromise = process.autosave('languageChange');
            } else {
              updatePromise = process.resetAll();
            }
            updatePromise.then(process.solution.files.updatePaths).then(resolve, reject);
          } else {
            resolve();
          }
          _updateTestFrameworkName();

          firebase.set(process.firebaseRef + '/runData/language', language);

          _lastLanguageName = language;
        });
      }

      function reset() {
        var part = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';

        process.disableEditors = true;
        if (assessmentResultProcess.restricted) {
          return $q.when();
        }
        return Solution['reset' + part](process.originalSolution, {
          data: { language: process.language }
        }).then(function () {
          process.fd.reset();
          _updateTestFrameworkName();
        }).catch(appAlert.modelError).finally(function () {
          process.disableEditors = false;
        });
      }

      function submitSolution() {
        return runCode('attempt');
      }

      function runTests() {
        return runCode('test');
      }

      function setResponse(response) {
        firebase.set(process.firebaseRef + '/runData/response', response);
      }

      function runCode(type) {
        process.startRun(type);
        var solution = process.solution;

        setResponse({ result: false });

        return $q.all([
        // track an attempted run
        process.autosave('init' + ucFirst(type)).catch(function () {
          /* ignore */
        }),
        // simultaneously start running the code
        runnerFrameService.run({
          language: solution.language,
          languageVersion: process.challengeLanguage.languageVersion,
          code: solution.code,
          fixture: type === 'test' ? solution.testCases : process.challengeLanguage.testCases,
          testFramework: type === 'test' ? solution.testFramework : process.challengeLanguage.testFramework,
          ciphered: type === 'test' ? [] : ['fixture'],
          setup: process.challengeLanguage.preloaded || '',
          services: challenge.enabledServices,
          successMode: solution.successMode || challenge.successMode,
          relayId: solution.id
        }).catch(function (e) {
          if (e && e.canceled) {
            process.autosave('cancel' + ucFirst(type));
          } else {
            process.autosave('error' + ucFirst(type));
          }
          return $q.reject(e);
        })]).then(function (_ref) {
          var _ref2 = slicedToArray(_ref, 2),
              response = _ref2[1];

          appAlert.clear();
          process.runResponse = response;
          process.runResponse.type = type;
          setResponse(response);

          if (assessmentResultProcess.restricted) {
            return $q.when(response).then(process._saveSuccess);
          }

          // save results back to the server
          var run = {
            code: solution.code,
            language: solution.language,
            testCases: solution.testCases,
            response: response
          };
          var method = type === 'test' ? 'logTest' : 'logAttempt';
          return process.originalSolution[method](run).then(function () {
            process.fd.syncUnchanged();
            process.solution.updateScore();
            assessmentResultProcess.refreshStageStatuses();
            return response;
          }).then(process._saveSuccess, process._saveFail).catch(appAlert.modelError.with({
            prefix: 'There was an error saving your results:'
          }));
        }).finally(process.endRun);
      }
    }
  }]);

  var challengeCodeOfConductTemplate = "<div class=\"challenge-code-of-conduct\" ng-if=\"$ctrl.codeOfConduct && $ctrl.codeOfConduct.enabled\"> <h5>Code of Conduct</h5> <p class=\"note\" ng-if=\"$ctrl.codeOfConduct.requireAcknowledgement\"> Review each rule and confirm your acknowledgement before starting this challenge. </p> <p class=\"code-of-conduct-intro\" ng-if=\"!$ctrl.codeOfConduct.requireAcknowledgement\"> Please review what is allowed before you begin this challenge. </p> <div class=\"code-of-conduct-rule\" ng-class=\"{ acknowledged: rule.acknowledged }\" ng-repeat=\"rule in $ctrl.codeOfConduct.rules track by rule.id\"> <p class=\"rule-message\">{{ rule.message }}</p> <div class=\"qualified-checkbox code-of-conduct-acknowledge\" ng-if=\"$ctrl.codeOfConduct.requireAcknowledgement\"> <input type=\"checkbox\" id=\"{{$ctrl.checkboxId(rule)}}\" ng-model=\"rule.acknowledged\" ng-disabled=\"$ctrl.beginning\"> <label for=\"{{$ctrl.checkboxId(rule)}}\"> {{ rule.acknowledgementLabel || 'I understand and agree' }} </label> </div> </div> </div> ";

  var challengeCodeOfConductComponentModule = angular.module('Q.components.challenge-code-of-conduct', []).component('challengeCodeOfConduct', {
    template: challengeCodeOfConductTemplate,
    bindings: {
      codeOfConduct: '<',
      challengeId: '<',
      beginning: '<',
      checkboxIdPrefix: '@?'
    },
    controller: function ChallengeCodeOfConductCtrl() {
      var ctrl = this;

      ctrl.$onInit = function () {
        ctrl.checkboxIdPrefix = ctrl.checkboxIdPrefix || 'code-of-conduct';
      };

      ctrl.checkboxId = function (rule) {
        return ctrl.checkboxIdPrefix + '-' + rule.id + '-' + (ctrl.challengeId || 'challenge');
      };
    }
  });

  var languageSortFilterModule = angular.module('Q.filters.language-sort', []).filter('languageFilter', function () {
    return function languageFilter(arr) {
      return arr.filter(function (l) {
        return languages.getLang(l);
      });
    };
  }).filter('languageSort', function () {
    return function languageSort(arr, primary) {
      return arr.slice().sort(primary ? languages.primaryCompare : languages.compare);
    };
  });

  var tooltipComponentModule = angular.module('Q.components.tooltip', [bubbleDialogComponentModule])

  /**
   * @ngdoc directive
   * @name tooltip
   * @description
   * A popover tooltip shown on mouseover
   * @element ANY
   * @param {template} tooltip - Text of the tooltip
   * @param {template} alt - Text of the tooltip unless specified by {@param tooltip}
   * @param {template} tooltipPosition - Position of the tooltip
   * @param {template} tooltipDelay - Adjust how long to wait before showing, defaults to 600
   * @param {template} tooltipShortcutHint - If provided, show a shortcut key hint
   *
   * @example
   * <example>
   * <div class="btn btn-default" tooltip="Simple Tooltip">Hover me!</div>
   * </example>
   */
  .directive('tooltip', ['bubbleDialogService', '$document', '$sce', function (bubbleDialogService, $document, $sce) {
    var lastInstance = void 0;

    return {
      restrict: 'A',
      controller: function controller() {
        var ctrl = this;
        ctrl.instance = null;
        ctrl.disabled = false;
        ctrl.setDisabled = function (disabled) {
          ctrl.disabled = disabled;
          if (disabled && ctrl.instance) {
            ctrl.instance.close();
            ctrl.instance = null;
          }
        };
      },
      require: ['tooltip', '?tooltipHelpIcon'],
      link: function link(scope, element, attrs, _ref) {
        var _ref2 = slicedToArray(_ref, 2),
            tooltipCtrl = _ref2[0],
            tooltipHelpIconCtrl = _ref2[1];

        var getContent = function getContent() {
          return attrs.tooltip || attrs.alt;
        };

        var isOpen = tooltipCtrl.isOpen = function () {
          return getContent() && !tooltipCtrl.disabled && tooltipCtrl.instance && !tooltipCtrl.instance.closed;
        };

        var open = tooltipCtrl.open = function () {
          var delay = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;

          if (getContent() && !tooltipCtrl.disabled) {
            if (isOpen()) {
              if (delay === 0 && tooltipCtrl.instance && lastInstance === tooltipCtrl.instance) {
                tooltipCtrl.instance.showNow();
              }
            } else {
              if (lastInstance) {
                scope.$applyAsync(lastInstance.close);
              }
              var content = getContent();
              if (attrs.tooltipShortcutHint) {
                content = $sce.trustAsHtml('<span class="tooltip-shortcut"><span>' + content + '</span><span class="shortcut-hint">' + attrs.tooltipShortcutHint + '</span></span>');
              }
              tooltipCtrl.instance = lastInstance = bubbleDialogService.open(scope, element, {
                closeable: true,
                content: content,
                position: attrs.tooltipPosition,
                positionArrow: tooltipCtrl.helpIconElement,
                className: 'tooltip',
                delay: delay
              });
            }
          }
        };

        var close = tooltipCtrl.close = function () {
          if (tooltipCtrl.instance) {
            tooltipCtrl.instance.close();
            tooltipCtrl.instance = null;
          }
        };

        tooltipCtrl.toggle = function () {
          var delay = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;

          if (isOpen()) {
            close();
          } else {
            open(delay);
          }
        };

        var delay = function delay() {
          return +(attrs.tooltipDelay || (tooltipHelpIconCtrl ? 0 : 600));
        };

        element.on('mouseenter', function () {
          return open(delay());
        }).on('mouseleave', close);

        scope.$on('$destroy', close);
      }
    };
  }])

  // A simple component that renders a question mark in a circle, which can be used for highlighting a tooltip
  // If added below a tooltip, it enables that tooltip to be opened explicitly by clicking
  // It can also be used as the source of a tooltip (with the same click-to-show behavior)
  .component('tooltipHelpIcon', {
    require: {
      tooltipCtrl: '^tooltip'
    },
    template: '',
    controller: ['$element', '$scope', function controller($element, $scope) {
      var ctrl = this;
      ctrl.$onInit = function () {
        ctrl.tooltipCtrl.helpIconElement = $element;
      };
      $element.on('mouseenter', function (event) {
        if ($element.attr('tooltip')) return;
        stopEvent(event);
        $scope.$applyAsync(function () {
          return ctrl.tooltipCtrl.open();
        });
      });
      $element.on('click', function (event) {
        stopEvent(event);
        $scope.$applyAsync(function () {
          return ctrl.tooltipCtrl.toggle();
        });
      });
    }]
  }).directive('dropdownToggle', function () {
    return {
      restrict: 'A',
      require: ['?^dropdown', '?^tooltip'],
      link: function link(scope, el, attr, _ref3) {
        var _ref4 = slicedToArray(_ref3, 2),
            dropdownCtrl = _ref4[0],
            tooltipCtrl = _ref4[1];

        if (dropdownCtrl && tooltipCtrl) {
          scope.$watch(function () {
            return dropdownCtrl.isOpen();
          }, tooltipCtrl.setDisabled);
        }
      }
    };
  });

  var languagePickerTemplate = "<div class=\"language-picker\" ng-class=\"{ multi: multi, disabled: disabled, 'language-picker-icon': !listStyle, 'language-picker-list': listStyle, 'primary-compare': primaryCompare }\"> <div class=\"inner-container\"> <ul class=\"language-container\"> <li ng-repeat=\"language in ::availableLanguages | languageSort:primaryCompare\" class=\"language\" ng-class=\"{ active: isActive(language), primary: isPrimary(language), secondary: !isPrimary(language) }\" ng-click=\"select(language)\" tooltip=\"{{ listStyle ? '' : getTitle(language) }}\" tooltip-delay=\"0\"> <span class=\"language-icon icon-lang-{{ ::language | lowercase }}\"></span> <span class=\"language-title\" ng-if=\"listStyle\" ng-bind=\"getListTitle(language)\"></span> </li> </ul> <div ng-show=\"errorMsg\" class=\"error\">{{ errorMsg }}</div> </div> </div> ";

  var languagePickerDropdownTemplate = "<div class=\"dropdown\" dropdown dropdown-append-to-body auto-close=\"outsideClick\" is-open=\"$ctrl.open\"> <button class=\"btn-default btn-{{$ctrl.btnSize}} dropdown-toggle\" dropdown-toggle> <span ng-if=\"!$ctrl.active.length\" class=\"placeholder\">Any Language</span> <span ng-repeat=\"lang in $ctrl.active\" class=\"language-icon icon-lang-{{lang}}\" ng-bind=\"$ctrl.toName(lang, $index)\"></span> <span class=\"sep\"></span> <span class=\"caret\"></span> </button> <div class=\"dropdown-menu language-picker-dropdown-menu\" dropdown-menu> <div class=\"help-block\">Select one or more languages</div> <button class=\"btn-default btn-sm\" ng-click=\"$ctrl.clear()\" ng-disabled=\"$ctrl.active.length === 0\"> Clear Selections </button> <language-picker multi=\"true\" list-style=\"true\" primary-compare=\"!$ctrl.languages\" languages=\"$ctrl.languages\" ng-model=\"$ctrl.active\" on-change=\"$ctrl.activeChanged()\" active-watch=\"$ctrl.active\"></language-picker> </div> </div> ";

  /**
   * @name Language-Picker
   * @docType shared
   * @category directive
   * @description
   * A directive that allows you to pick between languages
   * @example
   *
   * <h2>Default Language Picker</h2>
   * <p>Select one item from the list of all languages</p></br>
   * <example>
   * <language-picker>
   * </language-picker>
   * </example>
   *
   * <h2>Disabled Example</h2>
   * <p>just try and click one... I dare you!</p></br>
   * <example>
   * <language-picker disabled="true" languages="['javascript', 'ruby']" active-watch="['ruby']">
   * </language-picker>
   * </example>
   *
   * <h2>Multiple Language Picker</h2>
   * <p>Select multiple items from a list of pre-determined languages, with a requirement of one language</p></br>
   * <example>
   * <language-picker multi="true"
   *  require-one="true"
   *  languages="['javascript', 'ruby', 'coffeescript', 'python']"
   *  active-watch="['javascript', 'coffeescript']"></language-picker>
   * </example>
   *
   * <h2>Required Languages</h2>
   * <p>Additional restrictions are also supported:</p></br>
   * <example>
   * <language-picker multi="true"
   *  require-one="true"
   *  languages="['javascript', 'python', 'haskell', 'ruby']"
   *  active-watch="['javascript', 'ruby', 'python']"
   *  required-languages="{javascript: 'JavaScript is required because its the bomb!', ruby: 'ruby is pretty cool too' }"></language-picker>
   *  </example>
   * @returns Directive Definition Object {object}
   */
  var languagePickerComponentModule = angular.module('Q.components.language-picker', [languageSortFilterModule, tooltipComponentModule]).directive('languagePicker', function () {
    return {
      scope: {
        languages: '<', // if not provided, defaults to all supported languages
        multi: '<', // is this a multi-select? defaults to false
        onChange: '&', // the event that occurs when a language is select or un-selected. For multi-select it will send an array of languages, for single-select it will return a single language
        active: '=?ngModel',
        activeWatch: '=', // a model to bind the active language(s) to
        inactiveWatch: '=',
        requireOne: '=', // is it required that one be selected at all times? Available for multi-select
        disabled: '<',
        requiredLanguages: '=',
        primaryCompare: '<',
        listStyle: '<'
      },
      require: ['?ngModel', 'languagePicker'],
      replace: true,
      template: languagePickerTemplate,
      controller: ['$scope', '$timeout', function controller($scope, $timeout) {
        var ngModel = void 0;
        this.setNgModel = function (ngm) {
          ngModel = ngm;
          ngModel.$render = function () {
            return resetValidity();
          };
          resetValidity();
        };

        function resetValidity() {
          if ($scope.requireOne) {
            var viewValue = ngModel.$viewValue;
            if (viewValue && viewValue.length > 0) {
              ngModel.$setValidity('requireOne', null);
            } else {
              ngModel.$setValidity('requireOne', false);
            }
          }
        }

        $scope.availableLanguages = $scope.languages || languages.all.map(function (l) {
          return l.id;
        });
        $scope.active = $scope.active || [];
        $scope.isActive = function (lang) {
          return $scope.active && $scope.active.indexOf(lang) !== -1;
        };
        $scope.$watch('activeWatch', function (newValue) {
          if (newValue) {
            $scope.active = angular.isArray(newValue) ? newValue : [newValue];
          }
        });
        $scope.$watch('inactiveWatch', function (newValue) {
          if (newValue) {
            var inactives = angular.isArray(newValue) ? newValue : [newValue];
            $scope.active = $scope.availableLanguages.filter(function (l) {
              return inactives.indexOf(l) === -1;
            });
          }
        });

        var timer = void 0;

        function showError(msg) {
          $scope.errorMsg = msg;
          $timeout.cancel(timer);
          timer = $timeout(function () {
            delete $scope.errorMsg;
          }, 2000);
        }

        // a case-insensitive object search
        function ciObjectSearch(obj, search) {
          for (var prop in obj) {
            if (obj.hasOwnProperty(prop)) {
              if (prop.toLowerCase() === search.toLowerCase()) {
                return obj[prop];
              }
            }
          }
        }

        $scope.getTitle = function (lang) {
          return languages[lang].name;
        };
        $scope.getListTitle = function (lang) {
          return languages[lang].namePlain;
        };
        $scope.isPrimary = function (lang) {
          return languages[lang].primary;
        };

        $scope.select = function (language) {
          if ($scope.disabled) {
            return;
          }
          if ($scope.multi) {
            $scope.active = $scope.active || [];
            var index = $scope.active.indexOf(language);
            if (index === -1) {
              // selecting a language
              $scope.active.push(language);
            } else {
              // unselecting a language
              if ($scope.requiredLanguages && ciObjectSearch($scope.requiredLanguages, language)) {
                // This language is required
                showError(ciObjectSearch($scope.requiredLanguages, language));
              } else if ($scope.requireOne && $scope.active.length === 1) {
                // one active language is required
                showError('At least one language is required');
              } else {
                $scope.active.splice(index, 1);
              }
            }
            $scope.onChange({
              activeLanguages: $scope.active,
              inactiveLanguages: $scope.availableLanguages.filter(function (l) {
                return $scope.active.indexOf(l) === -1;
              })
            });
          } else {
            $scope.active = [language];
            $scope.onChange({ language: language });
          }
          if (ngModel) {
            ngModel.$setViewValue($scope.active.slice());
            resetValidity();
          }
        };
      }],
      link: function link(scope, elem, attr, _ref) {
        var _ref2 = slicedToArray(_ref, 2),
            ngModelCtrl = _ref2[0],
            languagePicker = _ref2[1];

        if (ngModelCtrl) {
          languagePicker.setNgModel(ngModelCtrl);
        }
      }
    };
  }).component('languagePickerDropdown', {
    bindings: {
      onChange: '&',
      btnSize: '@',
      languages: '<'
    },
    require: {
      ngModel: 'ngModel'
    },
    template: languagePickerDropdownTemplate,
    controller: function controller() {
      var ctrl = this;
      ctrl.toName = function (lang, index) {
        var n = '';
        if (ctrl.active.length < 3) {
          n = languages.toName(lang);
          if (index < ctrl.active.length - 1) {
            n += ',';
          }
        }
        return n;
      };
      ctrl.$onInit = function () {
        ctrl.active = ctrl.ngModel.$viewValue || [];
        ctrl.ngModel.$render = function () {
          ctrl.active = ctrl.ngModel.$viewValue || [];
          ctrl.onChange();
        };
      };
      ctrl.activeChanged = function () {
        ctrl.ngModel.$setViewValue(ctrl.active.slice());
        ctrl.onChange();
      };
      ctrl.clear = function () {
        ctrl.active = [];
        ctrl.activeChanged();
        ctrl.open = false;
      };
    }
  });

  var interactUserListTemplate = "<ul class=\"users-list\"> <li ng-hide=\"$ctrl.usersList.length\" class=\"note\">Waiting for connection…</li> <li ng-show=\"$ctrl.usersList.length === 1\" class=\"note\"> {{ $ctrl.type === 'candidate' ? 'A coding session is not currently running.'\n    : 'Waiting for test participant.' }} </li> <li ng-show=\"$ctrl.usersList.length > 1\" class=\"note\"> The following people are connected to this coding session: </li> <li ng-repeat=\"user in $ctrl.usersList\" ng-class=\"{ me: user.$self }\"> <span class=\"color\" ng-style=\"{ 'background-color': user.color }\"></span> <span class=\"name\">{{ user.name }}</span> <span class=\"type\">{{ user.type }}</span> </li> </ul> ";

  var interactUserListComponentModule = angular.module('Q.components.interact-user-list', []).component('interactUserList', {
    template: interactUserListTemplate,
    bindings: {
      usersList: '<',
      type: '@'
    }
  });

  var codeChallengeTemplate = "<challenge-intro-dialog show-intro=\"$ctrl.showIntro\" assessment-process=\"assessments.process\" challenge=\"$ctrl.challenge\" begin-challenge=\"$ctrl.beginChallenge()\"> <div compile-template=\"$ctrl.introDialogTemplate\"></div> </challenge-intro-dialog> <div class=\"classic-code-challenge-wrapper\" ng-class=\"{ 'dark-bg': $root.$ideDark, 'no-solution': !$ctrl.process.solution.id, ready: $ctrl.process }\" link-multi-file-challenges=\"$ctrl\"> <nav class=\"classic-code-challenge-toolbar\" role=\"navigation\"> <span class=\"restricted-mode\" ng-if=\"assessments.process.restricted\" tooltip=\"{{assessments.process.readOnly ? 'This challenge can not be edited' : 'Changes made to this challenge will not be saved'}}\"> <span class=\"icon-{{assessments.process.readOnly ? 'readonly' : 'eye'}}\"></span> <label>{{assessments.process.readOnly ? 'Read-Only' : 'Restricted'}} Mode</label> <tooltip-help-icon></tooltip-help-icon> </span> <button class=\"btn-default btn-sm reset\" ng-if=\"!assessments.process.restricted && !$ctrl.expired\" ng-click=\"$ctrl.openResetDialog()\" ng-disabled=\"$ctrl.process.disableEditors\"> Reset… </button> <span class=\"solution-language\"> <label>Language:</label> <span class=\"icon-{{$ctrl.currentLanguage ? 'lang-' + $ctrl.currentLanguage : 'blank'}}\"></span> <span>{{$ctrl.currentLanguageName}}</span> </span> <span ng-if=\"$ctrl.process.challengeLanguage.languageVersionName\"> <label>Version: </label> <span>{{$ctrl.process.challengeLanguage.languageVersionName}}</span> </span> <button class=\"navbar-btn btn-default btn-sm\" ng-if=\"!assessments.process.restricted && $ctrl.languageIds.length > 1 && !$ctrl.expired\" ng-click=\"$ctrl.openChangeLanguageDialog()\" ng-disabled=\"$ctrl.process.disableEditors\"> Change… </button> <multi-file-toolbar actions=\"$ctrl.actions\" on-click=\"$ctrl.clickFile(action)\" ng-if=\"!$ctrl.expired\"></multi-file-toolbar> <interact-editor-button interact-service=\"$ctrl.interact\" expired=\"$ctrl.expired\"></interact-editor-button> <span class=\"left-right-sep\"></span> <div class=\"challenge-timer\" ng-if=\"!assessments.process.restricted && $ctrl.process.timerStartedAt\"> <horizontal-countdown-timer class=\"prominent\" started-at=\"$ctrl.process.timerStartedAt\" timeout-seconds=\"$ctrl.process.timerTimeoutSeconds\" on-expired=\"$ctrl.expired = expired\" expired-message=\"{{ $ctrl.process.challenge.candidateNotesScored ? 'Time\\'s up!' : 'Time\\'s up! (You can still edit Your Notes)' }}\"></horizontal-countdown-timer> </div> <button class=\"navbar-btn btn-{{ !$ctrl.testsPassed() ? 'primary' : 'default' }} btn-sm run-tests\" ng-if=\"$ctrl.sampleTestCasesEnabled && !$ctrl.challenge.disableIdeFeatures\" ng-click=\"$ctrl.runTests()\" ng-disabled=\"$ctrl.process.disableEditors || $ctrl.expired || $ctrl.process.running || $ctrl.testsEmpty\" tooltip=\"Run your solution against your tests{{$ctrl.runTestsTooltip}}\" tooltip-shortcut-hint=\"{{$root.KeyCodes.SYMBOLS.COMMAND_KEY}} + '\"> Run Tests </button> <span class=\"step-sep\" ng-if=\"$ctrl.sampleTestCasesEnabled\"></span> <button class=\"navbar-btn btn-{{ $ctrl.testsPassed() && !$ctrl.isCompleted() ? 'primary' : 'default' }} btn-sm submit\" ng-click=\"$ctrl.submitSolution()\" ng-disabled=\"$ctrl.process.disableEditors || $ctrl.expired || $ctrl.process.running || !$ctrl.process.solution.code\" tooltip=\"Run your solution against the final tests\" tooltip-shortcut-hint=\"{{$root.KeyCodes.SYMBOLS.COMMAND_KEY}} + Enter\"> Submit Solution </button> <span class=\"step-sep\"></span> <button class=\"navbar-btn btn-{{ $ctrl.testsPassed() && $ctrl.isCompleted() ? 'primary' : 'default' }} btn-sm next\" ng-disabled=\"!$ctrl.expired && !$ctrl.attemptedCurrentSolution() && !$ctrl.isSample() && !assessments.process.liveInterviewActive\" ng-click=\"$ctrl.next()\" ng-bind=\"$ctrl.nextLabel()\"></button> </nav> <multi-file-layout open-files=\"$ctrl.openFiles\" workspace=\"$ctrl.workspace\" closeable=\"false\" on-run=\"$ctrl.submitSolution()\" on-test-run=\"$ctrl.runTests()\" on-file-change=\"$ctrl.onFileChange({file: file, userInitiated: userInitiated })\" on-tab-focused=\"$ctrl.onTabFocused({ file: file })\" on-file-copy=\"$ctrl.process.onCopy(copied)\" on-file-paste=\"$ctrl.process.onPaste(file, pasted)\" parent-controller=\"$ctrl\" get-toolbar-template=\"$ctrl.getToolbarTemplate(file)\" readonly=\"$ctrl.process.disableEditors || $ctrl.process.readOnly || $ctrl.submitting\" expired=\"$ctrl.expired\" cursor=\"$ctrl.process.cursor\" remember-ui></multi-file-layout> </div> ";

  var introDialogTemplate = "<div class=\"modal-content\"> <div class=\"modal-header\"> <h4 class=\"modal-title\"> Challenge #{{ ::$ctrl.challenge.position }}: Coding Task </h4> <div class=\"theme\"> <span tooltip=\"(You can change this and more under IDE Settings)\"> Editor Theme: </span> <div class=\"btn-group\"> <button class=\"btn-default light\" ng-model=\"$ctrl.codemirrorOpts.sharedOpts.theme\" btn-radio=\"'light'\" tooltip=\"Light Theme\" tooltip-delay=\"0\"></button> <button class=\"btn-default dark\" ng-model=\"$ctrl.codemirrorOpts.sharedOpts.theme\" btn-radio=\"'dark'\" tooltip=\"Dark Theme\" tooltip-delay=\"0\"></button> </div> </div> </div> <div class=\"modal-body\"> <div class=\"summary\" ng-if=\"$ctrl.challenge.summary\" markdown=\"$ctrl.challenge.summary\"></div> <p class=\"note\"> For this challenge you will be asked to write code. Please make sure to craft a quality solution. </p> <div class=\"challenge-timer-alert\" ng-if=\"$ctrl.challenge.isTimed()\"> <div class=\"alert alert-info icon-stopwatch\"> <p> This challenge has a time limit of {{ $ctrl.challenge.timeLimitMin |\n          durationFormat:'minutes' }}. Once the time runs out, you will not be able to modify your solution. </p> </div> <p class=\"note\"> You can hide the challenge timer by clicking on it. It will reappear if time begins to run out. </p> </div> <p class=\"note\" ng-show=\"assessments.process.timer.isCountDown\"> You have <timer-widget timer-only=\"true\" timer=\"assessments.process.timer\"></timer-widget> remaining for this assessment. </p> <challenge-code-of-conduct code-of-conduct=\"$ctrl.codeOfConduct\" challenge-id=\"$ctrl.challenge.id\" beginning=\"$ctrl.beginning\" checkbox-id-prefix=\"classic-code-conduct\"></challenge-code-of-conduct> <div class=\"languages\" ng-if=\"$ctrl.languageIds.length > 0\"> <div class=\"languages-title\" ng-if=\"$ctrl.languageIds.length > 1\"> Select a language for your solution: </div> <div class=\"languages-title\" ng-if=\"$ctrl.languageIds.length < 2\"> Your solution will be coded using: </div> <language-picker languages=\"$ctrl.languageIds\" on-change=\"$ctrl.switchLanguage(language)\" active-watch=\"$ctrl.currentLanguage\"></language-picker> <p class=\"note\" ng-if=\"$ctrl.isCarriedForward\"> The language you select will also be used on some of the following challenges. </p> </div> <div class=\"alert alert-info\" ng-if=\"$ctrl.completeReferenceSolution\"> This challenge will pull your solution from a previous challenge that you have completed. </div> <div class=\"alert alert-warning\" ng-if=\"$ctrl.incompleteReferenceSolution\"> This challenge will pull your solution from a previous challenge that you have not completed. We recommend that you return to the previous challenge and try to complete it before moving forward. </div> <div class=\"alert alert-danger\" ng-if=\"$ctrl.unattemptedReferenceSolution\"> This challenge will pull your solution from a previous challenge that you have not attempted. Please go back and attempt all challenges before attempting this one. </div> </div> <div class=\"modal-footer\"> <button class=\"btn-default\" ng-if=\"$ctrl.unattemptedReferenceSolution || $ctrl.incompleteReferenceSolution\" ng-click=\"$ctrl.previous()\" ng-disabled=\"$ctrl.beginning\"> Back to Previous </button> <button class=\"btn-primary\" ng-click=\"$ctrl.beginChallenge()\" ng-disabled=\"$ctrl.unableToBeginChallenge()\"> Start Challenge {{ $ctrl.currentLanguageName ? 'in\n      '+$ctrl.currentLanguageName : '' }} <span class=\"shortcut-hint\">{{$ctrl.process.beginChallengeKeyStroke}}</span> </button> </div> </div> ";

  var challengeIntroDialogTemplate = "<div class=\"modal fade in\" ng-if=\"$ctrl.enabled\" global-events> <div class=\"modal-dialog\"> <div class=\"modal-content\" ng-if=\"!$ctrl.restricted\" ng-transclude></div> <div class=\"modal-content\" ng-if=\"$ctrl.restricted\"> <div class=\"modal-header\"> <h4 class=\"modal-title\"> Challenge #{{ ::$ctrl.challenge.position }}: Never Started </h4> </div> <div class=\"modal-body\"> <p> This challenge was never started, and the assessment is in {{$ctrl.modeLabel()}} mode. </p> <p>The challenge cannot be started at this time.</p> </div> <div class=\"modal-footer\"> <button class=\"btn-primary\" ng-click=\"$ctrl.next()\"> Next Challenge </button> </div> </div> </div> </div> <div class=\"modal-backdrop fade in\" ng-if=\"$ctrl.enabled\"></div> ";

  /* eslint-disable angular/module-getter */

  var GLOBAL_ACTION_KEYS = {
    'Command-Shift-s': 'save',
    'Command-s': 'apply',
    'Command-p': 'preview',
    'Command-?': 'help',
    'Command-enter': 'run',
    "Command-'": 'test'
  };

  // List of all actions (could include some that don't have keys)
  var GLOBAL_ACTIONS = Object.values(GLOBAL_ACTION_KEYS);

  var GLOBAL_ACTION_PREFIX = 'globalAction:';

  var GLOBAL_ACTION_EVENTS = {};

  var globalActionsComponentModule = angular.module('Q.services.global-actions-component', []);

  /**
   * @ngdoc directive
   * @name globalActionSave
   *
   * @description
   * Performs the specified action when a save event is triggered
   *
   * @element ANY
   * @restrict A
   * @param {expression} globalActionSave {@link guide/expression Expression} to evaluate
   */

  /**
   * @ngdoc directive
   * @name globalActionApply
   *
   * @description
   * Performs the specified action when an apply event is triggered
   *
   * @element ANY
   * @restrict A
   * @param {expression} globalActionApply {@link guide/expression Expression} to evaluate
   */

  /**
   * @ngdoc directive
   * @name globalActionPreview
   *
   * @description
   * Performs the specified action when a preview event is triggered
   *
   * @element ANY
   * @restrict A
   * @param {expression} globalActionPreview {@link guide/expression Expression} to evaluate
   */

  /**
   * @ngdoc directive
   * @name globalActionHelp
   *
   * @description
   * Performs the specified action when a help event is triggered
   *
   * @element ANY
   * @restrict A
   * @param {expression} globalActionHelp {@link guide/expression Expression} to evaluate
   */

  /**
   * @ngdoc directive
   * @name globalActionRun
   *
   * @description
   * Performs the specified action when a run event is triggered
   *
   * @element ANY
   * @restrict A
   * @param {expression} globalActionRun {@link guide/expression Expression} to evaluate
   */

  GLOBAL_ACTIONS.forEach(function (action) {
    GLOBAL_ACTION_EVENTS[action] = GLOBAL_ACTION_PREFIX + action;

    var directiveName = 'globalAction' + ucFirst(action);
    globalActionsComponentModule.directive(directiveName, function () {
      return {
        restrict: 'A',
        link: function link(scope, element, attrs) {
          scope.$on(GLOBAL_ACTION_EVENTS[action], function ($event) {
            stopEvent($event);
            scope.$evalAsync(attrs[directiveName], { $event: $event });
          });
        }
      };
    });
  });

  globalActionsComponentModule.factory('globalActionsService', ['$rootScope', function ($rootScope) {
    function trigger(action) {
      for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      return $rootScope.$broadcast.apply($rootScope, [GLOBAL_ACTION_EVENTS[action]].concat(args)).defaultPrevented;
    }

    var svc = {};
    GLOBAL_ACTIONS.forEach(function (action) {
      svc[action] = trigger.bind(null, action);
    });

    return svc;
  }]);

  var globalEventsComponentModule = angular.module('Q.components.global-events', [keycodesModule, globalActionsComponentModule])

  /**
   * @name GlobalEvents
   * @docType shared
   * @category directive
   * @param $document {service} - Angular {@link https://docs.angularjs.org/api/ng/service/$document $document} service
   * @param $rootScope {service} - Angular {@link https://docs.angularjs.org/api/ng/service/$rootScope $rootScope} service
   * @description
   * The globalEvents directive allows you to listen in to global keyboard (and other)
   * events via scope events.
   *
   * Events are broadcast down the scope in the form of «event-type», and for keyboard events,
   * «event-type»:«keycode» and «event-type»:«common-name»
   *
   */
  .directive('globalEvents', ['$document', '$rootScope', 'KeyCodes', 'globalActionsService', function ($document, $rootScope, KeyCodes, globalActionsService) {
    var bound = {};
    var commonCodes = {
      8: 'backspace',
      9: 'tab',
      13: 'enter',
      27: 'escape',
      32: 'space',
      33: 'pagedown',
      34: 'pageup',
      35: 'home',
      36: 'end',
      37: 'left',
      38: 'up',
      39: 'right',
      40: 'down',
      46: 'delete'
    };
    return {
      restrict: 'A',
      link: function link(scope, element, attrs) {
        var eventType = attrs.globalEvents || 'keydown';

        // we only want a single global-events emitter per type
        if (!bound[eventType]) {
          bound[eventType] = function (e) {
            if (e.defaultPrevented) return;
            var which = e.keyCode || e.which;
            var common = commonCodes[which];
            var char = e.key && e.key.length === 1 && e.key.toLowerCase();
            var props = { which: which, common: common, char: char };
            var prevented = $rootScope.$broadcast(eventType, e, props).defaultPrevented;
            if (!prevented && /key/.test(eventType)) {
              prevented = $rootScope.$broadcast(eventType + ':' + which, e, props).defaultPrevented;
              if (!prevented) {
                if (common) {
                  prevented = $rootScope.$broadcast(eventType + ':' + common, e, props).defaultPrevented;
                } else if (char) {
                  prevented = $rootScope.$broadcast(eventType + ':' + char, e, props).defaultPrevented;
                }
              }
            }

            if (!prevented && (common || char)) {
              var actionKey = '';
              if (KeyCodes.isCommand(e)) {
                actionKey += 'Command-';
              }
              if (KeyCodes.isAlt(e)) {
                actionKey += 'Alt-';
              }
              if (KeyCodes.isShift(e)) {
                actionKey += 'Shift-';
              }
              actionKey += common || char;
              var action = GLOBAL_ACTION_KEYS[actionKey];
              if (action) {
                prevented = globalActionsService[action](e, props);
              }
            }

            if (prevented) {
              stopEvent(e);
            }
          };
          $document.on(eventType, bound[eventType]);
          // track how many listeners we have on this event
          bound[eventType].count = 1;
        } else {
          bound[eventType].count++;
        }

        scope.$on('$destroy', function () {
          bound[eventType].count--;
          if (bound[eventType].count < 1) {
            $document.off(eventType, bound[eventType]);
            bound[eventType] = false;
          }
        });
      }
    };
  }]);

  var challengeIntroDialogComponentModule = angular.module('Q.components.challenge-intro-dialog', [globalEventsComponentModule]).component('challengeIntroDialog', {
    bindings: {
      showIntro: '<',
      assessmentProcess: '<',
      challenge: '<',
      beginChallenge: '&'
    },
    transclude: true,
    controller: 'ChallengeIntroDialogCtrl',
    template: challengeIntroDialogTemplate
  }).controller('ChallengeIntroDialogCtrl', ['$scope', '$element', '$animate', '$timeout', '$log', function ChallengeIntroDialogCtrl($scope, $element, $animate, $timeout, $log) {
    var ctrl = this;

    ctrl.enabled = false;

    var listener = angular.noop;

    var setEnabled = function setEnabled(enabled) {
      ctrl.enabled = enabled;
      $element.toggleClass('enabled', enabled);

      listener();
      if (enabled) {
        listener = $scope.$on('socket:model.changed:Solution', function ($event, data) {
          var newSolution = data.model;
          if (ctrl.showIntro && newSolution && newSolution.challengeId === ctrl.challenge.id && newSolution.assessmentResultId === ctrl.assessmentResult.id) {
            // solution was created elsewhere, start the challenge now
            ctrl.beginChallenge();
          }
        });
      }
    };

    ctrl.$onInit = function () {
      if (!ctrl.assessmentProcess) {
        return $log.error('Invariant violation: ctrl.assessmentProcess was unexpectedly undefined');
      }

      ctrl.assessmentResult = ctrl.assessmentProcess.assessmentResult;
      ctrl.restricted = ctrl.assessmentProcess.restricted;
      ctrl.modeLabel = function () {
        return (EDIT_MODE_LABELS[ctrl.assessmentProcess.editMode] || '').toLowerCase();
      };
      ctrl.next = ctrl.assessmentProcess.next;

      setEnabled(ctrl.showIntro);

      ctrl.$onChanges = function () {
        if (ctrl.enabled && !ctrl.showIntro) {
          var $children = $element.children();
          var childCounts = $children.length;
          angular.forEach($children, function (child) {
            $animate.removeClass(child, 'in').then(function () {
              childCounts--;
              if (childCounts <= 0) {
                setEnabled(false);
              }
            });
          });

          // should be unnecessary, but I refuse to let this issue return.
          // this ensures that, if something breaks with the animations, we _always_ remove
          // the overlays after 1 second
          $timeout(function () {
            setEnabled(false);
          }, 1000);
        }
      };
    };

    // allow global keystrokes for "next"
    $scope.$on(GLOBAL_ACTION_EVENTS.run, function ($event) {
      if (ctrl.enabled && ctrl.showIntro) {
        $event.preventDefault();
        ctrl.beginChallenge();
        $scope.$apply();
      }
    });
  }]);

  var fontNode = void 0;

  var CODEMIRROR_DEFAULT_OPTIONS = {
    indentUnit: 2,
    tabSize: 4,
    indentWithTabs: false,
    lineWrapping: true,
    smartIndent: true,
    electricChars: true,
    lineNumbers: true,
    themes: {
      dark: 'qualified-dark',
      light: 'qualified-light'
    },
    theme: 'light',
    keyMap: 'default',
    matchBrackets: true,
    autoCloseBrackets: true,
    foldGutter: true,
    gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
    styleActiveLine: true,
    extraKeys: {},
    font: '',
    fileMap: {},
    undoDepth: 2000
  };

  // These properties are inherited by per-editor options, so they point back to the
  // shared options
  var CODEMIRROR_EDITABLE_OPTIONS = ['indentUnit', 'tabSize', 'indentWithTabs', 'lineWrapping', 'smartIndent', 'electricChars', 'lineNumbers', 'theme', 'keyMap', 'matchBrackets', 'autoCloseBrackets', 'foldGutter', 'styleActiveLine', 'font', 'fileMap'];

  var codemirrorOptionsComponentModule = angular.module('Q.components.codemirror-options', [keycodesModule, accountServiceModule, globalActionsComponentModule]).factory('codemirrorOptions', ['accountService', '$rootScope', '$document', '$window', 'KeyCodes', 'globalActionsService', '$timeout', function (accountService, $rootScope, $document, $window, KeyCodes, globalActionsService, $timeout) {
    var sharedOpts = void 0;
    var _opts = {};

    function _setupOpts() {
      initializeExtraKeys($window.CodeMirror, KeyCodes.COMMAND_KEY, globalActionsService);
      _opts = angular.merge({}, CODEMIRROR_DEFAULT_OPTIONS, accountService.prefs().codemirror);
      updateFont();
      updateFileMap();

      // setup default theme
      _updateTheme(_opts.theme);
    }

    _setupOpts();
    if (!accountService.get().verified) {
      accountService.whenReady().then(_setupOpts);
    }
    $rootScope.$on('app.login', _setupOpts);
    $rootScope.$on('app.logout', _setupOpts);

    // Shared options are sort of a magic source for handling editable options
    // when values are set here, certain actions are automatically applied to the base options
    sharedOpts = {};
    Object.keys(_opts).forEach(function (opt) {
      Object.defineProperty(sharedOpts, opt, {
        get: function get() {
          return _opts[opt];
        },
        set: function set(val) {
          _opts[opt] = val;
          _setOption(opt, val, _opts);
        },

        enumerable: true,
        configurable: true
      });
    });
    Object.defineProperty(sharedOpts, 'indentSpaces', {
      get: function get() {
        if (sharedOpts.indentWithTabs) return false;
        return sharedOpts.indentUnit;
      },
      set: function set(val) {
        if (val === false) {
          sharedOpts.indentUnit = 4;
          sharedOpts.indentWithTabs = true;
        } else {
          sharedOpts.indentUnit = val;
          sharedOpts.indentWithTabs = false;
        }
      }
    });

    var propUpdateMap = {
      theme: _updateTheme,
      font: updateFont,
      fileMap: updateFileMap
    };

    function _setOption(option, value, opt) {
      opt[option] = value;

      if (propUpdateMap[option]) {
        propUpdateMap[option](value);
      }

      _savePref(option, value);
    }

    function _updateTheme(value) {
      var $html = $document.find('html');
      angular.forEach(_opts.themes, function (_, t) {
        $rootScope['$ide' + ucFirst(t)] = t === value;
        $html.toggleClass('ide-' + t, t === value);
      });
      $rootScope.$ideDarkClass = value === 'dark' ? 'dark-bg' : '';
    }

    var debounceSave = void 0;

    function _savePref(option, value) {
      if (CODEMIRROR_EDITABLE_OPTIONS.includes(option)) {
        // save back to user prefs
        var userPrefs = accountService.prefs();
        userPrefs.codemirror = userPrefs.codemirror || {};
        if (angular.equals(value, CODEMIRROR_DEFAULT_OPTIONS[option])) {
          delete userPrefs.codemirror[option];
        } else {
          userPrefs.codemirror[option] = value;
        }
        if (!debounceSave) {
          debounceSave = $timeout(function () {
            if (!$rootScope.playbackToken) accountService.savePrefs();
            $rootScope.$broadcast('codemirrorOptionsChanged', sharedOpts);
            debounceSave = null;
          });
        }
      }
    }

    /**
     * Creates a new set of options based on a given file name (with extension) file type.
     * This is a special object that will inherit editable options (so user preferences are propagated).
     *
     * This returns a unique object every time it is called.
     *
     * @param {string} filename
     * @returns {Object}
     */
    function createOpts(filename) {
      var lang = languages.langFromFilename(filename) || languages.text;
      var mode = lang.codemirror;

      var opts = { mode: mode };
      Object.keys(_opts).forEach(function (option) {
        if (option === 'theme') {
          Object.defineProperty(opts, option, {
            get: function get() {
              return _opts.themes[sharedOpts.theme] || _opts.themes.light;
            },

            enumerable: true
          });
        } else if (CODEMIRROR_EDITABLE_OPTIONS.includes(option)) {
          var _customValue = void 0;
          Object.defineProperty(opts, option, {
            get: function get() {
              return _customValue === undefined ? _opts[option] : _customValue;
            },
            set: function set(val) {
              _customValue = val;
            },

            enumerable: true
          });
        } else {
          opts[option] = angular.copy(_opts[option]);
        }
      });
      if (mode === 'markdown' || mode === 'gfm') {
        opts.extraKeys.Enter = 'newlineAndIndentContinueMarkdownList';
        opts.inputStyle = 'contenteditable';
        opts.spellcheck = true;
      }
      if (lang.ac) {
        opts.extraKeys['Ctrl-Space'] = 'autocomplete';
      }

      return opts;
    }

    /**
     * Trigger an update when the font is modified.
     */
    function updateFont() {
      var font = _opts.font;
      if (font) {
        if (!fontNode) {
          fontNode = angular.element('<style>');
          $document.find('body').append(fontNode);
        }
        fontNode.text('ui-codemirror:not(.markdown-codemirror) pre {font: ' + font + ' !important;}');
      } else if (fontNode) {
        fontNode.remove();
        fontNode = null;
      }
    }

    /**
     * Trigger an update with the file map is modified
     */
    function updateFileMap() {
      Object.keys(fileMap).forEach(function (k) {
        delete fileMap[k];
      });
      angular.extend(fileMap, _opts.fileMap);
    }

    return {
      sharedOpts: sharedOpts,
      createOpts: createOpts,
      updateFont: updateFont,
      updateFileMap: updateFileMap
    };
  }]);

  var didInitializeExtraKeys = false;

  // Finish setting up the advanced keystrokes
  function initializeExtraKeys(CodeMirror, commandKey, globalActionsService) {
    if (!CodeMirror || didInitializeExtraKeys) return;
    // from the sublime keymap
    var cmds = CodeMirror.commands;
    var map = CodeMirror.keyMap['default'];
    var Pos = CodeMirror.Pos;

    map['Tab'] = 'indentMore';
    map['Shift-Tab'] = 'indentLess';
    map[commandKey + '--'] = 'toggleFold';
    map[commandKey + '-/'] = 'toggleComment';
    // enable global apply action for Vim
    cmds.save = globalActionsService.apply;

    cmds[map[commandKey + '-D'] = 'duplicateLine'] = function (cm) {
      cm.operation(function () {
        var rangeCount = cm.listSelections().length;
        for (var i = 0; i < rangeCount; i++) {
          var range = cm.listSelections()[i];
          if (range.empty()) {
            cm.replaceRange(cm.getLine(range.head.line) + '\n', Pos(range.head.line, 0));
          } else {
            cm.replaceRange(cm.getRange(range.from(), range.to()), range.from());
          }
        }
        cm.scrollIntoView();
      });
    };

    var swapLineCombo = 'Shift-' + commandKey + '-';

    cmds[map['Shift-Alt-Up'] = map[swapLineCombo + 'Up'] = 'swapLineUp'] = function swapLineUp(cm) {
      if (cm.isReadOnly()) return CodeMirror.Pass;
      var ranges = cm.listSelections(),
          linesToMove = [];
      var at = cm.firstLine() - 1;
      var newSels = [];
      for (var i = 0; i < ranges.length; i++) {
        var range = ranges[i];
        var from = range.from().line - 1;
        var to = range.to().line;
        newSels.push({
          anchor: Pos(range.anchor.line - 1, range.anchor.ch),
          head: Pos(range.head.line - 1, range.head.ch)
        });
        if (range.to().ch === 0 && !range.empty()) --to;
        if (from > at) {
          linesToMove.push(from, to);
        } else if (linesToMove.length) linesToMove[linesToMove.length - 1] = to;
        at = to;
      }
      cm.operation(function () {
        for (var _i = 0; _i < linesToMove.length; _i += 2) {
          var _from = linesToMove[_i];
          var _to = linesToMove[_i + 1];
          var line = cm.getLine(_from);
          cm.replaceRange('', Pos(_from, 0), Pos(_from + 1, 0), '+swapLine');
          if (_to > cm.lastLine()) {
            cm.replaceRange('\n' + line, Pos(cm.lastLine()), null, '+swapLine');
          } else {
            cm.replaceRange(line + '\n', Pos(_to, 0), null, '+swapLine');
          }
        }
        cm.setSelections(newSels);
        cm.scrollIntoView();
      });
    };

    cmds[map['Shift-Alt-Down'] = map[swapLineCombo + 'Down'] = 'swapLineDown'] = function swapLineDown(cm) {
      if (cm.isReadOnly()) return CodeMirror.Pass;
      var ranges = cm.listSelections(),
          linesToMove = [];
      var at = cm.lastLine() + 1;
      for (var i = ranges.length - 1; i >= 0; i--) {
        var range = ranges[i];
        var from = range.to().line + 1;
        var to = range.from().line;
        if (range.to().ch === 0 && !range.empty()) from--;
        if (from < at) {
          linesToMove.push(from, to);
        } else if (linesToMove.length) linesToMove[linesToMove.length - 1] = to;
        at = to;
      }
      cm.operation(function () {
        for (var _i2 = linesToMove.length - 2; _i2 >= 0; _i2 -= 2) {
          var _from2 = linesToMove[_i2];
          var _to2 = linesToMove[_i2 + 1];
          var line = cm.getLine(_from2);
          if (_from2 === cm.lastLine()) {
            cm.replaceRange('', Pos(_from2 - 1), Pos(_from2), '+swapLine');
          } else {
            cm.replaceRange('', Pos(_from2, 0), Pos(_from2 + 1, 0), '+swapLine');
          }
          cm.replaceRange(line + '\n', Pos(_to2, 0), null, '+swapLine');
        }
        cm.scrollIntoView();
      });
    };

    didInitializeExtraKeys = true;
  }

  function cursorData(cm) {
    var base = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    var cursor = cm.getCursor();
    base.line = cursor.line;
    base.ch = cursor.ch;
    return base;
  }

  var resetCodeDialogTemplate = "<div class=\"reset-code-dialog\"> <div class=\"modal-header\"> <h4 class=\"modal-title\"> Reset Solution{{ $ctrl.testCasesEnabled ? ' and Sample Tests' : ''}} </h4> <button class=\"close\" aria-label=\"Close\" ng-click=\"$ctrl.cancel()\"> <span aria-hidden=\"true\">&times;</span> </button> </div> <div class=\"modal-body modal-long-form\"> <section ng-if=\"!$ctrl.processing\"> <p ng-if=\"$ctrl.testCasesEnabled\"> What parts of your solution would you like to reset? </p> <p ng-if=\"!$ctrl.testCasesEnabled\"> Are you sure you want to reset your solution? </p> <div class=\"help-block\"> <p> Note: you can undo the reset using <span class=\"shortcut-hint\">{{KeyCodes.SYMBOLS.COMMAND_KEY}} + Z</span> within an editor if you choose, as long as you haven't left the page. </p> <p>If you leave the page, your changes will be lost.</p> </div> <ul class=\"action-links\"> <li ng-if=\"$ctrl.testCasesEnabled\" class=\"reset-all\"> <a ng-click=\"$ctrl.reset()\" tabindex=\"0\"> <strong>Reset</strong> your solution code and the sample tests. </a> </li> <li class=\"reset-solution\"> <a ng-click=\"$ctrl.reset('Solution')\" tabindex=\"0\"> <strong>Reset</strong> {{ $ctrl.testCasesEnabled ? 'just' : ''}} your solution. </a> </li> <li ng-if=\"$ctrl.testCasesEnabled\" class=\"reset-test-cases\"> <a ng-click=\"$ctrl.reset('TestCases')\" tabindex=\"0\"> <strong>Reset</strong> just the sample tests. </a> </li> <li class=\"cancel\"> <a ng-click=\"$ctrl.cancel()\" tabindex=\"0\"> <strong>Cancel</strong>: Do not reset anything. </a> </li> </ul> </section> <section ng-if=\"$ctrl.processing\" class=\"processing-animation\"> <p><span class=\"loading-spinner loading-spinner-large\"></span></p> <p>Please wait...</p> </section> </div> </div> ";

  var resetCodeComponentModule = angular.module('Q.components.reset-code-dialog', [modalComponentModule]).factory('resetCodeDialog', ['$modal', function resetCodeDialog($modal) {
    return {
      open: function open(_challengeProcess) {
        return $modal.openOnce('resetCodeDialog', {
          template: resetCodeDialogTemplate,
          controller: 'ResetCodeDialogCtrl as $ctrl',
          resolve: {
            challengeProcess: function challengeProcess() {
              return _challengeProcess;
            }
          }
        }).result;
      }
    };
  }]).controller('ResetCodeDialogCtrl', ['$modalInstance', 'challengeProcess', function ResetCodeDialogCtrl($modalInstance, challengeProcess) {
    var ctrl = this;

    ctrl.$onInit = function () {
      ctrl.testCasesEnabled = challengeProcess.solution && challengeProcess.solution.sampleTestCasesEnabled && !challengeProcess.solution.sampleTestCasesReadOnly;
    };

    ctrl.cancel = function () {
      return $modalInstance.dismiss();
    };

    ctrl.reset = function (type) {
      ctrl.processing = true;
      challengeProcess.reset(type).then(ctrl.cancel).catch(function () {
        ctrl.processing = false;
      });
    };
  }]);

  var changeLanguageDialogTemplate = "<div class=\"change-language-dialog\"> <div class=\"modal-header\"> <h4 class=\"modal-title\">Change Solution Language</h4> <button class=\"close\" aria-label=\"Close\" ng-click=\"$ctrl.cancel()\"> <span aria-hidden=\"true\">&times;</span> </button> </div> <div class=\"modal-body modal-long-form\"> <section ng-if=\"!$ctrl.processing\"> <p> Switching to a different language will reset your solution. If you choose a language you have not previously worked on, you will start over. </p> <div class=\"help-block\"> <p> As long as you stay on this page without reloading, you can switch back to a language and restore that solution. </p> <p>If you leave the page, your previous solutions will be lost.</p> </div> <div class=\"language-picker-box\"> <language-picker languages=\"$ctrl.currentLanguageIds\" disabled=\"true\"></language-picker> <span class=\"to\"></span> <language-picker languages=\"$ctrl.languageIds\" ng-model=\"$ctrl.newLanguage\"></language-picker> </div> <ul class=\"action-links\"> <li> <a ng-click=\"$ctrl.change()\" ng-disabled=\"!$ctrl.langChanged()\" ng-bind=\"$ctrl.changeButtonText()\" tabindex=\"0\"></a> </li> <li class=\"cancel\"> <a ng-click=\"$ctrl.cancel()\" tabindex=\"0\"> <strong>Do not change</strong>: Continue using {{\n            $ctrl.languageName($ctrl.currentLanguage) }}. </a> </li> </ul> </section> <section ng-if=\"$ctrl.processing\" class=\"processing-animation\"> <p><span class=\"loading-spinner loading-spinner-large\"></span></p> <p>Please wait...</p> </section> </div> </div> ";

  var changeLanguageComponentModule = angular.module('Q.components.change-language-dialog', [modalComponentModule]).factory('changeLanguageDialog', ['$modal', '$q', function changeLanguageDialog($modal, $q) {
    return {
      open: function open(_solution, _languageIds, _switchLanguageFn) {
        if (!_solution) return $q.reject();
        return $modal.openOnce('changeLanguageDialog', {
          template: changeLanguageDialogTemplate,
          controller: 'ChangeLanguageDialogCtrl as $ctrl',
          resolve: {
            solution: function solution() {
              return _solution;
            },
            languageIds: function languageIds() {
              return _languageIds;
            },
            switchLanguageFn: function switchLanguageFn() {
              return _switchLanguageFn;
            }
          }
        }).result;
      }
    };
  }]).controller('ChangeLanguageDialogCtrl', ['$modalInstance', 'solution', 'languageIds', 'switchLanguageFn', function ChangeLanguageDialogCtrl($modalInstance, solution, languageIds, switchLanguageFn) {
    var ctrl = this;

    ctrl.testCasesEnabled = solution.sampleTestCasesEnabled;

    var currentLanguage = ctrl.currentLanguage = solution.language;
    ctrl.currentLanguageIds = [currentLanguage];
    ctrl.languageIds = languageIds.slice().filter(function (lid) {
      return lid !== currentLanguage;
    });
    ctrl.newLanguage = [];

    ctrl.langChanged = function () {
      return ctrl.newLanguage[0];
    };

    ctrl.languageName = languages.toName;

    ctrl.changeButtonText = function () {
      return ctrl.langChanged() ? 'Switch to solving in ' + languages.toName(ctrl.newLanguage) : 'Choose a new language from above';
    };

    ctrl.cancel = function () {
      return $modalInstance.dismiss();
    };

    ctrl.change = function () {
      ctrl.processing = true;
      switchLanguageFn(ctrl.newLanguage[0]).then(ctrl.cancel).catch(function () {
        ctrl.processing = false;
      });
    };
  }]);

  // import { fooServiceModule } from '../../services/foo/foo-service';

  var bufferedSaveHelperModule = angular.module('Q.helpers.buffered-save-helper', [
    // fooServiceModule,
  ]).factory('bufferedSaveHelper', ['$timeout', '$q', function ($timeout, $q) {
    var AUTOSAVE_RATE = 500;

    /**
     * This function creates a function that can be used to safely save changes repeatedly. These changes
     * are buffered, so we don't make more than one request per maxRate, unless there's a tag (specifying
     * it's an important update).
     *
     * Each call to the generated function returns a Promise, which is guaranteed to resolve or reject eventually
     * (assuming the underlying saveFn resolves), even if it's delayed (due to grouping saves) or buffered until
     * an existing save completes.
     *
     * @TODO One issue with this design is we always save the current model at the time the current save completes,
     *       rather than caching the save at that time.
     *
     * @param {Function} saveFn The function that performs the actual save. Will be passed in a tag if provided.
     * @param {number} maxRate Override default max rate to make saves
     * @returns {Function} Function that will only save on regular intervals
     */
    return function bufferedSaveHelper(saveFn) {
      var maxRate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : AUTOSAVE_RATE;

      var delay = null;
      var saving = false;
      // stores a deferred that's resolved after all delayed and waiting saves are completed
      var savingDeferred = void 0;

      // these are saves waiting on the delay to time out
      var delayedSaves = [];

      // these are saves that need to be triggered after the current save completes
      var waitingSaves = [];

      // used to resolve or reject any delayed save deferreds
      var notifyDelayedSaves = function notifyDelayedSaves(type) {
        return function (resp) {
          delayedSaves.forEach(function (d) {
            return d[type](resp);
          });
          delayedSaves = [];
          return $q[type](resp);
        };
      };

      // This method is supposed to only enable triggering autosaves every 500ms or so
      // and other saves immediately. The design needs to enable listening to the returned promise,
      // which should always be resolved or rejected once we've actually handled a save, even if that save was
      // triggered from somewhere else.
      // There's also the oddity of needing a secondary workflow for triggering a save as soon as we return
      // from the previous save attempt.
      function bufferedSave(tag) {
        if (saving) {
          // we can't re-save until we know the previous save was successful
          // store the tag for future use, as well as a deferred for this save
          waitingSaves.tag = waitingSaves.tag || tag;
          var d = $q.defer();
          waitingSaves.push(d);
          return d.promise;
        }

        if (delay) {
          // clear any previous delay (but retain the delayedSaves)
          $timeout.cancel(delay);
          delay = null;
        }

        // initialize a new afterSave state if necessary
        savingDeferred = savingDeferred || $q.defer();
        bufferedSave.afterSave = savingDeferred.promise;

        function _saveNow(tag) {
          saving = true;
          delay = null;
          return saveFn(tag).then(notifyDelayedSaves('resolve'), notifyDelayedSaves('reject')).finally(function () {
            saving = false;
            if (waitingSaves.length) {
              // if we have any waiting saves, convert them to delayed saves, and re-save
              var _tag = waitingSaves.tag;
              delayedSaves = waitingSaves;
              waitingSaves = [];
              // if this is a tagged save, it'll get triggered immediately
              bufferedSave(_tag);
            } else {
              savingDeferred.resolve();
              savingDeferred = null;
            }
          });
        }

        if (tag) {
          // trigger tagged saves immediately
          return _saveNow(tag);
        } else {
          // delay at least 500ms before starting autosaves (which will continue to stretch out until
          // input stops, or we get a tagged save)
          var _d = $q.defer();
          delayedSaves.push(_d);
          delay = $timeout(_saveNow, maxRate);
          return _d.promise;
        }
      }

      // returns true if we're saving or waiting on a save
      bufferedSave.isSaving = function () {
        return !!(saving || delayedSaves.length || waitingSaves.length);
      };
      // initialize afterSave to resolve immediately
      bufferedSave.afterSave = $q.resolve();

      return bufferedSave;
    };
  }]);

  /**
   * Generates a pseudo-unique color based on a string.
   * The color will be the same every time (assuming the saturation & lightness inputs are consistent).
   *
   * @param str String to convert (will be used to vary the hue only)
   * @param saturation Adjust the color saturation, 0.0 = gray, 1.0 = full color, defaults to 0.8
   * @param lightness Adjust the overall lightness, 0.0 = black, 1.0 = white, defaults to 0.65
   * @returns {*}
   */
  function colorFromString(str) {
    var saturation = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0.8;
    var lightness = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0.65;

    var a = 1;
    for (var i = 0; i < str.length; i++) {
      a = 17 * (a + str.charCodeAt(i)) % 360;
    }
    var hue = a / 360;

    return hsl2hex({ h: hue, s: saturation, l: lightness });
  }

  /**
   * Converts a color from rgb to rgb-hex
   * @param r
   * @param g
   * @param b
   * @returns {string}
   */
  function rgb2hex(_ref) {
    var r = _ref.r,
        g = _ref.g,
        b = _ref.b;

    function digits(n) {
      var m = Math.round(255 * n).toString(16);
      return m.length === 1 ? '0' + m : m;
    }

    return '#' + digits(r) + digits(g) + digits(b);
  }

  /**
   * Converts a string hex value to an RGB object;
   * @param hex
   * @returns {{r: Number, g: Number, b: Number}}
   */
  function hex2rgb(hex) {
    if (isObject(hex)) return hex;
    hex = hex[0] === '#' ? hex.slice(1) : hex;
    return {
      r: parseInt(hex.slice(0, 2), 16) / 255,
      g: parseInt(hex.slice(2, 4), 16) / 255,
      b: parseInt(hex.slice(4, 6), 16) / 255
    };
  }

  /**
   * Converts a color from hsl to rgb-hex
   * @param h
   * @param s
   * @param l
   * @returns {string}
   */
  function hsl2hex(_ref2) {
    var h = _ref2.h,
        s = _ref2.s,
        l = _ref2.l;

    if (s === 0) {
      return rgb2hex({ r: l, g: l, b: l });
    }
    var var2 = l < 0.5 ? l * (1 + s) : l + s - s * l;
    var var1 = 2 * l - var2;
    var hue2rgb = function hue2rgb(hue) {
      if (hue < 0) {
        hue += 1;
      }
      if (hue > 1) {
        hue -= 1;
      }
      if (6 * hue < 1) {
        return var1 + (var2 - var1) * 6 * hue;
      }
      if (2 * hue < 1) {
        return var2;
      }
      if (3 * hue < 2) {
        return var1 + (var2 - var1) * 6 * (2 / 3 - hue);
      }
      return var1;
    };
    return rgb2hex({
      r: hue2rgb(h + 1 / 3),
      g: hue2rgb(h),
      b: hue2rgb(h - 1 / 3)
    });
  }

  function blend(_ref3) {
    var c1 = _ref3.c1,
        c2 = _ref3.c2,
        _ref3$ratio = _ref3.ratio,
        ratio = _ref3$ratio === undefined ? 0.5 : _ref3$ratio;

    c1 = hex2rgb(c1);
    c2 = hex2rgb(c2);
    var c1Ratio = 1 - ratio;
    var c2Ratio = ratio;
    return {
      r: c1.r * c1Ratio + c2.r * c2Ratio,
      g: c1.g * c1Ratio + c2.g * c2Ratio,
      b: c1.b * c1Ratio + c2.b * c2Ratio
    };
  }

  function blend2hex(_ref4) {
    var c1 = _ref4.c1,
        c2 = _ref4.c2,
        ratio = _ref4.ratio;

    return rgb2hex(blend({ c1: c1, c2: c2, ratio: ratio }));
  }

  var TYPE_MAPPINGS = {
    Candidate: 'Taking Assessment',
    TeamMember: 'Interviewer'
  };

  var FIREBASE_REF_ROOT = 'liveInterviews/';
  var FIREBASE_REF_ACTIVE = FIREBASE_REF_ROOT + 'active/';

  var RUN_TRIGGER_PATH = '/trigger';
  var RUN_TYPE_TESTS = 'tests';
  var RUN_TYPE_SUBMIT = 'attempt';
  var SUBMIT_PATH = '/submitting';
  var SUBMIT_RUNNING = 'running';
  var SUBMIT_COMPLETED = 'completed';

  var liveInterviewServiceModule = angular.module('Q.services.live-interview-service', [assessmentResultModelModule, firebaseServiceModule]).factory('liveInterviewService', ['firebase', 'AssessmentResult', function (firebase, AssessmentResult) {
    var _assessmentResultsWatcher = null;

    var svc = {
      assessmentResults: [],
      watchAssessments: watchAssessments,
      unwatchAssessments: unwatchAssessments,
      join: join,
      destroy: destroy
    };

    return svc;

    /**
     * Start watching for active assessment results.
     *
     * Call unwatchAssessments() when finished
     *
     * @param team Team to watch
     * @returns {Array} Active assessment results array (can be used directly, or via `liveInterviewService.assessmentResults`
     */
    function watchAssessments(team) {
      unwatchAssessments();

      var teamId = team.id || team;

      function updateAssessmentResults(activeIds) {
        svc.assessmentResults.length = 0;
        var ids = [];
        angular.forEach(activeIds, function (_, id) {
          if (!/^\$/.test(id)) {
            ids.push(id);
          }
        });
        AssessmentResult.fetchAllIds(ids, {
          includes: ['team_candidate']
        }).then(function (ars) {
          var _svc$assessmentResult;

          (_svc$assessmentResult = svc.assessmentResults).push.apply(_svc$assessmentResult, toConsumableArray(ars.filter(function (ar) {
            return ar.canEdit();
          })));
        });
      }

      firebase.get(FIREBASE_REF_ACTIVE + teamId).then(function (activeIds) {
        updateAssessmentResults(activeIds);
        _assessmentResultsWatcher = activeIds.$watch(function () {
          return updateAssessmentResults(activeIds);
        });
      });

      return svc.assessmentResults;
    }

    /**
     * Stop watching the active assessment results.
     */
    function unwatchAssessments() {
      if (_assessmentResultsWatcher) {
        _assessmentResultsWatcher();
        _assessmentResultsWatcher = null;
        svc.assessmentResults.length = 0;
      }
    }

    /**
     * Destroys any data associated with the assessment result
     * @param assessmentResult
     */
    function destroy(assessmentResult) {
      firebase.remove(_getRef(assessmentResult));
    }

    /**
     * Joins a live interview, either as a candidate or a team member. Provides methods
     * to manipulate the live interview.
     *
     * Note: please make sure to disconnect() for team members
     *
     * @param assessmentResult AssessmentResult of the live interview.
     * @param user Current user who is joining the live interview
     * @returns {LiveInterview} An object with methods to manipulate the live interview
     */
    function join(assessmentResult, user) {
      // This is the base ref for storing details about this assessment for live interviews
      // The parent paths to this are non-enumerable on Firebase, so you can't loop through
      // them to find existing solutions.
      var firebaseRef = _getRef(assessmentResult);
      var firebaseActiveChallengeRef = firebaseRef + '/activeChallengeId';

      // Enables teams to see candidates who have are connected in a secure-ish fashion.
      // We use the team ID followed by assessmentResult ID, because it doesn't provide a way
      // to see other solutions within Firebase by listing based on team ID, yet a team can
      // quickly identify who's connected by listing the assessmentResult IDs and looking up
      // the associated team candidates.
      var firebaseActiveRef = FIREBASE_REF_ACTIVE + assessmentResult.teamId + '/' + assessmentResult.id;
      var unwatch = {};

      var type = TYPE_MAPPINGS[user.$type] || user.$type;
      var isCandidate = type === TYPE_MAPPINGS.Candidate;

      var userProfile = {
        id: user.id,
        name: user.name,
        profileImgFileId: user.profileImg && user.profileImg.fileId,
        type: type,
        color: colorFromString(user.name + ':' + type)
      };
      var _usersRef = null;
      var usersList = [];

      var svc = {
        assessmentResult: assessmentResult,
        id: assessmentResult.id,
        // tracks whether we're currently connected (so we can avoid errors)
        connected: false,
        userProfile: userProfile,
        updateUserProfile: updateUserProfile,
        firebaseRef: firebaseRef,
        firebaseActiveChallengeRef: firebaseActiveChallengeRef,
        setActiveChallengeId: setActiveChallengeId,
        firebaseActiveRef: firebaseActiveRef,
        disconnect: disconnect,
        usersList: usersList,
        destroy: destroy,
        unwatch: unwatch
        // TODO: move activeChallenge tracking, current results tracking, etc
        // activeChallenge: null,
      };

      eventEmitter(svc);

      reconnect();

      return svc;

      //// Public Methods

      /**
       * Call after making changes to the user profile to propagate them to Firebase
       */
      function updateUserProfile() {
        if (unwatch.user) unwatch.user.update();
      }

      /**
       * Connects or reconnects to the live interview
       */
      function reconnect() {
        if (svc.connected) return;

        svc.connected = true;
        // connect this user
        unwatch.user = firebase.setWhenConnected(firebaseRef + '/users/' + user.id, userProfile);

        if (isCandidate) {
          unwatch.candidateActive = firebase.setWhenConnected(firebaseActiveRef, true);
        }

        firebase.get(firebaseRef + '/users').then(function (usersRef) {
          _usersRef = usersRef;
          _updateUsersList();
          var ulUnwatch = usersRef.$watch(_updateUsersList);
          unwatch.usersList = function () {
            usersList.length = 0;
            ulUnwatch();
            _usersRef = null;
          };

          svc.emit('connected');
        });
      }

      function setActiveChallengeId(challengeId) {
        return firebase.set(firebaseActiveChallengeRef, challengeId);
      }

      /**
       * Disconnects from the live interview (removes watchers)
       */
      function disconnect() {
        svc.connected = false;
        angular.forEach(unwatch, function (uw, k) {
          uw();
          delete unwatch[k];
        });
        svc.emit('disconnected');
      }

      /**
       * Destroys the live interview if it is no longer in use
       */
      function destroy() {
        firebase.remove(firebaseRef);
        disconnect();
        svc.emit('destroyed');
      }

      //// Private

      /**
       * Updates the connected users list
       * @private
       */
      function _updateUsersList() {
        usersList.length = 0;
        if (_usersRef) {
          angular.forEach(_usersRef, function (props, id) {
            if (id[0] === '$') return;
            props.$self = id === user.id;
            usersList.push(props);
          });
          usersList.sort(function (a, b) {
            if (a.$self) return -1;
            if (b.$self) return 1;
            return compareIgnoreCase(a.name, b.name);
          });
          svc.emit('usersListChanged', usersList);
        }
      }
    }

    //// Private

    function _getRef(assessmentResult) {
      return FIREBASE_REF_ROOT + assessmentResult.teamCandidateId + '/' + assessmentResult.assessmentId;
    }
  }]);

  var challengeRouteInteractHelperModule = angular.module('Q.helpers.challenge-route-interact-helper', [firebaseModule]).factory('challengeRouteInteractHelper', ['firebase', function (firebase) {
    /**
     * Sets up basic listeners for Interact event triggers
     * @param ctrl
     * @param assessmentResultProcess
     * @param $scope
     */
    function init(ctrl, assessmentResultProcess, $scope) {
      var challengeProcess = ctrl.process;
      var unwatchTrigger = null;
      var firebaseRef = assessmentResultProcess.liveInterview.firebaseRef;
      firebase.get(firebaseRef + RUN_TRIGGER_PATH).then(function (triggerRef) {
        unwatchTrigger = triggerRef.$watch(function () {
          var res = false;
          if (triggerRef.$value === RUN_TYPE_TESTS) {
            res = ctrl.runTests();
          } else if (triggerRef.$value === RUN_TYPE_SUBMIT) {
            res = ctrl.submitSolution();
          }

          if (triggerRef.$value) {
            triggerRef.$remove();
          }

          if (res === false) {
            // clear anything waiting
            challengeProcess.startRun().finally(challengeProcess.endRun);
          }
        });
      });
      $scope.$on('$destroy', function () {
        if (unwatchTrigger) unwatchTrigger();
      });

      ctrl.startSubmitSolution = function () {
        firebase.set(firebaseRef + SUBMIT_PATH, {
          id: ctrl.challenge.id,
          state: SUBMIT_RUNNING,
          ts: Date.now()
        });
      };
      ctrl.endSubmitSolution = function () {
        firebase.set(firebaseRef + SUBMIT_PATH, {
          id: ctrl.challenge.id,
          state: SUBMIT_COMPLETED,
          ts: Date.now()
        });
      };
    }

    return {
      init: init
    };
  }]);

  var multiFilePathDialogTemplate = "<form class=\"multi-file-path-form\" name=\"multFilePathForm\" ng-submit=\"multFilePathForm.$valid && $ctrl.add()\" novalidate> <div class=\"modal-header\"> <h4 class=\"modal-title\" ng-bind=\"$ctrl.title\"></h4> <button class=\"close\" aria-label=\"Close\" ng-click=\"$ctrl.cancel()\"> <span aria-hidden=\"true\">&times;</span> </button> </div> <div class=\"modal-body\"> <p ng-bind=\"$ctrl.prompt\"></p> <form-group> <div class=\"input-group input-group-inlined\"> <span class=\"path-header input-group-addon input-group-addon-inlined no-gap\" ng-click=\"$ctrl.focusInput()\"> <file-icon file=\"$ctrl\"></file-icon> <span class=\"path-root\" ng-if=\"!$ctrl.showReferenceChoice\">{{$ctrl.isReference ? 'Reference' : 'Project'}}/</span> <span class=\"path-root dropdown\" dropdown ng-if=\"$ctrl.showReferenceChoice\"> <a class=\"dropdown-toggle\" dropdown-toggle><span class=\"caret\"></span> {{$ctrl.isReference ? 'Reference' :\n              'Project'}}/</a> <ul class=\"dropdown-menu\" dropdown-menu> <li> <a ng-click=\"$ctrl.isReference = false\" ng-class=\"{selected:!$ctrl.isReference}\"> Project Files </a> </li> <li> <a ng-click=\"$ctrl.isReference = true\" ng-class=\"{selected:$ctrl.isReference}\"> Reference Files </a> </li> </ul> </span> </span> <input type=\"text\" class=\"form-control\" name=\"multiFilePathInput\" ng-model=\"$ctrl.path\" ng-required=\"!$ctrl.allowRoot\" placeholder=\"{{ $ctrl.placeholder }}\" ng-disabled=\"$ctrl.disabled\" ng-change=\"$ctrl.onPathChanged()\" autocomplete=\"off\" multi-file-path-input> </div> <form-group-messages> { filePath: $ctrl.invalidMessage || 'Invalid path', required: 'Please provide a path', } </form-group-messages> </form-group> <div ng-if=\"$ctrl.showReferenceChoice && !$ctrl.isReference && $ctrl.allowRoot\" class=\"qualified-checkbox\"> <input id=\"multi-file-path-dialog-extract-reference\" type=\"checkbox\" ng-model=\"$ctrl.extractReference\"> <label for=\"multi-file-path-dialog-extract-reference\"> Convert <em>*.ref.*</em> files to reference files </label> </div> <div ng-if=\"$ctrl.showDirectoryToggle\" class=\"qualified-checkbox\"> <input id=\"multi-file-path-dialog-is-directory\" type=\"checkbox\" ng-model=\"$ctrl.directory\" ng-change=\"$ctrl.directoryManualToggled = true\"> <label for=\"multi-file-path-dialog-is-directory\"> Create as directory </label> </div> <div class=\"alert alert-warning\" ng-show=\"$ctrl.warningMessage && !multFilePathForm.$invalid\" ng-bind-html=\"$ctrl.warningMessage || '&nbsp'\"></div> <div class=\"help-block file-path-examples\" ng-if=\"$ctrl.examples\"> <strong>Example:</strong> <div> <span ng-repeat=\"example in $ctrl.examples\"><strong>{{$ctrl.cleanPath}}/</strong>{{example}}</span> <span ng-if=\"$ctrl.moreFiles\">and {{$ctrl.moreFiles}} more…</span> </div> </div> <div ng-if=\"$ctrl.hasReference\" class=\"qualified-checkbox\"> <input id=\"multi-file-path-dialog-rename-reference\" type=\"checkbox\" ng-model=\"$ctrl.renameReference\" ng-change=\"multFilePathForm.multiFilePathInput.$validate()\"> <label for=\"multi-file-path-dialog-rename-reference\">Also rename {{ $ctrl.isReference ? 'Project' : 'Reference' }} File{{$ctrl.directory ? 's under the same directory' : ''}}</label> </div> </div> <div class=\"modal-footer\"> <button class=\"cancel btn-default\" ng-click=\"$ctrl.cancel()\" ng-disabled=\"$ctrl.disabled\"> Cancel </button> <input type=\"submit\" class=\"submit btn-primary\" value=\"OK\" ng-disabled=\"$ctrl.disabled\"> </div> </form> ";

  var multiFilePathDialogComponentModule = angular.module('Q.components.multi-file-path-dialog', [modalComponentModule]).factory('multiFilePathDialog', ['$modal', function ($modal) {
    return {
      /**
       *
       * @param params.path {String?} Initial path (blank)
       * @param params.title {String?} Dialog title
       * @param params.prompt {String?} Prompt for input
       * @param params.placeholder {String?} placeholder for input
       * @param params.isReference {boolean?} true if this is a reference file or directory
       * @param params.hasReference {boolean?} true if this file has a reference file that should be renamed
       * @param params.allowRoot {boolean?} true if we can return an empty string (root)
       * @param params.examples {Array<string>?} if provided, shown as an example of the final path for multiple file imports
       * @param params.moreFiles {Number?} shows there there's more files than the examples
       * @param params.directory {boolean?} true if this is a directory
       * @param params.showDirectoryToggle {boolean?} true if we want to let the user change the directory mode
       * @param params.invalid {Function?} Optional validation method, which needs to return `false` or a message to show if invalid
       * @param params.warning (Function?} Optional method which returns a warning if necessary
       * @returns {Promise} With the resulting path
       */
      open: function open(_params) {
        return $modal.openOnce('multiFilePathDialog', {
          template: multiFilePathDialogTemplate,
          controller: 'MultiFilePathDialogCtrl as $ctrl',
          windowClass: 'multi-file-path-dialog',
          size: 'md',
          resolve: {
            params: function params() {
              return _params;
            }
          }
        }).result;
      }
    };
  }]).controller('MultiFilePathDialogCtrl', ['$modalInstance', '$scope', 'params', function MultiFilePathDialogCtrl($modalInstance, $scope, params) {
    var _params$path = params.path,
        path = _params$path === undefined ? '' : _params$path,
        _params$directory = params.directory,
        directory = _params$directory === undefined ? false : _params$directory,
        _params$showDirectory = params.showDirectoryToggle,
        showDirectoryToggle = _params$showDirectory === undefined ? false : _params$showDirectory,
        _params$isReference = params.isReference,
        isReference = _params$isReference === undefined ? false : _params$isReference,
        _params$showReference = params.showReferenceChoice,
        showReferenceChoice = _params$showReference === undefined ? false : _params$showReference,
        _params$hasReference = params.hasReference,
        hasReference = _params$hasReference === undefined ? false : _params$hasReference,
        _params$allowRoot = params.allowRoot,
        allowRoot = _params$allowRoot === undefined ? false : _params$allowRoot,
        _params$accessLevels = params.accessLevels,
        accessLevels = _params$accessLevels === undefined ? false : _params$accessLevels,
        _params$title = params.title,
        title = _params$title === undefined ? 'Add File' : _params$title,
        _params$prompt = params.prompt,
        prompt = _params$prompt === undefined ? 'New file path & name:' : _params$prompt,
        _params$placeholder = params.placeholder,
        placeholder = _params$placeholder === undefined ? 'src/lib/util.js' : _params$placeholder,
        examples = params.examples,
        moreFiles = params.moreFiles,
        _params$invalid = params.invalid,
        invalid = _params$invalid === undefined ? function () {
      return false;
    } : _params$invalid,
        _params$warning = params.warning,
        warning = _params$warning === undefined ? function () {
      return false;
    } : _params$warning;


    var ctrl = this;
    ctrl.disabled = false;

    ctrl.path = path;
    ctrl.examples = examples;
    ctrl.moreFiles = moreFiles;

    ctrl.directory = directory;
    ctrl.showDirectoryToggle = showDirectoryToggle;
    ctrl.isReference = ctrl.reference = isReference; // ctrl.reference is for icon styling
    ctrl.showReferenceChoice = showReferenceChoice;
    ctrl.extractReference = hasReference;
    ctrl.hasReference = hasReference;
    ctrl.renameReference = true;
    ctrl.allowRoot = allowRoot;

    ctrl.accessLevels = accessLevels;
    ctrl.accessLevel = accessLevels && accessLevels[0].value;

    ctrl.title = title;
    ctrl.prompt = prompt;
    ctrl.placeholder = placeholder;

    ctrl.onPathChanged = function () {
      if (showDirectoryToggle && ctrl.path && !ctrl.directoryManualToggled) {
        // unless manually set, swap directory state automatically if the path ends with `/`
        ctrl.directory = ctrl.path.endsWith('/');
      }
      var cleanedPath = cleanPath(ctrl.path);
      ctrl.cleanPath = cleanedPath ? '/' + cleanedPath : '';
      ctrl.warningMessage = warning(cleanedPath, ctrl);
    };

    ctrl.cancel = function () {
      return $modalInstance.dismiss('cancel');
    };

    ctrl.pathIsInvalid = function () {
      var path = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : ctrl.path;

      ctrl.invalidMessage = invalid(cleanPath(path), ctrl.renameReference);
      ctrl.icon = ctrl.cleanPath && ctrl.invalidMessage ? 'warning' : null;
      return ctrl.invalidMessage;
    };

    ctrl.focusInput = function () {
      var input = $modalInstance.getElement().querySelector('input[name="multiFilePathInput"]');
      input && input.focus();
    };

    ctrl.add = function () {
      ctrl.path = cleanPath(ctrl.path);
      if (!ctrl.pathIsInvalid()) {
        $modalInstance.close(ctrl);
      }
    };
  }]).directive('multiFilePathInput', function () {
    return {
      restrict: 'A',
      require: 'ngModel',
      link: function link(scope, elem, attr, ngModelCtrl) {
        ngModelCtrl.$validators.filePath = function (modelValue) {
          return !modelValue || !scope.$ctrl.pathIsInvalid(modelValue);
        };
        scope.$applyAsync(function () {
          var start = elem.val().lastIndexOf('/') + 1;
          elem.focus().selectInput(start);
        });
      }
    };
  });

  var multiFileOverwriteDialogTemplate = "<div class=\"modal-header\"> <h4 class=\"modal-title\">Duplicate file paths</h4> <button class=\"close\" aria-label=\"Close\" ng-click=\"$ctrl.cancel()\"> <span aria-hidden=\"true\">&times;</span> </button> </div> <div class=\"modal-body\"> <p> The following paths already exist within your project. Check any you would like to overwrite. <br>(The overwritten files will be moved to your deleted files.) </p> <div class=\"checkable-input-set one-col\"> <div class=\"qualified-checkbox\" ng-repeat=\"p in $ctrl.paths\"> <input id=\"multi-file-overwrite-dialog-path{{$index}}\" type=\"checkbox\" ng-model=\"p.checked\" ng-disabled=\"p.isLocked\"> <label for=\"multi-file-overwrite-dialog-path{{$index}}\"> /{{p.path}} <span class=\"case-change-note\" ng-if=\"!p.isLocked && p.caseChange\" tooltip=\"Note: This will change the letter case of the current file\"></span> <span class=\"locked-note\" ng-if=\"p.isLocked\" tooltip=\"Unable to create file: {{p.lockedReason}}\"></span> </label> </div> </div> <div class=\"checkable-input-set one-col\" ng-if=\"$ctrl.refPaths.length\"> <h4>Reference Files</h4> <div class=\"qualified-checkbox\" ng-repeat=\"p in $ctrl.refPaths\"> <input id=\"multi-file-overwrite-dialog-ref-path{{$index}}\" type=\"checkbox\" ng-model=\"p.checked\" ng-disabled=\"p.isLocked\"> <label for=\"multi-file-overwrite-dialog-ref-path{{$index}}\"> /{{p.path}} <span class=\"locked-note\" ng-if=\"p.isLocked\">({{p.lockedReason}})</span> </label> </div> </div> </div> <div class=\"modal-footer\"> <button class=\"cancel btn-default\" ng-click=\"$ctrl.cancel()\">Cancel</button> <button class=\"overwrite-all btn-warning\" ng-click=\"$ctrl.overwriteAll()\"> Overwrite All </button> <button class=\"submit btn-primary\" ng-click=\"$ctrl.continue()\"> Continue </button> </div> ";

  var multiFileOverwriteDialogComponentModule = angular.module('Q.components.multi-file-overwrite-dialog', [modalComponentModule]).factory('multiFileOverwriteDialog', ['$modal', '$q', function ($modal, $q) {
    function getDuplicates(paths, files) {
      var filePaths = files.filter(function (f) {
        return !f.directory;
      }).map(function (f) {
        return f.path.toLowerCase();
      });
      var restrictedDirPaths = files.filter(function (f) {
        return f.directoryLocked;
      }).map(function (f) {
        return f.path.toLowerCase();
      });

      return paths.map(cleanPath).sort(compareIgnoreCase).map(function (path) {
        var props = {
          checked: false,
          path: path
        };

        var pathLower = path.toLowerCase();

        // Check for paths that would attempt to convert a file into a directory
        var conflictDir = filePaths.some(function (fp) {
          return pathLower.startsWith(fp + '/');
        });
        if (conflictDir) {
          props.isLocked = true;
          props.lockedReason = 'overwrites file "' + conflictDir + ' with a directory';
          return props;
        }

        // Check for paths that would modify a restricted directory
        var restrictedDir = restrictedDirPaths.find(function (fp) {
          return pathLower.startsWith(fp + '/');
        });
        if (restrictedDir) {
          props.isLocked = true;
          props.lockedReason = '"' + restrictedDir + '" is restricted';
          return props;
        }

        // Check for paths that are a duplicate
        var duplicate = files.find(function (f) {
          return f.path.toLowerCase() === pathLower;
        });
        if (duplicate) {
          props.isDuplicate = true;

          // see if we can modify this file
          var lockedReason = null;
          if (duplicate.readOnly) {
            lockedReason = 'original file is read-only';
          } else if (duplicate.disabled) {
            lockedReason = 'original file is locked';
          } else if (duplicate.directory) {
            lockedReason = 'file would overwrite a directory';
          }

          props.isLocked = !!lockedReason;
          props.lockedReason = lockedReason;
          props.caseChange = !duplicate.locked && duplicate.path !== path;
          return props;
        }

        // this will get filtered out
        return false;
      })

      // Remove any empty results
      .filter(identity);
    }

    return {
      /**
       *
       * @param params.paths {Array<string>} Paths to check
       * @param params.files {Array<{path:string}>} Files to compare against
       * @param params.referencePaths {Array<string>?} Relative Paths to check
       * @param params.referenceFiles {Array<{path:string}>?} Files to compare relative paths against
       * @returns {Promise<Array<string>>} Resolves with an array of paths to keep, rejects if canceled
       */
      open: function open(params) {
        var duplicatePaths = getDuplicates(params.paths, params.files);
        var duplicateRefPaths = params.referencePaths ? getDuplicates(params.referencePaths, params.referenceFiles) : [];

        if (duplicatePaths.length || duplicateRefPaths.length) {
          return $modal.openOnce('multiFileOverwriteDialog', {
            template: multiFileOverwriteDialogTemplate,
            controller: 'MultiFileOverwriteDialogCtrl as $ctrl',
            windowClass: 'multi-file-overwrite-dialog',
            resolve: {
              paths: function paths() {
                return duplicatePaths;
              },
              refPaths: function refPaths() {
                return duplicateRefPaths;
              }
            }
          }).result.then(function (_ref) {
            var excludePaths = _ref.paths,
                excludedRefPaths = _ref.refPaths;

            // return all paths, minus the duplicate paths that weren't included
            return {
              paths: params.paths.filter(function (p) {
                return !excludePaths.includes(p);
              }),
              referencePaths: (params.referencePaths || []).filter(function (p) {
                return !excludedRefPaths.includes(p);
              })
            };
          });
        } else {
          // everything is OK
          return $q.resolve({
            paths: params.paths,
            referencePaths: params.referencePaths
          });
        }
      }
    };
  }]).controller('MultiFileOverwriteDialogCtrl', ['$modalInstance', 'paths', 'refPaths', function MultiFileOverwriteDialogCtrl($modalInstance, paths, refPaths) {
    var ctrl = this;

    ctrl.paths = paths;
    ctrl.refPaths = refPaths;
    ctrl.cancel = function () {
      // reject dialog
      $modalInstance.dismiss('cancel');
    };
    ctrl.overwriteAll = function () {
      // close dialog with just conflict paths
      $modalInstance.close({
        paths: ctrl.paths.filter(function (p) {
          return p.isLocked;
        }).map(function (p) {
          return p.path;
        }),
        refPaths: ctrl.refPaths.filter(function (p) {
          return p.isLocked;
        }).map(function (p) {
          return p.path;
        })
      });
    };
    ctrl.continue = function () {
      // close dialog with all excluded or conflict paths
      $modalInstance.close({
        paths: ctrl.paths.filter(function (p) {
          return !p.checked || p.isLocked;
        }).map(function (p) {
          return p.path;
        }),
        refPaths: ctrl.refPaths.filter(function (p) {
          return !p.checked || p.isLocked;
        }).map(function (p) {
          return p.path;
        })
      });
    };
  }]);

  var smartIdeDialogTemplate = "<div class=\"modal-header\"> <h4 class=\"modal-title\"> <span class=\"smart-ide-icon\"></span> About Qualified SmartIDE </h4> <button class=\"close\" aria-label=\"Close\" ng-click=\"$close()\"> <span aria-hidden=\"true\">&times;</span> </button> </div> <div class=\"modal-body\"> <div class=\"markdown\"> <p> SmartIDE is a tool that brings advanced IDE features directly into our current web-based code editor. There's nothing special you have to do to start using these features. </p> <div class=\"alert alert-info\"> <p><strong>Smart IDE Status</strong></p> <ul class=\"tags-group\"> <li class=\"tag-smart-ide no-icon\"> <span class=\"icon-{{$ctrl.status.icon}}\"></span> {{$ctrl.status.toolbarTooltip}} </li> </ul> <p> SmartIDE works by spinning up a server out in the cloud to perform code analysis and provide feedback. While it's starting up, you won't be able to use the SmartIDE features, but this usually only takes a few seconds. You can identify the current status by looking at the SmartIDE icon in the toolbar. </p> <p><strong>Enable or Disable SmartIDE</strong></p> <p> You can use the buttons below to enable or disable the enhanced IDE features. </p> <p class=\"buttons\"> <button class=\"btn-info btn-plain-text\" ng-click=\"$ctrl.restartSmartIde()\" ng-if=\"$ctrl.smartIdeEnabled\" ng-disabled=\"$ctrl.disabled\" tooltip=\"Restarts the SmartIDE servers if there is any trouble with Smart IDE\"> Restart SmartIDE </button> <button class=\"btn-default btn-plain-text\" ng-click=\"$ctrl.smartIdeEnabled = false\" ng-if=\"$ctrl.smartIdeEnabled\" ng-disabled=\"$ctrl.disabled\" tooltip=\"Disables SmartIDE for this challenge, you can re-enable it if you want\"> Disable SmartIDE for this challenge </button> <button class=\"btn-info btn-plain-text\" ng-click=\"$ctrl.smartIdeEnabled = true\" ng-if=\"!$ctrl.smartIdeEnabled\" ng-disabled=\"$ctrl.disabled\" tooltip=\"Re-Enables SmartIDE for this challenge\"> Re-Enable SmartIDE </button> </p> </div> <h3>Leave Feedback</h3> <p>Leave feedback about your experience with SmartIDE</p> <user-feedback-form existing-feedback=\"$ctrl.existingUserFeedback\" feedback-type=\"feature\" feature-name=\"SmartIDE\" solution=\"$ctrl.solution\" challenge=\"$ctrl.challenge\"></user-feedback-form> <h3>Smart IDE Features</h3> <ul class=\"bullets\"> <li> <strong>Code Completion</strong>: SmartIDE provides context-aware, intelligent code completion. You will see it automatically make suggestions as you type, or you can show the suggestions at any time using <kbd>CTRL/CMD + Space</kbd> </li> <li> <strong>Inline Documentation</strong>: Documentation for methods is shown automatically while making suggestions. You can also see the documentation for any method or property by hovering over its name. </li> <li> <strong>Hinting & Error Checking</strong>: Real-time hinting and error-checking is available as you work. You'll see markers in the gutter on lines that contain possible errors. </li> </ul> </div> </div> <div class=\"modal-footer\"> <button class=\"btn-default\" ng-click=\"$close()\">Close</button> </div> ";

  var userFeedbackFormTemplate = "<form name=\"userFeedbackForm\" ng-submit=\"userFeedbackForm.$valid && $ctrl.submit()\"> <div class=\"feedback-rating\"> <star-rating ng-model=\"$ctrl.rating\" ng-disabled=\"$ctrl.disabled\"></star-rating> </div> <form-group class=\"feedback-notes\"> <div> <label for=\"userFeedbackForm.feedbackNotes\"> Feedback Notes? </label> <textarea class=\"form-control\" rows=\"2\" name=\"userFeedbackForm.feedbackNotes\" id=\"userFeedbackForm.feedbackNotes\" ng-disabled=\"$ctrl.disabled\" ng-model=\"$ctrl.feedbackNotes\" msd-elastic=\"\\n\"></textarea> </div> </form-group> <div class=\"submit-button\"> <button type=\"submit\" class=\"btn-primary btn-primary-alt btn-sm\" ng-disabled=\"$ctrl.disabled || !$ctrl.hasChanges()\" ng-bind=\"$ctrl.submitText()\"></button> <span class=\"feedback-status-text {{$ctrl.feedbackStatusClass()}}\" ng-bind=\"$ctrl.feedbackStatusText()\"></span> </div> </form> ";

  var starRatingComponentModule = angular.module('Q.components.star-rating', []).component('starRating', {
    bindings: {
      stars: '<'
    },
    require: {
      ngModelCtrl: 'ngModel'
    },
    controller: 'StarRatingCtrl',
    template: '<div class="star-rating-root"></div>'
  }).controller('StarRatingCtrl', ['$element', '$scope', function StarRatingCtrl($element, $scope) {
    var ctrl = this;

    ctrl.$onInit = ctrl.$onChanges = function () {
      var ngModelCtrl = ctrl.ngModelCtrl;
      var maxStars = +ctrl.stars || 5;
      var stars = [];

      var readonly = function readonly() {
        return $element.attr('disabled') || $element.attr('readonly');
      };

      var render = ngModelCtrl.$render = function () {
        var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : ngModelCtrl.$viewValue;

        // renders all stars at or below the value as active
        stars.forEach(function (s, i) {
          s.toggleClass('star-rating-active', i <= value);
        });
      };

      var root = $element.querySelector('.star-rating-root');
      root.empty();

      // render all stars, including a 0-value "star" for clearing the value

      var _loop = function _loop(value) {
        var star = angular.element('<span class="star-rating-star star-rating-star-' + value + '">');
        root.append(star);
        stars.push(star);

        // Star selection. We include mouseup so the user can drag the mouse around to the value they want,
        // like a slider
        star.on('click mouseup', function () {
          if (readonly()) return;
          render(value);
          $scope.$applyAsync(function () {
            if (value !== ngModelCtrl.$viewValue) {
              ngModelCtrl.$setViewValue(value);
            }
          });
        });

        star.on('mouseenter', function () {
          if (readonly()) return;
          // highlight every start at or below this one
          stars.forEach(function (s, i) {
            s.toggleClass('star-rating-hover', i <= value);
          });
        });

        star.on('mouseleave', function () {
          // clear all star hover markers (will be triggered immediately before mouseenter, so there's no
          // fear of flickering
          stars.forEach(function (s) {
            s.toggleClass('star-rating-hover', false);
          });
        });
      };

      for (var value = 0; value <= maxStars; value++) {
        _loop(value);
      }

      render();
    };
  }]);

  var userFeedbackFormComponentModule = angular.module('Q.components.user-feedback-form', [starRatingComponentModule, userFeedbackModelModule]).component('userFeedbackForm', {
    bindings: {
      existingFeedback: '<',

      feedbackType: '@',
      featureName: '@',
      tags: '<',

      team: '<',
      assessment: '<',
      assessmentResult: '<',
      challenge: '<',
      solution: '<',

      onSubmit: '&'
    },
    controller: 'UserFeedbackFormCtrl',
    template: userFeedbackFormTemplate
  }).controller('UserFeedbackFormCtrl', ['UserFeedback', 'appAlert', '$window', function UserFeedbackFormCtrl(UserFeedback, appAlert, $window) {
    var ctrl = this;

    var existingFeedback = void 0;
    var didSubmit = void 0;

    ctrl.$onInit = function () {
      existingFeedback = ctrl.existingFeedback || {
        rating: 0,
        feedbackNotes: ''
      };
      ctrl.rating = existingFeedback.rating;
      ctrl.feedbackNotes = existingFeedback.feedbackNotes;
    };

    ctrl.submitText = function () {
      return existingFeedback.id ? 'Update Feedback' : 'Submit Feedback';
    };

    ctrl.feedbackStatusText = function () {
      return didSubmit ? 'Thank you for your feedback!' : '';
    };
    ctrl.feedbackStatusClass = function () {
      return didSubmit ? 'text-success' : '';
    };
    ctrl.hasChanges = function () {
      return ctrl.rating !== existingFeedback.rating || ctrl.feedbackNotes !== existingFeedback.feedbackNotes;
    };

    ctrl.submit = function () {
      if (ctrl.disabled) return;
      didSubmit = false;
      ctrl.disabled = true;
      UserFeedback.saveOrCreate(existingFeedback, {
        rating: ctrl.rating,
        feedbackNotes: ctrl.feedbackNotes,
        feedbackType: ctrl.feedbackType,
        featureName: ctrl.featureName,
        tags: ctrl.tags,
        teamId: ctrl.team && ctrl.team.id,
        assessmentId: ctrl.assessment && ctrl.assessment.id,
        assessmentResultId: ctrl.assessmentResult && ctrl.assessmentResult.id,
        challengeId: ctrl.challenge && ctrl.challenge.id,
        solutionId: ctrl.solution && ctrl.solution.id,
        browserProperties: {
          appId: appConfig.appId,
          release: appConfig.release,
          ua: $window.navigator.userAgent,
          language: $window.navigator.language,
          windowSize: $window.innerWidth + 'x' + $window.innerHeight
        }
      }).then(function (newData) {
        didSubmit = true;
        existingFeedback = newData;
        ctrl.onSubmit(newData);
      }).catch(appAlert.modelError).finally(function () {
        ctrl.disabled = false;
      });
    };
  }]);

  var smartIdeDialogComponentModule = angular.module('Q.components.smart-ide-dialog', [modalComponentModule, userFeedbackModelModule, userFeedbackFormComponentModule]).factory('smartIdeDialog', ['$modal', 'UserFeedback', function ($modal, UserFeedback) {
    return {
      /**
       * Opens a new iFrame dialog.
       *
       * @returns {{result:Promise, opened:Promise, closed:Promise, close:Function, dismiss:Function, reload:Function, setUrl:Function}} modalInstance
       */
      open: function open(_mfc) {
        return $modal.openOnce('smartIdeDialog', {
          template: smartIdeDialogTemplate,
          controller: 'SmartIdeDialogCtrl as $ctrl',
          windowClass: 'smart-ide-dialog',
          allowDarkMode: true,
          size: 'lg',
          resolve: {
            mfc: function mfc() {
              return _mfc;
            },
            userFeedback: function userFeedback() {
              if (_mfc) {
                return UserFeedback.findExisting({
                  feedbackType: 'feature',
                  featureName: 'SmartIDE',
                  solution: _mfc.solution,
                  challenge: _mfc.challenge
                });
              }
            }
          }
        });
      }
    };
  }]).controller('SmartIdeDialogCtrl', ['$scope', '$modalInstance', 'mfc', 'userFeedback', function SmartIdeDialogCtrl($scope, $modalInstance, mfc, userFeedback) {
    var ctrl = this;

    ctrl.status = mfc.workspaceStatusAction;

    ctrl.existingUserFeedback = userFeedback;
    ctrl.solution = mfc.solution;
    ctrl.challenge = mfc.challenge;

    ctrl.restartSmartIde = function () {
      return mfc.restartSmartIde();
    };

    Object.defineProperties(ctrl, {
      smartIdeEnabled: {
        get: function get() {
          return !!(mfc.workspace && mfc.workspace.enabled);
        },
        set: function set(val) {
          mfc.toggleSmartIde(val);
        }
      },
      disabled: {
        get: function get() {
          return mfc.workspace && mfc.workspace.status === WORKSPACE_STATUS.DESTROYING;
        }
      }
    });
  }]);

  // stores user preference on smart IDE enabled in session, needs challenge ID appended
  var SMART_IDE_DISABLED_PREFERENCE_KEY = 'QualifiedSmartIdeDisabled-';

  // Shown when SmartIDE gives up connecting. It leads with the fact that the
  // assessment itself is unaffected, because the reported failure mode was a
  // candidate who believed the whole editor had frozen and stopped working.
  var SMART_IDE_CONNECTION_FAILED_MESSAGE = 'SmartIDE could not connect, so code completion and inspection are ' + 'unavailable. Your code and the rest of the challenge are unaffected — you ' + 'can keep working, or restart SmartIDE to try again.';

  var SMART_IDE_RESTART_LINK_TITLE = 'Restart SmartIDE';

  var multiFileChallengeServiceSmartIdeModule = angular.module('Q.services.multi-file-challenge-service-smartide', [codemirrorWorkspaceServiceModule, storageServiceModule, smartIdeDialogComponentModule, trackerServiceModule, appAlertComponentModule]).factory('multiFileChallengeServiceSmartIde', ['$timeout', 'multiFileChallengeServiceDND', 'codemirrorWorkspaceService', 'smartIdeDialog', '$q', '$sessionStorage', 'tracker', 'appAlert', function ($timeout, multiFileChallengeServiceDND, codemirrorWorkspaceService, smartIdeDialog, $q, $sessionStorage, tracker, appAlert) {
    /**
     * Sets up the SmartIDE workspace.
     *
     * @param {*} svc - MultiFileChallengeService
     * @param {*} opts
     * @param {boolean?} opts.editingChallenge - If true, override the challenge's SmartIDE enabled flag
     * @param {Function} workspaceUpdater - Callback whenever workspace is changed
     */
    function setupSmartIde(svc, opts, workspaceUpdater) {
      var workspace = void 0;
      var smartIdeEnabled = void 0;
      var smartIdeDisabledKey = SMART_IDE_DISABLED_PREFERENCE_KEY + svc.challenge.id + '-' + (svc.solution ? svc.solution.id || 'preview' : '');

      /**
       * Re-lays out the file tabs, on the next digest so the workspace change
       * has landed first.
       *
       * @private
       */
      var _refreshFileLayout = function _refreshFileLayout() {
        if (!svc.fileLayout) return;
        $timeout(function () {
          if (svc.fileLayout) {
            svc.fileLayout.refresh();
          }
        });
      };

      /**
       * Creates a workspace or dummy workspace if it's enabled.
       *
       * @param {boolean?} enabled - Allows the user to disable SmartIDE if they don't want it
       * @returns {{}} Workspace Service
       * @private
       */
      var _createWorkspace = function _createWorkspace(enabled) {
        var workspace = codemirrorWorkspaceService.createIfEnabled({
          enabled: smartIdeEnabled && enabled,
          solution: svc.solution,
          challenge: svc.challenge,
          initialFilesGetter: function initialFilesGetter() {
            return svc.allFiles;
          }
        });
        var track = function track(eventName) {
          var solution = svc.solution || {};
          var challenge = svc.challenge || {};
          var assessment = solution.assessment || {};
          tracker.trackEvent('SmartIDE ' + eventName, {
            solutionId: solution.id,
            challengeId: challenge.id,
            assessmentId: assessment.id
          });
        };
        workspace.on(WORKSPACE_STATUS.CONNECTED, function () {
          track('Started');
          if (svc.solution) {
            svc.solution.usedSmartIde = true;
          }
        });
        workspace.on(WORKSPACE_STATUS.CONNECTION_FAILED, function () {
          track('Failed');
          // The lightbulb tooltip alone is not enough — a candidate can lose
          // an entire timed assessment without ever hovering it.
          appAlert.error(SMART_IDE_CONNECTION_FAILED_MESSAGE, {
            linkTitle: SMART_IDE_RESTART_LINK_TITLE,
            linkAction: function linkAction() {
              return svc.restartSmartIde();
            }
          });
        });
        workspace.on(WORKSPACE_STATUS.CONNECTION_ERROR, function () {
          return track('Error');
        });

        workspaceUpdater(workspace);
        return workspace;
      };

      // toolbar action for workspaces
      svc.workspaceStatusAction = {
        id: 'workspace-status',
        get icon() {
          var baseIcon = 'lightbulb';
          var _workspace = workspace,
              status = _workspace.status;

          if (status === WORKSPACE_STATUS.CONNECTED) {
            baseIcon += '-on';
          } else if ([WORKSPACE_STATUS.DISABLED, WORKSPACE_STATUS.DESTROYING, WORKSPACE_STATUS.CONNECTION_FAILED].includes(status)) {
            baseIcon += '-disabled';
          }
          return baseIcon + ' workspace-status-' + status;
        },
        get toolbarTooltip() {
          return WORKSPACE_STATUS_LABELS[workspace.status];
        },
        onToolbarClick: function onToolbarClick() {
          smartIdeDialog.open(svc);
        },
        get hidden() {
          return !smartIdeEnabled || !workspace.status;
        }
      };

      /**
       * Allow the user to turn SmartIDE on or off. Saves the user's preference.
       * @param {boolean?} enabled - If provided, sets SmartIDE to that state. Otherwise, toggles the current state.
       * @returns {Promise} When resolved, SmartIDE will either be destroyed, or have begun setting up.
       */
      svc.toggleSmartIde = function () {
        var enabled = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : !workspace.enabled;

        // we store whether the user wants it disabled;
        if (enabled) {
          delete $sessionStorage[smartIdeDisabledKey];
        } else {
          $sessionStorage[smartIdeDisabledKey] = true;
        }

        var promise = $q.resolve();

        if (!enabled && workspace.enabled) {
          // wait until we destroy the service
          promise = workspace.destroy().then(function () {
            // then set up the dummy service
            svc.workspace = workspace = _createWorkspace(false);
          });
        } else if (enabled && !workspace.enabled) {
          // immediately set up the full service (if available)
          svc.workspace = workspace = _createWorkspace(true);
        }
        return promise.finally(_refreshFileLayout);
      };

      /**
       * Tears SmartIDE down and brings it back up. Shared by the SmartIDE
       * dialog and the connection-failure alert so there is one restart path.
       *
       * @returns {Promise} Resolves once SmartIDE has begun setting up again
       */
      svc.restartSmartIde = function () {
        return svc.toggleSmartIde(false).then(function () {
          return svc.toggleSmartIde(true);
        });
      };

      /**
       * Everything that decides which language server pod a workspace needs.
       * Two calls with the same signature would build an identical workspace.
       *
       * @returns {string} Comparable signature of the current configuration
       * @private
       */
      var _workspaceSignature = function _workspaceSignature() {
        var runConfig = svc.challenge && svc.challenge.runConfig || {};
        // Stringified rather than joined so the language server and service
        // lists compare as lists, and so an absent value stays distinct from
        // an empty one.
        return angular.toJson([svc.challenge && svc.challenge.id, svc.solution && svc.solution.id, runConfig.language, runConfig.languageVersion, runConfig.preset, runConfig.languageServers, runConfig.services, svc.challenge && svc.challenge.smartIdeEnabled(opts.editingChallenge), !!$sessionStorage[smartIdeDisabledKey]]);
      };

      /**
       * The file contents the pod was last seeded with, so a re-seed only
       * happens when a file actually moved.
       *
       * @returns {string} Comparable digest of every file's path and contents
       * @private
       */
      var _filesDigest = function _filesDigest() {
        return angular.toJson(svc.allFiles.map(function (file) {
          return [file.path, file.contents];
        }));
      };

      // Statuses a workspace cannot come back from, so reconfiguring has to
      // build a new one rather than hand this one back to the editors.
      var DEAD_STATUSES = [WORKSPACE_STATUS.CONNECTION_FAILED, WORKSPACE_STATUS.DESTROYING];
      var _workspaceIsLive = function _workspaceIsLive() {
        return !!svc.workspace && svc.workspace.enabled && !DEAD_STATUSES.includes(svc.workspace.status);
      };

      var lastWorkspaceSignature = null;
      var lastFilesDigest = null;

      /**
       * Configures and enables or disable SmartIDE, should be called whenever the language changes
       */
      svc.reconfigureSmartIde = function () {
        // Callers fire this on any solution re-init, not just a language
        // change — including a change-language dialog the candidate opened
        // and then dismissed. Rebuilding there threw away a working pod and
        // cost ~20s of cold start for no change at all (QUAL-2250), so keep
        // the workspace we have when nothing it depends on has moved.
        var signature = _workspaceSignature();

        if (signature === lastWorkspaceSignature && _workspaceIsLive()) {
          svc.updateFilePaths();
          // A code reset reaches us by this same path, and replaces the
          // solution's files wholesale without telling the pod. Re-seeding is
          // one request against a warm pod rather than a rebuild — but only
          // when something actually moved, so a dismissed dialog costs
          // nothing and never re-triggers diagnostics under the cursor.
          var filesDigest = _filesDigest();
          if (filesDigest !== lastFilesDigest) {
            lastFilesDigest = filesDigest;
            svc.workspace.initializeFiles(svc.allFiles);
          }
          _refreshFileLayout();
          return;
        }
        lastWorkspaceSignature = signature;

        if (svc.workspace) {
          svc.workspace.destroy();
          svc.workspace = null;
        }

        svc.updateFilePaths();

        smartIdeEnabled = svc.challenge.smartIdeEnabled(opts.editingChallenge);

        workspace = _createWorkspace(!$sessionStorage[smartIdeDisabledKey]);
        // A new workspace seeds the pod from `initialFilesGetter` itself, so
        // these files are already on it.
        lastFilesDigest = _filesDigest();

        if (smartIdeEnabled) {
          svc.workspace = workspace;
        }
        _refreshFileLayout();
      };

      // perform initial SmartIDE configuration
      svc.reconfigureSmartIde();
    }

    return {
      setupSmartIde: setupSmartIde
    };
  }]);

  var virtualFileDownloadServiceModule = angular.module('Q.services.virtual-file-download-service', []).factory('virtualFileDownloadService', ['$window', '$document', '$timeout', function ($window, $document, $timeout) {
    function download(filename, contents) {
      var type = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'text/plain';

      // ignore leading path in file name
      filename = splitPath(filename).filename;

      if ($window.navigator && $window.navigator.msSaveOrOpenBlob) {
        // Manage IE11+ & Edge
        var blob = new Blob([contents], { type: type });
        $window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        var doc = $document[0];
        var fakeLink = doc.createElement('a');
        doc.body.appendChild(fakeLink);

        fakeLink.setAttribute('href', 'data:application/octet-stream;charset=utf-8,' + encodeURIComponent(contents));
        fakeLink.setAttribute('download', filename);
        fakeLink.click();
        $timeout(function () {
          // clean up 10s later
          doc.body.removeChild(fakeLink);
        }, 10000);
      }
    }

    return {
      download: download
    };
  }]);

  // limit all-at-once file importing (drag-and-drop)
  var MAX_IMPORT_FILES = 50;

  // limit total number of files
  var MAX_TOTAL_FILES = 100;

  // files should be no more than 1 MB
  var MAX_FILE_SIZE = 1048576;

  // AI session logs are the one thing a candidate drops that legitimately exceeds
  // 1 MB: the raw envelope carries the whole conversation plus every pasted image
  // inlined as base64, and a single screenshot is ~270-670 KB once encoded.
  //
  // The ceiling is not arbitrary. Solution files are embedded in a single Mongo
  // document and BSON caps a document at 16 MB, so an oversized drop does not just
  // fail on its own -- it fails the whole solution's save. Revisions compound it:
  // each stores a deflated copy of the file's contents, and base64 image data
  // barely compresses. 10 MB is generous for a real session and still leaves the
  // document clear of the ceiling.
  //
  // Deliberately narrow: every other dropped file stays at MAX_FILE_SIZE.
  var SESSION_LOG_MAX_FILE_SIZE = 10485760;

  // Matches the raw session-log envelopes written by the session-log extractors,
  // e.g. claude_session_log_raw_<session-id>.json. Every harness prefixes its raw
  // file, so this never has to guess which one produced it.
  var SESSION_LOG_FILENAME = /^(claude|codex|copilot)_session_log_raw(_.*)?\.json$/i;

  // force to false and smart code compressors can remove the resulting 'dead code':
  var DEBUG = true;

  /**
   * Compile the given `.gitignore` content (not filename!)
   * and return an object with `accepts`, `denies` and `inspects` methods.
   * These methods each accepts a single filename or path and determines whether
   * they are acceptable or unacceptable according to the `.gitignore` definition.
   *
   * @param  {String} content The `.gitignore` content to compile.
   * @return {Object}         The helper object with methods that operate on the compiled content.
   */
  function compile(content) {
    var parsed = parse(content),
        positives = parsed[0],
        negatives = parsed[1];

    return {
      /// Helper (which can be overridden by userland code) invoked when
      /// any `accepts()`, `denies()` or `inspects()` fail to help
      /// the developer analyze what is going on inside: some gitignore spec
      /// bits are non-intuitive / non-trivial, after all.
      diagnose: function diagnose(query) {
        if (DEBUG) {
          console.log(query.query + ':', query);
        }
      },
      /// Return TRUE when the given `input` path PASSES the gitignore filters,
      /// i.e. when the given input path is DENIED.
      ///
      /// Notes:
      /// - you MUST postfix a input directory with '/' to ensure the gitignore
      ///   rules can be applied conform spec.
      /// - you MAY prefix a input directory with '/' when that directory is
      ///   'rooted' in the same directory as the compiled .gitignore spec file.
      accepts: function accepts(input, expected) {
        if (input[0] === '/') input = input.slice(1);
        input = '/' + input;

        var acceptRe = negatives[0];
        var acceptTest = acceptRe.test(input);
        var denyRe = positives[0];
        var denyTest = denyRe.test(input);
        var returnVal = acceptTest || !denyTest;
        // See the test/fixtures/gitignore.manpage.txt near line 680 (grep for "uber-nasty"):
        // to resolve chained rules which reject, then accept, we need to establish
        // the precedence of both accept and reject parts of the compiled gitignore by
        // comparing match lengths.
        // Since the generated consolidated regexes are lazy, we must loop through all lines' regexes instead:
        var acceptMatch = void 0,
            denyMatch = void 0;
        if (acceptTest && denyTest) {
          var _iteratorNormalCompletion = true;
          var _didIteratorError = false;
          var _iteratorError = undefined;

          try {
            for (var _iterator = negatives[1][Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
              var re = _step.value;

              var m = re.exec(input);
              if (m) {
                if (!acceptMatch) {
                  acceptMatch = m;
                } else if (acceptMatch[0].length < m[0].length) {
                  acceptMatch = m;
                }
              }
            }
          } catch (err) {
            _didIteratorError = true;
            _iteratorError = err;
          } finally {
            try {
              if (!_iteratorNormalCompletion && _iterator.return) {
                _iterator.return();
              }
            } finally {
              if (_didIteratorError) {
                throw _iteratorError;
              }
            }
          }

          var _iteratorNormalCompletion2 = true;
          var _didIteratorError2 = false;
          var _iteratorError2 = undefined;

          try {
            for (var _iterator2 = positives[1][Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
              var _re = _step2.value;

              var m = _re.exec(input);
              if (m) {
                if (!denyMatch) {
                  denyMatch = m;
                } else if (denyMatch[0].length < m[0].length) {
                  denyMatch = m;
                }
              }
            }
            // acceptMatch = acceptRe.exec(input);
            // denyMatch = denyRe.exec(input);
          } catch (err) {
            _didIteratorError2 = true;
            _iteratorError2 = err;
          } finally {
            try {
              if (!_iteratorNormalCompletion2 && _iterator2.return) {
                _iterator2.return();
              }
            } finally {
              if (_didIteratorError2) {
                throw _iteratorError2;
              }
            }
          }

          returnVal = acceptMatch[0].length >= denyMatch[0].length;
        }

        if (expected != null && expected !== returnVal) {
          this.diagnose({ query: 'accepts', input: input, expected: expected, acceptRe: acceptRe, acceptTest: acceptTest, acceptMatch: acceptMatch, denyRe: denyRe, denyTest: denyTest, denyMatch: denyMatch, combine: '(Accept || !Deny)', returnVal: returnVal });
        }
        return returnVal;
      },
      /// Return TRUE when the given `input` path FAILS the gitignore filters,
      /// i.e. when the given input path is ACCEPTED.
      ///
      /// Notes:
      /// - you MUST postfix a input directory with '/' to ensure the gitignore
      ///   rules can be applied conform spec.
      /// - you MAY prefix a input directory with '/' when that directory is
      ///   'rooted' in the same directory as the compiled .gitignore spec file.
      denies: function denies(input, expected) {
        if (input[0] === '/') input = input.slice(1);
        input = '/' + input;

        var acceptRe = negatives[0];
        var acceptTest = acceptRe.test(input);
        var denyRe = positives[0];
        var denyTest = denyRe.test(input);
        // boolean logic:
        //
        // denies = !accepts =>
        // = !(Accept || !Deny) =>
        // = (!Accept && !!Deny) =>
        // = (!Accept && Deny)
        var returnVal = !acceptTest && denyTest;
        // See the test/fixtures/gitignore.manpage.txt near line 680 (grep for "uber-nasty"):
        // to resolve chained rules which reject, then accept, we need to establish
        // the precedence of both accept and reject parts of the compiled gitignore by
        // comparing match lengths.
        // Since the generated regexes are all set up to be GREEDY, we can use the
        // consolidated regex for this, instead of having to loop through all lines' regexes:
        var acceptMatch = void 0,
            denyMatch = void 0;
        if (acceptTest && denyTest) {
          var _iteratorNormalCompletion3 = true;
          var _didIteratorError3 = false;
          var _iteratorError3 = undefined;

          try {
            for (var _iterator3 = negatives[1][Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
              var re = _step3.value;

              var m = re.exec(input);
              if (m) {
                if (!acceptMatch) {
                  acceptMatch = m;
                } else if (acceptMatch[0].length < m[0].length) {
                  acceptMatch = m;
                }
              }
            }
          } catch (err) {
            _didIteratorError3 = true;
            _iteratorError3 = err;
          } finally {
            try {
              if (!_iteratorNormalCompletion3 && _iterator3.return) {
                _iterator3.return();
              }
            } finally {
              if (_didIteratorError3) {
                throw _iteratorError3;
              }
            }
          }

          var _iteratorNormalCompletion4 = true;
          var _didIteratorError4 = false;
          var _iteratorError4 = undefined;

          try {
            for (var _iterator4 = positives[1][Symbol.iterator](), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
              var _re2 = _step4.value;

              var m = _re2.exec(input);
              if (m) {
                if (!denyMatch) {
                  denyMatch = m;
                } else if (denyMatch[0].length < m[0].length) {
                  denyMatch = m;
                }
              }
            }
            // acceptMatch = acceptRe.exec(input);
            // denyMatch = denyRe.exec(input);

            // boolean logic: !(A>=B) ==> A<B
          } catch (err) {
            _didIteratorError4 = true;
            _iteratorError4 = err;
          } finally {
            try {
              if (!_iteratorNormalCompletion4 && _iterator4.return) {
                _iterator4.return();
              }
            } finally {
              if (_didIteratorError4) {
                throw _iteratorError4;
              }
            }
          }

          returnVal = acceptMatch[0].length < denyMatch[0].length;
        }

        if (expected != null && expected !== returnVal) {
          this.diagnose({ query: 'denies', input: input, expected: expected, acceptRe: acceptRe, acceptTest: acceptTest, acceptMatch: acceptMatch, denyRe: denyRe, denyTest: denyTest, denyMatch: denyMatch, combine: '(!Accept && Deny)', returnVal: returnVal });
        }
        return returnVal;
      },
      /// Return TRUE when the given `input` path is inspected by any .gitignore
      /// filter line.
      ///
      /// You can use this method to help construct the decision path when you
      /// process nested .gitignore files: .gitignore filters in subdirectories
      /// MAY override parent .gitignore filters only when there's actually ANY
      /// filter in the child .gitignore after all.
      ///
      /// Notes:
      /// - you MUST postfix a input directory with '/' to ensure the gitignore
      ///   rules can be applied conform spec.
      /// - you MAY prefix a input directory with '/' when that directory is
      ///   'rooted' in the same directory as the compiled .gitignore spec file.
      inspects: function inspects(input, expected) {
        if (input[0] === '/') input = input.slice(1);
        input = '/' + input;

        var acceptRe = negatives[0];
        var acceptTest = acceptRe.test(input);
        var denyRe = positives[0];
        var denyTest = denyRe.test(input);
        // when any filter 'touches' the input path, it must match,
        // no matter whether it's a deny or accept filter line:
        var returnVal = acceptTest || denyTest;

        if (expected != null && expected !== returnVal) {
          this.diagnose({ query: 'inspects', input: input, expected: expected, acceptRe: acceptRe, acceptTest: acceptTest, denyRe: denyRe, denyTest: denyTest, combine: '(Accept || Deny)', returnVal: returnVal });
        }
        return returnVal;
      }
    };
  }

  /**
   * Parse the given `.gitignore` content and return an array
   * containing positives and negatives.
   * Each of these in turn contains a regexp which will be
   * applied to the 'rooted' paths to test for *deny* or *accept*
   * respectively.
   *
   * @param  {String} content  The content to parse,
   * @return {Array[]}         The parsed positive and negatives definitions.
   */
  function parse(content) {
    return content.split('\n').map(function (line) {
      line = line.trim();
      return line;
    }).filter(function (line) {
      return line && line[0] !== '#';
    }).reduce(function (lists, line) {
      var isNegative = line[0] === '!';
      if (isNegative) {
        line = line.slice(1);
      }
      if (isNegative) {
        lists[1].push(line);
      } else {
        lists[0].push(line);
      }
      return lists;
    }, [[], []]).map(function (list) {
      list = list.sort().map(prepareRegexPattern);

      // don't need submatches, hence we use should use non-capturing `(?:...)` grouping regexes:
      // those are generally faster than their submatch-capturing brothers:
      if (list.length > 0) {
        return [new RegExp('(?:' + list.join(')|(?:') + ')'), list.map(function (re) {
          return new RegExp(re);
        })];
      }
      // this regex *won't match a thing*:
      return [new RegExp('$^'), []];
    });
  }

  function prepareRegexPattern(pattern) {
    // https://git-scm.com/docs/gitignore#_pattern_format
    //
    // * ...
    //
    // * If there is a separator at the beginning or middle (or both) of the pattern,
    //   then the pattern is relative to the directory level of the particular
    //   .gitignore file itself.
    //   Otherwise the pattern may also match at any level below the .gitignore level.
    //
    // * ...
    //
    // * For example, a pattern `doc/frotz/` matches `doc/frotz` directory, but
    //   not `a/doc/frotz` directory; however `frotz/` matches `frotz` and `a/frotz`
    //   that is a directory (all paths are relative from the .gitignore file).
    //
    var input = pattern;
    var re = '';
    var rooted = false;
    var directory = false;
    if (pattern[0] === '/') {
      rooted = true;
      pattern = pattern.slice(1);
    }
    if (pattern[pattern.length - 1] === '/') {
      directory = true;
      pattern = pattern.slice(0, pattern.length - 1);
    }
    // keep character ranges intact:
    var rangeRe = /^((?:[^\[\\]|(?:\\.))*)\[((?:[^\]\\]|(?:\\.))*)\]/;
    // ^ could have used the 'y' sticky flag, but there's some trouble with infine loops inside
    //   the matcher below then...
    var match = void 0;

    while ((match = rangeRe.exec(pattern)) !== null) {
      if (match[1].includes('/')) {
        rooted = true;
        // ^ cf. man page:
        //
        //   If there is a separator at the beginning or middle (or both)
        //   of the pattern, then the pattern is relative to the directory
        //   level of the particular .gitignore file itself. Otherwise
        //   the pattern may also match at any level below the .gitignore level.
      }
      re += transpileRegexPart(match[1]);
      re += '[' + match[2] + ']';

      pattern = pattern.slice(match[0].length);
    }
    if (pattern) {
      if (pattern.includes('/')) {
        rooted = true;
        // ^ cf. man page:
        //
        //   If there is a separator at the beginning or middle (or both)
        //   of the pattern, then the pattern is relative to the directory
        //   level of the particular .gitignore file itself. Otherwise
        //   the pattern may also match at any level below the .gitignore level.
      }
      re += transpileRegexPart(pattern);
    }

    // prep regexes assuming we'll always prefix the check string with a '/':
    if (rooted) {
      re = '^\\/' + re;
    } else {
      re = '\\/' + re;
    }
    // cf spec:
    //
    //   If there is a separator at the end of the pattern then the pattern
    //   will only match directories, otherwise the pattern can match
    //   **both files and directories**.                   (emphasis mine)
    if (directory) {
      // match the directory itself and anything within:
      re += '\\/';
    } else {
      // match the file itself, or, when it is a directory, match the directory and anything within:
      re += '(?:$|\\/)';
    }

    // regex validation diagnostics: better to check if the part is valid
    // then to discover it's gone haywire in the big conglomerate at the end.
    if (DEBUG) {
      try {
      } catch (ex) {
        console.log('failed regex:', { input: input, re: re, ex: ex });
      }
    }
    return re;

    function transpileRegexPart(re) {
      return re
      // unescape for these will be escaped again in the subsequent `.replace(...)`,
      // whether they were escaped before or not:
      .replace(/\\(.)/g, '$1')
      // escape special regex characters:
      .replace(/[\-\[\]\{\}\(\)\+\.\\\^\$\|]/g, '\\$&').replace(/\?/g, '[^/]').replace(/\/\*\*\//g, '(?:/|(?:/.+/))').replace(/^\*\*\//g, '(?:|(?:.+/))').replace(/\/\*\*$/g, function () {
        directory = true; // `a/**` should match `a/`, `a/b/` and `a/b`, the latter by implication of matching directory `a/`
        return '(?:|(?:/.+))'; // `a/**` also accepts `a/` itself
      }).replace(/\*\*/g, '.*')
      // `a/*` should match `a/b` and `a/b/` but NOT `a` or `a/`
      // meanwhile, `a/*/` should match `a/b/` and `a/b/c` but NOT `a` or `a/` or `a/b`
      .replace(/\/\*(\/|$)/g, '/[^/]+$1').replace(/\*/g, '[^/]*').replace(/\//g, '\\/');
    }
  }

  var IMAGE_MIME_TYPE_REGEX = /^image\/.*/i;
  var IMAGE_FILE_EXTENSION_REGEX = /\.(jpg|jpeg|gif|png|svg|bmp|eps|psd|tiff|ico|jxr|wdp)$/i;
  var IMAGE_DATA_URL_REGEX = /^data:image\//i;

  function isImageFile(file) {
    if (!file) return false;
    return IMAGE_MIME_TYPE_REGEX.test(file.type || '') || IMAGE_FILE_EXTENSION_REGEX.test(file.name || file.path || '');
  }

  function isImageDataUrl(contents) {
    return IMAGE_DATA_URL_REGEX.test(contents || '');
  }

  var TOO_MANY_FILES = {};

  // exclude some common directories from ever being supported on drag-and-drop uploads
  var ALWAYS_EXCLUDE = /(^|\/)\.(git|hg|svn|idea|vscode)(\/|$)/;

  // Raw AI session logs are the one file type allowed past the 1 MB limit; see
  // SESSION_LOG_MAX_FILE_SIZE for why the exemption is this narrow. Matched on the
  // basename so a nested drop is treated the same as a top-level one.
  function maxSizeFor(file) {
    var name = (file.relativePath || file.name || '').split('/').pop();
    return SESSION_LOG_FILENAME.test(name) ? SESSION_LOG_MAX_FILE_SIZE : MAX_FILE_SIZE;
  }

  function formatSizeLimit(bytes) {
    return Math.round(bytes / 1048576) + ' MB';
  }

  var multiFileChallengeServiceDragAnDropModule = angular.module('Q.services.multi-file-challenge-service-dnd', [appAlertComponentModule, multiFilePathDialogComponentModule, multiFileOverwriteDialogComponentModule]).factory('multiFileChallengeServiceDND', ['appAlert', '$window', '$document', '$timeout', '$q', '$log', 'multiFilePathDialog', 'multiFileOverwriteDialog', function (appAlert, $window, $document, $timeout, $q, $log, multiFilePathDialog, multiFileOverwriteDialog) {
    var dragFileOverEl = void 0;

    function getFileContents(file) {
      if (!$window.FileReader) return $q.reject('No File Reader Model');
      return $q(function (resolve, reject) {
        var reader = new FileReader();
        reader.onload = function (evt) {
          resolve(evt.target.result);
        };
        reader.onerror = reader.onabort = reject;
        if (isImageFile(file)) {
          reader.readAsDataURL(file);
        } else {
          reader.readAsText(file);
        }
      });
    }

    function setupDragAndDrop(svc, _createFile) {
      var dragOverShowing = false;
      var dragExitTimer = void 0;

      /**
       * Sets up the drag over event, along with a nice visual drop guide, if this is a file event
       * @param event
       */
      function handleDragOver(event) {
        if (!isFileEvent(event) || svc.expired) return;
        stopEvent(event);
        if (!dragFileOverEl) {
          dragFileOverEl = angular.element('<div class="multi-file-challenge-drag-over"><span>Drop files to import</span></div>');
          $document.find('body').append(dragFileOverEl);
        }
        $timeout.cancel(dragExitTimer);
        if (!dragOverShowing) {
          dragOverShowing = true;
          dragFileOverEl.addClass('active-setup');
          $timeout(function () {
            dragFileOverEl.addClass('active');
          }, 1, false);
        }
      }

      /**
       * Clears the visual drop guide if this is a file event
       * @param event
       */
      function handleDragExit(event) {
        if (!isFileEvent(event)) return;
        stopEvent(event);
        if (dragFileOverEl) {
          dragExitTimer = $timeout(function () {
            dragFileOverEl.removeClass('active');
            dragExitTimer = $timeout(function () {
              dragFileOverEl.removeClass('active-setup');
              dragOverShowing = false;
            }, 300, false);
          }, 250, false);
        }
      }

      var safeMimeTypes = /^(text\/|application\/(x-)?(ecmascript|javascript|json|typescript|xhtml|xml|yaml|(script\.)?.{0,2}sh$)|image\/(svg|png|jpeg))/;

      /**
       * Allows dropping files & directories from the file system
       * @param event
       */
      function handleDrop(event) {
        if (!isFileEvent(event) || svc.expired) return;
        // always stop the event here
        handleDragExit(event);

        var dt = event.dataTransfer;
        if (dt) {
          if (dt.items) {
            var allFiles = Array.from(dt.items).filter(function (item) {
              return item.webkitGetAsEntry || item.kind === 'file';
            }).map(function (item) {
              var webkitEntry = isFunction(item.getEntry) ? item.getEntry() : isFunction(item.webkitGetAsEntry) && item.webkitGetAsEntry();
              if (webkitEntry && webkitEntry.filesystem) {
                return walkFileSystem(webkitEntry.filesystem.root).catch(function (err) {
                  if (err === TOO_MANY_FILES) {
                    return [TOO_MANY_FILES];
                  }
                  $log.error('Unable to read directories', err);
                  return [item.getAsFile()];
                });
              } else {
                return [item.getAsFile()];
              }
            });
            $q.all(allFiles).then(function (filesAndDirs) {
              var _ref;

              importFiles((_ref = []).concat.apply(_ref, toConsumableArray(filesAndDirs)));
            });
          } else if (dt.files) {
            importFiles(Array.from(dt.files));
          }
        }
      }

      /**
       * Walks a file system directory to get a flat list of all nested files with relative paths
       * NOTE: this may stop after 100 files per folder due to browser limitations, but we don't really
       * care, since we don't _really_ want to support that many files
       * @param directory {File}
       * @returns {Promise<Array<File>>}
       */
      function walkFileSystem(directory) {
        var pending = 0;
        var files = [];

        return $q(function (resolve, reject) {
          function _readDirectory(directory) {
            pending++;

            var reader = directory.createReader();
            var relativePath = directory.fullPath.replace(/^\//, '').replace(/(.+?)\/?$/, '$1/');
            reader.readEntries(function (entries) {
              pending--;
              if (files.length + entries.length > MAX_IMPORT_FILES) {
                return reject(TOO_MANY_FILES);
              }
              angular.forEach(entries, function (entry) {
                if (entry.isFile) {
                  pending++;
                  entry.file(function (file) {
                    file.relativePath = relativePath + file.name;
                    files.push(file);
                    if (--pending === 0) {
                      resolve(files);
                    }
                  }, reject);
                } else {
                  _readDirectory(entry);
                }
              });

              if (pending === 0) {
                resolve(files);
              }
            }, reject);
          }

          _readDirectory(directory);
        });
      }

      /**
       * Handles the final import after processing the drop
       * @param files {Array<File>}
       */
      function importFiles(files) {
        if (!files.length || svc.expired) return;

        if (files.length > MAX_IMPORT_FILES || files.includes(TOO_MANY_FILES)) {
          appAlert.error('Error attempting to import too many files, please limit importing to no more than ' + MAX_IMPORT_FILES + ' files');
          return;
        }

        // TODO: expose the following for use in other scenarios
        var gitIgnore = svc.files.find(function (f) {
          return f.path === '.gitignore';
        });
        var gitIgnoreMatcher = gitIgnore && compile(gitIgnore.contents);
        var ignoredFileChecker = function ignoredFileChecker(filePath) {
          return ALWAYS_EXCLUDE.test(filePath) || gitIgnoreMatcher && gitIgnoreMatcher.denies(filePath);
        };
        var foundPath = {};
        files = files
        // exclude unsafe files (for now)
        .filter(function (f) {
          var fPath = f.relativePath || f.name;
          if (!(languages.validExt(f.name) || safeMimeTypes.test(f.type))) {
            $log.info('Ignoring dropped file with unknown type: ' + f.name + ' (' + f.type + ')');
            return false;
          }
          if (ignoredFileChecker(fPath)) {
            $log.info('Ignoring dropped file due to ignore rules: ' + fPath);
            return false;
          }
          // filter out duplicates (weird scenarios can cause it)
          return foundPath[fPath] ? false : foundPath[fPath] = true;
        });

        var dialogResult = void 0;
        if (files.length > 1) {
          var examples = files.slice(0, files.length > 6 ? 5 : 6).map(function (f) {
            return f.relativePath || f.name;
          });
          dialogResult = multiFilePathDialog.open({
            title: 'Importing ' + files.length + ' files',
            prompt: 'Where should the imported files be added?',
            placeholder: 'new/path/ (blank is OK)',
            allowRoot: true,
            directory: true,
            showReferenceChoice: !!svc.referenceFiles,
            examples: examples,
            moreFiles: files.length - examples.length
          });
        } else if (files.length) {
          var f = files[0];
          var fPath = f.relativePath || f.name;
          dialogResult = multiFilePathDialog.open({
            title: 'Import ' + fPath,
            prompt: 'Path for imported file',
            path: fPath,
            showReferenceChoice: !!svc.referenceFiles
          });
        } else {
          appAlert.warning('Unable to determine that file type by mime or extension.');
        }

        if (dialogResult) {
          var basePath = '';
          var importToReference = void 0;
          var importToFiles = void 0;
          var extractedReferenceFiles = void 0;
          dialogResult.then(function (_ref2) {
            var path = _ref2.path,
                isReference = _ref2.isReference,
                extractReference = _ref2.extractReference;

            importToReference = isReference;
            importToFiles = isReference ? svc.referenceFiles : svc.files;
            if (files.length === 1) {
              // overwrite the single file's path
              files[0].relativePath = path;
            } else {
              // set up to overwrite all files
              basePath = path && path + '/';

              if (!isReference && extractReference) {
                extractedReferenceFiles = [];
                files = files.filter(function (f) {
                  var fPath = f.relativePath || f.name;
                  if (/\.ref\./.test(fPath)) {
                    extractedReferenceFiles.push(f);
                    f.relativePath = fPath.replace(/\.ref\./, '.');
                    return false;
                  }
                  return true;
                });
              }
            }

            // check for conflicts (this allows us to replace a file on single drag-and-drop)
            return multiFileOverwriteDialog.open({
              paths: files.map(function (f) {
                return basePath + (f.relativePath || f.name);
              }),
              files: importToFiles,
              referencePaths: extractedReferenceFiles && extractedReferenceFiles.map(function (f) {
                return basePath + (f.relativePath || f.name);
              }),
              referenceFiles: svc.referenceFiles
            });
          }).then(function (_ref3) {
            var includedPaths = _ref3.paths,
                includedRefPaths = _ref3.referencePaths;

            var importPromises = [];
            var filesImported = 0;
            var fileToOpen = false;

            var stopImporting = false;

            function importFiles(filesToImport, allowedPaths, filesArray, reference) {
              if (!allowedPaths.length) return;

              filesToImport.forEach(function (f) {
                var fPath = basePath + (f.relativePath || f.name);
                if (stopImporting || !allowedPaths.includes(fPath)) return;

                var maxSize = maxSizeFor(f);
                if (f.size > maxSize) {
                  appAlert.warning('File "' + (f.relativePath || f.name) + '" is too large, please limit files to ' + formatSizeLimit(maxSize));
                  stopImporting = true;
                  return;
                }
                var importPromise = getFileContents(f).catch(function (err) {
                  if (stopImporting) return;
                  $log.error('Error loading file from system', err);
                  var errorMsg = err ? ' (' + err.toString() + ')' : '';
                  appAlert.warning('Unable to read contents for file "' + (f.relativePath || f.name) + '"' + errorMsg);
                  // create file without contents
                  return '';
                }).then(function (contents) {
                  if (stopImporting) return;
                  var newFile = _createFile({ path: fPath, contents: contents }, reference, false);
                  if (newFile) {
                    fileToOpen = fileToOpen || newFile;
                  } else {
                    stopImporting = true;
                  }
                });
                importPromises.push(importPromise);
              });
            }

            importFiles(files, includedPaths, importToFiles, importToReference);
            if (extractedReferenceFiles) {
              importFiles(extractedReferenceFiles, includedRefPaths, svc.referenceFiles, true);
            }

            $q.all(importPromises).then(function () {
              if (!stopImporting) {
                if (filesImported > 1) {
                  appAlert.success('Imported ' + filesImported + ' files successfully');
                } else if (fileToOpen) {
                  svc.clickFile(fileToOpen);
                }
                svc.onFileChange({ force: true });
              }
            });
          });
        }
      }

      /**
       * Sets up a drop handler on the selected element
       * @param $scope {*?} Angular scope so we can stop listening
       * @param selector {string?} Defaults to '.app', but can be any element we want to listen on (such as a model backdrop)
       */
      svc.setupDrop = function ($scope) {
        var selector = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '.app';

        var $app = $document.querySelector(selector);
        $app.on('drop', handleDrop);
        $app.on('dragenter dragover', handleDragOver);
        $app.on('dragexit dragleave dragend', handleDragExit);
        var destroy = function destroy() {
          $app.off('drop', handleDrop);
          $app.off('dragenter dragover', handleDragOver);
          $app.off('dragexit dragleave dragend', handleDragExit);
        };
        svc._mfcListeners.push(destroy);
        if ($scope) {
          $scope.$on('$destroy', destroy);
        }
      };
    }

    return {
      setupDragAndDrop: setupDragAndDrop
    };
  }]);

  // prevent matching a leading directory _or_ any entire path
  // the (/|$) means inline slash or end-of-line
  var existingFileRegex = function existingFileRegex(path) {
    var exact = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    return new RegExp('^' + escapeRegex(cleanPath(path)) + (exact ? '$' : '(/|$)'), 'i');
  };

  var multiFileChallengeServiceFileManagementModule = angular.module('Q.services.multi-file-challenge-service-fm', [appAlertComponentModule, multiFilePathDialogComponentModule, multiFileOverwriteDialogComponentModule, multiFileChallengeServiceDragAnDropModule, multiFileChallengeServiceSmartIdeModule, codeFileModelModule, virtualFileDownloadServiceModule, dialogComponentModule]).factory('multiFileChallengeServiceFM', ['$timeout', 'appAlert', 'multiFilePathDialog', 'multiFileOverwriteDialog', 'AdvancedCodeChallengeFile', 'AdvancedCodeChallengeReferenceFile', 'AdvancedCodeSolutionFile', 'virtualFileDownloadService', 'dialogService', 'multiFileChallengeServiceDND', 'multiFileChallengeServiceSmartIde', function ($timeout, appAlert, multiFilePathDialog, multiFileOverwriteDialog, AdvancedCodeChallengeFile, AdvancedCodeChallengeReferenceFile, AdvancedCodeSolutionFile, virtualFileDownloadService, dialogService, multiFileChallengeServiceDND, multiFileChallengeServiceSmartIde) {
    var REFERENCE_PREFIX = '[Reference]/';
    var REFERENCE_PREFIX_REGEX = /^\[Reference](\/|$)/;

    function setupFileManagement(svc, opts) {
      var workspace = void 0;
      multiFileChallengeServiceSmartIde.setupSmartIde(svc, opts, function (ws) {
        workspace = ws;
      });

      var _files = function _files(reference) {
        return reference ? svc.referenceFiles || [] : svc.files;
      };
      var _alertOnTooManyFiles = function _alertOnTooManyFiles(props) {
        var reference = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : props.reference;

        if (!props.directory && _files(reference).reduce(function (s, f) {
          return s + (f.directory ? 0 : 1);
        }, 0) >= MAX_TOTAL_FILES) {
          appAlert.warning('Too many files: projects are limited to ' + MAX_TOTAL_FILES + ' files. Please delete some before continuing.');
          return true;
        }
      };

      var _checkForExistingPath = function _checkForExistingPath(path, reference) {
        var exact = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

        var pathTest = existingFileRegex(path, exact);
        // check for full path _OR_ leading
        return _files(reference).find(function (f) {
          return pathTest.test(f.path);
        });
      };
      var _checkForExistingParentDirectory = function _checkForExistingParentDirectory(path, reference) {
        var parent = parentPath(path);
        return _files(reference).find(function (f) {
          return !f.directory && parent.startsWith(f.path);
        });
      };
      var _checkForExisting = function _checkForExisting(path, reference) {
        return _checkForExistingPath(path, reference) || _checkForExistingParentDirectory(path, reference);
      };

      var _invalidCheck = function _invalidCheck(existingPath, reference, checkBoth) {
        return function (newPath, renameReference) {
          if (!angular.isString(existingPath) || newPath.toLowerCase() !== existingPath.toLowerCase()) {
            var match = // check expected path
            _checkForExisting(newPath, reference) ||
            // if requested, check the alternate path, too
            checkBoth && renameReference && _checkForExisting(newPath, !reference);
            if (match) {
              if (match.hidden) {
                return 'The path "' + newPath + '" is restricted and cannot be used.';
              }
              var type = match.reference ? 'Reference' : 'Project';
              var dir = match.directory || match.path.toLowerCase() !== newPath.toLowerCase();
              return 'There is an existing ' + type + ' ' + (dir ? 'Directory' : 'File') + ' at "' + (dir ? newPath : match.path) + '".';
            } else if (svc.solution) {
              var parent = svc.findParentFile(newPath);
              if (parent && parent.$access !== 'readwrite') {
                return 'The path "' + newPath + '" is restricted and cannot be used.';
              }
            }
          }
        };
      };

      var _warningCheck = function _warningCheck() {
        var ignoreCheck = svc.challenge.createIgnoreCheckFunction();
        return function (path, _ref) {
          var directory = _ref.directory;

          if (ignoreCheck(path)) {
            return svc.solution ? 'Note: This path is not included when assessing your solution, but you can use it in your own tests.<br>(Your edits will still be saved, and can be viewed by anyone reviewing your code.)' : 'Note: If a candidate changes ' + (directory ? 'files here, they' : 'this file, it') + ' will be ignored when assessing their solution.<br>(You will still be able to review their edits after submission.)';
          }
        };
      };

      var _renameFile = svc.renameFile = function (_ref2) {
        var file = _ref2.file,
            _ref2$path = _ref2.path,
            path = _ref2$path === undefined ? file.path : _ref2$path,
            _ref2$reference = _ref2.reference,
            reference = _ref2$reference === undefined ? false : _ref2$reference,
            newPath = _ref2.newPath,
            renameReference = _ref2.renameReference,
            _ref2$directory = _ref2.directory,
            directory = _ref2$directory === undefined ? file.directory : _ref2$directory;

        if (file && file.locked || svc.expired) return;
        if (newPath !== path) {
          if (directory) {
            var pathTest = existingFileRegex(path);
            var _renameChild = function _renameChild(f) {
              var oldPath = f.path;
              if (!f.locked && pathTest.test(oldPath)) {
                f.path = newPath + oldPath.slice(path.length);
                workspace.renameFile(f, oldPath);
              }
            };
            _files(reference).forEach(_renameChild);
            if (renameReference) {
              _files(!reference).forEach(_renameChild);
            }
          } else {
            var relFile = svc.findRelatedFile(file);
            if (renameReference && relFile) {
              relFile.path = newPath;
              workspace.renameFile(relFile, path);
            }
            file.path = newPath;
            workspace.renameFile(file, path);
          }
          // notify others
          if (svc.interact && svc.interact.canEdit) {
            svc.interact.renameFile({ oldPath: path, newPath: newPath });
          }

          return true;
        }
      };

      /**
       * Creates a file. If a file already exists with the same path, we update it's contents instead.
       *
       * @param {*} props - new file properties
       * @param {string} props.path - new file path (required)
       * @param {string} props.contents - new file contents (required)
       * @param {boolean?} reference - referenceFile or regular file
       * @param {boolean?} click - Click the file to highlight and open it
       * @param {boolean?} alertOnError - if false, silently ignore errors
       *
       * @returns {CodeFile|boolean} New file (or existing file if one already exists), or false if unable to create or update the file
       * @private
       */
      var _createFile = svc.createFile = function (props) {
        var reference = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
        var click = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
        var alertOnError = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;

        // See if we have an existing file, if so, update it
        var existingFile = svc.findFile(props.path, reference);
        if (existingFile) {
          if (!!existingFile.directory !== !!props.directory) {
            if (alertOnError) {
              appAlert.warning('Trying to create a new ' + (props.directory ? 'directory' : 'file') + ' over an existing same path: ' + props.path);
            }
            return false;
          }
          if (existingFile.disabled || existingFile.readOnly) {
            if (alertOnError) {
              appAlert.warning('Trying to modify an existing locked file or directory: ' + props.path);
            }
            return false;
          }
          if (!existingFile.directory) {
            existingFile.contents = props.contents;
          }
          if (existingFile.path !== props.path) {
            // case changed on path
            _renameFile({
              file: existingFile,
              newPath: props.path,
              reference: reference
            });
          } else {
            workspace.updateFile(existingFile);
          }
          return existingFile;
        }

        // don't allow creating too many files (slows performance, creates overly complex challenges)
        if (_alertOnTooManyFiles(props, reference)) {
          return false;
        }

        var Model = svc.solution ? AdvancedCodeSolutionFile : reference ? AdvancedCodeChallengeReferenceFile : AdvancedCodeChallengeFile;
        var newFileProps = angular.copy(props);
        delete newFileProps.id; // in case we're cloning a file
        if (svc.solution && svc.challenge) {
          var challengeFile = svc.challenge.files.find(function (cf) {
            return cf.path === newFileProps.path;
          });
          newFileProps.challengeFileId = challengeFile && challengeFile.id;
        }
        var newFile = Model.createInstance(newFileProps);
        _files(reference).push(newFile);
        workspace.addFile(newFile);
        if (click) {
          $timeout(function () {
            svc.clickFile(newFile);
          }, 50);
        }
        // notify others
        if (svc.interact && svc.interact.canEdit) {
          svc.interact.createFile({
            id: newFile.path,
            properties: newFileProps
          });
        }
        return newFile;
      };

      /**
       * Returns a file matched by path or classic code field
       * @param {string?} pathOrField - Path or field to match on
       * @param {boolean?} reference - If true, search reference files
       * @returns {CodeFile|undefined} Matching file if found
       */
      svc.findFile = function (pathOrField) {
        var reference = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

        if (pathOrField) {
          var pathTest = existingFileRegex(pathOrField, true);
          var fieldName = pathOrField.replace(/\.[a-z]+$/i, '');
          return _files(reference).find(function (f) {
            return pathTest.test(f.path) || f.field && f.field === fieldName;
          });
        }
      };
      svc.findParentFile = function (path) {
        var reference = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

        var file = void 0;
        path = cleanPath(path);
        while (!file && path.length) {
          file = svc.findFile(path, reference);
          path = path.replace(/(^|\/)[^\/]+$/, '');
        }
        return file;
      };

      svc.findRelatedFile = function (file) {
        return !!svc.referenceFiles && svc.findFile(file.path, !file.reference);
      };

      var addFile = svc.addFile = function () {
        var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
            _ref3$basePath = _ref3.basePath,
            basePath = _ref3$basePath === undefined ? '' : _ref3$basePath,
            _ref3$contents = _ref3.contents,
            contents = _ref3$contents === undefined ? '' : _ref3$contents,
            prompt = _ref3.prompt,
            placeholder = _ref3.placeholder,
            reference = _ref3.reference,
            directory = _ref3.directory;

        if (svc.expired || _alertOnTooManyFiles({ directory: directory }, reference)) return;

        multiFilePathDialog.open({
          path: basePath,
          prompt: prompt,
          placeholder: placeholder,
          directory: directory,
          showDirectoryToggle: true,
          isReference: reference,
          invalid: _invalidCheck(null, reference),
          warning: _warningCheck()
        }).then(function (_ref4) {
          var path = _ref4.path,
              directory = _ref4.directory;

          _createFile({ path: path, contents: contents, directory: directory }, reference);
          svc.changeReason = 'addFile';
          updateAllAfterFileChanges();
        });
      };

      svc.addFileTo = function (opts) {
        opts.basePath = opts.basePath || '';
        if (!/\/$/.test(opts.basePath)) opts.basePath += '/';
        addFile(opts);
      };

      svc.canAddFileTo = function (file, path, directory) {
        if (!directory) return false;
        if (file) return file.$access === 'readwrite';
        var parentFile = svc.findParentFile(path);
        return !parentFile || parentFile.$access === 'readwrite';
      };

      svc.rename = function (file) {
        var path = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : file.path;
        var reference = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : file && file.reference;
        var newPath = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : path;

        if (file && file.locked || svc.expired) return;
        var directory = !file || file.directory;
        var hasReference = svc.referenceFiles && (directory || file && file.hasReference);
        multiFilePathDialog.open({
          path: newPath,
          title: 'Rename or Move ' + (directory ? 'Directory' : 'File'),
          prompt: 'New path & name:',
          isReference: reference,
          hasReference: hasReference,
          directory: directory,
          invalid: _invalidCheck(path, reference, hasReference),
          warning: _warningCheck()
        }).then(function (_ref5) {
          var newPath = _ref5.path,
              renameReference = _ref5.renameReference;

          var renamed = _renameFile({
            file: file,
            path: path,
            reference: reference,
            newPath: newPath,
            renameReference: renameReference,
            directory: directory
          });
          if (renamed) {
            svc.changeReason = 'renameFile';
            updateAllAfterFileChanges();
          }
        });
      };

      svc.close = function (file) {
        remove(svc.openFiles, file);
      };

      svc.deletedFiles = [];
      var _addToDeleted = function _addToDeleted(file) {
        // We only ever store copies of deleted files, to prevent reference issues
        var toDelete = angular.copy(file);
        delete toDelete.id;
        if (toDelete.reference) {
          // mark reference files so we don't have to worry about collisions
          toDelete.path = REFERENCE_PREFIX + toDelete.path;
        }
        toDelete.$deleted = true;

        var existingIndex = svc.deletedFiles.findIndex(function (df) {
          return df.path === toDelete.path && df.directory === toDelete.directory;
        });
        if (existingIndex > -1) {
          // simply overwrite existing deleted file
          svc.deletedFiles[existingIndex] = toDelete;
        } else {
          if (!file.directory) {
            // purge any deleted files that would be descendant of this file
            var purgePath = existingFileRegex(toDelete.path);
            removeMatching(svc.deletedFiles, function (df) {
              return purgePath.test(df.path);
            });
          }

          // purge any deleted files that match parent directories
          var parents = toDelete.path.split('/');
          parents.pop(); // skip the exact path

          var _loop = function _loop() {
            var purgePath = parents.join('/');
            var purgeIndex = svc.deletedFiles.findIndex(function (df) {
              return df.path === purgePath && !df.directory;
            });
            if (purgeIndex > -1) {
              svc.deletedFiles.splice(purgeIndex, 1);
            }
            parents.pop();
          };

          while (parents.length) {
            _loop();
          }

          svc.deletedFiles.push(toDelete);
        }
      };
      var _deleteFile = function _deleteFile(file) {
        if (file.locked) return;
        svc.close(file);
        // Always remove from active files array
        remove(_files(file.reference), file);
        // Set softDeletedAt for files
        file.softDeletedAt = new Date();
        // Add to deleted files list
        _addToDeleted(file);
        workspace.deleteFile(file);
      };
      svc.delete = function (file) {
        var path = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : file.path;
        var reference = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : file && file.reference;
        var delReference = arguments[3];

        if (file && file.locked || svc.expired) return;

        var directory = !file || file.directory;
        if (svc.referenceFiles && delReference === undefined) {
          var msg = void 0;
          if (directory) {
            var pathTest = existingFileRegex(path);
            if (_files(!reference).some(function (f) {
              return pathTest.test(f.path) && !f.locked;
            })) {
              msg = 'Also delete any ' + (reference ? 'Project' : 'Reference') + ' Files under "' + path + '"?';
            }
          } else if (file.hasReference) {
            msg = 'Also delete the associated ' + (reference ? 'Project' : 'Reference') + ' File?';
          }
          // only ask if there are any matching reference files
          if (msg) {
            dialogService.confirm(msg, 'Delete Related Files', ['Cancel', 'No', 'Yes']).then(function (btn) {
              svc.delete(file, path, reference, btn.id === 'yes');
            });
            return;
          }
        }
        if (!directory) {
          var relFile = svc.findRelatedFile(file);
          if (delReference && relFile) {
            _deleteFile(relFile);
          }
          _deleteFile(file);
        } else {
          var _pathTest = existingFileRegex(path);
          if (!reference || delReference) {
            svc.files.filter(function (f) {
              return _pathTest.test(f.path) && !f.locked;
            }).forEach(_deleteFile);
          }
          if (reference || delReference) {
            svc.referenceFiles.filter(function (f) {
              return _pathTest.test(f.path) && !f.locked;
            }).forEach(_deleteFile);
          }
        }
        svc.changeReason = 'deleteFile';
        updateAllAfterFileChanges();
        // notify others
        if (svc.interact && svc.interact.canEdit) {
          svc.interact.deleteFile({ id: file.path });
        }
      };

      var _getDeletedFiles = function _getDeletedFiles(file, path) {
        if (!file || file.directory) {
          var pathTest = existingFileRegex(path);
          return svc.deletedFiles.filter(function (f) {
            return pathTest.test(f.path);
          });
        } else {
          return [file];
        }
      };

      svc.undelete = function (file) {
        var path = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : file && file.path;

        if (!path || svc.expired) return;

        var reference = file ? file.reference : REFERENCE_PREFIX_REGEX.test(path);

        var filesToRestore = _getDeletedFiles(file, path);

        multiFileOverwriteDialog.open({
          paths: filesToRestore.map(function (f) {
            return f.path.replace(REFERENCE_PREFIX_REGEX, '');
          }),
          files: _files(reference)
        }).then(function (_ref6) {
          var paths = _ref6.paths;

          filesToRestore.filter(function (f) {
            return paths.includes(f.path.replace(REFERENCE_PREFIX_REGEX, ''));
          }).forEach(function (file) {
            var files = _files(reference);
            remove(svc.deletedFiles, file);
            if (file.reference) {
              // clean the reference file trick
              file.path = file.path.replace(REFERENCE_PREFIX_REGEX, '');
            }

            // remove the overwritten file if there is one
            var overwrittenFile = svc.findFile(file.path, reference);
            if (overwrittenFile) {
              _deleteFile(overwrittenFile);
            }
            file.$deleted = false;
            files.push(file);
            workspace.addFile(file);
            if (filesToRestore.length < 3) {
              svc.clickFile(file);
            }
          });
          svc.changeReason = 'restoreFile';
          updateAllAfterFileChanges();
        });
      };

      svc.purge = function (file) {
        var path = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : file && file.path;

        if (!path || svc.expired) return;

        _getDeletedFiles(file, path).forEach(function (fileToPurge) {
          svc.close(fileToPurge);
          remove(svc.deletedFiles, fileToPurge);
        });
      };

      // only used by assess, resets a solution file to a challenge file
      var _resetFile = function _resetFile(file) {
        if (file.disabled || file.readOnly) return;

        var originalFile = svc.challenge.files.find(function (f) {
          return f.path === file.path;
        });

        if (originalFile) {
          if (originalFile.contents === file.contents) return;
          if (!file.locked) {
            // back up a copy of the file to deleted if we can
            _addToDeleted(file);
          }
          file.contents = originalFile.contents;
          workspace.updateFile(file);
        } else if (!file.locked) {
          // just delete if we can
          _deleteFile(file);
        }
      };

      svc.reset = function (file) {
        if (svc.expired) return;
        _resetFile(file);
        svc.changeReason = 'resetFile';
        updateAllAfterFileChanges();
      };
      // only used by assess, resets all solutions files to challenge files
      svc.resetAll = function () {
        if (svc.expired) return;
        svc.files.slice().forEach(_resetFile);
        svc.challenge.files.forEach(function (cf) {
          if (!svc.files.some(function (sf) {
            return sf.path === cf.path;
          })) {
            // restore original files that were deleted
            var props = {};
            ['path', 'contents', 'access', 'deletable', 'directory', 'challengeFileId', 'includeOnTest', 'includeOnSubmit', 'initialLayout', 'testGroups'].forEach(function (k) {
              props[k] = cf[k];
            });
            _createFile(props, false, false);
          }
        });
        svc.changeReason = 'resetAll';
        updateAllAfterFileChanges();
      };

      svc.export = function (file) {
        virtualFileDownloadService.download(file.path, file.contents);
      };

      function updateAllAfterFileChanges() {
        if (svc.refreshOnFilesChanged) {
          // refresh the controllers as well
          svc.refreshOnFilesChanged();
        } else {
          _updateAfterFileChanges();
        }
      }

      function _updateAfterFileChanges() {
        if (svc.referenceFiles) {
          svc.files.forEach(function (f) {
            f.hasReference = false;
          });
          svc.referenceFiles.forEach(function (refFile) {
            var setupFile = svc.findFile(refFile.path);
            if (setupFile) {
              setupFile.hasReference = refFile.hasReference = true;
            } else {
              refFile.hasReference = false;
            }
          });
        }

        if (svc.solution && svc.challenge) {
          var readOnly = !!svc.readOnly || svc.interact && svc.interact.cannotEdit;
          var restricted = !!svc.restricted;

          var isIgnored = svc.challenge.createIgnoreCheckFunction();
          // flag files that can be reset or are ignored
          svc.files.forEach(function (f) {
            var challengeFile = svc.challenge.files.find(function (cf) {
              return cf.path === f.path;
            });
            f.canReset = challengeFile && !challengeFile.directory;
            f.unchanged = challengeFile && challengeFile.contents === f.contents;
            f.hasChallengeFile = !!challengeFile;
            f.restricted = restricted;
            f.ignored = f.$access === 'readwrite' && (!f.includeOnSubmit || isIgnored(f.path));
            f.lockedLayout = svc.challenge.lockedLayout();
            f.$readOnly = readOnly;
            f.$interact = !!svc.interact;
          });
        }

        // TODO: this might be too inefficient, so we might need to determine which files are changed and only
        //   save those files. Or, we might want to move this logic to CodeMirrorWorkspace, and have
        //   that component track what contents might have changed since then.
        workspace.saveAllFiles();

        if (opts.afterFileChanges) {
          opts.afterFileChanges(svc.changeReason);
          delete svc.changeReason;
        }
      }

      svc.updateAfterFileChanges = _updateAfterFileChanges;

      svc.openRelatedFile = function (file) {
        var relFile = svc.findRelatedFile(file);
        if (relFile) {
          svc.clickFile(relFile);
        } else {
          _createFile(file, !file.reference);
        }
      };

      var DUPLICATE_PROPS = ['path', 'contents', 'directory'];
      if (!svc.solution) {
        DUPLICATE_PROPS.push('access', 'includeOnTest', 'includeOnSubmit', 'deletable', 'testGroups', 'initialLayout');
      }

      svc.duplicate = function (existingFile) {
        var existingPath = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : existingFile.path;
        var reference = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : existingFile && existingFile.reference;

        if (existingFile && existingFile.disabled || svc.expired) return;

        var directory = !existingFile || existingFile.directory;
        var targetFiles = _files(reference);
        var dialogResult = void 0;
        var newFiles = [existingFile];
        var pathMatcher = existingFileRegex(existingPath);
        if (directory) {
          newFiles = targetFiles.filter(function (f) {
            return pathMatcher.test(f.path) && (!svc.solution || !f.disabled);
          });
          dialogResult = multiFilePathDialog.open({
            title: 'Duplicate Directory "' + existingPath + '"',
            prompt: 'Where should the new directory be added?',
            path: existingPath,
            directory: true,
            allowRoot: false,
            invalid: _invalidCheck(null, reference),
            warning: _warningCheck()
          });
        } else {
          dialogResult = multiFilePathDialog.open({
            title: 'Duplicate "' + existingPath + '"',
            prompt: 'Path for new file',
            path: existingPath,
            invalid: _invalidCheck(null, reference),
            warning: _warningCheck()
          });
        }
        newFiles = newFiles.map(function (f) {
          var newProps = {};
          DUPLICATE_PROPS.forEach(function (k) {
            newProps[k] = angular.copy(f[k]);
          });
          return newProps;
        });
        dialogResult.then(function (_ref7) {
          var newPath = _ref7.path;

          var tail = directory ? '/' : '';
          var openedCount = 0;
          var filesToOpen = [];
          newFiles.forEach(function (newFileProps) {
            if (newFileProps.path === existingPath) {
              newFileProps.path = newPath;
            } else {
              newFileProps.path = newFileProps.path.replace(pathMatcher, newPath + tail);
            }
            // only auto-open the first 4 files
            openedCount++;
            var newFile = _createFile(newFileProps, reference, false);
            if (newFile && openedCount < 5) {
              filesToOpen.push(newFile);
            }
          });

          filesToOpen.forEach(svc.clickFile);

          svc.changeReason = 'duplicateFile';
          updateAllAfterFileChanges();
        });
      };

      multiFileChallengeServiceDND.setupDragAndDrop(svc, _createFile);

      _updateAfterFileChanges();
    }

    return {
      setupFileManagement: setupFileManagement
    };
  }]);

  // If the current editor pauses longer than this, we allow others to take over editing silently
  var INTERACT_EDITOR_TIMEOUT_MS = 2000;

  // firebase data older than 5 minutes is considered stale, and we ignore it
  var INTERACT_STALE_DATA_TIMEOUT_MS = 5 * 60 * 1000;

  var INTERACT_STATUS_CONNECTING = 'connecting';
  var INTERACT_STATUS_CONNECTED = 'connected';
  var INTERACT_STATUS_DISCONNECTED = 'disconnected';

  var activeFileIdKey = 'activeFileId';
  var activeFileKey = 'activeFile';
  var activeEditorIdKey = 'activeEditorId';
  var activeEditorKey = 'activeEditor';
  var savedVersionKey = 'savedVersion';
  var editedVersionKey = 'editedVersion';
  var fileActionKey = 'fileAction';

  var FILE_ACTION_DELETE = 'Deleted';
  var FILE_ACTION_RENAME = 'Renamed';
  var FILE_ACTION_CREATE = 'Created';
  var MULTIPLE_FILES_CHANGED_ACTION = 'sChanged';

  var _ON_DISCONNECT = {
    toDestroy: [activeFileKey],
    toNull: [activeFileIdKey, activeFileKey, activeEditorIdKey, activeEditorKey]
  };

  var solutionInteractServiceModule = angular.module('Q.services.solution-interact-service', [firebaseServiceModule, solutionModelModule]).factory('solutionInteractService', ['firebase', '$q', '$log', '$timeout', 'Solution', function (firebase, $q, $log, $timeout, Solution) {
    return {
      /**
       * Starts Interact on the provided service.
       *
       * @param {string} challengeRef - Firebase ref for this challenge
       * @param {Object} liveInterview - Object returned from liveInterviewService.join
       * @param {String} solutionId - ID of Solution being interacted with
       */
      start: function start(challengeRef, liveInterview, solutionId) {
        return _startInteract(firebase, $q, $timeout, $log, Solution, challengeRef, liveInterview, solutionId);
      }
    };
  }]);

  /**
   * Sets up an Interact service.
   *
   * This service is designed around a one-editor-at-a-time workflow.
   * Each user can take over editing at any time, and while they are editing, all other users are assumed to be in
   * read-only mode. This prevents the complexity of tracking multiple users editing simultaneously, which isn't
   * really that useful in practice.
   *
   * The service starts by connecting to and watching two primary properties: activeFileId and activeEditorId.
   * Based on these, we can decide when to open (and/or close) files, and who has editing access.
   *
   * If a file is modified via the updateFile method, we assume the current user is now the editor, and update the
   * appropriate fields.
   *
   * Users of this service should rely on the built-in event emitter to handle changes.
   *
   * Whenever the solution is saved, the methods markSaving and markSaved should be called, to ensure we can identify
   * when it's safe to transfer editor control.
   *
   * Events:
   * - connected - we're successfully connected
   * - connectionError<{ response }> - if something went wrong while connecting
   * - disconnected - we've successfully disconnected
   * - activeFileIdChanged<{ activeFileId }> - the active file has changed (could be null)
   * - activeEditorChanged<{ activeEditorId, activeEditor }> - the active editor has changed (could be null)
   * - fileChanged<{ id, properties }> - a file has been modified (also called when first opened if not stale)
   * - savedVersionChanged<{ savedVersion }> - The saved version has been incremented (may or may not mean that the challenge is saved to our server)
   * - editedVersionChanged<{ editedVersion }> - The edited version has been incremented, likely means the challenge is no longer in-sync with the server
   * - fileCreated<{ id, properties }> - a file has been created
   * - fileRenamed<{ oldPath, newPath }> - a file has been renamed
   * - fileDeleted<{ id }> - a file has been deleted
   * - filesChanged<{ ids }> - several files have been changed at once
   *
   * Note: For file change events, it's assumed the visible file will be tracked via activeFileIdChanged events, since it's
   * possible a file is opened for editing, but the contents are stale or unmodified
   *
   *
   * @param {Object }firebase - Angular Firebase library
   * @param {Promise} $q - Angular Promise Library
   * @param {Object} $timeout - Angular $timeout library
   * @param {Object} $log - Angular $log library
   * @param {Solution} Solution - Solution model
   * @param {string} challengeRef - Base reference for the Firebase properties
   * @param {Object} liveInterview - LiveInterviewService instance
   * @param {String} solutionId - ID of Solution being interacted with
   * @return {Object} The Interact Service
   * @private
   */
  function _startInteract(firebase, $q, $timeout, $log, Solution, challengeRef, liveInterview, solutionId) {
    // will get appended with the file ID, allows us to track the file contents
    var filesRef = challengeRef + '/files';
    var interactRef = challengeRef + '/interact';
    var activeFileIdRef = interactRef + '/' + activeFileIdKey;
    var activeEditorIdRef = interactRef + '/' + activeEditorIdKey;
    var savedVersionRef = interactRef + '/' + savedVersionKey;
    var editedVersionRef = interactRef + '/' + editedVersionKey;
    var fileActionRef = interactRef + '/' + fileActionKey;
    var getFileRef = function getFileRef(id) {
      return filesRef + '/' + encodeForFirebaseRef(id);
    };

    var unwatch = {};

    var revisionsCount = function revisionsCount() {
      var solution = solutionId && Solution.get(solutionId);
      return solution && solution.revisionsCount || 0;
    };

    var saving = false;
    var editedWhileSaving = false;

    var fileChangeTimer = void 0;

    var svc = {
      status: INTERACT_STATUS_DISCONNECTED,
      isDisconnected: true,
      isConnecting: false,
      isConnected: false,

      // A promise that resolves when the service is connected, or rejects if there was an error
      initializationPromise: null,

      // Firebase values
      activeFileId: null,
      activeFile: null,
      activeEditorId: null,
      activeEditor: null,
      savedVersion: revisionsCount(),
      editedVersion: revisionsCount(),

      // Utility booleans automatically updated when data changes
      isBeingEdited: false,
      isBeingEditedByOthers: false,
      isUserActiveEditor: false,
      hasOtherEditor: false,
      isSaved: true,
      canEdit: true,

      lastEdited: 0,

      buttonTooltip: '',
      baseButtonClasses: '',
      buttonClasses: function buttonClasses(fileId) {
        var classes = svc.baseButtonClasses;
        if (svc.isBeingEdited && (!fileId || svc.activeFileId === fileId)) {
          classes += ' being-edited';
          if (svc.isBeingEditedByOthers) {
            classes += ' being-edited-by-others';
          }
        }
        return classes;
      },


      /**
       * Call whenever the user is the active editor and modifies a file.
       *
       * @param {*} file
       * @param {string} file.id - ID of file being tracked
       * @param {Object} file.properties - properties of file to track (should be a simple hash)
       */
      updateFile: function updateFile() {
        var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
            _ref$id = _ref.id,
            id = _ref$id === undefined ? svc.activeFileId : _ref$id,
            properties = _ref.properties;

        if (!id || svc.isDisconnected) return;

        _setActiveEditor();

        svc.activeFileId = id;
        firebase.set(activeFileIdRef, id);

        if (properties !== undefined) {
          // update edited version if necessary. Increment by 2 if we're mid-save.
          if (saving) {
            editedWhileSaving = true;
          }
          firebase.set(editedVersionRef, svc.savedVersion + 1);
          var oldProps = svc.activeFile && svc.activeFile.properties || {};
          firebase.set(getFileRef(id), {
            lastEdited: svc.lastEdited,
            properties: angular.extend({}, oldProps, properties)
          });
        }
        _updateService();
      },


      /**
       * Updates many files at once.
       * @param {Array<{id:string, properties:*}>} files
       */
      updateFiles: function updateFiles(files) {
        if (!files || !files.length || svc.isDisconnected) return;

        _setActiveEditor();

        var ids = [];

        files.forEach(function (_ref2) {
          var id = _ref2.id,
              properties = _ref2.properties;

          if (properties !== undefined) {
            // update edited version if necessary. Increment by 2 if we're mid-save.
            if (saving) {
              editedWhileSaving = true;
            }
            firebase.set(editedVersionRef, svc.savedVersion + 1);
            firebase.set(getFileRef(id), {
              lastEdited: svc.lastEdited,
              properties: angular.extend({ contents: '', selections: [] }, properties)
            });
            ids.push(id);
          }
        });

        if (ids.length) {
          firebase.set(fileActionRef, {
            action: MULTIPLE_FILES_CHANGED_ACTION,
            ids: ids
          });
        }
      },


      /**
       * Looks up the Interact contents of a file
       * @param {string} id - ID of file to get
       * @returns {Promise<{id:string, properties:*}>} Resolves with the file contents
       */
      getFile: function getFile(_ref3) {
        var id = _ref3.id;

        if (svc.disconnected) return $q.resolve();
        return firebase.get(getFileRef(id));
      },


      /**
       * Notify that a file has been created
       * @param {string} id - new file ID
       * @param {*?} properties - new file properties
       */
      createFile: function createFile(_ref4) {
        var id = _ref4.id,
            properties = _ref4.properties;

        if (!id || svc.isDisconnected) return;
        _setActiveEditor();
        firebase.set(fileActionRef, {
          action: FILE_ACTION_CREATE,
          id: id,
          properties: properties
        });
      },


      /**
       * Notify that a file has been renamed
       * @param {string?} id - optional file ID
       * @param {string} oldPath - old file path
       * @param {string} newPath - new file path
       */
      renameFile: function renameFile(_ref5) {
        var id = _ref5.id,
            oldPath = _ref5.oldPath,
            newPath = _ref5.newPath;

        if (!oldPath || svc.isDisconnected) return;
        _setActiveEditor();
        firebase.set(fileActionRef, {
          action: FILE_ACTION_RENAME,
          id: id,
          oldPath: oldPath,
          newPath: newPath
        });
      },
      deleteFile: function deleteFile(_ref6) {
        var id = _ref6.id;

        if (!id || svc.isDisconnected) return;
        _setActiveEditor();
        firebase.set(fileActionRef, { action: FILE_ACTION_DELETE, id: id });
      },


      /**
       * Call when saving the current model, to track if any edits happen while saving.
       */
      markSaving: function markSaving() {
        if (svc.isUserActiveEditor) {
          saving = true;
        }
      },


      /**
       * Call whenever the solution is saved. Increments the savedVersion so we can track if the changes are in-sync.
       */
      markSaved: function markSaved() {
        if (svc.isUserActiveEditor) {
          svc.savedVersion = revisionsCount();
          // Update edited version. If we edited While saving, we bump it up again
          firebase.set(editedVersionRef, svc.savedVersion + (editedWhileSaving ? 1 : 0));
          // Store the savedVersion second, because we want to note that we haven't saved yet
          firebase.set(savedVersionRef, svc.savedVersion);
          _updateService();
        }
        saving = editedWhileSaving = false;
      },


      /**
       * Can be used to reconnect this service if disconnected, also used when created to set up
       * the initial connections.
       *
       * @return {Promise} That resolves when everything is ready
       */
      reconnect: function reconnect() {
        if (!svc.isDisconnected) return;

        // start the last edit timer now, in case we come in mid-edit
        svc.lastEdited = Date.now();
        _startFileChangeTimer();

        _setStatus(INTERACT_STATUS_CONNECTING);
        return svc.initializationPromise = $q.all([_setupValueWatcher(activeFileIdRef, _handleActiveFileIdChange), _setupValueWatcher(activeEditorIdRef, _handleActiveEditorIdChange), _setupValueWatcher(savedVersionRef, _handleVersionChange.bind(savedVersionRef, savedVersionKey)), _setupValueWatcher(editedVersionRef, _handleVersionChange.bind(editedVersionRef, editedVersionKey)), firebase.get(fileActionRef).then(function (fileAction) {
          if (svc.isDisconnected) return;
          // we only respond to changes to file actions, ignoring it on startup
          unwatch[fileActionRef] = fileAction.$watch(function () {
            return _handleFileActionChange(fileAction);
          });
        })]).then(function () {
          if (svc.isDisconnected) return;
          _setStatus(INTERACT_STATUS_CONNECTED);
          svc.emit('connected', { interact: svc });
          return svc;
        }).catch(function (response) {
          // reset everything if something went wrong
          _disconnect();
          svc.emit('connectionError', { interact: svc, response: response });
          return $q.reject(response);
        });
      },


      /**
       * Disconnects and destroys any connections for this instance.
       */
      disconnect: function disconnect() {
        if (svc.isDisconnected) return;
        _disconnect();
        svc.emit('disconnected', { interact: svc });
      },


      /**
       * Completely destroys this Interact session
       */
      destroy: function destroy() {
        _disconnect();
        svc.off(true);
      }
    };

    // enable events on the service
    eventEmitter(svc);

    // update our active editor in case the user list was out-of-sync with our current editor ID
    liveInterview.on('usersListChanged', function () {
      return _handleActiveEditorIdChange();
    });

    // start the service
    svc.reconnect();

    return svc;

    //// Private Internal Methods

    function _setActiveEditor() {
      if (svc.activeEditorId !== liveInterview.userProfile.id) {
        _handleActiveEditorIdChange(liveInterview.userProfile.id);
        firebase.set(activeEditorIdRef, svc.activeEditorId);
      }

      svc.lastEdited = Date.now();
      _startFileChangeTimer();
      _updateService();
    }

    /**
     * Sets up a Firebase watcher for the value reference
     * @param {string} ref - Firebase reference
     * @param {Function} handler - If provided, handles changes to the value, otherwise it's just copied to the service
     * @return {Promise}
     * @private
     */
    function _setupValueWatcher(ref, handler) {
      return firebase.get(ref).then(function (obj) {
        if (svc.isDisconnected) return;
        handler(obj.$value);
        unwatch[ref] = obj.$watch(function () {
          return handler(obj.$value);
        });
      });
    }

    /**
     * Clears all properties, destroys any watchers, and updates the service variables
     * @private
     */
    function _disconnect() {
      _setStatus(INTERACT_STATUS_DISCONNECTED);
      angular.forEach(unwatch, function (uw, k) {
        uw();
        delete unwatch[k];
      });

      var users = liveInterview.usersList || [];
      // if nobody is left on the interview, clean up
      if (users.length === 0 || users.length === 1 && users[0].$self) {
        firebase.remove(interactRef);
        firebase.remove(filesRef);
      }

      _ON_DISCONNECT.toDestroy.forEach(function (k) {
        svc[k] && svc[k].$destroy();
      });
      _ON_DISCONNECT.toNull.forEach(function (k) {
        svc[k] = null;
      });
      _updateService();
    }

    /**
     * Sets the service status and updates any service variables
     * @param status
     * @private
     */
    function _setStatus(status) {
      svc.status = status;
      svc.isConnecting = status === INTERACT_STATUS_CONNECTING;
      svc.isConnected = status === INTERACT_STATUS_CONNECTED;
      svc.isDisconnected = status === INTERACT_STATUS_DISCONNECTED;
      _updateService();
    }

    /**
     * Sets up utility booleans based on current service status
     * @private
     */
    function _updateService() {
      var editor = svc.isConnected && svc.activeEditor;
      svc.isUserActiveEditor = !!editor && editor.$self;
      svc.hasOtherEditor = !!editor && !editor.$self;
      svc.isBeingEdited = !!editor && Date.now() - svc.lastEdited < INTERACT_EDITOR_TIMEOUT_MS;
      svc.isBeingEditedByOthers = svc.hasOtherEditor && svc.isBeingEdited;
      // TODO: how to handle dropoffs? what happens if the current editor is unable to save changes?
      svc.isSaved = svc.savedVersion >= svc.editedVersion;
      var couldEdit = svc.canEdit;
      svc.canEdit = svc.isUserActiveEditor || !svc.isBeingEdited && svc.isSaved;
      svc.cannotEdit = !svc.canEdit;

      _updateButtonTooltip();
      _updateBaseButtonClasses();

      if (svc.canEdit !== couldEdit) {
        svc.emit('canEditChanged', { interact: svc, canEdit: svc.canEdit });
      }
    }

    /**
     * Updates the button tooltip text based on the current service state
     * @private
     */
    function _updateButtonTooltip() {
      var tooltip = 'Nobody is currently editing the solution, you can edit at any time.';
      if (svc.isConnecting) {
        tooltip = 'Connecting to Interact service';
      } else if (svc.isDisconnected) {
        tooltip = 'Unable to connect to Interact service. Click to try again';
      } else if (svc.isBeingEditedByOthers) {
        var who = svc.activeEditor ? svc.activeEditor.name : 'someone else';
        var clickAction = svc.canEdit ? 'Click to take control.' : 'Please wait while changes are synced.';
        tooltip = 'The solution is being actively edited by ' + who + '. ' + clickAction;
      } else if (svc.isBeingEdited) {
        tooltip = 'You are currently editing the solution.';
      } else if (svc.activeEditor) {
        if (svc.activeEditor.$self) {
          tooltip += ' (You were the last editor.)';
        } else {
          tooltip += ' (' + svc.activeEditor.name + ' was the last editor.)';
        }
      }
      svc.buttonTooltip = tooltip;
    }

    /**
     * Updates the base button classes based on the current service state
     * @private
     */
    function _updateBaseButtonClasses() {
      var classes = svc.activeEditor ? 'has-active-editor' : 'no-active-editor';
      if (svc.isConnecting) {
        classes += ' connecting';
      } else if (svc.isDisconnected) {
        classes += ' disconnected';
      } else if (svc.isBeingEdited) {
        classes += svc.canEdit ? ' can-edit' : ' syncing';
      }
      svc.baseButtonClasses = classes;
    }

    /**
     * Handles a version change. We assume that versions only increment,
     * so if our version is higher (and we're the active editor), we override the version stored on the server.
     * @param {string} ref - firebase ref
     * @param {string} key - service key
     * @param {number} version - new version from Firebase
     * @private
     */
    function _handleVersionChange(ref, key, version) {
      if (version && svc[key] < version) {
        svc[key] = version;
        _updateService();
        svc.emit(key + 'Changed', defineProperty({ interact: svc }, key, version));
      } else if (svc.isUserActiveEditor && svc[key] !== version) {
        firebase.set(ref, svc[key]);
      }
    }

    /**
     * When the active file is changed, we need to update our watcher, and send out events.
     * @param {string} activeFileId - The new active file ID.
     * @private
     */
    function _handleActiveFileIdChange(activeFileId) {
      if (svc.activeFileId !== activeFileId) {
        svc.activeFileId = activeFileId || null;
        _updateService();
        svc.emit('activeFileIdChanged', { interact: svc, activeFileId: activeFileId });
        _watchFile();
      }
    }

    /**
     * Updates the active editor. Looks up the active editor in the liveInterview usersList.
     *
     * @param {string?} activeEditorId - When provided, sets the active Editor ID
     * @private
     */
    function _handleActiveEditorIdChange() {
      var activeEditorId = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : svc.activeEditorId;

      svc.activeEditorId = activeEditorId || null;
      var activeEditor = liveInterview.usersList.find(function (u) {
        return u.id === activeEditorId;
      }) || null;
      // clear old editor on connect
      if (svc.isConnecting && activeEditor && activeEditor.$self) {
        svc.activeEditorId = activeEditor = null;
        firebase.set(activeEditorIdRef, '');
        firebase.set(activeFileIdRef, '');
        firebase.set(fileActionRef, {});
      }
      if (!idEquals(activeEditor, svc.activeEditor)) {
        svc.activeEditor = activeEditor;
        _updateService();
        svc.emit('activeEditorChanged', {
          interact: svc,
          activeEditorId: activeEditorId,
          activeEditor: activeEditor
        });
      }
      // switch file watching if necessary
      _watchFile();
    }

    /**
     * Handles changes to the file action object. Supported actions: Created, Renamed, Deleted
     *
     * Simply emits a unique event when an action occurs.
     *
     * @param {{ action: string, id: string?, properties: object?, oldPath: string?, newPath: string? }} fileAction
     * @private
     */
    function _handleFileActionChange(fileAction) {
      if (!svc.isUserActiveEditor && isObject(fileAction)) {
        var action = fileAction.action,
            id = fileAction.id,
            ids = fileAction.ids,
            properties = fileAction.properties,
            oldPath = fileAction.oldPath,
            newPath = fileAction.newPath;

        svc.emit('file' + action, {
          interact: svc,
          id: id,
          ids: ids,
          properties: properties,
          oldPath: oldPath,
          newPath: newPath
        });
      }
    }

    /**
     * Stops watching the current active file
     * @private
     */
    function _unwatchFile() {
      if (unwatch.activeFile) {
        unwatch.activeFile();
        delete unwatch.activeFile;
        if (svc.activeFile) {
          svc.activeFile.$destroy();
        }
        svc.activeFile = null;
      }
    }

    /**
     * Watches the active file. At first, we have to look up the file, which we immediately treat as an edit.
     * Then we watch for changes.
     *
     * If the current file matches the current, actively watched file, we do nothing.
     *
     * If there is no active file ID, we clear everything and exit.
     *
     * @private
     */
    function _watchFile() {
      if (unwatch.activeFile && svc.activeFile && svc.activeFile.id === svc.activeFileId) {
        // no need to watch, already watching
        return;
      }
      if (!svc.activeFileId) {
        _unwatchFile();
        return;
      }
      firebase.get(getFileRef(svc.activeFileId)).then(function (activeFileRef) {
        _unwatchFile();
        svc.activeFile = activeFileRef;
        _handleEdit();
        unwatch.activeFile = activeFileRef.$watch(function () {
          return _handleEdit();
        });
      }).catch(function (err) {
        $log.error('Unable to watch file for Interact', err);
        // unwatch the previous file if something went wrong, and clear any values
        _unwatchFile();
        svc.activeFileId = null;
      });
    }

    /**
     * Handles changes to the actively watched file
     *
     * If the user is the active editor, we ignore the changes.
     * If the active file exists, but has not been edited in a long while (is stale), we ignore the changes.
     * Otherwise, we emit a change event.
     *
     * @private
     */
    function _handleEdit(force) {
      if (!svc.isUserActiveEditor && svc.activeFile && (force || Date.now() - svc.activeFile.lastEdited < INTERACT_STALE_DATA_TIMEOUT_MS)) {
        svc.lastEdited = Date.now();
        _startFileChangeTimer();
        var properties = {};
        angular.forEach(svc.activeFile.properties || {}, function (v, k) {
          if (k[0] !== '$') {
            properties[k] = v;
          }
        });
        if (Object.keys(properties).length) {
          svc.emit('fileChanged', {
            interact: svc,
            id: svc.activeFileId,
            properties: properties
          });
        }
      }
      _updateService();
    }

    /**
     * After the editor timer runs out, we update our service booleans
     */
    function _startFileChangeTimer() {
      $timeout.cancel(fileChangeTimer);
      fileChangeTimer = $timeout(function () {
        _updateService();
        svc.emit('activeEditorChanged', {
          interact: svc,
          activeEditorId: svc.activeEditorId,
          activeEditor: svc.activeEditor
        });
      }, INTERACT_EDITOR_TIMEOUT_MS);
    }
  }

  var MAX_AUTO_OPENED_FILES = 4;

  var multiFileChallengeServiceInteractModule = angular.module('Q.services.multi-file-challenge-service-interact', [solutionInteractServiceModule, appAlertComponentModule]).factory('multiFileChallengeServiceInteract', ['$rootScope', 'solutionInteractService', 'appAlert', '$timeout', '$interval', '$document', function ($rootScope, solutionInteractService, appAlert, $timeout, $interval, $document) {
    function setupInteract(svc) {
      svc.startInteract = function (challengeRef, liveInterview) {
        if (svc.interact) {
          if (svc.interact.isDisconnected) {
            svc.interact.reconnect();
          }
          return;
        }

        var interact = svc.interact = solutionInteractService.start(challengeRef, liveInterview, svc.solution);
        svc._mfcListeners.push(interact.destroy);

        var _fileToOpen = null;
        var autoOpenedFiles = [];

        /**
         * Updates a file if it exists, and opens it if we are following the current editor.
         * If the file doesn't exist, we track it, and attempt again if any file gets updated.
         * @param {string} id
         * @param {Object?} properties
         * @private
         */
        function _updateAndOpenFile(_ref) {
          var id = _ref.id,
              _ref$properties = _ref.properties,
              properties = _ref$properties === undefined ? {} : _ref$properties;

          _fileToOpen = null;
          var contents = properties.contents,
              selections = properties.selections;

          var file = svc.findFile(id);
          if (file) {
            if (isString(contents)) {
              file.contents = contents;
            }
            svc.withFileLayout(function (fileLayout) {
              var wasOpen = svc.openFiles.includes(file);
              if (svc.interact.followEditor && !svc.interact.privateEditing) {
                fileLayout.open(file, { cursor: selections });
                svc.workspace && svc.workspace.hideAllPopups();
                if (!wasOpen) {
                  // the next two lines add or move the file to the front of the autoOpen queue
                  remove(autoOpenedFiles, file);
                  autoOpenedFiles.push(file);
                  // only preserve the last 4 auto-opened files
                  while (autoOpenedFiles.length > MAX_AUTO_OPENED_FILES) {
                    fileLayout.close(autoOpenedFiles.shift());
                  }
                }
              } else if (wasOpen && selections) {
                // just update cursor information in the background
                fileLayout.setCursor(file, selections);
              }
            });
            svc.updateWebPreview(true);
          } else {
            _fileToOpen = { id: id, properties: properties };
          }
        }

        // If false, we don't automatically open and close files
        interact.followEditor = true;
        interact.privateEditing = false;
        interact.toggleFollowEditor = function () {
          var newVal = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : !interact.followEditor;

          if (interact.privateEditing) {
            interact.privateEditing = false;
            newVal = true;
          }
          interact.followEditor = newVal;
          if (newVal) {
            _updateAndOpenFile({ id: interact.activeFileId });
          }
        };

        //// MultiFileChallengeService listeners

        svc.on('fileChange', function (event) {
          var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
              file = _ref2.file,
              selections = _ref2.selections,
              userInitiated = _ref2.userInitiated;

          if (userInitiated && interact.canEdit && file && file.path) {
            if (!file.$hideFromInteract) {
              interact.updateFile({
                id: file.interactId,
                properties: {
                  contents: file.contents,
                  selections: selections || []
                }
              });
            }
          } else if (_fileToOpen) {
            _updateAndOpenFile(_fileToOpen);
          }
        });

        var privateEditingFileIds = ['runnerframe', 'idesettings', 'webpreview', 'candidatenotes', 'reviews'];

        var privateEditingTimeout = null;
        var _setPrivateEditing = function _setPrivateEditing(val) {
          $timeout.cancel(privateEditingTimeout);
          interact.privateEditing = val;
          if (val) {
            // after 10 seconds, clear the flag if there's been no further focus changes
            privateEditingTimeout = $timeout(function () {
              interact.privateEditing = false;
            }, 10000);
          }
        };
        svc.on('tabFocused', function (event) {
          var _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
              file = _ref3.file;

          // if we're focused on a tab that doesn't work with Interact, temporarily stop following
          _setPrivateEditing(file.$hideFromInteract || privateEditingFileIds.includes(file.id));
        });
        // polls for iframe focus every second, assume we're editing something locally if an
        // iframe has focus
        var iframeFocusListener = $interval(function () {
          var activeElement = $document[0].activeElement;
          if (activeElement && activeElement.nodeName === 'IFRAME') {
            _setPrivateEditing(true);
          }
        }, 1000);
        svc._mfcListeners.push(function () {
          $interval.cancel(iframeFocusListener);
        });

        //// Model Listeners

        var solutionListener = $rootScope.$on('socket:model.changed:Solution', function () {
          if (_fileToOpen) {
            _updateAndOpenFile(_fileToOpen);
          }
        });
        svc._mfcListeners.push(solutionListener);

        //// SolutionInteractService Listeners

        interact.on('connectionError', function (event, _ref4) {
          var response = _ref4.response;

          appAlert.modelError(response, {
            prefix: 'There was an issue setting up Interact:'
          });
        });
        interact.on('activeFileIdChanged', function (event, _ref5) {
          var activeFileId = _ref5.activeFileId;

          _updateAndOpenFile({ id: activeFileId });
        });
        interact.on('activeEditorChanged', function () /* event, { activeEditor } */{
          // update our read-only status
          svc.refreshOnFilesChanged();
        });
        interact.on('canEditChanged', function (event, _ref6) {
          var canEdit = _ref6.canEdit;

          // hide all popups when we're not the active editor
          svc.workspace && svc.workspace.enablePopups(canEdit);
        });
        interact.on('fileChanged', function (event, eventProperties) {
          _updateAndOpenFile(eventProperties);
        });
        interact.on('filesChanged', function (event, _ref7) {
          var ids = _ref7.ids;

          ids.forEach(function (id) {
            interact.getFile({ id: id }).then(function (interactFile) {
              var file = svc.findFile(id);
              var contents = interactFile.properties && interactFile.properties.contents;
              if (file && isString(contents)) {
                file.contents = contents;
              }
            });
          });
        });
        interact.on('fileCreated', function (event, _ref8) {
          var properties = _ref8.properties;

          svc.createFile(properties, false, true, false);
        });
        interact.on('fileRenamed', function (event, _ref9) {
          var oldPath = _ref9.oldPath,
              newPath = _ref9.newPath;

          var file = svc.findFile(oldPath);
          if (file) {
            svc.renameFile({ file: file, newPath: newPath });
          }
        });
        interact.on('fileDeleted', function (event, _ref10) {
          var id = _ref10.id;

          var file = svc.findFile(id);
          if (file) {
            svc.delete(file, file.path, false, false);
          }
        });
      };
    }

    return {
      setupInteract: setupInteract
    };
  }]);

  var multiFileChallengeServiceModule = angular.module('Q.services.multi-file-challenge-service', [multiFileChallengeServiceFileManagementModule, multiFileChallengeServiceInteractModule, runnerFrameComponentModule, onConfirmComponentModule, challengeModelModule, codeFileModelModule]).directive('linkMultiFileChallenges', function () {
    return {
      restrict: 'A',
      require: 'linkMultiFileChallenges',
      controller: ['$scope', '$attrs', function controller($scope, $attrs) {
        var ctrl = this;
        var _svc = void 0; // keep track of the last one
        var getSvc = function getSvc() {
          var svc = $scope.$eval($attrs.linkMultiFileChallenges);
          if (svc && svc !== _svc) {
            _svc = svc;
            svc.fileTrees = svc.fileTrees || [];
          }
          return svc || _svc;
        };

        $scope.$watch(getSvc, function (newSvc, oldSvc) {
          if (newSvc && oldSvc && newSvc !== oldSvc) {
            var refresh = false;
            // relink trees and layout
            if (oldSvc.fileTrees) {
              oldSvc.fileTrees.forEach(ctrl.linkFileTree);
              refresh = oldSvc.fileTrees.length;
            }
            if (oldSvc.layout) {
              ctrl.linkLayout(oldSvc.layout);
              refresh = true;
            }
            if (refresh && newSvc.updateAfterFileChanges) {
              newSvc.updateAfterFileChanges();
            }
            // clear old links
            oldSvc.fileTrees = [];
            oldSvc.fileLayout = null;
          }
        });

        ctrl.linkFileTree = function (fileTree) {
          var svc = getSvc();
          if (!svc) return;
          fileTree.dragEnabled = true;
          addUnique(svc.fileTrees, fileTree);
          if (fileTree.files === svc.files || fileTree.files === svc.referenceFiles) {
            // can re-order via drag-and-drop for these trees
            fileTree.onFileDrop = function (_ref) {
              var file = _ref.file,
                  path = _ref.path,
                  dropPath = _ref.dropPath,
                  dropDirectory = _ref.dropDirectory,
                  event = _ref.event;

              stopEvent(event);
              var newPath = cleanPath(dropPath);
              if (!dropDirectory) {
                newPath = parentPath(newPath);
              }
              newPath += '/' + splitPath(path).filename;
              svc.rename(file, path, fileTree.files === svc.referenceFiles, newPath);
            };
          } // otherwise just drag to layout

          fileTree.onFileDragStart = function (_ref2) {
            var file = _ref2.file;

            if (file && file.directory !== true && svc.fileLayout) {
              svc.fileLayout.startDragging(file);
            }
          };
          fileTree.onFileDragStop = function () {
            if (svc.fileLayout) {
              svc.fileLayout.stopDragging();
            }
          };
        };
        ctrl.unlinkFileTree = function (fileTree) {
          var svc = getSvc();
          if (!svc) return;
          remove(svc.fileTrees, fileTree);
        };
        ctrl.linkLayout = function (layout) {
          var svc = getSvc();
          if (!svc) return;
          if (svc.fileLayout !== layout) {
            svc.fileLayout = layout;

            layout.onTabDragStart = function (_ref3) {
              var file = _ref3.file;

              if (file && file.path && !file.$deleted && !file.locked) {
                svc.fileTrees.forEach(function (ft) {
                  ft.startDragging(file);
                });
              }
            };
            layout.onFileDragStop = function () {
              svc.fileTrees.forEach(function (ft) {
                ft.stopDragging();
              });
            };
          }
        };
      }]
    };
  }).factory('multiFileChallengeService', ['$timeout', 'runnerFrameService', '$rootScope', '$q', '$state', 'multiFileChallengeServiceFM', 'multiFileChallengeServiceInteract', 'Challenge', 'CodeFile', '$log', function ($timeout, runnerFrameService, $rootScope, $q, $state, multiFileChallengeServiceFM, multiFileChallengeServiceInteract, Challenge, CodeFile, $log) {
    var mfcsCounter = 0;

    /**
     * Sets up common multi-file challenge controls
     * @param {*?} opts.svc - If provided, add methods to this object, otherwise add to a new object
     * @param {Challenge} opts.challenge - Required for configuration
     * @param {boolean?} opts.editingChallenge - If true, then we are editing a challenge (used to always set up SmartIDE for the editor no matter the challenge config)
     * @param {Solution?} opts.solution - If provided, configure in solution mode & use solution as the base
     * @param {String?} opts.docsArticleId - If provided, add the KB panel with this article
     * @param {Array>?} opts.docsArticleList - If provided, adds an article list to the KB pane
     * @param {String?} opts.docsArticleListTitle - Sets title of article list
     * @param {String?} opts.docsArticleListContent - Adds some markdown above the article list
     * @param {boolean?} opts.review - If true, configure for review mode
     * @param {String|Function?} opts.toolbarTemplate - If string, the toolbar for files with a path.
     *      If a function, will be passed in the file object.
     * @param {Function?} opts.getRunFiles - If provided, this function is used to determine which files to include when running.
     *      Otherwise, only test files are included.
     * @param {Function<file>?} opts.afterFileChanges - If provided, will be called when a file is added, renamed/moved, or destroyed
     *
     * @returns {*} Configured service object
     */
    function create() {
      var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var _opts$svc = opts.svc,
          svc = _opts$svc === undefined ? {} : _opts$svc,
          challenge = opts.challenge,
          solution = opts.solution,
          challengeConfig = opts.challengeConfig,
          reviewMode = opts.review,
          enableAiAgent = opts.enableAiAgent;


      $log.info('This assessment will have an AI agent', enableAiAgent);

      svc._id = ++mfcsCounter; // used to uniquely identify mfcs's
      svc.challenge = svc.challenge || challenge;
      svc.challenge.options = svc.challenge.options || {};
      svc.solution = svc.solution || solution;
      if (svc.solution) {
        svc.solution.options = svc.solution.options || {};
      }
      svc.challengeConfig = challengeConfig || [];

      if (!svc.on) {
        eventEmitter(svc);
      }
      svc.updateWebPreview = function (force) {
        if (!svc.expired && svc.webPreviewFile) {
          // re-render preview
          svc.webPreviewFile.sync(force);
        }
      };
      svc.updateFilePaths = function () {
        var model = svc.solution || svc.challenge;
        if (model.files && model.files.updatePaths) {
          var renamedFiles = model.files.updatePaths();
          if (svc.workspace) {
            renamedFiles.forEach(function (_ref4) {
              var file = _ref4.file,
                  oldPath = _ref4.oldPath;

              svc.workspace.renameFile(file, oldPath);
              // notify others
              if (svc.interact && svc.interact.canEdit) {
                svc.interact.renameFile({ oldPath: oldPath, newPath: file.path });
              }
            });
          }
        }
      };
      svc.onFileChange = function () {
        var _ref5 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
            force = _ref5.force,
            file = _ref5.file,
            userInitiated = _ref5.userInitiated;

        var _ref6 = file || {},
            cursor = _ref6.cursor,
            selections = _ref6.selections;

        svc.updateWebPreview(force);
        if (file && userInitiated) {
          // TODO: filter out cursor-only changes?
          svc.updateFilePaths();
        }
        if (svc.updateAfterFileChanges) {
          svc.updateAfterFileChanges();
        }
        svc.emit('fileChange', { file: file, userInitiated: userInitiated, cursor: cursor, selections: selections });
      };

      svc.onTabFocused = function () {
        var _ref7 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
            file = _ref7.file;

        svc.emit('tabFocused', { file: file });
      };

      Object.defineProperties(svc, {
        files: {
          get: function get() {
            return (svc.solution ? svc.solution.files : svc.challenge.files) || [];
          },

          configurable: true
        },
        referenceFiles: {
          get: function get() {
            return svc.solution ? null : svc.challenge.referenceFiles || [];
          },

          configurable: true
        },
        allFiles: {
          get: function get() {
            return [].concat(svc.files, svc.referenceFiles || []);
          },

          configurable: true
        },
        challengeOptions: {
          get: function get() {
            if (svc.solution) {
              return svc.solution.options = svc.solution.options || {};
            } else if (svc.challenge) {
              return svc.challenge.options = svc.challenge.options || {};
            } else {
              return {};
            }
          },

          configurable: true
        }
      });

      svc.fileTrees = svc.fileTrees || [];

      /**
       * Handles the asynchronous nature of having a fileLayout controller. The callback is called
       * when we finally have a fileLayout controller available (if ever)
       * @param {Function} cb - callback with the fileLayout controller as the first argument
       * @param {boolean} changeState - If false, then only call the callback if we already have it.
       */
      svc.withFileLayout = function (cb) {
        var changeState = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

        if (changeState && opts.layoutState && !$state.is(opts.layoutState)) {
          // clear the layout control so we can reload it
          svc.fileLayout = null;
          var unlisten = $rootScope.$on('$stateChangeSuccess', function () {
            unlisten();
            $timeout(svc.withFileLayout.bind(svc, cb, false));
          });
          // switch to the proper state for editing files if necessary
          if ($state.current && $state.current.name && !$state.transition) {
            $state.go(opts.layoutState);
          }
          return;
        }
        if (svc.fileLayout) {
          cb(svc.fileLayout);
        }
      };
      svc.openFile = function (file) {
        if (file) {
          svc.withFileLayout(function (layout) {
            layout.open(file);
          });
        }
      };
      svc.openInitialFile = function (file) {
        $timeout(function () {
          svc.openFile(file || svc.files[0]);
        }, 50);
      };

      setupLayoutLink(svc, opts);
      multiFileChallengeServiceFM.setupFileManagement(svc, opts);
      multiFileChallengeServiceInteract.setupInteract(svc);
      addChallengeEditorTools(svc, opts);
      if (!reviewMode) {
        linkVirtualFiles(svc, svc.challengeConfig);
        linkVirtualFiles(svc, svc.challengeEditorTools);
      }

      svc.refreshOpenFiles = function () {
        if (svc.openFiles) {
          // fix references to changed yet open files
          var allFiles = svc.allFiles;
          var oldOpenFiles = svc.openFiles.slice();
          svc.openFiles.length = 0;
          oldOpenFiles.forEach(function (file) {
            if (file && file.$type && file.path && !file.$deleted) {
              // verify matching files by type & ID (preferred) or path
              file = allFiles.find(function (f) {
                return f.$type === file.$type && (f.id && f.id === file.id || f.path === file.path);
              });
            }
            if (file) {
              // add unique to prevent ng-repeater errors
              addUnique(svc.openFiles, file);
            }
          });
        }
      };

      setupFileListeners(svc);

      return svc;
    }

    function setupLayoutLink(svc, opts) {
      svc.clickFile = function (file) {
        if (file && isFunction(file.clickAction)) {
          file.clickAction();
        } else if (file && file !== svc.docsArticleFile && file.docsArticleId && svc.showHelp) {
          svc.showHelp(file.docsArticleId);
        } else if (file && (!file.directory || file.directory === 'openable') && !file.disabled) {
          if (file.docsArticleId && svc.setHelpArticle) {
            svc.setHelpArticle(file.docsArticleId);
          }
          svc.openFile(file);
        }
        svc.fileTrees.forEach(function (fileTree) {
          if (fileTree && fileTree.files.includes(file)) {
            fileTree.select(file);
          }
        });
      };

      svc.getToolbarTemplate = function (file) {
        return file.toolbarTemplate || file.path && opts.toolbarTemplate;
      };
    }

    function addChallengeEditorTools(svc, opts) {
      if (!svc.runnerFrameFile) {
        svc.runnerFrameFile = {
          id: 'runnerframe',
          icon: 'transport-play',
          label: 'Run Output',
          tooltip: 'View the output from running code',
          template: opts.runnerFrameTemplate || 'runnerFrame',
          tabset: 'bottomRight',
          get initialResponse() {
            return svc.runResponse;
          },
          get hidden() {
            // web-only project challenges (previews on, candidate tests off)
            // have nothing to show here
            return svc.challenge.disableIdeFeatures || !svc.challenge.candidateTestsEnabled && !svc.webPreviewFile.hidden;
          }
        };
      }
      if (!svc.webPreviewFile) {
        svc.webPreviewFile = {
          id: 'webpreview',
          path: 'webpreview',
          icon: 'globe',
          label: 'Web Preview',
          tooltip: 'View a real-time preview of this web application',
          template: opts.webPreviewTemplate || 'webPreview',
          directory: 'openable',
          tabset: 'topRight',
          get responsiveMode() {
            return svc.challenge && svc.challenge.responsiveMode();
          },
          get uniqueKey() {
            return svc.solution ? svc.solution.id : svc.challenge.id;
          },
          get files() {
            return svc._getRunFiles(false, true);
          },
          get console() {
            return svc.webPreviewConsoleFile.console;
          },
          get hidden() {
            // simply hide this if disabled
            return !(svc.challenge && svc.challenge.webPreviews()) || svc.challenge.disableIdeFeatures;
          },
          sync: angular.noop,
          setSyncFunction: function setSyncFunction(fn) {
            svc.webPreviewFile.sync = fn || angular.noop;
          },
          openConsole: function openConsole() {
            svc.clickFile(svc.webPreviewConsoleFile);
          }
        };
        svc.webPreviewConsoleFile = {
          id: 'webpreviewconsole',
          path: 'webpreview/console',
          icon: 'lang-shell',
          label: 'Web Console',
          tooltip: 'View the console output from the web preview',
          template: opts.webPreviewConsoleTemplate || 'webPreviewConsole',
          lastTabset: 'bottomRight',
          get hidden() {
            return svc.webPreviewFile.hidden || svc.challenge.disableIdeFeatures;
          },
          console: []
        };
      }
      svc.chatAgentFile = {
        id: 'chatagent',
        icon: 'z-action-ai',
        label: 'AI Assistant',
        tooltip: 'Get help from the AI assistant',
        template: 'chatagent',
        lastTabset: 'topRight',
        defaultTabset: 'topRight',
        closeable: false
      };
      svc.ideSettingsFile = {
        id: 'idesettings',
        icon: 'cog',
        label: 'IDE Settings',
        tooltip: 'Configure how the code editor works',
        template: 'ideOpts',
        hide: {
          layout: true,
          fileMap: !svc.challenge.isAdvancedCode()
        },
        get hidden() {
          return svc.challenge.disableIdeFeatures;
        }
      };
      if (svc.challenge.disableIdeFeatures) {
        svc.challengeEditorTools = [];
      } else {
        svc.challengeEditorTools = [svc.runnerFrameFile, svc.webPreviewFile, svc.webPreviewConsoleFile, svc.ideSettingsFile].filter(angular.identity);
      }
      if (opts.enableAiAgent) {
        svc.challengeEditorTools.push(svc.chatAgentFile);
      }

      if ((opts.docsArticleId || opts.docsArticleList) && !svc.challenge.disableIdeFeatures) {
        svc.docsArticleFile = {
          id: 'help',
          icon: 'help',
          label: 'IDE Help',
          path: 'docs',
          template: 'docsPane',
          directory: opts.docsAdditionalArticles && 'openable',
          docsHomeArticle: opts.docsArticleId,
          docsArticleId: opts.docsArticleId,
          $docsArticleId: opts.docsArticleId,
          docsArticleList: opts.docsArticleList,
          docsArticleListTitle: opts.docsArticleListTitle,
          docsArticleListContent: opts.docsArticleListContent,
          defaultTabset: 'topRight'
        };
        svc.challengeEditorTools.push(svc.docsArticleFile);
        svc.setHelpArticle = function (articleId) {
          svc.docsArticleFile.$docsArticleId = articleId;
        };
        svc.showHelp = function (articleId) {
          svc.clickFile(svc.docsArticleFile);
          svc.setHelpArticle(articleId);
        };
        if (opts.docsAdditionalArticles) {
          opts.docsAdditionalArticles.forEach(function (article) {
            article.$draggable = false;
            svc.challengeEditorTools.push(article);
          });
        }
      }

      if (opts.editChallengeEditorTools) {
        opts.editChallengeEditorTools(svc);
      }
      setupRunnerFrame(svc, svc.runnerFrameFile, opts);
      setupActionsToolbar(svc);
    }

    function setupRunnerFrame(svc, runnerFrameFile, opts) {
      svc.loadRunResponse = function (response) {
        svc.runResponse = response;
        svc.withFileLayout(function (fileLayout) {
          // force the frame to refresh
          runnerFrameFile.forceRefresh = Date.now();
          fileLayout.open(runnerFrameFile);
        });
      };
      svc._getRunFiles = function () {
        var includeEncrypted = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
        var forWebPreview = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

        var files = void 0;
        if (opts.getRunFiles) {
          files = opts.getRunFiles(svc, forWebPreview);
        } else {
          files = Challenge.getRunFiles(svc.challenge, svc);
        }
        if (!includeEncrypted) {
          files = files.filter(function (f) {
            return !f.encrypted && f.$access !== 'restricted' && f.$access !== 'hidden';
          });
        }
        return files;
      };
      svc.run = function () {
        if (svc.expired) return $q.reject();

        if (svc.webPreviewFile) {
          // always re-render on run
          svc.webPreviewFile.sync();
        }

        return $q(function (resolve, reject) {
          svc.withFileLayout(function (fileLayout) {
            // for autoruns, we don't activate (focus) the run tab when web previews are visible
            var activateTab = !svc.isAutoRun || !fileLayout.find(svc.webPreviewFile);
            svc.isAutoRun = false;

            runnerFrameFile.closeable = runnerFrameFile.closeable && 'disabled';
            var delay = 0;
            if (!fileLayout.find(runnerFrameFile)) {
              delay = 2000;
            }
            fileLayout.open(runnerFrameFile, {
              activate: activateTab || delay !== 0
            });

            // restrict to the important bits
            var files = svc._getRunFiles().map(function (f) {
              return {
                path: f.path,
                contents: f.contents,
                encrypted: f.encrypted
              };
            });

            $timeout(function () {
              runnerFrameService.run(angular.extend({ privilegeMode: svc.privilegedMode || 'full' }, svc.challenge.runConfig, { files: files })).finally(function () {
                runnerFrameFile.closeable = runnerFrameFile.closeable && true;
              }).then(function (response) {
                // store in case we close and reopen the runner frame
                svc.runResponse = response;
                svc.setRunResultStatus();
                return response;
              }).then(resolve, reject);
            }, delay);
          });
        });
      };

      svc.setRunResultStatus = function () {
        var response = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : svc.runResponse;

        var status = '';
        if (response && response.result) {
          var result = svc.runResponse.result;
          var passed = Math.max(result.passed, 0) || 0;
          var errors = Math.max(result.errors, 0) || 0;
          var failed = Math.max(result.failed, 0) || 0;
          if (passed > 0 && !errors && !failed) {
            status = 'success';
          } else if (errors || !passed) {
            status = 'danger';
          } else if (failed) {
            status = 'warning';
          } // else, nothing passed, nothing failed
        }
        runnerFrameFile.status = status;
      };

      svc.setRunResultStatus();
    }

    function setupActionsToolbar(svc) {
      if (!svc.actions) {
        var canEditLayout = !svc.challenge.lockedLayout();
        svc.actions = [svc.workspaceStatusAction, canEditLayout && {
          icon: 'plus',
          toolbarTooltip: 'New Project File',
          onToolbarClick: function onToolbarClick() {
            return svc.addFile();
          }
        }, canEditLayout && { sep: true }, svc.ideSettingsFile, svc.docsArticleFile].filter(angular.identity);
      }
    }

    /**
     * linkVirtualFiles is a utility function that dynamically binds file state
     * to challenge options using property descriptors.
     * It enables persistent UI state (like tab placement and activation)
     * for special virtual files by syncing their properties (tabset, tabindex, active)
     * with a shared options object.
     * @param {*} svc
     * @param {*} fileArray
     */
    function linkVirtualFiles(svc, fileArray) {
      fileArray.forEach(function (f) {
        if (f.id) {
          ['tabset', 'tabindex', 'active'].forEach(function (field) {
            var key = (f.id + '$' + field).toLowerCase();
            // If the file has a default value for the field, set it in the options object
            if (f[field]) {
              svc.challengeOptions[key] = f[field];
            }
            Object.defineProperty(f, field, {
              get: function get() {
                return svc.challengeOptions[key];
              },
              set: function set(val) {
                svc.challengeOptions[key] = val;
              },

              enumerable: false
            });
          });
        }
      });
    }

    var restoreFocusTimer = void 0;

    function restoreOpenFiles(svc) {
      $timeout.cancel(restoreFocusTimer);
      svc.openFiles = [];

      function openWith(openFn) {
        if (svc.solution) {
          svc.solution.files.forEach(openFn);
          svc.filesModel = svc.solution;
        } else if (svc.challenge) {
          svc.filesModel = svc.challenge;
          svc.challenge.files.forEach(openFn);
          svc.challenge.referenceFiles.forEach(openFn);
        }
        svc.challengeConfig.forEach(openFn);
        svc.challengeEditorTools.forEach(openFn);
      }

      openWith(function (file) {
        if (file.tabset && !file.disabled && !file.hidden) {
          svc.openFiles.push(file);
        }
      });

      if (svc.openFiles.length === 0) {
        var focusOnFile = void 0;
        openWith(function (file) {
          if (file.defaultTabset && !file.disabled && !file.hidden) {
            file.tabset = file.defaultTabset;
            file.tabindex = file.defaultTabindex;
            file.active = file.defaultActive;
            if (file.defaultActive && (!focusOnFile || !focusOnFile.path)) {
              // focus on the most useful file first
              focusOnFile = file;
            }
            svc.openFiles.push(file);
          }
        });

        return restoreFocusTimer = $timeout(function () {
          // make sure this file is still open
          // (kinda lame, but better than dealing with the async issues)
          if (svc.openFiles.includes(focusOnFile)) {
            svc.clickFile(focusOnFile);
          }
        }, 500);
      }

      return $q.when();
    }

    /**
     * Shallow listeners are setup to track when the underlying data arrays are changed
     */
    function setupFileListeners(svc) {
      var refFilesDebounce = void 0;
      svc.refreshOnFilesChanged = function () {
        $timeout.cancel(refFilesDebounce);
        refFilesDebounce = $timeout(function () {
          svc.refreshOpenFiles();

          if (svc.solution) {
            CodeFile.configureAccessLevels(svc.solution.files);
          }

          svc.updateAfterFileChanges();

          // update the layout & file trees
          [svc.fileLayout].concat(svc.fileTrees).forEach(function (controller) {
            if (controller && controller.refresh) {
              controller.refresh();
            }
          });
        }, 50);
      };

      var oldDestroy = svc.destroy || angular.noop;
      var listeners = svc._mfcListeners = [$rootScope.$watchCollection(function () {
        return svc.openFiles;
      }, svc.refreshOnFilesChanged), $rootScope.$watchCollection(function () {
        return svc.files;
      }, svc.refreshOnFilesChanged), $rootScope.$watchCollection(function () {
        return svc.referenceFiles;
      }, svc.refreshOnFilesChanged)];
      svc.destroy = function () {
        for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }

        listeners.forEach(function (fn) {
          fn();
        });
        if (svc.off) {
          svc.off(true);
        }
        if (svc.workspace) {
          svc.workspace.destroy();
        }
        oldDestroy.apply.apply(oldDestroy, [svc].concat(args));
        svc.destroyed = true;
      };
    }

    return {
      create: create,
      restoreOpenFiles: restoreOpenFiles
    };
  }]);

  var multiFileLayoutTemplate = "<ui-layout options=\"{ flow: 'column' }\" ng-class=\"$ctrl.classes()\" remember-ui=\"$ctrl.rui.vert\"> <div class=\"topBottomLeft\" ui-layout-container min-size=\"100px\" size=\"50%\"> <ui-layout options=\"{ flow: 'row' }\" remember-ui=\"$ctrl.rui.left\"> <div class=\"info code-tabs code-lang-tabs topLeft\" ui-layout-container min-size=\"100px\" size=\"50%\"> <compile-template src=\"$ctrl.tabsetTemplate\" scope-vars=\"{ tabsetId: 'topLeft', tabset: $ctrl.tabsets.topLeft }\" ng-if=\"$ctrl.tabsets.topLeft.length\"></compile-template> <div class=\"no-files\" multi-file-drop-zone=\"topLeft\" ng-if=\"!$ctrl.tabsets.topLeft.length\"> <div> <span class=\"qualified-icon\"></span> <p class=\"drop-notification\">Drop to open the file</p> <p> Choose a file from the list, or add a file with the <span class=\"icon-plus\"></span> button. </p> <p>You can also drag in files from your computer.</p> </div> </div> </div> <div class=\"info code-tabs code-lang-tabs bottomLeft\" ui-layout-container min-size=\"100px\" ng-if=\"$ctrl.tabsets.bottomLeft.length\"> <compile-template src=\"$ctrl.tabsetTemplate\" scope-vars=\"{ tabsetId: 'bottomLeft', tabset: $ctrl.tabsets.bottomLeft }\"></compile-template> </div> <div class=\"drop-zone-placeholder\" ng-class=\"{ 'no-right': !$ctrl.tabsets.topRight.length }\" multi-file-drop-zone=\"bottomLeft\" ng-if=\"$ctrl.tabsets.topLeft.length&&!$ctrl.tabsets.bottomLeft.length\"> <span>Add Tabs Here</span> </div> </ui-layout> </div> <div class=\"drop-zone-placeholder\" multi-file-drop-zone=\"topRight\" ng-if=\"$ctrl.tabsets.topLeft.length && !$ctrl.tabsets.topRight.length\"> <span>Add Tabs Here</span> </div> <div class=\"topBottomRight\" ui-layout-container min-size=\"100px\" ng-if=\"$ctrl.tabsets.topRight.length\"> <ui-layout options=\"{ flow: 'row' }\" remember-ui=\"$ctrl.rui.right\"> <div class=\"info code-tabs code-lang-tabs topRight\" ui-layout-container min-size=\"100px\" size=\"50%\"> <compile-template src=\"$ctrl.tabsetTemplate\" scope-vars=\"{ tabsetId: 'topRight', tabset: $ctrl.tabsets.topRight }\"></compile-template> </div> <div class=\"info code-tabs code-lang-tabs bottomRight\" ui-layout-container min-size=\"100px\" ng-if=\"$ctrl.tabsets.bottomRight.length\"> <compile-template src=\"$ctrl.tabsetTemplate\" scope-vars=\"{ tabsetId: 'bottomRight', tabset: $ctrl.tabsets.bottomRight }\"></compile-template> </div> <div class=\"drop-zone-placeholder\" multi-file-drop-zone=\"bottomRight\" ng-if=\"!$ctrl.tabsets.bottomRight.length\"> <span>Add Tabs Here</span> </div> </ui-layout> </div> </ui-layout> ";

  var multiFileLayoutTabsetTemplate = "<tabset> <li class=\"multi-file-layout-tab multi-file-layout-actions\" ng-if=\"tabsetId === 'topLeft' && $ctrl.tabBeforeActions\" compile-template=\"$ctrl.tabBeforeActions\" scope-vars=\"$ctrl.scopeVars()\"></li> <tab ng-repeat=\"fileTab in tabset\" class=\"multi-file-layout-tab multi-file-layout-file {{ fileTab.tabClasses() }}\" no-highlight=\"true\" on-select=\"$ctrl.focus(fileTab)\" active=\"fileTab.active\" default-tab=\"fileTab.defaultActive\"> <tab-heading tooltip=\"{{ fileTab.tooltip ? fileTab.tooltip + ' - ' : '' }}{{$ctrl.disableTabMoving?'':'Click-and-drag to rearrange tabs'}}\"> <span multi-file-draggable-tab=\"!$ctrl.disableTabMoving && fileTab\"> <span ng-if=\"fileTab.labelTemplate\" compile-template=\"fileTab.labelTemplate\" scope-vars=\"$ctrl.scopeVars(fileTab)\"></span> <span ng-if=\"!fileTab.labelTemplate\"> <file-icon file=\"fileTab.file\"></file-icon> {{ fileTab.label }} <span ng-if=\"fileTab.file.invalid\" class=\"multi-file-layout-tab-invalid\" tooltip=\"Make sure everything is correct on this tab\"></span> </span> <button class=\"close\" aria-label=\"Close\" ng-click=\"$ctrl.close(fileTab)\" ng-if=\"fileTab.closeable()\" ng-disabled=\"fileTab.closeDisabled()\"> <span aria-hidden=\"true\">&times;</span> </button> </span> </tab-heading> <div class=\"file-tab-contents\" multi-file-contents=\"fileTab\" compile-template=\"fileTab.template\" scope-vars=\"$ctrl.scopeVars(fileTab)\"></div> </tab> <li class=\"multi-file-layout-tab multi-file-layout-actions\" ng-if=\"$ctrl.tabAfterActions && ((tabsetId === 'topRight' && $ctrl.tabsets.topRight.length) || (tabsetId === 'topLeft' && !$ctrl.tabsets.topRight.length))\" compile-template=\"$ctrl.tabAfterActions\" scope-vars=\"$ctrl.scopeVars()\"></li> </tabset> <div class=\"drop-zone-block\" multi-file-drop-zone=\"{{ tabsetId }}\"></div> ";

  var fileTabTemplateCodeEditor = "<div class=\"multi-file-layout-code-editor\" ng-class=\"{ 'has-toolbar': fileTab.toolbarTemplate }\" relink-on-change=\"fileTab.relinkOnWorkspaceChange\"> <ui-codemirror ui-codemirror-opts=\"mflCtrl.createCodeOpts(fileTab)\" ng-model=\"fileTab.file.contents\" ng-change=\"mflCtrl._onFileChange(fileTab.file, true)\" ng-readonly=\"fileTab.readOnly\" ng-disabled=\"fileTab.disabled\" document-id=\"fileTab.file.id\"></ui-codemirror> <div ng-if=\"fileTab.toolbarTemplate\" class=\"multi-file-layout-code-toolbar code\" compile-template=\"fileTab.toolbarTemplate\"></div> </div> ";

  var fileTabTemplateMarkdownEditor = "<div class=\"multi-file-layout-code-editor\" ng-class=\"{ 'has-toolbar': fileTab.toolbarTemplate }\"> <div class=\"alert alert-warning\" ng-if=\"fileTab.file.notice\"> {{fileTab.file.notice}} </div> <markdown-editor allow-dark-mode=\"true\" ng-model=\"fileTab.file.contents\" ng-change=\"mflCtrl._onFileChange(fileTab.file, true)\" placeholder=\"{{fileTab.file.placeholder}}\" stretch=\"true\" ng-focus=\"mflCtrl.select(fileTab)\" ng-readonly=\"fileTab.readOnly\" ng-disabled=\"fileTab.disabled\" id=\"mfl-markdown-{{fileTab.ftId}}\" on-cursor-activity=\"mflCtrl.onCursorActivity(fileTab, cm)\" on-paste=\"mflCtrl.handlePaste(fileTab, change)\" on-copy=\"mflCtrl.handleCopy(fileTab, copied)\"></markdown-editor> <div ng-if=\"fileTab.toolbarTemplate\" class=\"multi-file-layout-code-toolbar markdown-toolbar\" compile-template=\"fileTab.toolbarTemplate\"></div> </div> ";

  var fileTabTemplateMarkdownViewer = "<scroll-overflow-indicator allow-dark-mode> <div markdown=\"fileTab.file.contents || fileTab.file.markdown\" language=\"fileTab.file.language\" multi-language=\"fileTab.file.multiLanguage\"></div> </scroll-overflow-indicator> ";

  var fileTabTemplateImageViewer = "<div class=\"multi-file-layout-image-viewer\"> <img ng-if=\"fileTab.file.contents\" ng-src=\"{{ fileTab.file.contents }}\" alt=\"{{ fileTab.filename || fileTab.file.path || 'image' }}\"> </div> ";

  var fileTabTemplateInstructionsViewer = "<scroll-overflow-indicator allow-dark-mode> <div markdown=\"fileTab.file.challenge.instructions\" multi-language=\"true\" language=\"fileTab.file.language\"></div> <score-card-instructions challenge=\"fileTab.file.challenge\"></score-card-instructions> </scroll-overflow-indicator> ";

  var fileTabTemplateDocsPane = "<docs-pane article-list=\"fileTab.file.docsArticleList\" article-list-title=\"{{fileTab.file.docsArticleListTitle}}\" article-list-content=\"fileTab.file.docsArticleListContent\" article-id=\"fileTab.file.$docsArticleId\" home-article=\"fileTab.file.docsHomeArticle\"></docs-pane> ";

  var fileTabTemplateRunnerFrame = "<div class=\"runner-output stretch\" relink-on-change=\"fileTab.file.forceRefresh\"> <runner-frame initial-response=\"fileTab.file.initialResponse\" response=\"fileTab.file.response\" ng-if=\"!fileTab.file.disabled\" on-focus=\"mflCtrl.setFocusedTab(fileTab)\" on-ready=\"fileTab.file.setResponse = setResponse\"></runner-frame> </div> ";

  var fileTabTemplateWebPreview = "<div class=\"web-preview\"> <web-preview files=\"fileTab.file.files\" on-focus=\"mflCtrl.setFocusedTab(fileTab)\" set-sync-function=\"fileTab.file.setSyncFunction(sync)\" auto-sync=\"fileTab.file.autoSync\" responsive-mode=\"fileTab.file.responsiveMode\" ng-if=\"!fileTab.file.disabled\" console=\"fileTab.file.console\" on-console-open=\"fileTab.file.openConsole()\" disable-web-preview=\"fileTab.disableWebPreview\"></web-preview> </div> ";

  var fileTabTemplateWebPreviewConsole = "<div class=\"web-preview-console\"> <web-preview-console console=\"fileTab.file.console\"></web-preview-console> </div> ";

  var fileTabTemplateIDEOpts = "<codemirror-ide-opts hide=\"fileTab.file.hide || { layout: true }\"></codemirror-ide-opts> ";

  var fileTabTemplateChatAgent = "<div class=\"ui-modern-frame-wrapper\"> <ui-modern-frame route=\"mflCtrl.chatAgentIframeRoute(fileTab)\" scrolling=\"true\" on-focus=\"mflCtrl.setFocusedTab(fileTab)\"></ui-modern-frame> </div> ";

  var REMEMBER_UI_KEY = 'REMEMBERUI';

  var SCROLL_EVENT = 'scroll';
  var MANUAL_SCROLL_EVENTS = 'mousedown mouseup wheel mousewheel touchstart touchend';

  var rememberUIComponentModule = angular.module('Q.components.remember-ui', [storageServiceModule, scopedKeyHelperModule]).run(['$rootScope', '$localStorage', '$sessionStorage', function ($rootScope, $localStorage, $sessionStorage) {
    $rootScope.$on('app.logout', function () {
      // clear all stored settings
      delete $localStorage[REMEMBER_UI_KEY];
      delete $sessionStorage[REMEMBER_UI_KEY];
    });
  }])

  /**
   * This service is used to store and retrieve state- or location- based UI information in either
   * localStorage or sessionStorage. For example, it might store which tab was selected,
   * or the scroll position of a window.
   *
   * It's mainly intended to be used with directives to automatically restore the UI when switching
   * routes.
   */
  .factory('RememberUIService', ['$rootScope', 'scopedKey', '$localStorage', '$sessionStorage', function ($rootScope, scopedKey, $localStorage, $sessionStorage) {
    var memory = {};

    memory.perm = $localStorage[REMEMBER_UI_KEY] || {};
    $localStorage[REMEMBER_UI_KEY] = memory.perm;
    memory.session = $sessionStorage[REMEMBER_UI_KEY] || {};
    $sessionStorage[REMEMBER_UI_KEY] = memory.session;

    // re-initialize storage after login or logout
    $rootScope.$on('app.login', function () {
      $localStorage[REMEMBER_UI_KEY] = memory.perm;
      $sessionStorage[REMEMBER_UI_KEY] = memory.session;
    });
    $rootScope.$on('app.logout', function () {
      memory.perm = {};
      memory.session = {};
    });

    return {
      retrieve: retrieve,
      store: store,
      forget: forget,
      getOpts: getOpts
    };

    //// public functions

    /**
     * Returns the current remembered UI settings
     *
     * Options are used to determine where the value is stored:
     *
     * - id: a unique id for the item
     * - scope: what scope is this stored on?
     *
     * @param opts
     * @returns {*}
     */
    function retrieve(opts) {
      opts = getOpts(opts);
      var val = opts.store[opts.key];
      if (val === undefined && opts.default) {
        val = opts.default;
      }
      return val;
    }

    function store(val, opts) {
      opts = getOpts(opts);
      opts.store[opts.key] = val;
    }

    function forget(opts) {
      opts = getOpts(opts);
      delete opts.store[opts.key];
    }

    //// private functions

    function getOpts(opts) {
      opts = opts || '';

      if (angular.isString(opts)) opts = { id: opts };

      opts = angular.extend({
        id: '',
        scope: 'state',
        session: false
      }, opts);

      opts.key = scopedKey(opts.scope);
      opts.key += '(' + opts.id + ')';
      opts.store = opts.session ? memory.session : memory.perm;

      return opts;
    }
  }]).factory('RememberUIWindowScroll', ['RememberUIService', '$rootScope', '$window', '$timeout', '$state', 'scopedKey', function (RememberUIService, $rootScope, $window, $timeout, $state, scopedKey) {
    var opts = {
      id: 'WINDOWSCROLL',
      default: 0,
      scope: function scope() {
        // allow a route to override the scoped key for scrolling
        var rememberScrollKey = $state.current && $state.current.data.rememberScrollKey;
        return rememberScrollKey ? scopedKey.activeState(rememberScrollKey) : scopedKey.activeState('url');
      },

      session: true
    };

    var initialized = false;
    var active = false;

    function enable() {
      active = true;

      if (!initialized) {
        var init = RememberUIService.retrieve(opts);
        var $windowEl = angular.element($window);
        var ignoreChanges = true;
        // track pop state to ensure we only restore scroll on back/forward or reload
        // defaults to true to handle page reloading
        var didPopState = true;

        var storeScrollPos = function storeScrollPos() {
          if (active && !ignoreChanges) {
            $rootScope.$applyAsync(function () {
              return RememberUIService.store($windowEl.scrollTop(), opts);
            });
          }
        };

        var setScrollPos = function setScrollPos(keepIgnoring) {
          //noinspection JSUnusedAssignment
          ignoreChanges = true;
          $windowEl.scrollTop(didPopState ? RememberUIService.retrieve(opts) : 0);
          ignoreChanges = !!keepIgnoring;
          if (!didPopState) {
            storeScrollPos();
          }
        };

        if (init !== undefined) {
          setScrollPos(true);
          $timeout(setScrollPos, 50);
        }

        $windowEl.on(SCROLL_EVENT, storeScrollPos);

        $rootScope.$on('$stateChangeStart', function () {
          if (active) {
            ignoreChanges = true;
            $windowEl.scrollTop(0);
          }
        });
        $windowEl.on('popstate', function () {
          if (active) {
            didPopState = true;
          }
        });
        $rootScope.$on('$stateChangeSuccess', function () {
          if (active && !$rootScope.stateHelperRedirecting) {
            $timeout(function () {
              setScrollPos();
              didPopState = false;
            }, 100);
          }
        });

        ignoreChanges = false;

        initialized = true;
      }
    }

    function disable() {
      active = false;
    }

    function forget() {
      RememberUIService.forget(opts);
    }

    return {
      enable: enable,
      disable: disable,
      forget: forget
    };
  }])

  /**
   * Provides a controller for other directives to latch onto for remembering the UI configuration.
   *
   * It is recommended that you provide at least an ID attribute on the node, or pass an ID into the `remember-ui`
   * attribute.
   *
   * Usage: <any remember-ui="{options}|'id'">
   *
   * Options:
   *
   * - if nothing is passed in, the node's ID attribute will be used as the storage id, and if that fails,
   *   the node's nodeType will be used instead (not recommended).
   *
   * - if just passed in a string, use that for id, and use the default options:
   *     - scope: 'state'
   *     - session: false
   *
   * - if passed in an object, this will be used to determine the following settings:
   *     - id: unique ID for the node
   *     - scope: what scope is this stored on?
   *          'state' = state id (default)
   *          Array = use state id followed by specified $stateParams
   *          'url' = full url without the hash
   *          function = return a string of your choosing, options are passed back in
   *          anything else = converted to string, used as is
   *     - session: set to true to only store this value for the given session. By default, all settings
   *          are put on localStorage, and only cleared on app logout.
   */
  .directive('rememberUi', function () {
    return {
      // make sure it runs before normal directives
      priority: 100,
      restrict: 'A',
      controller: 'RememberUICtrl',
      compile: function compile() {
        return {
          pre: function pre(scope, elem, attrs, ctrl) {
            ctrl.init(elem, attrs, scope.$eval(attrs.rememberUi));
          }
        };
      }
    };
  })

  /**
   * Directive that enables storing arbitrary scope data in a RememberUI key.
   */
  .directive('rememberUiData', ['$parse', function ($parse) {
    return {
      restrict: 'A',
      require: 'rememberUi',
      link: function link(scope, elem, attr, rememberUiCtrl) {
        var scopeProperty = attr.rememberUiData || scope.$eval(attr.rememberUi);
        var oldVal = rememberUiCtrl.retrieve();
        if (oldVal) {
          var v = $parse(scopeProperty);
          v.assign(scope, oldVal);
        }
        scope.$watch(scopeProperty, function (newData) {
          rememberUiCtrl.store(newData);
        });
      }
    };
  }])

  /**
   * Directive to remember the scroll position of a node.
   *
   * Unlike the default remember-ui directive, this one changes two defaults:
   *
   * - scope is 'url': this means that scrolling is remembered for the _exact_ URL by default
   * - session is true: we forget scroll position if the user leaves the page and comes back later, much like
   *     the browser's default scroll memory
   *
   */
  .directive('rememberUiScroll', ['$timeout', '$state', 'scopedKey', '$window', '$rootScope', function ($timeout, $state, scopedKey, $window, $rootScope) {
    return {
      restrict: 'A',
      controller: 'RememberUICtrl',
      compile: function compile() {
        return {
          pre: function pre(scope, elem, attrs, ctrl) {
            var opts = scope.$eval(attrs.rememberUiScroll);
            if (!opts || angular.isString(opts)) {
              opts = { id: opts };
            }

            opts = angular.extend({
              default: 0,
              scope: function scope() {
                // allow a route to override the scoped key for scrolling
                var rememberScrollKey = $state.current && $state.current.data.rememberScrollKey;
                return rememberScrollKey ? scopedKey.activeState(rememberScrollKey) : scopedKey.activeState('url');
              },

              session: true
            }, opts);

            ctrl.init(elem, attrs, opts);
          },
          post: function post(scope, elem, attrs, ctrl) {
            var init = ctrl.retrieve();
            var lastKey = null;
            var ignoreChanges = true;
            // track pop state to ensure we only restore scroll on back/forward or reload
            // defaults to true to handle page reloading
            var didPopState = true;
            var scrolledWhileLoading = false;

            var storeScrollPos = function storeScrollPos() {
              if (!ignoreChanges) {
                ctrl.store(elem.scrollTop());
              }
            };

            var loadingScrollListener = function loadingScrollListener() {
              if (ignoreChanges && scrolledWhileLoading !== true) {
                var top = elem.scrollTop();
                var maxTop = top - elem.offsetHeight();
                if (top !== scrolledWhileLoading && scrolledWhileLoading <= maxTop) {
                  scrolledWhileLoading = true;
                }
              }
            };

            var restoreScrollPos = function restoreScrollPos(keepIgnoring) {
              ignoreChanges = true;
              elem[0].scrollTop = didPopState ? ctrl.retrieve() : 0;
              ignoreChanges = !!keepIgnoring;
              if (!didPopState) {
                storeScrollPos();
              }
            };

            if (init !== undefined) {
              restoreScrollPos(true);
              $timeout(restoreScrollPos, 50);
            }

            elem.on(SCROLL_EVENT, storeScrollPos);
            elem.on(MANUAL_SCROLL_EVENTS, loadingScrollListener);
            scope.$on('$stateChangeStart', function () {
              if (lastKey === ctrl.getOpts().scope) return;
              ignoreChanges = true;
              scrolledWhileLoading = elem[0].scrollTop;
              $timeout(function () {
                if (scrolledWhileLoading !== true) {
                  scrolledWhileLoading = elem[0].scrollTop = didPopState ? ctrl.retrieve() : 0;
                }
              }, 1, false);
            });
            var windowPopStateListener = function windowPopStateListener() {
              didPopState = true;
            };
            angular.element($window).on('popstate', windowPopStateListener);
            scope.$on('$stateChangeSuccess', function () {
              if (!$rootScope.stateHelperRedirecting) {
                var key = scopedKey.activeState(ctrl.getOpts().scope);
                if (lastKey === key) return;
                lastKey = key;
                $timeout(function () {
                  if (scrolledWhileLoading === true) {
                    ignoreChanges = false;
                    storeScrollPos();
                  } else {
                    restoreScrollPos();
                  }
                  scrolledWhileLoading = false;
                  didPopState = false;
                }, 100, false);
              }
            });

            ignoreChanges = false;

            scope.$on('$destroy', function () {
              elem.off(SCROLL_EVENT, storeScrollPos);
              elem.off(MANUAL_SCROLL_EVENTS, loadingScrollListener);
              angular.element($window).off('popstate', windowPopStateListener);
            });
          }
        };
      }
    };
  }]).directive('disableUiScroll', ['RememberUIWindowScroll', function (RememberUIWindowScroll) {
    return {
      restrict: 'A',
      require: '^?rememberUiScroll',
      link: function link(scope, elem, attr, rememberUiScrollCtrl) {
        var forget = function forget(val) {
          if (val === 'true') {
            RememberUIWindowScroll.forget();
            if (rememberUiScrollCtrl) {
              rememberUiScrollCtrl.forget();
            }
          }
        };
        attr.$observe('disableUiScroll', forget);
        forget(attr.disableUiScroll);
      }
    };
  }])

  /**
   * Shared controller for remember-ui and remember-ui-scroll
   */
  .controller('RememberUICtrl', ['RememberUIService', function (RememberUIService) {
    var ctrl = this;
    var _opts = void 0;

    ctrl.init = function (elem, attr, opts) {
      if (opts === false) return;
      _opts = opts || {};
      if (angular.isString(opts)) {
        _opts = { id: opts };
      }
      _opts.id = _opts.id || attr.id || elem[0].nodeName.toLowerCase();
    };

    ctrl.retrieve = function () {
      return _opts && RememberUIService.retrieve(_opts);
    };

    ctrl.store = function (val) {
      return _opts && RememberUIService.store(val, _opts);
    };

    ctrl.forget = function () {
      return _opts && RememberUIService.forget(_opts);
    };
    ctrl.getOpts = function () {
      return _opts && RememberUIService.getOpts(_opts);
    };
  }]);

  var scopeHelperModule = angular.module('Q.helpers.scope-helper', []).factory('scopeHelper', ['$rootScope', '$timeout', function ($rootScope, $timeout) {
    return {
      wrapCallback: function wrapCallback(fn) {
        var scope = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : $rootScope;

        return function () {
          for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
            args[_key] = arguments[_key];
          }

          scope.$applyAsync(function () {
            return fn.apply(undefined, args);
          });
        };
      },
      oncePerDigest: function oncePerDigest(callback) {
        var scope = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : $rootScope;

        var unwatch = void 0;

        function listenOncePerDigest() {
          if (unwatch || unwatch === false) return;
          unwatch = scope.$watch(function () {
            unwatch();
            unwatch = null;
            callback();
            $timeout(listenOncePerDigest, 0, false);
          });
        }

        listenOncePerDigest();

        return function stop() {
          if (unwatch) unwatch();
          unwatch = false;
        };
      }
    };
  }]);

  var tabTemplate = "<li ng-class=\"{active: tabCtrl.active, disabled: tabCtrl.disabled, 'no-highlight': tabCtrl.noHighlight}\"> <a ng-click=\"tabCtrl.select($event)\" tabindex=\"0\" tab-heading-transclude>{{ tabCtrl.heading }}</a> </li> ";

  var tabSetTemplate = "<div class=\"tabset\"> <div class=\"tab-scroller\" ng-class=\"{ scrollable: tabsetCtrl.scrollable, 'should-scroll': tabsetCtrl.shouldScroll(), 'has-left': tabsetCtrl.hasLeft(), 'has-right': tabsetCtrl.hasRight() }\" msd-wheel=\"tabsetCtrl.scrollWheel($event, $delta, $deltaX, $deltaY)\"> <a class=\"tab-scroller-left\" tabindex=\"0\" ng-click=\"tabsetCtrl.scrollLeft($event)\" ng-show=\"tabsetCtrl.hasLeft()\"></a> <div class=\"tab-container\"> <ul class=\"nav nav-{{tabsetCtrl.type || 'tabs'}}\" ng-style=\"{ left: tabsetCtrl.getScrollOffset() }\" ng-class=\"{'nav-stacked': tabsetCtrl.vertical, 'nav-justified': tabsetCtrl.justified}\" ng-transclude></ul> </div> <a class=\"tab-scroller-right\" tabindex=\"0\" ng-click=\"tabsetCtrl.scrollRight($event)\" ng-show=\"tabsetCtrl.hasRight()\"></a> </div> <div class=\"tab-content\"> <div class=\"tab-pane {{ tab.classes }}\" ng-repeat=\"tab in tabsetCtrl.tabs\" ng-class=\"{active: tab.active}\" tab-content-transclude=\"tab\"></div> </div> </div> ";

  var mousewheelComponentModule = 'monospaced.mousewheel';

  var SCROLLER_BUTTON_WIDTH = 25;

  var TABSET_EVT_SELECT = 'tabset.select';

  /*
   * Heavily modified replacement for ui-bootstrap tabs
   */

  var tabsComponentModule = angular.module('Q.components.tabs', [rememberUIComponentModule, mousewheelComponentModule, scopeHelperModule, windowEventHelperModule, keycodesModule]).controller('TabsetCtrl', ['$scope', '$element', '$timeout', 'scopeHelper', function TabsetCtrl($scope, $element, $timeout, scopeHelper) {
    var ctrl = this;
    ctrl.$onInit = function () {
      var tabs = ctrl.tabs = [];
      var outerScroller = $element[0].querySelector('.tab-container');
      var innerScroller = $element[0].querySelector('.tab-container > ul');
      var scrollOffset = 0;
      var destroyed = void 0;

      if (ctrl.scrollable === undefined) ctrl.scrollable = true;

      ctrl.select = function (selectedTab) {
        angular.forEach(tabs, function (tab) {
          if (tab.active && tab !== selectedTab) {
            tab.active = false;
            tab.onDeselect();
          }
        });
        selectedTab.active = true;
        selectedTab.onSelect();
        $timeout(function () {
          ctrl.scrollToTab(selectedTab);
          $scope.$broadcast(TABSET_EVT_SELECT, selectedTab);
        }, 10);
      };

      ctrl.addTab = function addTab(tab) {
        tabs.push(tab);
        // we can't run the select function on the first tab
        // since that would select it twice
        if (tabs.length === 1) {
          tab.active = true;
        } else if (tab.active || tab.defaultTab) {
          ctrl.select(tab);
        }
      };

      ctrl.removeTab = function removeTab(tab) {
        var index = tabs.indexOf(tab);
        //Select a new tab if the tab to be removed is selected and not destroyed
        if (tabs.length > 1 && !destroyed) {
          if (tab.active) {
            // If this is the last tab, select the previous tab. else, the next tab.
            var newActiveIndex = index === tabs.length - 1 ? index - 1 : index + 1;
            ctrl.select(tabs[newActiveIndex]);
          } else {
            // just refresh the layout
            _resizeDebounce();
          }
        }
        tabs.splice(index, 1);
      };

      //// SCROLLING

      var maxScroll = 0;

      function _setMaxScroll() {
        maxScroll = innerScroller.offsetWidth - outerScroller.offsetWidth;
      }

      // this method is designed to only update once at the beginning of each digest cycle, to
      // prevent infinite digest errors
      scopeHelper.oncePerDigest(_setMaxScroll, $scope);

      function _isVisible(el) {
        return !angular.element(el).isHidden();
      }

      ctrl.scrollToTab = function (tab) {
        _setMaxScroll();
        var el = tab.element || (angular.isNumber(tab) ? innerScroller.children[tab] : tab);
        var elLeft = el.offsetLeft;
        var elRight = elLeft + el.offsetWidth;

        // ensure right is on-screen
        var scrollRight = scrollOffset + outerScroller.offsetWidth;
        if (scrollOffset < maxScroll) {
          scrollRight -= SCROLLER_BUTTON_WIDTH;
        }
        if (elRight > scrollRight) {
          scrollOffset += elRight - scrollRight;
        }

        // ensure left is on-screen
        var scrollLeft = scrollOffset;
        if (scrollOffset > 0) {
          scrollLeft += SCROLLER_BUTTON_WIDTH;
        }
        if (elLeft < scrollLeft) {
          scrollOffset = elLeft - SCROLLER_BUTTON_WIDTH;
        }

        // catch scrolling too far on resize events
        scrollOffset = Math.max(0, Math.min(maxScroll, scrollOffset));
      };
      ctrl.shouldScroll = function () {
        if (ctrl.scrollable) {
          return maxScroll > 0;
        } else {
          return false;
        }
      };

      ctrl.getScrollOffset = function () {
        return -scrollOffset + 'px';
      };
      ctrl.hasLeft = function () {
        return scrollOffset > 0;
      };
      ctrl.scrollLeft = function ($event) {
        if (ctrl.hasLeft()) {
          $event.preventDefault();
          for (var i = innerScroller.children.length - 1; i >= 0; i--) {
            var child = innerScroller.children[i];
            if (_isVisible(child) && innerScroller.children[i].offsetLeft < scrollOffset) {
              ctrl.scrollToTab(child);
              break;
            }
          }
        }
      };
      ctrl.hasRight = function () {
        return scrollOffset < maxScroll;
      };
      ctrl.scrollRight = function ($event) {
        if (ctrl.hasRight()) {
          $event.preventDefault();
          var scrollRight = scrollOffset + outerScroller.offsetWidth;
          for (var i = 0; i < innerScroller.children.length; i++) {
            var child = innerScroller.children[i];
            if (_isVisible(child) && child.offsetLeft + child.offsetWidth > scrollRight) {
              ctrl.scrollToTab(child);
              break;
            }
          }
        }
      };
      ctrl.scrollWheel = function ($event, $delta) {
        if ($delta && ctrl.shouldScroll()) {
          $event.preventDefault();
          scrollOffset = Math.max(0, Math.min(maxScroll, scrollOffset - 20 * $delta));
        }
      };

      var _resizePromise = void 0;

      function _resize() {
        _setMaxScroll();
        for (var i = 0; i < tabs.length; i++) {
          if (tabs[i].active) {
            ctrl.scrollToTab(tabs[i]);
            break;
          }
        }
      }

      function _resizeDebounce() {
        if (_resizePromise) return;
        // delay next resize for at least 250ms
        _resizePromise = $timeout(function () {
          _resizePromise = null;
          _resize();
        }, 250);
      }

      $scope.$on(WINDOW_EVT_RESIZE, _resizeDebounce);
      $scope.$on(UI_LAYOUT_EVT_RESIZE, _resizeDebounce);

      $scope.$on('$destroy', function () {
        destroyed = true;
      });
    };
  }]).directive('tabset', ['$timeout', '$window', function ($timeout, $window) {
    return {
      require: ['tabset', '?rememberUi'],
      restrict: 'EA',
      transclude: true,
      replace: true,
      scope: {
        type: '@',
        scrollable: '=?'
      },
      controller: 'TabsetCtrl as tabsetCtrl',
      bindToController: true,
      template: tabSetTemplate,
      link: function link(scope, element, attrs, _ref) {
        var _ref2 = slicedToArray(_ref, 2),
            tabsetCtrl = _ref2[0],
            rememberUi = _ref2[1];

        tabsetCtrl.vertical = angular.isDefined(attrs.vertical) ? scope.$parent.$eval(attrs.vertical) : false;
        tabsetCtrl.justified = angular.isDefined(attrs.justified) ? scope.$parent.$eval(attrs.justified) : false;

        if (rememberUi) {
          var defaultTab = rememberUi.retrieve();
          // need to delay one cycle for ng-repeated tabs
          $timeout(function () {
            if (defaultTab && tabsetCtrl.tabs[defaultTab]) {
              tabsetCtrl.select(tabsetCtrl.tabs[defaultTab]);
            }
          });
          scope.$on(TABSET_EVT_SELECT, function () {
            var which = -1;
            angular.forEach(tabsetCtrl.tabs, function (tab, i) {
              if (tab.active) {
                which = i;
              }
            });
            if (which > -1) {
              rememberUi.store(which);
            } else {
              rememberUi.forget();
            }
          });
        }

        // enable animations after the initial digest
        $window.requestAnimationFrame(function () {
          return element[0].querySelector('.tab-scroller').className += ' init';
        });
      }
    };
  }]).controller('TabCtrl', ['$element', function TabCtrl($element) {
    var tabCtrl = this;

    tabCtrl.disabled = false;
    tabCtrl.active = false;
    tabCtrl.element = $element[0];

    tabCtrl.select = function ($event) {
      stopEvent($event);
      if (!tabCtrl.disabled) {
        tabCtrl.onSelect();
        tabCtrl.active = true;
      }
    };
  }]).directive('tab', ['$parse', 'KeyCodes', function ($parse, KeyCodes) {
    return {
      require: ['tab', '^tabset'],
      restrict: 'EA',
      replace: true,
      template: tabTemplate,
      transclude: true,
      scope: {
        defaultTab: '=?',
        active: '=?',
        heading: '@',
        onSelect: '&',
        onDeselect: '&',
        noHighlight: '='
      },
      controller: 'TabCtrl as tabCtrl',
      bindToController: true,
      link: function postLink(scope, elm, attrs, _ref3, transclude) {
        var _ref4 = slicedToArray(_ref3, 2),
            tabCtrl = _ref4[0],
            tabsetCtrl = _ref4[1];

        tabCtrl.classes = elm.attr('class');

        scope.$watch('tabCtrl.active', function (active) {
          if (active) {
            tabsetCtrl.select(tabCtrl);
          }
        });

        elm.querySelector('li > a').on('keydown', function (event) {
          if (event.which === KeyCodes.ENTER || event.which === KeyCodes.SPACE) {
            tabCtrl.select(event);
          }
        });

        tabCtrl.disabled = false;
        if (attrs.disabled) {
          scope.$parent.$watch($parse(attrs.disabled), function (value) {
            tabCtrl.disabled = !!value;
          });
        }

        tabsetCtrl.addTab(tabCtrl);
        scope.$on('$destroy', function () {
          tabsetCtrl.removeTab(tabCtrl);
        });

        //We need to transclude later, once the content container is ready.
        //when this link happens, we're inside a tab heading.
        tabCtrl.$transclude = function (fn) {
          transclude(scope.$parent, fn);
        };
      }
    };
  }]).directive('tabHeadingTransclude', function () {
    return {
      restrict: 'A',
      require: '^tab',
      link: function link(scope, elm) {
        scope.$watch('tabCtrl.headingElement', function updateHeadingElement(heading) {
          if (heading) {
            elm.html('');
            elm.append(heading);
          }
        });
      }
    };
  }).directive('tabContentTransclude', function () {
    return {
      restrict: 'A',
      require: '^tabset',
      link: function link(scope, elm, attrs) {
        var tab = scope.$eval(attrs.tabContentTransclude);
        scope.tabCtrl = tab;

        // Now our tab is ready to be transcluded: both the tab heading area
        // and the tab content area are loaded.  Transclude 'em both.
        tab.$transclude(function (contents) {
          angular.forEach(contents, function (node) {
            if (isTabHeading(node)) {
              //Let tabHeadingTransclude know.
              tab.headingElement = node;
            } else {
              elm.append(node);
            }
          });
        });
      }
    };

    function isTabHeading(node) {
      return node.tagName && (node.hasAttribute('tab-heading') || node.hasAttribute('data-tab-heading') || node.tagName.toLowerCase() === 'tab-heading' || node.tagName.toLowerCase() === 'data-tab-heading');
    }
  });

  /* eslint-disable angular/timeout-service */

  var FULLSCREEN_EVT_CHANGE = 'fullscreen.change';
  var FULLSCREEN_EVT_ENTER = 'fullscreen.enter';
  var FULLSCREEN_EVT_EXIT = 'fullscreen.exit';
  var FULLSCREEN_EVT_ESCAPE = 'fullscreen.escape';

  var fullscreenToggleComponentModule = angular.module('Q.components.fullscreen-toggle', [keycodesModule]).directive('fullscreenToggle', ['$document', '$window', '$timeout', '$rootScope', 'KeyCodes', function ($document, $window, $timeout, $rootScope, KeyCodes) {
    var BODY_FULLSCREEN_CLASS = 'fullscreen-active';
    var FULLSCREEN_CLASS = 'fullscreen-element';
    var activeElements = [];

    function isActiveElement(el) {
      return activeElements[0] && activeElements[0][0] === el[0];
    }

    function resize(isFullScreen) {
      $window.setTimeout(function () {
        angular.element($window).triggerHandler('resize');
      });
      $timeout(function () {
        $rootScope.$broadcast(FULLSCREEN_EVT_CHANGE, isFullScreen);
        $rootScope.$broadcast(isFullScreen ? FULLSCREEN_EVT_ENTER : FULLSCREEN_EVT_EXIT, isFullScreen);
      });
    }

    function escapeAll($event) {
      if (activeElements.length && $event.which === KeyCodes.ESCAPE) {
        $rootScope.$broadcast(FULLSCREEN_EVT_ESCAPE);
      }
    }

    var escapeLinked = false;

    return {
      restrict: 'E',
      replace: true,
      scope: {
        target: '@',
        active: '=?'
      },
      template: '<a class="fullscreen-toggle" ng-class="{active:active}" tooltip="Toggle Full Screen" ng-click="toggleState()"><span class="icon"></span><span class="text-hide">Full Screen</span></a>',
      link: function link(scope) {
        scope.active = !!scope.active;

        var getEl = function getEl() {
          return angular.element($document[0].getElementById(scope.target));
        };

        function enterFullScreen() {
          var el = getEl();
          if (!escapeLinked) {
            $document.on('keydown', escapeAll);
            escapeLinked = true;
          }

          if (!isActiveElement(el)) {
            if (activeElements.length) {
              activeElements[0].removeClass(FULLSCREEN_CLASS);
            }
            activeElements.unshift(el);
          }
          el.addClass(FULLSCREEN_CLASS);
          $document.find('body').addClass(BODY_FULLSCREEN_CLASS);

          resize(true);
        }

        function exitFullScreen() {
          var el = getEl();
          if (activeElements.length && isActiveElement(el)) {
            activeElements.shift();
          }
          el.removeClass(FULLSCREEN_CLASS);
          if (activeElements.length) {
            activeElements[0].addClass(FULLSCREEN_CLASS);
          } else {
            // in a non-angular timeout to prevent $digest errors (seems to be related to the window.onResize event)
            $document.find('body').removeClass(BODY_FULLSCREEN_CLASS);
          }

          resize(false);

          if (activeElements.length === 0) {
            $document.off('keydown', escapeAll);
            escapeLinked = false;
          }
        }

        function _updateState() {
          if (scope.active) {
            enterFullScreen();
          } else {
            exitFullScreen();
          }
        }

        if (scope.active) {
          enterFullScreen();
        }

        scope.toggleState = function () {
          scope.active = !scope.active;
        };

        scope.$watch('active', function (newValue, oldValue) {
          if (oldValue !== newValue) {
            _updateState();
          }
        });

        scope.$on(FULLSCREEN_EVT_CHANGE, function () {
          scope.$applyAsync(function () {
            return scope.active = getEl().hasClass(FULLSCREEN_CLASS);
          });
        });

        scope.$on(FULLSCREEN_EVT_ESCAPE, function () {
          scope.$applyAsync(function () {
            return scope.active = false;
          });
        });
      }
    };
  }]);

  function trackLineWidgets(cm, matcher, createWidget) {
    var lineWidgets = [];
    var timer = void 0;
    if (matcher instanceof RegExp) {
      matcher = matcher.test.bind(matcher);
    }

    function update() {
      var _lineWidgets = lineWidgets;
      lineWidgets = [];
      var lineWidgetLines = _lineWidgets.map(function (lw) {
        return cm.getLineNumber(lw.widget.line);
      });
      cm.eachLine(function (lineHandle) {
        var lineNumber = cm.getLineNumber(lineHandle);
        if (matcher(lineHandle.text)) {
          var widgetIdx = lineWidgetLines.indexOf(lineNumber);
          var previous = _lineWidgets[widgetIdx];

          var _ref = createWidget(lineHandle.text, previous, lineNumber) || {},
              keep = _ref.keep,
              element = _ref.element,
              options = _ref.options,
              id = _ref.id;

          if (keep) {
            lineWidgets.push(previous);
            _lineWidgets[widgetIdx] = null;
          } else {
            if (element) {
              var widget = cm.addLineWidget(lineNumber, element, options);
              lineWidgets.push({ widget: widget, element: element, id: id });
            }
          }
        }
      });
      // remove any remaining line widgets, as they didn't get reused
      _lineWidgets.forEach(function (w) {
        return w && w.widget.clear();
      });
    }

    update();

    cm.on('change', function () {
      if (timer) {
        // eslint-disable-next-line angular/timeout-service
        clearTimeout(timer);
      }
      // eslint-disable-next-line angular/timeout-service
      timer = setTimeout(update, 500);
    });

    return update;
  }

  var codemirrorComponentModule = angular.module('Q.components.codemirror', [accountServiceModule, fullscreenToggleComponentModule, codemirrorOptionsComponentModule]).constant('uiCodemirrorConfig', {})

  /**
   * @category directive
   *
   * @ngdoc directive
   *
   * @name uiCodemirror
   * @restrict E
   *
   * @description
   * Renders a CodeMirror editor on a node.
   *
   * @param uiCodemirrorOpts {expression} Provide options to the CodeMirror instance
   * @param uiRefresh {expression=} Refresh the CodeMirror instance if this property changes
   * @param autofocus {boolean=} If truthy and this editor is visible, focus in this editor after creation
   * @param placeholder {template=} Enables adding placeholder text to the CodeMirror, which can be multiline if you use &#10; for newlines
   * @param documentId {template=} If provided, should be a unique ID to track cursor position across pages
   * @param codemirrorLabel {template=} Render a label for this CodeMirror
   * @param codemirrorTestFramework {expression=} Render the test framework used in this CodeMirror
   * @param codemirrorOnTestFrameworkClick {function=} Action to evaluate when clicking on the test framework label
   *
   */

  .directive('uiCodemirror', ['$window', '$timeout', 'uiCodemirrorConfig', '$q', function uiCodemirrorDirective($window, $timeout, uiCodemirrorConfig, $q) {
    var documentIdData = {};

    return {
      restrict: 'E',
      require: ['uiCodemirror', '?ngModel', '^?formGroup'],
      controller: ['$scope', '$element', function UiCodemirrorCtrl($scope, $element) {
        var ctrl = this;
        var readyPromise = $q.defer();

        ctrl.$onFocusChange = angular.noop;
        ctrl.whenReady = function () {
          return readyPromise.promise;
        };
        ctrl.fileInfo = null; // Store file info for code selection context

        ctrl.init = function (codemirror) {
          ctrl.codemirror = codemirror;
          readyPromise.resolve(codemirror);
          codemirror.on('focus', function () {
            ctrl.hasFocus = true;
            $element.addClass('focus');
            ctrl.$onFocusChange(true);
            if ($element.attr('ng-focus')) {
              $scope.$eval($element.attr('ng-focus'));
            }
          });
          codemirror.on('blur', function () {
            ctrl.hasFocus = false;
            $element.removeClass('focus');
            ctrl.$onFocusChange(false);
            if ($element.attr('ng-blur')) {
              $scope.$eval($element.attr('ng-blur'));
            }
          });
          codemirror.on('copy', function (cm, e) {
            if ($scope.disabledOrReadonly) {
              // When disabled, we disable codemirror's copy function so we can ensure the
              // browser is able to copy
              e.codemirrorIgnore = true;
            }
          });

          // Feature 8: Code Selection Context for AI Chat
          // Track code selection but don't send automatically
          // Instead, wait for user to trigger via keyboard shortcut or context menu

          ctrl.currentSelection = null;

          codemirror.on('cursorActivity', function (cm) {
            var selection = cm.getSelection();
            var from = cm.getCursor('from');
            var to = cm.getCursor('to');

            // Check if there's an actual selection (not just cursor position)
            var hasSelection = selection && selection.length > 0 && (from.line !== to.line || from.ch !== to.ch);

            if (hasSelection) {
              // Store current selection - use ctrl.fileInfo which is set in postLink
              ctrl.currentSelection = {
                selectedCode: selection,
                startLine: from.line + 1, // CodeMirror is 0-indexed
                endLine: to.line + 1,
                filePath: ctrl.fileInfo ? ctrl.fileInfo.path : 'unknown',
                language: ctrl.fileInfo ? ctrl.fileInfo.language : 'text',
                timestamp: Date.now()
              };
            } else {
              // Selection cleared
              ctrl.currentSelection = null;
            }
          });

          // Keyboard shortcut: Cmd/Win + L to add selection to chat
          codemirror.on('keydown', function (cm, event) {
            var isMac = /Mac/.test(navigator.platform);
            var modifierKey = isMac ? event.metaKey : event.ctrlKey;

            // Check for Cmd/Win + L (keyCode 76 for 'L')
            if (modifierKey && (event.key === 'l' || event.key === 'L' || event.keyCode === 76)) {
              // ALWAYS prevent default to stop browser from opening URL bar
              event.preventDefault();
              event.stopPropagation();

              if (ctrl.currentSelection) {
                // Send selection to chat
                $scope.$emit('codemirror:add-to-chat', ctrl.currentSelection);
              }
              return false; // Extra safety to prevent default
            }
          });
        };
      }],
      link: postLink
    };

    /**
     * Configure context menu for adding code selection to AI chat
     * Only shows "Add to Chat" option when:
     * - Code is selected
     * - AI chat feature is enabled
     * - Chat agent tab is available
     */
    function configureContextMenu(codemirror, scope, elem, ctrl) {
      var contextMenu = null;

      // Create context menu element
      function createContextMenu() {
        var menu = document.createElement('div');
        menu.className = 'codemirror-context-menu';
        menu.style.cssText = '\n            position: absolute;\n            background: white;\n            border: 1px solid #ccc;\n            border-radius: 4px;\n            box-shadow: 0 2px 8px rgba(0,0,0,0.15);\n            padding: 4px 0;\n            z-index: 10000;\n            display: none;\n          ';

        var menuItem = document.createElement('div');
        menuItem.className = 'context-menu-item';
        menuItem.innerHTML = '\n            <i class="z-action-ai" style="margin-right: 8px;"></i>\n            Add to Chat\n            <span style="float: right; margin-left: 20px; color: #999; font-size: 11px;">\n              ' + (/Mac/.test(navigator.platform) ? '⌘L' : 'Ctrl+L') + '\n            </span>\n          ';
        menuItem.style.cssText = '\n            padding: 8px 16px;\n            cursor: pointer;\n            user-select: none;\n            display: flex;\n            align-items: center;\n          ';

        menuItem.addEventListener('mouseenter', function () {
          this.style.background = '#f0f0f0';
        });

        menuItem.addEventListener('mouseleave', function () {
          this.style.background = 'white';
        });

        menuItem.addEventListener('click', function () {
          if (ctrl.currentSelection) {
            scope.$emit('codemirror:add-to-chat', ctrl.currentSelection);
          }
          hideContextMenu();
        });

        menu.appendChild(menuItem);
        document.body.appendChild(menu);
        return menu;
      }

      function showContextMenu(event) {
        // Only show if there's a selection
        if (!ctrl.currentSelection) {
          return;
        }

        // Check if AI chat is available by looking for chat agent iframe
        var chatIframe = document.querySelector('iframe[src*="embed/agent-chat"]');
        if (!chatIframe) {
          return; // Chat not available, don't show menu
        }

        event.preventDefault();
        event.stopPropagation(); // Stop event from bubbling up

        if (!contextMenu) {
          contextMenu = createContextMenu();
        }

        contextMenu.style.display = 'block';
        contextMenu.style.left = event.pageX + 'px';
        contextMenu.style.top = event.pageY + 'px';
      }

      function hideContextMenu() {
        if (contextMenu) {
          contextMenu.style.display = 'none';
        }
      }

      // Listen for right-click on CodeMirror
      var wrapper = codemirror.getWrapperElement();
      wrapper.addEventListener('contextmenu', showContextMenu, true); // Use capture phase

      // Hide context menu when clicking elsewhere
      var hideOnClick = function hideOnClick() {
        hideContextMenu();
      };

      var hideOnRightClick = function hideOnRightClick(e) {
        // If right-click is outside CodeMirror, hide our menu
        if (!wrapper.contains(e.target)) {
          hideContextMenu();
        }
      };

      document.addEventListener('click', hideOnClick);
      document.addEventListener('contextmenu', hideOnRightClick);

      // Cleanup on destroy
      scope.$on('$destroy', function () {
        wrapper.removeEventListener('contextmenu', showContextMenu);
        document.removeEventListener('click', hideOnClick);
        document.removeEventListener('contextmenu', hideOnRightClick);
        if (contextMenu && contextMenu.parentNode) {
          contextMenu.parentNode.removeChild(contextMenu);
        }
      });
    }

    /**
     * Configures the element in the post link phase
     * @param scope
     * @param elem
     * @param attrs
     * @param uiCodemirrorCtrl
     * @param ngModel
     * @param formGroupCtrl
     */
    function postLink(scope, elem, attrs, _ref2) {
      var _ref3 = slicedToArray(_ref2, 3),
          uiCodemirrorCtrl = _ref3[0],
          ngModel = _ref3[1],
          formGroupCtrl = _ref3[2];

      if (angular.isUndefined($window.CodeMirror)) {
        throw new Error('Missing CodeMirror');
      }

      if (!$window.CodeMirror.findModeByName) {
        var find = function find(name) {
          var l = languages.getLang(name);
          if (l && l.codemirror) {
            return l;
          }
        };
        $window.CodeMirror.findModeByName = find;
        $window.CodeMirror.findModeByExtension = find;
        $window.CodeMirror.findModeByName = find;
      }

      var codemirrorOptions = angular.extend({ value: elem.text() }, uiCodemirrorConfig.codemirror || {}, scope.$eval(attrs.uiCodemirrorOpts));
      if (attrs.placeholder) {
        codemirrorOptions.placeholder = attrs.placeholder;
      }

      // Feature 8: Make file info available for code selection context
      if (codemirrorOptions.file) {
        scope.file = codemirrorOptions.file;
        uiCodemirrorCtrl.fileInfo = codemirrorOptions.file; // Also store in controller
      }

      scope.disabledOrReadonly = true;
      if (attrs.disabled || attrs.readonly) {
        scope.disabledOrReadonly = true;
        codemirrorOptions.readOnly = true;
      }

      elem.html('');
      var codemirror = new $window.CodeMirror(function (cmEl) {
        return elem.append(cmEl);
      }, codemirrorOptions);

      if (attrs.onCreate) {
        scope.$eval(attrs.onCreate, { codemirror: codemirror });
      }
      uiCodemirrorCtrl.init(codemirror);

      // Feature 8: Context menu for adding code to AI chat
      configureContextMenu(codemirror, scope, elem, uiCodemirrorCtrl);

      configOptionsWatcher(codemirror, attrs.uiCodemirrorOpts, scope);
      configUiRefreshAttribute(codemirror, attrs.uiRefresh, scope);
      configLabel(codemirror, scope, elem, attrs, uiCodemirrorCtrl);

      configNgModelLink(codemirror, ngModel, scope, uiCodemirrorCtrl);
      configAutoRefresh(codemirror, scope, elem);
      configureDocumentIdData(codemirror, scope, attrs);
      configFocus(codemirror, scope, elem, attrs, formGroupCtrl);
      // onLoad callback
      if (angular.isFunction(codemirrorOptions.onLoad)) {
        scope.$applyAsync(function () {
          codemirrorOptions.onLoad(codemirror);
        });
      }
    }

    /** ******************************************************************************************
     * Watches for changes to the options, and updates the CM as necessary
     * @param codemirror
     * @param uiCodemirrorAttr
     * @param scope
     */
    function configOptionsWatcher(codemirror, uiCodemirrorAttr, scope) {
      if (!uiCodemirrorAttr) {
        return;
      }

      var codemirrorDefaultsKeys = Object.keys($window.CodeMirror.defaults);
      scope.$watch(uiCodemirrorAttr, updateOptions, true);

      function updateOptions(newValues, oldValue) {
        if (!angular.isObject(newValues)) {
          return;
        }
        codemirrorDefaultsKeys.forEach(function (key) {
          if (newValues.hasOwnProperty(key)) {
            if (oldValue && newValues[key] === oldValue[key]) {
              return;
            }

            codemirror.setOption(key, newValues[key]);
          }
        });
      }
    }

    /** ******************************************************************************************
     * Links the ngModel controller to the CM instance
     * @param codemirror
     * @param ngModel
     * @param scope
     * @param uiCodemirrorCtrl
     */
    function configNgModelLink(codemirror, ngModel, scope, uiCodemirrorCtrl) {
      if (!ngModel) {
        return;
      }
      // CodeMirror expects a string, so make sure it gets one.
      // This does not change the model.
      ngModel.$formatters.push(function (value) {
        if (angular.isUndefined(value) || value === null) {
          return '';
        } else if (angular.isObject(value) || angular.isArray(value)) {
          throw new Error('ui-codemirror cannot use an object or an array as a model');
        }
        return value;
      });

      var updatingFromRender = false;

      function render() {
        //Code mirror expects a string so make sure it gets one
        //Although the formatter have already done this, it can be possible that another formatter returns undefined (for example the required directive)
        var safeViewValue = ngModel.$viewValue || '';
        if (getValue() === safeViewValue) return;

        updatingFromRender = true;
        var startedCurrentRenderClean = codemirror.isClean();
        var cursor = codemirror.getCursor();
        var selections = codemirror.somethingSelected() ? codemirror.listSelections() : false;
        var movedCursor = cursor.line > 0 || cursor.ch > 0;
        codemirror.$nonUserInput = true;
        codemirror.setValue(safeViewValue);
        // Don't set cursor or selection in read-only mode, or CM takes focus
        if (movedCursor || !scope.disabledOrReadonly || codemirror.hasFocus()) {
          try {
            // ensure the user doesn't lose their place
            if (selections) {
              codemirror.setSelections(selections);
            } else {
              codemirror.setCursor(cursor);
            }
          } catch (e) {
            // ignore cursor errors
          }
        }
        codemirror.$nonUserInput = false;
        updatingFromRender = false;

        if (startedCurrentRenderClean && safeViewValue) {
          codemirror.clearHistory();
        }
      }

      // ensure the initial model view value is set
      render();

      // Override the ngModelController $render method, which is what gets called when the model is updated.
      // This takes care of the synchronizing the codeMirror element with the underlying model, in the case that it is changed by something else.
      ngModel.$render = render;

      // returns the current editor value
      function getValue() {
        return codemirror.getValue();
      }

      // Keep the ngModel in sync with changes from CodeMirror
      codemirror.on('change', function () {
        // ignore changes if this is updating
        if (updatingFromRender) return;
        scope.$evalAsync(function () {
          var newValue = getValue();
          if (newValue !== ngModel.$viewValue) {
            ngModel.$setViewValue(newValue, uiCodemirrorCtrl.hasFocus ? undefined : 'blur');
          }
        });
      });

      uiCodemirrorCtrl.$onFocusChange = function (hasFocus) {
        ngModel.$setViewValue(getValue(), hasFocus ? undefined : 'blur');
      };

      scope.$on('$destroy', function () {
        // ensure we trigger a blur change if getting destroyed
        ngModel.$setViewValue(getValue(), 'blur');
      });
    }

    /** ******************************************************************************************
     * Refreshes the CM instance if a ui-refresh attribute changes
     *
     * @param codeMirror
     * @param uiRefreshAttr
     * @param scope
     */
    function configUiRefreshAttribute(codeMirror, uiRefreshAttr, scope) {
      if (!uiRefreshAttr) {
        return;
      }

      scope.$watch(uiRefreshAttr, function (newVal, oldVal) {
        // Skip the initial watch firing
        if (newVal !== oldVal) {
          $timeout(function () {
            codeMirror.refresh();
          });
        }
      });
    }

    /** ******************************************************************************************
     * Enables autofocus
     * @param codemirror
     * @param scope
     * @param elem
     * @param attrs
     * @param formGroupCtrl
     */
    function configFocus(codemirror, scope, elem, attrs, formGroupCtrl) {
      codemirror.refresh();
      if (formGroupCtrl) {
        codemirror.on('focus', function () {
          return formGroupCtrl.setFocus(true);
        });
        codemirror.on('blur', function () {
          return formGroupCtrl.setFocus(false);
        });
      }
      if (scope.$eval(attrs.autofocus)) {
        $timeout(function () {
          if (elem.children().isVisible()) {
            codemirror.focus();
          }
        }, 100, false);
      }
    }

    /** ******************************************************************************************
     * Sets up an optional label inside the CM itself
     * @param codemirror
     * @param scope
     * @param elem
     * @param attrs
     * @param uiCodemirrorCtrl
     */
    function configLabel(codemirror, scope, elem, attrs, uiCodemirrorCtrl) {
      var tfWrapper = void 0;
      var labelWrapper = void 0;

      var label = attrs.codemirrorLabel;
      var testFramework = attrs.codemirrorTestFramework;

      function updateLabel() {
        if (labelWrapper) {
          labelWrapper.text(attrs.codemirrorLabel);
        }
        var testFramework = attrs.codemirrorTestFramework;
        if (tfWrapper && testFramework) {
          var tfId = scope.$eval(testFramework);
          tfWrapper.text(languages.frameworks[tfId] || tfId);
        }
      }

      if (label || testFramework) {
        uiCodemirrorCtrl.cmLabel = angular.element('<div class="codemirror-label"></div>');
        elem.querySelector('.CodeMirror-lines').prepend(uiCodemirrorCtrl.cmLabel);
        if (label) {
          labelWrapper = angular.element('<span class="label-text"></span>');
          uiCodemirrorCtrl.cmLabel.append(labelWrapper);
          attrs.$observe('codemirrorLabel', updateLabel);
        }
        if (testFramework) {
          if (attrs.codemirrorOnTestFrameworkClick) {
            tfWrapper = angular.element('<a class="codemirror-test-framework-link"></a>');
            tfWrapper.on('click', function () {
              scope.$applyAsync(function () {
                return scope.$eval(attrs.codemirrorOnTestFrameworkClick);
              });
            });
          } else {
            tfWrapper = angular.element('<span></span>');
          }
          tfWrapper.addClass('codemirror-test-framework');
          uiCodemirrorCtrl.cmLabel.append(tfWrapper);
          scope.$watch(attrs.codemirrorTestFramework, updateLabel);
        }
        updateLabel();
      }
    }

    /** ******************************************************************************************
     * Automatically refresh the CM instance based on certain events
     * @param codemirror
     * @param scope
     * @param elem
     */
    function configAutoRefresh(codemirror, scope, elem) {
      if (codemirror) {
        var refreshEvent = null;
        var refresh = function refresh() {
          if (refreshEvent) window.clearTimeout(refreshEvent);
          // we're running this outside Angular on purpose
          // eslint-disable-next-line angular/timeout-service
          refreshEvent = window.setTimeout(function () {
            if (elem.isVisible('basic')) {
              codemirror.refresh();
            }
          }, 10);
        };
        scope.$on(TABSET_EVT_SELECT, refresh);
        scope.$on(UI_LAYOUT_EVT_RESIZE, refresh);
        scope.$on(FULLSCREEN_EVT_CHANGE, refresh);
        scope.$watch(function () {
          // check on every watch if we need to refresh
          // (will automatically be checked after the current digest completes)
          if (codemirror && !elem.isVisible('basic')) {
            refresh();
          }
        });

        scope.$watch(function () {
          return elem.attr('disabled') || elem.attr('readonly');
        }, function (disabled) {
          scope.disabledOrReadonly = !!disabled;
          codemirror.setOption('readOnly', scope.disabledOrReadonly);
        });
      }
    }

    function configureDocumentIdData(codemirror, scope, attrs) {
      if (attrs.documentId) {
        codemirror.$nonUserInput = true;
        // track unique document ID data
        var docId = scope.$eval(attrs.documentId);
        if (!documentIdData[docId]) {
          documentIdData[docId] = {};
        }
        var docData = documentIdData[docId];
        if (docData.cursor) {
          codemirror.setCursor(docData.cursor);
        }
        if (docData.history) {
          codemirror.setHistory(docData.history);
        }
        scope.$on('$destroy', function () {
          // preserve cursors between views
          docData.cursor = codemirror.getCursor();
          docData.history = codemirror.getHistory();
        });
        codemirror.$nonUserInput = false;
      }
    }
  }]);

  var codemirrorIdeOptsTemplate = "<form class=\"codemirror-ide-opts\"> <div class=\"form-group theme\" ng-if=\"!hide.theme\"> <label class=\"control-label\">Theme:</label> <div class=\"btn-group\"> <button class=\"btn-default light\" ng-model=\"sharedOpts.theme\" btn-radio=\"'light'\"> Light </button> <button class=\"btn-default dark\" ng-model=\"sharedOpts.theme\" btn-radio=\"'dark'\"> Dark </button> </div> </div> <h2>Editor</h2> <div class=\"form-group\" ng-if=\"!hide.font\"> <label class=\"control-label\">Custom Font:</label> <div> <input type=\"text\" class=\"form-control\" placeholder='14px/20px Menlo, Consolas, \"Courier New\", monospace' ng-model=\"sharedOpts.font\" ng-model-options=\"{ updateOn: 'default blur', debounce: { 'default': 350, 'blur': 0 } }\" ng-change=\"updateFont()\"> <div class=\"help-block\"> <span tooltip=\"This includes any place you are editing code inside the application, except for markdown editors.\"> Change the default <code>font</code> CSS styling for code editors <tooltip-help-icon></tooltip-help-icon> </span> <br> <a href=\"https://developer.mozilla.org/en-US/docs/Web/CSS/font\" target=\"_blank\" rel=\"nofollow noopener\" class=\"font-css-help\"> CSS font styling help </a> </div> </div> </div> <div class=\"form-group\" ng-if=\"!hide.lineWrapping\"> <label class=\"control-label\">Line Wrapping:</label> <div class=\"btn-group\"> <button class=\"btn-default\" ng-model=\"sharedOpts.lineWrapping\" btn-radio=\"true\"> Soft Wrap </button> <button class=\"btn-default\" ng-model=\"sharedOpts.lineWrapping\" btn-radio=\"false\"> Off </button> </div> </div> <div class=\"form-group\" ng-if=\"!hide.lineNumbers\"> <label class=\"control-label\">Line Numbers:</label> <div class=\"btn-group\"> <button class=\"btn-default\" ng-model=\"sharedOpts.lineNumbers\" btn-radio=\"true\"> On </button> <button class=\"btn-default\" ng-model=\"sharedOpts.lineNumbers\" btn-radio=\"false\"> Off </button> </div> </div> <h2>Input Control</h2> <div class=\"form-group\" ng-if=\"!hide.editorMode\"> <label class=\"control-label\">Editor Mode:</label> <div class=\"btn-group\"> <button class=\"btn-default\" ng-model=\"sharedOpts.keyMap\" btn-radio=\"'default'\"> Default </button> <button class=\"btn-default\" ng-model=\"sharedOpts.keyMap\" btn-radio=\"'vim'\"> Vim </button> <button class=\"btn-default\" ng-model=\"sharedOpts.keyMap\" btn-radio=\"'emacs'\"> Emacs </button> </div> </div> <div class=\"form-group\" ng-if=\"!hide.tab\"> <label class=\"control-label\">Tab Mode:</label> <div class=\"btn-group\"> <button class=\"btn-default\" ng-model=\"sharedOpts.indentSpaces\" btn-radio=\"2\"> 2 Spaces </button> <button class=\"btn-default\" ng-model=\"sharedOpts.indentSpaces\" btn-radio=\"4\"> 4 Spaces </button> <button class=\"btn-default\" ng-model=\"sharedOpts.indentSpaces\" btn-radio=\"false\"> Tabs </button> </div> </div> <div class=\"form-group\" ng-if=\"!hide.closeBrackets\"> <label class=\"control-label\">Auto Close Brackets:</label> <div class=\"btn-group\"> <button class=\"btn-default\" ng-model=\"sharedOpts.autoCloseBrackets\" btn-radio=\"true\"> On </button> <button class=\"btn-default\" ng-model=\"sharedOpts.autoCloseBrackets\" btn-radio=\"false\"> Off </button> </div> </div> <div class=\"form-group\" ng-if=\"!hide.smartIndent\"> <label class=\"control-label\">Smart Indent:</label> <div> <div class=\"btn-group\"> <button class=\"btn-default\" ng-model=\"sharedOpts.smartIndent\" btn-radio=\"true\"> On </button> <button class=\"btn-default\" ng-model=\"sharedOpts.smartIndent\" btn-radio=\"false\"> Off </button> </div> <div class=\"qualified-checkbox\"> <input type=\"checkbox\" id=\"sharedOpts.electricChars\" ng-model=\"sharedOpts.electricChars\" ng-disabled=\"!sharedOpts.smartIndent\"> <label for=\"sharedOpts.electricChars\"> Re-Indent When Typing Special Characters </label> </div> </div> </div> <h2 ng-if=\"!hide.fileMap\">Map File Formats</h2> <div class=\"form-group\" ng-if=\"!hide.fileMap\"> <div> <simple-repeater ng-model=\"fileMap\" blank-item=\"{}\" item-label=\"File Mapping\" on-add=\"updateFileMap()\" on-remove=\"updateFileMap()\"> <item-content> <div class=\"file-mapping\"> <input type=\"text\" class=\"form-control\" ng-model=\"item.from\" ng-model-options=\"{ updateOn: 'default blur', debounce: { 'default': 350, 'blur': 0 } }\" ng-change=\"updateFileMap()\" placeholder=\"filename or *.ext\"> <div class=\"form-control-select\"> <select ng-model=\"item.to\" ng-change=\"updateFileMap()\" ng-options=\"lang.id as lang.name for lang in languages\"> <option value=\"\" class=\"placeholder\" ng-if=\"!item.lang\"> Editor Mode </option> </select> </div> </div> </item-content> </simple-repeater> <div class=\"help-block\"> Map either complete filenames or simple extension patterns onto known editor modes. You'll have to close and reopen any IDE tabs to see changes with existing files. </div> </div> </div> </form> ";

  var transcludeWithComponentModule = angular.module('Q.components.transclude-with', [])

  /**
   * @name TranscludeWith
   * @docType shared
   * @category directive
   * @description
   * Allows a directive to pass items back into a transcluded scope.
   *
   * Mainly useful for a directive that wants to use an ng-repeat, but allow the caller to define some or
   * all of the ng-repeat's contents
   *
   * Usage:
   *
   * <pre><code>
   * &lt;ANY transclude-with="..."> or &lt;transclude-with scope="...">
   * </code></pre>
   *
   * The value of scope should be a hash, where the properties will be mapped { fromScope: toScope }
   *
   * So, for example, if a directive has this format:
   *
   * <pre><code>
   *   &lt;transclude-with scope="{ item: 'foo' }">
   * </code></pre>
   *
   * then the transcluded content of the directive will have access to a property called `foo`, which is
   * the value of scope.foo from inside the directive.
   *
   * Example:
   *
   * <pre><code>
   * &lt;li ng-repeat="item in ctrl.items">
   *     &lt;transclude-with scope="{ item: ctrl.repeatItemName }">&lt;/transclude-with>
   * &lt;/li>
   * </code></pre>
   * @returns Directive Definition Object {object}
   */
  .directive('transcludeWith', function () {
    return {
      restrict: 'AE',
      link: function link(scope, elem, attrs, ctrl, transclude) {
        var transcludedScopeProperties = attrs.transcludeWith || attrs.scope;

        if (!transcludedScopeProperties) {
          throw new Error('No transcluded scope properties found for transclude-with');
        }
        if (!transclude) {
          throw new Error('No transclude function for transclude-with');
        }

        // parse the inline object
        transcludedScopeProperties = scope.$eval(transcludedScopeProperties);

        transclude(function (clone, tScope) {
          // duplicates ng-transclude core functionality
          elem.empty();
          elem.append(clone);

          // now add in the directive's scope properties
          angular.forEach(transcludedScopeProperties, function (tScopeName, scopeName) {
            var watch = tScopeName[0] === '=';
            if (watch) {
              tScopeName = tScopeName.slice(1);
              scope.$watch(scopeName, function () {
                return tScope[tScopeName] = scope[scopeName];
              });
            }
            tScope[tScopeName] = scope[scopeName];
          });
        }, null, attrs.transcludeSlot);
      }
    };
  });

  var AUTO_SCROLL_TOP_BUFFER = 20;
  var AUTO_SCROLL_BOTTOM_BUFFER = 50;
  var AUTO_SCROLL_INTERVAL = 35;
  var AUTO_SCROLL_AMOUNT = 10;

  var asSortableHelperModule = angular.module('Q.helpers.as-sortable-helper', []).factory('asSortableHelper', ['$timeout', function ($timeout) {
    var autoScrollTimer = null;

    function clearAutoScrollTimer() {
      if (autoScrollTimer) {
        $timeout.cancel(autoScrollTimer);
        autoScrollTimer = null;
      }
    }

    return {
      autoScroll: function autoScroll(opts) {
        var oldDragMove = opts.dragMove;
        var oldDragEnd = opts.dragEnd;

        opts.dragMove = function (itemPosition, containment, event) {
          if (!event) return;

          clearAutoScrollTimer();

          var scrollContainer = containment.scrollableParent();
          var scrollContainerOffset = scrollContainer.offset();

          function autoScrollBy(amount) {
            var adjust = function adjust() {
              return scrollContainer.scrollTop(scrollContainer.scrollTop() + amount);
            };
            adjust();
            autoScrollTimer = $timeout(function () {
              return autoScrollBy(amount);
            }, AUTO_SCROLL_INTERVAL, false);
          }

          if (event.pageY < scrollContainerOffset.top + AUTO_SCROLL_TOP_BUFFER) {
            autoScrollBy(-AUTO_SCROLL_AMOUNT);
          } else if (event.pageY > scrollContainerOffset.top + scrollContainerOffset.height - AUTO_SCROLL_BOTTOM_BUFFER) {
            autoScrollBy(AUTO_SCROLL_AMOUNT);
          }

          if (oldDragMove) {
            oldDragMove.call(this, itemPosition, containment, event);
          }
        };

        opts.dragEnd = function () {
          clearAutoScrollTimer();

          if (oldDragEnd) {
            for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
              args[_key] = arguments[_key];
            }

            oldDragEnd.apply(this, args);
          }
        };

        return opts;
      }
    };
  }]);

  var simplerRepeaterTemplate = "<div ng-transclude=\"header\" class=\"repeater-footer\"></div> <div class=\"repeater-items\" id=\"simple-repeater-{{ $ctrl.counter }}\" ng-model=\"$ctrl.items\" as-sortable=\"$ctrl._sortable\"> <div class=\"repeater-item\" ng-repeat=\"item in $ctrl.items track by $ctrl._trackBy(item, $index)\" as-sortable-item> <div class=\"repeater-content\" transclude-with=\"{ $index: '=$repeaterIndex', item: '=' + ($ctrl.itemKey || 'item') }\" transclude-slot=\"row\"></div> <div class=\"sort-handle\" ng-show=\"$ctrl.sortable\"> <span as-sortable-item-handle tooltip=\"Drag to rearrange\"></span> </div> <div class=\"remove-button\"> <span ng-click=\"$ctrl.removeItem($index)\" tooltip=\"Remove {{ $ctrl.itemLabel || 'Item' }}\"></span> </div> </div> </div> <div ng-transclude=\"footer\" class=\"repeater-footer\"></div> <div class=\"add-button\"> <button class=\"btn-default btn-sm\" ng-click=\"$ctrl.addItem()\"> Add {{ $ctrl.itemLabel || 'Item' }} </button> <span class=\"sortable-note\" ng-show=\"$ctrl.sortable\"> Drag <span class=\"icon-drag-sort\"></span> to rearrange </span> </div> ";

  var repeaterCounter = 0;

  var simpleRepeaterComponentModule = angular.module('Q.components.simple-repeater', [transcludeWithComponentModule, asSortableHelperModule]).component('simpleRepeater', {
    restrict: 'E',
    transclude: {
      row: 'itemContent',
      header: '?repeaterHeader',
      footer: '?repeaterFooter'
    },
    template: simplerRepeaterTemplate,
    bindings: {
      items: '=ngModel',
      itemKey: '@',
      itemLabel: '@',
      blankItem: '<',
      sortable: '<',
      onAdd: '&',
      onRemove: '&',
      onSort: '&'
    },
    require: {
      ngModel: 'ngModel'
    },
    controller: 'SimpleRepeaterCtrl'
  }).controller('SimpleRepeaterCtrl', ['asSortableHelper', '$timeout', '$element', function SimpleRepeaterCtrl(asSortableHelper, $timeout, $element) {
    var ctrl = this;

    repeaterCounter++;
    ctrl.counter = repeaterCounter;

    // if a string then track by index otherwise try to track by a model id and fall back to creating a new id
    ctrl._trackBy = function (item, index) {
      if (!angular.isObject(item)) {
        return index;
      } else if (item.id || item._id) {
        return item.id || item._id;
      } else {
        defineValue(item, '_id', Math.random().toString(16).substr(2));
        return item._id;
      }
    };

    ctrl._sortable = asSortableHelper.autoScroll({
      containment: '#simple-repeater-' + ctrl.counter,
      containerPositioning: 'relative',
      accept: function accept(sourceItemHandleScope, destSortableScope) {
        return sourceItemHandleScope.itemScope.sortableScope.$id === destSortableScope.$id;
      },
      orderChanged: function orderChanged() {
        ctrl.onSort();
        ctrl.ngModel.$setDirty();
      }
    });

    ctrl.addItem = function () {
      var newItem = angular.copy(ctrl.blankItem);
      if (newItem === undefined) {
        newItem = {};
      }
      if (!ctrl.items) {
        ctrl.items = [];
        ctrl.ngModel.$setViewValue(ctrl.items);
      }
      ctrl.items.push(newItem);
      ctrl.ngModel.$setDirty();

      ctrl.onAdd(newItem);

      $timeout(function () {
        var newChild = $element[0].querySelector('.repeater-items > :last-child > .repeater-content');
        var inputs = newChild.querySelectorAll('input,textarea,select');
        if (inputs.length) {
          var firstInput = null;
          angular.forEach(inputs, function (input) {
            if (!firstInput && input.focus && input.nodeName && (input.nodeName.toUpperCase() !== 'INPUT' || ['checkbox', 'radio'].indexOf(input.type) === -1)) {
              firstInput = input;
            }
          });
          firstInput = firstInput || inputs[0];
          if (firstInput.focus) {
            firstInput.focus();
          }
        }
      }, 250);
    };

    ctrl.removeItem = function (index) {
      if (ctrl.items && ctrl.items.length > index) {
        var item = ctrl.items[index];
        ctrl.items.splice(index, 1);
        ctrl.ngModel.$setDirty();

        ctrl.onRemove(item, index);
      }
    };
  }]);

  var codemirrorIdeOptsComponentModule = angular.module('Q.components.codemirror.ide-opts', [codemirrorOptionsComponentModule, simpleRepeaterComponentModule]).directive('codemirrorIdeOpts', ['codemirrorOptions', function (codemirrorOptions) {
    return {
      restrict: 'E',
      template: codemirrorIdeOptsTemplate,
      link: function link(scope, elem, attr) {
        var sharedOpts = codemirrorOptions.sharedOpts;
        scope.languages = languages.known;
        var fileMap$$1 = scope.fileMap = [];
        angular.forEach(sharedOpts.fileMap, function (to, from) {
          fileMap$$1.push({ from: from, to: to });
        });
        fileMap$$1.sort(function (a, b) {
          return compareIgnoreCase(a.from, b.from);
        });
        scope.hide = scope.$eval(attr.hide || '{}');
        scope.sharedOpts = sharedOpts;
        scope.updateFont = codemirrorOptions.updateFont;

        scope.updateFileMap = function () {
          sharedOpts.fileMap = fileMap$$1.filter(function (a) {
            return a.from && a.to;
          }).reduce(function (map, _ref) {
            var from = _ref.from,
                to = _ref.to;

            map[from] = to;
            return map;
          }, {});
          codemirrorOptions.updateFileMap();
        };
      }
    };
  }]);

  /* eslint-disable angular/timeout-service */

  var syncScrollComponentModule = angular.module('Q.components.sync-scroll', [])

  // Directives to allow you to synchronize scrolling across multiple
  // overflowed elements

  // Requires providing a key to sync scrolling on

  .directive('syncScroll', ['syncScrollService', function (syncScrollService) {
    return {
      restrict: 'A',
      link: function link(scope, elem, attr) {
        var key = attr.syncScroll;
        if (key) {
          var relative = attr.syncScrollAbsolute !== 'false';
          var shouldListen = attr.syncScrollListen !== 'false';

          syncScrollService.addElement(key, elem, relative, shouldListen);

          scope.$on('$destroy', function () {
            return syncScrollService.removeElement(key, elem);
          });
        }
      }
    };
  }]).directive('codemirrorSyncScroll', ['syncScrollService', function (syncScrollService) {
    return {
      restrict: 'A',
      require: 'uiCodemirror',
      link: function link(scope, elem, attr, uiCodemirrorCtrl) {
        var key = attr.codemirrorSyncScroll;
        if (key) {
          var scrollElement = void 0;
          var relative = attr.syncScrollAbsolute !== 'false';
          var shouldListen = attr.syncScrollListen !== 'false';

          uiCodemirrorCtrl.whenReady().then(function () {
            scrollElement = elem.querySelector('.CodeMirror-scroll');
            syncScrollService.addElement(key, scrollElement, relative, shouldListen);
          });

          scope.$on('$destroy', function () {
            return syncScrollService.removeElement(key, scrollElement);
          });
        }
      }
    };
  }]).factory('syncScrollService', ['$window', function ($window) {
    var keys = {};

    var svc = {
      addElement: function addElement(key, $el, relative, shouldListen) {
        if (!keys[key]) {
          keys[key] = [];
          keys[key].scrollFn = _onScroll(key);
        }
        keys[key].push({ relative: relative, $el: $el, el: $el[0] });
        if (shouldListen !== false) {
          $el.on('scroll', keys[key].scrollFn);
        }
      },
      removeElement: function removeElement(key, $el) {
        if ($el && $el[0] && keys[key]) {
          var idx = keys[key].find(function (e) {
            return e.el === $el[0];
          });
          if (idx > -1) {
            $el.off('scroll', keys[key].scrollFn);
            keys[key].splice(idx, 1);
          }
          if (keys[key].length === 0) {
            delete keys[key];
          }
        }
      },
      pause: function pause(key) {
        svc.setPaused(key, true);
      },
      resume: function resume(key) {
        svc.setPaused(key, false);
      },
      setPaused: function setPaused(key, paused) {
        if (keys[key]) {
          keys[key].paused = paused;
        }
      }
    };

    return svc;

    //// private functions

    function _onScroll(key) {
      var eventTarget = void 0;
      return function ($event) {
        if (keys[key].paused) return;
        if (eventTarget && eventTarget !== $event.target) return;
        eventTarget = $event.target;

        var target = $event.target;
        var offset = target.scrollTop;
        var relativeOffset = offset / (target.scrollHeight - target.clientHeight);

        keys[key].forEach(function (_ref) {
          var relative = _ref.relative,
              el = _ref.el;

          if (el !== eventTarget) {
            var scrollTo = offset;
            if (relative) {
              scrollTo = relativeOffset * (el.scrollHeight - el.clientHeight);
            }
            el.scrollTop = scrollTo;
          }
        });

        $window.setTimeout(function () {
          return eventTarget = null;
        }, 100);
      };
    }
  }]);

  var markdownEditorTemplate = "<div class=\"markdown-editor {{ $ctrl.allowDarkMode && $root.$ideDarkClass }}\" ng-class=\"{ inline: !$ctrl.fullscreen, fullscreen: $ctrl.fullscreen, focus: $ctrl.hasFocus || $ctrl.fullscreen, empty: !$ctrl.value, 'collapse-on-empty': $ctrl.collapseOnEmpty, 'collapse-on-blur': $ctrl.collapseOnBlur, 'auto-expand': $ctrl.autoExpand, stretch: $ctrl.stretch, ready: $ctrl.ready, 'allow-dark-mode': $ctrl.allowDarkMode }\"> <div class=\"tab-content-wrapper\"> <div class=\"edit\" ng-class=\"{active: $ctrl.show.edit}\"> <ui-codemirror class=\"markdown-codemirror\" ng-model=\"$ctrl.value\" ui-codemirror-opts=\"$ctrl.createOpts()\" ng-readonly=\"$ctrl.readonly\" ng-disabled=\"$ctrl.disabled\" codemirror-sync-scroll=\"{{ $ctrl.syncId() }}\" autofocus=\"$ctrl.autofocus\"></ui-codemirror> <div ng-if=\"!$ctrl.readonly && !$ctrl.disabled && !$ctrl.disableUploads\" class=\"upload-help\" ngf-select ngf-change=\"$ctrl.selectedFiles($files)\"> Embed images by dragging & dropping, copy-and-paste, or <a href>selecting them</a>. (Max 15Mb) </div> </div> <div class=\"preview active\" ng-if=\"$ctrl.show.preview\" sync-scroll=\"{{ $ctrl.syncId() }}\" sync-scroll-listen=\"false\"> <div markdown=\"$ctrl.value\"></div> </div> <div class=\"help active\" ng-if=\"$ctrl.show.help\"> <docs-pane article-id=\"$ctrl.helpArticle\" hide-toolbar=\"true\" theme=\"{{!$ctrl.allowDarkMode && 'light'}}\"></docs-pane> </div> </div> <ul class=\"tabs\"> <li class=\"edit\" ng-click=\"$ctrl.selectTab('edit')\" ng-class=\"{ active: $ctrl.show.edit }\" tabindex=\"{{ $ctrl.tabIndexForTabs() }}\" tooltip=\"Edit\"> <a><span>Edit</span></a> </li> <li class=\"preview\" ng-click=\"$ctrl.selectTab('preview')\" ng-class=\"{ active: $ctrl.show.preview }\" tabindex=\"{{ $ctrl.tabIndexForTabs() }}\" tooltip=\"Preview\"> <a><span>Preview</span></a> </li> <li class=\"help\" ng-click=\"$ctrl.selectTab('help')\" ng-class=\"{ active: $ctrl.show.help }\" tabindex=\"{{ $ctrl.tabIndexForTabs() }}\" tooltip=\"Help on Markdown Syntax & Features\"> <a><span>Markdown Help</span></a> </li> <li class=\"fs\" ng-if=\"$ctrl.nodeId\"> <fullscreen-toggle target=\"{{ $ctrl.nodeId }}\" active=\"$ctrl.fullscreen\"></fullscreen-toggle> </li> </ul> </div> ";

  var markdownEditorNoTabsTemplate = "<div class=\"markdown-editor inline no-tabs {{ $ctrl.allowDarkMode && $root.$ideDarkClass }}\" ng-class=\"{ focus: $ctrl.hasFocus, empty: !$ctrl.value, 'collapse-on-empty': $ctrl.collapseOnEmpty, 'collapse-on-blur': $ctrl.collapseOnBlur, 'auto-expand': $ctrl.autoExpand, stretch: $ctrl.stretch, ready: $ctrl.ready }\"> <div class=\"tab-content-wrapper\"> <div class=\"edit active\"> <ui-codemirror class=\"markdown-codemirror\" ng-model=\"$ctrl.value\" ui-codemirror-opts=\"$ctrl.createOpts()\" ng-disabled=\"$ctrl.disabled\" codemirror-sync-scroll=\"{{ $ctrl.syncScrollId }}\" autofocus=\"$ctrl.autofocus\"></ui-codemirror> <div ng-if=\"!$ctrl.disableUploads\" class=\"upload-help\" ngf-select ngf-change=\"$ctrl.selectedFiles($files)\"> Embed images by dragging & dropping, copy-and-paste, or <a href>selecting them</a>. (Max 15Mb) </div> </div> </div> </div> ";

  var uniqueUploaderId = 0;

  var MarkdownFileUploader = function () {
    function MarkdownFileUploader(cm, file) {
      classCallCheck(this, MarkdownFileUploader);

      uniqueUploaderId++;
      this.cm = cm;
      this.file = file;
      this.uniqueId = uniqueUploaderId;
    }

    createClass(MarkdownFileUploader, [{
      key: 'start',
      value: function start(uploadPromise) {
        var _this = this;

        var needsExtraNewline = this.initializeCursor();
        var newlines = needsExtraNewline ? '\n\n' : '\n';

        this.insertText = '![Uploading #' + this.uniqueId + ': ' + this.file.name + ']()';

        this.cm.replaceSelection(newlines + this.insertText);

        var _cm$getCursor = this.cm.getCursor(),
            line = _cm$getCursor.line;

        var progressBar = angular.element('<span></span>');
        var progressEl = angular.element('<div class="markdown-file-upload-progress starting"></div>');
        progressEl.append(progressBar);
        var lineWidget = this.cm.addLineWidget(line, progressEl[0]);

        uploadPromise.then(function (url) {
          // success
          var desc = _this.file.name;
          _this.replaceInserted('![' + desc + '](' + url + ')');
        }, function () {
          // error
          _this.replaceInserted('![Error Uploading Image]()');
        }, function (perc) {
          // progress
          progressEl.removeClass('starting');
          progressBar.css({ width: 100 * perc + '%' });
        }).finally(function () {
          // null out external references
          _this.cm = _this.file = null;
          progressEl.removeClass('starting');
          progressEl.addClass('closing');
          // eslint-disable-next-line angular/timeout-service
          setTimeout(function () {
            return lineWidget.clear();
          }, 250);
        });
      }
    }, {
      key: 'initializeCursor',
      value: function initializeCursor() {
        var cm = this.cm;

        var _cm$getCursor2 = cm.getCursor(),
            line = _cm$getCursor2.line;

        var selectedLine = cm.getLine(line);
        if (cm.getLine(line) !== '') {
          cm.setCursor({ line: line, ch: selectedLine.length });
        }
        return selectedLine.length > 0;
      }
    }, {
      key: 'replaceInserted',
      value: function replaceInserted(value) {
        var _this2 = this;

        var cm = this.cm;
        cm.eachLine(function (lineHandle) {
          var start = lineHandle.text.indexOf(_this2.insertText);
          if (start > -1) {
            var line = cm.getLineNumber(lineHandle);
            cm.replaceRange(value, { line: line, ch: start }, { line: line, ch: start + _this2.insertText.length });
            return true;
          }
        });
      }
    }]);
    return MarkdownFileUploader;
  }();

  var ngFileUploadComponentModule = 'ngFileUpload';

  var fileUploadServiceModule = angular.module('Q.services.file-upload-service', [ngFileUploadComponentModule, loadingBarModule]).factory('fileUploadService', ['Upload', 'cfpLoadingBar', '$window', '$q', '$http', function fileUploadService(Upload, cfpLoadingBar, $window, $q, $http) {
    var activeFileProgress = [];

    var DEFAULT_ERROR_MESSAGE = 'Unable to upload file';

    function _setActiveProgress(final) {
      var sum = activeFileProgress.reduce(function (s, v) {
        return s + v;
      }, 0);
      var len = activeFileProgress.length;
      if (sum === len && final) {
        activeFileProgress.length = 0;
        cfpLoadingBar.complete();
      } else if (len > 0) {
        cfpLoadingBar.start();
        cfpLoadingBar.set(sum / len);
      }
    }

    /**
     * Uploads a single file
     * @param file {File} File object passed in from a browser API
     * @param rejected {Array} List of rejected files (for error reporting)
     * @param validator {Function} Validator used for checking the files
     * @param showProgress {boolean} Set to false to hide the loading bar for progress
     * @returns {Promise}
     */
    function handleSingle(file) {
      var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
          _ref$rejected = _ref.rejected,
          rejected = _ref$rejected === undefined ? [] : _ref$rejected,
          _ref$validator = _ref.validator,
          validator = _ref$validator === undefined ? null : _ref$validator,
          _ref$showProgress = _ref.showProgress,
          showProgress = _ref$showProgress === undefined ? true : _ref$showProgress;

      var upload = void 0;
      if (angular.isArray(file)) file = file[0];
      if (!file) {
        var msg = null;
        if (rejected.length) {
          msg = 'No valid files selected.';
          if (validator && validator.label) {
            msg += ' You can use ' + validator.label + '.';
          }
        }
        return $q.reject(msg);
      }

      if (file.size && file.size >= 15728640) {
        return $q.reject('Please only upload files smaller than 15Mb');
      }

      var activeFileIndex = activeFileProgress.length;
      if (showProgress) {
        activeFileProgress[activeFileIndex] = 0;
        _setActiveProgress();
      }

      var uploadDeferred = $q.defer();

      if (angular.isString(file)) {
        upload = $http.post(appConfig.uploadRoute, { file: file }, { ignoreLoadingBar: true });
      } else {
        upload = Upload.upload({
          url: appConfig.uploadRoute,
          data: { file: file },
          ignoreLoadingBar: true
        }).progress(function (evt) {
          var progress = 0.95 * evt.loaded / evt.total;
          if (showProgress) {
            // max 95% for file uploads, leaving the remaining 5% for brightness, etc
            activeFileProgress[activeFileIndex] = progress;
            _setActiveProgress();
          }
          uploadDeferred.notify(progress);
        });
      }

      upload.then(function (resp) {
        var files = resp.data.files;
        var error = false;

        if (!files.length) {
          // no files were returned
          error = true;
        } else {
          var _file = files[0];
          if (_file.success !== true) {
            // server reports an error for this image
            if (_file.s3 && _file.s3.err) {
              // if we have an S3 error, we're done
              error = _file.s3.err;
            } else if (validator === imageValidator) {
              // only worry about cloudinary errors on image uploads
              error = true;
              if (_file.cloudinary && _file.cloudinary.error) {
                error = _file.cloudinary.error.message;
              }
            }
          }
        }

        return error === false ? resp : $q.reject(error);
      }).then(function (resp) {
        return resultToModel(resp.data.files[0]);
      }).catch(function (resp) {
        var reason = void 0;
        if (!resp) {
          reason = DEFAULT_ERROR_MESSAGE;
        } else if (angular.isString(resp)) {
          resp = {
            reason: resp
          };
        } else {
          var data = resp.data;
          if (data && data.errors) {
            reason = data.errors[0] || DEFAULT_ERROR_MESSAGE;
          } else if (!resp.reason && !resp.statusText) {
            reason = DEFAULT_ERROR_MESSAGE;
          }
        }
        return $q.reject(reason ? { data: { reason: reason } } : resp);
      }).finally(function () {
        if (showProgress) {
          activeFileProgress[activeFileIndex] = 1;
          _setActiveProgress(true);
        }
        uploadDeferred.notify(1);
      }).then(uploadDeferred.resolve, uploadDeferred.reject);

      return uploadDeferred.promise;
    }

    function loadImagePreview(file) {
      // don't preview if we can't read it, it's not an image, it's a string, or the file size is over 10Mb
      if ($window.FileReader === undefined || !previewImageValidator(file) || angular.isString(file) || file.size > 1e7) {
        return $q.reject();
      }

      return $q(function (resolve) {
        var reader = new FileReader();
        reader.onload = function (evt) {
          resolve(evt.target.result);
        };
        reader.readAsDataURL(file);
      });
    }

    function previewImageValidator(file) {
      return file && /\.(jpg|jpeg|gif|png|svg)$/.test(file.name);
    }

    previewImageValidator.label = 'simple images, such as jpg, png, or gif';

    function imageValidator(file) {
      return file && (IMAGE_MIME_TYPE_REGEX.test(file.type || '') || IMAGE_FILE_EXTENSION_REGEX.test(file.name || ''));
    }

    imageValidator.label = 'most images, including jpg, png, or psd';

    function documentValidator(file) {
      return file && /\.(docx?|pdf|rtf|txt|pptx?|ppsx?|html?|odt|pages|md|markdown)$/i.test(file.name);
    }

    documentValidator.label = 'common document formats, such as docx, pdf, rtf, html, or even plain text';

    /**
     * Converts a server upload result into a storable document upload model
     * @param result
     * @returns {{fileId: *, fileFormat: *, width: null, height: null, resourceType: *|null, size: _bi255.bytes|*|{base: number, units: string[]}|FUZZY_NUMBER_TYPES.bytes|{base, units}}}
     */
    function resultToModel(result) {
      var s3Id = result.s3.name.replace(/\..*/, '');
      var s3Format = result.s3.name.replace(/.*\./, '');
      return {
        fileId: result.cloudinary.public_id || s3Id,
        fileFormat: result.cloudinary.format || s3Format,
        // pass back nulls so the server clears existing values
        width: result.cloudinary.width || null,
        height: result.cloudinary.height || null,
        resourceType: result.cloudinary.resource_type || null,
        size: result.cloudinary.bytes || result.s3.size
      };
    }

    /**
     * Gets the raw download URL for an uploaded image
     * @param model
     * @returns {*}
     */
    function downloadUrl(model) {
      if (!model || !model.fileId && !model.rawUrl) return null;
      if (model.rawUrl) {
        return model.rawUrl;
      } else if (model.fileFormat && !/\.\w+$/.test(model.fileId)) {
        return '' + appConfig.s3BaseUrl + model.fileId + (model.fileFormat ? '.' + model.fileFormat : '');
      } else {
        return '' + appConfig.s3BaseUrl + model.fileId;
      }
    }

    /**
     * Preloads an image for display
     * @param imageUrl
     */
    function preloadImage(imageUrl) {
      var img = void 0;
      return $q(function (resolve, reject) {
        img = document.createElement('img');
        img.src = imageUrl;
        img.style.display = 'none';
        document.body.appendChild(img);

        img.onload = function () {
          return resolve(img);
        };
        img.onerror = function () {
          return reject();
        };
        // catch that sometimes the image is loaded already
        if (img.complete) {
          resolve(img);
        }
      }).finally(function () {
        if (img) {
          angular.element(img).remove();
        }
      });
    }

    return {
      handleSingle: handleSingle,
      imageValidator: imageValidator,
      documentValidator: documentValidator,
      loadImagePreview: loadImagePreview,
      downloadUrl: downloadUrl,
      preloadImage: preloadImage
    };
  }]).filter('downloadUrl', ['fileUploadService', function (fileUploadService) {
    return function (model) {
      return fileUploadService.downloadUrl(model);
    };
  }]);

  var cloudinaryServiceModule = angular.module('Q.services.cloudinary-service', []).factory('cloudinaryService', ['$window', function ($window) {
    var dpr = $window.devicePixelRatio;
    var factor = dpr > 1 ? dpr : false;

    var service = {
      getUrl: function getUrl(fileId, params) {
        params = params || {
          width: 200
        };
        // either the id itself, a model or a upload result can be passed in
        if (angular.isObject(fileId)) {
          if (!fileId.width) return null;
          if (!params.format && /^(gif|jpe?g|png|svg)$/.test(fileId.fileFormat)) {
            params.format = fileId.fileFormat;
          }
          /*jshint camelcase: false */
          fileId = fileId.fileId || fileId.public_id;
        }

        // support null values so that its easier to consume this method
        if (!fileId) return null;

        if (factor && params.retina) {
          if (params.width) params.width = Math.ceil(params.width * factor);
          if (params.height) params.height = Math.ceil(params.height * factor);
        }
        // cap at 5000px
        if (params.width) params.width = Math.min(5000, params.width);
        if (params.height) params.height = Math.min(5000, params.height);

        var options = [];
        toOption(params.width, 'w');
        toOption(params.height, 'h');
        toOption(params.x, 'x');
        toOption(params.y, 'y');
        toOption(params.radius, 'r');
        toOption(params.transformation, 't');
        toOption(params.quality, 'q');
        toOption(params.angle, 'a');
        toOption(params.effect, 'e');

        // valid crop values are crop/scale/fit/fill/limit/pad/thumb
        toOption(params.crop, 'c');

        // valid gravity values include face/faces/north_west/north_east/south_west/south_east
        toOption(params.gravity, 'g');

        return '' + appConfig.cloudinaryBaseUrl + options.join(',') + '/' + fileId + '.' + (params.format || 'png');

        function toOption(value, prefix) {
          if (value) {
            options.push(prefix + '_' + value);
          }
        }
      }
    };

    return service;
  }]).filter('cloudinaryImage', ['cloudinaryService', function (cloudinaryService) {
    return function (field, width, height, retina) {
      if (height === true) {
        height = null;
        retina = true;
      }
      return cloudinaryService.getUrl(field, { width: width, height: height, retina: retina });
    };
  }]);

  var docsPaneTemplate = "<div class=\"docs-toolbar\" ng-if=\"!$ctrl.hideToolbar\"> <a class=\"home btn btn-link\" ng-click=\"$ctrl.goHome()\" ng-disabled=\"!$ctrl.src\" tooltip=\"Takes you back to the first topic\"></a> <span class=\"flex-fill\"></span> <a class=\"open btn btn-link\" ng-click=\"$ctrl.newWindow()\" ng-show=\"$ctrl.src\" tooltip=\"Open this help topic in a new tab\"></a> </div> <iframe ng-if=\"$ctrl.src\" src=\"{{ $ctrl.src }}\" sandbox=\"allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-scripts\"></iframe> <div class=\"article-list {{$root.$ideDarkClass}}\" ng-if=\"!$ctrl.src\"> <h1>{{ $ctrl.articleListTitle || 'Articles' }}</h1> <div ng-if=\"$ctrl.articleListContent\" markdown=\"$ctrl.articleListContent\"></div> <ul ng-if=\"$ctrl.articleList && $ctrl.articleList.length\" class=\"bullets\"> <li ng-repeat=\"item in $ctrl.articleList track by item.id\"> <a class=\"link\" ng-click=\"$ctrl.loadArticle(item.id)\" ng-bind=\"item.title || item.id\"></a> </li> </ul> </div> ";

  var getDocsURL = function getDocsURL(articleId) {
    var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
        _ref$embedded = _ref.embedded,
        embedded = _ref$embedded === undefined ? false : _ref$embedded,
        _ref$theme = _ref.theme,
        theme = _ref$theme === undefined ? undefined : _ref$theme,
        _ref$section = _ref.section,
        section = _ref$section === undefined ? undefined : _ref$section;

    if (articleId[0] === '/') {
      articleId = articleId.slice(1);
    }
    var params = [];
    if (embedded) {
      params.push('embedded=true');
    }
    if (theme) {
      params.push('theme=' + theme);
    }
    return (window.Dev && window.Dev.docsUrl || appConfig.docsUrl) + '/' + articleId.replace(/_/g, '-') + (params ? '?' + params.join('&') : '') + (section ? '#' + section : '');
  };

  var docsPaneComponentModule = angular.module('Q.components.docs-pane', [markdownComponentModule]).component('docsPane', {
    bindings: {
      articleId: '=',
      homeArticle: '=?',
      articleList: '<',
      articleListTitle: '@',
      articleListContent: '<',
      theme: '@',
      hideToolbar: '<'
    },
    controller: 'DocsPaneCtrl',
    template: docsPaneTemplate
  }).controller('DocsPaneCtrl', ['$scope', '$element', '$rootScope', '$sce', '$timeout', '$window', function DocsPaneCtrl($scope, $element, $rootScope, $sce, $timeout, $window) {
    var ctrl = this;

    var lastTheme = void 0;

    var loadArticle = ctrl.loadArticle = function () {
      var articleId = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : ctrl.articleId;

      ctrl.articleId = articleId;
      ctrl.src = '';
      if (articleId) {
        lastTheme = $rootScope.$ideDarkClass;
        ctrl.src = $sce.trustAsResourceUrl(getDocsURL(articleId, {
          embedded: true,
          theme: ctrl.theme || $rootScope.$ideDarkClass || 'light'
        }));
      }
    };

    ctrl.$onInit = function () {
      if (!ctrl.homeArticle) {
        ctrl.homeArticle = ctrl.articleId;
      }
      loadArticle();
    };

    $scope.$watch('$ctrl.articleId', function () {
      loadArticle();
    });

    $scope.$watch(function () {
      return $rootScope.$ideDarkClass;
    }, function () {
      if ($rootScope.$ideDarkClass !== lastTheme) {
        // TODO: set the article theme without losing the current URL
        loadArticle();
      }
    });

    ctrl.goHome = function () {
      ctrl.src = '';
      if (ctrl.homeArticle) {
        ctrl.articleId = ctrl.homeArticle;
      } else if (ctrl.articleList) {
        ctrl.articleId = null;
      }
      $timeout(loadArticle);
    };

    // ctrl.go = (dir) => {
    //	// TODO: handle history control via postmessage?
    // };

    ctrl.newWindow = function () {
      if (ctrl.src) {
        // TODO: handle when we've navigated away from the original source
        //   This requires using postMessage, or figuring out how to make the
        //   domains effectively the same
        $window.open(ctrl.src.toString().replace(/embedded=true&?/, ''));
      }
    };
  }]);

  // matches an image ref on it's own line
  var MD_BLOCK_IMAGE = /^\s*!\[[^\]]*\]\(([^)]+)\)\s*$/;
  var fenceTest = /^(```+)/;
  var fenceCount = /^```+/gm;
  var markdownEditorIndex = 0;

  var markdownEditorComponentModule = angular.module('Q.components.markdown-editor', [markdownComponentModule, codemirrorComponentModule, codemirrorOptionsComponentModule, fullscreenToggleComponentModule, docsPaneComponentModule, syncScrollComponentModule, appAlertComponentModule, fileUploadServiceModule, cloudinaryServiceModule, ngFileUploadComponentModule]).directive('markdownEditor', ['$timeout', function ($timeout) {
    return {
      restrict: 'AE',
      template: function template(tElement, tAttrs) {
        return tAttrs.hideTabs ? markdownEditorNoTabsTemplate : markdownEditorTemplate;
      },
      require: ['?ngModel', 'markdownEditor'],
      controller: 'MarkdownEditorCtrl as $ctrl',
      bindToController: true,
      scope: {
        disabled: '<ngDisabled',
        readonly: '<ngReadonly',
        nodeId: '@id',
        allowDarkMode: '<',
        hideTabs: '@',
        defaultTab: '@',
        collapseOnEmpty: '<',
        collapseOnBlur: '<',
        autoExpand: '<',
        stretch: '<',
        autofocus: '<',
        disableUploads: '<',
        onCursorActivity: '&',
        onPaste: '&',
        onCopy: '&',
        // Note: for hardcoded multiline placeholders, either use `&#10;` for the newline character or use angular properties:
        //
        //    placeholder="this has&#10;two lines"
        //    placeholder="{{ ::'this has\ntwo lines '}}"
        //
        // Neither are particularly readable, but the top one is probably better.
        placeholder: '@',
        syncScrollId: '@'
      },
      link: function link(scope, elem, attrs, _ref) {
        var _ref2 = slicedToArray(_ref, 2),
            ngModel = _ref2[0],
            ctrl = _ref2[1];

        ctrl.bindToModel(ngModel);
        elem.attr('tabindex', '-1');

        scope.$watch(function () {
          return ctrl.hasFocus;
        }, function (focus) {
          if (focus === true || focus === false) {
            elem.triggerHandler(focus ? 'focus' : 'blur');
          }
        });

        elem.on('focus', ctrl.setFocus);
        elem.on('blur', ctrl.setFocus);

        // prevents animation before initial rendering is complete
        $timeout(function () {
          return ctrl.ready = true;
        }, 100);
      }
    };
  }]).controller('MarkdownEditorCtrl', ['$scope', '$element', '$document', 'codemirrorOptions', '$timeout', 'appAlert', '$sce', 'fileUploadService', 'cloudinaryService', '$window', function MarkdownEditorCtrl($scope, $element, $document, codemirrorOptions, $timeout, appAlert, $sce, fileUploadService, cloudinaryService, $window) {
    var ctrl = this;

    var codeMirror = void 0;
    var ngModel = void 0;
    var index = ++markdownEditorIndex;

    ctrl.value = '';

    ctrl.syncId = function () {
      return ctrl.syncScrollId || (ctrl.nodeId ? 'markdown-editor-' + index : '');
    };

    ctrl.tabIndexForTabs = function () {
      return (ctrl.collapseOnEmpty && !ctrl.value || ctrl.collapseOnBlur) && !ctrl.hasFocus ? -1 : 0;
    };

    var blurTimeout = null;
    ctrl.setFocus = function () {
      var focus = $element[0].contains($document[0].activeElement);
      if (blurTimeout) {
        $timeout.cancel(blurTimeout);
        blurTimeout = null;
      }
      if (ctrl.hasFocus !== focus) {
        if (focus) {
          if (ctrl.collapseOnBlur && !ctrl.autoExpand && ctrl.ready) {
            // prevents jarring height transitions
            $element.children().css('min-height', $element.offsetHeight() + 'px');
          }
          ctrl.hasFocus = true;
        } else {
          if (ngModel) {
            ngModel.$setViewValue(ctrl.value, 'blur');
          }
          blurTimeout = $timeout(function () {
            if (ctrl.collapseOnBlur && !ctrl.show.edit) {
              ctrl.selectTab('edit', false);
            }
            $element.children().css('min-height', null);
            ctrl.hasFocus = false;
          }, 100);
        }
        $timeout(function () {
          if (codeMirror) codeMirror.refresh();
        }, 100, false);
      }
    };

    var cmOptions = null;
    ctrl.createOpts = function () {
      if (!cmOptions) {
        // create a new set of options for every markdown editor, because otherwise the placeholder
        // is overridden
        cmOptions = angular.copy(CODEMIRROR_DEFAULT_OPTIONS);
        cmOptions.mode = languages.markdown.codemirror;
        cmOptions.gutters = [];
        cmOptions.lineNumbers = false;
        cmOptions.styleActiveLine = false;
        cmOptions.inputStyle = 'contenteditable';
        cmOptions.spellcheck = true;
        if (!ctrl.allowDarkMode) {
          cmOptions.theme = CODEMIRROR_DEFAULT_OPTIONS.themes.light;
        } else {
          Object.defineProperty(cmOptions, 'theme', {
            get: function get$$1() {
              return CODEMIRROR_DEFAULT_OPTIONS.themes[codemirrorOptions.sharedOpts.theme];
            }
          });
        }
        cmOptions.onLoad = function (cm) {
          codeMirror = cm;
          cm.on('focus', function () {
            $scope.$applyAsync(ctrl.setFocus);
          });
          cm.on('blur', function () {
            $scope.$applyAsync(ctrl.setFocus);
          });
          cm.on('cursorActivity', function (cm) {
            return ctrl.onCursorActivity({ cm: cm });
          });
          var valueInitialized = false;
          cm.on('change', function (evt, change) {
            if (change.origin === 'paste') {
              ctrl.onPaste({ change: change });
            }
            // During playback (readonly + programmatic setValue), scroll to
            // the end of the new content so the viewer follows along.
            if (change.origin === 'setValue') {
              if (valueInitialized && ctrl.readonly) {
                var lastLine = cm.lastLine();
                cm.setCursor({
                  line: lastLine,
                  ch: cm.getLine(lastLine).length
                });
              }
              valueInitialized = true;
            }
          });
          var onCopy = function onCopy() {
            return ctrl.onCopy({ copied: cm.doc.getSelection('\n') });
          };
          cm.on('copy', onCopy);
          cm.on('cut', onCopy);

          if (!ctrl.disableUploads) {
            // Some file upload logic borrowed from https://github.com/chemix/Nette-Texy-CodeMirror
            cm.on('paste', function (instance, event) {
              var clipboardData = event.clipboardData;
              if (angular.isObject(clipboardData)) {
                _uploadFiles(event, clipboardData.files || clipboardData.items);
              }
            });
            cm.on('drop', function (instance, event) {
              _uploadFiles(event, event.dataTransfer && event.dataTransfer.files);
            });
          }

          trackLineWidgets(cm, MD_BLOCK_IMAGE, function (text, previous) {
            var image = text.match(MD_BLOCK_IMAGE)[1];
            if (previous) {
              var img = previous.element.firstChild;
              if (img.src !== image) {
                img.src = image;
                previous.widget.changed();
              }
              return { keep: true };
            } else {
              var element = document.createElement('div');
              element.className = 'markdown-editor-image-preview';
              var _img = document.createElement('img');
              _img.src = image;
              element.appendChild(_img);
              return {
                element: element,
                options: { above: true, handleMouseEvents: true }
              };
            }
          });
        };
        cmOptions.placeholder = ctrl.placeholder;
        var isInCode = function isInCode(cm) {
          var line = cm.getCursor().line;
          // see if we're in a code block
          while (line >= 0) {
            var lineTokens = cm.getLineTokens(line);
            if (lineTokens[0] && lineTokens[0].state.overlay) {
              return !!lineTokens[0].state.overlay.codeBlock;
            }
            line--;
          }
          return false;
        };
        cmOptions.extraKeys.Tab = function (cm) {
          if (isInCode(cm)) {
            return $window.CodeMirror.commands.indentMore(cm);
          }
          focusNextElement();
        };
        cmOptions.extraKeys['Shift-Tab'] = function (cm) {
          if (isInCode(cm)) {
            return $window.CodeMirror.commands.indentLess(cm);
          }
          focusPreviousElement();
        };
        cmOptions.extraKeys.Enter = function (cm) {
          // attempt to complete a fenced code block
          var sel = cm.listSelections();
          var cursor = cm.getCursor();
          if (sel.length === 1 && fenceTest.test(cm.getLine(cursor.line))) {
            var fences = cm.getValue().match(fenceCount);
            if (fences.length % 2) {
              // if there's an odd number of fences, then we'll insert the next one automatically
              var fence = cm.getLine(cursor.line).match(fenceTest)[1];
              cm.replaceSelection('\n');
              var updatedCursor = cm.getCursor();
              cm.replaceSelection('\n' + fence);
              cm.setCursor(updatedCursor);
              return;
            }
          }
          // default to the original command
          return $window.CodeMirror.commands.newlineAndIndentContinueMarkdownList(cm);
        };
      }
      return cmOptions;
    };

    // bind our internal ngModel value to the external ngModel value
    ctrl.bindToModel = function (_ngModel) {
      ngModel = _ngModel;
      if (!ngModel) return;

      ngModel.$render = function () {
        return ctrl.value = ngModel.$viewValue;
      };
      $scope.$watch('$ctrl.value', function (newValue) {
        ngModel.$setViewValue(newValue, ctrl.hasFocus ? undefined : 'blur');
      });
      $scope.$on('$destroy', function () {
        // ensure we trigger a blur change if getting destroyed
        ngModel.$setViewValue(ctrl.value, 'blur');
      });
    };

    var show = ctrl.show = {
      edit: true,
      preview: false,
      help: false
    };

    var smallScreen = false;

    ctrl.selectTab = function (which) {
      var setFocus = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

      if (which === 'help') {
        if (!ctrl.helpArticle) {
          ctrl.helpArticle = 'reference/markdown';
        }
        if (!ctrl.fullscreen) {
          $window.open(getDocsURL(ctrl.helpArticle), 'qualified-help', 'noopener,noreferrer');
          return;
        }
      }

      ctrl.setFocus();

      if (!ctrl.fullscreen || smallScreen) {
        show.edit = show.preview = show.help = false;
      } else if (which !== 'edit') {
        show.preview = show.help = false;
      }
      ctrl.show[which] = true;

      if (which === 'edit' && setFocus) {
        if (codeMirror) {
          $timeout(function () {
            codeMirror.refresh();
            codeMirror.focus();
          }, 100, false);
        }
      }
    };

    ctrl.$onInit = function () {
      $timeout(function () {
        ctrl.selectTab(ctrl.defaultTab || 'edit', false);
      });
    };

    $scope.$watch('$ctrl.fullscreen', function (fs, ofs) {
      if (fs !== ofs) {
        smallScreen = angular.element($window).offsetWidth() <= 768;
        ctrl.ready = false;
        $element.children().css('min-height', null);
        if (fs && !smallScreen) {
          show.edit = show.preview = true;
          show.help = false;
        } else {
          show.edit = true;
          show.preview = show.help = false;
        }
        $timeout(function () {
          ctrl.ready = true;
          if (codeMirror) {
            codeMirror.refresh();
            if (ofs !== undefined) {
              codeMirror.focus();
            }
          }
        }, 100);
      }
    });

    function _uploadFiles(event, files) {
      files = Array.from(files || []);
      if (!files.length || ctrl.readonly || ctrl.disabled) return;

      stopEvent(event);
      if (angular.isNumber(event.clientX) && angular.isNumber(event.clientY)) {
        // attempt to move the cursor if we're dropping somewhere specific
        codeMirror.setCursor(codeMirror.coordsChar({ left: event.clientX, top: event.clientY }));
      }

      var images = [];
      var rejects = [];
      files.forEach(function (f) {
        f = angular.isFunction(f.getAsFile) ? f.getAsFile() : f;
        if (fileUploadService.imageValidator(f)) {
          images.push(f);
        } else {
          rejects.push(f);
        }
      });
      if (!images.length && rejects.length) {
        var msg = '<strong>Cannot embed ' + (rejects.length > 1 ? 'those file types' : 'that file type') + '.</strong>' + (' You can use ' + fileUploadService.imageValidator.label + '.');
        if (event.type === 'paste') {
          msg += '<br>(Note that you may not be able to paste multiple files at once.)';
        }
        appAlert.warning($sce.trustAsHtml(msg));
        return;
      }
      images.forEach(function (file) {
        var uploadPromise = fileUploadService.handleSingle(file, { showProgress: false, imageExtras: false }).then(function (fileData) {
          return cloudinaryService.getUrl(fileData, {
            width: 1000,
            height: 1000,
            crop: 'limit'
          });
        }).catch(appAlert.modelError);
        new MarkdownFileUploader(codeMirror, file).start(uploadPromise);
      });
    }

    ctrl.selectedFiles = function (files) {
      return _uploadFiles({}, files);
    };
  }]);

  var relinkOnChangeComponentModule = angular.module('Q.components.relink-on-change', [])

  /**
   * This directive can be used to force child directives to relink if the provided value changes.
   *
   * It does this by destroying the existing elements and scope, and transcluding them back, similar to
   * how ngIf works, but with a more focused design.
   */
  .directive('relinkOnChange', function () {
    return {
      restrict: 'A',
      transclude: 'element',
      priority: 600, // equal to ng-if
      link: function link($scope, $element, $attr, ctrl, $transclude) {
        var childScope = void 0;
        var previousElements = void 0;
        $scope.$watch($attr.relinkOnChange, function (val, oldVal) {
          if (val !== oldVal) {
            if (previousElements) {
              previousElements.remove();
            }
            if (childScope) {
              childScope.$destroy();
            }
          }

          $transclude(function (clone, newScope) {
            childScope = newScope;
            previousElements = clone;
            // this tries to prevent the dom element ordering from getting screwed up
            if ($element[0].parentNode && $element[0].previousElementSibling) {
              $element.after(clone);
            } else {
              $element.parent().prepend(clone);
            }
          });
        });
      }
    };
  });

  var templates = createCommonjsModule(function (module, exports) {

      Object.defineProperty(exports, "__esModule", { value: true });
      function getMainFile(template) {
          if (template === "vue-cli") {
              return "src/main.js";
          }
          if (template === "angular-cli") {
              return "src/main.ts";
          }
          if (template === "create-react-app-typescript") {
              return "src/index.tsx";
          }
          if (template === "parcel") {
              return "index.html";
          }
          if (template === "gatsby") {
              return "src/pages/index.js";
          }
          if (template === "nuxt") {
              // Wildcard, because nuxt is not specific on this
              return "package.json";
          }
          if (template === "next") {
              // Wildcard, because next is not specific on this
              return "package.json";
          }
          if (template === "apollo") {
              // Wildcard, because apollo is not specific on this
              return "package.json";
          }
          if (template === "reason") {
              // Wildcard, because reason is not specific on this
              return "package.json";
          }
          if (template === "sapper") {
              // Wildcard, because sapper is not specific on this
              return "package.json";
          }
          if (template === "nest") {
              return "src/main.ts";
          }
          if (template === "static") {
              return "index.html";
          }
          return "src/index.js";
      }
      exports.getMainFile = getMainFile;
      var SANDBOX_CONFIG = "sandbox.config.json";
      function getTemplate(packageJSONPackage, modules) {
          var sandboxConfig = modules[SANDBOX_CONFIG] || modules["/" + SANDBOX_CONFIG];
          if (sandboxConfig) {
              try {
                  var config = JSON.parse(sandboxConfig.content);
                  if (config.template) {
                      return config.template;
                  }
              } catch (e) {}
          }
          var _a = packageJSONPackage.dependencies,
              dependencies = _a === void 0 ? {} : _a,
              _b = packageJSONPackage.devDependencies,
              devDependencies = _b === void 0 ? {} : _b;
          var totalDependencies = Object.keys(dependencies).concat(Object.keys(devDependencies));
          var nuxt = ["nuxt", "nuxt-edge"];
          if (totalDependencies.some(function (dep) {
              return nuxt.indexOf(dep) > -1;
          })) {
              return "nuxt";
          }
          if (totalDependencies.indexOf("next") > -1) {
              return "next";
          }
          var apollo = ["apollo-server", "apollo-server-express", "apollo-server-hapi", "apollo-server-koa", "apollo-server-lambda", "apollo-server-micro"];
          if (totalDependencies.some(function (dep) {
              return apollo.indexOf(dep) > -1;
          })) {
              return "apollo";
          }
          if (totalDependencies.indexOf("ember-cli") > -1) {
              return "ember";
          }
          if (totalDependencies.indexOf("sapper") > -1) {
              return "sapper";
          }
          var moduleNames = Object.keys(modules);
          if (moduleNames.some(function (m) {
              return m.endsWith(".vue");
          })) {
              return "vue-cli";
          }
          if (moduleNames.some(function (m) {
              return m.endsWith(".re");
          })) {
              return "reason";
          }
          if (totalDependencies.indexOf("gatsby") > -1) {
              return "gatsby";
          }
          if (totalDependencies.indexOf("parcel-bundler") > -1) {
              return "parcel";
          }
          if (totalDependencies.indexOf("react-scripts") > -1) {
              return "create-react-app";
          }
          if (totalDependencies.indexOf("react-scripts-ts") > -1) {
              return "create-react-app-typescript";
          }
          if (totalDependencies.indexOf("@angular/core") > -1) {
              return "angular-cli";
          }
          if (totalDependencies.indexOf("preact-cli") > -1) {
              return "preact-cli";
          }
          if (totalDependencies.indexOf("svelte") > -1) {
              return "svelte";
          }
          if (totalDependencies.indexOf("vue") > -1) {
              return "vue-cli";
          }
          var dojo = ["@dojo/core", "@dojo/framework"];
          if (totalDependencies.some(function (dep) {
              return dojo.indexOf(dep) > -1;
          })) {
              return "@dojo/cli-create-app";
          }
          if (totalDependencies.indexOf("cx") > -1) {
              return "cxjs";
          }
          if (totalDependencies.indexOf("@nestjs/core") > -1 || totalDependencies.indexOf("@nestjs/common") > -1) {
              return "nest";
          }
          return undefined;
      }
      exports.getTemplate = getTemplate;
      
  });

  unwrapExports(templates);
  var templates_1 = templates.getMainFile;
  var templates_2 = templates.getTemplate;

  var lodash_isequal = createCommonjsModule(function (module, exports) {
    /**
     * Lodash (Custom Build) <https://lodash.com/>
     * Build: `lodash modularize exports="npm" -o ./`
     * Copyright JS Foundation and other contributors <https://js.foundation/>
     * Released under MIT license <https://lodash.com/license>
     * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
     * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
     */

    /** Used as the size to enable large array optimizations. */
    var LARGE_ARRAY_SIZE = 200;

    /** Used to stand-in for `undefined` hash values. */
    var HASH_UNDEFINED = '__lodash_hash_undefined__';

    /** Used to compose bitmasks for value comparisons. */
    var COMPARE_PARTIAL_FLAG = 1,
        COMPARE_UNORDERED_FLAG = 2;

    /** Used as references for various `Number` constants. */
    var MAX_SAFE_INTEGER = 9007199254740991;

    /** `Object#toString` result references. */
    var argsTag = '[object Arguments]',
        arrayTag = '[object Array]',
        asyncTag = '[object AsyncFunction]',
        boolTag = '[object Boolean]',
        dateTag = '[object Date]',
        errorTag = '[object Error]',
        funcTag = '[object Function]',
        genTag = '[object GeneratorFunction]',
        mapTag = '[object Map]',
        numberTag = '[object Number]',
        nullTag = '[object Null]',
        objectTag = '[object Object]',
        promiseTag = '[object Promise]',
        proxyTag = '[object Proxy]',
        regexpTag = '[object RegExp]',
        setTag = '[object Set]',
        stringTag = '[object String]',
        symbolTag = '[object Symbol]',
        undefinedTag = '[object Undefined]',
        weakMapTag = '[object WeakMap]';

    var arrayBufferTag = '[object ArrayBuffer]',
        dataViewTag = '[object DataView]',
        float32Tag = '[object Float32Array]',
        float64Tag = '[object Float64Array]',
        int8Tag = '[object Int8Array]',
        int16Tag = '[object Int16Array]',
        int32Tag = '[object Int32Array]',
        uint8Tag = '[object Uint8Array]',
        uint8ClampedTag = '[object Uint8ClampedArray]',
        uint16Tag = '[object Uint16Array]',
        uint32Tag = '[object Uint32Array]';

    /**
     * Used to match `RegExp`
     * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
     */
    var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

    /** Used to detect host constructors (Safari). */
    var reIsHostCtor = /^\[object .+?Constructor\]$/;

    /** Used to detect unsigned integer values. */
    var reIsUint = /^(?:0|[1-9]\d*)$/;

    /** Used to identify `toStringTag` values of typed arrays. */
    var typedArrayTags = {};
    typedArrayTags[float32Tag] = typedArrayTags[float64Tag] = typedArrayTags[int8Tag] = typedArrayTags[int16Tag] = typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] = typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] = typedArrayTags[uint32Tag] = true;
    typedArrayTags[argsTag] = typedArrayTags[arrayTag] = typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] = typedArrayTags[dataViewTag] = typedArrayTags[dateTag] = typedArrayTags[errorTag] = typedArrayTags[funcTag] = typedArrayTags[mapTag] = typedArrayTags[numberTag] = typedArrayTags[objectTag] = typedArrayTags[regexpTag] = typedArrayTags[setTag] = typedArrayTags[stringTag] = typedArrayTags[weakMapTag] = false;

    /** Detect free variable `global` from Node.js. */
    var freeGlobal = _typeof(commonjsGlobal) == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

    /** Detect free variable `self`. */
    var freeSelf = (typeof self === 'undefined' ? 'undefined' : _typeof(self)) == 'object' && self && self.Object === Object && self;

    /** Used as a reference to the global object. */
    var root = freeGlobal || freeSelf || Function('return this')();

    /** Detect free variable `exports`. */
    var freeExports = 'object' == 'object' && exports && !exports.nodeType && exports;

    /** Detect free variable `module`. */
    var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

    /** Detect the popular CommonJS extension `module.exports`. */
    var moduleExports = freeModule && freeModule.exports === freeExports;

    /** Detect free variable `process` from Node.js. */
    var freeProcess = moduleExports && freeGlobal.process;

    /** Used to access faster Node.js helpers. */
    var nodeUtil = function () {
      try {
        return freeProcess && freeProcess.binding && freeProcess.binding('util');
      } catch (e) {}
    }();

    /* Node.js helper references. */
    var nodeIsTypedArray = nodeUtil && nodeUtil.isTypedArray;

    /**
     * A specialized version of `_.filter` for arrays without support for
     * iteratee shorthands.
     *
     * @private
     * @param {Array} [array] The array to iterate over.
     * @param {Function} predicate The function invoked per iteration.
     * @returns {Array} Returns the new filtered array.
     */
    function arrayFilter(array, predicate) {
      var index = -1,
          length = array == null ? 0 : array.length,
          resIndex = 0,
          result = [];

      while (++index < length) {
        var value = array[index];
        if (predicate(value, index, array)) {
          result[resIndex++] = value;
        }
      }
      return result;
    }

    /**
     * Appends the elements of `values` to `array`.
     *
     * @private
     * @param {Array} array The array to modify.
     * @param {Array} values The values to append.
     * @returns {Array} Returns `array`.
     */
    function arrayPush(array, values) {
      var index = -1,
          length = values.length,
          offset = array.length;

      while (++index < length) {
        array[offset + index] = values[index];
      }
      return array;
    }

    /**
     * A specialized version of `_.some` for arrays without support for iteratee
     * shorthands.
     *
     * @private
     * @param {Array} [array] The array to iterate over.
     * @param {Function} predicate The function invoked per iteration.
     * @returns {boolean} Returns `true` if any element passes the predicate check,
     *  else `false`.
     */
    function arraySome(array, predicate) {
      var index = -1,
          length = array == null ? 0 : array.length;

      while (++index < length) {
        if (predicate(array[index], index, array)) {
          return true;
        }
      }
      return false;
    }

    /**
     * The base implementation of `_.times` without support for iteratee shorthands
     * or max array length checks.
     *
     * @private
     * @param {number} n The number of times to invoke `iteratee`.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Array} Returns the array of results.
     */
    function baseTimes(n, iteratee) {
      var index = -1,
          result = Array(n);

      while (++index < n) {
        result[index] = iteratee(index);
      }
      return result;
    }

    /**
     * The base implementation of `_.unary` without support for storing metadata.
     *
     * @private
     * @param {Function} func The function to cap arguments for.
     * @returns {Function} Returns the new capped function.
     */
    function baseUnary(func) {
      return function (value) {
        return func(value);
      };
    }

    /**
     * Checks if a `cache` value for `key` exists.
     *
     * @private
     * @param {Object} cache The cache to query.
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function cacheHas(cache, key) {
      return cache.has(key);
    }

    /**
     * Gets the value at `key` of `object`.
     *
     * @private
     * @param {Object} [object] The object to query.
     * @param {string} key The key of the property to get.
     * @returns {*} Returns the property value.
     */
    function getValue(object, key) {
      return object == null ? undefined : object[key];
    }

    /**
     * Converts `map` to its key-value pairs.
     *
     * @private
     * @param {Object} map The map to convert.
     * @returns {Array} Returns the key-value pairs.
     */
    function mapToArray(map) {
      var index = -1,
          result = Array(map.size);

      map.forEach(function (value, key) {
        result[++index] = [key, value];
      });
      return result;
    }

    /**
     * Creates a unary function that invokes `func` with its argument transformed.
     *
     * @private
     * @param {Function} func The function to wrap.
     * @param {Function} transform The argument transform.
     * @returns {Function} Returns the new function.
     */
    function overArg(func, transform) {
      return function (arg) {
        return func(transform(arg));
      };
    }

    /**
     * Converts `set` to an array of its values.
     *
     * @private
     * @param {Object} set The set to convert.
     * @returns {Array} Returns the values.
     */
    function setToArray(set$$1) {
      var index = -1,
          result = Array(set$$1.size);

      set$$1.forEach(function (value) {
        result[++index] = value;
      });
      return result;
    }

    /** Used for built-in method references. */
    var arrayProto = Array.prototype,
        funcProto = Function.prototype,
        objectProto = Object.prototype;

    /** Used to detect overreaching core-js shims. */
    var coreJsData = root['__core-js_shared__'];

    /** Used to resolve the decompiled source of functions. */
    var funcToString = funcProto.toString;

    /** Used to check objects for own properties. */
    var hasOwnProperty = objectProto.hasOwnProperty;

    /** Used to detect methods masquerading as native. */
    var maskSrcKey = function () {
      var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
      return uid ? 'Symbol(src)_1.' + uid : '';
    }();

    /**
     * Used to resolve the
     * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
     * of values.
     */
    var nativeObjectToString = objectProto.toString;

    /** Used to detect if a method is native. */
    var reIsNative = RegExp('^' + funcToString.call(hasOwnProperty).replace(reRegExpChar, '\\$&').replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$');

    /** Built-in value references. */
    var Buffer = moduleExports ? root.Buffer : undefined,
        _Symbol = root.Symbol,
        Uint8Array = root.Uint8Array,
        propertyIsEnumerable = objectProto.propertyIsEnumerable,
        splice = arrayProto.splice,
        symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

    /* Built-in method references for those with the same name as other `lodash` methods. */
    var nativeGetSymbols = Object.getOwnPropertySymbols,
        nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined,
        nativeKeys = overArg(Object.keys, Object);

    /* Built-in method references that are verified to be native. */
    var DataView = getNative(root, 'DataView'),
        Map = getNative(root, 'Map'),
        Promise = getNative(root, 'Promise'),
        Set = getNative(root, 'Set'),
        WeakMap = getNative(root, 'WeakMap'),
        nativeCreate = getNative(Object, 'create');

    /** Used to detect maps, sets, and weakmaps. */
    var dataViewCtorString = toSource(DataView),
        mapCtorString = toSource(Map),
        promiseCtorString = toSource(Promise),
        setCtorString = toSource(Set),
        weakMapCtorString = toSource(WeakMap);

    /** Used to convert symbols to primitives and strings. */
    var symbolProto = _Symbol ? _Symbol.prototype : undefined,
        symbolValueOf = symbolProto ? symbolProto.valueOf : undefined;

    /**
     * Creates a hash object.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function Hash(entries) {
      var index = -1,
          length = entries == null ? 0 : entries.length;

      this.clear();
      while (++index < length) {
        var entry = entries[index];
        this.set(entry[0], entry[1]);
      }
    }

    /**
     * Removes all key-value entries from the hash.
     *
     * @private
     * @name clear
     * @memberOf Hash
     */
    function hashClear() {
      this.__data__ = nativeCreate ? nativeCreate(null) : {};
      this.size = 0;
    }

    /**
     * Removes `key` and its value from the hash.
     *
     * @private
     * @name delete
     * @memberOf Hash
     * @param {Object} hash The hash to modify.
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function hashDelete(key) {
      var result = this.has(key) && delete this.__data__[key];
      this.size -= result ? 1 : 0;
      return result;
    }

    /**
     * Gets the hash value for `key`.
     *
     * @private
     * @name get
     * @memberOf Hash
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function hashGet(key) {
      var data = this.__data__;
      if (nativeCreate) {
        var result = data[key];
        return result === HASH_UNDEFINED ? undefined : result;
      }
      return hasOwnProperty.call(data, key) ? data[key] : undefined;
    }

    /**
     * Checks if a hash value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf Hash
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function hashHas(key) {
      var data = this.__data__;
      return nativeCreate ? data[key] !== undefined : hasOwnProperty.call(data, key);
    }

    /**
     * Sets the hash `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf Hash
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the hash instance.
     */
    function hashSet(key, value) {
      var data = this.__data__;
      this.size += this.has(key) ? 0 : 1;
      data[key] = nativeCreate && value === undefined ? HASH_UNDEFINED : value;
      return this;
    }

    // Add methods to `Hash`.
    Hash.prototype.clear = hashClear;
    Hash.prototype['delete'] = hashDelete;
    Hash.prototype.get = hashGet;
    Hash.prototype.has = hashHas;
    Hash.prototype.set = hashSet;

    /**
     * Creates an list cache object.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function ListCache(entries) {
      var index = -1,
          length = entries == null ? 0 : entries.length;

      this.clear();
      while (++index < length) {
        var entry = entries[index];
        this.set(entry[0], entry[1]);
      }
    }

    /**
     * Removes all key-value entries from the list cache.
     *
     * @private
     * @name clear
     * @memberOf ListCache
     */
    function listCacheClear() {
      this.__data__ = [];
      this.size = 0;
    }

    /**
     * Removes `key` and its value from the list cache.
     *
     * @private
     * @name delete
     * @memberOf ListCache
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function listCacheDelete(key) {
      var data = this.__data__,
          index = assocIndexOf(data, key);

      if (index < 0) {
        return false;
      }
      var lastIndex = data.length - 1;
      if (index == lastIndex) {
        data.pop();
      } else {
        splice.call(data, index, 1);
      }
      --this.size;
      return true;
    }

    /**
     * Gets the list cache value for `key`.
     *
     * @private
     * @name get
     * @memberOf ListCache
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function listCacheGet(key) {
      var data = this.__data__,
          index = assocIndexOf(data, key);

      return index < 0 ? undefined : data[index][1];
    }

    /**
     * Checks if a list cache value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf ListCache
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function listCacheHas(key) {
      return assocIndexOf(this.__data__, key) > -1;
    }

    /**
     * Sets the list cache `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf ListCache
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the list cache instance.
     */
    function listCacheSet(key, value) {
      var data = this.__data__,
          index = assocIndexOf(data, key);

      if (index < 0) {
        ++this.size;
        data.push([key, value]);
      } else {
        data[index][1] = value;
      }
      return this;
    }

    // Add methods to `ListCache`.
    ListCache.prototype.clear = listCacheClear;
    ListCache.prototype['delete'] = listCacheDelete;
    ListCache.prototype.get = listCacheGet;
    ListCache.prototype.has = listCacheHas;
    ListCache.prototype.set = listCacheSet;

    /**
     * Creates a map cache object to store key-value pairs.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function MapCache(entries) {
      var index = -1,
          length = entries == null ? 0 : entries.length;

      this.clear();
      while (++index < length) {
        var entry = entries[index];
        this.set(entry[0], entry[1]);
      }
    }

    /**
     * Removes all key-value entries from the map.
     *
     * @private
     * @name clear
     * @memberOf MapCache
     */
    function mapCacheClear() {
      this.size = 0;
      this.__data__ = {
        'hash': new Hash(),
        'map': new (Map || ListCache)(),
        'string': new Hash()
      };
    }

    /**
     * Removes `key` and its value from the map.
     *
     * @private
     * @name delete
     * @memberOf MapCache
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function mapCacheDelete(key) {
      var result = getMapData(this, key)['delete'](key);
      this.size -= result ? 1 : 0;
      return result;
    }

    /**
     * Gets the map value for `key`.
     *
     * @private
     * @name get
     * @memberOf MapCache
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function mapCacheGet(key) {
      return getMapData(this, key).get(key);
    }

    /**
     * Checks if a map value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf MapCache
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function mapCacheHas(key) {
      return getMapData(this, key).has(key);
    }

    /**
     * Sets the map `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf MapCache
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the map cache instance.
     */
    function mapCacheSet(key, value) {
      var data = getMapData(this, key),
          size = data.size;

      data.set(key, value);
      this.size += data.size == size ? 0 : 1;
      return this;
    }

    // Add methods to `MapCache`.
    MapCache.prototype.clear = mapCacheClear;
    MapCache.prototype['delete'] = mapCacheDelete;
    MapCache.prototype.get = mapCacheGet;
    MapCache.prototype.has = mapCacheHas;
    MapCache.prototype.set = mapCacheSet;

    /**
     *
     * Creates an array cache object to store unique values.
     *
     * @private
     * @constructor
     * @param {Array} [values] The values to cache.
     */
    function SetCache(values) {
      var index = -1,
          length = values == null ? 0 : values.length;

      this.__data__ = new MapCache();
      while (++index < length) {
        this.add(values[index]);
      }
    }

    /**
     * Adds `value` to the array cache.
     *
     * @private
     * @name add
     * @memberOf SetCache
     * @alias push
     * @param {*} value The value to cache.
     * @returns {Object} Returns the cache instance.
     */
    function setCacheAdd(value) {
      this.__data__.set(value, HASH_UNDEFINED);
      return this;
    }

    /**
     * Checks if `value` is in the array cache.
     *
     * @private
     * @name has
     * @memberOf SetCache
     * @param {*} value The value to search for.
     * @returns {number} Returns `true` if `value` is found, else `false`.
     */
    function setCacheHas(value) {
      return this.__data__.has(value);
    }

    // Add methods to `SetCache`.
    SetCache.prototype.add = SetCache.prototype.push = setCacheAdd;
    SetCache.prototype.has = setCacheHas;

    /**
     * Creates a stack cache object to store key-value pairs.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function Stack(entries) {
      var data = this.__data__ = new ListCache(entries);
      this.size = data.size;
    }

    /**
     * Removes all key-value entries from the stack.
     *
     * @private
     * @name clear
     * @memberOf Stack
     */
    function stackClear() {
      this.__data__ = new ListCache();
      this.size = 0;
    }

    /**
     * Removes `key` and its value from the stack.
     *
     * @private
     * @name delete
     * @memberOf Stack
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function stackDelete(key) {
      var data = this.__data__,
          result = data['delete'](key);

      this.size = data.size;
      return result;
    }

    /**
     * Gets the stack value for `key`.
     *
     * @private
     * @name get
     * @memberOf Stack
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function stackGet(key) {
      return this.__data__.get(key);
    }

    /**
     * Checks if a stack value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf Stack
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function stackHas(key) {
      return this.__data__.has(key);
    }

    /**
     * Sets the stack `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf Stack
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the stack cache instance.
     */
    function stackSet(key, value) {
      var data = this.__data__;
      if (data instanceof ListCache) {
        var pairs = data.__data__;
        if (!Map || pairs.length < LARGE_ARRAY_SIZE - 1) {
          pairs.push([key, value]);
          this.size = ++data.size;
          return this;
        }
        data = this.__data__ = new MapCache(pairs);
      }
      data.set(key, value);
      this.size = data.size;
      return this;
    }

    // Add methods to `Stack`.
    Stack.prototype.clear = stackClear;
    Stack.prototype['delete'] = stackDelete;
    Stack.prototype.get = stackGet;
    Stack.prototype.has = stackHas;
    Stack.prototype.set = stackSet;

    /**
     * Creates an array of the enumerable property names of the array-like `value`.
     *
     * @private
     * @param {*} value The value to query.
     * @param {boolean} inherited Specify returning inherited property names.
     * @returns {Array} Returns the array of property names.
     */
    function arrayLikeKeys(value, inherited) {
      var isArr = isArray(value),
          isArg = !isArr && isArguments(value),
          isBuff = !isArr && !isArg && isBuffer(value),
          isType = !isArr && !isArg && !isBuff && isTypedArray(value),
          skipIndexes = isArr || isArg || isBuff || isType,
          result = skipIndexes ? baseTimes(value.length, String) : [],
          length = result.length;

      for (var key in value) {
        if ((inherited || hasOwnProperty.call(value, key)) && !(skipIndexes && (
        // Safari 9 has enumerable `arguments.length` in strict mode.
        key == 'length' ||
        // Node.js 0.10 has enumerable non-index properties on buffers.
        isBuff && (key == 'offset' || key == 'parent') ||
        // PhantomJS 2 has enumerable non-index properties on typed arrays.
        isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset') ||
        // Skip index properties.
        isIndex(key, length)))) {
          result.push(key);
        }
      }
      return result;
    }

    /**
     * Gets the index at which the `key` is found in `array` of key-value pairs.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {*} key The key to search for.
     * @returns {number} Returns the index of the matched value, else `-1`.
     */
    function assocIndexOf(array, key) {
      var length = array.length;
      while (length--) {
        if (eq(array[length][0], key)) {
          return length;
        }
      }
      return -1;
    }

    /**
     * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
     * `keysFunc` and `symbolsFunc` to get the enumerable property names and
     * symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Function} keysFunc The function to get the keys of `object`.
     * @param {Function} symbolsFunc The function to get the symbols of `object`.
     * @returns {Array} Returns the array of property names and symbols.
     */
    function baseGetAllKeys(object, keysFunc, symbolsFunc) {
      var result = keysFunc(object);
      return isArray(object) ? result : arrayPush(result, symbolsFunc(object));
    }

    /**
     * The base implementation of `getTag` without fallbacks for buggy environments.
     *
     * @private
     * @param {*} value The value to query.
     * @returns {string} Returns the `toStringTag`.
     */
    function baseGetTag(value) {
      if (value == null) {
        return value === undefined ? undefinedTag : nullTag;
      }
      return symToStringTag && symToStringTag in Object(value) ? getRawTag(value) : objectToString(value);
    }

    /**
     * The base implementation of `_.isArguments`.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an `arguments` object,
     */
    function baseIsArguments(value) {
      return isObjectLike(value) && baseGetTag(value) == argsTag;
    }

    /**
     * The base implementation of `_.isEqual` which supports partial comparisons
     * and tracks traversed objects.
     *
     * @private
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @param {boolean} bitmask The bitmask flags.
     *  1 - Unordered comparison
     *  2 - Partial comparison
     * @param {Function} [customizer] The function to customize comparisons.
     * @param {Object} [stack] Tracks traversed `value` and `other` objects.
     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
     */
    function baseIsEqual(value, other, bitmask, customizer, stack) {
      if (value === other) {
        return true;
      }
      if (value == null || other == null || !isObjectLike(value) && !isObjectLike(other)) {
        return value !== value && other !== other;
      }
      return baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual, stack);
    }

    /**
     * A specialized version of `baseIsEqual` for arrays and objects which performs
     * deep comparisons and tracks traversed objects enabling objects with circular
     * references to be compared.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
     * @param {Function} customizer The function to customize comparisons.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Object} [stack] Tracks traversed `object` and `other` objects.
     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
     */
    function baseIsEqualDeep(object, other, bitmask, customizer, equalFunc, stack) {
      var objIsArr = isArray(object),
          othIsArr = isArray(other),
          objTag = objIsArr ? arrayTag : getTag(object),
          othTag = othIsArr ? arrayTag : getTag(other);

      objTag = objTag == argsTag ? objectTag : objTag;
      othTag = othTag == argsTag ? objectTag : othTag;

      var objIsObj = objTag == objectTag,
          othIsObj = othTag == objectTag,
          isSameTag = objTag == othTag;

      if (isSameTag && isBuffer(object)) {
        if (!isBuffer(other)) {
          return false;
        }
        objIsArr = true;
        objIsObj = false;
      }
      if (isSameTag && !objIsObj) {
        stack || (stack = new Stack());
        return objIsArr || isTypedArray(object) ? equalArrays(object, other, bitmask, customizer, equalFunc, stack) : equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
      }
      if (!(bitmask & COMPARE_PARTIAL_FLAG)) {
        var objIsWrapped = objIsObj && hasOwnProperty.call(object, '__wrapped__'),
            othIsWrapped = othIsObj && hasOwnProperty.call(other, '__wrapped__');

        if (objIsWrapped || othIsWrapped) {
          var objUnwrapped = objIsWrapped ? object.value() : object,
              othUnwrapped = othIsWrapped ? other.value() : other;

          stack || (stack = new Stack());
          return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
        }
      }
      if (!isSameTag) {
        return false;
      }
      stack || (stack = new Stack());
      return equalObjects(object, other, bitmask, customizer, equalFunc, stack);
    }

    /**
     * The base implementation of `_.isNative` without bad shim checks.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a native function,
     *  else `false`.
     */
    function baseIsNative(value) {
      if (!isObject(value) || isMasked(value)) {
        return false;
      }
      var pattern = isFunction(value) ? reIsNative : reIsHostCtor;
      return pattern.test(toSource(value));
    }

    /**
     * The base implementation of `_.isTypedArray` without Node.js optimizations.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
     */
    function baseIsTypedArray(value) {
      return isObjectLike(value) && isLength(value.length) && !!typedArrayTags[baseGetTag(value)];
    }

    /**
     * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     */
    function baseKeys(object) {
      if (!isPrototype(object)) {
        return nativeKeys(object);
      }
      var result = [];
      for (var key in Object(object)) {
        if (hasOwnProperty.call(object, key) && key != 'constructor') {
          result.push(key);
        }
      }
      return result;
    }

    /**
     * A specialized version of `baseIsEqualDeep` for arrays with support for
     * partial deep comparisons.
     *
     * @private
     * @param {Array} array The array to compare.
     * @param {Array} other The other array to compare.
     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
     * @param {Function} customizer The function to customize comparisons.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Object} stack Tracks traversed `array` and `other` objects.
     * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
     */
    function equalArrays(array, other, bitmask, customizer, equalFunc, stack) {
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
          arrLength = array.length,
          othLength = other.length;

      if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
        return false;
      }
      // Assume cyclic values are equal.
      var stacked = stack.get(array);
      if (stacked && stack.get(other)) {
        return stacked == other;
      }
      var index = -1,
          result = true,
          seen = bitmask & COMPARE_UNORDERED_FLAG ? new SetCache() : undefined;

      stack.set(array, other);
      stack.set(other, array);

      // Ignore non-index properties.
      while (++index < arrLength) {
        var arrValue = array[index],
            othValue = other[index];

        if (customizer) {
          var compared = isPartial ? customizer(othValue, arrValue, index, other, array, stack) : customizer(arrValue, othValue, index, array, other, stack);
        }
        if (compared !== undefined) {
          if (compared) {
            continue;
          }
          result = false;
          break;
        }
        // Recursively compare arrays (susceptible to call stack limits).
        if (seen) {
          if (!arraySome(other, function (othValue, othIndex) {
            if (!cacheHas(seen, othIndex) && (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
              return seen.push(othIndex);
            }
          })) {
            result = false;
            break;
          }
        } else if (!(arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
          result = false;
          break;
        }
      }
      stack['delete'](array);
      stack['delete'](other);
      return result;
    }

    /**
     * A specialized version of `baseIsEqualDeep` for comparing objects of
     * the same `toStringTag`.
     *
     * **Note:** This function only supports comparing values with tags of
     * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {string} tag The `toStringTag` of the objects to compare.
     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
     * @param {Function} customizer The function to customize comparisons.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Object} stack Tracks traversed `object` and `other` objects.
     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
     */
    function equalByTag(object, other, tag, bitmask, customizer, equalFunc, stack) {
      switch (tag) {
        case dataViewTag:
          if (object.byteLength != other.byteLength || object.byteOffset != other.byteOffset) {
            return false;
          }
          object = object.buffer;
          other = other.buffer;

        case arrayBufferTag:
          if (object.byteLength != other.byteLength || !equalFunc(new Uint8Array(object), new Uint8Array(other))) {
            return false;
          }
          return true;

        case boolTag:
        case dateTag:
        case numberTag:
          // Coerce booleans to `1` or `0` and dates to milliseconds.
          // Invalid dates are coerced to `NaN`.
          return eq(+object, +other);

        case errorTag:
          return object.name == other.name && object.message == other.message;

        case regexpTag:
        case stringTag:
          // Coerce regexes to strings and treat strings, primitives and objects,
          // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
          // for more details.
          return object == other + '';

        case mapTag:
          var convert = mapToArray;

        case setTag:
          var isPartial = bitmask & COMPARE_PARTIAL_FLAG;
          convert || (convert = setToArray);

          if (object.size != other.size && !isPartial) {
            return false;
          }
          // Assume cyclic values are equal.
          var stacked = stack.get(object);
          if (stacked) {
            return stacked == other;
          }
          bitmask |= COMPARE_UNORDERED_FLAG;

          // Recursively compare objects (susceptible to call stack limits).
          stack.set(object, other);
          var result = equalArrays(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
          stack['delete'](object);
          return result;

        case symbolTag:
          if (symbolValueOf) {
            return symbolValueOf.call(object) == symbolValueOf.call(other);
          }
      }
      return false;
    }

    /**
     * A specialized version of `baseIsEqualDeep` for objects with support for
     * partial deep comparisons.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
     * @param {Function} customizer The function to customize comparisons.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Object} stack Tracks traversed `object` and `other` objects.
     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
     */
    function equalObjects(object, other, bitmask, customizer, equalFunc, stack) {
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
          objProps = getAllKeys(object),
          objLength = objProps.length,
          othProps = getAllKeys(other),
          othLength = othProps.length;

      if (objLength != othLength && !isPartial) {
        return false;
      }
      var index = objLength;
      while (index--) {
        var key = objProps[index];
        if (!(isPartial ? key in other : hasOwnProperty.call(other, key))) {
          return false;
        }
      }
      // Assume cyclic values are equal.
      var stacked = stack.get(object);
      if (stacked && stack.get(other)) {
        return stacked == other;
      }
      var result = true;
      stack.set(object, other);
      stack.set(other, object);

      var skipCtor = isPartial;
      while (++index < objLength) {
        key = objProps[index];
        var objValue = object[key],
            othValue = other[key];

        if (customizer) {
          var compared = isPartial ? customizer(othValue, objValue, key, other, object, stack) : customizer(objValue, othValue, key, object, other, stack);
        }
        // Recursively compare objects (susceptible to call stack limits).
        if (!(compared === undefined ? objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack) : compared)) {
          result = false;
          break;
        }
        skipCtor || (skipCtor = key == 'constructor');
      }
      if (result && !skipCtor) {
        var objCtor = object.constructor,
            othCtor = other.constructor;

        // Non `Object` object instances with different constructors are not equal.
        if (objCtor != othCtor && 'constructor' in object && 'constructor' in other && !(typeof objCtor == 'function' && objCtor instanceof objCtor && typeof othCtor == 'function' && othCtor instanceof othCtor)) {
          result = false;
        }
      }
      stack['delete'](object);
      stack['delete'](other);
      return result;
    }

    /**
     * Creates an array of own enumerable property names and symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names and symbols.
     */
    function getAllKeys(object) {
      return baseGetAllKeys(object, keys, getSymbols);
    }

    /**
     * Gets the data for `map`.
     *
     * @private
     * @param {Object} map The map to query.
     * @param {string} key The reference key.
     * @returns {*} Returns the map data.
     */
    function getMapData(map, key) {
      var data = map.__data__;
      return isKeyable(key) ? data[typeof key == 'string' ? 'string' : 'hash'] : data.map;
    }

    /**
     * Gets the native function at `key` of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {string} key The key of the method to get.
     * @returns {*} Returns the function if it's native, else `undefined`.
     */
    function getNative(object, key) {
      var value = getValue(object, key);
      return baseIsNative(value) ? value : undefined;
    }

    /**
     * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
     *
     * @private
     * @param {*} value The value to query.
     * @returns {string} Returns the raw `toStringTag`.
     */
    function getRawTag(value) {
      var isOwn = hasOwnProperty.call(value, symToStringTag),
          tag = value[symToStringTag];

      try {
        value[symToStringTag] = undefined;
        var unmasked = true;
      } catch (e) {}

      var result = nativeObjectToString.call(value);
      if (unmasked) {
        if (isOwn) {
          value[symToStringTag] = tag;
        } else {
          delete value[symToStringTag];
        }
      }
      return result;
    }

    /**
     * Creates an array of the own enumerable symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of symbols.
     */
    var getSymbols = !nativeGetSymbols ? stubArray : function (object) {
      if (object == null) {
        return [];
      }
      object = Object(object);
      return arrayFilter(nativeGetSymbols(object), function (symbol) {
        return propertyIsEnumerable.call(object, symbol);
      });
    };

    /**
     * Gets the `toStringTag` of `value`.
     *
     * @private
     * @param {*} value The value to query.
     * @returns {string} Returns the `toStringTag`.
     */
    var getTag = baseGetTag;

    // Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
    if (DataView && getTag(new DataView(new ArrayBuffer(1))) != dataViewTag || Map && getTag(new Map()) != mapTag || Promise && getTag(Promise.resolve()) != promiseTag || Set && getTag(new Set()) != setTag || WeakMap && getTag(new WeakMap()) != weakMapTag) {
      getTag = function getTag(value) {
        var result = baseGetTag(value),
            Ctor = result == objectTag ? value.constructor : undefined,
            ctorString = Ctor ? toSource(Ctor) : '';

        if (ctorString) {
          switch (ctorString) {
            case dataViewCtorString:
              return dataViewTag;
            case mapCtorString:
              return mapTag;
            case promiseCtorString:
              return promiseTag;
            case setCtorString:
              return setTag;
            case weakMapCtorString:
              return weakMapTag;
          }
        }
        return result;
      };
    }

    /**
     * Checks if `value` is a valid array-like index.
     *
     * @private
     * @param {*} value The value to check.
     * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
     * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
     */
    function isIndex(value, length) {
      length = length == null ? MAX_SAFE_INTEGER : length;
      return !!length && (typeof value == 'number' || reIsUint.test(value)) && value > -1 && value % 1 == 0 && value < length;
    }

    /**
     * Checks if `value` is suitable for use as unique object key.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
     */
    function isKeyable(value) {
      var type = typeof value === 'undefined' ? 'undefined' : _typeof(value);
      return type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean' ? value !== '__proto__' : value === null;
    }

    /**
     * Checks if `func` has its source masked.
     *
     * @private
     * @param {Function} func The function to check.
     * @returns {boolean} Returns `true` if `func` is masked, else `false`.
     */
    function isMasked(func) {
      return !!maskSrcKey && maskSrcKey in func;
    }

    /**
     * Checks if `value` is likely a prototype object.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
     */
    function isPrototype(value) {
      var Ctor = value && value.constructor,
          proto = typeof Ctor == 'function' && Ctor.prototype || objectProto;

      return value === proto;
    }

    /**
     * Converts `value` to a string using `Object.prototype.toString`.
     *
     * @private
     * @param {*} value The value to convert.
     * @returns {string} Returns the converted string.
     */
    function objectToString(value) {
      return nativeObjectToString.call(value);
    }

    /**
     * Converts `func` to its source code.
     *
     * @private
     * @param {Function} func The function to convert.
     * @returns {string} Returns the source code.
     */
    function toSource(func) {
      if (func != null) {
        try {
          return funcToString.call(func);
        } catch (e) {}
        try {
          return func + '';
        } catch (e) {}
      }
      return '';
    }

    /**
     * Performs a
     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * comparison between two values to determine if they are equivalent.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
     * @example
     *
     * var object = { 'a': 1 };
     * var other = { 'a': 1 };
     *
     * _.eq(object, object);
     * // => true
     *
     * _.eq(object, other);
     * // => false
     *
     * _.eq('a', 'a');
     * // => true
     *
     * _.eq('a', Object('a'));
     * // => false
     *
     * _.eq(NaN, NaN);
     * // => true
     */
    function eq(value, other) {
      return value === other || value !== value && other !== other;
    }

    /**
     * Checks if `value` is likely an `arguments` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an `arguments` object,
     *  else `false`.
     * @example
     *
     * _.isArguments(function() { return arguments; }());
     * // => true
     *
     * _.isArguments([1, 2, 3]);
     * // => false
     */
    var isArguments = baseIsArguments(function () {
      return arguments;
    }()) ? baseIsArguments : function (value) {
      return isObjectLike(value) && hasOwnProperty.call(value, 'callee') && !propertyIsEnumerable.call(value, 'callee');
    };

    /**
     * Checks if `value` is classified as an `Array` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an array, else `false`.
     * @example
     *
     * _.isArray([1, 2, 3]);
     * // => true
     *
     * _.isArray(document.body.children);
     * // => false
     *
     * _.isArray('abc');
     * // => false
     *
     * _.isArray(_.noop);
     * // => false
     */
    var isArray = Array.isArray;

    /**
     * Checks if `value` is array-like. A value is considered array-like if it's
     * not a function and has a `value.length` that's an integer greater than or
     * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
     * @example
     *
     * _.isArrayLike([1, 2, 3]);
     * // => true
     *
     * _.isArrayLike(document.body.children);
     * // => true
     *
     * _.isArrayLike('abc');
     * // => true
     *
     * _.isArrayLike(_.noop);
     * // => false
     */
    function isArrayLike(value) {
      return value != null && isLength(value.length) && !isFunction(value);
    }

    /**
     * Checks if `value` is a buffer.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
     * @example
     *
     * _.isBuffer(new Buffer(2));
     * // => true
     *
     * _.isBuffer(new Uint8Array(2));
     * // => false
     */
    var isBuffer = nativeIsBuffer || stubFalse;

    /**
     * Performs a deep comparison between two values to determine if they are
     * equivalent.
     *
     * **Note:** This method supports comparing arrays, array buffers, booleans,
     * date objects, error objects, maps, numbers, `Object` objects, regexes,
     * sets, strings, symbols, and typed arrays. `Object` objects are compared
     * by their own, not inherited, enumerable properties. Functions and DOM
     * nodes are compared by strict equality, i.e. `===`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
     * @example
     *
     * var object = { 'a': 1 };
     * var other = { 'a': 1 };
     *
     * _.isEqual(object, other);
     * // => true
     *
     * object === other;
     * // => false
     */
    function isEqual(value, other) {
      return baseIsEqual(value, other);
    }

    /**
     * Checks if `value` is classified as a `Function` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a function, else `false`.
     * @example
     *
     * _.isFunction(_);
     * // => true
     *
     * _.isFunction(/abc/);
     * // => false
     */
    function isFunction(value) {
      if (!isObject(value)) {
        return false;
      }
      // The use of `Object#toString` avoids issues with the `typeof` operator
      // in Safari 9 which returns 'object' for typed arrays and other constructors.
      var tag = baseGetTag(value);
      return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
    }

    /**
     * Checks if `value` is a valid array-like length.
     *
     * **Note:** This method is loosely based on
     * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
     * @example
     *
     * _.isLength(3);
     * // => true
     *
     * _.isLength(Number.MIN_VALUE);
     * // => false
     *
     * _.isLength(Infinity);
     * // => false
     *
     * _.isLength('3');
     * // => false
     */
    function isLength(value) {
      return typeof value == 'number' && value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
    }

    /**
     * Checks if `value` is the
     * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
     * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an object, else `false`.
     * @example
     *
     * _.isObject({});
     * // => true
     *
     * _.isObject([1, 2, 3]);
     * // => true
     *
     * _.isObject(_.noop);
     * // => true
     *
     * _.isObject(null);
     * // => false
     */
    function isObject(value) {
      var type = typeof value === 'undefined' ? 'undefined' : _typeof(value);
      return value != null && (type == 'object' || type == 'function');
    }

    /**
     * Checks if `value` is object-like. A value is object-like if it's not `null`
     * and has a `typeof` result of "object".
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
     * @example
     *
     * _.isObjectLike({});
     * // => true
     *
     * _.isObjectLike([1, 2, 3]);
     * // => true
     *
     * _.isObjectLike(_.noop);
     * // => false
     *
     * _.isObjectLike(null);
     * // => false
     */
    function isObjectLike(value) {
      return value != null && (typeof value === 'undefined' ? 'undefined' : _typeof(value)) == 'object';
    }

    /**
     * Checks if `value` is classified as a typed array.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
     * @example
     *
     * _.isTypedArray(new Uint8Array);
     * // => true
     *
     * _.isTypedArray([]);
     * // => false
     */
    var isTypedArray = nodeIsTypedArray ? baseUnary(nodeIsTypedArray) : baseIsTypedArray;

    /**
     * Creates an array of the own enumerable property names of `object`.
     *
     * **Note:** Non-object values are coerced to objects. See the
     * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
     * for more details.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.keys(new Foo);
     * // => ['a', 'b'] (iteration order is not guaranteed)
     *
     * _.keys('hi');
     * // => ['0', '1']
     */
    function keys(object) {
      return isArrayLike(object) ? arrayLikeKeys(object) : baseKeys(object);
    }

    /**
     * This method returns a new empty array.
     *
     * @static
     * @memberOf _
     * @since 4.13.0
     * @category Util
     * @returns {Array} Returns the new empty array.
     * @example
     *
     * var arrays = _.times(2, _.stubArray);
     *
     * console.log(arrays);
     * // => [[], []]
     *
     * console.log(arrays[0] === arrays[1]);
     * // => false
     */
    function stubArray() {
      return [];
    }

    /**
     * This method returns `false`.
     *
     * @static
     * @memberOf _
     * @since 4.13.0
     * @category Util
     * @returns {boolean} Returns `false`.
     * @example
     *
     * _.times(2, _.stubFalse);
     * // => [false, false]
     */
    function stubFalse() {
      return false;
    }

    module.exports = isEqual;
  });

  /******************************************************************************
  Copyright (c) Microsoft Corporation.

  Permission to use, copy, modify, and/or distribute this software for any
  purpose with or without fee is hereby granted.

  THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
  REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
  AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
  INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
  LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
  OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
  PERFORMANCE OF THIS SOFTWARE.
  ***************************************************************************** */

  var _assign = function __assign() {
      _assign = Object.assign || function __assign(t) {
          for (var s, i = 1, n = arguments.length; i < n; i++) {
              s = arguments[i];
              for (var p in s) {
                  if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
              }
          }
          return t;
      };
      return _assign.apply(this, arguments);
  };

  function __awaiter(thisArg, _arguments, P, generator) {
      function adopt(value) {
          return value instanceof P ? value : new P(function (resolve) {
              resolve(value);
          });
      }
      return new (P || (P = Promise))(function (resolve, reject) {
          function fulfilled(value) {
              try {
                  step(generator.next(value));
              } catch (e) {
                  reject(e);
              }
          }
          function rejected(value) {
              try {
                  step(generator["throw"](value));
              } catch (e) {
                  reject(e);
              }
          }
          function step(result) {
              result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
          }
          step((generator = generator.apply(thisArg, _arguments || [])).next());
      });
  }

  function __generator(thisArg, body) {
      var _ = { label: 0, sent: function sent() {
              if (t[0] & 1) throw t[1];return t[1];
          }, trys: [], ops: [] },
          f,
          y,
          t,
          g;
      return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function () {
          return this;
      }), g;
      function verb(n) {
          return function (v) {
              return step([n, v]);
          };
      }
      function step(op) {
          if (f) throw new TypeError("Generator is already executing.");
          while (_) {
              try {
                  if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
                  if (y = 0, t) op = [op[0] & 2, t.value];
                  switch (op[0]) {
                      case 0:case 1:
                          t = op;break;
                      case 4:
                          _.label++;return { value: op[1], done: false };
                      case 5:
                          _.label++;y = op[1];op = [0];continue;
                      case 7:
                          op = _.ops.pop();_.trys.pop();continue;
                      default:
                          if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                              _ = 0;continue;
                          }
                          if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                              _.label = op[1];break;
                          }
                          if (op[0] === 6 && _.label < t[1]) {
                              _.label = t[1];t = op;break;
                          }
                          if (t && _.label < t[2]) {
                              _.label = t[2];_.ops.push(op);break;
                          }
                          if (t[2]) _.ops.pop();
                          _.trys.pop();continue;
                  }
                  op = body.call(thisArg, _);
              } catch (e) {
                  op = [6, e];y = 0;
              } finally {
                  f = t = 0;
              }
          }if (op[0] & 5) throw op[1];return { value: op[0] ? op[1] : void 0, done: true };
      }
  }

  function __spreadArray(to, from, pack) {
      if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
          if (ar || !(i in from)) {
              if (!ar) ar = Array.prototype.slice.call(from, 0, i);
              ar[i] = from[i];
          }
      }
      return to.concat(ar || Array.prototype.slice.call(from));
  }

  /**
   * This file is a copy of the resolver from the `codesandbox-api` package.
   * We wanted to avoid to reference codesandbox-api because of the code that runs on load in the package.
   * The plan is to take some time and refactor codesandbox-api into what it was supposed to be in the first place,
   * an abstraction over the actions that can be dispatched between the bundler and the iframe.
   */
  var Protocol = /** @class */function () {
      function Protocol(type, handleMessage, protocol) {
          var _this = this;
          this.type = type;
          this.handleMessage = handleMessage;
          this.protocol = protocol;
          this._disposeMessageListener = this.protocol.channelListen(function (msg) {
              return __awaiter(_this, void 0, void 0, function () {
                  var message, result, response, err_1, response;
                  return __generator(this, function (_a) {
                      switch (_a.label) {
                          case 0:
                              if (!(msg.type === this.getTypeId() && msg.method)) return [3 /*break*/, 4];
                              message = msg;
                              _a.label = 1;
                          case 1:
                              _a.trys.push([1, 3,, 4]);
                              return [4 /*yield*/, this.handleMessage(message)];
                          case 2:
                              result = _a.sent();
                              response = {
                                  type: this.getTypeId(),
                                  msgId: message.msgId,
                                  result: result
                              };
                              this.protocol.dispatch(response);
                              return [3 /*break*/, 4];
                          case 3:
                              err_1 = _a.sent();
                              response = {
                                  type: this.getTypeId(),
                                  msgId: message.msgId,
                                  error: {
                                      message: err_1.message
                                  }
                              };
                              this.protocol.dispatch(response);
                              return [3 /*break*/, 4];
                          case 4:
                              return [2 /*return*/];
                      }
                  });
              });
          });
      }
      Protocol.prototype.getTypeId = function () {
          return "protocol-" + this.type;
      };
      Protocol.prototype.dispose = function () {
          this._disposeMessageListener();
      };
      return Protocol;
  }();

  var IFrameProtocol = /** @class */function () {
      function IFrameProtocol(iframe, origin) {
          // React to messages from any iframe
          this.globalListeners = {};
          this.globalListenersCount = 0;
          // React to messages from the iframe owned by this instance
          this.channelListeners = {};
          this.channelListenersCount = 0;
          // Random number to identify this instance of the client when messages are coming from multiple iframes
          this.channelId = Math.floor(Math.random() * 1000000);
          this.frameWindow = iframe.contentWindow;
          this.origin = origin;
          this.globalListeners = [];
          this.channelListeners = [];
          this.eventListener = this.eventListener.bind(this);
          if (typeof window !== "undefined") {
              window.addEventListener("message", this.eventListener);
          }
      }
      IFrameProtocol.prototype.cleanup = function () {
          window.removeEventListener("message", this.eventListener);
          this.globalListeners = {};
          this.channelListeners = {};
          this.globalListenersCount = 0;
          this.channelListenersCount = 0;
      };
      // Sends the channelId and triggers an iframeHandshake promise to resolve,
      // so the iframe can start listening for messages (based on the id)
      IFrameProtocol.prototype.register = function () {
          if (!this.frameWindow) {
              return;
          }
          this.frameWindow.postMessage({
              type: "register-frame",
              origin: document.location.origin,
              id: this.channelId
          }, this.origin);
      };
      // Messages are dispatched from the client directly to the instance iframe
      IFrameProtocol.prototype.dispatch = function (message) {
          if (!this.frameWindow) {
              return;
          }
          this.frameWindow.postMessage(_assign({ $id: this.channelId, codesandbox: true }, message), this.origin);
      };
      // Add a listener that is called on any message coming from an iframe in the page
      // This is needed for the `initialize` message which comes without a channelId
      IFrameProtocol.prototype.globalListen = function (listener) {
          var _this = this;
          if (typeof listener !== "function") {
              return function () {
                  return;
              };
          }
          var listenerId = this.globalListenersCount;
          this.globalListeners[listenerId] = listener;
          this.globalListenersCount++;
          return function () {
              delete _this.globalListeners[listenerId];
          };
      };
      // Add a listener that is called on any message coming from an iframe with the instance channelId
      // All other messages (eg: from other iframes) are ignored
      IFrameProtocol.prototype.channelListen = function (listener) {
          var _this = this;
          if (typeof listener !== "function") {
              return function () {
                  return;
              };
          }
          var listenerId = this.channelListenersCount;
          this.channelListeners[listenerId] = listener;
          this.channelListenersCount++;
          return function () {
              delete _this.channelListeners[listenerId];
          };
      };
      // Handles message windows coming from iframes
      IFrameProtocol.prototype.eventListener = function (evt) {
          // skip events originating from different iframes
          if (evt.source !== this.frameWindow) {
              return;
          }
          var message = evt.data;
          if (!message.codesandbox) {
              return;
          }
          Object.values(this.globalListeners).forEach(function (listener) {
              return listener(message);
          });
          if (message.$id !== this.channelId) {
              return;
          }
          Object.values(this.channelListeners).forEach(function (listener) {
              return listener(message);
          });
      };
      return IFrameProtocol;
  }();

  var SandpackLogLevel;
  (function (SandpackLogLevel) {
      SandpackLogLevel[SandpackLogLevel["None"] = 0] = "None";
      SandpackLogLevel[SandpackLogLevel["Error"] = 10] = "Error";
      SandpackLogLevel[SandpackLogLevel["Warning"] = 20] = "Warning";
      SandpackLogLevel[SandpackLogLevel["Info"] = 30] = "Info";
      SandpackLogLevel[SandpackLogLevel["Debug"] = 40] = "Debug";
  })(SandpackLogLevel || (SandpackLogLevel = {}));

  var DEPENDENCY_ERROR_MESSAGE = "[sandpack-client]: \"dependencies\" was not specified - provide either a package.json or a \"dependencies\" value";
  var ENTRY_ERROR_MESSAGE = "[sandpack-client]: \"entry\" was not specified - provide either a package.json with the \"main\" field or an \"entry\" value";
  function createPackageJSON(dependencies, devDependencies, entry) {
      if (dependencies === void 0) {
          dependencies = {};
      }
      if (devDependencies === void 0) {
          devDependencies = {};
      }
      if (entry === void 0) {
          entry = "/index.js";
      }
      return JSON.stringify({
          name: "sandpack-project",
          main: entry,
          dependencies: dependencies,
          devDependencies: devDependencies
      }, null, 2);
  }
  function addPackageJSONIfNeeded(files, dependencies, devDependencies, entry) {
      var _a, _b;
      var normalizedFilesPath = normalizePath(files);
      var packageJsonFile = normalizedFilesPath["/package.json"];
      /**
       * Create a new package json
       */
      if (!packageJsonFile) {
          if (!dependencies) throw new Error(DEPENDENCY_ERROR_MESSAGE);
          if (!entry) throw new Error(ENTRY_ERROR_MESSAGE);
          normalizedFilesPath["/package.json"] = {
              code: createPackageJSON(dependencies, devDependencies, entry)
          };
          return normalizedFilesPath;
      }
      /**
       * Merge package json with custom setup
       */
      if (packageJsonFile) {
          var packageJsonContent = JSON.parse(packageJsonFile.code);
          if (!dependencies && !packageJsonContent.dependencies) {
              throw new Error(DEPENDENCY_ERROR_MESSAGE);
          }
          if (dependencies) {
              packageJsonContent.dependencies = _assign(_assign({}, (_a = packageJsonContent.dependencies) !== null && _a !== void 0 ? _a : {}), dependencies !== null && dependencies !== void 0 ? dependencies : {});
          }
          if (devDependencies) {
              packageJsonContent.devDependencies = _assign(_assign({}, (_b = packageJsonContent.devDependencies) !== null && _b !== void 0 ? _b : {}), devDependencies !== null && devDependencies !== void 0 ? devDependencies : {});
          }
          if (entry) {
              packageJsonContent.main = entry;
          }
          normalizedFilesPath["/package.json"] = {
              code: JSON.stringify(packageJsonContent, null, 2)
          };
      }
      return normalizedFilesPath;
  }
  function extractErrorDetails(msg) {
      if (msg.title === "SyntaxError") {
          var title = msg.title,
              path = msg.path,
              message = msg.message,
              line = msg.line,
              column = msg.column;
          return { title: title, path: path, message: message, line: line, column: column };
      }
      var relevantStackFrame = getRelevantStackFrame(msg.payload.frames);
      if (!relevantStackFrame) {
          return { message: msg.message };
      }
      var errorInCode = getErrorInOriginalCode(relevantStackFrame);
      var errorLocation = getErrorLocation(relevantStackFrame);
      var errorMessage = formatErrorMessage(relevantStackFrame._originalFileName, msg.message, errorLocation, errorInCode);
      return {
          message: errorMessage,
          title: msg.title,
          path: relevantStackFrame._originalFileName,
          line: relevantStackFrame._originalLineNumber,
          column: relevantStackFrame._originalColumnNumber
      };
  }
  function getRelevantStackFrame(frames) {
      if (!frames) {
          return;
      }
      return frames.find(function (frame) {
          return !!frame._originalFileName;
      });
  }
  function getErrorLocation(errorFrame) {
      return errorFrame ? " (" + errorFrame._originalLineNumber + ":" + errorFrame._originalColumnNumber + ")" : "";
  }
  function getErrorInOriginalCode(errorFrame) {
      var lastScriptLine = errorFrame._originalScriptCode[errorFrame._originalScriptCode.length - 1];
      var numberOfLineNumberCharacters = lastScriptLine.lineNumber.toString().length;
      var leadingCharacterOffset = 2;
      var barSeparatorCharacterOffset = 3;
      var extraLineLeadingSpaces = leadingCharacterOffset + numberOfLineNumberCharacters + barSeparatorCharacterOffset + errorFrame._originalColumnNumber;
      return errorFrame._originalScriptCode.reduce(function (result, scriptLine) {
          var leadingChar = scriptLine.highlight ? ">" : " ";
          var lineNumber = scriptLine.lineNumber.toString().length === numberOfLineNumberCharacters ? "" + scriptLine.lineNumber : " " + scriptLine.lineNumber;
          var extraLine = scriptLine.highlight ? "\n" + " ".repeat(extraLineLeadingSpaces) + "^" : "";
          return result + // accumulator
          "\n" + leadingChar + // > or " "
          " " + lineNumber + // line number on equal number of characters
          " | " + scriptLine.content + // code
          extraLine // line under the highlighed line to show the column index
          ;
      }, "");
  }
  function formatErrorMessage(filePath, message, location, errorInCode) {
      return filePath + ": " + message + location + "\n" + errorInCode;
  }
  /* eslint-disable @typescript-eslint/no-explicit-any */
  var normalizePath = function normalizePath(path) {
      if (typeof path === "string") {
          return path.startsWith("/") ? path : "/" + path;
      }
      if (Array.isArray(path)) {
          return path.map(function (p) {
              return p.startsWith("/") ? p : "/" + p;
          });
      }
      if ((typeof path === 'undefined' ? 'undefined' : _typeof(path)) === "object" && path !== null) {
          return Object.entries(path).reduce(function (acc, _a) {
              var key = _a[0],
                  content = _a[1];
              var fileName = key.startsWith("/") ? key : "/" + key;
              acc[fileName] = content;
              return acc;
          }, {});
      }
      return null;
  };

  var _a;
  var BUNDLER_URL = "https://" + ((_a = "1.20.9") === null || _a === void 0 ? void 0 : _a.replace(/\./g, "-")) + "-sandpack.codesandbox.io/";
  var SandpackClient = /** @class */function () {
      function SandpackClient(selector, sandboxInfo, options) {
          var _this = this;
          if (options === void 0) {
              options = {};
          }
          this.getTranspilerContext = function () {
              return new Promise(function (resolve) {
                  var unsubscribe = _this.listen(function (message) {
                      if (message.type === "transpiler-context") {
                          resolve(message.data);
                          unsubscribe();
                      }
                  });
                  _this.dispatch({ type: "get-transpiler-context" });
              });
          };
          this.options = options;
          this.sandboxInfo = sandboxInfo;
          this.bundlerURL = options.bundlerURL || BUNDLER_URL;
          this.bundlerState = undefined;
          this.errors = [];
          this.status = "initializing";
          if (typeof selector === "string") {
              this.selector = selector;
              var element = document.querySelector(selector);
              if (!element) {
                  throw new Error("[sandpack-client]: the element '" + selector + "' was not found");
              }
              this.element = element;
              this.iframe = document.createElement("iframe");
              this.initializeElement();
          } else {
              this.element = selector;
              this.iframe = selector;
          }
          if (!this.iframe.getAttribute("sandbox")) {
              this.iframe.setAttribute("sandbox", "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts");
              this.iframe.setAttribute("allow", "accelerometer; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; clipboard-write;");
          }
          this.setLocationURLIntoIFrame();
          this.iframeProtocol = new IFrameProtocol(this.iframe, this.bundlerURL);
          this.unsubscribeGlobalListener = this.iframeProtocol.globalListen(function (mes) {
              if (mes.type !== "initialized" || !_this.iframe.contentWindow) {
                  return;
              }
              _this.iframeProtocol.register();
              if (_this.options.fileResolver) {
                  _this.fileResolverProtocol = new Protocol("fs", function (data) {
                      return __awaiter(_this, void 0, void 0, function () {
                          return __generator(this, function (_a) {
                              if (data.method === "isFile") {
                                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                  return [2 /*return*/, this.options.fileResolver.isFile(data.params[0])];
                              } else if (data.method === "readFile") {
                                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                  return [2 /*return*/, this.options.fileResolver.readFile(data.params[0])];
                              } else {
                                  throw new Error("Method not supported");
                              }
                          });
                      });
                  }, _this.iframeProtocol);
              }
              _this.updatePreview(_this.sandboxInfo, true);
          });
          this.unsubscribeChannelListener = this.iframeProtocol.channelListen(function (mes) {
              switch (mes.type) {
                  case "start":
                      {
                          _this.errors = [];
                          break;
                      }
                  case "status":
                      {
                          _this.status = mes.status;
                          break;
                      }
                  case "action":
                      {
                          if (mes.action === "show-error") {
                              _this.errors = __spreadArray(__spreadArray([], _this.errors, true), [extractErrorDetails(mes)], false);
                          }
                          break;
                      }
                  case "state":
                      {
                          _this.bundlerState = mes.state;
                          break;
                      }
              }
          });
      }
      SandpackClient.prototype.setLocationURLIntoIFrame = function () {
          var _a;
          var urlSource = this.options.startRoute ? new URL(this.options.startRoute, this.bundlerURL).toString() : this.bundlerURL;
          (_a = this.iframe.contentWindow) === null || _a === void 0 ? void 0 : _a.location.replace(urlSource);
          this.iframe.src = urlSource;
      };
      SandpackClient.prototype.cleanup = function () {
          this.unsubscribeChannelListener();
          this.unsubscribeGlobalListener();
          this.iframeProtocol.cleanup();
      };
      SandpackClient.prototype.updateOptions = function (options) {
          if (!lodash_isequal(this.options, options)) {
              this.options = options;
              this.updatePreview();
          }
      };
      SandpackClient.prototype.updatePreview = function (sandboxInfo, isInitializationCompile) {
          var _a, _b, _c, _d;
          if (sandboxInfo === void 0) {
              sandboxInfo = this.sandboxInfo;
          }
          this.sandboxInfo = _assign(_assign({}, this.sandboxInfo), sandboxInfo);
          var files = this.getFiles();
          var modules = Object.keys(files).reduce(function (prev, next) {
              var _a;
              return _assign(_assign({}, prev), (_a = {}, _a[next] = {
                  code: files[next].code,
                  path: next
              }, _a));
          }, {});
          var packageJSON = JSON.parse(createPackageJSON(this.sandboxInfo.dependencies, this.sandboxInfo.devDependencies, this.sandboxInfo.entry));
          try {
              packageJSON = JSON.parse(files["/package.json"].code);
          } catch (e) {
              console.error("[sandpack-client]: could not parse package.json file: " + e.message);
          }
          // TODO move this to a common format
          var normalizedModules = Object.keys(files).reduce(function (prev, next) {
              var _a;
              return _assign(_assign({}, prev), (_a = {}, _a[next] = {
                  content: files[next].code,
                  path: next
              }, _a));
          }, {});
          this.dispatch({
              type: "compile",
              codesandbox: true,
              version: 3,
              isInitializationCompile: isInitializationCompile,
              modules: modules,
              reactDevTools: this.options.reactDevTools,
              externalResources: this.options.externalResources || [],
              hasFileResolver: Boolean(this.options.fileResolver),
              disableDependencyPreprocessing: this.sandboxInfo.disableDependencyPreprocessing,
              template: this.sandboxInfo.template || templates_2(packageJSON, normalizedModules),
              showOpenInCodeSandbox: (_a = this.options.showOpenInCodeSandbox) !== null && _a !== void 0 ? _a : true,
              showErrorScreen: (_b = this.options.showErrorScreen) !== null && _b !== void 0 ? _b : true,
              showLoadingScreen: (_c = this.options.showLoadingScreen) !== null && _c !== void 0 ? _c : true,
              skipEval: this.options.skipEval || false,
              clearConsoleDisabled: !this.options.clearConsoleOnFirstCompile,
              logLevel: (_d = this.options.logLevel) !== null && _d !== void 0 ? _d : SandpackLogLevel.Info,
              customNpmRegistries: this.options.customNpmRegistries
          });
      };
      SandpackClient.prototype.dispatch = function (message) {
          /**
           * Intercept "refresh" dispatch: this will make sure
           * that the iframe is still in the location it's supposed to be.
           * External links inside the iframe will change the location and
           * prevent the user from navigating back.
           */
          if (message.type === "refresh") {
              this.setLocationURLIntoIFrame();
          }
          this.iframeProtocol.dispatch(message);
      };
      SandpackClient.prototype.listen = function (listener) {
          return this.iframeProtocol.channelListen(listener);
      };
      /**
       * Get the URL of the contents of the current sandbox
       */
      SandpackClient.prototype.getCodeSandboxURL = function () {
          var files = this.getFiles();
          var paramFiles = Object.keys(files).reduce(function (prev, next) {
              var _a;
              return _assign(_assign({}, prev), (_a = {}, _a[next.replace("/", "")] = {
                  content: files[next].code,
                  isBinary: false
              }, _a));
          }, {});
          return fetch("https://codesandbox.io/api/v1/sandboxes/define?json=1", {
              method: "POST",
              body: JSON.stringify({ files: paramFiles }),
              headers: {
                  Accept: "application/json",
                  "Content-Type": "application/json"
              }
          }).then(function (x) {
              return x.json();
          }).then(function (res) {
              return {
                  sandboxId: res.sandbox_id,
                  editorUrl: "https://codesandbox.io/s/" + res.sandbox_id,
                  embedUrl: "https://codesandbox.io/embed/" + res.sandbox_id
              };
          });
      };
      SandpackClient.prototype.getFiles = function () {
          var sandboxInfo = this.sandboxInfo;
          if (sandboxInfo.files["/package.json"] === undefined) {
              return addPackageJSONIfNeeded(sandboxInfo.files, sandboxInfo.dependencies, sandboxInfo.devDependencies, sandboxInfo.entry);
          }
          return this.sandboxInfo.files;
      };
      SandpackClient.prototype.initializeElement = function () {
          this.iframe.style.border = "0";
          this.iframe.style.width = this.options.width || "100%";
          this.iframe.style.height = this.options.height || "100%";
          this.iframe.style.overflow = "hidden";
          if (!this.element.parentNode) {
              // This should never happen
              throw new Error("[sandpack-client]: the given iframe does not have a parent.");
          }
          this.element.parentNode.replaceChild(this.iframe, this.element);
      };
      return SandpackClient;
  }();

  var webPreviewTemplate = "<form class=\"web-preview-toolbar\" ng-submit=\"$ctrl.loaded && $ctrl.go()\"> <div class=\"nav-buttons btn-group\"> <button class=\"back btn-default btn-sm\" ng-click=\"$ctrl.back()\" ng-disabled=\"!$ctrl.hasBack || !$ctrl.loaded\" tooltip=\"Navigate Back\"></button> <button class=\"forward btn-default btn-sm\" ng-click=\"$ctrl.forward()\" ng-disabled=\"!$ctrl.hasForward || !$ctrl.loaded\" tooltip=\"Navigate Forward\"></button> <button class=\"reload btn-default btn-sm\" ng-click=\"$ctrl.reload()\" ng-disabled=\"!$ctrl.loaded && !$ctrl.neverLoaded\" tooltip=\"Reload the current route\"></button> <button class=\"home btn-default btn-sm\" ng-click=\"$ctrl.home()\" ng-disabled=\"!$ctrl.loaded || !$ctrl.path\" tooltip=\"Reload at the root\"></button> </div> <div class=\"url-input input-group input-group-inlined\"> <div class=\"input-group-addon input-group-addon-inlined no-gap\"> <span class=\"icon-globe\"></span> local/ </div> <input type=\"text\" class=\"form-control\" ng-model=\"$ctrl.path\" ng-readonly=\"!$ctrl.loaded\" autocomplete=\"off\"> <button type=\"submit\" class=\"go input-group-addon input-group-addon-inlined\" ng-class=\"{ active: $ctrl.lastPath !== $ctrl.path }\" tooltip=\"Load URL\"></button> </div> <div class=\"sync-buttons\"> <button class=\"responsive btn-default btn-sm {{$ctrl.windowSize ? 'active' : ''}}\" tooltip=\"Responsive Mode\" ng-click=\"$ctrl.toggleResponsiveMode()\"></button> <button class=\"sync btn-default btn-sm\" ng-click=\"$ctrl.detectedError ? $ctrl.resume() : $ctrl.sync(true)\" ng-disabled=\"!$ctrl.loaded\" tooltip=\"Manually synchronize the preview with the latest changes\"> <span class=\"{{ $ctrl.syncing ? 'loading-spinner' : 'icon-transport-replay' }}\"></span> Sync </button> <div class=\"auto-sync qualified-checkbox qualified-checkbox-switch-sm qualified-checkbox-switch qualified-checkbox-switch-onoff\" tooltip=\"When enabled, the preview will automatically synchronize to your solution as you make changes\"> <input type=\"checkbox\" ng-model=\"$ctrl.autoSync\" ng-change=\"$ctrl.autoSync && $ctrl.sync(true)\" ng-disabled=\"$ctrl.detectedError\" id=\"web-preview-checkbox-{{(challengeEdit || $ctrl.mfc || $ctrl)._id}}\"> <label for=\"web-preview-checkbox-{{(challengeEdit || $ctrl.mfc || $ctrl)._id}}\"> Auto </label> </div> </div> </form> <div ng-if=\"$ctrl.neverLoaded && !$ctrl.loadErrorDismissed\" class=\"alert alert-warning\" compile-template=\"$ctrl.loadErrorTemplate\"></div> <div class=\"responsive-mode-options\" ng-if=\"$ctrl.windowSize\"> <div class=\"dropdown\" dropdown dropdown-append-to-body=\"true\" dropdown-position=\"bottom\"> <button class=\"btn-default btn-xs dropdown-toggle preset-selector\" dropdown-toggle tooltip=\"Presets\"> {{ $ctrl.windowSize.preset.label || 'Custom' }} <span class=\"caret\"></span> </button> <ul class=\"dropdown-menu web-preview-responsive-mode\" dropdown-menu> <li class=\"dropdown-header\">Presets</li> <li ng-repeat=\"preset in ::$ctrl.responsivePresets track by preset.id\"> <a class=\"responsive-preset {{preset.id}} {{preset.selected ? 'selected' : ''}}\" ng-click=\"$ctrl.setResponsiveMode(preset.id)\"> <span class=\"icon-{{preset.icon}}\"></span> {{preset.label}} </a> </li> </ul> </div> <div class=\"responsive-size-inputs\"> <input type=\"number\" min=\"40\" pattern=\"^[1-9]\\d+$\" class=\"form-control input-sm\" ng-model=\"$ctrl.windowSize.width\" ng-model-options=\"{ updateOn: 'default blur', debounce: { 'default': 500, 'blur': 0 } }\" ng-change=\"$ctrl.customResponsiveMode()\"> <span>×</span> <input type=\"number\" min=\"40\" pattern=\"^[1-9]\\d+$\" class=\"form-control input-sm\" ng-model=\"$ctrl.windowSize.height\" ng-model-options=\"{ updateOn: 'default blur', debounce: { 'default': 500, 'blur': 0 } }\" ng-change=\"$ctrl.customResponsiveMode()\"> </div> <button class=\"btn-default btn-xs rotate-display {{$ctrl.windowSize.landscape ? 'landscape' : ''}}\" ng-click=\"$ctrl.rotateResponsiveMode()\" tooltip=\"Rotate Display\"></button> </div> <div class=\"responsive-wrapper {{$ctrl.windowSize ? 'responsive-mode' : ''}}\" ng-show=\"$ctrl.showPreview\"> <div class=\"responsive-resize-wrapper\"> <iframe class=\"web-preview-frame\"></iframe> </div> </div> <div class=\"web-preview-loading\" ng-show=\"$ctrl.showPreview && !$ctrl.loaded && !$ctrl.neverLoaded\"> <div class=\"help-block\"> <p class=\"loading-spinner loading-spinner-large\"></p> <p> {{$ctrl.loadingMessage}} <br> {{$ctrl.statusMessage}} </p> </div> </div> <div class=\"web-preview-info help-block markdown\" ng-hide=\"$ctrl.showPreview || $ctrl.detectedError\"> <p><strong>Web preview</strong></p> <p> To begin, make sure you have configured a <code>package.json</code> file within your codebase. </p> </div> <div class=\"web-preview-info help-block markdown\" ng-show=\"$ctrl.detectedError\"> <p><strong>Web preview</strong></p> <h2>Possible Error Detected</h2> <p> We've detected that you reloaded the page, possibly due to an error in web preview. </p> <p> To prevent you from getting stuck, we've paused web previews. If you'd like to continue, please click the button below. </p> <p> <button class=\"btn-primary\" ng-click=\"$ctrl.resumePreview()\"> Resume Web Previews </button> </p> </div> ";

  var webPreviewLoadErrorTemplate = "<button type=\"button\" aria-label=\"Close\" tooltip=\"Don't show this message again\" class=\"close\" ng-click=\"$ctrl.dismissLoadError()\"> <span aria-hidden=\"true\">×</span> </button> <p> There may be an issue loading the preview. <a class=\"link alert-link\" ng-click=\"$ctrl.reload()\">Please try again.</a> </p> <p>If that doesn't work, check the following and then reload the page:</p> <ul class=\"bullets\"> <li tooltip=\"Content blockers can prevent communication between the solver and the preview frame.\"> Disable any content or ad blockers for this page <tooltip-help-icon></tooltip-help-icon> </li> <li tooltip=\"This is needed because LocalStorage and Web Workers are blocked otherwise, preventing web testing.\"> Make sure cookies are enabled (including 3rd-party, which can be disabled by default) <tooltip-help-icon></tooltip-help-icon> </li> <li tooltip=\"This is needed because LocalStorage is blocked otherwise, preventing web testing.\"> Ensure you are not using <em>Incognito Mode</em> or <em>Private Browsing</em> <tooltip-help-icon></tooltip-help-icon> </li> <li ng-show=\"$ctrl.iframeSrc\" tooltip=\"Sometimes firewalls can prevent access to the web preview web server.\"> Make sure you have access to <a ng-href=\"{{$ctrl.iframeSrc}}\" target=\"_blank\" class=\"link alert-link\" ng-bind=\"$ctrl.iframeSrc\"></a> <tooltip-help-icon></tooltip-help-icon> </li> </ul> ";

  var webPreviewLoadErrorDialogTemplate = "<div class=\"modal-header\"> <h4 class=\"modal-title\"> Error Loading Web Preview <button class=\"close\" aria-label=\"Close\" ng-click=\"$dismiss()\"> <span aria-hidden=\"true\">&times;</span> </button> </h4> </div> <div class=\"modal-body\" compile-template=\"$ctrl.loadErrorTemplate\"></div> <div class=\"modal-footer\"> <button class=\"btn-default\" ng-click=\"$dismiss()\">Cancel</button> <button class=\"btn-primary\" ng-click=\"$ctrl.reload()\">Try Again</button> </div> ";

  var webPreviewConsoleTemplate = "<form class=\"web-preview-console-toolbar\"> <div class=\"nav-buttons btn-group\"> <button class=\"clear btn-default btn-sm\" ng-click=\"$ctrl.clear()\" ng-disabled=\"!$ctrl.console.length\" tooltip=\"Clear Console\"></button> <button class=\"line-wrap btn-default btn-sm\" ng-class=\"{ active: $ctrl.console.lineWrap }\" ng-click=\"$ctrl.console.lineWrap = !$ctrl.console.lineWrap\" tooltip=\"Wrap long lines\"></button> <div class=\"persist qualified-checkbox qualified-checkbox-switch-sm qualified-checkbox-switch qualified-checkbox-switch-onoff\" tooltip=\"When enabled, do not clear logs when reloading or syncing\"> <input type=\"checkbox\" ng-model=\"$ctrl.console.persist\" id=\"web-preview-console-persist-{{(challengeEdit || $ctrl.mfc || $ctrl)._id}}\"> <label for=\"web-preview-console-persist-{{(challengeEdit || $ctrl.mfc || $ctrl)._id}}\"> Persist </label> </div> <div class=\"spacer\"></div> <button class=\"auto-scroll btn-default btn-sm\" ng-class=\"{ active: $ctrl.autoScroll }\" ng-click=\"$ctrl.toggleAutoScroll()\" tooltip=\"Auto scroll console as logs come in\"></button> </div> </form> ";

  var webPreviewConsoleComponentModule = angular.module('Q.components.web-preview-console', [scrollMonitorServiceModule]).component('webPreviewConsole', {
    bindings: {
      console: '<'
    },
    controller: 'WebPreviewConsoleCtrl',
    template: webPreviewConsoleTemplate
  }).controller('WebPreviewConsoleCtrl', ['$scope', '$element', 'scrollMonitorService', function WebPreviewConsoleCtrl($scope, $element, scrollMonitorService) {
    var ctrl = this;
    var $history = $element.querySelector('.web-preview-console-history');

    ctrl.clear = function () {
      ctrl.console.length = 0;
    };

    ctrl.autoScroll = true;
    ctrl.toggleAutoScroll = function () {
      ctrl.autoScroll = !ctrl.autoScroll;
      if (ctrl.autoScroll) {
        $history.scrollTop($history[0].scrollHeight - $history[0].offsetHeight);
      }
    };

    ctrl.expand = function (d) {
      if (d.expandable) {
        ctrl.autoScroll = false;
        d.expanded = !d.expanded;
      }
    };

    ctrl.$onInit = function () {
      if (ctrl.console.lineWrap === undefined) {
        ctrl.console.lineWrap = true;
      }
      var scrollWatcher = scrollMonitorService.addListener($history, function (event, data) {
        if (event.type === 'scroll' || event.type === 'touchmove') {
          // if we're at the bottom, auto scroll
          ctrl.autoScroll = data.viewportScrollHeight <= data.viewportScrollTop + data.viewportHeight;
          // update UI if necessary
          $scope.$applyAsync();
        } else if (ctrl.autoScroll) {
          // scroll to bottom if anything changed
          $history.scrollTop(data.viewportScrollHeight - data.viewportHeight);
        }
      });
      $scope.$on('$destroy', function () {
        scrollWatcher.destroy();
      });
    };
  }]);

  /**
   * Checks if `value` is the
   * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
   * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
   *
   * @static
   * @memberOf _
   * @since 0.1.0
   * @category Lang
   * @param {*} value The value to check.
   * @returns {boolean} Returns `true` if `value` is an object, else `false`.
   * @example
   *
   * _.isObject({});
   * // => true
   *
   * _.isObject([1, 2, 3]);
   * // => true
   *
   * _.isObject(_.noop);
   * // => true
   *
   * _.isObject(null);
   * // => false
   */
  function isObject$1(value) {
    var type = typeof value === 'undefined' ? 'undefined' : _typeof(value);
    return value != null && (type == 'object' || type == 'function');
  }

  var isObject_1 = isObject$1;

  /** Detect free variable `global` from Node.js. */
  var freeGlobal = _typeof(commonjsGlobal) == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

  var _freeGlobal = freeGlobal;

  /** Detect free variable `self`. */
  var freeSelf = (typeof self === 'undefined' ? 'undefined' : _typeof(self)) == 'object' && self && self.Object === Object && self;

  /** Used as a reference to the global object. */
  var root = _freeGlobal || freeSelf || Function('return this')();

  var _root = root;

  /**
   * Gets the timestamp of the number of milliseconds that have elapsed since
   * the Unix epoch (1 January 1970 00:00:00 UTC).
   *
   * @static
   * @memberOf _
   * @since 2.4.0
   * @category Date
   * @returns {number} Returns the timestamp.
   * @example
   *
   * _.defer(function(stamp) {
   *   console.log(_.now() - stamp);
   * }, _.now());
   * // => Logs the number of milliseconds it took for the deferred invocation.
   */
  var now$1 = function now() {
    return _root.Date.now();
  };

  var now_1 = now$1;

  /** Built-in value references. */
  var _Symbol2 = _root.Symbol;

  var _Symbol = _Symbol2;

  /** Used for built-in method references. */
  var objectProto = Object.prototype;

  /** Used to check objects for own properties. */
  var hasOwnProperty = objectProto.hasOwnProperty;

  /**
   * Used to resolve the
   * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
   * of values.
   */
  var nativeObjectToString = objectProto.toString;

  /** Built-in value references. */
  var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

  /**
   * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
   *
   * @private
   * @param {*} value The value to query.
   * @returns {string} Returns the raw `toStringTag`.
   */
  function getRawTag(value) {
    var isOwn = hasOwnProperty.call(value, symToStringTag),
        tag = value[symToStringTag];

    try {
      value[symToStringTag] = undefined;
      var unmasked = true;
    } catch (e) {}

    var result = nativeObjectToString.call(value);
    if (unmasked) {
      if (isOwn) {
        value[symToStringTag] = tag;
      } else {
        delete value[symToStringTag];
      }
    }
    return result;
  }

  var _getRawTag = getRawTag;

  /** Used for built-in method references. */
  var objectProto$1 = Object.prototype;

  /**
   * Used to resolve the
   * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
   * of values.
   */
  var nativeObjectToString$1 = objectProto$1.toString;

  /**
   * Converts `value` to a string using `Object.prototype.toString`.
   *
   * @private
   * @param {*} value The value to convert.
   * @returns {string} Returns the converted string.
   */
  function objectToString(value) {
    return nativeObjectToString$1.call(value);
  }

  var _objectToString = objectToString;

  /** `Object#toString` result references. */
  var nullTag = '[object Null]',
      undefinedTag = '[object Undefined]';

  /** Built-in value references. */
  var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;

  /**
   * The base implementation of `getTag` without fallbacks for buggy environments.
   *
   * @private
   * @param {*} value The value to query.
   * @returns {string} Returns the `toStringTag`.
   */
  function baseGetTag(value) {
    if (value == null) {
      return value === undefined ? undefinedTag : nullTag;
    }
    return symToStringTag$1 && symToStringTag$1 in Object(value) ? _getRawTag(value) : _objectToString(value);
  }

  var _baseGetTag = baseGetTag;

  /**
   * Checks if `value` is object-like. A value is object-like if it's not `null`
   * and has a `typeof` result of "object".
   *
   * @static
   * @memberOf _
   * @since 4.0.0
   * @category Lang
   * @param {*} value The value to check.
   * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
   * @example
   *
   * _.isObjectLike({});
   * // => true
   *
   * _.isObjectLike([1, 2, 3]);
   * // => true
   *
   * _.isObjectLike(_.noop);
   * // => false
   *
   * _.isObjectLike(null);
   * // => false
   */
  function isObjectLike(value) {
    return value != null && (typeof value === 'undefined' ? 'undefined' : _typeof(value)) == 'object';
  }

  var isObjectLike_1 = isObjectLike;

  /** `Object#toString` result references. */
  var symbolTag = '[object Symbol]';

  /**
   * Checks if `value` is classified as a `Symbol` primitive or object.
   *
   * @static
   * @memberOf _
   * @since 4.0.0
   * @category Lang
   * @param {*} value The value to check.
   * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
   * @example
   *
   * _.isSymbol(Symbol.iterator);
   * // => true
   *
   * _.isSymbol('abc');
   * // => false
   */
  function isSymbol(value) {
    return (typeof value === 'undefined' ? 'undefined' : _typeof(value)) == 'symbol' || isObjectLike_1(value) && _baseGetTag(value) == symbolTag;
  }

  var isSymbol_1 = isSymbol;

  /** Used as references for various `Number` constants. */
  var NAN = 0 / 0;

  /** Used to match leading and trailing whitespace. */
  var reTrim = /^\s+|\s+$/g;

  /** Used to detect bad signed hexadecimal string values. */
  var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

  /** Used to detect binary string values. */
  var reIsBinary = /^0b[01]+$/i;

  /** Used to detect octal string values. */
  var reIsOctal = /^0o[0-7]+$/i;

  /** Built-in method references without a dependency on `root`. */
  var freeParseInt = parseInt;

  /**
   * Converts `value` to a number.
   *
   * @static
   * @memberOf _
   * @since 4.0.0
   * @category Lang
   * @param {*} value The value to process.
   * @returns {number} Returns the number.
   * @example
   *
   * _.toNumber(3.2);
   * // => 3.2
   *
   * _.toNumber(Number.MIN_VALUE);
   * // => 5e-324
   *
   * _.toNumber(Infinity);
   * // => Infinity
   *
   * _.toNumber('3.2');
   * // => 3.2
   */
  function toNumber(value) {
    if (typeof value == 'number') {
      return value;
    }
    if (isSymbol_1(value)) {
      return NAN;
    }
    if (isObject_1(value)) {
      var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
      value = isObject_1(other) ? other + '' : other;
    }
    if (typeof value != 'string') {
      return value === 0 ? value : +value;
    }
    value = value.replace(reTrim, '');
    var isBinary = reIsBinary.test(value);
    return isBinary || reIsOctal.test(value) ? freeParseInt(value.slice(2), isBinary ? 2 : 8) : reIsBadHex.test(value) ? NAN : +value;
  }

  var toNumber_1 = toNumber;

  /** Error message constants. */
  var FUNC_ERROR_TEXT = 'Expected a function';

  /* Built-in method references for those with the same name as other `lodash` methods. */
  var nativeMax = Math.max,
      nativeMin = Math.min;

  /**
   * Creates a debounced function that delays invoking `func` until after `wait`
   * milliseconds have elapsed since the last time the debounced function was
   * invoked. The debounced function comes with a `cancel` method to cancel
   * delayed `func` invocations and a `flush` method to immediately invoke them.
   * Provide `options` to indicate whether `func` should be invoked on the
   * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
   * with the last arguments provided to the debounced function. Subsequent
   * calls to the debounced function return the result of the last `func`
   * invocation.
   *
   * **Note:** If `leading` and `trailing` options are `true`, `func` is
   * invoked on the trailing edge of the timeout only if the debounced function
   * is invoked more than once during the `wait` timeout.
   *
   * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
   * until to the next tick, similar to `setTimeout` with a timeout of `0`.
   *
   * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
   * for details over the differences between `_.debounce` and `_.throttle`.
   *
   * @static
   * @memberOf _
   * @since 0.1.0
   * @category Function
   * @param {Function} func The function to debounce.
   * @param {number} [wait=0] The number of milliseconds to delay.
   * @param {Object} [options={}] The options object.
   * @param {boolean} [options.leading=false]
   *  Specify invoking on the leading edge of the timeout.
   * @param {number} [options.maxWait]
   *  The maximum time `func` is allowed to be delayed before it's invoked.
   * @param {boolean} [options.trailing=true]
   *  Specify invoking on the trailing edge of the timeout.
   * @returns {Function} Returns the new debounced function.
   * @example
   *
   * // Avoid costly calculations while the window size is in flux.
   * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
   *
   * // Invoke `sendMail` when clicked, debouncing subsequent calls.
   * jQuery(element).on('click', _.debounce(sendMail, 300, {
   *   'leading': true,
   *   'trailing': false
   * }));
   *
   * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
   * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
   * var source = new EventSource('/stream');
   * jQuery(source).on('message', debounced);
   *
   * // Cancel the trailing debounced invocation.
   * jQuery(window).on('popstate', debounced.cancel);
   */
  function debounce(func, wait, options) {
    var lastArgs,
        lastThis,
        maxWait,
        result,
        timerId,
        lastCallTime,
        lastInvokeTime = 0,
        leading = false,
        maxing = false,
        trailing = true;

    if (typeof func != 'function') {
      throw new TypeError(FUNC_ERROR_TEXT);
    }
    wait = toNumber_1(wait) || 0;
    if (isObject_1(options)) {
      leading = !!options.leading;
      maxing = 'maxWait' in options;
      maxWait = maxing ? nativeMax(toNumber_1(options.maxWait) || 0, wait) : maxWait;
      trailing = 'trailing' in options ? !!options.trailing : trailing;
    }

    function invokeFunc(time) {
      var args = lastArgs,
          thisArg = lastThis;

      lastArgs = lastThis = undefined;
      lastInvokeTime = time;
      result = func.apply(thisArg, args);
      return result;
    }

    function leadingEdge(time) {
      // Reset any `maxWait` timer.
      lastInvokeTime = time;
      // Start the timer for the trailing edge.
      timerId = setTimeout(timerExpired, wait);
      // Invoke the leading edge.
      return leading ? invokeFunc(time) : result;
    }

    function remainingWait(time) {
      var timeSinceLastCall = time - lastCallTime,
          timeSinceLastInvoke = time - lastInvokeTime,
          timeWaiting = wait - timeSinceLastCall;

      return maxing ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting;
    }

    function shouldInvoke(time) {
      var timeSinceLastCall = time - lastCallTime,
          timeSinceLastInvoke = time - lastInvokeTime;

      // Either this is the first call, activity has stopped and we're at the
      // trailing edge, the system time has gone backwards and we're treating
      // it as the trailing edge, or we've hit the `maxWait` limit.
      return lastCallTime === undefined || timeSinceLastCall >= wait || timeSinceLastCall < 0 || maxing && timeSinceLastInvoke >= maxWait;
    }

    function timerExpired() {
      var time = now_1();
      if (shouldInvoke(time)) {
        return trailingEdge(time);
      }
      // Restart the timer.
      timerId = setTimeout(timerExpired, remainingWait(time));
    }

    function trailingEdge(time) {
      timerId = undefined;

      // Only invoke if we have `lastArgs` which means `func` has been
      // debounced at least once.
      if (trailing && lastArgs) {
        return invokeFunc(time);
      }
      lastArgs = lastThis = undefined;
      return result;
    }

    function cancel() {
      if (timerId !== undefined) {
        clearTimeout(timerId);
      }
      lastInvokeTime = 0;
      lastArgs = lastCallTime = lastThis = timerId = undefined;
    }

    function flush() {
      return timerId === undefined ? result : trailingEdge(now_1());
    }

    function debounced() {
      var time = now_1(),
          isInvoking = shouldInvoke(time);

      lastArgs = arguments;
      lastThis = this;
      lastCallTime = time;

      if (isInvoking) {
        if (timerId === undefined) {
          return leadingEdge(lastCallTime);
        }
        if (maxing) {
          // Handle invocations in a tight loop.
          clearTimeout(timerId);
          timerId = setTimeout(timerExpired, wait);
          return invokeFunc(lastCallTime);
        }
      }
      if (timerId === undefined) {
        timerId = setTimeout(timerExpired, wait);
      }
      return result;
    }
    debounced.cancel = cancel;
    debounced.flush = flush;
    return debounced;
  }

  var debounce_1 = debounce;

  /** Error message constants. */
  var FUNC_ERROR_TEXT$1 = 'Expected a function';

  /**
   * Creates a throttled function that only invokes `func` at most once per
   * every `wait` milliseconds. The throttled function comes with a `cancel`
   * method to cancel delayed `func` invocations and a `flush` method to
   * immediately invoke them. Provide `options` to indicate whether `func`
   * should be invoked on the leading and/or trailing edge of the `wait`
   * timeout. The `func` is invoked with the last arguments provided to the
   * throttled function. Subsequent calls to the throttled function return the
   * result of the last `func` invocation.
   *
   * **Note:** If `leading` and `trailing` options are `true`, `func` is
   * invoked on the trailing edge of the timeout only if the throttled function
   * is invoked more than once during the `wait` timeout.
   *
   * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
   * until to the next tick, similar to `setTimeout` with a timeout of `0`.
   *
   * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
   * for details over the differences between `_.throttle` and `_.debounce`.
   *
   * @static
   * @memberOf _
   * @since 0.1.0
   * @category Function
   * @param {Function} func The function to throttle.
   * @param {number} [wait=0] The number of milliseconds to throttle invocations to.
   * @param {Object} [options={}] The options object.
   * @param {boolean} [options.leading=true]
   *  Specify invoking on the leading edge of the timeout.
   * @param {boolean} [options.trailing=true]
   *  Specify invoking on the trailing edge of the timeout.
   * @returns {Function} Returns the new throttled function.
   * @example
   *
   * // Avoid excessively updating the position while scrolling.
   * jQuery(window).on('scroll', _.throttle(updatePosition, 100));
   *
   * // Invoke `renewToken` when the click event is fired, but not more than once every 5 minutes.
   * var throttled = _.throttle(renewToken, 300000, { 'trailing': false });
   * jQuery(element).on('click', throttled);
   *
   * // Cancel the trailing throttled invocation.
   * jQuery(window).on('popstate', throttled.cancel);
   */
  function throttle(func, wait, options) {
    var leading = true,
        trailing = true;

    if (typeof func != 'function') {
      throw new TypeError(FUNC_ERROR_TEXT$1);
    }
    if (isObject_1(options)) {
      leading = 'leading' in options ? !!options.leading : leading;
      trailing = 'trailing' in options ? !!options.trailing : trailing;
    }
    return debounce_1(func, wait, {
      'leading': leading,
      'maxWait': wait,
      'trailing': trailing
    });
  }

  var throttle_1 = throttle;

  var RUNTIME_ERRROR_CHECKS_KEY = 'QualifiedWebPreviewErrors';
  var DEFAULT_SANDPACK_VERSION = '0.0.54';
  // Minimum Angular major version that requires the 2.19.8 bundler for web preview
  var ANGULAR_MIN_VERSION_FOR_NEW_BUNDLER = 12;
  var BUNDLER_URLS = {
    // smooshpack@0.0.54
    '0.0.54': 'https://qsp-0-0-54.qualified.io/',
    // smooshpack@0.0.66
    '0.0.66': 'https://qsp-0-0-66.qualified.io/',
    // smooshpack@1.0.0-alpha-31
    '1.0.0': 'https://qsp-1-0-0.qualified.io/',
    // @codesandbox/sandpack-client@0.10.11
    '0.10.11': 'https://qsp-0-10-11.qualified.io/',
    // @codesandbox/sandpack-client@1.20.9
    '1.20.9': 'https://qsp-1-20-9.qualified.io/',
    // @codesandbox/sandpack-client@2.19.8
    '2.19.8': 'https://qsp-2-19-8.web.app/'
  };

  var PRESET_SIZE = /(\d+)x(\d+)/;
  var RESPONSIVE_PRESETS = [['smartphone-sm', 'Smartphone, Small', '320x568'], ['smartphone-md', 'Smartphone, Medium', '375x667'], ['smartphone-lg', 'Smartphone, Large', '414x738'], ['smartphone-xl', 'Smartphone, XL', '414x896'], ['tablet-portrait', 'Tablet, Portrait', '768x1024'], ['tablet-landscape', 'Tablet, Landscape', '1024x768'], ['hdtv-720', '720p HDTV', '1280x720'], ['hdtv-1080', '1080p HDTV', '1920x1080']];

  var lastResponsiveModes = {};

  var didShowLoadError = false;

  /**
   * Inside the files for the web preview must be a package.json
   *
   * This package.json can contain customizations for the web preview renderer:
   *
   * @typedef webPreview~packageJson
   * @property {Object} webPreview - Contains web-preview customizations
   * @property {string} webPreview.bundlerURL - override the bundler URL to something custom. Must be a complete URL with protocol.
   * @property {string} webPreview.bundlerVersion - Use a custom version for sandpack. Recommended for published challenges. Currently supports 0.0.54, 0.0.66, and 1.0.0
   * @property {boolean} webPreview.migrateHeadContent - If true, moves external and inlined styles & scripts to the body tag. Otherwise, they are removed during bundling.
   * @property {string} webPreview.template - Override the dynamically selected template within sandpack.
   * @property {boolean} webPreview.skipEval - If true, don't actually evaluate code. Not sure what this is used for.
   */

  /**
   * Moves externally referenced or inlined LINK, STYLE, or SCRIPT contents
   * to the body. The order is maintained as best as possible.
   *
   * Local referenced files are left as-is, or it causes a different set of errors.
   *
   * It's possible to change the semantics of the page this way, but it's the only way to make sure sandpack works as
   * expected, without losing critical information.
   *
   * @param html
   * @returns {string}
   * @private
   */
  function _migrateHeadContent(html) {
    try {
      var mainNode = document.createElement('html');
      mainNode.innerHTML = html;
      var body = mainNode.querySelector('body');
      var nodes = mainNode.querySelectorAll('head > script, head > link, head > style');
      if (body && nodes.length) {
        Array.from(nodes).reverse().forEach(function (node) {
          var src = node.getAttribute('href') || node.getAttribute('src');
          if (!src || /^(https?:)?\/\//.test(src)) {
            // moves the node if it's external or inlined
            body.prepend(node);
            body.prepend(document.createTextNode('\n'));
          }
        });
        // replace with new HTML
        html = mainNode.outerHTML;
      }
    } catch (ex) {}
    return html;
  }

  var webPreviewComponentModule = angular.module('Q.components.web-preview', [codeHighlightComponentModule, windowEventHelperModule, modalComponentModule, compileTemplateComponentModule, storageServiceModule, trackerServiceModule, webPreviewConsoleComponentModule])
  /**
   * @ngdoc directive
   * @docType shared
   * @category directive
   * @name webPreview
   * @description
   * Renders a web preview using sandpack-client
   *
   * @restrict E
   * @param {expression} files Array of files used to render. Should include package.json
   * @param {expression} onFocus Call back triggered when the iframe gets focus
   * @param {expression} setSyncFunction Provides a callback function called `sync` used to trigger a render on
   *                     changes (note: files will not be watched!)
   * @param {expression} autoSync set default value for when to auto sync on changes, track to remember across uses
   * @param {expression} responsiveMode Provides a default responsive mode value (either preset or /\d+x\d+/)
   * @param {expression} console stores the console log
   */
  .component('webPreview', {
    bindings: {
      // triggered when the iframe gets triggered
      onFocus: '&',
      // used to trigger a reload, so we're not watching files
      setSyncFunction: '&',
      // set default value for when to auto sync on changes
      autoSync: '=?',
      // sets the default a responsive mode value (ignored after startup)
      responsiveMode: '<',
      // Unique ID to use when preserving settings when opening & closing the tab
      uniqueKey: '<',
      // Allow the web preview to be force-disabled
      disableWebPreview: '<?'
    },
    controller: 'WebPreviewCtrl',
    template: webPreviewTemplate
  }).controller('WebPreviewCtrl', ['$scope', '$element', '$attrs', '$timeout', 'highlightCode', '$modal', '$sessionStorage', 'tracker', function WebPreviewCtrl($scope, $element, $attrs, $timeout, highlightCode, $modal, $sessionStorage, tracker) {
    var ctrl = this;

    var client = void 0;
    var history = [];
    var historyPtr = -1;
    var currentUrl = void 0;
    var previewHash = void 0;
    if (!$sessionStorage[RUNTIME_ERRROR_CHECKS_KEY]) {
      $sessionStorage[RUNTIME_ERRROR_CHECKS_KEY] = {};
      $sessionStorage.$apply();
    }
    var runtimeErrorChecks = $sessionStorage[RUNTIME_ERRROR_CHECKS_KEY];

    var $outputIframe = $element.querySelector('iframe');

    var URL_PART_MATCHER = /^((https?:)?(\/\/)?[^\/]+\/?)/i;

    var syncBuffer = void 0;
    var lastUsedBundlerURL = void 0;
    var packageJsonParsed = { webPreview: {} };

    ctrl.loadingMessage = 'Building Preview';
    ctrl.loadErrorTemplate = webPreviewLoadErrorTemplate;

    var neverLoadedTimeout = void 0;
    var detectedLoadError = void 0;
    var loadErrorDialog = void 0;
    var _clearLoadError = function _clearLoadError() {
      ctrl.neverLoaded = false;
      if (loadErrorDialog) {
        loadErrorDialog.dismiss();
        loadErrorDialog = null;
      }
    };
    var _showLoadError = function _showLoadError() {
      ctrl.neverLoaded = true;
      // eslint-disable-next-line angular/no-private-call
      if (!$scope.$$destroyed) {
        tracker.trackError('Web Preview Load Failed', {
          messageEventData: detectedLoadError === true ? null : detectedLoadError,
          bundlerURL: lastUsedBundlerURL,
          packageJSON: packageJsonParsed
        });
        if (!didShowLoadError) {
          didShowLoadError = true;
          loadErrorDialog = $modal.openOnce('webPreviewLoadError', {
            scope: $scope,
            template: webPreviewLoadErrorDialogTemplate,
            windowClass: 'no-intercom'
          });
        }
      }
      ctrl.iframeSrc = $outputIframe[0].src;
    };
    var _resetLoaded = function _resetLoaded() {
      $timeout.cancel(neverLoadedTimeout);
      detectedLoadError = false;
      ctrl.loaded = false;
      _clearLoadError();
      neverLoadedTimeout = $timeout(function () {
        if (!ctrl.loaded) {
          _showLoadError();
        }
      }, 15000);
    };

    var toFilesKey = function toFilesKey(files) {
      return Object.keys(files).sort().map(function (k) {
        return k + '/' + files[k].code;
      }).join('\n');
    };

    var getBundlerURL = function getBundlerURL(files) {
      var version = DEFAULT_SANDPACK_VERSION;
      var url = void 0;
      var packageJson = files['/package.json'];
      if (packageJson) {
        try {
          packageJsonParsed = JSON.parse(packageJson.code) || {};
          packageJsonParsed.webPreview = packageJsonParsed.webPreview || {};
          var bundlerURL = packageJsonParsed.webPreview.bundlerURL || packageJsonParsed.bundlerURL;
          var bundlerVersion = packageJsonParsed.webPreview.bundlerVersion || packageJsonParsed.sandpackVersion;
          if (bundlerURL) {
            url = bundlerURL;
          } else if (/\d+\.\d+\.\d+/.test(bundlerVersion)) {
            // Explicit override from package.json — always takes priority
            version = bundlerVersion;
          } else {
            // Auto-detect: if Angular 12+ is in dependencies, use the 2.19.8 bundler to avoid pollyfills and zone.js issues
            // Else will have issues while using http/common
            var deps = Object.assign({}, packageJsonParsed.dependencies, packageJsonParsed.devDependencies);
            var angularVersionStr = deps['@angular/core'];
            var angularMajor = angularVersionStr && parseInt(angularVersionStr.replace(/[^0-9]/, ''), 10);
            if (angularMajor && angularMajor >= ANGULAR_MIN_VERSION_FOR_NEW_BUNDLER) {
              version = '2.19.8';
            }
          }
        } catch (err) {}
      }
      if (!url) {
        if (BUNDLER_URLS[version]) {
          url = BUNDLER_URLS[version];
        } else {
          // fallback to any codesandbox servers for testing purposes (should never be used on a final challenge)
          url = 'https://sandpack-' + version.replace(/\./g, '-') + '.codesandbox.io';
        }
      }
      var changed = client && lastUsedBundlerURL && url !== lastUsedBundlerURL;
      lastUsedBundlerURL = url;
      return { url: url, changed: changed };
    };

    var clearRuntimeError = function clearRuntimeError() {
      if (previewHash) {
        runtimeErrorChecks[previewHash] = undefined;
        $sessionStorage.$apply();
      }
      ctrl.detectedError = false;
    };

    /**
     * Used to try to detect when a page has been reloaded while a web preview was open.
     * Useful in case of infinite loop bugs and other page lockup bugs.
     *
     * FIRST SYNC
     * If we are syncing for the first time, we check to see if the we ran a preview without ever getting
     * destroyed. If the hash exists in session the first time we try to sync, we stop the sync, and
     * show a warning message.
     *
     * FOLLOWUP SYNCS
     * If the user already approved running code, we unset the flag (assumption is everything is running OK),
     * and allow the page to render.
     * If they haven't, but the hash has changed, we unset the old hash.
     * After approval, we set the flag again.
     *
     * If an error was detected (on page load or somehow on subsequent syncs), we freeze the web preview until the candidate resumes it.
     *
     */
    var checkForRuntimeError = function checkForRuntimeError(files) {
      if (window.parent !== window) {
        // workaround: don't check for runtime errors
        // when embedded to avoid false positives
        return false;
      }

      // will be unset on first load
      var oldPreviewHash = previewHash;

      // convert fileset into a hashcode
      previewHash = hashCode(JSON.stringify(files));

      // if this isn't the first load, and we haven't already detected an error or the hash changed,
      // clear the old hash. This allows the sync to happen on follow up attempts
      if (oldPreviewHash && (!ctrl.detectedError || oldPreviewHash !== previewHash)) {
        // unset the value on old hashes if they've changed, or we've already "approved" this code
        runtimeErrorChecks[oldPreviewHash] = undefined;
      }

      // if there's a bind to force-disable web preview, do so, then unset it
      if (ctrl.disableWebPreview) {
        ctrl.disableWebPreview = false;
        ctrl.detectedError = true;
      }

      // if we haven't already detected an error, see if we think one happened
      // will only be set on first loads when the file hashes haven't changed
      ctrl.detectedError = ctrl.detectedError || runtimeErrorChecks[previewHash];

      // flag that the web preview has run
      runtimeErrorChecks[previewHash] = 1;
      $sessionStorage.$apply();

      return ctrl.detectedError;
    };

    ctrl.resumePreview = function () {
      clearRuntimeError();
      ctrl.autoSync = true;
      ctrl.sync(true);
    };
    // Throttle reload to avoid overloading the rendering thread with too much work.
    // This should help avoid freezes, particularly in Project Code Challenges with Web Preview.
    var sync = ctrl.sync = throttle_1(function (force) {
      $timeout.cancel(syncBuffer);

      // eslint-disable-next-line angular/no-private-call
      if ($scope.$$destroyed || !force && !ctrl.autoSync) return;

      if (!force) {
        // wait until there's 1s of delay before syncing on auto renders
        syncBuffer = $timeout(function () {
          sync('buffered');
        }, 1000);
        return;
      }

      var challengeFiles = $scope.$parent.$eval($attrs.files);
      ctrl.showPreview = challengeFiles && challengeFiles.length > 1 && challengeFiles.some(function (f) {
        return f.path === 'package.json';
      });
      if (ctrl.showPreview) {
        var files = {};
        challengeFiles.forEach(function (f) {
          files['/' + f.path] = {
            code: f.contents
          };
        });

        if (checkForRuntimeError(files)) {
          ctrl.autoSync = false;
          if (client) {
            currentUrl = null;
            ctrl.reload();
          }
          return;
        }

        var bundlerURL = getBundlerURL(files);
        var webPreviewConfig = packageJsonParsed.webPreview;

        if (webPreviewConfig.migrateHeadContent) {
          angular.forEach(files, function (data, path) {
            if (/\.html?$/.test(path)) {
              data.code = _migrateHeadContent(data.code);
            }
          });
        }

        if (bundlerURL.changed) {
          currentUrl = null;
          ctrl.reload();
        } else if (client) {
          var shouldUpdate = force !== 'buffered' || !client.sandboxInfo.files || toFilesKey(client.sandboxInfo.files) !== toFilesKey(files);
          if (shouldUpdate) {
            client.sandboxInfo.files = files;
            client.updatePreview();
            if (force === 'reload') {
              ctrl.reload();
            }
          }
        } else {
          client = new SandpackClient($outputIframe[0], {
            files: files,
            template: webPreviewConfig.template
          }, {
            bundlerURL: bundlerURL.url,
            showOpenInCodeSandbox: false,
            skipEval: webPreviewConfig.skipEval
          });
          _resetLoaded();
        }
      }
    }, 1000);

    var _resetHistory = function _resetHistory() {
      history.length = 0;
      historyPtr = -1;
    };

    var _updateCtrlUrl = function _updateCtrlUrl() {
      ctrl.path = currentUrl && currentUrl.replace(URL_PART_MATCHER, '');
      ctrl.lastPath = ctrl.path;
      ctrl.hasBack = historyPtr > 0;
      ctrl.hasForward = history.length - 1 > historyPtr;
    };
    var _loadUrl = function _loadUrl(url) {
      _resetLoaded();
      if (url) {
        $outputIframe[0].src = url;
      }
      currentUrl = url;
      _updateCtrlUrl();
      _resetHistory();
    };
    var _postMessage = function _postMessage(data) {
      data.codesandbox = true;
      $outputIframe[0].contentWindow.postMessage(data, '*');
    };

    ctrl.loadErrorDismissed = false;
    ctrl.dismissLoadError = function () {
      ctrl.loadErrorDismissed = true;
    };

    ctrl.go = function () {
      var path = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : ctrl.path;

      var urlParts = currentUrl && currentUrl.match(URL_PART_MATCHER);
      if (urlParts && urlParts[1]) {
        _loadUrl(urlParts[1] + path);
      }
    };

    ctrl.back = function () {
      if (historyPtr > 0) {
        _postMessage({ type: 'urlback' });
      }
    };

    ctrl.forward = function () {
      if (history.length - 1 > historyPtr) {
        _postMessage({ type: 'urlforward' });
      }
    };

    ctrl.home = function () {
      ctrl.go('');
    };

    ctrl.reload = function () {
      if (currentUrl) {
        _loadUrl(currentUrl);
      } else {
        // we never loaded successfully, completely reload
        if (client && angular.isFunction(client.cleanup)) {
          // unhook listener
          client.cleanup();
        }
        client = null;
        sync(true);
      }
    };

    ctrl.responsivePresets = RESPONSIVE_PRESETS.map(function (_ref) {
      var _ref2 = slicedToArray(_ref, 3),
          id = _ref2[0],
          label = _ref2[1],
          size = _ref2[2];

      return {
        id: id,
        icon: id.split('-')[0],
        label: label,
        size: size,
        selected: false
      };
    });

    var responsiveResizeListener = void 0;
    var $resizeWrapper = void 0;
    var lastResponsiveSize = void 0;
    var responsiveAnimation = void 0;

    var _updateResponsiveSize = function _updateResponsiveSize(width, height) {
      var changeSize = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;

      width = Math.max(40, +width || 0);
      height = Math.max(40, +height || 0);
      ctrl.windowSize = {
        width: width,
        height: height,
        preset: null,
        landscape: width > height
      };
      lastResponsiveSize = width + 'x' + height;
      if (ctrl.uniqueKey) {
        lastResponsiveModes[ctrl.uniqueKey] = lastResponsiveSize;
      }
      ctrl.responsivePresets.forEach(function (preset) {
        preset.selected = preset.size === lastResponsiveSize;
        if (preset.selected) {
          ctrl.windowSize.preset = preset;
        }
      });
      if (changeSize) {
        $resizeWrapper.addClass('resizing');
        $resizeWrapper.css({
          width: width + 20 + 'px',
          height: height + 20 + 'px'
        });
        $timeout.cancel(responsiveAnimation);
        responsiveAnimation = $timeout(function () {
          $resizeWrapper.removeClass('resizing');
        }, 250, false);
      }
    };

    var _configureResizeListener = function _configureResizeListener() {
      if (!responsiveResizeListener) {
        var onResize = function onResize() {
          if (ctrl.windowSize && $resizeWrapper && $resizeWrapper.isVisible('basic')) {
            _updateResponsiveSize($resizeWrapper.offsetWidth() - 20, $resizeWrapper.offsetHeight() - 20, false);
          }
        };
        if (window.ResizeObserver) {
          responsiveResizeListener = new ResizeObserver(onResize);
          $timeout(function () {
            responsiveResizeListener.observe($resizeWrapper[0]);
          });
        } else if (window.MutationObserver) {
          responsiveResizeListener = new MutationObserver(onResize);
          $timeout(function () {
            responsiveResizeListener.observe($resizeWrapper[0], {
              attributes: true
            });
          });
        }
      }
    };

    ctrl.toggleResponsiveMode = function () {
      var newSize = void 0;
      if (!ctrl.windowSize) {
        newSize = lastResponsiveSize || ctrl.responsivePresets[0].id;
      }
      ctrl.setResponsiveMode(newSize);
    };

    ctrl.setResponsiveMode = function (value) {
      if (!$resizeWrapper) {
        $resizeWrapper = $element.querySelector('.responsive-resize-wrapper');
      }
      ctrl.responsivePresets.forEach(function (p) {
        return p.selected = false;
      });
      ctrl.windowSize = null;
      var size = null;
      if (responsiveResizeListener) {
        responsiveResizeListener.disconnect();
        responsiveResizeListener = null;
      }
      if (PRESET_SIZE.test(value)) {
        size = value;
      } else if (value) {
        var preset = ctrl.responsivePresets.find(function (p) {
          return p.id === value;
        });
        if (preset) {
          size = preset.size;
        }
      }
      if (size) {
        var _size$match = size.match(PRESET_SIZE),
            _size$match2 = slicedToArray(_size$match, 3),
            width = _size$match2[1],
            height = _size$match2[2];

        _updateResponsiveSize(width, height);
        _configureResizeListener();
      } else {
        if (ctrl.uniqueKey) {
          delete lastResponsiveModes[ctrl.uniqueKey];
        }
        $resizeWrapper.css({
          width: null,
          height: null
        });
      }
    };

    ctrl.customResponsiveMode = function () {
      if (ctrl.windowSize && ctrl.windowSize.width >= 40 && ctrl.windowSize.height > 40) {
        ctrl.setResponsiveMode(ctrl.windowSize.width + 'x' + ctrl.windowSize.height);
      }
    };

    ctrl.rotateResponsiveMode = function () {
      if (ctrl.windowSize) {
        ctrl.setResponsiveMode(ctrl.windowSize.height + 'x' + ctrl.windowSize.width);
      }
    };

    var handleUrlChange = function handleUrlChange(data) {
      // handle history navigation
      var popped = false;
      if (data.action === 'POP') {
        var newPtr = historyPtr + data.diff;
        popped = history[newPtr] === data.url;
        if (popped) {
          historyPtr = newPtr;
        } else {
          // we're out of sync, treat it as a reset/reload
          _resetHistory();
        }
      }

      // handle other loading scenarios
      if (!popped) {
        if (historyPtr > -1) {
          historyPtr = data.action === 'REPLACE' ? historyPtr : historyPtr + 1;
          // drop all future states
          history = history.slice(0, historyPtr);
        } else {
          historyPtr = 0;
        }
        history.push(data.url);
      }

      currentUrl = data.url;
      _updateCtrlUrl();
    };

    var STATUS_MESSAGES = {
      'installing-dependencies': 'Installing Dependencies',
      transpiling: 'Transpiling Code',
      evaluating: 'Evaluating Code',
      idle: 'Ready'
    };

    ctrl.$onInit = function () {
      if (ctrl.autoSync === undefined) {
        ctrl.autoSync = true;
      }
      ctrl.hasConsole = !!ctrl.console;
      // set up focus
      $scope.$on('document:focusoutActiveElement', function ($e, event, activeElement) {
        if ($outputIframe[0] && activeElement === $outputIframe[0]) {
          ctrl.onFocus();
        }
      });

      $scope.$on('window:message', function ($event, messageEvent) {
        var data = messageEvent && messageEvent.data;
        if (data && equivalentUrl(messageEvent.origin, lastUsedBundlerURL)) {
          var type = data.type;
          if (type === 'initialized') {
            // something triggered a full page reload
            if (ctrl.loaded) {
              // change loading message
              ctrl.loadingMessage = 'Reloading Preview';
            }
            ctrl.statusMessage = '';
            _resetLoaded();
            _resetHistory();
          } else if (type === 'status') {
            ctrl.statusMessage = STATUS_MESSAGES[data.status];
          } else if (type === 'start') {
            ctrl.syncing = true;
          } else if (type === 'done') {
            if (!ctrl.loaded) {
              if (!detectedLoadError) {
                tracker.trackEvent('Web Preview Load Succeeded', {
                  bundlerURL: lastUsedBundlerURL,
                  packageJSON: packageJsonParsed
                });
              }
            }
            ctrl.loaded = true;
            if (detectedLoadError) {
              _showLoadError();
            } else {
              _clearLoadError();
            }
            ctrl.syncing = false;
          } else if (type === 'urlchange') {
            handleUrlChange(data);
          }
        }
      });

      $scope.$on('$destroy', function () {
        if (client && angular.isFunction(client.cleanup)) {
          // unhook listener
          client.cleanup();
        }
        // clear sync reference
        ctrl.setSyncFunction({ sync: null });
        clearRuntimeError();
        if (responsiveResizeListener) {
          responsiveResizeListener.disconnect();
        }
      });
      ctrl.setSyncFunction({ sync: sync });
      sync(true);

      var rMode = ctrl.uniqueKey && lastResponsiveModes[ctrl.uniqueKey] || ctrl.responsiveMode;
      ctrl.setResponsiveMode(rMode);
    };
  }]);

  var fileIconTemplate = "<span class=\"language-icon icon-{{ $ctrl.icon }}\" ng-bind=\"$ctrl.extension\"></span> <span ng-show=\"$ctrl.file.status\" class=\"file-status-icon file-status-{{$ctrl.file.status}}\"></span> <del></del> ";

  // List of all icons we have in the system
  // There's no easy way to extract the list of `lang-*` icons, so this is manually duplicated here
  // Note: Leave the leading and trailing pipes (|) to avoid substring matching
  var LANG_ICONS = '|txt|angular|c|clojure|coffeescript|cpp|crystal|csharp|css|dart|django|elasticsearch|elixir|erlang|fsharp|go|groovy|haskell|html|java|javascript|jquery|kotlin|lua|markdown|mongo|mysql|net|nodejs|objc|ocaml|php|postgresql|python|r|rails|react|redis|ruby|rust|scala|shell|solidity|sql|sqlite|swift|typescript|';

  // Provides a way to map known extensions into something shorter or neater
  var EXT_MAP = {
    less: '{L}',
    scss: '{S}',
    sass: '{S}',
    svg: '🏞',
    xml: '</>'
  };

  var fileIconComponentModule = angular.module('Q.components.file-icon', []).component('fileIcon', {
    bindings: {
      file: '<',
      expanded: '<'
    },
    controller: 'FileIconCtrl',
    template: fileIconTemplate
  }).controller('FileIconCtrl', ['$element', '$scope', function FileIconCtrl($element, $scope) {
    var ctrl = this;

    ctrl.$onInit = function () {
      var file = ctrl.file;
      ctrl.extension = '';
      if (ctrl.icon) {
        $element.removeClass(ctrl.icon);
      }
      if (!file || file.path === undefined && !file.icon) {
        $element.addClass('no-icon');
        return;
      }
      $element.removeClass('no-icon');

      ctrl.icon = getFileIcon(file);
      $element.addClass(ctrl.icon);

      var custom = angular.isString(file.icon);
      $element.toggleClass('custom', custom);

      if (file && !file.directory && !custom) {
        var ext = getFileLanguage(file);
        ext = EXT_MAP[ext] || ext;
        if (ext && ext.length < 4 && !LANG_ICONS.includes('|' + ext + '|')) {
          ctrl.extension = ext;
        }
      }
      $element.toggleClass('extension', !!ctrl.extension);

      $element.toggleClass('is-dir', !!file.directory);
      $element.toggleClass('expanded', !!ctrl.expanded);
      $element.toggleClass('inv', !!file.reference);
      $element.toggleClass('deleted', !!file.$deleted);
    };
    $scope.$watch(function () {
      return '' + ctrl.expanded + (ctrl.file ? ctrl.file.path + ctrl.file.icon + ctrl.file.$deleted : '');
    }, function () {
      ctrl.$onInit();
    });
  }]);

  var scrollOverflowIndicatorTemplate = "<div class=\"scrollable-container\" ng-transclude></div> <span class=\"scroll-up\" tooltip=\"Scroll up to see more\"></span> <span class=\"scroll-down\" tooltip=\"Scroll down to see more\"></span> ";

  // how far we must be from the edge before we show or hide the indicator
  var SCROLL_INDICATOR_BUFFER = 20;

  var scrollOverflowIndicatorModule = angular.module('Q.components.scroll-overflow-indicator', [scrollMonitorServiceModule]).directive('scrollOverflowIndicator', ['scrollMonitorService', function (scrollMonitorService) {
    return {
      restrict: 'E',
      transclude: true,
      template: scrollOverflowIndicatorTemplate,
      link: function link(scope, el, attrs) {
        if (attrs.scrollOverflowIndicator === 'false') return;

        el.addClass('scroll-overflow-indicator');
        el.toggleClass('allow-dark-mode', attrs.allowDarkMode !== undefined);

        var scrollableContainer = el.children()[0];
        var scrollUpIndicator = el.children()[1];
        var scrollDownIndicator = el.children()[2];

        var watcher = scrollMonitorService.addListener(scrollableContainer, function (event, scrollData) {
          // show we can scroll up only if we've scroll some amount
          el.toggleClass('can-scroll-up', scrollData.viewportScrollTop > SCROLL_INDICATOR_BUFFER);

          // space remaining to scroll
          var scrollBottom = scrollableContainer.scrollHeight - (scrollData.viewportHeight + scrollData.viewportScrollTop);

          el.toggleClass('can-scroll-down', scrollBottom > SCROLL_INDICATOR_BUFFER);
        });

        scope.$on('$destroy', function () {
          watcher.destroy();
        });

        [scrollUpIndicator, scrollDownIndicator].forEach(function (ind) {
          // pass through the scroll wheel
          angular.element(ind).on('wheel', function (event) {
            if (event.deltaY) {
              el.children('div')[0].scrollTop += event.deltaY;
            }
          });
        });
      }
    };
  }]);

  var frameTemplate = "<div class=\"ui-modern-frame\"> <div class=\"ui-modern-frame__loading\" ng-if=\"$ctrl.loading\"> <div class=\"ui-modern-frame__loading-spinner\"></div> </div> <iframe class=\"ui-modern-frame__iframe\" ng-class=\"{ 'ui-modern-frame__iframe--loading': $ctrl.loading }\" frameborder=\"0\" ng-attr-scrolling=\"{{ $ctrl.scrolling || 'no' }}\"></iframe> </div> ";

  var uiModernFrameComponentModule = angular.module('Q.components.ui-modern-frame', [windowEventHelperModule]).component('uiModernFrame', {
    template: frameTemplate,
    bindings: {
      route: '<',
      api: '<',
      scrolling: '<',
      onFocus: '&'
    },
    controller: ['$element', '$scope', '$location', '$timeout', '$log', function UiModernFrameCtrl($element, $scope, $location, $timeout, $log) {
      function iframeUrlBase() {
        if ($location.host().indexOf('localhost') >= 0) {
          return 'http://localhost:8082/ui-modern/'; // Port base for development
        }
        return '/ui-modern/'; // Path base for production
      }

      var ctrl = this;

      $scope.$on('document:focusoutActiveElement', function ($e, event, activeElement) {
        if (activeElement === $element.find('iframe')[0]) {
          ctrl.onFocus();
        }
      });

      ctrl.$onInit = function () {
        ctrl.loading = true;
        var iframe = $element.find('iframe')[0];
        iframe.src = iframeUrlBase() + ctrl.route;

        if (ctrl.api) {
          ctrl.connection = connectToChild({
            iframe: $element.find('iframe')[0],
            methods: ctrl.api,
            timeout: 300000
          });
          ctrl.connection.promise.then(function () {
            ctrl.loading = false;
          }).catch(function (err) {
            if (!ctrl.$destroyed && !appConfig.devMode) {
              $log.error(err);
            }
          });
        } else {
          $timeout(function () {
            return ctrl.loading = false;
          }, 0);
        }
        $timeout(function () {
          return ctrl.loading = false;
        }, 5000);
      };

      ctrl.$onChanges = function (changes) {
        if (changes.route) {
          // reload the iframe if the URL changes
          ctrl.$onInit();
        }
      };

      ctrl.$onDestroy = function () {
        ctrl.$destroyed = true;

        if (ctrl.connection) {
          try {
            ctrl.connection.destroy();
          } catch (err) {
            /* ignore errors */
          }
          ctrl.connection = null;
        }
      };
    }]
  });

  // can use template: 'key' to use built-in templates
  var FILE_TAB_TEMPLATES = {
    ideOpts: fileTabTemplateIDEOpts,
    runnerFrame: fileTabTemplateRunnerFrame,
    imageViewer: fileTabTemplateImageViewer,
    markdownViewer: fileTabTemplateMarkdownViewer,
    instructionsViewer: fileTabTemplateInstructionsViewer,
    markdown: fileTabTemplateMarkdownEditor,
    code: fileTabTemplateCodeEditor,
    docsPane: fileTabTemplateDocsPane,
    webPreview: fileTabTemplateWebPreview,
    webPreviewConsole: fileTabTemplateWebPreviewConsole,
    chatagent: fileTabTemplateChatAgent
  };

  var ftCounter = 0;

  var FileTab = function () {
    function FileTab(_ref) {
      var layout = _ref.layout,
          file = _ref.file,
          tabset = _ref.tabset,
          disableWebPreview = _ref.disableWebPreview,
          tabindex = _ref.tabindex,
          _ref$active = _ref.active,
          active = _ref$active === undefined ? file.active || false : _ref$active;
      classCallCheck(this, FileTab);

      this.layout = layout;
      this._workspace = layout.workspace;
      this.file = file;
      this.tabset = tabset;
      this.lastTabset = file.lastTabset || tabset;
      this.tabindex = tabindex;
      this.active = active;
      this.defaultActive = active;
      this.disableWebPreview = disableWebPreview;
      this.ftId = ++ftCounter;

      this.$update();
    }

    // only activates the tab itself, no focus


    createClass(FileTab, [{
      key: 'activate',
      value: function activate() {
        this._active = true;
      }
    }, {
      key: '$update',
      value: function $update() {
        var file = this.file;
        this.filename = splitPath(file.path).filename;

        var template = file.template;
        var tabClass = '';
        if (!template) {
          if (isImageDataUrl(file && file.contents)) {
            template = fileTabTemplateImageViewer;
          } else if (file.$markdown) {
            if (file.$markdownReadOnly) {
              template = fileTabTemplateMarkdownViewer;
            } else {
              template = fileTabTemplateMarkdownEditor;
            }
          } else if (file.path) {
            template = fileTabTemplateCodeEditor;
            tabClass = 'code-tab';
          } // else provide your own!
        }
        if (FILE_TAB_TEMPLATES[template]) {
          template = FILE_TAB_TEMPLATES[template];
          if (file.template === 'runnerFrame') {
            tabClass = 'code-tab runner-frame';
          }
        }
        this.toolbarTemplate = this.layout.getToolbarTemplate({ file: file });
        this.template = template;
        this.tabClass = tabClass;

        if (this.layout.workspace && this._workspace !== this.layout.workspace) {
          this._workspace = this.layout.workspace;
          // force a relinking if the workspace changed
          this.relinkOnWorkspaceChange = Date.now();
        }
      }
    }, {
      key: 'followLayoutEditing',
      value: function followLayoutEditing() {
        return this.file && !this.file.alwaysEdit;
      }
    }, {
      key: 'closeable',
      value: function closeable() {
        return this.file.closeable !== false && this.layout && this.layout.closeable !== false && (!Array.isArray(this.layout.closeable) || this.layout.closeable.includes(this.file));
      }
    }, {
      key: 'closeDisabled',
      value: function closeDisabled() {
        return this.file.closeable === 'disabled';
      }
    }, {
      key: 'onClose',
      value: function onClose() {}
    }, {
      key: 'tabClasses',
      value: function tabClasses() {
        var ret = this.tabClass || '';
        if (this.layout.draggingFileTab === this) {
          ret += ' dragged-file-tab';
        } else if (this.focused) {
          ret += ' focused-tab';
        }
        if (this.file.tabClasses) {
          ret += ' ' + this.file.tabClasses;
        }
        return ret;
      }
    }, {
      key: 'active',
      set: function set$$1(active) {
        if (active !== this._active) {
          this._active = active;
          this.file.active = active;
          if (active && this.layout.initialized) {
            // enables the layout to handle focusing
            this.layout.select(this);
          }
        }
      },
      get: function get$$1() {
        return this._active;
      }
    }, {
      key: 'label',
      get: function get$$1() {
        return this.file.label || this.filename;
      }
    }, {
      key: 'labelTemplate',
      get: function get$$1() {
        return this.file.labelTemplate;
      }
    }, {
      key: 'tooltip',
      get: function get$$1() {
        return this.file.tabTooltip || this.file.path;
      }
    }, {
      key: 'tabset',
      set: function set$$1(tabset) {
        this._tabset = tabset;
        this.file.tabset = tabset;
      },
      get: function get$$1() {
        return this._tabset;
      }
    }, {
      key: 'lastTabset',
      set: function set$$1(tabset) {
        this._lastTabset = tabset;
        this.file.lastTabset = tabset;
      },
      get: function get$$1() {
        return this._lastTabset;
      }
    }, {
      key: 'tabindex',
      set: function set$$1(tabindex) {
        this._tabindex = tabindex;
        this.file.tabindex = tabindex;
      },
      get: function get$$1() {
        return this._tabindex;
      }
    }, {
      key: 'disabled',
      get: function get$$1() {
        return this.layout.disabled && this.followLayoutEditing() || this.file && this.file.disabled;
      }
    }, {
      key: 'readOnly',
      get: function get$$1() {
        return this.expired || this.layout.readonly && this.followLayoutEditing() || this.file && this.file.readOnly;
      }
    }, {
      key: 'expired',
      get: function get$$1() {
        return this.layout.expired && this.file && this.file.expirable;
      }
    }]);
    return FileTab;
  }();

  var FileTabPlaceholder = function FileTabPlaceholder(fileTab) {
    classCallCheck(this, FileTabPlaceholder);

    this.$placeholder = true;
    this.label = fileTab.label;
    this.labelTemplate = fileTab.labelTemplate;
    this.file = fileTab.file;
    this.closeable = fileTab.closeable;
    this.tabClasses = function () {
      return 'drag-and-drop-placeholder';
    };
  };

  var multiFileLayoutComponentModule = angular.module('Q.components.multi-file-layout', [tabsComponentModule, uiLayoutComponentModule, codemirrorComponentModule, codemirrorOptionsComponentModule, codemirrorIdeOptsComponentModule, docsPaneComponentModule, markdownEditorComponentModule, markdownComponentModule, runnerFrameComponentModule, keycodesModule, relinkOnChangeComponentModule, webPreviewComponentModule, fileIconComponentModule, scrollOverflowIndicatorModule, uiModernFrameComponentModule]).component('multiFileLayout', {
    bindings: {
      openFiles: '=?',
      onRun: '&',
      onTestRun: '&',
      onFileChange: '&',
      onFilePaste: '&',
      onFileCopy: '&',
      onTabMoved: '&',
      onTabFocused: '&',

      disableTabMoving: '<',

      getToolbarTemplate: '&',

      parentController: '@',

      preserveOnClose: '<',

      // if true, any `expirable` code or markdown files will be marked as readOnly
      expired: '<',

      // if true, disables editing all code or markdown files
      disabled: '<',

      // if true, puts all code or markdown editors into readonly mode
      readonly: '<',

      // if set, enables syncing the current focus object
      cursor: '=?',

      // if false, don't close any files. If array, only allow closing those files
      closeable: '<',

      rememberUi: '<',

      // allows using LSP with our codemirror editors
      workspace: '<',

      // enables controlling the layout externally
      multiFileLayoutCtrl: '=?',
      // called after initialization is complete
      onInitialized: '&',

      // A template that can be used to inject non-tab actions into the tab bar
      tabBeforeActions: '<?',
      tabAfterActions: '<?',

      // Allows for force-disabling the web preview on load in tabs that use it
      disableWebPreview: '<?'
    },
    require: {
      multiFileChallengesCtrl: '^?linkMultiFileChallenges'
    },
    controller: 'MultiFileLayoutCtrl',
    template: multiFileLayoutTemplate
  }).controller('MultiFileLayoutCtrl', ['$scope', '$timeout', 'codemirrorOptions', 'KeyCodes', '$document', '$q', '$log', '$rootScope', function MultiFileLayoutCtrl($scope, $timeout, codemirrorOptions, KeyCodes, $document, $q, $log, $rootScope) {
    var ctrl = this;

    ctrl.tabsetTemplate = multiFileLayoutTabsetTemplate;

    var fileTabs = ctrl.fileTabs = [];

    var tabsets = ctrl.tabsets = {
      topLeft: [],
      topRight: [],
      bottomLeft: [],
      bottomRight: []
    };

    var find = ctrl.find = function (file) {
      if (file instanceof FileTab) {
        return file;
      } else if (angular.isString(file)) {
        var fieldName = file.replace(/\.[a-z]+$/i, '');
        return fileTabs.find(function (ft) {
          return ft.file.path === file || ft.file.field && ft.file.field === fieldName;
        });
      } else {
        return fileTabs.find(function (ft) {
          return ft.file === file;
        });
      }
    };

    var setCursor = ctrl.setCursor = function (file, cursor) {
      if (cursor) {
        var fileTab = find(file);
        if (fileTab && fileTab.setCursor) {
          fileTab.setCursor(cursor);
        }
      }
    };

    var focusTimer = null;
    var focus = ctrl.focus = function (file, cursor, force) {
      // only focus on the first tab until initialized, and never if we're in the background
      if (focusTimer && !ctrl.initialized) {
        return;
      }

      var fileTab = ctrl.activeFileTab = find(file);
      $timeout.cancel(focusTimer);
      focusTimer = $timeout(function () {
        focusTimer = null;
        ctrl.setFocusedTab(fileTab);
        fileTab.activate();
        if (force || $document[0].hasFocus()) {
          if (fileTab.focus) {
            fileTab.focus(cursor);
          }
          ctrl._onFileChange(file);
        } else {
          // just set the cursor on this tab now
          setCursor(file, cursor);
        }
      }, 100);
    };
    ctrl.chatAgentIframeRoute = function (fileTab) {
      // Build route with query parameters for authentication
      var params = new URLSearchParams();

      // Get data from fileTab.file.challenge and parent controller
      var challenge = fileTab && fileTab.file && fileTab.file.challenge;
      var solution = fileTab && fileTab.file && fileTab.file.solution;

      // Try to get from parent controller if available
      var parentCtrl = $scope.$parent && $scope.$parent.$ctrl;

      // Get challengeId from challenge (REQUIRED)
      if (challenge && challenge.id) {
        params.append('challengeId', challenge.id);
      } else if (parentCtrl && parentCtrl.challenge && parentCtrl.challenge.id) {
        params.append('challengeId', parentCtrl.challenge.id);
      }

      // Get assessmentId from solution's assessmentResult (OPTIONAL - not available in preview mode)
      if (solution && solution.assessmentResult) {
        params.append('assessmentId', solution.assessmentResult.assessmentId);
      } else if (parentCtrl && parentCtrl.solution && parentCtrl.solution.assessmentResult) {
        params.append('assessmentId', parentCtrl.solution.assessmentResult.assessmentId);
      } else if (parentCtrl && parentCtrl.assessmentResult && parentCtrl.assessmentResult.assessmentId) {
        params.append('assessmentId', parentCtrl.assessmentResult.assessmentId);
      }
      // Note: In preview mode, assessmentId won't be available
      // Rails will handle this by looking for dry_run assessment_result

      // Get language from solution (OPTIONAL - for code challenges)
      if (solution && solution.language) {
        params.append('language', solution.language);
      } else if (parentCtrl && parentCtrl.solution && parentCtrl.solution.language) {
        params.append('language', parentCtrl.solution.language);
      }

      // Get AI agent message from challenge (OPTIONAL - configurable message for candidates)
      if (challenge && challenge.aiAgentMessage) {
        params.append('aiAgentMessage', encodeURIComponent(challenge.aiAgentMessage));
      } else if (parentCtrl && parentCtrl.challenge && parentCtrl.challenge.aiAgentMessage) {
        params.append('aiAgentMessage', encodeURIComponent(parentCtrl.challenge.aiAgentMessage));
      }

      // Determine if viewing as team member
      // If there's no assessmentId, it means we're in preview/edit mode (team member)
      // Candidates always have an assessmentId from their assessment result
      var hasAssessmentId = params.has('assessmentId');
      var isTeamMember = !hasAssessmentId;

      if (isTeamMember) {
        params.append('isTeamMember', 'true');
      }

      var route = 'embed/agent-chat?' + params.toString();
      return route;
    };

    ctrl.broadcastToEmbeddedFrames = function (payload) {
      var iframes = document.querySelectorAll('iframe');
      iframes.forEach(function (iframe) {
        try {
          if (iframe.contentWindow) {
            $log.debug('Sending message to iframe', payload, iframe);
            iframe.contentWindow.postMessage(payload, '*');
          }
        } catch (err) {
          $log.debug('Failed to postMessage to iframe', err);
        }
      });
    };

    // Feature 8: Code Selection Context for AI Chat
    // Listen for explicit "add to chat" events (via keyboard shortcut or context menu)
    ctrl.currentCodeSelection = null;

    $scope.$on('codemirror:add-to-chat', function ($event, codeSelection) {
      // Store current selection
      ctrl.currentCodeSelection = codeSelection;

      // Find chat agent iframe by searching for iframe with agent-chat in src
      // Using vanilla JS instead of jqLite filter (not supported)
      var iframes = document.querySelectorAll('iframe');
      var chatIframe = null;

      for (var i = 0; i < iframes.length; i++) {
        if (iframes[i].src && iframes[i].src.indexOf('embed/agent-chat') !== -1) {
          chatIframe = iframes[i];
          break;
        }
      }

      if (chatIframe && chatIframe.contentWindow) {
        // Send selection to Vue chat UI via postMessage
        chatIframe.contentWindow.postMessage({
          type: 'CODE_SELECTION_UPDATE',
          selection: codeSelection
        }, '*' // In development, allow all origins. In production, should specify exact origin
        );
      }
    });

    // Listen for requests from iframe to get current selection
    var handlePostMessage = function handlePostMessage(event) {
      if (event.data && event.data.type === 'REQUEST_CODE_SELECTION') {
        // Re-send current selection if available
        if (ctrl.currentCodeSelection) {
          // Find chat agent iframe using vanilla JS
          var iframes = document.querySelectorAll('iframe');
          var chatIframe = null;

          for (var i = 0; i < iframes.length; i++) {
            if (iframes[i].src && iframes[i].src.indexOf('embed/agent-chat') !== -1) {
              chatIframe = iframes[i];
              break;
            }
          }

          if (chatIframe && chatIframe.contentWindow) {
            chatIframe.contentWindow.postMessage({
              type: 'CODE_SELECTION_UPDATE',
              selection: ctrl.currentCodeSelection
            }, '*');
          }
        }
      }
    };

    window.addEventListener('message', handlePostMessage);

    // Cleanup on destroy
    $scope.$on('$destroy', function () {
      window.removeEventListener('message', handlePostMessage);
    });
    ctrl._onFileChange = function (file, userInitiated) {
      ctrl.onFileChange({ file: file, userInitiated: userInitiated });
    };

    ctrl.setFocusedTab = function (fileTab) {
      ctrl.activeFileTab = fileTab;
      ctrl.fileTabs.forEach(function (ft) {
        return ft.focused = ft === fileTab;
      });

      if (!fileTab) {
        $log.error("Invariant violation: expected fileTab to be defined but it wasn't");
        return;
      }

      ctrl.onTabFocused({ file: fileTab.file });
    };

    ctrl.resetOpenFiles = function () {
      fileTabs.length = 0;
      angular.forEach(tabsets, function (ts) {
        return ts.length = 0;
      });
      ctrl.refresh();
    };

    // Open or select a file, moving focus over automatically
    ctrl.select = ctrl.open = function (file) {
      var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
          _ref2$activate = _ref2.activate,
          activate = _ref2$activate === undefined ? true : _ref2$activate,
          cursor = _ref2.cursor;

      var fileTab = find(file);
      if (!fileTab) {
        fileTab = addFile(file, activate);
        addUnique(ctrl.openFiles, file);
      }
      if (activate) {
        fileTab.active = true;
        (tabsets[fileTab.tabset] || []).forEach(function (ft) {
          ft.active = ft === fileTab;
        });
        focus(fileTab, cursor);
      } else {
        setCursor(file, cursor);
        ctrl._onFileChange(file);
      }
      return fileTab;
    };

    // Close a file
    ctrl.close = function (file) {
      var fileTab = find(file);
      if (fileTab) {
        $q.resolve(fileTab.onClose()).then(function () {
          remove(tabsets[fileTab.tabset], fileTab);
          remove(fileTabs, fileTab);
          remove(ctrl.openFiles, fileTab.file);
          fileTab.lastTabset = fileTab.tabset;
          if (!ctrl.preserveOnClose) {
            fileTab.tabset = null;
            fileTab.tabindex = null;
          }
          if (ctrl.initialized) {
            cleanEmptyTabsets();
            ctrl._onFileChange();
          }
        });
      }
    };

    ctrl.toggle = function (file, open, activate) {
      return open ? ctrl.open(file, { activate: activate }) : ctrl.close(file);
    };

    // moves a file to a specific tabset & optional tab index
    // if the tab index is not provided, it defaults to the end of the tabset
    ctrl.move = function (file, tabset) {
      var tabindex = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : Infinity;

      var fileTab = find(file);
      if (fileTab) {
        if (fileTab.$draggedIn) {
          fileTabs.push(fileTab);
          addUnique(ctrl.openFiles, fileTab.file);
          delete fileTab.$draggedIn;
        } else {
          remove(tabsets[fileTab.tabset], fileTab);
        }
        fileTab.tabset = tabset;
        tabsets[tabset].splice(tabindex, 0, fileTab);
        cleanEmptyTabsets();
        $timeout(function () {
          ctrl.select(fileTab);
          ctrl.onTabMoved(fileTab);
        });
      }
    };

    ctrl.onCursorActivity = function (fileTab, cm) {
      var file = fileTab.file;
      if (file.path) {
        ctrl.selections = file.selections = cm.listSelections();
        ctrl.cursor = file.cursor = cursorData(cm, {
          path: file.path,
          field: file.field
        });
        $scope.$applyAsync(function () {
          ctrl._onFileChange(file, !cm.$nonUserInput);
        });
      } // TODO: handle non-path files?
    };

    ctrl.handlePaste = function (fileTab, change) {
      ctrl.onFilePaste({
        file: fileTab.file,
        change: change,
        pasted: change.text.join('\n')
      });
    };

    ctrl.handleCopy = function (fileTab, copied) {
      ctrl.onFileCopy({
        file: fileTab.file,
        copied: copied
      });
    };

    ctrl.createCodeOpts = memo(function (fileTab) {
      var filename = splitPath(fileTab.file.path).filename;
      var destroyFilenameWatcher = $scope.$watch(function () {
        return splitPath(fileTab.file.path).filename;
      }, function (newVal) {
        if (newVal !== filename) {
          ctrl.createCodeOpts.delete(fileTab);
        }
      });
      var opts = codemirrorOptions.createOpts(filename);

      // Feature 8: Pass file info to CodeMirror for code selection context
      opts.file = fileTab.file; // Make file available to CodeMirror scope

      if (ctrl.workspace) {
        // add in the workspace gutter
        addUnique(opts.gutters, 'cmw-gutter');
      }
      opts.onLoad = function (cmEditor) {
        var _cmEditor$addKeyMap;

        ['focus', 'mousedown', 'touchstart'].forEach(function (event) {
          cmEditor.on(event, function () {
            $scope.$applyAsync(function () {
              ctrl.select(fileTab);
            });
          });
        });
        cmEditor.on('blur', function () {
          $scope.$applyAsync();
        });
        cmEditor.on('change', function (evt, change) {
          if (change.origin === 'paste') {
            ctrl.handlePaste(fileTab, change);
          }
        });
        var onCopy = function onCopy() {
          ctrl.handleCopy(fileTab, cmEditor.doc.getSelection('\n'));
        };
        cmEditor.on('copy', onCopy);
        cmEditor.on('cut', onCopy);

        var preventDefaultOnFiles = function preventDefaultOnFiles(instance, event) {
          if (isFileEvent(event)) {
            // allow the parent component to handle file drop events
            event.codemirrorIgnore = true;
          }
        };

        cmEditor.on('drop', preventDefaultOnFiles);
        cmEditor.on('dragover', preventDefaultOnFiles);
        cmEditor.on('dragenter', preventDefaultOnFiles);
        cmEditor.on('dragleave', preventDefaultOnFiles);
        cmEditor.on('cursorActivity', function (cm) {
          ctrl.onCursorActivity(fileTab, cm);
        });
        // After user input, pause playback auto-follow briefly so reviewers can
        // inspect code without being pulled by incoming playback updates.
        var PAUSE_PLAYBACK_AUTOSCROLL_MS = 3000;
        // Generic "scroll" can also come from programmatic updates, so we only
        // trust it when a manual intent signal happened very recently.
        var MANUAL_SCROLL_INTENT_WINDOW_MS = 250;
        var markManualScrollIntent = function markManualScrollIntent() {
          var source = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'unknown';

          cmEditor.$playbackManualScrollIntentAt = Date.now();
          cmEditor.$playbackManualScrollIntentSource = source;
        };
        var pausePlaybackAutoScroll = function pausePlaybackAutoScroll() {
          var source = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'unknown';

          var pauseUntil = Date.now() + PAUSE_PLAYBACK_AUTOSCROLL_MS;
          cmEditor.$playbackUserScrollPauseUntil = pauseUntil;
          var scrollInfo = cmEditor.getScrollInfo();
          cmEditor.$playbackPausedScrollPosition = {
            left: scrollInfo.left,
            top: scrollInfo.top
          };
          $log.debug('[playback-scroll] user scroll pause set', source, fileTab.file.path || fileTab.file.id, pauseUntil);
        };
        cmEditor.on('scroll', function () {
          if (cmEditor.$nonUserInput) return;
          var now = Date.now();
          var intentAt = cmEditor.$playbackManualScrollIntentAt || 0;
          if (now - intentAt <= MANUAL_SCROLL_INTENT_WINDOW_MS) {
            // Promote trusted scroll intent to a full auto-follow pause window.
            pausePlaybackAutoScroll('scroll:' + (cmEditor.$playbackManualScrollIntentSource || 'unknown'));
          }
        });
        var scroller = cmEditor.getScrollerElement ? cmEditor.getScrollerElement() : null;
        if (scroller) {
          scroller.addEventListener('wheel', function () {
            markManualScrollIntent('wheel');
            pausePlaybackAutoScroll('wheel');
          }, {
            passive: true
          });
          scroller.addEventListener('touchmove', function () {
            markManualScrollIntent('touchmove');
            pausePlaybackAutoScroll('touchmove');
          }, {
            passive: true
          });
          scroller.addEventListener('keydown', function (event) {
            var keys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', 'Space'];
            if (keys.includes(event.key)) {
              markManualScrollIntent('keydown:' + event.key);
              pausePlaybackAutoScroll('keydown:' + event.key);
            }
          });
        }

        cmEditor.addKeyMap((_cmEditor$addKeyMap = {}, defineProperty(_cmEditor$addKeyMap, KeyCodes.COMMAND_KEY + '-Enter', ctrl.onRun), defineProperty(_cmEditor$addKeyMap, KeyCodes.COMMAND_KEY + "-'", ctrl.onTestRun), _cmEditor$addKeyMap));

        if (fileTab.file.cmOnLoad) {
          fileTab.file.cmOnLoad(cmEditor);
        }

        if (fileTab.file.cursor && fileTab.file.cursor.line) {
          // restore cursor
          cmEditor.$nonUserInput = true;
          cmEditor.setCursor(fileTab.file.cursor);
          cmEditor.$nonUserInput = false;
        }

        if (ctrl.workspace) {
          ctrl.workspace.openEditor(fileTab.file, cmEditor);
          fileTab.onClose = function () {
            ctrl.createCodeOpts.delete(fileTab);
            destroyFilenameWatcher();
            if (!ctrl.$destroyed && ctrl.workspace) {
              // ensure we save documents and properly close the editor
              return ctrl.workspace.saveOpenFile(fileTab.file).then(function () {
                return ctrl.workspace.closeFile(fileTab.file);
              });
            }
          };
        }
      };
      return opts;
    });

    function getTabindex(v) {
      return v.tabindex >= 0 ? v.tabindex : Infinity;
    }

    function addFile(file, activate) {
      var tabset = file.tabset || file.lastTabset || file.defaultTabset || 'topLeft';
      var fileTab = new FileTab({
        layout: ctrl,
        file: file,
        tabset: tabset,
        tabindex: getTabindex(file),
        active: activate || file.active,
        disableWebPreview: ctrl.disableWebPreview
      });
      if (fileTab.file.id === 'runnerframe' && $rootScope.playbackToken) {
        fileTab.tabindex = 2;
        fileTab.focused = false;
        fileTab.active = false;
        fileTab.defaultActive = false;
      }
      if (fileTab.file.id === 'webpreview' && $rootScope.playbackToken) {
        fileTab.active = true;
        fileTab.focused = true;
      }
      fileTabs.push(fileTab);
      tabsets[tabset].push(fileTab);
      if (ctrl.initialized) {
        cleanEmptyTabsets();
      } else {
        // Handle initial sorting based on previous layout
        tabsets[tabset].sort(function (a, b) {
          return getTabindex(a) - getTabindex(b);
        });
      }
      if (ctrl.initialized && (activate || file.active)) {
        focus(fileTab);
      }
      return fileTab;
    }

    ctrl.classes = function () {
      return Object.keys(tabsets).map(function (ts) {
        return tabsets[ts].length ? ts : 'not-' + ts;
      }).concat(ctrl.draggingFileTab ? 'dragging-tab' : [], ctrl.openFiles.length === 1 ? 'single-tab-mode' : [], ctrl.disabledPlaceholder, $scope.$root.$ideDarkClass).join(' ');
    };

    ctrl.startDragging = function (fileTabOrFile, event) {
      var external = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;

      var fileTab = fileTabOrFile;
      if (!(fileTabOrFile instanceof FileTab)) {
        fileTab = find(fileTabOrFile);
        if (!fileTab) {
          fileTab = new FileTab({
            layout: ctrl,
            file: fileTabOrFile,
            active: false
          });
          // track that this is a temporary file tab
          fileTab.$draggedIn = true;
        }
      }

      ctrl.draggingFileTab = fileTab;
      ctrl.disabledPlaceholder = [fileTab.tabset === 'topLeft' && ctrl.openFiles.length === 1 && 'topRight', fileTab.tabset === 'topLeft' && ctrl.tabsets.topLeft.length === 1 && 'bottomLeft', fileTab.tabset === 'topRight' && ctrl.tabsets.topRight.length === 1 && 'bottomRight'].filter(angular.identity).map(function (v) {
        return 'disable-' + v;
      }).join(' ');

      if (!external) {
        var contents = fileTab.file.contents || fileTab.label;
        event.dataTransfer.setData('text/plain', contents);

        if (fileTab.filename && window.URL && window.File) {
          var data = URL.createObjectURL(new File([contents], fileTab.filename));
          event.dataTransfer.setData('DownloadUrl', 'text/plain:' + fileTab.filename + ':' + data);
        }

        if (ctrl.onTabDragStart) {
          ctrl.onTabDragStart({ file: fileTab.file, event: event });
        }
      }
    };

    var _dropzones = [];
    ctrl.addDropZoneCleanup = function (fn) {
      addUnique(_dropzones, fn);
    };
    ctrl.removeDropZoneCleanup = function (fn) {
      remove(_dropzones, fn);
    };
    ctrl.stopDragging = function () {
      var external = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;

      ctrl.draggingFileTab = null;
      _dropzones.forEach(function (fn) {
        fn();
      });
      if (!external && ctrl.onTabDragStop) {
        ctrl.onTabDragStop();
      }
    };

    // Fallback: if the dragged tab is removed mid-drag, its own dragend
    // never fires and draggingFileTab is stuck on forever (QUAL-2326).
    var clearDraggingFileTab = function clearDraggingFileTab() {
      ctrl.stopDragging(true);
    };
    $document.on('dragend drop', clearDraggingFileTab);
    $scope.$on('$destroy', function () {
      $document.off('dragend drop', clearDraggingFileTab);
    });

    // ensures we don't have tabs only on the bottom or the right
    function cleanEmptyTabsets() {
      var _moveTabs = function _moveTabs(from, to) {
        var _tabsets$to;

        (_tabsets$to = tabsets[to]).push.apply(_tabsets$to, toConsumableArray(tabsets[from]));
        tabsets[from].length = 0;
        tabsets[to].forEach(function (ft, idx) {
          ft.tabset = to;
          ft.tabindex = idx;
        });
      };
      if (tabsets.topRight.length === 0) {
        // move bottomRight up
        _moveTabs('bottomRight', 'topRight');
      }
      if (tabsets.topLeft.length === 0 && !$rootScope.playbackToken) {
        if (tabsets.bottomLeft.length === 0) {
          // move everything to the left
          _moveTabs('topRight', 'topLeft');
          _moveTabs('bottomRight', 'bottomLeft');
        } else {
          // just move bottomLeft up
          _moveTabs('bottomLeft', 'topLeft');
        }
      }
      // update tabindexes if necessary
      angular.forEach(tabsets, function (tabset) {
        tabset.forEach(function (ft, idx) {
          ft.tabindex = idx;
        });
      });
    }

    ctrl.$onInit = function () {
      ctrl.scopeVars = function (fileTab) {
        var ctrlName = ctrl.parentController || '$ctrl';
        return defineProperty({
          fileTab: fileTab,
          mflCtrl: ctrl
        }, ctrlName, $scope.$parent[ctrlName]);
      };
      if (!Array.isArray(ctrl.openFiles)) {
        ctrl.openFiles = [];
      }
      ctrl.openFiles.forEach(function (file) {
        addFile(file);
      });
      ctrl.multiFileLayoutCtrl = ctrl;

      ctrl.refresh = function () {
        // reinitialize
        ctrl.initialized = false;
        // check to see if anything disappeared & close it
        var missingFilesTabs = fileTabs.filter(function (ft) {
          return !ctrl.openFiles.includes(ft.file);
        });
        if (ctrl.preserveOnClose) {
          missingFilesTabs.forEach(ctrl.close);
        } else {
          missingFilesTabs.forEach(function (missingFileTab) {
            // try to map it to an open file that might have been replaced, but isn't already mapped
            var matchedFile = ctrl.openFiles.find(function (f) {
              return fileTabs.every(function (ft) {
                return f !== ft.file;
              }) && (f.id && f.id === missingFileTab.file.id || angular.equals(f, missingFileTab.file));
            });
            if (matchedFile) {
              // fix fileTab (it'll be updated below)
              missingFileTab.file = matchedFile;
            } else {
              // close no longer open file tab
              ctrl.close(missingFileTab);
            }
          });
        }
        // update all existing files
        fileTabs.forEach(function (ft) {
          ft.$update();
        });
        // open any newly opened files
        ctrl.openFiles.filter(function (file) {
          return !fileTabs.find(function (ft) {
            return ft.file === file;
          });
        }).forEach(function (file) {
          addFile(file);
        });
        // cleanup
        cleanEmptyTabsets();
        ctrl._onFileChange();
        $timeout(function () {
          ctrl.initialized = true;
        });

        if (ctrl.multiFileChallengesCtrl) {
          ctrl.multiFileChallengesCtrl.linkLayout(ctrl);
        }
      };

      if (ctrl.multiFileChallengesCtrl) {
        ctrl.multiFileChallengesCtrl.linkLayout(ctrl);
      }

      $timeout(function () {
        ctrl.initialized = true;
        ctrl.onInitialized();
        $scope.$watch(function () {
          return ctrl.openFiles.map(function (f) {
            return f.id + f.path + f.label;
          }).join('\n');
        }, ctrl.refresh);
      });

      var rui = ctrl.rui = {
        vert: false,
        left: false,
        right: false
      };
      if (ctrl.rememberUi) {
        var opts = angular.extend({
          id: '',
          scope: 'url',
          session: true
        }, isString(ctrl.rememberUi) ? { id: ctrl.rememberUi } : ctrl.rememberUi);
        rui.vert = angular.extend({}, opts, {
          id: opts.id + '--layout-vert-splitter'
        });
        rui.left = angular.extend({}, opts, {
          id: opts.id + '--layout-left-splitter'
        });
        rui.right = angular.extend({}, opts, {
          id: opts.id + '--layout-right-splitter'
        });
      }
    };
  }]).directive('multiFileDropZone', ['$document', '$log', function ($document, $log) {
    return {
      restrict: 'A',
      require: '^multiFileLayout',
      link: function link(scope, elem, attr, multiFileLayoutCtrl) {
        if (multiFileLayoutCtrl.disableTabMoving) return;
        var placeholder = void 0;
        var tabindex = void 0;
        var dragEnterCounter = 0;
        var nextScroll = 0;
        var cleanupPlaceholder = function cleanupPlaceholder() {
          dragEnterCounter = 0;
          remove(multiFileLayoutCtrl.tabsets[attr.multiFileDropZone], placeholder);
          placeholder = null;
          scope.$apply();
        };
        multiFileLayoutCtrl.addDropZoneCleanup(cleanupPlaceholder);
        scope.$on('$destroy', function () {
          multiFileLayoutCtrl.removeDropZoneCleanup(cleanupPlaceholder);
        });

        elem.on('dragenter', function () {
          dragEnterCounter++;
          elem.addClass('drag-over');
          tabindex = undefined;
        }).on('dragover', function (event) {
          if (multiFileLayoutCtrl.draggingFileTab) {
            stopEvent(event);

            event.dataTransfer.dropEffect = 'move';
            var newTabindex = Infinity;

            if (!$document[0].elementsFromPoint) {
              $log.error("Browser doesn't support elementsFromPoint");
              return;
            }

            var dragOverElements = $document[0].elementsFromPoint(event.clientX, event.clientY);

            // see if we're over a tab
            var tabOver = dragOverElements.find(function (el) {
              return el.nodeName === 'LI' && el.classList.contains('multi-file-layout-tab');
            });
            if (tabOver) {
              // calculate tabindex of hovering node
              newTabindex = 0;
              var placeholderTab = tabOver.parentElement.querySelector('li.drag-and-drop-placeholder');
              if (placeholderTab) {
                var tabOverRect = tabOver.getBoundingClientRect();
                var placeholderTabRect = placeholderTab.getBoundingClientRect();
                if (event.clientX - tabOverRect.x > placeholderTabRect.width) {
                  // prevent annoying flipping between different sized tabs
                  newTabindex += 1;
                }
              }
              var child = tabOver;
              while ((child = child.previousElementSibling) !== null) {
                newTabindex += 1;
              }
            }

            // see if we need to scroll the tabs
            var tabScroller = dragOverElements.find(function (el) {
              return el.nodeName === 'A' && (el.classList.contains('tab-scroller-left') || el.classList.contains('tab-scroller-right'));
            });
            if (tabScroller && Date.now() > nextScroll) {
              angular.element(tabScroller).triggerHandler('click');
              // only scroll a tab every .5 seconds
              nextScroll = Date.now() + 500;
            }

            var targetTabset = attr.multiFileDropZone;
            var targetTabsetArray = multiFileLayoutCtrl.tabsets[targetTabset];

            if (!placeholder) {
              if (targetTabsetArray.length !== 0) {
                placeholder = new FileTabPlaceholder(multiFileLayoutCtrl.draggingFileTab);
                targetTabsetArray.splice(newTabindex, 0, placeholder);
                scope.$apply();
              }
            } else if (newTabindex !== tabindex) {
              remove(targetTabsetArray, placeholder);
              targetTabsetArray.splice(newTabindex, 0, placeholder);
              scope.$apply();
            }
            tabindex = newTabindex;
          }
        }).on('dragleave', function () {
          dragEnterCounter--;
          if (dragEnterCounter < 1) {
            elem.removeClass('drag-over');
            cleanupPlaceholder();
          }
        }).on('drop', function (event) {
          cleanupPlaceholder();
          elem.removeClass('drag-over');
          if (multiFileLayoutCtrl.draggingFileTab) {
            stopEvent(event);
            scope.$applyAsync(function () {
              var targetTabset = multiFileLayoutCtrl.tabsets[attr.multiFileDropZone];
              var existingTabindex = targetTabset.indexOf(multiFileLayoutCtrl.draggingFileTab);
              if (existingTabindex > -1 && existingTabindex < tabindex) {
                // adjust final tab index if we're just moving past the current location
                // (this is due to the placeholder element)
                tabindex--;
              }
              multiFileLayoutCtrl.move(multiFileLayoutCtrl.draggingFileTab, attr.multiFileDropZone, tabindex);
              multiFileLayoutCtrl.draggingFileTab = null;
            });
          }
        });
      }
    };
  }]).directive('multiFileDraggableTab', ['$timeout', function ($timeout) {
    var counter = 0;
    return {
      restrict: 'A',
      link: function link(scope, elem, attr) {
        if (scope.$eval(attr.multiFileDraggableTab) === false) return;
        elem.attr('id', 'draggable-tab-' + ++counter).attr('draggable', true).on('dragstart', function (event) {
          scope.$apply(function () {
            var fileTab = scope.$eval(attr.multiFileDraggableTab);
            var layout = fileTab.layout;
            layout.startDragging(fileTab, event, false);
          });
        }).on('dragend', function () {
          $timeout(function () {
            var fileTab = scope.$eval(attr.multiFileDraggableTab);
            fileTab.layout.stopDragging(false);
          });
        });
      }
    };
  }]).directive('multiFileContents', ['$timeout', '$log', function ($timeout, $log) {
    return {
      restrict: 'A',
      link: function link(scope, elem, attr) {
        var fileTab = scope.$eval(attr.multiFileContents);
        if (fileTab) {
          elem.on('click', function () {
            scope.$apply(function () {
              // ensures we highlight the tab if we have focus
              fileTab.layout.setFocusedTab(fileTab);
            });
          });

          var lastFocused = void 0;

          elem.on('focusin', function (event) {
            lastFocused = event.target;
            if (lastFocused.matches('.CodeMirror textarea')) {
              // change last focused to parent CodeMirror node, rather than it's textarea input node
              // Otherwise we can't set the cursor
              while (!lastFocused.matches('.CodeMirror')) {
                lastFocused = lastFocused.parentElement;
              }
            }
          });

          fileTab.setCursor = function (cursor, cm) {
            var isPlayback = !!fileTab.file.$reviewMode;
            if (!cursor && !isPlayback) return;

            if (!cm) {
              cm = elem[0].querySelector('.CodeMirror');
              if (!cm) return;
            }
            cm.$nonUserInput = true;
            if (cm.CodeMirror) cm = cm.CodeMirror;

            var shouldScrollIntoView = true;
            var playbackScrollTarget = null;
            var isUserScrollPaused = false;

            if (isPlayback) {
              var lastLine = cm.lastLine();
              var lastLineCh = cm.getLine(lastLine).length;
              // Prefer server/playback cursor (where the edit happened), fallback
              // to file end when cursor data is unavailable.
              var playbackCursor = cursor && angular.isNumber(cursor.line) ? {
                line: cursor.line,
                ch: angular.isNumber(cursor.ch) ? cursor.ch : 0
              } : {
                line: lastLine,
                ch: lastLineCh
              };
              var previousPlaybackState = cm.$playbackViewportState;
              var now = Date.now();
              isUserScrollPaused = now < (cm.$playbackUserScrollPauseUntil || 0);
              var SCROLL_INTERVAL_MS = 1200;
              var SCROLL_LINE_BATCH = 3;
              var SCROLL_CHAR_BATCH = 120;

              if (!isUserScrollPaused) {
                cm.$playbackPausedScrollPosition = null;
                var beforeCursorScroll = cm.getScrollInfo();
                // Keep cursor synced to playback progress without changing viewport.
                cm.setCursor(playbackCursor);
                cm.scrollTo(beforeCursorScroll.left, beforeCursorScroll.top);
              } else {
                $log.debug('[playback-scroll] skip cursor sync while user pause active', fileTab.file.path || fileTab.file.id, cm.$playbackUserScrollPauseUntil);
                if (cm.$playbackPausedScrollPosition) {
                  cm.scrollTo(cm.$playbackPausedScrollPosition.left, cm.$playbackPausedScrollPosition.top);
                }
              }

              shouldScrollIntoView = false;
              if (previousPlaybackState) {
                var hasNewLines = lastLine > previousPlaybackState.lastLine;
                var hasNewCharsOnLastLine = lastLine === previousPlaybackState.lastLine && lastLineCh > previousPlaybackState.lastLineCh;
                // Cursor movement can indicate edits in-place even when file length
                // did not change (e.g., middle-of-file playback edits).
                var hasCursorProgress = playbackCursor.line !== previousPlaybackState.cursorLine || playbackCursor.ch !== previousPlaybackState.cursorCh;

                if (hasNewLines || hasNewCharsOnLastLine || hasCursorProgress) {
                  var lineDelta = hasNewLines ? lastLine - previousPlaybackState.lastLine : hasCursorProgress ? Math.abs(playbackCursor.line - previousPlaybackState.cursorLine) : 0;
                  var charDelta = hasNewCharsOnLastLine ? lastLineCh - previousPlaybackState.lastLineCh : hasCursorProgress ? Math.abs(playbackCursor.ch - previousPlaybackState.cursorCh) : 0;

                  var pendingLines = (previousPlaybackState.pendingLines || 0) + lineDelta;
                  var pendingChars = (previousPlaybackState.pendingChars || 0) + charDelta;
                  var timeSinceLastScroll = now - (previousPlaybackState.lastScrollAt || previousPlaybackState.startedAt || now);
                  var shouldBatchScroll = (pendingLines >= SCROLL_LINE_BATCH || pendingChars >= SCROLL_CHAR_BATCH || timeSinceLastScroll >= SCROLL_INTERVAL_MS) && !isUserScrollPaused;

                  if (shouldBatchScroll) {
                    // Only auto-scroll if the editing area is near the
                    // current viewport. This prevents yanking the user
                    // back when they've manually scrolled away to read
                    // other code. They "opt back in" naturally by
                    // scrolling back near the editing area.
                    var scrollInfo = cm.getScrollInfo();
                    var bottomVisibleLine = cm.lineAtHeight(scrollInfo.top + scrollInfo.clientHeight, 'local');
                    var NEAR_VIEWPORT_LINES = 30;
                    if (lastLine <= bottomVisibleLine + NEAR_VIEWPORT_LINES) {
                      shouldScrollIntoView = true;
                      // Follow the actual edit location, not always file end.
                      playbackScrollTarget = {
                        line: playbackCursor.line,
                        ch: playbackCursor.ch
                      };
                    }
                    cm.$playbackViewportState = {
                      lastLine: lastLine,
                      lastLineCh: lastLineCh,
                      cursorLine: playbackCursor.line,
                      cursorCh: playbackCursor.ch,
                      pendingLines: 0,
                      pendingChars: 0,
                      lastScrollAt: now,
                      startedAt: previousPlaybackState.startedAt || now
                    };
                  } else {
                    cm.$playbackViewportState = {
                      lastLine: lastLine,
                      lastLineCh: lastLineCh,
                      cursorLine: playbackCursor.line,
                      cursorCh: playbackCursor.ch,
                      pendingLines: pendingLines,
                      pendingChars: pendingChars,
                      lastScrollAt: previousPlaybackState.lastScrollAt || now,
                      startedAt: previousPlaybackState.startedAt || now
                    };
                  }
                } else {
                  cm.$playbackViewportState = {
                    lastLine: lastLine,
                    lastLineCh: lastLineCh,
                    cursorLine: playbackCursor.line,
                    cursorCh: playbackCursor.ch,
                    pendingLines: previousPlaybackState.pendingLines || 0,
                    pendingChars: previousPlaybackState.pendingChars || 0,
                    lastScrollAt: previousPlaybackState.lastScrollAt || now,
                    startedAt: previousPlaybackState.startedAt || now
                  };
                }
              } else {
                cm.$playbackViewportState = {
                  lastLine: lastLine,
                  lastLineCh: lastLineCh,
                  cursorLine: playbackCursor.line,
                  cursorCh: playbackCursor.ch,
                  pendingLines: 0,
                  pendingChars: 0,
                  lastScrollAt: now,
                  startedAt: now
                };
              }
            } else if (cursor.line !== null) {
              cm.$playbackViewportState = null;
              cm.setCursor(cursor);
            } else if (cursor.head && cursor.anchor) {
              cm.$playbackViewportState = null;
              cm.setSelection(cursor.anchor, cursor.head);
            } else if (cursor[0] && cursor[0].head && cursor[0].anchor) {
              cm.$playbackViewportState = null;
              cm.setSelections(cursor);
            }
            if (isPlayback && isUserScrollPaused && cm.$playbackPausedScrollPosition) {
              shouldScrollIntoView = false;
              cm.scrollTo(cm.$playbackPausedScrollPosition.left, cm.$playbackPausedScrollPosition.top);
            }
            if (shouldScrollIntoView) {
              var _now = Date.now();
              if (_now < (cm.$playbackUserScrollPauseUntil || 0)) {
                $log.debug('[playback-scroll] blocked autoscroll due to active user pause', fileTab.file.path || fileTab.file.id, cm.$playbackUserScrollPauseUntil);
                shouldScrollIntoView = false;
              }
            }
            if (shouldScrollIntoView) {
              if (playbackScrollTarget) {
                var _scrollInfo = cm.getScrollInfo();
                var topVisibleLine = cm.lineAtHeight(_scrollInfo.top, 'local');
                var _bottomVisibleLine = cm.lineAtHeight(_scrollInfo.top + _scrollInfo.clientHeight, 'local');
                // Avoid redundant motion: if target is already visible, don't scroll.
                if (playbackScrollTarget.line >= topVisibleLine && playbackScrollTarget.line <= _bottomVisibleLine) {
                  shouldScrollIntoView = false;
                }
              }
            }
            if (shouldScrollIntoView) {
              if (playbackScrollTarget) {
                $log.debug('[playback-scroll] autoscroll to target', fileTab.file.path || fileTab.file.id, playbackScrollTarget);
                cm.scrollIntoView(playbackScrollTarget, 80);
              } else {
                cm.scrollIntoView(null);
              }
            }
            cm.$nonUserInput = false;
          };

          fileTab.focus = function (cursor) {
            fileTab.layout.setFocusedTab(fileTab);
            $timeout(function () {
              var toFocus = void 0;
              if (lastFocused && lastFocused.focus && angular.element(lastFocused).isVisible('basic')) {
                toFocus = lastFocused;
              } else {
                toFocus = Array.from(elem[0].querySelectorAll('.CodeMirror,input,textarea,select,a,button')).find(function (input) {
                  return (input.CodeMirror || input.focus) && angular.element(input).isVisible();
                });
                if (!toFocus && elem.focus) {
                  toFocus = elem;
                }
              }
              if (toFocus) {
                var cm = toFocus.CodeMirror;
                if (cm) {
                  var isPlayback = !!fileTab.file.$reviewMode;
                  var focusScrollInfo = null;
                  if (isPlayback) {
                    focusScrollInfo = cm.getScrollInfo();
                  }
                  cm.focus();
                  if (focusScrollInfo) {
                    cm.scrollTo(focusScrollInfo.left, focusScrollInfo.top);
                  }
                  fileTab.setCursor(cursor, cm);
                } else {
                  toFocus.focus({ preventScroll: true });
                }
              } else {
                fileTab.setCursor(cursor);
              }
            }, 100, false);
          };
          fileTab.focus.contents = elem;
          scope.$on('$destroy', function () {
            if (fileTab.focus.contents === elem) {
              fileTab.focus = null;
            }
          });
        }
      }
    };
  }]);

  var participantsFileTabTemplate = "<div class=\"user-list\"> <interact-user-list users-list=\"fileTab.file.usersList\" type=\"candidate\"></interact-user-list> </div> ";

  /**
   * Creates a tab object for MultiFileLayout that contains Interact participants
   * @param {assessmentResultProcess} process
   * @returns {{template: *, readonly usersList: *, readonly hidden: *, readonly icon: string, id: string, label: string}}
   */
  function createInteractParticipantsTab(process) {
    return {
      id: 'interactparticipants',
      get icon() {
        return 'status-done ' + (process.liveInterview.usersList.length > 1 ? 'participants-active' : '');
      },
      label: 'Interact',
      template: participantsFileTabTemplate,
      get hidden() {
        return !process.liveInterviewActive;
      },
      get usersList() {
        return process.liveInterviewActive && process.liveInterview.usersList;
      }
    };
  }

  var TESTCASES_LABEL_READONLY = 'Sample Tests';
  var TESTCASES_LABEL_READWRITE = TESTCASES_LABEL_READONLY + ' (Modifiable)';

  var CLASSIC_CHALLENGE_LAYOUT_PROPS = {
    code: {
      label: 'Your Solution',
      tabTooltip: 'Input your solution here',
      tabset: 'topLeft',
      tabindex: 1,
      active: true
    },
    testcases: {
      label: TESTCASES_LABEL_READWRITE,
      tabTooltip: 'Modify or add test cases to validate your solution before submitting',
      tabset: 'bottomLeft',
      tabindex: 1,
      active: true,
      get smallWindow() {
        return {
          tabset: 'topLeft',
          tabindex: 2,
          active: false
        };
      }
    },
    instructions: {
      tabset: 'topRight',
      tabindex: 1,
      active: true
    },
    candidatenotes: {
      tabset: 'topRight',
      tabindex: 2
    },
    help: {
      tabset: 'topRight',
      tabindex: 3
    },
    interactparticipants: {
      tabset: 'topRight',
      tabindex: 4
    },
    idesettings: {
      tabset: 'topRight',
      tabindex: 5
    },
    runnerframe: {
      tabset: 'bottomRight',
      tabindex: 1,
      active: true
    }
  };

  var codeChallengeRouteModule = angular.module('Q.routes.assessments.challenges.code-challenge', [keycodesModule, uiRouterStateModule, codeChallengeProcessServiceModule, appAlertComponentModule, challengeCodeOfConductComponentModule, languagePickerComponentModule, interactUserListComponentModule, dialogComponentModule, compileTemplateComponentModule, challengeIntroDialogComponentModule, resetCodeComponentModule, changeLanguageComponentModule, bufferedSaveHelperModule, challengeRouteInteractHelperModule, multiFileChallengeServiceModule, multiFileLayoutComponentModule, codemirrorOptionsComponentModule]).config(['$stateProvider', function ($stateProvider) {
    $stateProvider.state('assessments.challenges.codeChallenge', {
      data: {
        bodyClass: 'assessments challenges classic-code-challenge',
        label: 'Assessment Challenge IDE'
      },
      resolve: {
        challengeProcess: /* @ngInject */['challenge', 'process', 'codeChallengeProcessService', function challengeProcess(challenge, process, codeChallengeProcessService) {
          return challenge ? codeChallengeProcessService.create(challenge, process) : null;
        }]
      },
      onExit: /* @ngInject */['challengeProcess', function onExit(challengeProcess) {
        if (challengeProcess) {
          challengeProcess.autosave('switchedFromChallenge');
          challengeProcess.onExit();
        }
      }],
      views: {
        '@assessments': {
          template: codeChallengeTemplate,
          controller: 'CodeChallengeCtrl as $ctrl'
        }
      }
    });
  }]).controller('CodeChallengeCtrl', ['$scope', 'process', 'challenge', 'challengeProcess', 'codemirrorOptions', '$state', '$timeout', '$window', 'dialogService', 'tracker', 'KeyCodes', 'Challenge', 'Solution', 'bufferedSaveHelper', 'resetCodeDialog', 'changeLanguageDialog', 'challengeRouteInteractHelper', 'multiFileChallengeService', '$log', function CodeChallengeCtrl($scope, process, challenge, challengeProcess, codemirrorOptions, $state, $timeout, $window, dialogService, tracker, KeyCodes, Challenge, Solution, bufferedSaveHelper, resetCodeDialog, changeLanguageDialog, challengeRouteInteractHelper, multiFileChallengeService, $log) {
    var ctrl = this;

    var challengeItem = process.assessmentResult.getChallengeItem(challenge);
    ctrl.challenge = angular.extend(challenge, {
      position: challengeItem.position
    });
    ctrl.isCarriedForward = Challenge.isCarriedForward(challenge, process.assessment);
    ctrl.process = challengeProcess;
    ctrl.required = process.getCurrentStage().required;
    ctrl.nextIsChallenge = process.nextIsChallenge;
    ctrl.nextLabel = process.nextLabel;
    ctrl.next = process.next;
    ctrl.previous = process.previous;
    ctrl.codeStatus = '';
    ctrl.testCasesStatus = '';

    var saveSolution = bufferedSaveHelper(challengeProcess.autosave);
    ctrl.saveSolution = saveSolution;

    var challengeConfig = challengeProcess.mfcChallengeConfig();

    function setupMFCS() {
      var solution = challengeProcess.solution;
      solution.files.forEach(function (file) {
        var field = file.field;
        Object.defineProperties(file, {
          status: {
            get: function get$$1() {
              return ctrl[field + 'Status'];
            }
          },
          // store layout options back onto the parent model
          options: {
            get: function get$$1() {
              if (!solution.options) {
                solution.options = {};
              }
              if (!solution.options[file.id]) {
                solution.options[file.id] = {};
              }
              return solution.options[file.id];
            },
            set: function set$$1(val) {
              angular.extend(this.options, val);
            }
          }
        });
      });

      var interactFile = createInteractParticipantsTab(process);
      multiFileChallengeService.create({
        enableAiAgent: challengeProcess.challenge.showAiAgentToCandidates,
        svc: ctrl,
        challenge: challengeProcess.challenge,
        solution: solution,
        docsArticleList: [],
        challengeConfig: challengeConfig,
        afterFileChanges: saveSolution,
        editChallengeEditorTools: function editChallengeEditorTools(svc) {
          // inserts above help
          insertBefore(svc.challengeEditorTools, svc.docsArticleFile, interactFile);
        }
      });

      ctrl.privilegedMode = 'none';

      ctrl.runResponse = ctrl.runResponse || solution.getRunResponse();

      $scope.$on('$destroy', ctrl.destroy);

      var startInteract = function startInteract() {
        if (!ctrl.openFiles.includes(interactFile)) {
          ctrl.openFiles.push(interactFile);
          if (ctrl.fileLayout) ctrl.fileLayout.resetOpenFiles();
        }
        ctrl.startInteract(challengeProcess.firebaseRef, process.liveInterview);
        challengeProcess.interact = ctrl.interact;
      };
      $scope.$on('liveInterviewStarted', startInteract);
      if (process.liveInterviewActive) {
        $scope.$applyAsync(startInteract);
      }

      var smallWindow = angular.element($window).offsetHeight() < 800;
      var openableFiles = [].concat(toConsumableArray(ctrl.files), toConsumableArray(ctrl.challengeEditorTools), toConsumableArray(challengeConfig));
      var layoutProps = CLASSIC_CHALLENGE_LAYOUT_PROPS;
      if (ctrl.challenge.enableAiAgent) {
        layoutProps = angular.merge({}, CLASSIC_CHALLENGE_LAYOUT_PROPS);
      }
      ctrl.openFiles = Object.entries(layoutProps).map(function (_ref) {
        var _ref2 = slicedToArray(_ref, 2),
            id = _ref2[0],
            props = _ref2[1];

        var file = openableFiles.find(function (f) {
          return f.id === id || f.path === id;
        });
        if (file) {
          var _layoutProps = Object.assign({}, props, smallWindow && props.smallWindow || {});
          Object.entries(_layoutProps).forEach(function (_ref3) {
            var _ref4 = slicedToArray(_ref3, 2),
                key = _ref4[0],
                value = _ref4[1];

            if (file[key] === undefined) file[key] = value;
          });
          if (file.id === 'testcases') {
            if (!ctrl.challenge.sampleTestCasesEnabled) {
              file.access = 'hidden';
            } else if (ctrl.challenge.sampleTestCasesReadOnly) {
              file.access = 'readonly';
              file.label = TESTCASES_LABEL_READONLY;
            }
          }
          return !file.hidden && file;
        }
        return null;
      }).filter(identity);

      var updateArticleList = function updateArticleList() {
        if (!ctrl.docsArticleFile) return;
        ctrl.docsArticleFile.docsArticleList = ctrl.docsArticleList = [{
          id: 'for-candidates/challenge-solver/summary',
          title: 'Using the challenge solver'
        }, {
          id: 'for-candidates/challenge-solver/debugging-tips',
          title: 'Debugging Tips'
        }];
        if (challengeProcess.solution) {
          var langName = languages.toName(challengeProcess.solution.language);
          var frameworkName = languages.frameworks[challengeProcess.solution.testFramework] || challengeProcess.solution.testFramework;
          ctrl.docsArticleList.push({
            id: 'reference/languages/' + challengeProcess.solution.language,
            title: 'About the ' + langName + ' Runtime'
          });
          if (ctrl.challenge.sampleTestCasesEnabled) {
            ctrl.docsArticleList.push({
              id: 'reference/languages/' + challengeProcess.solution.language + '/' + challengeProcess.solution.testFramework,
              title: 'Testing with ' + frameworkName
            });
          }
        }
      };
      $scope.$watch('$ctrl.process.solution.language+","+$ctrl.process.solution.testFramework', updateArticleList);
      updateArticleList();

      ctrl.openInitialFile();
    }

    var _initSolutionTests = function _initSolutionTests() {
      var solution = challengeProcess.solution;
      var code = languages.stripComments(challengeProcess.language, solution && solution.testCases);
      ctrl.testsEmpty = !code;
      ctrl.runTestsTooltip = code ? '' : ' (Make sure you have tests to run!)';
      ctrl.sampleTestCasesEnabled = !solution || solution.sampleTestCasesEnabled;
      ctrl.sampleTestCasesReadOnly = ctrl.sampleTestCasesEnabled && solution && solution.sampleTestCasesReadOnly;

      solution.files.updatePaths();

      if (ctrl.reconfigureSmartIde) {
        ctrl.reconfigureSmartIde();
      }

      if (ctrl.interact) {
        var files = solution.files.map(function (file) {
          return {
            id: file.interactId,
            properties: { contents: file.contents }
          };
        });

        ctrl.interact.updateFiles(files);
      }
    };

    var lastAttempt = {
      code: '',
      response: {},
      language: null
    };

    var requireSubmit = function requireSubmit() {
      return challengeProcess.solution && // we have a solution
      !ctrl.expired && // the timer hasn't expired
      !ctrl.attemptedCurrentSolution() && // we have _not_ run the current solution
      !ctrl.isSample() && // we are not in practice or demo mode
      process.assessmentResult.isStarted() && // the assessment result is in the started state
      !process.restricted && // we're not in a restricted editing mode
      !process.liveInterviewActive; // we're not in a live interview
    };

    ctrl.openResetDialog = function () {
      return resetCodeDialog.open(challengeProcess).finally(_initSolutionTests);
    };
    ctrl.openChangeLanguageDialog = function () {
      return changeLanguageDialog.open(challengeProcess.solution, ctrl.languageIds, ctrl.switchLanguage).finally(_initSolutionTests);
    };

    $scope.$on('$stateChangeStart', function (event, toState) {
      if (toState.name !== 'assessments.welcome' && requireSubmit()) {
        // yell at the candidate for not submitting their code
        event.preventDefault();

        $timeout(function () {
          tracker.trackEvent('User Did Not Submit Challenge');
          dialogService.confirm('<p>Please submit your solution ' + (challengeProcess.solution.attempts ? 'with your current changes' : 'at least once') + ' before continuing, using the <em>Submit Solution</em> button in the top right.</p>\n<p>Would you like to submit your solution now?</p>\n', 'Submit Solution', [{ id: 'yes', text: 'Submit Solution' }, { id: 'cancel', text: 'Continue Editing' }]).then(ctrl.submitSolution);
        });
      }
    });

    challengeProcess.resaveAfterFail = function () {
      return saveSolution('resaveAfterFail');
    };

    var solution = challengeProcess.solution;
    var buildCodeOfConductState = function buildCodeOfConductState() {
      var team = process.assessment && process.assessment.team;
      var enabled = !!(team && team.hasFeature('codeOfConduct') && challenge.cocEnabled);
      var rules = enabled ? challenge.codeOfConductRules() : [];
      rules.forEach(function (rule) {
        rule.acknowledged = false;
      });
      return {
        enabled: enabled,
        rules: rules,
        requireAcknowledgement: enabled && challenge.cocRequireAcknowledgement !== false
      };
    };

    ctrl.codeOfConduct = buildCodeOfConductState();
    ctrl.codeOfConductAcknowledged = function () {
      if (!ctrl.codeOfConduct.enabled) return true;
      if (!ctrl.codeOfConduct.requireAcknowledgement) return true;
      return ctrl.codeOfConduct.rules.every(function (rule) {
        return rule.acknowledged;
      });
    };

    if (solution) {
      challengeProcess.autosave('switchedToChallenge');
      lastAttempt.response = ctrl.initialResponse = solution.getRunResponse();
      var result = lastAttempt.response.result;
      if (result && (result.passed > 0 || result.failed > 0)) {
        // we're trying to guess if they've run this code or not when reloading
        lastAttempt.code = solution.code;
        lastAttempt.language = solution.language;
      }
      _initSolutionTests();
      setupMFCS();
    } else {
      ctrl.codemirrorOpts = codemirrorOptions;
      ctrl.showIntro = true;
      ctrl.introDialogTemplate = introDialogTemplate;
      ctrl.unableToBeginChallenge = function () {
        return ctrl.unattemptedReferenceSolution || !ctrl.currentLanguage || !ctrl.codeOfConductAcknowledged() || ctrl.beginning;
      };
      ctrl.beginChallenge = function () {
        if (ctrl.unableToBeginChallenge()) return;
        ctrl.beginning = true;
        challengeProcess.beginChallenge().then(function () {
          _initSolutionTests();
          setupMFCS();
          process.addSolution(challengeProcess.originalSolution);
          ctrl.showIntro = false;
        }).catch($log.error).finally(function () {
          $timeout(function () {
            // delayed to let dialog close
            ctrl.beginning = false;
          }, 100);
        });
      };
    }

    var prevSol = Challenge.getCarriedForwardIds(challenge).map(function (cfId) {
      return Solution.filter({
        assessmentResultId: process.assessmentResult.id,
        challengeId: cfId
      })[0];
    }).filter(angular.identity)[0];
    var availableLanguages = void 0;
    if (prevSol && prevSol.language) {
      // if this is a carry forward, and we have a previous solution, lock down the language
      availableLanguages = [languages[prevSol.language]];
    } else if (solution && solution.language && Challenge.hasBeenCarriedForward(challenge, process.assessmentResult)) {
      // if this is the first solution in a series of carry forward, and we've started the second, lock down the language
      availableLanguages = [languages[solution.language]];
    } else {
      availableLanguages = challengeItem.availableLanguages();
    }
    availableLanguages.sort(languages.compare);
    ctrl.languageIds = availableLanguages.map(function (l) {
      return l.id;
    });

    function _setLanguage() {
      if (!languages[challengeProcess.language]) {
        $log.error("Invariant violation: expected languages[challengeProcess.language] to be defined, but it wasn't");
        return;
      }

      var lang = languages[challengeProcess.language].id;
      if (challengeProcess.challengeLanguagePicked) {
        ctrl.currentLanguage = lang;
        ctrl.currentLanguageName = languages[lang].name;
      }
      ctrl.currentChallengeLanguage = ctrl.challenge.languages.find(function (language) {
        return language.name === lang;
      });

      ctrl.unattemptedReferenceSolution = ctrl.incompleteReferenceSolution = ctrl.completeReferenceSolution = false;

      if (ctrl.currentChallengeLanguage && !challengeProcess.solution) {
        // for showing carry forward warnings in the intro dialog
        var challengeIds = process.challengeItems.map(function (ci) {
          return ci.challengeId;
        });
        var referenceIds = ctrl.currentChallengeLanguage.challengeReferenceIds;
        var referenced = referenceIds.length && referenceIds.every(function (referenceId) {
          return challengeIds.includes(referenceId);
        });
        if (referenced) {
          var referenceStatus = ctrl.currentChallengeLanguage.referenceSolutionsStatus;
          ctrl.unattemptedReferenceSolution = referenceStatus === 'unattempted' || referenceStatus === 'none';
          ctrl.incompleteReferenceSolution = referenceStatus === 'incomplete';
          ctrl.completeReferenceSolution = referenceStatus === 'complete';
        }
      }
    }

    ctrl.switchLanguage = function (language) {
      var promise = challengeProcess.switchToLanguage(language);
      _setLanguage();
      return promise;
    };

    if (ctrl.languageIds.length === 1) {
      // if there's only one, make sure it's selected
      challengeProcess.switchToLanguage(ctrl.languageIds[0]);
    }

    _setLanguage();

    ctrl.runTests = function () {
      if (!challengeProcess.solution || ctrl.beginning || !ctrl.sampleTestCasesEnabled || ctrl.testsEmpty || ctrl.expired) {
        // don't run tests if we're not ready, they are disabled, or they are empty
        return;
      }
      ctrl.openFile(ctrl.runnerFrameFile);
      ctrl.codeStatus = ctrl.testCasesStatus = '';
      challengeProcess.runTests().then(function (data) {
        var result = data && data.result || {};
        if (result.passed && !result.errors && !result.failed) {
          ctrl.testCasesStatus = 'success';
        } else if (result.errors || result.failed) {
          ctrl.testCasesStatus = 'danger';
        }
      });
    };

    ctrl.submitSolution = function () {
      if (!challengeProcess.solution || ctrl.beginning || ctrl.expired) {
        // don't submit until we're ready
        return;
      }
      ctrl.openFile(ctrl.runnerFrameFile);
      ctrl.codeStatus = '';
      challengeProcess.submitSolution().then(function () {
        lastAttempt.code = challengeProcess.solution.code;
        lastAttempt.language = challengeProcess.solution.language;
        lastAttempt.response = challengeProcess.runResponse || {};

        if (ctrl.isCompleted()) {
          ctrl.codeStatus = 'success';
        } else {
          ctrl.codeStatus = 'danger';
        }
      });
    };

    challengeRouteInteractHelper.init(ctrl, process, $scope);

    ctrl.isSample = process.isSample;

    var _hasChangedSolution = function _hasChangedSolution() {
      var solution = challengeProcess.solution;
      return !(solution && lastAttempt.code === solution.code && lastAttempt.language === solution.language);
    };

    ctrl.testsPassed = function () {
      return !ctrl.sampleTestCasesEnabled || ctrl.testCasesStatus === 'passed' || ctrl.isCompleted();
    };

    ctrl.attemptedCurrentSolution = function () {
      if (_hasChangedSolution()) {
        return false;
      } else {
        return !!lastAttempt.response.result;
      }
    };

    ctrl.isCompleted = function () {
      if (ctrl.attemptedCurrentSolution()) {
        var _result = lastAttempt.response.result;
        if (_result) {
          // with hidden cases, we can't rely on completed to be true :-\
          return _result.completed || _result.passed > 0 && _result.errors === 0 && _result.failed === 0;
        } else {
          return challengeProcess.solution && challengeProcess.solution.completedAt;
        }
      }
      return false;
    };

    challengeProcess.onEnter($scope);
  }]);

  var quizChallengeProcessServiceModule = angular.module('Q.routes.assessments.challenges.code-challenge.quiz-challenge-process-service', [solutionsServiceModule, firebaseServiceModule, appAlertComponentModule, challengeProcessHelperModule]).factory('quizChallengeProcessService', ['solutionsService', 'Solution', '$q', 'firebase', 'appAlert', 'challengeProcessHelper', function (solutionsService, Solution, $q, firebase, appAlert, challengeProcessHelper) {
    return {
      create: create
    };

    //// functions:

    function create(challenge, assessmentResultProcess) {
      var assessmentResult = assessmentResultProcess.assessmentResult;
      var process = {
        challenge: challenge,
        beginChallenge: beginChallenge,
        incomplete: incomplete,
        cleanupAfterSave: cleanupAfterSave
      };

      return challengeProcessHelper(process, assessmentResultProcess);

      //// functions:

      function beginChallenge() {
        return assessmentResultProcess.startAssessmentResult().then(function () {
          return solutionsService.create(challenge, assessmentResult);
        }).then(process.initSolution).catch(appAlert.modelError);
      }

      function incomplete() {
        return process.solution && !process.solution.allAnswered();
      }

      function cleanupAfterSave(force) {
        // due to the way answers are stored, we have to clean them up, because the IDs get changed on every save
        if (process.solution && process.solution.answers && process.originalSolution.answers) {
          var keys = ['id', 'questionRevisionNumber', 'score'];
          if (force) {
            keys.push('values');
          }
          process.solution.answers.forEach(function (ans) {
            var oAns = process.originalSolution.answers.find(function (a) {
              return a.questionId === ans.questionId;
            });
            if (oAns) {
              keys.forEach(function (key) {
                ans[key] = oAns[key];
              });
            }
          });
        }
      }
    }
  }]);

  var regexHighlightComponentModule = angular.module('Q.components.regex-highlight', []).directive('regexHighlight', ['$sanitize', 'highlightRegex', function ($sanitize, highlightRegex) {
    return {
      restrict: 'A',
      scope: {
        regexHighlight: '='
      },
      link: function link(scope, element, attrs) {
        element.addClass('markdown');

        var shouldWrap = element[0].nodeName !== 'CODE';

        if (!shouldWrap) {
          element.addClass('regex');
        }

        render(scope.regexHighlight || element.text() || '');
        if (attrs.regexHighlight) {
          scope.$watch('regexHighlight', render);
        }

        function render(code) {
          var highlightedCode = $sanitize(highlightRegex(code));
          if (shouldWrap) {
            highlightedCode = '<pre><code class="regex">' + highlightedCode + '</code></pre>';
          }
          element.html(highlightedCode);
        }
      }
    };
  }]).factory('highlightRegex', function () {
    // Partially copied from below
    /* Regex Colorizer v0.3.1
     * (c) 2010-2012 Steven Levithan <http://stevenlevithan.com/regex/colorizer/>
     * MIT license
     */

    /*--------------------------------------
     *  Private variables
     *------------------------------------*/

    var delimMatcher = /^\/(.*)\/([a-z]*)$/;
    var regexToken = /\[\^?]?(?:[^\\\]]+|\\[\S\s]?)*]?|\\(?:0(?:[0-3][0-7]{0,2}|[4-7][0-7]?)?|[1-9][0-9]*|x[0-9A-Fa-f]{2}|u[0-9A-Fa-f]{4}|c[A-Za-z]|[\S\s]?)|\((?:\?[:=!]?)?|(?:[?*+]|\{[0-9]+(?:,[0-9]*)?\})\??|[^.?*+^${[()|\\]+|./g;
    var charClassToken = /[^\\-]+|-|\\(?:[0-3][0-7]{0,2}|[4-7][0-7]?|x[0-9A-Fa-f]{2}|u[0-9A-Fa-f]{4}|c[A-Za-z]|[\S\s]?)/g;
    var charClassParts = /^(\[\^?)(]?(?:[^\\\]]+|\\[\S\s]?)*)(]?)$/;
    var quantifier = /^(?:[?*+]|\{[0-9]+(?:,[0-9]*)?\})\??$/;
    var type = {
      NONE: 0,
      RANGE_HYPHEN: 1,
      SHORT_CLASS: 2,
      ALTERNATOR: 3
    };
    var error = {
      UNCLOSED_CLASS: 'Unclosed character class',
      INCOMPLETE_TOKEN: 'Incomplete regex token',
      INVALID_RANGE: 'Reversed or invalid range',
      INVALID_GROUP_TYPE: 'Invalid or unsupported group type',
      UNBALANCED_LEFT_PAREN: 'Unclosed grouping',
      UNBALANCED_RIGHT_PAREN: 'No matching opening parenthesis',
      INTERVAL_OVERFLOW: 'Interval quantifier cannot use value over 65,535',
      INTERVAL_REVERSED: 'Interval quantifier range is reversed',
      UNQUANTIFIABLE: 'Quantifiers must be preceded by a token that can be repeated',
      IMPROPER_EMPTY_ALTERNATIVE: 'Empty alternative effectively truncates the regex here'
    };

    /*--------------------------------------
     *  Private helper functions
     *------------------------------------*/

    /**
     * Returns HTML for error highlighting.
     * @private
     * @param {String} str Pattern to apply error highlighting to.
     * @param {String} [desc] Error description.
     * @returns {String} HTML for error highlighting.
     */
    function errorize(str, desc) {
      return '<b class="err"' + (desc ? ' title="' + desc + '"' : '') + '>' + str + '</b>';
    }

    /**
     * Returns HTML for group highlighting.
     * @private
     * @param {String} str Pattern to apply group highlighting to.
     * @param {Number} depth Group nesting depth.
     * @returns {String} HTML for group highlighting.
     */
    function groupize(str, depth) {
      return '<b class="g' + depth + '">' + str + '</b>';
    }

    /**
     * Expands &, <, and > characters in the provided string to HTML entities &amp;, &lt;, and &gt;.
     * @private
     * @param {String} str String with characters to expand.
     * @returns {String} String with characters expanded.
     */
    function expandHtmlEntities(str) {
      return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    /**
     * Returns the character code for the provided regex token. Supports tokens used within character
     * classes only, since that's all it's currently needed for.
     * @private
     * @param {String} token Regex token.
     * @returns {Number} Character code of the provided token, or NaN.
     */
    function getTokenCharCode(token) {
      // Escape sequence
      if (token.length > 1 && token.charAt(0) === '\\') {
        var t = token.slice(1);
        // Control character
        if (/^c[A-Za-z]$/.test(t)) {
          return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.indexOf(t.charAt(1).toUpperCase()) + 1;
        }
        // Two or four digit hexadecimal character code
        if (/^(?:x[0-9A-Fa-f]{2}|u[0-9A-Fa-f]{4})$/.test(t)) {
          return parseInt(t.slice(1), 16);
        }
        // One to three digit octal character code up to 377 (0xFF)
        if (/^(?:[0-3][0-7]{0,2}|[4-7][0-7]?)$/.test(t)) {
          return parseInt(t, 8);
        }
        // Shorthand class or incomplete token
        if (t.length === 1 && 'cuxDdSsWw'.indexOf(t) > -1) {
          return NaN;
        }
        // Metacharacter representing a single character index, or escaped literal character
        if (t.length === 1) {
          switch (t) {
            case 'b':
              return 8; // Backspace
            case 'f':
              return 12; // Form feed
            case 'n':
              return 10; // Line feed
            case 'r':
              return 13; // Carriage return
            case 't':
              return 9; // Horizontal tab
            case 'v':
              return 11; // Vertical tab
            default:
              return t.charCodeAt(0); // Escaped literal character
          }
        }
      }
      // Unescaped literal token(s)
      if (token !== '\\') {
        return token.charCodeAt(0);
      }
      return NaN;
    }

    /**
     * Applies regex syntax highlighting to the provided character class. Character classes have their
     * own syntax rules which are different (sometimes quite subtly) from surrounding regex syntax.
     * Hence, they're treated as a single token and parsed separately.
     * @private
     * @param {String} value Character class pattern to be colorized.
     * @returns {String} HTML for displaying the character class with syntax highlighting.
     */
    function parseCharClass(value) {
      var output = '';
      var parts = charClassParts.exec(value);
      var lastToken = {
        rangeable: false,
        type: type.NONE
      };
      var match = void 0;
      var m = void 0;
      parts = {
        opening: parts[1],
        content: parts[2],
        closing: parts[3]
      };

      output += parts.closing ? parts.opening : errorize(parts.opening, error.UNCLOSED_CLASS);

      // The charClassToken regex does most of the tokenization grunt work
      while (match = charClassToken.exec(parts.content)) {
        m = match[0];
        // Escape
        if (m.charAt(0) === '\\') {
          /* Inside character classes, browsers differ on how they handle the following:
           * - Any representation of character index zero (\0, \00, \000, \x00, \u0000).
           * - "\c", when not followed by A-Z or a-z.
           * - "\x", when not followed by two hex characters.
           * - "\u", when not followed by four hex characters.
           * However, although representations of character index zero within character
           * classes don't work on their own in Firefox, they don't throw an error, they work
           * when used with ranges, and it's highly unlikely that the user will actually have
           * such a character in their test data, so such tokens are highlighted normally.
           * The remaining metasequences are flagged as errors.
           */
          if (/^\\[cux]$/.test(m)) {
            output += errorize(m, error.INCOMPLETE_TOKEN);
            lastToken = { rangeable: lastToken.type !== type.RANGE_HYPHEN };
            // Shorthand class (matches more than one character index)
          } else if (/^\\[dsw]$/i.test(m)) {
            output += '<b>' + m + '</b>';
            /* Traditional regex behavior is that a shorthand class should be unrangeable.
             * Hence, [-\dz], [\d-z], and [z-\d] should all be equivalent. However, at
             * least some browsers handle this inconsistently. E.g., Firefox 2 throws an
             * invalid range error for [z-\d] and [\d--].
             */
            lastToken = {
              rangeable: lastToken.type !== type.RANGE_HYPHEN,
              type: type.SHORT_CLASS
            };
            // Unescaped "\" at the end of the regex
          } else if (m === '\\') {
            output += errorize(m, error.INCOMPLETE_TOKEN);
            // Don't need to set lastToken since this is the end of the line
            // Metasequence representing a single character index, or escaped literal character
          } else {
            output += '<b>' + expandHtmlEntities(m) + '</b>';
            lastToken = {
              rangeable: lastToken.type !== type.RANGE_HYPHEN,
              charCode: getTokenCharCode(m)
            };
          }
          // Hyphen (might indicate a range)
        } else if (m === '-') {
          if (lastToken.rangeable) {
            // Save the regex's lastIndex so we can reset it after checking the next token
            var lastIndex = charClassToken.lastIndex;
            var nextToken = charClassToken.exec(parts.content);

            if (nextToken) {
              var nextTokenCharCode = getTokenCharCode(nextToken[0]);
              // Hypen for a reverse range (e.g., z-a) or shorthand class (e.g., \d-x or x-\S)
              if (!isNaN(nextTokenCharCode) && lastToken.charCode > nextTokenCharCode || lastToken.type === type.SHORT_CLASS || /^\\[dsw]$/i.test(nextToken[0])) {
                output += errorize('-', error.INVALID_RANGE);
                // Hyphen creating a valid range
              } else {
                output += '<u>-</u>';
              }
              lastToken = {
                rangeable: false,
                type: type.RANGE_HYPHEN
              };
            } else {
              // Hyphen at the end of a properly closed character class (literal character)
              if (parts.closing) {
                output += '-'; // Since this is a literal, it's technically "rangeable", but that doesn't matter
                // Hyphen at the end of an unclosed character class (i.e., the end of the regex)
              } else {
                output += '<u>-</u>';
              }
            }

            // Reset the regex's lastIndex so the next while loop iteration will continue appropriately
            charClassToken.lastIndex = lastIndex;
            // Hyphen at the beginning of a character class or after a non-rangeable token
          } else {
            output += '-';
            lastToken = { rangeable: lastToken.type !== type.RANGE_HYPHEN };
          }
          // Literal character sequence
        } else {
          output += expandHtmlEntities(m);
          lastToken = {
            rangeable: m.length > 1 || lastToken.type !== type.RANGE_HYPHEN,
            charCode: m.charCodeAt(m.length - 1)
          };
        }
      } // End charClassToken loop

      return output + parts.closing;
    }

    /*--------------------------------------
     *  Public methods
     *------------------------------------*/

    /**
     * Applies regex syntax highlighting to the provided regex pattern string.
     * @memberOf RegexColorizer
     * @param {String} pattern Regex pattern to be colorized.
     * @returns {String} HTML for displaying the regex with syntax highlighting.
     * @example
     *
     * RegexColorizer.colorizeText('^regexp? pattern$');
     */
    function highlightRegex(pattern) {
      var output = '';
      var capturingGroupCount = 0;
      var groupStyleDepth = 0;
      var openGroups = [];
      var lastToken = {
        quantifiable: false,
        type: type.NONE
      };
      var match = void 0;
      var m = void 0;
      var char0 = void 0;
      var char1 = void 0;

      var flags = '';
      var delims = pattern.match(delimMatcher);
      if (delims) {
        pattern = delims[1];
        flags = delims[2] || '';
      }

      while (match = regexToken.exec(pattern)) {
        m = match[0];
        char0 = m.charAt(0);
        char1 = m.charAt(1);
        // Character class
        if (char0 === '[') {
          output += '<i>' + parseCharClass(m) + '</i>';
          lastToken = { quantifiable: true };
          // Group opening
        } else if (char0 === '(') {
          // If this is an invalid group type, mark the error and don't count it towards
          // group depth or total count
          if (m.length === 2) {
            // m is "(?"
            output += errorize(m, error.INVALID_GROUP_TYPE);
          } else {
            if (m.length === 1) {
              capturingGroupCount++;
            }
            groupStyleDepth = groupStyleDepth === 5 ? 1 : groupStyleDepth + 1;
            /* Record the group opening's position and character sequence so we can later
             * mark it as invalid if it turns out to be unclosed in the remainder of the
             * regex. The value of index is the position plus the length of the opening <b>
             * element with group-depth class.
             */
            openGroups.push({
              index: output.length + '<b class="gN">'.length,
              opening: m
            });
            // Add markup to the group-opening character sequence
            output += groupize(m, groupStyleDepth);
          }
          lastToken = { quantifiable: false };
          // Group closing
        } else if (char0 === ')') {
          // If this is an invalid group closing
          if (!openGroups.length) {
            output += errorize(')', error.UNBALANCED_RIGHT_PAREN);
            lastToken = { quantifiable: false };
          } else {
            output += groupize(')', groupStyleDepth);
            /* Although at least in some browsers it is possible to quantify lookaheads,
             * this adds no value, doesn't work as you'd expect in JavaScript, and is an
             * error with some regex flavors such as PCRE (also ES5?), so flag them as
             * unquantifiable.
             */
            lastToken = {
              quantifiable: !/^[=!]/.test(openGroups[openGroups.length - 1].opening.charAt(2)),
              style: 'g' + groupStyleDepth
            };
            groupStyleDepth = groupStyleDepth === 1 ? 5 : groupStyleDepth - 1;
            // Drop the last opening paren from depth tracking
            openGroups.pop();
          }
          // Escape or backreference
        } else if (char0 === '\\') {
          // Backreference or octal character code without a leading zero
          if (/^[1-9]/.test(char1)) {
            /* What does "\10" mean?
             * - Backref 10, if 10 or more capturing groups opened before this point.
             * - Backref 1 followed by "0", if 1-9 capturing groups opened before this point.
             * - Otherwise, it's octal character index 10 (since 10 is in octal range 0-377).
             * In the case of \8 or \9 when as many capturing groups weren't opened before
             * this point, they're highlighted as special tokens. However, they should
             * probably be marked as errors since the handling is browser-specific. E.g.,
             * in Firefox 2 they seem to be equivalent to "(?!)", while in IE 7 they match
             * the literal characters "8" and "9", which is correct handling. I don't mark
             * them as errors because it would seem inconsistent to users who don't
             * understand the highlighting rules for octals, etc. In fact, octals are not
             * included in ECMA-262v3, but all the big browsers support them.
             */
            var nonBackrefDigits = '';
            var num = +m.slice(1);
            while (num > capturingGroupCount) {
              nonBackrefDigits = /[0-9]$/.exec(num)[0] + nonBackrefDigits;
              num = Math.floor(num / 10); // Drop the last digit
            }
            if (num > 0) {
              output += '<b>\\' + num + '</b>' + nonBackrefDigits;
            } else {
              var parts = /^\\([0-3][0-7]{0,2}|[4-7][0-7]?|[89])([0-9]*)/.exec(m);
              output += '<b>\\' + parts[1] + '</b>' + parts[2];
            }
            lastToken = { quantifiable: true };
            // Metasequence
          } else if (/^[0bBcdDfnrsStuvwWx]/.test(char1)) {
            /* Browsers differ on how they handle:
             * - "\c", when not followed by A-Z or a-z.
             * - "\x", when not followed by two hex characters.
             * - "\u", when not followed by four hex characters.
             * Hence, such metasequences are flagged as errors.
             */
            if (/^\\[cux]$/.test(m)) {
              output += errorize(m, error.INCOMPLETE_TOKEN);
              lastToken = { quantifiable: false };
              // Unquantifiable metasequence
            } else if ('bB'.indexOf(char1) > -1) {
              output += '<b>' + m + '</b>';
              lastToken = { quantifiable: false };
              // Quantifiable metasequence
            } else {
              output += '<b>' + m + '</b>';
              lastToken = { quantifiable: true };
            }
            // Unescaped "\" at the end of the regex
          } else if (m === '\\') {
            output += errorize(m, error.INCOMPLETE_TOKEN);
            // Don't need to set lastToken since this is the end of the line
            // Escaped literal character
          } else {
            output += expandHtmlEntities(m);
            lastToken = { quantifiable: true };
          }
          // Quantifier
        } else if (quantifier.test(m)) {
          if (lastToken.quantifiable) {
            var interval = /^\{([0-9]+)(?:,([0-9]*))?/.exec(m);
            // Interval quantifier out of range for Firefox
            if (interval && (+interval[1] > 65535 || interval[2] && +interval[2] > 65535)) {
              output += errorize(m, error.INTERVAL_OVERFLOW);
              // Interval quantifier in reverse numeric order
            } else if (interval && interval[2] && +interval[1] > +interval[2]) {
              output += errorize(m, error.INTERVAL_REVERSED);
            } else {
              // Quantifiers for groups are shown in the style of the (preceeding) group's depth
              output += (lastToken.style ? '<b class="' + lastToken.style + '">' : '<b>') + m + '</b>';
            }
          } else {
            output += errorize(m, error.UNQUANTIFIABLE);
          }
          lastToken = { quantifiable: false };
          // Vertical bar (alternator)
        } else if (m === '|') {
          /* If there is a vertical bar at the very start of the regex, flag it as an error
           * since it effectively truncates the regex at that point. If two top-level
           * vertical bars are next to each other, flag it as an error for similar reasons.
           */
          if (lastToken.type === type.NONE || lastToken.type === type.ALTERNATOR && !openGroups.length) {
            output += errorize(m, error.IMPROPER_EMPTY_ALTERNATIVE);
          } else {
            // Alternators within groups are shown in the style of the containing group's depth
            output += openGroups.length ? groupize('|', groupStyleDepth) : '<b>|</b>';
          }
          lastToken = {
            quantifiable: false,
            type: type.ALTERNATOR
          };
          // ^ or $ anchor
        } else if (m === '^' || m === '$') {
          output += '<b>' + m + '</b>';
          lastToken = { quantifiable: false };
          // Dot (.)
        } else if (m === '.') {
          output += '<b>.</b>';
          lastToken = { quantifiable: true };
          // Literal character sequence
        } else {
          output += expandHtmlEntities(m);
          lastToken = { quantifiable: true };
        }
      } // End regexToken loop

      // Mark the opening character sequence for each unclosed grouping as invalid
      var numCharsAdded = 0;
      var errorIndex = void 0;
      var i = void 0;
      for (i = 0; i < openGroups.length; i++) {
        errorIndex = openGroups[i].index + numCharsAdded;
        output = output.slice(0, errorIndex) + errorize(openGroups[i].opening, error.UNBALANCED_LEFT_PAREN) + output.slice(errorIndex + openGroups[i].opening.length);
        numCharsAdded += errorize('', error.UNBALANCED_LEFT_PAREN).length;
      }

      return '<span class="delim">/</span>' + output + '<span class="delim">/' + flags + '</span>';
    }

    return highlightRegex;
  });

  function stringifyMatchers(matchers) {
    if (!Array.isArray(matchers)) matchers = [matchers];
    return matchers.map(function (m) {
      return m.keyword.toString() + JSON.stringify(m);
    }).join('|');
  }

  var highlightMatchesComponentModule = angular.module('Q.components.highlight-matches', [nodeChangeListenerServiceModule]).directive('highlightMatches', ['$window', '$timeout', 'nodeChangeListener', function ($window, $timeout, nodeChangeListener) {
    function isRegex(o) {
      return o instanceof RegExp;
    }

    var defaultProps = {
      separateWordSearch: false,
      diacritics: false,
      acrossElements: true
    };

    return {
      restrict: 'A',
      scope: {
        highlightMatchesOptions: '<',
        highlightMatchesEach: '&',
        highlightMatchesDone: '&'
      },
      link: function link(scope, elem, attr) {
        if (!$window.Mark) return;

        var marker = new $window.Mark(elem[0]);

        // When greater than 0, we don't make any changes
        var isHighlighting = 0;
        var needsHighlight = false;

        var getMatchers = function getMatchers() {
          return scope.$parent.$eval(attr.highlightMatches) || [];
        };

        function highlight() {
          if (isHighlighting) {
            needsHighlight = true;
            return;
          }

          // set to 1, then increment up for each className/kw combo
          isHighlighting = 1;
          // clean up previous matches
          marker.unmark();

          var options = scope.highlightMatchesOptions || {};

          var matches = getMatchers();
          if (!angular.isArray(matches)) {
            matches = [matches];
          }

          angular.forEach(matches, function (config) {
            if (!angular.isObject(config) || isRegex(config)) {
              config = { keyword: config };
            }

            var keyword = config.keyword;
            if (!keyword) return;

            isHighlighting++;

            var markOpts = angular.extend({}, defaultProps, options, config, {
              each: function each(node) {
                scope.highlightMatchesEach({ config: config, keyword: keyword, node: node });
                if (config.each) config.each(node);
              },
              done: function done(total) {
                scope.highlightMatchesDone({ config: config, keyword: keyword, total: total });
                if (config.done) config.done(total);
                // decrement for each className/kw combo we've completed
                isHighlighting--;
              }
            });

            if (!isRegex(keyword) && /\s/.test(keyword)) {
              // Mark.js doesn't handle whitespace very well
              keyword = new RegExp(escapeRegex(keyword), markOpts.caseSensitive ? '' : 'i');
            }

            if (isRegex(keyword)) {
              marker.markRegExp(keyword, markOpts);
            } else {
              marker.mark(keyword, markOpts);
            }
          });

          $timeout(function () {
            // decrement by 1 since we've either added in each kw, or we found no kws
            // delayed to make sure we don't end up causing a loop with constant re-highlighting
            isHighlighting--;
            if (needsHighlight) {
              needsHighlight = false;
              highlightLater();
            }
          }, 100, false);
        }

        var highlightTimer = null;

        function highlightLater() {
          if (isHighlighting) return;
          if (highlightTimer) $timeout.cancel(highlightTimer);
          highlightTimer = $timeout(highlight, 250, false);
        }

        highlight();

        //// Change Watchers

        scope.$watch(function () {
          return stringifyMatchers(getMatchers());
        }, highlight);

        var nodeChangeListenerStop = nodeChangeListener.listen(elem, highlightLater);

        scope.$on('$destroy', function () {
          nodeChangeListenerStop();
        });
      }
    };
  }]);

  var FUZZY_NUMBER_TYPES = {
    percent: {
      base: 1000,
      units: ['%']
    },
    numberUpper: {
      base: 1000,
      units: ['', ' Thousand', ' Million', ' Billion', ' Trillion']
    },
    number: {
      base: 1000,
      units: ['', ' thousand', ' million', ' billion', ' trillion']
    },
    numberShort: {
      base: 1000,
      units: ['', 'K', 'M', 'B', 'T']
    },
    bits: {
      base: 1024,
      units: ['', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb']
    },
    bytes: {
      base: 1024,
      units: ['', 'KB', 'MB', 'GB', 'TB', 'PB']
    },
    bitRate: {
      base: 1024,
      units: ['', 'Kbps', 'Mbps', 'Gbps', 'Tbps', 'Pbps']
    },
    byteRate: {
      base: 1024,
      units: ['', 'KBps', 'MBps', 'GBps', 'TBps', 'PBps']
    }
  };

  var fuzzyNumberFilterModule = angular.module('Q.filters.fuzzy-number', []).filter('fuzzyNumber', ['$filter', function ($filter) {
    return function (number) {
      var type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'number';
      var sigfigs = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 3;
      var alwaysShowSign = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;

      if (!angular.isNumber(number)) return '';
      var opts = FUZZY_NUMBER_TYPES[type];
      var base = opts.base;
      var units = opts.units;

      var unitIndex = units.length - 1;
      var adjustedNumber = number;
      var fractionSize = 0;

      while (unitIndex > 0) {
        if (number >= Math.pow(base, unitIndex)) {
          adjustedNumber = number / Math.pow(base, unitIndex);
          break;
        }
        unitIndex--;
      }

      var floorNumber = Math.floor(adjustedNumber);
      if (floorNumber !== adjustedNumber) {
        var len = floorNumber.toFixed(0).length;
        if (len < sigfigs) {
          fractionSize = sigfigs - len;
        }
      }

      var formattedNumber = $filter('number')(adjustedNumber, fractionSize).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, ''); // remove trailing zeroes

      if (alwaysShowSign && number > 0) {
        formattedNumber = '+' + formattedNumber;
      }

      return formattedNumber + units[unitIndex];
    };
  }]);

  var horizontalCountdownTimerTemplate = "<div class=\"timer-text\"> <div class=\"timer-status-text\"></div> <div class=\"timer-note-hideable\">Click to Hide</div> </div> <div class=\"timer-hidden-message\">Click to show the timer</div> <div class=\"timer-progress-wrapper\"> <div class=\"timer-progress\"></div> </div> ";

  // shares the timer status across this session
  var timerGloballyHidden = false;

  var horizontalCountdownTimerComponentModule = angular.module('Q.components.horizontal-countdown-timer', [timerHelperModule]).component('horizontalCountdownTimer', {
    bindings: {
      startedAt: '<',
      timeoutSeconds: '<',
      onExpired: '&',
      expiredMessage: '@'
    },
    controller: 'HorizontalCountdownTimerCtrl',
    template: horizontalCountdownTimerTemplate
  }).controller('HorizontalCountdownTimerCtrl', ['$scope', '$element', 'timerHelper', function HorizontalCountdownTimerCtrl($scope, $element, timerHelper) {
    var MAX_WARNING_SEC = 5 * 60; // never warn for more than 5 minutes
    var ctrl = this;
    // runs the countdown render
    var timer = void 0;
    // tracks the end of the timer separate from the render timer
    var timeLimitTimer = void 0;
    // tracks when to start warning
    var timerTailPercent = 75;
    // if true, then we always show even if the candidate stopped
    var timerRunningOut = false;

    var BLANK_MESSAGE = '——';

    ctrl.$onInit = function () {
      if (ctrl.startedAt && ctrl.timeoutSeconds) {
        createTimerHandler();
      } else {
        clearTimers();
        setExpired(false);
        setTimerEl(BLANK_MESSAGE, 100);
      }

      ctrl.$onChanges = function (changes) {
        if (changes.startedAt || changes.timeoutSeconds) {
          createTimerHandler();
        }
      };
    };

    function _setHidden(state) {
      if (!timerRunningOut) {
        timerGloballyHidden = state;
      }
      $element.toggleClass('timer-running-out', timerRunningOut);
      $element.toggleClass('timer-hidden', !timerRunningOut && timerGloballyHidden);
    }

    _setHidden(timerGloballyHidden);

    $element.on('click', function (event) {
      stopEvent(event);
      // toggles if we're allowed
      _setHidden(!timerGloballyHidden);
    });

    function setExpired(expired) {
      $element.toggleClass('expired', expired);
      ctrl.onExpired({ expired: expired });
    }

    function clearTimers() {
      if (timer) timer.stop();
      if (timeLimitTimer) timerHelper.longTimeout.cancel(timeLimitTimer);
      timer = null;
      timeLimitTimer = null;
    }

    function timerEnded() {
      var expired = ctrl.startedAt !== 0;
      setExpired(expired);
      var msg = expired ? ctrl.expiredMessage || "Time's Up!" : BLANK_MESSAGE;
      setTimerEl(msg, 100);
    }

    function createTimerHandler() {
      var startedAt = ctrl.startedAt;
      var timeoutSec = ctrl.timeoutSeconds;
      if (timeoutSec * 0.25 > MAX_WARNING_SEC) {
        // never set the warning range for more than this
        timerTailPercent = 100 * (timeoutSec - MAX_WARNING_SEC) / timeoutSec;
      } else {
        timerTailPercent = 75;
      }
      clearTimers();
      var timeoutMs = timeoutSec * 1000 - (new Date() - startedAt);
      if (timeoutMs < 1) {
        timerRunningOut = true;
        timerEnded();
      } else {
        timerRunningOut = false;
        setExpired(false);
        timer = timerHelper.runEvery(function () {
          var secElapsed = (new Date() - startedAt) / 1000;
          var secRemaining = Math.floor(timeoutSec - secElapsed);
          var remaining = [];
          if (secRemaining > 3600) {
            var h = Math.floor(secRemaining / 3600);
            secRemaining -= h * 3600;
            remaining.push(h + 'h');
          }
          if (remaining.length || secRemaining > 60) {
            var m = Math.floor(secRemaining / 60);
            secRemaining -= m * 60;
            remaining.push(m + 'm');
          }
          remaining.push(secRemaining + 's');
          var percent = 100 * secElapsed / timeoutSec;
          setTimerEl(remaining.join(' '), percent);
        }, 250, false);
        timeLimitTimer = timerHelper.longTimeout(function () {
          clearTimers();
          timerEnded();
        }, timeoutMs);
      }
    }

    function setTimerEl(timerText, percent) {
      var timerBoxEl = $element;
      timerRunningOut = percent > timerTailPercent;
      if (timerRunningOut) {
        _setHidden(false);
      }
      timerBoxEl.toggleClass('warning', timerRunningOut).toggleClass('danger', percent > timerTailPercent * 90 / 75).toggleClass('final-warning', percent > timerTailPercent * 95 / 75).toggleClass('completed', percent >= 100);

      timerBoxEl.querySelector('.timer-status-text').html(timerText);
      timerBoxEl.querySelector('.timer-progress').css({ width: percent + '%' });
    }

    $scope.$on('$destroy', clearTimers);
  }]);

  var resizeToFillLeftoverComponentModule = angular.module('Q.components.resize-to-fill-leftover', [scrollMonitorServiceModule])

  /**
   * @ngdoc directive
   * @docType shared
   * @category directive
   * @name resizeToFillLeftover
   * @description
   * When provided a node selector, this will find the first node that matches, then resize the
   * element it's on to make sure that it is at least (viewport.height - otherNode.height) pixels high.
   *
   * @restrict A
   * @element ANY
   * @param {template} resizeToFillLeftover If provided, use this selector to detect the last item (otherwise use
   *                      the previous item in the parent node)
   * @param {template} resizeToFillLeftoverOffset If provided, reduce the adjusted height by this much
   */
  .directive('resizeToFillLeftover', ['scrollMonitorService', '$log', '$document', '$timeout', function (scrollMonitorService, $log, $document, $timeout) {
    return {
      restrict: 'A',
      link: function link(scope, el, attrs) {
        var scrollWatcher = null;
        var nodeToTrack = null;
        var prev = null;

        attrs.$observe('resizeToFillLeftover', updateRefNode);

        scope.$on(TABSET_EVT_SELECT, _triggerUpdate);

        scope.$on('$destroy', function () {
          if (scrollWatcher) scrollWatcher.destroy();
        });

        function _triggerUpdate() {
          if (scrollWatcher) {
            $timeout(function () {
              scrollWatcher.listener.triggerUpdate(null, true);
            }, 100);
          }
        }

        function updateRefNode() {
          var node = attrs.resizeToFillLeftover;
          if (node !== '#' && (node || node === '')) {
            nodeToTrack = node;
            // async because the watched item might not be rendered yet
            scope.$applyAsync(function () {
              if (scrollWatcher) {
                scrollWatcher.destroy();
              }
              try {
                scrollWatcher = scrollMonitorService.addListener(el.scrollableParent(), onScroll);
                _triggerUpdate();
              } catch (err) {
                // we don't _really_ need to handle this
                $log.debug(err);
              }
            });
          }
        }

        function onScroll(event, data) {
          var node = nodeToTrack ? $document[0].querySelector(nodeToTrack) : el[0].previousElementSibling;
          if (node) {
            var nodeHeight = angular.element(node).offsetHeight();
            var offset = +attrs.resizeToFillLeftoverOffset || 0;
            var height = data.viewportHeight - nodeHeight - offset;
            if (prev !== height) {
              el.css({ 'min-height': height + 'px' });
              prev = height;
            }
          }
        }
      }
    };
  }]);

  var voiceInputTemplate = "<div class=\"voice-input-container\" role=\"region\" aria-label=\"Voice Input Controls\"> <div class=\"browser-warning\" ng-if=\"!$ctrl.isSupported\" role=\"alert\"> Voice input is not supported in your browser. Try using Chrome, Edge, or Safari. </div> <div class=\"voice-inputs-group\"> <ng-transclude></ng-transclude> <div class=\"voice-controls\" ng-if=\"$ctrl.enabled\"> <button type=\"button\" class=\"btn\" ng-class=\"{'btn-danger': $ctrl.isRecording, 'btn-primary': !$ctrl.isRecording}\" ng-click=\"$ctrl.toggleRecording()\" ng-disabled=\"$ctrl.disabled || !$ctrl.isSupported\" aria-label=\"{{ $ctrl.isRecording ? 'Stop Recording' : 'Start Recording' }}\"> <span ng-if=\"$ctrl.isRecording\">Stop</span> <span ng-if=\"!$ctrl.isRecording\">Record</span> <span ng-class=\"{'icon-record': !$ctrl.isRecording, 'icon-stop': $ctrl.isRecording}\" aria-hidden=\"true\"></span> </button> </div> </div> <div class=\"voice-feedback\" role=\"status\" aria-live=\"polite\"> <span class=\"recording-status\" ng-if=\"$ctrl.isRecording\">Recording in progress...</span> <span class=\"recording-error\" ng-if=\"$ctrl.error\">{{ $ctrl.error }}</span> </div> </div> ";

  var voiceInputComponentModule = angular.module('Q.components.voice-input', []).factory('RecorderService', ['$window', '$q', '$log', '$timeout', '$interval', function ($window, $q, $log, $timeout, $interval) {
    return {
      /**
       * Creates and initializes a new audio recorder
       * @returns {Promise} Promise that resolves with a recorder object or rejects with an error
       */
      createRecorder: function createRecorder() {
        var deferred = $q.defer();

        // Check if the browser supports MediaRecorder API
        if (!($window.MediaRecorder && navigator.mediaDevices)) {
          deferred.reject('MediaRecorder not supported');
          return deferred.promise;
        }

        // Define supported audio formats in order of preference
        // We try these formats in sequence until we find one the browser supports
        var mimeTypes = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/ogg'];

        // Find the first supported audio format
        var supportedType = mimeTypes.find(function (type) {
          try {
            return MediaRecorder.isTypeSupported(type);
          } catch (e) {
            $log.warn('Error checking mime type:', type, e);
            return false;
          }
        });

        // If no supported format is found, reject the promise
        if (!supportedType) {
          deferred.reject('No supported audio format found');
          return deferred.promise;
        }

        $log.info('Using audio format:', supportedType);

        // Declare audioMonitor at a higher scope so it's accessible in both .then() and .catch()
        var audioMonitor = null;

        // Request access to the user's microphone
        navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1, // Mono audio (single channel)
            echoCancellation: true, // Reduce echo
            noiseSuppression: true, // Reduce background noise
            sampleRate: 44100, // Standard audio sample rate
            autoGainControl: true, // Automatically adjust volume
            latency: 0, // Minimize delay
            volume: 1.0 // Full volume
          }
        }).then(function (stream) {
          $log.log('Got media stream:', stream.getAudioTracks()[0].getSettings());

          // Ensure the audio track is active
          var audioTrack = stream.getAudioTracks()[0];
          if (!audioTrack.enabled) {
            audioTrack.enabled = true;
          }

          // Create audio context for analyzing audio levels
          var audioContext = new ($window.AudioContext || $window.webkitAudioContext)();

          // Ensure the AudioContext is running (might be suspended on some browsers)
          if (audioContext.state === 'suspended') {
            audioContext.resume();
          }

          // Set up audio analyzer to monitor audio levels
          var source = audioContext.createMediaStreamSource(stream);
          var analyser = audioContext.createAnalyser();
          analyser.fftSize = 2048; // Size of FFT (determines frequency resolution)
          analyser.smoothingTimeConstant = 0.8; // Smoothing factor for visualization
          source.connect(analyser);

          var dataArray = new Uint8Array(analyser.frequencyBinCount);

          // Create the recorder first before referencing it in checkAudio
          var recorder = new $window.MediaRecorder(stream, {
            mimeType: supportedType,
            audioBitsPerSecond: 128000 // 128kbps audio quality
          });

          // Verify if the stream is active and receiving data
          var tracks = stream.getAudioTracks();
          tracks.forEach(function (track) {
            $log.log('Audio track state:', {
              id: track.id,
              label: track.label,
              enabled: track.enabled,
              muted: track.muted,
              readyState: track.readyState
            });
          });

          $log.log('Recorder state:', recorder.state);
          $log.log('Recorder mime type:', recorder.mimeType);

          /**
           * Function to monitor audio levels during recording
           * Helps detect if microphone is working properly
           */
          var checkAudio = function checkAudio() {
            if (recorder && recorder.state === 'recording') {
              // Get current audio frequency data
              analyser.getByteFrequencyData(dataArray);

              // Calculate average audio level
              var audioLevel = dataArray.reduce(function (a, b) {
                return a + b;
              }) / dataArray.length;
              $log.log('Audio level:', audioLevel);

              // If no audio is detected after 500ms, show warnings
              if (audioLevel === 0 && recorder.state === 'recording') {
                $log.warn('No audio detected. Please verify that:');
                $log.warn('1. The microphone is not muted');
                $log.warn('2. The browser has permission to use the microphone');
                $log.warn('3. The correct audio input device is selected');
              }
            }
          };

          // Enumerate available audio input devices for debugging
          navigator.mediaDevices.enumerateDevices().then(function (devices) {
            var audioDevices = devices.filter(function (device) {
              return device.kind === 'audioinput';
            });
            $log.log('Audio devices available:', audioDevices);
          }).catch(function (err) {
            return $log.error('Error enumerating devices:', err);
          });

          // Start monitoring audio levels every 500ms
          audioMonitor = $interval(checkAudio, 500);

          // Array to store audio data chunks as they come in
          var chunks = [];

          /**
           * Event handler for when audio data becomes available
           * This is called periodically during recording (every 100ms as set in start())
           */
          recorder.ondataavailable = function (e) {
            $log.log('Data available event:', {
              size: e.data.size,
              type: e.data.type,
              state: recorder.state,
              timestamp: new Date().toISOString()
            });

            if (e.data.size > 0) {
              chunks.push(e.data);
            } else {
              $log.warn('Received empty data chunk');
            }
          };

          /**
           * Event handler for recorder errors
           */
          recorder.onerror = function (e) {
            $log.error('Recorder error:', e);
            $interval.cancel(audioMonitor);
            deferred.reject('Recording error: ' + e.name);
          };

          // Resolve the promise with an object containing recorder control methods
          deferred.resolve({
            /**
             * Starts the recording process
             * Clears any previous chunks and begins capturing audio
             */
            start: function start() {
              chunks.length = 0; // Clear previous recording data
              try {
                recorder.start(100); // Start recording, get data every 100ms
                $log.log('Recording started, state:', recorder.state);
              } catch (e) {
                $log.error('Error starting recorder:', e);
                $interval.cancel(audioMonitor);
                throw e;
              }
            },

            /**
             * Stops the recording and processes the captured audio
             * @returns {Promise} Promise that resolves with the audio blob or rejects with an error
             */
            stop: function stop() {
              var deferred = $q.defer();
              var timeoutId = void 0;

              // Request any final data that hasn't been delivered yet
              recorder.requestData();

              /**
               * Event handler for when recording stops
               * This processes all collected audio chunks into a single blob
               */
              recorder.onstop = function () {
                $log.log('Recording stopped, state:', recorder.state);

                // Give time for the last chunk to arrive before processing
                timeoutId = $timeout(function () {
                  $log.log('Final chunks:', chunks.length);

                  if (chunks.length === 0) {
                    deferred.reject('No audio data captured');
                    return;
                  }

                  try {
                    // Create a single blob from all audio chunks
                    var blob = new Blob(chunks, {
                      type: supportedType
                    });
                    $log.log('Created blob:', {
                      size: blob.size,
                      type: blob.type
                    });

                    if (blob.size === 0) {
                      deferred.reject('Created empty audio blob');
                      return;
                    }

                    // Stop all audio tracks to release the microphone
                    stream.getTracks().forEach(function (track) {
                      $log.log('Stopping track:', track.label, track.readyState);
                      track.stop();
                    });
                    deferred.resolve(blob);
                  } catch (e) {
                    $log.error('Error creating blob:', e);
                    deferred.reject(e);
                  }
                }, 100); // Wait 100ms for final chunks
              };

              try {
                recorder.stop(); // Stop the recorder
              } catch (e) {
                $log.error('Error stopping recorder:', e);
                $interval.cancel(audioMonitor);
                $timeout.cancel(timeoutId);
                deferred.reject(e);
              }

              return deferred.promise;
            },

            /**
             * Checks if recording is currently in progress
             * @returns {boolean} True if recording, false otherwise
             */
            isRecording: function isRecording() {
              return recorder.state === 'recording';
            },

            /**
             * Cleans up all resources used by the recorder
             * Should be called when done with the recorder to prevent memory leaks
             */
            cleanup: function cleanup() {
              $interval.cancel(audioMonitor);
              if (recorder.state === 'recording') {
                recorder.stop();
              }
              stream.getTracks().forEach(function (track) {
                return track.stop();
              });
              audioContext.close();
            }
          });
        }).catch(function (err) {
          $interval.cancel(audioMonitor);
          deferred.reject(err);
        });

        return deferred.promise;
      }
    };
  }]).component('voiceInput', {
    template: voiceInputTemplate,
    transclude: true,
    bindings: {
      enabled: '<',
      disabled: '<',
      onTextChange: '&'
    },
    controller: 'VoiceInputCtrl'
  }).controller('VoiceInputCtrl',
  /* @ngInject */
  ['$scope', '$window', '$log', '$timeout', '$http', 'RecorderService', function VoiceInputController($scope, $window, $log, $timeout, $http, RecorderService) {
    var ctrl = this;

    ctrl.isRecording = false;
    ctrl.error = null;
    ctrl.isSupported = !!($window.MediaRecorder && navigator.mediaDevices);

    var recorder = null;
    var inputElement = null;

    /**
     * Starts the audio recording process
     * Initializes the recorder if needed and begins capturing audio
     */
    function startRecording() {
      if (ctrl.isRecording || ctrl.disabled) return;

      inputElement = document.activeElement;
      ctrl.error = null;

      if (!recorder) {
        // First time recording - create a new recorder
        RecorderService.createRecorder().then(function (rec) {
          recorder = rec;
          ctrl.isRecording = true;
          recorder.start();
        }).catch(function (error) {
          ctrl.error = 'Error initializing recorder: ' + error.message;
          $log.error('Recorder initialization error:', error);
        });
      } else {
        // Recorder already exists, just start it
        ctrl.isRecording = true;
        recorder.start();
      }
    }

    /**
     * Stops the audio recording and processes the result
     * Sends the audio to the server for transcription
     */
    function stopRecording() {
      if (!recorder || !ctrl.isRecording) return;

      ctrl.isRecording = false;
      recorder.stop().then(function (audioBlob) {
        $log.log('Audio blob:', {
          type: audioBlob.type,
          size: audioBlob.size
        });

        if (audioBlob.size === 0) {
          throw new Error('No audio data captured');
        }

        // Prepare the audio data for upload
        var formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');

        // Send the audio to the server for transcription
        return $http.post(appConfig.audioRecordingUploadRoute, formData, {
          headers: {
            'Content-Type': undefined
          },
          transformRequest: angular.identity
        }).catch(function (error) {
          $log.error('Server error:', error.data);
          throw error.data.error || error.statusText || 'Server error';
        });
      }).then(function (response) {
        if (response.data && response.data.text) {
          // Successfully got transcription, pass it to the callback
          ctrl.onTextChange({ text: response.data.text });
        } else {
          throw new Error('No transcription received');
        }
      }).catch(function (error) {
        ctrl.error = typeof error === 'string' ? error : 'Error processing audio. Please try again.';
        $log.error('Speech-to-text error:', error);
      }).finally(function () {
        // Reset recorder so a new one will be created next time
        if (recorder) {
          recorder.cleanup();
          recorder = null;
        }

        // Restore focus to the previous element
        if (inputElement) {
          $timeout(function () {
            return inputElement.focus();
          }, 0);
        }
      });
    }

    /**
     * Toggles recording state between start and stop
     */
    ctrl.toggleRecording = function () {
      if (ctrl.isRecording) {
        stopRecording();
      } else {
        startRecording();
      }
    };

    /**
     * Handles changes to component bindings
     * Stops recording if component becomes disabled
     */
    ctrl.$onChanges = function (changes) {
      if (changes.disabled && changes.disabled.currentValue && ctrl.isRecording) {
        stopRecording();
      }
    };

    /**
     * Cleanup when component is destroyed
     * Ensures all resources are properly released
     */
    ctrl.$onDestroy = function () {
      if (recorder) {
        recorder.cleanup();
        recorder = null;
      }
      inputElement = null;
    };
  }]);

  var quizChallengeQuestionsTemplate = "<div class=\"question-exponent-{{ $ctrl.questionExponent() }} {{ $ctrl.quizClasses() }}\" global-events ng-form=\"quizChallengeQuestionsForm\"> <div class=\"questions-toolbar\" ng-if=\"$ctrl.modes.solve && ($ctrl._challenge.isTimed() || $ctrl._challenge.isQuestionTimed())\"> <div> <div class=\"progress\" ng-if=\"$ctrl._challenge.isQuestionTimed()\" ng-bind=\"$ctrl.questionsProgress()\"></div> <horizontal-countdown-timer started-at=\"$ctrl.timerStartedAt\" timeout-seconds=\"$ctrl.timerTimeoutSeconds\" on-expired=\"$ctrl.expired = expired\"></horizontal-countdown-timer> <div class=\"next-question\" ng-if=\"$ctrl._challenge.isQuestionTimed() || $ctrl.afterQuizActionLabel\"> <button class=\"btn-{{ $ctrl.expired || $ctrl.completed ? 'primary' : 'default' }} btn-sm\" ng-click=\"$ctrl.nextQuestion()\" ng-disabled=\"$ctrl.nextDisabled()\" ng-bind=\"$ctrl.nextQuestionLabel()\"></button> </div> </div> </div> <div class=\"questions-content\" ng-hide=\"$ctrl.modes.solve && !$ctrl.solution\"> <div> <div class=\"alert alert-danger\" ng-show=\"$ctrl.showIncompleteWarning && $ctrl.solution.canStillAnswer($ctrl._challenge)\"> Please answer the marked questions. </div> <div class=\"well randomized-question-order\" ng-if=\"($ctrl.modes.review || $ctrl.modes.preview) && !$ctrl.candidateReview && ($ctrl.solutionQuestionOrder || $ctrl.solution.randomizedQuestionOrder)\"> <form-group> <label>View Questions in</label> <div class=\"checkable-input-set horizontal\"> <div class=\"qualified-radio\"> <input type=\"radio\" id=\"{{$ctrl.solution.id}}.originalQuestionOrder.false\" ng-model=\"$ctrl.useRandomizedQuestionOrder\" ng-value=\"false\" ng-change=\"$ctrl.updateSolution()\"> <label for=\"{{$ctrl.solution.id}}.originalQuestionOrder.false\" tooltip=\"Shows the questions in the fixed order on the challenge\"> Original Challenge Order </label> </div> <div class=\"qualified-radio\"> <input type=\"radio\" id=\"{{$ctrl.solution.id}}.originalQuestionOrder.true\" ng-model=\"$ctrl.useRandomizedQuestionOrder\" ng-value=\"true\" ng-change=\"$ctrl.updateSolution()\"> <label for=\"{{$ctrl.solution.id}}.originalQuestionOrder.true\" tooltip=\"Shows the questions in the order the {{$root.PT.participant}} solved them in\"> <q-pt>Participant</q-pt>'s Randomized Order </label> </div> </div> </form-group> </div> <div class=\"instructions\" markdown=\"$ctrl._challenge.instructions\" ng-if=\"$ctrl.modes.solve && !$ctrl._challenge.isQuestionTimed()\"></div> <div ng-repeat=\"question in $ctrl._challenge.questions track by question.id\" ng-repeat-alias=\"$questionIndex\" ng-if=\"$ctrl.showQuestion($index)\" scroll-here=\"$ctrl.modes.solve && $ctrl._challenge.isQuestionTimed()\" class=\"question question-{{ question.baseType() }} question-{{ question.answerType() }} {{ $ctrl.errorClass($index) }}\" id=\"quiz-challenge-questions-{{::$ctrl.mode}}-{{$index}}\"> <div class=\"prompt\"> <span class=\"counter\">{{ $index + 1 }}.</span> <div markdown=\"question.prompt\" expandable=\"$ctrl.summarize\" max-lines=\"3\"></div> <div class=\"max-selections\" ng-if=\"$ctrl.modes.solve && question.isMultipleAnswer()\" ng-bind-html=\"question.remainingSelectionsMessage($ctrl.solution.answers[$questionIndex])\"></div> </div> <div class=\"answer\" compile-template=\"$ctrl.questionTemplate(question)\"></div> <expandable class=\"answer-validation-details\" label=\"{{ question.optionsSummary($ctrl._challenge, $ctrl.solution.answers[$index]) }}\" label-collapsed=\"{{ question.optionsSummary($ctrl._challenge, $ctrl.solution.answers[$index]) }} (Expand for Details)\" ng-if=\"$ctrl.showValidationDetails(question, $index)\"> <div ng-if=\"question.enableGptTransform\" class=\"ai-validation-info-item\"> <label>AI System Prompt</label> <markdown-editor ng-model=\"question.aiSystemPrompt\" ng-readonly=\"true\" allow-dark-mode=\"true\" auto-expand=\"false\" collapse-on-blur=\"true\"></markdown-editor> </div> <div ng-if=\"question.enableGptTransform\" class=\"ai-validation-info-item\"> <label> AI Response <small>({{question.aiModel}})</small> </label> <markdown-editor ng-model=\"$ctrl.solution.answers[$questionIndex].aiResponse\" ng-readonly=\"true\" allow-dark-mode=\"true\" auto-expand=\"false\" collapse-on-blur=\"true\"></markdown-editor> </div> <quiz-challenge-question-matchers question=\"question\" results=\"$ctrl.matcherNotes[$index].results\"></quiz-challenge-question-matchers> </expandable> <p class=\"options-summary\" ng-if=\"$ctrl.showOptionsSummary(question, $index)\"> <span ng-bind=\"question.optionsSummary($ctrl._challenge, $ctrl.solution.answers[$index])\"></span> <a ui-sref=\"challengeEdit.questions({ challengeId: $ctrl._challenge.id, '#': 'challenge-question-'+($index+1)})\" class=\"edit-link\" ng-if=\"$ctrl.editButtons\">Edit Question</a> </p> <div class=\"next-question\" ng-if=\"$ctrl.modes.solve\"> <button class=\"btn-{{ $ctrl.expired ? 'primary' : 'default' }} btn-sm\" ng-if=\"$ctrl._challenge.isQuestionTimed()\" ng-click=\"$ctrl.nextQuestion()\" ng-disabled=\"$ctrl.nextDisabled()\"> Next Question <span class=\"shortcut-hint\">{{$ctrl.nextQuestionKeyStroke}}</span> </button> <span ng-hide=\"$ctrl._challenge.isQuestionTimed() || $ctrl.currentQuestionIdx !== $index || $ctrl.nextDisabled()\"> Press <span class=\"shortcut-hint\">{{$ctrl.nextQuestionKeyStroke}}</span> for Next Question </span> </div> </div> <div class=\"note\" ng-if=\"!$ctrl._challenge.questions.length\"> No questions added. </div> <div class=\"note\" ng-if=\"$ctrl.completed\">All questions answered.</div> <score-card-instructions challenge=\"$ctrl._challenge\" ng-if=\"$ctrl.modes.solve\"></score-card-instructions> <div class=\"after-quiz-action\" ng-if=\"$ctrl.afterQuizActionLabel && (!$ctrl._challenge.isQuestionTimed() || $ctrl.completed)\"> <button class=\"btn-{{ ($ctrl.completed || $ctrl.allAnswered) ? 'primary' : 'default' }} btn-sm\" ng-click=\"$ctrl.onAfterQuizAction()\"> {{ $ctrl.afterQuizActionLabel}} <span class=\"after-quiz-action-keystroke shortcut-hint\">{{$ctrl.nextQuestionKeyStroke}}</span> </button> </div> <div resize-to-fill-leftover=\"#quiz-challenge-questions-{{::$ctrl.mode}}-{{$ctrl._challenge.questions.length-1}}\" resize-to-fill-leftover-offset=\"{{ $ctrl.afterQuizActionLabel ? 110 : 50 }}\" ng-if=\"$ctrl.modes.solve && !$ctrl._challenge.isQuestionTimed()\"></div> </div> </div> </div> ";

  var quizChallengeQuestionMatcherTemplate = "<div ng-if=\"$ctrl.question.matchers.length\" class=\"matchers type-{{ $ctrl.question.isCode() ? 'code' : 'plain' }} {{ $ctrl.results ? 'has-results' : '' }}\"> <div ng-repeat=\"m in $ctrl.question.matchers track by $index\" class=\"{{ $ctrl.matcherClass(m, $index) }}\" ng-class=\"{ negate: m.negate }\"> <div class=\"options\"> <span class=\"type\">{{ $ctrl.getMatcherType(m, $index) }}</span> <span class=\"case\">{{ m.case ? 'Exact Case' : 'Case Insensitive' }}</span> </div> <code ng-if=\"$ctrl.question.isCode() && (m.type === 'exact' || m.type === 'includes')\" code-highlight=\"m.value\" language=\"$ctrl.question.editorLanguage()\"></code> <code ng-if=\"!$ctrl.question.isCode() && (m.type === 'exact' || m.type === 'includes')\" ng-bind=\"m.value\"></code> <code ng-if=\"m.type !== 'exact' && m.type !== 'includes'\" regex-highlight=\"'/' + m.value + '/' + (m.case?'':'i') + (m.multi?'m':'')\"></code> </div> </div> ";

  var previewMCTemplate = "<div ng-repeat=\"choice in question.choices track by $index\" class=\"qualified-{{ question.inputType() }}\" ng-init=\"isAnswer = question.isAnswer($index)\"> <input type=\"{{ question.inputType() }}\" disabled=\"disabled\" ng-model=\"isAnswer\" ng-value=\"true\" id=\"q{{question.id}}.a{{$index}}\"> <label for=\"q{{question.id}}.a{{$index}}\" ng-class=\"{ weighted: question.weight }\"> <span class=\"weighted-value\" ng-if=\"question.weight\" ng-class=\"{ correct: choice.value > 0, incorrect: choice.value < 0 }\"> {{question.weight * choice.value / question.maxSum |\n      fuzzyNumber:'number':3:true}}pts </span> <div markdown=\"choice.label\"></div> </label> </div> ";

  var previewTextTemplate = "<p> Answer format: <strong>{{ question.answerTypeLabel() }}</strong> </p> <div ng-if=\"question.defaultValue\" class=\"default-value-preview\"> <p>Default Value:</p> <div code-highlight=\"question.defaultValue\" language=\"question.editorLanguage()\" line-number-start=\"question.isCode() && question.isMultiline() ? 1 : false\"></div> </div> <div ng-if=\"question.enableGptTransform\"> <p ng-if=\"question.aiSystemPrompt\">AI System Prompt:</p> <small ng-if=\"question.aiSystemPrompt\" language=\"markdown\" code-highlight=\"question.aiSystemPrompt\"> </small> <p ng-if=\"!question.aiSystemPrompt\"> <em>No prompt has been added yet</em> </p> </div> <div ng-show=\"question.matchers.length\"> <p>{{question.enableGptTransform ? \"AI response v\" : \"V\"}}alidated by:</p> <quiz-challenge-question-matchers question=\"question\"></quiz-challenge-question-matchers> </div> ";

  var reviewMCTemplate = "<div ng-repeat=\"choice in question.choices track by $index\" class=\"qualified-{{ question.inputType() }} {{ question.isCorrect($index, $ctrl.solution.answers[$questionIndex]) ? 'correct' : 'wrong' }}\"> <input type=\"{{ question.inputType() }}\" disabled=\"disabled\" ng-checked=\"question.isAnswer($index, $ctrl.solution.answers[$questionIndex])\" id=\"q{{question.id}}.a{{$index}}\"> <label for=\"q{{question.id}}.a{{$index}}\" ng-class=\"{ weighted: question.weight }\"> <span class=\"weighted-value {{ question.isAnswer($index, $ctrl.solution.answers[$questionIndex]) ? 'selected' : 'unselected' }}\" ng-if=\"question.weight\" ng-class=\"{ correct: choice.value > 0, incorrect: choice.value < 0 }\"> {{question.weight * choice.value / question.maxSum |\n      fuzzyNumber:'number':3:true}}pts </span> <div markdown=\"choice.label\"></div> </label> </div> ";

  var reviewTextTemplate = "<div ng-if=\"$ctrl.solution.answers[$questionIndex].values[0]\" highlight-matches=\"$ctrl.matcherNotes[$questionIndex].highlightMatches\"> <div ng-if=\"question.isPlain()\" class=\"answer-plain-text\" ng-bind=\"$ctrl.solution.answers[$questionIndex].values[0].trim()\"></div> <div ng-if=\"question.isMarkdown()\" markdown=\"$ctrl.solution.answers[$questionIndex].values[0]\"></div> <div ng-if=\"question.isCode()\" code-highlight=\"$ctrl.solution.answers[$questionIndex].values[0]\" language=\"question.options.language\" line-number-start=\"question.answerType() === 'lcode' ? 1 : 0\"></div> </div> <div ng-if=\"!$ctrl.solution.answers[$questionIndex].values[0]\" class=\"no-answer text-muted\"> No Answer Provided </div> ";

  var solveCodeTemplate = "<div class=\"form-group {{ $ctrl.errorClass($questionIndex) }}\"> <p class=\"language-note\" ng-show=\"question.hasLanguage()\"> Please answer using {{ question.languageLabel() }}: </p> <ui-codemirror class=\"quiz-codemirror\" ui-codemirror-opts=\"$ctrl.createCMOpts(question)\" ng-model=\"$ctrl.solution.answers[$questionIndex].values[0]\" ng-required=\"$ctrl.highlightErrors\" ng-change=\"$ctrl._onAnswerChange($questionIndex)\" ng-readonly=\"$ctrl.readonly\" ng-disabled=\"$ctrl.disabled || $ctrl.expired\" ng-focus=\"$ctrl.focusOn($questionIndex)\" ng-keydown=\"$ctrl.onKeyDown($questionIndex)\" ng-blur=\"$ctrl.onBlur($questionIndex)\"></ui-codemirror> </div> ";

  var solveLongTemplate = "<div class=\"form-group {{ $ctrl.errorClass($questionIndex) }}\"> <markdown-editor ng-model=\"$ctrl.solution.answers[$questionIndex].values[0]\" ng-required=\"$ctrl.highlightErrors\" ng-change=\"$ctrl._onAnswerChange($questionIndex)\" id=\"q{{question.id}}.value\" auto-expand=\"true\" collapse-on-blur=\"!question.isEssay()\" ng-readonly=\"$ctrl.readonly\" ng-disabled=\"$ctrl.disabled || $ctrl.expired\" allow-dark-mode=\"$ctrl.allowDarkMode\" ng-focus=\"$ctrl.focusOn($questionIndex)\" ng-keydown=\"$ctrl.onKeyDown($questionIndex)\" ng-blur=\"$ctrl.onBlur($questionIndex)\"></markdown-editor> </div> ";

  var solvePlainTemplate = "<div class=\"form-group {{ $ctrl.errorClass($questionIndex) }}\"> <voice-input enabled=\"question.options.voiceToText\" disabled=\"$ctrl.disabled || $ctrl.expired\" on-text-change=\"$ctrl.solution.answers[$questionIndex].values[0] = text; $ctrl._onAnswerChange($questionIndex)\"> <textarea class=\"form-control\" ng-model=\"$ctrl.solution.answers[$questionIndex].values[0]\" ng-required=\"$ctrl.highlightErrors\" ng-change=\"$ctrl._onAnswerChange($questionIndex)\" ng-readonly=\"$ctrl.readonly || question.options.voiceToText\" ng-disabled=\"$ctrl.disabled || $ctrl.expired\" ng-focus=\"$ctrl.focusOn($questionIndex)\" ng-keydown=\"$ctrl.onKeyDown($questionIndex)\" ng-blur=\"$ctrl.onBlur($questionIndex)\" rows=\"4\" msd-elastic=\"\\n\"></textarea> </voice-input> </div> ";

  var solveMCMATemplate = "<div ng-repeat=\"choice in question.choices track by $index\" class=\"qualified-checkbox\"> <input type=\"checkbox\" ng-model=\"$ctrl.solution.answers[$questionIndex].$mcmaValues[$index]\" ng-change=\"$ctrl.updateMCMA(question, $questionIndex, $index)\" id=\"q{{question.id}}.v{{$index}}\" ng-readonly=\"$ctrl.readonly\" ng-disabled=\"$ctrl.disabled || $ctrl.expired || question.shouldDisable($ctrl.solution.answers[$questionIndex], $index)\" ng-focus=\"$ctrl.focusOn($questionIndex)\" ng-click=\"$ctrl.focusOn($questionIndex)\"> <label for=\"q{{question.id}}.v{{$index}}\" markdown=\"choice.label\"></label> <label for=\"q{{question.id}}.v{{$index}}\" class=\"keyhint\" ng-show=\"$ctrl.currentQuestionIdx === $questionIndex\" ng-bind=\"$ctrl.keyHintFor($index)\"></label> </div> ";

  var solveMCSATemplate = "<div ng-repeat=\"choice in question.choices track by $index\" class=\"qualified-radio\"> <input type=\"radio\" ng-model=\"$ctrl.solution.answers[$questionIndex].values[0]\" ng-change=\"$ctrl._onAnswerChange($questionIndex)\" ng-value=\"$index\" name=\"q{{question.id}}.value\" id=\"q{{question.id}}.v{{$index}}\" ng-readonly=\"$ctrl.readonly\" ng-disabled=\"$ctrl.disabled || $ctrl.expired\" ng-focus=\"$ctrl.focusOn($questionIndex)\" ng-click=\"$ctrl.focusOn($questionIndex)\"> <label for=\"q{{question.id}}.v{{$index}}\" markdown=\"choice.label\"></label> <label for=\"q{{question.id}}.v{{$index}}\" class=\"keyhint\" ng-show=\"$ctrl.currentQuestionIdx === $questionIndex\" ng-bind=\"$ctrl.keyHintFor($index)\"></label> </div> ";

  var solveShortTemplate = "<div class=\"form-group {{ $ctrl.errorClass($questionIndex) }}\"> <voice-input enabled=\"question.options.voiceToText\" disabled=\"$ctrl.disabled || $ctrl.expired\" on-text-change=\"$ctrl.solution.answers[$questionIndex].values[0] = text; $ctrl._onAnswerChange($questionIndex)\"> <input class=\"form-control\" type=\"text\" autocomplete=\"off\" ng-model=\"$ctrl.solution.answers[$questionIndex].values[0]\" ng-required=\"$ctrl.highlightErrors\" ng-change=\"$ctrl._onAnswerChange($questionIndex)\" ng-readonly=\"$ctrl.readonly || question.options.voiceToText\" ng-disabled=\"$ctrl.disabled || $ctrl.expired\" ng-focus=\"$ctrl.focusOn($questionIndex)\" ng-keydown=\"$ctrl.onKeyDown($questionIndex)\" ng-blur=\"$ctrl.onBlur($questionIndex)\"> </voice-input> </div> ";

  var ngRepeatAliasDirectiveModule = angular.module('Q.components.ng-repeat-alias', [])

  /**
   * @docType shared
   * @category directive
   *
   * @ngdoc directive
   *
   * @name ngRepeatAlias
   * @restrict A
   * @scope
   * @priority 400
   *
   * @description
   * Provides alias(es) for ngRepeat properties. The main reason for this over ngInit is it can handle when the list
   * is using "track by" and gets reordered. With ngInit, the value is never updated.
   *
   * With this method, the values are set on every digest. This ensures that the values will always be up-to-date,
   * but could cause digest errors if you are dealing with a lot of properties.
   *
   * @param {expression} ngRepeatAlias - if just a valid identifier, sets that scope var to $index.
   *     Otherwise should be a hash of { newName: currentName }
   *
   */
  .directive('ngRepeatAlias', function () {
    return {
      restrict: 'A',
      link: function link(scope, el, attr) {
        var setValues = void 0;
        if (/^[$\w]+$/.test(attr.ngRepeatAlias)) {
          setValues = function setValues() {
            scope[attr.ngRepeatAlias] = scope.$index;
          };
        } else {
          setValues = function setValues() {
            angular.extend(scope, scope.$eval(attr.ngRepeatAlias));
          };
        }
        setValues();
        scope.$watch(setValues);
      }
    };
  });

  /**
   * This component is used to render questions in several ways:
   *
   * - As the details view
   * - As the "answers" view (which is the same thing)
   * - For taking the quiz
   * - For reviewing a candidate's submission
   *
   */

  var quizChallengeQuestionsComponentModule = angular.module('Q.components.quiz-challenge-questions', [compileTemplateComponentModule, markdownEditorComponentModule, codemirrorComponentModule, codemirrorOptionsComponentModule, regexHighlightComponentModule, highlightMatchesComponentModule, scrollToComponentModule, timerHelperModule, globalEventsComponentModule, keycodesModule, fuzzyNumberFilterModule, horizontalCountdownTimerComponentModule, resizeToFillLeftoverComponentModule, ngRepeatAliasDirectiveModule, voiceInputComponentModule]).component('quizChallengeQuestions', {
    bindings: {
      mode: '@',
      challenge: '<?',
      editButtons: '<',
      solution: '<',
      candidateReview: '<',
      summarize: '<',
      hideUnanswered: '<',
      disabled: '<',
      readonly: '<',
      highlightErrors: '<',
      onAnswerChange: '&',
      showIncompleteWarning: '<',
      afterQuizActionLabel: '<',
      onAfterQuizAction: '&',
      allowDarkMode: '<',
      useRandomizedQuestionOrder: '=?',
      solutionQuestionOrder: '<'
    },
    controller: 'QuizChallengeQuestionsCtrl',
    template: quizChallengeQuestionsTemplate
  }).controller('QuizChallengeQuestionsCtrl', ['$scope', '$element', '$timeout', 'QuizQuestion', 'quizQuestionMatcherNotes', 'timerHelper', 'KeyCodes', 'scrollToElement', '$q', '$log', 'quizTextQuestionCMOpts', function QuizChallengeQuestionsCtrl($scope, $element, $timeout, QuizQuestion, quizQuestionMatcherNotes, timerHelper, KeyCodes, scrollToElement, $q, $log, quizTextQuestionCMOpts) {
    var ctrl = this;
    var el = $element[0];

    var usingChallengeSnapshot = false;

    function clearTimer() {
      startTimer(0, 0);
    }

    function startTimer(startedAt, timeoutSeconds) {
      ctrl.timerStartedAt = startedAt;
      ctrl.timerTimeoutSeconds = timeoutSeconds;
    }

    var TEMPLATES = {
      preview: {
        mc: previewMCTemplate,
        text: previewTextTemplate
      },
      review: {
        mc: reviewMCTemplate,
        text: reviewTextTemplate
      },
      solve: {
        // single line of code
        scode: solveCodeTemplate,
        // multiple lines of code
        lcode: solveCodeTemplate,
        // single line of unformatted text
        short: solveShortTemplate,
        // markdown editor
        long: solveLongTemplate,
        // plain text editor
        lplain: solvePlainTemplate,
        // markdown editor, full-page
        essay: solveLongTemplate,
        // plain text editor, full-page
        essayplain: solvePlainTemplate,
        // multiple choice, multiple answers (checkboxes)
        mcma: solveMCMATemplate,
        // multiple choice, single answer (radios)
        mcsa: solveMCSATemplate
      }
    };

    function updateQuestionOrder() {
      var questionOrder = ctrl.solutionQuestionOrder || ctrl.solution && ctrl.solution.randomizedQuestionOrder;
      if (Array.isArray(questionOrder) && ctrl.useRandomizedQuestionOrder) {
        // to randomize the question order, we'll prototypically inherit the original model, then
        // override the questions array with the randomly sorted one
        ctrl._challenge = Object.create(ctrl._challenge);
        ctrl._challenge.questions = sortBy(ctrl._challenge.questions, function (q1) {
          return questionOrder.indexOf(q1.id) || Infinity;
        }, 'position');
      }
    }

    function updateSolution() {
      if (ctrl.solution) {
        usingChallengeSnapshot = !!ctrl.solution.challengeSnapshot;
        if (usingChallengeSnapshot) {
          ctrl._challenge = ctrl.solution.getChallengeSnapshot();
        } else {
          ctrl._challenge = ctrl.challenge;
        }
        updateQuestionOrder();
        _refreshAnswers();
        if (ctrl.modes.solve) {
          ctrl.currentQuestionIdx = null;

          clearTimer();
          var challenge = ctrl._challenge;

          if (!challenge) {
            $log.error("Invariant Violation: Expected challenge to be defined but it wasn't");
          }

          if (challenge && challenge.isTimed()) {
            startTimer(ctrl.solution.timerStartedAt, challenge.timeLimitMin * 60);
            if (ctrl.solution.canStillAnswer(challenge)) {
              ctrl.nextQuestion();
            } else {
              ctrl.currentQuestionIdx = challenge.questions.length;
            }
          } else {
            ctrl.nextQuestion();
          }
        }
        updateMatcherNotes();
      } else {
        ctrl._challenge = ctrl.challenge;
        updateQuestionOrder();
      }
    }

    ctrl.updateSolution = updateSolution;

    function _setupAnswer(question, answer) {
      answer.values = answer.values || [];
      if (question.isMultipleAnswer()) {
        defineValue(answer, '$mcmaValues', []);
        question.choices.forEach(function (_, cidx) {
          answer.$mcmaValues[cidx] = answer.values.indexOf(cidx) !== -1;
        });
      }
    }

    function _setDefaultValue(question, answer) {
      if (question.isText() && question.defaultValue && !answer.values[0]) {
        answer.values[0] = question.defaultValue;
      }
    }

    function _refreshAnswers() {
      if (ctrl.solution) {
        // we need to make sure the answers are sorted and matching the challenge data,
        // and in the same order
        var answers = ctrl.solution.answers || [];

        ctrl.solution.answers = [];
        (ctrl._challenge && ctrl._challenge.questions || []).forEach(function (q, idx) {
          var answer = answers.find(function (a) {
            return a.questionId === q.id;
          });
          if (!answer) {
            answer = {
              questionId: q.id,
              values: []
            };
          }
          ctrl.solution.answers[idx] = answer;
          _setupAnswer(q, answer);
          if (!ctrl._challenge.isQuestionTimed()) {
            _setDefaultValue(q, answer);
          }
        });

        _checkAllAnswered();
      }
    }

    ctrl.currentQuestionIdx = null;
    ctrl.showQuestion = function ($index) {
      if (ctrl.modes.solve) {
        if (!ctrl.solution) {
          return false;
        }
        if (ctrl._challenge.isQuestionTimed()) {
          return ctrl.currentQuestionIdx === $index;
        }
      } else if (ctrl.modes.review && ctrl.hideUnanswered) {
        return ctrl.solution && ctrl.solution.answered($index);
      }
      return true;
    };

    function _checkAllAnswered() {
      ctrl.allAnswered = ctrl.solution && typeof ctrl.solution.allAnswered === 'function' && ctrl.solution.allAnswered();
      return ctrl.allAnswered;
    }

    ctrl._onAnswerChange = function ($questionIndex) {
      // check if every question has an answer
      _checkAllAnswered();
      var answer = ctrl.solution && ctrl.solution.answers[$questionIndex];
      var question = ctrl._challenge.questions[$questionIndex];

      if (answer && !answer.completedAt) {
        // track when the answer was last updated
        answer.answeredAt = new Date();

        // Calculate and update length score if length validation is enabled
        if (question.options && question.options.lengthValidation && question.options.lengthValidation.enabled) {
          // Calculate length score from the current answer text
          var answerText = answer.values[0] || '';
          answer.lengthScore = question.calculateLengthScore(answerText);

          // Also update the total score based on accuracy and length scores
          if (answer.accuracyScore !== undefined) {
            answer.score = (answer.accuracyScore + answer.lengthScore) / 2;
          }
        }
      }

      $q.when(ctrl.onAnswerChange()).then(function () {
        if (!ctrl.allAnswered) {
          // Also check if every question has an answer after saving, but only to mark it if it already
          // isn't. This is because the async events can come out of order, and we don't want to clear
          // the state accidentally
          _checkAllAnswered();
        }
      });
    };

    function _setupSolver() {
      ctrl.nextQuestion = function (reverse) {
        var challenge = ctrl._challenge;
        if (reverse !== true && ctrl.currentQuestionIdx >= challenge.questions.length) {
          ctrl.onAfterQuizAction();
        } else if (ctrl.solution) {
          var scrollToQuestionIdx = 0;
          if (challenge.isQuestionTimed()) {
            // Note: reverse is ignored for question-timed, you can only go forwards
            var answers = ctrl.solution.answers;
            if (answers[ctrl.currentQuestionIdx]) {
              // mark previous question as answered
              answers[ctrl.currentQuestionIdx].completedAt = new Date();
            }
            // find the first question that wasn't answered yet _and_ has time left
            ctrl.currentQuestionIdx = ctrl.solution.needsAnswerIdx(challenge, answers);
            if (ctrl.currentQuestionIdx === -1) {
              scrollToQuestionIdx = ctrl.currentQuestionIdx = challenge.questions.length;
              clearTimer();
              ctrl.completed = true;
            } else {
              var q = challenge.questions[ctrl.currentQuestionIdx];
              var ans = answers[ctrl.currentQuestionIdx];
              if (!ans.startedAt) ans.startedAt = new Date();
              _setDefaultValue(q, ans);
              startTimer(ans.startedAt, q._timeLimitSec(challenge));
            }

            ctrl._onAnswerChange();
          } else {
            // switch focus forwards or backwards for other types of quizzes
            var qIdx = ctrl.currentQuestionIdx;
            if (ctrl.currentQuestionIdx === null) {
              qIdx = 0;
            } else if (reverse === true && qIdx > 0) {
              qIdx--;
            } else if (reverse !== true && qIdx < challenge.questions.length) {
              qIdx++;
            }
            scrollToQuestionIdx = ctrl.currentQuestionIdx = qIdx;
          }

          $timeout(function () {
            if (scrollToQuestionIdx >= challenge.questions.length) {
              // focus on the "next challenge" button
              var nextButton = $element.querySelector('.after-quiz-action button');
              if (nextButton.length) {
                nextButton.focus();
                scrollToElement({ target: nextButton });
              }
            } else {
              var question = el.querySelectorAll('.question')[scrollToQuestionIdx];
              if (!question) return;
              var inputElement = question.querySelector('.answer input, .answer textarea');
              var questionsContent = el.querySelector('.questions-content');
              var scroll = questionsContent && questionsContent.scrollTop;
              if (inputElement && inputElement.focus) {
                inputElement.focus();
              }
              if (scrollToQuestionIdx === 0 && reverse !== true) {
                // always scroll to top
                if (questionsContent) {
                  questionsContent.scrollTop = 0;
                }
              } else {
                questionsContent.scrollTop = scroll;
                var promptElement = question.querySelector('.prompt');
                if (promptElement) {
                  $timeout(function () {
                    scrollToElement({ target: promptElement, center: 0 });
                  }, 250);
                }
              }
            }
          }, 100);
        }
      };

      ctrl.focusOn = function (index) {
        // track manual focus changes (i.e.: user clicking through or tabbing through)
        ctrl.currentQuestionIdx = index;
      };

      var KEY_HINTS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789,./;\'[]-=\\';
      var KEY_HINT_CHARS = KEY_HINTS.toLowerCase();

      ctrl.keyHintFor = function ($index) {
        return KEY_HINTS[$index] || '';
      };

      ctrl.nextQuestionKeyStroke = KeyCodes.SYMBOLS.COMMAND_KEY + ' + ' + KeyCodes.SYMBOLS.ENTER;
      // allow global keystrokes for "next" and selecting multiple choice answers via keystrokes
      $scope.$on('keydown', function ($event, e, props) {
        // only listen if we have a solution, and on descendants or parents of this element
        if (!ctrl.solution || !e.target || !(el.contains(e.target) || e.target.contains(el))) {
          return;
        }

        var reverse = KeyCodes.isShift(e);

        if (props.common === 'enter' && (!ctrl.nextDisabled() || reverse) && KeyCodes.isCommand(e)) {
          $event.preventDefault();
          $scope.$applyAsync(function () {
            ctrl.nextQuestion(reverse);
          });
        } else {
          var keyhintIndex = KEY_HINT_CHARS.indexOf(props.char);
          if (keyhintIndex > -1) {
            var $questionIndex = ctrl.currentQuestionIdx;
            var q = ctrl._challenge.questions[$questionIndex];
            var answer = ctrl.solution.answers && ctrl.solution.answers[$questionIndex];
            if (q && q.isMultipleChoice() && keyhintIndex < q.choices.length && answer) {
              $event.preventDefault();
              $scope.$applyAsync(function () {
                if (q.maxSelections === 1) {
                  answer.values[0] = keyhintIndex;
                  ctrl._onAnswerChange($questionIndex);
                } else {
                  answer.$mcmaValues[keyhintIndex] = !answer.$mcmaValues[keyhintIndex];
                  ctrl.updateMCMA(q, $questionIndex, keyhintIndex);
                }
              });
            }
          }
        }
      });
      ctrl.nextQuestionLabel = function () {
        return ctrl.completed && ctrl.afterQuizActionLabel ? ctrl.afterQuizActionLabel : 'Next Question';
      };
      ctrl.nextDisabled = function () {
        var index = ctrl.currentQuestionIdx;
        var allAnswered = ctrl.completed || !ctrl.challenge.isQuestionTimed() && ctrl.allAnswered;

        var questions = ctrl.challenge.questions;


        if (allAnswered && questions && index >= questions.length - 1) {
          return !ctrl.afterQuizActionLabel;
        }
        return !ctrl.solution || !ctrl.expired && index !== null && !ctrl.solution.answered(index);
      };
      ctrl.questionsProgress = function () {
        var questions = ctrl._challenge.questions;
        if (!questions || !questions.length) {
          return '';
        }
        var question = ctrl.completed ? questions.length : ctrl.currentQuestionIdx + 1;
        return question + ' / ' + questions.length + ' Questions';
      };
    }

    ctrl.$onInit = function () {
      ctrl.mode = ctrl.mode || 'preview';
      ctrl.modes = defineProperty({}, ctrl.mode, true);
      if (!ctrl.challenge && ctrl.solution) {
        ctrl.challenge = ctrl.solution.challenge;
      }
      ctrl._challenge = ctrl.challenge;
      if (ctrl.modes.solve) {
        _setupSolver();
      }

      if (ctrl.useRandomizedQuestionOrder === undefined) {
        ctrl.useRandomizedQuestionOrder = !!(ctrl.modes.solve || ctrl.candidateReview);
      }
      updateSolution();

      $scope.$on('socket:model.changed:Solution', function (event, data) {
        if (data.id && ctrl.solution && data.id === ctrl.solution.id) {
          _refreshAnswers();
        }
      });

      // configure change detection AFTER initialization
      ctrl.$onChanges = function (changes) {
        if (changes.solution || changes.challenge) {
          updateSolution();
        }
      };
    };

    ctrl.quizClasses = function () {
      var c = [ctrl.mode];
      if (ctrl.summarize) c.push('summarize');
      if (ctrl.highlightErrors === false) c.push('disable-error-highlighting');
      if (ctrl.candidateReview) c.push('candidate-review');
      // blockInstructionCopy defaults true (see QuizChallenge); only absent when authored off
      if (ctrl._challenge && ctrl._challenge.blockInstructionCopy !== false) c.push('copy-protected');
      c.push(ctrl._challenge && ctrl._challenge.isQuestionTimed() ? 'question-timed' : 'not-question-timed');
      return c.join(' ');
    };

    var watchingSolution = false;
    ctrl.matcherNotes = [];

    function updateMatcherNotes() {
      ctrl.matcherNotes = [];
      if (ctrl.solution && ctrl.modes.review && ctrl.solution.answers && ctrl._challenge.questions) {
        ctrl._challenge.questions.forEach(function (q, idx) {
          var answer = ctrl.solution.answers[idx];
          var aiResponse = answer && answer.aiResponse;
          var answerValue = answer && answer.values && answer.values[0];
          var matcherAnswer = aiResponse || answerValue;
          ctrl.matcherNotes[idx] = quizQuestionMatcherNotes.generate(matcherAnswer, q);
        });

        if (!watchingSolution) {
          watchingSolution = true;
          $scope.$watch(function () {
            return (ctrl.solution.answers || []).map(function (a) {
              return a.values.join('|');
            }).join(';');
          }, updateMatcherNotes);
        }
      }
    }

    ctrl.showValidationDetails = function (question, index) {
      var answers = ctrl.solution && ctrl.solution.answers;
      var answer = answers && answers[index];
      var mNotes = ctrl.matcherNotes[index];
      return ctrl.modes.review && !ctrl.candidateReview && question.isText() && answer && answer.values && answer.values[0] && mNotes && mNotes.results.length;
    };
    ctrl.showOptionsSummary = function (question, index) {
      return !ctrl.modes.solve && !ctrl.candidateReview && !ctrl.showValidationDetails(question, index);
    };

    ctrl.questionExponent = function () {
      var count = ctrl._challenge && ctrl._challenge.questions && ctrl._challenge.questions.length;
      // we support properly rendering up to 999 questions :-)
      return count < 10 ? 1 : count < 100 ? 2 : 3;
    };

    ctrl.questionTemplate = function (question) {
      var modeTemplates = TEMPLATES[ctrl.mode];
      var template = void 0;
      if (ctrl.modes.preview || ctrl.modes.review) {
        template = modeTemplates[question.isText() ? 'text' : 'mc'];
      } else {
        template = modeTemplates[question.answerType()];
      }
      return template;
    };

    ctrl.errorClass = function (questionIndex) {
      if (ctrl.highlightErrors && ctrl.modes.solve && ctrl.solution) {
        var empty$$1 = true;
        var answers = ctrl.solution.answers;
        if (answers && answers[questionIndex] && answers[questionIndex].values) {
          var val = answers[questionIndex].values[0];
          empty$$1 = !(val || val === 0);
        }
        if (empty$$1) return 'has-error';
      }
      if (ctrl.modes.review) {
        var classes = 'unanswered';
        var _answers = ctrl.solution && ctrl.solution.answers;
        if (_answers && _answers[questionIndex]) {
          if (_answers[questionIndex].score < 100) {
            classes = _answers[questionIndex].values.length ? 'has-error' : 'has-error unanswered';
          } else {
            classes = '';
          }
        }
        return classes;
      }
      return '';
    };

    ctrl.updateMCMA = function (question, $questionIndex, $choiceIndex) {
      if (ctrl.solution && ctrl.solution.answers) {
        var answer = ctrl.solution.answers[$questionIndex];
        if (question.isMultipleAnswer()) {
          if (!Array.isArray(answer.$mcmaValues)) {
            _setupAnswer(question, answer);
          }
          // if the max number of selections have already been reached then prevent additional selections
          if (answer.$mcmaValues[$choiceIndex] && question.updateMaxSelections() === answer.values.length) {
            answer.$mcmaValues[$choiceIndex] = false;
          } else {
            answer.values = [];
            answer.$mcmaValues.forEach(function (val, idx) {
              if (val) {
                answer.values.push(idx);
              }
            });
          }
        }
      }
      ctrl._onAnswerChange($questionIndex);
    };

    ctrl.onKeyDown = function ($questionIndex) {
      var answer = ctrl.solution.answers[$questionIndex];
      if (!answer.typingStartedAt) {
        answer.typingStartedAt = new Date();
      }
      // Increase the typed characters count
      if (!answer.typedKeysCount) {
        answer.typedKeysCount = 0;
      }
      // Only count printable characters, not special keys like shift, ctrl, etc
      answer.typedKeysCount++;
    };
    // Register typing end
    ctrl.onBlur = function ($questionIndex) {
      var answer = ctrl.solution.answers[$questionIndex];
      answer.lastTypedAt = new Date();
    };

    quizTextQuestionCMOpts.create(ctrl);
  }]).factory('quizTextQuestionCMOpts', ['codemirrorOptions', function quizTextQuestionCMOpts(codemirrorOptions) {
    var filenameFromQuestion = function filenameFromQuestion(question) {
      return question.answerType() + '-quiz-question.' + question.editorLanguage();
    };
    return {
      create: function create(ctrl) {
        ctrl.codemirrorOptions = codemirrorOptions;
        var optsCache = {};
        ctrl.createCMOpts = mappedMemo(filenameFromQuestion, function (filename, question) {
          if (!optsCache[filename]) {
            var opts = codemirrorOptions.createOpts(filename);
            optsCache[filename] = opts;
            if (question.answerType() === 'scode') {
              angular.extend(opts, {
                lineNumbers: false,
                foldGutter: false,
                gutters: [],
                styleActiveLine: false,
                onLoad: function onLoad(cm) {
                  cm.on('beforeChange', function (cm, changeObj) {
                    if (changeObj.text.length > 1) {
                      changeObj.text = [changeObj.text.join('')];
                    }
                  });
                }
              });
              opts.extraKeys.Tab = false;
              opts.extraKeys['Shift-Tab'] = false;
            }
          }
          return optsCache[filename];
        });
      }
    };
  }]).factory('quizQuestionMatcherNotes', function () {
    return {
      // Method to generate points based on matchers selected and answer given
      generate: function generate(answer, question) {
        var data = {
          highlightMatches: [],
          results: [],
          points: answer ? question.weight : 0
        };
        var matchers = question.matchers || [];
        // Note: we're checking to make sure this is a string because sometimes it isn't
        // This shouldn't happen, but I think it's due to a disconnect between old DB values that aren't
        // syncing up at some point (say, a challenge was changed later and something isn't matching)
        // It's hard to find, so this is a simple check to at least prevent errors.
        if (matchers.length && angular.isString(answer)) {
          var correctMatchers = 0;
          matchers.forEach(function (m) {
            var match = m.value;
            var matched = void 0;
            if (m.type === 'exact') {
              if (m.case) {
                matched = answer === match;
              } else {
                matched = answer.toLowerCase() === match.toLowerCase();
              }
            } else if (m.type === 'includes') {
              if (m.case) {
                matched = answer.indexOf(match) > -1;
              } else {
                matched = answer.toLowerCase().indexOf(match.toLowerCase()) > -1;
              }
            } else {
              match = new RegExp(match, (m.case ? '' : 'i') + (m.multi ? 'm' : ''));
              matched = match.test(answer);
            }
            var correct = m.negate ? !matched : matched;
            if (correct) correctMatchers++;
            var mData = {
              match: match,
              matched: matched,
              correct: correct,
              keyword: match,
              className: correct ? 'correct' : 'incorrect',
              caseSensitive: !!m.case
            };
            data.results.push(mData);
            if (m.type !== 'exact') {
              data.highlightMatches.push(mData);
            }
          });
          if (question.allOrNothing) {
            data.points = correctMatchers === matchers.length ? question.weight : 0;
          } else {
            data.points = Math.round(100 * question.weight * correctMatchers / matchers.length) / 100;
          }
        }
        return data;
      }
    };
  }).component('quizChallengeQuestionMatchers', {
    bindings: {
      question: '<',
      results: '<'
    },
    template: quizChallengeQuestionMatcherTemplate,
    controller: /* @ngInject */['QuizQuestion', function controller(QuizQuestion) {
      var ctrl = this;

      ctrl.getMatcherType = function (item, idx) {
        // returns a shortened form
        var result = ctrl.results && ctrl.results[idx];
        return QuizQuestion.getMatcherLabel(item, result);
      };

      ctrl.matcherClass = function (m, idx) {
        var matcherClass = m.type;
        var result = ctrl.results && ctrl.results[idx];
        if (result) {
          matcherClass += ' ' + (result.correct ? 'correct' : 'incorrect');
        }
        return matcherClass;
      };
    }]
  });

  var quizChallengeTemplate = "<challenge-intro-dialog show-intro=\"$ctrl.showIntro\" assessment-process=\"assessments.process\" challenge=\"$ctrl.challenge\" begin-challenge=\"$ctrl.beginChallenge()\"> <div compile-template=\"$ctrl.introDialogTemplate\"></div> </challenge-intro-dialog> <ui-layout class=\"quiz-challenge-wrapper\" ng-class=\"{ 'no-solution': !$ctrl.process.solution, 'dark-bg': $root.$ideDark }\" options=\"{ flow : 'column' }\" remember-ui=\"'quiz-layout-vert-splitter'\"> <div class=\"quiz-challenge\" ui-layout-container min-size=\"350px\" ng-class=\"{ ready: $ctrl.process }\"> <form name=\"quizChallengeForm\" ng-submit=\"$ctrl.saveSolution(true)\"> <div class=\"help-block help-block-tip restricted-mode\" ng-if=\"assessments.process.restricted\"> <strong>Read-Only Mode:</strong> You can review your selections but cannot make changes. </div> <quiz-challenge-questions challenge=\"$ctrl.challenge\" on-answer-change=\"$ctrl.saveSolution()\" mode=\"{{assessments.process.restricted ? 'review' : 'solve'}}\" candidate-review=\"true\" highlight-errors=\"$ctrl.highlightErrors\" solution=\"$ctrl.process.solution\" show-incomplete-warning=\"$ctrl.highlightErrors && $ctrl.process.incomplete()\" after-quiz-action-label=\"$ctrl.nextLabel()\" on-after-quiz-action=\"$ctrl.next()\" allow-dark-mode=\"true\" readonly=\"$ctrl.process.readOnly\"></quiz-challenge-questions> </form> </div> <div class=\"live-interview\" ng-if=\"assessments.process.liveInterviewActive\" ui-layout-container size=\"400px\" min-size=\"350px\"> <div class=\"user-list\"> <interact-user-list users-list=\"assessments.process.liveInterview.usersList\" type=\"candidate\"></interact-user-list> </div> </div> </ui-layout> ";

  var introDialogTemplate$1 = "<div class=\"modal-content\"> <div class=\"modal-header\"> <h4 class=\"modal-title\"> Challenge #{{ ::$ctrl.challenge.position }}: Q&amp;A </h4> </div> <div class=\"modal-body\"> <div class=\"summary\" ng-if=\"$ctrl.challenge.summary\" markdown=\"$ctrl.challenge.summary\"></div> <div class=\"instructions\" markdown=\"$ctrl.challenge.instructions\" ng-if=\"$ctrl.challenge.isQuestionTimed()\"></div> <div class=\"challenge-timer-alert\" ng-if=\"!$ctrl.challenge.isUntimed()\"> <div class=\"alert alert-info icon-stopwatch\" ng-if=\"$ctrl.challenge.isTimed()\"> <p> This challenge has a time limit of {{ $ctrl.challenge.timeLimitMin |\n          durationFormat:'minutes' }}. Once the time runs out, you will not be able to modify your answers. </p> </div> <div class=\"alert alert-info icon-quiz-timed-per-question\" ng-if=\"$ctrl.challenge.isQuestionTimed()\"> <p> Each question on this challenge has a time limit. Please answer them as quickly as you can. Once the time runs out or you move on, you will not be able to change your answer. </p> <p ng-if=\"$ctrl.challenge.isBonusTimed()\"> <strong>For this challenge you will receive a higher score the faster you answer each question!</strong> </p> </div> <p class=\"note\"> You can hide the {{$ctrl.challenge.isQuestionTimed() ? 'question' :\n        'challenge'}} timer by clicking on it. It will reappear if time begins to run out. </p> </div> <p class=\"note\" ng-if=\"$ctrl.challenge.isUntimed()\"> There is no time limit to answer the following questions, but how fast you complete it is still tracked. Please make sure you have time to focus on the questions. </p> <p class=\"note\" ng-show=\"assessments.process.timer.isCountDown\"> You have <timer-widget timer-only=\"true\" timer=\"assessments.process.timer\"></timer-widget> remaining for this assessment. </p> </div> <div class=\"modal-footer\"> <button class=\"btn-primary\" ng-click=\"$ctrl.beginChallenge()\" ng-disabled=\"$ctrl.beginning\"> Start Q&amp;A Challenge <span class=\"shortcut-hint\">{{$ctrl.process.beginChallengeKeyStroke}}</span> </button> </div> </div> ";

  var quizChallengeRouteModule = angular.module('Q.routes.assessments.challenges.quiz-challenge', [uiRouterStateModule, firebaseServiceModule, quizChallengeProcessServiceModule, quizChallengeQuestionsComponentModule, interactUserListComponentModule, dialogComponentModule, challengeIntroDialogComponentModule, bufferedSaveHelperModule]).config(['$stateProvider', function ($stateProvider) {
    $stateProvider.state('assessments.challenges.quizChallenge', {
      data: {
        bodyClass: 'assessments challenges quiz-challenge',
        label: 'Assessment Challenge IDE'
      },
      resolve: {
        config: /* @ngInject */['configService', function config(configService) {
          return configService.load().catch(function () {
            return null;
          });
        }],
        challengeProcess: /* @ngInject */['challenge', 'config', 'process', 'quizChallengeProcessService', function challengeProcess(challenge, config, process, quizChallengeProcessService) {
          return challenge ? quizChallengeProcessService.create(challenge, process) : null;
        }]
      },
      onExit: /* @ngInject */['challengeProcess', function onExit(challengeProcess) {
        return challengeProcess && challengeProcess.onExit();
      }],
      views: {
        '@assessments': {
          template: quizChallengeTemplate,
          controller: 'QuizChallengeCtrl as $ctrl'
        }
      }
    });
  }]).controller('QuizChallengeCtrl', ['$scope', 'process', 'challenge', 'challengeProcess', 'dialogService', '$log', '$timeout', 'bufferedSaveHelper', '$state', function QuizChallengeCtrl($scope, process, challenge, challengeProcess, dialogService, $log, $timeout, bufferedSaveHelper, $state) {
    var ctrl = this;

    var challengeItem = process.assessmentResult.getChallengeItem(challenge);

    if (!challengeItem) {
      $log.error("Invariant violation: challengeItem was expected to be defined but wasn't");
      return;
    }

    ctrl.challenge = angular.extend(challenge, {
      position: challengeItem.position
    });
    ctrl.process = challengeProcess;
    ctrl.nextLabel = process.nextLabel;
    ctrl.next = process.next;
    ctrl.previous = process.previous;

    ctrl.highlightErrors = !process.restricted && challengeProcess.solution && challengeProcess.solution.attempted();

    if (!challengeProcess.solution) {
      ctrl.showIntro = true;
      ctrl.introDialogTemplate = introDialogTemplate$1;
      ctrl.beginChallenge = function () {
        if (ctrl.beginning) return;
        ctrl.beginning = true;
        challengeProcess.beginChallenge().then(function () {
          process.addSolution(challengeProcess.originalSolution);
          ctrl.showIntro = false;
        }).finally(function () {
          $timeout(function () {
            // delayed to let dialog close
            ctrl.beginning = false;
          }, 100);
        });
      };
    }

    var saveSolution = bufferedSaveHelper(challengeProcess.autosave);
    ctrl.saveSolution = saveSolution;

    challengeProcess.resaveAfterFail = function () {
      return saveSolution('resaveAfterFail');
    };

    var requireCompleteMultipleChoice = function requireCompleteMultipleChoice() {
      return challengeProcess.solution && // we have a solution
      challengeProcess.solution.hasEmptyMultipleChoice() && // we have any empty multiple choice
      !process.isSample() && // we are not in practice or demo mode
      process.assessmentResult.isStarted() && // the assessment result is in the started state
      !process.restricted && // we're not in a restricted editing mode
      !process.liveInterviewActive; // we're not in a live interview
    };

    $scope.$watch(function () {
      return process.restricted;
    }, function (newVal, oldVal) {
      if (oldVal !== newVal) {
        $state.reload();
      }
    });

    $scope.$on('$stateChangeStart', function (event, toState, toParams) {
      if (toState.name !== 'assessments.welcome' && (saveSolution.isSaving() || requireCompleteMultipleChoice())) {
        event.preventDefault();

        var showNotice = function showNotice() {
          dialogService.alert('\t<p>You need to at least answer all multiple choice questions before continuing.</p>\n\t', 'Be Sure to Answer Completely');
        };

        if (saveSolution.isSaving()) {
          var overlay = dialogService.busyOverlay('', 'Please wait while we save your answers');
          saveSolution.afterSave.then(function () {
            overlay.close();
            if (requireCompleteMultipleChoice()) {
              showNotice();
            } else {
              $state.go(toState, toParams);
            }
          });
        } else {
          $timeout(showNotice());
        }
      }
    });

    challengeProcess.onEnter($scope);
  }]);

  var advancedCodeChallengeProcessServiceModule = angular.module('Q.routes.assessments.challenges.code-challenge.advanced-code-challenge-process-service', [codeFileModelModule, solutionsServiceModule, firebaseServiceModule, appAlertComponentModule, challengeProcessHelperModule]).factory('advancedCodeChallengeProcessService', ['solutionsService', 'Solution', 'AdvancedCodeSolutionFile', '$q', '$location', 'firebase', 'appAlert', 'challengeProcessHelper', function (solutionsService, Solution, AdvancedCodeSolutionFile, $q, $location, firebase, appAlert, challengeProcessHelper) {
    return {
      create: create
    };

    //// functions:

    function create(challenge, assessmentResultProcess) {
      var assessmentResult = assessmentResultProcess.assessmentResult;

      var process = {
        challenge: challenge,
        beginChallenge: beginChallenge,
        cleanupAfterSave: cleanupAfterSave,
        setResponse: setResponse,
        logTest: logTest,
        autoRunTests: false,
        disableWebPreview: !!$location.search().disablewebpreview
      };

      return challengeProcessHelper(process, assessmentResultProcess);

      //// functions:

      function beginChallenge() {
        return assessmentResultProcess.startAssessmentResult().then(function () {
          return solutionsService.create(challenge, assessmentResult);
        }).then(process.initSolution).catch(appAlert.modelError);
      }

      function cleanupAfterSave(force) {
        // due to the way files are stored, we have to clean them up, because the IDs can get changed
        if (process.solution && process.solution.files && process.originalSolution.files) {
          var keys = ['id', 'access', 'deletable', 'directory', 'challengeFileId', 'includeOnTest', 'includeOnSubmit', 'initialLayout', 'testGroups'];
          if (force) {
            keys.push('contents');
          }
          process.solution.files.forEach(function (solutionFile) {
            var originalFile = process.originalSolution.files.find(function (a) {
              return a.path === solutionFile.path;
            });
            if (originalFile) {
              keys.forEach(function (key) {
                solutionFile[key] = originalFile[key];
              });
            }
          });
          if (process.refreshOnFilesChanged) {
            process.refreshOnFilesChanged(force);
          }
        }
      }

      function setResponse(response) {
        firebase.set(process.firebaseRef + '/runData/response', response);
      }

      function logTest(response) {
        appAlert.clear();
        setResponse(response);
        if (assessmentResultProcess.restricted) {
          return $q.when(response).then(process._saveSuccess);
        }

        // save results back to the server
        var run = {
          // TODO: add in any other changes?
          response: response
        };
        return process.originalSolution.logTest(run).then(function () {
          process.fd.syncUnchanged();
          process.solution.updateScore();
          assessmentResultProcess.refreshStageStatuses();
          return response;
        }).then(process._saveSuccess, process._saveFail).catch(appAlert.modelError.with({
          prefix: 'There was an error saving your results:'
        }));
      }
    }
  }]);

  var advancedCodeChallengeTemplate = "<challenge-intro-dialog show-intro=\"$ctrl.showIntro\" assessment-process=\"assessments.process\" challenge=\"$ctrl.challenge\" begin-challenge=\"$ctrl.beginChallenge()\"> <div compile-template=\"$ctrl.introDialogTemplate\"></div> </challenge-intro-dialog> <div class=\"advanced-code-challenge-wrapper\" ng-class=\"{ 'dark-bg': $root.$ideDark, 'no-solution': !$ctrl.process.solution.id, ready: $ctrl.process, 'using-attach': $ctrl.process.usingAttach }\" link-multi-file-challenges=\"$ctrl\"> <div class=\"advanced-code-challenge-toolbar\"> <span class=\"restricted-mode\" ng-if=\"assessments.process.restricted\" tooltip=\"{{assessments.process.readOnly ? 'This challenge can not be edited' : 'Changes made to this challenge will not be saved'}}\"> <span class=\"icon-{{assessments.process.readOnly ? 'readonly' : 'eye'}}\"></span> <span>{{assessments.process.readOnly ? 'Read-Only' : 'Restricted'}} Mode</span> <tooltip-help-icon></tooltip-help-icon> </span> <button class=\"btn-default btn-sm {{$ctrl.process.usingAttach?'internal':'external'}}-ide-button\" ng-if=\"!assessments.process.readOnly && !$ctrl.expired && $ctrl.challenge.externalIdeEnabled()\" ng-click=\"$ctrl.toggleExternalIde()\" tooltip=\"{{$ctrl.attachTooltip()}}\"> Use {{$ctrl.process.usingAttach?'Web':'Your'}} IDE </button> <button class=\"btn-default btn-sm download\" ng-if=\"!assessments.process.readOnly && !$ctrl.expired && $ctrl.challenge.enableFileDownloads\" ng-click=\"$ctrl.process.solution.download(assessments.account.user)\" tooltip=\"Download all solution files as a ZIP\"> Download Files </button> <button class=\"btn-default btn-sm reset-all-button\" ng-if=\"!assessments.process.restricted && !$ctrl.expired\" on-confirm=\"$ctrl.resetAll()\" on-confirm-message=\"Reset the entire challenge? (Reverted files will be moved to deleted files.)\" tooltip=\"Reset the files in this challenge\" ng-disabled=\"$ctrl.process.usingAttach\"> Reset Everything… </button> <multi-file-toolbar actions=\"$ctrl.actions\" on-click=\"$ctrl.clickFile(action)\" ng-if=\"!$ctrl.expired\"></multi-file-toolbar> <interact-editor-button interact-service=\"$ctrl.interact\" expired=\"$ctrl.expirationReason || $ctrl.expired\"></interact-editor-button> <span class=\"left-right-sep\"></span> <div class=\"challenge-timer\" ng-if=\"!assessments.process.restricted && $ctrl.process.timerStartedAt\"> <horizontal-countdown-timer class=\"prominent\" started-at=\"$ctrl.process.timerStartedAt\" timeout-seconds=\"$ctrl.process.timerTimeoutSeconds\" on-expired=\"$ctrl.setExpired(expired)\" expired-message=\"{{ $ctrl.process.challenge.candidateNotesScored ? 'Time\\'s up!' : 'Time\\'s up! (You can still edit Your Notes)' }}\"></horizontal-countdown-timer> </div> <button class=\"btn-{{ $ctrl.primaryIsPreview() ? 'primary' : 'default' }} btn-sm web-preview-button\" ng-click=\"$ctrl.showWebPreview()\" tooltip=\"Preview your solution\" ng-if=\"$ctrl.challenge.webPreviews()\"> Web Preview </button> <span class=\"step-sep\" ng-if=\"$ctrl.challenge.webPreviews()\"></span> <button class=\"btn-{{ $ctrl.primaryIsTests() ? 'primary' : 'default' }} btn-sm run-tests-button\" ng-if=\"$ctrl.challenge.candidateTestsEnabled && !$ctrl.challenge.disableIdeFeatures\" ng-click=\"$ctrl.runTests()\" ng-disabled=\"$ctrl.expired || $ctrl.running\" tooltip=\"Run your code against your tests\" tooltip-shortcut-hint=\"{{$root.KeyCodes.SYMBOLS.COMMAND_KEY}} + Enter\"> Run Tests </button> <div class=\"auto-sync qualified-checkbox qualified-checkbox-switch-sm qualified-checkbox-switch qualified-checkbox-switch-onoff\" ng-if=\"$ctrl.challenge.candidateTestsEnabled && $ctrl.process.usingAttach && !$ctrl.expired\" tooltip=\"Automatically run tests on every save\"> <input type=\"checkbox\" ng-model=\"$ctrl.process.autoRunTests\" ng-change=\"$ctrl.process.autoRunTests && $ctrl.runTests()\" id=\"attach-auto-tests\"> <label for=\"attach-auto-tests\"> Auto </label> </div> <span class=\"step-sep\" ng-if=\"$ctrl.challenge.candidateTestsEnabled\"></span> <button ng-if=\"assessments.process.restricted\" class=\"btn-{{ $ctrl.primaryIsSubmit() ? 'primary' : 'default' }} btn-sm submit-button\" ng-click=\"$ctrl.next()\" ng-disabled=\"$ctrl.running\" tooltip=\"{{$ctrl.submitTooltip}}\" ng-bind=\"assessments.process.nextLabel()\"></button> <div class=\"btn-group\" ng-if=\"!assessments.process.restricted\"> <button class=\"btn-{{ $ctrl.primaryIsSubmit() && !$ctrl.showSubmitNext() ? 'primary' : 'default' }} btn-sm submit-button\" ng-click=\"$ctrl.submitSolution()\" ng-disabled=\"$ctrl.running\" tooltip=\"{{$ctrl.submitTooltip}}\" ng-bind=\"$ctrl.submitLabel\"></button> <button class=\"btn-primary btn-primary-alt btn-sm submit-button-options\" ng-if=\"$ctrl.challenge.submissionFeedback && $ctrl.solution.failedTestNames.length\" ng-click=\"$ctrl.showSubmissionFeedback()\" tooltip=\"Review the last submission results\"></button> <button class=\"btn-primary btn-sm submit-button-next\" ng-if=\"$ctrl.showSubmitNext()\" ng-click=\"$ctrl.next()\" tooltip=\"{{assessments.process.nextLabel()}}\"></button> </div> </div> <ui-layout options=\"{ flow: 'column' }\" remember-ui=\"$ctrl.challenge.canCloseTabs() ? 'advanced-code-challenge-tree-splitter' : false\" ng-if=\"$ctrl.solution\"> <div class=\"file-tree-sidebar\" ui-layout-container size=\"240px\" ng-if=\"$ctrl.challenge.canCloseTabs()\"> <file-tree class=\"open-files\" files=\"$ctrl.openFiles\" flat=\"true\" on-file-click=\"$ctrl.clickFile(file)\" header-label=\"Open Files\" hide-file-tree-menu=\"true\" collapse-root=\"true\" files-closeable=\"true\"></file-tree> <file-tree class=\"challenge-config\" files=\"$ctrl.challengeConfig\" open-files=\"$ctrl.openFiles\" on-file-click=\"$ctrl.clickFile(file)\" header-label=\"Challenge Info\" hide-file-tree-menu=\"true\" disable-sort=\"true\"></file-tree> <file-tree files=\"$ctrl.files\" open-files=\"$ctrl.openFiles\" header-label=\"Project Files\" on-file-add-click=\"$ctrl.addFile()\" on-file-click=\"$ctrl.clickFile(file)\" file-menu-template=\"$ctrl.templates.fileMenu\" file-menu-class=\"$ctrl.fileMenuClass(file)\" expired=\"$ctrl.expired\" read-only=\"assessments.process.readOnly\" disabled=\"$ctrl.submitting\"></file-tree> <file-tree class=\"deleted-files-tree\" ng-if=\"$ctrl.deletedFiles.length && !$ctrl.process.usingAttach && !$ctrl.expired\" collapse-root=\"true\" files=\"$ctrl.deletedFiles\" open-files=\"$ctrl.openFiles\" header-label=\"Deleted Files\" header-tooltip=\"Temporary bin for files you've deleted. These are only kept around until you reload the page.\" on-file-click=\"$ctrl.clickFile(file)\" file-menu-template=\"$ctrl.templates.deletedMenu\" read-only=\"assessments.process.readOnly\" disabled=\"$ctrl.submitting\"></file-tree> <file-tree class=\"challenge-editor-tools\" ng-if=\"!$ctrl.challenge.disableIdeFeatures\" files=\"$ctrl.challengeEditorTools\" open-files=\"$ctrl.openFiles\" on-file-click=\"$ctrl.clickFile(file)\" header-label=\"Web IDE Tools\" hide-file-tree-menu=\"true\" disable-sort=\"true\"></file-tree> </div> <div ui-layout-container min-size=\"100px\" ng-if=\"true\"> <multi-file-layout open-files=\"$ctrl.openFiles\" workspace=\"$ctrl.workspace\" closeable=\"$ctrl.closeableFiles\" on-run=\"$ctrl.runTests()\" on-test-run=\"$ctrl.runTests()\" on-file-change=\"$ctrl.onFileChange({file: file, userInitiated: userInitiated })\" on-tab-focused=\"$ctrl.onTabFocused({ file: file })\" on-file-copy=\"$ctrl.process.onCopy(copied)\" on-file-paste=\"$ctrl.process.onPaste(file, pasted)\" on-tab-moved=\"$ctrl.onTabMoved(file)\" parent-controller=\"$ctrl\" get-toolbar-template=\"$ctrl.getToolbarTemplate(file)\" readonly=\"$ctrl.process.readOnly || $ctrl.submitting\" expired=\"$ctrl.expired\" disabled=\"$ctrl.process.usingAttach\" cursor=\"$ctrl.process.cursor\" disable-web-preview=\"$ctrl.process.disableWebPreview\" remember-ui></multi-file-layout> </div> </ui-layout> </div> ";

  var introDialogTemplate$2 = "<div class=\"modal-content\"> <div class=\"modal-header\"> <h4 class=\"modal-title\"> Challenge #{{ ::$ctrl.challenge.position }}: Project Coding Task </h4> <div class=\"theme\"> <span tooltip=\"(You can change this and more under IDE Settings)\"> Editor Theme: </span> <div class=\"btn-group\"> <button class=\"btn-default light\" ng-model=\"$ctrl.codemirrorOpts.sharedOpts.theme\" btn-radio=\"'light'\" tooltip=\"Light Theme\" tooltip-delay=\"0\"></button> <button class=\"btn-default dark\" ng-model=\"$ctrl.codemirrorOpts.sharedOpts.theme\" btn-radio=\"'dark'\" tooltip=\"Dark Theme\" tooltip-delay=\"0\"></button> </div> </div> </div> <div class=\"modal-body\"> <div class=\"summary\" ng-if=\"$ctrl.challenge.summary\" markdown=\"$ctrl.challenge.summary\"></div> <p class=\"note\"> For this challenge you will be asked to write code. Please make sure to craft a quality solution. </p> <div class=\"challenge-timer-alert\" ng-if=\"$ctrl.challenge.isTimed()\"> <div class=\"alert alert-info icon-stopwatch\"> <p> This challenge has a time limit of {{ $ctrl.challenge.timeLimitMin |\n          durationFormat:'minutes' }}. Once the time runs out, you will not be able to modify your solution. </p> </div> <p class=\"note\"> You can hide the challenge timer by clicking on it. It will reappear if time begins to run out. </p> </div> <p class=\"note\" ng-if=\"assessments.process.timer.isCountDown\"> You have <timer-widget timer-only=\"true\" timer=\"assessments.process.timer\"></timer-widget> remaining for this assessment. </p> <p class=\"note\" ng-if=\"$ctrl.challenge.externalIdeEnabled()\"> You can use your own IDE for solving this challenge. It requires installing a small Node application for synchronizing file changes between your file system and our web interface. <a href=\"{{::$root.appConfig.docsUrl}}/for-candidates/advanced-challenge-ide/external-ide\" target=\"_blank\" rel=\"noopener\"> Learn more here. </a> </p> <challenge-code-of-conduct code-of-conduct=\"$ctrl.codeOfConduct\" challenge-id=\"$ctrl.challenge.id\" beginning=\"$ctrl.beginning\" checkbox-id-prefix=\"advanced-code-conduct\"></challenge-code-of-conduct> </div> <div class=\"modal-footer\"> <button class=\"btn-primary\" ng-click=\"$ctrl.beginChallenge()\" ng-disabled=\"$ctrl.unableToBeginChallenge()\" title=\"Start {{ $ctrl.challenge.externalIdeEnabled() ? 'Web IDE' : 'Code Challenge' }} {{ $ctrl.process.beginChallengeKeyStroke }}\"> Start {{ $ctrl.challenge.externalIdeEnabled() ? 'Web IDE' : 'Code\n      Challenge' }} <span class=\"shortcut-hint\">{{$ctrl.process.beginChallengeKeyStroke}}</span> </button> <button class=\"btn-primary btn-primary-alt\" ng-click=\"$ctrl.beginChallenge(true)\" ng-disabled=\"$ctrl.unableToBeginChallenge()\" ng-if=\"$ctrl.challenge.externalIdeEnabled()\" title=\"Start in Your IDE\"> Start in Your IDE </button> </div> </div> ";

  var fileTreeTemplate = "<div class=\"file-tree-header {{ $ctrl.rootCollapsed ? 'collapsed' : 'expanded' }}\" ng-if=\"$ctrl.headerLabel\"> <span class=\"expander\" ng-if=\"$ctrl.collapseRoot\" ng-click=\"$ctrl.rootCollapsed = !$ctrl.rootCollapsed\"></span> <span class=\"file-tree-label\" ng-class=\"{clickable: $ctrl.collapseRoot}\" ng-click=\"$ctrl.collapseRoot && ($ctrl.rootCollapsed = !$ctrl.rootCollapsed)\" tooltip=\"{{ $ctrl.headerTooltip }}\"> {{ $ctrl.headerLabel + ($ctrl.showChangedOnly ? ' (Changed)' : '')}} <tooltip-help-icon ng-if=\"$ctrl.headerTooltip\"></tooltip-help-icon> </span> <a ng-repeat=\"action in $ctrl.headerActions\" ng-hide=\"action.hidden\" ng-click=\"!($ctrl.disabled || action.disabled) && action.onClick()\" ng-disabled=\"$ctrl.disabled || action.disabled\" tooltip=\"{{action.tooltip}}\" class=\"file-tree-action file-tree-action-{{action.id}}\"></a> <a class=\"file-tree-add\" ng-if=\"$ctrl.showAddButton && !$ctrl.expired && !$ctrl.readOnly\" ng-disabled=\"$ctrl.disabled\" ng-click=\"$ctrl.onFileAddClick({ $event: $event })\" tooltip=\"Add a File or Directory\"></a> <div class=\"dropdown\" dropdown dropdown-append-to-body ng-if=\"!$ctrl.hideFileTreeMenu\"> <a class=\"file-tree-config dropdown-toggle\" dropdown-toggle tooltip=\"File View Controls\"></a> <ul class=\"dropdown-menu\" dropdown-menu> <li> <a class=\"directories-on-top\" ng-class=\"{selected: $ctrl.prefs.directoriesOnTop}\" ng-click=\"$ctrl.setDirOnTop(!$ctrl.prefs.directoriesOnTop)\" tooltip=\"Group directories at the top, rather than mixing them in\" tooltip-position=\"right\"> Directories on Top </a> </li> <li> <a class=\"compact-directories\" ng-class=\"{selected: $ctrl.prefs.compactEmptyDirectories}\" ng-click=\"$ctrl.setCompactDir(!$ctrl.prefs.compactEmptyDirectories)\" tooltip=\"Collects empty directories to compact the view\" tooltip-position=\"right\"> Compact Empty Directories </a> </li> <li role=\"separator\" class=\"divider\"></li> <li> <a class=\"collapse-all\" ng-click=\"$ctrl.collapseAll()\"> Collapse All </a> </li> <li> <a class=\"expand-all\" ng-click=\"$ctrl.expandAll()\"> Expand All </a> </li> <li ng-if=\"$ctrl.changedFiles\" role=\"separator\" class=\"divider\"></li> <li ng-if=\"$ctrl.changedFiles\"> <a class=\"toggle-modified\" ng-click=\"$ctrl.toggleChanged()\"> {{ $ctrl.showChangedOnly ? 'Show All Files' : 'Show Changed Files\n          Only' }} </a> </li> </ul> </div> </div> <div class=\"file-tree\" ng-class=\"{ 'review-mode': $ctrl.reviewMode }\" ng-hide=\"$ctrl.rootCollapsed\"> <div ng-repeat=\"fileRow in $ctrl.fileRows track by fileRow.key\" class=\"file-row\" ng-hide=\"fileRow.hidden\" ng-if=\"!fileRow.directory || !$ctrl.showChangedOnly\" ng-class=\"{ directory: fileRow.directory && !$ctrl.flat, collapsed: fileRow.collapsed, expanded: !fileRow.collapsed, disabled: fileRow.file.disabled, open: $ctrl.isOpen(fileRow.file), selected: $ctrl.selectedFile && fileRow.file === $ctrl.selectedFile, changed: $ctrl.isChanged(fileRow.file), separate: fileRow.file.separateFileRow }\" ng-contextmenu=\"$ctrl.fileMenuClick($event, fileRow, true)\" ng-style=\"{ 'padding-left': fileRow.leftPadding }\"> <span class=\"expander\" ng-click=\"fileRow.setCollapsed(!fileRow.collapsed)\"></span> <span class=\"file-row-content\" ng-click=\"$ctrl.handleClick($event, fileRow)\" file-tree-draggable-file-row=\"fileRow\"> <file-icon file=\"fileRow.file || fileRow\" expanded=\"!fileRow.collapsed\"></file-icon> <span class=\"file-row-label\" tooltip=\"{{fileRow.file.tooltip || fileRow.file.path || fileRow.label}}\" tooltip-position=\"right\" ng-bind=\"fileRow.label\"></span> </span> <button ng-hide=\"$ctrl.expired || $ctrl.readOnly || $ctrl.disabled\" class=\"file-row-menu-btn {{ $ctrl._fileMenuClass(fileRow) }}\" ng-click=\"$ctrl.fileMenuClick($event, fileRow)\" tooltip=\"File Settings & Actions\" tooltip-position=\"right\"></button> <button class=\"file-row-close-btn\" ng-if=\"$ctrl.filesCloseable && fileRow.file\" ng-click=\"$ctrl.close(fileRow.file)\" tooltip=\"Close File\" tooltip-position=\"right\"></button> </div> </div> ";

  var ngContextmenuDirectiveModule = angular.module('Q.components.ng-contextmenu', [])

  /**
   * @ngdoc directive
   * @name ngContextmenu
   *
   * @description
   * The ngContextmenu directive allows you to specify custom behavior when the user tries to open the context menu.
   *
   * @element ANY
   * @priority 0
   * @param {expression} ngContextmenu {@link guide/expression Expression} to evaluate upon
   * click. ({@link guide/expression#-event- Event object is available as `$event`})
   */
  .directive('ngContextmenu', ['$parse', function ($parse) {
    return function (scope, element, attrs) {
      element.bind('contextmenu', function (event) {
        scope.$apply(function () {
          stopEvent(event);
          $parse(attrs.ngContextmenu)(scope, { $event: event });
        });
      });
    };
  }]);

  // import { fooServiceModule } from '../../services/foo/foo-service';

  var MAX_DEPTH = 8;
  var NESTED_OFFSET = 14;

  var FileRow = function () {
    function FileRow(_ref) {
      var tree = _ref.tree,
          file = _ref.file,
          path = _ref.path,
          filename = _ref.filename,
          _ref$directory = _ref.directory,
          directory = _ref$directory === undefined ? false : _ref$directory,
          _ref$parent = _ref.parent,
          parent = _ref$parent === undefined ? null : _ref$parent,
          _ref$collapsed = _ref.collapsed,
          collapsed = _ref$collapsed === undefined ? directory : _ref$collapsed;
      classCallCheck(this, FileRow);

      defineValue(this, 'tree', tree);
      defineValue(this, 'parent', parent);
      this.file = file;
      this.path = path || file && file.label;
      this.filename = filename || file && file.label;
      // we use `\n` for sorting because it comes before all other allowed characters
      this.dirInlineSortPath = (parent && parent.dirInlineSortPath || '') + '\n' + filename;
      this.dirTopSortPath = (parent && parent.dirTopSortPath || '') + (directory ? '\n0' : '\n1') + filename;
      this.directory = directory;

      this.key = (this.file && this.file.id || '') + ':' + (this.file && this.file.reference ? '[ref]/' : '') + this.path;

      if (directory) {
        this.collapsed = collapsed;
        this.children = [];
      }

      if (parent) {
        parent.children.push(this);
      }
    }

    createClass(FileRow, [{
      key: '_isHidden',
      value: function _isHidden() {
        return this.parent && (this.parent.collapsed && !this.parent.compacted || this.parent._isHidden());
      }
    }, {
      key: 'setDepth',
      value: function setDepth() {
        this.depth = (this.compacted ? 0 : 1) + (this.parent ? this.parent.depth : 0);
        this.leftPadding = (Math.min(this.depth, MAX_DEPTH) - 1) * NESTED_OFFSET + 'px';
      }
    }, {
      key: 'setCollapsed',
      value: function setCollapsed(val) {
        if (this.directory) {
          this.collapsed = val;
          if (this.file) {
            this.file.collapsed = val;
          }
        }
      }
    }, {
      key: 'hidden',
      get: function get$$1() {
        return this.tree.rootCollapsed || this.compacted || !!this._isHidden();
      }
    }, {
      key: 'label',
      get: function get$$1() {
        if (this.file && this.file.label) {
          return this.file.label; // useful for non-file components
        }
        return (this.parent && this.parent.compacted ? this.parent.label + '/' : '') + this.filename;
      }
    }]);
    return FileRow;
  }();

  var fileTreeComponentModule = angular.module('Q.components.file-tree', [accountServiceModule, keycodesModule, bubbleDialogComponentModule, fileIconComponentModule, ngContextmenuDirectiveModule]).component('fileTree', {
    bindings: {
      // if true, the root (header) is collapsed (and collapseable), which is useful for things like
      // deleted files
      collapseRoot: '<',

      // header settings
      headerLabel: '@',
      headerTooltip: '@',
      headerActions: '<',
      hideFileTreeMenu: '<',
      onFileAddClick: '&',

      // tree config
      files: '<',
      selectedFile: '<',
      onFileClick: '&',
      fileMenuClass: '&',
      fileMenuTemplate: '<',
      disableSort: '<',
      autoExpandAll: '<',
      reviewMode: '<',
      expired: '<',
      disabled: '<',
      readOnly: '<',
      // if true, ignore file paths
      flat: '<',
      // if true, shows an X that removes files from the files array
      filesCloseable: '<',

      // highlights files that are open
      openFiles: '<',
      // highlights files that were changed
      changedFiles: '<'
    },
    require: {
      multiFileChallengesCtrl: '^?linkMultiFileChallenges'
    },
    controller: 'FileTreeCtrl',
    template: fileTreeTemplate
  }).controller('FileTreeCtrl', ['$scope', '$attrs', 'accountService', 'KeyCodes', 'bubbleDialogService', '$rootScope', '$log', function FileTreeCtrl($scope, $attrs, accountService, KeyCodes, bubbleDialogService, $rootScope, $log) {
    var ctrl = this;
    ctrl.fileRows = [];

    ctrl.$onInit = function () {
      loadPrefs();
      ctrl.rootCollapsed = ctrl.collapseRoot;
      ctrl.treeCloseable = !!$attrs.onCloseTree;
      updateFilePaths();
      if ($attrs.onFileAddClick) {
        ctrl.showAddButton = true;
      }

      // watch the file paths, since we only really care about file paths
      $scope.$watch(function () {
        return (ctrl.files || []).map(function (f) {
          return f.id + '!' + f.hidden + '!' + f.path;
        }).join('\n');
      }, updateFilePaths);

      if (ctrl.multiFileChallengesCtrl) {
        ctrl.multiFileChallengesCtrl.linkFileTree(ctrl);
        $scope.$on('$destroy', function () {
          ctrl.multiFileChallengesCtrl.unlinkFileTree(ctrl);
        });
      }
    };

    ctrl.refresh = updateFilePaths;

    ctrl.handleClick = function ($event, fileRow) {
      if (fileRow.directory) {
        fileRow.setCollapsed(!fileRow.collapsed);
      }
      if (fileRow.file) {
        ctrl.onFileClick({
          $event: $event,
          file: fileRow.file,
          index: fileRow.index
        });
      }
    };

    ctrl.collapseAll = function () {
      if (ctrl.collapseRoot) {
        ctrl.rootCollapsed = true;
      }
      ctrl.fileRows.forEach(function (fr) {
        fr.setCollapsed(true);
      });
    };
    ctrl.expandAll = function () {
      ctrl.rootCollapsed = false;
      if (ctrl.showChangedOnly) {
        ctrl.toggleChanged();
      }
      ctrl.fileRows.forEach(function (fr) {
        fr.setCollapsed(false);
      });
    };

    ctrl.toggleChanged = function () {
      ctrl.showChangedOnly = !ctrl.showChangedOnly;
      ctrl.flat = !!ctrl.showChangedOnly;
      updateFilePaths();
    };

    ctrl.select = function (file) {
      // ensures a given file is visible
      var fileRow = ctrl.fileRows.find(function (fr) {
        return fr.file === file || fr.path === file.path;
      });
      while (fileRow) {
        fileRow.setCollapsed(false);
        fileRow = fileRow.parent;
      }
    };

    ctrl.close = function (file) {
      if (file) {
        var fileRow = ctrl.fileRows.find(function (fr) {
          return fr.file === file;
        });
        remove(ctrl.fileRows, fileRow);
        remove(ctrl.files, file);
      }
    };

    ctrl.isOpen = function (file) {
      return ctrl.openFiles && ctrl.openFiles.includes(file);
    };

    ctrl.isChanged = function (file) {
      return ctrl.changedFiles && ctrl.changedFiles.includes(file);
    };

    ctrl._fileMenuClass = function (fileRow) {
      var fmClass = ctrl.fileMenuClass(fileRow);
      if (fmClass === false) {
        return '';
      } else {
        fmClass = fmClass || '';
      }
      if (fmClass) {
        fmClass += ' always-show';
      }
      if (ctrl.fileMenuTemplate) {
        fmClass += ' has-menu';
      }
      return fmClass;
    };
    var fileMenu = null;

    ctrl.fileMenuClick = function ($event, fileRow, contextMenu) {
      if (!ctrl.fileMenuTemplate || ctrl.expired || ctrl.readOnly || ctrl.disabled) return;

      if (fileMenu) {
        var menuFileRow = fileMenu.fileRow;
        fileMenu.menu.close();
        if (fileRow === menuFileRow) {
          fileMenu = null;
          return;
        }
      }
      var scope = $scope.$parent.$new();
      scope.path = fileRow.path;
      scope.directory = fileRow.directory;
      scope.file = fileRow.file;
      scope.$index = fileRow.$index;
      fileMenu = {
        menu: bubbleDialogService.open(scope, $event.target, {
          className: 'file-tree-row-menu',
          menu: ctrl.fileMenuTemplate,
          position: contextMenu ? 'bottom' : 'right',
          mousePosition: contextMenu && $event
        }),
        fileRow: fileRow
      };
      fileMenu.menu.result.finally(function () {
        if (fileRow === fileMenu.fileRow) {
          fileMenu = null;
        }
      });
    };

    ctrl.startDragging = function (file, event) {
      var external = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;

      var fileRow = ctrl.fileRows.find(function (fr) {
        return fr === file || fr.file === file;
      });
      ctrl.draggingFileRow = fileRow;
      if (fileRow && !external) {
        var contents = fileRow.file && fileRow.file.contents || fileRow.label;
        event.dataTransfer.setData('text/plain', contents);

        if (fileRow.filename && window.URL && window.File) {
          var data = URL.createObjectURL(new File([contents], fileRow.filename));
          event.dataTransfer.setData('DownloadUrl', 'text/plain:' + fileRow.filename + ':' + data);
        }
        if (ctrl.onFileDragStart) {
          ctrl.onFileDragStart({
            file: fileRow.file,
            path: fileRow.path,
            event: event
          });
        }
      }
    };

    ctrl.stopDragging = function () {
      var external = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;

      ctrl.draggingFileRow = null;
      if (!external && ctrl.onFileDragStop) {
        ctrl.onFileDragStop();
      }
    };

    ctrl.dropFile = function (fileRow, event) {
      if (ctrl.onFileDrop) {
        ctrl.onFileDrop({
          file: ctrl.draggingFileRow.file,
          path: ctrl.draggingFileRow.path,
          dropPath: fileRow.path,
          dropDirectory: fileRow.directory,
          event: event
        });
      }
    };

    var frWasCollapsed = {};

    function updateFilePaths() {
      var dirHash = {};
      ctrl.fileRows.forEach(function (fr) {
        frWasCollapsed[fr.path] = fr.collapsed;
        if (fr.id) {
          frWasCollapsed[fr.id] = fr.collapsed;
        }
      });
      var fileRows = ctrl.fileRows = [];
      var files = (ctrl.files || []).filter(function (f) {
        return (!ctrl.showChangedOnly || ctrl.isChanged(f)) && (!f.hidden || ctrl.reviewMode);
      } // remove hidden files at this level
      ).map(function (a) {
        // we don't allow leading or trailing slashes, back slashes, or multiple slashes, so fix them
        if (a.path) {
          defineValue(a, '$path', cleanPath(a.path));
        }
        return a;
      });
      if (!ctrl.disableSort) {
        files.sort(function (a, b) {
          return compareIgnoreCase(a.$path, b.$path);
        });
      }
      files.forEach(function (file, index) {
        var _splitPath = splitPath(file.$path),
            pathParts = _splitPath.pathParts,
            filename = _splitPath.filename;

        var parentPath$$1 = void 0;
        if (ctrl.flat) {
          // flattens the hierarchy
          parentPath$$1 = '';
        } else {
          // insert missing directories
          var checkedPath = [];
          pathParts.forEach(function (segment) {
            var parentPath$$1 = checkedPath.join('/');
            checkedPath.push(segment);
            var virtualParentPath = checkedPath.join('/');
            if (!dirHash[virtualParentPath]) {
              var dir = dirHash[virtualParentPath] = new FileRow({
                tree: ctrl,
                path: virtualParentPath,
                filename: segment,
                directory: true,
                parent: dirHash[parentPath$$1],
                collapsed: frWasCollapsed[virtualParentPath]
              });
              fileRows.push(dir);
            }
          });
          parentPath$$1 = pathParts.join('/');
        }

        // create internal model
        var fileRow = new FileRow(angular.extend({}, file, {
          tree: ctrl,
          file: file,
          filename: filename,
          parent: dirHash[parentPath$$1],
          collapsed: frWasCollapsed[file.id] !== undefined ? frWasCollapsed[file.id] : frWasCollapsed[file.$path]
        }));

        // tracking original file
        fileRow.index = index;
        fileRows.push(fileRow);
        if (fileRow.directory) {
          dirHash[file.$path] = fileRow;
        }
      });

      sortFileRows();
      updateCompactedDirectories();

      if (ctrl.autoExpandAll) {
        ctrl.expandAll();
      }
    }

    function sortFileRows() {
      if (!ctrl.disableSort) {
        ctrl.fileRows.sort(function (a, b) {
          var key = ctrl.flat ? 'label' : ctrl.prefs.directoriesOnTop ? 'dirTopSortPath' : 'dirInlineSortPath';
          return compareIgnoreCase(a[key], b[key]);
        });
      }
    }

    function updateCompactedDirectories() {
      if (ctrl.prefs.compactEmptyDirectories) {
        ctrl.fileRows.filter(function (fr) {
          return fr.directory && fr.children.length === 1 && fr.children[0].directory;
        }).forEach(function (fr) {
          fr.compacted = true;
        });
      } else {
        ctrl.fileRows.forEach(function (fr) {
          fr.compacted = false;
        });
      }

      ctrl.fileRows.forEach(function (fr) {
        fr.setDepth();
      });
    }

    //// preference syncing

    function loadPrefs() {
      var prefs = accountService.prefs();

      // Handle case where prefs.filetree might be a stringified JSON from old data
      if (typeof prefs.filetree === 'string') {
        try {
          prefs.filetree = JSON.parse(prefs.filetree);
        } catch (e) {
          $log.error('Error parsing filetree prefs:', e, prefs.filetree);
          // If parsing fails, reset to default
          prefs.filetree = null;
        }
      }

      ctrl.prefs = prefs.filetree = prefs.filetree || {
        directoriesOnTop: !KeyCodes.IS_MAC, // macs mix in directories by default
        compactEmptyDirectories: false
      };
    }

    ctrl.setDirOnTop = function (val) {
      ctrl.prefs.directoriesOnTop = val;
      sortFileRows();
      if (!$rootScope.playbackToken) accountService.savePrefs();
    };
    ctrl.setCompactDir = function (val) {
      ctrl.prefs.compactEmptyDirectories = val;
      updateCompactedDirectories();
      if (!$rootScope.playbackToken) accountService.savePrefs();
    };

    $scope.$watch('$ctrl.prefs.directoriesOnTop', ctrl.setDirOnTop);
    $scope.$watch('$ctrl.prefs.compactEmptyDirectories', ctrl.setCompactDir);
  }]).directive('fileTreeDraggableFileRow', ['$timeout', function ($timeout) {
    var counter = 0;
    return {
      restrict: 'A',
      require: '^fileTree',
      link: function link(scope, elem, attr, fileTreeCtrl) {
        var getFileRow = function getFileRow() {
          return scope.$eval(attr.fileTreeDraggableFileRow);
        };
        var fileRow = getFileRow();

        var canDrag = function canDrag() {
          return fileTreeCtrl.dragEnabled && fileRow && (
          // either no file (virtual directory) or a non-locked, draggable file
          !fileRow.file || !fileRow.file.disabled && fileRow.file.$draggable !== false);
        };

        scope.$watch(canDrag, function () {
          elem.attr('draggable', canDrag());
        });

        var canDrop = false;
        var dragEnterCounter = 0;

        elem.attr('id', 'draggable-file-row-' + ++counter)

        // enable or disable drag-and-drop
        .attr('draggable', canDrag() || null)

        // enable dragging the file/filerow
        .on('dragstart', function (event) {
          scope.$apply(function () {
            fileTreeCtrl.startDragging(getFileRow(), event, false);
          });
        }).on('dragend', function () {
          $timeout(function () {
            fileTreeCtrl.stopDragging(false);
          });
        })

        // enable dropping a file/filerow here
        .on('dragenter', function () {
          dragEnterCounter++;
          var draggingFileRow = fileTreeCtrl.draggingFileRow;
          // save for future drag events
          canDrop = fileTreeCtrl.onFileDrop &&
          // we have a draggable row
          draggingFileRow && !fileTreeCtrl.expired && !fileTreeCtrl.disabled && !fileTreeCtrl.readOnly &&
          // it's not the current row
          draggingFileRow !== getFileRow() && (
          // it either has to be a virtual directory, or a non-locked file
          !draggingFileRow.file || !draggingFileRow.file.locked);
          if (canDrop) {
            elem.addClass('drag-over');
          }
        }).on('dragover', function (event) {
          if (canDrop) {
            stopEvent(event);
            event.dataTransfer.dropEffect = 'move';
          }
        }).on('dragleave', function () {
          dragEnterCounter--;
          if (dragEnterCounter < 1) {
            elem.removeClass('drag-over');
          }
        }).on('drop', function (event) {
          dragEnterCounter = 0;
          elem.removeClass('drag-over');
          if (canDrop) {
            stopEvent(event);
            scope.$applyAsync(function () {
              fileTreeCtrl.dropFile(getFileRow(), event);
            });
          }
        });
      }
    };
  }]);

  var advancedCodeChallengeFileToolbar = "<div class=\"left-buttons\" ng-if=\"!fileTab.file.$deleted && !fileTab.file.alwaysLocked && !$ctrl.expired && !$ctrl.process.usingAttach\"> <span class=\"file-warning file-ignored-warning\" ng-if=\"fileTab.file.ignored\" tooltip=\"{{fileTab.file.ignoredWarningTooltip()}}\"> {{fileTab.file.changesIgnored() ? 'Changes Ignored' : 'Not Assessed'}} </span> <a class=\"interact-edit-notice {{$ctrl.mfc.interact.buttonClasses(fileTab.file.path)}}\" tooltip=\"{{$ctrl.mfc.interact.buttonTooltip}}\" ng-if=\"$ctrl.mfc.interact\" ng-click=\"$ctrl.mfc.interact.canEdit && $ctrl.mfc.interact.updateFile({ id: fileTab.file.interactId })\"></a> <a class=\"file-rename\" ng-click=\"$ctrl.mfc.rename(fileTab.file)\" ng-disabled=\"$ctrl.mfc.interact.cannotEdit\">Rename</a> <a ng-click=\"$ctrl.mfc.delete(fileTab.file)\" tooltip=\"Delete\" ng-disabled=\"$ctrl.mfc.interact.cannotEdit\"><span class=\"icon-trashcan\"></span></a> <a ng-click=\"$ctrl.mfc.duplicate(fileTab.file)\" tooltip=\"Duplicate\" ng-disabled=\"$ctrl.mfc.interact.cannotEdit\"><span class=\"icon-duplicate\"></span></a> <a class=\"file-reset\" ng-if=\"fileTab.file.canReset\" ng-disabled=\"fileTab.file.unchanged || $ctrl.mfc.interact.cannotEdit\" on-confirm=\"$ctrl.mfc.reset(fileTab.file)\" on-confirm-message=\"Reset this file's contents? (Current changes will be moved to deleted files.)\">Reset…</a> </div> <div class=\"left-buttons\" ng-if=\"fileTab.file.alwaysLocked && !fileTab.file.alwaysReadOnly && !$ctrl.expired && !$ctrl.process.usingAttach\"> <span class=\"access-notice locked-notice\" ng-if=\"!fileTab.file.lockedLayout\" tooltip=\"This file can be modified, but not renamed or deleted\"> Path-Locked File </span> <a class=\"interact-edit-notice {{$ctrl.mfc.interact.buttonClasses(fileTab.file.path)}}\" tooltip=\"{{$ctrl.mfc.interact.buttonTooltip}}\" ng-if=\"$ctrl.mfc.interact\" ng-click=\"$ctrl.mfc.interact.canEdit && $ctrl.mfc.interact.updateFile({ id: fileTab.file.interactId })\"></a> <a class=\"file-reset\" ng-if=\"fileTab.file.canReset\" ng-disabled=\"fileTab.file.unchanged || $ctrl.mfc.interact.cannotEdit\" on-confirm=\"$ctrl.mfc.reset(fileTab.file)\" on-confirm-message=\"Reset this file's contents? (This cannot be undone!)\">Reset…</a> </div> <div class=\"left-buttons\" ng-if=\"fileTab.file.alwaysReadOnly &&  !fileTab.file.$deleted && !$ctrl.expired && !$ctrl.process.usingAttach\"> <span class=\"access-notice read-only-notice\" tooltip=\"This file can be read, but not modified in any way\"> Read Only File </span> <a class=\"interact-edit-notice {{$ctrl.mfc.interact.buttonClasses(fileTab.file.path)}}\" tooltip=\"{{$ctrl.mfc.interact.buttonTooltip}}\" ng-if=\"$ctrl.mfc.interact\" ng-click=\"$ctrl.mfc.interact.canEdit && $ctrl.mfc.interact.updateFile({ id: fileTab.file.interactId })\"></a> <a ng-click=\"$ctrl.mfc.duplicate(fileTab.file)\" ng-if=\"!$ctrl.mfc.readOnly\" tooltip=\"Duplicate\"><span class=\"icon-duplicate\"></span></a> </div> <div class=\"left-buttons\" ng-if=\"fileTab.file.$deleted && !$ctrl.expired && !$ctrl.process.usingAttach\"> <span class=\"access-notice deleted-notice\">Deleted File</span> <a class=\"file-undelete\" ng-click=\"$ctrl.mfc.undelete(fileTab.file)\">Undelete</a> </div> <div class=\"left-buttons\" ng-if=\"$ctrl.expired\"> <span class=\"access-notice read-only-notice\" tooltip=\"This file can be read but not modified anymore\"> Read Only ({{$ctrl.expirationReason||'time limit reached'}}) </span> </div> <div class=\"left-buttons\" ng-if=\"!$ctrl.expired && $ctrl.process.usingAttach\"> <span class=\"access-notice read-only-notice\" tooltip=\"This file can be read but not modified while using an external IDE\"> Read Only (using an external editor) </span> </div> <div class=\"right-buttons\"> <a ng-click=\"$ctrl.mfc.export(fileTab.file)\"><span class=\"icon-export\"></span> Save Locally</a> </div> ";

  var advancedCodeChallengeFileMenu = "<ul class=\"dropdown-menu\" ng-if=\"$ctrl.process.usingAttach\"> <li class=\"disabled\"> <a> File editing is disabled in the client UI while using your own IDE </a> </li> <li> <a class=\"file-use-internal-ide\" ng-click=\"$ctrl.toggleExternalIde()\"> Use Web IDE… </a> </li> </ul> <ul class=\"dropdown-menu\" ng-if=\"!$ctrl.process.usingAttach && file.locked\"> <li class=\"disabled\"> <a ng-if=\"!file.disabled && !directory && file.readOnly && $ctrl.mfc.interact.cannotEdit\"> The solution is currently being edited by {{$ctrl.mfc.interact.activeEditor.name}}. </a> <a ng-if=\"!file.disabled && !directory && file.readOnly && !$ctrl.mfc.interact.cannotEdit\"> This file can be opened, but not<br> renamed, edited, or deleted </a> <a ng-if=\"!file.disabled && !directory && !file.readOnly\"> This file can be edited, but not<br> renamed or deleted </a> <a ng-show=\"!file.disabled && directory\"> This directory can be viewed, but not<br> {{ $ctrl.mfc.canAddFileTo(file, path, directory) ? 'renamed or deleted' :\n      'renamed, deleted, or have files added to it' }} </a> <a ng-show=\"file.disabled\"> This {{ directory ? 'directory' : 'file' }} is part of the<br> challenge, but is not viewable </a> </li> </ul> <ul class=\"dropdown-menu\" ng-if=\"!$ctrl.process.usingAttach && $ctrl.mfc.canAddFileTo(file, path, directory)\"> <li> <a class=\"file-add\" ng-click=\"$ctrl.mfc.addFileTo({basePath: path})\"> New File or Directory Here </a> </li> </ul> <ul class=\"dropdown-menu\" ng-if=\"!file.locked && !$ctrl.process.usingAttach && !$ctrl.mfc.interact.cannotEdit && (file.$deletable || file.canReset || !directory)\"> <li ng-if=\"!file || file.$deletable\"> <a class=\"file-rename\" ng-click=\"$ctrl.mfc.rename(file, path)\"> Rename / Move </a> </li> <li ng-if=\"file.canReset\"> <a class=\"file-reset\" ng-disabled=\"file.unchanged\" on-confirm=\"$ctrl.mfc.reset(file)\" on-confirm-message=\"Reset this file's contents? (Current changes will be moved to deleted files.)\" on-confirm-position=\"right\"> Reset… </a> </li> <li ng-if=\"!file || file.$deletable\"> <a class=\"file-delete\" ng-click=\"$ctrl.mfc.delete(file, path)\"> Delete </a> </li> </ul> <ul class=\"dropdown-menu\" ng-if=\"!file.disabled && !$ctrl.process.usingAttach && !$ctrl.mfc.interact.cannotEdit\"> <li> <a class=\"file-duplicate\" ng-click=\"$ctrl.mfc.duplicate(file, path)\"> Duplicate </a> </li> </ul> ";

  var mfcDeletedFileMenu = "<ul class=\"dropdown-menu\"> <li> <a class=\"file-undelete\" ng-click=\"(challengeEdit || $ctrl.mfc || $ctrl).undelete(file, path)\">Undelete</a> </li> <li> <a class=\"file-purge\" on-confirm=\"(challengeEdit || $ctrl.mfc || $ctrl).purge(file, path)\" on-confirm-message=\"Are you sure you want to delete this {{!file||file.directory?'directory and all files within it':'file'}} forever?\" on-confirm-position=\"right\"> Purge… </a> </li> </ul> ";

  var multiFileToolbarTemplate = "<button class=\"btn-default btn-sm multi-file-toolbar-action-{{action.id}}\" ng-repeat-start=\"action in $ctrl.actions track by $index\" ng-if=\"!action.hidden && !action.sep\" ng-click=\"$ctrl.click(action)\" ng-disabled=\"action.disabled\" tooltip=\"{{ action.toolbarTooltip || action.label || action.path }}\" tooltip-shortcut-hint=\"{{ action.toolbarTooltipShortcut }}\"> <file-icon file=\"action\"></file-icon> <span ng-if=\"action.toolbarLabel\" ng-bind=\"action.toolbarLabel\"></span> </button> <span ng-repeat-end ng-if=\"!action.hidden && action.sep\" class=\"sep\"></span> ";

  var multiFileToolbarComponentModule = angular.module('Q.components.multi-file-toolbar', [fileIconComponentModule]).component('multiFileToolbar', {
    bindings: {
      actions: '<',
      onClick: '&'
    },
    controller: 'MultiFileToolbarCtrl',
    template: multiFileToolbarTemplate
  }).controller('MultiFileToolbarCtrl', function MultiFileToolbarCtrl() {
    var ctrl = this;

    ctrl.click = function (action) {
      if (action.onToolbarClick) {
        action.onToolbarClick();
      } else {
        ctrl.onClick({ action: action });
      }
    };
  });

  var attachDialogTemplate = "<div class=\"attach-dialog\"> <div class=\"modal-header\"> <h4 class=\"modal-title\">Use Your Own IDE</h4> <a href=\"{{::$root.appConfig.docsUrl}}/for-candidates/advanced-challenge-ide/external-ide\" target=\"_blank\" rel=\"noopener\" class=\"btn-default btn-sm\" tooltip=\"Learn More about Using Your Own IDE\">What's This?</a> <button class=\"close\" aria-label=\"Close\" ng-click=\"$ctrl.cancel()\"> <span aria-hidden=\"true\">&times;</span> </button> </div> <div class=\"modal-body modal-long-form\"> <section> <p> Using our <a href=\"https://gitlab.com/qualified/attach\" target=\"_blank\" rel=\"noopener\">Qualified Attach Node module</a>, you can edit your solution in your own IDE. Attach works by linking saved files directly back to this browser window, saving your changes, running tests, and updating the web preview automatically. </p> <h2>Steps</h2> <ol> <li> You'll need <a href=\"https://nodejs.org/\" target=\"_blank\" rel=\"noopener\"><strong>Node 10</strong></a> or newer and <strong><a href=\"https://www.npmjs.com/\" target=\"_blank\" rel=\"noopener\">NPM</a> or <a href=\"https://yarnpkg.com/\">Yarn</a></strong> installed to use Qualified Attach. </li> <li> Choose from the commands below to <strong>install and run</strong> the <code>qualified-attach</code> application.<br> <em>If the program was run without a token, you can also copy it from below and provide it to the app.</em> </li> <li> Attach will copy all of the files used within this challenge to a local directory of your choosing. </li> <li> <strong>Edit your solution files</strong> like normal, then simply <strong>save them</strong>. Changes will automatically be pushed back to the web browser, where they will be saved to Qualified, and real-time web previews are updated. </li> <li> You can trigger running tests either from the browser buttons, as normal, or using attach to send them from the command line. </li> </ol> <div class=\"alert alert-warning\"> <strong>Warning!</strong> Do not close the browser, or your changes may be lost! </div> </section> <section ng-if=\"$ctrl.loading\" class=\"processing-animation\"> <p><span class=\"loading-spinner loading-spinner-large\"></span></p> <p>Loading Token Info...</p> </section> <section ng-if=\"!$ctrl.loading && $ctrl.connectError\" class=\"processing-error\"> <h5>There was an error setting up the connection.</h5> <p>Please use the button below to try again.</p> <p> <button class=\"btn-default\" ng-click=\"$ctrl.connect()\"> Reload Connection </button> </p> </section> <section ng-if=\"!$ctrl.loading && !$ctrl.connectError\" class=\"install-commands\"> <h2>Installation & Attach Commands</h2> <div class=\"help-block\"> <p> If you have NPM or Yarn already installed, choose from the commands below to get started. </p> </div> <tabset> <tab ng-repeat=\"installer in $ctrl.installers\"> <tab-heading ng-bind=\"installer.label\"></tab-heading> <div> <p class=\"help-block\" ng-bind=\"installer.desc\"></p> <div class=\"terminal-command\"> <button class=\"btn-primary\" copy-button copy-button-value=\"installer.cmd\" tooltip=\"Copy Command to your clipboard\"></button> <pre><code ng-repeat=\"cmd in installer.cmds\" data-prompt=\"{{$ctrl.terminalPrompt}}\" ng-bind=\"cmd\"></code></pre> </div> </div> </tab> </tabset> </section> <section ng-if=\"!$ctrl.loading && !$ctrl.connectError\" class=\"token\"> <h2>Token</h2> <div class=\"help-block\"> <p> If you need to set up qualified-attach manually, use the token below when prompted. </p> </div> <div class=\"terminal-command\"> <button class=\"btn-primary\" copy-button copy-button-value=\"$ctrl.token\" tooltip=\"Copy Token to your clipboard\"></button> <pre><code ng-bind=\"$ctrl.token\"></code></pre> </div> </section> </div> <div class=\"modal-footer\"> <a class=\"btn-default help\" target=\"_blank\" rel=\"noopener\" href=\"{{::$root.appConfig.docsUrl}}/for-candidates/advanced-challenge-ide/external-ide\"> Help </a> <button class=\"btn-default\" ng-click=\"$ctrl.cancel()\" ng-disabled=\"$ctrl.disabled\"> Cancel </button> </div> </div> ";

  var detachDialogTemplate = "<div class=\"attach-dialog detach\"> <div class=\"modal-header\"> <h4 class=\"modal-title\">Return to the Web IDE</h4> <button class=\"close\" aria-label=\"Close\" ng-click=\"$ctrl.cancel()\"> <span aria-hidden=\"true\">&times;</span> </button> </div> <div class=\"modal-body modal-long-form\"> <section> <p> If you return to the web IDE, the attach session will no longer be synced to the browser.<br> You'll need to erase your local code, and restart the attach process. </p> <ul class=\"action-links\"> <li> <a ng-click=\"$ctrl.detach()\" tabindex=\"0\"> <strong>Disable Attach</strong>: Switch to editing in the Web IDE </a> </li> <li class=\"cancel\"> <a ng-click=\"$ctrl.cancel()\" tabindex=\"0\"> <strong>Cancel</strong> Continue using my editor </a> </li> </ul> </section> </div> </div> ";

  var ablyClientServiceModule = angular.module('Q.services.ably-client-service', []).factory('ablyClientService', ['$log', '$window', '$rootScope', '$http', function ($log, $window, $rootScope, $http) {
    var clients = {};

    function destroy(svc) {
      try {
        if (svc.client) {
          svc.client.connection.close();
        }
        delete clients[svc.$clientKey];
        delete svc.channel;
        delete svc.client;
        delete svc.$clientKey;
        delete svc.$channelKey;
        delete svc.channelError;
      } catch (ex) {}
    }

    return {
      /**
       * Dresses an existing service by creating or re-using appropriate Ably client and channel
       * @param {*} svc - Service object
       * @param {String} ablyKey? -
       * @param {String} clientKey? -
       * @param {String} channelId -
       * @param {String} clientId -
       * @param {String} tokenUrl - optional URL, will default to main ably endpoint if not provided
       * @param {String} channelEventPrefix -
       * @returns {*} Socket
       */
      connect: function connect(_ref) {
        var svc = _ref.svc,
            clientKey = _ref.clientKey,
            channelId = _ref.channelId,
            clientId = _ref.clientId,
            tokenUrl = _ref.tokenUrl,
            _ref$channelEventPref = _ref.channelEventPrefix,
            channelEventPrefix = _ref$channelEventPref === undefined ? 'socket' : _ref$channelEventPref;

        var channelKey = clientKey + '\n' + channelId;
        if (svc.channel && svc.$channelKey === channelKey && !svc.channelError) {
          return svc;
        } else if (svc.channelError || svc.client && svc.$channelKey !== channelKey) {
          // only allow one channel per service (this could be a bad idea)
          destroy(svc);
        }

        // delete in case there was a previous channel error
        delete svc.channelError;

        try {
          if (!$window.Ably) {
            throw new Error('Ably client code not loaded');
          }
          if (!clientKey && !svc.client) {
            throw new Error('No key found for Ably');
          }
          if (!channelId) {
            throw new Error('Unable to find channel ID');
          }

          svc.$channelKey = channelKey;
          svc.$clientKey = clientKey;

          var refreshToken = function refreshToken() {
            return $http.post(tokenUrl || appConfig.baseApiRoot + 'ably/token').then(function (resp) {
              return (resp.data || {}).token;
            });
          };

          if (!clients[clientKey] && !svc.client) {
            clients[clientKey] = new $window.Ably.Realtime({
              clientId: clientId || null,
              recover: function recover(lastConnectionDetails, cb) {
                // Always attempt recovery in Ably 2.0
                cb(true);
              },
              authCallback: function authCallback() {
                return refreshToken().catch(function (e) {
                  // status <= 0 is a client-side network failure or timeout;
                  // Ably retries authCallback itself, so don't alert on it
                  var isNetworkError = e && e.status <= 0;
                  $log[isNetworkError ? 'warn' : 'error']('[Ably] Token refresh failed', e);
                  throw e;
                });
              }
            });

            // Monitor connection failures
            clients[clientKey].connection.on('failed', function () {
              $log.error('[Ably] Connection failed', { clientKey: clientKey });
            });
          }
          svc.client = svc.client || clients[clientKey];

          // provide the ability to listen to other channels and broadcast their connection status
          svc.listen = function (channelId, eventPrefix) {
            var channel = svc.client.channels.get(channelId);

            svc.client.connection.on('connected', function () {
              $rootScope.$broadcast(eventPrefix + ':connected');
              // ensure the channel is attached, used for when a connection has to reconnect itself
              if (channel) {
                channel.attach();
              }
              $log.debug(clientKey + ' - ' + channelId + ' socket channel is ready.');
            });

            channel.on('detached', function () {
              $log.debug(clientKey + ' - ' + channelId + ' socket channel is detached');
              $rootScope.$broadcast(eventPrefix + ':disconnected');
              // ensure that if the channel gets disconnected that it connects again
              if (svc.client.connection.state === 'connected') {
                channel.attach();
              }
            });

            channel.on('failed', function (err) {
              $log.error('[Ably] Channel failed', {
                clientKey: clientKey,
                channelId: channelId,
                error: err
              });
            });

            $log.debug('Channel subscribed: ' + clientKey + ' - ' + channelId);
            return channel;
          };

          svc.channel = svc.listen(channelId, channelEventPrefix);
        } catch (ex) {
          destroy(svc);
          svc.channelError = ex || new Error('Error creating Ably connection');
          $log.error('Error creating Ably connection (' + channelEventPrefix + ':' + clientKey + ')', ex, {
            channelId: channelId,
            clientId: clientId,
            channelEventPrefix: channelEventPrefix
          });
        }

        return svc;
      },

      destroy: destroy
    };
  }]);

  var attachServiceModule = angular.module('Q.services.attach-service', [accountServiceModule, ablyClientServiceModule]).factory('attachService', ['$q', '$http', '$log', '$timeout', 'accountService', 'ablyClientService', function ($q, $http, $log, $timeout, accountService, ablyClientService) {
    var channelServices = {};

    var svc = {
      /**
       * Creates a channel for listening to attach events
       * @param challengeId {String} Challenge ID for the socket channel
       * @param $scope {rootScope.Scope?} Optional scope to automatically clean up subscriptions when destroyed
       * @returns {*}
       */
      create: function create(challengeId, $scope) {
        var accountUser = accountService.get().user;

        if (!accountUser) {
          $log.error('Invariant violation: accountUser was unexpectedly undefined');
          return;
        }

        var _ablyClientService$co = ablyClientService.connect({
          svc: svc,
          clientKey: 'attach',
          clientId: accountUser.id,
          tokenUrl: appConfig.baseApiRoot + 'attach/ably_token',
          channelId: 'attach:' + accountUser.id + ':' + challengeId,
          channelEventPrefix: 'attach'
        }),
            channel = _ablyClientService$co.channel,
            channelError = _ablyClientService$co.channelError;

        if (!channel || channelError) {
          throw new Error('Unable to connect to realtime channel. Please make sure you are not running any content blockers, and reload the page.', { cause: channelError });
        }

        // Enter presence so that Attach can make sure the browser is connected.
        // Will also emit `leave` when the browser disconnects.
        channel.presence.enter({ app: 'assess' });

        var subs = [];
        var channelService = {
          channel: channel,
          subscribe: function subscribe(event, handler) {
            var existing = subs.find(function (_ref) {
              var _ref2 = slicedToArray(_ref, 2),
                  subEvent = _ref2[0],
                  subHandler = _ref2[1];

              return subEvent === event && subHandler === handler;
            });
            if (existing) {
              return existing[2];
            }
            channel.subscribe(event, handler);
            var unsub = function unsub() {
              return channelService.unsubscribe(event, handler);
            };
            subs.push([event, handler, unsub]);
            return unsub;
          },
          unsubscribe: function unsubscribe(event, handler) {
            channel.unsubscribe(event, handler);
            var idx = subs.findIndex(function (_ref3) {
              var _ref4 = slicedToArray(_ref3, 2),
                  event = _ref4[0],
                  handler = _ref4[1];

              return event === event && handler === handler;
            });
            if (idx > -1) {
              subs.splice(idx, 1);
            }
          },
          destroy: function destroy() {
            subs.forEach(function (sub) {
              return channelService.unsubscribe(sub[0], sub[1]);
            });
            subs.length = 0;
            remove(channelServices[challengeId], channelService);
            if (channelServices[challengeId].length === 0) {
              // nobody listening, shut down this socket
              ablyClientService.destroy(svc);
            }
          }
        };

        if ($scope) {
          $scope.$on('$destroy', function () {
            // clean up any subscribers. Run out-of-digest, and delayed since it's low priority
            $timeout(channelService.destroy, 1000, false);
          });
        }

        if (!channelServices[challengeId]) channelServices[challengeId] = [];
        channelServices[challengeId].push(channelService);

        return channelService;
      }
    };

    return svc;
  }]);

  var clipboardComponentModule = 'angular-clipboard';

  var copyButtonComponentModule = angular.module('Q.components.copy-button', [keycodesModule, bubbleDialogComponentModule, clipboardComponentModule])

  /**
   * @ngdoc directive
   * @name copyButton
   * @description
   * Turns a button into a one-click text copy action
   * @element ANY
   * @restrict A
   *
   * @param {template} copyButtonLabel? Label text
   * @param {expression} copyButtonValue Value to be copied
   */
  .directive('copyButton', ['clipboard', 'bubbleDialogService', '$timeout', '$sce', '$window', 'KeyCodes', function (clipboard, bubbleDialogService, $timeout, $sce, $window, KeyCodes) {
    return {
      restrict: 'A',
      link: function link(scope, elem, attr) {
        elem.addClass('copy-button');
        elem.text(attr.copyButtonLabel);

        var successTimeout = null;

        function showSuccess() {
          if (successTimeout) {
            $timeout.cancel(successTimeout);
          }
          elem.addClass('copy-button-success');
          if (attr.copyButtonLabel) {
            elem.css({ 'min-width': elem.offsetWidth() + 'px' });
            elem.text('Copied!');
          }
          successTimeout = $timeout(function () {
            if (attr.copyButtonLabel) {
              elem.css({ 'min-width': null });
              elem.text(attr.copyButtonLabel);
            }
            elem.removeClass('copy-button-success');
            successTimeout = null;
          }, 2000, false);
        }

        elem.on('click', function (event) {
          stopEvent(event);

          var value = attr.copyButtonValue ? scope.$eval(attr.copyButtonValue) : '';

          if (clipboard.supported) {
            try {
              clipboard.copyText(value);
              showSuccess();
              return;
            } catch (e) {
              // ignore copy errors, just show the pop-up
            }
          }

          var dialogContent = '<div>Press <span class="shortcut-hint">' + KeyCodes.SYMBOLS.COMMAND_KEY + ' + C</span> to copy the text below:</div>\n<div><input type="text" autofocus class="form-input" value="' + escapeHTML(value) + '"></div>';

          var bubbleResult = bubbleDialogService.open(scope, elem, {
            title: attr.copyButtonLabel,
            content: $sce.trustAsHtml(dialogContent)
          });

          bubbleResult.result.then(null, null, function (status) {
            if (status.ready) {
              var el = status.element;
              var $in = el.find('input');
              if ($in.length) {
                $in.focus().selectInput();
                var inputEl = $in[0];
                inputEl.selectionStart = 0;
                inputEl.selectionEnd = inputEl.value.length;
                $in.on('copy', function () {
                  showSuccess();
                  bubbleResult.close();
                });
              }
            }
          });
        });
      }
    };
  }]);

  var attachComponentModule = angular.module('Q.components.attach-dialog', [modalComponentModule, attachServiceModule, appAlertComponentModule, copyButtonComponentModule]).factory('attachDialog', ['$modal', function attachDialog($modal) {
    return {
      open: function open(_attach, _solution) {
        return $modal.openOnce('detachDialog', {
          template: _attach ? attachDialogTemplate : detachDialogTemplate,
          controller: 'AttachDialogCtrl as $ctrl',
          size: _attach && 'lg',
          resolve: {
            attach: function attach() {
              return _attach;
            },
            solution: function solution() {
              return _solution;
            }
          }
        }).result;
      }
    };
  }]).controller('AttachDialogCtrl', ['$scope', '$modalInstance', 'attach', 'solution', 'attachService', '$http', '$q', '$location', 'appAlert', function AttachDialogCtrl($scope, $modalInstance, attach, solution, attachService, $http, $q, $location, appAlert) {
    var TERMINAL_PROMPT_WINDOWS = '>';
    var TERMINAL_PROMPT_DEFAULT = '$';
    var HOST = $location.host();
    var BASE_URL_ENV = 'Q_ATTACH_API_BASE_URL';
    var BASE_URL = $location.protocol() + '://' + HOST + ':' + $location.port() + '/api';
    var TERMINAL_BASE_URL = IS_WINDOWS ? 'SET "' + BASE_URL_ENV + '=' + BASE_URL + '\n' : BASE_URL_ENV + '=' + BASE_URL + ' ' + TERMINAL_CONTINUE;
    var PACKAGE = '@qualified/attach';
    var NPM_COMMAND = 'npm i -g ' + PACKAGE;
    var COMMAND_BASE = (HOST === 'www.qualified.io' ? '' : TERMINAL_BASE_URL) + 'qualified-attach --token=TOKEN';

    var ctrl = this;

    ctrl.cancel = function () {
      return $modalInstance.dismiss();
    };
    ctrl.terminalPrompt = IS_WINDOWS ? TERMINAL_PROMPT_WINDOWS : TERMINAL_PROMPT_DEFAULT;
    ctrl.installers = [{
      label: 'NPM Install & Attach',
      baseCmd: NPM_COMMAND,
      desc: 'Installs the Attach runner with NPM and immediately starts it for this challenge.'
    }, {
      label: 'Already Installed',
      desc: 'If you already have the Attach runner installed, this command starts it for this challenge.'
    }];

    ctrl.attach = function (detectedNotice) {
      if (detectedNotice) {
        appAlert.success('Attach connected successfully!');
      }
      $modalInstance.close(true);
    };

    ctrl.detach = function () {
      $modalInstance.close(false);
    };

    if (attach) {
      ctrl.connect = function () {
        if (ctrl.loading) return;
        try {
          ctrl.connectError = false;
          ctrl.loading = 2;
          var channel = attachService.create(solution.challengeId, $scope).channel;
          var id = channel.name.split(':')[1];
          // Close the dialog when Attach enters presence.
          var onEnter = function onEnter(_ref) {
            var clientId = _ref.clientId,
                data = _ref.data;

            if (clientId === id && data && data.app === 'attach') {
              ctrl.attach(true);
              channel.presence.unsubscribe('enter', onEnter);
            }
          };
          channel.presence.subscribe('enter', onEnter);

          channel.presence.get({ clientId: id }).then(function (members) {
            if (members && members.find(function (m) {
              return m.data && m.data.app === 'attach';
            })) {
              ctrl.attach(true);
            }
          }).catch(appAlert.modelError).catch(function () {
            ctrl.connectError = true;
          }).finally(function () {
            ctrl.loading--;
          });

          // Get token
          $http.post(appConfig.baseApiRoot + 'attach/token', {
            assessmentResultId: solution.assessmentResultId,
            challengeId: solution.challengeId
          }).then(function (resp) {
            var data = resp.data || {};
            ctrl.token = (data.data || data).token;
            if (!ctrl.token) {
              return $q.reject('Unable to retrieve token, please try again');
            }
            var cmd = COMMAND_BASE.replace('TOKEN', ctrl.token);
            ctrl.installers.forEach(function (ins) {
              ins.cmd = ins.baseCmd ? '' + ins.baseCmd + TERMINAL_COMMAND_SEP + cmd : cmd;
              ins.cmds = IS_WINDOWS ? ins.cmd.split('\n').map(function (c) {
                return c + '\n';
              }) : [ins.cmd];
            });
          }).catch(appAlert.modelError).catch(function () {
            ctrl.connectError = true;
          }).finally(function () {
            ctrl.loading--;
          });
        } catch (err) {
          appAlert.modelError(err);
        }
      };
      ctrl.connect();
    }
  }]);

  var challengeSubmittingDialogTemplate = "<div class=\"modal-header\"> <h4 class=\"modal-title\"> <span ng-show=\"$ctrl.showWarning\"> <span class=\"icon-warning\"></span> {{ $ctrl.remaining === 1 ? 'Final Submission' : 'Use a Submission?' }} </span> <span ng-show=\"$ctrl.inProgress\"> Submitting Solution </span> <span ng-hide=\"$ctrl.inProgress || $ctrl.showWarning\"> Submission Results </span> <button class=\"close\" aria-label=\"Close\" ng-click=\"$ctrl.cancel()\" ng-if=\"$ctrl.showWarning || $ctrl.onlyShowResults\"> <span aria-hidden=\"true\">&times;</span> </button> </h4> </div> <div class=\"modal-body\"> <div ng-if=\"$ctrl.showWarning\"> <p ng-show=\"$ctrl.remaining === 1\"> This is the last time you will be able to submit your code. No further changes will be allowed after this. </p> <p ng-hide=\"$ctrl.remaining === 1\"> Continuing will use up a submission attempt, leaving you with {{$ctrl.remaining-1}} more submission attempt{{$ctrl.remaining===2?'':'s'}}. </p> <p>Are you sure you are ready to submit your code for scoring?</p> <ul class=\"action-links\"> <li> <a ng-click=\"$ctrl.startSubmission()\" tabindex=\"0\"> <strong>Submit</strong> your solution </a> </li> <li class=\"cancel\"> <a ng-click=\"$ctrl.cancel()\" tabindex=\"0\"> <strong>Cancel</strong>: Continue editing the solution </a> </li> <li class=\"secondary muted\"> <a ng-click=\"$ctrl.complete()\" tabindex=\"0\" tooltip=\"{{$ctrl.skipTooltip()}}\"> <strong>{{ $ctrl.editIsPrimary() ? 'Skip' : 'Continue' }}</strong> {{\n          $ctrl.nextLabel }} </a> </li> </ul> </div> <busy-indicator ng-if=\"$ctrl.inProgress\"></busy-indicator> <div ng-if=\"$ctrl.inProgress\" class=\"in-progress\"> <p ng-show=\"$ctrl.queuing\"> Your solution is being queued for automated scoring </p> <p ng-hide=\"$ctrl.queuing\"> Your solution is being automatically scored <span class=\"help-block\" ng-show=\"$ctrl.solution.isRetrying()\"> (Retrying due to timeout or unexpected error) </span> </p> </div> <div ng-if=\"!$ctrl.inProgress && !$ctrl.showWarning\" class=\"submitted\"> <div ng-if=\"$ctrl.submissionFailure\" class=\"failed-message\"> <p> <strong>There was an issue with submitting your code.</strong> </p> <div ng-if=\"$ctrl.submissionInvalid\"> <p>The code did not return any test case results. Please try again.</p> <p class=\"help-block\"> If the issue persists, there may be an issue with your code causing problems with running it properly. </p> </div> <div ng-if=\"$ctrl.submissionTimeout\"> <p> The code timed out and the code could not be scored. Please try again. </p> <p class=\"help-block\"> If the issue persists, you may have a performance or logic issue with your code. </p> </div> </div> <div ng-if=\"$ctrl.failedTests() && !$ctrl.submissionFailure\" class=\"failed-message\" ng-class=\"{ 'can-edit': $ctrl.canEdit() }\"> <p>The following issues were detected when submitting your code:</p> <ul class=\"submission-errors\"> <li ng-repeat=\"msg in $ctrl.issues track by $index\" ng-bind=\"msg\"></li> </ul> <p class=\"help-block\" ng-if=\"$ctrl.attemptNotUsed && $ctrl.canEdit()\"> <strong>Note:</strong> This submission did not use one of your attempts. You still have {{$ctrl.remaining}} attempt(s) left. </p> </div> <div ng-if=\"!$ctrl.failedTests() && !$ctrl.submissionFailure\" class=\"success-message\"> <p>Congratulations, your code has passed all the submission tests!</p> <p>You can now continue to the next step.</p> </div> <ul class=\"action-links\" ng-if=\"!$ctrl.onlyShowResults\"> <li class=\"edit-solution\" ng-if=\"$ctrl.editIsPrimary()\"> <a ng-click=\"$ctrl.complete(true)\" tabindex=\"0\"> <strong>Continue</strong> working on your solution </a> </li> <li class=\"continue\" ng-class=\"{ 'secondary muted': $ctrl.editIsPrimary() }\"> <a ng-click=\"$ctrl.complete()\" tabindex=\"0\" tooltip=\"{{$ctrl.skipTooltip()}}\"> <strong>{{ $ctrl.editIsPrimary() ? 'Skip' : 'Continue' }}</strong> {{\n          $ctrl.nextLabel }} </a> </li> <li class=\"secondary muted {{ $ctrl.canEdit() ? 'edit' : 'review'}}-solution\" ng-if=\"!$ctrl.editIsPrimary()\"> <a ng-click=\"$ctrl.complete(true)\" tabindex=\"0\"> <span ng-show=\"$ctrl.canEdit()\"> <strong>Refine</strong> your solution </span> <span ng-hide=\"$ctrl.canEdit()\"> <strong>Review</strong> your solution </span> </a> </li> </ul> </div> </div> <div class=\"modal-footer\" ng-if=\"$ctrl.onlyShowResults\"> <button class=\"btn-primary\" ng-click=\"$ctrl.cancel()\">Close</button> </div> ";

  var busyIndicatorDialogTemplate = "<div class=\"modal-header\" ng-if=\"$ctrl.title\"> <h4 class=\"modal-title\"> {{ $ctrl.title }} <button class=\"close\" aria-label=\"Close\" ng-click=\"$ctrl.cancel()\" ng-if=\"$ctrl.closeable\"> <span aria-hidden=\"true\">&times;</span> </button> </h4> </div> <div class=\"modal-body\"> <busy-indicator size=\"$ctrl.size\"></busy-indicator> <div class=\"help-block\" ng-if=\"$ctrl.message\" ng-bind=\"$ctrl.message\"></div> </div> ";

  var busyIndicatorComponentModule = angular.module('Q.components.busy-indicator', [modalComponentModule]).component('busyIndicator', {
    bindings: {
      size: '<'
    },
    controller: ['$element', function BusyIndicatorCtrl($element) {
      this.$onInit = function () {
        var _this = this;

        var size = this.size || 6;
        for (var i = 0; i < size; i++) {
          $element.append('<span>');
        }
        this.$onChanges = function (changes) {
          if (changes.size) {
            $element.empty();
            _this.$onInit();
          }
        };
      };
    }],
    template: ''
  }).factory('busyIndicatorDialog', ['$modal', '$timeout', function ($modal, $timeout) {
    var dialog = void 0;
    var ctrl = void 0;
    return {
      /**
       *
       * @param {string} options.message - Main message
       * @param {string} options.title - Main title
       * @param {number?} options.size - Number of dots
       * @param {boolean?} options.closeable - If true, allow user to close the dialog
       */
      open: function open(options) {
        if (ctrl) {
          angular.extend(ctrl, options);
          return;
        }

        dialog = $modal.openOnce('busyIndicatorDialog', {
          template: busyIndicatorDialogTemplate,
          controller: function controller() {
            ctrl = this;
            angular.extend(ctrl, options);
            ctrl.cancel = function () {
              return dialog.dismiss();
            };
          },
          controllerAs: '$ctrl',
          backdrop: 'static',
          windowClass: 'busy-indicator-dialog no-intercom',
          size: 'md',
          keyboard: false
        });

        dialog.result.finally(function () {
          dialog = ctrl = null;
        });
      },

      get showing() {
        return !!dialog;
      },
      close: function close() {
        if (dialog) {
          var _dialog = dialog;
          dialog = ctrl = null;
          $timeout(function () {
            _dialog.dismiss();
          });
        }
      }
    };
  }]);

  var MASTRA_BASE_URL = window.location.host.includes('localhost') ? 'http://localhost:4111/v1' : '/mastra/v1';

  var challengeSubmittingDialogComponentModule = angular.module('Q.components.challenge-submitting-dialog', [timerHelperModule, modalComponentModule, appAlertComponentModule, busyIndicatorComponentModule, trackerServiceModule, messageHubServiceModule]).factory('challengeSubmittingDialog', ['$modal', function challengeSubmittingDialog($modal) {
    var dialog = null;

    function close() {
      if (dialog) {
        dialog.dismiss();
      }
      dialog = null;
    }

    return {
      close: close,
      /**
       * Opens the challenge submitting dialog and submits the challenge. Handles warnings and feedback
       * @param {Solution} props.solution
       * @param {Function} props.saveSolution
       * @param {Function} props.afterSubmit
       * @param {boolean} props.expired
       * @param {boolean} props.warn
       * @param {string} props.nextLabel
       * @param {string} props.onlyShowResults
       * @returns {Promise<boolean>} Returns a promise that rejects if the user changed their mind, and resolves
       * true if they'd like to continue editing the solution, or false if we should continue
       */
      open: function open(_props) {
        close();

        dialog = $modal.openOnce('challengeSubmittingDialog', {
          template: challengeSubmittingDialogTemplate,
          controller: 'ChallengeSubmittingDialogCtrl as $ctrl',
          backdrop: _props.onlyShowResults ? true : 'static',
          windowClass: 'challenge-submitting-dialog',
          keyboard: !_props.onlyShowResults,
          resolve: {
            props: function props() {
              return _props;
            }
          }
        });

        return dialog.result.finally(function () {
          dialog = null;
        });
      }
    };
  }]).controller('ChallengeSubmittingDialogCtrl', ['$modalInstance', '$scope', '$q', 'props', 'timerHelper', 'appAlert', '$timeout', 'Solution', 'tracker', 'messageHubService', '$log', function ChallengeSubmittingDialogCtrl($modalInstance, $scope, $q, props, timerHelper, appAlert, $timeout, Solution, tracker, messageHubService, $log) {
    var ctrl = this;
    var solution = props.solution,
        saveSolution = props.saveSolution,
        afterSubmit = props.afterSubmit,
        expired = props.expired,
        warn = props.warn,
        nextLabel = props.nextLabel,
        onlyShowResults = props.onlyShowResults;
    var longTimeout = timerHelper.longTimeout,
        runEvery = timerHelper.runEvery;


    ctrl.solution = solution;
    ctrl.issues = solution.updateSubmissionFailedMessages();
    ctrl.failedTests = function () {
      return ctrl.issues.length;
    };
    ctrl.editIsPrimary = function () {
      return ctrl.failedTests() && ctrl.canEdit();
    };
    ctrl.skipTooltip = function () {
      return ctrl.editIsPrimary() ? 'Only move forward if you don\'t believe you can improve on your solution!' : '';
    };
    ctrl.nextLabel = nextLabel;
    ctrl.complete = $modalInstance.close;
    ctrl.cancel = $modalInstance.dismiss;
    var initialRemaining = solution.remainingAttempts();
    ctrl.hasLimits = initialRemaining < Infinity;
    ctrl.canEdit = function () {
      return ctrl.remaining !== 0 && !expired;
    };

    // Listen for auth messages from the embedded ui-modern iframe to capture the Mastra token
    window.addEventListener('message', function (event) {
      var data = event && event.data;
      if (!data || data.type !== 'mastra-auth') {
        return;
      }

      try {
        if (data.token) {
          localStorage.setItem('MASTRA_CHAT_TOKEN', data.token);
        }
      } catch (e) {
        $log.error('Failed to capture Mastra token', e);
      }
    });

    if (onlyShowResults || !ctrl.canEdit) {
      // shows the previous results, if we have any
      ctrl.onlyShowResults = onlyShowResults;
    } else if (initialRemaining === 1 && !expired || initialRemaining !== Infinity && warn) {
      ctrl.showWarning = true;
      ctrl.remaining = initialRemaining;
      ctrl.startSubmission = startSubmission;
    } else {
      startSubmission();
    }

    $timeout(function () {
      return ctrl.dialogReady = true;
    });

    var startTime = Date.now();

    var trackData = function trackData() {
      var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      var time = Math.floor((Date.now() - startTime) / 1000);
      var assessment = solution.assessment || {};
      return _extends({
        id: solution.id,
        value: time, // for GA
        time: time,
        challengeId: solution.challenge.id,
        solutionId: solution.id,
        assessmentId: solution.assessmentId,
        assessmentTitle: assessment.title,
        teamCandidateId: solution.teamCandidateId,
        teamId: solution.teamId,
        team: assessment.team && assessment.team.name
      }, props);
    };

    function archiveMastraConversation() {
      try {
        var token = localStorage.getItem('MASTRA_CHAT_TOKEN');

        $log.info('Archiving Mastra conversation', solution.id, MASTRA_BASE_URL, token);

        if (!MASTRA_BASE_URL || !token || !solution || !solution.id) {
          return;
        }

        var url = MASTRA_BASE_URL + '/solutions/' + solution.id + '/archive-conversation';

        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
          }
        }).catch(function () {
          $log.error('Failed to request archive Mastra conversation');
        });
      } catch (e) {
        $log.error('Failed to archive Mastra conversation', e);
      }
    }

    function startSubmission() {
      ctrl.inProgress = true;
      ctrl.queuing = true;
      ctrl.showWarning = false;
      // we'll attempt to save up to 4 times if we get an already submitting error, waiting 15s
      // between each attempt. After the last one, we just give up and let the candidate continue.
      var submissions = 0;
      var tryToSubmit = function tryToSubmit() {
        submissions++;
        return Solution.submit(solution, {
          data: {
            submissionHash: solution.generateSubmissionHash()
          }
        }).catch(function (resp) {
          if (resp && resp.status === 403 && resp.data && /already submitting/.test(resp.data.reason)) {
            if (submissions > 3) {
              return $q.resolve();
            } else {
              tracker.trackError('Challenge Submit Reattempt', trackData({ submissions: submissions }));
              return $timeout(tryToSubmit, 15000);
            }
          }
          messageHubService.postError(ERROR_CONTEXT.submitSolution, resp);
          return appAlert.modelError(resp);
        });
      };

      saveSolution.afterSave.then(tryToSubmit).then(waitOnSubmission).catch($modalInstance.dismiss);
    }

    var submissionFeedback = solution.challenge.submissionFeedback;

    function waitOnSubmission() {
      ctrl.queuing = false;
      var submissionStartTime = Date.now();

      var errorTimeout = longTimeout(function () {
        if (!solution.isSubmitted()) {
          tracker.trackError('Challenge Submit Timeout', trackData());
          // TODO: is there a better way to handle this?
          $modalInstance.dismiss();
          appAlert.error('There was an error finishing the submission of your solution, please refresh the page and try again.');
        }
      }, 15 * 60 * 1000); // wait up to 15 minutes

      var autoRedirectTimer = void 0;
      var manualRefreshTimer = void 0;
      var alreadyHandledSubmitted = false;

      function checkSubmitted() {
        var source = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'unknown';

        var elapsedTime = Date.now() - submissionStartTime;

        if (!alreadyHandledSubmitted && solution.isSubmitted()) {
          var likelySocket = elapsedTime < 25000 && source === 'socket-watch';

          clearTimers();
          solution.DSRefresh().finally(function () {
            alreadyHandledSubmitted = true;
            tracker.trackEvent('Challenge Submit Completed', trackData({
              detectionMethod: source,
              detectionTimeMs: elapsedTime,
              likelySocket: likelySocket
            }));
            archiveMastraConversation();
            afterSubmit();
            ctrl.submissionFailure = solution.isSubmissionInvalid() || solution.isSubmissionTimeout();
            if (submissionFeedback || ctrl.submissionFailure) {
              ctrl.submissionInvalid = solution.isSubmissionInvalid();
              ctrl.submissionTimeout = solution.isSubmissionTimeout();
              ctrl.remaining = solution.remainingAttempts();
              ctrl.attemptNotUsed = ctrl.hasLimits && ctrl.remaining === initialRemaining;
              ctrl.issues = solution.updateSubmissionFailedMessages();
              ctrl.inProgress = false;
            } else {
              ctrl.complete();
            }
          });
        }
      }

      var reloadSolution = function reloadSolution() {
        solution.DSRefresh({ ignoreLoadingBar: true }).then(function () {
          return checkSubmitted('manual-refresh');
        });
      };

      function clearTimers() {
        longTimeout.cancel(errorTimeout);
        runEvery.cancel(autoRedirectTimer);
        runEvery.cancel(manualRefreshTimer);
      }

      $scope.$on('$destroy', clearTimers);

      $scope.$watch(function () {
        return solution.isSubmitted();
      }, function (newVal, oldVal) {
        if (newVal !== oldVal) {
          checkSubmitted('socket-watch');
        }
      });

      // Also always manually refresh every 30s in case sockets aren't working correctly
      manualRefreshTimer = runEvery(reloadSolution, 30 * 1000);

      $timeout(function () {
        return checkSubmitted('initial-timeout');
      }, 1000);
    }
  }]);

  function isSolutionFilesDiff(oVal, nVal, k) {
    if (k === 'files') {
      return !oVal !== !nVal || oVal.length !== nVal.length || oVal.some(function (of, idx) {
        return !of.encrypted && isDiff(of, nVal[idx]);
      });
    } else {
      return isDiff(oVal, nVal, k);
    }
  }

  var interactEditorButtonTemplate = "<div ng-if=\"$ctrl.interact\" class=\"btn-group dropdown\" ng-class=\"{'private-editing': $ctrl.privateEditing() }\" dropdown dropdown-append-to-body rel=\"group\"> <button class=\"active-editor-button btn-{{$ctrl.btnStyle()}} btn-sm btn-plain-text {{$ctrl.buttonClasses()}}\" ng-click=\"$ctrl.onClick()\" ng-disabled=\"$ctrl.disabled()\" tooltip=\"{{$ctrl.interact.buttonTooltip}}\"> <span> <span class=\"color\" ng-style=\"{ 'background-color': !$ctrl.expired && $ctrl.interact.activeEditor.color }\"></span> <uploaded-image class=\"profile-image\" ng-if=\"!$ctrl.expired && $ctrl.interact.activeEditor.profileImgFileId\" file=\"$ctrl.interact.activeEditor.profileImgFileId\" size=\"20\"></uploaded-image> <span class=\"name\">{{ $ctrl.editorLabel() }}</span> </span> </button> <button class=\"dropdown-toggle btn-{{$ctrl.btnStyle()}} btn-sm dropdown-toggle-caret\" dropdown-toggle ng-disabled=\"$ctrl.disabled()\" tooltip=\"Interact session options…\"> <span class=\"caret\"></span> </button> <ul class=\"dropdown-menu interact-editor-button-menu\" dropdown-menu> <li> <a ng-class=\"{selected: $ctrl.interact.followEditor, 'private-editing': $ctrl.privateEditing() }\" ng-click=\"$ctrl.interact.toggleFollowEditor()\"> Automatically show and focus on changed files </a> </li> <li class=\"disabled\" ng-show=\"$ctrl.privateEditing()\"> <a> (Ignored while you are locally making changes) </a> </li> </ul> </div> ";

  var interactEditorButtonComponentModule = angular.module('Q.components.interact-editor-button', []).component('interactEditorButton', {
    bindings: {
      interact: '<interactService',
      ngDisabled: '<',
      expired: '<'
    },
    controller: 'InteractEditorButtonCtrl',
    template: interactEditorButtonTemplate
  }).controller('InteractEditorButtonCtrl', function InteractEditorButtonCtrl() {
    var ctrl = this;

    ctrl.btnStyle = function () {
      return ctrl.interact && !ctrl.expired && ctrl.interact.cannotEdit ? 'info' : 'default';
    };

    ctrl.privateEditing = function () {
      return ctrl.interact && ctrl.interact.privateEditing && ctrl.interact.hasOtherEditor;
    };

    ctrl.disabled = function () {
      return ctrl.expired || ctrl.ngDisabled || !ctrl.interact || ctrl.interact.isConnecting;
    };

    ctrl.buttonClasses = function () {
      var base = ctrl.interact && ctrl.interact.buttonClasses() || '';
      if (ctrl.expired) {
        base += ' challenge-expired';
      }
      return base;
    };

    ctrl.editorLabel = function () {
      var interact = ctrl.interact;
      var label = 'Initializing…';
      if (interact) {
        if (ctrl.expired) {
          label = isString(ctrl.expired) ? ucFirst(ctrl.expired) : 'Time limit reached';
        } else if (interact.activeEditor) {
          label = interact.isUserActiveEditor ? 'You are editing' : interact.activeEditor.name;
        } else if (interact.isConnecting) {
          label = 'Connecting to Interact…';
        } else if (interact.isDisconnected) {
          label = 'Not Connected';
        } else {
          label = 'No Recent Editor';
        }
      }
      return label;
    };

    ctrl.onClick = function () {
      var interact = ctrl.interact;
      if (interact && !ctrl.disabled()) {
        if (interact.isDisconnected) {
          interact.reconnect();
        } else if (interact.isConnected) {
          // takes over editing
          interact.updateFile();
        }
      }
    };
  });

  var advancedCodeChallengeRouteModule = angular.module('Q.routes.assessments.challenges.advanced-code-challenge', [uiRouterStateModule, solutionModelModule, advancedCodeChallengeProcessServiceModule, appAlertComponentModule, challengeCodeOfConductComponentModule, interactUserListComponentModule, challengeIntroDialogComponentModule, multiFileChallengeServiceModule, multiFileLayoutComponentModule, multiFileToolbarComponentModule, fileTreeComponentModule, codemirrorOptionsComponentModule, codeFileModelModule, onConfirmComponentModule, horizontalCountdownTimerComponentModule, attachComponentModule, attachServiceModule, trackerServiceModule, challengeSubmittingDialogComponentModule, bufferedSaveHelperModule, interactEditorButtonComponentModule, challengeRouteInteractHelperModule]).config(['$stateProvider', function ($stateProvider) {
    $stateProvider.state('assessments.challenges.advancedCodeChallenge', {
      data: {
        bodyClass: 'assessments challenges advanced-code-challenge',
        label: 'Assessment Challenge IDE'
      },
      resolve: {
        config: /* @ngInject */['configService', function config(configService) {
          return configService.load().catch(function () {
            return null;
          });
        }],
        challengeProcess: /* @ngInject */['challenge', 'config', 'process', 'advancedCodeChallengeProcessService', function challengeProcess(challenge, config, process, advancedCodeChallengeProcessService) {
          return challenge ? advancedCodeChallengeProcessService.create(challenge, process) : null;
        }]
      },
      onExit: /* @ngInject */['challengeProcess', function onExit(challengeProcess) {
        return challengeProcess && challengeProcess.onExit();
      }],
      views: {
        '@assessments': {
          template: advancedCodeChallengeTemplate,
          controller: 'AdvancedCodeChallengeCtrl as $ctrl'
        }
      }
    });
  }]).controller('AdvancedCodeChallengeCtrl', ['$scope', 'process', 'challenge', 'challengeProcess', 'Solution', 'appAlert', '$timeout', 'bufferedSaveHelper', '$state', 'multiFileChallengeService', 'codemirrorOptions', 'CodeFile', 'AdvancedCodeSolutionFile', '$q', 'tracker', 'attachDialog', 'attachService', 'challengeSubmittingDialog', 'challengeRouteInteractHelper', '$log', function AdvancedCodeChallengeCtrl($scope, process, challenge, challengeProcess, Solution, appAlert, $timeout, bufferedSaveHelper, $state, multiFileChallengeService, codemirrorOptions, CodeFile, AdvancedCodeSolutionFile, $q, tracker, attachDialog, attachService, challengeSubmittingDialog, challengeRouteInteractHelper, $log) {
    var ctrl = this;

    ctrl.challenge = challenge;
    ctrl.$onInit = function () {
      var challengeItem = process.assessmentResult.getChallengeItem(challenge);
      if (!challengeItem) {
        $log.error('Invariant violation: challengeItem was unexpectedly undefined in AdvancedCodeChallengeCtrl');
        return;
      }

      ctrl.challenge = angular.extend(ctrl.challenge, {
        position: challengeItem.position
      });
    };

    ctrl.process = challengeProcess;
    ctrl.next = process.next;
    ctrl.previous = process.previous;

    var saveSolution = bufferedSaveHelper(challengeProcess.autosave);
    ctrl.saveSolution = saveSolution;

    var hasPersistedLayoutState = function hasPersistedLayoutState(options) {
      var keys = Object.keys(options || {});
      return keys.some(function (key) {
        return (/\$(tabset|tabindex|active)$/i.test(key)
        );
      });
    };

    var hasSavedLayoutOptions = hasPersistedLayoutState(challengeProcess.solution && challengeProcess.solution.options);

    Object.defineProperties(ctrl, {
      readOnly: {
        get: function get$$1() {
          return process.readOnly;
        }
      },
      restricted: {
        get: function get$$1() {
          return process.restricted;
        }
      }
    });

    var challengeConfig = challengeProcess.mfcChallengeConfig();

    var hasChangedSinceSubmission = function hasChangedSinceSubmission() {
      var solution = challengeProcess.solution;
      return !!(solution && !(solution.isSubmitted() && solution.submissionHash === solution.generateSubmissionHash()));
    };

    var hasFailedSubmissionAttempts = function hasFailedSubmissionAttempts() {
      var solution = challengeProcess.solution;
      return solution && solution.hasFailedSubmissionAttempts();
    };

    function afterInitSolution() {
      if (challengeProcess.fd) {
        var data = challengeProcess.fd.data;
        // restore nested models
        if (data.files && data.files.length) {
          data.files = data.files.map(function (val) {
            return AdvancedCodeSolutionFile.createInstance(val);
          });

          CodeFile.configureAccessLevels(data.files);
          var lockedLayout = challengeProcess.challenge.lockedLayout();
          var shouldForceInitialLayout = !lockedLayout && !hasSavedLayoutOptions;
          // also configure the default open files
          AdvancedCodeSolutionFile.configureInitialLayout(data.files, {
            mfcs: ctrl,
            always: shouldForceInitialLayout,
            lockedLayout: lockedLayout
          });
          Solution.configureInitialLayout(ctrl, {
            always: shouldForceInitialLayout,
            lockedLayout: lockedLayout
          });
        }

        multiFileChallengeService.restoreOpenFiles(ctrl);

        challengeProcess.fd.isDiff = isSolutionFilesDiff;
      }

      // assume we've submitted if we can
      ctrl.didSubmit = !hasFailedSubmissionAttempts() && !hasChangedSinceSubmission();
    }

    challengeProcess.afterInitSolution = afterInitSolution;

    ctrl.primaryIsPreview = function () {
      return !ctrl.didPreview && !ctrl.expired;
    };
    ctrl.primaryIsTests = function () {
      return ctrl.didPreview && !ctrl.passedTests && !ctrl.expired;
    };
    ctrl.primaryIsSubmit = function () {
      return ctrl.expired || ctrl.didPreview && (ctrl.passedTests || !ctrl.challenge.candidateTestsEnabled);
    };
    ctrl.showSubmitNext = function () {
      return ctrl.didSubmit && !ctrl.expired && (challenge.submissionFeedback && !hasFailedSubmissionAttempts() || !hasChangedSinceSubmission());
    };

    ctrl.setExpired = function (expired) {
      if (expired) {
        ctrl.expired = ctrl.didSubmit = true;
        ctrl.submitLabel = process.nextLabel();
        ctrl.submitTooltip = (process.nextIsChallenge() ? 'Continue with' : 'Complete') + ' your assessment';
      }
    };

    var updateSubmissionStatus = function updateSubmissionStatus() {
      ctrl.submitLabel = 'Submit';
      ctrl.submitTooltip = 'Submits your code for scoring. You can still modify your solution while there is time left on your assessment.';
      if (challenge.limitSubmissionAttempts) {
        var remaining = challengeProcess.solution ? challengeProcess.solution.remainingAttempts() : challenge.limitSubmissionAttempts;
        if (remaining) {
          if (remaining < 2) {
            ctrl.submitLabel = 'Final Submit';
            ctrl.submitTooltip = 'Submits your code for final scoring. You will no longer be able to modify your solution.';
          } else {
            ctrl.submitLabel = 'Submit (' + remaining + ' remaining)';
          }
        } else {
          ctrl.expirationReason = 'submission limit reached';
          ctrl.setExpired(true);
        }
      }
    };
    updateSubmissionStatus();

    function setupMFCS(useExternalIde) {
      var docsAdditionalArticles = [{
        icon: 'help',
        label: 'Debugging Tips',
        path: 'docs/debugging',
        docsArticleId: 'for-candidates/advanced-challenge-ide/debugging-tips'
      }, {
        icon: 'lang-' + challenge.runConfig.language,
        label: 'Testing Framework',
        path: 'docs/framework',
        docsArticleId: 'reference/languages/' + challenge.runConfig.language + '/' + challenge.runConfig.testFramework
      }];
      if (challenge.externalIdeEnabled()) {
        docsAdditionalArticles.push({
          icon: 'help',
          label: 'Use Your Own IDE',
          path: 'docs/external-ide',
          docsArticleId: 'for-candidates/advanced-challenge-ide/external-ide'
        });
      }

      multiFileChallengeService.create({
        svc: ctrl,
        enableAiAgent: challengeProcess.challenge.showAiAgentToCandidates,
        challenge: challengeProcess.challenge,
        solution: challengeProcess.solution,
        docsArticleId: 'for-candidates/advanced-challenge-ide/summary',
        docsAdditionalArticles: docsAdditionalArticles,
        challengeConfig: challengeConfig,
        toolbarTemplate: advancedCodeChallengeFileToolbar,
        afterFileChanges: saveSolution,
        editChallengeEditorTools: function editChallengeEditorTools(svc) {
          // inserts above help
          insertBefore(svc.challengeEditorTools, svc.docsArticleFile, createInteractParticipantsTab(process));
        }
      });

      // Minor hack to enable using the same toolbar code here and in the hire app
      ctrl.mfc = ctrl;

      // disables add button when using attach
      var addProps = {
        disabled: {
          get: function get$$1() {
            return ctrl.process.usingAttach;
          }
        },
        hidden: {
          get: function get$$1() {
            return process.readOnly;
          }
        }
      };
      Object.defineProperties(ctrl.actions[0], addProps);
      Object.defineProperties(ctrl.actions[1], addProps);

      ctrl.privilegedMode = 'none';

      ctrl.runResponse = ctrl.runResponse || challengeProcess.solution.getRunResponse();

      ctrl.templates = {
        fileMenu: advancedCodeChallengeFileMenu,
        deletedMenu: mfcDeletedFileMenu
      };

      // initializes file drag-and-drop via MultiFileService
      if (!challenge.lockedLayout()) {
        ctrl.setupDrop($scope);
      }
      $scope.$on('$destroy', ctrl.destroy);

      ctrl.didPreview = !challenge.webPreviews() || challengeProcess.solution.attempted() || ctrl.webPreviewFile.tabset && ctrl.webPreviewFile.active;
      ctrl.showWebPreview = function () {
        ctrl.didPreview = true;
        ctrl.clickFile(ctrl.webPreviewFile);
      };
      ctrl.passedTests = challengeProcess.solution.passed();
      var lastRunFileContents = {};
      var updateLastRunFileContents = function updateLastRunFileContents() {
        lastRunFileContents = {};
        var files = challengeProcess.solution.files || [];
        files.forEach(function (f) {
          lastRunFileContents[f.path] = f.contents;
        });
      };
      ctrl.runTests = function () {
        if (ctrl.expired || !challenge.candidateTestsEnabled) return false;
        ctrl.didPreview = true;
        challengeProcess.setResponse({ result: false });
        challengeProcess.startRun();
        return $q.all([
        // track an attempted run
        challengeProcess.autosave('initTest').catch(function () {
          /* ignore errors */
        }),
        // simultaneously start running the code
        ctrl.run().catch(function (e) {
          if (e && e.canceled) {
            challengeProcess.autosave('cancelTest');
          } else {
            challengeProcess.autosave('errorTest');
          }
          return $q.reject(e);
        })]).then(function (_ref) {
          var _ref2 = slicedToArray(_ref, 2),
              response = _ref2[1];

          return challengeProcess.logTest(response);
        }).then(function () {
          ctrl.passedTests = challengeProcess.solution.passed();
          ctrl.didSubmit = false;
          updateLastRunFileContents();
        }).finally(challengeProcess.endRun);
      };
      ctrl.submitSolution = function () {
        var autoNext = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;

        var handleAutoNext = function handleAutoNext() {
          ctrl.$allowNext = true;
          if (autoNext) {
            challengeProcess.fd.finish();
            ctrl.next();
          }
        };

        if (ctrl.expired) {
          ctrl.didSubmit = true;
          handleAutoNext();
          ctrl.endSubmitSolution();
          return $q.resolve();
        }

        if (ctrl.running) {
          // don't run multiple times
          return $q.reject();
        }

        ctrl.startSubmitSolution();
        ctrl.running = ctrl.submitting = true;
        return challengeSubmittingDialog.open({
          solution: challengeProcess.solution,
          saveSolution: saveSolution,
          afterSubmit: function afterSubmit() {
            process.refreshStageStatuses();
            ctrl.endSubmitSolution();
          },
          expired: ctrl.expired,
          warn: !autoNext,
          nextLabel: process.nextActionLabel()
        }).then(function (shouldReview) {
          appAlert.clear();
          ctrl.didSubmit = !shouldReview || !ctrl.expired && !hasFailedSubmissionAttempts() && !hasChangedSinceSubmission();
          if (shouldReview) {
            updateLastRunFileContents();
            // don't allow moving forward
            return $q.reject();
          } else {
            handleAutoNext();
          }
        }).finally(function () {
          ctrl.endSubmitSolution();
          updateSubmissionStatus();
          ctrl.running = ctrl.submitting = false;
        });
      };

      ctrl.showSubmissionFeedback = function () {
        challengeSubmittingDialog.open({
          solution: challengeProcess.solution,
          expired: ctrl.expired,
          onlyShowResults: true,
          nextLabel: process.nextActionLabel()
        });
      };

      var requireSubmit = function requireSubmit() {
        return !ctrl.$allowNext && // special flag that means we've just submitted, just let the user pass
        !process.assessmentResult.isClosed() && // always allow transition if closed
        challengeProcess.solution && // user didn't even start
        challengeProcess.solution.remainingAttempts() && // user still has attempts
        !process.restricted && // we're not in a restricted editing mode
        !ctrl.expired && ( // the challenge didn't expire
        // require submit if not submitted at all OR we have limited attempts and submission failure messages
        !ctrl.didSubmit || challenge.limitSubmissionAttempts && hasFailedSubmissionAttempts() || hasChangedSinceSubmission());
      };

      $scope.$on('$stateChangeStart', function (event, toState, toParams, fromState, fromParams, options) {
        // capture and prevent changing the page if we're moving to another challenge,
        // and we've not already correctly submitted the result
        if (toState.name !== 'error' && toState.name !== 'assessments.welcome' && requireSubmit()) {
          event.preventDefault();
          ctrl.submitSolution(false).then(function () {
            challengeProcess.fd && challengeProcess.fd.finish();
            $state.go(toState, toParams, options);
          });
        }
      });

      $scope.$on('socket:model.updated:Solution:' + challengeProcess.solution.id, updateSubmissionStatus);

      $scope.$watch(function () {
        return process.editMode;
      }, function (newVal, oldVal) {
        if (newVal !== oldVal) {
          ctrl.refreshOnFilesChanged();
        }
      });

      challengeRouteInteractHelper.init(ctrl, process, $scope);

      var startInteract = function startInteract() {
        ctrl.startInteract(challengeProcess.firebaseRef, process.liveInterview);
        challengeProcess.interact = ctrl.interact;
      };
      $scope.$on('liveInterviewStarted', startInteract);
      if (process.liveInterviewActive) startInteract();

      ctrl.on('fileChange', function () {
        ctrl.saveSolution();
        var solution = challengeProcess.solution;
        ctrl.didPreview = ctrl.didPreview || solution.attempted();
        var files = solution.files || [];
        ctrl.hasFileChanges = Object.keys(lastRunFileContents).length !== files.length || files.some(function (f) {
          return !f.encrypted && lastRunFileContents[f.path] !== f.contents;
        });
        ctrl.passedTests = solution.passed() && !ctrl.hasFileChanges;
        ctrl.didSubmit = ctrl.didSubmit && !hasChangedSinceSubmission();
      });

      var nonFileTabMoved = false;
      ctrl.onTabMoved = function (file) {
        if (!nonFileTabMoved && !file.path) {
          nonFileTabMoved = true;
        }
      };

      process.refreshOnFilesChanged = function (force) {
        CodeFile.configureAccessLevels(challengeProcess.solution.files);
        ctrl.refreshOnFilesChanged();
        if (force) {
          multiFileChallengeService.restoreOpenFiles(ctrl);
        }
      };

      ctrl.attachTooltip = function () {
        return challengeProcess.usingAttach ? 'Revert to using the web editor' : 'Open this challenge in any IDE or other editor for solving locally';
      };

      var attachChannelService = void 0;
      var previousFilesLayout = void 0;
      var attachFilesLayout = [[challengeConfig[0], 'topLeft', 0, true], [challengeConfig[1], 'topLeft', 1], [ctrl.runnerFrameFile, 'topRight', 0, true], [ctrl.webPreviewFile, 'bottomRight', 0, true]];

      var setOpenFiles = function setOpenFiles(layout) {
        ctrl.openFiles = layout.filter(function (_ref3) {
          var _ref4 = slicedToArray(_ref3, 1),
              f = _ref4[0];

          return f && !f.hidden;
        }).map(function (_ref5) {
          var _ref6 = slicedToArray(_ref5, 4),
              f = _ref6[0],
              tabset = _ref6[1],
              tabindex = _ref6[2],
              active = _ref6[3];

          f.tabset = tabset;
          f.tabindex = tabindex;
          f.active = active;
          return f;
        });
        if (ctrl.fileLayout) ctrl.fileLayout.resetOpenFiles();
      };

      ctrl.toggleExternalIde = function () {
        var ATTACH_EVENT = 'Attach Connection Status Changed';
        var useAttach = !challengeProcess.usingAttach;
        tracker.trackEvent(ATTACH_EVENT, { state: 'waiting' });
        attachDialog.open(useAttach, ctrl.solution).then(function () {
          challengeProcess.usingAttach = useAttach;
          if (useAttach) {
            tracker.trackEvent(ATTACH_EVENT, { state: 'connected' });
            process.usedAttach = true; // flag for the survey if they even click the button
            if (!nonFileTabMoved) {
              // if the user didn't re-arrange the workspace manually, let's clean up the UI to be more effective
              previousFilesLayout = ctrl.openFiles.map(function (f) {
                return [f, f.tabset, f.tabindex, f.active];
              });
              setOpenFiles(attachFilesLayout);
            } else {
              previousFilesLayout = null;
            }
            challengeProcess.solution.usedExternalIde = true;
            challengeProcess.solution.usingExternalIde = true;
            attachChannelService = attachService.create(ctrl.solution.challengeId, $scope);

            attachChannelService.subscribe('test', ctrl.runTests);
            attachChannelService.subscribe('submit', ctrl.submitSolution.bind(ctrl, true));

            var testsDebounce = void 0;
            var saveChanges = function saveChanges() {
              $scope.$applyAsync(function () {
                ctrl.onFileChange({ force: true });
                if (challengeProcess.autoRunTests) {
                  if (testsDebounce) {
                    $timeout.cancel(testsDebounce);
                  }
                  testsDebounce = $timeout(function () {
                    ctrl.isAutoRun = true;
                    ctrl.runTests();
                  }, 250);
                }
              });
            };

            // due to the delayed nature, creating or updating a file is effectively the same process
            var addOrUpdate = function addOrUpdate(msg) {
              var _msg$data = msg.data,
                  path = _msg$data.path,
                  contents = _msg$data.contents;

              ctrl.createFile({ path: path, contents: contents }, false, false);
              saveChanges();
            };
            attachChannelService.subscribe('file_added', addOrUpdate);
            attachChannelService.subscribe('file_changed', addOrUpdate);

            attachChannelService.subscribe('file_removed', function (_ref7) {
              var data = _ref7.data;

              var f = ctrl.findFile(data.path);
              if (f) {
                ctrl.delete(f);
                saveChanges();
              }
            });

            attachChannelService.subscribe('show_alert', function (_ref8) {
              var data = _ref8.data;

              if (angular.isString(data.message)) {
                appAlert.show(data.message, data.type);
              }
            });
          } else if (attachChannelService) {
            tracker.trackEvent(ATTACH_EVENT, { state: 'disconnected' });
            if (previousFilesLayout) {
              setOpenFiles(previousFilesLayout);
            }
            challengeProcess.solution.usingExternalIde = false;
            attachChannelService.destroy();
          } else {
            // canceled
            tracker.trackEvent(ATTACH_EVENT, { state: 'canceled' });
          }
        }, function () {
          // canceled
          tracker.trackEvent(ATTACH_EVENT, { state: 'canceled' });
        }).catch(function (err) {
          tracker.trackEvent(ATTACH_EVENT, {
            state: 'errored',
            error: err
          });
          return appAlert.modelError.with({
            prefix: 'Unable to initialize Attach:'
          })(err);
        });
      };

      ctrl.closeableFiles = challenge.canCloseTabs() ? true : [ctrl.ideSettingsFile, ctrl.docsArticleFile, ctrl.webPreviewConsoleFile];

      afterInitSolution();

      if (useExternalIde || challengeProcess.solution.usingExternalIde) {
        // open the dialog if we were previously using the IDE
        $timeout(ctrl.toggleExternalIde);
      }
    }

    ctrl.fileMenuClass = function (file) {
      // return the file-access icon for non-read-write files
      var menuClass = '';
      if (file) {
        if (file.$access !== 'readwrite') {
          menuClass += 'file-access-' + file.$access;
        }
      }
      return menuClass;
    };

    var buildCodeOfConductState = function buildCodeOfConductState() {
      var team = process.assessment && process.assessment.team;
      var enabled = !!(team && team.hasFeature('codeOfConduct') && challenge.cocEnabled);
      var rules = enabled ? challenge.codeOfConductRules() : [];
      rules.forEach(function (rule) {
        rule.acknowledged = false;
      });
      return {
        enabled: enabled,
        rules: rules,
        requireAcknowledgement: enabled && challenge.cocRequireAcknowledgement !== false
      };
    };
    ctrl.codeOfConduct = buildCodeOfConductState();
    ctrl.codeOfConductAcknowledged = function () {
      if (!ctrl.codeOfConduct.enabled) return true;
      if (!ctrl.codeOfConduct.requireAcknowledgement) return true;
      return ctrl.codeOfConduct.rules.every(function (rule) {
        return rule.acknowledged;
      });
    };
    ctrl.unableToBeginChallenge = function () {
      return ctrl.beginning || !ctrl.codeOfConductAcknowledged();
    };

    if (challengeProcess.solution) {
      setupMFCS();
    } else {
      ctrl.codemirrorOpts = codemirrorOptions;
      ctrl.showIntro = true;
      ctrl.introDialogTemplate = introDialogTemplate$2;
      ctrl.beginChallenge = function (useExternalIde) {
        if (ctrl.unableToBeginChallenge()) return;
        ctrl.beginning = true;
        challengeProcess.beginChallenge().then(function () {
          process.addSolution(challengeProcess.originalSolution);
          hasSavedLayoutOptions = hasPersistedLayoutState(challengeProcess.solution && challengeProcess.solution.options);
          setupMFCS(useExternalIde);
          ctrl.showIntro = false;
        }).finally(function () {
          $timeout(function () {
            // delayed to let dialog close
            ctrl.beginning = false;
          }, 100);
        });
      };
    }

    challengeProcess.resaveAfterFail = function () {
      return saveSolution('resaveAfterFail');
    };

    challengeProcess.onEnter($scope, ['AdvancedCodeSolutionFile']);
  }]);

  var challengesRouteModule = angular.module('Q.routes.assessments.challenges', [uiRouterStateModule, codeChallengeRouteModule, quizChallengeRouteModule, advancedCodeChallengeRouteModule, modalComponentModule, stateHelperModule, accountServiceModule]).config(['$stateProvider', function ($stateProvider) {
    $stateProvider.state('assessments.challenges', {
      url: '/challenges/{challengeId:ObjectOrQId}',
      resolve: {
        challenge: /* @ngInject */['accountService', 'process', 'Challenge', '$stateParams', 'stateHelper', function challenge(accountService, process, Challenge, $stateParams, stateHelper) {
          return accountService.loggedIn && Challenge.fetch($stateParams.challengeId, {
            bypassCache: true
          }).catch(function () {
            return stateHelper.redirectInResolve('assessments.welcome');
          });
        }],
        processIsReady: /* @ngInject */['accountService', 'process', function processIsReady(accountService, process) {
          return accountService.loggedIn && process.whenAssessmentResultReady();
        }]
      },
      controller: ['challenge', '$state', 'processIsReady', 'process', 'config', 'proctoringSatisfied', function controller(challenge, $state, processIsReady, process, config, proctoringSatisfied) {
        if (process.invitationIssue || process.significantTimeDelay || !proctoringSatisfied()) {
          // if there is an issue go to the welcome screen where we can tell them about it
          return $state.go('assessments.welcome');
        } else if (process.isFinished()) {
          return process.next();
        } else if (process.canEdit && challenge) {
          return $state.go('assessments.challenges.' + challenge.typeState());
        } else {
          // no challenge, unknown type, or assessment is not in an editable state
          return $state.go('assessments.welcome');
        }
      }]
    });
  }]);

  var secondsToTimeFilterModule = angular.module('Q.filters.seconds-to-time', [momentModule]).filter('secondsToTime', ['moment', function (moment$$1) {
    return function (seconds) {
      var d = moment$$1.duration(seconds || 0, 's');
      return Math.floor(d.asHours()) + ':' + padNum(d.minutes()) + ':' + padNum(d.seconds());
    };
  }]);

  // matches any protocol
  var PROTOCOL_REGEX = /^[a-z]+:(\/*)(?:www\.)?/i;

  // used to extract the domain ($1) from a URL.  Matches any protocol or protocol-less domain,
  // also matches bare domains.  Drops any path content.
  var DOMAIN_REGEX = /^(?:(?:[a-z]+:)?\/\/)?(?:www\.)?([^\/]+).*/i;

  var safeUrlFilterModule = angular.module('Q.filters.safe-url-filter', []).constant('safeUrl', function (s) {
    if (s) {
      s = s.toString();
      if (!PROTOCOL_REGEX.test(s)) {
        s = 'http://' + s.replace(/^[\/:]+/, '');
      }
    }
    return s;
  }).filter('safeUrl', ['safeUrl', function (safeUrl) {
    return safeUrl;
  }]).filter('simpleUrl', function () {
    return function (s) {
      var domainOnly = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

      if (s) {
        if (domainOnly) {
          return s.toString().replace(DOMAIN_REGEX, '$1');
        } else {
          return s.toString().replace(PROTOCOL_REGEX, '');
        }
      } else {
        return s;
      }
    };
  });

  var RISK_LEVEL_TOOLTIPS = {
    low: 'Similar solutions have been found, but there is a low risk of cheating.',
    medium: 'Similar solutions have been found. It is recommended to look further into the solution.',
    high: 'Very similar or exact solutions have been found that likely indicate this code was not original.'
  };

  /**
   * @docType shared
   * @category directive
   * @name Score-Label
   * @description
   * A directive that displays a candidates score on a test
   * @returns Directive Definition Object {object}
   */
  var scoreLabelComponentModule = angular.module('Q.components.score-label', [permissionsServiceModule]).directive('scoreLabel', ['permissionsService', function (permissionsService) {
    return {
      restrict: 'E',
      scope: {
        value: '<',
        unsubmitted: '<',
        lowerIsBetter: '<',
        similarity: '<',
        excludeFromAccuracy: '<',
        onSimilarityClick: '&'
      },
      template: '<span class="label score {{ scoreClass() }}" ng-bind="score()"></span><span class="label score-risk score-risk-{{riskLevel()}}" ng-if="riskLevel()" tooltip="{{riskTooltip()}}" ng-click="onSimilarityClick()"></span>',
      link: function link(scope) {
        function hasScore() {
          return scope.value !== null && scope.value !== undefined && !isNaN(parseFloat(scope.value));
        }

        scope.score = function () {
          return hasScore() ? Math.floor(scope.value || 0) + '%' : '';
        };
        scope.scoreClass = function () {
          var className = 'score-';
          if (scope.excludeFromAccuracy) {
            className += 'excluded';
          } else if (!hasScore()) {
            className += 'none';
          } else if (scope.unsubmitted) {
            className += 'unsubmitted';
          } else {
            className += Math.min(100, Math.floor((scope.value || 0) / 10) * 10);
          }
          if (scope.lowerIsBetter) {
            className += ' lower-is-better';
          }
          if (scope.riskLevel()) {
            className += ' score-has-risk';
          }
          return className;
        };

        scope.riskLevel = function () {
          var similarity = scope.similarity;
          var riskLevel = similarity && similarity.riskLevel;
          return permissionsService.feature('codeSimilarity') && riskLevel !== 'none' && riskLevel;
        };

        scope.riskTooltip = function () {
          return RISK_LEVEL_TOOLTIPS[scope.riskLevel()];
        };
      }
    };
  }]);

  var assessmentSubmittingDialogTemplate = "<div class=\"modal-header\"> <h4 class=\"modal-title\"> <span ng-show=\"$ctrl.inProgress\">Your {{$ctrl.noun.assessment}} is being processed…</span> <span ng-hide=\"$ctrl.inProgress\">Your {{$ctrl.noun.assessment}} has been processed!</span> </h4> </div> <div class=\"modal-body\"> <p class=\"alert alert-info timer-ran-out\" ng-if=\"$ctrl.timerRanOut\"> Your {{$ctrl.noun.assessment}} has been auto-submitted as the time limit has been reached. </p> <busy-indicator ng-if=\"$ctrl.inProgress\"></busy-indicator> <div ng-if=\"$ctrl.inProgress\" class=\"in-progress\"> <p>Please wait for the next steps.</p> </div> <div ng-if=\"!$ctrl.inProgress\" class=\"submitted\"> <div ng-if=\"!$ctrl.assessmentResult.submissionMessage\" class=\"default-message\"> This {{$ctrl.noun.assessment}} has been successfully submitted. </div> <div ng-if=\"$ctrl.assessmentResult.submissionMessage\" class=\"submission-message\" markdown=\"$ctrl.assessmentResult.submissionMessage\"></div> <div class=\"next-steps\" ng-show=\"$ctrl.assessmentResult.hasPostAssessmentRedirect()\"> <div ng-if=\"$ctrl.assessmentResult.retrySchedule && !$ctrl.assessmentResult.submissionMessage\"> <p> Click <strong>Try Again</strong> below to attempt your {{$ctrl.noun.assessment}} again. </p> <p class=\"help-block\" ng-if=\"$ctrl.assessmentResult.retrySchedule.action === 'retake'\"> Your {{$ctrl.noun.assessment}} will be reset and you can retake the {{$ctrl.noun.assessment}} from scratch. </p> <p class=\"help-block\" ng-if=\"$ctrl.assessmentResult.retrySchedule.action !== 'retake'\"> You will be able to continue your {{$ctrl.noun.assessment}} from where you left off. </p> </div> <div ng-if=\"$ctrl.assessmentResult.nextAssessmentId\"> <p> Click <strong>Continue</strong> below to move on to the next {{$ctrl.noun.assessment}}. </p> <p class=\"help-block\"> We also sent you an email to the next part, if you choose to take it later. </p> </div> <div ng-if=\"$ctrl.assessmentResult.$redirectUrl\"> <p>Click <strong>Continue</strong> below to go to the next step.</p> </div> <p> You may also <a href ng-click=\"$ctrl.close()\">close this dialog</a> to review your results or leave comments. </p> </div> </div> </div> <div class=\"modal-footer\" ng-class=\"{ 'dialog-ready': $ctrl.dialogReady }\" ng-hide=\"$ctrl.inProgress\"> <button class=\"btn-primary\" ng-click=\"$ctrl.complete()\"> <span ng-if=\"$ctrl.assessmentResult.hasPostAssessmentRedirect()\"> {{$ctrl.assessmentResult.retrySchedule ? 'Try Again' : 'Continue'}} <span class=\"auto-redirect-count-down\" ng-show=\"$ctrl.autoRedirectDelay >= 0\"> in {{ $ctrl.autoRedirectDelay }} seconds </span> </span> <span ng-if=\"!$ctrl.assessmentResult.hasPostAssessmentRedirect()\"> Close </span> </button> </div> ";

  var assessmentSubmittingDialogComponentModule = angular.module('Q.components.assessment-submitting-dialog', [timerHelperModule, safeUrlFilterModule, modalComponentModule, appAlertComponentModule, busyIndicatorComponentModule, trackerServiceModule]).factory('assessmentSubmittingDialog', ['$modal', '$q', function assessmentSubmittingDialog($modal, $q) {
    var dialog = null;

    function close() {
      if (dialog) {
        dialog.dismiss();
      }
      dialog = null;
    }

    return {
      close: close,
      open: function open(_assessmentResult) {
        var _timerRanOut = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

        close();
        if (_assessmentResult.isClosed() && !_assessmentResult.isSubmitting() && !_assessmentResult.hasPostAssessmentActions() && !_timerRanOut) {
          // no need to show the dialog if we have nothing to do
          return $q.reject();
        }

        dialog = $modal.openOnce('assessmentSubmittingDialog', {
          template: assessmentSubmittingDialogTemplate,
          controller: 'AssessmentSubmittingDialogCtrl as $ctrl',
          backdrop: 'static',
          windowClass: 'assessment-submitting-dialog',
          keyboard: false,
          resolve: {
            assessmentResult: function assessmentResult() {
              return _assessmentResult;
            },
            timerRanOut: function timerRanOut() {
              return _timerRanOut;
            }
          }
        });

        return dialog.result.finally(function () {
          dialog = null;
        });
      }
    };
  }]).controller('AssessmentSubmittingDialogCtrl', ['$modalInstance', '$scope', 'AssessmentResult', 'assessmentResult', 'timerRanOut', 'timerHelper', 'appAlert', '$timeout', 'tracker', function AssessmentSubmittingDialogCtrl($modalInstance, $scope, AssessmentResult, assessmentResult, timerRanOut, timerHelper, appAlert, $timeout, tracker) {
    var ctrl = this;
    var longTimeout = timerHelper.longTimeout,
        runEvery = timerHelper.runEvery;
    // lock in post-submission changes

    ctrl.assessmentResult = AssessmentResult.createInstance(assessmentResult);
    ctrl.timerRanOut = timerRanOut;
    ctrl.inProgress = assessmentResult.isInProgress();
    var assessment = assessmentResult.assessment || {};
    ctrl.noun = {
      assessment: assessment.nounLower || 'assessment',
      Assessment: assessment.nounUpper || 'Assessment'
    };

    var startTime = Date.now();

    var trackData = function trackData() {
      var time = Math.floor((Date.now() - startTime) / 1000);
      return {
        id: assessmentResult.id,
        value: time, // for GA
        time: time,
        assessmentResultId: assessmentResult.id,
        assessmentId: assessmentResult.assessmentId,
        assessmentTitle: assessment.title,
        teamCandidateId: assessmentResult.teamCandidateId,
        teamId: assessmentResult.teamId,
        team: assessment.team && assessment.team.name
      };
    };

    var errorTimeout = longTimeout(function () {
      if (!assessmentResult.isSubmitted()) {
        tracker.trackError('Assessment Submit Timeout', trackData());
        ctrl.close();
        appAlert.error('There was an error finishing the submission of your assessment, please refresh the page and try again.');
      }
    }, 5 * 60 * 1000); // wait up to 5 minutes

    var autoRedirectTimer = void 0;
    var manualRefreshTimer = void 0;
    var completeListener = void 0;

    ctrl.complete = function () {
      return $modalInstance.close();
    };

    ctrl.close = function () {
      return $modalInstance.dismiss();
    };

    function checkSubmitted() {
      ctrl.inProgress = assessmentResult.isInProgress();
      if (assessmentResult.isSubmitted()) {
        if (completeListener) {
          completeListener();
          completeListener = false;
        }
        // lock in post-submission changes
        ctrl.assessmentResult = AssessmentResult.createInstance(assessmentResult);
        longTimeout.cancel(errorTimeout);
        tracker.trackEvent('Assessment Submit Completed', trackData());
        runEvery.cancel(manualRefreshTimer);
        if (!assessmentResult.hasPostAssessmentActions()) {
          ctrl.complete();
        } else if (assessmentResult.submissionMessage) {
          ctrl.autoRedirectDelay = -1;
        } else {
          ctrl.autoRedirectDelay = 60;
          autoRedirectTimer = runEvery(function () {
            ctrl.autoRedirectDelay--;
            if (ctrl.autoRedirectDelay < 1) {
              ctrl.complete();
            }
          }, 1000);
        }
      }
    }

    var reloadResult = function reloadResult() {
      assessmentResult.DSRefresh({ ignoreLoadingBar: true }).then(checkSubmitted);
    };

    $scope.$on('$destroy', function () {
      longTimeout.cancel(errorTimeout);
      runEvery.cancel(autoRedirectTimer);
      runEvery.cancel(manualRefreshTimer);
      if (completeListener) {
        completeListener();
        completeListener = false;
      }
    });

    if (ctrl.inProgress) {
      completeListener = $scope.$watch(function () {
        return assessmentResult.state;
      }, checkSubmitted);

      // Also always manually refresh every 30s in case sockets aren't working correctly
      manualRefreshTimer = runEvery(reloadResult, 30 * 1000);

      checkSubmitted();
    }

    $timeout(function () {
      return ctrl.dialogReady = true;
    });
  }]);

  var reviewTemplate = "<article class=\"review-details\" ng-show=\"::assessments.process.assessmentResult\"> <section> <h2> Review{{ (!assessments.process.restricted && review.canEdit) ? ' & Final\n      Submission' : '' }} </h2> <div ng-if=\"!assessments.isSample()\"> <div ng-if=\"!assessments.process.restricted && review.canEdit\"> <div class=\"alert alert-danger\" ng-if=\"!review.isSafeToSubmit()\"> <p> <strong>Your {{ assessments.assessment.nounLower }} is incomplete.</strong> </p> <p ng-if=\"!review.process.isSafeToSubmit()\"> Please attempt each challenge before submitting. </p> <p ng-if=\"review.hasUnusedAttempts\"> At least one of your incomplete challenges can still be worked on. </p> <p ng-if=\"review.hasUnusedAttempts\"> <a class=\"btn-danger btn-sm btn-plain-text\" ui-sref-no-link=\"{{review.incompleteStage.state}}({{ review.incompleteStage.stateParams }})\" ui-sref-disable-on-transition=\"true\"> Continue working on this challenge </a> </p> </div> <div class=\"alert alert-warning\" ng-if=\"review.isSafeToSubmit() && !review.process.isFullyComplete()\"> <p> <strong>Some of your challenges are incomplete.</strong> </p> <p> Challenges marked incomplete may use additional scoring beyond the provided tests. Please review the specific requirements for each challenge. </p> <p>You can still submit if you are done.</p> </div> <div class=\"alert assessment-completed-alert\" ng-if=\"review.isSafeToSubmit() && review.process.isFullyComplete()\"> <div class=\"text\"> <h3>Great Job!</h3> <p> Everything looks ready to be scored. This is your last opportunity to review your work and leave any final comments. </p> <p> Once you submit your {{ assessments.assessment.nounLower }} it will be automatically scored and {{\n              assessments.assessment.team.name }} will be notified of the results. </p> </div> <div class=\"buttons\" sticky-header> <button class=\"btn-primary\" ng-disabled=\"review.submitting\" ng-click=\"review.submit()\"> Submit My {{ assessments.assessment.nounUpper }} </button> </div> </div> <div ng-if=\"assessments.hasCodeChallenges() || !review.isSingleChallengeAssessment\" class=\"help-block help-block-tip\"> <p ng-if=\"assessments.hasCodeChallenges()\"> Make sure your solutions pass as many test cases as possible, with best practices and production-level code. </p> <p ng-if=\"!review.isSingleChallengeAssessment\"> If you want to leave any comments on your solutions, you still can by clicking the arrow on the left below, and editing <em>Your Notes</em>. </p> </div> </div> <div ng-if=\"!assessments.process.restricted && !review.canEdit\"> <div class=\"successful-submission alert alert-info\" ng-if=\"review.submitting\"> Submitting your {{ assessments.assessment.nounLower }}. </div> <div class=\"successful-submission alert alert-success\" ng-if=\"review.didSubmit\"> You have successfully submitted your {{\n          assessments.assessment.nounLower }}. </div> <div ng-if=\"!review.submitting && !review.didSubmit\"> <div class=\"unsuccessful-submission alert alert-info\" ng-if=\"!review.timerRanOut()\"> This {{ assessments.assessment.nounLower }} can no longer be edited. </div> <div class=\"automatic-submission alert alert-info\" ng-if=\"review.timerRanOut()\"> Your {{ assessments.assessment.nounLower }} was automatically submitted, and can no longer be edited. <br>Some scores may be updated later via an automated process. </div> </div> </div> </div> </section> <div class=\"alert assessment-completed-alert upsell\" ng-if=\"assessments.isDemo()\"> <div class=\"text\"> <h3>Ready to use Qualified with <strong>your</strong> team?</h3> <p> Now that you've tried the <strong>{{ assessments.assessment.title }} Demo</strong>, sign up for a free trial to start using Qualified today, or contact our sales team for more information. </p> </div> <div class=\"buttons\"> <a ng-href=\"/signup?{{ review.landingParams }}\" class=\"btn-primary\">Free Trial Sign Up</a> </div> </div> <div class=\"alert assessment-completed-alert practice-completion\" ng-if=\"assessments.isPractice()\"> <div class=\"text\"> <h3>You're ready for your real assessment!</h3> <p> Thanks for running through our <strong>Practice Assessment</strong>! Now you can return to your full assessment. </p> </div> <div class=\"buttons\"> <a ng-href=\"{{ $stateParams.returnTo }}\" class=\"btn-primary\" ng-if=\"assessments.canClose()\" ng-click=\"assessments.close($event)\"> Return to Your Assessment </a> </div> </div> <section class=\"solution-review\" ng-if=\"!review.isSingleChallengeAssessment || review.canReviewSolutions()\"> <table class=\"app-table no-animate\"> <tbody> <tr ng-repeat-start=\"stage in review.process.stages.challenges\" class=\"challenge-summary stage-{{ review.getStatus(stage) }}\" ng-class=\"{ expanded: stage.expanded, collapsed: !stage.expanded }\"> <td class=\"stage-expander\"> <a href ng-click=\"stage.expanded = !stage.expanded\" ng-if=\"review.canReviewSolution(stage)\" tooltip=\"Expand to view code solutions and edit your notes.\"></a> </td> <td class=\"status\"></td> <td class=\"solution-title\" ng-if=\"review.canEdit\"> <a ui-sref-no-link=\"{{stage.state}}({{ stage.stateParams }})\" ui-sref-disable-on-transition=\"true\" tooltip=\"Click to {{stage.isCompleted() ? 'review' : 'edit'}} this challenge\"> Challenge #{{ $index + 1 }} </a> </td> <td class=\"solution-title\" ng-if=\"!review.canEdit\"> <a href ng-click=\"stage.expanded = true\"> Challenge #{{ $index + 1 }} </a> </td> <td class=\"solution-tests\" ng-bind=\"review.getSummary(stage)\"></td> </tr> <tr ng-repeat-end ng-if=\"stage.expanded && review.canReviewSolution(stage)\" class=\"solution-details\"> <td></td> <td colspan=\"5\"> <tabset> <tab default-tab=\"true\" ng-if=\"stage.solution.isClassicCode() && review.canReviewSolutions()\"> <tab-heading> <span class=\"icon-code\"></span> Solution </tab-heading> <div code-highlight=\"stage.solution.code\" language=\"stage.solution.language\" line-number-start=\"1\"></div> </tab> <tab ng-if=\"stage.solution.testCases && review.canReviewSolutions()\"> <tab-heading> <span class=\"icon-tasks\"></span> Your Tests </tab-heading> <div code-highlight=\"stage.solution.testCases\" language=\"review.frameworkLanguage(stage.solution)\" line-number-start=\"1\"></div> </tab> <tab default-tab=\"true\" ng-if=\"stage.solution.isAdvancedCode() && review.canReviewSolutions()\"> <tab-heading> <span class=\"icon-code\"> Your Solution</span> </tab-heading> <div> <file-list-viewer files=\"stage.solution.files\"></file-list-viewer> </div> </tab> <tab default-tab=\"true\" ng-if=\"stage.solution.isQuiz() && review.canReviewSolutions()\"> <tab-heading> <span class=\"icon-quiz\"></span> Your Answers </tab-heading> <div> <quiz-challenge-questions mode=\"review\" candidate-review=\"true\" solution=\"stage.solution\"></quiz-challenge-questions> </div> </tab> <tab> <tab-heading> <span class=\"icon-note\"></span> Your Notes {{assessments.process.editModeRestricted ? '' : '(Editable)'}} </tab-heading> <div> <div class=\"alert alert-warning\" ng-if=\"stage.solution.notesLocked()\"> The time limit has passed, so your notes can no longer be edited. </div> <markdown-editor ng-model=\"stage.solution.candidateNotes\" ng-model-options=\"{ updateOn: 'default blur', debounce: { 'default': 1000, 'blur': 0 } }\" ng-readonly=\"assessments.process.editModeRestricted || stage.solution.notesLocked()\" ng-change=\"review.saveSolution(stage.solution)\" placeholder=\"{{ stage.solution.challenge.candidateNotesPlaceholder(stage.solution.isCode() ? 'solution' : 'answers') }}\" auto-expand=\"true\"></markdown-editor> </div> </tab> </tabset> </td> </tr> </tbody> </table> </section> <section class=\"comments\"> <h3>{{ review.commentsLabel() }}</h3> <form> <div class=\"form-group\"> <markdown-editor ng-model=\"review.assessmentResult.candidateRemarks\" ng-disabled=\"review.submitting\" ng-readonly=\"assessments.process.editModeRestricted\" ng-change=\"review.modifyComments()\" placeholder=\"Optional comments about the {{ assessments.assessment.nounLower }} process, challenges, etc.\" collapse-on-blur=\"true\" collapse-on-empty=\"true\" auto-expand=\"true\"></markdown-editor> </div> </form> <div class=\"submit-comments\" ng-show=\"review.assessmentResult.candidateRemarks && review.modifiedComments && !review.canEdit && !assessments.process.editModeRestricted\"> <a href class=\"btn-primary\" ng-click=\"review.update()\" ng-disabled=\"review.submitting\"> Submit Comments </a> </div> <div class=\"help-block\" ng-show=\"review.remarksSubmitted && !review.modifiedComments && !review.canEdit && !review.submitting\"> Thank you, your comments have been received! You may still edit them if you would like. </div> </section> <div ng-if=\"!review.canEdit && !assessments.process.isEmbedded\"> <p ng-if=\"!assessments.isTeamMember() && !assessments.isSample()\"> You can no longer make changes to your challenge submissions. You can manage your account settings or log out if you are done. </p> <p ng-if=\"assessments.isTeamMember()\"> Thanks for testing this {{ assessments.assessment.nounLower }}! You can now review your results. <br>If you want to re-try the {{ assessments.assessment.nounLower }}, use the button below to restart it. </p> <p ng-if=\"assessments.isSample()\"> If you want to try the {{ assessments.assessment.nounLower }} again, use the button below to restart it. </p> </div> <section ng-if=\"review.showActions\"> <div class=\"buttons\"> <button ng-show=\"review.canEdit && !review.topSubmit\" class=\"btn-{{ assessments.isSample() ? 'default' : 'primary btn-primary-alt' }}\" ng-disabled=\"review.submitting || !review.isSafeToSubmit()\" ng-click=\"review.submit()\"> Submit {{ assessments.isSample() ? 'Feedback' : \"My \" +\n        assessments.assessment.nounLower }} </button> <button class=\"btn-primary\" ng-if=\"!review.canEdit && review.hasPostAssessmentRedirect && !review.submitting\" ng-click=\"assessments.performPostAssessmentAction()\"> {{review.assessmentResult.retrySchedule ? 'Try Again' : ('Continue Your\n        '+assessments.assessment.nounUpper)}} </button> <a class=\"btn-primary\" ng-if=\"assessments.isTeamMember() && !review.canEdit && !assessments.process.isEmbedded\" ng-href=\"{{ assessments.hireUrl }}/candidates/all/{{ assessments.process.assessmentResult.teamCandidateId }}?assessmentId={{ assessments.assessment.id }}\" ng-click=\"assessments.close($event)\" ng-disabled=\"review.submitting\"> Review {{ assessments.assessment.nounUpper }} Results </a> <a ng-href=\"{{ $stateParams.returnTo }}\" class=\"btn-{{ review.canEdit ? 'default' : 'primary' }}\" ng-if=\"assessments.isPractice() && assessments.canClose()\" ng-click=\"assessments.close($event)\" ng-disabled=\"review.submitting\"> Return to Your {{ assessments.assessment.nounUpper }} </a> <a href class=\"btn-info\" ng-if=\"!review.canEdit && (assessments.isTeamMember() || assessments.isSample())\" ng-click=\"assessments.restart()\" ng-disabled=\"review.submitting\"> {{ assessments.isPractice() ? 'Practice' : assessments.isDemo() ? 'Try\n        the Demo' : 'Test' }} Again </a> <a href=\"/\" class=\"btn-default\" ng-if=\"!assessments.process.isEmbedded && assessments.isDemo() && !assessments.canClose()\" ng-disabled=\"review.submitting\"> Return to the Qualified Home Page </a> <a href class=\"btn-default\" ng-if=\"!assessments.process.isEmbedded && assessments.canClose() && !assessments.isPractice()\" ng-click=\"assessments.close($event)\" ng-disabled=\"review.submitting\"> Close {{ assessments.fromSetup ? 'and Return to Setup' : '' }} </a> <a ng-if=\"!review.canEdit && !assessments.isTeamMember()\" href class=\"btn-default\" ng-click=\"review.process.logout()\" ng-disabled=\"review.submitting\"> Log Out </a> </div> </section> </article> ";

  var fileListViewerTemplate = "<div class=\"file-list-viewer-wrapper {{$root.$ideDarkClass}}\"> <div class=\"form-control-select\"> <select ng-options=\"file as file.path for file in $ctrl.files | orderBy:'path'\" ng-model=\"$ctrl.selectedFile\" ng-change=\"$ctrl.selectFile($ctrl.selectedFile)\"></select> </div> <file-tree files=\"$ctrl.files\" changed-files=\"$ctrl.changedFiles\" selected-file=\"$ctrl.selectedFile\" on-file-click=\"$ctrl.selectFile(file)\" auto-expand-all=\"true\" review-mode=\"$ctrl.reviewMode\"></file-tree> <div class=\"file-contents\"> <div ng-if=\"$ctrl.isMarkdown\" markdown=\"$ctrl.selectedFile.contents\"></div> <div ng-if=\"$ctrl.isImage\" class=\"file-contents-image\"> <img ng-src=\"{{ $ctrl.selectedFile.contents }}\" alt=\"{{ $ctrl.selectedFile.path }}\"> </div> <ui-codemirror ng-if=\"$ctrl.selectedFile && !$ctrl.isMarkdown && !$ctrl.isImage\" ui-codemirror-opts=\"$ctrl.codemirrorOpts\" ng-model=\"$ctrl.selectedFile.contents\" readonly=\"readonly\"></ui-codemirror> <div ng-if=\"!$ctrl.selectedFile && $ctrl.files.length\" class=\"no-file\"> Select a file to view its contents </div> <div ng-if=\"!$ctrl.selectedFile && !$ctrl.files.length\" class=\"no-file\"> No files to view </div> </div> </div> ";

  var fileListViewerComponentModule = angular.module('Q.components.file-list-viewer', [codeFileModelModule, fileTreeComponentModule, markdownComponentModule, codemirrorComponentModule, codemirrorOptionsComponentModule]).component('fileListViewer', {
    bindings: {
      files: '<',
      changedFiles: '<',
      reviewMode: '<'
    },
    controller: 'FileListViewerCtrl',
    template: fileListViewerTemplate
  }).controller('FileListViewerCtrl', ['codemirrorOptions', 'AdvancedCodeSolutionFile', function FileListViewerCtrl(codemirrorOptions, AdvancedCodeSolutionFile) {
    var ctrl = this;

    ctrl.$onInit = function () {
      if (!ctrl.files || !ctrl.files.includes(ctrl.selectedFile)) {
        var file = null;
        // pick something else
        if (ctrl.files && ctrl.files.length) {
          if (ctrl.changedFiles && ctrl.changedFiles[0]) {
            file = ctrl.changedFiles[0];
          } else {
            file = AdvancedCodeSolutionFile.initialLayout(ctrl.files)[0] || ctrl.files[0];
          }
        }
        ctrl.selectFile(file);
      }

      ctrl.$onChanges = ctrl.$onInit;
    };

    ctrl.selectFile = function (file) {
      if (file && (file.disabled && !ctrl.reviewMode || file.directory)) return;
      ctrl.selectedFile = file;
      ctrl.isMarkdown = file && /\.(md|markdown)$/.test(file.path);
      ctrl.isImage = file && isImageFile(file);
      ctrl.codemirrorOpts = file && codemirrorOptions.createOpts(file.path);
    };
  }]);

  var stickyHeaderComponentModule = angular.module('Q.components.sticky-header', [scrollMonitorServiceModule]).directive('stickyHeader', ['scrollMonitorService', '$timeout', '$window', function (scrollMonitorService, $timeout, $window) {
    return {
      restrict: 'A',
      link: function link(scope, $elem, attr) {
        if (attr.stickyHeaderNonMobile !== undefined && angular.element($window).offsetWidth() < 768) {
          return;
        }
        $timeout(function () {
          // create clone of the element to act as a placeholder when we scroll off-screen
          var elem = $elem[0];
          var $placeholder = $elem.clone();
          var placeholder = $placeholder[0];
          $placeholder.addClass('sticky-header-placeholder-inactive');
          $elem.addClass('sticky-header');
          $elem.after($placeholder);

          var viewport = attr.stickyHeader || $elem.scrollableParent();

          var lastTop = null;
          var lastLeft = null;
          var lastActive = false;

          var isTable = elem.nodeName === 'THEAD' || elem.nodeName === 'TR';

          var scrollWatcher = scrollMonitorService.addListener(viewport, function (event, data) {
            var nonScrollEvent = event && event.type !== 'scroll';
            var elTop = data.elementTop;
            if (lastActive) {
              // the element top won't be useful if we were already active
              if (nonScrollEvent) {
                // if we've had a DOM change, lastTop may no longer be correct
                elTop = $placeholder.offsetTop() - $placeholder.scrollableParent().offsetTop();
              } else {
                // otherwise, if just scrolling, we can safely use the last known top
                elTop = lastTop;
              }
            }
            var elLeft = lastActive ? lastLeft : $elem.offsetLeft();
            var active = data.viewportScrollTop >= elTop;
            var shouldUpdate = lastActive !== active;

            // toggle classes if we're changing state
            if (shouldUpdate) {
              $elem.toggleClass('sticky-header-active', active);
              $placeholder.toggleClass('sticky-header-placeholder-inactive', !active);
              $placeholder.toggleClass('sticky-header-placeholder-active', active);

              $elem.css('top', active ? data.viewportTop + 'px' : null);
              $elem.css('left', active ? elLeft + 'px' : null);
              $elem.css('margin-top', active ? '0' : null);
            }
            if (shouldUpdate || active) {
              if (attr.stickyHeaderSetWidth !== 'false') {
                $elem.css('width', active ? placeholder.offsetWidth + 'px' : null);
              }
              if (attr.stickyHeaderSetHeight !== 'false') {
                $elem.css('height', active ? placeholder.offsetHeight + 'px' : null);
              }
            }

            // update table cells everytime if active, or if changing states
            if (isTable && (shouldUpdate || active)) {
              var origCells = elem.querySelectorAll('td,th');
              var phCells = placeholder.querySelectorAll('td,th');
              angular.forEach(phCells, function (cell, idx) {
                var width = active ? cell.offsetWidth + 'px' : null;
                var height = active ? cell.offsetHeight + 'px' : null;
                angular.element(origCells[idx]).css('min-width', width);
                angular.element(origCells[idx]).css('min-height', height);
              });
            }

            lastActive = active;
            lastTop = elTop;
            lastLeft = elLeft;
          }, $elem);

          scope.$on('$destroy', function () {
            return scrollWatcher.destroy();
          });
        }, 250);
      }
    };
  }]);

  var reviewRouteModule = angular.module('Q.routes.assessments.review', [uiRouterStateModule, timerHelperModule, safeUrlFilterModule, scoreLabelComponentModule, appAlertComponentModule, dialogComponentModule, assessmentSubmittingDialogComponentModule,
  // surveyDialogComponentModule,
  markdownEditorComponentModule, codeHighlightComponentModule, quizChallengeQuestionsComponentModule, fileListViewerComponentModule, stickyHeaderComponentModule]).config(['$stateProvider', function ($stateProvider) {
    var candidateRemarks = '';

    $stateProvider.state('assessments.review', {
      url: '/review',
      template: reviewTemplate,
      data: {
        permissions: {
          loggedIn: true,
          redirectTo: 'assessments.welcome'
        },
        bodyClass: 'assessments review',
        label: 'Assessment Review'
      },
      resolve: {
        assessmentResult: /* @ngInject */['process', function assessmentResult(process) {
          return process.whenAssessmentResultReady();
        }],
        initialLoad: /* @ngInject */['$state', function initialLoad($state) {
          return !$state.current || !$state.current.name;
        }]
      },
      controllerAs: 'review',
      controller: /* @ngInject */['$scope', 'process', 'assessmentResult', 'initialLoad', 'assessmentResultsService', 'appAlert', 'accountService', 'dialogService', '$window', '$log', 'assessmentSubmittingDialog', 'Solution', '$state', 'photoManager', function ReviewCtrl($scope, process, assessmentResult, initialLoad, assessmentResultsService, appAlert, accountService, dialogService, $window, $log, assessmentSubmittingDialog, Solution, $state, photoManager) {
        if (process.invitationIssue) {
          // if there is an issue go to the welcome screen where we can tell them about it
          return $state.go('assessments.welcome');
        }

        var isOpen = assessmentResult && !assessmentResult.isClosed();
        var hasSolutions = assessmentResult && assessmentResult.solutions && assessmentResult.solutions.length;
        if (!assessmentResult || isOpen && process.embedOptions.hideReview || isOpen && initialLoad && !hasSolutions) {
          $state.go('assessments.welcome');
          return;
        }

        var ctrl = this;
        ctrl.process = process;
        if (process.liveInterview.setActiveChallengeId) {
          process.liveInterview.setActiveChallengeId(null);
        }
        if (process.timer && assessmentResult.isStarted()) {
          process.timer.start(assessmentResult);
        }
        ctrl.assessmentResult = assessmentResult;

        // restore unsaved notes
        if (candidateRemarks) {
          assessmentResult.candidateRemarks = candidateRemarks;
        }

        ctrl.incompleteStage = !process.assessmentResult.isClosed() && process.stages.challenges.find(function (c) {
          return c.solution && c.solution.isAdvancedCode() && c.solution.hasFailedSubmissionAttempts() && c.solution.remainingAttempts();
        });
        ctrl.hasUnusedAttempts = !!ctrl.incompleteStage;

        ctrl.isSafeToSubmit = function () {
          return process.isSafeToSubmit() && !ctrl.hasUnusedAttempts;
        };

        ctrl.canReviewSolutions = function () {
          return assessmentResult.isStarted() || assessmentResult.assessment.showSolutionsToCandidates;
        };

        // don't show the attempted data if there's no submitted data
        function notSubmitted(solution) {
          return solution && solution.isCode() && !solution.attempted();
        }

        // Show a different score & summary if the timer ran out
        function waitingOnScore(solution) {
          return assessmentResult.timerRanOut() && notSubmitted(solution);
        }

        ctrl.timerRanOut = function () {
          // only treat this as a timer ran out scenario if the timer ran out, this is not a demo or
          // practice assessment, and at least one started code challenge has a 0 score
          return assessmentResult.timerRanOut() && !process.isSample() && assessmentResult.validSolutions().some(waitingOnScore);
        };

        function updateCommentStatus() {
          ctrl.remarksSubmitted = assessmentResult.isSubmitted() && !!assessmentResult.candidateRemarks;
          if (assessmentResult.isSubmitted()) {
            candidateRemarks = '';
          }
          ctrl.modifiedComments = false;
        }

        updateCommentStatus();

        ctrl.isSingleChallengeAssessment = process.challengeCount < 2;
        ctrl.canEdit = process.canEdit;
        ctrl.showActions = process.isTeamMember() || !process.restricted && (process.canEdit || !process.isEmbedded);
        ctrl.hasPostAssessmentRedirect = assessmentResult.hasPostAssessmentRedirect();
        ctrl.topSubmit = !process.isSample() && ctrl.canEdit && process.isSafeToSubmit() && process.isFullyComplete();

        var user = accountService.get().user;
        ctrl.landingParams = ['email', 'firstName', 'lastName'].map(function (k) {
          return k + '=' + encodeURIComponent(user[k]);
        }).join('&');

        function _postSubmitHandler() {
          updateCommentStatus();
          ctrl.canEdit = process.canEdit;
          ctrl.didSubmit = assessmentResult.isSubmitted() || assessmentResult.isTerminated();
          ctrl.hasPostAssessmentRedirect = assessmentResult.hasPostAssessmentRedirect();
          ctrl.submitting = false;

          if (assessmentResult.isClosed()) {
            process.postSubmitted(assessmentResult);
            photoManager.stop();
          }
        }

        ctrl.submit = function () {
          ctrl.submitting = true;
          process.submitAssessment()
          // .then(({ redirected }) => {
          // 	if(!redirected) {
          // 		surveyDialog.openCandidateSurvey(assessmentResult, process);
          // 	}
          // })
          .finally(_postSubmitHandler);
        };

        ctrl.update = function () {
          ctrl.submitting = true;
          ctrl.savedComments = false;
          assessmentResultsService.update(assessmentResult).then(updateCommentStatus).catch(appAlert.modelError).finally(function () {
            ctrl.submitting = false;
          });
        };

        ctrl.modifyComments = function () {
          ctrl.modifiedComments = true;
          candidateRemarks = assessmentResult.candidateRemarks;
        };

        ctrl.getStatus = function (stage) {
          return waitingOnScore(stage.solution) ? 'unknown' : stage.status;
        };
        ctrl.getSummary = function (stage) {
          if (waitingOnScore(stage.solution)) {
            return 'Timer Ran out';
          } else if (notSubmitted(stage.solution)) {
            return 'Submitting…';
          } else {
            return stage.label();
          }
        };
        ctrl.canReviewSolution = function (stage) {
          return stage.solution && !waitingOnScore(stage.solution);
        };

        ctrl.commentsLabel = function () {
          var label = 'Comments?';
          if (ctrl.process.restricted || ctrl.remarksSubmitted) {
            label = 'Your Comments';
          } else if (process.isPractice()) {
            label = 'Comments about the Practice Assessment?';
          } else if (process.isDemo()) {
            label = 'Comments about the Demo?';
          }
          return label;
        };

        // if we've already been submitted or is still submitting,
        // let the submitting dialog pop open
        if (assessmentResult.isClosed()) {
          ctrl.submit();
          if (assessmentResult.isSubmitted() || assessmentResult.isTerminated()) {
            _postSubmitHandler();
          }
        }

        ctrl.saveSolution = function (solution) {
          Solution.update(solution, {
            candidateNotes: solution.candidateNotes
          }).catch(appAlert.modelError.with({ prefix: 'Unable to save your notes:' }));
        };

        ctrl.frameworkLanguage = function (solution) {
          return languages.frameworkLanguage(solution.language, solution.testFramework);
        };

        process.postUpdated(null, {
          id: 'review',
          title: 'Review',
          index: process.challengeCount + 1
        });
      }]
    });
  }]);

  var SECOND = 1000;
  var WARNING_TIME = 5 * 60;

  var assessmentTimerServiceModule = angular.module('Q.services.assessment-timer-service', [timerHelperModule]).factory('assessmentTimerService', ['timerHelper', '$rootScope', function (timerHelper, $rootScope) {
    var Timer = function () {
      function Timer(isCountUp) {
        classCallCheck(this, Timer);

        this.intervalPromise = null;
        this.isCountUp = !!isCountUp;
        this.isCountDown = !isCountUp;

        eventEmitter(this);
      }

      createClass(Timer, [{
        key: 'stop',
        value: function stop() {
          var self = this;
          self.fire('tick');
          if (self.intervalPromise) {
            self.intervalPromise.stop();
            self.intervalPromise = null;
            self.fire('stop');
          }
          self.stopTime = Date.now();
        }
      }, {
        key: 'reset',
        value: function reset() {
          this.startTime = null;
          this.time = 0;
          this.stop();
          this.stopTime = null;
        }
      }, {
        key: 'fire',
        value: function fire(event) {
          this.emit(event, this.time);
        }
      }, {
        key: 'timeElapsed',
        value: function timeElapsed() {
          if (this.startTime) {
            var endTime = !this.isRunning() && this.stopTime ? this.stopTime : Date.now();
            return Math.floor((endTime - this.startTime) / SECOND);
          } else {
            return 0;
          }
        }
      }, {
        key: 'isRunning',
        value: function isRunning() {
          return !!this.intervalPromise;
        }
      }, {
        key: 'getTime',
        value: function getTime() {
          return this.time;
        }
      }]);
      return Timer;
    }();

    var CountUpTimer = function (_Timer) {
      inherits(CountUpTimer, _Timer);

      function CountUpTimer(timeExpected) {
        classCallCheck(this, CountUpTimer);

        var _this = possibleConstructorReturn(this, (CountUpTimer.__proto__ || Object.getPrototypeOf(CountUpTimer)).call(this, true));

        _this.timeExpected = timeExpected;
        return _this;
      }

      createClass(CountUpTimer, [{
        key: 'start',
        value: function start() {
          var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
              wallTimeSinceStart = _ref.wallTimeSinceStart;

          var self = this;
          if (self.isRunning()) return;

          self.startTime = Date.now();
          if (wallTimeSinceStart) self.startTime -= wallTimeSinceStart * 1000;
          self.time = self.timeElapsed();
          self.intervalPromise = timerHelper.runEvery(function () {
            self.time = self.timeElapsed();
            self.fire('tick');
          }, SECOND / 2, false);
          self.fire('start');
        }
      }, {
        key: 'progress',
        value: function progress() {
          var totalProgress = this.timeElapsed() / this.timeExpected * 100;
          return Math.max(0, Math.min(100, totalProgress || 0));
        }
      }]);
      return CountUpTimer;
    }(Timer);

    var CountDownTimer = function (_Timer2) {
      inherits(CountDownTimer, _Timer2);

      function CountDownTimer(timeAllotted) {
        classCallCheck(this, CountDownTimer);

        var _this2 = possibleConstructorReturn(this, (CountDownTimer.__proto__ || Object.getPrototypeOf(CountDownTimer)).call(this, false));

        _this2.timeAllotted = timeAllotted;
        _this2.time = _this2.timeAllotted;
        _this2.warned = false;
        _this2.fire('tick');
        return _this2;
      }

      createClass(CountDownTimer, [{
        key: 'reset',
        value: function reset() {
          get(CountDownTimer.prototype.__proto__ || Object.getPrototypeOf(CountDownTimer.prototype), 'reset', this).call(this);
          this.time = this.timeAllotted;
          this.warned = false;
        }
      }, {
        key: 'start',
        value: function start() {
          var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
              wallTimeSinceStart = _ref2.wallTimeSinceStart,
              timerExpiresIn = _ref2.timerExpiresIn;

          var self = this;
          if (self.isRunning()) return;

          self.startTime = Date.now();
          if (wallTimeSinceStart) self.startTime -= wallTimeSinceStart * 1000;
          self.time = timerExpiresIn;
          self.fire('tick');

          if (self.time <= 0) {
            self.time = 0;
            self.fire('done');
          } else {
            self.intervalPromise = timerHelper.runEvery(function () {
              self.time = self.timeAllotted - self.timeElapsed();
              self.fire('tick');
              if (self.time <= 0) {
                self.warned = true;
                self.time = 0;
                self.stop();
                $rootScope.$applyAsync(function () {
                  return self.fire('done');
                });
              } else if (self.time <= WARNING_TIME && !self.warned) {
                self.warned = true;
                $rootScope.$applyAsync(function () {
                  return self.fire('warning');
                });
              }
            }, SECOND / 2, false);

            self.fire('start');
          }
        }
      }, {
        key: 'progress',
        value: function progress() {
          var totalProgress = this.timeElapsed() / this.timeAllotted * 100;
          return Math.max(0, Math.min(100, totalProgress || 0));
        }
      }]);
      return CountDownTimer;
    }(Timer);

    return { CountDownTimer: CountDownTimer, CountUpTimer: CountUpTimer };
  }]);

  var productMessageBannerTemplate = "<div> <span ng-if=\"$ctrl.service.icon\" class=\"message-icon icon-{{$ctrl.service.icon}}\"></span> <span class=\"message\" ng-bind=\"$ctrl.message\"></span> <a ng-if=\"$ctrl.service.linkTitle\" class=\"alert-link\" ng-href=\"{{$ctrl.service.link}}\" ng-click=\"$ctrl.service.linkAction()\" ng-bind=\"$ctrl.service.linkTitle\"></a> <button ng-if=\"$ctrl.service.closeable\" class=\"close\" data-dismiss=\"message\" aria-label=\"Close\" ng-click=\"$ctrl.service.hide()\"> <span aria-hidden=\"true\">&times;</span> </button> </div> ";

  var PRODUCT_BANNER_MODES = {
    info: 'info',
    success: 'success',
    warning: 'warning',
    danger: 'danger',
    error: 'danger'
  };

  var productMessageBannerComponentModule = angular.module('Q.components.product-message-banner', []).factory('productMessageBannerService', function () {
    var svc = {
      $onChange: angular.noop,
      message: null,
      show: show,
      showInfo: show.bind(null, 'info'),
      showSuccess: show.bind(null, 'success'),
      showWarning: show.bind(null, 'warning'),
      showDanger: show.bind(null, 'danger'),
      hide: function hide() {
        svc.message = null;
        svc.$onChange();
      },

      get active() {
        return !!svc.message;
      }
    };

    function show(mode, message) {
      var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
          link = _ref.link,
          linkTitle = _ref.linkTitle,
          linkAction = _ref.linkAction,
          _ref$closeable = _ref.closeable,
          closeable = _ref$closeable === undefined ? true : _ref$closeable,
          icon = _ref.icon;

      svc.mode = PRODUCT_BANNER_MODES[mode] || mode.info;
      svc.icon = icon;
      svc.message = message;
      svc.link = link;
      svc.linkTitle = linkTitle;
      svc.linkAction = linkAction || angular.noop;
      svc.closeable = closeable;
      svc.$onChange();
    }

    return svc;
  }).component('productMessageBanner', {
    controller: 'ProductMessageBannerCtrl',
    template: productMessageBannerTemplate
  }).controller('ProductMessageBannerCtrl', ['$element', 'productMessageBannerService', function ProductMessageBannerCtrl($element, productMessageBannerService) {
    var ctrl = this;

    ctrl.service = productMessageBannerService;

    function update() {
      ['info', 'success', 'warning', 'danger'].forEach(function (mode) {
        $element.removeClass('alert-' + mode);
      });
      var active = productMessageBannerService.active;
      $element.toggleClass('active', active);
      $element.parent().toggleClass('has-product-message-banner', active);
      if (active) {
        // preserves the rendered message on hide
        ctrl.message = productMessageBannerService.message;
        $element.addClass('alert-' + productMessageBannerService.mode);
      }
    }

    ctrl.$onInit = function () {
      $element.addClass('alert');
    };

    productMessageBannerService.$onChange = update;

    ctrl.$postLink = update;
  }]);

  /**
   * Configures a service that tracks the release version through sockets and API-based config.
   * If the app release differs from the current release, we show a banner
   */
  var releaseAlertServiceModule = angular.module('Q.services.release-alert-service', [configServiceModule, productMessageBannerComponentModule]).factory('releaseAlertService', ['configService', 'productMessageBannerService', '$http', '$window', '$timeout', '$log', function (configService, productMessageBannerService, $http, $window, $timeout, $log) {
    // TODO: provide hooks into this service as to when it's OK
    //   to just automatically reload.
    // Thoughts:
    // - Listen to state change events and clearing a flag if the URL changes
    //   - Individual states can alert they are safe to reload (i.e., simple lists)
    // - Use idle service to recognize if the page is idle, and only reload then
    // - Maybe look at automatically detecting $dirty input fields or forms (maybe hook
    //   into <form> component)?
    //
    // We don't release so often this is necessary, but sometimes we release multiple times
    //   in a row, which might be annoying. Automatically handling it means we could avoid
    //   annoying users.

    var alertTimeout = void 0;
    var loaded = void 0;
    var forceReload = function forceReload() {
      $http.get($window.location.href, {
        headers: {
          Pragma: 'no-cache',
          Expires: -1,
          'Cache-Control': 'no-cache'
        }
      }).finally(function () {
        $window.location.reload(true);
      });
    };

    var cancelTimeout = function cancelTimeout() {
      $timeout.cancel(alertTimeout);
      alertTimeout = null;
    };

    var showAlert = function showAlert(newRelease) {
      cancelTimeout();

      $log.info('New Qualified deployment detected. Old "' + appConfig.release + '" -> New "' + newRelease + '"');

      // Delay banner for a bit to ensure the deploy isn't rolled back or something similar.
      // this gives us a 4+ minute buffer to rollback changes, or to ensure that the
      // deploy has fully rolled out to everything.
      // There's some randomness hiding in here, too, so we can stagger users reloading the
      // page right after a rollout.
      alertTimeout = $timeout(function () {
        productMessageBannerService.showSuccess('A new version of Qualified is available!', {
          linkTitle: 'When you are ready, click here to activate!',
          linkAction: forceReload,
          icon: 'product-changes-sm'
        });
      }, (4 + Math.random() * 4) * 60 * 1000);
    };
    var svc = {
      listen: function listen(sockets) {
        if (!appConfig.release || !$window.Modernizr || loaded) {
          $log.debug('Skipping listening for release updates');
          return;
        }

        loaded = true;

        configService.load().then(function (config) {
          if (config.release && config.release !== appConfig.release) {
            showAlert(config.release);
          }

          svc.channel = sockets.listen('release', 'release');

          svc.channel.subscribe('deployed', function (data) {
            var _ref = data.data || {},
                release = _ref.release,
                env = _ref.env;

            cancelTimeout();

            if ($window.Modernizr['qualified-' + env] && release !== appConfig.release) {
              showAlert(release);
            }
          });
        }).catch(function () {});
      }
    };

    return svc;
  }]);

  var socketServiceModule = angular.module('Q.services.socket-service', [jsDataModule, allModelsModule, configServiceModule, ablyClientServiceModule, releaseAlertServiceModule, permissionsServiceModule]).factory('socketService', ['DS', '$rootScope', '$window', '$log', '$q', '$timeout', 'configService', 'ablyClientService', 'releaseAlertService', 'permissionsService', function (DS, $rootScope, $window, $log, $q, $timeout, configService, ablyClientService, releaseAlertService, permissionsService) {
    var injectFlags = {};

    var whenReadyCallbacks = [];

    var svc = {
      client: null,
      channel: null,
      presenceWatchers: {},
      whenReady: function whenReady(cb) {
        return $q(function (resolve) {
          if (svc.channel && svc.account) {
            resolve(cb(svc));
          } else {
            whenReadyCallbacks.push(function () {
              return resolve(cb(svc));
            });
          }
        });
      },

      // delays the creation of the socket and returns a promise instead, in order to allow the page time to
      // do other things
      create: function create(account) {
        // if the channel was already created then just return it
        if (svc.account === account && svc.channel) {
          return svc.channel;
        } else {
          return _create(account, 'default');
        }
      },
      enterPresence: function enterPresence(channelId, memberData) {
        svc.whenReady(function () {
          return _enterPresence(channelId, memberData);
        });
      },
      leavePresence: function leavePresence(channelId) {
        if (svc.client) {
          svc.client.channels.get(channelId).presence.leave();
        }
      },


      /**
       * Creates an object which will watch presence and manage a list that ensures a clientId (user id)
       * is only presence once within the list.
       * @param name watcher name, useful for retrieving the instance later using `socketService.presenceWatchers[name]`
       * @param channelId
       * @param onChange callback for when the underlying list is modified. Will be called on any list change, as well on initialization
       * @return {*}
       */
      createPresenceWatcher: function createPresenceWatcher(name, channelId) {
        var onChange = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : angular.noop;

        return svc.whenReady(function () {
          var channel = svc.client.channels.get(channelId);
          var watcher = svc.presenceWatchers[name] = {
            channelId: channelId,
            channel: channel,
            list: []
          };
          var log = function log(member) {
            return $log.debug('Presence data received', member);
          };
          var addOrUpdate = function addOrUpdate(member) {
            log(member);
            // we only track unique client ids (we ignore multiple connections by the same client)
            // clientId is the candidateId
            var existing = watcher.list.find(function (m) {
              return m.clientId === member.clientId;
            });
            existing ? existing.data = member.data : watcher.list.push(member);
          };

          // get the initial list of current members
          channel.presence.get().then(function (members) {
            (members || []).forEach(addOrUpdate);
            onChange(watcher);
          }).catch(function (err) {
            // 80002/80003: connection suspended/disconnected. A client
            // connectivity problem Ably recovers from, so don't alert on it
            var isConnectivityError = err && (err.code === 80002 || err.code === 80003);
            $log[isConnectivityError ? 'warn' : 'error']('[Ably] presence.get failed', err);
          });
          channel.presence.subscribe('enter', function (member) {
            addOrUpdate(member);
            onChange(watcher);
          });
          channel.presence.subscribe('update', function (member) {
            addOrUpdate(member);
            onChange(watcher);
          });
          channel.presence.subscribe('leave', function (member) {
            log(member);
            // use connection id to minimize the chances of removing a client that has multiple connections only
            // NOTE: there is still the chance that the original connection is closed, causing them to be removed
            // when multiple connections are still live. For now we are going to allow this to happen in order to keep
            // things simple. The user will be reactivated on the new connection once the next update comes through.
            watcher.list = watcher.list.filter(function (m) {
              return m.connectionId !== member.connectionId;
            });
            onChange(watcher);
          });

          onChange(watcher);
          $log.debug('Watching ' + name + ' presence on ' + channelId);
          return watcher;
        });
      },


      /**
       * Completely destroys an account's or user object's socket
       *
       * - Removes all listeners
       * - Disconnects the socket
       * - removes references to the socket
       */
      destroy: function destroy() {
        $log.debug('Disconnecting sockets.');
        if (svc.client) {
          svc.client.connection.close();
        }
        svc.client = null;
        svc.channel = null;
      }
    };
    return svc;

    //// private functions

    function _enterPresence(channelId, memberData) {
      svc.client.channels.get(channelId).presence.enter(memberData);
    }

    /**
     * Creates a new socket based on the account or user object
     * @param account accountService.get() or TeamMember or Candidate
     * @param ablyKey
     * @returns {*} Socket
     */
    function _create(account) {
      var clientKey = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'default';

      if (svc.account === account && svc.channel) return svc.channel;

      var channelId = _getChannelId(account);
      var clientId = _getClientId(account);
      var isCandidate = _isCandidate(account);

      ablyClientService.connect({
        svc: svc,
        clientKey: clientKey,
        clientId: clientId,
        channelId: channelId
      });

      if (!svc.channel) {
        return;
      }

      svc.account = account;

      try {
        // presence
        svc.channel.presence.enter();

        // create
        _bindToChannel('model.created', function (data) {
          // TODO: only inject or update when we care about the data
          _inject(data, 'created');
        });

        // update
        _bindToChannel('model.updated', function (data) {
          // TODO: only inject or update when we care about the data
          // only patch data if a candidate
          _inject(data, 'updated', isCandidate);
        });

        // digest update (server emits this when an embedded digest is saved)
        // Treat as a standard update so any already-loaded instances refresh and routes/lists react normally.
        _bindToChannel('model.digest_updated', function (data) {
          $log.debug('!!! Digest updated', data);
          _inject(data, 'updated', isCandidate);
        });

        // signal
        if (permissionsService.feature('signals')) {
          _bindToChannel('model.signal', function (data) {
            _inject(data, 'signal');
          });
        }

        // destroy
        _bindToChannel('model.destroyed', function (data) {
          if (data.type && DS.definitions[data.type]) {
            var instance = DS.get(data.type, data.id);

            if (instance) {
              DS.eject(data.type, data.id);
              _broadcast('destroyed', data);
            }
          }
        });

        _bindToChannel('error', function (_ref) {
          var type = _ref.type,
              data = _ref.data;

          $rootScope.$broadcast('socket:error', { data: data, type: type });
        });

        _bindToChannel('success', function (_ref2) {
          var type = _ref2.type,
              data = _ref2.data;

          $rootScope.$broadcast('socket:success', { data: data, type: type });
        });

        _bindToChannel('test', function (data) {
          $window.console.dir(data);
        });

        if (clientKey === 'default') {
          releaseAlertService.listen(svc);
        }
      } catch (ex) {
        $log.error('Error creating Ably connection', ex);
      }

      // make sure any waiting callbacks are ran
      whenReadyCallbacks.forEach(function (cb) {
        return cb();
      });
      whenReadyCallbacks.length = 0;

      return svc;
    }

    function _bindToChannel(event, handler) {
      svc.channel.subscribe(event, function (msg) {
        try {
          handler(msg.data);
        } catch (err) {
          $log.error('Error from socket on event \'' + event, err, msg);
        }
      });
    }

    /**
     * Gets the channel ID for an account or user object
     * @param account
     * @returns {*}
     * @private
     */
    function _getChannelId(account) {
      if (account.user) account = account.user;
      if (account.team) {
        return account.team.envRoomId;
      }
      return account.envRoomId;
    }

    /**
     * Gets the client ID for an account or user object
     * @param account
     * @returns {*}
     * @private
     */
    function _getClientId(account) {
      return account.user ? account.user.id : account.id;
    }

    /**
     * Returns true if the user is a candidate
     * @param account
     * @returns {*}
     * @private
     */
    function _isCandidate(account) {
      return (account.user ? account.user : account).$type === 'Candidate';
    }

    /**
     * Broadcasts the event 6 ways:
     *
     * model.[event]
     * model.changed
     * model.[event]:type
     * model.changed:type
     * model.[event]:type:id (existing only)
     * model.changed:type:id (existing only)
     *
     * @param type
     * @param data
     * @private
     */
    function _broadcast(type, data) {
      $rootScope.$broadcast('socket:model.' + type, data);
      $rootScope.$broadcast('socket:model.changed', data, type);
      $rootScope.$broadcast('socket:model.' + type + ':' + data.type, data);
      $rootScope.$broadcast('socket:model.changed:' + data.type, data, type);
      if (type !== 'created' && type !== 'destroyed') {
        $rootScope.$broadcast('socket:model.' + type + ':' + data.type + ':' + data.id, data);
        $rootScope.$broadcast('socket:model.changed:' + data.type + ':' + data.id, data, type);
      }
    }

    /**
     * Handles injecting and linking the data.
     * @param data
     * @param eventName
     * @private
     */
    function _inject(data, eventName, allowPatchData) {
      var Model = DS.definitions[data.type];

      if (!Model) {
        return;
      }

      var notify = function notify(eventName, model) {
        data.model = model;
        _broadcast(eventName, data);
      };

      var meta = Model.meta;
      var instance = Model.get(data.id);

      if (instance && data.updatedAt && instance.updatedAt && instance.updatedAt.getTime) {
        // if the updatedAt timestamp is less than that of the data already loaded locally then we can
        // just ignore the inject call.
        data.updatedAt = new Date(data.updatedAt);
        if (instance.updatedAt.getTime() > data.updatedAt.getTime()) {
          // do not bother to call the callback since we will just be ignoring the call
          $log.debug('[Sockets] ignoring old data:', data.type, data.id, '[local: ' + instance.updatedAt + ']', '[remote: ' + data.updatedAt + ']');
          return;
        }
      }

      if (!instance && eventName === 'updated') {
        $log.debug('[Sockets] ignoring ' + data.type + '(' + data.id + ') as it\'s not loaded and this is an \'updated\' event', data);
        return;
      }

      // We only inject into instances that are already loaded or that don't have includes configured.
      // If includes are configured then we will want to do a pull so that we can be sure to get all of the data
      // We also only handle if patch data is allowed, which currently is only used for candidate users
      var pushedInstanceProps = allowPatchData && instance && angular.isObject(data.model) && Model.deserialize(data.type, data.model);
      if (pushedInstanceProps) {
        // enable the deserializer to clean up the incoming data
        Model.inject(pushedInstanceProps);
        notify(eventName, pushedInstanceProps);
      } else if (meta.neverFetchOnPush) {
        if (instance) {
          // notify listeners that the model changed, but don't reload or update it
          notify(eventName, instance);
        }
      } else if (instance || !meta.fetchOnPush || meta.fetchOnPush(data)) {
        // only look up models if we've already got a copy,
        // or the Model doesn't tell us to ignore pushed data

        // prevent making too many requests at once
        var injectKey = data.type + ':' + data.name;
        var alreadyInjecting = injectFlags[injectKey];
        if (alreadyInjecting) {
          // notify that we'll need to make another update
          alreadyInjecting.needsUpdate = true;
          return;
        }

        var options = {
          ignoreLoadingBar: true, // no need to show as busy
          bypassCache: true // always reload, even if we already have it
        };
        if (meta.includeOnPull) {
          // include anything we always include on socket events
          options.params = {
            includes: meta.includeOnPull.slice()
          };
        }

        /**
         * Makes a request to the server to load the model
         * @param eventName
         */
        var handleInjection = function handleInjection(eventName) {
          var injecting = injectFlags[injectKey] = {};

          Model.find(data.id, options).then(function (model) {
            return notify(eventName, model);
          }).finally(function () {
            // clear flag
            delete injectFlags[injectKey];
            if (injecting.needsUpdate) {
              handleInjection('updated');
            }
          });
        };

        handleInjection(eventName);
      } else {
        // else ignore the push, don't trigger an event
        $log.debug('[Sockets] ignoring ' + data.type + '(' + data.id + ') as it\'s not loaded', data);
      }
    }
  }]);

  var IDLE_AFTER_MINUTES = 15;
  var IDLE_CHECK_RATE_SECONDS = 30;
  var ACTIVE_EVENTS = ['mousemove', 'mousedown', 'touchstart', 'click', 'keydown', 'scroll'];

  var idleDetectionHelperModule = angular.module('Q.helpers.idle-detection-helper', [timerHelperModule]).run(['$rootScope', '$document', 'timerHelper', function ($rootScope, $document, timerHelper) {
    var wasIdle = false;
    var idleCount = 0;
    var maxIdleCount = IDLE_AFTER_MINUTES * 60 / IDLE_CHECK_RATE_SECONDS;

    // On every activity event we reset the number of idle counts
    ACTIVE_EVENTS.forEach(function (event) {
      $document[0].addEventListener(event, function () {
        idleCount = 0;
        if (wasIdle) {
          wasIdle = false;
          // notify listeners that we're no longer idle
          $rootScope.$broadcast('idleDetection', false);
        }
      }, true);
    });

    // On every TIMER_DELAY tick, we increment the idle counts. If we get over our max,
    // we broadcast an event
    timerHelper.runEvery(function () {
      idleCount++;
      if (!wasIdle && idleCount >= maxIdleCount) {
        wasIdle = true;
        // notify listeners that we've gone idle
        $rootScope.$broadcast('idleDetection', true);
      }
    }, IDLE_CHECK_RATE_SECONDS * 1000);
  }]);

  var editProfileDialogTemplate = "<form class=\"edit-profile-form\" name=\"editProfileForm\" ng-submit=\"editProfileForm.$valid && $ctrl.save()\" novalidate> <div class=\"modal-header\"> <h4 class=\"modal-title\">Edit Your Profile</h4> <button class=\"close\" aria-label=\"Close\" ng-click=\"$ctrl.cancel()\"> <span aria-hidden=\"true\">&times;</span> </button> </div> <div class=\"modal-body\"> <div class=\"layout-two-columns\"> <form-group label=\"First Name\" field-name=\"a first name\"> <input class=\"form-control\" type=\"text\" required ng-model=\"$ctrl.data.firstName\" ng-disabled=\"$ctrl.disabled\" autofocus> </form-group> <form-group label=\"Last Name\" field-name=\"a last name\"> <input class=\"form-control\" type=\"text\" required ng-model=\"$ctrl.data.lastName\" ng-disabled=\"$ctrl.disabled\"> </form-group> </div> </div> <div class=\"modal-footer\"> <input type=\"button\" class=\"cancel btn-default\" value=\"Cancel\" ng-click=\"$ctrl.cancel()\" ng-disabled=\"$ctrl.disabled\"> <input type=\"submit\" class=\"submit btn-primary\" value=\"Save Changes\" ng-disabled=\"$ctrl.disabled\"> </div> </form> ";

  var editProfileDialogComponentModule = angular.module('Q.components.edit-profile-dialog', [accountServiceModule, candidateModelModule, modalComponentModule, appAlertComponentModule]).factory('editProfileDialog', ['$modal', function editProfileDialog($modal) {
    return {
      /**
       * @param {TeamCandidate} teamCandidate - team candidate to edit
       */
      open: function open() {
        return $modal.openOnce('editProfileDialog', {
          template: editProfileDialogTemplate,
          controller: 'EditProfileDialogCtrl as $ctrl'
          // resolve: {},
        }).result;
      }
    };
  }]).controller('EditProfileDialogCtrl', ['$modalInstance', 'accountService', 'Candidate', 'appAlert', function EditProfileDialogCtrl($modalInstance, accountService, Candidate, appAlert) {
    var ctrl = this;
    var user = accountService.get().user;

    ctrl.data = {
      firstName: user.firstName,
      lastName: user.lastName
    };
    ctrl.candidate = user;

    $modalInstance.preventDismissWhen(function () {
      return Object.keys(ctrl.data).some(function (k) {
        return ctrl.data[k] !== user[k];
      });
    });

    ctrl.save = function () {
      ctrl.disabled = true;
      Candidate.update(user, ctrl.data).then(function () {
        appAlert.success('Saved');
        $modalInstance.close(user);
      }).catch(appAlert.modelError).finally(function () {
        return ctrl.disabled = false;
      });
    };

    ctrl.cancel = function () {
      $modalInstance.dismiss('cancel');
    };
  }]);

  var MS_PER_SECOND = 1000;
  var SECONDS_PER_MINUTE = 60;

  /**
   * Seconds left on the countdown the current challenge shows for itself, or `null` when the
   * candidate is not looking at a challenge countdown at all (untimed challenge, a challenge
   * whose timer has not started yet, or no challenge on screen).
   *
   * @param {Object} stage the current stage from the assessment result process
   * @param {number} now current time in milliseconds
   * @returns {?number}
   */
  function challengeSecondsRemaining(stage, now) {
    if (!stage || !stage.challengeId) return null;

    var challenge = stage.challenge,
        solution = stage.solution;

    if (!challenge || !solution || solution.pendingTimerReset) return null;

    if (challenge.isQuestionTimed()) {
      // Per-question quizzes have no single deadline, so we use the sum of every question's
      // limit: the longest the candidate could still be held on this challenge. An infinite
      // total means no question is actually limited, so there is no countdown to defer to.
      var totalMinutes = challenge.totalTimeLimitMin();
      return isFinite(totalMinutes) ? totalMinutes * SECONDS_PER_MINUTE : null;
    }

    if (!challenge.isTimed() || !solution.timerStartedAt) return null;

    var startedAt = new Date(solution.timerStartedAt).getTime();
    if (isNaN(startedAt)) return null;

    var expiresAt = startedAt + challenge.timeLimitMin * SECONDS_PER_MINUTE * MS_PER_SECOND;
    return (expiresAt - now) / MS_PER_SECOND;
  }

  /**
   * Decides whether the sidebar assessment timer should be shown.
   *
   * A candidate working inside a timed challenge already has that challenge's countdown in the
   * toolbar, and two competing deadlines are confusing — so we drop the assessment timer while
   * they are in there. The exception is the deadline that actually binds: if the assessment runs
   * out before the challenge would, that is the countdown worth watching, so it stays.
   *
   * @param {Object} params
   * @param {Object} params.timer the assessment timer (`assessmentTimerService`)
   * @param {Object} params.stage the current stage from the assessment result process
   * @param {number} [now] current time in milliseconds
   * @returns {boolean}
   */
  function shouldShowAssessmentTimer() {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        timer = _ref.timer,
        stage = _ref.stage;

    var now = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Date.now();

    if (!timer) return false;

    var challengeRemaining = challengeSecondsRemaining(stage, now);

    // No challenge countdown on screen, so the assessment timer is the only one.
    if (challengeRemaining === null) return true;

    // The challenge countdown is spent; the assessment timer is useful again.
    if (challengeRemaining <= 0) return true;

    // A count-up timer has no deadline, so it can never be the one that binds.
    if (timer.isCountUp) return false;

    return timer.time <= challengeRemaining;
  }

  var STAGE_STATUSES = {
    NEW: 'new',
    PARTIAL: 'partial',
    COMPLETED: 'completed',
    EXPIRED: 'expired',
    FAILED: 'failed'
  };

  var STAGE_LABEL = {
    new: 'Not Started',
    partial: 'Incomplete',
    completed: 'Ready for Scoring',
    nonChallengeCompleted: 'Completed',
    closedCompleted: 'Scored',
    expired: 'No Longer Editable',
    failed: 'Error on Run'
  };

  var assessmentResultProcessServiceModule = angular.module('Q.routes.assessments.assessment-result-process-service', [timerHelperModule, accountServiceModule, assessmentResultsServiceModule, liveInterviewServiceModule, assessmentTimerServiceModule, appAlertComponentModule, dialogComponentModule, scrollToComponentModule, assessmentSubmittingDialogComponentModule, solutionModelModule, assessmentResultModelModule, windowEventHelperModule, socketServiceModule, firebaseServiceModule, messageHubServiceModule, safeUrlFilterModule, idleDetectionHelperModule, editProfileDialogComponentModule, trackerServiceModule]).factory('assessmentResultProcess', ['$rootScope', 'Solution', 'assessmentResultsService', 'assessmentTimerService', 'accountService', '$state', '$q', 'appAlert', 'liveInterviewService', 'timerHelper', 'AssessmentResult', 'dialogService', 'scrollToElement', '$log', '$document', '$filter', 'assessmentSubmittingDialog', '$interval', '$timeout', 'allModels', 'socketService', 'firebase', '$window', 'messageHubService', 'embedOptions', 'safeUrl', 'editProfileDialog', 'configService', 'tracker', function ($rootScope, Solution, assessmentResultsService, assessmentTimerService, accountService, $state, $q, appAlert, liveInterviewService, timerHelper, AssessmentResult, dialogService, scrollToElement, $log, $document, $filter, assessmentSubmittingDialog, $interval, $timeout, allModels, socketService, firebase, $window, messageHubService, embedOptions, safeUrl, editProfileDialog, configService, tracker) {
    return {
      create: create
    };

    /**
     * Creates a Stage object used to track the current stage of the application process.
     *
     * @param state Required state id (for tracking current/next)
     * @param stateParams Optional state params to pinpoint current/next
     * @param assessmentResult
     * @constructor
     */
    function Stage(state, stateParams, assessmentResult) {
      var _this = this;

      this.state = state;
      this.stateParams = stateParams;
      this.status = STAGE_STATUSES.NEW;
      this.isCompleted = function () {
        return _this.status === STAGE_STATUSES.COMPLETED || _this.status === STAGE_STATUSES.EXPIRED;
      };
      this.label = function () {
        if (_this.status === STAGE_STATUSES.COMPLETED) {
          if (!assessmentResult) {
            return STAGE_LABEL.nonChallengeCompleted;
          } else if (assessmentResult.isClosed()) {
            return STAGE_LABEL.closedCompleted;
          }
        }
        return STAGE_LABEL[_this.status];
      };
      this.tooltip = function () {
        return _this.status === 'new' ? '' : ' (' + STAGE_LABEL[_this.status] + ')';
      };
    }

    /**
     * Creates an assessment result process object based on the given assessment.
     *
     * This object can be used to determine information about the current assessment result, such as number of
     * challenge stages, and—once the user has logged in—status of each stage.
     *
     * @param {Object} auth - Authorization data acquired from the API
     * @param {number} heartbeatInterval - The number of seconds between each heartbeat
     * @returns {{finished: boolean, assessmentResult: null, assessment: *, team: (*|.modes.team|qualified.models.team), stages: {welcome: Stage, challenges: Array, review: Stage}}}
     */
    function create(auth, heartbeatInterval) {
      var assessment = auth.assessment;
      var CountDownTimer = assessmentTimerService.CountDownTimer,
          CountUpTimer = assessmentTimerService.CountUpTimer;

      //=======================================================
      // SETUP
      //=======================================================

      var stages = {
        welcome: new Stage('assessments.welcome'),
        challenges: [],
        review: new Stage('assessments.review')
      };

      var highlights = { timer: false, stages: false };

      // TODO: clarify why these are needed in the context of loading/readiness stages
      // In brief, the assessmentResult property is loaded asynchronously
      // on the assessmentResultProcess. whenAssessmentResultReady() can
      // be used to wait for it.
      var readyDeferred = $q.defer();
      var loadAssessmentResultPromise = null;
      var assessmentResultDeferred = $q.defer();

      var rootScopeListeners = [];

      var process = {
        get canEdit() {
          return process.assessmentResult && process.assessmentResult.canEdit();
        },
        get editMode() {
          var embedMode = this.embedOptions && this.embedOptions.mode;
          var arMode = this.assessmentResult && this.assessmentResult.editMode;
          if (embedMode === EDIT_MODE_READONLY || arMode === EDIT_MODE_READONLY) {
            return EDIT_MODE_READONLY;
          } else if (embedMode === EDIT_MODE_RESTRICTED || arMode === EDIT_MODE_RESTRICTED) {
            return EDIT_MODE_RESTRICTED;
          } else {
            return EDIT_MODE_NORMAL;
          }
        },
        get readOnly() {
          return this.editModeReadOnly || this.editMode !== EDIT_MODE_RESTRICTED && !this.canEdit;
        },
        get restricted() {
          return this.readOnly || this.editModeRestricted;
        },
        get editModeReadOnly() {
          return this.editMode === EDIT_MODE_READONLY;
        },
        get editModeRestricted() {
          return this.editModeReadOnly || this.editMode === EDIT_MODE_RESTRICTED;
        },
        isFinished: function isFinished() {
          return process.assessmentResult && process.assessmentResult.isClosed();
        },

        get significantTimeDelay() {
          return configService.config.significantTimeDelay && (assessment.enforcedTimeLimit || this.hasTimedChallenges) && !this.isFinished() && !this.invitationIssue;
        },
        isFinishedOrInvalid: function isFinishedOrInvalid() {
          return this.invitationIssue || this.isFinished() || this.significantTimeDelay;
        },
        isSafeToSubmit: function isSafeToSubmit() {
          var attemptedAll = process.stages.challenges.every(function (s) {
            return s.status !== STAGE_STATUSES.NEW;
          });
          return attemptedAll || process.isTeamMember() || process.isSample();
        },
        isFullyComplete: function isFullyComplete() {
          return process.assessmentResult && (process.isSample() || stages.challenges.every(function (s) {
            return s.isCompleted();
          }));
        },

        logout: function logout() {
          if (process.timer && process.timer.isRunning()) {
            dialogService.confirm('There is an active timer on this assessment which will not stop when you logout. Are you sure you want to continue?', 'Active Timer').then(function (_ref) {
              var id = _ref.id;

              if (id === 'yes') {
                accountService.logoutAndReload();
              }
            });
          } else {
            accountService.logoutAndReload();
          }
        },
        liveInterview: {},
        assessment: assessment,
        stages: stages,
        timer: null,
        /**
         * Whether the sidebar assessment timer should be displayed. Hidden while the candidate
         * is inside a challenge showing its own countdown, unless the assessment is the
         * deadline that will hit first.
         */
        get showTimer() {
          return !this.restricted && shouldShowAssessmentTimer({
            timer: this.timer,
            stage: getCurrentStage()
          });
        },
        auth: auth,
        get invitationIssue() {
          return !!this.auth.denyReason || this.unableToLoadAssessmentResult;
        },
        configureCountDownTimer: function configureCountDownTimer(timeAllotted) {
          process.timer = new CountDownTimer(timeAllotted);
          process.timer.on('done', _timerRanOut);
          process.timer.on('warning', function (timer, seconds) {
            appAlert.warning('Warning! Less than ' + Math.ceil(seconds / 60) + ' minutes remaining. Finish up any last work.');
          });
          process.configureTimerDisplay();
        },
        configureCountUpTimer: function configureCountUpTimer(estimatedTime) {
          process.timer = new CountUpTimer(estimatedTime);
          process.configureTimerDisplay();
        },

        background: false,
        configureTimerDisplay: function configureTimerDisplay() {
          var title = $document.querySelector('head title');
          var timeMatcher = /^\S+ \d+:\d\d:\d\d - /;
          var secondsToTime = $filter('secondsToTime');
          on('document:focus', function () {
            title.text(title.text().replace(timeMatcher, ''));
          });
          process.timer.on('tick', function () {
            if (process.background && !process.isFinished()) {
              // if warned, shows alarm clock emoji, otherwise a stopwatch emoji
              var icon = process.timer.warned ? '\u23F0 ' : '\u23F1 ';
              title.text(icon + secondsToTime(process.timer.time) + ' - ' + title.text().replace(timeMatcher, ''));
            }
          });
        },

        goToReview: function goToReview() {
          return $state.go('assessments.review');
        },
        highlight: function highlight(component) {
          var duration = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 3;

          if (highlights[component]) timerHelper.longTimeout.cancel(highlights[component]);
          scrollToElement({
            target: '.assessment-sidebar .assessment-nav .' + component
          }).then(function () {
            highlights[component] = timerHelper.longTimeout(function () {
              highlights[component] = false;
            }, duration * 1000);
          });
        },
        isHighlighting: function isHighlighting(component) {
          return component ? !!highlights[component] : Object.keys(highlights).some(function (k) {
            return !!highlights[k];
          });
        },
        stopHighlight: function stopHighlight() {
          angular.forEach(highlights, function (v, k) {
            if (highlights[k]) {
              timerHelper.longTimeout.cancel(highlights[k]);
              highlights[k] = false;
            }
          });
        },

        challengeCount: 0,
        estimatedTime: assessment.estimatedTime(),
        enforcedTimeLimit: !!assessment.enforcedTimeLimit,
        hasTimedChallenges: assessment.hasTimedChallenges(),
        allStages: allStages,
        getCurrentStage: getCurrentStage,
        addSolution: addSolution,
        next: next,
        previous: previous,
        nextIsChallenge: nextIsChallenge,
        nextLabel: function nextLabel() {
          return nextIsChallenge() ? 'Next Challenge' : 'Review ' + (assessment.nounUpper || 'Assessment');
        },
        nextActionLabel: function nextActionLabel() {
          return nextIsChallenge() ? 'to the next challenge' : 'to review the ' + (assessment.nounLower || 'assessment');
        },

        startAssessmentResult: startAssessmentResult,
        loadAssessmentResult: loadAssessmentResult,
        get loadingAssessmentResult() {
          return !!loadAssessmentResultPromise;
        },
        refreshStageStatuses: refreshStageStatuses,
        destroy: destroy,
        submitAssessment: submitAssessment,
        whenReady: whenReady,
        whenAssessmentResultReady: whenAssessmentResultReady,
        isTeamMember: function isTeamMember() {
          return auth && !!auth.testTeamMember;
        },
        isPractice: function isPractice() {
          return assessment && assessment.isPractice;
        },
        isDemo: function isDemo() {
          return assessment && assessment.isDemo;
        },
        isSample: function isSample() {
          return assessment && assessment.isSample;
        },

        get challengeItems() {
          return process.assessmentResult ? process.assessmentResult.getChallengeItems() : assessment.challengeItems;
        },
        hasCodeChallenges: function hasCodeChallenges() {
          return process.challengeItems.some(function (ci) {
            return !ci.challenge || ci.challenge.isCode();
          });
        },

        get isEmbedded() {
          return messageHubService.embedded;
        },
        restart: restart,
        reset: reset,
        performPostAssessmentAction: performPostAssessmentAction,
        editProfile: editProfileDialog.open
      };

      var warnBeforeClosingListener = warnBeforeClosing(function () {
        // warn if the assessment result should be submitted before closing
        var shouldWarn = accountService.get().verified && process.canEdit && // can't make changes
        !process.restricted && // can't make changes
        !process.isTeamMember() && // doesn't matter for team members
        !process.isSample() && // or demo or practice assessments
        !process.isEmbedded; // let the parent window deal with it for embeds
        if (shouldWarn) {
          return 'Your assessment is not submitted yet.';
        }
      });

      if (!process.invitationIssue) {
        // configure mock timer for welcome
        if (assessment.enforcedTimeLimit) {
          process.timer = new CountDownTimer(assessment.enforcedTimeLimit * 60);
        } else if (process.estimatedTime) {
          process.timer = new CountUpTimer(process.estimatedTime * 60);
        }
      }

      // adding a function to each of the one-off stages to refresh its status
      // these are usually called logging in & out, and whenever we switch stages
      stages.welcome.refresh = function () {
        this.status = process.assessmentResult ? STAGE_STATUSES.COMPLETED : STAGE_STATUSES.NEW;
      };
      stages.review.refresh = function () {
        this.status = process.isFinished() ? STAGE_STATUSES.COMPLETED : STAGE_STATUSES.NEW;
      };

      //=======================================================
      // Embedding
      //=======================================================

      var postLoaded = function postLoaded() {
        messageHubService.postLoaded({
          assessment: process.assessment,
          assessmentResult: process.assessmentResult
        });
      };

      readyDeferred.promise.then(postLoaded);

      assessmentResultDeferred.promise.then(postLoaded, messageHubService.postLoadError);

      process.postUpdated = function (challenge, stateInfo) {
        messageHubService.postUpdated({
          assessment: process.assessment,
          assessmentResult: process.assessmentResult,
          challenge: challenge,
          stateInfo: stateInfo
        });
      };
      process.postSolutionUpdated = messageHubService.postSolutionUpdated;
      process.postSubmitted = messageHubService.postSubmitted;
      process.embedOptions = embedOptions;

      messageHubService.onNext = process.next;
      messageHubService.onPrevious = process.previous;
      messageHubService.onSwitchChallenge = function (challengeId) {
        var c = stages[challengeId] || allStages()[challengeId];
        if (!c) {
          // try to go by challenge ID
          c = stages.challenges.find(function (stage) {
            return stage.challengeId === challengeId;
          });
        }
        if (c) {
          $state.go(c.state, c.stateParams);
        } else {
          throw new Error('Unable to find challenge by index or id: ' + challengeId);
        }
      };
      messageHubService.onSubmit = function () {
        submitAssessment();
      };

      //=======================================================
      // EVENTS
      //=======================================================

      accountService.whenReady().then(function (account) {
        var promise = account.verified ? loadAssessmentResult() : $q.resolve();
        promise.catch(function () {
          return null;
        }).then(function (assessmentResult) {
          readyDeferred.resolve(process);

          // resolve assessmentResult because there's no way to continue if we can't show the welcome screen
          if (!assessmentResult && embedded && embedOptions.hideWelcome) {
            process.unableToLoadAssessmentResult = true;
            assessmentResultDeferred.resolve(null);
          }
        });
      });

      if (!auth.assessmentResult) {
        on('app.accountSet', function () {
          if (!process.assessmentResult && !loadAssessmentResultPromise) {
            process.assessmentLoaded = false;
            loadAssessmentResult();
          }
        });
      }

      // initial setup of challenge stages
      setupChallengeStages();

      on('app.logout', function ($event) {
        return $event.reload = true;
      });

      // update stages when the user manually clicks to switch
      on('$stateChangeSuccess', refreshStageStatuses);

      // track previous presence data sent
      var previousPresenceData = null;

      //=======================================================
      // RETURN OBJECT
      //=======================================================

      return process;

      //=======================================================
      // FUNCTIONS
      //=======================================================

      /**
       * Enters or updates presence on the active candidates team channel, which can be used
       * within Hire app to understand who is actively using the platform
       */
      function setPresence() {
        var user = accountService.get().user || {};
        var presenceData = {
          assessmentId: process.assessment.id,
          assessmentTitle: process.assessment.title,
          assessmentResultId: process.assessmentResult && process.assessmentResult.id,
          accessLocked: process.assessmentResult && process.assessmentResult.accessLocked,
          isTeamMember: process.isTeamMember(),
          isEmbedded: process.isEmbedded,
          name: user.name,
          email: process.auth.email,
          isActiveInteractSession: process.assessmentResult && process.assessmentResult.isActiveInteractSession
        };

        // try to minimize duplicate requests that could cause Ably message usage explosions
        if (previousPresenceData !== JSON.stringify(presenceData)) {
          socketService.enterPresence('candidates:' + process.assessment.teamId, presenceData);
          previousPresenceData = JSON.stringify(presenceData);
        }
      }

      /**
       * Marks the user as no longer present
       */
      function leavePresence() {
        socketService.leavePresence('candidates:' + process.assessment.teamId);
      }

      /**
       * After the user has logged in (or been verified by other means), we need to configure the
       * AssessmentResult and the stages for this current assessment app.
       */
      function loadAssessmentResult() {
        // no need to do this twice, or if the result is not present yet
        if (process.assessmentLoaded || !auth.assessmentResult) {
          // Attempting to log a rare issue where assessment result is null with no deny reason, in order to take future action when it comes up again.
          if (!auth.assessmentResult && !auth.denyReason && !auth.registrationRequired && !auth.emailVerificationRequired) {
            $log.error('Assessment Result is null, auth info: ' + auth, auth);
          }
          return $q.resolve(process.assessmentResult);
        }

        if (assessment.unavailable) {
          assessmentResultDeferred.resolve(null);
          return $q.resolve();
        }
        if (loadAssessmentResultPromise) return loadAssessmentResultPromise;

        var user = accountService.get().user;
        // set this here, just in case the registration process updated the auth after the initial initiation code was set.
        process.assessmentResult = auth.assessmentResult;
        loadAssessmentResultPromise = messageHubService.authorize(process.assessment).catch(function (err) {
          $state.go('error');
          return $q.reject(err, { error: err });
        }).then(function () {
          var assessmentResult = process.assessmentResult;
          if (!assessmentResult) return null;

          if (embedded) {
            // track that this assessment result was embedded
            tracker.trackEvent('Assessment Embedded', {
              id: assessmentResult.id,
              assessmentResultId: assessmentResult.id,
              assessmentId: assessment.id,
              assessmentTitle: assessment.title,
              teamId: assessmentResult.teamId,
              team: assessment.team && assessment.team.name
            });
            if (!assessmentResult.wasEmbedded) {
              // store back on the assessment result so we can show this if we want
              assessmentResultsService.update(assessmentResult, {
                wasEmbedded: true
              });
            }
          }

          // listen for changes to the assessment result object, in case it was closed externally
          watch(function () {
            return assessmentResult.state;
          }, function () {
            if (process.isFinished()) {
              // jump to the final screen if we've been closed
              next();
            }
          });

          // we want to send a fresh heartbeat on each stage change to ensure the activeTimedSolutionId gets set as quickly as possible
          watch(process.getCurrentStage, _heartbeat);

          watchCollection(function () {
            return [assessmentResult.isActiveInteractSession, process.wasAccessLocked, process.liveInterviewActive];
          }, setPresence);

          process.wasAccessLocked = assessmentResult.accessLocked;

          var enforcedTimeLimit = assessmentResult.enforcedTimeLimit;
          var estimatedTime = process.estimatedTime;


          if (enforcedTimeLimit && !process.invitationIssue) {
            process.configureCountDownTimer(enforcedTimeLimit * 60);
          } else if (estimatedTime && !process.invitationIssue) {
            process.configureCountUpTimer((estimatedTime || 0) * 60);
          } else {
            // clear dummy timer
            process.timer = null;
          }

          if (assessmentResult.isStarted()) {
            startTiming();
          }

          setupChallengeStages();
          refreshStageStatuses();
          assessmentResultDeferred.resolve(assessmentResult);

          socketService.create(accountService.get());

          process.liveInterview = liveInterviewService.join(assessmentResult, user);

          watch(function () {
            return process.liveInterview.usersList.length;
          }, function (length) {
            if (length > 1 && !process.liveInterviewActive) {
              process.liveInterviewActive = true;
              var _user = process.liveInterview.usersList.find(function (u) {
                return !u.$self;
              });
              appAlert.info(_user.name + ' has connected to start an interactive coding session.');
              $rootScope.$broadcast('liveInterviewStarted');
            }
          });

          var checkInteractive = function checkInteractive() {
            if (assessmentResult.isActiveInteractSession && !process.liveInterviewActive) {
              firebase.ifConnected().catch(function (err) {
                process.unableToConnect = err;
                appAlert.error('Unable to start an interactive session. "' + (err && err.message || err || 'Unknown Error') + '"', {
                  linkTitle: 'Click here for help',
                  linkAction: function linkAction() {
                    $window.open(appConfig.docsUrl + '/for-candidates/interact-issues');
                  }
                });
              });
            }
          };
          checkInteractive();
          watch(function () {
            return assessmentResult.isActiveInteractSession;
          }, checkInteractive);
          process.assessmentLoaded = true;
          return assessmentResult;
        }).catch(function (err) {
          if ($window.Sentry) {
            $window.Sentry.setContext({
              error: err,
              cookie: $document.cookie
            });
          }
          var reason = '';
          if (err) {
            messageHubService.postLoadError(err);
            reason = err.reason || err.statusText || err.message || '';
            $log.error('Unable to load assessment: ' + (reason || err.statusCode), err);
          }
          appAlert.error('Could not start the assessment at this time' + (reason && ' (' + reason + ')') + '. You may need to log out and reload this page. To log out, access the menu on the top left of the page.');
        }).finally(function () {
          loadAssessmentResultPromise = null;
        });

        return loadAssessmentResultPromise;
      }

      function startAssessmentResult() {
        return assessmentResultsService.start(process.assessmentResult.id).then(function () {
          return startTiming();
        }).catch(function (err) {
          if (err) {
            var code = err.status || err.statusCode || err.statusText || err.message;
            var msg = 'Unable to start assessment: ' + code;
            $log.error(msg, JSON.stringify(err));
          }

          messageHubService.postLoadError(err);
          if ($window.Sentry) {
            $window.Sentry.setContext({
              responseError: err,
              cookie: $document.cookie,
              assessmentResult: process.assessmentResult
            });
          }
          appAlert.error('Could not start the assessment at this time.');
          return $q.reject();
        });
      }

      function startTiming() {
        if (process.timer && !process.timer.isRunning()) {
          process.timer.start(process.assessmentResult);
        }
        if (!process._heartbeat) {
          _setHeartbeat();
        }
        if (!process._focusTracking) {
          process._focusTracking = true;
          on('document:focus', function () {
            process.background = false;
            _heartbeat();
          });
          on('document:blur', function () {
            // delay marking as background until after the event has been fully processed
            $timeout(function () {
              process.background = true;
            }, 1, false);
          });
          on('idleDetection', function ($event, isIdle) {
            process.isIdle = isIdle;
            _heartbeat();
          });
        }
      }

      function _clearHeartbeat() {
        _heartbeat();
        $interval.cancel(process._heartbeat);
      }

      function _setHeartbeat() {
        process._heartbeat = $interval(_heartbeat, heartbeatInterval * 1000);
      }

      function _heartbeat() {
        var stage = getCurrentStage();
        if (stage && stage.solution && !process.isIdle && !process.restricted) {
          var solution = stage.solution;

          var opts = {
            data: {
              background: process.background
            }
          };

          Solution.heartbeat(solution, opts);
        }
      }

      function submitAssessment() {
        var dialog = assessmentSubmittingDialog.open(process.assessmentResult).then(process.performPostAssessmentAction);
        var submit = assessmentResultsService.submit(process.assessmentResult).then(function () {
          cleanupOnSubmit();
          if (!$state.is('assessments.review')) {
            $state.go('assessments.review');
          }
        }).catch(function (resp) {
          assessmentSubmittingDialog.close();
          messageHubService.postError(ERROR_CONTEXT.submitAssessment, resp);
          return appAlert.modelError(resp);
        });

        return $q.all([dialog, submit]).then(function () {
          return {
            redirected: process.assessmentResult.hasPostAssessmentRedirect(),
            assessmentResult: process.assessmentResult
          };
        });
      }

      function _timerRanOut() {
        assessmentSubmittingDialog.open(process.assessmentResult, true).then(process.performPostAssessmentAction);
        AssessmentResult.find(process.assessmentResult.id, {
          bypassCache: true
        }).then(function (result) {
          // the server should auto-submit this, but if not we'll submit it.
          if (!result.isSubmitted()) {
            result.closedReason = 'timer_ran_out';
            return assessmentResultsService.submit(result);
          }
        })
        // because the server is handling this, we can ignore submission errors
        .catch(function () {
          return null;
        }).finally(function () {
          $state.go('assessments.review');
        });
      }

      //-------------------------------------------------------
      // Stage Management
      //-------------------------------------------------------

      /**
       * Creates a single, ordered array of all the stages in this application
       * @returns {Array.<Stage>}
       */
      function allStages() {
        return [].concat(stages.welcome, stages.challenges, stages.review);
      }

      /**
       * Returns the current stage based on the current $state (or welcome if none is found)
       * @returns {Stage}
       */
      function getCurrentStage() {
        // default to welcome if not on a valid URL
        var current = stages.welcome;
        allStages().forEach(function (stage) {
          if ($state.includes(stage.state, stage.stateParams)) {
            current = stage;
          }
        });
        return current;
      }

      function _getNextStage() {
        var currentStage = void 0;
        var nextStage = null;

        allStages().forEach(function (stage) {
          if (!currentStage) {
            if ($state.includes(stage.state, stage.stateParams)) {
              currentStage = stage;
            }
          } else if (!nextStage || nextStage.isCompleted()) {
            nextStage = stage;
          }
        });
        return nextStage;
      }

      function _getPreviousStage() {
        var currentStage = getCurrentStage();
        var stages = allStages();
        var idx = stages.findIndex(function (stage) {
          return stage === currentStage;
        });
        if (idx > 0) {
          return stages[--idx];
        }
      }

      /**
       * Returns to the previous challenge
       */
      function previous() {
        var previousStage = _getPreviousStage();
        refreshStageStatuses();
        if (previousStage) {
          $state.go(previousStage.state, previousStage.stateParams);
        }
      }

      /**
       * Skips to the next stage in the process, useful for calling after completing a stage
       */
      function next() {
        if (process.isFinished()) {
          $state.go('assessments.review').then(function () {
            return process.started = true;
          });
          return;
        }
        var nextStage = _getNextStage();
        refreshStageStatuses();
        if (nextStage) {
          $state.go(nextStage.state, nextStage.stateParams).then(function () {
            process.started = true;
          });
        }
      }

      function nextIsChallenge() {
        if (process.isFinished()) return false;
        var nextStage = _getNextStage();
        return nextStage && !!nextStage.challengeId;
      }

      /**
       * Loops through the assessment's ChallengeItems and creates a Stage for each one.
       *
       * If the AssessmentResult is loaded, then this will also configure things like
       * the Challenge title and the current status based on the solution.
       *
       */
      function setupChallengeStages() {
        var ar = process.assessmentResult;
        var solutions = ar ? Solution.filter({ assessmentResultId: ar.id }) : [];
        var challengeItems = process.challengeItems;

        stages.challenges = challengeItems.sort(function (a, b) {
          return a.position - b.position;
        }).map(function (challengeItem) {
          var challengeId = challengeItem.challengeId;
          var challengeStage = new Stage('assessments.challenges', { challengeId: challengeId }, ar);

          challengeStage.challengeId = challengeId;
          challengeStage.required = challengeItem.required;
          challengeStage.position = challengeItem.position;

          if (process.assessmentResult) {
            refreshStage(challengeStage, solutions.find(function (s) {
              return s.challengeId === challengeId;
            }));
          }

          return challengeStage;
        });

        process.challengeCount = stages.challenges.length;
      }

      /**
       * Updates the status on every stage
       */
      function refreshStageStatuses() {
        angular.forEach(allStages(), function (stage) {
          return refreshStage(stage);
        });
      }

      /**
       * Updates the status on a challenge stage
       *
       * @param stage the stage to refresh
       * @param solution Optional solution to set on the stage, used for determining status
       */
      function refreshStage(stage, solution) {
        if (stage.refresh) {
          stage.refresh();
        } else if (stage.challengeId) {
          var challenge = stage.challenge = stage.challenge || solution && solution.challenge;

          if (challenge) {
            stage.state = 'assessments.challenges.' + challenge.typeState();
          }

          if (solution) {
            stage.solution = solution;
          } else {
            solution = stage.solution;
          }

          var status = STAGE_STATUSES.NEW;
          var _setStatus = function _setStatus(_ref2) {
            var failed = _ref2.failed,
                completed = _ref2.completed;

            status = failed ? STAGE_STATUSES.FAILED : completed ? STAGE_STATUSES.COMPLETED : status;
          };

          if (solution) {
            status = STAGE_STATUSES.PARTIAL;
            var attempted = solution.attempted();
            var allowPartialPassing = !challenge.enforceAllTestsOnPublish;

            if (solution.isAdvancedCode()) {
              var candidateTestsDisabled = !challenge.candidateTestsEnabled;

              if (challenge.submissionFeedback) {
                var submissionFailed = solution.updateSubmissionFailedMessages().length;
                var submissionPassed = solution.isSubmitted() && !submissionFailed;
                // If we have submission feedback...
                if (submissionPassed) {
                  // ...and we are in the "passed" state, always mark as passed (ignore candidate tests)
                  _setStatus({ completed: true });
                } else if (candidateTestsDisabled) {
                  // ...and no candidate tests, then we use submission feedback for flagging failures
                  _setStatus({
                    failed: solution.isSubmitted() && submissionFailed
                  });
                } else {
                  // ...and candidate tests, then we use candidate tests for failed/partial
                  _setStatus({
                    // uses candidate test counts for failed flag, falls back to submission
                    // feedback for completed
                    failed: attempted && !solution.passed()
                  });
                }
              } else {
                _setStatus({
                  failed: solution.isSubmitted() && !solution.isSubmissionSuccess(),
                  // solution was submitted at all (assumes it'll get processed in the background)
                  completed: solution.isSubmissionSuccess()
                });
              }
            } else {
              // for other types, only set failed/completed flags if the code was attempted
              if (attempted) {
                if (solution.isCode()) {
                  _setStatus({
                    // no passed test cases at all (probably a runtime error)
                    failed: !solution.passedTests(),
                    // passed all tests or at least some if not expected to pass all
                    completed: allowPartialPassing || solution.passed()
                  });
                } else if (solution.isQuiz()) {
                  // for quizzes, we only mark partial/completed
                  _setStatus({
                    // either everything is answered, or time is up
                    completed: solution.allAnswered() || !solution.canStillAnswer()
                  });
                }
              }
            }

            if (solution.expired() && status !== STAGE_STATUSES.COMPLETED) {
              // always mark incomplete expired challenges as expired, because there's nothing the
              // candidate can do at this point.
              status = STAGE_STATUSES.EXPIRED;
            }
          }
          stage.status = status;
        }
      }

      function addSolution(solution) {
        refreshStage(getCurrentStage(), solution);
      }

      function restart() {
        if (process.assessmentResult) {
          return AssessmentResult.openWithAlert(process.assessmentResult).then(function () {
            return goToWelcomeAndReload();
          });
        }
        return $q.reject();
      }

      function reset(resetAll) {
        return AssessmentResult.resetWithAlert(process.assessmentResult, {
          data: { resetAll: resetAll }
        }).then(function () {
          return goToWelcomeAndReload();
        });
      }

      function performPostAssessmentAction() {
        var ar = process.assessmentResult;
        if (ar.retrySchedule || ar.nextAssessmentId) {
          // For now we are hard loading the page since there are some potential errors causing
          // goToWelcomeAndReload to fail (usually caused by a phantom challenges GET which I can't track down.
          // TODO: fix challenges API call and revert
          $window.location = '/assess/' + (ar.retrySchedule ? ar.assessmentId : ar.nextAssessmentId);
          // goToWelcomeAndReload({
          // 	assessmentId: ar.retrySchedule ? ar.assessmentId : ar.nextAssessmentId,
          // });
        } else if (ar.$redirectUrl) {
          // HACK: enables sending the candidate to the start screen
          // TODO: convert to valid feature later
          // TODO: also add close-tab as an option
          // TODO: allow logging out as an option on close tab and redirect
          if (/^http:\/\/logout$/.test(ar.$redirectUrl)) {
            process.logout();
          } else {
            $window.location = safeUrl(ar.$redirectUrl);
          }
        }
      }

      function goToWelcomeAndReload() {
        var params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;

        allModels.ejectAll({
          exclude: LOGOUT_EJECT_EXCLUDES
        });
        $state.go('assessments.welcome', params, { reload: true });
      }

      //-------------------------------------------------------
      // Event Management
      //-------------------------------------------------------

      function watch(val, listener) {
        return rootScopeListeners.push($rootScope.$watch(val, listener));
      }

      function watchCollection(val, listener) {
        return rootScopeListeners.push($rootScope.$watchCollection(val, listener));
      }

      function on(event, listener) {
        rootScopeListeners.push($rootScope.$on(event, listener));
      }

      function destroy() {
        leavePresence();
        angular.forEach(rootScopeListeners, function (off) {
          return off();
        });
        warnBeforeClosingListener();
      }

      function cleanupOnSubmit() {
        _clearHeartbeat();
        if (process.liveInterview) {
          // after we submit the assessment, there's no need to store anything about it in
          // Firebase (interviewers will automatically be disconnected)
          process.liveInterview.destroy();
        }
        process.timer && process.timer.stop();
      }

      function whenReady() {
        return readyDeferred.promise;
      }

      function whenAssessmentResultReady() {
        return assessmentResultDeferred.promise;
      }
    }
  }]);

  var assessmentsServiceModule = angular.module('Q.services.assessments-service', [assessmentModelModule]).factory('assessmentsService', ['Assessment', function (Assessment) {
    var defaultIncludes = ['team', 'team.customer'];

    return {
      find: find,
      findWithChallenges: findWithChallenges
    };

    function _find(id, params) {
      params.includes = [].concat(params.includes || [], defaultIncludes);
      return Assessment.fetch(id, { params: params });
    }

    function find(id) {
      return _find(id);
    }

    function findWithChallenges(id, params) {
      return _find(id, angular.merge(params, {
        includes: 'challenge_items.challenge'
      }));
    }
  }]);

  var uploadedImageTemplate = "<div class=\"uploaded-image\" ng-style=\"{ 'background-color': file.background, 'background-image': 'url(' + url + ')', width: imageWidth, height: imageHeight }\"></div> ";

  var uploadedImageComponentModule = angular.module('Q.components.uploaded-image', [cloudinaryServiceModule]).directive('uploadedImage', ['cloudinaryService', function (cloudinaryService) {
    return {
      restrict: 'AE',
      scope: {
        file: '=',
        size: '=',
        hardcodeSize: '=',
        defaultSrc: '@',
        width: '=?',
        height: '=?'
      },
      replace: true,
      template: uploadedImageTemplate,
      link: function link(scope) {
        scope.$watch('file.fileId || file', function () {
          if (scope.file) {
            scope.url = cloudinaryService.getUrl(scope.file, {
              width: scope.width,
              height: scope.height,
              retina: true
            });
          } else if (scope.defaultSrc) {
            scope.url = scope.defaultSrc;
          }
        });
        scope.width = scope.width || scope.size || 100;
        scope.imageWidth = scope.hardcodeSize === false ? '' : scope.width + 'px';
        scope.imageHeight = scope.hardcodeSize === false ? '' : (scope.height || scope.width) + 'px';
      }
    };
  }]);

  var timeRemainingFilterModule = angular.module('Q.filters.time-remaining', [momentModule]).filter('timeRemaining', ['moment', function (moment$$1) {
    return function timeAgo(value, format) {
      if (typeof value === 'undefined' || value === null) {
        return '';
      }

      var d = moment$$1.duration(value, format);
      var mins = d.minutes();
      var hours = Math.floor(d.asMinutes() - mins) / 60;

      var pluralize = function pluralize(s, count) {
        return count === 1 ? s : s + 's';
      };

      if (hours > 0) {
        return hours + ' ' + pluralize('hour', mins) + ', ' + mins + ' ' + pluralize('minute', mins);
      } else if (mins > 0) {
        return mins + ' ' + pluralize('minute', mins);
      } else {
        return 'less than a minute';
      }
    };
  }]);

  var timerWidgetTemplate = " <div class=\"count-up-timer\" ng-if=\"$ctrl.countUp\"></div> <div class=\"count-down-timer\" ng-if=\"!$ctrl.countUp\"> <svg width=\"200\" height=\"200\" viewBox=\"0 0 200 200\" xmlns=\"http://www.w3.org/2000/svg\"> <g fill=\"none\" fill-rule=\"evenodd\"> <path d=\"M100 183.49c-17.292 0-33.548-6.734-45.778-18.963-12.23-12.23-18.962-28.486-18.962-45.778 0-17.295 6.733-33.55 18.962-45.78C65.966 61.23 83.428 54.05 100 54.05c16.398 0 34.046 7.185 45.79 18.922 12.23 12.23 18.962 28.485 18.962 45.777 0 17.29-6.733 33.545-18.962 45.775-12.242 12.23-28.498 18.962-45.79 18.962z\" class=\"watch-background\" fill=\"#FFF\"/> <path d=\"M100 54.05c16.572 0 34.034 7.178 45.778 18.922 12.23 12.23 18.962 28.485 18.962 45.777 0 17.29-6.733 33.545-18.962 45.775-12.23 12.23-28.486 18.962-45.778 18.962V54.05z\" class=\"timer-right-half\" fill=\"#FFF\"/> <path d=\"M100 54.05c-16.572 0-34.034 7.178-45.778 18.922-12.23 12.23-18.962 28.485-18.962 45.777 0 17.29 6.733 33.545 18.962 45.775 12.23 12.23 28.486 18.962 45.778 18.962V54.05z\" class=\"timer-left-half\" fill=\"#FFF\"/> <path d=\"M106.25 37.737c41.95 3.194 75 38.244 75 81.013 0 44.875-36.375 81.25-81.25 81.25s-81.25-36.375-81.25-81.25c0-42.77 33.05-77.82 75-81.013V25h-25V12.5c0-6.906 5.594-12.5 12.5-12.5h37.5c6.906 0 12.5 5.594 12.5 12.5V25h-25v12.737zM100 181.25c16.694 0 32.387-6.5 44.206-18.306 11.806-11.806 18.306-27.5 18.306-44.194 0-16.694-6.5-32.388-18.306-44.194C132.87 63.226 115.83 56.288 100 56.288c-16 0-32.856 6.93-44.194 18.268C44 86.362 37.5 102.056 37.5 118.75c0 16.694 6.5 32.387 18.306 44.194C67.612 174.75 83.306 181.25 100 181.25z\" class=\"watch-frame\" fill=\"#AAA\"/> <g fill=\"#AAA\"> <path d=\"M36.037 115.784h10.447v5.97H36.037z\"/> <path d=\"M153.374 115.784h10.447v5.97h-10.444z\"/> <path d=\"M102.716 54.537v10.447h-5.97V54.537\"/> <path d=\"M102.716 171.874v10.447h-5.97v-10.444\"/> </g> <path d=\"M99.9 62.732c-.56 0-1.052.442-1.104 1l-5.01 53.887c-.36 5.134 2.433 7.38 6.22 7.38 3.788 0 6.582-2.25 6.22-7.38l-5.016-53.89c-.05-.55-.544-1-1.103-1H99.9z\" class=\"watch-hand\" fill=\"#353536\"/> </g> </svg> </div> <span class=\"time\" ng-show=\"$ctrl.showTime\"></span> ";

  var AUTO_HIDE_TIMER = 20000; // Autohide the timer after 20 seconds

  var COLORS = [[0, '#AAAAAA'], [40, '#AAAAAA'], [50, '#4BB05D'], [70, '#4BB05D'], [85, '#EEB854'], [98, '#B63D4C'], [100, '#B63D4C']];

  function progressToColors(progress) {
    for (var i = 1; i < COLORS.length; i++) {
      var c = COLORS[i];
      if (progress === c[0]) {
        return { c1: c[1], c2: c[1], ratio: 0 };
      }
      if (progress < c[0]) {
        var c1 = COLORS[i - 1];
        var ratio = (progress - c1[0]) / (c[0] - c1[0]);
        return { c1: c1[1], c2: c[1], ratio: ratio };
      }
    }
  }

  var timerWidgetComponentModule = angular.module('Q.components.timer-widget', [timerHelperModule, secondsToTimeFilterModule, timeRemainingFilterModule]).component('timerWidget', {
    bindings: {
      timer: '<',
      timerOnly: '<',
      showOnChange: '<'
    },
    template: timerWidgetTemplate,
    controller: 'TimerWidgetCtrl'
  }).controller('TimerWidgetCtrl', ['$scope', '$element', '$filter', '$timeout', 'timerHelper', function TimerWidgetCtrl($scope, $element, $filter, $timeout, timerHelper) {
    var ctrl = this;

    var timeRemaining = $filter('timeRemaining');
    var secondsToTime = $filter('secondsToTime');

    function updateTimer() {
      var timer = ctrl.timer;
      var timerTime = timer ? timer.time : 0;
      var progress = timer ? timer.progress() : 0;
      var progressColorRGB = blend(progressToColors(progress));
      var progressColor = rgb2hex(progressColorRGB);

      var timeText = $element.querySelector('.time');
      var text = '--:--:--';
      if (timer) {
        if (ctrl.timerOnly) {
          // show a human formatted remaining minutes, rounded down
          text = timeRemaining(timerTime, 's');
          timeText.css('color', progressColor);
        } else {
          text = secondsToTime(timerTime);
        }
      }
      timeText.text(text);

      if (ctrl.countUp) {
        $element.querySelector('.count-up-timer').css('color', progressColor);
      } else {
        var rightHalf = $element.querySelector('svg .timer-right-half');
        var leftHalf = $element.querySelector('svg .timer-left-half');
        var watchHand = $element.querySelector('svg .watch-hand');
        var progressFill = blend2hex({
          c1: '#FFFFFF',
          c2: progressColorRGB,
          ratio: 0.5
        });

        watchHand.css('transform', 'rotate(' + progress * 3.6 + 'deg)');

        // For the following, we're using two half circles that overlap. If we're below 50,
        // the left side hides some part of the right side, and the right rotates around up to 50%
        // Once we're over 50%, the right is static, and the left now rotates through a half turn
        // This creates the illusion of a radial fill
        leftHalf.toggleClass('timer-progress-colors', progress >= 50);
        if (progress < 50) {
          rightHalf.css({
            transform: 'rotate(' + (progress - 50) * 3.6 + 'deg)',
            fill: progressFill
          });
          leftHalf.css({
            transform: 'rotate(0)',
            fill: null
          });
        } else {
          rightHalf.css({
            transform: 'rotate(0)',
            fill: progressFill
          });
          leftHalf.css({
            transform: 'rotate(' + (progress - 100) * 3.6 + 'deg)',
            fill: progressFill
          });
        }
      }
    }

    var timerTickListenerOff = null;

    function listen() {
      if (ctrl.timer) {
        timerTickListenerOff = ctrl.timer.on('tick', updateTimer);
      }
    }

    function stopListening() {
      if (timerTickListenerOff) timerTickListenerOff();
      timerTickListenerOff = null;
    }

    function initTimer() {
      ctrl.countUp = !!(ctrl.timer && ctrl.timer.isCountUp);
      stopListening();
      listen();
      toggleTime(!!ctrl.timer);
    }

    var manualTimeChange = false;
    var autoTimerHide = null;

    function toggleTime() {
      var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : !ctrl.showTime;

      ctrl.showTime = state;
      updateTimer();

      if (autoTimerHide) {
        timerHelper.longTimeout.cancel(autoTimerHide);
        autoTimerHide = null;
      }

      if (state && !manualTimeChange) {
        autoTimerHide = timerHelper.longTimeout(function () {
          toggleTime(false);
        }, AUTO_HIDE_TIMER);
      }
    }

    ctrl.$onInit = function () {
      initTimer();

      if (ctrl.timerOnly) {
        $element.addClass('timer-only');
      }

      $element.on('click', function () {
        if (ctrl.timer) {
          manualTimeChange = true;
          $scope.$applyAsync(function () {
            return toggleTime();
          });
        }
      });

      // force re-rendering after the template has loaded
      $timeout(updateTimer);

      ctrl.$onChanges = function (changes) {
        if (changes.timer) {
          initTimer();
        }
        if (changes.showOnChange && !manualTimeChange) {
          toggleTime(true);
        }
      };
    };
  }]);

  var assessmentsTemplate = "<div class=\"assessments app\" id=\"app\" ng-class=\"{ 'has-sample-banner': assessments.isPractice(), 'has-sidebar': !assessments.process.embedOptions.hideSidebar }\"> <div class=\"sample-banner\" ng-if=\"assessments.isPractice()\"> <div class=\"practice\"> <h4> <span class=\"qualified-logo-full-color\"><span>Qualified</span></span> Practice Assessment </h4> <div class=\"buttons\"> <a ng-if=\"assessments.canClose()\" class=\"return\" ng-href=\"{{ $stateParams.returnTo }}\" ng-click=\"assessments.close($event)\"> Return to Your Assessment </a> </div> </div> </div> <product-message-banner></product-message-banner> <compile-template src=\"assessments.navTemplate\" ng-if=\"!assessments.process.embedOptions.hideSidebar\" class=\"{{ $state.params.challengeId && $root.$ideDarkClass }}\"></compile-template> <div class=\"app-content\"> <div ui-view class=\"app-content-body\"></div> </div> </div> <div class=\"highlight-background\" ng-click=\"assessments.process.stopHighlight()\" ng-if=\"assessments.process.isHighlighting()\"></div> ";

  var assessmentsNavTemplate = "<nav class=\"sidebar assessment-sidebar {{ $root.$ideDarkClass }}\" ng-class=\"{ 'can-edit': assessments.process.canEdit && assessments.proctoringSatisfied(), 'can-not-edit': !assessments.process.canEdit || !assessments.proctoringSatisfied() }\"> <div class=\"account-menu dropdown\" dropdown dropdown-position=\"right\" ng-if=\"!assessments.process.isEmbedded\"> <a class=\"dropdown-toggle\" dropdown-toggle tooltip=\"Menu\" tooltip-position=\"right\"> <span class=\"sr-only\">Menu</span> </a> <ul class=\"dropdown-menu\" dropdown-menu> <li ng-if=\"assessments.isTeamMember()\"> <a ng-href=\"{{ assessments.hireUrl }}\" class=\"icon-undo\"> Back to the Team Account </a> </li> <li> <a href=\"{{::$root.appConfig.docsUrl}}/for-candidates\" target=\"_blank\" rel=\"noopener\" class=\"icon-tutorial\"> Documentation </a> </li> <li ng-if=\"!assessments.assessment.isPractice\"> <a ng-click=\"assessments.showHelp()\" class=\"icon-help\"> Get Help </a> </li> <li class=\"divider\"></li> <li> <a ng-click=\"assessments.account.user && assessments.process.editProfile()\" ng-disabled=\"!assessments.account.user\" class=\"icon-user\"> Edit Your Profile </a> </li> <li> <a ng-click=\"assessments.account.user && assessments.process.logout()\" ng-disabled=\"!assessments.account.user\" class=\"icon-blank\"> Log Out </a> </li> <li class=\"divider\"></li> <li> <a href=\"/\" target=\"_blank\" rel=\"noopener\"> Powered by &nbsp;<span class=\"qualified-logo-full-color\"><span>Qualified</span></span> </a> </li> </ul> </div> <ul class=\"assessment-nav\" ng-class=\"{ highlighting: assessments.process.isHighlighting() }\"> <li class=\"stage info\" ng-if=\"!assessments.process.isFinishedOrInvalid() && !assessments.process.embedOptions.hideWelcome\" ui-sref-active=\"active\"> <a ui-sref-no-link=\"assessments.welcome()\" tooltip=\"Welcome &amp; Instructions\" tooltip-position=\"right\" tooltip-delay=\"0\"></a> </li> <li ng-repeat=\"item in assessments.process.stages.challenges\" ng-if=\"!assessments.process.isFinishedOrInvalid()\" ng-class=\"{ highlighted: assessments.process.isHighlighting('stage') }\" class=\"stage challenge status-{{ item.status }} has-status-icon\" ui-sref-active=\"active\"> <a ui-sref-no-link=\"{{item.state}}({{ item.stateParams }})\" ui-sref-disable-on-transition=\"true\" ng-disabled=\"!assessments.process.canEdit || !assessments.proctoringSatisfied()\" tooltip=\"Challenge #{{ ::($index + 1) }}{{ item.tooltip() }}\" tooltip-position=\"right\" tooltip-delay=\"0\"> <span ng-bind=\"::($index + 1)\" class=\"index digits-{{::($index+1).toString().length}}\"></span> </a> </li> <li class=\"stage submit has-status-icon\" ng-if=\"!assessments.process.isFinishedOrInvalid() && !assessments.process.assessment.unavailable && !assessments.process.embedOptions.hideReview\" ng-class=\"{ 'ready-to-submit': assessments.process.isFullyComplete(), highlighted: assessments.process.isHighlighting('submit') }\" ui-sref-active=\"active\"> <a ui-sref-no-link=\"assessments.review()\" ng-disabled=\"!assessments.process.canEdit || !assessments.proctoringSatisfied()\" tooltip=\"Review &amp; Submit {{ assessments.process.assessment.nounUpper }}\" tooltip-position=\"right\" tooltip-delay=\"0\"></a> </li> <li ng-if=\"assessments.process.isFinishedOrInvalid() && !assessments.process.invitationIssue\" class=\"stage review status-completed has-status-icon\" ui-sref-active=\"active\"> <a ui-sref-no-link=\"assessments.review()\" tooltip=\"Review\" tooltip-position=\"right\" tooltip-delay=\"0\"></a> </li> </ul> <timer-widget ng-class=\"{ highlighted: assessments.process.isHighlighting('timer') }\" ng-if=\"assessments.process.showTimer\" tooltip=\"Click to toggle timer display\" tooltip-position=\"right\" timer=\"assessments.process.timer\" show-on-change=\"$state.current.name + $state.params.challengeId\"></timer-widget> <div class=\"powered-by-qualified\"> <a href=\"https://www.qualified.io\" target=\"_blank\" rel=\"noopener\" tooltip=\"Powered by Qualified.io\" tooltip-position=\"right\"> <span class=\"qualified-icon-full-color{{$state.params.challengeId && $root.$ideDark?'-white':''}}\"><span>Q</span></span> </a> </div> </nav> ";

  var uiSrefNoLinkDirectiveModule = angular.module('Q.components.ui-sref-no-link', [uiRouterStateModule])

  /**
   * @ngdoc directive
   * @name uiSrefNoLink
   * @description
   * Directive that replicates ui-sref without setting the href property on a link.
   * This is used in the Assess app to prevent opening challenges in a new window.
   *
   * @element ANY
   * @param {template} uiSrefNoLink sref
   * @param {template} uiSrefDisableOnTransition Disable while in a transition
   * @param {expression=} uiSrefOpts Options to pass to the state
   */
  .directive('uiSrefNoLink', ['$state', function ($state) {
    function parseStateRef(ref, current) {
      var preparsed = ref.match(/^\s*({[^}]*})\s*$/);
      var parsed = void 0;
      if (preparsed) ref = current + '(' + preparsed[1] + ')';
      parsed = ref.replace(/\n/g, ' ').match(/^([^(]+?)\s*(\((.*)\))?$/);
      if (!parsed || parsed.length !== 4) throw new Error('Invalid state ref \'' + ref + '\'');
      return { state: parsed[1], paramExpr: parsed[3] || null };
    }

    return {
      restrict: 'A',
      require: ['?^uiSrefActive', '?^uiSrefActiveEq'],
      link: function link(scope, element, attrs, uiSrefActive) {
        var ref = parseStateRef(attrs.uiSrefNoLink, $state.current.name);
        var def = { state: ref.state, params: null };
        var active = uiSrefActive[1] || uiSrefActive[0];

        def.options = angular.extend({
          relative: $state.$current,
          inherit: true
        }, attrs.uiSrefOpts ? scope.$eval(attrs.uiSrefOpts) : {});

        var update = function update(val) {
          if (val) def.params = angular.copy(val);
          // eslint-disable-next-line angular/no-private-call
          if (active) active.$$addStateInfo(ref.state, def.params);
        };

        if (ref.paramExpr) {
          scope.$watch(ref.paramExpr, function (val) {
            if (val !== def.params) update(val);
          }, true);
          def.params = angular.copy(scope.$eval(ref.paramExpr));
        }
        update();

        element.bind('click', function (event) {
          stopEvent(event);
          var disabled = element.attr('disabled') || attrs.uiSrefDisableOnTransition && $state.transition;
          if (!disabled && (def.options.reload || !$state.is(def.state, def.params))) {
            // don't redirect if nothing has changed
            $state.go(def.state, def.params, def.options);
          }
        });
      }
    };
  }]);

  // Because base64 encoding includes URL-unfriendly characters (/, +, and =), we map them
  // to URL-safe characters _, -, and ~ (tilde).
  // This means we don't have to escape the characters. We also get the benefit of slightly better obfuscation,
  // since the value doesn't look as much like a base64 encoding.
  var base64Map = { '/': '_', '+': '-', '=': '~' };
  var base64MapReverse = Object.entries(base64Map).reduce(function (o, _ref) {
    var _ref2 = slicedToArray(_ref, 2),
        k = _ref2[0],
        v = _ref2[1];

    o[v] = k;
    return o;
  }, {});

  /**
   * Reverses an obfuscation performed by obfuscateId.
   *
   * @param {string} obfuscatedId - Obfuscated string. Should match /^[-_~a-zA-Z0-9]+$/. The input is not validated,
   * and could throw if it contains invalid characters.
   *
   * @returns {string} Original ID as a hexadecimal string
   */
  function deobfuscateId(obfuscatedId) {
    return atob(obfuscatedId.replace(/[-_~]/g, function (v) {
      return base64MapReverse[v];
    })).split('').map(function (c) {
      return padNum(c.charCodeAt(0).toString(16));
    }).join('');
  }

  // A live webcam is deliberately not enough: blocked uploads leave the camera running with
  // nothing stored. Photos already on the result count too, since the photo manager is rebuilt
  // empty on every page load.
  function isProctoringSatisfied() {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        _ref$proctoringEnable = _ref.proctoringEnabled,
        proctoringEnabled = _ref$proctoringEnable === undefined ? false : _ref$proctoringEnable,
        _ref$proctoringDisabl = _ref.proctoringDisabled,
        proctoringDisabled = _ref$proctoringDisabl === undefined ? false : _ref$proctoringDisabl,
        _ref$photoStored = _ref.photoStored,
        photoStored = _ref$photoStored === undefined ? false : _ref$photoStored,
        _ref$resultHasStoredP = _ref.resultHasStoredPhotos,
        resultHasStoredPhotos = _ref$resultHasStoredP === undefined ? false : _ref$resultHasStoredP;

    if (!proctoringEnabled || proctoringDisabled) {
      return true;
    }

    return photoStored || resultHasStoredPhotos;
  }

  // A reloading candidate is let straight back in by the gate above, so nothing else would
  // restart their camera. The getUserMedia grant survives the reload, so this needs no click.
  function shouldResumeProctoringCapture() {
    var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        _ref2$proctoringEnabl = _ref2.proctoringEnabled,
        proctoringEnabled = _ref2$proctoringEnabl === undefined ? false : _ref2$proctoringEnabl,
        _ref2$proctoringDisab = _ref2.proctoringDisabled,
        proctoringDisabled = _ref2$proctoringDisab === undefined ? false : _ref2$proctoringDisab,
        _ref2$resultHasStored = _ref2.resultHasStoredPhotos,
        resultHasStoredPhotos = _ref2$resultHasStored === undefined ? false : _ref2$resultHasStored,
        _ref2$running = _ref2.running,
        running = _ref2$running === undefined ? false : _ref2$running;

    if (!proctoringEnabled || proctoringDisabled || running) {
      return false;
    }

    return resultHasStoredPhotos;
  }

  var DEFAULT_HEARTBEAT = 10;

  var assessmentsRouteModule = angular.module('Q.routes.assessments', [uiRouterStateModule, stateHelperModule, welcomeRouteModule, challengesRouteModule, secondsToTimeFilterModule, reviewRouteModule, assessmentResultProcessServiceModule, assessmentsServiceModule, assessmentResultsServiceModule, photoManagerServiceModule, accountServiceModule, uploadedImageComponentModule, timerWidgetComponentModule, uiSrefNoLinkDirectiveModule, resetAssessmentComponentModule, productMaintenanceServiceModule, appAlertComponentModule, productMessageBannerComponentModule, messageHubServiceModule, dialogComponentModule]).config(['$stateProvider', '$urlRouterProvider', '$urlMatcherFactoryProvider', function ($stateProvider, $urlRouterProvider, $urlMatcherFactoryProvider) {
    var ObjectOrQId = $urlMatcherFactoryProvider.type('ObjectOrQId');
    $urlMatcherFactoryProvider.type('AssessmentId', {
      pattern: new RegExp('practice(?:-\\w+)*|QE[-~\\w]{16}|' + ObjectOrQId.pattern.source)
    });

    // redirects for the old `welcome` routes
    $urlRouterProvider.when('/{assessmentId:AssessmentId}/welcome', '/{assessmentId}');
    $urlRouterProvider.when('/{assessmentId:AssessmentId}/welcome/', '/{assessmentId}');

    var assessmentTitle = void 0;

    $stateProvider.state('assessments', {
      abstract: true,
      url: '/{assessmentId:AssessmentId}?fromSetup&returnTo&redirectUrl&invite',
      params: {
        fromSetup: null,
        returnTo: null,
        redirectUrl: null,
        invite: null
      },
      template: assessmentsTemplate,
      data: {
        bodyClass: 'assessments',
        pageTitle: function pageTitle() {
          return assessmentTitle ? assessmentTitle : 'Assessment';
        }
      },
      resolve: {
        assessmentResultAuth: /* @ngInject */['$stateParams', 'invitationsService', 'messageHubService', '$q', 'appAlert', function assessmentResultAuth($stateParams, invitationsService, messageHubService, $q, appAlert) {
          return messageHubService.whenReady().then(function () {
            var accessToken = $stateParams.invite;
            var assessmentId = $stateParams.assessmentId;
            if (/^QE/.test(assessmentId)) {
              if (embedded) {
                assessmentId = deobfuscateId(assessmentId.slice(2));
              } else {
                throw new Error('This URL only works while embedded');
              }
            }
            return invitationsService.authorize(accessToken, assessmentId).then(function (auth) {
              if (auth.assessment) {
                messageHubService.postLoaded({
                  assessment: auth.assessment
                });
              }
              return auth;
            }).catch(function (err) {
              if (err.data && err.data.reason) {
                appAlert.error(err.data.reason);
              }

              return $q.reject(err);
            });
          });
        }],
        assessment: /* @ngInject */['assessmentResultAuth', function assessment(assessmentResultAuth) {
          return assessmentResultAuth.assessment;
        }],
        maintenanceModeCheck: /* @ngInject */['assessmentResultAuth', 'productMaintenanceService', 'stateHelper', function maintenanceModeCheck(assessmentResultAuth, productMaintenanceService, stateHelper) {
          return !!assessmentResultAuth.candidate || productMaintenanceService.checkLogin(assessmentResultAuth.assessment).catch(function () {
            return stateHelper.redirectInResolve.apply(stateHelper, toConsumableArray(productMaintenanceService.errorState()));
          });
        }],
        config: /* @ngInject */['configService', function config(configService) {
          return configService.load().catch(function () {
            return null;
          });
        }],
        process: /* @ngInject */['assessmentResultProcess', 'config', 'assessmentResultAuth', function process(assessmentResultProcess, config, assessmentResultAuth) {
          var heartbeat = config && config.challengeHeartbeat || DEFAULT_HEARTBEAT;
          return assessmentResultProcess.create(assessmentResultAuth, heartbeat).whenReady();
        }],
        photoManager: /* @ngInject */['photoManagerService', function photoManager(photoManagerService) {
          return photoManagerService.build();
        }],
        proctoringSatisfied: /* @ngInject */['assessment', 'photoManager', 'process', function proctoringSatisfied(assessment, photoManager, process) {
          return function () {
            var assessmentResult = process.assessmentResult;


            return isProctoringSatisfied({
              proctoringEnabled: assessment.proctoringEnabled(),
              proctoringDisabled: !!(assessmentResult && assessmentResult.proctoringDisabled),
              photoStored: photoManager.hasStoredPhoto(),
              resultHasStoredPhotos: !!(assessmentResult && assessmentResult.hasProctoringImages)
            });
          };
        }]
      },
      controller: /* @ngInject */['$scope', 'assessment', 'assessmentResultAuth', 'process', 'accountService', '$stateParams', '$window', '$state', 'resetAssessmentDialog', 'dialogService', 'photoManager', 'proctoringSatisfied', '$log', function loginCtrl($scope, assessment, assessmentResultAuth, process, accountService, $stateParams, $window, $state, resetAssessmentDialog, dialogService, photoManager, proctoringSatisfied, $log) {
        // clears the invite key when logging out
        if (assessment && assessment.inviteOnly) {
          appConfig.resetOnLogout = $state.href('loggedOut');
        } else {
          appConfig.resetOnLogout = $state.href('assessments', {
            invite: null
          });
        }

        var ctrl = this;

        ctrl.navTemplate = assessmentsNavTemplate;

        var opener = null;
        try {
          if ($window.opener && $window.opener.location.host === $window.location.host) {
            opener = $window.opener;
          }
        } catch (ex) {}

        ctrl.hireUrl = appConfig.hireRoute;

        // if we've loaded an assessment, set up the reset return path for errors
        appConfig.onResetReturn = !!assessment && $state.href('.');

        ctrl.proctoringSatisfied = proctoringSatisfied;

        // Best effort: their stored photos already satisfy the gate, so failing here must not
        // lock them out of an assessment they are part-way through.
        var resumeProctoringCapture = function resumeProctoringCapture() {
          var assessmentResult = process.assessmentResult;


          if (!assessmentResult || !shouldResumeProctoringCapture({
            proctoringEnabled: assessment.proctoringEnabled(),
            proctoringDisabled: !!assessmentResult.proctoringDisabled,
            resultHasStoredPhotos: !!assessmentResult.hasProctoringImages,
            running: photoManager.running()
          })) {
            return;
          }

          photoManager.start({
            assessmentResultId: assessmentResult.id,
            teamCandidateId: assessmentResult.teamCandidateId
          })
          // No welcome photo covers a resumed session.
          .then(function () {
            return photoManager.takeAssessmentPhotos({ coversSessionStart: true });
          }).catch(function (err) {
            $log.error('Proctoring: could not resume capture on reload', err);
          });
        };
        process.whenAssessmentResultReady().then(resumeProctoringCapture);
        ctrl.account = accountService.get();
        ctrl.assessment = assessment;
        ctrl.isTeamMember = process.isTeamMember;
        ctrl.isPractice = process.isPractice;
        ctrl.isDemo = process.isDemo;
        ctrl.isSample = process.isSample;
        ctrl.hasCodeChallenges = process.hasCodeChallenges;
        ctrl.fromSetup = $stateParams.fromSetup;
        ctrl.canClose = function () {
          return !!opener || process.isTeamMember() || process.isSample() && $stateParams.returnTo;
        };
        ctrl.restart = function () {
          if (process.isTeamMember()) {
            resetAssessmentDialog.open(process);
          } else {
            process.restart();
          }
        };
        ctrl.performPostAssessmentAction = process.performPostAssessmentAction;
        ctrl.close = function ($event) {
          var target = $event.target;
          var link = target && target.getAttribute('href');
          if (opener) {
            var linkPath = target && target.pathname || '';
            var windowPath = opener.location.pathname || '';
            var windowRoot = windowPath.match(/^\/\w+/);
            if (!link || !windowRoot || linkPath.indexOf(windowRoot[0]) === 0) {
              stopEvent($event);
              if (link) {
                opener.location = link;
              }
              if (opener.focus) {
                opener.focus();
              }
              $window.close();
              return;
            }
            // we've got an opener, but it's to a different root app, so leave it be
          }

          if (!link && process.isTeamMember()) {
            stopEvent($event);
            // redirect to hire route
            $window.location = appConfig.hireRoute;
          }
        };

        ctrl.process = process;

        ctrl.showHelp = function () {
          var teamHelp = void 0;
          if (assessment.team && assessment.team.supportLink) {
            teamHelp = '<a href="' + escapeHTML(assessment.team.supportLink) + '">please click here to contact ' + escapeHTML(assessment.team.name) + '</a>';
          } else {
            teamHelp = 'please reach out to your contact at ' + (assessment.team ? escapeHTML(assessment.team.name) : 'the team that invited you.');
          }
          dialogService.alert('<p>\nIf you are having trouble with your ' + escapeHTML(assessment.nounLower) + ',\n<strong>' + teamHelp + '</strong>.\n<br/>\nThey will be best able to help you work through issues with solving challenges.\n</p>\n<hr>\n<p class="help-block">\nYou can also\n<a href="' + appConfig.docsUrl + '/for-candidates" target="_blank" rel="noopener">use our documentation</a>\nfor help with using the Qualified application.\n</p>', 'Getting Help');
        };

        $scope.$on('$destroy', function () {
          ctrl.process.destroy();
          photoManager.stop();
        });
      }],
      controllerAs: 'assessments'
    });
  }]);

  var loggedOutTemplate = "<h1> <span class=\"qualified-logo-full-color\"><span>Qualified</span></span> </h1> <h2>You have been logged out</h2> <p>Thank you for completing the {{noun}}. You may now close this tab.</p> <div class=\"footer legal-links\"> <legal-links hide-contact=\"true\"></legal-links> </div> ";

  var loggedOutRouteModule = angular.module('Q.routes.logged-out', [legalLinksComponentModule]).config(['$stateProvider', function ($stateProvider) {
    $stateProvider.state('loggedOut', {
      url: '/logged-out',
      params: {
        noun: null
      },
      template: loggedOutTemplate,
      data: {
        pageTitle: 'You have been logged out',
        bodyClass: 'logged-out'
      },
      resolve: {
        accountServiceCheck: /* @ngInject */['accountService', function accountServiceCheck(accountService) {
          return accountService.whenReady();
        }]
      },
      controller: /* @ngInject */['accountServiceCheck', '$scope', '$stateParams', function controller(accountServiceCheck, $scope, $stateParams) {
        $scope.noun = $stateParams.noun || 'assessment';
      }]
    });
  }]);

  // Configure global state settings here, as well as depend on all the
  // routes needed for the app to start

  // The order of the routes matters, so we define the config in it's own module,
  // then each module in the priority we need the routes to load
  var allRoutesModule = angular.module('Q.routes.all', [uiRouterModule, permissionsServiceModule, sharedRouteConfigModule, assessmentsRouteModule, loggedOutRouteModule]);

  function getParentStateName(state) {
    return state.parent || state.name.match(/\./) && state.name.split(/\./)[0];
  }

  var setPageTitle = angular.noop;

  var stateSpecificComponentModule = angular.module('Q.components.state-specific', []).factory('titleFormatter', function () {
    var separator = ' - ';
    return function (baseTitle, pageTitle) {
      var titles = [].concat(pageTitle || []);
      titles.unshift(baseTitle);
      return titles.reverse().join(separator);
    };
  }).directive('body', function () {
    var bodyClass = void 0;
    return {
      restrict: 'E',
      link: function link(scope, el) {
        scope.$on('$stateChangeSuccess', function (evt, toState) {
          if (bodyClass) {
            el.removeClass(bodyClass);
          }
          bodyClass = toState.data && toState.data.bodyClass;
          if (bodyClass) {
            el.addClass(bodyClass);
          }
        });
      }
    };
  }).directive('title', ['titleFormatter', '$injector', function (titleFormatter, $injector) {
    var baseTitle = void 0;
    return {
      restrict: 'E',
      link: function link(scope, el) {
        baseTitle = [el.text()];
        setPageTitle = function setPageTitle(pageTitle) {
          el.text(titleFormatter(baseTitle, pageTitle));
        };
        scope.$on('$stateChangeSuccess', function (evt, toState) {
          var pageTitle = toState.data && toState.data.pageTitle;
          if (pageTitle && !angular.isString(pageTitle)) {
            pageTitle = $injector.invoke(pageTitle);
          }

          setPageTitle(pageTitle);
        });
      }
    };
  }]).factory('nestedPageTitleHelper', ['$state', '$injector', function ($state, $injector) {
    function getTitle(val) {
      if (!angular.isString(val)) {
        val = $injector.invoke(val);
      }
      return val;
    }

    return function (myState, myTitle) {
      var current = $state.get(myState);
      var currentTitle = getTitle(myTitle);
      var parentName = getParentStateName(current);
      var parent = parentName ? $state.get(parentName) : false;
      var parentTitle = [];
      if (parent && parent.data && parent.data.pageTitle) {
        parentTitle = getTitle(parent.data.pageTitle);
      }
      return [].concat(parentTitle, currentTitle);
    };
  }]);

  /**
   * @docType shared
   * @category directive
   * @name Autosave
   * @example
   *   <form ng-submit="myControl.save()" autosave has-changes="myControl.hasChanges()" autosave-debounce="5000">...</form>
   * @description
   * Enables automatic saves on any form when the form is marked dirty.
   *
   * @param autosave {statement} - Evaluated when an autosave occurs, defaults to ng-submit.  This statement should return a promise or true to know when a save is successful
   * @param hasChanges {statement} - Evaluated to determine if there have been changes, defaults to `(form.$valid && form.$dirty)`
   * @param autosave-debounce {number} - Number of milliseconds to debounce saves, defaults to 2000 (2s)
   * @returns Directive Definition Object {object}
   */
  var autosaveComponentModule = angular.module('Q.components.forms.autosave', []).directive('autosave', ['$timeout', function ($timeout) {
    return {
      restrict: 'A',
      require: '^form',
      link: function link(scope, elem, attr, formCtrl) {
        var debounce = parseInt(attr.autosaveDebounce || 2000, 10);
        var markErrors = !!attr.markErrors;
        var nextSave = -1;
        var delayedSaveTimer = false;

        scope.$watch(function () {
          if (!delayedSaveTimer && hasChanges()) _save();
        });

        scope.$on('$destroy', function () {
          if (delayedSaveTimer) {
            $timeout.cancel(delayedSaveTimer);
          }
        });

        function hasChanges() {
          var hasChanges = attr.hasChanges && scope.$eval(attr.hasChanges) || formCtrl.$valid && formCtrl.$dirty;
          if (markErrors && !formCtrl.$valid && formCtrl.$dirty) {
            formCtrl.$setSubmitted();
          }
          return hasChanges;
        }

        function _save() {
          var delay = 100;

          if (Date.now() < nextSave) {
            delay = nextSave - Date.now();
          }
          delayedSaveTimer = $timeout(function () {
            delayedSaveTimer = false;
            if (hasChanges()) _handleSave();
          }, delay);
        }

        function _handleSave() {
          nextSave = Date.now() + debounce;

          _cleanForm();

          var fnName = _getSaveAttr(attr);

          scope.$eval(fnName);
        }

        function _cleanForm() {
          if (formCtrl.$valid) {
            formCtrl.$setPristine();
            formCtrl.$setUntouched();
          }
        }
      }
    };
  }]);

  function _getSaveAttr(attr) {
    var ret = false;
    ['autosave', 'ngSubmit'].some(function (name) {
      if (attr[name] && angular.isString(attr[name]) && attr[name].length > 1) {
        ret = attr[name];
        return true;
      }
    });
    if (!ret) throw new Error('Unable to find save function');
    return ret;
  }

  var autoSaveFormComponentModule = angular.module('Q.components.forms.auto-save-form', []).directive('autoSaveForm', function () {
    return {
      restrict: 'A',
      require: 'form',
      link: function link(scope, elem, attrs, ngForm) {
        scope.$on('$stateChangeStart', function (event) {
          if (event.defaultPrevented) return;
          if (ngForm.$valid) {
            if (/\(.*\)/.test(attrs.autoSaveForm)) {
              scope.$eval(attrs.autoSaveForm, { $event: event });
            } else {
              scope.$eval(attrs.ngSubmit, { $event: event });
            }
          }
        });
      }
    };
  });

  var autoUrlProtocolComponentModule = angular.module('Q.components.forms.auto-url-protocol', []).directive('autoUrlProtocol', function () {
    return {
      require: 'ngModel',
      link: function link(scope, el, attrs, ngModel) {
        ngModel.$parsers.push(function (v) {
          if (v.length && !/^[a-z]+:/.test(v)) {
            v = 'http://' + v;
          }
          return v;
        });
      }
    };
  });

  var buttonComponentModule = angular.module('Q.components.forms.button', []).directive('button', function () {
    return {
      restrict: 'E',
      link: function link(scope, elem) {
        if (!elem.attr('type')) {
          elem.attr('type', 'button');
        }
      }
    };
  });

  var formGroupTemplate = "<div class=\"form-group\" ng-transclude></div> ";

  /*eslint-disable angular/angularelement */

  var $ = angular.element;

  function _addAttrIfMissing($node, attr, val) {
    if (!$node.attr(attr)) {
      $node.attr(attr, val);
    }
  }

  var formGroupComponentModule = angular.module('Q.components.forms.form-group', [formGroupMessagesComponentModule])

  // This directive enables us to recompile the content of the form-group, so we can
  // automatically add content like form-group-messages, labels, and name/id/form references.
  // This saves a lot of extra redundant text in general.
  .directive('formGroup', function () {
    return {
      restrict: 'E',
      priority: 50.1,
      require: '^form',
      compile: function compile(tElement, iAttrs) {
        // determine node name
        var $input = tElement.querySelector('[ng-model]');
        var name = $input.attr('name') || $input.attr('id') || $input.attr('ng-model');
        var id = $input.attr('id') || name;

        _addAttrIfMissing($input, 'name', name);
        _addAttrIfMissing($input, 'id', id);

        // Add generic label if missing, and add attributes if existing
        var $label = tElement.find('label').eq(0);
        if ($label.length === 0 && iAttrs.label) {
          $label = $('<label>');
          $label.text(iAttrs.label);
          tElement.prepend($label);
        }
        _addAttrIfMissing($label, 'for', id);
        $label.addClass('control-label');

        // Add generic messages block if missing
        var $errors = tElement.querySelector('form-group-messages');
        if (!$errors.length) {
          $errors = $('<form-group-messages></form-group-messages>');
          tElement.append($errors);
        }
      }
    };
  })

  // This directive runs at a lower priority, and enables us to do two things:
  // 1. Hook into the focus/blur events of any child inputs.
  // 2. Capture data used by our form-group-messages controller so we can generate nice generic error messages.
  .directive('formGroup', function () {
    return {
      restrict: 'E',
      require: 'formGroup',
      priority: 50,
      transclude: true,
      template: formGroupTemplate,
      controller: ['$element', function FormGroupCtrl($element) {
        var ctrl = this;
        ctrl.setFocus = function (hasFocus) {
          return $element.children().toggleClass('has-focus', hasFocus);
        };
      }],
      link: function link(scope, elem, attrs, formGroupCtrl) {
        elem.children().addClass(attrs.formGroupClass);
        if (attrs.hasSuccess) {
          scope.$watch(attrs.hasSuccess, function (val) {
            elem.children().toggleClass('has-success', !!val);
          });
        }
        if (attrs.hasWarning) {
          scope.$watch(attrs.hasWarning, function (val) {
            elem.children().toggleClass('has-warning', !!val);
          });
        }
        scope.$applyAsync(function () {
          var $inputs = elem.querySelectorAll('[ng-model]');
          formGroupCtrl.$inputs = $inputs;
          formGroupCtrl.attrs = attrs;
          $inputs.on('focus', function () {
            return formGroupCtrl.setFocus(true);
          });
          $inputs.on('blur', function () {
            return formGroupCtrl.setFocus(false);
          });

          if (formGroupCtrl.configureMessages) {
            formGroupCtrl.configureMessages($inputs);
          }
        });
      }
    };
  });

  var inputGroupRequiredComponentModule = angular.module('Q.components.forms.input-group-required', []).directive('inputGroupRequired', function () {
    return {
      restrict: 'A',
      link: function link(scope, elem) {
        scope.$watch(function () {
          return !!elem[0].querySelector('.ng-invalid-required');
        }, function (invalid) {
          elem.toggleClass('ng-invalid-required', invalid);
        });
      }
    };
  });

  var loginVerificationComponentModule = angular.module('Q.components.forms.login-verification', [formGroupMessagesComponentModule]).directive('loginVerification', ['FORM_GROUP_MESSAGES', function (FORM_GROUP_MESSAGES) {
    FORM_GROUP_MESSAGES.add('incorrectPassword', 'Incorrect current password.');

    return {
      require: 'ngModel',
      link: function link(scope, element, attrs, ngModel) {
        function setValidity(obj) {
          angular.forEach(obj, function (val, key) {
            ngModel.$setValidity(key, !val);
          });
        }

        scope.$watch(attrs.loginVerification, function (newValue, oldValue) {
          if (oldValue !== newValue) {
            setValidity(newValue);
          }
        });
        setValidity(scope.$eval(attrs.loginVerification));
      }
    };
  }]);

  function markValidation(restrict) {
    return function () {
      return {
        restrict: restrict,
        require: '^form',
        link: function link(scope, element, attrs, formCtrl) {
          scope.$applyAsync(function () {
            var names = angular.element(element[0].querySelectorAll('[ng-model]')).map('attr', 'name');
            var test = attrs.markInvalidIf;
            if (names.length) {
              var wasInvalid = false;
              scope.$watch(function () {
                var pending = !wasInvalid && formCtrl.$pending;
                return !pending && names.some(function (name) {
                  // Mark a control invalid if the field is invalid and one of the following is true:
                  // - The form has been submitted
                  // - The field has been touched AND is not pristine (has been modified)
                  // leftoverText is a fix for ngTagsInput
                  var t = formCtrl && formCtrl[name] && !formCtrl[name].$valid && (isFormSubmitted$1(formCtrl) || formCtrl[name].$touched && (!formCtrl[name].$pristine || formCtrl[name].$error.leftoverText));
                  if (test) {
                    t = t || scope.$eval(test);
                  }
                  return t;
                });
              }, function (isInvalid) {
                wasInvalid = isInvalid;
                element.toggleClass('has-error', isInvalid);
              });
            }
          });
        }
      };
    };
  }

  function isFormSubmitted$1(form) {
    /* eslint-disable angular/no-private-call */
    while (!form.$submitted && form.$$parentForm && form.$$parentForm.$name) {
      form = form.$$parentForm;
    }
    /* eslint-enable */
    return form.$submitted;
  }

  /**
   * @name MarkValidation
   * @docType shared
   * @category function
   * @description
   * Marks bootstrap validation classes on the given element, based on
   * the first input with an ng-model it finds.
   * @param restrict {string} - restrict parameter for directives ('AEC' or any combination)
   * @returns directive descriptor {function} - a function that returns a Directive Definition Object
   */
  var markValidationComponentModule = angular.module('Q.components.forms.mark-validation', []).value('isFormSubmitted', isFormSubmitted$1).directive('markValidation', markValidation('A')).directive('formGroup', markValidation('C'));

  var multilinePlaceholderComponentModule = angular.module('Q.components.forms.multiline-placeholder', []).directive('multilinePlaceholder', function () {
    return {
      restrict: 'A',
      link: function link(scope, elem, attr) {
        var placeholder = angular.element('<div class="multiline-placeholder-element"></div>');
        placeholder.text(attr.multilinePlaceholder);
        elem.attr('placeholder', '');
        elem[0].parentNode.insertBefore(placeholder[0], elem[0]);

        var updateEmpty = function updateEmpty() {
          return placeholder.toggleClass('empty', !elem.val());
        };
        elem.on('keyup keypress blur change', updateEmpty);
        scope.$applyAsync(updateEmpty);
      }
    };
  });

  /*====================
   * 2023-01-10 - Phil DeJarnett
   * NOTE: This module has been stripped down significantly.
   *
   * If we need to bring back international phone number parsing & rendering, look at the history of this file
   * around 2023-01-10, which includes the ability to format and parse international phone numbers, as well as
   * detecting the country of origin for those phone numbers using country calling codes.
   *
   * As phone numbers are no longer a critical part of our application, we removed that library to reduce application
   * weight.
   *===================*/

  var usNumber = /^(\+\d)?\d{7}(\d{3,})?(x\d+)?$/;
  var validNumber = /\+?\d{5,}(x\d+;)?/;

  function cleanPhoneNumber(v) {
    if (v && v.length && /\d/.test(v)) {
      return v.replace(/[-\s\.\(\)]+/g, '');
    }
    return v;
  }

  function formatPhoneNumber(v) {
    var phone = v;
    v = cleanPhoneNumber(v);
    if (usNumber.test(v)) {
      phone = '';
      if (v.length > 10 && v[0] === '+') {
        phone = '+' + v[1] + ' ';
        v = v.slice(2);
      }
      phone += v.replace(/^(...)?(...)(....)(x.+)?$/, function (m, n1, n2, n3, nExt) {
        var ret = '';
        if (n1) {
          ret += '(' + n1 + ') ';
        }
        ret += n2 + '-' + n3;
        if (nExt) {
          ret += ' ' + nExt;
        }
        return ret;
      });
    }
    return phone;
  }

  function validate(v) {
    return validNumber.test(cleanPhoneNumber(v));
  }

  var phoneNumberValidatorComponentModule = angular.module('Q.components.forms.phone-number-validator', [formGroupMessagesComponentModule]).constant('formatPhoneNumber', formatPhoneNumber).filter('phoneNumber', function () {
    return formatPhoneNumber;
  }).directive('phoneNumber', ['$parse', 'FORM_GROUP_MESSAGES', function ($parse, FORM_GROUP_MESSAGES) {
    FORM_GROUP_MESSAGES.add('phoneNumber', 'Please provide a valid phone number.');

    return {
      require: ['ngModel', '?^phoneNumberCountry'],
      link: function link(scope, el, attrs, _ref) {
        var _ref2 = slicedToArray(_ref, 2),
            ngModel = _ref2[0],
            pnCountry = _ref2[1];

        if (pnCountry) {
          pnCountry.ngModel = ngModel;
        }

        ngModel.$parsers.push(function (value) {
          var cleanedResult = value && cleanPhoneNumber(value);
          return usNumber.test(cleanedResult) ? cleanedResult : value;
        });

        ngModel.$formatters.push(formatPhoneNumber);

        ngModel.$validators.phoneNumber = function (modelValue) {
          // enable soft validations
          if (attrs.phoneNumberEnableInvalid) {
            return true;
          } else {
            return !modelValue || validate(modelValue);
          }
        };

        el.on('blur', function () {
          if (ngModel.$valid) {
            ngModel.$setViewValue(formatPhoneNumber(ngModel.$modelValue));
            ngModel.$render();
          }
        });
      }
    };
  }]);

  var APP_ALERT_OFFSET_HEIGHT = 85;

  var scrollToErrorComponentModule = angular.module('Q.components.forms.scroll-to-error', [scrollToComponentModule, appAlertComponentModule])

  /**
   * @docType shared
   * @category directive
   *
   * @ngdoc directive
   * @name scrollToError
   * @restrict A
   * @description
   * Scrolls to the first form group or element marked with an error whenever a form is submitted.
   *
   * @param {string} scrollTarget Optional selector to pick the scrolling target, defaults the nearest scrollable element.
   * @param {string} relativeParent Optional selector to pick a parent to calculate relative offsets (for example, if there's a fixed header), defaults to the scroll target.
   * @param {expression} scrollToErrorOffset If provided, the scroll-to position is offset by the given amount.  If not provided and showMessage is enabled, the offset amount will be determined by the alert.
   * @param {string} showMessage If `true`, shows an app-alert message on error.  If any other string, shows that message.  If not provided, no message shown.
   * @param {expression} scrollToError Can be used to change the document before showing the error
   *
   * @example
   *   <form ng-submit="myControl.save()" scroll-to-error>...</form>
   */
  .directive('scrollToError', ['scrollToElement', '$timeout', 'appAlert', function (scrollToElement, $timeout, appAlert) {
    return {
      restrict: 'A',
      require: 'form',
      priority: -1,
      link: function link(scope, elem, attrs, ngFormCtrl) {
        var errorAlert = { close: angular.noop };
        scope.$watch(function () {
          return ngFormCtrl.$valid;
        }, function (valid) {
          if (valid) {
            errorAlert.close();
          }
        });
        scope.$on('$destroy', function () {
          errorAlert.close();
        });

        ngFormCtrl.scrollToError = function () {
          if (ngFormCtrl.$invalid) {
            if (attrs.showMessage) {
              var msg = 'Please fix the marked errors';
              if (attrs.showMessage !== 'true' && attrs.showMessage !== 'show-message') {
                msg = attrs.showMessage;
              }
              errorAlert = appAlert.error(msg);
            }
            if (attrs.scrollToError) {
              scope.$eval(attrs.scrollToError);
            }

            $timeout(function () {
              var firstErrorElem = elem[0].querySelector('.form-group.has-error') || elem[0].querySelector('.ng-invalid');
              if (firstErrorElem) {
                var offset = 0;
                if (attrs.scrollToErrorOffset) {
                  offset = scope.$eval(attrs.scrollToErrorOffset);
                } else if (attrs.showMessage) {
                  offset = APP_ALERT_OFFSET_HEIGHT;
                }

                scrollToElement({
                  target: firstErrorElem,
                  scrollTarget: attrs.scrollTarget,
                  relativeParent: attrs.relativeParent,
                  ignoreIfVisible: true,
                  offset: offset
                }).then(function () {
                  var errorInputElement = firstErrorElem;
                  if (['INPUT', 'SELECT', 'TEXTAREA'].indexOf(errorInputElement.nodeName) === -1) {
                    // the element isn't necessarily focusable, so look for an invalid child input element
                    errorInputElement = firstErrorElem.querySelector('input.ng-invalid, select.ng-invalid, textarea.ng-invalid');
                  }
                  if (!errorInputElement) {
                    // OK, that didn't work, now look for any child input element
                    errorInputElement = firstErrorElem.querySelector('input, select, textarea');
                  }
                  if (errorInputElement && errorInputElement.focus) {
                    // set the focus out-of-$digest, since this is a DOM command
                    $timeout(function () {
                      return errorInputElement.focus();
                    }, 1, false);
                  }
                });
              }
            });
          }
        };
        elem.on('submit', ngFormCtrl.scrollToError);
      }
    };
  }]);

  var selectComponentModule = angular.module('Q.components.forms.select', []).directive('select', function () {
    return {
      restrict: 'E',
      require: '?ngModel',
      link: function link(scope, elem, attrs, ngModelCtrl) {
        var opts = elem.find('option');
        var once = false;
        if (opts.length && opts.hasClass('placeholder')) {
          elem.on('keyup keydown', function () {
            return elem.toggleClass('placeholder', elem[0].value === '');
          });

          scope.$watch(function () {
            return elem[0].value;
          }, function (val, oldVal) {
            if (!once || val !== oldVal) {
              elem.toggleClass('placeholder', val === '');
              once = true;
            }
          });
        }
        if (elem.parent().hasClass('form-control-select')) {
          elem.on('focus', function () {
            elem.parent().addClass('focused');
          });
          elem.on('blur', function () {
            elem.parent().removeClass('focused');
          });
          scope.$watch(function () {
            return elem.attr('disabled') + elem.attr('readonly');
          }, function () {
            elem.parent().attr('disabled', elem.attr('disabled') === undefined ? null : 'disabled');
            elem.parent().attr('readonly', elem.attr('readonly') === undefined ? null : 'readonly');
          });
          if (ngModelCtrl) {
            var toggleInvalidClass = function toggleInvalidClass() {
              elem.parent().toggleClass('ng-invalid-required', !!ngModelCtrl.$error.required);
            };
            ngModelCtrl.$viewChangeListeners.push(toggleInvalidClass);
            // allows us to update the appearance whenever the model value changes without another watch
            ngModelCtrl.$formatters.push(function (val) {
              scope.$applyAsync(toggleInvalidClass);
              return val;
            });
            toggleInvalidClass();
          }
        }
      }
    };
  });

  var stopRouteChangeIfInvalidComponentModule = angular.module('Q.components.forms.stop-route-change-if-invalid', [appAlertComponentModule]).factory('showFormError', ['appAlert', function (appAlert) {
    return function showFormError(ngForm, event) {
      if (Array.isArray(ngForm)) {
        return ngForm.map(function (form) {
          return showFormError(form, event);
        }).some(angular.identity);
      }
      if (ngForm && ngForm.$invalid) {
        if (event) {
          event.preventDefault();
        }
        ngForm.$setSubmitted();
        if (ngForm.scrollToError) {
          ngForm.scrollToError();
        } else {
          appAlert.error('Please fix the marked errors.');
        }
        return true;
      }
    };
  }]).directive('stopRouteChangeIfInvalid', ['showFormError', '$state', function (showFormError, $state) {
    return {
      restrict: 'A',
      require: 'form',
      link: function link(scope, elem, attrs, ngForm) {
        var userLoggedOut = false;

        function preventDefaultOnInvalid(event, toState) {
          var toStateName = toState && toState.name;
          var fromStateName = $state.current && $state.current.name;
          // Only process if...
          if (!userLoggedOut && // still logged in
          !event.defaultPrevented && // not prevented elsewhere
          fromStateName && // not the initial load
          toStateName !== 'error' && // not switching to an error
          toStateName !== fromStateName && // we're actually switching routes
          !scope.$eval(attrs.stopRouteChangeIfInvalid) // not told to ignore
          ) {
              showFormError(ngForm, event);
            }
        }

        scope.$on('warnOnChanges.shouldContinue', preventDefaultOnInvalid);
        scope.$on('$stateChangeStart', preventDefaultOnInvalid);
        scope.$on('app.logout', function () {
          return userLoggedOut = true;
        });
      }
    };
  }]);

  var tagsInputTabindexComponentModule = angular.module('Q.components.forms.tags-input-tabindex', []).directive('tagsInput', function () {
    return {
      priority: -1,
      link: function link(scope, elem) {
        elem.attr('tabindex', '-1');
      }
    };
  });

  /* eslint-disable angular/module-setter, angular/module-getter */

  var CHROME_BROWSER = /Chrome/.test(window.navigator.userAgent);

  function disableAutocomplete(element) {
    // fix for LastPass
    element.attr('data-lpignore', 'true');
    // fix for Dashlane
    element.attr('data-form-type', 'other');

    // disables Chrome's stupid autofill which ignores autocomplete because why the hell not?
    // now you have to give Chrome a random value to prevent it from autofilling
    element.attr('autocomplete', CHROME_BROWSER ? uuid() : 'off');
  }

  var autocompleteFixesComponentModule = angular.module('Q.components.autocomplete-fixes', []).directive('autocomplete', function () {
    return {
      restrict: 'A',
      priority: 1000,
      link: function link(scope, element) {
        if (['off', 'false'].includes(element.attr('autocomplete'))) {
          disableAutocomplete(element);
        }
      }
    };
  });

  ['datepickerPopup', 'typeahead'].forEach(function (name) {
    // augment 3rd-party directives to disable autocomplete
    autocompleteFixesComponentModule.directive(name, function () {
      return {
        restrict: 'A',
        priority: 1000,
        link: function link(scope, element) {
          disableAutocomplete(element);
        }
      };
    });
  });

  var formsComponentModule = angular.module('Q.components.forms', [autosaveComponentModule, autoSaveFormComponentModule, autoUrlProtocolComponentModule, buttonComponentModule, formDataComponentModule, formGroupComponentModule, inputGroupRequiredComponentModule, loginVerificationComponentModule, markValidationComponentModule, multilinePlaceholderComponentModule, phoneNumberValidatorComponentModule, scrollToErrorComponentModule, selectComponentModule, serverValidationErrorsComponentModule, stopRouteChangeIfInvalidComponentModule, tagsInputTabindexComponentModule, uniqueEmailValidatorComponentModule, warnOnChangesComponentModule, autocompleteFixesComponentModule]);

  var dropdownComponentModule = angular.module('Q.components.dropdown', [positionHelperModule]).constant('dropdownConfig', {
    openClass: 'open'
  }).factory('dropdownService', ['$document', '$rootScope', function ($document, $rootScope) {
    var openScope = null;

    return {
      open: function open(dropdownScope) {
        if (!openScope) {
          $document.bind('click', closeDropdown);
          $document.bind('keydown', escapeKeyBind);
        }

        if (openScope && openScope !== dropdownScope) {
          openScope.isOpen = false;
        }

        openScope = dropdownScope;
      },
      close: function close(dropdownScope) {
        if (openScope === dropdownScope) {
          openScope = null;
          $document.unbind('click', closeDropdown);
          $document.unbind('keydown', escapeKeyBind);
        }
      }
    };

    function closeDropdown(evt) {
      // This method may still be called during the same mouse event that
      // unbound this event handler. So check openScope before proceeding.
      if (!openScope) {
        return;
      }

      // if requested, always ignore clicks
      if (evt && openScope.getAutoClose() === 'disabled') {
        return;
      }

      var eventIsIn = function eventIsIn(el) {
        return el && el[0].contains(evt.target);
      };

      // always ignore the toggle (button) for this dropdown
      if (evt && eventIsIn(openScope.getToggleElement())) {
        return;
      }

      var $element = openScope.getElement();
      var $menu = openScope.getDropdownMenu();
      // if requested, ignore clicks inside the menu or anwhere in the dropdown
      if (evt && openScope.getAutoClose() === 'outsideClick' && (eventIsIn($element) || eventIsIn($menu))) {
        return;
      }

      openScope.isOpen = false;

      // eslint-disable-next-line angular/no-private-call
      if (!$rootScope.$$phase) {
        openScope.$apply();
      }
    }

    function escapeKeyBind(evt) {
      if (evt.which === 27) {
        openScope.focusToggleElement();
        closeDropdown();
      }
    }
  }]).controller('DropdownCtrl', ['$scope', '$attrs', '$parse', 'dropdownConfig', 'dropdownService', '$animate', '$document', 'positionHelper', '$timeout', function ($scope, $attrs, $parse, dropdownConfig, dropdownService, $animate, $document, positionHelper, $timeout) {
    var ctrl = this;
    var scope = $scope.$new();
    // create a child scope so we are not polluting original one
    var openClass = dropdownConfig.openClass;
    var getIsOpen = void 0;
    var setIsOpen = angular.noop;
    var toggleInvoker = $attrs.onToggle ? $parse($attrs.onToggle) : angular.noop;
    var appendToBody = false;
    var dropUp = false;

    ctrl.init = function (element) {
      ctrl.$element = element;

      if ($attrs.isOpen) {
        getIsOpen = $parse($attrs.isOpen);
        setIsOpen = getIsOpen.assign;

        $scope.$watch(getIsOpen, function (value) {
          scope.isOpen = !!value;
        });
      }

      dropUp = element.hasClass('dropup');
    };

    ctrl.addDropdownMenu = function (dropdownMenu) {
      ctrl.dropdownMenu = dropdownMenu;
      appendToBody = angular.isDefined($attrs.dropdownAppendToBody);
      if (appendToBody && dropdownMenu) {
        dropdownMenu.addClass('dropdown-in-body');
        $document.find('body').append(dropdownMenu);
        $scope.$on('$destroy', function handleDestroyEvent() {
          dropdownMenu.remove();
        });
      }
    };

    ctrl.toggle = function (open) {
      return scope.isOpen = arguments.length ? !!open : !scope.isOpen;
    };

    // Allow other directives to watch status
    ctrl.isOpen = function () {
      return scope.isOpen;
    };

    scope.getToggleElement = function () {
      return ctrl.toggleElement;
    };

    scope.getDropdownMenu = function () {
      return ctrl.dropdownMenu;
    };

    scope.getAutoClose = function () {
      return $attrs.autoClose || 'always'; //or 'outsideClick' or 'disabled'
    };

    scope.getElement = function () {
      return ctrl.$element;
    };

    scope.focusToggleElement = function () {
      if (ctrl.toggleElement) {
        ctrl.toggleElement.focus();
      }
    };

    var positionUnwatch = null;

    scope.$watch('isOpen', function (isOpen, wasOpen) {
      if (appendToBody && ctrl.dropdownMenu) {
        var positionEl = ctrl.$element;
        if (positionEl.querySelector('[dropdown-toggle]')) {
          positionEl = positionEl.querySelector('[dropdown-toggle]');
        }
        if (isOpen && !positionUnwatch) {
          var position = $attrs.dropdownPosition;
          if (!position || position[0] === '?') {
            position = (dropUp ? 'top' : positionHelper.topOrBottom(positionEl)) + (position ? position.slice(1) : '');
          }
          positionUnwatch = positionHelper.autoPositionPopup(scope, positionEl, ctrl.dropdownMenu, position, { setMinWidth: true });
        } else if (!isOpen && positionUnwatch) {
          positionUnwatch();
          positionUnwatch = null;
        }
        $timeout(function () {
          ctrl.dropdownMenu.css({
            display: isOpen ? 'block' : 'none'
          });
        }, 0, false);
      }

      $animate[isOpen ? 'addClass' : 'removeClass'](ctrl.$element, openClass);

      if (isOpen) {
        scope.focusToggleElement();
        dropdownService.open(scope);
      } else {
        dropdownService.close(scope);
      }

      setIsOpen($scope, isOpen);
      if (angular.isDefined(isOpen) && isOpen !== wasOpen) {
        toggleInvoker($scope, { open: !!isOpen });
      }
    });

    $scope.$on('$locationChangeSuccess', function () {
      if (scope.getAutoClose() === 'always') {
        scope.isOpen = false;
      }
    });

    $scope.$on('$destroy', function () {
      scope.$destroy();
    });
  }])

  /**
   * @ngdoc directive
   * @name dropdown
   * @description
   * Creates a dropdown menu
   *
   * @restrict A
   * @element ANY
   * @param {expression} isOpen Value to manually toggle the menu
   * @param {expression} onToggle Callback when toggled
   * @param {*} dropdownAppendToBody If true, the dropdown is appended to the body
   * @param {template=} dropdownPosition Force the position of the dropdown
   * @param {template=} autoClose Change the closing behavior
   *
   * @example
   * <example>
   * <a track-click="event.name" track-data="{ properties }"></a>
   * </example>
   */
  .directive('dropdown', function () {
    return {
      restrict: 'A',
      controller: 'DropdownCtrl',
      link: function link(scope, element, attrs, dropdownCtrl) {
        dropdownCtrl.init(element);
      }
    };
  }).directive('dropdownMenu', function () {
    return {
      restrict: 'A',
      require: '?^dropdown',
      link: function link(scope, element, attrs, dropdownCtrl) {
        if (!dropdownCtrl) {
          return;
        }
        dropdownCtrl.addDropdownMenu(element);
      }
    };
  }).directive('dropdownToggle', function () {
    return {
      restrict: 'A',
      require: '?^dropdown',
      link: function link(scope, element, attrs, dropdownCtrl) {
        if (!dropdownCtrl) {
          return;
        }

        dropdownCtrl.toggleElement = element;

        var toggleDropdown = function toggleDropdown(event) {
          event.preventDefault();

          if (!element.hasClass('disabled') && !attrs.disabled) {
            scope.$apply(function () {
              dropdownCtrl.toggle();
            });
          }
        };

        element.bind('click', toggleDropdown);

        // WAI-ARIA
        element.attr({ 'aria-haspopup': true, 'aria-expanded': false });
        scope.$watch(dropdownCtrl.isOpen, function (isOpen) {
          element.attr('aria-expanded', !!isOpen);
        });

        scope.$on('$destroy', function () {
          element.unbind('click', toggleDropdown);
        });
      }
    };
  });

  var uiBootstrapButtonsModule = 'ui.bootstrap.buttons';

  // Implement fullstory init here for asses(Datadog rum replacement)
  angular.module('qualified', [
  // general includes
  ngAriaModule, ngSanitizeModule, ngAnimateModule, loadingBarModule, loadingPanelServiceModule,

  // configuration modules
  configModule, autoRetryInterceptorModule, authInterceptorModule, railsInterceptorModule,

  // remote exception handling
  exceptionHandlerServiceModule,

  // routing setup
  allRoutesModule, stateSpecificComponentModule,

  // all models
  allModelsModule,

  // alert on releases
  releaseAlertServiceModule, productMaintenanceServiceModule,

  // bootstrap and common components
  uiBootstrapButtonsModule, dropdownComponentModule, uiLayoutComponentModule, formsComponentModule, tabsComponentModule, modalComponentModule, tooltipComponentModule,

  //analytics
  trackerServiceModule]).controller('AssessAppCtrl', function () {
    // nothing to do
  });

}());
